
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AuthProvider, useAuth } from "./AuthContext";
import LoginComponent_ROJO from "./LoginComponent_ROJO";
import { supabase } from "./supabaseClient";

// URL del logo BigBurguer - El archivo debe estar en la carpeta public del proyecto
const LOGO_BIGBURGUER = "/logo BIGBURGUER.jpg.jpg";

// Componente de Logo BigBurguer que usa la imagen real
const LogoBigBurguer = ({ size = 50 }) => (
  <img 
    src={LOGO_BIGBURGUER}
    alt="BigBurguer Logo"
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      objectFit: "cover",
      boxShadow: "0 4px 15px rgba(255, 107, 53, 0.4)",
      border: "3px solid #ffc53d"
    }}
  />
);

const SUPABASE_BUCKET_DOCS = "empleados-docs";
const SUPABASE_BUCKET_MEMORANDOS = SUPABASE_BUCKET_DOCS; // reutiliza el bucket existente

function slugifyPathSegment(texto) {
  if (!texto) return "archivo";
  let value = String(texto);
  if (typeof value.normalize === "function") {
    value = value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }
  value = value.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase();
  return value || "archivo";
}

/* ====================== NOTIFICACIONES SMS (TWILIO) ====================== */

// Función para formatear número de teléfono colombiano (sin + para Onurix)
function formatearTelefonoColombia(telefono) {
  if (!telefono) return null;
  let num = telefono.toString().replace(/\D/g, ''); // Solo dígitos
  
  // Si es número colombiano de 10 dígitos que empieza con 3, agregar 57
  if (num.length === 10 && num.startsWith('3')) {
    return '57' + num;
  }
  // Si ya tiene código de país (12 dígitos: 57 + 10), devolverlo limpio
  if (num.length === 12 && num.startsWith('57')) {
    return num;
  }
  // Si tiene 13 dígitos y empieza con 57, quitar el dígito extra
  if (num.length === 13 && num.startsWith('57')) {
    return num.slice(0, 12);
  }
  return num;
}

// Función para enviar SMS via API de Twilio
async function enviarSMS(telefono, mensaje) {
  if (!telefono) {
    console.warn("📱 No se puede enviar SMS: teléfono no proporcionado");
    return { ok: false, error: "Teléfono no proporcionado" };
  }

  // Formatear número antes de enviar
  const telefonoFormateado = formatearTelefonoColombia(telefono);
  console.log("📱 Enviando SMS a:", telefonoFormateado, "(original:", telefono, ")");

  try {
    // URL de la API de SMS en la intranet (Vercel)
    const API_SMS_URL = "https://intranetbigburguer.com/api/enviar-sms";
    
    const response = await fetch(API_SMS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ telefono: telefonoFormateado, mensaje }),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error("❌ Error enviando SMS:", data);
      return { ok: false, error: data.error || "Error al enviar SMS" };
    }

    console.log("✅ SMS enviado correctamente:", data);
    return { ok: true, sid: data.sid };
  } catch (error) {
    console.error("❌ Error de red al enviar SMS:", error);
    return { ok: false, error: error.message };
  }
}

// Función para notificar a un empleado por SMS sobre una nueva tarea asignada
async function notificarTareaAsignada(empleado, tarea) {
  if (!empleado?.telefono) {
    console.warn("📱 Empleado sin teléfono registrado, no se puede notificar");
    return;
  }

  const mensaje = `Nueva tarea asignada
Tipo: ${tarea.tipo || "Tarea"}
Sede: ${typeof tarea.sede === "object" ? tarea.sede?.nombre : tarea.sede || "N/A"}
Fecha limite: ${tarea.fechaLimite || "Sin fecha"}
Revise la intranet.`;

  return enviarSMS(empleado.telefono, mensaje);
}

// Función para notificar a un empleado sobre respuesta a su solicitud
async function notificarRespuestaSolicitud(empleado, solicitud, estado) {
  if (!empleado?.telefono) {
    console.warn("📱 Empleado sin teléfono registrado, no se puede notificar");
    return;
  }

  let mensaje = "";
  
  if (estado === "aprobado") {
    mensaje = `Su solicitud ha sido APROBADA. Ingrese a la intranet para ver los detalles.`;
  } else if (estado === "negado") {
    mensaje = `Su solicitud ha sido RECHAZADA. Ingrese a la intranet para ver los detalles.`;
  } else if (estado === "en_proceso") {
    mensaje = `Solicitud con REQUERIMIENTO. Ingrese a la intranet para ver los detalles y responder.`;
  } else {
    mensaje = `Su solicitud ha sido actualizada. Ingrese a la intranet para ver los detalles.`;
  }

  return enviarSMS(empleado.telefono, mensaje);
}

// Función para notificar a un empleado sobre una nueva solicitud del empleador
async function notificarSolicitudEmpleador(empleado, solicitud) {
  if (!empleado?.telefono) {
    console.warn("📱 Empleado sin teléfono registrado, no se puede notificar");
    return { ok: false, error: "Empleado sin teléfono" };
  }

  const mensaje = `BIGBURGUER - Nueva solicitud del empleador. 
Asunto: ${solicitud.asunto || "Solicitud"}
Ingrese a la intranet para ver los detalles y responder.`;

  return enviarSMS(empleado.telefono, mensaje);
}

/* ====================== DIAGNÓSTICO SUPABASE ====================== */

// Prueba la conexión con Supabase
async function probarConexionSupabase() {
  try {
    console.log("🔍 Probando conexión con Supabase...");
    const { data, error } = await supabase
      .from("empleados")
      .select("id")
      .limit(1);
    
    if (error) {
      console.error("❌ Error de conexión:", error.message);
      console.error("Detalles:", error);
      return false;
    }
    
    console.log("✅ Conexión exitosa con Supabase");
    return true;
  } catch (e) {
    console.error("❌ Error al probar conexión:", e.message);
    console.error("Stack:", e);
    return false;
  }
}

/* ====================== FUNCIONES SUPABASE ====================== */

// Cargar empleados desde Supabase (con filtro opcional por empresa)
async function cargarEmpleadosDeSupabase(empresaId = null) {
  try {
    let query = supabase.from("empleados").select("*");
    
    // Si se especifica empresaId, filtrar por empresa
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("Error cargando empleados:", error);
      return [];
    }

    // Mapear campos de BD a React y limpiar datos
    const empleadosLimpios = (data || []).map(emp => {
      // Construir nombre completo desde nombres + apellidos
      const nombres = emp.nombres || "";
      const apellidos = emp.apellidos || "";
      const nombreCompleto = `${nombres} ${apellidos}`.trim();
      
      const empleadoMapeado = {
        id: emp.id,
        nombre: nombreCompleto || "", // Nombre completo: nombres + apellidos
        nombres: nombres, // Guardar nombres por separado
        apellidos: apellidos, // Guardar apellidos por separado
        tipoDoc: emp.tipodoc || "",
        documento: emp.documento || "",
        fechaNacimiento: emp.fechanacimiento || "",
        fechaExpedicion: emp.fechaexpedicion || "",
        perfil: emp.perfil || "",
        rh: emp.rh || "",
        edad: emp.edad || "",
        nacionalidad: emp.nacionalidad || "",
        telefono: emp.telefono || "",
        direccion: emp.direccion || "",
        fechaIngreso: emp.fechaingreso || "",
        tipoContrato: emp.tipocontrato || "",
        sede: emp.sede || "",
        cargo: emp.cargo || "",
        fechaRetiro: emp.fecharetiro || "",
        eps: emp.eps || "",
        arl: emp.arl || "",
        fondo: emp.fondopensiones || "",
        banco: emp.banco || "",
        tipoCuenta: emp.tipocuenta || "",
        numeroCuenta: emp.numerocuenta || "",
        salarioBase: emp.salariobase || 0,
        salarioProm6m: emp.promedioultimos6meses || 0,
        valorBonificacion: emp.bonificacion || 0,
        afiliadoSeguridadSocial: emp.afiliadoseguridadsocial !== false, // Por defecto true
        docs: Array.isArray(emp.docs)
          ? emp.docs.map((doc) => {
              if (!doc) return doc;
              const next = { ...doc };
              if (!next.bucket && next.path) {
                next.bucket = SUPABASE_BUCKET_DOCS;
              }
              return next;
            })
          : [],
        activo: emp.activo !== false,
        // Campos que React usa pero no están en BD
        hijos: [],
        tieneHijos: false,
        smlv: 1750905, // SMLV 2025-2026 actualizado
        ultimaPrima: "",
        ultimasCesantias: "",
        ultimasVacaciones: "",
        ultimaLiquidacionContrato: "",
      };

      // Limpiar sede si está en formato JSON
      if (empleadoMapeado.sede && typeof empleadoMapeado.sede === 'string') {
        try {
          const sedeObj = JSON.parse(empleadoMapeado.sede);
          if (sedeObj && sedeObj.nombre) {
            empleadoMapeado.sede = sedeObj.nombre;
          }
        } catch (e) {
          // Si no es JSON válido, dejar como está
        }
      }

      return empleadoMapeado;
    });

    return empleadosLimpios;
  } catch (e) {
    console.error("Error en cargarEmpleadosDeSupabase:", e);
    return [];
  }
}

// Guardar empleado en Supabase (con empresa_id opcional para multi-empresa)
async function guardarEmpleadoEnSupabase(empleado, empresaId = null) {
  try {
    console.log("📝 Empleado recibido:", empleado);
    console.log("   - tipoDoc:", empleado.tipoDoc);
    console.log("   - sede:", empleado.sede);
    console.log("   - nombres:", empleado.nombres);
    console.log("   - apellidos:", empleado.apellidos);
    console.log("   - empresa_id:", empresaId);

    // Función para convertir cualquier valor a string simple
    const toString = (val) => {
      if (val === null || val === undefined) return "";
      if (typeof val === 'object') return JSON.stringify(val);
      return String(val).trim();
    };

    // Función para convertir a número
    const toNumber = (val) => {
      const num = Number(val);
      return isNaN(num) ? 0 : num;
    };

    // Extraer el nombre de la sede si es objeto
    let sedeNombre = empleado.sede;
    if (sedeNombre && typeof sedeNombre === 'object' && sedeNombre.nombre) {
      sedeNombre = sedeNombre.nombre;
    } else if (typeof sedeNombre === 'object') {
      sedeNombre = "";
    }

    // Preparar los campos LIMPIOS como strings/números
    const datosLimpios = {
      id: empleado.id ? toString(empleado.id) : Math.random().toString(36).slice(2, 9),
      tipodoc: toString(empleado.tipoDoc),
      documento: toString(empleado.documento),
      nombres: toString(empleado.nombres),
      apellidos: toString(empleado.apellidos),
      fechanacimiento: toString(empleado.fechaNacimiento),
      fechaexpedicion: toString(empleado.fechaExpedicion),
      perfil: toString(empleado.perfil),
      rh: toString(empleado.rh),
      edad: toString(empleado.edad),
      nacionalidad: toString(empleado.nacionalidad),
      telefono: toString(empleado.telefono),
      direccion: toString(empleado.direccion),
      fechaingreso: toString(empleado.fechaIngreso),
      tipocontrato: toString(empleado.tipoContrato),
      sede: toString(sedeNombre),  // Usa el nombre extraído
      cargo: toString(empleado.cargo),
      fecharetiro: toString(empleado.fechaRetiro),
      eps: toString(empleado.eps),
      arl: toString(empleado.arl),
      fondopensiones: toString(empleado.fondo),
      banco: toString(empleado.banco),
      tipocuenta: toString(empleado.tipoCuenta),
      numerocuenta: toString(empleado.numeroCuenta),
      salariobase: toNumber(empleado.salarioBase),
      promedioultimos6meses: toNumber(empleado.salarioProm6m),
      bonificacion: toNumber(empleado.valorBonificacion),
      afiliadoseguridadsocial: empleado.afiliadoSeguridadSocial !== false,
      docs: Array.isArray(empleado.docs) ? empleado.docs : [],
      activo: empleado.activo !== false
    };
    
    // Agregar empresa_id si se proporciona
    if (empresaId) {
      datosLimpios.empresa_id = empresaId;
    }

    console.log("📦 Datos limpios a guardar:", datosLimpios);
    console.log("   - tipodoc:", datosLimpios.tipodoc);
    console.log("   - sede:", datosLimpios.sede);

    let error;
    let data;

    // Verificar si el empleado ya existe en Supabase
    const { data: existente, error: errorVerif } = await supabase
      .from("empleados")
      .select("id")
      .eq("id", datosLimpios.id)
      .maybeSingle();

    if (existente) {
      // Si existe, hacer UPDATE
      console.log("🔄 Actualizando empleado con ID:", datosLimpios.id);
      const result = await supabase
        .from("empleados")
        .update(datosLimpios)
        .eq("id", datosLimpios.id);
      error = result.error;
      data = result.data;
    } else {
      // Si no existe, hacer INSERT
      console.log("➕ Insertando empleado nuevo con ID:", datosLimpios.id);
      const result = await supabase
        .from("empleados")
        .insert([datosLimpios]);
      error = result.error;
      data = result.data;
    }
    
    if (error) {
      console.error("❌ Error al guardar:", error.message);
      console.error("Detalles:", JSON.stringify(error, null, 2));
      return false;
    }

    console.log("✅ Empleado guardado correctamente en Supabase");
    return true;
  } catch (e) {
    console.error("❌ Error en guardarEmpleadoEnSupabase:", e.message);
    console.error("Stack:", e.stack);
    return false;
  }
}

// Cargar sedes desde Supabase
async function cargarSedesDeSupabase(empresaId = null) {
  try {
    let query = supabase.from("sedes").select("*");
    
    // Si se especifica empresaId, filtrar por empresa
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("Error cargando sedes:", error);
      return [];
    }
    const sedesMapeadas = (data || []).map((sede) => {
      // Parsear firma electrónica si existe y validar formato
      let firmaElectronica = null;
      if (sede.firma_electronica) {
        try {
          const parsed = typeof sede.firma_electronica === 'string' 
            ? JSON.parse(sede.firma_electronica) 
            : sede.firma_electronica;
          // Solo aceptar si tiene una imagen base64 válida
          if (parsed && parsed.firma && typeof parsed.firma === 'string' && parsed.firma.startsWith('data:image')) {
            firmaElectronica = parsed;
          } else {
            console.warn(`⚠️ Sede "${sede.nombre}": firma_electronica sin formato válido, ignorando`);
          }
        } catch (e) {
          console.warn('Error parseando firma_electronica:', e);
        }
      }
      return {
        id: sede.id || null,
        nombre: sede.nombre || "",
        nit: sede.nit || "",
        razonSocial: sede.razonsocial || sede.razonSocial || "",
        representanteLegal: sede.representantelegal || sede.representanteLegal || "",
        tipoDocRepresentante: sede.tipodocrepresentante || sede.tipoDocRepresentante || "Cédula de Ciudadanía",
        cedulaRepresentante: sede.cedularepresentante || sede.cedulaRepresentante || "",
        generoRepresentante: sede.generorepresentante || sede.generoRepresentante || "Masculino",
        direccion: sede.direccion || "",
        telefono: sede.telefono || "",
        firmaElectronica: firmaElectronica,
      };
    });
    console.log("📋 Sedes cargadas con datos de representante:", sedesMapeadas.map(s => ({
      nombre: s.nombre,
      representanteLegal: s.representanteLegal,
      tipoDocRepresentante: s.tipoDocRepresentante,
      cedulaRepresentante: s.cedulaRepresentante
    })));
    return sedesMapeadas;
  } catch (e) {
    console.error("Error en cargarSedesDeSupabase:", e);
    return [];
  }
}

function prepararPayloadSede(sede, empresaId = null) {
  const id = sede.id || uid();
  const payload = {
    id,
    nombre: sede.nombre || "",
    nit: sede.nit || "",
    razonsocial: sede.razonSocial || sede.razonsocial || "",
    representantelegal: sede.representanteLegal || sede.representantelegal || "",
    tipodocrepresentante: sede.tipoDocRepresentante || sede.tipodocrepresentante || "Cédula de Ciudadanía",
    cedularepresentante: sede.cedulaRepresentante || sede.cedularepresentante || "",
    generorepresentante: sede.generoRepresentante || sede.generorepresentante || "Masculino",
    direccion: sede.direccion || "",
    telefono: sede.telefono || "",
    firma_electronica: sede.firmaElectronica ? JSON.stringify(sede.firmaElectronica) : null,
  };
  // Agregar empresa_id si está definido
  if (empresaId) {
    payload.empresa_id = empresaId;
  }
  return payload;
}

// Payload básico sin los campos nuevos (para compatibilidad)
function prepararPayloadSedeBasico(sede, empresaId = null) {
  const id = sede.id || uid();
  const payload = {
    id,
    nombre: sede.nombre || "",
    nit: sede.nit || "",
    razonsocial: sede.razonSocial || sede.razonsocial || "",
    representantelegal: sede.representanteLegal || sede.representantelegal || "",
    direccion: sede.direccion || "",
    telefono: sede.telefono || "",
  };
  // Agregar empresa_id si está definido
  if (empresaId) {
    payload.empresa_id = empresaId;
  }
  return payload;
}

async function crearSedeEnSupabase(sede, empresaId = null) {
  try {
    const payload = prepararPayloadSede(sede, empresaId);
    console.log("📦 Payload para crear sede:", payload);
    const { error } = await supabase.from("sedes").insert([payload]);
    if (error) {
      console.warn("⚠️ Error con campos nuevos, intentando con campos básicos...", error);
      // Intentar con payload básico si fallan los campos nuevos
      const payloadBasico = prepararPayloadSedeBasico(sede, empresaId);
      const { error: error2 } = await supabase.from("sedes").insert([payloadBasico]);
      if (error2) {
        console.error("❌ Error creando sede en Supabase:", error2);
        alert("Para guardar todos los datos del representante legal, agrega estas columnas en Supabase:\n- tipodocrepresentante (text)\n- cedularepresentante (text)\n- generorepresentante (text)");
        return false;
      }
      console.log("✅ Sede creada en Supabase (sin campos nuevos):", payloadBasico.nombre);
      return true;
    }
    console.log("✅ Sede creada en Supabase:", payload.nombre);
    return true;
  } catch (e) {
    console.error("❌ Error en crearSedeEnSupabase:", e);
    return false;
  }
}

async function actualizarSedeEnSupabase(sede, empresaId = null) {
  try {
    if (!sede?.id) {
      console.error("❌ No se puede actualizar una sede sin id válido");
      return false;
    }
    const payload = prepararPayloadSede(sede, empresaId);
    console.log("📦 Payload para actualizar sede:", payload);
    const { error } = await supabase
      .from("sedes")
      .update(payload)
      .eq("id", payload.id);
    if (error) {
      console.warn("⚠️ Error con campos nuevos, intentando con campos básicos...", error);
      // Intentar con payload básico si fallan los campos nuevos
      const payloadBasico = prepararPayloadSedeBasico(sede, empresaId);
      const { error: error2 } = await supabase
        .from("sedes")
        .update(payloadBasico)
        .eq("id", payloadBasico.id);
      if (error2) {
        console.error("❌ Error actualizando sede en Supabase:", error2);
        alert("Para guardar todos los datos del representante legal, agrega estas columnas en Supabase:\n- tipodocrepresentante (text)\n- cedularepresentante (text)\n- generorepresentante (text)");
        return false;
      }
      console.log("✅ Sede actualizada en Supabase (sin campos nuevos):", payloadBasico.nombre);
      return true;
    }
    console.log("✅ Sede actualizada en Supabase:", payload.nombre);
    return true;
  } catch (e) {
    console.error("❌ Error en actualizarSedeEnSupabase:", e);
    return false;
  }
}

async function eliminarSedeEnSupabase(id) {
  try {
    if (!id) {
      console.error("❌ No se puede eliminar una sede sin id");
      return false;
    }
    const { error } = await supabase.from("sedes").delete().eq("id", id);
    if (error) {
      console.error("❌ Error eliminando sede en Supabase:", error);
      return false;
    }
    console.log("✅ Sede eliminada en Supabase:", id);
    return true;
  } catch (e) {
    console.error("❌ Error en eliminarSedeEnSupabase:", e);
    return false;
  }
}

// Limpiar datos históricos: convertir sede de JSON a texto
async function limpiarSedesEnHistoricos() {
  try {
    console.log("🧹 Limpiando datos históricos de sedes...");
    
    const { data, error: errorLectura } = await supabase
      .from("empleados")
      .select("id, sede");
    
    if (errorLectura) {
      console.error("Error leyendo empleados:", errorLectura);
      return;
    }

    // Filtrar solo aquellos que tienen JSON en sede
    const aActualizar = [];
    (data || []).forEach(emp => {
      if (emp.sede && typeof emp.sede === 'string' && emp.sede.includes('{')) {
        try {
          const sedeObj = JSON.parse(emp.sede);
          if (sedeObj && sedeObj.nombre) {
            aActualizar.push({ id: emp.id, nombre: sedeObj.nombre });
          }
        } catch (e) {
          // No es JSON válido, ignorar
        }
      }
    });

    if (aActualizar.length === 0) {
      console.log("✅ No hay datos para limpiar");
      return;
    }

    console.log(`📝 Actualizando ${aActualizar.length} registros...`);

    // Actualizar cada uno
    for (const emp of aActualizar) {
      const { error: errorUpdate } = await supabase
        .from("empleados")
        .update({ sede: emp.nombre })
        .eq("id", emp.id);
      
      if (errorUpdate) {
        console.error(`❌ Error actualizando ${emp.id}:`, errorUpdate);
      } else {
        console.log(`✅ Actualizado ${emp.id}: ${emp.nombre}`);
      }
    }

    console.log("🧹 Limpieza completada");
  } catch (e) {
    console.error("Error en limpiarSedesEnHistoricos:", e);
  }
}

function mapHorarioSupabase(row) {
  if (!row) return null;
  return {
    id: row.id,
    semanaInicio: row.semana_inicio || row.semanainicio || row.semanaInicio || "",
    semanaFin: row.semana_fin || row.semanafin || row.semanaFin || "",
    estado: row.estado || "temporal",
    diasCerrados: row.dias_cerrados || [],
    celdas: row.celdas || {},
    eventos: row.eventos || {},
    eventosPorDia: row.eventos_por_dia || row.eventosPorDia || {},
    creadoEn: row.creado_en || row.created_at || null,
    actualizadoEn: row.actualizado_en || row.updated_at || null,
    nombre: row.nombre || null,
  };
}

async function cargarHorariosDeSupabase(empresaId = null) {
  try {
    console.log("📅 [Horarios] Cargando horarios de Supabase, empresa:", empresaId || "TODAS", "| tipo:", typeof empresaId);
    
    let data = [];
    let error = null;
    
    if (empresaId) {
      // Cargar horarios de la empresa específica
      console.log("📅 [Horarios] Ejecutando query con empresa_id =", empresaId);
      const { data: horariosEmpresa, error: err1 } = await supabase
        .from("horarios")
        .select("id, semana_inicio, semana_fin, estado, dias_cerrados, celdas, eventos, eventos_por_dia, creado_en, actualizado_en, nombre, empresa_id")
        .eq("empresa_id", empresaId)
        .order("semana_inicio", { ascending: false });
      
      // También cargar horarios sin empresa asignada (para compatibilidad)
      const { data: horariosSinEmpresa, error: err2 } = await supabase
        .from("horarios")
        .select("id, semana_inicio, semana_fin, estado, dias_cerrados, celdas, eventos, eventos_por_dia, creado_en, actualizado_en, nombre, empresa_id")
        .is("empresa_id", null)
        .order("semana_inicio", { ascending: false });
      
      if (err1) {
        console.error("❌ Error cargando horarios de empresa:", err1);
      }
      if (err2) {
        console.error("❌ Error cargando horarios sin empresa:", err2);
      }
      
      // Combinar ambos resultados
      data = [...(horariosEmpresa || []), ...(horariosSinEmpresa || [])];
      
      console.log("📅 [Horarios] Horarios encontrados para empresa", empresaId, ":", horariosEmpresa?.length || 0, "| IDs:", (horariosEmpresa || []).map(h => h.id.substring(0,8)).join(", "));
      console.log("📅 [Horarios] Horarios sin empresa (compartidos):", horariosSinEmpresa?.length || 0);
    } else {
      // Cargar todos los horarios
      const result = await supabase
        .from("horarios")
        .select("id, semana_inicio, semana_fin, estado, dias_cerrados, celdas, eventos, eventos_por_dia, creado_en, actualizado_en, nombre, empresa_id")
        .order("semana_inicio", { ascending: false });
      
      data = result.data || [];
      error = result.error;
      
      if (error) {
        console.error("❌ Error cargando horarios:", error);
        return [];
      }
    }
    
    console.log("📅 [Horarios] Total horarios encontrados:", data?.length || 0);
    if (data?.length > 0) {
      console.log("📅 [Horarios] Semanas disponibles:", data.map(h => h.semana_inicio).join(", "));
      // Debug: mostrar empresa_id de cada horario
      console.log("📅 [Horarios] Detalle empresa_id:", data.map(h => `${h.semana_inicio}: ${h.empresa_id || 'null'}`).join(" | "));
    }
    
    return (data || []).map(mapHorarioSupabase).filter(Boolean);
  } catch (e) {
    console.error("❌ Error en cargarHorariosDeSupabase:", e);
    return [];
  }
}

async function guardarHorarioEnSupabase(horario, empresaId = null) {
  try {
    if (!horario?.id) {
      console.error("❌ No se puede guardar un horario sin id");
      return false;
    }
    const payload = {
      id: horario.id,
      semana_inicio: horario.semanaInicio,
      semana_fin: horario.semanaFin,
      estado: horario.estado,
      dias_cerrados: horario.diasCerrados || [],
      celdas: horario.celdas || {},
      eventos: horario.eventos || {},
      eventos_por_dia: horario.eventosPorDia || {},
      actualizado_en: new Date().toISOString(),
    };
    if (horario.creadoEn) {
      payload.creado_en = horario.creadoEn;
    } else {
      payload.creado_en = new Date().toISOString();
    }
    // Agregar empresa_id si está disponible
    if (empresaId) {
      payload.empresa_id = empresaId;
    }
    const { error } = await supabase.from("horarios").upsert([payload], { onConflict: "id" });
    if (error) {
      console.error("❌ Error guardando horario en Supabase:", error);
      if (typeof window !== "undefined") {
        alert(`No se pudo guardar el horario en Supabase: ${error.message || "error desconocido"}`);
      }
      return false;
    }
    console.log("✅ Horario guardado en Supabase", horario.id);
    return true;
  } catch (e) {
    console.error("❌ Error en guardarHorarioEnSupabase:", e);
    if (typeof window !== "undefined") {
      alert(`No se pudo guardar el horario en Supabase: ${e.message || e}`);
    }
    return false;
  }
}

// Mapear ausencia desde Supabase a formato interno
function mapAusenciaSupabase(row) {
  if (!row) return null;
  
  let razon = row.razon || "";
  
  // Intentar parsear razon si es JSON (por compatibilidad con datos más complejos)
  if (typeof razon === "string" && razon.startsWith("{")) {
    try {
      const parsed = JSON.parse(razon);
      razon = parsed;
    } catch (e) {
      // Mantener como string si no es JSON válido
    }
  }

  return {
    id: row.id,
    empleadoId: row.empleadoid || "",
    tipo: row.tipo || "Permiso",
    origen: "",
    inicio: row.fechainicio || "",
    fin: row.fechafin || "",
    periodo: row.periodo || "",
    detalleVac: [],
    motivo: row.motivo || (typeof razon === "string" ? razon : ""),
    cobroPagado: row.cobro === true || row.cobro === "true" || row.cobro === 1,
    cobroFecha: "",
    reintegro: row.reintegro || "",
    dias: row.diastotales ? Number(row.diastotales) : 0,
    motivoModificacion: row.motivo_modificacion || "",
    fechaModificacion: row.fecha_modificacion || null,
    historialModificaciones: row.historial_modificaciones || [],
    creadoEn: row.created_at || null,
    actualizadoEn: row.updated_at || null,
  };
}

// Preparar ausencia para guardar en Supabase
function prepararPayloadAusencia(a) {
  if (!a) return null;
  const orNull = (value) => {
    if (value === "" || typeof value === "undefined" || value === null) return null;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed === "" ? null : trimmed;
    }
    return value;
  };

  return {
    id: a.id || uid(),
    empleadoid: orNull(a.empleadoId),
    tipo: a.tipo || "Permiso",
    fechainicio: orNull(a.inicio),
    fechafin: orNull(a.fin),
    periodo: orNull(a.periodo),
    motivo: orNull(a.motivo || ""),
    razon: orNull(a.motivo || ""),
    reintegro: orNull(a.reintegro),
    cobro: a.cobroPagado === true || a.cobroPagado === "true" || a.cobroPagado === 1,
    diastotales: a.dias || 0,
    motivo_modificacion: orNull(a.motivoModificacion || ""),
    fecha_modificacion: a.fechaModificacion || null,
    historial_modificaciones: a.historialModificaciones || [],
    created_at: a.creadoEn || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
}

// Guardar ausencia en Supabase
async function guardarAusenciaEnSupabase(ausencia, empresaId = null) {
  try {
    console.log("📝 Ausencia a guardar (original):", JSON.stringify(ausencia, null, 2));
    
    const payload = prepararPayloadAusencia(ausencia);
    if (!payload) {
      console.error("❌ No se pudo preparar el payload de la ausencia");
      return null;
    }
    
    // Agregar empresa_id si está disponible
    if (empresaId) {
      payload.empresa_id = empresaId;
    }

    console.log("📤 Payload preparado para Supabase:", JSON.stringify(payload, null, 2));

    let result;
    if (ausencia.id) {
      console.log("🔄 Intentando UPDATE del registro...");
      // Intentar UPDATE
      result = await supabase
        .from("ausencias")
        .update(payload)
        .eq("id", ausencia.id)
        .select();

      const { data: updateData, error: updateError } = result;

      // Si UPDATE no encontró el registro, hacer INSERT
      if (updateError) {
        console.log("⚠️  UPDATE falló, intentando INSERT...");
        result = await supabase
          .from("ausencias")
          .insert([payload])
          .select();
      } else if (!updateData || updateData.length === 0) {
        console.log("⚠️  UPDATE no afectó ningún registro, intentando INSERT...");
        result = await supabase
          .from("ausencias")
          .insert([payload])
          .select();
      }
    } else {
      console.log("✨ Haciendo INSERT de nuevo registro...");
      // INSERT
      result = await supabase
        .from("ausencias")
        .insert([payload])
        .select();
    }

    const { data, error } = result;

    if (error) {
      console.error("❌ Error guardando ausencia en Supabase:");
      console.error(JSON.stringify(error, null, 2));
      console.error("Código de error:", error.code);
      console.error("Mensaje:", error.message);
      return null;
    }

    console.log("✅ Respuesta de Supabase:", JSON.stringify(data, null, 2));
    const registro = Array.isArray(data) ? data[0] : data;
    const mapeado = mapAusenciaSupabase(registro || payload);
    console.log("✅ Ausencia mapeada:", JSON.stringify(mapeado, null, 2));
    return mapeado;
  } catch (e) {
    console.error("❌ Error en guardarAusenciaEnSupabase:", e.message);
    console.error("Stack:", e.stack);
    return null;
  }
}

// Cargar ausencias desde Supabase
async function cargarAusenciasDeSupabase(empresaId = null) {
  try {
    console.log("🔍 Cargando ausencias desde Supabase...");
    let query = supabase
      .from("ausencias")
      .select("id, empleadoid, tipo, fechainicio, fechafin, periodo, motivo, razon, reintegro, cobro, diastotales, created_at, updated_at, empresa_id")
      .order("created_at", { ascending: false });
    
    // Si se especifica empresaId, filtrar por empresa
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;

    if (error) {
      console.error("❌ Error cargando ausencias:");
      console.error(JSON.stringify(error, null, 2));
      return [];
    }

    console.log("📊 Datos crudos de Supabase:", JSON.stringify(data, null, 2));
    const mapeadas = (data || []).map(mapAusenciaSupabase).filter(Boolean);
    console.log("✅ Ausencias mapeadas:", JSON.stringify(mapeadas, null, 2));
    return mapeadas;
  } catch (e) {
    console.error("❌ Error en cargarAusenciasDeSupabase:", e.message);
    console.error(e.stack);
    return [];
  }
}

// 🔹 Eliminar ausencia de Supabase
async function eliminarAusenciaDeSupabase(ausenciaId) {
  try {
    console.log("🗑️ Eliminando ausencia de Supabase:", ausenciaId);
    
    const { error } = await supabase
      .from("ausencias")
      .delete()
      .eq("id", ausenciaId);
    
    if (error) {
      console.error("❌ Error eliminando ausencia de Supabase:", error.message);
      return false;
    }
    
    console.log("✅ Ausencia eliminada de Supabase:", ausenciaId);
    return true;
  } catch (e) {
    console.error("❌ Error en eliminarAusenciaDeSupabase:", e.message);
    return false;
  }
}

/* ====================== MEMORANDOS SUPABASE ====================== */
function mapMemorandoSupabase(row) {
  if (!row) return null;
  
  let adjuntos = [];
  if (row.adjuntos) {
    if (typeof row.adjuntos === "string") {
      try {
        adjuntos = JSON.parse(row.adjuntos);
      } catch (e) {
        console.warn("Error al parsear adjuntos:", e);
        adjuntos = [];
      }
    } else if (Array.isArray(row.adjuntos)) {
      adjuntos = row.adjuntos;
    }
  }
  
  adjuntos = (adjuntos || []).map((adj) => {
    if (!adj) return adj;
    const next = { ...adj };
    if (!next.bucket && next.path) {
      next.bucket = SUPABASE_BUCKET_MEMORANDOS;
    }
    if (!next.id) {
      next.id = uid();
    }
    return next;
  });

  return {
    id: row.id,
    empleadoId: row.empleadoid || "",
    tipoProceso: row.tipo_proceso || "MEMORANDO",
    descripcion: row.asunto || "",
    fechaEnvio: row.fechaemision || "",
    fechaDescargos: row.fecha_descargos || "",
    fechaDecision: row.fecha_decision || "",
    decision: row.contenido || "",
    creadoEn: row.created_at || null,
    actualizadoEn: row.updated_at || null,
    adjuntos,
    cerrado: row.leido === true || row.leido === "true" || row.leido === 1,
    cerradoEn: null,
  };
}

function prepararPayloadMemorando(m) {
  if (!m) return null;
  const orNull = (value) => {
    if (value === "" || typeof value === "undefined" || value === null) return null;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed === "" ? null : trimmed;
    }
    return value;
  };
  const id = ensureUuid(m.id);
  const empleadoId = orNull(m.empleadoId);
  const leido = m.cerrado === true || m.cerrado === "true" || m.cerrado === 1;
  
  const adjuntosArray = Array.isArray(m.adjuntos)
    ? m.adjuntos.map((adj) => {
        if (!adj) return adj;
        const next = { ...adj };
        if (!next.id) {
          next.id = uid();
        }
        if (!next.bucket && next.path) {
          next.bucket = SUPABASE_BUCKET_MEMORANDOS;
        }
        return next;
      })
    : [];

  return {
    id,
    empleadoid: empleadoId ? String(empleadoId) : null,
    tipo_proceso: m.tipoProceso || "MEMORANDO",
    asunto: m.descripcion || "",
    contenido: m.decision || "",
    tipo: null,
    fechaemision: orNull(m.fechaEnvio),
    fecha_descargos: orNull(m.fechaDescargos),
    fecha_decision: orNull(m.fechaDecision),
    leido,
    adjuntos: adjuntosArray.length > 0 ? adjuntosArray : null,
    created_at: m.creadoEn || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
}

async function cargarMemorandosDeSupabase(empresaId = null) {
  try {
    let query = supabase
      .from("memorandos")
      .select(
        "id, empleadoid, tipo_proceso, asunto, contenido, tipo, fechaemision, fecha_descargos, fecha_decision, leido, adjuntos, created_at, updated_at, empresa_id"
      )
      .order("created_at", { ascending: false });
    
    // Si se especifica empresaId, filtrar por empresa
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;

    if (error) {
      console.error("❌ Error cargando memorandos:");
      console.error(JSON.stringify(error, null, 2));
      return [];
    }

    return (data || []).map(mapMemorandoSupabase).filter(Boolean);
  } catch (e) {
    console.error("❌ Error en cargarMemorandosDeSupabase:", e.message);
    return [];
  }
}

async function guardarMemorandoEnSupabase(memorando, empresaId = null) {
  try {
    const payload = prepararPayloadMemorando(memorando);
    if (!payload) {
      console.error("❌ No se pudo preparar el payload del memorando");
      return null;
    }
    
    // Agregar empresa_id si está disponible
    if (empresaId) {
      payload.empresa_id = empresaId;
    }

    console.log("📤 Enviando memorando a Supabase:");
    console.log(JSON.stringify(payload, null, 2));

    const { data, error } = await supabase
      .from("memorandos")
      .upsert([payload], { onConflict: "id" })
      .select();

    if (error) {
      console.error("❌ Error guardando memorando en Supabase:");
      console.error(JSON.stringify(error, null, 2));
      console.log("📋 Payload que causó el error:");
      console.log(JSON.stringify(payload, null, 2));
      return null;
    }

    const registro = Array.isArray(data) ? data[0] : data;
    return mapMemorandoSupabase(registro || payload);
  } catch (e) {
    console.error("❌ Error en guardarMemorandoEnSupabase:", {
      message: e.message,
      stack: e.stack,
    });
    return null;
  }
}

async function eliminarMemorandoEnSupabase(id) {
  try {
    if (!id) {
      console.error("❌ No se puede eliminar un memorando sin id");
      return false;
    }

    const { error } = await supabase.from("memorandos").delete().eq("id", id);
    if (error) {
      console.error("❌ Error eliminando memorando en Supabase:", error);
      return false;
    }
    return true;
  } catch (e) {
    console.error("❌ Error en eliminarMemorandoEnSupabase:", e);
    return false;
  }
}

/* ====================== OTROS DESCUENTOS SUPABASE ====================== */
// Estructura: { id, empleadoId, concepto, tipo: "meses"|"indefinido", valorMensual, valorTotal, mesesRestantes, mesesOriginales, activo, fechaCreacion, empresa_id }

async function cargarOtrosDescuentosDeSupabase(empresaId = null) {
  try {
    let query = supabase
      .from("otros_descuentos")
      .select("*")
      .eq("activo", true)
      .order("created_at", { ascending: false });
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;
    if (error) {
      console.error("❌ Error cargando otros descuentos:", error);
      return [];
    }
    
    return (data || []).map(row => ({
      id: row.id,
      empleadoId: row.empleado_id,
      concepto: row.concepto || "",
      tipo: row.tipo || "indefinido", // "meses" o "indefinido"
      valorMensual: Number(row.valor_mensual) || 0,
      valorQuincenal: Number(row.valor_quincenal) || Math.round((Number(row.valor_mensual) || 0) / 2),
      mesesRestantes: Number(row.meses_restantes) || 0,
      mesesOriginales: Number(row.meses_restantes) || 0, // Usar meses_restantes como fallback
      activo: row.activo !== false,
      fechaCreacion: row.created_at,
      empresaId: row.empresa_id,
      origenNominaId: row.origen_nomina_id || null, // Para identificar saldos pendientes de nómina
      esQuincenaUnica: row.es_quincena_unica || false, // Para 1 sola quincena
    }));
  } catch (e) {
    console.error("❌ Error en cargarOtrosDescuentosDeSupabase:", e);
    return [];
  }
}

async function guardarOtroDescuentoEnSupabase(descuento, empresaId = null) {
  try {
    const valorMensual = Number(descuento.valorMensual) || 0;
    // Usar valorQuincenal directo si existe, sino calcular
    const valorQuincenal = descuento.valorQuincenal || Math.round(valorMensual / 2);
    const payload = {
      id: ensureUuid(descuento.id),
      empleado_id: descuento.empleadoId,
      concepto: descuento.concepto,
      tipo: descuento.tipo, // "meses" o "indefinido"
      valor_mensual: valorMensual,
      valor_quincenal: valorQuincenal,
      meses_restantes: descuento.mesesRestantes || 0,
      activo: descuento.activo !== false,
      es_quincena_unica: descuento.esQuincenaUnica || false,
    };
    
    if (empresaId) {
      payload.empresa_id = empresaId;
    }
    
    const { data, error } = await supabase
      .from("otros_descuentos")
      .upsert([payload], { onConflict: "id" })
      .select();
    
    if (error) {
      console.error("❌ Error guardando otro descuento:", error);
      return null;
    }
    
    return data?.[0] || payload;
  } catch (e) {
    console.error("❌ Error en guardarOtroDescuentoEnSupabase:", e);
    return null;
  }
}

async function eliminarOtroDescuentoEnSupabase(id) {
  try {
    // En lugar de eliminar, marcar como inactivo
    const { error } = await supabase
      .from("otros_descuentos")
      .update({ activo: false })
      .eq("id", id);
    
    if (error) {
      console.error("❌ Error eliminando otro descuento:", error);
      return false;
    }
    return true;
  } catch (e) {
    console.error("❌ Error en eliminarOtroDescuentoEnSupabase:", e);
    return false;
  }
}

// Crear saldo pendiente automático cuando el neto es negativo
async function crearSaldoPendienteNomina(empleadoId, valorNegativo, quincena, mes, nominaId, empresaId = null) {
  try {
    const conceptoQuincena = quincena === 1 ? "Q1" : "Q2";
    const concepto = `Saldo ${conceptoQuincena} ${mes}`;
    const valorAbsoluto = Math.abs(valorNegativo);
    
    const nuevoDescuento = {
      id: ensureUuid(),
      empleado_id: empleadoId,
      concepto: concepto,
      tipo: "meses",
      valor_mensual: valorAbsoluto * 2, // Para que quincenal sea el valor exacto
      valor_quincenal: valorAbsoluto,
      meses_restantes: 1, // Solo 1 quincena (se descuenta la siguiente)
      activo: true,
      empresa_id: empresaId,
      origen_nomina_id: nominaId, // Para rastrear de qué nómina viene
    };

    const { data, error } = await supabase
      .from("otros_descuentos")
      .upsert([nuevoDescuento], { onConflict: "id" })
      .select();

    if (error) {
      console.error("❌ Error creando saldo pendiente:", error);
      return null;
    }

    console.log(`💰 Saldo pendiente creado: ${concepto} - $${valorAbsoluto.toLocaleString()} para empleado ${empleadoId}`);
    return data?.[0] || nuevoDescuento;
  } catch (e) {
    console.error("❌ Error en crearSaldoPendienteNomina:", e);
    return null;
  }
}

// Eliminar saldos pendientes creados por una nómina específica
async function eliminarSaldosPendientesPorNomina(nominaId) {
  try {
    // Buscar saldos pendientes que fueron creados por esta nómina
    const { data: saldos, error: fetchError } = await supabase
      .from("otros_descuentos")
      .select("*")
      .eq("origen_nomina_id", nominaId);

    if (fetchError) {
      console.error("❌ Error buscando saldos pendientes:", fetchError);
      return [];
    }

    if (!saldos || saldos.length === 0) {
      console.log("📋 No hay saldos pendientes para eliminar de esta nómina");
      return [];
    }

    // Eliminar (marcar como inactivos) los saldos encontrados
    const { error: deleteError } = await supabase
      .from("otros_descuentos")
      .update({ activo: false })
      .eq("origen_nomina_id", nominaId);

    if (deleteError) {
      console.error("❌ Error eliminando saldos pendientes:", deleteError);
      return saldos;
    }

    console.log(`🗑️ ${saldos.length} saldo(s) pendiente(s) eliminados de la nómina ${nominaId}`);
    return saldos;
  } catch (e) {
    console.error("❌ Error en eliminarSaldosPendientesPorNomina:", e);
    return [];
  }
}

// Restaurar saldos pendientes (reactivar los que fueron desactivados)
async function restaurarSaldosPendientesNomina(saldosARestaurar) {
  try {
    if (!saldosARestaurar || saldosARestaurar.length === 0) return;
    
    for (const saldo of saldosARestaurar) {
      await supabase
        .from("otros_descuentos")
        .update({ activo: true, meses_restantes: 1 })
        .eq("id", saldo.id);
    }
    
    console.log(`♻️ ${saldosARestaurar.length} saldo(s) pendiente(s) restaurados`);
  } catch (e) {
    console.error("❌ Error restaurando saldos pendientes:", e);
  }
}

// Reducir meses restantes después de descontar en nómina
async function reducirMesesDescuento(id) {
  try {
    // Primero obtener el registro actual
    const { data: current, error: fetchError } = await supabase
      .from("otros_descuentos")
      .select("meses_restantes, tipo")
      .eq("id", id)
      .single();
    
    if (fetchError || !current) return false;
    
    // Solo reducir si es tipo "meses" y tiene meses restantes
    if (current.tipo === "meses" && current.meses_restantes > 0) {
      const nuevosMeses = current.meses_restantes - 1;
      const { error } = await supabase
        .from("otros_descuentos")
        .update({ 
          meses_restantes: nuevosMeses,
          activo: nuevosMeses > 0 // Desactivar si llegó a 0
        })
        .eq("id", id);
      
      if (error) {
        console.error("❌ Error reduciendo meses:", error);
        return false;
      }
    }
    return true;
  } catch (e) {
    console.error("❌ Error en reducirMesesDescuento:", e);
    return false;
  }
}

/* ====================== COMIDAS POR QUINCENA SUPABASE ====================== */
// Guardar registro de comidas para una quincena específica
// Estructura tabla: id (uuid), periodo (text), comidas_data (jsonb), empresa_id (uuid), created_at

async function guardarComidasQuincenaEnSupabase(periodo, comidasData, empresaId = null) {
  try {
    // periodo = "2026-01-Q1" (ejemplo)
    // comidasData = { empleadoId1: valor1, empleadoId2: valor2, ... }
    
    if (!comidasData || Object.keys(comidasData).length === 0) {
      console.log("⚠️ No hay comidas para guardar");
      return true;
    }

    // Primero cargar datos existentes para fusionarlos
    const { data: existente } = await supabase
      .from("comidas_quincena")
      .select("id, comidas_data")
      .eq("periodo", periodo)
      .eq("empresa_id", empresaId || null)
      .maybeSingle();

    // Fusionar comidas existentes con las nuevas
    const comidasFusionadas = {
      ...(existente?.comidas_data || {}),
      ...comidasData
    };

    if (existente?.id) {
      // Actualizar registro existente
      const { error } = await supabase
        .from("comidas_quincena")
        .update({ comidas_data: comidasFusionadas })
        .eq("id", existente.id);
      
      if (error) {
        console.error("❌ Error actualizando comidas de quincena:", error);
        return false;
      }
    } else {
      // Crear nuevo registro
      const registro = {
        periodo: periodo,
        comidas_data: comidasFusionadas,
        empresa_id: empresaId || null,
      };
      
      const { error } = await supabase
        .from("comidas_quincena")
        .insert([registro]);
      
      if (error) {
        console.error("❌ Error insertando comidas de quincena:", error);
        return false;
      }
    }
    
    console.log(`✅ Comidas guardadas para período ${periodo}:`, Object.keys(comidasFusionadas).length, "empleados totales");
    return true;
  } catch (e) {
    console.error("❌ Error en guardarComidasQuincenaEnSupabase:", e);
    return false;
  }
}

async function cargarComidasQuincenaDeSupabase(empresaId = null) {
  try {
    let query = supabase
      .from("comidas_quincena")
      .select("*")
      .order("periodo", { ascending: false });
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;
    if (error) {
      console.error("❌ Error cargando comidas:", error);
      return {};
    }
    
    // Convertir a estructura: { "2026-01-Q1": { empId1: valor1, empId2: valor2 }, ... }
    const resultado = {};
    (data || []).forEach(row => {
      if (row.periodo && row.comidas_data) {
        // El comidas_data ya es un objeto JSONB con { empleadoId: valor, ... }
        resultado[row.periodo] = row.comidas_data;
      }
    });
    
    console.log(`✅ Comidas cargadas desde Supabase:`, Object.keys(resultado).length, "períodos");
    return resultado;
  } catch (e) {
    console.error("❌ Error en cargarComidasQuincenaDeSupabase:", e);
    return {};
  }
}

/* ====================== AJUSTES DE NÓMINA SUPABASE ====================== */
// Estructura: id, empresa_id, empleado_id, tipo_beneficiario (empleado/empresa), tipo_ajuste (horario/descanso),
// fechas_ajuste (jsonb array), total_horas_normales, total_horas_nocturnas, total_horas_festivas, 
// valor_total, concepto, nomina_id, periodo_aplicacion, mes_nomina, quincena, anio, estado, aplicado_en_nomina

async function cargarAjustesNominaDeSupabase(empresaId, periodoAplicacion = null) {
  try {
    let query = supabase
      .from("ajustes_nomina")
      .select("*")
      .eq("empresa_id", empresaId)
      .neq("estado", "cancelado")
      .order("fecha_creacion", { ascending: false });
    
    if (periodoAplicacion) {
      query = query.eq("periodo_aplicacion", periodoAplicacion);
    }
    
    const { data, error } = await query;
    if (error) {
      console.error("❌ Error cargando ajustes de nómina:", error);
      return [];
    }
    
    return (data || []).map(row => ({
      id: row.id,
      empresaId: row.empresa_id,
      empleadoId: row.empleado_id,
      empleadoNombre: row.empleado_nombre,
      tipoBeneficiario: row.tipo_beneficiario, // "empleado" o "empresa"
      tipoAjuste: row.tipo_ajuste, // "horario" o "descanso"
      fechasAjuste: row.fechas_ajuste || [], // Array de { fecha, tipoHora, cantidadHoras, valorHora, valorTotal, descripcion }
      totalHorasNormales: Number(row.total_horas_normales) || 0,
      totalHorasNocturnas: Number(row.total_horas_nocturnas) || 0,
      totalHorasFestivas: Number(row.total_horas_festivas) || 0,
      valorTotal: Number(row.valor_total) || 0,
      concepto: row.concepto || "",
      nominaId: row.nomina_id,
      periodoAplicacion: row.periodo_aplicacion,
      mesNomina: row.mes_nomina,
      quincena: row.quincena,
      anio: row.anio,
      estado: row.estado || "pendiente",
      aplicadoEnNomina: row.aplicado_en_nomina || false,
      fechaCreacion: row.fecha_creacion,
      fechaAplicacion: row.fecha_aplicacion,
      creadoPor: row.creado_por,
    }));
  } catch (e) {
    console.error("❌ Error en cargarAjustesNominaDeSupabase:", e);
    return [];
  }
}

async function guardarAjusteNominaEnSupabase(ajuste, empresaId) {
  try {
    const payload = {
      id: ajuste.id || ensureUuid(),
      empresa_id: empresaId,
      empleado_id: ajuste.empleadoId,
      empleado_nombre: ajuste.empleadoNombre,
      tipo_beneficiario: ajuste.tipoBeneficiario,
      tipo_ajuste: ajuste.tipoAjuste,
      fechas_ajuste: ajuste.fechasAjuste || [],
      total_horas_normales: ajuste.totalHorasNormales || 0,
      total_horas_nocturnas: ajuste.totalHorasNocturnas || 0,
      total_horas_festivas: ajuste.totalHorasFestivas || 0,
      valor_total: ajuste.valorTotal || 0,
      concepto: ajuste.concepto || "",
      nomina_id: ajuste.nominaId || null,
      periodo_aplicacion: ajuste.periodoAplicacion,
      mes_nomina: ajuste.mesNomina,
      quincena: ajuste.quincena,
      anio: ajuste.anio,
      estado: ajuste.estado || "pendiente",
      aplicado_en_nomina: ajuste.aplicadoEnNomina || false,
      creado_por: ajuste.creadoPor || null,
    };

    const { data, error } = await supabase
      .from("ajustes_nomina")
      .upsert([payload], { onConflict: "id" })
      .select();

    if (error) {
      console.error("❌ Error guardando ajuste de nómina:", error);
      return null;
    }

    console.log(`✅ Ajuste de nómina guardado: ${ajuste.empleadoNombre} - $${ajuste.valorTotal?.toLocaleString()}`);
    return data?.[0] || payload;
  } catch (e) {
    console.error("❌ Error en guardarAjusteNominaEnSupabase:", e);
    return null;
  }
}

async function verificarAjusteExistente(empresaId, empleadoId, fechas) {
  try {
    // Buscar ajustes existentes para el mismo empleado que contengan alguna de las fechas
    const { data, error } = await supabase
      .from("ajustes_nomina")
      .select("*")
      .eq("empresa_id", empresaId)
      .eq("empleado_id", empleadoId)
      .neq("estado", "cancelado");

    if (error) {
      console.error("❌ Error verificando ajuste existente:", error);
      return [];
    }

    // Filtrar ajustes que tengan fechas coincidentes
    const ajustesCoincidentes = (data || []).filter(ajuste => {
      const fechasAjuste = ajuste.fechas_ajuste || [];
      return fechasAjuste.some(fa => fechas.includes(fa.fecha));
    });

    return ajustesCoincidentes.map(row => ({
      id: row.id,
      periodoAplicacion: row.periodo_aplicacion,
      mesNomina: row.mes_nomina,
      quincena: row.quincena,
      fechasAjuste: row.fechas_ajuste,
      valorTotal: Number(row.valor_total) || 0,
      estado: row.estado,
    }));
  } catch (e) {
    console.error("❌ Error en verificarAjusteExistente:", e);
    return [];
  }
}

async function marcarAjusteComoAplicado(ajusteId, nominaId) {
  try {
    const { error } = await supabase
      .from("ajustes_nomina")
      .update({ 
        estado: "aplicado", 
        aplicado_en_nomina: true,
        nomina_id: nominaId,
        fecha_aplicacion: new Date().toISOString()
      })
      .eq("id", ajusteId);

    if (error) {
      console.error("❌ Error marcando ajuste como aplicado:", error);
      return false;
    }
    return true;
  } catch (e) {
    console.error("❌ Error en marcarAjusteComoAplicado:", e);
    return false;
  }
}

async function cancelarAjusteNomina(ajusteId) {
  try {
    const { error } = await supabase
      .from("ajustes_nomina")
      .update({ estado: "cancelado" })
      .eq("id", ajusteId);

    if (error) {
      console.error("❌ Error cancelando ajuste:", error);
      return false;
    }
    return true;
  } catch (e) {
    console.error("❌ Error en cancelarAjusteNomina:", e);
    return false;
  }
}

async function revertirAjustesDeNomina(nominaId) {
  try {
    // Buscar ajustes aplicados en esta nómina y revertirlos a pendiente
    const { error } = await supabase
      .from("ajustes_nomina")
      .update({ 
        estado: "pendiente", 
        aplicado_en_nomina: false,
        nomina_id: null,
        fecha_aplicacion: null
      })
      .eq("nomina_id", nominaId);

    if (error) {
      console.error("❌ Error revirtiendo ajustes:", error);
      return false;
    }
    console.log(`♻️ Ajustes revertidos para nómina ${nominaId}`);
    return true;
  } catch (e) {
    console.error("❌ Error en revertirAjustesDeNomina:", e);
    return false;
  }
}

// ====================== CÁLCULO DE PROMEDIO DE ÚLTIMOS 6 MESES (MENSUAL) ======================
async function calcularPromedioUltimos6Meses(empleadoId, empresaId, fechaActual = null) {
  try {
    if (!fechaActual) fechaActual = new Date();
    
    const mesActual = fechaActual.getMonth(); // 0-11
    const anioActual = fechaActual.getFullYear();
    
    // Constantes 2026
    const SALARIO_MINIMO_2026 = 1750905; // Salario mínimo legal vigente
    const SALARIO_MINIMO_QUINCENA = Math.round(SALARIO_MINIMO_2026 / 2); // 875,452.50
    const AUXILIO_TRANSPORTE = 300000; // Auxilio de transporte aproximado
    const SALARIO_MINIMO_CON_AUXILIO = SALARIO_MINIMO_2026 + AUXILIO_TRANSPORTE; // ~2,050,905
    
    // Determinar si estamos en primer semestre (enero-junio) o segundo (julio-diciembre)
    const esPrimerSemestre = mesActual < 6; // 0-5 = enero-junio
    
    // Meses a buscar (últimos 6 meses del semestre correspondiente)
    let mesesABuscar = [];
    if (esPrimerSemestre) {
      mesesABuscar = [0, 1, 2, 3, 4, 5];
    } else {
      mesesABuscar = [6, 7, 8, 9, 10, 11];
    }
    
    // Buscar registros de nómina
    const { data: registrosDirectos, error: errorDirecto } = await supabase
      .from('resumenes_nomina')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('anio', anioActual)
      .in('mes_numero', mesesABuscar);
    
    if (errorDirecto) throw errorDirecto;
    
    const valoresBruto = [];
    const detalleQuincenas = [];
    
    if (registrosDirectos && Array.isArray(registrosDirectos)) {
      for (const registro of registrosDirectos) {
        if (registro.empleados_detalle && Array.isArray(registro.empleados_detalle)) {
          const empleado = registro.empleados_detalle.find(e => e.empleadoId === empleadoId || e.id === empleadoId);
          if (empleado) {
            let valorQuincena = 0;
            let razon = '';
            
            const diasIncapacidad = empleado.incapacidad?.dias || empleado.diasIncapacidad || 0;
            const diasVacaciones = empleado.vacaciones?.dias || empleado.diasVacaciones || 0;
            const bruto = empleado.bruto || empleado.devengado || 0;
            
            // Regla 1: Si hay más de 6 días de vacaciones en la quincena
            if (diasVacaciones > 6) {
              valorQuincena = SALARIO_MINIMO_QUINCENA; // Sin auxilio de transporte
              razon = `Vacaciones (${diasVacaciones} días)`;
            }
            // Regla 2: Si hay más de 10 días de incapacidad
            else if (diasIncapacidad > 10) {
              valorQuincena = SALARIO_MINIMO_QUINCENA; // Sin auxilio de transporte
              razon = `Incapacidad (${diasIncapacidad} días)`;
            }
            // Regla 3: Si BRUTO es 0 o nulo
            else if (bruto === 0 || bruto === null) {
              valorQuincena = 0;
              razon = 'BRUTO = 0';
            }
            // Regla 4: Usar BRUTO normal
            else {
              valorQuincena = bruto;
              razon = 'BRUTO normal';
            }
            
            valoresBruto.push(valorQuincena);
            detalleQuincenas.push({
              mes: registro.mes_numero,
              quincena: registro.quincena,
              valor: valorQuincena,
              razon: razon,
              diasVacaciones,
              diasIncapacidad,
              brutoOriginal: bruto
            });
          }
        }
      }
    }
    
    // Calcular promedio quincenal
    const promedioQuincenal = valoresBruto.length > 0
      ? Math.round(valoresBruto.reduce((a, b) => a + b, 0) / valoresBruto.length)
      : 0;
    
    // Convertir a MENSUAL (promedio quincenal × 2)
    let promedioMensual = promedioQuincenal * 2;
    let aplicoMinimo = false;
    
    // Validar si es menor que salario mínimo + auxilio de transporte
    if (promedioMensual < SALARIO_MINIMO_CON_AUXILIO) {
      promedioMensual = SALARIO_MINIMO_CON_AUXILIO;
      aplicoMinimo = true;
    }
    
    return {
      promedio: promedioMensual,
      promedioQuincenal,
      valoresBruto,
      detalleQuincenas,
      aplicoMinimo,
      semestre: esPrimerSemestre ? 'Enero-Junio' : 'Julio-Diciembre',
      anio: anioActual,
      cantidadQuincenas: valoresBruto.length,
      salarioMinimoVigente: SALARIO_MINIMO_2026,
      auxilioTransporte: AUXILIO_TRANSPORTE
    };
  } catch (e) {
    console.error("❌ Error calculando promedio:", e);
    return { 
      promedio: 0, 
      promedioQuincenal: 0,
      valoresBruto: [], 
      detalleQuincenas: [], 
      aplicoMinimo: false,
      error: e.message 
    };
  }
}

// ====================== LIQUIDACIÓN MASIVA DE PROMEDIO PARA TODOS LOS EMPLEADOS ======================
async function liquidarPromedioTodosEmpleados(empresaId, empleadosList, fechaActual = null) {
  try {
    if (!fechaActual) fechaActual = new Date();
    
    console.log(`🔄 Iniciando liquidación masiva de ${empleadosList.length} empleados...`);
    
    const resultados = {
      exitosos: [],
      errores: [],
      resumen: { procesados: 0, exitosos: 0, fallidos: 0 }
    };
    
    for (const empleado of empleadosList) {
      try {
        const resultado = await calcularPromedioUltimos6Meses(empleado.id, empresaId, fechaActual);
        
        if (resultado.error) {
          resultados.errores.push({ empleado: empleado.nombre, error: resultado.error });
          resultados.resumen.fallidos++;
        } else {
          // Actualizar en base de datos con valor MENSUAL
          const { error: updateError } = await supabase
            .from('empleados')
            .update({ promedioultimos6meses: resultado.promedio })
            .eq('id', empleado.id);
          
          if (updateError) {
            resultados.errores.push({ empleado: empleado.nombre, error: updateError.message });
            resultados.resumen.fallidos++;
          } else {
            resultados.exitosos.push({
              empleado: empleado.nombre,
              promedio: resultado.promedio,
              promedioQuincenal: resultado.promedioQuincenal,
              quincenas: resultado.cantidadQuincenas,
              aplicoMinimo: resultado.aplicoMinimo
            });
            resultados.resumen.exitosos++;
          }
        }
      } catch (e) {
        resultados.errores.push({ empleado: empleado.nombre, error: e.message });
        resultados.resumen.fallidos++;
      }
      resultados.resumen.procesados++;
    }
    
    return resultados;
  } catch (e) {
    console.error("❌ Error en liquidación masiva:", e);
    return { exitosos: [], errores: [{ general: e.message }], resumen: { procesados: 0, exitosos: 0, fallidos: 0 } };
  }
}

/* ====================== RESÚMENES EJECUTIVOS DE NÓMINA SUPABASE ====================== */
// Estructura: id, empresa_id, periodo, quincena, mes, año, fecha_generacion, 
// total_empleados, total_devengado, total_descuentos, total_neto, resumen_json

async function guardarResumenNominaEnSupabase(resumen, empresaId = null) {
  try {
    const payload = {
      id: resumen.id || uid(),
      empresa_id: empresaId,
      periodo: resumen.periodo, // "2026-01-Q1"
      quincena: resumen.quincena, // 1 o 2
      mes: resumen.mes, // "Enero 2026"
      anio: resumen.anio, // 2026
      mes_numero: resumen.mesNumero, // 1-12
      fecha_generacion: resumen.fechaGeneracion || new Date().toISOString(),
      rango_inicio: resumen.rangoInicio,
      rango_fin: resumen.rangoFin,
      total_empleados: resumen.totalEmpleados,
      total_devengado: resumen.totalDevengado,
      total_descuentos: resumen.totalDescuentos,
      total_neto: resumen.totalNeto,
      total_salud: resumen.totalSalud || 0,
      total_pension: resumen.totalPension || 0,
      total_prestamos: resumen.totalPrestamos || 0,
      total_comidas: resumen.totalComidas || 0,
      total_otros_descuentos: resumen.totalOtrosDescuentos || 0,
      total_aux_transporte: resumen.totalAuxTransporte || 0,
      total_bonificaciones: resumen.totalBonificaciones || 0,
      total_incapacidades: resumen.totalIncapacidades || 0,
      resumen_json: resumen.detalleCompleto || {},
      empleados_detalle: resumen.empleadosDetalle || [],
    };

    console.log("📊 Guardando resumen ejecutivo de nómina:", payload);

    const { error } = await supabase
      .from("resumenes_nomina")
      .upsert([payload], { onConflict: "id" });

    if (error) {
      console.error("❌ Error guardando resumen de nómina:", error);
      return false;
    }

    console.log("✅ Resumen de nómina guardado correctamente");
    return true;
  } catch (e) {
    console.error("❌ Error en guardarResumenNominaEnSupabase:", e);
    return false;
  }
}

async function cargarResumenesNominaDeSupabase(empresaId = null, filtros = {}) {
  try {
    let query = supabase
      .from("resumenes_nomina")
      .select("*")
      .order("fecha_generacion", { ascending: false });

    // Filtrar por empresa
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }

    // Filtrar por quincena específica
    if (filtros.quincena) {
      query = query.eq("quincena", filtros.quincena);
    }

    // Filtrar por mes
    if (filtros.mesNumero) {
      query = query.eq("mes_numero", filtros.mesNumero);
    }

    // Filtrar por año
    if (filtros.anio) {
      query = query.eq("anio", filtros.anio);
    }

    // Filtrar por periodo específico (ej: "2026-01-Q1")
    if (filtros.periodo) {
      query = query.eq("periodo", filtros.periodo);
    }

    const { data, error } = await query;

    if (error) {
      console.error("❌ Error cargando resúmenes de nómina:", error);
      return [];
    }

    return (data || []).map(row => ({
      id: row.id,
      empresaId: row.empresa_id,
      periodo: row.periodo,
      quincena: row.quincena,
      mes: row.mes,
      anio: row.anio,
      mesNumero: row.mes_numero,
      fechaGeneracion: row.fecha_generacion,
      rangoInicio: row.rango_inicio,
      rangoFin: row.rango_fin,
      totalEmpleados: row.total_empleados,
      totalDevengado: row.total_devengado,
      totalDescuentos: row.total_descuentos,
      totalNeto: row.total_neto,
      totalSalud: row.total_salud,
      totalPension: row.total_pension,
      totalPrestamos: row.total_prestamos,
      totalComidas: row.total_comidas,
      totalOtrosDescuentos: row.total_otros_descuentos,
      totalAuxTransporte: row.total_aux_transporte,
      totalBonificaciones: row.total_bonificaciones,
      totalIncapacidades: row.total_incapacidades,
      resumenJson: row.resumen_json,
      empleadosDetalle: row.empleados_detalle,
    }));
  } catch (e) {
    console.error("❌ Error en cargarResumenesNominaDeSupabase:", e);
    return [];
  }
}

// Función para eliminar un resumen ejecutivo de Supabase
async function eliminarResumenNominaDeSupabase(resumenId) {
  try {
    const { error } = await supabase
      .from("resumenes_nomina")
      .delete()
      .eq("id", resumenId);

    if (error) {
      console.error("❌ Error eliminando resumen de nómina:", error);
      return false;
    }

    console.log("✅ Resumen de nómina eliminado correctamente");
    return true;
  } catch (e) {
    console.error("❌ Error en eliminarResumenNominaDeSupabase:", e);
    return false;
  }
}

// Función helper para generar el resumen ejecutivo a partir de una nómina
function generarResumenEjecutivo(nomina, mesNomina, quincena) {
  const items = nomina.items || [];
  
  // Extraer año y mes del rango
  const fechaInicio = nomina.rango?.inicio || new Date().toISOString().slice(0, 10);
  const partes = fechaInicio.split("-");
  const anio = parseInt(partes[0]) || new Date().getFullYear();
  const mesNumero = parseInt(partes[1]) || (new Date().getMonth() + 1);
  
  // Calcular totales
  const totales = items.reduce((acc, item) => {
    acc.devengado += Math.round((item.bruto || 0) + (item.auxTransp || 0) + (item.adicionales?.bonificacion || 0) + (item.incapacidad?.valorTotal || 0));
    acc.salud += item.descuentos?.salud || 0;
    acc.pension += item.descuentos?.pension || 0;
    acc.prestamos += item.descuentos?.prestamos || 0;
    acc.comidas += item.descuentos?.comida || 0;
    acc.otrosDescuentos += item.descuentos?.otrosDescuentos || 0;
    acc.descuentos += (item.descuentos?.salud || 0) + (item.descuentos?.pension || 0) + 
                      (item.descuentos?.prestamos || 0) + (item.descuentos?.comida || 0) + 
                      (item.descuentos?.otrosDescuentos || 0);
    acc.neto += Math.round(item.neto || 0);
    acc.auxTransporte += item.auxTransp || 0;
    acc.bonificaciones += item.adicionales?.bonificacion || 0;
    acc.incapacidades += item.incapacidad?.valorTotal || 0;
    return acc;
  }, {
    devengado: 0, salud: 0, pension: 0, prestamos: 0, comidas: 0, 
    otrosDescuentos: 0, descuentos: 0, neto: 0, auxTransporte: 0, 
    bonificaciones: 0, incapacidades: 0
  });

  // Periodo en formato "YYYY-MM-QX"
  const periodo = `${anio}-${String(mesNumero).padStart(2, '0')}-Q${quincena}`;

  return {
    id: `resumen_${nomina.id}`,
    periodo: periodo,
    quincena: quincena,
    mes: mesNomina,
    anio: anio,
    mesNumero: mesNumero,
    fechaGeneracion: new Date().toISOString(),
    rangoInicio: nomina.rango?.inicio,
    rangoFin: nomina.rango?.fin,
    totalEmpleados: items.length,
    totalDevengado: totales.devengado,
    totalDescuentos: totales.descuentos,
    totalNeto: totales.neto,
    totalSalud: totales.salud,
    totalPension: totales.pension,
    totalPrestamos: totales.prestamos,
    totalComidas: totales.comidas,
    totalOtrosDescuentos: totales.otrosDescuentos,
    totalAuxTransporte: totales.auxTransporte,
    totalBonificaciones: totales.bonificaciones,
    totalIncapacidades: totales.incapacidades,
    empleadosDetalle: items.map(item => ({
      id: item.empleadoId,
      nombre: item.empleadoNombre,
      documento: item.documento,
      cargo: item.empleadoCargo,
      bruto: item.bruto,
      neto: item.neto,
    })),
    detalleCompleto: {
      nombre: nomina.nombre,
      fechaCreacion: nomina.fechaCreacion,
      items: items,
    },
  };
}

// Guardar nómina en Supabase
async function guardarNominaEnSupabase(nomina, empresaId = null) {
  try {
    console.log("📝 Guardando nómina en Supabase:", JSON.stringify(nomina, null, 2));
    
    // Guardar un registro por cada empleado en la nómina
    if (!nomina.items || nomina.items.length === 0) {
      console.warn("⚠️ No hay empleados en la nómina");
      return true;
    }

    const detallesPrestamos = nomina.detallesPrestamos || [];
    let todosGuardados = true;

    for (const item of nomina.items) {
      // Encontrar detalle de préstamos para este empleado
      const detallePrestamo = detallesPrestamos.find(
        (dp) => dp.empleadoId === item.empleadoId
      ) || { totalPrestamos: 0, detallePrestamosFull: [], cuotasProcesadas: [] };

      // Mapear a campos de Supabase
      const registroNomina = {
        id: `${nomina.id}_${item.empleadoId}`,
        empleadoid: item.empleadoId,
        periodo: nomina.rango?.inicio || new Date().toISOString(),
        fechageneracion: nomina.fechaCreacion || new Date().toISOString(),
        salariobase: item.bruto || 0,
        horasextras: item.detalleHoras?.hExDia || 0,
        valorextras: (item.detalleHoras?.hExDia || 0) * 8088, // Tarifa hora extra diurna
        bonificacion: item.adicionales?.bonificacion || 0,
        auxtransporte: item.auxTransp || 0,
        descuentosalud: item.descuentos?.salud || 0,
        descuentopension: item.descuentos?.pension || 0,
        descuentoarl: 0,
        descuentoprestamos: detallePrestamo.totalPrestamos || 0,
        descuentocomida: item.descuentos?.comida || 0,
        otros_descuentos: item.descuentos?.otrosDescuentos || 0,
        detalle_otros_descuentos: item.descuentos?.detalleOtros || [],
        detalle_horas_completo: { 
          ...(item.detalleHoras || { hNorm: 0, hNoct: 0, hFest: 0, hExDia: 0, hExNoc: 0, hExFest: 0 }),
          metodoLiquidacion: item.metodoLiquidacion || "con_recargos"
        },
        horas_totales: { 
          ...(item.horasTotales || item.detalleHoras || { hNorm: 0, hNoct: 0, hFest: 0, hExDia: 0, hExNoc: 0, hExFest: 0 }),
          metodoLiquidacion: item.metodoLiquidacion || "con_recargos"
        },
        detalle_por_fecha: item.detallePorFecha || [],
        detalle_prestamos: detallePrestamo.detallePrestamosFull || [],
        cuotas_procesadas: detallePrestamo.cuotasProcesadas || [],
        incapacidad_data: item.incapacidad || { dias: 0, valorDiario: 0, valorTotal: 0 },
        // Ajustes de nómina
        ajustes_nomina: item.ajustes || { sumaEmpleado: 0, restaEmpresa: 0, netoAjuste: 0, detalle: [], cantidad: 0 },
        totaldescuentos: 
          (item.descuentos?.salud || 0) +
          (item.descuentos?.pension || 0) +
          (item.descuentos?.prestamos || 0) +
          (item.descuentos?.comida || 0) +
          (item.descuentos?.otrosDescuentos || 0) +
          (item.ajustes?.restaEmpresa || 0),
        totaldevengado: item.bruto + (item.auxTransp || 0) + (item.adicionales?.bonificacion || 0) + (item.ajustes?.sumaEmpleado || 0),
        totalneto: item.neto || 0,
        estado: "Procesada",
      };
      
      // Agregar empresa_id si está disponible
      if (empresaId) {
        registroNomina.empresa_id = empresaId;
      }

      console.log(
        `📤 Guardando nómina para empleado ${item.empleadoId}:`,
        JSON.stringify(registroNomina, null, 2)
      );

      const { error } = await supabase
        .from("nominas")
        .upsert([registroNomina], { onConflict: "id" });

      if (error) {
        console.error(
          `❌ Error guardando nómina para empleado ${item.empleadoId}:`,
          error
        );
        todosGuardados = false;
      } else {
        console.log(`✅ Nómina guardada para empleado ${item.empleadoId}`);
      }
    }

    return todosGuardados;
  } catch (e) {
    console.error("❌ Error en guardarNominaEnSupabase:", e);
    return false;
  }
}

// Eliminar nómina de Supabase (para reabrir y editar)
async function eliminarNominaDeSupabase(nominaId) {
  try {
    console.log("🗑️ Eliminando nómina de Supabase:", nominaId);
    
    // Eliminar todos los registros que comiencen con este nominaId
    const { error } = await supabase
      .from("nominas")
      .delete()
      .like("id", `${nominaId}_%`);
    
    if (error) {
      console.error("❌ Error eliminando nómina:", error);
      return false;
    }
    
    console.log("✅ Nómina eliminada de Supabase exitosamente");
    return true;
  } catch (e) {
    console.error("❌ Error en eliminarNominaDeSupabase:", e);
    return false;
  }
}

// Helper global para reconstruir rango de quincena desde una fecha
function getRangoQuincenaDesde(fechaISO) {
  if (!fechaISO) return { inicio: "", fin: "" };

  let date;
  if (typeof fechaISO === "string") {
    const soloFecha = fechaISO.slice(0, 10);
    date = parseISO(soloFecha);
  } else {
    date = new Date(fechaISO);
  }

  const yyyy = date.getFullYear();
  const mm = date.getMonth() + 1;
  const dd = date.getDate();
  const half = dd <= 15 ? 1 : 2;
  
  // Calcular los días de la quincena
  const lastDay = new Date(yyyy, mm, 0).getDate();
  const start = half === 1 ? 1 : 16;
  const end = half === 1 ? 15 : lastDay;
  
  const inicioISO = toISO(new Date(yyyy, mm - 1, start));
  const finISO = toISO(new Date(yyyy, mm - 1, end));
  
  return {
    inicio: inicioISO,
    fin: finISO
  };
}

// Cargar nóminas desde Supabase
async function cargarNominasDeSupabase(empresaId = null) {
  try {
    let query = supabase
      .from("nominas")
      .select("*")
      .order("fechageneracion", { ascending: false });
    
    // Si se especifica empresaId, filtrar por empresa
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("❌ Error cargando nóminas:", error);
      return [];
    }

    console.log("📥 Datos crudos de nominas:", JSON.stringify(data, null, 2));

    if (!data || data.length === 0) {
      console.log("⚠️ No hay nóminas en la base de datos");
      return [];
    }

    // Cargar empleados desde Supabase para hacer match
    const { data: empleadosDB, error: errorEmpleados } = await supabase
      .from("empleados")
      .select("id, nombres, apellidos, documento");
    
    const mapEmpleados = {};
    const mapPorDocumento = {}; // Map alternativo por documento
    
    if (empleadosDB && !errorEmpleados) {
      empleadosDB.forEach(emp => {
        const nombreCompleto = `${emp.nombres || ""} ${emp.apellidos || ""}`.trim() || "Sin nombre";
        mapEmpleados[emp.id] = {
          nombre: nombreCompleto,
          documento: emp.documento || "",
        };
        // También indexar por documento para búsqueda alternativa
        if (emp.documento) {
          mapPorDocumento[emp.documento] = {
            id: emp.id,
            nombre: nombreCompleto,
            documento: emp.documento,
          };
        }
      });
    }

    console.log("📊 Mapa de empleados cargado:", mapEmpleados);
    console.log("📊 Mapa por documento:", mapPorDocumento);

    // Agrupar registros por ID de nómina (porque se guardan uno por empleado)
    const nominasMap = new Map();

    data.forEach((registro) => {
      // El ID tiene formato: nominaId_empleadoId
      const partes = registro.id.split("_");
      const nominaId = partes.slice(0, -1).join("_") || registro.id;
      
      console.log(`📝 Procesando registro: id=${registro.id}, nominaId=${nominaId}, empleadoid=${registro.empleadoid}, periodo="${registro.periodo}"`);
      
      if (!nominasMap.has(nominaId)) {
        const fechaGen = registro.fechageneracion ? new Date(registro.fechageneracion) : new Date();
        const mes = String(fechaGen.getMonth() + 1).padStart(2, "0");
        const año = fechaGen.getFullYear();
        
        // Reconstruir el rango desde la fecha de período
        // Si periodo no está disponible, usar fechageneracion como fallback
        const periodoFecha = registro.periodo || (registro.fechageneracion ? registro.fechageneracion.slice(0, 10) : null);
        const rangoReconstruido = periodoFecha ? getRangoQuincenaDesde(periodoFecha) : { inicio: "", fin: "" };
        
        console.log(`📋 Reconstruyendo nómina ${nominaId}: periodo="${registro.periodo}", periodoFecha="${periodoFecha}", rango="${JSON.stringify(rangoReconstruido)}"`);
        
        nominasMap.set(nominaId, {
          id: nominaId,
          nombre: `Nómina ${mes}/${año}`,
          fechaCreacion: registro.fechageneracion || new Date().toISOString(),
          periodo: registro.periodo || `${año}-${mes}`,
          rango: rangoReconstruido.inicio && rangoReconstruido.fin
            ? rangoReconstruido
            : { inicio: "", fin: "" },
          items: [],
          estado: registro.estado || "Procesada",
          metodoLiquidacion: "con_recargos",
          createdAt: registro.created_at,
          updatedAt: registro.updated_at,
        });
      }

      const nomina = nominasMap.get(nominaId);
      
      // Obtener datos del empleado
      // 1. Intentar por ID directo
      let empData = mapEmpleados[registro.empleadoid];
      
      // 2. Si no lo encuentra por ID, intentar usar como documento
      if (!empData && mapPorDocumento[registro.empleadoid]) {
        empData = mapPorDocumento[registro.empleadoid];
      }
      
      // 3. Si sigue sin encontrar, usar el ID como fallback
      if (!empData) {
        empData = { 
          nombre: `Empleado (ID: ${registro.empleadoid})`, 
          documento: "" 
        };
      }
      
      console.log(`✅ Datos encontrados para ${registro.empleadoid}:`, empData);
      
      // Parsear horas totales desde JSON
      const horasTotalesData = typeof registro.horas_totales === "string"
        ? JSON.parse(registro.horas_totales)
        : registro.horas_totales;
      
      // Parsear detalle de horas desde JSON
      const detalleHorasData = typeof registro.detalle_horas_completo === "string"
        ? JSON.parse(registro.detalle_horas_completo)
        : registro.detalle_horas_completo;
      
      // Extraer método de liquidación desde los campos JSON
      const metodoGuardado = detalleHorasData?.metodoLiquidacion || horasTotalesData?.metodoLiquidacion || "con_recargos";
      
      // Parsear incapacidad desde JSON
      const incapacidadData = typeof registro.incapacidad_data === "string"
        ? JSON.parse(registro.incapacidad_data)
        : registro.incapacidad_data;
      
      // Parsear ajustes desde JSON
      const ajustesData = typeof registro.ajustes_nomina === "string"
        ? JSON.parse(registro.ajustes_nomina)
        : registro.ajustes_nomina;
      
      // Agregar empleado al array items
      nomina.items.push({
        empleadoId: registro.empleadoid,
        empleadoNombre: empData.nombre,
        documento: empData.documento,
        bruto: Number(registro.salariobase) || 0,
        horasTotales: horasTotalesData || {
          hNorm: 0,
          hNoct: 0,
          hFest: 0,
          hExDia: 0,
          hExNoc: 0,
          hExFest: 0,
        },
        detalleHoras: detalleHorasData || { 
                hNorm: 0,
                hNoct: 0,
                hFest: 0,
                hExDia: Number(registro.horasextras) || 0,
                hExNoc: 0,
                hExFest: 0,
              },
        detallePorFecha:
          typeof registro.detalle_por_fecha === "string"
            ? JSON.parse(registro.detalle_por_fecha)
            : registro.detalle_por_fecha || [],
        incapTotalQuincena: false,
        incapacidad: incapacidadData || { dias: 0, valorDiario: 0, valorTotal: 0 },
        auxTransp: Number(registro.auxtransporte) || 0,
        adicionales: { bonificacion: Number(registro.bonificacion) || 0 },
        descuentos: {
          salud: Number(registro.descuentosalud) || 0,
          pension: Number(registro.descuentopension) || 0,
          arl: Number(registro.descuentoarl) || 0,
          prestamos: Number(registro.descuentoprestamos) || 0,
          comida: Number(registro.descuentocomida) || 0,
          otrosDescuentos: Number(registro.otros_descuentos) || 0,
          detalleOtros: typeof registro.detalle_otros_descuentos === "string"
            ? JSON.parse(registro.detalle_otros_descuentos)
            : registro.detalle_otros_descuentos || [],
        },
        ajustes: ajustesData || { sumaEmpleado: 0, restaEmpresa: 0, netoAjuste: 0, detalle: [], cantidad: 0 },
        neto: Number(registro.totalneto) || 0,
        totalDescuentos: Number(registro.totaldescuentos) || 0,
        totalDevengado: Number(registro.totaldevengado) || 0,
        detallesPrestamos:
          typeof registro.detalle_prestamos === "string"
            ? JSON.parse(registro.detalle_prestamos)
            : registro.detalle_prestamos || [],
        cuotasProcesadas:
          typeof registro.cuotas_procesadas === "string"
            ? JSON.parse(registro.cuotas_procesadas)
            : registro.cuotas_procesadas || [],
        metodoLiquidacion: metodoGuardado,
      });
    });

    const nominasProcessadas = Array.from(nominasMap.values());
    
    // Actualizar el método de liquidación de cada nómina basado en sus items
    nominasProcessadas.forEach(nomina => {
      if (nomina.items.length > 0) {
        // Usar el método del primer item (todos deberían tener el mismo)
        nomina.metodoLiquidacion = nomina.items[0].metodoLiquidacion || "con_recargos";
      }
    });

    console.log(`✅ Nóminas cargadas desde Supabase (total: ${nominasProcessadas.length}):`);
    nominasProcessadas.forEach((n) => {
      console.log(`  - Nómina "${n.nombre}" (${n.id}): ${n.items.length} empleados, método: ${n.metodoLiquidacion}, rango: ${n.rango.inicio} a ${n.rango.fin}`);
    });
    console.log("Datos completos:", JSON.stringify(nominasProcessadas, null, 2));
    return nominasProcessadas;
  } catch (e) {
    console.error("❌ Error en cargarNominasDeSupabase:", e);
    return [];
  }
}

function safelyParseJSON(value, fallback = null) {
  try {
    if (typeof value !== "string") return fallback;
    return JSON.parse(value);
  } catch (e) {
    return fallback;
  }
}

async function guardarCertificacionEnSupabase(cert, empresaId = null) {
  try {
    const nowISO = new Date().toISOString();
    const payload = {
      id: cert.id || uid(),
      empleadoid: cert.empleadoId || null,
      empleadonombre: cert.empleadoNombre || null,
      sedeid: cert.sedeId || null,
      sedename: cert.sedeName || null,
      tiposalario: cert.tipoSalario || null,
      datos: cert.datos || null,
      fechageneracion: cert.fechaGeneracion || toISO(new Date()),
      created_at: cert.createdAt || nowISO,
      updated_at: nowISO,
    };
    
    // Agregar empresa_id si está disponible
    if (empresaId) {
      payload.empresa_id = empresaId;
    }

    const { data, error } = await supabase
      .from("certificaciones")
      .upsert(payload, { onConflict: "id" })
      .select()
      .maybeSingle();

    if (error) {
      console.error("❌ Error guardando certificación en Supabase:", error);
      return false;
    }

    return {
      id: payload.id,
      empleadoId: payload.empleadoid,
      empleadoNombre: payload.empleadonombre,
      sedeId: payload.sedeid,
      sedeName: payload.sedename,
      tipoSalario: payload.tiposalario,
      datos: cert.datos || {},
      fechaGeneracion: payload.fechageneracion,
      createdAt: data?.created_at || payload.created_at,
      updatedAt: data?.updated_at || payload.updated_at,
    };
  } catch (e) {
    console.error("❌ Error en guardarCertificacionEnSupabase:", e);
    return false;
  }
}

async function cargarCertificacionesDeSupabase(empresaId = null) {
  try {
    let query = supabase
      .from("certificaciones")
      .select("*")
      .order("created_at", { ascending: false });
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;

    if (error) {
      console.error("Error cargando certificaciones:", error);
      return [];
    }

    return (data || []).map((row) => ({
      id: row.id,
      empleadoId: row.empleadoid || null,
      empleadoNombre: row.empleadonombre || "",
      sedeId: row.sedeid || null,
      sedeName: row.sedename || "",
      tipoSalario: row.tiposalario || null,
      datos: typeof row.datos === "string" ? safelyParseJSON(row.datos, {}) : row.datos || {},
      fechaGeneracion: row.fechageneracion || null,
      createdAt: row.created_at || null,
      updatedAt: row.updated_at || null,
    }));
  } catch (e) {
    console.error("Error en cargarCertificacionesDeSupabase:", e);
    return [];
  }
}

async function eliminarCertificacionEnSupabase(id) {
  try {
    if (!id) return false;
    const { error } = await supabase
      .from("certificaciones")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("❌ Error eliminando certificación en Supabase:", error);
      return false;
    }

    return true;
  } catch (e) {
    console.error("❌ Error en eliminarCertificacionEnSupabase:", e);
    return false;
  }
}

// ====== FUNCIONES CONTRATOS EN SUPABASE ======
async function guardarContratoEnSupabase(contrato, empresaId = null) {
  try {
    const nowISO = new Date().toISOString();
    
    // Payload completo con campos de firma
    const payloadCompleto = {
      id: contrato.id || uid(),
      empleadoid: contrato.empleadoId || null,
      empleadonombre: contrato.empleadoNombre || null,
      sedeid: contrato.sedeId || null,
      sedename: contrato.sedeName || null,
      tipocontrato: contrato.tipoContrato || "Indefinido",
      datos: contrato.datos || null,
      fechageneracion: contrato.fechaGeneracion || toISO(new Date()),
      firmado: contrato.firmado || false,
      firma_trabajador: contrato.firmaTrabajador || null,
      firma_empleador: contrato.firmaEmpleador || null,
      fecha_firma: contrato.fechaFirma || null,
      tipo_firma_empleador: contrato.tipoFirmaEmpleador || null,
      created_at: contrato.createdAt || nowISO,
      updated_at: nowISO,
    };
    
    // Payload básico sin campos de firma (para compatibilidad)
    const payloadBasico = {
      id: payloadCompleto.id,
      empleadoid: payloadCompleto.empleadoid,
      empleadonombre: payloadCompleto.empleadonombre,
      sedeid: payloadCompleto.sedeid,
      sedename: payloadCompleto.sedename,
      tipocontrato: payloadCompleto.tipocontrato,
      datos: payloadCompleto.datos,
      fechageneracion: payloadCompleto.fechageneracion,
      created_at: payloadCompleto.created_at,
      updated_at: payloadCompleto.updated_at,
    };
    
    // Agregar empresa_id si está disponible
    if (empresaId) {
      payloadCompleto.empresa_id = empresaId;
      payloadBasico.empresa_id = empresaId;
    }

    console.log("📦 Intentando guardar contrato con campos de firma...");
    
    const { data, error } = await supabase
      .from("contratos")
      .upsert(payloadCompleto, { onConflict: "id" })
      .select()
      .maybeSingle();

    if (error) {
      console.warn("⚠️ Error con campos de firma, intentando sin ellos:", error.message);
      
      // Intentar con payload básico
      const { data: data2, error: error2 } = await supabase
        .from("contratos")
        .upsert(payloadBasico, { onConflict: "id" })
        .select()
        .maybeSingle();
        
      if (error2) {
        console.error("❌ Error guardando contrato en Supabase:", error2);
        return false;
      }
      
      console.log("✅ Contrato guardado (sin campos de firma). Ejecuta el SQL para agregar las columnas de firma.");
      alert("⚠️ Contrato guardado pero SIN las firmas.\n\nPara guardar contratos firmados, ejecuta el SQL en Supabase para agregar las columnas:\n- firmado\n- firma_trabajador\n- firma_empleador\n- fecha_firma\n- tipo_firma_empleador");
      
      return {
        id: payloadBasico.id,
        empleadoId: payloadBasico.empleadoid,
        empleadoNombre: payloadBasico.empleadonombre,
        sedeId: payloadBasico.sedeid,
        sedeName: payloadBasico.sedename,
        tipoContrato: payloadBasico.tipocontrato,
        datos: contrato.datos || {},
        fechaGeneracion: payloadBasico.fechageneracion,
        firmado: false,
        createdAt: data2?.created_at || payloadBasico.created_at,
        updatedAt: data2?.updated_at || payloadBasico.updated_at,
      };
    }

    console.log("✅ Contrato guardado correctamente con firmas");
    
    return {
      id: payloadCompleto.id,
      empleadoId: payloadCompleto.empleadoid,
      empleadoNombre: payloadCompleto.empleadonombre,
      sedeId: payloadCompleto.sedeid,
      sedeName: payloadCompleto.sedename,
      tipoContrato: payloadCompleto.tipocontrato,
      datos: contrato.datos || {},
      fechaGeneracion: payloadCompleto.fechageneracion,
      firmado: payloadCompleto.firmado,
      firmaTrabajador: payloadCompleto.firma_trabajador,
      firmaEmpleador: payloadCompleto.firma_empleador,
      fechaFirma: payloadCompleto.fecha_firma,
      tipoFirmaEmpleador: payloadCompleto.tipo_firma_empleador,
      createdAt: data?.created_at || payloadCompleto.created_at,
      updatedAt: data?.updated_at || payloadCompleto.updated_at,
    };
  } catch (e) {
    console.error("❌ Error en guardarContratoEnSupabase:", e);
    return false;
  }
}

async function cargarContratosDeSupabase(empresaId = null) {
  try {
    let query = supabase
      .from("contratos")
      .select("*")
      .order("created_at", { ascending: false });
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;

    if (error) {
      console.error("Error cargando contratos:", error);
      return [];
    }

    return (data || []).map((row) => ({
      id: row.id,
      empleadoId: row.empleadoid || null,
      empleadoNombre: row.empleadonombre || "",
      sedeId: row.sedeid || null,
      sedeName: row.sedename || "",
      tipoContrato: row.tipocontrato || "Indefinido",
      datos: typeof row.datos === "string" ? safelyParseJSON(row.datos, {}) : row.datos || {},
      fechaGeneracion: row.fechageneracion || null,
      firmado: row.firmado || false,
      firmaTrabajador: row.firma_trabajador || null,
      firmaEmpleador: row.firma_empleador || null,
      fechaFirma: row.fecha_firma || null,
      tipoFirmaEmpleador: row.tipo_firma_empleador || null,
      createdAt: row.created_at || null,
      updatedAt: row.updated_at || null,
    }));
  } catch (e) {
    console.error("Error en cargarContratosDeSupabase:", e);
    return [];
  }
}

async function eliminarContratoEnSupabase(id) {
  try {
    if (!id) return false;
    const { error } = await supabase
      .from("contratos")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("❌ Error eliminando contrato en Supabase:", error);
      return false;
    }

    return true;
  } catch (e) {
    console.error("❌ Error en eliminarContratoEnSupabase:", e);
    return false;
  }
}

function prepararVentasCuadreParaSupabase(cuadre) {
  const ventas = cuadre?.ventas || {};
  // Calcular totales de transferencias
  const transfer = Array.isArray(cuadre?.transfer) ? cuadre.transfer : [];
  let totalBancolombia = 0, totalDavivienda = 0, totalDaviplata = 0, totalNequi = 0;
  transfer.forEach((t) => {
    totalBancolombia += Number(t.bancolombia || 0);
    totalDavivienda += Number(t.davivienda || 0);
    totalDaviplata += Number(t.daviplata || 0);
    totalNequi += Number(t.nequi || 0);
  });
  // Calcular total datafonos
  const datafonos = Array.isArray(cuadre?.datafonos) ? cuadre.datafonos : [];
  const totalDatafonos = datafonos.reduce((sum, d) => sum + Number(d.valor || 0), 0);

  // Online Rappi
  const onlineRappi = Number(ventas.onlineRappi || 0);

  // 🔹 NUEVOS CÁLCULOS PARA GUARDAR - TOTALES CONSOLIDADOS
  const ventaEfectivo = Number(ventas.ventaEfectivo || 0);
  const totalTransferencias = totalBancolombia + totalDavivienda + totalDaviplata + totalNequi;
  const totalVentas = ventaEfectivo + totalTransferencias + totalDatafonos + onlineRappi;
  
  // Valor inicial y facturas pendientes para calcular "debería quedar"
  const valorInicial = Number(cuadre?.valorInicial ?? 0) || 0;
  const facturas = Array.isArray(cuadre?.facturas) ? cuadre.facturas : [];
  const totalFacturasPendientes = facturas.filter(f => f.estado === "Pendiente").reduce((s, f) => s + Number(f.valor || 0), 0);
  const deberíaQuedarEnCaja = valorInicial - totalFacturasPendientes;
  
  // Efectivo en caja y diferencia
  const efectivoEnCaja = Number(ventas.efectivoEnCaja || 0);
  const diferenciaCaja = efectivoEnCaja - deberíaQuedarEnCaja;
  
  // Estado del cuadre (cuadrado, sobrante o faltante)
  let estadoCuadre = "Cuadrado";
  if (Math.abs(diferenciaCaja) >= 1) {
    estadoCuadre = diferenciaCaja > 0 ? "Sobrante" : "Faltante";
  }

  return {
    valorInicial: valorInicial,
    caja: {
      ventaEfectivo: ventas.ventaEfectivo || "",
      sobrante: ventas.sobrante || "",
      huboDescuadre: ventas.huboDescuadre === "Sí" ? "Sí" : "No",
      valorDescuadre: ventas.valorDescuadre || "",
      onlineRappi: ventas.onlineRappi || "",
      // 🔹 Campos de descuadre en transferencia
      huboDescuadreTransferencia: ventas.huboDescuadreTransferencia || "",
      valorDescuadreTransferencia: ventas.valorDescuadreTransferencia || "",
      numeroPedidoDescuadreTransferencia: ventas.numeroPedidoDescuadreTransferencia || "",
      bancoDescuadreTransferencia: ventas.bancoDescuadreTransferencia || "",
      // 🔹 Campos de descuadre en datáfono
      huboDescuadreDatafono: ventas.huboDescuadreDatafono || "",
      valorDescuadreDatafono: ventas.valorDescuadreDatafono || "",
      // 🔹 CRITICAL: Campos de efectivo confirmado (antes estaban faltando)
      efectivoEnCaja: ventas.efectivoEnCaja || "",
      efectivoEnCajaConfirmado: ventas.efectivoEnCajaConfirmado || false,
      fechaConfirmacionEfectivo: ventas.fechaConfirmacionEfectivo || null,
      // 🔹 Campos de totales FUDO
      ventaEfectivoFudo: ventas.ventaEfectivoFudo || null,
      onlineRappiFudo: ventas.onlineRappiFudo || null,
      datafonoFudo: ventas.datafonoFudo || null,
      totalesFudoFecha: ventas.totalesFudoFecha || null,
      totalesFudoPedidos: ventas.totalesFudoPedidos || null,
      // 🔹 Campo de efectivo final confirmado (segunda jornada)
      efectivoFinalConfirmado: ventas.efectivoFinalConfirmado || false,
      // 🔹 NUEVOS CAMPOS: TOTALES CONSOLIDADOS PARA CUADRES CERRADOS
      totalVentas: totalVentas,
      totalTransferencias: totalTransferencias,
      totalDatafonos: totalDatafonos,
      deberíaQuedarEnCaja: deberíaQuedarEnCaja,
      diferenciaCaja: diferenciaCaja,
      estadoCuadre: estadoCuadre,
      montoSobranteFaltante: Math.abs(diferenciaCaja),
    },
    ventasPorMedioPago: {
      efectivo: Number(ventas.ventaEfectivo || 0),
      bancolombia: totalBancolombia,
      davivienda: totalDavivienda,
      daviplata: totalDaviplata,
      nequi: totalNequi,
      datafono: totalDatafonos,
      onlineRappi: onlineRappi,
      totalTransferencias: totalBancolombia + totalDavivienda + totalDaviplata + totalNequi,
      totalGeneral: Number(ventas.ventaEfectivo || 0) + totalBancolombia + totalDavivienda + totalDaviplata + totalNequi + totalDatafonos + onlineRappi,
    },
    transfer: transfer,
    denoms: Array.isArray(cuadre?.denoms) ? cuadre.denoms : [],
    denominacionesActivas: !!cuadre?.denominacionesActivas,
    turnosActivos: !!cuadre?.turnosActivos,
    adelantosActivos: !!cuadre?.adelantosActivos,
    // 🔹 Ingresos Varios
    ingresosVarios: Array.isArray(cuadre?.ingresosVarios) ? cuadre.ingresosVarios : [],
    ingresosVariosActivos: !!cuadre?.ingresosVariosActivos,
  };
}

function prepararDomiciliosCuadreParaSupabase(cuadre) {
  return {
    domicilios: Array.isArray(cuadre?.domicilios) ? cuadre.domicilios : [],
    turnosActivos: !!cuadre?.turnosActivos,
    turnos: Array.isArray(cuadre?.turnos) ? cuadre.turnos : [],
    adelantosActivos: !!cuadre?.adelantosActivos,
    adelantos: Array.isArray(cuadre?.adelantos) ? cuadre.adelantos : [],
  };
}

function prepararDescuentosCuadreParaSupabase(cuadre) {
  return {
    novedadesActivas: !!cuadre?.novedadesActivas,
    novedades: Array.isArray(cuadre?.novedades) ? cuadre.novedades : [],
    comentarioModificacion: cuadre?.comentarioModificacion || "",
    totales: cuadre?.totales || {},
    // Facturas eliminadas (para sincronización colaborativa)
    facturasEliminadas: Array.isArray(cuadre?.facturasEliminadas) ? cuadre.facturasEliminadas : [],
  };
}

function mapearRowCuadreDesdeSupabase(row) {
  const ventasRaw = typeof row?.ventas === "string" ? safelyParseJSON(row.ventas, {}) : row?.ventas || {};
  const facturasRaw = typeof row?.facturas === "string" ? safelyParseJSON(row.facturas, []) : row?.facturas || [];
  const domiciliosRaw = typeof row?.domicilios === "string" ? safelyParseJSON(row.domicilios, {}) : row?.domicilios || {};
  const descuentosRaw = typeof row?.descuentos === "string" ? safelyParseJSON(row.descuentos, {}) : row?.descuentos || {};

  const caja = ventasRaw?.caja || ventasRaw?.ventas || {};
  const estadoNormalizado = (row?.estado || "").toLowerCase();
  const comentario =
    descuentosRaw?.comentarioModificacion ??
    descuentosRaw?.comentarios ??
    row?.observaciones ??
    "";

  const denomsDesdeBd = Array.isArray(ventasRaw?.denoms)
    ? ventasRaw.denoms
    : Array.isArray(ventasRaw?.denominaciones)
    ? ventasRaw.denominaciones
    : [];

  const denomsNormalizados =
    denomsDesdeBd && denomsDesdeBd.length
      ? denomsDesdeBd
      : DENOMINACIONES.map((valor) => ({ valor, cantidad: 0, subtotal: 0 }));

  return {
    id: row?.id,
    sede: row?.sede || "",
    fecha: row?.fecha || "",
    empleadoId: row?.responsable || "",
    estado: estadoNormalizado === "borrador" ? "borrador" : "final",
    valorInicial: Number(ventasRaw?.valorInicial ?? 0) || 0,
    ventas: {
      ventaEfectivo: caja?.ventaEfectivo || "",
      sobrante: caja?.sobrante || "",
      huboDescuadre: caja?.huboDescuadre === "Sí" ? "Sí" : "No",
      valorDescuadre: caja?.valorDescuadre || "",
      onlineRappi: caja?.onlineRappi || "",
      // 🔹 Campos de descuadre de transferencia
      huboDescuadreTransferencia: caja?.huboDescuadreTransferencia || "",
      valorDescuadreTransferencia: caja?.valorDescuadreTransferencia || "",
      numeroPedidoDescuadreTransferencia: caja?.numeroPedidoDescuadreTransferencia || "",
      bancoDescuadreTransferencia: caja?.bancoDescuadreTransferencia || "",
      // 🔹 Campos de descuadre de datáfono
      huboDescuadreDatafono: caja?.huboDescuadreDatafono || "",
      valorDescuadreDatafono: caja?.valorDescuadreDatafono || "",
      // 🔹 CRITICAL: Campos de efectivo confirmado (mapeados desde Supabase)
      efectivoEnCaja: caja?.efectivoEnCaja || "",
      efectivoEnCajaConfirmado: caja?.efectivoEnCajaConfirmado || false,
      fechaConfirmacionEfectivo: caja?.fechaConfirmacionEfectivo || null,
      // 🔹 Campos de totales FUDO (mapeados desde Supabase)
      ventaEfectivoFudo: caja?.ventaEfectivoFudo || null,
      onlineRappiFudo: caja?.onlineRappiFudo || null,
      datafonoFudo: caja?.datafonoFudo || null,
      totalesFudoFecha: caja?.totalesFudoFecha || null,
      totalesFudoPedidos: caja?.totalesFudoPedidos || null,
      // 🔹 Campo de efectivo final confirmado (segunda jornada)
      efectivoFinalConfirmado: caja?.efectivoFinalConfirmado || row?.efectivo_final_confirmado || false,
      // 🔹 NUEVOS CAMPOS: TOTALES CONSOLIDADOS (desde Supabase)
      totalVentas: caja?.totalVentas || 0,
      totalTransferencias: caja?.totalTransferencias || 0,
      totalDatafonos: caja?.totalDatafonos || 0,
      deberíaQuedarEnCaja: caja?.deberíaQuedarEnCaja || 0,
      diferenciaCaja: caja?.diferenciaCaja || 0,
      estadoCuadre: caja?.estadoCuadre || "Desconocido",
      montoSobranteFaltante: caja?.montoSobranteFaltante || 0,
    },
    transfer: Array.isArray(ventasRaw?.transfer)
      ? ventasRaw.transfer
      : Array.isArray(ventasRaw?.transferencias)
      ? ventasRaw.transferencias
      : [],
    denoms: denomsNormalizados,
    denominacionesActivas: !!(ventasRaw?.denominacionesActivas),
    facturas: Array.isArray(facturasRaw) ? facturasRaw : [],
    domicilios: Array.isArray(domiciliosRaw?.domicilios)
      ? domiciliosRaw.domicilios
      : Array.isArray(domiciliosRaw)
      ? domiciliosRaw
      : [],
    turnosActivos: !!(domiciliosRaw?.turnosActivos ?? ventasRaw?.turnosActivos),
    turnos: Array.isArray(domiciliosRaw?.turnos)
      ? domiciliosRaw.turnos
      : Array.isArray(ventasRaw?.turnos)
      ? ventasRaw.turnos
      : [],
    adelantosActivos: !!(domiciliosRaw?.adelantosActivos ?? ventasRaw?.adelantosActivos),
    adelantos: Array.isArray(domiciliosRaw?.adelantos)
      ? domiciliosRaw.adelantos
      : Array.isArray(ventasRaw?.adelantos)
      ? ventasRaw.adelantos
      : [],
    novedadesActivas: !!descuentosRaw?.novedadesActivas,
    novedades: Array.isArray(descuentosRaw?.novedades) ? descuentosRaw.novedades : [],
    // Facturas eliminadas (para sincronización colaborativa - evita que facturas borradas reaparezcan)
    facturasEliminadas: Array.isArray(descuentosRaw?.facturasEliminadas) ? descuentosRaw.facturasEliminadas : [],
    // 🔹 Bases de domicilios
    basesDomiciliosActivas: !!(typeof row?.bases_domicilios === "string" 
      ? safelyParseJSON(row.bases_domicilios, []).length > 0 
      : (row?.bases_domicilios || []).length > 0),
    basesDomicilios: typeof row?.bases_domicilios === "string" 
      ? safelyParseJSON(row.bases_domicilios, []) 
      : Array.isArray(row?.bases_domicilios) ? row.bases_domicilios : [],
    // 🔹 Ingresos Varios (venta de materia prima, etc.)
    ingresosVariosActivos: !!(ventasRaw?.ingresosVariosActivos || (Array.isArray(ventasRaw?.ingresosVarios) && ventasRaw.ingresosVarios.length > 0)),
    ingresosVarios: Array.isArray(ventasRaw?.ingresosVarios) ? ventasRaw.ingresosVarios : [],
    // 🔹 Campo de efectivo final confirmado (leído de la columna de Supabase)
    efectivoFinalConfirmado: row?.efectivo_final_confirmado || false,
    ingresosVarios: Array.isArray(ventasRaw?.ingresosVarios) ? ventasRaw.ingresosVarios : [],
    datafonos: Array.isArray(row?.datafonos) ? row.datafonos : [],
    comentarioModificacion: comentario,
    totales: descuentosRaw?.totales || ventasRaw?.totales || {},
    cerradoPorId: row?.cerrado_por_id || null,
    cerradoPorNombre: row?.cerrado_por_nombre || null,
    fechaCierre: row?.fecha_cierre || null,
    // Campos de precierre
    precierreRealizado: row?.precierre_realizado || false,
    precierrePendienteConfirmacion: row?.precierre_pendiente_confirmacion || false,
    precierre: row?.precierre_realizado ? {
      fecha: row?.precierre_fecha || null,
      porId: row?.precierre_por_id || null,
      porNombre: row?.precierre_por_nombre || null,
      asignadoAId: row?.precierre_asignado_a_id || null,
      asignadoANombre: row?.precierre_asignado_a_nombre || null,
      observaciones: row?.precierre_observaciones || "",
      totales: typeof row?.precierre_totales === "string" ? safelyParseJSON(row.precierre_totales, {}) : row?.precierre_totales || {},
      confirmadoPorReceptor: row?.precierre_confirmado_por_receptor || false,
      fechaConfirmacion: row?.precierre_fecha_confirmacion || null,
      confirmadoPorNombre: row?.precierre_confirmado_por_nombre || null,
    } : null,
    // 🔹 Campos de colaborador
    colaboradorId: row?.colaborador_id || null,
    colaboradorNombre: row?.colaborador_nombre || null,
    createdAt: row?.created_at || null,
    updatedAt: row?.updated_at || null,
  };
}

// Función para eliminar un cuadre diario de Supabase Y marcarlo como eliminado en localStorage
// 🔐 SEGURIDAD: Solo permite eliminar cuadres de la empresa especificada
async function eliminarCuadreDiarioDeSupabase(cuadreId, empresaId = null) {
  try {
    if (!cuadreId) {
      console.error("❌ eliminarCuadreDiarioDeSupabase requiere un ID válido");
      return false;
    }

    console.log("🗑️ Eliminando cuadre de Supabase:", cuadreId);
    
    // PRIMERO: Marcar como eliminado en localStorage para evitar re-sincronización
    marcarCuadreComoEliminado(cuadreId);
    
    // SEGUNDO: Eliminar de Supabase (solo si pertenece a la empresa)
    let query = supabase
      .from("cuadres_diarios")
      .delete()
      .eq("id", cuadreId);
    
    // 🔐 Si hay empresaId, agregar filtro para solo eliminar cuadres de esa empresa
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { error } = await query;

    if (error) {
      console.error("❌ Error eliminando cuadre diario de Supabase:", error);
      // Reintentar una vez más
      let queryRetry = supabase
        .from("cuadres_diarios")
        .delete()
        .eq("id", cuadreId);
      if (empresaId) {
        queryRetry = queryRetry.eq("empresa_id", empresaId);
      }
      const { error: error2 } = await queryRetry;
      if (error2) {
        console.error("❌ Error eliminando cuadre (reintento):", error2);
        return false;
      }
    }

    console.log("✅ Cuadre eliminado de Supabase correctamente:", cuadreId);
    return true;
  } catch (e) {
    console.error("❌ Error en eliminarCuadreDiarioDeSupabase:", e);
    return false;
  }
}

async function guardarCuadreDiarioEnSupabase(cuadre, empresaId = null) {
  try {
    if (!cuadre?.id) {
      console.error("❌ guardarCuadreDiarioEnSupabase requiere un ID válido");
      return null;
    }
    
    // 🔐 SEGURIDAD: Si no hay empresaId, NO guardar el cuadre
    // Esto evita crear cuadres sin empresa que podrían verse por todos
    if (!empresaId) {
      console.error("❌ guardarCuadreDiarioEnSupabase: NO se proporcionó empresaId - cuadre NO guardado por seguridad");
      return null;
    }

    // 🔹 PREPARAR VENTAS Y EXTRAER VALORES CALCULADOS
    const ventasPreparada = prepararVentasCuadreParaSupabase(cuadre);
    
    // 🔹 EXTRAER VALORES CALCULADOS PARA GUARDAR COMO COLUMNAS SEPARADAS
    const efectivoEnCaja = Number(ventasPreparada?.caja?.efectivoEnCaja) || 0;
    const deberíaQuedarEnCaja = Number(ventasPreparada?.caja?.deberíaQuedarEnCaja) || 0;
    const totalVentas = Number(ventasPreparada?.caja?.totalVentas) || 0;
    const totalTransferencias = Number(ventasPreparada?.caja?.totalTransferencias) || 0;
    const totalDatafonos = Number(ventasPreparada?.caja?.totalDatafonos) || 0;
    const sobranteNeto = totalVentas - totalTransferencias - totalDatafonos;
    const diferenciaCaja = Number(ventasPreparada?.caja?.diferenciaCaja) || 0;
    const estadoCuadre = ventasPreparada?.caja?.estadoCuadre || "Cuadrado";
    const montoDiferencia = Number(ventasPreparada?.caja?.montoSobranteFaltante) || 0;
    
    // 🔹 CALCULAR VALOR TOTAL DEL SOBRANTE Y DIFERENCIA NETA (para guardar en BD)
    const diferencia = efectivoEnCaja - deberíaQuedarEnCaja - sobranteNeto;
    const valorTotalSobrante = sobranteNeto + diferencia;
    const diferenciaNeta = valorTotalSobrante - sobranteNeto; // Igual a "diferencia"
    
    // 🔹 EXTRAER INGRESOS VARIOS Y CALCULAR TOTALES
    const ingresosVariosArray = Array.isArray(cuadre?.ingresosVarios) ? cuadre.ingresosVarios : [];
    const totalIngresosVarios = ingresosVariosArray.reduce((sum, ing) => sum + (Number(ing?.monto) || 0), 0);
    const cantidadIngresosVarios = ingresosVariosArray.length;
    
    // 🔹 EXTRAER NOVEDADES Y CONTAR
    const novedadesArray = Array.isArray(cuadre?.novedades) ? cuadre.novedades : [];
    const cantidadNovedades = novedadesArray.length;
    
    // 🔹 EXTRAER COMENTARIOS DE NOVEDADES
    const comentariosNovedades = novedadesArray
      .filter(nov => nov?.comentarios?.trim())
      .map(nov => `${nov.novedad || 'Sin novedad'}: ${nov.comentarios}`)
      .join(' | ');
    const textoNovedadesCompleto = novedadesArray
      .map(nov => `[${nov.novedad || 'N/A'}] Reportado a: ${nov.aQuien || 'N/A'} - ${nov.comentarios || 'Sin comentarios'}`)
      .join(' | ');

    const payload = {
      id: cuadre.id,
      sede: cuadre?.sede || null,
      fecha: cuadre?.fecha || null,
      responsable: cuadre?.empleadoId || null,
      estado: (cuadre?.estado || "final").toLowerCase() === "borrador" ? "Borrador" : "Final",
      fue_reabierto: cuadre?.fueReabierto || false,
      ventas: ventasPreparada,
      facturas: Array.isArray(cuadre?.facturas) ? cuadre.facturas : [],
      domicilios: prepararDomiciliosCuadreParaSupabase(cuadre),
      descuentos: prepararDescuentosCuadreParaSupabase(cuadre),
      datafonos: Array.isArray(cuadre?.datafonos) ? cuadre.datafonos : [],
      observaciones: cuadre?.comentarioModificacion || "",
      cerrado_por_id: cuadre?.cerradoPorId || null,
      cerrado_por_nombre: cuadre?.cerradoPorNombre || null,
      fecha_cierre: cuadre?.fechaCierre || null,
      // Campos de precierre
      precierre_realizado: cuadre?.precierreRealizado || false,
      precierre_fecha: cuadre?.precierre?.fecha || null,
      precierre_por_id: cuadre?.precierre?.porId || null,
      precierre_por_nombre: cuadre?.precierre?.porNombre || null,
      precierre_asignado_a_id: cuadre?.precierre?.asignadoAId || null,
      precierre_asignado_a_nombre: cuadre?.precierre?.asignadoANombre || null,
      precierre_observaciones: cuadre?.precierre?.observaciones || null,
      precierre_totales: cuadre?.precierre?.totales || null,
      precierre_pendiente_confirmacion: cuadre?.precierrePendienteConfirmacion || false,
      precierre_confirmado_por_receptor: cuadre?.precierre?.confirmadoPorReceptor || false,
      precierre_fecha_confirmacion: cuadre?.precierre?.fechaConfirmacion || null,
      precierre_confirmado_por_nombre: cuadre?.precierre?.confirmadoPorNombre || null,
      // 🔹 Campos de acciones rápidas
      turnos: Array.isArray(cuadre?.turnos) ? cuadre.turnos : [],
      adelantos: Array.isArray(cuadre?.adelantos) ? cuadre.adelantos : [],
      novedades: novedadesArray,
      bases_domicilios: Array.isArray(cuadre?.basesDomicilios) ? cuadre.basesDomicilios : [],
      // 🔹 INGRESOS VARIOS
      ingresos_varios: ingresosVariosArray,
      total_ingresos_varios: totalIngresosVarios,
      cantidad_ingresos_varios: cantidadIngresosVarios,
      // 🔹 NOVEDADES (duplicadas como columnas para fácil acceso)
      cantidad_novedades: cantidadNovedades,
      comentarios_novedades: comentariosNovedades,
      novedades_completo: textoNovedadesCompleto,
      // 🔹 Campos de colaborador
      colaborador_id: cuadre?.colaboradorId || null,
      colaborador_nombre: cuadre?.colaboradorNombre || null,
      // 🔹 Campo de efectivo final confirmado (segunda jornada)
      efectivo_final_confirmado: cuadre?.ventas?.efectivoFinalConfirmado || cuadre?.efectivoFinalConfirmado || false,
      // 🔹 NUEVAS COLUMNAS - Valores calculados de cuadre (duplicados pero como columnas separadas)
      efectivo_en_caja: efectivoEnCaja,
      deberia_quedar_en_caja: deberíaQuedarEnCaja,
      sobrante_neto: sobranteNeto,
      diferencia_caja: diferenciaCaja,
      estado_cuadre: estadoCuadre,
      monto_diferencia: montoDiferencia,
      valor_total_sobrante: valorTotalSobrante,
      diferencia_neta: diferenciaNeta,
      empresa_id: empresaId, // 🔐 SIEMPRE incluir empresa_id
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from("cuadres_diarios")
      .upsert(payload, { onConflict: "id" })
      .select()
      .maybeSingle();

    if (error) {
      console.error("❌ Error guardando cuadre diario en Supabase:", error);
      return null;
    }

    return mapearRowCuadreDesdeSupabase(data || payload);
  } catch (e) {
    console.error("❌ Error en guardarCuadreDiarioEnSupabase:", e);
    return null;
  }
}

async function cargarCuadresDiariosDeSupabase(empresaId = null) {
  try {
    // 🔐 SEGURIDAD: Si no hay empresaId, NO cargar ningún cuadre
    // Esto evita que empleados vean cuadres de otras empresas
    if (!empresaId) {
      console.warn("⚠️ cargarCuadresDiariosDeSupabase: NO se proporcionó empresaId - retornando vacío por seguridad");
      return [];
    }
    
    // FILTRO: Excluir cuadres de prueba anteriores al 7 de febrero de 2026
    const FECHA_MINIMA_CUADRES = "2026-02-07";
    
    let query = supabase
      .from("cuadres_diarios")
      .select("*")
      .eq("empresa_id", empresaId) // 🔐 SIEMPRE filtrar por empresa
      .gte("fecha", FECHA_MINIMA_CUADRES) // Solo cuadres >= 7 de febrero de 2026
      .order("fecha", { ascending: false })
      .order("updated_at", { ascending: false });
    
    const { data, error } = await query;

    if (error) {
      console.error("❌ Error cargando cuadres diarios desde Supabase:", error);
      return [];
    }

    console.log(`✅ Cuadres cargados para empresa ${empresaId}: ${(data || []).length}`);
    return (data || []).map((row) => mapearRowCuadreDesdeSupabase(row));
  } catch (e) {
    console.error("❌ Error en cargarCuadresDiariosDeSupabase:", e);
    return [];
  }
}

// 🖨️ Guardar tirilla de impresión en Supabase para consulta posterior
async function guardarTirillaEnSupabase(cuadreId, tirillaHTML, cuadre, empresaId = null) {
  try {
    if (!cuadreId || !tirillaHTML) {
      console.warn("⚠️ guardarTirillaEnSupabase: Faltan datos (cuadreId o HTML)");
      return false;
    }

    const payload = {
      id: uid(), // Generar ID único para la tirilla
      cuadre_id: cuadreId,
      contenido_html: tirillaHTML,
      sede: cuadre?.sede || null,
      fecha_cuadre: cuadre?.fecha || null,
      responsable_id: cuadre?.empleadoId || null,
      responsable_nombre: cuadre?.cerradoPorNombre || null,
      fecha_impresion: new Date().toISOString(),
      empresa_id: empresaId || null,
      created_at: new Date().toISOString()
    };

    const { error } = await supabase
      .from("tirillas_impresas")
      .insert([payload]);

    if (error) {
      console.error("❌ Error guardando tirilla en Supabase:", error);
      return false;
    }

    console.log("✅ Tirilla guardada en Supabase para cuadre:", cuadreId);
    return true;
  } catch (e) {
    console.error("❌ Error en guardarTirillaEnSupabase:", e);
    return false;
  }
}

// 📋 Obtener tirilla guardada de Supabase
async function obtenerTirillaDeSupabase(cuadreId) {
  try {
    const { data, error } = await supabase
      .from("tirillas_impresas")
      .select("*")
      .eq("cuadre_id", cuadreId)
      .order("fecha_impresion", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error("❌ Error obteniendo tirilla:", error);
      return null;
    }

    return data;
  } catch (e) {
    console.error("❌ Error en obtenerTirillaDeSupabase:", e);
    return null;
  }
}

/* ====================== SISTEMA DE REVISIÓN DE CUADRES ====================== */

// Guardar revisión de cuadre en Supabase
async function guardarRevisionCuadreEnSupabase(revision, empresaId = null) {
  try {
    if (!revision?.id) {
      console.error("❌ guardarRevisionCuadreEnSupabase requiere un ID válido");
      return null;
    }

    const payload = {
      id: revision.id,
      cuadre_id: revision.cuadre_id,
      fecha_revision: revision.fecha_revision || new Date().toISOString(),
      fecha_cierre: revision.fecha_cierre || null,
      estado: revision.estado || 'en_revision',
      revisor_principal_id: revision.revisor_principal_id || null,
      revisor_principal_nombre: revision.revisor_principal_nombre || null,
      observaciones_generales: revision.observaciones_generales || '',
      sede: revision.sede || null,
      fecha_cuadre: revision.fecha_cuadre || null,
      empleado_cuadre_id: revision.empleado_cuadre_id || null,
      empleado_cuadre_nombre: revision.empleado_cuadre_nombre || null,
      revision_facturas: revision.revision_facturas || { aprobado: null, observaciones: '', items: [] },
      revision_domicilios: revision.revision_domicilios || { aprobado: null, observaciones: '', items: [] },
      revision_transferencias: revision.revision_transferencias || { aprobado: null, observaciones: '', items: [] },
      revision_turnos: revision.revision_turnos || { aprobado: null, observaciones: '', items: [] },
      revision_adelantos: revision.revision_adelantos || { aprobado: null, observaciones: '', items: [] },
      revision_denominaciones: revision.revision_denominaciones || { aprobado: null, observaciones: '' },
      revision_novedades: revision.revision_novedades || { aprobado: null, observaciones: '', items: [] },
      revision_datafonos: revision.revision_datafonos || { aprobado: null, observaciones: '', items: [] },
      total_cuadre: revision.total_cuadre || 0,
      diferencia: revision.diferencia || 0,
      empresa_id: empresaId || null,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from("revisiones_cuadre")
      .upsert(payload, { onConflict: "id" })
      .select()
      .maybeSingle();

    if (error) {
      console.error("❌ Error guardando revisión de cuadre:", error);
      return null;
    }

    // Actualizar estado de revisión en el cuadre original
    if (revision.cuadre_id) {
      await supabase
        .from("cuadres_diarios")
        .update({ 
          estado_revision: revision.estado,
          revision_id: revision.id 
        })
        .eq("id", revision.cuadre_id);
    }

    return data || payload;
  } catch (e) {
    console.error("❌ Error en guardarRevisionCuadreEnSupabase:", e);
    return null;
  }
}

// Cargar revisiones de cuadres desde Supabase
async function cargarRevisionesCuadresDeSupabase(empresaId = null, filtros = {}) {
  try {
    let query = supabase
      .from("revisiones_cuadre")
      .select("*")
      .order("fecha_revision", { ascending: false });
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    if (filtros.sede) {
      query = query.eq("sede", filtros.sede);
    }
    if (filtros.estado) {
      query = query.eq("estado", filtros.estado);
    }
    if (filtros.fechaDesde) {
      query = query.gte("fecha_cuadre", filtros.fechaDesde);
    }
    if (filtros.fechaHasta) {
      query = query.lte("fecha_cuadre", filtros.fechaHasta);
    }
    if (filtros.empleadoId) {
      query = query.eq("empleado_cuadre_id", filtros.empleadoId);
    }
    
    const { data, error } = await query;

    if (error) {
      console.error("❌ Error cargando revisiones de cuadres:", error);
      return [];
    }

    return data || [];
  } catch (e) {
    console.error("❌ Error en cargarRevisionesCuadresDeSupabase:", e);
    return [];
  }
}

// Cargar una revisión específica por ID
async function cargarRevisionCuadrePorId(revisionId) {
  try {
    const { data, error } = await supabase
      .from("revisiones_cuadre")
      .select("*")
      .eq("id", revisionId)
      .maybeSingle();

    if (error) {
      console.error("❌ Error cargando revisión:", error);
      return null;
    }

    return data;
  } catch (e) {
    console.error("❌ Error en cargarRevisionCuadrePorId:", e);
    return null;
  }
}

// Verificar si un cuadre ya tiene revisión
async function verificarRevisionExistente(cuadreId) {
  try {
    const { data, error } = await supabase
      .from("revisiones_cuadre")
      .select("id, estado")
      .eq("cuadre_id", cuadreId)
      .maybeSingle();

    if (error) {
      console.error("❌ Error verificando revisión existente:", error);
      return null;
    }

    return data; // null si no existe, objeto con id y estado si existe
  } catch (e) {
    console.error("❌ Error en verificarRevisionExistente:", e);
    return null;
  }
}

// Guardar asignación de sección a empleado
async function guardarAsignacionRevision(asignacion, empresaId = null) {
  try {
    if (!asignacion?.id) {
      console.error("❌ guardarAsignacionRevision requiere un ID válido");
      return null;
    }

    const payload = {
      id: asignacion.id,
      revision_id: asignacion.revision_id,
      empleado_id: asignacion.empleado_id,
      empleado_nombre: asignacion.empleado_nombre,
      seccion: asignacion.seccion,
      estado: asignacion.estado || 'pendiente',
      aprobado: asignacion.aprobado !== undefined ? asignacion.aprobado : null,
      observaciones: asignacion.observaciones || '',
      items_revisados: asignacion.items_revisados || [],
      fecha_asignacion: asignacion.fecha_asignacion || new Date().toISOString(),
      fecha_completado: asignacion.fecha_completado || null,
      asignado_por_id: asignacion.asignado_por_id || null,
      asignado_por_nombre: asignacion.asignado_por_nombre || null,
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from("asignaciones_revision")
      .upsert(payload, { onConflict: "id" })
      .select()
      .maybeSingle();

    if (error) {
      console.error("❌ Error guardando asignación:", error);
      return null;
    }

    return data || payload;
  } catch (e) {
    console.error("❌ Error en guardarAsignacionRevision:", e);
    return null;
  }
}

// Cargar asignaciones de una revisión
async function cargarAsignacionesRevision(revisionId) {
  try {
    const { data, error } = await supabase
      .from("asignaciones_revision")
      .select("*")
      .eq("revision_id", revisionId)
      .order("fecha_asignacion", { ascending: true });

    if (error) {
      console.error("❌ Error cargando asignaciones:", error);
      return [];
    }

    return data || [];
  } catch (e) {
    console.error("❌ Error en cargarAsignacionesRevision:", e);
    return [];
  }
}

// Eliminar asignación
async function eliminarAsignacionRevision(asignacionId) {
  try {
    const { error } = await supabase
      .from("asignaciones_revision")
      .delete()
      .eq("id", asignacionId);

    if (error) {
      console.error("❌ Error eliminando asignación:", error);
      return false;
    }

    return true;
  } catch (e) {
    console.error("❌ Error en eliminarAsignacionRevision:", e);
    return false;
  }
}

/* ====================== SISTEMA DE BLOQUEO Y SOLICITUDES DE CUADRES ====================== */

// Bloquear un cuadre cuando un usuario lo abre
async function bloquearCuadre(cuadreId, usuarioId, usuarioNombre) {
  try {
    const { error } = await supabase
      .from("cuadres_bloqueos")
      .upsert({
        cuadre_id: cuadreId,
        usuario_id: usuarioId,
        usuario_nombre: usuarioNombre,
        bloqueado_en: new Date().toISOString(),
        activo: true
      }, { onConflict: "cuadre_id" });
    
    if (error) {
      console.error("❌ Error bloqueando cuadre:", error);
      return false;
    }
    return true;
  } catch (e) {
    console.error("❌ Error en bloquearCuadre:", e);
    return false;
  }
}

// Desbloquear un cuadre cuando el usuario lo cierra o sale
async function desbloquearCuadre(cuadreId) {
  try {
    const { error } = await supabase
      .from("cuadres_bloqueos")
      .update({ activo: false })
      .eq("cuadre_id", cuadreId);
    
    if (error) {
      console.error("❌ Error desbloqueando cuadre:", error);
      return false;
    }
    return true;
  } catch (e) {
    console.error("❌ Error en desbloquearCuadre:", e);
    return false;
  }
}

// Verificar si un cuadre está bloqueado
async function verificarBloqueoCuadre(cuadreId) {
  try {
    const { data, error } = await supabase
      .from("cuadres_bloqueos")
      .select("*")
      .eq("cuadre_id", cuadreId)
      .eq("activo", true)
      .maybeSingle();
    
    if (error) {
      console.error("❌ Error verificando bloqueo:", error);
      return null;
    }
    return data; // null si no está bloqueado, objeto con info si está bloqueado
  } catch (e) {
    console.error("❌ Error en verificarBloqueoCuadre:", e);
    return null;
  }
}

// Enviar solicitud de acceso a un cuadre bloqueado
async function enviarSolicitudAcceso(cuadreId, solicitanteId, solicitanteNombre, propietarioId) {
  try {
    // Verificar si ya existe una solicitud pendiente o aprobada del mismo usuario
    const { data: existente } = await supabase
      .from("cuadres_solicitudes")
      .select("*")
      .eq("cuadre_id", cuadreId)
      .eq("solicitante_id", solicitanteId)
      .in("estado", ["pendiente", "aprobado"])
      .maybeSingle();
    
    if (existente) {
      console.log("⚠️ Ya existe una solicitud del mismo usuario para este cuadre");
      if (existente.estado === "pendiente") {
        alert("Ya tienes una solicitud pendiente para este cuadre.");
      } else {
        alert("Ya tienes acceso aprobado a este cuadre.");
      }
      return null;
    }
    
    const { data, error } = await supabase
      .from("cuadres_solicitudes")
      .insert({
        cuadre_id: cuadreId,
        solicitante_id: solicitanteId,
        solicitante_nombre: solicitanteNombre,
        propietario_id: propietarioId,
        estado: "pendiente",
        fecha_solicitud: new Date().toISOString()
      })
      .select()
      .single();
    
    if (error) {
      console.error("❌ Error enviando solicitud:", error);
      return null;
    }
    return data;
  } catch (e) {
    console.error("❌ Error en enviarSolicitudAcceso:", e);
    return null;
  }
}

// Cargar solicitudes pendientes (para el propietario del cuadre o gerentes)
async function cargarSolicitudesPendientes(usuarioId, esGerente = false) {
  try {
    let query = supabase
      .from("cuadres_solicitudes")
      .select("*")
      .eq("estado", "pendiente")
      .order("fecha_solicitud", { ascending: false });
    
    // Si no es gerente, solo ver solicitudes de sus cuadres
    if (!esGerente) {
      query = query.eq("propietario_id", usuarioId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("❌ Error cargando solicitudes:", error);
      return [];
    }
    return data || [];
  } catch (e) {
    console.error("❌ Error en cargarSolicitudesPendientes:", e);
    return [];
  }
}

// Responder a una solicitud de acceso
async function responderSolicitudAcceso(solicitudId, aprobado, cuadreId = null) {
  try {
    const { error: updateError } = await supabase
      .from("cuadres_solicitudes")
      .update({
        estado: aprobado ? "aprobado" : "rechazado",
        fecha_respuesta: new Date().toISOString()
      })
      .eq("id", solicitudId);
    
    if (updateError) {
      console.error("❌ Error respondiendo solicitud:", updateError);
      return false;
    }
    
    // Si se aprueba, desbloquear el cuadre para que el solicitante pueda acceder
    if (aprobado && cuadreId) {
      await desbloquearCuadre(cuadreId);
    }
    
    return true;
  } catch (e) {
    console.error("❌ Error en responderSolicitudAcceso:", e);
    return false;
  }
}

// Verificar si el usuario tiene una solicitud aprobada para un cuadre
async function verificarSolicitudAprobada(cuadreId, usuarioId) {
  try {
    const { data, error } = await supabase
      .from("cuadres_solicitudes")
      .select("*")
      .eq("cuadre_id", cuadreId)
      .eq("solicitante_id", usuarioId)
      .eq("estado", "aprobado")
      .maybeSingle();
    
    if (error) {
      console.error("❌ Error verificando solicitud aprobada:", error);
      return false;
    }
    return !!data;
  } catch (e) {
    console.error("❌ Error en verificarSolicitudAprobada:", e);
    return false;
  }
}

// Cargar todos los accesos aprobados para un cuadre específico (para gerentes)
async function cargarAccesosAprobadosPorCuadre(cuadreId) {
  try {
    const { data, error } = await supabase
      .from("cuadres_solicitudes")
      .select("*")
      .eq("cuadre_id", cuadreId)
      .eq("estado", "aprobado")
      .order("fecha_solicitud", { ascending: false });
    
    if (error) {
      console.error("❌ Error cargando accesos del cuadre:", error);
      return [];
    }
    
    // Filtrar para obtener solo un registro por usuario (el más reciente)
    const usuariosUnicos = new Map();
    (data || []).forEach((acceso) => {
      if (!usuariosUnicos.has(acceso.solicitante_id)) {
        usuariosUnicos.set(acceso.solicitante_id, acceso);
      }
    });
    
    return Array.from(usuariosUnicos.values());
  } catch (e) {
    console.error("❌ Error en cargarAccesosAprobadosPorCuadre:", e);
    return [];
  }
}

// Revocar acceso a un cuadre (solo gerentes)
async function revocarAccesoCuadre(solicitudId) {
  try {
    const { error } = await supabase
      .from("cuadres_solicitudes")
      .update({
        estado: "revocado",
        fecha_respuesta: new Date().toISOString()
      })
      .eq("id", solicitudId);
    
    if (error) {
      console.error("❌ Error revocando acceso:", error);
      return false;
    }
    return true;
  } catch (e) {
    console.error("❌ Error en revocarAccesoCuadre:", e);
    return false;
  }
}

/* ====================== SISTEMA DE TRABAJO COLABORATIVO EN TIEMPO REAL ====================== */

// Registrar que un usuario está activo en un cuadre
async function registrarUsuarioActivoEnCuadre(cuadreId, usuarioId, usuarioNombre) {
  try {
    const { error } = await supabase
      .from("cuadres_usuarios_activos")
      .upsert({
        cuadre_id: cuadreId,
        usuario_id: usuarioId,
        usuario_nombre: usuarioNombre,
        ultima_actividad: new Date().toISOString()
      }, { onConflict: "cuadre_id,usuario_id" });
    
    if (error) {
      console.error("❌ Error registrando usuario activo:", error);
      return false;
    }
    return true;
  } catch (e) {
    console.error("❌ Error en registrarUsuarioActivoEnCuadre:", e);
    return false;
  }
}

// Remover usuario activo cuando sale del cuadre
async function removerUsuarioActivoEnCuadre(cuadreId, usuarioId) {
  try {
    const { error } = await supabase
      .from("cuadres_usuarios_activos")
      .delete()
      .eq("cuadre_id", cuadreId)
      .eq("usuario_id", usuarioId);
    
    if (error) {
      console.error("❌ Error removiendo usuario activo:", error);
      return false;
    }
    return true;
  } catch (e) {
    console.error("❌ Error en removerUsuarioActivoEnCuadre:", e);
    return false;
  }
}

// Cargar usuarios activos en un cuadre (activos en los últimos 60 segundos)
async function cargarUsuariosActivosEnCuadre(cuadreId) {
  try {
    const hace60segundos = new Date(Date.now() - 60000).toISOString();
    
    const { data, error } = await supabase
      .from("cuadres_usuarios_activos")
      .select("*")
      .eq("cuadre_id", cuadreId)
      .gte("ultima_actividad", hace60segundos)
      .order("ultima_actividad", { ascending: false });
    
    if (error) {
      console.error("❌ Error cargando usuarios activos:", error);
      return [];
    }
    return data || [];
  } catch (e) {
    console.error("❌ Error en cargarUsuariosActivosEnCuadre:", e);
    return [];
  }
}

// ====================== FUNCIONES DE FUSIÓN SIN PÉRDIDA DE DATOS ======================
// Cuando dos usuarios editan el mismo campo, NINGUNO se pierde
// Uno se queda en el campo original, el otro se crea en una fila nueva

/**
 * Fusiona dos arrays SIN CREAR DUPLICADOS
 * Usa estrategia "el más reciente gana" basado en updatedAt
 * NO crea items nuevos por conflicto - solo mantiene el más actualizado
 */
function fusionarArraysSinPerder(arrayLocal, arrayRemoto, camposAFusionar, generarId) {
  const resultado = [];
  const mapLocal = new Map();
  const mapRemoto = new Map();
  const idsVistos = new Set();
  
  // Indexar por ID
  (arrayLocal || []).forEach(item => {
    if (item && item.id) mapLocal.set(item.id, item);
  });
  (arrayRemoto || []).forEach(item => {
    if (item && item.id) mapRemoto.set(item.id, item);
  });
  
  // Procesar items locales
  mapLocal.forEach((itemLocal, id) => {
    if (idsVistos.has(id)) return;
    idsVistos.add(id);
    
    const itemRemoto = mapRemoto.get(id);
    
    if (!itemRemoto) {
      // Solo existe en local - conservar
      resultado.push(itemLocal);
    } else {
      // Existe en ambos - usar el MÁS RECIENTE (basado en updatedAt)
      const timestampLocal = itemLocal.updatedAt || 0;
      const timestampRemoto = itemRemoto.updatedAt || 0;
      
      if (timestampRemoto > timestampLocal) {
        // Remoto es más reciente - usar remoto
        resultado.push({ ...itemRemoto, updatedAt: Date.now() });
      } else {
        // Local es más reciente o igual - usar local
        resultado.push({ ...itemLocal, updatedAt: Date.now() });
      }
    }
  });
  
  // Agregar items que solo existen en remoto
  mapRemoto.forEach((itemRemoto, id) => {
    if (!idsVistos.has(id)) {
      idsVistos.add(id);
      resultado.push(itemRemoto);
    }
  });
  
  // Eliminar items marcados como creados por conflicto que sean duplicados
  // (Limpia duplicados históricos)
  const resultadoLimpio = eliminarDuplicadosPorValores(resultado, camposAFusionar);
  
  return resultadoLimpio;
}

/**
 * Elimina items duplicados basándose en los valores de los campos (no solo el ID)
 * Esto limpia duplicados creados por la fusión anterior
 */
function eliminarDuplicadosPorValores(items, campos) {
  const vistos = new Map();
  const resultado = [];
  
  items.forEach(item => {
    // Crear una clave basada en los valores de los campos
    const clave = campos.map(c => String(item[c] || "").trim().toLowerCase()).join("|");
    
    // Si la clave está vacía (todos los campos vacíos), usar el ID
    const claveReal = clave.replace(/\|/g, "").trim() === "" ? item.id : clave;
    
    if (!vistos.has(claveReal)) {
      vistos.set(claveReal, item);
      resultado.push(item);
    } else {
      // Si ya existe, conservar el más reciente
      const existente = vistos.get(claveReal);
      if ((item.updatedAt || 0) > (existente.updatedAt || 0)) {
        // Reemplazar el existente con el más reciente
        const idx = resultado.indexOf(existente);
        if (idx >= 0) {
          resultado[idx] = item;
          vistos.set(claveReal, item);
        }
      }
      console.log(`🧹 Eliminado duplicado: ${claveReal}`);
    }
  });
  
  return resultado;
}

/**
 * Fusiona un campo simple sin perder datos
 * Si ambos tienen valor diferente, retorna ambos concatenados o un array
 */
function fusionarCampoSinPerder(valorLocal, valorRemoto, esNumerico = false) {
  const localTieneValor = valorLocal !== undefined && valorLocal !== null && String(valorLocal).trim() !== "";
  const remotoTieneValor = valorRemoto !== undefined && valorRemoto !== null && String(valorRemoto).trim() !== "";
  
  // Si solo uno tiene valor, usar ese
  if (localTieneValor && !remotoTieneValor) return valorLocal;
  if (remotoTieneValor && !localTieneValor) return valorRemoto;
  
  // Si ambos tienen el mismo valor, retornar cualquiera
  if (String(valorLocal).trim() === String(valorRemoto).trim()) return valorLocal;
  
  // CONFLICTO: Ambos tienen valores diferentes
  if (esNumerico) {
    // Para números, sumar o conservar el mayor
    const numLocal = parseFloat(valorLocal) || 0;
    const numRemoto = parseFloat(valorRemoto) || 0;
    console.log(`⚠️ Conflicto numérico: ${numLocal} vs ${numRemoto} - conservando mayor`);
    return Math.max(numLocal, numRemoto);
  } else {
    // Para texto, concatenar con separador
    console.log(`⚠️ Conflicto texto: "${valorLocal}" vs "${valorRemoto}" - concatenando`);
    return `${valorLocal} | ${valorRemoto}`;
  }
}

// ====================== FIN FUNCIONES DE FUSIÓN SIN PÉRDIDA ======================

// Fusionar cuadres de forma colaborativa - SIN PÉRDIDA DE DATOS
// localPrioridad: true = usuario está editando, false = sincronización normal
function fusionarCuadresColaborativo(cuadreLocal, cuadreRemoto, localPrioridad = false) {
  // Si no hay cuadre remoto, retornar local
  if (!cuadreRemoto) return cuadreLocal;
  
  // 🔐 PRIORIDAD ABSOLUTA: Si el cuadre remoto está cerrado (estado "final"),
  // aceptar TODOS los datos del remoto sin fusionar. El cierre es irreversible.
  if (cuadreRemoto.estado === "final" && cuadreLocal.estado !== "final") {
    console.log("🔐 Cuadre remoto está CERRADO - aceptando todos los datos del remoto");
    return { ...cuadreRemoto };
  }
  
  // Crear una copia del cuadre local
  const resultado = { ...cuadreLocal };
  
  console.log(`🔀 Fusionando cuadres - ${localPrioridad ? 'LOCAL' : 'REMOTO'} tiene PRIORIDAD...`);
  
  // Helper: fusionar un campo
  // Si localPrioridad=true: local gana siempre (usuario está editando)
  // Si localPrioridad=false: REMOTO SIEMPRE GANA (incluso si está vacío - para propagar borrados)
  const fusionarCampo = (valorLocal, valorRemoto, campoExisteEnRemoto = true) => {
    const localTieneValor = valorLocal !== undefined && valorLocal !== null && String(valorLocal).trim() !== "";
    
    if (localPrioridad) {
      // Usuario editando: local gana siempre
      if (localTieneValor) return valorLocal;
      // Si local no tiene valor, usar remoto
      return valorRemoto !== undefined ? valorRemoto : valorLocal;
    } else {
      // Sincronización normal: REMOTO SIEMPRE GANA (incluso vacío = borrado)
      // Esto permite que los borrados se propaguen correctamente
      if (campoExisteEnRemoto) {
        return valorRemoto; // Usar remoto aunque esté vacío
      }
      return valorLocal;
    }
  };
  
  // Helper: fusionar un item completo campo por campo
  // MEJORADO: Usa timestamps para determinar qué versión es más reciente
  const fusionarItem = (itemLocal, itemRemoto, campos) => {
    if (!itemRemoto) return itemLocal;
    if (!itemLocal) return itemRemoto;
    
    const fusionado = { ...itemLocal };
    
    // Si tenemos timestamps, comparar para decidir prioridad
    const timestampLocal = itemLocal.updatedAt || 0;
    const timestampRemoto = itemRemoto.updatedAt || 0;
    
    // SIEMPRE priorizar el que tenga timestamp más reciente
    // Esto asegura que los cambios de cualquier usuario se mantengan
    const localEsMasReciente = timestampLocal > timestampRemoto;
    const remotoEsMasReciente = timestampRemoto > timestampLocal;
    
    // Si el remoto es más reciente, usar TODOS sus campos (el usuario remoto hizo cambios después)
    if (remotoEsMasReciente && !localPrioridad) {
      console.log(`🔄 Item remoto es más reciente (${timestampRemoto} > ${timestampLocal}) - usando datos remotos`);
      campos.forEach(campo => {
        fusionado[campo] = itemRemoto[campo];
      });
      fusionado.updatedAt = timestampRemoto;
      return fusionado;
    }
    
    // Si el local es más reciente, mantenerlo (incluso durante sincronización normal)
    if (localEsMasReciente) {
      console.log(`✅ Item local es más reciente (${timestampLocal} > ${timestampRemoto}) - manteniendo datos locales`);
      fusionado.updatedAt = timestampLocal;
      return fusionado;
    }
    
    // Si tienen el mismo timestamp o no hay timestamps, usar lógica de fusión campo por campo
    campos.forEach(campo => {
      const campoExisteEnRemoto = itemRemoto.hasOwnProperty(campo) || campo in itemRemoto;
      
      if (localPrioridad) {
        // Usuario editando - local gana
        const localTieneValor = itemLocal[campo] !== undefined && itemLocal[campo] !== null && String(itemLocal[campo]).trim() !== "";
        if (localTieneValor) {
          fusionado[campo] = itemLocal[campo];
        } else {
          fusionado[campo] = itemRemoto[campo] !== undefined ? itemRemoto[campo] : itemLocal[campo];
        }
      } else {
        // Sincronización normal - remoto gana
        fusionado[campo] = fusionarCampo(itemLocal[campo], itemRemoto[campo], campoExisteEnRemoto);
      }
    });
    
    // Mantener el timestamp más reciente
    fusionado.updatedAt = Math.max(timestampLocal, timestampRemoto);
    
    // Mantener campos adicionales del remoto que no estén en la lista
    Object.keys(itemRemoto).forEach(key => {
      if (!campos.includes(key) && key !== 'id' && key !== 'updatedAt') {
        fusionado[key] = fusionarCampo(itemLocal[key], itemRemoto[key], true);
      }
    });
    return fusionado;
  };
  
  // ====================== FUSIONAR FACTURAS ELIMINADAS ======================
  // Primero fusionar las listas de facturas eliminadas de ambos cuadres
  const facturasEliminadasLocales = Array.isArray(cuadreLocal.facturasEliminadas) ? cuadreLocal.facturasEliminadas : [];
  const facturasEliminadasRemotas = Array.isArray(cuadreRemoto.facturasEliminadas) ? cuadreRemoto.facturasEliminadas : [];
  
  // Combinar todas las eliminadas (tanto locales como remotas)
  const facturasEliminadasMap = new Map();
  [...facturasEliminadasLocales, ...facturasEliminadasRemotas].forEach(fe => {
    if (fe.id && !facturasEliminadasMap.has(fe.id)) {
      facturasEliminadasMap.set(fe.id, fe);
    }
  });
  resultado.facturasEliminadas = Array.from(facturasEliminadasMap.values());
  
  // Crear sets para verificación rápida
  const idsEliminados = new Set(resultado.facturasEliminadas.map(fe => fe.id));
  
  console.log(`🗑️ Facturas eliminadas combinadas: ${resultado.facturasEliminadas.length} (IDs: ${Array.from(idsEliminados).join(', ')})`);
  
  // ====================== FUSIONAR FACTURAS: EL MÁS RECIENTE GANA ======================
  // IGUAL que domicilios y transfer: NO agregar nada del otro lado
  if (Array.isArray(cuadreRemoto.facturas) || Array.isArray(cuadreLocal.facturas)) {
    const timestampLocal = new Date(cuadreLocal.updated_at || 0).getTime();
    const timestampRemoto = new Date(cuadreRemoto.updated_at || 0).getTime();
    const remotoEsMasReciente = timestampRemoto > timestampLocal;
    
    if (localPrioridad) {
      // Usuario editando: conservar local
      resultado.facturas = Array.isArray(cuadreLocal.facturas) ? cuadreLocal.facturas : [];
    } else if (remotoEsMasReciente) {
      // Remoto es más reciente - usar remoto COMPLETO
      resultado.facturas = Array.isArray(cuadreRemoto.facturas) ? cuadreRemoto.facturas : [];
    } else {
      // Local es más reciente - conservar local
      resultado.facturas = Array.isArray(cuadreLocal.facturas) ? cuadreLocal.facturas : [];
    }
    console.log(`📋 Facturas: ${resultado.facturas.length} (${localPrioridad ? 'local-prioridad' : remotoEsMasReciente ? 'remoto-reciente' : 'local-reciente'})`);
  }
  
  // ====================== NUEVA ESTRATEGIA PARA ARRAYS ======================
  // NO fusionar elemento por elemento - usar el array del cuadre MÁS RECIENTE
  // Esto evita duplicados cuando hay múltiples usuarios
  // =========================================================================
  
  const timestampLocal = new Date(cuadreLocal.updated_at || 0).getTime();
  const timestampRemoto = new Date(cuadreRemoto.updated_at || 0).getTime();
  const remotoEsMasReciente = timestampRemoto > timestampLocal;
  
  // Fusionar domicilios: EL MÁS RECIENTE GANA (sin crear duplicados)
  if (Array.isArray(cuadreRemoto.domicilios) || Array.isArray(cuadreLocal.domicilios)) {
    if (localPrioridad) {
      // Usuario editando: conservar local
      resultado.domicilios = Array.isArray(cuadreLocal.domicilios) ? cuadreLocal.domicilios : [];
    } else if (remotoEsMasReciente) {
      // Remoto es más reciente - usar remoto
      resultado.domicilios = Array.isArray(cuadreRemoto.domicilios) ? cuadreRemoto.domicilios : [];
    } else {
      // Local es más reciente - conservar local
      resultado.domicilios = Array.isArray(cuadreLocal.domicilios) ? cuadreLocal.domicilios : [];
    }
    console.log(`🛵 Domicilios: ${resultado.domicilios.length} (${localPrioridad ? 'local-prioridad' : remotoEsMasReciente ? 'remoto-reciente' : 'local-reciente'})`);
  }
  
  // Fusionar transferencias: EL MÁS RECIENTE GANA
  if (Array.isArray(cuadreRemoto.transfer) || Array.isArray(cuadreLocal.transfer)) {
    if (localPrioridad) {
      resultado.transfer = Array.isArray(cuadreLocal.transfer) ? cuadreLocal.transfer : [];
    } else if (remotoEsMasReciente) {
      resultado.transfer = Array.isArray(cuadreRemoto.transfer) ? cuadreRemoto.transfer : [];
    } else {
      resultado.transfer = Array.isArray(cuadreLocal.transfer) ? cuadreLocal.transfer : [];
    }
    console.log(`💳 Transfer: ${resultado.transfer.length} (${localPrioridad ? 'local-prioridad' : remotoEsMasReciente ? 'remoto-reciente' : 'local-reciente'})`);
  }
  
  // Fusionar datafonos: EL MÁS RECIENTE GANA
  if (Array.isArray(cuadreRemoto.datafonos) || Array.isArray(cuadreLocal.datafonos)) {
    if (localPrioridad) {
      resultado.datafonos = Array.isArray(cuadreLocal.datafonos) ? cuadreLocal.datafonos : [];
    } else if (remotoEsMasReciente) {
      resultado.datafonos = Array.isArray(cuadreRemoto.datafonos) ? cuadreRemoto.datafonos : [];
    } else {
      resultado.datafonos = Array.isArray(cuadreLocal.datafonos) ? cuadreLocal.datafonos : [];
    }
    console.log(`📱 Datafonos: ${resultado.datafonos.length}`);
  }
  
  // Fusionar ventas (objeto, no array) - respeta localPrioridad
  if (cuadreRemoto.ventas && typeof cuadreRemoto.ventas === 'object') {
    const ventasLocal = cuadreLocal.ventas || {};
    const ventasRemoto = cuadreRemoto.ventas;
    resultado.ventas = {};
    
    // 🔐 CAMPOS PROTEGIDOS: 
    // - Si fueron modificados por GERENCIA, NUNCA pueden ser revertidos por REMOTO
    // - Si fueron confirmados (efectivoEnCajaConfirmado=true), NO pueden ser revertidos a false
    const camposProtegidos = ['efectivoEnCaja', 'efectivoEnCajaConfirmado', 'efectivoFinalConfirmado', 'efectivoFinalTieneOportunidadModificacion'];
    const efectivoModificadoPorGerencia = ventasLocal.efectivoModificadoPorGerencia === true;
    const efectivoYaConfirmado = ventasLocal.efectivoEnCajaConfirmado === true;
    
    // Combinar todos los campos de ambos
    const todosLosCampos = new Set([...Object.keys(ventasLocal), ...Object.keys(ventasRemoto)]);
    todosLosCampos.forEach(campo => {
      const valorLocal = ventasLocal[campo];
      const valorRemoto = ventasRemoto[campo];
      const localTieneValor = valorLocal !== undefined && valorLocal !== null && String(valorLocal).trim() !== "";
      const campoExisteEnRemoto = ventasRemoto.hasOwnProperty(campo);
      
      // 🔐 Si GERENCIA modificó efectivo, estos campos usan LOCAL SIEMPRE
      if (efectivoModificadoPorGerencia && camposProtegidos.includes(campo)) {
        resultado.ventas[campo] = valorLocal;
        if (campo === 'efectivoEnCaja') {
          console.log(`🔐 EFECTIVO PROTEGIDO POR GERENCIA: mantener ${valorLocal} (ignorar remoto ${valorRemoto})`);
        }
        return;
      }
      
      // 🔐 Proteger confirmación de efectivo: si ya fue confirmado, NO puede revertirse a false
      if (campo === 'efectivoEnCajaConfirmado' && efectivoYaConfirmado) {
        // Si local tiene true, SIEMPRE mantenerlo (no permitir que remoto lo revierta a false)
        if (valorLocal === true) {
          resultado.ventas[campo] = true;
          console.log(`🔐 CONFIRMACIÓN PROTEGIDA: mantener efectivoEnCajaConfirmado=true (ignorar remoto ${valorRemoto})`);
          return;
        }
      }
      
      // 🔐 Proteger flags de oportunidad de modificación: si ya fue usada (false), no puede revertirse a true
      if (campo === 'efectivoFinalTieneOportunidadModificacion' && valorLocal === false) {
        // Si ya fue usada, mantenerla como false para siempre
        resultado.ventas[campo] = false;
        return;
      }
      
      if (localPrioridad) {
        // Usuario editando: local gana
        if (localTieneValor) {
          resultado.ventas[campo] = valorLocal;
        } else if (campoExisteEnRemoto) {
          resultado.ventas[campo] = valorRemoto;
        }
      } else {
        // Sincronización: remoto gana (incluso si está vacío = borrado)
        if (campoExisteEnRemoto) {
          resultado.ventas[campo] = valorRemoto;
        } else if (localTieneValor) {
          resultado.ventas[campo] = valorLocal;
        }
      }
    });
  }
  
  // Fusionar turnos: EL MÁS RECIENTE GANA
  if (Array.isArray(cuadreRemoto.turnos) || Array.isArray(cuadreLocal.turnos)) {
    if (localPrioridad) {
      resultado.turnos = Array.isArray(cuadreLocal.turnos) ? cuadreLocal.turnos : [];
    } else if (remotoEsMasReciente) {
      resultado.turnos = Array.isArray(cuadreRemoto.turnos) ? cuadreRemoto.turnos : [];
    } else {
      resultado.turnos = Array.isArray(cuadreLocal.turnos) ? cuadreLocal.turnos : [];
    }
    console.log(`⏰ Turnos: ${resultado.turnos.length}`);
  }
  
  // Fusionar adelantos: EL MÁS RECIENTE GANA
  if (Array.isArray(cuadreRemoto.adelantos) || Array.isArray(cuadreLocal.adelantos)) {
    if (localPrioridad) {
      resultado.adelantos = Array.isArray(cuadreLocal.adelantos) ? cuadreLocal.adelantos : [];
    } else if (remotoEsMasReciente) {
      resultado.adelantos = Array.isArray(cuadreRemoto.adelantos) ? cuadreRemoto.adelantos : [];
    } else {
      resultado.adelantos = Array.isArray(cuadreLocal.adelantos) ? cuadreLocal.adelantos : [];
    }
    console.log(`💵 Adelantos: ${resultado.adelantos.length}`);
  }
  
  // Fusionar novedades: EL MÁS RECIENTE GANA
  if (Array.isArray(cuadreRemoto.novedades) || Array.isArray(cuadreLocal.novedades)) {
    if (localPrioridad) {
      resultado.novedades = Array.isArray(cuadreLocal.novedades) ? cuadreLocal.novedades : [];
    } else if (remotoEsMasReciente) {
      resultado.novedades = Array.isArray(cuadreRemoto.novedades) ? cuadreRemoto.novedades : [];
    } else {
      resultado.novedades = Array.isArray(cuadreLocal.novedades) ? cuadreLocal.novedades : [];
    }
    console.log(`📝 Novedades: ${resultado.novedades.length}`);
  }
  
  // Fusionar campos simples: usar el que tenga valor, preferir remoto si ambos tienen
  const valorInicialLocal = cuadreLocal.valorInicial;
  const valorInicialRemoto = cuadreRemoto.valorInicial;
  if (valorInicialRemoto !== undefined && valorInicialRemoto !== null && valorInicialRemoto !== 0) {
    resultado.valorInicial = valorInicialRemoto;
  } else if (valorInicialLocal !== undefined && valorInicialLocal !== null) {
    resultado.valorInicial = valorInicialLocal;
  }
  
  // Comentarios: fusionar EVITANDO DUPLICADOS
  const comentarioLocal = cuadreLocal.comentarioModificacion || "";
  const comentarioRemoto = cuadreRemoto.comentarioModificacion || "";
  if (comentarioRemoto && comentarioRemoto !== comentarioLocal) {
    // 🔹 Verificar si uno contiene al otro para evitar duplicados
    const localContieneRemoto = comentarioLocal.includes(comentarioRemoto);
    const remotoContieneLocal = comentarioRemoto.includes(comentarioLocal);
    
    if (localContieneRemoto) {
      // El local ya tiene todo lo del remoto, mantener local
      resultado.comentarioModificacion = comentarioLocal;
    } else if (remotoContieneLocal) {
      // El remoto tiene todo lo del local, usar remoto
      resultado.comentarioModificacion = comentarioRemoto;
    } else {
      // Son diferentes, usar el más largo (que probablemente tiene más información)
      resultado.comentarioModificacion = comentarioRemoto.length > comentarioLocal.length 
        ? comentarioRemoto 
        : comentarioLocal;
    }
  }
  
  // Totales: fusionar
  if (cuadreRemoto.totales && Object.keys(cuadreRemoto.totales).length > 0) {
    resultado.totales = { ...(cuadreLocal.totales || {}), ...cuadreRemoto.totales };
  }
  
  // Actualizar timestamp
  resultado.updated_at = cuadreRemoto.updated_at || cuadreLocal.updated_at;
  
  console.log("✅ Cuadres fusionados SIN PÉRDIDA DE DATOS - todos los registros combinados");
  return resultado;
}

function normalizarNumeroInventario(valor) {
  if (valor === null || valor === undefined) return 0;
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : 0;
  if (typeof valor === "string") {
    const limpio = valor.replace(/[^0-9,.-]+/g, "").replace(/,/g, ".");
    const parsed = parseFloat(limpio);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function mapearLineaInventario(inventario, linea, categoria, empresaId = null) {
  const cabecera = {
    fecha: inventario?.fecha || "",
    sede: inventario?.sede || "",
    encargadoId: inventario?.encargadoId || "",
    personalCocina: inventario?.personalCocina || "",
    novedades: inventario?.novedades || "",
    cerrado: !!inventario?.cerrado,
  };

  const row = {
    id: `${inventario.id}::${linea.id}`,
    sede: inventario?.sede || null,
    articulo: linea?.producto || null,
    cantidad: normalizarNumeroInventario(linea?.existencias),
    valorunitario: 0,
    valortotal:
      (linea?.solicitar === "Sí" || linea?.solicitar === "si")
        ? normalizarNumeroInventario(linea?.cantidad)
        : 0,
    responsable: inventario?.encargadoId || null,
    fecharegistro: inventario?.fecha || null,
    observaciones: JSON.stringify({
      inventarioId: inventario.id,
      categoria,
      lineaId: linea?.id,
      producto: linea?.producto,
      existencias: linea?.existencias ?? "",
      solicitar: linea?.solicitar ?? "No",
      cantidadSolicitada: linea?.cantidad ?? "",
      cabecera,
    }),
    updated_at: new Date().toISOString(),
  };
  
  // Agregar empresa_id si está disponible
  if (empresaId) {
    row.empresa_id = empresaId;
  }
  
  return row;
}

function mapearFilaInventarioDesdeSupabase(row) {
  const info = typeof row?.observaciones === "string"
    ? safelyParseJSON(row.observaciones, null)
    : row?.observaciones || null;

  const inventarioId =
    info?.inventarioId ||
    (row?.id && row.id.includes("::") ? row.id.split("::")[0] : row?.id);

  const cabecera = info?.cabecera || {};

  return {
    inventarioId,
    linea: {
      id: info?.lineaId || row?.id || uid(),
      producto: info?.producto || row?.articulo || "",
      existencias:
        info?.existencias !== undefined
          ? info.existencias
          : row?.cantidad !== null && row?.cantidad !== undefined
          ? String(row.cantidad)
          : "",
      solicitar: info?.solicitar || "No",
      cantidad: info?.cantidadSolicitada || "",
      categoria: info?.categoria || "diario",
    },
    cabecera: {
      fecha: cabecera?.fecha || row?.fecharegistro || "",
      sede: cabecera?.sede || row?.sede || "",
      encargadoId: cabecera?.encargadoId || row?.responsable || "",
      personalCocina: cabecera?.personalCocina || "",
      novedades: cabecera?.novedades || "",
      cerrado: !!cabecera?.cerrado,
    },
    raw: row,
  };
}

async function guardarInventarioEnSupabase(inventario, empresaId = null) {
  try {
    if (!inventario?.id) {
      console.error("Inventario sin id, no se puede guardar en Supabase");
      return null;
    }

    const lineas = Array.isArray(inventario?.lineas) ? inventario.lineas : [];
    const rows = lineas.map((linea) =>
      mapearLineaInventario(
        inventario,
        linea,
        linea?.categoria || "diario",
        empresaId
      )
    );

    if (!rows.length) {
      console.warn("Inventario sin líneas, se omite guardado en Supabase");
      return inventario;
    }

    await supabase
      .from("inventario")
      .delete()
      .like("id", `${inventario.id}::%`);

    const { data, error } = await supabase
      .from("inventario")
      .insert(rows)
      .select();

    if (error) {
      console.error("❌ Error guardando inventario en Supabase:", error);
      return null;
    }

    const agrupado = new Map();
    (data || rows).forEach((row) => {
      const mapped = mapearFilaInventarioDesdeSupabase(row);
      const key = mapped.inventarioId;
      if (!agrupado.has(key)) {
        agrupado.set(key, {
          id: key,
          fecha: mapped.cabecera.fecha,
          sede: mapped.cabecera.sede,
          encargadoId: mapped.cabecera.encargadoId,
          personalCocina: mapped.cabecera.personalCocina,
          novedades: mapped.cabecera.novedades,
          cerrado: mapped.cabecera.cerrado,
          lineas: [],
        });
      }
      agrupado.get(key).lineas.push({
        id: mapped.linea.id,
        producto: mapped.linea.producto,
        existencias: mapped.linea.existencias,
        solicitar: mapped.linea.solicitar,
        cantidad: mapped.linea.cantidad,
        categoria: mapped.linea.categoria,
      });
    });

    return agrupado.get(inventario.id) || inventario;
  } catch (e) {
    console.error("❌ Error en guardarInventarioEnSupabase:", e);
    return null;
  }
}

async function cargarInventariosDeSupabase(empresaId = null) {
  try {
    let query = supabase
      .from("inventario")
      .select("*")
      .order("fecharegistro", { ascending: false })
      .order("updated_at", { ascending: false });
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;

    if (error) {
      console.error("❌ Error cargando inventarios desde Supabase:", error);
      return [];
    }

    const agrupado = new Map();

    (data || []).forEach((row) => {
      const mapped = mapearFilaInventarioDesdeSupabase(row);
      const key = mapped.inventarioId;
      if (!key) return;

      if (!agrupado.has(key)) {
        agrupado.set(key, {
          id: key,
          fecha: mapped.cabecera.fecha,
          sede: mapped.cabecera.sede,
          encargadoId: mapped.cabecera.encargadoId,
          personalCocina: mapped.cabecera.personalCocina,
          novedades: mapped.cabecera.novedades,
          cerrado: mapped.cabecera.cerrado,
          lineas: [],
        });
      } else {
        // Si cualquier línea tiene cerrado = true, marcar el inventario como cerrado
        if (mapped.cabecera.cerrado) {
          agrupado.get(key).cerrado = true;
        }
      }

      agrupado.get(key).lineas.push({
        id: mapped.linea.id,
        producto: mapped.linea.producto,
        existencias: mapped.linea.existencias,
        solicitar: mapped.linea.solicitar,
        cantidad: mapped.linea.cantidad,
        categoria: mapped.linea.categoria,
      });
    });

    return Array.from(agrupado.values()).sort((a, b) =>
      (a.fecha || "") < (b.fecha || "") ? 1 : -1
    );
  } catch (e) {
    console.error("❌ Error en cargarInventariosDeSupabase:", e);
    return [];
  }
}

async function eliminarInventarioDeSupabase(inventarioId) {
  try {
    if (!inventarioId) return false;
    const { error } = await supabase
      .from("inventario")
      .delete()
      .like("id", `${inventarioId}::%`);

    if (error) {
      console.error("❌ Error eliminando inventario en Supabase:", error);
      return false;
    }

    return true;
  } catch (e) {
    console.error("❌ Error en eliminarInventarioDeSupabase:", e);
    return false;
  }
}

function prepararProcesoPagoParaSupabase(tarea) {
  if (!tarea) return null;
  const detallesPlano = JSON.parse(JSON.stringify(tarea));
  const descripcion =
    tarea.comentariosGestion ||
    tarea.motivo ||
    tarea.novedades ||
    tarea.comunicacionTexto ||
    tarea.detalleSolicitud ||
    "";

  return {
    id: tarea.id || uid(),
    nombre: tarea.tipo || tarea.nombre || tarea.id || "Proceso",
    descripcion: descripcion || null,
    estado: tarea.estado || "Pendiente",
    fechacreacion: tarea.fechaCreacion || null,
    fechaejecucion: tarea.fechaLimite || null,
    responsable: tarea.responsableId || tarea.responsable || null,
    detalles: detallesPlano,
    updated_at: new Date().toISOString(),
  };
}

function mapearProcesoPagoDesdeSupabase(row) {
  const detalles =
    typeof row?.detalles === "string"
      ? safelyParseJSON(row.detalles, {})
      : row?.detalles || {};

  const soportes = Array.isArray(detalles?.soportes)
    ? detalles.soportes.map((s) => ({ ...s }))
    : [];
  if (!Array.isArray(detalles.soportes)) {
    detalles.soportes = soportes;
  }

  // Mapear pagosEfectivo para Pago de facturas en efectivo
  const pagosEfectivo = Array.isArray(detalles?.pagosEfectivo)
    ? detalles.pagosEfectivo.map((p) => ({ ...p }))
    : [];
  detalles.pagosEfectivo = pagosEfectivo;

  // Mapear documentosAdjuntos para Afiliación a seguridad social
  const documentosAdjuntos = Array.isArray(detalles?.documentosAdjuntos)
    ? detalles.documentosAdjuntos.map((d) => ({ ...d }))
    : [];
  detalles.documentosAdjuntos = documentosAdjuntos;

  // Mapear empleadosSeleccionados para liquidaciones
  const empleadosSeleccionados = Array.isArray(detalles?.empleadosSeleccionados)
    ? detalles.empleadosSeleccionados.map((e) => ({ ...e }))
    : [];
  detalles.empleadosSeleccionados = empleadosSeleccionados;

  // Mapear empleadosFirma para firma de documentos
  const empleadosFirma = Array.isArray(detalles?.empleadosFirma)
    ? detalles.empleadosFirma.map((e) => ({ ...e }))
    : [];
  detalles.empleadosFirma = empleadosFirma;

  // Mapear soportesFudo para cancelaciones FUDO
  const soportesFudo = Array.isArray(detalles?.soportesFudo)
    ? detalles.soportesFudo.map((s) => ({ ...s }))
    : [];
  detalles.soportesFudo = soportesFudo;

  let adjuntoGestion =
    detalles?.adjuntoGestion && typeof detalles.adjuntoGestion === "object"
      ? { ...detalles.adjuntoGestion }
      : null;
  if (
    adjuntoGestion &&
    typeof adjuntoGestion.url === "string" &&
    (adjuntoGestion.url.startsWith("blob:") || adjuntoGestion.url.startsWith("data:"))
  ) {
    adjuntoGestion = null;
  }
  detalles.adjuntoGestion = adjuntoGestion;

  const base = {
    id: row?.id || detalles.id || uid(),
    fechaCreacion: row?.fechacreacion || detalles.fechaCreacion || "",
    fechaLimite: row?.fechaejecucion || detalles.fechaLimite || "",
    estado: row?.estado || detalles.estado || "Pendiente",
    responsableId: row?.responsable || detalles.responsableId || "",
    tipo: detalles.tipo || row?.nombre || detalles.nombre || "",
    tipoModulo: detalles.tipoModulo || "ADMIN",
  };

  const descripcion = row?.descripcion || detalles.descripcion || "";
  if (!detalles.comentariosGestion && descripcion) {
    detalles.comentariosGestion = descripcion;
  }

  return {
    ...base,
    ...detalles,
    soportes,
    pagosEfectivo,
    documentosAdjuntos,
    empleadosSeleccionados,
    empleadosFirma,
    soportesFudo,
    adjuntoGestion,
  };
}

async function guardarProcesoPagoEnSupabase(tarea, empresaId = null) {
  try {
    const payload = prepararProcesoPagoParaSupabase(tarea);
    if (!payload) {
      console.error("❌ No se pudo preparar el payload para Supabase");
      return null;
    }
    
    // Agregar empresa_id si está disponible y no es "todas"
    if (empresaId && empresaId !== "todas") {
      payload.empresa_id = empresaId;
    }
    
    console.log("📝 Guardando proceso de pago en Supabase:", payload.id, "empresa_id:", empresaId);

    const { data, error } = await supabase
      .from("procesos_pago")
      .upsert(payload, { onConflict: "id" })
      .select()
      .maybeSingle();

    if (error) {
      console.error("❌ Error guardando proceso de pago en Supabase:", error);
      console.error("❌ Payload enviado:", JSON.stringify(payload, null, 2));
      return null;
    }
    
    console.log("✅ Proceso de pago guardado exitosamente:", data?.id);

    return mapearProcesoPagoDesdeSupabase(data || payload);
  } catch (e) {
    console.error("❌ Error en guardarProcesoPagoEnSupabase:", e);
    return null;
  }
}

async function cargarProcesosPagoDeSupabase(empresaId = null) {
  try {
    console.log("🔄 Cargando procesos de pago... empresaId recibido:", empresaId);
    
    let data, error;
    
    if (empresaId && empresaId !== "todas") {
      // 🔹 Filtrar por empresa específica + incluir tareas con empresa_id NULL (antiguas)
      const resultado = await supabase
        .from("procesos_pago")
        .select("*")
        .or(`empresa_id.eq.${empresaId},empresa_id.is.null`)
        .order("created_at", { ascending: false });
      
      data = resultado.data;
      error = resultado.error;
      
      console.log(`📊 Tareas encontradas (empresa ${empresaId} + NULL): ${(data || []).length}`);
    } else {
      // Sin empresa o "todas", cargar todas
      const resultado = await supabase
        .from("procesos_pago")
        .select("*")
        .order("created_at", { ascending: false });
      
      data = resultado.data;
      error = resultado.error;
    }

    if (error) {
      console.error("❌ Error cargando procesos de pago desde Supabase:", error);
      console.error("❌ Detalles del error:", JSON.stringify(error));
      return [];
    }

    console.log(`✅ Cargadas ${(data || []).length} tareas de procesos administrativos para empresa: ${empresaId || 'TODAS'}`);
    
    // Mostrar las primeras tareas para depuración
    if (data && data.length > 0) {
      console.log("📋 Primeras 3 tareas cargadas:");
      data.slice(0, 3).forEach((t, i) => {
        console.log(`   ${i + 1}. ID: ${t.id}, Estado: ${t.estado}, Tipo: ${t.nombre || t.detalles?.tipo}, empresa_id: ${t.empresa_id}`);
      });
    }
    
    return (data || []).map((row) => mapearProcesoPagoDesdeSupabase(row));
  } catch (e) {
    console.error("❌ Error en cargarProcesosPagoDeSupabase:", e);
    console.error("❌ Stack:", e.stack);
    return [];
  }
}

async function eliminarProcesoPagoEnSupabase(id) {
  try {
    if (!id) return false;
    const { error } = await supabase
      .from("procesos_pago")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("❌ Error eliminando proceso de pago en Supabase:", error);
      return false;
    }

    return true;
  } catch (e) {
    console.error("❌ Error en eliminarProcesoPagoEnSupabase:", e);
    return false;
  }
}

async function subirSoporteProcesoPago(file, opciones = {}) {
  if (!file) return null;

  const nombreVisible = (opciones?.nombre || file.name || "Soporte").trim() || "Soporte";
  const carpetaBase = opciones?.carpeta || "procesos-admin";
  const tareaId = opciones?.tareaId ? slugifyPathSegment(opciones.tareaId) : null;
  const extension = file.name && file.name.includes(".")
    ? file.name.substring(file.name.lastIndexOf("."))
    : "";

  const identificadorArchivo = uid();
  const partesRuta = [slugifyPathSegment(carpetaBase)];
  if (tareaId) {
    partesRuta.push(tareaId);
  }
  const ruta = `${partesRuta.join("/")}/${Date.now()}_${identificadorArchivo}_${slugifyPathSegment(nombreVisible)}${extension}`;

  const { error: uploadError } = await supabase.storage
    .from(SUPABASE_BUCKET_DOCS)
    .upload(ruta, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type || undefined,
    });

  if (uploadError) {
    throw new Error(uploadError.message || "No se pudo subir el soporte a Supabase Storage.");
  }

  const { data: urlData, error: urlError } = supabase.storage
    .from(SUPABASE_BUCKET_DOCS)
    .getPublicUrl(ruta);

  if (urlError || !urlData?.publicUrl) {
    throw new Error(urlError?.message || "No se pudo obtener la URL pública del soporte subido.");
  }

  return {
    id: identificadorArchivo,
    nombre: nombreVisible,
    url: urlData.publicUrl,
    tipo: file.type || "",
    size: typeof file.size === "number" ? file.size : null,
    bucket: SUPABASE_BUCKET_DOCS,
    path: ruta,
    creadoEn: new Date().toISOString(),
  };
}

// Guardar liquidación en Supabase
async function guardarLiquidacionEnSupabase(liquidacion, empresaId = null) {
  try {
    // Convertir arrays/objetos a JSON strings para JSONB
    const detalle = liquidacion.detalle ? JSON.stringify(liquidacion.detalle) : null;
    const vacacionesbuckets = liquidacion.vacacionesBuckets ? JSON.stringify(liquidacion.vacacionesBuckets) : null;
    
    // Usar nombres de columnas exactos (minúsculas) como existen en Supabase
    const payload = {
      id: liquidacion.id || uid(),
      empleadoid: liquidacion.empleadoId || null,
      empleadonombre: liquidacion.empleadoNombre || null,
      empleadodoc: liquidacion.empleadoDoc || null,
      tipo: liquidacion.tipo || null,
      valor: Number(liquidacion.total ?? liquidacion.valor ?? 0) || 0,
      fecha: liquidacion.fecha || toISO(new Date()),
      fechainicio: liquidacion.fechaInicio || null,
      fechafin: liquidacion.fechaFin || null,
      periodo: liquidacion.periodo || null,
      diasvacacionesliquidados: liquidacion.diasVacacionesLiquidados || null,
      detalle: detalle,
      vacacionesbuckets: vacacionesbuckets,
    };
    
    // Agregar empresa_id si está disponible y no es "todas"
    if (empresaId && empresaId !== "todas") {
      payload.empresa_id = empresaId;
    }
    
    console.log("📦 Preparando guardado en Supabase:", {
      id: payload.id,
      tipo: payload.tipo,
      valor: payload.valor,
      empleado: payload.empleadonombre,
    });

    console.log("📡 Conectando a tabla 'liquidaciones' en Supabase...");
    
    // Intentar insertar
    const { error: insertError } = await supabase
      .from("liquidaciones")
      .insert([payload]);

    if (insertError) {
      // Si el error es de duplicate key, intentamos actualizar
      if (insertError.code === '23505' || insertError.message.includes('duplicate')) {
        console.log("🔄 ID existe, intentando actualizar...");
        const { error: updateError } = await supabase
          .from("liquidaciones")
          .update(payload)
          .eq("id", payload.id);
        
        if (updateError) {
          console.error("❌ Error actualizando liquidación:", updateError.message);
          return false;
        }
        console.log("✅ Liquidación actualizada en Supabase:", payload.id);
      } else {
        console.error("❌ Error insertando liquidación:", insertError.message);
        return false;
      }
    } else {
      console.log("✅ Liquidación guardada en Supabase:", payload.id);
    }
    
    return true;
  } catch (e) {
    console.error("❌ Error en guardarLiquidacionEnSupabase:", e.message);
    return false;
  }
}

// Eliminar liquidación de Supabase
async function eliminarLiquidacionDeSupabase(liquidacionId) {
  try {
    const { error } = await supabase
      .from("liquidaciones")
      .delete()
      .eq("id", liquidacionId);

    if (error) {
      console.error("❌ Error eliminando liquidación:", error.message);
      return false;
    }
    
    console.log("✅ Liquidación eliminada de Supabase:", liquidacionId);
    return true;
  } catch (e) {
    console.error("❌ Error en eliminarLiquidacionDeSupabase:", e.message);
    return false;
  }
}

// Cargar liquidaciones desde Supabase
async function cargarLiquidacionesDeSupabase(empresaId = null) {
  try {
    let query = supabase
      .from("liquidaciones")
      .select("*")
      .order("fecha", { ascending: false });
    
    // 🔹 Filtrar por empresa si no es "todas" (para multi-empresa)
    // Incluir también los que tienen empresa_id NULL (liquidaciones antiguas)
    if (empresaId && empresaId !== "todas") {
      query = query.or(`empresa_id.eq.${empresaId},empresa_id.is.null`);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("Error cargando liquidaciones:", error);
      return [];
    }
    
    // Mapear desde la estructura real de la tabla (con nombres en minúsculas)
    const liquidaciones = (data || []).map((row) => {
      let detalle = [];
      let vacacionesbuckets = [];
      
      // Parsear detalle si viene como JSON
      if (row.detalle) {
        if (typeof row.detalle === 'string') {
          try {
            detalle = JSON.parse(row.detalle);
          } catch (e) {
            detalle = [];
          }
        } else if (Array.isArray(row.detalle)) {
          detalle = row.detalle;
        }
      }
      
      // Parsear vacacionesbuckets si viene como JSON
      if (row.vacacionesbuckets) {
        if (typeof row.vacacionesbuckets === 'string') {
          try {
            vacacionesbuckets = JSON.parse(row.vacacionesbuckets);
          } catch (e) {
            vacacionesbuckets = [];
          }
        } else if (Array.isArray(row.vacacionesbuckets)) {
          vacacionesbuckets = row.vacacionesbuckets;
        }
      }
      
      return {
        id: row.id,
        tipo: row.tipo || "",
        empleadoId: row.empleadoid || "",
        empleadoNombre: row.empleadonombre || "",
        empleadoDoc: row.empleadodoc || "",
        valor: row.valor || 0,
        total: row.valor || 0,
        fecha: row.fecha || "",
        fechaInicio: row.fechainicio || null,
        fechaFin: row.fechafin || null,
        periodo: row.periodo || "",
        diasVacacionesLiquidados: row.diasvacacionesliquidados || null,
        detalle: detalle,
        vacacionesBuckets: vacacionesbuckets,
        creadoEn: row.created_at || null,
        actualizadoEn: row.updated_at || null,
      };
    });
    
    console.log(`✅ ${liquidaciones.length} liquidaciones cargadas desde Supabase`);
    return liquidaciones;
  } catch (e) {
    console.error("Error en cargarLiquidacionesDeSupabase:", e);
    return [];
  }
}

// Mapear vacación desde Supabase a formato interno
function mapVacacionSupabase(row) {
  if (!row) return null;

  return {
    id: row.id,
    empleadoId: row.empleadoid || "",
    inicio: row.fechainicio || "",
    fin: row.fechafin || "",
    dias: row.diassolicitados ? Number(row.diassolicitados) : 0,
    estado: row.estado || "Pendiente",
    aprobadoPor: row.aprobadopor || "",
    fechaAprobacion: row.fechaaprobacion || "",
    creadoEn: row.created_at || null,
    actualizadoEn: row.updated_at || null,
  };
}

// Preparar vacación para guardar en Supabase
function prepararPayloadVacacion(v) {
  if (!v) return null;
  const orNull = (value) => {
    if (value === "" || typeof value === "undefined" || value === null) return null;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed === "" ? null : trimmed;
    }
    return value;
  };

  return {
    id: v.id || uid(),
    empleadoid: orNull(v.empleadoId),
    fechainicio: orNull(v.inicio),
    fechafin: orNull(v.fin),
    diassolicitados: v.dias || 0,
    estado: v.estado || "Pendiente",
    aprobadopor: orNull(v.aprobadoPor),
    fechaaprobacion: orNull(v.fechaAprobacion),
    created_at: v.creadoEn || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
}

// Guardar vacación en Supabase
async function guardarVacacionesEnSupabase(vacacion, empresaId = null) {
  try {
    console.log("📝 Vacación a guardar (original):", JSON.stringify(vacacion, null, 2));
    
    const payload = prepararPayloadVacacion(vacacion);
    if (!payload) {
      console.error("❌ No se pudo preparar el payload de la vacación");
      return null;
    }
    
    // Agregar empresa_id si está disponible
    if (empresaId) {
      payload.empresa_id = empresaId;
    }

    console.log("📤 Payload preparado para Supabase:", JSON.stringify(payload, null, 2));

    let result;
    if (vacacion.id) {
      console.log("🔄 Intentando UPDATE del registro...");
      // Intentar UPDATE
      result = await supabase
        .from("vacaciones")
        .update(payload)
        .eq("id", vacacion.id)
        .select();

      const { data: updateData, error: updateError } = result;

      // Si UPDATE no encontró el registro, hacer INSERT
      if (updateError) {
        console.log("⚠️  UPDATE falló, intentando INSERT...");
        result = await supabase
          .from("vacaciones")
          .insert([payload])
          .select();
      } else if (!updateData || updateData.length === 0) {
        console.log("⚠️  UPDATE no afectó ningún registro, intentando INSERT...");
        result = await supabase
          .from("vacaciones")
          .insert([payload])
          .select();
      }
    } else {
      console.log("✨ Haciendo INSERT de nuevo registro...");
      // INSERT
      result = await supabase
        .from("vacaciones")
        .insert([payload])
        .select();
    }

    const { data, error } = result;

    if (error) {
      console.error("❌ Error guardando vacación en Supabase:");
      console.error(JSON.stringify(error, null, 2));
      console.error("Código de error:", error.code);
      console.error("Mensaje:", error.message);
      return null;
    }

    console.log("✅ Respuesta de Supabase:", JSON.stringify(data, null, 2));
    const registro = Array.isArray(data) ? data[0] : data;
    const mapeada = mapVacacionSupabase(registro || payload);
    console.log("✅ Vacación mapeada:", JSON.stringify(mapeada, null, 2));
    return mapeada;
  } catch (e) {
    console.error("❌ Error en guardarVacacionesEnSupabase:", e.message);
    console.error("Stack:", e.stack);
    return null;
  }
}

// Cargar vacaciones desde Supabase
async function cargarVacacionesDeSupabase(empresaId = null) {
  try {
    console.log("🔍 Cargando vacaciones desde Supabase...");
    let query = supabase
      .from("vacaciones")
      .select("id, empleadoid, fechainicio, fechafin, diassolicitados, estado, aprobadopor, fechaaprobacion, created_at, updated_at, empresa_id")
      .order("created_at", { ascending: false });
    
    // Si se especifica empresaId, filtrar por empresa
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;

    if (error) {
      console.error("❌ Error cargando vacaciones:");
      console.error(JSON.stringify(error, null, 2));
      return [];
    }

    console.log("📊 Datos crudos de Supabase:", JSON.stringify(data, null, 2));
    const mapeadas = (data || []).map(mapVacacionSupabase).filter(Boolean);
    console.log("✅ Vacaciones mapeadas:", JSON.stringify(mapeadas, null, 2));
    return mapeadas;
  } catch (e) {
    console.error("❌ Error en cargarVacacionesDeSupabase:", e.message);
    console.error(e.stack);
    return [];
  }
}

/* ====================== PRESTAMOS SUPABASE ====================== */
function mapPrestamoSupabase(row) {
  if (!row) return null;

  // Parsear plan si viene como string JSON
  let plan = [];
  if (row.plan) {
    if (typeof row.plan === "string") {
      try {
        plan = JSON.parse(row.plan);
      } catch (e) {
        console.warn("Error al parsear plan de cuotas:", e);
        plan = [];
      }
    } else if (Array.isArray(row.plan)) {
      plan = row.plan;
    }
  }

  return {
    id: row.id,
    empleadoId: row.empleadoid || "",
    valor: row.valor ? Number(row.valor) : 0,
    monto: row.valor ? Number(row.valor) : 0,
    fecha: row.fechasolicitud || "",
    fechaSolicitud: row.fechasolicitud || "",
    fechaAprobacion: row.fechaaprobacion || "",
    formaPago: row.forma_pago || "Quincenal",
    tipo: row.tipo || "Préstamo",
    cuotas: row.cuotas ? Number(row.cuotas) : 12,
    cuotasRestantes: row.cuotasrestantes ? Number(row.cuotasrestantes) : (row.cuotas ? Number(row.cuotas) : 12),
    plan: plan,
    saldo: row.saldo ? Number(row.saldo) : (row.valor ? Number(row.valor) : 0),
    estado: row.estado || "Pendiente",
    razon: row.razon || "",
    motivo: row.motivo || "",
    descFecha: row.desc_fecha || "",
    descSede: row.desc_sede || "",
    motivoDanio: row.motivo_danio || "",
    creadoEn: row.created_at || null,
    actualizadoEn: row.updated_at || null,
  };
}

// Preparar préstamo para guardar en Supabase
function prepararPayloadPrestamo(p) {
  if (!p) return null;
  const orNull = (value) => {
    if (value === "" || typeof value === "undefined" || value === null) return null;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed === "" ? null : trimmed;
    }
    return value;
  };

  return {
    id: p.id || uid(),
    empleadoid: orNull(p.empleadoId),
    valor: p.valor || p.monto || 0,
    cuotas: p.cuotas || 12,
    cuotasrestantes: p.cuotasRestantes || (p.cuotas || 12),
    saldo: p.saldo || p.valor || p.monto || 0,
    fechasolicitud: orNull(p.fecha || p.fechaSolicitud),
    fechaaprobacion: orNull(p.fechaAprobacion),
    estado: p.estado || "Pendiente",
    razon: orNull(p.razon),
    motivo: orNull(p.motivo),
    // Campos adicionales que faltaban
    tipo: orNull(p.tipo) || "Préstamo",
    forma_pago: orNull(p.formaPago) || "Quincenal",
    desc_fecha: orNull(p.descFecha),
    desc_sede: orNull(p.descSede),
    motivo_danio: orNull(p.motivoDanio),
    plan: p.plan ? JSON.stringify(p.plan) : "[]",
    created_at: p.creadoEn || new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
}

// Guardar préstamo en Supabase
async function guardarPrestamoEnSupabase(prestamo, empresaId = null) {
  try {
    console.log("📝 Préstamo a guardar (original):", JSON.stringify(prestamo, null, 2));
    console.log("📝 Motivo del préstamo:", prestamo.motivo || "(sin motivo)");
    
    const payload = prepararPayloadPrestamo(prestamo);
    if (!payload) {
      console.error("❌ No se pudo preparar el payload del préstamo");
      return null;
    }
    
    // Agregar empresa_id si está disponible
    if (empresaId) {
      payload.empresa_id = empresaId;
    }

    console.log("📤 Payload preparado para Supabase:", JSON.stringify(payload, null, 2));
    console.log("📤 Motivo en payload:", payload.motivo || "(null)");

    let result;
    if (prestamo.id) {
      console.log("🔄 Intentando UPDATE del registro...");
      // Intentar UPDATE
      result = await supabase
        .from("prestamos")
        .update(payload)
        .eq("id", prestamo.id)
        .select();

      const { data: updateData, error: updateError } = result;

      // Si UPDATE no encontró el registro, hacer INSERT
      if (updateError) {
        console.log("⚠️  UPDATE falló, intentando INSERT...");
        result = await supabase
          .from("prestamos")
          .insert([payload])
          .select();
      } else if (!updateData || updateData.length === 0) {
        console.log("⚠️  UPDATE no afectó ningún registro, intentando INSERT...");
        result = await supabase
          .from("prestamos")
          .insert([payload])
          .select();
      }
    } else {
      console.log("✨ Haciendo INSERT de nuevo registro...");
      // INSERT
      result = await supabase
        .from("prestamos")
        .insert([payload])
        .select();
    }

    const { data, error } = result;

    if (error) {
      console.error("❌ Error guardando préstamo en Supabase:");
      console.error(JSON.stringify(error, null, 2));
      console.error("Código de error:", error.code);
      console.error("Mensaje:", error.message);
      return null;
    }

    console.log("✅ Respuesta de Supabase:", JSON.stringify(data, null, 2));
    const registro = Array.isArray(data) ? data[0] : data;
    const mapeado = mapPrestamoSupabase(registro || payload);
    console.log("✅ Préstamo mapeado:", JSON.stringify(mapeado, null, 2));
    return mapeado;
  } catch (e) {
    console.error("❌ Error en guardarPrestamoEnSupabase:", e.message);
    console.error("Stack:", e.stack);
    return null;
  }
}

// ============================================================
// ================== BITÁCORA DE PRÉSTAMOS ==================
// ============================================================

// Guardar en bitácora cuando se elimina un préstamo
async function guardarBitacoraPrestamoEliminado(prestamo, usuarioId, usuarioNombre, motivo) {
  try {
    const payload = {
      id: uid(),
      prestamo_id: prestamo.id,
      empleado_id: prestamo.empleadoId,
      empleado_nombre: prestamo.empleado?.nombre || "Desconocido",
      valor: prestamo.valor || 0,
      cuotas: prestamo.cuotas || 0,
      cuotas_descontadas: (prestamo.plan || []).filter(c => c.noDescontable).length,
      saldo: prestamo.saldo || prestamo.valor || 0,
      fecha_prestamo: prestamo.fecha || prestamo.fechaSolicitud || null,
      tipo_prestamo: prestamo.tipo || "Préstamo",
      estado_al_eliminar: prestamo.estado || "Pendiente",
      plan_cuotas: JSON.stringify(prestamo.plan || []),
      usuario_eliminador_id: usuarioId,
      usuario_eliminador_nombre: usuarioNombre,
      motivo_eliminacion: motivo,
      fecha_eliminacion: new Date().toISOString(),
      empresa_id: prestamo.empresa_id || null
    };

    console.log("📝 Guardando en bitácora de préstamos eliminados:", payload);

    const { data, error } = await supabase
      .from("bitacora_prestamos_eliminados")
      .insert([payload])
      .select();

    if (error) {
      console.error("❌ Error guardando bitácora:", error);
      // Intentar crear la tabla si no existe
      if (error.code === "42P01") {
        console.log("⚠️ La tabla bitacora_prestamos_eliminados no existe. Guardando en localStorage.");
        // Guardar en localStorage como respaldo
        const bitacoraLocal = JSON.parse(localStorage.getItem("bitacora_prestamos_eliminados") || "[]");
        bitacoraLocal.push(payload);
        localStorage.setItem("bitacora_prestamos_eliminados", JSON.stringify(bitacoraLocal));
      }
      return null;
    }

    console.log("✅ Bitácora guardada correctamente");
    return data;
  } catch (e) {
    console.error("❌ Error en guardarBitacoraPrestamoEliminado:", e);
    return null;
  }
}

// Eliminar préstamo de Supabase
async function eliminarPrestamoDeSupabase(prestamoId) {
  try {
    console.log("🗑️ Eliminando préstamo:", prestamoId);
    
    const { error } = await supabase
      .from("prestamos")
      .delete()
      .eq("id", prestamoId);

    if (error) {
      console.error("❌ Error eliminando préstamo:", error);
      return false;
    }

    console.log("✅ Préstamo eliminado correctamente");
    return true;
  } catch (e) {
    console.error("❌ Error en eliminarPrestamoDeSupabase:", e);
    return false;
  }
}

// Cargar bitácora de novedades a crédito (eliminaciones y abonos)
async function cargarBitacoraPrestamosEliminados() {
  try {
    console.log("📋 Cargando bitácora de novedades a crédito...");
    
    const { data, error } = await supabase
      .from("bitacora_prestamos_eliminados")
      .select("*")
      .order("fecha_eliminacion", { ascending: false });

    if (error) {
      console.error("❌ Error cargando bitácora:", error);
      // Intentar cargar desde localStorage como respaldo
      const bitacoraLocal = JSON.parse(localStorage.getItem("bitacora_prestamos_eliminados") || "[]");
      console.log("📋 Cargando desde localStorage:", bitacoraLocal.length, "registros");
      return bitacoraLocal;
    }

    console.log("✅ Bitácora cargada:", data?.length || 0, "registros");
    return data || [];
  } catch (e) {
    console.error("❌ Error en cargarBitacoraPrestamosEliminados:", e);
    // Intentar cargar desde localStorage como respaldo
    const bitacoraLocal = JSON.parse(localStorage.getItem("bitacora_prestamos_eliminados") || "[]");
    return bitacoraLocal;
  }
}

// Cargar préstamos desde Supabase
async function cargarPrestamosDeSupabase(empresaId = null) {
  try {
    console.log("🔍 Cargando préstamos desde Supabase... empresaId:", empresaId);
    let query = supabase
      .from("prestamos")
      .select("id, empleadoid, valor, cuotas, cuotasrestantes, saldo, fechasolicitud, fechaaprobacion, estado, razon, motivo, tipo, forma_pago, desc_fecha, desc_sede, motivo_danio, plan, created_at, updated_at, empresa_id")
      .order("created_at", { ascending: false });
    
    // NO filtramos por empresa_id aquí porque muchos préstamos no tienen empresa_id asignado
    // El filtro por empresa se hará en el frontend usando el empleadoId
    // Los préstamos se asociarán solo con empleados de la empresa actual
    
    const { data, error } = await query;

    if (error) {
      console.error("❌ Error cargando préstamos:");
      console.error(JSON.stringify(error, null, 2));
      return [];
    }

    console.log("📊 Préstamos cargados:", data?.length || 0);
    // Verificar si hay motivos en los datos crudos
    const conMotivo = (data || []).filter(p => p.motivo);
    console.log("📝 Préstamos con motivo:", conMotivo.length);
    if (conMotivo.length > 0) {
      console.log("📝 Ejemplo motivo:", conMotivo[0].motivo);
    }
    
    const mapeados = (data || []).map(mapPrestamoSupabase).filter(Boolean);
    console.log("✅ Préstamos mapeados:", mapeados.length);
    return mapeados;
  } catch (e) {
    console.error("❌ Error en cargarPrestamosDeSupabase:", e.message);
    console.error(e.stack);
    return [];
  }
}

// ============================================================
// ================== CALENDARIO / EVENTOS ====================
// ============================================================

async function cargarEventosCalendarioDeSupabase(empresaId = null) {
  try {
    console.log("📅 Cargando eventos del calendario - empresaId:", empresaId);
    
    let query = supabase
      .from("calendario_eventos")
      .select("*")
      .order("fecha", { ascending: true });
    
    // Si hay empresaId, filtrar por esa empresa
    // Si no hay empresaId, cargar todos (para super admin o cuando no hay filtro)
    if (empresaId) {
      // Cargar eventos de la empresa específica O eventos sin empresa asignada
      query = query.or(`empresa_id.eq.${empresaId},empresa_id.is.null`);
    }
    
    const { data, error } = await query;
    if (error) {
      console.error("❌ Error cargando eventos:", error);
      return [];
    }
    
    console.log("📅 Eventos cargados desde Supabase (raw):", data?.length || 0);
    data?.forEach((e, i) => {
      console.log(`📅 Raw evento ${i+1}: id=${e.id} titulo="${e.titulo?.substring(0, 25)}" tipo=${e.tipo} creador_id=${e.creador_id} empresa_id=${e.empresa_id}`);
    });
    
    return (data || []).map(e => {
      // Parsear invitados
      const invitados = typeof e.invitados === 'string' ? JSON.parse(e.invitados) : (e.invitados || []);
      
      // Extraer metadatos adicionales del campo observaciones si existen
      let metadatos = {};
      let observacionesLimpias = e.observaciones || "";
      if (e.observaciones && e.observaciones.includes("__META__:")) {
        const metaMatch = e.observaciones.match(/__META__:(.+?)(?:__ENDMETA__|$)/);
        if (metaMatch) {
          try {
            metadatos = JSON.parse(metaMatch[1]);
          } catch (err) {}
        }
        // Limpiar los metadatos de las observaciones para mostrar en UI
        observacionesLimpias = e.observaciones.replace(/__META__:.+?(?:__ENDMETA__|$)/g, "").trim();
      }
      
      // Reconstruir el tipo original desde metadatos si existe
      const tipoReal = metadatos.tipo_original || e.tipo;
      
      return {
        ...e,
        tipo: tipoReal, // Usar el tipo original guardado en metadatos
        invitados,
        observaciones: observacionesLimpias,
        es_vencimiento: e.es_vencimiento || metadatos.es_vencimiento || false,
        empleado_asignado_id: e.empleado_asignado_id || metadatos.empleado_asignado_id || null
      };
    });
  } catch (e) {
    console.error("❌ Error en cargarEventosCalendarioDeSupabase:", e);
    return [];
  }
}

async function guardarEventoCalendarioEnSupabase(evento, empresaId = null) {
  try {
    console.log("📅 Guardando evento de calendario:", evento.titulo, "tipo:", evento.tipo, "empresaId:", empresaId);
    
    // Tipos válidos que probablemente acepta la BD (basado en el selector de UI)
    const tiposValidos = ["reunion", "visita", "evento"];
    const tipoOriginal = evento.tipo || "evento";
    const tipoParaBD = tiposValidos.includes(tipoOriginal) ? tipoOriginal : "evento";
    
    // Crear metadatos para campos que pueden no existir en la BD
    const metadatos = {
      es_vencimiento: evento.es_vencimiento || false,
      empleado_asignado_id: evento.empleado_asignado_id || null,
      tipo_original: tipoOriginal // Guardar el tipo original en metadatos
    };
    
    // Añadir metadatos al campo observaciones de forma compatible
    let observacionesConMeta = evento.observaciones || "";
    // Eliminar metadatos anteriores si existen
    observacionesConMeta = observacionesConMeta.replace(/__META__:.+?(?:__ENDMETA__|$)/g, "").trim();
    // Agregar nuevos metadatos
    observacionesConMeta = `${observacionesConMeta} __META__:${JSON.stringify(metadatos)}__ENDMETA__`.trim();
    
    // Determinar empresa_id: usar el pasado, o el del evento existente, o intentar obtener del usuario
    const empresaIdFinal = empresaId || evento.empresa_id || null;
    
    const payload = {
      id: evento.id || uid(),
      titulo: evento.titulo || "",
      tipo: tipoParaBD, // Usar tipo válido para la BD
      fecha: evento.fecha,
      hora_inicio: evento.hora_inicio || null,
      hora_fin: evento.hora_fin || null,
      sede_id: evento.sede_id || null,
      sede_nombre: evento.sede_nombre || null,
      descripcion: evento.descripcion || null,
      observaciones: observacionesConMeta || null,
      creador_id: evento.creador_id || null,
      creador_nombre: evento.creador_nombre || null,
      invitados: JSON.stringify(evento.invitados || []),
      estado: evento.estado || "pendiente",
      empresa_id: empresaIdFinal, // Siempre incluir empresa_id (puede ser null)
      updated_at: new Date().toISOString()
    };
    
    console.log("📅 empresa_id final para guardar:", empresaIdFinal);

    console.log("📅 Payload a enviar a Supabase:", JSON.stringify(payload, null, 2));

    const { data, error } = await supabase
      .from("calendario_eventos")
      .upsert(payload, { onConflict: "id" })
      .select()
      .maybeSingle();

    if (error) {
      console.error("❌ Error guardando evento:", error);
      console.error("❌ Código de error:", error.code);
      console.error("❌ Mensaje de error:", error.message);
      console.error("❌ Detalles del error:", error.details);
      console.error("❌ Hint del error:", error.hint);
      return null;
    }
    
    console.log("✅ Evento guardado exitosamente:", data?.id, data?.titulo);
    
    // Crear notificaciones para invitados
    if (evento.invitados && evento.invitados.length > 0) {
      const notificaciones = evento.invitados.map(inv => ({
        id: uid(),
        evento_id: payload.id,
        empleado_id: inv.id,
        leida: false,
        empresa_id: empresaId
      }));
      
      await supabase.from("calendario_notificaciones").upsert(notificaciones, { onConflict: "id" });
    }
    
    return data ? { ...data, invitados: JSON.parse(data.invitados || '[]') } : null;
  } catch (e) {
    console.error("❌ Error en guardarEventoCalendarioEnSupabase:", e);
    return null;
  }
}

async function eliminarEventoCalendarioEnSupabase(id) {
  try {
    const { error } = await supabase.from("calendario_eventos").delete().eq("id", id);
    return !error;
  } catch (e) {
    console.error("❌ Error eliminando evento:", e);
    return false;
  }
}

async function cargarNotificacionesCalendarioDeSupabase(empleadoId, empresaId = null) {
  try {
    let query = supabase
      .from("calendario_notificaciones")
      .select("*, calendario_eventos(*)")
      .eq("empleado_id", empleadoId)
      .eq("leida", false);
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;
    if (error) return [];
    return data || [];
  } catch (e) {
    return [];
  }
}

async function marcarNotificacionLeidaEnSupabase(notifId) {
  try {
    await supabase.from("calendario_notificaciones").update({ leida: true }).eq("id", notifId);
    return true;
  } catch (e) {
    return false;
  }
}

// ============================================================
// ================== DIRECTORIO PROVEEDORES ==================
// ============================================================

async function cargarProveedoresDeSupabase(empresaId = null) {
  try {
    let query = supabase
      .from("directorio_proveedores")
      .select("*")
      .eq("activo", true)
      .order("nombre", { ascending: true });
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;
    if (error) {
      console.error("❌ Error cargando proveedores:", error);
      return [];
    }
    return data || [];
  } catch (e) {
    console.error("❌ Error en cargarProveedoresDeSupabase:", e);
    return [];
  }
}

async function guardarProveedorEnSupabase(proveedor, empresaId = null) {
  try {
    const payload = {
      id: proveedor.id || uid(),
      nombre: proveedor.nombre || "",
      telefono: proveedor.telefono || null,
      telefono2: proveedor.telefono2 || null,
      email: proveedor.email || null,
      direccion: proveedor.direccion || null,
      productos_servicios: proveedor.productos_servicios || null,
      forma_pago: proveedor.forma_pago || null,
      banco: proveedor.banco || null,
      tipo_cuenta: proveedor.tipo_cuenta || null,
      numero_cuenta: proveedor.numero_cuenta || null,
      nit: proveedor.nit || null,
      contacto_nombre: proveedor.contacto_nombre || null,
      contacto_telefono: proveedor.contacto_telefono || null,
      observaciones: proveedor.observaciones || null,
      activo: proveedor.activo !== false,
      updated_at: new Date().toISOString()
    };
    
    if (empresaId) {
      payload.empresa_id = empresaId;
    }

    const { data, error } = await supabase
      .from("directorio_proveedores")
      .upsert(payload, { onConflict: "id" })
      .select()
      .maybeSingle();

    if (error) {
      console.error("❌ Error guardando proveedor:", error);
      return null;
    }
    return data;
  } catch (e) {
    console.error("❌ Error en guardarProveedorEnSupabase:", e);
    return null;
  }
}

async function eliminarProveedorEnSupabase(id) {
  try {
    const { error } = await supabase
      .from("directorio_proveedores")
      .update({ activo: false, updated_at: new Date().toISOString() })
      .eq("id", id);
    return !error;
  } catch (e) {
    return false;
  }
}

// ============================================================
// ================== DIRECTORIO INFO SEDES ===================
// ============================================================

async function cargarInfoSedesDeSupabase(empresaId = null) {
  try {
    let query = supabase
      .from("directorio_sedes_info")
      .select("*")
      .order("sede_nombre", { ascending: true });
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;
    if (error) {
      console.error("❌ Error cargando info sedes:", error);
      return [];
    }
    return (data || []).map(s => {
      const clavesRaw = typeof s.claves === 'string' ? JSON.parse(s.claves) : (s.claves || []);
      // Separar claves reales de bancos extra guardados
      const clavesReales = clavesRaw.filter(c => !c._esBanco);
      const bancosExtra = clavesRaw.filter(c => c._esBanco).map(c => ({
        id: c.id,
        banco: c.banco,
        tipo_cuenta: c.tipo_cuenta,
        numero_cuenta: c.numero_cuenta
      }));
      
      // Reconstruir array de bancos
      const bancosReconstruidos = [];
      if (s.banco) {
        bancosReconstruidos.push({
          id: 'banco_principal',
          banco: s.banco,
          tipo_cuenta: s.tipo_cuenta || '',
          numero_cuenta: s.numero_cuenta || ''
        });
      }
      bancosReconstruidos.push(...bancosExtra);
      
      return {
        ...s,
        claves: clavesReales,
        datos_importantes: typeof s.datos_importantes === 'string' ? JSON.parse(s.datos_importantes) : (s.datos_importantes || []),
        bancos: bancosReconstruidos
      };
    });
  } catch (e) {
    console.error("❌ Error en cargarInfoSedesDeSupabase:", e);
    return [];
  }
}

async function guardarInfoSedeEnSupabase(info, empresaId = null) {
  try {
    // Si hay múltiples bancos, guardarlos en el campo banco como JSON
    // Para compatibilidad con la BD existente que no tiene columna "bancos"
    let bancoData = info.banco || null;
    if (info.bancos && info.bancos.length > 0) {
      // Guardar el primer banco en los campos tradicionales para compatibilidad
      bancoData = info.bancos[0].banco || null;
    }
    
    const payload = {
      id: info.id || uid(),
      sede_id: info.sede_id || null,
      sede_nombre: info.sede_nombre || "",
      nit: info.nit || null,
      representante_legal: info.representante_legal || null,
      correo_notificaciones: info.correo_notificaciones || null,
      telefono_local: info.telefono_local || null,
      telefono_admin: info.telefono_admin || null,
      direccion: info.direccion || null,
      banco: bancoData,
      tipo_cuenta: info.bancos && info.bancos.length > 0 ? info.bancos[0].tipo_cuenta : (info.tipo_cuenta || null),
      numero_cuenta: info.bancos && info.bancos.length > 0 ? info.bancos[0].numero_cuenta : (info.numero_cuenta || null),
      claves: JSON.stringify([
        ...(info.claves || []),
        // Guardar bancos adicionales en claves con prefijo especial
        ...(info.bancos && info.bancos.length > 1 ? info.bancos.slice(1).map((b, i) => ({
          id: b.id || `banco_extra_${i}`,
          nombre: `🏦 Banco ${i + 2}: ${b.banco}`,
          valor: `${b.tipo_cuenta} - ${b.numero_cuenta}`,
          _esBanco: true,
          banco: b.banco,
          tipo_cuenta: b.tipo_cuenta,
          numero_cuenta: b.numero_cuenta
        })) : [])
      ]),
      datos_importantes: JSON.stringify(info.datos_importantes || []),
      observaciones: info.observaciones || null,
      updated_at: new Date().toISOString()
    };
    
    if (empresaId) {
      payload.empresa_id = empresaId;
    }

    const { data, error } = await supabase
      .from("directorio_sedes_info")
      .upsert(payload, { onConflict: "id" })
      .select()
      .maybeSingle();

    if (error) {
      console.error("❌ Error guardando info sede:", error);
      return null;
    }
    
    if (data) {
      // Reconstruir bancos desde los datos guardados
      const clavesGuardadas = JSON.parse(data.claves || '[]');
      const clavesReales = clavesGuardadas.filter(c => !c._esBanco);
      const bancosExtra = clavesGuardadas.filter(c => c._esBanco).map(c => ({
        id: c.id,
        banco: c.banco,
        tipo_cuenta: c.tipo_cuenta,
        numero_cuenta: c.numero_cuenta
      }));
      
      const bancosReconstruidos = [];
      if (data.banco) {
        bancosReconstruidos.push({
          id: 'banco_principal',
          banco: data.banco,
          tipo_cuenta: data.tipo_cuenta || '',
          numero_cuenta: data.numero_cuenta || ''
        });
      }
      bancosReconstruidos.push(...bancosExtra);
      
      return {
        ...data,
        claves: clavesReales,
        datos_importantes: JSON.parse(data.datos_importantes || '[]'),
        bancos: bancosReconstruidos
      };
    }
    return null;
  } catch (e) {
    console.error("❌ Error en guardarInfoSedeEnSupabase:", e);
    return null;
  }
}

// ============================================================
// ================== PLANES DE TRABAJO =======================
// ============================================================

const CHECKLIST_TAREAS_DEFAULT = [
  { id: "rev_comedor", tarea: "Revisión del Comedor", completado: false, observacion: "" },
  { id: "rev_cocina", tarea: "Revisión de Cocina", completado: false, observacion: "" },
  { id: "rev_neveras", tarea: "Revisión de Neveras", completado: false, observacion: "" },
  { id: "rev_caja", tarea: "Revisión de Caja", completado: false, observacion: "" },
  { id: "rev_exterior", tarea: "Revisión Exterior", completado: false, observacion: "" },
  { id: "rev_arqueo_fudo", tarea: "Revisión de Arqueo FUDO", completado: false, observacion: "" },
  { id: "rev_personal", tarea: "Revisión de Personal", completado: false, observacion: "" },
  { id: "rev_areas", tarea: "Revisión de Funcionamiento de Áreas", completado: false, observacion: "" },
  { id: "rev_whatsapp", tarea: "Revisión de WhatsApp", completado: false, observacion: "" },
  { id: "rev_deuda_locales", tarea: "Revisión de Deuda Otros Locales", completado: false, observacion: "" },
  { id: "rev_bodega", tarea: "Revisión de Bodega", completado: false, observacion: "" },
  { id: "rev_inventario", tarea: "Revisión de Inventario", completado: false, observacion: "" },
  { id: "rev_facturas", tarea: "Revisión de Facturas", completado: false, observacion: "" }
];

async function cargarPlanesTrabajoDeSupabase(empresaId = null) {
  try {
    let query = supabase
      .from("planes_trabajo")
      .select("*")
      .order("created_at", { ascending: false });
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;
    if (error) {
      console.error("❌ Error cargando planes de trabajo:", error);
      return [];
    }
    return (data || []).map(p => {
      // Parsear tareas - puede estar en 'tareas' o 'checklist'
      let tareasParseadas = [];
      if (p.tareas) {
        tareasParseadas = typeof p.tareas === 'string' ? JSON.parse(p.tareas) : p.tareas;
      } else if (p.checklist) {
        tareasParseadas = typeof p.checklist === 'string' ? JSON.parse(p.checklist) : p.checklist;
      }
      
      return {
        ...p,
        tareas: tareasParseadas,
        checklist: tareasParseadas,
        // Mapear campos legacy a nuevos campos si no existen
        empleado_asignado_id: p.empleado_asignado_id || p.responsable_id || null,
        empleado_asignado_nombre: p.empleado_asignado_nombre || p.responsable_nombre || null,
        fecha_limite: p.fecha_limite || p.fecha || null,
        cerrado: p.cerrado || false,
        porcentaje_completado: p.porcentaje_completado || 0
      };
    });
  } catch (e) {
    console.error("❌ Error en cargarPlanesTrabajoDeSupabase:", e);
    return [];
  }
}

async function guardarPlanTrabajoEnSupabase(plan, empresaId = null) {
  try {
    // Preparar las tareas como string JSON si no lo son
    let tareasJSON = plan.tareas;
    if (typeof tareasJSON !== 'string') {
      tareasJSON = JSON.stringify(tareasJSON || []);
    }
    
    const payload = {
      id: plan.id || uid(),
      nombre: plan.nombre || "",
      descripcion: plan.descripcion || "",
      fecha: plan.fecha || plan.fecha_limite || new Date().toISOString().split('T')[0],
      fecha_limite: plan.fecha_limite || plan.fecha || null,
      sede_id: plan.sede_id || null,
      sede_nombre: plan.sede_nombre || null,
      // Campos legacy para compatibilidad
      responsable_id: plan.empleado_asignado_id || plan.responsable_id || null,
      responsable_nombre: plan.empleado_asignado_nombre || plan.responsable_nombre || null,
      // Nuevos campos para asignación
      empleado_asignado_id: plan.empleado_asignado_id || plan.responsable_id || null,
      empleado_asignado_nombre: plan.empleado_asignado_nombre || plan.responsable_nombre || null,
      // Tareas - usar campo 'tareas' o 'checklist' según lo que exista
      tareas: tareasJSON,
      checklist: tareasJSON, // Mantener compatibilidad con campo anterior
      estado: plan.estado || "pendiente",
      porcentaje_completado: plan.porcentaje_completado || 0,
      observaciones_generales: plan.observaciones_generales || null,
      cerrado: plan.cerrado || false,
      cerrado_por: plan.cerrado_por || null,
      cerrado_por_nombre: plan.cerrado_por_nombre || null,
      fecha_cierre: plan.fecha_cierre || null,
      // Campos para devolución de planes
      observaciones_gerencia: plan.observaciones_gerencia || null,
      devuelto_por: plan.devuelto_por || null,
      devuelto_por_nombre: plan.devuelto_por_nombre || null,
      fecha_devolucion: plan.fecha_devolucion || null,
      created_at: plan.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    if (empresaId) {
      payload.empresa_id = empresaId;
    }

    console.log("📦 Payload a guardar en planes_trabajo:", payload);

    // Intentar con todos los campos primero
    let { data, error } = await supabase
      .from("planes_trabajo")
      .upsert(payload, { onConflict: "id" })
      .select()
      .maybeSingle();

    // Si hay error, intentar con campos mínimos
    if (error) {
      console.warn("⚠️ Error con todos los campos, intentando con campos básicos...", error.message);
      
      // Payload mínimo con solo campos que probablemente existan
      const payloadMinimo = {
        id: payload.id,
        nombre: payload.nombre,
        descripcion: payload.descripcion,
        fecha: payload.fecha,
        sede_id: payload.sede_id,
        sede_nombre: payload.sede_nombre,
        responsable_id: payload.responsable_id,
        responsable_nombre: payload.responsable_nombre,
        checklist: payload.checklist,
        estado: payload.estado,
        observaciones_generales: payload.observaciones_generales,
        observaciones_gerencia: payload.observaciones_gerencia,
        devuelto_por: payload.devuelto_por,
        devuelto_por_nombre: payload.devuelto_por_nombre,
        fecha_devolucion: payload.fecha_devolucion,
        updated_at: payload.updated_at
      };
      
      if (empresaId) {
        payloadMinimo.empresa_id = empresaId;
      }

      console.log("📦 Intentando con payload mínimo:", payloadMinimo);

      const resultado2 = await supabase
        .from("planes_trabajo")
        .upsert(payloadMinimo, { onConflict: "id" })
        .select()
        .maybeSingle();
      
      if (resultado2.error) {
        console.error("❌ Error guardando plan de trabajo:", resultado2.error);
        return null;
      }
      
      data = resultado2.data;
    }
    
    if (data) {
      // Parsear tareas/checklist al retornar
      const tareasParseadas = data.tareas 
        ? (typeof data.tareas === 'string' ? JSON.parse(data.tareas) : data.tareas)
        : (data.checklist 
          ? (typeof data.checklist === 'string' ? JSON.parse(data.checklist) : data.checklist)
          : []);
      
      return { ...data, tareas: tareasParseadas, checklist: tareasParseadas };
    }
    
    return null;
  } catch (e) {
    console.error("❌ Error en guardarPlanTrabajoEnSupabase:", e);
    return null;
  }
}

async function eliminarPlanTrabajoEnSupabase(id) {
  try {
    const { error } = await supabase.from("planes_trabajo").delete().eq("id", id);
    return !error;
  } catch (e) {
    return false;
  }
}

// ============================================================
// ============= BITÁCORA PLANES DE TRABAJO ===================
// ============================================================

async function guardarBitacoraPlanTrabajo(registro, empresaId = null) {
  try {
    const payload = {
      id: uid(),
      plan_id: registro.plan_id,
      plan_nombre: registro.plan_nombre || "",
      accion: registro.accion, // "creado", "asignado", "tarea_completada", "guardado", "cerrado", "reabierto"
      descripcion: registro.descripcion || "",
      empleado_id: registro.empleado_id || null,
      empleado_nombre: registro.empleado_nombre || "",
      sede_id: registro.sede_id || null,
      sede_nombre: registro.sede_nombre || "",
      fecha: new Date().toISOString(),
      mes: new Date().getMonth() + 1,
      anio: new Date().getFullYear(),
      datos_adicionales: JSON.stringify(registro.datos_adicionales || {}),
      empresa_id: empresaId
    };
    
    const { error } = await supabase
      .from("planes_trabajo_bitacora")
      .insert([payload]);
    
    if (error) {
      console.error("❌ Error guardando bitácora:", error);
      return false;
    }
    return true;
  } catch (e) {
    console.error("❌ Error en guardarBitacoraPlanTrabajo:", e);
    return false;
  }
}

async function cargarBitacoraPlanesDeSupabase(filtros = {}, empresaId = null) {
  try {
    let query = supabase
      .from("planes_trabajo_bitacora")
      .select("*")
      .eq("accion", "aprobado") // Solo mostrar planes aprobados definitivamente
      .order("fecha", { ascending: false });
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    if (filtros.sede_id) {
      query = query.eq("sede_id", filtros.sede_id);
    }
    if (filtros.empleado_id) {
      query = query.eq("empleado_id", filtros.empleado_id);
    }
    if (filtros.mes) {
      query = query.eq("mes", parseInt(filtros.mes));
    }
    if (filtros.anio) {
      query = query.eq("anio", parseInt(filtros.anio));
    }
    if (filtros.plan_id) {
      query = query.eq("plan_id", filtros.plan_id);
    }
    
    const { data, error } = await query;
    if (error) {
      console.error("❌ Error cargando bitácora:", error);
      return [];
    }
    return data || [];
  } catch (e) {
    console.error("❌ Error en cargarBitacoraPlanesDeSupabase:", e);
    return [];
  }
}

// ============================================================
// ========= NOTIFICACIONES PLANES DE TRABAJO =================
// ============================================================

async function crearNotificacionPlanTrabajo(notif, empresaId = null) {
  try {
    const payload = {
      id: uid(),
      plan_id: notif.plan_id,
      plan_nombre: notif.plan_nombre || "",
      empleado_id: notif.empleado_id,
      tipo: notif.tipo || "asignacion", // "asignacion", "tarea", "cierre", "revision"
      mensaje: notif.mensaje,
      leida: false,
      fecha: new Date().toISOString(),
      empresa_id: empresaId
    };
    
    const { error } = await supabase
      .from("planes_trabajo_notificaciones")
      .insert([payload]);
    
    if (error) {
      console.error("❌ Error creando notificación:", error);
      return false;
    }
    return true;
  } catch (e) {
    console.error("❌ Error en crearNotificacionPlanTrabajo:", e);
    return false;
  }
}

async function cargarNotificacionesPlanesDeSupabase(empleadoId, empresaId = null) {
  try {
    let query = supabase
      .from("planes_trabajo_notificaciones")
      .select("*")
      .eq("empleado_id", empleadoId)
      .eq("leida", false)
      .order("fecha", { ascending: false });
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;
    if (error) return [];
    return data || [];
  } catch (e) {
    return [];
  }
}

async function marcarNotificacionPlanLeidaEnSupabase(notifId) {
  try {
    await supabase
      .from("planes_trabajo_notificaciones")
      .update({ leida: true })
      .eq("id", notifId);
    return true;
  } catch (e) {
    return false;
  }
}

// ============================================================
// ===================== ARQUEOS DE CAJA ======================
// ============================================================

async function cargarArqueosCajaDeSupabase(empresaId = null) {
  try {
    let query = supabase
      .from("arqueos_caja")
      .select("*")
      .order("fecha", { ascending: false })
      .order("created_at", { ascending: false });
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;
    if (error) {
      console.error("❌ Error cargando arqueos:", error);
      return [];
    }
    return (data || []).map(a => ({
      ...a,
      // Parsear denominaciones si viene como string
      denominaciones: typeof a.denominaciones === 'string' ? JSON.parse(a.denominaciones || '{}') : (a.denominaciones || {}),
      // Mantener compatibilidad con campo notas/observaciones
      notas: a.observaciones || a.notas || ""
    }));
  } catch (e) {
    console.error("❌ Error en cargarArqueosCajaDeSupabase:", e);
    return [];
  }
}

async function guardarArqueoCajaEnSupabase(arqueo, empresaId = null) {
  try {
    const payload = {
      id: arqueo.id || uid(),
      tipo: arqueo.tipo || "caja",
      fecha: arqueo.fecha,
      hora: arqueo.hora || null,
      sede_id: arqueo.sede_id || null,
      cajero_id: arqueo.cajero_id || null,
      valor_inicial: arqueo.valor_inicial || 0,
      // Denominaciones guardadas como JSON
      denominaciones: JSON.stringify(arqueo.denominaciones || {}),
      total_efectivo: arqueo.total_efectivo || 0,
      datafono: arqueo.datafono || 0,
      transferencia_bancolombia: arqueo.transferencia_bancolombia || 0,
      transferencia_davivienda: arqueo.transferencia_davivienda || 0,
      transferencia_daviplata: arqueo.transferencia_daviplata || 0,
      transferencia_nequi: arqueo.transferencia_nequi || 0,
      total_transferencias: arqueo.total_transferencias || 0,
      ventas_proceso: arqueo.ventas_proceso || 0,
      gastos: arqueo.gastos || 0,
      total_general: arqueo.total_general || 0,
      diferencia: arqueo.diferencia || 0,
      estado_cuadre: arqueo.estado_cuadre || null,
      observaciones: arqueo.notas || arqueo.observaciones || null,
      estado: arqueo.estado || "cerrado",
      updated_at: new Date().toISOString()
    };
    
    if (empresaId) {
      payload.empresa_id = empresaId;
    }

    console.log("📦 Payload arqueo a guardar:", payload);

    const { data, error } = await supabase
      .from("arqueos_caja")
      .upsert(payload, { onConflict: "id" })
      .select()
      .maybeSingle();

    if (error) {
      console.error("❌ Error guardando arqueo:", error);
      console.error("❌ Detalle del error:", JSON.stringify(error, null, 2));
      return null;
    }
    
    console.log("✅ Arqueo guardado exitosamente:", data);
    
    // Parsear denominaciones al devolver
    if (data) {
      return { 
        ...data, 
        denominaciones: typeof data.denominaciones === 'string' ? JSON.parse(data.denominaciones || '{}') : (data.denominaciones || {}),
        notas: data.observaciones
      };
    }
    return null;
  } catch (e) {
    console.error("❌ Error en guardarArqueoCajaEnSupabase:", e);
    return null;
  }
}

async function eliminarArqueoCajaEnSupabase(id) {
  try {
    const { error } = await supabase.from("arqueos_caja").delete().eq("id", id);
    return !error;
  } catch (e) {
    return false;
  }
}

// ============================================================
// =========== MARCACIONES REALES (HIKVISION) =================
// ============================================================

/**
 * Estructura tabla marcaciones_reales:
 * - id (uuid)
 * - empleado_id (uuid, FK a empleados)
 * - documento (text) - documento del empleado para match
 * - fecha (date) - fecha del registro
 * - entrada1 (text) - primera entrada (formato 12h: "8:00 AM")
 * - salida1 (text) - primera salida
 * - entrada2 (text) - segunda entrada (horario partido)
 * - salida2 (text) - segunda salida
 * - tipo_horario (text) - "DERECHO" | "PARTIDO" | "INCOMPLETO"
 * - sede (text) - sede del empleado
 * - estado (text) - "pendiente" | "aprobado"
 * - aprobado_por (uuid) - empleado que aprobó
 * - fecha_aprobacion (timestamp)
 * - empresa_id (uuid)
 * - created_at (timestamp)
 * - archivo_origen (text) - nombre del archivo CSV de origen
 */

// Convertir hora de 24h a 12h AM/PM
function convertir24hA12h(hora24) {
  if (!hora24) return "";
  const partes = hora24.toString().trim().split(":");
  if (partes.length < 2) return hora24;
  let hh = parseInt(partes[0], 10);
  const mm = partes[1].padStart(2, "0");
  if (isNaN(hh)) return hora24;
  const ampm = hh >= 12 ? "PM" : "AM";
  const hora12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${hora12}:${mm} ${ampm}`;
}

// Parsear fecha del CSV (d/m/a) a formato ISO (YYYY-MM-DD)
function parsearFechaCSV(fechaStr) {
  if (!fechaStr) return null;
  const partes = fechaStr.trim().split("/");
  if (partes.length !== 3) return null;
  const [d, m, a] = partes;
  const dia = d.padStart(2, "0");
  const mes = m.padStart(2, "0");
  const anio = a.length === 2 ? `20${a}` : a;
  return `${anio}-${mes}-${dia}`;
}

// Extraer nombre de sede del departamento (quitar prefijo "bigburguer")
function extraerSedeDeDepto(depto) {
  if (!depto) return "";
  // Limpiar comillas y espacios
  const texto = depto.toString().trim().replace(/"/g, '').replace(/'/g, '');
  if (!texto) return "";
  
  const textoLower = texto.toLowerCase();
  
  // Quitar prefijos comunes (case insensitive) - con y sin espacio
  const prefijos = [
    "bigburguer ", "bigburguer-", "bigburguer_", "bigburguer",
    "big burguer ", "big burguer-", "big burguer_", "big burguer",
    "bigburger ", "bigburger-", "bigburger_", "bigburger",
    "big burger ", "big burger-", "big burger_", "big burger"
  ];
  
  for (const pref of prefijos) {
    if (textoLower.startsWith(pref)) {
      // Extraer la parte después del prefijo manteniendo el formato original
      let sedeExtraida = texto.substring(pref.length).trim();
      // Si quedó vacío, continuar buscando
      if (!sedeExtraida) continue;
      // Capitalizar primera letra de cada palabra
      sedeExtraida = sedeExtraida.split(' ')
        .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
        .join(' ');
      console.log("📍 Sede extraída de '" + texto + "' -> '" + sedeExtraida + "'");
      return sedeExtraida;
    }
  }
  
  // Si no tiene prefijo bigburguer, devolver como está (capitalizado)
  const resultado = texto.split(' ')
    .map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ');
  console.log("📍 Sede sin prefijo de '" + texto + "' -> '" + resultado + "'");
  return resultado;
}

// Mapeo de número de serie del dispositivo biométrico a sede
const MAPA_DISPOSITIVO_SEDE = {
  "FH4126819": "Corales",
  "GB9478932": "Av Sur",
  "FH4126802": "Villa Del Prado"
};

// Función para obtener sede por número de serie del dispositivo
function obtenerSedePorDispositivo(numeroSerie) {
  if (!numeroSerie) return "";
  const serieNormalizado = numeroSerie.toString().trim().toUpperCase();
  
  // Buscar coincidencia exacta o parcial
  for (const [serie, sede] of Object.entries(MAPA_DISPOSITIVO_SEDE)) {
    if (serieNormalizado === serie.toUpperCase() || serieNormalizado.includes(serie.toUpperCase())) {
      console.log("📍 Sede identificada por dispositivo '" + numeroSerie + "' -> '" + sede + "'");
      return sede;
    }
  }
  
  console.log("⚠️ Dispositivo no reconocido:", numeroSerie);
  return "";
}

// Procesar CSV de HikVision y agrupar por empleado/fecha
function procesarCSVMarcaciones(csvText, empleados) {
  const lineas = csvText.split(/\r?\n/).filter(l => l.trim());
  if (lineas.length < 2) return { error: "El archivo está vacío o no tiene datos" };
  
  // Detectar encabezados - limpiar BOM y caracteres especiales
  const primeraLinea = lineas[0].replace(/^\uFEFF/, '').replace(/[""]/g, '"');
  const encabezados = primeraLinea.split(/[,;\t]/).map(h => h.trim().toLowerCase().replace(/"/g, ''));
  
  console.log("📋 Encabezados detectados:", encabezados);
  
  // Buscar índices de columnas - primero busca coincidencia exacta, luego parcial
  const buscarColumna = (variantes) => {
    // Primero buscar coincidencia exacta
    for (const v of variantes) {
      const idx = encabezados.findIndex(h => h === v);
      if (idx !== -1) return idx;
    }
    // Si no hay exacta, buscar que contenga (pero evitar falsos positivos)
    for (const v of variantes) {
      // Solo buscar parcial si la variante tiene más de 3 caracteres
      if (v.length > 3) {
        const idx = encabezados.findIndex(h => h.includes(v) && !h.includes("apellido"));
        if (idx !== -1) return idx;
      }
    }
    return -1;
  };
  
  const iNombre = buscarColumna(["nombre", "first name", "name"]);
  const iApellido = buscarColumna(["apellido", "surname", "last name", "lastname"]);
  const iID = buscarColumna(["id", "documento", "cedula", "identificacion", "cc", "nit"]);
  const iDepto = buscarColumna(["departamento", "depto", "department", "area", "sede", "sucursal"]);
  const iFecha = buscarColumna(["fecha", "date"]);
  const iTiempo = buscarColumna(["tiempo", "time", "hora"]);
  // Agregar detección de columna de número de serie del dispositivo biométrico
  const iNumeroSerie = buscarColumna(["n.º de serie del dispositivo", "numero de serie", "nº de serie", "serial", "device serial", "n° de serie del dispositivo", "no. serie dispositivo", "serie dispositivo", "device sn", "sn"]);
  
  console.log("📋 Índices: ID=" + iID + ", Depto=" + iDepto + ", Fecha=" + iFecha + ", Tiempo=" + iTiempo + ", NumSerie=" + iNumeroSerie);
  
  if (iID === -1 || iFecha === -1 || iTiempo === -1) {
    return { error: "El archivo no tiene las columnas requeridas (ID, Fecha, Tiempo). Encabezados encontrados: " + encabezados.join(", ") };
  }
  
  // Crear mapa de empleados por documento para match rápido
  const mapaPorDoc = {};
  empleados.forEach(emp => {
    if (emp.documento) {
      mapaPorDoc[emp.documento.toString().trim()] = emp;
    }
  });
  
  // Debug: mostrar algunos documentos del sistema
  const docsDelSistema = Object.keys(mapaPorDoc).slice(0, 10);
  console.log("📋 Documentos en el sistema (primeros 10):", docsDelSistema);
  console.log("📋 Total empleados con documento:", Object.keys(mapaPorDoc).length);
  
  // Agrupar transacciones por empleado+fecha
  const agrupado = {}; // { "doc_fecha": { doc, fecha, sede, nombre, transacciones: [{hora}] } }
  const empleadosNoEncontrados = new Set();
  const documentosDelCSV = new Set(); // Para debug
  
  for (let i = 1; i < lineas.length; i++) {
    // Limpiar línea de comillas innecesarias y BOM
    const lineaLimpia = lineas[i].replace(/^\uFEFF/, '').replace(/[""]/g, '"');
    const cols = lineaLimpia.split(/[,;\t]/).map(c => c.replace(/"/g, '').trim());
    
    if (cols.length < Math.max(iID, iFecha, iTiempo) + 1) continue;
    
    const documento = (cols[iID] || "").toString().trim();
    const fechaCSV = (cols[iFecha] || "").trim();
    const tiempoCSV = (cols[iTiempo] || "").trim();
    const depto = iDepto >= 0 ? (cols[iDepto] || "").trim() : "";
    const nombreCSV = iNombre >= 0 ? (cols[iNombre] || "").trim() : "";
    const apellidoCSV = iApellido >= 0 ? (cols[iApellido] || "").trim() : "";
    // Extraer número de serie del dispositivo biométrico
    const numeroSerieDispositivo = iNumeroSerie >= 0 ? (cols[iNumeroSerie] || "").trim() : "";
    
    // Guardar documento del CSV para debug
    if (documento) documentosDelCSV.add(documento);
    
    // Debug: mostrar primer registro
    if (i === 1) {
      console.log("📋 Primera línea datos:", { documento, fechaCSV, tiempoCSV, depto, nombreCSV, apellidoCSV, numeroSerieDispositivo });
      console.log("📋 Columnas raw:", cols);
      console.log("📋 ¿Documento existe en sistema?", !!mapaPorDoc[documento], "Buscando:", documento);
    }
    
    if (!documento || !fechaCSV || !tiempoCSV) continue;
    
    const fechaISO = parsearFechaCSV(fechaCSV);
    if (!fechaISO) {
      if (i <= 3) console.log("📋 Fecha no parseada:", fechaCSV, "-> null");
      continue;
    }
    
    const empleado = mapaPorDoc[documento];
    if (!empleado) {
      empleadosNoEncontrados.add(`${nombreCSV} ${apellidoCSV} (${documento})`);
      continue;
    }
    
    // Prioridad para determinar sede:
    // 1. Número de serie del dispositivo (más confiable)
    // 2. Departamento como fallback
    let sedeExtraida = "";
    if (numeroSerieDispositivo) {
      sedeExtraida = obtenerSedePorDispositivo(numeroSerieDispositivo);
    }
    // Si no se obtuvo sede por dispositivo, usar departamento como fallback
    if (!sedeExtraida && depto) {
      sedeExtraida = extraerSedeDeDepto(depto);
    }
    
    const key = `${documento}_${fechaISO}`;
    if (!agrupado[key]) {
      agrupado[key] = {
        documento,
        fecha: fechaISO,
        empleadoId: empleado.id,
        nombreEmpleado: empleado.nombre || `${nombreCSV} ${apellidoCSV}`,
        transacciones: [] // Ahora guarda {hora, sede}
      };
    }
    
    // Guardar hora Y sede de cada transacción
    agrupado[key].transacciones.push({ hora: tiempoCSV, sede: sedeExtraida });
  }
  
  // Procesar cada grupo y asignar entradas/salidas
  const marcaciones = [];
  
  for (const key of Object.keys(agrupado)) {
    const grupo = agrupado[key];
    let trans = grupo.transacciones;
    
    // Ordenar por hora
    trans.sort((a, b) => {
      const [hA, mA] = a.hora.split(":").map(Number);
      const [hB, mB] = b.hora.split(":").map(Number);
      return (hA * 60 + (mA || 0)) - (hB * 60 + (mB || 0));
    });
    
    // Eliminar duplicados (mismo minuto)
    const sinDuplicados = [];
    let ultimaHora = "";
    for (const t of trans) {
      const horaMinuto = t.hora.substring(0, 5); // HH:MM
      if (horaMinuto !== ultimaHora) {
        sinDuplicados.push(t);
        ultimaHora = horaMinuto;
      }
    }
    trans = sinDuplicados;
    
    // Asignar según cantidad de transacciones
    let entrada1 = "", salida1 = "", entrada2 = "", salida2 = "";
    let sede1 = "", sede2 = "";
    let tipoHorario = "";
    
    // trans ahora tiene objetos {hora, sede}
    if (trans.length >= 1) {
      entrada1 = convertir24hA12h(trans[0].hora);
      sede1 = trans[0].sede;
    }
    if (trans.length >= 2) {
      salida1 = convertir24hA12h(trans[1].hora);
      // Si la sede de salida1 es diferente, la tomamos para sede1 (aunque normalmente es la misma)
      if (!sede1) sede1 = trans[1].sede;
    }
    if (trans.length >= 3) {
      entrada2 = convertir24hA12h(trans[2].hora);
      sede2 = trans[2].sede; // Sede del segundo turno
    }
    if (trans.length >= 4) {
      salida2 = convertir24hA12h(trans[3].hora);
      if (!sede2) sede2 = trans[3].sede;
    }
    
    // Determinar tipo de horario
    if (trans.length === 0) {
      tipoHorario = "";
    } else if (trans.length === 1) {
      tipoHorario = "INCOMPLETO";
    } else if (trans.length === 2) {
      tipoHorario = "DERECHO";
    } else if (trans.length === 3) {
      tipoHorario = "INCOMPLETO";
    } else if (trans.length >= 4) {
      tipoHorario = "PARTIDO";
    }
    
    if (tipoHorario) {
      marcaciones.push({
        empleadoId: grupo.empleadoId,
        documento: grupo.documento,
        fecha: grupo.fecha,
        sede1: sede1,  // Sede del primer turno (E1/S1)
        sede2: sede2,  // Sede del segundo turno (E2/S2) - puede ser diferente en PARTIDO
        nombreEmpleado: grupo.nombreEmpleado,
        entrada1,
        salida1,
        entrada2,
        salida2,
        tipoHorario,
        incompleto: tipoHorario === "INCOMPLETO"
      });
    }
  }
  
  // Debug final
  console.log("📋 Resumen procesamiento CSV:");
  console.log("   - Documentos únicos en CSV:", documentosDelCSV.size, Array.from(documentosDelCSV).slice(0, 5));
  console.log("   - Empleados NO encontrados:", empleadosNoEncontrados.size);
  console.log("   - Marcaciones generadas:", marcaciones.length);
  
  return {
    marcaciones,
    empleadosNoEncontrados: Array.from(empleadosNoEncontrados),
    totalProcesadas: marcaciones.length,
    documentosDelCSV: Array.from(documentosDelCSV) // Para debug
  };
}

// Guardar marcaciones temporales (pendientes de aprobación)
async function guardarMarcacionesTemporalesEnSupabase(marcaciones, archivoOrigen, subidoPor, empresaId = null) {
  try {
    const id = ensureUuid();
    const payload = {
      id,
      nombre_archivo: archivoOrigen,
      fecha_subida: new Date().toISOString(),
      subido_por: subidoPor,
      estado: "pendiente",
      marcaciones_json: JSON.stringify(marcaciones),
      total_registros: marcaciones.length,
      empresa_id: empresaId
    };
    
    const { error } = await supabase
      .from("marcaciones_temporales")
      .insert([payload]);
    
    if (error) {
      console.error("❌ Error guardando marcaciones temporales:", error);
      return { success: false, error: error.message };
    }
    
    console.log("✅ Marcaciones temporales guardadas:", id);
    return { success: true, id };
  } catch (e) {
    console.error("❌ Error en guardarMarcacionesTemporalesEnSupabase:", e);
    return { success: false, error: e.message };
  }
}

// Cargar marcaciones temporales pendientes de aprobación
async function cargarMarcacionesTemporalesDeSupabase(empresaId = null) {
  try {
    let query = supabase
      .from("marcaciones_temporales")
      .select("*")
      .order("fecha_subida", { ascending: false });
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("❌ Error cargando marcaciones temporales:", error);
      return [];
    }
    
    return (data || []).map(row => ({
      id: row.id,
      nombreArchivo: row.nombre_archivo,
      fechaSubida: row.fecha_subida,
      subidoPor: row.subido_por,
      estado: row.estado,
      marcaciones: typeof row.marcaciones_json === "string" 
        ? JSON.parse(row.marcaciones_json) 
        : (row.marcaciones_json || []),
      totalRegistros: row.total_registros,
      empresaId: row.empresa_id
    }));
  } catch (e) {
    console.error("❌ Error en cargarMarcacionesTemporalesDeSupabase:", e);
    return [];
  }
}

// Aprobar marcaciones temporales y moverlas a marcaciones_reales
async function aprobarMarcacionesTemporales(archivoId, aprobadoPor, empresaId = null) {
  try {
    // 1. Cargar el archivo temporal
    const { data: archivo, error: errArchivo } = await supabase
      .from("marcaciones_temporales")
      .select("*")
      .eq("id", archivoId)
      .single();
    
    if (errArchivo || !archivo) {
      return { success: false, error: "No se encontró el archivo de marcaciones" };
    }
    
    const marcaciones = typeof archivo.marcaciones_json === "string"
      ? JSON.parse(archivo.marcaciones_json)
      : (archivo.marcaciones_json || []);
    
    // 2. Cargar marcaciones existentes para evitar duplicados
    let queryExistentes = supabase
      .from("marcaciones_reales")
      .select("empleado_id, fecha");
    
    if (empresaId) {
      queryExistentes = queryExistentes.eq("empresa_id", empresaId);
    }
    
    const { data: existentes } = await queryExistentes;
    const existentesSet = new Set((existentes || []).map(e => `${e.empleado_id}_${e.fecha}`));
    
    // 3. Filtrar solo las nuevas (acumular, no sobrescribir)
    const nuevasMarcaciones = marcaciones.filter(m => {
      const key = `${m.empleadoId}_${m.fecha}`;
      return !existentesSet.has(key);
    });
    
    if (nuevasMarcaciones.length === 0) {
      // Actualizar estado del archivo temporal a aprobado aunque no haya nuevas
      await supabase
        .from("marcaciones_temporales")
        .update({ 
          estado: "aprobado",
          aprobado_por: aprobadoPor,
          fecha_aprobacion: new Date().toISOString()
        })
        .eq("id", archivoId);
      
      return { 
        success: true, 
        insertadas: 0, 
        omitidas: marcaciones.length,
        mensaje: "Todas las marcaciones ya existían, no se insertaron duplicados"
      };
    }
    
    // 4. Preparar payloads para insertar
    const payloads = nuevasMarcaciones.map(m => ({
      id: ensureUuid(),
      empleado_id: m.empleadoId,
      documento: m.documento,
      fecha: m.fecha,
      entrada1: m.entrada1 || null,
      salida1: m.salida1 || null,
      entrada2: m.entrada2 || null,
      salida2: m.salida2 || null,
      tipo_horario: m.tipoHorario,
      sede: m.sede1 || m.sede || null,  // Compatibilidad con formato viejo
      sede1: m.sede1 || m.sede || null,
      sede2: m.sede2 || null,
      estado: "aprobado",
      aprobado_por: aprobadoPor,
      fecha_aprobacion: new Date().toISOString(),
      empresa_id: empresaId,
      archivo_origen: archivo.nombre_archivo
    }));
    
    // 5. Insertar en marcaciones_reales
    const { error: errInsert } = await supabase
      .from("marcaciones_reales")
      .insert(payloads);
    
    if (errInsert) {
      console.error("❌ Error insertando marcaciones reales:", errInsert);
      return { success: false, error: errInsert.message };
    }
    
    // 6. Actualizar estado del archivo temporal
    await supabase
      .from("marcaciones_temporales")
      .update({ 
        estado: "aprobado",
        aprobado_por: aprobadoPor,
        fecha_aprobacion: new Date().toISOString()
      })
      .eq("id", archivoId);
    
    console.log(`✅ Marcaciones aprobadas: ${nuevasMarcaciones.length} nuevas, ${marcaciones.length - nuevasMarcaciones.length} omitidas`);
    return { 
      success: true, 
      insertadas: nuevasMarcaciones.length,
      omitidas: marcaciones.length - nuevasMarcaciones.length
    };
  } catch (e) {
    console.error("❌ Error en aprobarMarcacionesTemporales:", e);
    return { success: false, error: e.message };
  }
}

// Rechazar/eliminar archivo de marcaciones temporales
async function rechazarMarcacionesTemporales(archivoId) {
  try {
    const { error } = await supabase
      .from("marcaciones_temporales")
      .delete()
      .eq("id", archivoId);
    
    if (error) {
      console.error("❌ Error rechazando marcaciones:", error);
      return false;
    }
    
    console.log("✅ Marcaciones temporales rechazadas/eliminadas:", archivoId);
    return true;
  } catch (e) {
    console.error("❌ Error en rechazarMarcacionesTemporales:", e);
    return false;
  }
}

// Cargar marcaciones reales aprobadas
async function cargarMarcacionesRealesDeSupabase(empresaId = null, filtros = {}) {
  try {
    let query = supabase
      .from("marcaciones_reales")
      .select("*")
      .order("fecha", { ascending: false });
    
    if (empresaId) {
      query = query.eq("empresa_id", empresaId);
    }
    
    if (filtros.fechaDesde) {
      query = query.gte("fecha", filtros.fechaDesde);
    }
    if (filtros.fechaHasta) {
      query = query.lte("fecha", filtros.fechaHasta);
    }
    if (filtros.empleadoId) {
      query = query.eq("empleado_id", filtros.empleadoId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("❌ Error cargando marcaciones reales:", error);
      return [];
    }
    
    return (data || []).map(row => ({
      id: row.id,
      empleadoId: row.empleado_id,
      documento: row.documento,
      fecha: row.fecha,
      entrada1: row.entrada1,
      salida1: row.salida1,
      entrada2: row.entrada2,
      salida2: row.salida2,
      tipoHorario: row.tipo_horario,
      sede: row.sede,
      sede1: row.sede1 || row.sede,  // Compatibilidad con formato viejo
      sede2: row.sede2,
      estado: row.estado,
      aprobadoPor: row.aprobado_por,
      fechaAprobacion: row.fecha_aprobacion,
      archivoOrigen: row.archivo_origen,
      empresaId: row.empresa_id
    }));
  } catch (e) {
    console.error("❌ Error en cargarMarcacionesRealesDeSupabase:", e);
    return [];
  }
}

// Actualizar una marcación real (para corregir incompletos)
async function actualizarMarcacionReal(marcacionId, cambios) {
  try {
    const payload = {};
    if (cambios.entrada1 !== undefined) payload.entrada1 = cambios.entrada1;
    if (cambios.salida1 !== undefined) payload.salida1 = cambios.salida1;
    if (cambios.entrada2 !== undefined) payload.entrada2 = cambios.entrada2;
    if (cambios.salida2 !== undefined) payload.salida2 = cambios.salida2;
    if (cambios.tipoHorario !== undefined) payload.tipo_horario = cambios.tipoHorario;
    
    const { error } = await supabase
      .from("marcaciones_reales")
      .update(payload)
      .eq("id", marcacionId);
    
    if (error) {
      console.error("❌ Error actualizando marcación:", error);
      return false;
    }
    
    console.log("✅ Marcación actualizada:", marcacionId);
    return true;
  } catch (e) {
    console.error("❌ Error en actualizarMarcacionReal:", e);
    return false;
  }
}

// Eliminar una marcación real
async function eliminarMarcacionReal(marcacionId) {
  try {
    const { error } = await supabase
      .from("marcaciones_reales")
      .delete()
      .eq("id", marcacionId);
    
    return !error;
  } catch (e) {
    return false;
  }
}

let vista = null; 
 
/* ====================== UTILIDADES ====================== */
function uid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).slice(-4);
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function ensureUuid(value) {
  return value && UUID_REGEX.test(value) ? value : uid();
}

/**
 * Normaliza una fecha en varios formatos posibles a formato ISO 'yyyy-MM-dd'
 * Soporta: 'yyyy-MM-dd', 'd/MM/yy', 'd/MM/yyyy', 'dd/MM/yy', 'dd/MM/yyyy', Date object
 */
function normalizarFechaISO(fecha) {
  if (!fecha) return null;
  
  // Si ya es un objeto Date válido
  if (fecha instanceof Date) {
    if (isNaN(fecha.getTime())) return null;
    return fecha.toISOString().slice(0, 10);
  }
  
  const str = String(fecha).trim();
  if (!str) return null;
  
  // Ya está en formato ISO yyyy-MM-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const d = new Date(str + 'T00:00:00');
    if (!isNaN(d.getTime())) return str;
    return null;
  }
  
  // Formato d/MM/yy o dd/MM/yy o d/MM/yyyy o dd/MM/yyyy
  const match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (match) {
    const dia = parseInt(match[1], 10);
    const mes = parseInt(match[2], 10);
    let anio = parseInt(match[3], 10);
    
    // Si el año es de 2 dígitos, convertir a 4 dígitos
    if (anio < 100) {
      // Asumimos 2000-2099 para años < 100
      anio = anio < 50 ? 2000 + anio : 1900 + anio;
    }
    
    const fechaObj = new Date(anio, mes - 1, dia);
    if (isNaN(fechaObj.getTime())) return null;
    
    const y = fechaObj.getFullYear();
    const m = String(fechaObj.getMonth() + 1).padStart(2, '0');
    const d = String(fechaObj.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  
  // Intentar parsear como Date nativa
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }
  
  return null;
}

function toISO(d) {
  if (!d) return null;
  
  // Primero intentar normalizar la fecha
  const normalizado = normalizarFechaISO(d);
  if (normalizado) return normalizado;
  
  // Fallback: intentar con Date nativa
  try {
    const fecha = new Date(d);
    if (isNaN(fecha.getTime())) {
      console.warn('⚠️ Fecha inválida en toISO:', d);
      return null;
    }
    return fecha.toISOString().slice(0, 10);
  } catch (e) {
    console.warn('⚠️ Error procesando fecha en toISO:', d, e);
    return null;
  }
}

function parseISO(iso) {
  if (!iso) return new Date(NaN); // Fecha inválida
  
  // Primero normalizar la fecha
  const normalizado = normalizarFechaISO(iso);
  if (!normalizado) {
    console.warn('⚠️ Fecha inválida en parseISO:', iso);
    return new Date(NaN);
  }
  
  const [y, m, d] = normalizado.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function addDaysLocal(iso, n) {
  const dt = parseISO(iso);
  dt.setDate(dt.getDate() + n);
  return toISO(dt);
}
 
/* ---------- Festivos Colombia ---------- */
function easterDate(year) {
  // Algoritmo de Butcher (Calendario Gregoriano)
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3=Marzo, 4=Abril
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function moveToMonday(date) {
  // Mueve un Date a lunes siguiente según Ley Emiliani
  const d = new Date(date);
  const dow = d.getDay(); // 0=Domingo, 1=Lunes, ...
  if (dow === 1) return toISO(d);
  const diff = (8 - dow) % 7; // días hasta lunes siguiente
  d.setDate(d.getDate() + diff);
  return toISO(d);
}

function buildHolidaysCO(year) {
  const set = new Set();

  function addISO(iso) { set.add(iso); }
  function iso(y, m, d) { return toISO(new Date(y, m - 1, d)); }

  // Festivos especiales por decreto del Gobierno Nacional
  if (year === 2026) addISO(iso(year, 7, 13)); // 13 de julio 2026 – festivo decreto gobierno nacional

  // Fijos
  addISO(iso(year, 1, 1));   // Año Nuevo
  addISO(iso(year, 5, 1));   // Día del Trabajo
  addISO(iso(year, 7, 20));  // Independencia de Colombia
  addISO(iso(year, 8, 7));   // Batalla de Boyacá
  addISO(iso(year, 12, 8));  // Inmaculada Concepción
  addISO(iso(year, 12, 25)); // Navidad

  // Trasladables a lunes (Ley Emiliani)
  addISO(moveToMonday(new Date(year, 0, 6)));   // Reyes Magos (6 Ene)
  addISO(moveToMonday(new Date(year, 2, 19)));  // San José (19 Mar)
  addISO(moveToMonday(new Date(year, 5, 29)));  // San Pedro y San Pablo (29 Jun)
  addISO(moveToMonday(new Date(year, 7, 15)));  // Asunción de la Virgen (15 Ago)
  addISO(moveToMonday(new Date(year, 9, 12)));  // Día de la Raza (12 Oct)
  addISO(moveToMonday(new Date(year, 10, 1)));  // Todos los Santos (1 Nov)
  addISO(moveToMonday(new Date(year, 10, 11))); // Independencia de Cartagena (11 Nov)

  // Basados en Pascua
  const easter = easterDate(year); // Domingo de Resurrección
  const addOffset = (days) => {
    const d = new Date(easter);
    d.setDate(d.getDate() + days);
    return toISO(d);
  };
  // Jueves y Viernes Santo (no trasladables)
  addISO(addOffset(-3)); // Jueves Santo
  addISO(addOffset(-2)); // Viernes Santo
  // Ascensión (39 días después, trasladada a lunes)
  addISO(moveToMonday(new Date(new Date(easter).setDate(easter.getDate() + 39))));
  // Corpus Christi (60 días después, trasladada a lunes)
  addISO(moveToMonday(new Date(new Date(easter).setDate(easter.getDate() + 60))));
  // Sagrado Corazón (68 días después, trasladada a lunes)
  addISO(moveToMonday(new Date(new Date(easter).setDate(easter.getDate() + 68))));

  return set;
}

const __holidayCacheDefault = {};
function isHolidayCO(iso, cache) {
  const c = cache || __holidayCacheDefault;
  const y = parseISO(iso).getFullYear();
  if (!c[y]) c[y] = buildHolidaysCO(y);
  return c[y].has(iso);
}
function isBusinessDayVac(iso, cache) {
  const d = parseISO(iso);
  const dow = d.getDay();
  if (dow === 0) return false;
  if (isHolidayCO(iso, cache)) return false;
  return true;
}
function nextBusinessDayVac(iso, cache) {
  let cur = iso;
  do {
    cur = addDaysLocal(cur, 1);
  } while (!isBusinessDayVac(cur, cache));
  return cur;
}
function addBusinessDaysVac(inicioISO, n, cache) {
  let counted = 0;
  let cur = inicioISO;
  while (true) {
    if (isBusinessDayVac(cur, cache)) counted++;
    if (counted === n) return cur;
    cur = addDaysLocal(cur, 1);
  }
}
 
function isDateInRange(iso, start, end) {
  if (!start && !end) return false;
  const d = parseISO(iso).getTime();
  const s = start ? parseISO(start).getTime() : d;
  const e = end ? parseISO(end).getTime() : d;
  return d >= s && d <= e;
}
function seTraslapaRangoFechas(desdeA, hastaA, desdeB, hastaB) {
  if (!desdeA || !hastaA || !desdeB || !hastaB) return false;
  const a0 = parseISO(desdeA);
  const a1 = parseISO(hastaA);
  const b0 = parseISO(desdeB);
  const b1 = parseISO(hastaB);
  return a0 <= b1 && b0 <= a1;
}
 
/* ====================== VACACIONES POR PERÍODO ====================== */
// Un período = desde el aniversario N al aniversario N+1 (15 días).
// Etiquetamos el período con el AÑO en que se CUMPLE el aniversario.
 
/* ====================== UTILIDADES LIQUIDACIONES ====================== */
function daysBetweenInclusive(aISO, bISO){
  if (!aISO || !bISO) return 0;
  const a = startOfDay(parseISO(aISO));
  const b = startOfDay(parseISO(bISO));
  return Math.floor((b - a) / (24*3600*1000)) + 1;
}
 
// Convenio 360 días (base tradicional en prestaciones)
function days360(aISO, bISO){
  const a = parseISO(aISO), b = parseISO(bISO);
  const d1 = a.getDate() === 31 ? 30 : a.getDate();
  const d2 = b.getDate() === 31 ? 30 : b.getDate();
  const m1 = a.getMonth()+1, m2 = b.getMonth()+1;
  const y1 = a.getFullYear(), y2 = b.getFullYear();
  return (y2 - y1)*360 + (m2 - m1)*30 + (d2 - d1) + 1;
}
 
// Ajusta el rango a partir de la fecha de ingreso
function clampByIngreso(ingresoISO, desdeISO, hastaISO){
  if (!ingresoISO) return { from: desdeISO, to: hastaISO };
  const ingreso = parseISO(ingresoISO);
  const d = parseISO(desdeISO), h = parseISO(hastaISO);
  const from = d < ingreso ? ingreso : d;
  const to   = h;
  if (from > to) return { from: toISO(from), to: toISO(from) };
  return { from: toISO(from), to: toISO(to) };
}
 
// Base salarial para liquidaciones (elige la que uses en tu negocio)
function baseVac(empleado){ return Number(empleado?.promedioultimos6meses || empleado?.salarioProm6m || empleado?.salarioBase || empleado?.salariobase || 0); }
// (definida una sola vez)

// 🔹 NUEVA BASE CESANTÍAS: salario mensual + auxilio fijo de transporte
function baseCes(empleado) {
  const salario = Number(empleado?.promedioultimos6meses || empleado?.salarioProm6m || empleado?.salarioBase || empleado?.salariobase || 0);
  const auxTransporteFijo = 200000; // aquí fijas el auxilio según tu ejemplo
  return salario + auxTransporteFijo;
}

function basePrima(empleado){
  // Puedes dejar prima solo con salario (como estaba)
  return Number(empleado?.promedioultimos6meses || empleado?.salarioProm6m || empleado?.salarioBase || empleado?.salariobase || 0);
}

// daysBetweenInclusive ya está definida arriba; evitamos duplicados
 
/**
 * Cesantías:
 *  - Por cada 365 días trabajados → 30 días de salario (salario+auxilio).
 *  - Si el período es parcial, se prorratea:
 *      díasDerecho = 30 * (díasTrabajados / 365)
 *  - Valor cesantías = valorDía * díasDerecho
 */
function calcCesantias(empleado, desdeISO, hastaISO) {
  const base = baseCes(empleado);  // salario + auxilio
  const dias = Math.max(0, daysBetweenInclusive(desdeISO, hastaISO));
 
  if (!dias || !base) return { dias: 0, diasDerecho: 0, valor: 0 };
 
  const valorDia = base / 30;
  const diasDerecho = 30 * (dias / 365);  // 30 días por año, prorrateado
  const valor = Math.round(valorDia * diasDerecho);
 
  return { dias, diasDerecho, valor };
}
 
/**
 * Intereses a las cesantías:
 *  - 12% anual sobre el valor de las cesantías.
 *  - Proporcional a los días del mismo período: días / 365
 */
function calcInteresesCes(cesantiasValor, desdeISO, hastaISO) {
  const dias = Math.max(0, daysBetweenInclusive(desdeISO, hastaISO));
  if (!dias || !cesantiasValor) return 0;
 
  const interes = cesantiasValor * 0.12 * (dias / 365);
  return Math.round(interes);
}
 
// Prima semestral con recorte por ingreso
function rangoPrima(empleado, semestre, anio){
  const desde = semestre === 1 ? `${anio}-01-01` : `${anio}-07-01`;
  const hasta = semestre === 1 ? `${anio}-06-30` : `${anio}-12-31`;
  const cl = clampByIngreso(empleado?.fechaIngreso, desde, hasta);
  return cl;
}
function calcPrima(empleado, semestre, anio){
  const base = basePrima(empleado);
  const r = rangoPrima(empleado, semestre, anio);
  const d = Math.max(0, days360(r.from, r.to));
  return { desde:r.from, hasta:r.to, dias:d, valor: Math.round(base * d / 360) };
}
 
function startOfDay(d){ const x = new Date(d); x.setHours(0,0,0,0); return x; }
function addDays(d, n){ const x = new Date(d); x.setDate(x.getDate() + n); return x; }
 
/** Construye períodos completos (ya cumplidos respecto a "asOfISO") de 15 días */
function buildVacationPeriods(empleado, asOfISO = toISO(new Date())) {
  // Normalizar la fecha de ingreso para soportar varios formatos
  const fechaIngresoNorm = normalizarFechaISO(empleado?.fechaIngreso);
  if (!fechaIngresoNorm) {
    console.warn('⚠️ Empleado sin fecha de ingreso válida:', empleado?.nombre, 'Fecha:', empleado?.fechaIngreso);
    return [];
  }
  
  const ingreso = parseISO(fechaIngresoNorm);
  if (isNaN(ingreso.getTime())) {
    console.warn('⚠️ Fecha de ingreso inválida para empleado:', empleado?.nombre);
    return [];
  }
  const asOf = parseISO(asOfISO);
  if (isNaN(asOf.getTime())) {
    console.warn('⚠️ Fecha asOf inválida:', asOfISO);
    return [];
  }
 
  const periods = [];
  let i = 1;
 
  // (A) Períodos COMPLETOS (antiguos)
  while (true) {
    const from = new Date(ingreso);
    from.setFullYear(from.getFullYear() + (i - 1));
    const nextAnniv = new Date(ingreso);
    nextAnniv.setFullYear(nextAnniv.getFullYear() + i);
    const to = new Date(nextAnniv);
    to.setDate(to.getDate() - 1);
 
    if (to > asOf) break; // ya no hay completos
 
    const label = String(to.getFullYear()); // etiqueta por el año en que se cumple el aniversario
    periods.push({
      label,                    // p.ej. "2025"
      fromISO: toISO(startOfDay(from)),
      toISO:   toISO(startOfDay(to)),
      accrued: 15,             // periodo completo
      used: 0,
      pending: 15,
      ongoing: false,
    });
    i++;
  }

  // (B) Período EN CURSO desde el último aniversario hasta "asOf"
  const currentFrom = new Date(ingreso);
  currentFrom.setFullYear(currentFrom.getFullYear() + (i - 1));
  const theoreticalEnd = new Date(ingreso);
  theoreticalEnd.setFullYear(theoreticalEnd.getFullYear() + i);
  theoreticalEnd.setDate(theoreticalEnd.getDate() - 1);

  const fromISO = toISO(startOfDay(currentFrom));
  const toISOtheo = toISO(startOfDay(theoreticalEnd));
  const diasTranscurridos = Math.max(0, daysBetweenInclusive(fromISO, asOfISO));
  const accruedNowRaw = Math.min(15, 15 * (diasTranscurridos / 365));
  const accruedNow = Math.round(accruedNowRaw * 10) / 10; // entero y un decimal

  periods.push({
    label: `${parseISO(toISOtheo).getFullYear()} (en curso)`,
    fromISO,
    toISO: toISOtheo,
    accrued: accruedNow,
    used: 0,
    pending: accruedNow,
    ongoing: true,
  });
 
  return periods;
}
 
function contarDiasHabilesVacISO(inicioISO, finISO, holidaysCache){
  if (!inicioISO || !finISO) return 0;
  
  // Normalizar fechas
  const inicioNorm = normalizarFechaISO(inicioISO);
  const finNorm = normalizarFechaISO(finISO);
  if (!inicioNorm || !finNorm) return 0;
  
  let cur = parseISO(inicioNorm);
  const fin = parseISO(finNorm);
  if (isNaN(cur.getTime()) || isNaN(fin.getTime())) return 0;
  
  let count = 0;
  // Límite de seguridad para evitar bucles infinitos (máx 1000 días)
  let iteraciones = 0;
  while (cur <= fin && iteraciones < 1000) {
    const iso = toISO(cur);
    if (iso && isBusinessDayVac(iso, holidaysCache)) count++;
    cur.setDate(cur.getDate() + 1);
    iteraciones++;
  }
  return count;
}
 
/** Aplica ausencias de vacaciones a los períodos, descontando FIFO (antiguo→nuevo) */
/** Aplica ausencias de vacaciones a los períodos, descontando FIFO (antiguo→nuevo)
 * 🔹 MODIFICADO: Soporta ausencias con liquidacionDirecta (usa campo dias directamente)
 */
 function applyVacationUses(periods, ausenciasVac, holidaysCache) {
  const buckets = periods.map(p => ({...p}));
  
  // Ordenar ausencias por fecha de inicio (normalizando fechas)
  const ordenadas = [...ausenciasVac]
    .filter(a => a.inicio) // Filtrar ausencias sin fecha
    .sort((a, b) => {
      const fechaA = normalizarFechaISO(a.inicio);
      const fechaB = normalizarFechaISO(b.inicio);
      if (!fechaA && !fechaB) return 0;
      if (!fechaA) return 1;
      if (!fechaB) return -1;
      return parseISO(fechaA) - parseISO(fechaB);
    });
 
  for (const a of ordenadas) {
    // 🔹 Si es liquidación directa, usar el campo dias directamente
    const dias = a.liquidacionDirecta
      ? (a.dias || 0)
      : contarDiasHabilesVacISO(a.inicio, a.fin || a.inicio, holidaysCache);
 
    let rem = dias;
    for (const b of buckets) {
      if (rem <= 0) break;
      const can = Math.max(0, b.accrued - b.used);
      if (can <= 0) continue;
      const take = Math.min(can, rem);
      b.used += take;
      b.pending = Math.max(0, b.accrued - b.used);
      rem -= take;
    }
  }
  return buckets;
}
 
/** Dados N días por tomar, retorna detalle de qué períodos se descuentan (FIFO) */
function allocateVacationFromBuckets(buckets, diasSolicitados){
  let rem = Number(diasSolicitados||0);
  const detalle = [];
  for (const b of buckets) {
    if (rem <= 0) break;
    const can = b.pending;
    if (can <= 0) continue;
    const take = Math.min(can, rem);
    detalle.push({ periodo: b.label, dias: take });
    rem -= take;
  }
  return { detalle, faltantes: rem };
}
 
/* ====================== UI BASE ====================== */
function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1 text-sm mb-2">
      <span className="text-xs text-slate-600">{label}</span>
      {children}
    </label>
  );
}
function Input(props) {
  return (
    <input
      {...props}
      className={"border rounded px-2 py-1 text-sm w-full " + (props.className || "")}
    />
  );
}
function Button({ children, variant = "default", ...rest }) {
  // Estilos inline para el tema naranja/dorado
  const getButtonStyle = () => {
    const baseStyle = {
      padding: '10px 18px',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: '600',
      cursor: rest.disabled ? 'not-allowed' : 'pointer',
      opacity: rest.disabled ? 0.5 : 1,
      border: 'none',
      transition: 'all 0.2s ease',
    };
    
    if (variant === "primary") {
      return {
        ...baseStyle,
        background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
        color: 'white',
        boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)',
      };
    }
    if (variant === "outline") {
      return {
        ...baseStyle,
        background: 'white',
        color: '#ff6b35',
        border: '2px solid #ffe4c9',
      };
    }
    if (variant === "danger") {
      return {
        ...baseStyle,
        background: 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)',
        color: 'white',
        boxShadow: '0 2px 8px rgba(255, 77, 79, 0.3)',
      };
    }
    // default
    return {
      ...baseStyle,
      background: '#334155',
      color: 'white',
    };
  };

  return (
    <button {...rest} style={getButtonStyle()}>
      {children}
    </button>
  );
}
 
/* ====================== LOCAL STORAGE HELPERS ====================== */
 
const STORAGE_KEY = "cuadresDiarios_v1";
const STORAGE_ELIMINADOS_KEY = "cuadresEliminados_v1";

// Obtener lista de cuadres que fueron eliminados permanentemente
function obtenerCuadresEliminadosGlobal() {
  try {
    const eliminados = localStorage.getItem(STORAGE_ELIMINADOS_KEY);
    return eliminados ? JSON.parse(eliminados) : [];
  } catch {
    return [];
  }
}

// Marcar un cuadre como eliminado en localStorage (para evitar re-sincronización)
function marcarCuadreComoEliminado(cuadreId) {
  if (!cuadreId) return false;
  try {
    const eliminados = obtenerCuadresEliminadosGlobal();
    if (!eliminados.includes(cuadreId)) {
      eliminados.push(cuadreId);
      localStorage.setItem(STORAGE_ELIMINADOS_KEY, JSON.stringify(eliminados));
      console.log("✅ Cuadre marcado como eliminado en localStorage:", cuadreId);
    }
    // También limpiar el cuadre de la lista de cuadres en localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const filtrado = parsed.filter(c => c.id !== cuadreId);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filtrado));
      }
    } catch (e) {
      console.error("Error limpiando cuadre de localStorage:", e);
    }
    return true;
  } catch (e) {
    console.error("Error marcando cuadre como eliminado:", e);
    return false;
  }
}
 
function loadCuadres() {
  // ==========================================
  // DESHABILITADO: Ya no usamos localStorage para cuadres
  // Supabase es la ÚNICA fuente de verdad
  // Esto evita conflictos entre usuarios y empresas
  // ==========================================
  return [];
}
 
function saveCuadres(lista) {
  // ==========================================
  // DESHABILITADO: Ya no usamos localStorage para cuadres
  // Supabase es la ÚNICA fuente de verdad
  // Esto evita conflictos entre usuarios y empresas
  // ==========================================
  // No hacer nada - solo Supabase guarda cuadres
}

// ====================== INVENTARIO – LOCAL STORAGE ======================
const STORAGE_INV_KEY = "inventariosDiarios_v1";

function loadInventarios() {
  try {
    const raw = localStorage.getItem(STORAGE_INV_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveInventarios(lista) {
  localStorage.setItem(STORAGE_INV_KEY, JSON.stringify(lista || []));
}

// ====================== INVENTARIO – LISTAS DE PRODUCTOS ======================
const PRODUCTOS_INVENTARIO_DIARIO = [
  "Tomate",
  "Lechuga crespa",
  "Lechuga batavia",
  "Cebolla",
  "Plátanos",
  "Limones",
  "Ajo",
  "Pimentón",
  "Zanahoria",
  "Pan hamburguesa",
  "Pan perro",
  "Pan infantil",
  "Ripio",
  "Salchicha americana",
  "Salchicha ranchera",
  "Queso cuajada",
  "Queso loncha",
  "Tocineta",
  "Maíz",
  "Solomo desgranado",
  "Desgranado pollo",
  "Cerdo desgranado",
];

const PRODUCTOS_INVENTARIO_ADICIONAL = [
  "Aluminio",
  "6x8",
  "4x8",
  "Bolsa de basura",
  "T25",
  "T30",
  "Vaso desechable",
  "Tapas vaso",
  "Plato hondo 12 oz",
  "C1",
  "P1",
  "Plato pando 23 cm",
  "Copas 05",
  "Tapas 0.5",
  "Porta cubiertos",
  "Bolsa R15 estampada",
  "Pitillos",
  "Palos de chuzo",
  "Cucharas malteada",
  "Cuchara desgranado",
  "Bowl 26 oz",
  "Tapas bowl 26 oz",
  "Bowl 44 oz",
  "Tapas 44 oz",
  "Margarina",
  "Caja de hamburguesa",
  "Caja de perro",
  "Caja de pincho",
  "Servilletas mesa",
  "Servilletas domicilio",
  "Sal paprika",
  "Piña en lata",
  "Sal parrillera",
  "Aceite de oliva",
  "Mostaza",
  "Salsa de piña",
  "Salsa rosada",
  "Cheddar",
  "Guacamole",
  "Dulce maíz",
  "Salsa chocolate",
  "Vinagreta",
  "Salsa de queso",
  "Chip de chocolate",
  "Cofias",
  "Laurel molido",
  "Tomillo molido",
  "Orégano molido",
  "Azúcar",
  "Aceite",
  "Lijas",
  "Papas francesa",
  "Papas criolla",
  "Papas casco",
  "Dedos de queso",
  "Yucas",
  "Helado fresa",
  "Helado vainilla",
  "Helado chocolate",
  "Costillas",
  "Baby beef",
  "Churrasco",
  "Punta",
  "Pechugas",
  "Desengrasante",
  "Límpido",
  "Detergente",
  "Pulpa de lulo",
  "Pulpa de mora",
  "Pulpa de mango",
  "Pulpa de maracuyá",
  "Pulpa guanábana",
  "Pulpa limonada coco",
  "Arepas pequeñas",
  "Arepas grandes",
];

/* ====================== ROOT ====================== */
 
export function CuadreDiarioRoot({ sedes, empleados, usuarioActual = null, empleadoActual = null, esGerente = false, tienePermiso = () => true, filtroEmpresa = null }) {
  const [cuadres, setCuadres] = useState(() => loadCuadres());
  // Determinar modo inicial según permisos
  const modoInicial = tienePermiso("admin_cuadre_abrir") ? "crear" : 
                      tienePermiso("admin_cuadre_consultar") ? "consultar" : "crear";
  const [modo, setModo] = useState(modoInicial); // "crear" | "consultar"
  const [cuadreActivoId, setCuadreActivoId] = useState(null);
  const [cargandoCuadres, setCargandoCuadres] = useState(false);
  const [cuadreEditando, setCuadreEditando] = useState(null); // Para modal de edición
  const [cuadreAsignarColaborador, setCuadreAsignarColaborador] = useState(null); // Para modal de asignar colaborador
  
  // Estados para el sistema de bloqueo y solicitudes
  const [solicitudesPendientes, setSolicitudesPendientes] = useState([]);
  const [bloqueoActual, setBloqueoActual] = useState(null); // Info del bloqueo si hay uno
  const [mostrarModalSolicitud, setMostrarModalSolicitud] = useState(false);
  const [cuadreBloqueadoInfo, setCuadreBloqueadoInfo] = useState(null);
  const [enviandoSolicitud, setEnviandoSolicitud] = useState(false);
  const [solicitudEnviada, setSolicitudEnviada] = useState(false);
  const [cuadresConAccesoAprobado, setCuadresConAccesoAprobado] = useState([]); // IDs de cuadres con acceso aprobado
  
  // Estados para gestión de accesos (solo gerentes)
  const [mostrarModalGestionAccesos, setMostrarModalGestionAccesos] = useState(false);
  const [cuadreGestionAccesos, setCuadreGestionAccesos] = useState(null);
  const [accesosDelCuadre, setAccesosDelCuadre] = useState([]);
  const [cargandoAccesos, setCargandoAccesos] = useState(false);
  
  // Estados para trabajo colaborativo en tiempo real
  const [usuariosActivosEnCuadre, setUsuariosActivosEnCuadre] = useState([]);
  const [ultimaActualizacionRemota, setUltimaActualizacionRemota] = useState(null);
  const [ultimaSincronizacion, setUltimaSincronizacion] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);
  const ignorarProximaActualizacion = React.useRef(false);
  const broadcastChannelRef = React.useRef(null); // Canal de broadcast para sincronización instantánea
  const recibiendoBroadcastRef = React.useRef(false); // 🛡️ Flag para evitar loop de sincronización
  
  // 🔹 MODO COLABORADOR - Solo Transferencias y Domicilios
  const [modoColaborador, setModoColaborador] = useState(false);
  const [colaboradorCuadreId, setColaboradorCuadreId] = useState(null);
  const [colaboradorCuadre, setColaboradorCuadre] = useState(null);
  const [colaboradorSincronizando, setColaboradorSincronizando] = useState(false);
  
  // 🔒 BLOQUEO DE FILAS - Evita conflictos cuando dos editan
  // Formato: { "transfer_rowId": { usuarioId, usuarioNombre, timestamp }, "domicilios_rowId": {...} }
  const [filasBloqueadas, setFilasBloqueadas] = useState({});
  const bloqueoTimeout = React.useRef({});
  
  // Función para bloquear una fila (cuando el usuario empieza a editar)
  const bloquearFila = React.useCallback((seccion, filaId) => {
    const miId = empleadoActual?.id || usuarioActual?.id || "";
    const miNombre = empleadoActual?.nombre || usuarioActual?.nombre || "Usuario";
    const clave = `${seccion}_${filaId}`;
    
    // Actualizar estado local
    setFilasBloqueadas(prev => ({
      ...prev,
      [clave]: { usuarioId: miId, usuarioNombre: miNombre, timestamp: Date.now() }
    }));
    
    // Enviar broadcast
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.send({
        type: 'broadcast',
        event: 'fila_bloqueada',
        payload: {
          seccion,
          filaId,
          usuarioId: miId,
          usuarioNombre: miNombre,
          timestamp: Date.now()
        }
      }).catch(e => console.error("Error enviando bloqueo:", e));
    }
    
    // Auto-liberar después de 10 segundos de inactividad
    if (bloqueoTimeout.current[clave]) {
      clearTimeout(bloqueoTimeout.current[clave]);
    }
    bloqueoTimeout.current[clave] = setTimeout(() => {
      desbloquearFila(seccion, filaId);
    }, 10000);
  }, [empleadoActual, usuarioActual]);
  
  // Función para desbloquear una fila
  const desbloquearFila = React.useCallback((seccion, filaId) => {
    const miId = empleadoActual?.id || usuarioActual?.id || "";
    const clave = `${seccion}_${filaId}`;
    
    // Solo desbloquear si yo la tengo bloqueada
    setFilasBloqueadas(prev => {
      if (prev[clave]?.usuarioId === miId) {
        const nuevo = { ...prev };
        delete nuevo[clave];
        return nuevo;
      }
      return prev;
    });
    
    // Enviar broadcast
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.send({
        type: 'broadcast',
        event: 'fila_desbloqueada',
        payload: {
          seccion,
          filaId,
          usuarioId: miId,
          timestamp: Date.now()
        }
      }).catch(e => console.error("Error enviando desbloqueo:", e));
    }
    
    // Limpiar timeout
    if (bloqueoTimeout.current[clave]) {
      clearTimeout(bloqueoTimeout.current[clave]);
      delete bloqueoTimeout.current[clave];
    }
  }, [empleadoActual, usuarioActual]);
  
  // Verificar si una fila está bloqueada por OTRO usuario
  const filaEstaBloqueadaPorOtro = React.useCallback((seccion, filaId) => {
    const miId = empleadoActual?.id || usuarioActual?.id || "";
    const clave = `${seccion}_${filaId}`;
    const bloqueo = filasBloqueadas[clave];
    
    if (!bloqueo) return null;
    if (bloqueo.usuarioId === miId) return null; // Yo la tengo, no está bloqueada para mí
    
    // Verificar que el bloqueo no sea muy viejo (más de 15 segundos)
    if (Date.now() - bloqueo.timestamp > 15000) {
      return null;
    }
    
    return bloqueo;
  }, [filasBloqueadas, empleadoActual, usuarioActual]);
  
  // � SISTEMA DE DEBOUNCE - Evitar sincronización mientras el usuario escribe
  const ultimaEdicionRef = React.useRef(Date.now());
  const TIEMPO_ESPERA_SYNC = 3000; // Esperar 3 segundos después de la última edición
  
  // Función para marcar que el usuario está editando
  const marcarEdicion = React.useCallback(() => {
    ultimaEdicionRef.current = Date.now();
  }, []);
  
  // ==========================================
  // 🧹 LIMPIEZA AUTOMÁTICA DE LOCALSTORAGE AL MONTAR
  // Esto elimina cualquier dato corrupto o viejo de cuadres que pueda causar conflictos
  // SUPABASE es la ÚNICA fuente de verdad para cuadres
  // ==========================================
  useEffect(() => {
    try {
      // Limpiar todas las claves de localStorage relacionadas con cuadres
      localStorage.removeItem("cuadresDiarios_v1");
      localStorage.removeItem("cuadresDiarios");
      localStorage.removeItem("cuadres_borradores");
      localStorage.removeItem(STORAGE_KEY); // "cuadres_diarios_v2" o similar
      console.log("🧹 localStorage de cuadres limpiado - Supabase es la única fuente de verdad");
    } catch (e) {
      console.error("Error limpiando localStorage:", e);
    }
  }, []); // Solo al montar el componente
  
  // DETECTOR GLOBAL DE ACTIVIDAD - Pausa sync mientras escribes
  useEffect(() => {
    const handleInput = (e) => {
      const target = e.target;
      if (target && (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.isContentEditable ||
        target.closest('.subcard')
      )) {
        ultimaEdicionRef.current = Date.now();
      }
    };
    
    document.addEventListener('input', handleInput, true);
    document.addEventListener('keydown', handleInput, true);
    document.addEventListener('change', handleInput, true);
    
    return () => {
      document.removeEventListener('input', handleInput, true);
      document.removeEventListener('keydown', handleInput, true);
      document.removeEventListener('change', handleInput, true);
    };
  }, []);
  
  // FUNCION DE SINCRONIZACION MANUAL/AUTOMATICA
  const sincronizarCuadreActivo = React.useCallback(async (mostrarIndicador = true) => {
    if (!cuadreActivoId || sincronizando) return;
    
    if (mostrarIndicador) setSincronizando(true);
    
    try {
      // Cargar el cuadre más reciente de Supabase
      const { data, error } = await supabase
        .from("cuadres_diarios")
        .select("*")
        .eq("id", cuadreActivoId)
        .maybeSingle();
      
      if (error) {
        console.error("❌ Error sincronizando:", error);
        return;
      }
      
      if (data) {
        const cuadreRemoto = mapearRowCuadreDesdeSupabase(data);
        
        setCuadres((prev) => {
          return prev.map((c) => {
            if (c.id === cuadreActivoId) {
              // Fusionar cambios remotos con locales
              return fusionarCuadresColaborativo(c, cuadreRemoto);
            }
            return c;
          });
        });
        
        setUltimaSincronizacion(new Date());
        console.log("✅ Sincronización completada");
      }
    } catch (e) {
      console.error("❌ Error en sincronización:", e);
    } finally {
      if (mostrarIndicador) setSincronizando(false);
    }
  }, [cuadreActivoId, sincronizando]);
  
  // ⏱️ AUTO-SYNC INTELIGENTE - Solo sincroniza si no estás escribiendo
  useEffect(() => {
    if (!cuadreActivoId) return;
    
    console.log("⏱️ Iniciando auto-sync inteligente para cuadre:", cuadreActivoId);
    
    const intervaloSync = setInterval(() => {
      const tiempoDesdeUltimaEdicion = Date.now() - ultimaEdicionRef.current;
      
      // Solo sincronizar si han pasado al menos 3 segundos desde la última edición
      if (tiempoDesdeUltimaEdicion >= TIEMPO_ESPERA_SYNC) {
        sincronizarCuadreActivo(false); // Sin mostrar indicador para auto-sync
      } else {
        console.log(`⏸️ Sincronización pausada - usuario editando (${Math.round(tiempoDesdeUltimaEdicion/1000)}s desde última edición)`);
      }
    }, 5000); // Verificar cada 5 segundos (antes era 2)
    
    return () => {
      clearInterval(intervaloSync);
      console.log("⏱️ Auto-sync detenido");
    };
  }, [cuadreActivoId, sincronizarCuadreActivo]);
 
  useEffect(() => {
    const borradores = cuadres.filter((c) => c.estado !== "final");
    saveCuadres(borradores);
  }, [cuadres]);
  
  // ====================== SUSCRIPCIÓN EN TIEMPO REAL PARA TRABAJO COLABORATIVO ======================
  useEffect(() => {
    if (!cuadreActivoId) {
      setUsuariosActivosEnCuadre([]);
      return;
    }
    
    const miId = empleadoActual?.id || usuarioActual?.id || "";
    const miNombre = empleadoActual?.nombre || usuarioActual?.nombre || "Usuario";
    
    console.log("🔴 Iniciando suscripción en tiempo real para cuadre:", cuadreActivoId);
    
    // 🚀 CANAL DE BROADCAST - Sincronización INSTANTÁNEA (menos de 100ms)
    const broadcastChannel = supabase
      .channel(`cuadre-broadcast-${cuadreActivoId}`, {
        config: {
          broadcast: { self: false, ack: true }, // No recibir propios mensajes, confirmar recepción
        }
      })
      .on('broadcast', { event: 'cuadre_update' }, (payload) => {
        console.log("⚡ Cambio recibido via Broadcast");
        
        // Si el cambio viene de nosotros mismos, ignorarlo
        if (payload.payload?.senderId === miId) {
          console.log("⏭️ Ignorando actualización propia (broadcast)");
          return;
        }
        
        const cuadreRemoto = payload.payload?.cuadre;
        if (cuadreRemoto) {
          // 🛡️ MARCAR QUE ESTAMOS RECIBIENDO BROADCAST - evita loop de sincronización
          recibiendoBroadcastRef.current = true;
          
          // 🛡️ Verificar si el usuario está editando activamente
          const tiempoDesdeUltimaEdicion = Date.now() - ultimaEdicionRef.current;
          const usuarioEditando = tiempoDesdeUltimaEdicion < TIEMPO_ESPERA_SYNC;
          
          // 🛡️ NUEVA PROTECCIÓN: Si el usuario editó en los últimos 5 segundos,
          // SOLO actualizar campos simples, NO arrays (para evitar perder lo que escribe)
          if (usuarioEditando) {
            console.log("⏸️ Usuario editando - SOLO sincronizar campos simples, NO arrays");
            
            setCuadres((prev) => {
              return prev.map((c) => {
                if (c.id === cuadreActivoId) {
                  // Solo actualizar campos que NO son arrays editables
                  return {
                    ...c,
                    // Mantener arrays locales intactos mientras edita
                    facturasEliminadas: [...(c.facturasEliminadas || []), ...(cuadreRemoto.facturasEliminadas || []).filter(fe => 
                      !(c.facturasEliminadas || []).some(local => local.id === fe.id)
                    )],
                    // Sincronizar estado y campos no editables
                    estado: cuadreRemoto.estado === "final" ? "final" : c.estado,
                    updated_at: c.updated_at, // Mantener timestamp local
                  };
                }
                return c;
              });
            });
          } else {
            console.log("🔄 Aplicando cambios remotos COMPLETOS");
            setUltimaActualizacionRemota(new Date().toISOString());
            ignorarProximaActualizacion.current = true;
            
            setCuadres((prev) => {
              return prev.map((c) => {
                if (c.id === cuadreActivoId) {
                  return fusionarCuadresColaborativo(c, cuadreRemoto, false);
                }
                return c;
              });
            });
          }
          
          // Resetear flag después de 500ms para permitir futuras sincronizaciones
          setTimeout(() => {
            recibiendoBroadcastRef.current = false;
          }, 500);
        }
      })
      // � NUEVO: Receptor de ACTUALIZACIONES PARCIALES (Modo Colaborador)
      .on('broadcast', { event: 'cuadre_partial_update' }, (payload) => {
        console.log("⚡ Actualización PARCIAL recibida via Broadcast");
        
        // Si el cambio viene de nosotros mismos, ignorarlo
        if (payload.payload?.senderId === miId) {
          console.log("⏭️ Ignorando actualización parcial propia");
          return;
        }
        
        const { cuadreId, seccion, datos, timestamp } = payload.payload || {};
        
        if (cuadreId && seccion && datos) {
          console.log(`🔄 Aplicando actualización parcial: ${seccion}`);
          
          // Aplicar SOLO la sección que cambió, sin tocar el resto
          setCuadres((prev) => {
            return prev.map((c) => {
              if (c.id === cuadreId) {
                // Solo actualizar si el timestamp remoto es más reciente
                const timestampLocal = c[`${seccion}_timestamp`] || 0;
                if (timestamp > timestampLocal) {
                  console.log(`✅ Aplicando ${seccion} (remoto más reciente)`);
                  return {
                    ...c,
                    [seccion]: datos,
                    [`${seccion}_timestamp`]: timestamp
                  };
                } else {
                  console.log(`⏭️ Ignorando ${seccion} (local más reciente)`);
                  return c;
                }
              }
              return c;
            });
          });
          
          // También actualizar colaboradorCuadre si está activo
          if (modoColaborador && colaboradorCuadreId === cuadreId) {
            setColaboradorCuadre(prev => {
              if (!prev) return prev;
              const timestampLocal = prev[`${seccion}_timestamp`] || 0;
              if (timestamp > timestampLocal) {
                return {
                  ...prev,
                  [seccion]: datos,
                  [`${seccion}_timestamp`]: timestamp
                };
              }
              return prev;
            });
          }
        }
      })
      // 🗑️ NUEVO: Escuchar evento de eliminación de cuadre
      .on('broadcast', { event: 'cuadre_eliminado' }, (payload) => {
        console.log("🗑️ Evento de eliminación recibido via Broadcast");
        
        const cuadreIdEliminado = payload.payload?.cuadreId;
        if (cuadreIdEliminado && cuadreIdEliminado === cuadreActivoId) {
          console.log("⚠️ El cuadre activo fue eliminado por otro usuario:", cuadreIdEliminado);
          
          // Marcar como eliminado en localStorage
          try {
            const eliminados = localStorage.getItem(STORAGE_ELIMINADOS_KEY);
            const listaEliminados = eliminados ? JSON.parse(eliminados) : [];
            if (!listaEliminados.includes(cuadreIdEliminado)) {
              listaEliminados.push(cuadreIdEliminado);
              localStorage.setItem(STORAGE_ELIMINADOS_KEY, JSON.stringify(listaEliminados));
            }
          } catch (e) {
            console.error("Error guardando lista de eliminados:", e);
          }
          
          // Eliminar del estado local
          setCuadres((prev) => prev.filter((c) => c.id !== cuadreIdEliminado));
          setCuadreActivoId(null);
          
          // Notificar al usuario
          alert("⚠️ El cuadre que estabas viendo fue eliminado por otro usuario.");
        }
      })
      // 🔒 NUEVO: Escuchar evento de CIERRE DEFINITIVO de cuadre
      .on('broadcast', { event: 'cuadre_cerrado' }, (payload) => {
        console.log("🔒 Evento de CIERRE DEFINITIVO recibido via Broadcast");
        
        // Si el cierre viene de nosotros mismos, ignorarlo
        if (payload.payload?.senderId === miId) {
          console.log("⏭️ Ignorando cierre propio");
          return;
        }
        
        const cuadreIdCerrado = payload.payload?.cuadreId;
        const cuadreCerrado = payload.payload?.cuadreCerrado;
        const cerradoPor = payload.payload?.cerradoPor || "otro usuario";
        
        if (cuadreIdCerrado && cuadreCerrado) {
          console.log("🔒 Cuadre CERRADO DEFINITIVAMENTE por:", cerradoPor, "ID:", cuadreIdCerrado);
          
          // 🔥 LIMPIAR INMEDIATAMENTE del localStorage
          try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
              const locales = JSON.parse(stored);
              const limpios = locales.filter(c => c.id !== cuadreIdCerrado);
              if (limpios.length !== locales.length) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(limpios));
                console.log("🧹 Cuadre cerrado eliminado del localStorage:", cuadreIdCerrado);
              }
            }
          } catch (e) {
            console.error("Error limpiando cuadre cerrado del localStorage:", e);
          }
          
          // FORZAR la actualización del estado - SIN importar si el usuario está editando
          // El cierre definitivo tiene PRIORIDAD ABSOLUTA
          setCuadres((prev) => {
            return prev.map((c) => {
              if (c.id === cuadreIdCerrado) {
                // Usar el cuadre cerrado completo, NO fusionar
                return {
                  ...cuadreCerrado,
                  estado: "final" // Asegurar estado cerrado
                };
              }
              return c;
            });
          });
          
          // Notificar al usuario solo si es el cuadre que estaba viendo
          if (cuadreIdCerrado === cuadreActivoId) {
            mostrarAlertaVisual('info', `🔒 El cuadre fue cerrado definitivamente por ${cerradoPor}. Ya no se puede modificar.`);
          }
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log("⚡ Canal Broadcast conectado - sincronización INSTANTÁNEA activa");
        }
      });
    
    broadcastChannelRef.current = broadcastChannel;
    
    // Canal de respaldo: postgres_changes (por si broadcast falla)
    const channel = supabase
      .channel(`cuadre-realtime-${cuadreActivoId}`, {
        config: {
          broadcast: { self: false },
          presence: { key: '' },
        }
      })
      .on(
        'postgres_changes',
        {
          event: '*', // Escuchar INSERT, UPDATE y DELETE
          schema: 'public',
          table: 'cuadres_diarios',
          filter: `id=eq.${cuadreActivoId}`
        },
        (payload) => {
          console.log("📡 Cambio recibido en tiempo real:", payload.eventType);
          
          // Si el cambio viene de nosotros mismos, ignorarlo
          if (ignorarProximaActualizacion.current) {
            console.log("⏭️ Ignorando actualización propia");
            ignorarProximaActualizacion.current = false;
            return;
          }
          
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            const cuadreRemoto = mapearRowCuadreDesdeSupabase(payload.new);
            if (cuadreRemoto) {
              // 🛡️ MARCAR QUE ESTAMOS RECIBIENDO DATOS REMOTOS - evita loop
              recibiendoBroadcastRef.current = true;
              
              // 🛡️ Verificar si el usuario está editando
              const tiempoDesdeUltimaEdicion = Date.now() - ultimaEdicionRef.current;
              const usuarioEditando = tiempoDesdeUltimaEdicion < TIEMPO_ESPERA_SYNC;
              
              if (usuarioEditando) {
                // Usuario editando - NO actualizar arrays para no perder sus cambios
                console.log("⏸️ Usuario editando - ignorando postgres_changes para proteger datos locales");
              } else {
                console.log("🔄 Aplicando cambios remotos al cuadre - EN LÍNEA");
                setUltimaActualizacionRemota(new Date().toISOString());
                
                // Fusionar cambios remotos con el estado local
                setCuadres((prev) => {
                  return prev.map((c) => {
                    if (c.id === cuadreActivoId) {
                      return fusionarCuadresColaborativo(c, cuadreRemoto, false);
                    }
                    return c;
                  });
                });
              }
              
              // Resetear flag después de 500ms
              setTimeout(() => {
                recibiendoBroadcastRef.current = false;
              }, 500);
            }
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log("✅ Conectado en tiempo real - sincronización instantánea activa");
        } else {
          console.log("📡 Estado de suscripción realtime:", status);
        }
      });
    
    // Suscribirse a usuarios activos en el cuadre
    const channelUsuarios = supabase
      .channel(`cuadre-usuarios-${cuadreActivoId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'cuadres_usuarios_activos',
          filter: `cuadre_id=eq.${cuadreActivoId}`
        },
        async () => {
          // Recargar lista de usuarios activos
          const usuarios = await cargarUsuariosActivosEnCuadre(cuadreActivoId);
          setUsuariosActivosEnCuadre(usuarios);
        }
      )
      .subscribe();
    
    // Registrar usuario activo y cargar usuarios actuales
    if (miId) {
      registrarUsuarioActivoEnCuadre(cuadreActivoId, miId, miNombre);
      cargarUsuariosActivosEnCuadre(cuadreActivoId).then(setUsuariosActivosEnCuadre);
      
      // Actualizar presencia cada 15 segundos (más frecuente)
      const intervaloPresencia = setInterval(() => {
        registrarUsuarioActivoEnCuadre(cuadreActivoId, miId, miNombre);
      }, 15000);
      
      return () => {
        clearInterval(intervaloPresencia);
        removerUsuarioActivoEnCuadre(cuadreActivoId, miId);
        supabase.removeChannel(channel);
        supabase.removeChannel(channelUsuarios);
        supabase.removeChannel(broadcastChannel); // Limpiar canal broadcast
        broadcastChannelRef.current = null;
        console.log("🔴 Suscripción en tiempo real terminada");
      };
    }
    
    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(channelUsuarios);
      supabase.removeChannel(broadcastChannel); // Limpiar canal broadcast
      broadcastChannelRef.current = null;
    };
  }, [cuadreActivoId, empleadoActual, usuarioActual]);

  // 🔹 SUSCRIPCIÓN PARA MODO COLABORADOR
  const colaboradorBroadcastRef = React.useRef(null);
  
  useEffect(() => {
    if (!modoColaborador || !colaboradorCuadreId) {
      if (colaboradorBroadcastRef.current) {
        supabase.removeChannel(colaboradorBroadcastRef.current);
        colaboradorBroadcastRef.current = null;
      }
      return;
    }
    
    const miId = empleadoActual?.id || usuarioActual?.id || "";
    
    console.log("👥 [Colaborador] Iniciando suscripción para cuadre:", colaboradorCuadreId);
    
    const broadcastColaborador = supabase
      .channel(`cuadre-broadcast-${colaboradorCuadreId}`, {
        config: {
          broadcast: { self: false, ack: true },
        }
      })
      .on('broadcast', { event: 'cuadre_partial_update' }, (payload) => {
        console.log("👥 [Colaborador] Actualización parcial recibida");
        
        if (payload.payload?.senderId === miId) {
          return; // Ignorar propios mensajes
        }
        
        const { seccion, datos, timestamp } = payload.payload || {};
        
        if (seccion && datos) {
          setColaboradorCuadre(prev => {
            if (!prev) return prev;
            const timestampLocal = prev[`${seccion}_timestamp`] || 0;
            if (timestamp > timestampLocal) {
              console.log(`👥 [Colaborador] Aplicando ${seccion}`);
              return {
                ...prev,
                [seccion]: datos,
                [`${seccion}_timestamp`]: timestamp
              };
            }
            return prev;
          });
        }
      })
      .on('broadcast', { event: 'cuadre_update' }, (payload) => {
        console.log("👥 [Colaborador] Actualización completa recibida");
        
        if (payload.payload?.senderId === miId) {
          return;
        }
        
        const cuadreRemoto = payload.payload?.cuadre;
        if (cuadreRemoto) {
          // Solo actualizar transferencias y domicilios del colaborador
          setColaboradorCuadre(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              transfer: cuadreRemoto.transfer || prev.transfer,
              domicilios: cuadreRemoto.domicilios || prev.domicilios
            };
          });
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log("👥 [Colaborador] Canal conectado");
        }
      });
    
    colaboradorBroadcastRef.current = broadcastColaborador;
    broadcastChannelRef.current = broadcastColaborador; // Para enviar actualizaciones
    
    return () => {
      if (colaboradorBroadcastRef.current) {
        supabase.removeChannel(colaboradorBroadcastRef.current);
        colaboradorBroadcastRef.current = null;
      }
    };
  }, [modoColaborador, colaboradorCuadreId, empleadoActual, usuarioActual]);

  useEffect(() => {
    let activo = true;
    setCargandoCuadres(true);
    console.log("🔄 Cargando cuadres desde Supabase, filtroEmpresa:", filtroEmpresa);
    
    // 🔐 SEGURIDAD: Si no hay filtroEmpresa, no cargar ningún cuadre
    if (!filtroEmpresa) {
      console.warn("⚠️ No hay filtroEmpresa definido - no se cargarán cuadres por seguridad");
      setCuadres([]);
      setCargandoCuadres(false);
      return;
    }
    
    // Obtener lista de cuadres que fueron eliminados (para no re-sincronizarlos)
    const cuadresEliminados = obtenerCuadresEliminadosGlobal();
    console.log("🗑️ Cuadres marcados como eliminados:", cuadresEliminados.length);
    
    cargarCuadresDiariosDeSupabase(filtroEmpresa).then(async (lista) => {
      if (!activo) return;
      console.log("✅ Cuadres cargados de Supabase:", lista.length);
      
      // IMPORTANTE: Filtrar la lista de Supabase para excluir cuadres eliminados localmente
      const listaFiltrada = lista.filter(c => !cuadresEliminados.includes(c.id));
      console.log("🗑️ Cuadres filtrados (eliminados):", lista.length - listaFiltrada.length);
      
      // 🔥 NUEVO: Eliminar DEFINITIVAMENTE de Supabase los cuadres que están en la lista de eliminados
      // 🔐 SEGURIDAD: Solo eliminar cuadres de la empresa actual
      const cuadresAEliminarDeSupabase = lista.filter(c => cuadresEliminados.includes(c.id));
      if (cuadresAEliminarDeSupabase.length > 0) {
        console.log("🔥 Eliminando", cuadresAEliminarDeSupabase.length, "cuadres de Supabase que están marcados como eliminados...");
        for (const cuadre of cuadresAEliminarDeSupabase) {
          try {
            let query = supabase
              .from("cuadres_diarios")
              .delete()
              .eq("id", cuadre.id);
            
            // 🔐 Filtrar por empresa para seguridad adicional
            if (filtroEmpresa) {
              query = query.eq("empresa_id", filtroEmpresa);
            }
            
            const { error } = await query;
            if (error) {
              console.error("❌ Error eliminando cuadre de Supabase:", cuadre.id, error);
            } else {
              console.log("✅ Cuadre eliminado de Supabase definitivamente:", cuadre.id);
            }
          } catch (e) {
            console.error("❌ Error eliminando cuadre:", cuadre.id, e);
          }
        }
      }
      console.log("📋 Borradores en Supabase:", listaFiltrada.filter(c => c.estado === "borrador").length);
      console.log("📋 Finales en Supabase:", listaFiltrada.filter(c => c.estado === "final").length);
      
      // 🔥 PRIORIDAD ABSOLUTA: Supabase SIEMPRE tiene prioridad sobre datos locales
      // Esto garantiza que si un cuadre fue cerrado por otro usuario, se refleje correctamente
      setCuadres((prev) => {
        const borradoresLocales = (prev || []).filter((c) => c.estado !== "final");
        console.log("💾 Borradores locales:", borradoresLocales.length);
        
        // 🔒 IMPORTANTE: Solo agregar borradores locales que:
        // 1. NO existen en Supabase (por ID)
        // 2. NO fueron eliminados
        // Si un cuadre existe en Supabase (aunque sea con diferente estado), SUPABASE GANA
        const idsEnSupabase = new Set(listaFiltrada.map(c => c.id));
        const borradoresSinDuplicados = borradoresLocales.filter(
          (b) => !idsEnSupabase.has(b.id) && !cuadresEliminados.includes(b.id)
        );
        console.log("🔀 Borradores locales NUEVOS (no en Supabase):", borradoresSinDuplicados.length);
        
        // Si hay borradores locales que no están en Supabase, sincronizarlos
        if (borradoresSinDuplicados.length > 0) {
          console.log("🔄 Sincronizando", borradoresSinDuplicados.length, "borradores locales a Supabase...");
          borradoresSinDuplicados.forEach((borrador) => {
            guardarCuadreDiarioEnSupabase(borrador, filtroEmpresa).then((resultado) => {
              if (resultado) {
                console.log("✅ Borrador local sincronizado a Supabase:", resultado.id);
              } else {
                console.error("❌ Error sincronizando borrador local:", borrador.id);
              }
            }).catch((e) => {
              console.error("❌ Error sincronizando borrador local:", borrador.id, e);
            });
          });
        }
        
        // 🔥 RESULTADO: Supabase primero, luego borradores locales NUEVOS
        // Los cuadres de Supabase tienen el estado actualizado (incluyendo los cerrados)
        const resultado = [...listaFiltrada, ...borradoresSinDuplicados];
        console.log("📊 Total cuadres después de merge:", resultado.length);
        
        // 🔒 Limpiar localStorage de borradores que ya están cerrados en Supabase
        const cuadresCerradosEnSupabase = listaFiltrada.filter(c => c.estado === "final").map(c => c.id);
        if (cuadresCerradosEnSupabase.length > 0) {
          try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
              const locales = JSON.parse(stored);
              const limpios = locales.filter(c => !cuadresCerradosEnSupabase.includes(c.id));
              if (limpios.length !== locales.length) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(limpios));
                console.log("🧹 Limpiados", locales.length - limpios.length, "cuadres cerrados del localStorage");
              }
            }
          } catch (e) {
            console.error("Error limpiando localStorage:", e);
          }
        }
        
        return resultado;
      });
      setCargandoCuadres(false);
    }).catch((e) => {
      console.error("❌ Fallo sincronizando cuadres con Supabase", e);
      if (activo) {
        setCargandoCuadres(false);
      }
    });
    return () => {
      activo = false;
    };
  }, [filtroEmpresa]);

  // 🔒 CANAL GENERAL: Escuchar cierres de cuadres de la empresa (aunque no tengamos cuadre activo)
  useEffect(() => {
    if (!filtroEmpresa) return;
    
    const miId = empleadoActual?.id || usuarioActual?.id || "";
    const canalGeneralId = `cuadres-empresa-${filtroEmpresa}`;
    
    console.log("📡 Suscribiéndose al canal general de cuadres:", canalGeneralId);
    
    const canalGeneral = supabase.channel(canalGeneralId)
      .on('broadcast', { event: 'cuadre_cerrado' }, (payload) => {
        // Ignorar nuestros propios cierres
        if (payload.payload?.senderId === miId) return;
        
        const cuadreIdCerrado = payload.payload?.cuadreId;
        const cuadreCerrado = payload.payload?.cuadreCerrado;
        const cerradoPor = payload.payload?.cerradoPor || "otro usuario";
        
        if (cuadreIdCerrado && cuadreCerrado) {
          console.log("📡 [Canal General] Cuadre cerrado:", cuadreIdCerrado, "por:", cerradoPor);
          
          // Limpiar de localStorage
          try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
              const locales = JSON.parse(stored);
              const limpios = locales.filter(c => c.id !== cuadreIdCerrado);
              if (limpios.length !== locales.length) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(limpios));
                console.log("🧹 [Canal General] Cuadre cerrado eliminado del localStorage");
              }
            }
          } catch (e) {
            console.error("Error limpiando localStorage desde canal general:", e);
          }
          
          // Actualizar estado local
          setCuadres((prev) => {
            const yaExiste = prev.some(c => c.id === cuadreIdCerrado);
            if (yaExiste) {
              return prev.map((c) => c.id === cuadreIdCerrado ? { ...cuadreCerrado, estado: "final" } : c);
            }
            // Si no existe, agregarlo como cerrado
            return [...prev, { ...cuadreCerrado, estado: "final" }];
          });
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log("📡 Conectado al canal general de cuadres de la empresa");
        }
      });
    
    return () => {
      console.log("📡 Desconectando canal general de cuadres");
      supabase.removeChannel(canalGeneral);
    };
  }, [filtroEmpresa, empleadoActual, usuarioActual]);

  // Cargar solicitudes pendientes periódicamente
  useEffect(() => {
    const miId = empleadoActual?.id || usuarioActual?.id || "";
    if (!miId) return;
    
    const cargarSolicitudes = async () => {
      const solicitudes = await cargarSolicitudesPendientes(miId, esGerente);
      setSolicitudesPendientes(solicitudes);
    };
    
    cargarSolicitudes();
    // Verificar cada 10 segundos
    const intervalo = setInterval(cargarSolicitudes, 10000);
    
    return () => clearInterval(intervalo);
  }, [empleadoActual, usuarioActual, esGerente]);

  // Cargar cuadres con acceso aprobado para el usuario actual
  useEffect(() => {
    const miId = empleadoActual?.id || usuarioActual?.id || "";
    if (!miId) return;
    
    const cargarAccesosAprobados = async () => {
      try {
        const { data, error } = await supabase
          .from("cuadres_solicitudes")
          .select("cuadre_id")
          .eq("solicitante_id", String(miId))
          .eq("estado", "aprobado");
        
        if (error) {
          console.error("❌ Error cargando accesos aprobados:", error);
          return;
        }
        
        if (data && data.length > 0) {
          const ids = data.map(s => String(s.cuadre_id));
          setCuadresConAccesoAprobado(ids);
          console.log("✅ Cuadres con acceso aprobado para", miId, ":", ids);
        } else {
          setCuadresConAccesoAprobado([]);
          console.log("ℹ️ No hay cuadres con acceso aprobado para", miId);
        }
      } catch (e) {
        console.error("Error cargando accesos aprobados:", e);
      }
    };
    
    cargarAccesosAprobados();
    // Verificar cada 5 segundos (más frecuente para mejor experiencia)
    const intervalo = setInterval(cargarAccesosAprobados, 5000);
    
    return () => clearInterval(intervalo);
  }, [empleadoActual, usuarioActual]);

  // Bloquear cuadre cuando se abre y desbloquear cuando se cierra
  useEffect(() => {
    const miId = empleadoActual?.id || usuarioActual?.id || "";
    const miNombre = empleadoActual?.nombre || usuarioActual?.nombre || "Usuario";
    
    if (cuadreActivoId && miId) {
      bloquearCuadre(cuadreActivoId, miId, miNombre);
    }
    
    // Desbloquear al salir del cuadre
    return () => {
      if (cuadreActivoId) {
        desbloquearCuadre(cuadreActivoId);
      }
    };
  }, [cuadreActivoId, empleadoActual, usuarioActual]);

  // Manejar solicitud de acceso a cuadre bloqueado
  const handleSolicitarAcceso = async (cuadreId, propietarioId) => {
    const miId = empleadoActual?.id || usuarioActual?.id || "";
    const miNombre = empleadoActual?.nombre || usuarioActual?.nombre || "Usuario";
    
    if (!miId) {
      alert("Error: No se pudo identificar al usuario");
      return;
    }
    
    setEnviandoSolicitud(true);
    const resultado = await enviarSolicitudAcceso(cuadreId, miId, miNombre, propietarioId);
    setEnviandoSolicitud(false);
    
    if (resultado) {
      setSolicitudEnviada(true);
      alert("✅ Solicitud enviada. El empleado que tiene el cuadre abierto o un gerente debe aprobar tu acceso.");
    } else {
      alert("❌ Error al enviar la solicitud. Intenta nuevamente.");
    }
  };

  // Aprobar o rechazar solicitud
  const handleResponderSolicitud = async (solicitud, aprobado) => {
    console.log("🔄 Respondiendo solicitud:", solicitud.id, "Aprobado:", aprobado, "Cuadre:", solicitud.cuadre_id);
    const exito = await responderSolicitudAcceso(solicitud.id, aprobado, solicitud.cuadre_id);
    
    if (exito) {
      console.log("✅ Solicitud procesada exitosamente");
      setSolicitudesPendientes((prev) => prev.filter((s) => s.id !== solicitud.id));
      alert(aprobado ? "✅ Solicitud aprobada. El usuario puede acceder al cuadre." : "❌ Solicitud rechazada.");
    } else {
      console.error("❌ Error procesando solicitud");
      alert("Error al procesar la solicitud. Intenta nuevamente.");
    }
  };

  // Abrir modal de gestión de accesos (solo gerentes)
  const handleGestionarAccesos = async (cuadre) => {
    setCuadreGestionAccesos(cuadre);
    setCargandoAccesos(true);
    setMostrarModalGestionAccesos(true);
    
    const accesos = await cargarAccesosAprobadosPorCuadre(cuadre.id);
    
    // Verificar si el propietario tiene acceso revocado
    // El propietario revocado se identifica cuando solicitante_id = propietario_id y estado = revocado
    const { data: propietarioRevocado } = await supabase
      .from("cuadres_solicitudes")
      .select("*")
      .eq("cuadre_id", cuadre.id)
      .eq("solicitante_id", cuadre.empleadoId)
      .eq("propietario_id", cuadre.empleadoId)
      .eq("estado", "revocado")
      .maybeSingle();
    
    console.log("🔍 Verificando propietario revocado para cuadre:", cuadre.id, "empleadoId:", cuadre.empleadoId);
    console.log("🔍 Resultado:", propietarioRevocado);
    
    // Agregar al propietario del cuadre como primer elemento
    const propietario = empleados.find((e) => String(e.id) === String(cuadre.empleadoId));
    const listaTotalAccesos = [];
    
    if (propietario) {
      listaTotalAccesos.push({
        id: `propietario-${cuadre.id}`,
        solicitante_id: propietario.id,
        solicitante_nombre: propietario.nombre,
        esPropietario: true,
        accesoRevocado: !!propietarioRevocado,
        fecha_solicitud: cuadre.fecha,
        cuadre_id: cuadre.id
      });
    }
    
    // Agregar los accesos aprobados (excluyendo al propietario si ya está)
    accesos.forEach((acceso) => {
      if (String(acceso.solicitante_id) !== String(cuadre.empleadoId)) {
        listaTotalAccesos.push(acceso);
      }
    });
    
    setAccesosDelCuadre(listaTotalAccesos);
    setCargandoAccesos(false);
  };

  // Revocar acceso de un usuario a un cuadre
  const handleRevocarAcceso = async (acceso) => {
    const nombreUsuario = acceso.solicitante_nombre || 'este usuario';
    
    if (acceso.esPropietario) {
      // Si es el propietario, revocar su acceso pero mantener el registro de quién abrió el cuadre
      if (!window.confirm(`¿Seguro que deseas quitar el acceso de ${nombreUsuario} (propietario original) a este cuadre? El empleado ya no podrá editarlo pero seguirá apareciendo como quien lo abrió.`)) {
        return;
      }
      
      // Primero verificar si ya existe un registro de acceso revocado para el propietario
      const { data: existente } = await supabase
        .from("cuadres_solicitudes")
        .select("id")
        .eq("cuadre_id", acceso.cuadre_id)
        .eq("solicitante_id", acceso.solicitante_id)
        .eq("propietario_id", acceso.solicitante_id)
        .eq("estado", "revocado")
        .maybeSingle();
      
      if (existente) {
        // Ya existe, no hacer nada
        setAccesosDelCuadre((prev) => prev.map((a) => 
          a.id === acceso.id ? { ...a, accesoRevocado: true } : a
        ));
        alert(`✅ El acceso de ${nombreUsuario} ya estaba revocado.`);
        return;
      }
      
      // Crear un registro de acceso revocado para el propietario
      // El propietario se identifica porque solicitante_id === propietario_id
      console.log("📝 Insertando registro de revocación del propietario:", {
        cuadre_id: acceso.cuadre_id,
        solicitante_id: acceso.solicitante_id,
        solicitante_nombre: acceso.solicitante_nombre,
        propietario_id: acceso.solicitante_id,
        estado: "revocado"
      });
      
      const { data: insertedData, error } = await supabase
        .from("cuadres_solicitudes")
        .insert({
          cuadre_id: acceso.cuadre_id,
          solicitante_id: acceso.solicitante_id,
          solicitante_nombre: acceso.solicitante_nombre,
          propietario_id: acceso.solicitante_id,
          estado: "revocado",
          fecha_solicitud: new Date().toISOString()
        })
        .select();
      
      console.log("📝 Resultado del insert:", { data: insertedData, error });
      
      if (error) {
        console.error("❌ Error revocando acceso del propietario:", error);
        alert("❌ Error al revocar el acceso. Intenta nuevamente. Error: " + error.message);
        return;
      }
      
      if (!insertedData || insertedData.length === 0) {
        console.error("❌ No se insertó ningún registro");
        alert("❌ Error: No se pudo guardar la revocación. Verifica los permisos en Supabase.");
        return;
      }
      
      console.log("✅ Registro insertado exitosamente:", insertedData);
      
      setAccesosDelCuadre((prev) => prev.map((a) => 
        a.id === acceso.id ? { ...a, accesoRevocado: true } : a
      ));
      alert(`✅ Acceso de ${nombreUsuario} revocado. El empleado sigue apareciendo como quien abrió el cuadre pero ya no puede editarlo.`);
    } else {
      // Es un acceso aprobado normal
      if (!window.confirm(`¿Seguro que deseas revocar el acceso de ${nombreUsuario} a este cuadre?`)) {
        return;
      }
      
      const exito = await revocarAccesoCuadre(acceso.id);
      
      if (exito) {
        setAccesosDelCuadre((prev) => prev.filter((a) => a.id !== acceso.id));
        alert("✅ Acceso revocado exitosamente.");
      } else {
        alert("❌ Error al revocar el acceso. Intenta nuevamente.");
      }
    }
  };

  // Restaurar acceso de un usuario a un cuadre (solo para propietarios con acceso revocado)
  const handleRestaurarAcceso = async (acceso) => {
    const nombreUsuario = acceso.solicitante_nombre || 'este usuario';
    
    if (!window.confirm(`¿Seguro que deseas restaurar el acceso de ${nombreUsuario} a este cuadre?`)) {
      return;
    }
    
    // Eliminar el registro de acceso revocado del propietario
    // El propietario se identifica porque solicitante_id === propietario_id
    const { error } = await supabase
      .from("cuadres_solicitudes")
      .delete()
      .eq("cuadre_id", acceso.cuadre_id)
      .eq("solicitante_id", acceso.solicitante_id)
      .eq("propietario_id", acceso.solicitante_id)
      .eq("estado", "revocado");
    
    if (error) {
      console.error("❌ Error restaurando acceso:", error);
      alert("❌ Error al restaurar el acceso. Intenta nuevamente.");
      return;
    }
    
    setAccesosDelCuadre((prev) => prev.map((a) => 
      a.id === acceso.id ? { ...a, accesoRevocado: false } : a
    ));
    alert(`✅ Acceso de ${nombreUsuario} restaurado. El empleado puede editar el cuadre nuevamente.`);
  };

  // ====================== FUNCIÓN GLOBAL: Obtener facturas pendientes de días anteriores ======================
  // Esta función busca facturas pendientes de días anteriores que NO han sido pagadas
  // FILTRO: Solo considera cuadres a partir del 7 de febrero de 2026
  // IDENTIFICACIÓN: Por ID único (origenId), permitiendo múltiples facturas con el mismo nombre
  const FECHA_MINIMA_FACTURAS_GLOBAL = "2026-02-07";
  
  const obtenerFacturasPendientesAnterioresGlobal = (sede, fecha, facturasActuales = []) => {
    const pendientes = [];
    
    // 🔍 DIAGNÓSTICO: Ver todos los cuadres de esta sede
    const cuadresDeSede = cuadres.filter(c => c.sede === sede);
    console.log(`🔍 [DIAGNÓSTICO GLOBAL] Sede: ${sede}, Fecha actual: ${fecha}`);
    console.log(`🔍 [DIAGNÓSTICO GLOBAL] Total cuadres cargados: ${cuadres.length}`);
    console.log(`🔍 [DIAGNÓSTICO GLOBAL] Cuadres de esta sede: ${cuadresDeSede.length}`);
    cuadresDeSede.forEach(c => {
      console.log(`   📅 Cuadre ${c.fecha} - Estado: ${c.estado} - Facturas: ${(c.facturas || []).length}`);
      (c.facturas || []).forEach(f => {
        console.log(`      💰 ID: ${f.id} - OrigenId: ${f.origenId || 'N/A'} - Factura: ${f.factura || '(vacía)'} - Estado: ${f.estado}`);
      });
    });
    
    // 1. Recolectar todas las facturas que ya fueron pagadas en cualquier cuadre (por origenId Y por id)
    const facturasPagadasPorId = new Set();
    cuadres.forEach((c) => {
      if (c.sede === sede && c.fecha >= FECHA_MINIMA_FACTURAS_GLOBAL && Array.isArray(c.facturas)) {
        c.facturas.forEach((f) => {
          const estadoLower = (f.estado || "").toLowerCase();
          if (estadoLower.includes("pagada") || estadoLower.includes("caja") || estadoLower.includes("transferencia")) {
            // Agregar AMBOS IDs para cubrir todos los casos de rastreo
            if (f.id) {
              facturasPagadasPorId.add(String(f.id));
            }
            if (f.origenId) {
              facturasPagadasPorId.add(String(f.origenId));
            }
          }
        });
      }
    });
    
    // 2. Recolectar IDs de facturas que YA están en el cuadre actual (para evitar duplicados)
    const idsYaEnCuadre = new Set();
    (facturasActuales || []).forEach((f) => {
      // Agregar tanto el origenId como el id para cubrir todos los casos
      if (f.origenId) {
        idsYaEnCuadre.add(String(f.origenId));
      }
      if (f.id) {
        idsYaEnCuadre.add(String(f.id));
      }
    });
    
    console.log("📋 [GLOBAL] IDs de facturas ya pagadas:", Array.from(facturasPagadasPorId));
    console.log("📋 [GLOBAL] IDs ya en cuadre actual:", Array.from(idsYaEnCuadre));
    
    // 3. Buscar facturas pendientes de días anteriores que NO estén pagadas NI ya en el cuadre (por ID único)
    cuadres.forEach((c) => {
      if (c.sede === sede && c.fecha < fecha && c.fecha >= FECHA_MINIMA_FACTURAS_GLOBAL && Array.isArray(c.facturas)) {
        c.facturas.forEach((f) => {
          const estadoLower = (f.estado || "").toLowerCase();
          if (estadoLower === "pendiente" || estadoLower.includes("pendiente")) {
            // El ID único es el origenId si existe, sino el id de la factura
            const idUnico = f.origenId || f.id;
            
            // Solo agregar si:
            // - La factura tiene ID
            // - NO ha sido pagada en ningún cuadre (por ID)
            // - NO está ya en el cuadre actual (por ID)
            if (idUnico && !facturasPagadasPorId.has(String(idUnico)) && !idsYaEnCuadre.has(String(idUnico))) {
              // IMPORTANTE: Preservar el origenId original si ya existe (factura que pasó por múltiples cuadres)
              // Si no tiene origenId, usar su id como origenId
              const origenIdReal = f.origenId || f.id;
              pendientes.push({
                id: uid(),
                origenId: origenIdReal, // SIEMPRE el ID de la factura original
                factura: f.factura,
                valor: f.valor,
                estado: "Pendiente",
                origenFecha: f.origenFecha || c.fecha,
              });
              console.log(`⏳ [GLOBAL] Factura pendiente encontrada: ${f.factura} (origenId: ${origenIdReal}) - Origen: ${f.origenFecha || c.fecha}`);
            } else if (idUnico && facturasPagadasPorId.has(String(idUnico))) {
              console.log(`✅ [GLOBAL] Factura ${f.factura} (ID: ${idUnico}) ya fue pagada - NO se carga`);
            } else if (idUnico && idsYaEnCuadre.has(String(idUnico))) {
              console.log(`🔄 [GLOBAL] Factura ${f.factura} (ID: ${idUnico}) ya está en el cuadre actual - NO se duplica`);
            }
          }
        });
      }
    });
    
    console.log(`📊 [GLOBAL] Total facturas pendientes encontradas: ${pendientes.length}`);
    return pendientes;
  };
  // ====================== FIN FUNCIÓN GLOBAL ======================

  // Intentar abrir un cuadre verificando si está bloqueado
  const handleAbrirCuadre = async (cuadre) => {
    const miId = empleadoActual?.id || usuarioActual?.id || "";
    const esMiCuadre = String(cuadre.empleadoId) === String(miId);
    const tieneAccesoAprobado = cuadresConAccesoAprobado.includes(String(cuadre.id));
    
    // ==========================================
    // 🔐 VERIFICAR ESTADO ACTUAL EN SUPABASE antes de abrir
    // Esto evita que aparezca como abierto si ya fue cerrado por otro usuario
    // ==========================================
    try {
      const { data: cuadreActualizado, error: errorVerificar } = await supabase
        .from("cuadres_diarios")
        .select("estado, fecha, sede")
        .eq("id", cuadre.id)
        .maybeSingle();
      
      if (errorVerificar) {
        console.error("❌ Error verificando estado del cuadre:", errorVerificar);
      } else if (cuadreActualizado) {
        // Si el cuadre ya está cerrado en Supabase pero local dice que no
        if (cuadreActualizado.estado === "final" && cuadre.estado !== "final") {
          console.log("🔐 El cuadre ya fue cerrado por otro usuario - actualizando estado local");
          setCuadres(prev => prev.map(c => 
            c.id === cuadre.id ? { ...c, estado: "final" } : c
          ));
          alert("ℹ️ Este cuadre ya fue cerrado. Se ha actualizado la información.");
          return; // No abrir - ya está cerrado
        }
      } else {
        // El cuadre no existe en Supabase - puede haber sido eliminado
        console.log("⚠️ El cuadre no existe en Supabase - posiblemente fue eliminado");
        setCuadres(prev => prev.filter(c => c.id !== cuadre.id));
        alert("⚠️ Este cuadre ya no existe. Se ha removido de la lista.");
        return;
      }
    } catch (e) {
      console.error("❌ Error en verificación de cuadre:", e);
      // Continuar de todos modos si hay error de conexión
    }
    
    // Verificar estrictamente si es gerente (doble verificación)
    const perfilActual = (empleadoActual?.perfil || usuarioActual?.perfil || usuarioActual?.rol || "").toString().toUpperCase();
    const esRealmenteGerente = esGerente && (
      perfilActual.includes("GERENCIA") || 
      perfilActual.includes("GERENTE") || 
      perfilActual.includes("ADMIN") ||
      perfilActual.includes("SUPERVISOR") ||
      perfilActual.includes("COORDINADOR") ||
      perfilActual === "SUPER_ADMIN"
    );
    
    console.log("🔍 Intentando abrir cuadre:", cuadre.id);
    console.log("   - Mi ID:", miId);
    console.log("   - Es mi cuadre:", esMiCuadre);
    console.log("   - Tiene acceso aprobado:", tieneAccesoAprobado);
    console.log("   - Es gerente (prop):", esGerente);
    console.log("   - Es REALMENTE gerente:", esRealmenteGerente);
    console.log("   - Perfil actual:", perfilActual);
    console.log("   - Lista de accesos aprobados:", cuadresConAccesoAprobado);

    // 🔓 Permiso para retomar cualquier cuadre en proceso de cualquier sede, sin solicitar acceso
    const puedeRetomarCualquiera = tienePermiso("admin_cuadre_retomar_cualquiera");
    console.log("   - Puede retomar cualquiera:", puedeRetomarCualquiera);
    
    // Verificar si el propietario tiene acceso revocado
    // El propietario revocado se identifica cuando solicitante_id = propietario_id y estado = revocado
    if (esMiCuadre) {
      const { data: propietarioRevocado } = await supabase
        .from("cuadres_solicitudes")
        .select("*")
        .eq("cuadre_id", cuadre.id)
        .eq("solicitante_id", miId)
        .eq("propietario_id", miId)
        .eq("estado", "revocado")
        .maybeSingle();
      
      if (propietarioRevocado) {
        alert("❌ Tu acceso a este cuadre ha sido revocado por un gerente. Contacta a un gerente para restaurar tu acceso.");
        return;
      }
    }
    
    // Si es mi cuadre (sin revocación), soy gerente REAL, tengo acceso aprobado,
    // o tengo el permiso para retomar cualquier cuadre, abrir directamente
    if (esMiCuadre || esRealmenteGerente || tieneAccesoAprobado || puedeRetomarCualquiera) {
      console.log("✅ Abriendo cuadre directamente");
      
      // ====================== VERIFICAR FACTURAS PENDIENTES FALTANTES ======================
      // Al abrir un cuadre existente, verificar si hay facturas pendientes de días anteriores
      // que NO están en este cuadre y agregarlas automáticamente
      if (cuadre.estado !== "final") {
        console.log("🔍 Verificando facturas pendientes faltantes...");
        const pendientesFaltantes = obtenerFacturasPendientesAnterioresGlobal(
          cuadre.sede,
          cuadre.fecha,
          cuadre.facturas || []
        );
        
        if (pendientesFaltantes.length > 0) {
          console.log(`📥 [ABRIR DIRECTO] Agregando ${pendientesFaltantes.length} facturas pendientes faltantes al cuadre`);
          
          // Agregar las facturas pendientes faltantes al inicio del array de facturas
          const facturasActualizadas = [...pendientesFaltantes, ...(cuadre.facturas || [])];
          
          // Actualizar el cuadre con las nuevas facturas
          actualizarCuadre(cuadre.id, {
            facturas: facturasActualizadas,
          });
          
          console.log(`✅ Se agregaron ${pendientesFaltantes.length} factura(s) pendiente(s) de días anteriores`);
        } else {
          console.log("✅ No hay facturas pendientes faltantes por agregar");
        }
      }
      // ====================== FIN VERIFICACIÓN ======================
      
      // Cargar los accesos del cuadre para mostrar en el precierre
      const accesos = await cargarAccesosAprobadosPorCuadre(cuadre.id);
      const propietario = empleados.find((e) => String(e.id) === String(cuadre.empleadoId));
      const listaTotalAccesos = [];
      
      if (propietario) {
        listaTotalAccesos.push({
          id: `propietario-${cuadre.id}`,
          solicitante_id: propietario.id,
          solicitante_nombre: propietario.nombre,
          esPropietario: true,
          estado: 'aprobado',
          fecha_solicitud: cuadre.fecha,
          cuadre_id: cuadre.id
        });
      }
      
      // Agregar los accesos aprobados (excluyendo al propietario si ya está)
      accesos.forEach((acceso) => {
        if (String(acceso.solicitante_id) !== String(cuadre.empleadoId)) {
          listaTotalAccesos.push({ ...acceso, estado: 'aprobado' });
        }
      });
      
      setAccesosDelCuadre(listaTotalAccesos);
      setCuadreActivoId(cuadre.id);
      return;
    }
    
    // No tiene acceso, mostrar modal para solicitar
    const emp = empleados.find((e) => e.id === cuadre.empleadoId);
    setCuadreBloqueadoInfo({
      cuadre: cuadre,
      bloqueo: {
        usuario_id: cuadre.empleadoId,
        usuario_nombre: emp?.nombre || "Otro usuario",
        bloqueado_en: new Date().toISOString()
      }
    });
    setMostrarModalSolicitud(true);
    setSolicitudEnviada(false);
  };

  // ====================== LIMPIAR DUPLICADOS AL ABRIR CUADRE ======================
  // Este efecto se ejecuta cuando se abre un cuadre para eliminar duplicados existentes
  const limpiezaDuplicadosRealizada = React.useRef({});
  
  React.useEffect(() => {
    if (!cuadreActivoId) return;
    
    // Evitar limpiar el mismo cuadre múltiples veces en la misma sesión
    if (limpiezaDuplicadosRealizada.current[cuadreActivoId]) {
      return;
    }
    
    const cuadre = cuadres.find(c => c.id === cuadreActivoId);
    if (!cuadre) return;
    
    // Marcar como procesado inmediatamente
    limpiezaDuplicadosRealizada.current[cuadreActivoId] = true;
    
    // Función para verificar si un item está completamente vacío
    const itemEstaVacio = (item, campos) => {
      return campos.every(campo => {
        const val = item[campo];
        return val === undefined || val === null || val === "" || val === 0;
      });
    };
    
    // Función para limpiar duplicados y vacíos de un array
    const limpiarArray = (items, campos) => {
      if (!Array.isArray(items) || items.length === 0) return { resultado: [], cambios: 0 };
      
      const vistos = new Map();
      const resultado = [];
      let cambios = 0;
      
      items.forEach(item => {
        if (!item || !item.id) {
          cambios++;
          return; // Ignorar items sin ID
        }
        
        // Verificar si está completamente vacío
        if (itemEstaVacio(item, campos)) {
          cambios++; // Eliminar filas vacías
          console.log(`🗑️ Eliminando fila vacía: ${item.id}`);
          return;
        }
        
        // Crear clave basada en valores (normalizada)
        const valores = campos.map(c => String(item[c] || "").trim().toLowerCase());
        const clave = valores.join("|");
        
        // Si la clave tiene contenido, usarla para detectar duplicados
        const tieneContenido = valores.some(v => v !== "");
        
        if (tieneContenido) {
          if (!vistos.has(clave)) {
            vistos.set(clave, item);
            resultado.push(item);
          } else {
            // Duplicado por valores - conservar el más reciente
            const existente = vistos.get(clave);
            if ((item.updatedAt || 0) > (existente.updatedAt || 0)) {
              const idx = resultado.indexOf(existente);
              if (idx >= 0) {
                resultado[idx] = item;
                vistos.set(clave, item);
              }
            }
            cambios++;
            console.log(`🧹 Duplicado encontrado: ${clave.substring(0, 40)}`);
          }
        } else {
          // Sin contenido real - eliminar
          cambios++;
        }
      });
      
      return { resultado, cambios };
    };
    
    const patch = {};
    let totalCambios = 0;
    
    // Limpiar domicilios
    if (Array.isArray(cuadre.domicilios) && cuadre.domicilios.length > 0) {
      const { resultado, cambios } = limpiarArray(cuadre.domicilios, ['pedido', 'valor', 'pago', 'obs', 'domiciliario']);
      if (cambios > 0 || resultado.length !== cuadre.domicilios.length) {
        patch.domicilios = resultado;
        totalCambios += Math.abs(cuadre.domicilios.length - resultado.length);
        console.log(`🧹 Domicilios: ${cuadre.domicilios.length} -> ${resultado.length}`);
      }
    }
    
    // Limpiar transferencias
    if (Array.isArray(cuadre.transfer) && cuadre.transfer.length > 0) {
      const { resultado, cambios } = limpiarArray(cuadre.transfer, ['pedido', 'bancolombia', 'davivienda', 'nequi', 'daviplata']);
      if (cambios > 0 || resultado.length !== cuadre.transfer.length) {
        patch.transfer = resultado;
        totalCambios += Math.abs(cuadre.transfer.length - resultado.length);
        console.log(`🧹 Transferencias: ${cuadre.transfer.length} -> ${resultado.length}`);
      }
    }
    
    // Limpiar facturas - detectar duplicados por múltiples campos
    if (Array.isArray(cuadre.facturas) && cuadre.facturas.length > 0) {
      const vistos = new Map();
      const facturasLimpias = [];
      let cambiosFacturas = 0;
      
      cuadre.facturas.forEach(f => {
        if (!f || !f.id) return;
        
        // Para facturas, usar múltiples campos como clave de duplicado
        const pedido = String(f.pedido || f.factura || "").trim().toLowerCase();
        const valor = String(f.valor || "").trim();
        const efectivo = String(f.efectivo || "").trim();
        const pago = String(f.pago || "").trim();
        const obs = String(f.obs || f.observaciones || "").trim().toLowerCase();
        
        // Crear clave combinando todos los campos relevantes
        const clave = `${pedido}|${valor}|${efectivo}|${pago}|${obs}`;
        
        // Verificar si está completamente vacía
        const estaVacia = !pedido && !valor && !efectivo && !pago && !obs;
        
        if (estaVacia) {
          cambiosFacturas++;
          console.log(`🗑️ Factura vacía eliminada: ${f.id}`);
          return;
        }
        
        if (vistos.has(clave)) {
          // Es duplicado
          const existente = vistos.get(clave);
          if ((f.updatedAt || 0) > (existente.updatedAt || 0)) {
            const idx = facturasLimpias.indexOf(existente);
            if (idx >= 0) {
              facturasLimpias[idx] = f;
              vistos.set(clave, f);
            }
          }
          cambiosFacturas++;
          console.log(`🧹 Factura duplicada: ${clave.substring(0, 50)}`);
        } else {
          vistos.set(clave, f);
          facturasLimpias.push(f);
        }
      });
      
      if (cambiosFacturas > 0) {
        patch.facturas = facturasLimpias;
        totalCambios += cambiosFacturas;
        console.log(`🧹 Facturas: ${cuadre.facturas.length} -> ${facturasLimpias.length}`);
      }
    }
    
    // Si hubo cambios, actualizar el cuadre directamente en el estado y Supabase
    // 🛡️ PERO SOLO SI NO ESTAMOS RECIBIENDO UN BROADCAST (evita loop)
    if (totalCambios > 0 && !recibiendoBroadcastRef.current) {
      console.log(`🧹 Limpieza completada: ${totalCambios} items eliminados/corregidos`);
      
      // Actualizar estado local
      setCuadres(prev => prev.map(c => 
        c.id === cuadreActivoId ? { ...c, ...patch, updated_at: new Date().toISOString() } : c
      ));
      
      // Guardar en Supabase solo si NO venimos de un broadcast
      ignorarProximaActualizacion.current = true;
      const cuadreLimpio = { ...cuadre, ...patch };
      guardarCuadreDiarioEnSupabase(cuadreLimpio, filtroEmpresa).then(() => {
        console.log("✅ Cuadre limpio guardado en Supabase");
      }).catch(e => {
        console.error("Error guardando cuadre limpio:", e);
        ignorarProximaActualizacion.current = false;
      });
    } else if (totalCambios > 0) {
      console.log(`🛡️ Limpieza detectada pero ignorada (viene de broadcast)`);
    }
  }, [cuadreActivoId, cuadres, filtroEmpresa]);
  // ====================== FIN LIMPIEZA DE DUPLICADOS ======================
 
  const cuadreActivo = useMemo(
    () => cuadres.find((c) => c.id === cuadreActivoId) || null,
    [cuadres, cuadreActivoId]
  );
 
  // FILTRO: Excluir cuadres de prueba anteriores al 7 de febrero de 2026
  const FECHA_MINIMA_CUADRES = "2026-02-07";
  
  const cuadresEnProceso = useMemo(
    () => cuadres.filter((c) => c.estado !== "final" && c.fecha >= FECHA_MINIMA_CUADRES),
    [cuadres]
  );
 
  function crearOBuscarBorrador({ sede, fecha }) {
    const borrador = cuadres.find(
      (c) => c.sede === sede && c.fecha === fecha && c.estado !== "final"
    );
    if (borrador) return borrador;
 
    const nuevo = {
      id: uid(),
      sede,
      fecha,
      empleadoId: "",
      valorInicial: 0,
      // 🔹 COLABORADOR - Solo este usuario puede colaborar
      colaboradorId: null,
      colaboradorNombre: null,
      ventas: {
        ventaEfectivo: "",
        sobrante: "",
        huboDescuadre: "No",
        valorDescuadre: "",
      },
      facturas: [],
      facturasEliminadas: [], // Para sincronización colaborativa - evita que facturas borradas reaparezcan
      domicilios: [],
      transfer: [],
      turnosActivos: false,
      turnos: [],
      adelantosActivos: false,
      adelantos: [],
      denominacionesActivas: false,
      denoms: [],
      novedadesActivas: false,
      novedades: [],
      datafonos: [],
      totales: {},
      estado: "borrador",
      comentarioModificacion: "",
    };
    setCuadres((prev) => [...prev, nuevo]);
    // Sincronizar borrador con Supabase para que esté disponible en otros equipos
    console.log("💾 Guardando nuevo borrador en Supabase:", nuevo.id, "sede:", nuevo.sede, "fecha:", nuevo.fecha, "empresaId:", filtroEmpresa);
    guardarCuadreDiarioEnSupabase(nuevo, filtroEmpresa).then((resultado) => {
      if (resultado) {
        console.log("✅ Borrador guardado exitosamente en Supabase:", resultado.id);
      } else {
        console.error("❌ Error: guardarCuadreDiarioEnSupabase retornó null");
      }
    }).catch((e) => {
      console.error("❌ Error sincronizando borrador de cuadre:", e);
    });
    return nuevo;
  }
 
  // Debounce para sincronización - evitar enviar cada tecla
  const cuadreUpdateTimeout = React.useRef({});
  const broadcastTimeout = React.useRef(null);
  const ultimoEnvio = React.useRef({});
  
  function actualizarCuadre(id, patch) {
    const miId = empleadoActual?.id || usuarioActual?.id || "";
    
    setCuadres((prev) => {
      const updated = prev.map((c) => (c.id === id ? { ...c, ...patch, updated_at: new Date().toISOString() } : c));
      const cuadreActualizado = updated.find((c) => c.id === id);
      
      // 🚀 ENVIAR VIA BROADCAST CON DEBOUNCE (evita enviar cada tecla)
      if (cuadreActualizado && broadcastChannelRef.current && id === cuadreActivoId) {
        // Cancelar broadcast anterior si existe
        if (broadcastTimeout.current) {
          clearTimeout(broadcastTimeout.current);
        }
        
        // Esperar 300ms después de última tecla antes de enviar
        broadcastTimeout.current = setTimeout(() => {
          // Obtener el cuadre MÁS ACTUAL del estado
          const cuadreActual = cuadres.find(c => c.id === id) || cuadreActualizado;
          
          broadcastChannelRef.current.send({
            type: 'broadcast',
            event: 'cuadre_update',
            payload: {
              cuadre: cuadreActual,
              senderId: miId,
              timestamp: Date.now()
            }
          }).then(() => {
            console.log("⚡ Cambio enviado por Broadcast (después de debounce)");
          }).catch((e) => {
            console.error("Error enviando por broadcast:", e);
          });
        }, 300); // 300ms de debounce - esperar que usuario termine de escribir
      }
      
      // Sincronizar con Supabase (para persistencia) con debounce de 500ms
      if (cuadreActualizado) {
        if (cuadreUpdateTimeout.current[id]) {
          clearTimeout(cuadreUpdateTimeout.current[id]);
        }
        
        // 🔴 DATOS CRÍTICOS - Guardar INMEDIATAMENTE sin delay
        const efectivoConfirmadoAhora = cuadreActualizado?.ventas?.efectivoEnCajaConfirmado === true;
        const efectivoConfirmadoAntes = cuadreActivo?.ventas?.efectivoEnCajaConfirmado === true;
        const efectivoFinalAhora = cuadreActualizado?.ventas?.efectivoFinalConfirmado === true;
        const efectivoFinalAntes = cuadreActivo?.ventas?.efectivoFinalConfirmado === true || cuadreActivo?.efectivoFinalConfirmado === true;
        
        if ((efectivoConfirmadoAhora && !efectivoConfirmadoAntes) || (efectivoFinalAhora && !efectivoFinalAntes)) {
          // El efectivo acaba de ser confirmado O el valor final acaba de ser confirmado - guardar INMEDIATAMENTE
          console.log("⚡ GUARDANDO INMEDIATAMENTE:", efectivoFinalAhora && !efectivoFinalAntes ? "Efectivo FINAL confirmado" : "Efectivo confirmado en caja");
          ignorarProximaActualizacion.current = true;
          ultimoEnvio.current[id] = Date.now();
          guardarCuadreDiarioEnSupabase(cuadreActualizado, filtroEmpresa).catch((e) => {
            console.error("Error guardando efectivo confirmado:", e);
            ignorarProximaActualizacion.current = false;
          });
        } else {
          // Otros cambios - guardar con debounce de 500ms
          cuadreUpdateTimeout.current[id] = setTimeout(() => {
            ignorarProximaActualizacion.current = true;
            ultimoEnvio.current[id] = Date.now();
            guardarCuadreDiarioEnSupabase(cuadreActualizado, filtroEmpresa).catch((e) => {
              console.error("Error sincronizando cuadre:", e);
              ignorarProximaActualizacion.current = false;
            });
          }, 500); // 500ms - esperar antes de persistir
        }
      }
      
      return updated;
    });
  }
  
  // 🔹 ACTUALIZACIÓN PARCIAL - Para Modo Colaborador (solo envía la sección modificada)
  function actualizarCuadreParcial(id, seccion, datos) {
    const miId = empleadoActual?.id || usuarioActual?.id || "";
    const timestamp = Date.now();
    
    // Actualizar localmente
    setCuadres((prev) => {
      const updated = prev.map((c) => {
        if (c.id === id) {
          return {
            ...c,
            [seccion]: datos,
            [`${seccion}_timestamp`]: timestamp,
            updated_at: new Date().toISOString()
          };
        }
        return c;
      });
      
      const cuadreActualizado = updated.find((c) => c.id === id);
      
      // 🚀 ENVIAR VIA BROADCAST PARCIAL
      if (cuadreActualizado && broadcastChannelRef.current) {
        broadcastChannelRef.current.send({
          type: 'broadcast',
          event: 'cuadre_partial_update',
          payload: {
            cuadreId: id,
            seccion: seccion,
            datos: datos,
            timestamp: timestamp,
            senderId: miId
          }
        }).then(() => {
          console.log(`⚡ Actualización parcial (${seccion}) enviada`);
        }).catch((e) => {
          console.error("Error enviando actualización parcial:", e);
        });
      }
      
      // Sincronizar con Supabase (solo la sección afectada)
      if (cuadreActualizado) {
        if (cuadreUpdateTimeout.current[`${id}_${seccion}`]) {
          clearTimeout(cuadreUpdateTimeout.current[`${id}_${seccion}`]);
        }
        cuadreUpdateTimeout.current[`${id}_${seccion}`] = setTimeout(() => {
          ignorarProximaActualizacion.current = true;
          guardarCuadreDiarioEnSupabase(cuadreActualizado, filtroEmpresa).catch((e) => {
            console.error("Error sincronizando cuadre parcial:", e);
            ignorarProximaActualizacion.current = false;
          });
        }, 300); // Más rápido para parciales
      }
      
      return updated;
    });
    
    // También actualizar colaboradorCuadre si está en modo colaborador
    if (modoColaborador && colaboradorCuadreId === id) {
      setColaboradorCuadre(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          [seccion]: datos,
          [`${seccion}_timestamp`]: timestamp
        };
      });
    }
  }
 
  function guardarFinal(id, totales) {
    const existente = cuadres.find((c) => c.id === id);
    if (!existente) {
      console.error("No se encontró el cuadre a guardar", id);
      return;
    }

    // Registrar quién cerró el cuadre
    const cerradoPorId = empleadoActual?.id || usuarioActual?.id || "";
    const cerradoPorNombre = empleadoActual?.nombre || usuarioActual?.nombre || "Usuario desconocido";
    const fechaNuevoCierre = new Date().toISOString();

    // Verificar si es un cuadre reabierto y agregar novedades
    // 🔹 EVITAR DUPLICADOS: Solo agregar si no existe ya la información
    const comentarioExistente = existente.comentarioModificacion || "";
    
    let novedadesReabierto = "";
    if (existente.fueReabierto && !comentarioExistente.includes("CUADRE REABIERTO")) {
      const fechaCierreOriginalFormateada = existente.fechaCierreOriginal 
        ? new Date(existente.fechaCierreOriginal).toLocaleString("es-CO")
        : "No registrada";
      const fechaNuevoCierreFormateada = new Date(fechaNuevoCierre).toLocaleString("es-CO");
      
      novedadesReabierto = `\n\n🔄 **CUADRE REABIERTO**\n` +
        `• Cierre inicial: ${fechaCierreOriginalFormateada} por ${existente.cerradoPorOriginal || "N/A"}\n` +
        `• Reabierto: ${existente.reabiertoEn ? new Date(existente.reabiertoEn).toLocaleString("es-CO") : "N/A"} por ${existente.reabiertoPor || "N/A"}\n` +
        `• Nuevo cierre: ${fechaNuevoCierreFormateada} por ${cerradoPorNombre}`;
    }

    // 🔹 Registrar bases de domicilios en el cierre (EVITAR DUPLICADOS)
    let notaBasesDomicilios = "";
    const basesDom = existente.basesDomicilios || [];
    // Solo agregar si hay bases y NO existe ya la información
    if (basesDom.length > 0 && !comentarioExistente.includes("BASES DE DOMICILIOS")) {
      const formatCOP = (val) => new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(val || 0);
      
      const basesPendientes = basesDom.filter(base => {
        const totalAbonos = (base.abonos || []).reduce((sum, a) => sum + Number(a.valor || 0), 0);
        return (Number(base.valorBase || 0) - totalAbonos) > 0;
      });
      
      const totalEntregado = basesDom.reduce((sum, b) => sum + Number(b.valorBase || 0), 0);
      const totalRecuperado = basesDom.reduce((sum, b) => {
        return sum + (b.abonos || []).reduce((s, a) => s + Number(a.valor || 0), 0);
      }, 0);
      const totalPendienteBases = totalEntregado - totalRecuperado;

      notaBasesDomicilios = `\n\n🚴 **BASES DE DOMICILIOS**\n` +
        `• Total bases entregadas: ${formatCOP(totalEntregado)}\n` +
        `• Total recuperado: ${formatCOP(totalRecuperado)}\n` +
        `• Saldo pendiente: ${formatCOP(totalPendienteBases)}`;

      if (basesPendientes.length > 0) {
        notaBasesDomicilios += `\n\n⚠️ DOMICILIARIOS CON SALDO PENDIENTE:\n`;
        basesPendientes.forEach(base => {
          const totalAbonos = (base.abonos || []).reduce((s, a) => s + Number(a.valor || 0), 0);
          const saldo = Number(base.valorBase || 0) - totalAbonos;
          notaBasesDomicilios += `  • ${base.empleado}: ${formatCOP(saldo)}\n`;
        });
      }
    }

    const actualizado = {
      ...existente,
      totales: totales || {},
      estado: "final",
      cerradoPorId: cerradoPorId,
      cerradoPorNombre: cerradoPorNombre,
      fechaCierre: fechaNuevoCierre,
      // Agregar novedades de reapertura y bases de domicilios si aplica
      comentarioModificacion: comentarioExistente + novedadesReabierto + notaBasesDomicilios,
    };

    setCuadres((prev) =>
      prev.map((c) => (c.id === id ? actualizado : c))
    );

    guardarCuadreDiarioEnSupabase(actualizado, filtroEmpresa).then((sincronizado) => {
      if (!sincronizado) {
        alert("No se pudo sincronizar el cuadre con Supabase. Verifica tu conexión y vuelve a intentar.");
        setCuadres((prev) =>
          prev.map((c) => (c.id === id ? existente : c))
        );
        return;
      }

      setCuadres((prev) =>
        prev.map((c) =>
          c.id === id
            ? {
                ...sincronizado,
                comentarioModificacion:
                  actualizado.comentarioModificacion || sincronizado.comentarioModificacion || "",
              }
            : c
        )
      );
      
      // 🔒 LIMPIAR INMEDIATAMENTE del localStorage local
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const locales = JSON.parse(stored);
          const limpios = locales.filter(c => c.id !== id);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(limpios));
          console.log("🧹 Cuadre cerrado eliminado del localStorage local");
        }
      } catch (e) {
        console.error("Error limpiando localStorage:", e);
      }
      
      // 🔒 BROADCAST DE CIERRE DEFINITIVO - Notificar a TODOS los usuarios
      // Enviar a DOS canales: el específico del cuadre Y un canal general de la empresa
      const miId = empleadoActual?.id || usuarioActual?.id || "";
      
      // Canal 1: Específico del cuadre (para usuarios que lo tienen activo)
      const broadcastChannel = supabase.channel(`cuadre-broadcast-${id}`);
      broadcastChannel.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          broadcastChannel.send({
            type: 'broadcast',
            event: 'cuadre_cerrado',
            payload: {
              cuadreId: id,
              senderId: miId,
              cuadreCerrado: sincronizado,
              cerradoPor: cerradoPorNombre,
              fechaCierre: fechaNuevoCierre
            }
          }).then(() => {
            console.log("🔒 Broadcast de CIERRE DEFINITIVO enviado (canal cuadre)");
            supabase.removeChannel(broadcastChannel);
          });
        }
      });
      
      // Canal 2: General de la empresa (para refrescar la lista)
      const canalGeneral = supabase.channel(`cuadres-empresa-${filtroEmpresa || 'global'}`);
      canalGeneral.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          canalGeneral.send({
            type: 'broadcast',
            event: 'cuadre_cerrado',
            payload: {
              cuadreId: id,
              senderId: miId,
              cuadreCerrado: sincronizado,
              cerradoPor: cerradoPorNombre,
              fechaCierre: fechaNuevoCierre
            }
          }).then(() => {
            console.log("🔒 Broadcast de CIERRE DEFINITIVO enviado (canal empresa)");
            supabase.removeChannel(canalGeneral);
          });
        }
      });
      
      // 🚪 SACAR AL USUARIO DEL CUADRE CERRADO
      // Esto evita que el usuario siga viendo el cuadre como activo
      setCuadreActivoId(null);
      console.log("🚪 Usuario sacado del cuadre cerrado");
    });
  }
 
  function guardarComentario(id, comentario) {
    const existente = cuadres.find((c) => c.id === id);
    if (!existente) {
      console.error("No se encontró el cuadre para comentario", id);
      return;
    }

    const actualizado = { ...existente, comentarioModificacion: comentario };

    setCuadres((prev) =>
      prev.map((c) => (c.id === id ? actualizado : c))
    );

    if (existente.estado === "final") {
      guardarCuadreDiarioEnSupabase(actualizado, filtroEmpresa).then((sincronizado) => {
        if (!sincronizado) {
          alert("No se pudo guardar el comentario en Supabase. Intenta nuevamente.");
          setCuadres((prev) =>
            prev.map((c) => (c.id === id ? existente : c))
          );
          return;
        }

        setCuadres((prev) =>
          prev.map((c) =>
            c.id === id
              ? {
                  ...sincronizado,
                  comentarioModificacion: comentario,
                }
              : c
          )
        );
      });
    }
  }

  // Función para realizar PRECIERRE del cuadre (cambio de turno)
  function realizarPrecierre(id, asignadoAId, observaciones, totalesDetallados) {
    const existente = cuadres.find((c) => c.id === id);
    if (!existente) {
      console.error("No se encontró el cuadre para precierre", id);
      return;
    }

    if (existente.estado === "final") {
      alert("⚠️ Este cuadre ya está cerrado, no se puede hacer precierre.");
      return;
    }

    // Obtener datos del empleado que realiza el precierre
    const precierrePorId = empleadoActual?.id || usuarioActual?.id || "";
    const precierrePorNombre = empleadoActual?.nombre || usuarioActual?.nombre || "Usuario desconocido";
    
    // Obtener datos del empleado al que se asigna
    const empleadoAsignado = empleados.find(e => e.id === asignadoAId);
    const asignadoANombre = empleadoAsignado?.nombre || "No asignado";
    
    const fechaPrecierre = new Date().toISOString();
    const fechaPrecierreFormateada = new Date().toLocaleString("es-CO");
    
    // Formatear moneda para notas
    const formatCOP = (val) => new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(val || 0);
    
    // Crear registro de precierre con totales detallados
    const precierre = {
      fecha: fechaPrecierre,
      porId: precierrePorId,
      porNombre: precierrePorNombre,
      asignadoAId: asignadoAId,
      asignadoANombre: asignadoANombre,
      observaciones: observaciones || "",
      totales: totalesDetallados,
      confirmadoPorReceptor: false,
      fechaConfirmacion: null
    };
    
    // Agregar nota de precierre al comentario con detalle
    // 🔹 EVITAR DUPLICADOS: Solo agregar si no existe ya un precierre
    const comentarioExistente = existente.comentarioModificacion || "";
    const yaHayPrecierre = comentarioExistente.includes("PRECIERRE DE TURNO");
    
    const notaPrecierre = `\n\n📋 **PRECIERRE DE TURNO**\n` +
      `• Fecha: ${fechaPrecierreFormateada}\n` +
      `• Realizado por: ${precierrePorNombre}\n` +
      `• Asignado a: ${asignadoANombre}\n` +
      `• Observaciones: ${observaciones || "Sin observaciones"}\n\n` +
      `💰 VERIFICACIÓN DE EFECTIVO AL PRECIERRE:\n` +
      `  - Efectivo en caja confirmado: ${formatCOP(totalesDetallados?.efectivoEnCaja)}\n` +
      `  - Debía quedar en caja: ${formatCOP(totalesDetallados?.debeQuedarEnCaja)}\n` +
      `  - Diferencia: ${totalesDetallados?.diferenciaEfectivo >= 0 ? '+' : ''}${formatCOP(totalesDetallados?.diferenciaEfectivo)}\n` +
      `  - Estado: ${totalesDetallados?.estaCuadrado ? '✅ CUADRADO' : '⚠️ DESCUADRADO'}\n\n` +
      `💰 VENTAS AL PRECIERRE:\n` +
      `  - Efectivo: ${formatCOP(totalesDetallados?.ventaEfectivo)}\n` +
      `  - Bancolombia: ${formatCOP(totalesDetallados?.bancolombia)}\n` +
      `  - Davivienda: ${formatCOP(totalesDetallados?.davivienda)}\n` +
      `  - Daviplata: ${formatCOP(totalesDetallados?.daviplata)}\n` +
      `  - Nequi: ${formatCOP(totalesDetallados?.nequi)}\n` +
      `  - Datáfono: ${formatCOP(totalesDetallados?.totalDatafonos)}\n` +
      `  - Online Rappi: ${formatCOP(totalesDetallados?.onlineRappi)}\n` +
      `  - TOTAL VENTAS: ${formatCOP(totalesDetallados?.totalVentas)}\n\n` +
      `📋 GASTOS AL PRECIERRE:\n` +
      `  - Facturas Pendientes: ${formatCOP(totalesDetallados?.totalFacturasPendientes)}\n` +
      `  - Facturas Pagadas Caja: ${formatCOP(totalesDetallados?.totalFacturasCaja)}\n` +
      `  - Facturas Pagadas Transferencia: ${formatCOP(totalesDetallados?.totalFacturasTransferencia)}`;

    // Si ya hay un precierre, reemplazarlo con el nuevo en lugar de concatenar
    let nuevoComentario;
    if (yaHayPrecierre) {
      // Reemplazar el precierre anterior con el nuevo
      const indicePrecierre = comentarioExistente.indexOf("\n\n📋 **PRECIERRE DE TURNO**");
      if (indicePrecierre !== -1) {
        nuevoComentario = comentarioExistente.substring(0, indicePrecierre) + notaPrecierre;
      } else {
        nuevoComentario = comentarioExistente + notaPrecierre;
      }
    } else {
      nuevoComentario = comentarioExistente + notaPrecierre;
    }

    const actualizado = {
      ...existente,
      empleadoId: asignadoAId, // Cambiar el responsable al nuevo empleado
      precierreRealizado: true,
      precierre: precierre,
      precierrePendienteConfirmacion: true, // Nuevo: indica que el receptor debe confirmar
      comentarioModificacion: nuevoComentario,
    };

    setCuadres((prev) =>
      prev.map((c) => (c.id === id ? actualizado : c))
    );

    guardarCuadreDiarioEnSupabase(actualizado, filtroEmpresa).then((sincronizado) => {
      if (!sincronizado) {
        alert("No se pudo guardar el precierre en Supabase. Verifica tu conexión.");
        setCuadres((prev) =>
          prev.map((c) => (c.id === id ? existente : c))
        );
        return;
      }

      setCuadres((prev) =>
        prev.map((c) =>
          c.id === id
            ? {
                ...sincronizado,
                comentarioModificacion: actualizado.comentarioModificacion || sincronizado.comentarioModificacion || "",
              }
            : c
        )
      );
      
      alert(`✅ Precierre realizado exitosamente.\n\nEl cuadre fue asignado a: ${asignadoANombre}\n\nEl nuevo responsable puede continuar editando el cuadre.`);
    });
  }

  // Función para reabrir un cuadre cerrado (Gerencia o permiso admin_cuadre_reabrir)
  async function reabrirCuadre(id) {
    if (!esGerente && !tienePermiso("admin_cuadre_reabrir")) {
      alert("❌ No tienes permiso para reabrir cuadres. Requiere perfil Gerencia o el permiso 'Reabrir cuadres cerrados'.");
      return;
    }

    const existente = cuadres.find((c) => c.id === id);
    if (!existente) {
      console.error("No se encontró el cuadre a reabrir", id);
      return;
    }

    if (existente.estado !== "final") {
      alert("⚠️ Este cuadre no está cerrado, no se puede reabrir.");
      return;
    }

    if (!window.confirm("¿Está seguro que desea REABRIR este cuadre?\n\nEl cuadre pasará a estado 'En proceso' y podrá ser editado nuevamente.\n\n⚠️ IMPORTANTE: Una vez reabierto, este cuadre NO podrá ser borrado.")) {
      return;
    }

    const reabrirNombre = empleadoActual?.nombre || usuarioActual?.nombre || "Usuario desconocido";
    const fechaCierreAnterior = existente.fechaCierre || new Date().toISOString();
    
    const actualizado = {
      ...existente,
      estado: "borrador",
      fueReabierto: true, // Marcar que fue reabierto
      fechaCierreOriginal: existente.fechaCierreOriginal || fechaCierreAnterior, // Guardar fecha del primer cierre
      cerradoPorOriginal: existente.cerradoPorOriginal || existente.cerradoPorNombre, // Guardar quién cerró originalmente
      reabiertoEn: new Date().toISOString(),
      reabiertoPor: reabrirNombre,
      // Registrar quién reabrió en el comentario
      comentarioModificacion: (existente.comentarioModificacion || "") + 
        `\n[REABIERTO por ${reabrirNombre} el ${new Date().toLocaleString("es-CO")}]`,
    };

    setCuadres((prev) =>
      prev.map((c) => (c.id === id ? actualizado : c))
    );

    const sincronizado = await guardarCuadreDiarioEnSupabase(actualizado, filtroEmpresa);
    if (!sincronizado) {
      alert("❌ No se pudo sincronizar el cuadre reabierto con Supabase.");
      setCuadres((prev) =>
        prev.map((c) => (c.id === id ? existente : c))
      );
      return;
    }

    setCuadres((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...sincronizado,
              comentarioModificacion: actualizado.comentarioModificacion || sincronizado.comentarioModificacion || "",
            }
          : c
      )
    );

    alert("✅ Cuadre reabierto correctamente. Ahora puede editarlo en 'Cuadres en proceso'.");
    console.log("🔓 Cuadre reabierto:", id, "por:", reabrirNombre);
  }

  async function borrarCuadre(id) {
    const cuadreABorrar = cuadres.find((c) => c.id === id);
    
    // No permitir borrar cuadres que fueron reabiertos
    if (cuadreABorrar?.fueReabierto) {
      alert("❌ Este cuadre fue reabierto anteriormente y NO puede ser borrado.\n\nLos cuadres reabiertos quedan protegidos para mantener el historial de modificaciones.");
      return;
    }
    
    if (!window.confirm("¿Seguro que deseas borrar este cuadre en proceso?")) {
      return;
    }
    
    console.log("🗑️ Intentando eliminar cuadre:", id);
    const miId = empleadoActual?.id || usuarioActual?.id || "";
    
    // PRIMERO: Intentar eliminar de Supabase (ANTES de actualizar estado local)
    // 🔐 SEGURIDAD: Agregar filtro de empresa para no eliminar cuadres de otras empresas
    let eliminadoDeSupabase = false;
    try {
      let query = supabase
        .from("cuadres_diarios")
        .delete()
        .eq("id", id);
      
      // 🔐 Filtrar por empresa si está disponible
      if (filtroEmpresa) {
        query = query.eq("empresa_id", filtroEmpresa);
      }
      
      const { error } = await query;
      
      if (error) {
        console.error("❌ Error eliminando de Supabase (intento 1):", error);
        // Reintentar una vez más
        let queryRetry = supabase
          .from("cuadres_diarios")
          .delete()
          .eq("id", id);
        if (filtroEmpresa) {
          queryRetry = queryRetry.eq("empresa_id", filtroEmpresa);
        }
        const { error: error2 } = await queryRetry;
        if (error2) {
          console.error("❌ Error eliminando de Supabase (intento 2):", error2);
        } else {
          eliminadoDeSupabase = true;
        }
      } else {
        eliminadoDeSupabase = true;
        console.log("✅ Cuadre eliminado de Supabase correctamente:", id);
      }
    } catch (e) {
      console.error("❌ Error en borrarCuadre (Supabase):", e);
    }
    
    // 🚀 NUEVO: Enviar broadcast de eliminación a TODOS los usuarios conectados
    if (eliminadoDeSupabase) {
      try {
        const broadcastChannel = supabase.channel(`cuadre-broadcast-${id}`);
        await broadcastChannel.send({
          type: 'broadcast',
          event: 'cuadre_eliminado',
          payload: {
            cuadreId: id,
            senderId: miId,
            timestamp: Date.now()
          }
        });
        console.log("📢 Broadcast de eliminación enviado a todos los usuarios");
        await supabase.removeChannel(broadcastChannel);
      } catch (e) {
        console.error("Error enviando broadcast de eliminación:", e);
      }
    }
    
    // SEGUNDO: Marcar el ID como eliminado ANTES de todo (para evitar re-sincronización)
    try {
      const eliminados = localStorage.getItem(STORAGE_ELIMINADOS_KEY);
      const listaEliminados = eliminados ? JSON.parse(eliminados) : [];
      if (!listaEliminados.includes(id)) {
        listaEliminados.push(id);
        localStorage.setItem(STORAGE_ELIMINADOS_KEY, JSON.stringify(listaEliminados));
        console.log("✅ ID agregado a lista de eliminados:", id);
      }
    } catch (e) {
      console.error("Error guardando lista de eliminados:", e);
    }
    
    // TERCERO: Actualizar estado local
    setCuadres((prev) => prev.filter((c) => c.id !== id));
    if (cuadreActivoId === id) setCuadreActivoId(null);
    
    // NOTA: Ya no usamos localStorage para cuadres - Supabase es la única fuente de verdad
    // Esto evita conflictos entre usuarios y que aparezcan cuadres fantasma
    
    // Mostrar resultado al usuario
    if (eliminadoDeSupabase) {
      alert("✅ Cuadre eliminado correctamente");
    } else {
      alert("⚠️ El cuadre se eliminó localmente pero hubo un problema con el servidor.\n\nNo te preocupes, ya no volverá a aparecer porque está marcado como eliminado.");
    }
  }

  async function guardarEdicionCuadre() {
    if (!cuadreEditando) return;
    
    // Actualizar estado local primero
    const cuadreActualizado = { ...cuadreEditando };
    setCuadres((prev) =>
      prev.map((c) => (c.id === cuadreEditando.id ? cuadreActualizado : c))
    );
    
    // Guardar en Supabase
    try {
      const resultado = await guardarCuadreDiarioEnSupabase(cuadreActualizado, filtroEmpresa);
      if (resultado) {
        console.log("✅ Cuadre actualizado en Supabase");
      } else {
        console.error("❌ Error al sincronizar cuadre con Supabase");
      }
    } catch (e) {
      console.error("❌ Error guardando cuadre:", e);
    }
    
    setCuadreEditando(null);
    alert("Cuadre modificado exitosamente");
  }
 
  return (
    <div>
      {cargandoCuadres && (
        <div className="card" style={{ marginBottom: 12, backgroundColor: "#f1f5f9" }}>
          <p style={{ margin: 0, fontSize: 12, color: "#334155" }}>Sincronizando cuadres con Supabase…</p>
        </div>
      )}

      {/* PANEL DE SOLICITUDES PENDIENTES */}
      {solicitudesPendientes.length > 0 && (
        <div className="card" style={{ marginBottom: 12, backgroundColor: "#fef3c7", border: "2px solid #f59e0b" }}>
          <h3 style={{ margin: "0 0 8px 0", color: "#92400e", display: "flex", alignItems: "center", gap: 8 }}>
            🔔 Solicitudes de acceso pendientes ({solicitudesPendientes.length})
          </h3>
          <p style={{ margin: "0 0 12px 0", fontSize: 12, color: "#78350f" }}>
            Otros usuarios solicitan acceso a cuadres que tienes abiertos o en proceso.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {solicitudesPendientes.map((sol) => {
              const cuadreRelacionado = cuadres.find((c) => c.id === sol.cuadre_id);
              return (
                <div key={sol.id} style={{ 
                  background: "white", 
                  padding: 12, 
                  borderRadius: 8, 
                  border: "1px solid #fcd34d",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 8
                }}>
                  <div>
                    <div style={{ fontWeight: 600, color: "#1f2937" }}>
                      {sol.solicitante_nombre} solicita acceso
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>
                      Cuadre: {cuadreRelacionado?.fecha || "—"} - {cuadreRelacionado?.sede || "—"}
                    </div>
                    <div style={{ fontSize: 10, color: "#9ca3af" }}>
                      Solicitado: {new Date(sol.fecha_solicitud).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      type="button"
                      onClick={() => handleResponderSolicitud(sol, true)}
                      style={{ 
                        background: "#10b981", 
                        color: "white", 
                        border: "none", 
                        borderRadius: 6, 
                        padding: "6px 12px", 
                        fontSize: 12, 
                        fontWeight: 600,
                        cursor: "pointer" 
                      }}
                    >
                      ✅ Aprobar
                    </button>
                    <button
                      type="button"
                      onClick={() => handleResponderSolicitud(sol, false)}
                      style={{ 
                        background: "#ef4444", 
                        color: "white", 
                        border: "none", 
                        borderRadius: 6, 
                        padding: "6px 12px", 
                        fontSize: 12, 
                        fontWeight: 600,
                        cursor: "pointer" 
                      }}
                    >
                      ❌ Rechazar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MODAL DE CUADRE BLOQUEADO */}
      {mostrarModalSolicitud && cuadreBloqueadoInfo && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          padding: 20
        }} onClick={() => setMostrarModalSolicitud(false)}>
          <div style={{
            background: "white",
            borderRadius: 12,
            padding: 24,
            maxWidth: 450,
            width: "100%",
            boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <span style={{ fontSize: 48 }}>🔒</span>
            </div>
            <h3 style={{ margin: "0 0 8px 0", textAlign: "center", color: "#1f2937" }}>
              Cuadre en uso
            </h3>
            <p style={{ margin: "0 0 16px 0", textAlign: "center", color: "#6b7280", fontSize: 14 }}>
              Este cuadre está siendo editado por <strong>{cuadreBloqueadoInfo.bloqueo.usuario_nombre}</strong>.
            </p>
            <div style={{ 
              background: "#f3f4f6", 
              padding: 12, 
              borderRadius: 8, 
              marginBottom: 16,
              fontSize: 13
            }}>
              <div><strong>Fecha:</strong> {cuadreBloqueadoInfo.cuadre.fecha}</div>
              <div><strong>Sede:</strong> {cuadreBloqueadoInfo.cuadre.sede}</div>
              <div><strong>Bloqueado desde:</strong> {new Date(cuadreBloqueadoInfo.bloqueo.bloqueado_en).toLocaleString()}</div>
            </div>
            
            {solicitudEnviada ? (
              <div style={{ 
                background: "#d1fae5", 
                padding: 12, 
                borderRadius: 8, 
                textAlign: "center",
                color: "#065f46",
                fontSize: 13
              }}>
                ✅ Solicitud enviada. Espera la aprobación del empleado o de un gerente.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button
                  type="button"
                  disabled={enviandoSolicitud}
                  onClick={() => handleSolicitarAcceso(
                    cuadreBloqueadoInfo.cuadre.id, 
                    cuadreBloqueadoInfo.bloqueo.usuario_id
                  )}
                  style={{ 
                    background: enviandoSolicitud ? "#9ca3af" : "#3b82f6", 
                    color: "white", 
                    border: "none", 
                    borderRadius: 8, 
                    padding: "12px 16px", 
                    fontSize: 14, 
                    fontWeight: 600,
                    cursor: enviandoSolicitud ? "not-allowed" : "pointer" 
                  }}
                >
                  {enviandoSolicitud ? "Enviando..." : "📤 Solicitar acceso"}
                </button>
                <p style={{ margin: 0, fontSize: 11, color: "#9ca3af", textAlign: "center" }}>
                  Se enviará una notificación al empleado que tiene el cuadre abierto y a los gerentes.
                </p>
              </div>
            )}
            
            <button
              type="button"
              onClick={() => setMostrarModalSolicitud(false)}
              style={{ 
                width: "100%",
                marginTop: 12,
                background: "#e5e7eb", 
                color: "#374151", 
                border: "none", 
                borderRadius: 8, 
                padding: "10px 16px", 
                fontSize: 13,
                cursor: "pointer" 
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      <div className="tabs">
        {tienePermiso("admin_cuadre_abrir") && (
        <button
          className={modo === "crear" && !modoColaborador ? "tab active" : "tab"}
          onClick={() => {
            setModo("crear");
            setCuadreActivoId(null);
            setModoColaborador(false);
          }}
        >
          Abrir cuadre
        </button>
        )}
        {tienePermiso("admin_cuadre_consultar") && (
        <button
          className={modo === "consultar" && !modoColaborador ? "tab active" : "tab"}
          onClick={() => {
            setModo("consultar");
            setCuadreActivoId(null);
            setModoColaborador(false);
          }}
        >
          Consultar cuadres
        </button>
        )}
        {/* 🔹 NUEVO: Botón Modo Colaborador */}
        <button
          className={modoColaborador ? "tab active" : "tab"}
          onClick={() => {
            setModoColaborador(true);
            setCuadreActivoId(null);
            setColaboradorCuadreId(null);
            setColaboradorCuadre(null);
          }}
          style={{
            background: modoColaborador ? 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)' : undefined,
            color: modoColaborador ? 'white' : undefined,
            border: modoColaborador ? 'none' : undefined
          }}
        >
          👥 Modo Colaborador
        </button>
      </div>
      
      {/* 🔹 VISTA MODO COLABORADOR - Solo Transferencias y Domicilios */}
      {modoColaborador && (
        <div className="card" style={{ marginTop: 16 }}>
          <div style={{
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: 'white',
            padding: '16px 20px',
            marginBottom: '20px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '32px' }}>👥</span>
            <div>
              <div style={{ fontSize: '18px', fontWeight: '700' }}>MODO COLABORADOR</div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>
                Solo puedes ver y editar Transferencias y Domicilios. Sin conflictos con el usuario principal.
              </div>
            </div>
          </div>
          
          {!colaboradorCuadreId ? (
            // Seleccionar cuadre para colaborar
            (() => {
              const miId = empleadoActual?.id || usuarioActual?.id || "";
              // 🔹 FILTRAR: Solo cuadres donde YO soy el colaborador asignado
              const cuadresDisponiblesParaColaborar = cuadresEnProceso.filter(c => 
                String(c.colaboradorId) === String(miId)
              );
              
              return (
                <div>
                  <h3 style={{ marginTop: 0 }}>Cuadres asignados para colaborar</h3>
                  <p className="muted" style={{ marginBottom: 16 }}>
                    Solo puedes colaborar en cuadres donde el responsable te haya asignado como colaborador.
                  </p>
                  
                  {cuadresDisponiblesParaColaborar.length === 0 ? (
                    <div style={{
                      padding: 24,
                      textAlign: 'center',
                      backgroundColor: '#fef3c7',
                      borderRadius: 10,
                      border: '2px dashed #f59e0b'
                    }}>
                      <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
                      <p style={{ margin: 0, color: '#92400e', fontWeight: 600, fontSize: 14 }}>
                        No tienes cuadres asignados para colaborar
                      </p>
                      <p style={{ margin: '8px 0 0 0', color: '#a16207', fontSize: 12 }}>
                        El responsable del cuadre debe asignarte como colaborador desde su vista de cuadre.
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {cuadresDisponiblesParaColaborar.map((c) => {
                        const emp = empleados.find((e) => e.id === c.empleadoId);
                        return (
                          <div 
                            key={c.id}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '14px 18px',
                              backgroundColor: '#f0fdf4',
                              borderRadius: '10px',
                              border: '2px solid #22c55e'
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ color: '#22c55e' }}>✓</span>
                                {c.fecha} - {c.sede}
                              </div>
                              <div style={{ fontSize: 12, color: '#64748b' }}>
                                Responsable: {emp?.nombre || 'Sin asignar'}
                              </div>
                            </div>
                            <button
                              onClick={async () => {
                                setColaboradorSincronizando(true);
                                try {
                                  const { data, error } = await supabase
                                    .from("cuadres_diarios")
                                    .select("*")
                                    .eq("id", c.id)
                                    .maybeSingle();
                                  
                                  if (error) throw error;
                                  
                                  if (data) {
                                    const cuadreActualizado = mapearRowCuadreDesdeSupabase(data);
                                    setColaboradorCuadre(cuadreActualizado);
                                    setColaboradorCuadreId(c.id);
                                    setCuadres(prev => prev.map(cuadre => 
                                      cuadre.id === c.id ? cuadreActualizado : cuadre
                                    ));
                                  }
                                } catch (e) {
                                  console.error("Error cargando cuadre:", e);
                                  alert("Error al cargar el cuadre. Intenta de nuevo.");
                                } finally {
                                  setColaboradorSincronizando(false);
                                }
                              }}
                              style={{
                                padding: '10px 20px',
                                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            >
                              🚀 Colaborar
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            // Vista de colaboración activa
            <div>
              {colaboradorSincronizando && (
                <div style={{
                  padding: 12,
                  backgroundColor: '#fef3c7',
                  borderRadius: 8,
                  marginBottom: 16,
                  textAlign: 'center'
                }}>
                  ⏳ Sincronizando...
                </div>
              )}
              
              {/* Header con info del cuadre */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20,
                padding: '12px 16px',
                backgroundColor: '#f1f5f9',
                borderRadius: 10
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>
                    {colaboradorCuadre?.fecha} - {colaboradorCuadre?.sede}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    Responsable: {empleados.find(e => e.id === colaboradorCuadre?.empleadoId)?.nombre || 'Sin asignar'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={async () => {
                      setColaboradorSincronizando(true);
                      try {
                        const { data, error } = await supabase
                          .from("cuadres_diarios")
                          .select("*")
                          .eq("id", colaboradorCuadreId)
                          .maybeSingle();
                        
                        if (error) throw error;
                        
                        if (data) {
                          const cuadreActualizado = mapearRowCuadreDesdeSupabase(data);
                          setColaboradorCuadre(cuadreActualizado);
                          setCuadres(prev => prev.map(c => 
                            c.id === colaboradorCuadreId ? cuadreActualizado : c
                          ));
                        }
                      } catch (e) {
                        console.error("Error sincronizando:", e);
                      } finally {
                        setColaboradorSincronizando(false);
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: 13
                    }}
                  >
                    🔄 Sincronizar
                  </button>
                  <button
                    onClick={() => {
                      setColaboradorCuadreId(null);
                      setColaboradorCuadre(null);
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: 13
                    }}
                  >
                    ✕ Salir
                  </button>
                </div>
              </div>
              
              {/* Contenedor de Domicilios y Transferencias en dos columnas */}
              {colaboradorCuadre && (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '24px',
                  alignItems: 'start'
                }}>
                  <CuadroDomicilios
                    domicilios={colaboradorCuadre.domicilios || []}
                    onChange={(domicilios) => {
                      // Actualizar localmente
                      setColaboradorCuadre(prev => ({ ...prev, domicilios }));
                      // Enviar actualización parcial
                      actualizarCuadreParcial(colaboradorCuadreId, 'domicilios', domicilios);
                    }}
                    disabled={false}
                    bloquearFila={(filaId) => bloquearFila('domicilios', filaId)}
                    desbloquearFila={(filaId) => desbloquearFila('domicilios', filaId)}
                    filaEstaBloqueadaPorOtro={(filaId) => filaEstaBloqueadaPorOtro('domicilios', filaId)}
                    fecha={colaboradorCuadre.fecha}
                    sede={colaboradorCuadre.sede}
                    transferencias={colaboradorCuadre.transfer || []}
                  />
 
                  <CuadroTransfer
                    transfer={colaboradorCuadre.transfer || []}
                    onChange={(transfer) => {
                      // Actualizar localmente
                      setColaboradorCuadre(prev => ({ ...prev, transfer }));
                      // Enviar actualización parcial
                      actualizarCuadreParcial(colaboradorCuadreId, 'transfer', transfer);
                    }}
                    disabled={false}
                    bloquearFila={(filaId) => bloquearFila('transfer', filaId)}
                    desbloquearFila={(filaId) => desbloquearFila('transfer', filaId)}
                    filaEstaBloqueadaPorOtro={(filaId) => filaEstaBloqueadaPorOtro('transfer', filaId)}
                    fecha={colaboradorCuadre.fecha}
                    sede={colaboradorCuadre.sede}
                  />
                </div>
              )}
              
              {/* Indicador de modo colaborador */}
              <div style={{
                marginTop: 20,
                padding: 12,
                backgroundColor: '#ede9fe',
                borderRadius: 8,
                border: '1px solid #c4b5fd',
                fontSize: 12,
                color: '#5b21b6',
                textAlign: 'center'
              }}>
                ℹ️ Los cambios se sincronizan automáticamente con el usuario principal sin conflictos
              </div>
            </div>
          )}
        </div>
      )}
 
      {modo === "crear" && tienePermiso("admin_cuadre_abrir") && !modoColaborador && (
        <>
          {cuadresEnProceso.length > 0 && (
            <div className="card" style={{ marginBottom: 12 }}>
              <h2>Cuadres en proceso</h2>
              <p className="muted">
                {esGerente 
                  ? "Estos cuadres aún no han sido cerrados. Puedes retomarlos o eliminarlos."
                  : "Puedes ver todos los cuadres en proceso. Si otro usuario tiene uno abierto, puedes solicitar acceso."
                }
              </p>
              <table className="table table-small">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Sede</th>
                    <th>Empleado</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cuadresEnProceso
                    .map((c) => {
                    const emp = empleados.find((e) => e.id === c.empleadoId);
                    const miId = empleadoActual?.id || usuarioActual?.id || "";
                    const esMiCuadre = String(c.empleadoId) === String(miId);
                    const tieneAccesoAprobado = cuadresConAccesoAprobado.includes(String(c.id));
                    
                    // Verificar estrictamente si es gerente (doble verificación)
                    const perfilActual = (empleadoActual?.perfil || usuarioActual?.perfil || usuarioActual?.rol || "").toString().toUpperCase();
                    const esRealmenteGerente = esGerente && (
                      perfilActual.includes("GERENCIA") || 
                      perfilActual.includes("GERENTE") || 
                      perfilActual.includes("ADMIN") ||
                      perfilActual.includes("SUPERVISOR") ||
                      perfilActual.includes("COORDINADOR") ||
                      perfilActual === "SUPER_ADMIN"
                    );
                    
                    // Solo puede retomar si: es gerente REAL, es su cuadre, tiene acceso aprobado,
                    // o tiene el permiso para retomar cualquier cuadre abierto de cualquier sede
                    const puedeRetomar = esRealmenteGerente || esMiCuadre || tieneAccesoAprobado || tienePermiso("admin_cuadre_retomar_cualquiera");
                    const puedeBorrar = esRealmenteGerente; // Solo gerentes REALES pueden borrar
                    return (
                      <tr key={c.id} style={{ backgroundColor: esMiCuadre ? '#f0fdf4' : tieneAccesoAprobado ? '#eff6ff' : 'transparent' }}>
                        <td>{c.fecha}</td>
                        <td>{c.sede}</td>
                        <td>
                          {emp ? emp.nombre : "—"}
                          {esMiCuadre && <span style={{ marginLeft: 4, fontSize: 10, color: '#16a34a' }}>(Tuyo)</span>}
                          {tieneAccesoAprobado && !esMiCuadre && <span style={{ marginLeft: 4, fontSize: 10, color: '#2563eb' }}>(Acceso aprobado)</span>}
                        </td>
                        <td>
                          <span className="chip-borrador">
                            {c.estado === "final" ? "Final" : "En proceso"}
                          </span>
                        </td>
                        <td>
                          {puedeRetomar ? (
                            <Button
                              variant="outline"
                              style={tieneAccesoAprobado && !esMiCuadre ? { backgroundColor: '#10b981', color: 'white', border: 'none' } : {}}
                              onClick={() => handleAbrirCuadre(c)}
                            >
                              {tieneAccesoAprobado && !esMiCuadre ? '✅ Retomar (Aprobado)' : 'Retomar'}
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none' }}
                              onClick={() => {
                                // Mostrar directamente el modal de solicitud
                                setCuadreBloqueadoInfo({
                                  cuadre: c,
                                  bloqueo: {
                                    usuario_id: c.empleadoId,
                                    usuario_nombre: emp?.nombre || "Otro usuario",
                                    bloqueado_en: new Date().toISOString()
                                  }
                                });
                                setMostrarModalSolicitud(true);
                                setSolicitudEnviada(false);
                              }}
                            >
                              🔓 Solicitar acceso
                            </Button>
                          )}
                          {esRealmenteGerente && (
                            <Button
                              variant="outline"
                              style={{ marginLeft: 4, backgroundColor: '#fbbf24', color: '#1a1a2e', border: 'none', fontWeight: 'normal', fontSize: 13 }}
                              onClick={() => setCuadreEditando({ ...c })}
                            >
                              ✏️ Modificar
                            </Button>
                          )}
                          {esRealmenteGerente && (
                            <Button
                              variant="outline"
                              style={{ marginLeft: 4, backgroundColor: '#8b5cf6', color: 'white', border: 'none', fontWeight: 'normal', fontSize: 13 }}
                              onClick={() => handleGestionarAccesos(c)}
                            >
                              👥 Accesos
                            </Button>
                          )}
                          {(esMiCuadre || esRealmenteGerente) && (
                            <Button
                              variant="outline"
                              style={{ 
                                marginLeft: 4, 
                                backgroundColor: c.colaboradorId ? '#10b981' : '#6366f1', 
                                color: 'white', 
                                border: 'none', 
                                fontWeight: 'normal', 
                                fontSize: 13 
                              }}
                              onClick={() => setCuadreAsignarColaborador(c)}
                            >
                              👥👥 {c.colaboradorId ? 'Colaborador ✓' : 'Colaborador'}
                            </Button>
                          )}
                          {puedeBorrar && !c.fueReabierto && (
                            <Button
                              variant="outline"
                              style={{ marginLeft: 4, backgroundColor: '#dc2626', color: 'white', border: 'none', fontWeight: 'normal', fontSize: 13 }}
                              onClick={() => borrarCuadre(c.id)}
                            >
                              Borrar
                            </Button>
                          )}
                          {c.fueReabierto && (
                            <span style={{ marginLeft: 8, fontSize: 11, color: '#dc2626', fontWeight: 'bold' }}>
                              🔒 Reabierto (no borrable)
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Modal para modificar cuadre en proceso */}
          {cuadreEditando && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16
            }}>
              <div style={{
                background: 'white',
                borderRadius: 12,
                maxWidth: 500,
                width: '100%',
                maxHeight: '80vh',
                overflow: 'auto',
                padding: 24,
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
              }}>
                <h2 style={{ marginBottom: 20, color: '#ff6b35' }}>✏️ Modificar Cuadre en Proceso</h2>
                
                {/* Fecha */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>Fecha:</label>
                  <input
                    type="date"
                    value={cuadreEditando.fecha || ''}
                    onChange={(e) => setCuadreEditando({ ...cuadreEditando, fecha: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: 14 }}
                  />
                </div>

                {/* Sede */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>Sede:</label>
                  <select
                    value={cuadreEditando.sede || ''}
                    onChange={(e) => setCuadreEditando({ ...cuadreEditando, sede: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: 14 }}
                  >
                    <option value="">Selecciona una sede</option>
                    {sedes.map((s) => {
                      const sedeNombre = typeof s === 'string' ? s : s.nombre;
                      return (
                        <option key={sedeNombre} value={sedeNombre}>{sedeNombre}</option>
                      );
                    })}
                  </select>
                </div>

                {/* Empleado */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>Empleado Responsable:</label>
                  <select
                    value={cuadreEditando.empleadoId || ''}
                    onChange={(e) => setCuadreEditando({ ...cuadreEditando, empleadoId: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: 14 }}
                  >
                    <option value="">Selecciona un empleado</option>
                    {[...(empleados || [])].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map((emp) => (
                      <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                    ))}
                  </select>
                </div>

                {/* Valor Inicial */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>Valor Inicial ($):</label>
                  <input
                    type="number"
                    value={cuadreEditando.valorInicial || ''}
                    onChange={(e) => setCuadreEditando({ ...cuadreEditando, valorInicial: parseFloat(e.target.value) || 0 })}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: 14 }}
                    placeholder="0"
                  />
                </div>

                {/* Ventas - Venta Efectivo */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>Venta Efectivo ($):</label>
                  <input
                    type="number"
                    value={cuadreEditando.ventas?.ventaEfectivo || ''}
                    onChange={(e) => setCuadreEditando({ 
                      ...cuadreEditando, 
                      ventas: { ...cuadreEditando.ventas, ventaEfectivo: e.target.value }
                    })}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: 14 }}
                    placeholder="0"
                  />
                </div>

                {/* Ventas - Sobrante */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>Sobrante ($):</label>
                  <input
                    type="number"
                    value={cuadreEditando.ventas?.sobrante || ''}
                    onChange={(e) => setCuadreEditando({ 
                      ...cuadreEditando, 
                      ventas: { ...cuadreEditando.ventas, sobrante: e.target.value }
                    })}
                    style={{ width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: 14 }}
                    placeholder="0"
                  />
                </div>

                {/* Botones */}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
                  <button
                    onClick={() => setCuadreEditando(null)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#94a3b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={guardarEdicionCuadre}
                    style={{
                      padding: '10px 20px',
                      background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    💾 Guardar Cambios
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 🔹 MODAL PARA ASIGNAR COLABORADOR */}
          {cuadreAsignarColaborador && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.6)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16
            }}>
              <div style={{
                background: 'white',
                borderRadius: 16,
                maxWidth: 500,
                width: '100%',
                overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.4)'
              }}>
                {/* Header */}
                <div style={{
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                  padding: '20px 24px',
                  color: 'white'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 32 }}>👥👥</span>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Asignar Colaborador</h3>
                      <p style={{ margin: 0, fontSize: 13, opacity: 0.9 }}>
                        {cuadreAsignarColaborador.fecha} - {cuadreAsignarColaborador.sede}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Body */}
                <div style={{ padding: 24 }}>
                  <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: 14 }}>
                    El colaborador solo podrá editar <strong>Transferencias</strong> y <strong>Domicilios</strong>.
                    Verá este cuadre en su "Modo Colaborador".
                  </p>
                  
                  {cuadreAsignarColaborador.colaboradorId ? (
                    // Ya hay colaborador
                    <div style={{
                      padding: 16,
                      backgroundColor: '#d1fae5',
                      borderRadius: 12,
                      border: '2px solid #22c55e'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 700, color: '#166534', fontSize: 15 }}>
                            ✓ {cuadreAsignarColaborador.colaboradorNombre}
                          </div>
                          <div style={{ fontSize: 12, color: '#15803d', marginTop: 4 }}>
                            Actualmente asignado como colaborador
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (window.confirm(`¿Quitar a ${cuadreAsignarColaborador.colaboradorNombre} como colaborador?`)) {
                              actualizarCuadre(cuadreAsignarColaborador.id, { 
                                colaboradorId: null, 
                                colaboradorNombre: null 
                              });
                              setCuadreAsignarColaborador(prev => prev ? {...prev, colaboradorId: null, colaboradorNombre: null} : null);
                            }
                          }}
                          style={{
                            padding: '10px 18px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontSize: 13
                          }}
                        >
                          ✕ Quitar Colaborador
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Seleccionar colaborador
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                        Seleccionar empleado:
                      </label>
                      <select
                        id="selectColaboradorModal"
                        style={{
                          width: '100%',
                          padding: '12px 14px',
                          border: '2px solid #c4b5fd',
                          borderRadius: 10,
                          fontSize: 15,
                          backgroundColor: 'white'
                        }}
                        defaultValue=""
                      >
                        <option value="">-- Seleccionar empleado --</option>
                        {empleados
                          .filter(e => String(e.id) !== String(cuadreAsignarColaborador.empleadoId))
                          .map(e => (
                            <option key={e.id} value={e.id}>{e.nombre}</option>
                          ))
                        }
                      </select>
                      
                      <button
                        onClick={() => {
                          const select = document.getElementById('selectColaboradorModal');
                          const selectedId = select?.value;
                          if (!selectedId) {
                            alert("⚠️ Selecciona un empleado primero");
                            return;
                          }
                          const emp = empleados.find(e => String(e.id) === String(selectedId));
                          if (emp) {
                            actualizarCuadre(cuadreAsignarColaborador.id, { 
                              colaboradorId: emp.id, 
                              colaboradorNombre: emp.nombre 
                            });
                            setCuadreAsignarColaborador(prev => prev ? {...prev, colaboradorId: emp.id, colaboradorNombre: emp.nombre} : null);
                            alert(`✅ ${emp.nombre} ahora puede colaborar en este cuadre.\n\nEl empleado verá este cuadre en su "Modo Colaborador".`);
                          }
                        }}
                        style={{
                          marginTop: 16,
                          width: '100%',
                          padding: '14px',
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: 10,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontSize: 15,
                          boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)'
                        }}
                      >
                        ✓ Asignar Colaborador
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Footer */}
                <div style={{ 
                  padding: '16px 24px', 
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={() => setCuadreAsignarColaborador(null)}
                    style={{
                      padding: '10px 24px',
                      backgroundColor: '#94a3b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: 14
                    }}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal para gestionar accesos a cuadres (solo gerentes) */}
          {mostrarModalGestionAccesos && cuadreGestionAccesos && (
            <div style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16
            }}>
              <div style={{
                background: 'white',
                borderRadius: 12,
                maxWidth: 500,
                width: '100%',
                maxHeight: '80vh',
                overflow: 'auto',
                padding: 24,
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
              }}>
                <h2 style={{ marginBottom: 8, color: '#6b21a8' }}>👥 Gestión de Accesos</h2>
                <p style={{ marginBottom: 20, color: '#64748b', fontSize: 14 }}>
                  Cuadre: {cuadreGestionAccesos.sede} - {cuadreGestionAccesos.fecha}
                </p>
                
                {cargandoAccesos ? (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <div style={{ fontSize: 32 }}>⏳</div>
                    <p>Cargando accesos...</p>
                  </div>
                ) : accesosDelCuadre.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
                    <p>No hay usuarios con acceso a este cuadre</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {accesosDelCuadre.map((acceso) => (
                      <div 
                        key={acceso.id} 
                        style={{ 
                          padding: 16, 
                          background: acceso.accesoRevocado ? '#fef2f2' : (acceso.esPropietario ? '#fef3c7' : '#f8fafc'), 
                          borderRadius: 8, 
                          border: acceso.accesoRevocado ? '2px solid #dc2626' : (acceso.esPropietario ? '2px solid #f59e0b' : '1px solid #e2e8f0'),
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            {acceso.solicitante_nombre || 'Usuario desconocido'}
                            {acceso.esPropietario && (
                              <span style={{ 
                                fontSize: 10, 
                                backgroundColor: '#f59e0b', 
                                color: 'white', 
                                padding: '2px 6px', 
                                borderRadius: 4,
                                fontWeight: 600
                              }}>
                                ABRIÓ CUADRE
                              </span>
                            )}
                            {acceso.accesoRevocado && (
                              <span style={{ 
                                fontSize: 10, 
                                backgroundColor: '#dc2626', 
                                color: 'white', 
                                padding: '2px 6px', 
                                borderRadius: 4,
                                fontWeight: 600
                              }}>
                                SIN ACCESO
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            {acceso.esPropietario 
                              ? `Creó el cuadre el ${acceso.fecha_solicitud}`
                              : acceso.respondido_en 
                                ? `Aprobado: ${new Date(acceso.respondido_en).toLocaleString()}`
                                : `Solicitud: ${new Date(acceso.fecha_solicitud).toLocaleString()}`
                            }
                          </div>
                          {!acceso.esPropietario && acceso.respondido_por_nombre && (
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>
                              Por: {acceso.respondido_por_nombre}
                            </div>
                          )}
                        </div>
                        {acceso.accesoRevocado ? (
                          <button
                            onClick={() => handleRestaurarAcceso(acceso)}
                            style={{
                              padding: '8px 14px',
                              backgroundColor: '#16a34a',
                              color: 'white',
                              border: 'none',
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: 13
                            }}
                          >
                            ✅ Restaurar
                          </button>
                        ) : (
                          <button
                            onClick={() => handleRevocarAcceso(acceso)}
                            style={{
                              padding: '8px 14px',
                              backgroundColor: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: 13
                            }}
                          >
                            ❌ Revocar
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
                  <button
                    onClick={() => {
                      setMostrarModalGestionAccesos(false);
                      setCuadreGestionAccesos(null);
                      setAccesosDelCuadre([]);
                    }}
                    style={{
                      padding: '10px 24px',
                      backgroundColor: '#94a3b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          )}
 
          <CrearCuadre
            sedes={sedes}
            empleados={empleados}
            cuadreActivo={cuadreActivo}
            setCuadreActivoId={setCuadreActivoId}
            crearOBuscarBorrador={crearOBuscarBorrador}
            actualizarCuadre={actualizarCuadre}
            actualizarCuadreParcial={actualizarCuadreParcial}
            cuadres={cuadres}
            guardarFinal={guardarFinal}
            guardarComentario={guardarComentario}
            realizarPrecierre={realizarPrecierre}
            usuarioActual={usuarioActual}
            empleadoActual={empleadoActual}
            esGerente={esGerente}
            tienePermiso={tienePermiso}
            accesosDelCuadre={accesosDelCuadre}
            usuariosActivosEnCuadre={usuariosActivosEnCuadre}
            ultimaActualizacionRemota={ultimaActualizacionRemota}
            filtroEmpresa={filtroEmpresa}
            bloquearFila={bloquearFila}
            desbloquearFila={desbloquearFila}
            filaEstaBloqueadaPorOtro={filaEstaBloqueadaPorOtro}
          />
        </>
      )}
 
      {modo === "consultar" && tienePermiso("admin_cuadre_consultar") && !modoColaborador && (
        <>
          <ConsultasCuadres
            sedes={sedes}
            empleados={empleados}
            cuadres={cuadres}
            setCuadreActivoId={setCuadreActivoId}
            esGerente={esGerente}
            onGestionarAccesos={handleGestionarAccesos}
            onReabrirCuadre={reabrirCuadre}
            tienePermiso={tienePermiso}
          />
 
          {cuadreActivo && (
            <div style={{ marginTop: "16px" }}>
              <CuadreSoloLectura
                cuadre={cuadreActivo}
                empleados={empleados}
                onGuardarComentario={guardarComentario}
                tienePermiso={tienePermiso}
              />
            </div>
          )}
        </>
      )}
      
      {/* 🔄 BOTÓN FLOTANTE DE SINCRONIZACIÓN */}
      {cuadreActivoId && (
        <div style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "8px"
        }}>
          {/* Indicador de usuarios activos */}
          {usuariosActivosEnCuadre.length > 0 && (
            <div style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              padding: "8px 14px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: "600",
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}>
              <span style={{ fontSize: "14px" }}>👥</span>
              {usuariosActivosEnCuadre.length} usuario{usuariosActivosEnCuadre.length > 1 ? "s" : ""} trabajando
            </div>
          )}
          
          {/* Indicador de última sincronización */}
          <div style={{
            background: "rgba(0,0,0,0.75)",
            color: "#fff",
            padding: "6px 12px",
            borderRadius: "16px",
            fontSize: "11px",
            display: "flex",
            alignItems: "center",
            gap: "6px"
          }}>
            <span style={{ 
              width: "8px", 
              height: "8px", 
              borderRadius: "50%", 
              background: sincronizando ? "#ffc107" : "#4caf50",
              animation: sincronizando ? "pulse 1s infinite" : "none"
            }}></span>
            {sincronizando ? "Sincronizando..." : 
              ultimaSincronizacion 
                ? `Sync: ${Math.round((Date.now() - ultimaSincronizacion.getTime()) / 1000)}s`
                : "Auto-sync: 2s"
            }
          </div>
          
          {/* Botón de sincronización manual */}
          <button
            onClick={() => sincronizarCuadreActivo(true)}
            disabled={sincronizando}
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              border: "none",
              background: sincronizando 
                ? "linear-gradient(135deg, #ffc107 0%, #ff9800 100%)"
                : "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
              color: "white",
              fontSize: "24px",
              cursor: sincronizando ? "wait" : "pointer",
              boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s ease",
              animation: sincronizando ? "spin 1s linear infinite" : "none"
            }}
            title="Sincronizar ahora"
          >
            🔄
          </button>
        </div>
      )}
      
      {/* Estilos para animaciones */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ====================== ROOT INVENTARIO ======================
export function InventarioRoot({ sedes, empleados, tienePermiso = () => true, filtroEmpresa = null, usuarioActual = null, empleadoActual = null }) {
  const [inventarios, setInventarios] = useState(() => loadInventarios());
  // Determinar modo inicial según permisos
  const modoInicial = tienePermiso("admin_inventario_crear") ? "crear" : 
                      tienePermiso("admin_inventario_consultar") ? "consultar" : "crear";
  const [modo, setModo] = useState(modoInicial); // "crear" | "consultar"
  const [inventarioActivoId, setInventarioActivoId] = useState(null);
  const [cargandoInventarios, setCargandoInventarios] = useState(false);
  const [sincronizandoInventario, setSincronizandoInventario] = useState(false);

  const [cabecera, setCabecera] = useState(() => ({
    fecha: toISO(new Date()),
    sede: sedes && sedes.length ? sedes[0] : "",
    encargadoId: "",
    personalCocina: "",
    novedades: "",
  }));

  const [lineasDiario, setLineasDiario] = useState(() =>
    PRODUCTOS_INVENTARIO_DIARIO.map((nombre) => ({
      id: uid(),
      producto: nombre,
      existencias: "",
      solicitar: "No",
      cantidad: "",
    }))
  );

  const [adicionales, setAdicionales] = useState([]);
  const [productoAdicional, setProductoAdicional] = useState("");

  useEffect(() => {
    saveInventarios(inventarios);
  }, [inventarios]);

  useEffect(() => {
    let activo = true;
    setCargandoInventarios(true);
    console.log("🔄 Cargando inventarios desde Supabase, filtroEmpresa:", filtroEmpresa);
    cargarInventariosDeSupabase(filtroEmpresa)
      .then((lista) => {
        if (!activo) return;
        console.log("✅ Inventarios cargados de Supabase:", lista.length);
        console.log("📋 Inventarios abiertos en Supabase:", lista.filter(i => !i.cerrado).length);
        console.log("📋 Inventarios cerrados en Supabase:", lista.filter(i => i.cerrado).length);
        
        setInventarios((prev) => {
          const localesSinDuplicados = (prev || []).filter(
            (loc) => !lista.some((remoto) => remoto.id === loc.id)
          );
          console.log("💾 Inventarios locales:", (prev || []).length);
          console.log("🔀 Inventarios locales sin duplicados:", localesSinDuplicados.length);
          
          // NUEVO: Sincronizar inventarios locales que no están en Supabase
          if (localesSinDuplicados.length > 0) {
            console.log("🔄 Sincronizando", localesSinDuplicados.length, "inventarios locales a Supabase...");
            localesSinDuplicados.forEach((inv) => {
              guardarInventarioEnSupabase(inv, filtroEmpresa).then((resultado) => {
                if (resultado) {
                  console.log("✅ Inventario local sincronizado a Supabase:", resultado.id);
                } else {
                  console.error("❌ Error sincronizando inventario local:", inv.id);
                }
              }).catch((e) => {
                console.error("❌ Error sincronizando inventario local:", inv.id, e);
              });
            });
          }
          
          const resultado = [...lista, ...localesSinDuplicados];
          console.log("📊 Total inventarios después de merge:", resultado.length);
          return resultado;
        });
      })
      .catch((e) => {
        console.error("❌ No se pudieron cargar inventarios de Supabase", e);
      })
      .finally(() => {
        if (activo) setCargandoInventarios(false);
      });

    return () => {
      activo = false;
    };
  }, [filtroEmpresa]);

  const inventarioActivo = useMemo(
    () => inventarios.find((i) => i.id === inventarioActivoId) || null,
    [inventarios, inventarioActivoId]
  );

  const estaCerrado = !!(inventarioActivo && inventarioActivo.cerrado);

  // Cuando selecciono un inventario desde "consultar", cargo sus datos
  useEffect(() => {
    if (!inventarioActivo) return;

    setCabecera({
      fecha: inventarioActivo.fecha || "",
      sede: inventarioActivo.sede || "",
      encargadoId: inventarioActivo.encargadoId || "",
      personalCocina: inventarioActivo.personalCocina || "",
      novedades: inventarioActivo.novedades || "",
    });

    const lineasBase = inventarioActivo.lineas || [];
    const baseDiario = PRODUCTOS_INVENTARIO_DIARIO.map((nombre) => {
      const encontrada = lineasBase.find((l) => l.producto === nombre) || {};
      return {
        id: encontrada.id || uid(),
        producto: nombre,
        existencias: encontrada.existencias || "",
        solicitar: encontrada.solicitar || "No",
        cantidad: encontrada.cantidad || "",
      };
    });

    const adicionalesGuardados = lineasBase.filter(
      (l) => !PRODUCTOS_INVENTARIO_DIARIO.includes(l.producto)
    );

    setLineasDiario(baseDiario);
    setAdicionales(
      adicionalesGuardados.map((l) => ({
        id: l.id || uid(),
        producto: l.producto,
        existencias: l.existencias || "",
        solicitar: l.solicitar || "No",
        cantidad: l.cantidad || "",
      }))
    );
  }, [inventarioActivo]);

  // Crear inventario nuevo (limpiar formulario)
  function handleNuevo() {
    setInventarioActivoId(null);
    setCabecera({
      fecha: toISO(new Date()),
      sede: sedes && sedes.length ? sedes[0] : "",
      encargadoId: "",
      personalCocina: "",
      novedades: "",
    });
    setLineasDiario(
      PRODUCTOS_INVENTARIO_DIARIO.map((nombre) => ({
        id: uid(),
        producto: nombre,
        existencias: "",
        solicitar: "No",
        cantidad: "",
      }))
    );
    setAdicionales([]);
    setProductoAdicional("");
  }

  // Cambio de cabecera
  function updateCabecera(campo, valor) {
    setCabecera((prev) => ({ ...prev, [campo]: valor }));
  }

  // Cambio en una línea (diario o adicional)
  function updateLinea(lista, setLista, id, campo, valor) {
    setLista((prev) =>
      prev.map((l) =>
        l.id === id
          ? {
              ...l,
              [campo]: valor,
              ...(campo === "solicitar" && valor !== "Sí"
                ? { cantidad: "" }
                : {}),
            }
          : l
      )
    );
  }

  // Agregar producto adicional
  function handleAgregarAdicional() {
    if (!productoAdicional) {
      alert("Selecciona un producto adicional.");
      return;
    }
    if (
      lineasDiario.some((l) => l.producto === productoAdicional) ||
      adicionales.some((l) => l.producto === productoAdicional)
    ) {
      alert("Ese producto ya está incluido en el inventario.");
      return;
    }

    const nuevo = {
      id: uid(),
      producto: productoAdicional,
      existencias: "",
      solicitar: "No",
      cantidad: "",
    };
    setAdicionales((prev) => [...prev, nuevo]);
    setProductoAdicional("");
  }

  // Validar que todo el INVENTARIO DIARIO está diligenciado sin espacios en blanco
  function validarInventario() {
    const { fecha, sede, encargadoId, personalCocina, novedades } = cabecera;

    if (!fecha || !sede || !encargadoId || !personalCocina || !novedades) {
      alert(
        "Fecha, sede, encargado, personal en cocina y novedades son obligatorios."
      );
      return false;
    }

    const validarLinea = (l) => {
      if (!l.existencias) return false;
      if (!l.solicitar) return false;
      if (l.solicitar === "Sí" && !l.cantidad) return false;
      return true;
    };

    // INVENTARIO DIARIO obligatorio sin espacios en blanco
    for (const l of lineasDiario) {
      if (!validarLinea(l)) {
        alert(
          `En INVENTARIO DIARIO todos los campos son obligatorios. Revisa el producto "${l.producto}".`
        );
        return false;
      }
    }

    // Si hay adicionales, también validar los que el usuario haya agregado
    for (const l of adicionales) {
      if (!validarLinea(l)) {
        alert(
          `En INVENTARIO ADICIONAL todos los campos son obligatorios para los productos agregados. Revisa "${l.producto}".`
        );
        return false;
      }
    }

    return true;
  }
  function handleGuardar() {
    if (inventarioActivo && inventarioActivo.cerrado) {
      alert("Este inventario está cerrado y no se puede modificar.");
      return;
    }

    if (!validarInventario()) return;

    const lineasConCategoria = lineasDiario.map((l) => ({
      ...l,
      categoria: "diario",
    }));
    const adicionalesConCategoria = adicionales.map((l) => ({
      ...l,
      categoria: "adicional",
    }));

    const todasLineas = [...lineasConCategoria, ...adicionalesConCategoria];

    const base = {
      id: inventarioActivo ? inventarioActivo.id : uid(),
      fecha: cabecera.fecha,
      sede: cabecera.sede,
      encargadoId: cabecera.encargadoId,
      personalCocina: cabecera.personalCocina,
      novedades: cabecera.novedades,
      lineas: todasLineas,
      cerrado: inventarioActivo ? !!inventarioActivo.cerrado : false,
    };

    // Un solo inventario por fecha + sede
    const existente = inventarios.find(
      (i) =>
        i.fecha === base.fecha &&
        i.sede === base.sede &&
        i.id !== base.id
    );

    if (existente) {
      const ok = window.confirm(
        `Ya existe un inventario para la sede "${base.sede}" en la fecha ${base.fecha}. Se reemplazará por el nuevo. ¿Continuar?`
      );
      if (!ok) return;
    }

    setInventarios((prev) => {
      const sinActual = prev.filter((i) => i.id !== base.id);
      const sinDuplicados = sinActual.filter(
        (i) => !(i.fecha === base.fecha && i.sede === base.sede)
      );
      return [...sinDuplicados, base];
    });

    setInventarioActivoId(base.id);
    alert("Inventario guardado correctamente.");

    setSincronizandoInventario(true);
    guardarInventarioEnSupabase(base, filtroEmpresa)
      .then((guardado) => {
        if (!guardado) {
          alert("No se pudo sincronizar el inventario en Supabase. Intenta nuevamente.");
          return;
        }
        setInventarios((prev) =>
          prev.map((inv) => (inv.id === guardado.id ? guardado : inv))
        );
        // Limpiar el formulario después de guardar exitosamente
        handleNuevo();
      })
      .finally(() => setSincronizandoInventario(false));
  }

  function handleEliminar(id) {
    if (!window.confirm("¿Seguro que deseas eliminar este inventario?")) return;
    setInventarios((prev) => prev.filter((i) => i.id !== id));
    if (inventarioActivoId === id) {
      setInventarioActivoId(null);
      handleNuevo();
    }
    eliminarInventarioDeSupabase(id).catch((e) =>
      console.error("No se pudo eliminar inventario en Supabase", e)
    );
  }

  function handleCerrar() {
    if (!inventarioActivo) {
      alert("Primero guarda el inventario antes de cerrarlo.");
      return;
    }
  
    if (inventarioActivo.cerrado) {
      alert("Este inventario ya está cerrado.");
      return;
    }
  
    const ok = window.confirm(
      "Al cerrar el inventario ya no se podrá modificar, solo consultar o imprimir. ¿Deseas continuar?"
    );
    if (!ok) return;
  
    setInventarios((prev) =>
      prev.map((i) =>
        i.id === inventarioActivo.id ? { ...i, cerrado: true } : i
      )
    );
  
    alert("Inventario cerrado.");

    guardarInventarioEnSupabase({ ...inventarioActivo, cerrado: true }, filtroEmpresa).catch(
      (e) => console.error("No se pudo actualizar inventario cerrado en Supabase", e)
    );
  }
  
  function handleImprimir(inv) {
    if (!inv) return;

    const encargado =
      empleados.find((e) => e.id === inv.encargadoId)?.nombre || "—";

    const html = `
      <html>
        <head>
          <title>Inventario ${inv.fecha} - ${inv.sede}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: 'Arial', sans-serif;
              padding: 15px;
              background: #fff;
            }
            .container {
              max-width: 900px;
              margin: 0 auto;
              background: white;
            }
            .header-section {
              border-bottom: 2px solid #1d4ed8;
              padding-bottom: 8px;
              margin-bottom: 12px;
            }
            .header-title {
              font-size: 18px;
              font-weight: bold;
              color: #1d4ed8;
              margin-bottom: 6px;
            }
            .header-info {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 12px;
              font-size: 11px;
              line-height: 1.5;
            }
            .info-row {
              display: flex;
              justify-content: space-between;
            }
            .info-label {
              font-weight: 600;
              color: #333;
              width: 100px;
            }
            .info-value {
              color: #555;
              flex: 1;
              text-align: left;
              padding-left: 8px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
              font-size: 11px;
            }
            thead {
              background: #1d4ed8;
              color: white;
              text-align: left;
            }
            th {
              padding: 8px 10px;
              font-weight: 600;
              border: none;
            }
            tbody tr {
              border-bottom: 1px solid #e2e8f0;
            }
            tbody tr:nth-child(even) {
              background: #f9fafb;
            }
            td {
              padding: 6px 10px;
              color: #333;
            }
            .col-producto {
              font-weight: 500;
            }
            .col-numero {
              text-align: center;
            }
            .col-solicitar {
              text-align: center;
            }
            .col-cantidad {
              text-align: center;
            }
            footer {
              margin-top: 16px;
              padding-top: 8px;
              border-top: 1px solid #e2e8f0;
              text-align: center;
              font-size: 9px;
              color: #999;
            }
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header-section">
              <div class="header-title">INVENTARIO DIARIO</div>
              <div class="header-info">
                <div class="info-row">
                  <span class="info-label">Fecha:</span>
                  <span class="info-value">${inv.fecha}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Encargado:</span>
                  <span class="info-value">${encargado}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Sede:</span>
                  <span class="info-value">${inv.sede}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Personal:</span>
                  <span class="info-value">${inv.personalCocina || "—"}</span>
                </div>
              </div>
              ${inv.novedades ? `<div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #e5e7eb; font-size: 10px;"><strong>Novedades:</strong> ${inv.novedades}</div>` : ""}
            </div>

            <table>
              <thead>
                <tr>
                  <th style="width: 50%;">Producto</th>
                  <th style="width: 17%;">Existencias</th>
                  <th style="width: 17%;">Solicitar</th>
                  <th style="width: 16%;">Cantidad</th>
                </tr>
              </thead>
              <tbody>
                ${inv.lineas
                  .map(
                    (l) => `
                  <tr>
                    <td class="col-producto">${l.producto}</td>
                    <td class="col-numero">${l.existencias}</td>
                    <td class="col-solicitar">${l.solicitar}</td>
                    <td class="col-cantidad">${l.solicitar === "Sí" ? l.cantidad : "—"}</td>
                  </tr>
                `
                  )
                  .join("")}
              </tbody>
            </table>

            <footer>
              <p>Impreso: ${new Date().toLocaleString('es-CO')}</p>
            </footer>
          </div>
        </body>
      </html>
    `;

    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  }


  // Filtros avanzados en "consultar"
  const [filtroSede, setFiltroSede] = useState("");
  const [filtroPeriodo, setFiltroPeriodo] = useState("semana"); // "dia" | "semana" | "mes" | "todos"
  const [filtroFechaEspecifica, setFiltroFechaEspecifica] = useState(toISO(new Date()));
  const [vistaConsulta, setVistaConsulta] = useState("lista"); // "lista" | "tarjetas" | "procesar" | "pedidos_guardados"
  
  // Estados para procesar pedidos
  const [productosSeleccionados, setProductosSeleccionados] = useState({});
  const [pedidosPorSede, setPedidosPorSede] = useState({});
  const [mostrarResumenPedido, setMostrarResumenPedido] = useState(false);
  const [novedadesPedido, setNovedadesPedido] = useState(""); // Novedades del pedido
  const [productosYaEnviados, setProductosYaEnviados] = useState(new Set()); // Productos ya enviados en pedidos anteriores
  
  // Estados para pedidos guardados
  const [pedidosGuardados, setPedidosGuardados] = useState([]);
  const [cargandoPedidos, setCargandoPedidos] = useState(false);
  
  // Cargar productos ya enviados hoy (para excluirlos de la lista)
  async function cargarProductosYaEnviados() {
    try {
      const hoy = toISO(new Date());
      const { data, error } = await supabase
        .from('pedidos_consolidados')
        .select('productos_keys')
        .gte('fecha', hoy + 'T00:00:00')
        .lte('fecha', hoy + 'T23:59:59');
      
      if (error) throw error;
      
      const enviados = new Set();
      (data || []).forEach(p => {
        (p.productos_keys || []).forEach(key => enviados.add(key));
      });
      setProductosYaEnviados(enviados);
    } catch (err) {
      console.error('Error cargando productos enviados:', err);
    }
  }
  
  // Cargar productos enviados al montar y al cambiar de vista
  React.useEffect(() => {
    if (vistaConsulta === "procesar") {
      cargarProductosYaEnviados();
    }
  }, [vistaConsulta]);
  
  // Cargar pedidos guardados
  async function cargarPedidosGuardados() {
    setCargandoPedidos(true);
    try {
      const { data, error } = await supabase
        .from('pedidos_consolidados')
        .select('*')
        .order('fecha', { ascending: false })
        .limit(50);
      if (error) throw error;
      setPedidosGuardados(data || []);
    } catch (err) {
      console.error('Error cargando pedidos:', err);
    } finally {
      setCargandoPedidos(false);
    }
  }
  
  // Cargar pedidos cuando se cambie a la vista de pedidos guardados
  useEffect(() => {
    if (vistaConsulta === "pedidos_guardados") {
      cargarPedidosGuardados();
    }
  }, [vistaConsulta]);

  // Calcular rango de fechas según período
  const rangoFechas = useMemo(() => {
    const fechaBase = new Date(filtroFechaEspecifica + "T12:00:00");
    let inicio, fin;
    
    if (filtroPeriodo === "dia") {
      inicio = fin = filtroFechaEspecifica;
    } else if (filtroPeriodo === "semana") {
      const diaSemana = fechaBase.getDay();
      const inicioSemana = new Date(fechaBase);
      inicioSemana.setDate(fechaBase.getDate() - diaSemana);
      const finSemana = new Date(inicioSemana);
      finSemana.setDate(inicioSemana.getDate() + 6);
      inicio = toISO(inicioSemana);
      fin = toISO(finSemana);
    } else if (filtroPeriodo === "mes") {
      const año = fechaBase.getFullYear();
      const mes = fechaBase.getMonth();
      inicio = toISO(new Date(año, mes, 1));
      fin = toISO(new Date(año, mes + 1, 0));
    } else {
      inicio = null;
      fin = null;
    }
    return { inicio, fin };
  }, [filtroPeriodo, filtroFechaEspecifica]);

  const inventariosFiltrados = useMemo(() => {
    let lista = [...inventarios];
    
    // Filtro por sede
    if (filtroSede) {
      lista = lista.filter((i) => i.sede === filtroSede);
    }
    
    // Filtro por período
    if (rangoFechas.inicio && rangoFechas.fin) {
      lista = lista.filter((i) => i.fecha >= rangoFechas.inicio && i.fecha <= rangoFechas.fin);
    }
    
    // más recientes primero
    return lista.sort((a, b) => (a.fecha < b.fecha ? 1 : -1));
  }, [inventarios, filtroSede, rangoFechas]);

  // Consolidar productos solicitados de los inventarios filtrados
  // SOLO de inventarios CERRADOS (para Procesar pedido)
  const productosSolicitadosConsolidados = useMemo(() => {
    const consolidado = {};
    // Filtrar solo inventarios cerrados para procesar pedido
    const inventariosCerrados = inventariosFiltrados.filter(inv => !!inv.cerrado);
    console.log("📦 Procesar pedido - Total inventarios filtrados:", inventariosFiltrados.length);
    console.log("📦 Procesar pedido - Inventarios cerrados:", inventariosCerrados.length);
    console.log("📦 Detalle inventarios:", inventariosFiltrados.map(i => ({ id: i.id, sede: i.sede, cerrado: i.cerrado, lineas: i.lineas?.length })));
    inventariosCerrados.forEach((inv) => {
      if (!inv.lineas) return;
      inv.lineas.forEach((linea) => {
        if (linea.solicitar === "Sí" && linea.cantidad) {
          const key = `${inv.sede}::${linea.producto}`;
          if (!consolidado[key]) {
            consolidado[key] = {
              sede: inv.sede,
              producto: linea.producto,
              cantidad: 0,
              fechas: [],
              existenciasUltimo: linea.existencias
            };
          }
          consolidado[key].cantidad += parseInt(linea.cantidad) || 0;
          if (!consolidado[key].fechas.includes(inv.fecha)) {
            consolidado[key].fechas.push(inv.fecha);
          }
          consolidado[key].existenciasUltimo = linea.existencias;
        }
      });
    });
    return Object.values(consolidado);
  }, [inventariosFiltrados]);

  // Agrupar productos por sede para las tarjetas
  const productosPorSede = useMemo(() => {
    const porSede = {};
    productosSolicitadosConsolidados.forEach((item) => {
      if (!porSede[item.sede]) {
        porSede[item.sede] = [];
      }
      porSede[item.sede].push(item);
    });
    return porSede;
  }, [productosSolicitadosConsolidados]);

  // NUEVO: Agrupar por FECHA con novedades de cada sede
  const productosPorFecha = useMemo(() => {
    const porFecha = {};
    const inventariosCerrados = inventariosFiltrados.filter(inv => !!inv.cerrado);
    
    inventariosCerrados.forEach((inv) => {
      if (!inv.lineas) return;
      const fecha = inv.fecha;
      
      if (!porFecha[fecha]) {
        porFecha[fecha] = {
          fecha: fecha,
          sedes: {}
        };
      }
      
      if (!porFecha[fecha].sedes[inv.sede]) {
        // Buscar nombre del encargado
        const encargadoNombre = empleados.find(e => e.id === inv.encargadoId)?.nombre || 'N/A';
        porFecha[fecha].sedes[inv.sede] = {
          sede: inv.sede,
          novedades: inv.novedades || '',
          encargadoNombre: encargadoNombre,
          horaCreacion: inv.hora_creacion || inv.created_at ? new Date(inv.created_at || Date.now()).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' }) : '',
          productos: []
        };
      }
      
      // Agregar productos de esta sede
      inv.lineas.forEach((linea) => {
        if (linea.solicitar === "Sí" && linea.cantidad) {
          // Verificar si ya existe el producto para sumarlo
          const existente = porFecha[fecha].sedes[inv.sede].productos.find(p => p.producto === linea.producto);
          if (existente) {
            existente.cantidad += parseInt(linea.cantidad) || 0;
            existente.existencias = linea.existencias;
          } else {
            porFecha[fecha].sedes[inv.sede].productos.push({
              producto: linea.producto,
              cantidad: parseInt(linea.cantidad) || 0,
              existencias: linea.existencias
            });
          }
        }
      });
    });
    
    // Ordenar fechas de más reciente a más antigua
    return Object.values(porFecha).sort((a, b) => (a.fecha < b.fecha ? 1 : -1));
  }, [inventariosFiltrados, empleados]);

  // Filtrar productos que NO han sido enviados hoy
  const productosPorSedeDisponibles = useMemo(() => {
    const porSede = {};
    Object.entries(productosPorSede).forEach(([sede, productos]) => {
      const disponibles = productos.filter(p => !productosYaEnviados.has(`${p.sede}::${p.producto}`));
      if (disponibles.length > 0) {
        porSede[sede] = disponibles;
      }
    });
    return porSede;
  }, [productosPorSede, productosYaEnviados]);

  // Función para generar pedido final
  const [productosKeysSeleccionados, setProductosKeysSeleccionados] = useState([]);
  
  function generarPedidoFinal() {
    const pedidos = {};
    const keysSeleccionados = [];
    const empleadoNombre = empleadoActual?.nombre || usuarioActual?.nombre || usuarioActual?.email || 'Usuario';
    const horaActual = new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
    
    Object.entries(productosSeleccionados).forEach(([key, seleccionado]) => {
      if (seleccionado) {
        const item = productosSolicitadosConsolidados.find(
          (p) => `${p.sede}::${p.producto}` === key
        );
        if (item) {
          if (!pedidos[item.sede]) {
            pedidos[item.sede] = [];
          }
          pedidos[item.sede].push({
            producto: item.producto,
            cantidad: item.cantidad,
            existencias: item.existenciasUltimo,
            procesado_por: empleadoNombre,
            hora_procesado: horaActual
          });
          keysSeleccionados.push(key);
        }
      }
    });
    setPedidosPorSede(pedidos);
    setProductosKeysSeleccionados(keysSeleccionados);
    setNovedadesPedido(""); // Limpiar novedades al generar nuevo pedido
    setMostrarResumenPedido(true);
  }

  // Seleccionar/deseleccionar todos los productos de una sede
  function toggleSeleccionSede(sede, seleccionar) {
    const nuevaSeleccion = { ...productosSeleccionados };
    productosPorSedeDisponibles[sede]?.forEach((item) => {
      nuevaSeleccion[`${item.sede}::${item.producto}`] = seleccionar;
    });
    setProductosSeleccionados(nuevaSeleccion);
  }

  return (
    <div>
      {/* Cabecera ejecutiva */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #0f172a 0%, #1e293b 40%, #0ea5e9 100%)",
          borderRadius: 8,
          padding: "12px 16px",
          marginBottom: 16,
          color: "white",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2 className="text-sm font-semibold">
            Inventario – Ambiente administrativo
          </h2>
          <span style={{ fontSize: 11, opacity: 0.9 }}>
            Módulo ejecutivo de inventarios
          </span>
        </div>
      </div>

      {(cargandoInventarios || sincronizandoInventario) && (
        <div className="card" style={{ marginBottom: 12, backgroundColor: "#f8fafc" }}>
          <p style={{ margin: 0, fontSize: 12, color: "#0f172a" }}>
            {cargandoInventarios
              ? "Cargando inventarios desde Supabase…"
              : "Sincronizando inventario con Supabase…"}
          </p>
        </div>
      )}

      {/* Tabs Crear / Consultar */}
      <div className="tabs" style={{ marginBottom: 12 }}>
        {tienePermiso("admin_inventario_crear") && (
        <button
          className={modo === "crear" ? "tab active" : "tab"}
          onClick={() => {
            setModo("crear");
          }}
        >
          Crear inventario
        </button>
        )}
        {tienePermiso("admin_inventario_consultar") && (
        <button
          className={modo === "consultar" ? "tab active" : "tab"}
          onClick={() => {
            setModo("consultar");
            setInventarioActivoId(null);
          }}
        >
          Consultar inventarios
        </button>
        )}
      </div>

      {modo === "crear" && tienePermiso("admin_inventario_crear") && (
        <>
          {/* Formulario de cabecera */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 className="text-sm font-semibold" style={{ marginBottom: 8 }}>
              Datos del inventario
            </h3>
            <div className="grid-3">
              <Field label="Fecha">
                <Input
                  type="date"
                  value={cabecera.fecha}
                  onChange={(e) => updateCabecera("fecha", e.target.value)}
                  disabled={estaCerrado}
                />
              </Field>

              <Field label="Sede">
                <select
                  className="input"
                  value={cabecera.sede}
                  onChange={(e) => updateCabecera("sede", e.target.value)}
                  disabled={estaCerrado}
                >
                  <option value="">-- Selecciona --</option>
                  {sedes.map((s) => {
                    const sedeNombre = typeof s === 'string' ? s : (s?.nombre || s?.razonSocial || '');
                    return (
                      <option key={sedeNombre} value={sedeNombre}>
                        {sedeNombre}
                      </option>
                    );
                  })}
                </select>
              </Field>

              <Field label="Encargado">
                <select
                  className="input"
                  value={cabecera.encargadoId}
                  onChange={(e) =>
                    updateCabecera("encargadoId", e.target.value)
                    
                  }
                >
                  <option value="">-- Selecciona --</option>
                  {[...(empleados || [])].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nombre}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid-3">
              <Field label="Personal en la cocina">
                <Input
                  value={cabecera.personalCocina}
                  onChange={(e) =>
                    updateCabecera("personalCocina", e.target.value)
                  }
                  placeholder="Nombres del personal en cocina"
                />
              </Field>

              <Field label="Novedades">
                <textarea
                  className="input"
                  rows={2}
                  value={cabecera.novedades}
                  onChange={(e) =>
                    updateCabecera("novedades", e.target.value)
                  }
                  placeholder="Observaciones o novedades del día"
                />
              </Field>
            </div>

  
          </div>

{/* INVENTARIO DIARIO - Vista Compacta tipo Excel */}
<div className="card" style={{ marginBottom: 16, overflow: 'auto' }}>
  <h3 style={{ marginBottom: 12, fontSize: 16, fontWeight: 600, color: '#1e293b' }}>
    INVENTARIO DIARIO (obligatorio sin espacios en blanco)
  </h3>

  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
    <thead>
      <tr style={{ background: '#1e293b', color: 'white' }}>
        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 15 }}>Producto</th>
        <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, fontSize: 15, width: 110 }}>Existencia</th>
        <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, fontSize: 15, width: 90 }}>Pedir</th>
        <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, fontSize: 15, width: 110 }}>Cuántos</th>
      </tr>
    </thead>
    <tbody>
      {lineasDiario.map((l, idx) => (
        <tr 
          key={l.id} 
          style={{ 
            background: idx % 2 === 0 ? '#f8fafc' : '#ffffff',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <td style={{ padding: '10px 16px', fontWeight: 500, color: '#0f172a', fontSize: 15 }}>
            {l.producto}
          </td>
          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
            <Input
              value={l.existencias}
              onChange={(e) =>
                updateLinea(lineasDiario, setLineasDiario, l.id, "existencias", e.target.value)
              }
              placeholder="0"
              style={{ width: '100%', textAlign: 'center', padding: '8px 10px', fontSize: 15 }}
              disabled={estaCerrado}
            />
          </td>
          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
            <select
              className="input"
              value={l.solicitar}
              onChange={(e) =>
                updateLinea(lineasDiario, setLineasDiario, l.id, "solicitar", e.target.value)
              }
              style={{ width: '100%', padding: '8px 6px', fontSize: 14, textAlign: 'center' }}
              disabled={estaCerrado}
            >
              <option value="No">No</option>
              <option value="Sí">Sí</option>
            </select>
          </td>
          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
            <Input
              value={l.cantidad}
              onChange={(e) =>
                updateLinea(lineasDiario, setLineasDiario, l.id, "cantidad", e.target.value)
              }
              placeholder="0"
              style={{ 
                width: '100%', 
                textAlign: 'center', 
                padding: '8px 10px',
                fontSize: 15,
                background: l.solicitar !== "Sí" ? '#f1f5f9' : '#fff'
              }}
              disabled={l.solicitar !== "Sí" || estaCerrado}
            />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>
{/* INVENTARIO ADICIONAL - Vista Compacta tipo Excel */}
<div className="card" style={{ marginBottom: 16, overflow: 'auto' }}>
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 12,
      flexWrap: "wrap",
      gap: 10
    }}
  >
    <h3 style={{ fontSize: 16, fontWeight: 600, color: '#0ea5e9' }}>Inventario adicional</h3>
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <select
        className="input"
        value={productoAdicional}
        onChange={(e) => setProductoAdicional(e.target.value)}
        disabled={estaCerrado}
        style={{ fontSize: 14, padding: '8px 12px' }}
      >
        <option value="">-- Producto adicional --</option>
        {PRODUCTOS_INVENTARIO_ADICIONAL.map((p) => (
          <option key={p} value={p}>
            {p}
          </option>
        ))}
      </select>
      <Button variant="outline" onClick={handleAgregarAdicional} disabled={estaCerrado}>
        + Agregar
      </Button>
    </div>
  </div>

  {adicionales.length > 0 && (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15, marginTop: 8 }}>
      <thead>
        <tr style={{ background: '#0ea5e9', color: 'white' }}>
          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: 15 }}>Producto</th>
          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, fontSize: 15, width: 110 }}>Existencia</th>
          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, fontSize: 15, width: 90 }}>Pedir</th>
          <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, fontSize: 15, width: 110 }}>Cuántos</th>
        </tr>
      </thead>
      <tbody>
        {adicionales.map((l, idx) => (
          <tr 
            key={l.id} 
            style={{ 
              background: idx % 2 === 0 ? '#f0f9ff' : '#ffffff',
              borderBottom: '1px solid #e2e8f0'
            }}
          >
            <td style={{ padding: '10px 16px', fontWeight: 500, color: '#0f172a', fontSize: 15 }}>
              {l.producto}
            </td>
            <td style={{ padding: '8px 12px', textAlign: 'center' }}>
              <Input
                value={l.existencias}
                onChange={(e) =>
                  updateLinea(adicionales, setAdicionales, l.id, "existencias", e.target.value)
                }
                placeholder="0"
                style={{ width: '100%', textAlign: 'center', padding: '8px 10px', fontSize: 15 }}
                disabled={estaCerrado}
              />
            </td>
            <td style={{ padding: '8px 12px', textAlign: 'center' }}>
              <select
                className="input"
                value={l.solicitar}
                onChange={(e) =>
                  updateLinea(adicionales, setAdicionales, l.id, "solicitar", e.target.value)
                }
                style={{ width: '100%', padding: '8px 6px', fontSize: 14, textAlign: 'center' }}
                disabled={estaCerrado}
              >
                <option value="No">No</option>
                <option value="Sí">Sí</option>
              </select>
            </td>
            <td style={{ padding: '8px 12px', textAlign: 'center' }}>
              <Input
                value={l.cantidad}
                onChange={(e) =>
                  updateLinea(adicionales, setAdicionales, l.id, "cantidad", e.target.value)
                }
                placeholder="0"
                style={{ 
                  width: '100%', 
                  textAlign: 'center', 
                  padding: '8px 10px',
                  fontSize: 15,
                  background: l.solicitar !== "Sí" ? '#f1f5f9' : '#fff'
                }}
                disabled={l.solicitar !== "Sí" || estaCerrado}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )}

  {adicionales.length === 0 && (
    <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', padding: 20 }}>
      No hay productos adicionales. Usa el selector de arriba para agregar.
    </p>
  )}
</div>


{/* BOTONES GUARDAR / CERRAR */}
<div
  style={{
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginBottom: 24,
  }}
>
  {!estaCerrado && tienePermiso("admin_inventario_guardar") && (
    <Button onClick={handleGuardar}>Guardar inventario</Button>
  )}

  {inventarioActivo && tienePermiso("admin_inventario_cerrar") && (
    <Button
      variant={estaCerrado ? "outline" : "danger"}
      onClick={estaCerrado ? undefined : handleCerrar}
      disabled={estaCerrado}
    >
      {estaCerrado ? "Inventario cerrado" : "Cerrar inventario"}
    </Button>
  )}
</div>

        </>
      )}

      {modo === "consultar" && tienePermiso("admin_inventario_consultar") && (
        <div>
          {/* PANEL DE FILTROS INTERACTIVOS */}
          <div className="card" style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', marginBottom: 16 }}>
              Consultar y Procesar Inventarios
            </h3>

            {/* Filtros en fila */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, alignItems: 'flex-end' }}>
              {/* Filtro por sede */}
              <div style={{ minWidth: 150 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#64748b' }}>Sede</label>
                <select
                  className="input"
                  value={filtroSede}
                  onChange={(e) => setFiltroSede(e.target.value)}
                  style={{ fontSize: 14, padding: '8px 12px', width: '100%' }}
                >
                  <option value="">Todas las sedes</option>
                  {sedes.map((s) => {
                    const sedeNombre = typeof s === 'string' ? s : (s?.nombre || s?.razonSocial || '');
                    return (
                      <option key={sedeNombre} value={sedeNombre}>
                        {sedeNombre}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Filtro por período */}
              <div style={{ minWidth: 130 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#64748b' }}>Período</label>
                <select
                  className="input"
                  value={filtroPeriodo}
                  onChange={(e) => setFiltroPeriodo(e.target.value)}
                  style={{ fontSize: 14, padding: '8px 12px', width: '100%' }}
                >
                  <option value="dia">Día específico</option>
                  <option value="semana">Semana</option>
                  <option value="mes">Mes</option>
                  <option value="todos">Todos</option>
                </select>
              </div>

              {/* Fecha de referencia */}
              {filtroPeriodo !== "todos" && (
                <div style={{ minWidth: 150 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#64748b' }}>
                    {filtroPeriodo === "dia" ? "Fecha" : filtroPeriodo === "semana" ? "Semana de" : "Mes de"}
                  </label>
                  <input
                    type="date"
                    className="input"
                    value={filtroFechaEspecifica}
                    onChange={(e) => setFiltroFechaEspecifica(e.target.value)}
                    style={{ fontSize: 14, padding: '8px 12px', width: '100%' }}
                  />
                </div>
              )}

              {/* Mostrar rango seleccionado */}
              {rangoFechas.inicio && rangoFechas.fin && (
                <div style={{ 
                  background: '#f0f9ff', 
                  padding: '8px 12px', 
                  borderRadius: 6, 
                  fontSize: 12,
                  color: '#0369a1',
                  border: '1px solid #bae6fd'
                }}>
                  {rangoFechas.inicio === rangoFechas.fin 
                    ? `Fecha: ${rangoFechas.inicio}`
                    : `Del ${rangoFechas.inicio} al ${rangoFechas.fin}`
                  }
                </div>
              )}
            </div>

            {/* Botones de vista */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
              <button
                onClick={() => setVistaConsulta("lista")}
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: vistaConsulta === "lista" ? '2px solid #1e293b' : '1px solid #e2e8f0',
                  background: vistaConsulta === "lista" ? '#1e293b' : '#fff',
                  color: vistaConsulta === "lista" ? '#fff' : '#1e293b',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                📋 Lista de inventarios
              </button>
              <button
                onClick={() => setVistaConsulta("tarjetas")}
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: vistaConsulta === "tarjetas" ? '2px solid #0ea5e9' : '1px solid #e2e8f0',
                  background: vistaConsulta === "tarjetas" ? '#0ea5e9' : '#fff',
                  color: vistaConsulta === "tarjetas" ? '#fff' : '#0ea5e9',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                🏢 Ver por sede
              </button>
              <button
                onClick={() => {
                  setVistaConsulta("procesar");
                  setProductosSeleccionados({});
                  setMostrarResumenPedido(false);
                }}
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: vistaConsulta === "procesar" ? '2px solid #16a34a' : '1px solid #e2e8f0',
                  background: vistaConsulta === "procesar" ? '#16a34a' : '#fff',
                  color: vistaConsulta === "procesar" ? '#fff' : '#16a34a',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                📦 Procesar pedido
              </button>
              <button
                onClick={() => setVistaConsulta("pedidos_guardados")}
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: vistaConsulta === "pedidos_guardados" ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                  background: vistaConsulta === "pedidos_guardados" ? '#7c3aed' : '#fff',
                  color: vistaConsulta === "pedidos_guardados" ? '#fff' : '#7c3aed',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                📋 Pedidos Guardados
              </button>
            </div>

            {/* Resumen rápido */}
            <div style={{ 
              padding: 12, 
              background: '#f8fafc', 
              borderRadius: 8,
              display: 'flex',
              gap: 24,
              flexWrap: 'wrap'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#1e293b' }}>{inventariosFiltrados.length}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>Inventarios</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#0ea5e9' }}>
                  {[...new Set(inventariosFiltrados.map(i => i.sede))].length}
                </div>
                <div style={{ fontSize: 11, color: '#64748b' }}>Sedes</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#16a34a' }}>{productosSolicitadosConsolidados.length}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>Productos a pedir</div>
              </div>
            </div>
          </div>

          {/* VISTA: LISTA DE INVENTARIOS */}
          {vistaConsulta === "lista" && (
          <div className="card">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#1e293b', color: 'white' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left' }}>Fecha</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left' }}>Sede</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left' }}>Encargado</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center' }}>Estado</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center' }}>Acciones</th>
                  </tr>
                </thead>

  <tbody>
    {inventariosFiltrados.map((inv, idx) => {
      const enc = empleados.find((e) => e.id === inv.encargadoId)?.nombre || "—";
      const cerrado = !!inv.cerrado;
      const productosPedidos = (inv.lineas || []).filter(l => l.solicitar === "Sí").length;

      return (
        <tr key={inv.id} style={{ background: idx % 2 === 0 ? '#f8fafc' : '#fff', borderBottom: '1px solid #e2e8f0' }}>
          <td style={{ padding: '10px 16px', fontWeight: 500 }}>{inv.fecha}</td>
          <td style={{ padding: '10px 16px' }}>{inv.sede}</td>
          <td style={{ padding: '10px 16px' }}>{enc}</td>
          <td style={{ padding: '10px 16px', textAlign: 'center', minWidth: 150 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{
                padding: '6px 14px',
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 600,
                background: cerrado ? '#dcfce7' : '#fef3c7',
                color: cerrado ? '#16a34a' : '#d97706',
                whiteSpace: 'nowrap'
              }}>
                {cerrado ? "Cerrado" : "Abierto"}
              </span>
              {productosPedidos > 0 && (
                <span style={{
                  padding: '4px 10px',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                  background: '#fee2e2',
                  color: '#dc2626',
                  whiteSpace: 'nowrap'
                }}>
                  {productosPedidos} productos a pedir
                </span>
              )}
            </div>
          </td>
          <td style={{ padding: '8px 16px', textAlign: 'center' }}>
            <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="outline" onClick={() => { setInventarioActivoId(inv.id); setModo("crear"); }}>
                Ver
              </Button>
              {tienePermiso("admin_inventario_imprimir") && (
                <Button variant="outline" onClick={() => handleImprimir(inv)}>
                  Imprimir
                </Button>
              )}
              {tienePermiso("admin_inventario_eliminar") && (
                <Button variant="danger" onClick={() => handleEliminar(inv.id)}>
                  Eliminar
                </Button>
              )}
            </div>
          </td>
        </tr>
      );
    })}

    {inventariosFiltrados.length === 0 && (
      <tr>
        <td colSpan={5} style={{ textAlign: "center", padding: 24, color: '#64748b' }}>
          No hay inventarios con los filtros seleccionados.
        </td>
      </tr>
    )}
  </tbody>
              </table>
          </div>
          )}

          {/* VISTA: TARJETAS POR FECHA (UNIFICADO) */}
          {vistaConsulta === "tarjetas" && (
            <div>
              <div style={{ 
                background: '#fef3c7', 
                border: '1px solid #f59e0b', 
                borderRadius: 8, 
                padding: '10px 14px', 
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <span style={{ fontSize: 18 }}>ℹ️</span>
                <span style={{ fontSize: 13, color: '#92400e' }}>
                  <strong>Nota:</strong> Vista unificada por fecha. Solo se muestran productos de inventarios <strong>cerrados</strong>. Las novedades aparecen junto a cada sede.
                </span>
              </div>
              
              {/* Iterar por cada fecha */}
              {productosPorFecha.map((fechaData) => (
                <div key={fechaData.fecha} style={{ marginBottom: 24 }}>
                  {/* Encabezado de fecha */}
                  <div style={{ 
                    background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)', 
                    color: 'white', 
                    padding: '14px 20px',
                    borderRadius: '12px 12px 0 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                      📅 {new Date(fechaData.fecha + 'T12:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </h3>
                    <span style={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      padding: '6px 14px', 
                      borderRadius: 20,
                      fontSize: 13
                    }}>
                      {Object.keys(fechaData.sedes).length} sedes
                    </span>
                  </div>
                  
                  {/* Grid de sedes para esa fecha */}
                  <div style={{ 
                    background: '#f8fafc', 
                    border: '2px solid #1e293b',
                    borderTop: 'none',
                    borderRadius: '0 0 12px 12px',
                    padding: 16
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
                      {Object.values(fechaData.sedes).map((sedeData) => (
                        <div key={sedeData.sede} className="card" style={{ 
                          border: '2px solid #0ea5e9',
                          borderRadius: 12,
                          overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }}>
                          {/* Encabezado de sede */}
                          <div style={{ 
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', 
                            color: 'white', 
                            padding: '12px 16px'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>🏢 {sedeData.sede}</h4>
                              <span style={{ 
                                background: 'rgba(255,255,255,0.2)', 
                                padding: '4px 10px', 
                                borderRadius: 12,
                                fontSize: 12
                              }}>
                                {sedeData.productos.length} productos
                              </span>
                            </div>
                            {/* Info del encargado y hora */}
                            <div style={{ fontSize: 11, opacity: 0.9 }}>
                              <span>👤 {sedeData.encargadoNombre}</span>
                              {sedeData.horaCreacion && <span> • 🕐 {sedeData.horaCreacion}</span>}
                            </div>
                          </div>
                          
                          {/* Novedades de esta sede (si existen) */}
                          {sedeData.novedades && (
                            <div style={{ 
                              background: '#fef3c7', 
                              padding: '10px 14px', 
                              borderBottom: '1px solid #fcd34d'
                            }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>📝 Novedades:</div>
                              <div style={{ fontSize: 12, color: '#78350f', whiteSpace: 'pre-wrap' }}>{sedeData.novedades}</div>
                            </div>
                          )}
                          
                          {/* Lista de productos */}
                          <div style={{ padding: 12, maxHeight: 250, overflowY: 'auto' }}>
                            {sedeData.productos.map((p, idx) => (
                              <div key={idx} style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                padding: '8px 0',
                                borderBottom: idx < sedeData.productos.length - 1 ? '1px solid #e2e8f0' : 'none'
                              }}>
                                <span style={{ fontWeight: 500, fontSize: 14 }}>{p.producto}</span>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                  <span style={{ fontSize: 11, color: '#64748b' }}>Exist: {p.existencias}</span>
                                  <span style={{ 
                                    background: '#fee2e2', 
                                    color: '#dc2626', 
                                    padding: '4px 10px', 
                                    borderRadius: 12,
                                    fontSize: 13,
                                    fontWeight: 600
                                  }}>
                                    Pedir: {p.cantidad}
                                  </span>
                                </div>
                              </div>
                            ))}
                            {sedeData.productos.length === 0 && (
                              <div style={{ textAlign: 'center', padding: 16, color: '#94a3b8', fontSize: 13 }}>
                                Sin productos solicitados
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              
              {productosPorFecha.length === 0 && (
                <div className="card" style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>
                  No hay productos solicitados en el período seleccionado (solo se consideran inventarios cerrados).
                </div>
              )}
            </div>
          )}

          {/* VISTA: PROCESAR PEDIDO */}
          {vistaConsulta === "procesar" && !mostrarResumenPedido && (
            <div className="card">
              <div style={{ 
                background: '#fef3c7', 
                border: '1px solid #f59e0b', 
                borderRadius: 8, 
                padding: '10px 14px', 
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <span style={{ fontSize: 18 }}>ℹ️</span>
                <span style={{ fontSize: 13, color: '#92400e' }}>
                  <strong>Nota:</strong> Solo se muestran productos de inventarios <strong>cerrados</strong> que aún no han sido enviados hoy. Los productos ya incluidos en pedidos anteriores no aparecerán.
                </span>
              </div>
              <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16 }}>
                Selecciona los productos que deseas incluir en el pedido para cada sede.
              </p>
              
              {Object.entries(productosPorSedeDisponibles).map(([sede, productos]) => (
                <div key={sede} style={{ marginBottom: 20 }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '10px 16px',
                    background: '#1e293b',
                    color: 'white',
                    borderRadius: '8px 8px 0 0'
                  }}>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>🏢 {sede}</h4>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => toggleSeleccionSede(sede, true)}
                        style={{ background: '#16a34a', color: 'white', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                      >
                        ✓ Todos
                      </button>
                      <button
                        onClick={() => toggleSeleccionSede(sede, false)}
                        style={{ background: '#64748b', color: 'white', border: 'none', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                      >
                        ✗ Ninguno
                      </button>
                    </div>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9' }}>
                        <th style={{ padding: '8px 12px', textAlign: 'center', width: 50 }}>✓</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Producto</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', width: 100 }}>Existencia</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', width: 100 }}>Pedir</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productos.map((p, idx) => {
                        const key = `${p.sede}::${p.producto}`;
                        const seleccionado = !!productosSeleccionados[key];
                        return (
                          <tr 
                            key={idx} 
                            style={{ background: seleccionado ? '#dcfce7' : (idx % 2 === 0 ? '#fff' : '#f8fafc'), borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}
                            onClick={() => setProductosSeleccionados(prev => ({ ...prev, [key]: !prev[key] }))}
                          >
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              <input type="checkbox" checked={seleccionado} onChange={() => {}} style={{ width: 18, height: 18, cursor: 'pointer' }} />
                            </td>
                            <td style={{ padding: '10px 12px', fontWeight: 500 }}>{p.producto}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'center', color: '#64748b' }}>{p.existenciasUltimo}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                              <span style={{ background: '#fee2e2', color: '#dc2626', padding: '4px 12px', borderRadius: 12, fontWeight: 600 }}>{p.cantidad}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}

              {Object.keys(productosPorSedeDisponibles).length === 0 && (
                <p style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>No hay productos disponibles para enviar. Todos los productos ya fueron incluidos en pedidos anteriores o no hay inventarios cerrados.</p>
              )}

              {Object.keys(productosPorSedeDisponibles).length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16, gap: 8 }}>
                  <span style={{ padding: '10px 16px', background: '#f1f5f9', borderRadius: 8, fontSize: 13 }}>
                    {Object.values(productosSeleccionados).filter(Boolean).length} productos seleccionados
                  </span>
                  <Button onClick={generarPedidoFinal} disabled={Object.values(productosSeleccionados).filter(Boolean).length === 0}>
                    📦 Generar Pedido
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* RESUMEN DE PEDIDO - TARJETAS POR SEDE */}
          {vistaConsulta === "procesar" && mostrarResumenPedido && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                <h3 style={{ fontSize: 18, fontWeight: 600, color: '#16a34a', margin: 0 }}>📦 Pedido Consolidado</h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button variant="outline" onClick={() => setMostrarResumenPedido(false)}>← Volver</Button>
                  <Button variant="outline" onClick={() => {
                    // Imprimir todas las sedes en columnas
                    const win = window.open("", "_blank");
                    if (!win) return;
                    const sedes = Object.entries(pedidosPorSede);
                    const maxProductos = Math.max(...sedes.map(([,p]) => p.length));
                    let html = `<html><head><title>Pedido Consolidado - Todas las Sedes</title>
                      <style>
                        body { font-family: Arial, sans-serif; padding: 20px; }
                        h1 { color: #16a34a; text-align: center; margin-bottom: 5px; }
                        .fecha { text-align: center; color: #666; margin-bottom: 20px; }
                        table { width: 100%; border-collapse: collapse; }
                        th { background: #16a34a; color: white; padding: 12px 8px; font-size: 14px; }
                        td { border: 1px solid #ddd; padding: 10px 8px; vertical-align: top; font-size: 13px; }
                        .producto { font-weight: 500; }
                        .cantidad { color: #16a34a; font-weight: bold; }
                        @media print { body { padding: 10px; } }
                      </style></head><body>
                      <h1>📦 Pedido Consolidado</h1>
                      <p class="fecha">Fecha: ${new Date().toLocaleDateString('es-CO')} - ${new Date().toLocaleTimeString('es-CO')}</p>
                      <table><thead><tr>`;
                    sedes.forEach(([sede]) => { html += `<th>🏢 ${sede}</th>`; });
                    html += '</tr></thead><tbody>';
                    for (let i = 0; i < maxProductos; i++) {
                      html += '<tr>';
                      sedes.forEach(([, productos]) => {
                        const p = productos[i];
                        html += p 
                          ? `<td><span class="producto">${p.producto}</span><br><span class="cantidad">Cantidad: ${p.cantidad}</span></td>`
                          : '<td></td>';
                      });
                      html += '</tr>';
                    }
                    html += '</tbody></table></body></html>';
                    win.document.write(html);
                    win.document.close();
                    win.print();
                  }}>🖨️ Imprimir Todas las Sedes</Button>
                </div>
              </div>

              {/* CAMPO DE NOVEDADES Y INFO DEL GENERADOR */}
              <div style={{ marginBottom: 16, padding: 16, background: '#f0fdf4', borderRadius: 8, border: '1px solid #86efac' }}>
                <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: '#166534', fontWeight: 600 }}>👤 Generado por:</span>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{empleadoActual?.nombre || usuarioActual?.nombre || usuarioActual?.email || 'Usuario'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: '#166534', fontWeight: 600 }}>🕐 Hora:</span>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{new Date().toLocaleString('es-CO')}</span>
                  </div>
                </div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#166534', marginBottom: 6 }}>📝 Novedades del pedido:</label>
                <textarea
                  value={novedadesPedido}
                  onChange={(e) => setNovedadesPedido(e.target.value)}
                  placeholder="Escribe aquí cualquier novedad o instrucción especial para este pedido..."
                  style={{
                    width: '100%',
                    minHeight: 80,
                    padding: 12,
                    fontSize: 14,
                    border: '2px solid #86efac',
                    borderRadius: 8,
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* BOTÓN GUARDAR PEDIDO */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
                <Button variant="outline" onClick={() => setMostrarResumenPedido(false)}>← Volver a selección</Button>
                <Button onClick={async () => {
                  const sedesActuales = Object.keys(pedidosPorSede).sort();
                  const empleadoNombre = empleadoActual?.nombre || usuarioActual?.nombre || usuarioActual?.email || 'Usuario';
                  const hoy = toISO(new Date());
                  
                  try {
                    // Buscar pedido existente del día para unificar
                    const { data: pedidoExistente } = await supabase
                      .from('pedidos_consolidados')
                      .select('*')
                      .gte('fecha', hoy + 'T00:00:00')
                      .lte('fecha', hoy + 'T23:59:59')
                      .limit(1)
                      .single();
                    
                    if (pedidoExistente) {
                      // UNIFICAR: Agregar productos al pedido existente
                      const productosExistentes = pedidoExistente.productos || {};
                      const keysExistentes = pedidoExistente.productos_keys || [];
                      const novedadesExistentes = pedidoExistente.novedades || '';
                      
                      // Identificador único para este envío (agrupar productos por empleado+hora+novedad)
                      const envioId = `${empleadoNombre}_${new Date().toLocaleTimeString('es-CO')}`;
                      
                      // Merge de productos por sede - INCLUIR NOVEDADES EN CADA PRODUCTO
                      Object.entries(pedidosPorSede).forEach(([sede, prods]) => {
                        if (!productosExistentes[sede]) {
                          productosExistentes[sede] = [];
                        }
                        // Agregar novedades_envio y envio_id a cada producto
                        const prodsConNovedad = prods.map(p => ({
                          ...p,
                          novedades_envio: novedadesPedido || '',
                          envio_id: envioId
                        }));
                        productosExistentes[sede] = [...productosExistentes[sede], ...prodsConNovedad];
                      });
                      
                      // Merge de keys
                      const nuevasKeys = [...new Set([...keysExistentes, ...productosKeysSeleccionados])];
                      
                      // Merge de sedes
                      const todasSedes = [...new Set([...(pedidoExistente.sedes || []), ...sedesActuales])].sort();
                      
                      // Concatenar novedades si hay nuevas
                      let novedadesFinales = novedadesExistentes;
                      if (novedadesPedido) {
                        novedadesFinales = novedadesExistentes 
                          ? `${novedadesExistentes}\n---\n[${empleadoNombre} - ${new Date().toLocaleTimeString('es-CO')}]: ${novedadesPedido}`
                          : `[${empleadoNombre} - ${new Date().toLocaleTimeString('es-CO')}]: ${novedadesPedido}`;
                      }
                      
                      // Actualizar pedido existente
                      const { error } = await supabase
                        .from('pedidos_consolidados')
                        .update({
                          sedes: todasSedes,
                          productos: productosExistentes,
                          productos_keys: nuevasKeys,
                          total_productos: Object.values(productosExistentes).flat().length,
                          novedades: novedadesFinales || null
                        })
                        .eq('id', pedidoExistente.id);
                      
                      if (error) throw error;
                    } else {
                      // CREAR NUEVO: No hay pedido del día
                      // Identificador único para este envío
                      const envioId = `${empleadoNombre}_${new Date().toLocaleTimeString('es-CO')}`;
                      
                      // Agregar novedades_envio y envio_id a cada producto
                      const productosConNovedad = {};
                      Object.entries(pedidosPorSede).forEach(([sede, prods]) => {
                        productosConNovedad[sede] = prods.map(p => ({
                          ...p,
                          novedades_envio: novedadesPedido || '',
                          envio_id: envioId
                        }));
                      });
                      
                      const pedidoData = {
                        id: crypto.randomUUID(),
                        fecha: new Date().toISOString(),
                        sedes: sedesActuales,
                        productos: productosConNovedad,
                        estado: 'pendiente',
                        total_productos: Object.values(productosConNovedad).flat().length,
                        productos_keys: productosKeysSeleccionados || [],
                        novedades: novedadesPedido ? `[${empleadoNombre} - ${new Date().toLocaleTimeString('es-CO')}]: ${novedadesPedido}` : null,
                        generado_por_nombre: empleadoNombre,
                        hora_generacion: new Date().toLocaleTimeString('es-CO')
                      };
                      
                      const { error } = await supabase.from('pedidos_consolidados').insert([pedidoData]);
                      if (error) throw error;
                    }
                    
                    // Limpiar selección de productos que se guardaron
                    const nuevaSeleccion = { ...productosSeleccionados };
                    productosKeysSeleccionados.forEach(key => {
                      delete nuevaSeleccion[key];
                    });
                    setProductosSeleccionados(nuevaSeleccion);
                    
                    // Actualizar la lista de productos ya enviados
                    setProductosYaEnviados(prev => new Set([...prev, ...productosKeysSeleccionados]));
                    
                    // Limpiar y volver
                    setMostrarResumenPedido(false);
                    setNovedadesPedido("");
                    setPedidosPorSede({});
                    setProductosKeysSeleccionados([]);
                    
                    alert('✅ Pedido guardado correctamente. Los productos seleccionados ya no aparecerán para envíos posteriores.');
                  } catch (err) {
                    console.error('Error guardando pedido:', err);
                    alert('Error al guardar el pedido: ' + err.message);
                  }
                }}>💾 Guardar Pedido</Button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: 16 }}>
                {Object.entries(pedidosPorSede).map(([sede, productos]) => (
                  <div key={sede} className="card" style={{ border: '3px solid #16a34a', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 12px rgba(22, 163, 74, 0.2)' }}>
                    <div style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', color: 'white', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>🏢 {sede}</h4>
                        <span style={{ fontSize: 12, opacity: 0.9 }}>Pedido generado</span>
                      </div>
                      <div style={{ background: 'rgba(255,255,255,0.2)', padding: '8px 14px', borderRadius: 20, fontSize: 14, fontWeight: 600 }}>{productos.length} items</div>
                    </div>
                    <div style={{ padding: 16 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #16a34a' }}>
                            <th style={{ padding: '8px 0', textAlign: 'left', fontWeight: 600 }}>Producto</th>
                            <th style={{ padding: '8px 0', textAlign: 'center', fontWeight: 600, width: 100 }}>Cantidad</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productos.map((p, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                              <td style={{ padding: '12px 0', fontWeight: 500 }}>{p.producto}</td>
                              <td style={{ padding: '12px 0', textAlign: 'center' }}>
                                <span style={{ background: '#dcfce7', color: '#16a34a', padding: '6px 14px', borderRadius: 20, fontWeight: 700, fontSize: 16 }}>{p.cantidad}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ padding: '12px 16px', background: '#f0fdf4', display: 'flex', justifyContent: 'center', gap: 8 }}>
                      <Button variant="outline" onClick={() => {
                        const texto = productos.map(p => `• ${p.producto}: ${p.cantidad}`).join('\n');
                        navigator.clipboard.writeText(`📦 PEDIDO ${sede}\n${'─'.repeat(20)}\n${texto}`);
                        alert('Pedido copiado al portapapeles');
                      }}>📋 Copiar</Button>
                      <Button onClick={() => {
                        const win = window.open("", "_blank");
                        if (!win) return;
                        win.document.write(`<html><head><title>Pedido ${sede}</title><style>body{font-family:Arial;padding:20px}h1{color:#16a34a}table{width:100%;border-collapse:collapse;margin-top:20px}th,td{border:1px solid #ddd;padding:12px}th{background:#16a34a;color:white}</style></head><body><h1>📦 Pedido - ${sede}</h1><p>Fecha: ${new Date().toLocaleDateString('es-CO')}</p><table><thead><tr><th>Producto</th><th>Cantidad</th></tr></thead><tbody>${productos.map(p => `<tr><td>${p.producto}</td><td style="text-align:center;font-weight:bold">${p.cantidad}</td></tr>`).join('')}</tbody></table></body></html>`);
                        win.document.close();
                        win.print();
                      }}>🖨️ Imprimir</Button>
                    </div>
                  </div>
                ))}
              </div>

              {Object.keys(pedidosPorSede).length === 0 && (
                <div className="card" style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>No se seleccionaron productos para el pedido.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Vista de Pedidos Guardados */}
      {vistaConsulta === "pedidos_guardados" && (
        <div className="card" style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#7c3aed', margin: 0 }}>📋 Pedidos Guardados</h3>
            <button
              onClick={cargarPedidosGuardados}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: '1px solid #7c3aed',
                background: '#fff',
                color: '#7c3aed',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              🔄 Actualizar
            </button>
          </div>

          {cargandoPedidos ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
              Cargando pedidos guardados...
            </div>
          ) : pedidosGuardados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
              <p style={{ margin: 0, fontSize: 15 }}>No hay pedidos guardados aún</p>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#94a3b8' }}>Los pedidos consolidados aparecerán aquí cuando los guardes</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {pedidosGuardados.map((pedido, idx) => (
                <div
                  key={pedido.id || idx}
                  className="card"
                  style={{
                    padding: 16,
                    background: '#faf5ff',
                    border: '1px solid #e9d5ff',
                    borderRadius: 12
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: '#7c3aed', marginBottom: 4 }}>
                        Pedido #{idx + 1} - {new Date(pedido.fecha).toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {new Date(pedido.fecha).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 600,
                      background: pedido.estado === 'procesado' ? '#dcfce7' : '#fef3c7',
                      color: pedido.estado === 'procesado' ? '#16a34a' : '#d97706'
                    }}>
                      {pedido.estado === 'procesado' ? '✓ Procesado' : '⏳ Pendiente'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
                    <div style={{ background: '#fff', padding: '8px 14px', borderRadius: 8, border: '1px solid #e9d5ff' }}>
                      <span style={{ fontSize: 12, color: '#64748b' }}>Sedes: </span>
                      <span style={{ fontWeight: 600, color: '#374151' }}>{pedido.sedes?.join(', ') || 'N/A'}</span>
                    </div>
                    <div style={{ background: '#fff', padding: '8px 14px', borderRadius: 8, border: '1px solid #e9d5ff' }}>
                      <span style={{ fontSize: 12, color: '#64748b' }}>Total productos: </span>
                      <span style={{ fontWeight: 600, color: '#374151' }}>{pedido.total_productos || 0}</span>
                    </div>
                  </div>

                  {/* MOSTRAR NOVEDADES SI EXISTEN */}
                  {pedido.novedades && (
                    <div style={{ background: '#fef3c7', padding: 12, borderRadius: 8, marginBottom: 12, border: '1px solid #fcd34d' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>📝 Novedades:</div>
                      <div style={{ fontSize: 13, color: '#78350f', whiteSpace: 'pre-wrap' }}>{pedido.novedades}</div>
                    </div>
                  )}

                  {/* MOSTRAR GENERADO POR SI EXISTE */}
                  {pedido.generado_por_nombre && (
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                      <span>👤 Iniciado por: <strong>{pedido.generado_por_nombre}</strong></span>
                      {pedido.hora_generacion && <span> a las {pedido.hora_generacion}</span>}
                    </div>
                  )}

                  {/* MOSTRAR PRODUCTOS AGRUPADOS POR ENVÍO (EMPLEADO + HORA) */}
                  <div style={{ marginBottom: 12 }}>
                    {Object.entries(pedido.productos || {}).map(([sede, prods]) => {
                      // Agrupar productos por envio_id o procesado_por + hora_procesado
                      const gruposPorEnvio = {};
                      (prods || []).forEach(p => {
                        const envioKey = p.envio_id || `${p.procesado_por || 'N/A'}_${p.hora_procesado || ''}`;
                        if (!gruposPorEnvio[envioKey]) {
                          gruposPorEnvio[envioKey] = {
                            empleado: p.procesado_por || 'N/A',
                            hora: p.hora_procesado || '',
                            novedades: p.novedades_envio || '',
                            productos: []
                          };
                        }
                        gruposPorEnvio[envioKey].productos.push(p);
                      });
                      
                      return (
                        <div key={sede} style={{ marginBottom: 12, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#16a34a', padding: '8px 12px', background: '#dcfce7' }}>🏢 {sede}</div>
                          {Object.entries(gruposPorEnvio).map(([envioKey, grupo], idx) => (
                            <div key={envioKey} style={{ padding: '8px 12px', borderTop: idx > 0 ? '1px dashed #e2e8f0' : 'none', background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                              {/* Encabezado del envío - Empleado y Hora (UNA SOLA VEZ) */}
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#6366f1', marginBottom: 4 }}>
                                👤 {grupo.empleado} ({grupo.hora})
                              </div>
                              {/* Novedades del envío (si las hay) */}
                              {grupo.novedades && (
                                <div style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', padding: '4px 8px', borderRadius: 4, marginBottom: 4 }}>
                                  📝 {grupo.novedades}
                                </div>
                              )}
                              {/* Lista de productos (sin repetir empleado) */}
                              <div style={{ fontSize: 12, color: '#374151', paddingLeft: 8 }}>
                                {grupo.productos.slice(0, 5).map((p, i) => (
                                  <div key={i}>• {p.producto} <span style={{ fontWeight: 600 }}>({p.cantidad})</span></div>
                                ))}
                                {grupo.productos.length > 5 && <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>...y {grupo.productos.length - 5} más</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => {
                        const productosData = pedido.productos || {};
                        const sedesPedido = pedido.sedes || [];
                        const win = window.open("", "_blank");
                        if (!win) return;
                        
                        // Generar tablas por sede AGRUPADAS POR ENVÍO (empleado + hora + novedades)
                        let tablasPorSede = '';
                        if (typeof productosData === 'object' && !Array.isArray(productosData)) {
                          Object.entries(productosData).forEach(([sede, prods]) => {
                            if (Array.isArray(prods) && prods.length > 0) {
                              // Agrupar productos por envio_id o procesado_por + hora_procesado
                              const gruposPorEnvio = {};
                              prods.forEach(p => {
                                const envioKey = p.envio_id || (p.procesado_por || 'N/A') + '_' + (p.hora_procesado || '');
                                if (!gruposPorEnvio[envioKey]) {
                                  gruposPorEnvio[envioKey] = {
                                    empleado: p.procesado_por || 'N/A',
                                    hora: p.hora_procesado || '',
                                    novedades: p.novedades_envio || '',
                                    productos: []
                                  };
                                }
                                gruposPorEnvio[envioKey].productos.push(p);
                              });
                              
                              tablasPorSede += '<div style="margin-bottom:25px;">';
                              tablasPorSede += '<h3 style="color:#16a34a;margin:0 0 10px 0;padding:8px 12px;background:#dcfce7;border-radius:6px;">🏢 ' + sede + '</h3>';
                              
                              // Iterar por cada grupo de envío
                              Object.entries(gruposPorEnvio).forEach(([envioKey, grupo]) => {
                                // Encabezado del envío (Empleado + Hora - UNA SOLA VEZ)
                                tablasPorSede += '<div style="background:#f0f9ff;padding:8px 12px;border:1px solid #bae6fd;border-radius:6px 6px 0 0;margin-top:10px;">';
                                tablasPorSede += '<strong style="color:#0369a1;">👤 ' + grupo.empleado + ' (' + grupo.hora + ')</strong>';
                                // Novedades del envío (si las hay)
                                if (grupo.novedades) {
                                  tablasPorSede += '<div style="margin-top:4px;padding:6px 8px;background:#fef3c7;border-radius:4px;font-size:12px;color:#92400e;">📝 Novedades: ' + grupo.novedades + '</div>';
                                }
                                tablasPorSede += '</div>';
                                
                                // Tabla de productos (solo Producto y Cantidad)
                                tablasPorSede += '<table style="border-radius:0 0 6px 6px;overflow:hidden;">';
                                tablasPorSede += '<thead><tr><th>Producto</th><th style="text-align:center;width:100px">Cantidad</th></tr></thead>';
                                tablasPorSede += '<tbody>';
                                grupo.productos.forEach(p => {
                                  tablasPorSede += '<tr><td>' + p.producto + '</td><td style="text-align:center;font-weight:bold">' + p.cantidad + '</td></tr>';
                                });
                                tablasPorSede += '</tbody></table>';
                              });
                              
                              tablasPorSede += '</div>';
                            }
                          });
                        }
                        
                        // Agregar novedades al HTML
                        const novedadesHTML = pedido.novedades ? `
                          <div style="background:#fef3c7;padding:12px;border-radius:8px;margin-bottom:20px;border:1px solid #fcd34d">
                            <strong style="color:#92400e">📝 Novedades:</strong><br>
                            <span style="white-space:pre-wrap">${pedido.novedades}</span>
                          </div>
                        ` : '';
                        
                        let html = `<html><head><title>Pedido Guardado</title>
                          <style>
                            body{font-family:Arial,sans-serif;padding:20px;max-width:900px;margin:0 auto}
                            h1{color:#7c3aed;border-bottom:2px solid #7c3aed;padding-bottom:10px}
                            .info{background:#f3e8ff;padding:12px;border-radius:8px;margin-bottom:20px}
                            table{width:100%;border-collapse:collapse;margin-top:10px}
                            th{background:#7c3aed;color:white;padding:10px;text-align:left}
                            td{border:1px solid #ddd;padding:10px}
                            tr:nth-child(even){background:#f9fafb}
                          </style>
                        </head><body>
                        <h1>📋 Pedido Guardado</h1>
                        <div class="info">
                          <strong>Fecha:</strong> ${new Date(pedido.fecha).toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} - ${new Date(pedido.fecha).toLocaleTimeString('es-CO')}<br>
                          <strong>Sedes:</strong> ${sedesPedido.join(', ')}<br>
                          <strong>Estado:</strong> ${pedido.estado === 'procesado' ? 'Procesado' : 'Pendiente'}
                        </div>
                        ${novedadesHTML}
                        ${tablasPorSede}
                        </body></html>`;
                        
                        win.document.write(html);
                        win.document.close();
                        win.print();
                      }}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 6,
                        border: '1px solid #7c3aed',
                        background: '#fff',
                        color: '#7c3aed',
                        fontWeight: 500,
                        fontSize: 13,
                        cursor: 'pointer'
                      }}
                    >
                      🖨️ Imprimir
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm('¿Marcar este pedido como procesado?')) return;
                        const { error } = await supabase
                          .from('pedidos_consolidados')
                          .update({ estado: 'procesado' })
                          .eq('id', pedido.id);
                        if (error) {
                          alert('Error al actualizar: ' + error.message);
                        } else {
                          cargarPedidosGuardados();
                        }
                      }}
                      disabled={pedido.estado === 'procesado'}
                      style={{
                        padding: '8px 14px',
                        borderRadius: 6,
                        border: 'none',
                        background: pedido.estado === 'procesado' ? '#e2e8f0' : '#16a34a',
                        color: pedido.estado === 'procesado' ? '#94a3b8' : '#fff',
                        fontWeight: 500,
                        fontSize: 13,
                        cursor: pedido.estado === 'procesado' ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ✓ Marcar procesado
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ====================== INVENTARIO INTEGRAL ======================

// Guardar inventario integral en Supabase
async function guardarInventarioIntegralEnSupabase(inventario, empresaId = null) {
  try {
    const payload = {
      id: inventario.id || crypto.randomUUID(),
      fecha: inventario.fecha,
      sede_id: inventario.sedeId,
      sede_nombre: inventario.sedeNombre,
      empleado_id: inventario.empleadoId,
      empleado_nombre: inventario.empleadoNombre,
      lineas: inventario.lineas || [], // JSONB - enviar como array directo, no string
      estado: inventario.estado || 'borrador', // borrador | enviado | gestionado
      novedades: inventario.novedades || null,
      gestionado_por_id: inventario.gestionadoPorId || null,
      gestionado_por_nombre: inventario.gestionadoPorNombre || null,
      fecha_gestion: inventario.fechaGestion || null,
      empresa_id: empresaId,
      created_at: inventario.createdAt || new Date().toISOString()
    };
    
    console.log("📦 Payload inventario integral:", payload);
    
    const { data, error } = await supabase
      .from('inventarios_integrales')
      .upsert([payload], { onConflict: 'id' })
      .select();
    
    if (error) {
      console.error("❌ Error guardando inventario integral:", error);
      console.error("❌ Detalle del error:", error.message, error.details, error.hint);
      throw error;
    }
    
    console.log("✅ Inventario integral guardado:", data);
    return data[0];
  } catch (e) {
    console.error("❌ Error en guardarInventarioIntegralEnSupabase:", e);
    throw e;
  }
}

// Cargar inventarios integrales desde Supabase
async function cargarInventariosIntegralesDeSupabase(empresaId = null, empleadoId = null, verTodos = false) {
  try {
    let query = supabase
      .from('inventarios_integrales')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (empresaId) {
      query = query.eq('empresa_id', empresaId);
    }
    
    // Si no puede ver todos, solo mostrar los propios
    if (!verTodos && empleadoId) {
      query = query.eq('empleado_id', empleadoId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("❌ Error cargando inventarios integrales:", error);
      return [];
    }
    
    return (data || []).map(row => ({
      id: row.id,
      fecha: row.fecha,
      sedeId: row.sede_id,
      sedeNombre: row.sede_nombre,
      empleadoId: row.empleado_id,
      empleadoNombre: row.empleado_nombre,
      lineas: typeof row.lineas === 'string' ? JSON.parse(row.lineas) : (row.lineas || []),
      estado: row.estado,
      novedades: row.novedades,
      gestionadoPorId: row.gestionado_por_id,
      gestionadoPorNombre: row.gestionado_por_nombre,
      fechaGestion: row.fecha_gestion,
      empresaId: row.empresa_id,
      createdAt: row.created_at
    }));
  } catch (e) {
    console.error("❌ Error en cargarInventariosIntegralesDeSupabase:", e);
    return [];
  }
}

// Componente principal de Inventario Integral
export function InventarioIntegralRoot({ 
  sedes, 
  empleados, 
  tienePermiso, 
  filtroEmpresa, 
  usuarioActual, 
  empleadoActual,
  productosBase = []
}) {
  const [vista, setVista] = useState("lista"); // "lista" | "crear" | "ver" | "gestionar"
  const [inventarios, setInventarios] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [inventarioSeleccionado, setInventarioSeleccionado] = useState(null);
  const [guardando, setGuardando] = useState(false);
  
  // Estado para crear nuevo inventario
  const [nuevoInventario, setNuevoInventario] = useState({
    fecha: toISO(new Date()),
    sedeId: '',
    sedeNombre: '',
    lineas: productosBase.map(nombre => ({
      id: crypto.randomUUID(),
      producto: nombre,
      existencia: '',
      productoSolicitado: ''
    })),
    novedades: ''
  });
  
  // Filtros
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [filtroSede, setFiltroSede] = useState("");
  
  // Cargar inventarios al montar
  useEffect(() => {
    cargarInventarios();
  }, []);
  
  async function cargarInventarios() {
    setCargando(true);
    try {
      const puedeVerTodos = tienePermiso("admin_inventario_integral_ver_todos") || tienePermiso("admin_inventario_integral_gestionar");
      const data = await cargarInventariosIntegralesDeSupabase(
        filtroEmpresa, 
        empleadoActual?.id, 
        puedeVerTodos
      );
      setInventarios(data);
    } catch (e) {
      console.error("Error cargando inventarios:", e);
    }
    setCargando(false);
  }
  
  // Guardar nuevo inventario
  async function handleGuardarNuevo(estado = 'borrador') {
    if (!nuevoInventario.sedeId) {
      alert("Debes seleccionar una sede");
      return;
    }
    
    // Validar que al menos un producto tenga existencia
    const tieneExistencias = nuevoInventario.lineas.some(l => l.existencia && l.existencia.trim());
    if (!tieneExistencias && estado === 'enviado') {
      alert("Debes ingresar la existencia de al menos un producto antes de enviar");
      return;
    }
    
    setGuardando(true);
    try {
      const inventarioData = {
        id: crypto.randomUUID(),
        fecha: nuevoInventario.fecha,
        sedeId: nuevoInventario.sedeId,
        sedeNombre: nuevoInventario.sedeNombre,
        empleadoId: empleadoActual?.id || usuarioActual?.id,
        empleadoNombre: empleadoActual?.nombre || usuarioActual?.nombre || usuarioActual?.email,
        lineas: nuevoInventario.lineas,
        estado: estado,
        novedades: nuevoInventario.novedades,
        createdAt: new Date().toISOString()
      };
      
      await guardarInventarioIntegralEnSupabase(inventarioData, filtroEmpresa);
      
      // Notificar si se envió
      if (estado === 'enviado') {
        alert("✅ Inventario integral enviado correctamente. Ahora está disponible para gestión.");
      } else {
        alert("✅ Inventario guardado como borrador.");
      }
      
      // Resetear y volver a lista
      setNuevoInventario({
        fecha: toISO(new Date()),
        sedeId: '',
        sedeNombre: '',
        lineas: productosBase.map(nombre => ({
          id: crypto.randomUUID(),
          producto: nombre,
          existencia: '',
          productoSolicitado: ''
        })),
        novedades: ''
      });
      await cargarInventarios();
      setVista("lista");
    } catch (e) {
      console.error("Error guardando:", e);
      alert("Error al guardar: " + e.message);
    }
    setGuardando(false);
  }
  
  // Gestionar inventario (agregar producto solicitado)
  async function handleGestionar(inventario, nuevasLineas) {
    setGuardando(true);
    try {
      const inventarioActualizado = {
        ...inventario,
        lineas: nuevasLineas,
        estado: 'gestionado',
        gestionadoPorId: empleadoActual?.id || usuarioActual?.id,
        gestionadoPorNombre: empleadoActual?.nombre || usuarioActual?.nombre || usuarioActual?.email,
        fechaGestion: new Date().toISOString()
      };
      
      await guardarInventarioIntegralEnSupabase(inventarioActualizado, filtroEmpresa);
      alert("✅ Inventario gestionado correctamente.");
      await cargarInventarios();
      setVista("lista");
      setInventarioSeleccionado(null);
    } catch (e) {
      console.error("Error gestionando:", e);
      alert("Error al gestionar: " + e.message);
    }
    setGuardando(false);
  }
  
  // Filtrar inventarios
  const inventariosFiltrados = inventarios.filter(inv => {
    if (filtroEstado !== "todos" && inv.estado !== filtroEstado) return false;
    if (filtroSede && inv.sedeId !== filtroSede) return false;
    return true;
  });
  
  // Colores por estado
  const getEstadoStyle = (estado) => {
    switch(estado) {
      case 'borrador': return { bg: '#f3f4f6', color: '#6b7280', label: '📝 Borrador' };
      case 'enviado': return { bg: '#fef3c7', color: '#d97706', label: '📤 Enviado' };
      case 'gestionado': return { bg: '#dcfce7', color: '#16a34a', label: '✅ Gestionado' };
      default: return { bg: '#f3f4f6', color: '#6b7280', label: estado };
    }
  };

  return (
    <div style={{ padding: 0 }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)', 
        borderRadius: 12, 
        padding: 20, 
        marginBottom: 20,
        color: 'white'
      }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 28 }}>📊</span>
          Inventario Integral
        </h2>
        <p style={{ margin: '8px 0 0', fontSize: 13, opacity: 0.9 }}>
          Conteo físico completo con flujo de gestión y solicitud de productos
        </p>
      </div>
      
      {/* Tabs de navegación */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {(tienePermiso("admin_inventario_integral_ver") || tienePermiso("admin_inventario_integral_ver_todos")) && (
          <button
            onClick={() => { setVista("lista"); setInventarioSeleccionado(null); }}
            style={{
              padding: '10px 18px',
              borderRadius: 10,
              border: 'none',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              background: vista === "lista" ? 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)' : '#f3f4f6',
              color: vista === "lista" ? 'white' : '#374151'
            }}
          >
            📋 Ver Inventarios
          </button>
        )}
        {tienePermiso("admin_inventario_integral_crear") && (
          <button
            onClick={() => setVista("crear")}
            style={{
              padding: '10px 18px',
              borderRadius: 10,
              border: 'none',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              background: vista === "crear" ? 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)' : '#f3f4f6',
              color: vista === "crear" ? 'white' : '#374151'
            }}
          >
            ➕ Crear Inventario
          </button>
        )}
      </div>
      
      {/* VISTA: LISTA DE INVENTARIOS */}
      {vista === "lista" && (
        <div>
          {/* Filtros */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Estado</label>
              <select 
                value={filtroEstado} 
                onChange={(e) => setFiltroEstado(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
              >
                <option value="todos">Todos</option>
                <option value="borrador">📝 Borrador</option>
                <option value="enviado">📤 Enviado</option>
                <option value="gestionado">✅ Gestionado</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Sede</label>
              <select 
                value={filtroSede} 
                onChange={(e) => setFiltroSede(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}
              >
                <option value="">Todas las sedes</option>
                {sedes.map(s => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
            </div>
            <button
              onClick={cargarInventarios}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: '1px solid #7c3aed',
                background: 'white',
                color: '#7c3aed',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer'
              }}
            >
              🔄 Actualizar
            </button>
          </div>
          
          {/* Lista de inventarios */}
          {cargando ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
              Cargando inventarios...
            </div>
          ) : inventariosFiltrados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b', background: '#faf5ff', borderRadius: 12, border: '2px dashed #e9d5ff' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
              <p style={{ margin: 0, fontSize: 15 }}>No hay inventarios integrales</p>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#a78bfa' }}>
                {tienePermiso("admin_inventario_integral_crear") ? "Crea uno nuevo usando el botón 'Crear Inventario'" : "No tienes inventarios registrados"}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {inventariosFiltrados.map((inv) => {
                const estadoStyle = getEstadoStyle(inv.estado);
                const puedeGestionar = tienePermiso("admin_inventario_integral_gestionar") && inv.estado === 'enviado';
                const puedeEditar = tienePermiso("admin_inventario_integral_editar") || (inv.empleadoId === empleadoActual?.id && inv.estado === 'borrador');
                
                return (
                  <div
                    key={inv.id}
                    style={{
                      background: 'white',
                      borderRadius: 12,
                      padding: 16,
                      border: '2px solid #e9d5ff',
                      boxShadow: '0 2px 8px rgba(124, 58, 237, 0.1)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>
                          🏢 {inv.sedeNombre}
                        </div>
                        <div style={{ fontSize: 13, color: '#64748b' }}>
                          📅 {new Date(inv.fecha + 'T12:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        </div>
                      </div>
                      <span style={{
                        padding: '6px 14px',
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 600,
                        background: estadoStyle.bg,
                        color: estadoStyle.color
                      }}>
                        {estadoStyle.label}
                      </span>
                    </div>
                    
                    <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap', fontSize: 13 }}>
                      <div>
                        <span style={{ color: '#64748b' }}>Creado por: </span>
                        <span style={{ fontWeight: 600 }}>{inv.empleadoNombre}</span>
                      </div>
                      <div>
                        <span style={{ color: '#64748b' }}>Productos: </span>
                        <span style={{ fontWeight: 600 }}>{inv.lineas?.filter(l => l.existencia).length || 0}/{inv.lineas?.length || 0}</span>
                      </div>
                      {inv.gestionadoPorNombre && (
                        <div>
                          <span style={{ color: '#64748b' }}>Gestionado por: </span>
                          <span style={{ fontWeight: 600, color: '#16a34a' }}>{inv.gestionadoPorNombre}</span>
                        </div>
                      )}
                    </div>
                    
                    {inv.novedades && (
                      <div style={{ background: '#fef3c7', padding: 10, borderRadius: 8, marginBottom: 12, fontSize: 12 }}>
                        <span style={{ fontWeight: 600, color: '#92400e' }}>📝 Novedades: </span>
                        <span style={{ color: '#78350f' }}>{inv.novedades}</span>
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        onClick={() => { setInventarioSeleccionado(inv); setVista("ver"); }}
                        style={{
                          padding: '8px 14px',
                          borderRadius: 8,
                          border: '1px solid #7c3aed',
                          background: 'white',
                          color: '#7c3aed',
                          fontWeight: 600,
                          fontSize: 12,
                          cursor: 'pointer'
                        }}
                      >
                        👁️ Ver Detalle
                      </button>
                      {puedeGestionar && (
                        <button
                          onClick={() => { setInventarioSeleccionado(inv); setVista("gestionar"); }}
                          style={{
                            padding: '8px 14px',
                            borderRadius: 8,
                            border: 'none',
                            background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: 12,
                            cursor: 'pointer'
                          }}
                        >
                          ✅ Gestionar
                        </button>
                      )}
                      {puedeEditar && inv.estado === 'borrador' && (
                        <button
                          onClick={() => { 
                            setNuevoInventario({
                              ...inv,
                              sedeId: inv.sedeId,
                              sedeNombre: inv.sedeNombre
                            });
                            setInventarioSeleccionado(inv);
                            setVista("crear"); 
                          }}
                          style={{
                            padding: '8px 14px',
                            borderRadius: 8,
                            border: '1px solid #d97706',
                            background: 'white',
                            color: '#d97706',
                            fontWeight: 600,
                            fontSize: 12,
                            cursor: 'pointer'
                          }}
                        >
                          ✏️ Continuar
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      
      {/* VISTA: CREAR INVENTARIO */}
      {vista === "crear" && tienePermiso("admin_inventario_integral_crear") && (
        <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '2px solid #e9d5ff' }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#7c3aed' }}>
            ➕ {inventarioSeleccionado ? 'Editar' : 'Nuevo'} Inventario Integral
          </h3>
          
          {/* Cabecera del inventario */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>📅 Fecha</label>
              <input
                type="date"
                value={nuevoInventario.fecha}
                onChange={(e) => setNuevoInventario(prev => ({ ...prev, fecha: e.target.value }))}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '2px solid #e9d5ff', fontSize: 14 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>🏢 Sede *</label>
              <select
                value={nuevoInventario.sedeId}
                onChange={(e) => {
                  const sede = sedes.find(s => s.id === e.target.value);
                  setNuevoInventario(prev => ({ 
                    ...prev, 
                    sedeId: e.target.value,
                    sedeNombre: sede?.nombre || ''
                  }));
                }}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '2px solid #e9d5ff', fontSize: 14 }}
              >
                <option value="">Selecciona una sede</option>
                {sedes.map(s => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>👤 Diligenciado por</label>
              <input
                type="text"
                value={empleadoActual?.nombre || usuarioActual?.nombre || usuarioActual?.email || 'Usuario'}
                disabled
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '2px solid #e9d5ff', fontSize: 14, background: '#f8fafc', color: '#64748b' }}
              />
            </div>
          </div>
          
          {/* Tabla de productos - DOS COLUMNAS */}
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#374151' }}>
              📦 Productos ({nuevoInventario.lineas.filter(l => l.existencia).length}/{nuevoInventario.lineas.length} diligenciados)
            </h4>
            <div style={{ overflowX: 'auto', border: '2px solid #e9d5ff', borderRadius: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' }}>
                    <th style={{ padding: 10, textAlign: 'center', fontWeight: 700, borderBottom: '2px solid #e9d5ff', width: 35 }}>#</th>
                    <th style={{ padding: 10, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #e9d5ff' }}>Producto</th>
                    <th style={{ padding: 10, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #e9d5ff', minWidth: 150 }}>Existencia *</th>
                    <th style={{ padding: 10, textAlign: 'center', fontWeight: 700, borderBottom: '2px solid #e9d5ff', width: 35, background: '#ede9fe', borderLeft: '2px solid #e9d5ff' }}>#</th>
                    <th style={{ padding: 10, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #e9d5ff', background: '#ede9fe' }}>Producto</th>
                    <th style={{ padding: 10, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #e9d5ff', minWidth: 150, background: '#ede9fe' }}>Existencia *</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Dividir productos en dos mitades para mostrar en dos columnas
                    const mitad = Math.ceil(nuevoInventario.lineas.length / 2);
                    const filas = [];
                    for (let i = 0; i < mitad; i++) {
                      const lineaIzq = nuevoInventario.lineas[i];
                      const lineaDer = nuevoInventario.lineas[i + mitad];
                      const idxIzq = i;
                      const idxDer = i + mitad;
                      
                      filas.push(
                        <tr key={lineaIzq.id} style={{ background: i % 2 === 0 ? 'white' : '#faf5ff', borderBottom: '1px solid #f3e8ff' }}>
                          {/* Columna izquierda */}
                          <td style={{ padding: '8px 10px', textAlign: 'center', color: '#64748b', fontWeight: 500, fontSize: 11 }}>{idxIzq + 1}</td>
                          <td style={{ padding: '8px 10px', fontWeight: 600, fontSize: 12 }}>{lineaIzq.producto}</td>
                          <td style={{ padding: '6px 8px' }}>
                            <input
                              type="text"
                              value={lineaIzq.existencia}
                              onChange={(e) => {
                                const nuevasLineas = [...nuevoInventario.lineas];
                                nuevasLineas[idxIzq].existencia = e.target.value;
                                setNuevoInventario(prev => ({ ...prev, lineas: nuevasLineas }));
                              }}
                              placeholder="Ej: 2 cajas"
                              style={{ 
                                width: '100%', 
                                padding: '6px 10px', 
                                borderRadius: 6, 
                                border: `2px solid ${lineaIzq.existencia ? '#a78bfa' : '#e9d5ff'}`,
                                fontSize: 12,
                                background: lineaIzq.existencia ? '#f5f3ff' : 'white'
                              }}
                            />
                          </td>
                          {/* Columna derecha */}
                          {lineaDer ? (
                            <>
                              <td style={{ padding: '8px 10px', textAlign: 'center', color: '#64748b', fontWeight: 500, fontSize: 11, borderLeft: '2px solid #e9d5ff', background: i % 2 === 0 ? '#faf5ff' : '#f5f3ff' }}>{idxDer + 1}</td>
                              <td style={{ padding: '8px 10px', fontWeight: 600, fontSize: 12, background: i % 2 === 0 ? '#faf5ff' : '#f5f3ff' }}>{lineaDer.producto}</td>
                              <td style={{ padding: '6px 8px', background: i % 2 === 0 ? '#faf5ff' : '#f5f3ff' }}>
                                <input
                                  type="text"
                                  value={lineaDer.existencia}
                                  onChange={(e) => {
                                    const nuevasLineas = [...nuevoInventario.lineas];
                                    nuevasLineas[idxDer].existencia = e.target.value;
                                    setNuevoInventario(prev => ({ ...prev, lineas: nuevasLineas }));
                                  }}
                                  placeholder="Ej: 2 cajas"
                                  style={{ 
                                    width: '100%', 
                                    padding: '6px 10px', 
                                    borderRadius: 6, 
                                    border: `2px solid ${lineaDer.existencia ? '#a78bfa' : '#e9d5ff'}`,
                                    fontSize: 12,
                                    background: lineaDer.existencia ? '#f5f3ff' : 'white'
                                  }}
                                />
                              </td>
                            </>
                          ) : (
                            <>
                              <td style={{ padding: '8px 10px', borderLeft: '2px solid #e9d5ff', background: '#f8fafc' }}></td>
                              <td style={{ padding: '8px 10px', background: '#f8fafc' }}></td>
                              <td style={{ padding: '6px 8px', background: '#f8fafc' }}></td>
                            </>
                          )}
                        </tr>
                      );
                    }
                    return filas;
                  })()}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Novedades */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>📝 Novedades (opcional)</label>
            <textarea
              value={nuevoInventario.novedades}
              onChange={(e) => setNuevoInventario(prev => ({ ...prev, novedades: e.target.value }))}
              placeholder="Observaciones, productos dañados, faltantes importantes, etc."
              rows={3}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '2px solid #e9d5ff', fontSize: 13, resize: 'vertical' }}
            />
          </div>
          
          {/* Botones de acción */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button
              onClick={() => { setVista("lista"); setInventarioSeleccionado(null); }}
              style={{
                padding: '12px 24px',
                borderRadius: 10,
                border: '2px solid #e2e8f0',
                background: 'white',
                color: '#64748b',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={() => handleGuardarNuevo('borrador')}
              disabled={guardando}
              style={{
                padding: '12px 24px',
                borderRadius: 10,
                border: '2px solid #d97706',
                background: 'white',
                color: '#d97706',
                fontWeight: 600,
                fontSize: 14,
                cursor: guardando ? 'not-allowed' : 'pointer',
                opacity: guardando ? 0.7 : 1
              }}
            >
              {guardando ? '⏳ Guardando...' : '💾 Guardar Borrador'}
            </button>
            <button
              onClick={() => handleGuardarNuevo('enviado')}
              disabled={guardando}
              style={{
                padding: '12px 24px',
                borderRadius: 10,
                border: 'none',
                background: guardando ? '#94a3b8' : 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                color: 'white',
                fontWeight: 600,
                fontSize: 14,
                cursor: guardando ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)'
              }}
            >
              {guardando ? '⏳ Enviando...' : '📤 Guardar y Enviar'}
            </button>
          </div>
        </div>
      )}
      
      {/* VISTA: VER DETALLE */}
      {vista === "ver" && inventarioSeleccionado && (
        <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '2px solid #e9d5ff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#7c3aed' }}>
                📊 Detalle Inventario Integral
              </h3>
              <div style={{ fontSize: 14, color: '#64748b' }}>
                🏢 {inventarioSeleccionado.sedeNombre} • 📅 {new Date(inventarioSeleccionado.fecha + 'T12:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            </div>
            <span style={{
              padding: '6px 14px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              background: getEstadoStyle(inventarioSeleccionado.estado).bg,
              color: getEstadoStyle(inventarioSeleccionado.estado).color
            }}>
              {getEstadoStyle(inventarioSeleccionado.estado).label}
            </span>
          </div>
          
          {/* Info del creador y gestor */}
          <div style={{ display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap', fontSize: 13 }}>
            <div style={{ background: '#f5f3ff', padding: 12, borderRadius: 8 }}>
              <span style={{ color: '#64748b' }}>Creado por: </span>
              <span style={{ fontWeight: 600, color: '#7c3aed' }}>{inventarioSeleccionado.empleadoNombre}</span>
            </div>
            {inventarioSeleccionado.gestionadoPorNombre && (
              <div style={{ background: '#f0fdf4', padding: 12, borderRadius: 8 }}>
                <span style={{ color: '#64748b' }}>Gestionado por: </span>
                <span style={{ fontWeight: 600, color: '#16a34a' }}>{inventarioSeleccionado.gestionadoPorNombre}</span>
                <span style={{ color: '#64748b', marginLeft: 8, fontSize: 11 }}>
                  ({new Date(inventarioSeleccionado.fechaGestion).toLocaleString('es-CO')})
                </span>
              </div>
            )}
          </div>
          
          {inventarioSeleccionado.novedades && (
            <div style={{ background: '#fef3c7', padding: 12, borderRadius: 8, marginBottom: 16 }}>
              <span style={{ fontWeight: 600, color: '#92400e' }}>📝 Novedades: </span>
              <span style={{ color: '#78350f' }}>{inventarioSeleccionado.novedades}</span>
            </div>
          )}
          
          {/* Tabla de productos - DOS COLUMNAS */}
          <div style={{ overflowX: 'auto', border: '2px solid #e9d5ff', borderRadius: 10, marginBottom: 20 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' }}>
                  <th style={{ padding: 10, textAlign: 'center', fontWeight: 700, borderBottom: '2px solid #e9d5ff', width: 30 }}>#</th>
                  <th style={{ padding: 10, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #e9d5ff' }}>Producto</th>
                  <th style={{ padding: 10, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #e9d5ff' }}>Existencia</th>
                  {inventarioSeleccionado.estado === 'gestionado' && (
                    <th style={{ padding: 10, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #16a34a', background: '#bbf7d0' }}>Solicitado</th>
                  )}
                  <th style={{ padding: 10, textAlign: 'center', fontWeight: 700, borderBottom: '2px solid #e9d5ff', width: 30, borderLeft: '2px solid #e9d5ff', background: '#ede9fe' }}>#</th>
                  <th style={{ padding: 10, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #e9d5ff', background: '#ede9fe' }}>Producto</th>
                  <th style={{ padding: 10, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #e9d5ff', background: '#ede9fe' }}>Existencia</th>
                  {inventarioSeleccionado.estado === 'gestionado' && (
                    <th style={{ padding: 10, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #16a34a', background: '#bbf7d0' }}>Solicitado</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const mitad = Math.ceil(inventarioSeleccionado.lineas.length / 2);
                  const filas = [];
                  for (let i = 0; i < mitad; i++) {
                    const lineaIzq = inventarioSeleccionado.lineas[i];
                    const lineaDer = inventarioSeleccionado.lineas[i + mitad];
                    
                    filas.push(
                      <tr key={lineaIzq.id || i} style={{ background: i % 2 === 0 ? 'white' : '#faf5ff', borderBottom: '1px solid #f3e8ff' }}>
                        {/* Columna izquierda */}
                        <td style={{ padding: '8px 10px', textAlign: 'center', color: '#64748b', fontSize: 11 }}>{i + 1}</td>
                        <td style={{ padding: '8px 10px', fontWeight: 600, fontSize: 12 }}>{lineaIzq.producto}</td>
                        <td style={{ padding: '8px 10px', color: lineaIzq.existencia ? '#374151' : '#94a3b8', fontSize: 12 }}>
                          {lineaIzq.existencia || '—'}
                        </td>
                        {inventarioSeleccionado.estado === 'gestionado' && (
                          <td style={{ padding: '8px 10px', color: lineaIzq.productoSolicitado ? '#16a34a' : '#94a3b8', fontWeight: lineaIzq.productoSolicitado ? 600 : 400, fontSize: 12, background: i % 2 === 0 ? '#dcfce7' : '#bbf7d0' }}>
                            {lineaIzq.productoSolicitado || '—'}
                          </td>
                        )}
                        {/* Columna derecha */}
                        {lineaDer ? (
                          <>
                            <td style={{ padding: '8px 10px', textAlign: 'center', color: '#64748b', fontSize: 11, borderLeft: '2px solid #e9d5ff', background: i % 2 === 0 ? '#faf5ff' : '#f5f3ff' }}>{i + mitad + 1}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 600, fontSize: 12, background: i % 2 === 0 ? '#faf5ff' : '#f5f3ff' }}>{lineaDer.producto}</td>
                            <td style={{ padding: '8px 10px', color: lineaDer.existencia ? '#374151' : '#94a3b8', fontSize: 12, background: i % 2 === 0 ? '#faf5ff' : '#f5f3ff' }}>
                              {lineaDer.existencia || '—'}
                            </td>
                            {inventarioSeleccionado.estado === 'gestionado' && (
                              <td style={{ padding: '8px 10px', color: lineaDer.productoSolicitado ? '#16a34a' : '#94a3b8', fontWeight: lineaDer.productoSolicitado ? 600 : 400, fontSize: 12, background: i % 2 === 0 ? '#dcfce7' : '#bbf7d0' }}>
                                {lineaDer.productoSolicitado || '—'}
                              </td>
                            )}
                          </>
                        ) : (
                          <>
                            <td style={{ padding: '8px 10px', borderLeft: '2px solid #e9d5ff', background: '#f8fafc' }}></td>
                            <td style={{ padding: '8px 10px', background: '#f8fafc' }}></td>
                            <td style={{ padding: '8px 10px', background: '#f8fafc' }}></td>
                            {inventarioSeleccionado.estado === 'gestionado' && (
                              <td style={{ padding: '8px 10px', background: '#dcfce7' }}></td>
                            )}
                          </>
                        )}
                      </tr>
                    );
                  }
                  return filas;
                })()}
              </tbody>
            </table>
          </div>
          
          <button
            onClick={() => { setVista("lista"); setInventarioSeleccionado(null); }}
            style={{
              padding: '12px 24px',
              borderRadius: 10,
              border: '2px solid #7c3aed',
              background: 'white',
              color: '#7c3aed',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer'
            }}
          >
            ← Volver a la lista
          </button>
        </div>
      )}
      
      {/* VISTA: GESTIONAR */}
      {vista === "gestionar" && inventarioSeleccionado && tienePermiso("admin_inventario_integral_gestionar") && (
        <GestionarInventarioIntegral
          inventario={inventarioSeleccionado}
          onGuardar={handleGestionar}
          onCancelar={() => { setVista("lista"); setInventarioSeleccionado(null); }}
          guardando={guardando}
        />
      )}
    </div>
  );
}

// Componente para gestionar inventario
function GestionarInventarioIntegral({ inventario, onGuardar, onCancelar, guardando }) {
  const [lineas, setLineas] = useState(inventario.lineas.map(l => ({
    ...l,
    productoSolicitado: l.productoSolicitado || ''
  })));
  
  return (
    <div style={{ background: 'white', borderRadius: 12, padding: 20, border: '2px solid #16a34a' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#16a34a' }}>
            ✅ Gestionar Inventario Integral
          </h3>
          <div style={{ fontSize: 14, color: '#64748b' }}>
            🏢 {inventario.sedeNombre} • 📅 {new Date(inventario.fecha + 'T12:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
            Creado por: <strong>{inventario.empleadoNombre}</strong>
          </div>
        </div>
      </div>
      
      {inventario.novedades && (
        <div style={{ background: '#fef3c7', padding: 12, borderRadius: 8, marginBottom: 16 }}>
          <span style={{ fontWeight: 600, color: '#92400e' }}>📝 Novedades del empleado: </span>
          <span style={{ color: '#78350f' }}>{inventario.novedades}</span>
        </div>
      )}
      
      <div style={{ background: '#f0fdf4', padding: 12, borderRadius: 8, marginBottom: 16, border: '1px solid #86efac' }}>
        <span style={{ fontSize: 13, color: '#166534' }}>
          💡 <strong>Indicación:</strong> Revisa las existencias y completa la columna "Producto Solicitado" para indicar cuánto se debe enviar.
        </span>
      </div>
      
      {/* Tabla de productos con columna editable - DOS COLUMNAS */}
      <div style={{ overflowX: 'auto', border: '2px solid #16a34a', borderRadius: 10, marginBottom: 20 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' }}>
              <th style={{ padding: 8, textAlign: 'center', fontWeight: 700, borderBottom: '2px solid #16a34a', width: 30 }}>#</th>
              <th style={{ padding: 8, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #16a34a' }}>Producto</th>
              <th style={{ padding: 8, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #16a34a' }}>Existencia</th>
              <th style={{ padding: 8, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #16a34a', background: '#bbf7d0', minWidth: 120 }}>Solicitado *</th>
              <th style={{ padding: 8, textAlign: 'center', fontWeight: 700, borderBottom: '2px solid #16a34a', width: 30, borderLeft: '2px solid #16a34a', background: '#dcfce7' }}>#</th>
              <th style={{ padding: 8, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #16a34a', background: '#dcfce7' }}>Producto</th>
              <th style={{ padding: 8, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #16a34a', background: '#dcfce7' }}>Existencia</th>
              <th style={{ padding: 8, textAlign: 'left', fontWeight: 700, borderBottom: '2px solid #16a34a', background: '#86efac', minWidth: 120 }}>Solicitado *</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const mitad = Math.ceil(lineas.length / 2);
              const filas = [];
              for (let i = 0; i < mitad; i++) {
                const lineaIzq = lineas[i];
                const lineaDer = lineas[i + mitad];
                const idxIzq = i;
                const idxDer = i + mitad;
                
                filas.push(
                  <tr key={lineaIzq.id || i} style={{ background: i % 2 === 0 ? 'white' : '#f0fdf4', borderBottom: '1px solid #dcfce7' }}>
                    {/* Columna izquierda */}
                    <td style={{ padding: '6px 8px', textAlign: 'center', color: '#64748b', fontSize: 10 }}>{idxIzq + 1}</td>
                    <td style={{ padding: '6px 8px', fontWeight: 600, fontSize: 11 }}>{lineaIzq.producto}</td>
                    <td style={{ padding: '6px 8px', color: lineaIzq.existencia ? '#374151' : '#94a3b8', fontSize: 11 }}>
                      {lineaIzq.existencia || '—'}
                    </td>
                    <td style={{ padding: '4px 6px' }}>
                      <input
                        type="text"
                        value={lineaIzq.productoSolicitado}
                        onChange={(e) => {
                          const nuevasLineas = [...lineas];
                          nuevasLineas[idxIzq].productoSolicitado = e.target.value;
                          setLineas(nuevasLineas);
                        }}
                        placeholder="Ej: 5 cajas"
                        style={{ 
                          width: '100%', 
                          padding: '5px 8px', 
                          borderRadius: 5, 
                          border: `2px solid ${lineaIzq.productoSolicitado ? '#16a34a' : '#86efac'}`,
                          fontSize: 11,
                          background: lineaIzq.productoSolicitado ? '#f0fdf4' : 'white'
                        }}
                      />
                    </td>
                    {/* Columna derecha */}
                    {lineaDer ? (
                      <>
                        <td style={{ padding: '6px 8px', textAlign: 'center', color: '#64748b', fontSize: 10, borderLeft: '2px solid #16a34a', background: i % 2 === 0 ? '#f0fdf4' : '#dcfce7' }}>{idxDer + 1}</td>
                        <td style={{ padding: '6px 8px', fontWeight: 600, fontSize: 11, background: i % 2 === 0 ? '#f0fdf4' : '#dcfce7' }}>{lineaDer.producto}</td>
                        <td style={{ padding: '6px 8px', color: lineaDer.existencia ? '#374151' : '#94a3b8', fontSize: 11, background: i % 2 === 0 ? '#f0fdf4' : '#dcfce7' }}>
                          {lineaDer.existencia || '—'}
                        </td>
                        <td style={{ padding: '4px 6px', background: i % 2 === 0 ? '#f0fdf4' : '#dcfce7' }}>
                          <input
                            type="text"
                            value={lineaDer.productoSolicitado}
                            onChange={(e) => {
                              const nuevasLineas = [...lineas];
                              nuevasLineas[idxDer].productoSolicitado = e.target.value;
                              setLineas(nuevasLineas);
                            }}
                            placeholder="Ej: 5 cajas"
                            style={{ 
                              width: '100%', 
                              padding: '5px 8px', 
                              borderRadius: 5, 
                              border: `2px solid ${lineaDer.productoSolicitado ? '#16a34a' : '#86efac'}`,
                              fontSize: 11,
                              background: lineaDer.productoSolicitado ? '#f0fdf4' : 'white'
                            }}
                          />
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '6px 8px', borderLeft: '2px solid #16a34a', background: '#f8fafc' }}></td>
                        <td style={{ padding: '6px 8px', background: '#f8fafc' }}></td>
                        <td style={{ padding: '6px 8px', background: '#f8fafc' }}></td>
                        <td style={{ padding: '4px 6px', background: '#f8fafc' }}></td>
                      </>
                    )}
                  </tr>
                );
              }
              return filas;
            })()}
          </tbody>
        </table>
      </div>
      
      {/* Botones de acción */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button
          onClick={onCancelar}
          style={{
            padding: '12px 24px',
            borderRadius: 10,
            border: '2px solid #e2e8f0',
            background: 'white',
            color: '#64748b',
            fontWeight: 600,
            fontSize: 14,
            cursor: 'pointer'
          }}
        >
          Cancelar
        </button>
        <button
          onClick={() => onGuardar(inventario, lineas)}
          disabled={guardando}
          style={{
            padding: '12px 24px',
            borderRadius: 10,
            border: 'none',
            background: guardando ? '#94a3b8' : 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
            color: 'white',
            fontWeight: 600,
            fontSize: 14,
            cursor: guardando ? 'not-allowed' : 'pointer',
            boxShadow: '0 4px 12px rgba(22, 163, 74, 0.3)'
          }}
        >
          {guardando ? '⏳ Guardando...' : '✅ Finalizar Gestión'}
        </button>
      </div>
    </div>
  );
}


/* ====================== CREAR / EDITAR CUADRE ====================== */
 
// Denominaciones de billetes/monedas en COP usadas en el cuadre diario
const DENOMINACIONES = [
 
    100000,
    50000,
    20000,
    10000,
    5000,
    2000,
    1000,
    500,
    200,
    100,
    50,
  ];
  
  function CrearCuadre({
    sedes,
    empleados,
    cuadreActivo,
    setCuadreActivoId,
    crearOBuscarBorrador,
    actualizarCuadre,
    actualizarCuadreParcial,
    cuadres,
    guardarFinal,
    guardarComentario,
    realizarPrecierre,
    usuarioActual = null,
    empleadoActual = null,
    esGerente = false,
    tienePermiso = () => true,
    accesosDelCuadre = [],
    usuariosActivosEnCuadre = [],
    ultimaActualizacionRemota = null,
    filtroEmpresa = null,
    bloquearFila = () => {},
    desbloquearFila = () => {},
    filaEstaBloqueadaPorOtro = () => null,
  }) {
    const hoyISO = toISO(new Date());
  
    // Obtener el ID del empleado actual basado en el usuario autenticado
    const empleadoActualId = empleadoActual?.id || usuarioActual?.id || "";

    // Selección inicial
    const [sedeSel, setSedeSel] = useState("");
    // El empleado se asigna automáticamente al usuario autenticado
    const [empleadoSel, setEmpleadoSel] = useState(empleadoActualId);
    const [valorInicial, setValorInicial] = useState("");
    const [fechaSel, setFechaSel] = useState(hoyISO);
  
    const [totales, setTotales] = useState({});
    const [comentario, setComentario] = useState("");
    
    // Estados para el modal de precierre
    const [mostrarModalPrecierre, setMostrarModalPrecierre] = useState(false);
    const [precierreAsignadoA, setPrecierreAsignadoA] = useState("");
    const [precierreObservaciones, setPrecierreObservaciones] = useState("");
    
    // Estado para el modal de confirmación de precierre recibido
    const [mostrarAlertaConfirmarPrecierre, setMostrarAlertaConfirmarPrecierre] = useState(false);
    
    // 🔹 Estado para el efectivo que ingresa el receptor al confirmar precierre
    const [efectivoReceptor, setEfectivoReceptor] = useState("");
    const [efectivoReceptorConfirmado, setEfectivoReceptorConfirmado] = useState(false);
    
    // Estado para reasignar cuadre (solo Gerencia)
    const [reasignarACuadre, setReasignarACuadre] = useState("");
    
    // Estado para mostrar alertas visuales en la parte superior
    const [alertaCuadre, setAlertaCuadre] = useState({ visible: false, tipo: '', mensaje: '' });
    
    // Función para mostrar alerta visual (se mantiene hasta que el usuario la cierre)
    const mostrarAlertaVisual = (tipo, mensaje) => {
      setAlertaCuadre({ visible: true, tipo, mensaje });
    };

    // Actualizar empleadoSel cuando cambie el empleadoActual
    useEffect(() => {
      if (!cuadreActivo && empleadoActualId) {
        setEmpleadoSel(empleadoActualId);
      }
    }, [empleadoActualId, cuadreActivo]);
  
    // Sincronizar cuando cambio de cuadreActivo (retomar / consultar)
    useEffect(() => {
      if (cuadreActivo) {
        setSedeSel(cuadreActivo.sede || "");
        // Si es gerente, mantener el empleado del cuadre; si no, usar el actual
        setEmpleadoSel(cuadreActivo.empleadoId || empleadoActualId || "");
        setValorInicial(
          cuadreActivo.valorInicial !== undefined &&
          cuadreActivo.valorInicial !== null
            ? String(cuadreActivo.valorInicial)
            : ""
        );
        setFechaSel(cuadreActivo.fecha || hoyISO);
        setTotales(cuadreActivo.totales || {});
        setComentario(cuadreActivo.comentarioModificacion || "");
        
        // Verificar si hay un precierre pendiente de confirmación
        // y si el empleado actual es el receptor del precierre
        if (cuadreActivo.precierrePendienteConfirmacion && 
            cuadreActivo.precierre?.asignadoAId && 
            (cuadreActivo.precierre.asignadoAId === empleadoActualId || 
             cuadreActivo.precierre.asignadoAId === (empleadoActual?.id || usuarioActual?.id))) {
          setMostrarAlertaConfirmarPrecierre(true);
        }
      } else {
        // No hay cuadre activo → limpiar a valores por defecto
        setSedeSel("");
        setEmpleadoSel(empleadoActualId || "");
        setValorInicial("");
        setFechaSel(hoyISO);
        setTotales({});
        setComentario("");
      }
    }, [cuadreActivo, hoyISO, empleadoActualId]);
  
    function iniciarCuadre() {
      if (!sedeSel || !empleadoSel || !valorInicial || !fechaSel) {
        alert("Sede, empleado, fecha y valor inicial son obligatorios");
        return;
      }
  
      // ====================== RESTRICCIÓN: Solo un cuadre en proceso por sede ======================
      // Buscar si hay algún cuadre EN PROCESO (no cerrado) para esta sede, sin importar la fecha
      const cuadreEnProceso = cuadres.find(
        (c) => c.sede === sedeSel && c.estado !== "final"
      );
  
      if (cuadreEnProceso && cuadreEnProceso.fecha !== fechaSel) {
        alert(
          `⚠️ No puedes abrir un nuevo cuadre para la sede "${sedeSel}".

Ya existe un cuadre EN PROCESO del día ${cuadreEnProceso.fecha}.

Debes cerrar primero ese cuadre antes de abrir uno nuevo.`
        );
        return;
      }
      // ====================== FIN RESTRICCIÓN ======================
  
      // Buscar si ya existe un cuadre para esa sede + fecha
      const existente = cuadres.find(
        (c) => c.sede === sedeSel && c.fecha === fechaSel
      );
  
      if (existente && existente.estado === "final") {
        alert(
          `Ya existe un cuadre CERRADO para la sede "${sedeSel}" en la fecha ${fechaSel}. No es posible abrir otro.`
        );
        setCuadreActivoId(existente.id);
        return;
      }
  
      // ====================== FUNCIÓN HELPER: Obtener facturas pendientes de días anteriores ======================
      // Esta función busca facturas pendientes de días anteriores que NO han sido pagadas
      // FILTRO: Solo considera cuadres a partir del 7 de febrero de 2026
      // IDENTIFICACIÓN: Por ID único (origenId), permitiendo múltiples facturas con el mismo nombre
      const FECHA_MINIMA_FACTURAS = "2026-02-07";
      
      const obtenerFacturasPendientesAnteriores = (sede, fecha, facturasActuales = []) => {
        const pendientes = [];
        
        // 🔍 DIAGNÓSTICO: Ver todos los cuadres de esta sede
        const cuadresDeSede = cuadres.filter(c => c.sede === sede);
        console.log(`🔍 [DIAGNÓSTICO] Sede: ${sede}, Fecha actual: ${fecha}`);
        console.log(`🔍 [DIAGNÓSTICO] Total cuadres cargados: ${cuadres.length}`);
        console.log(`🔍 [DIAGNÓSTICO] Cuadres de esta sede: ${cuadresDeSede.length}`);
        cuadresDeSede.forEach(c => {
          console.log(`   📅 Cuadre ${c.fecha} - Estado: ${c.estado} - Facturas: ${(c.facturas || []).length}`);
          (c.facturas || []).forEach(f => {
            console.log(`      💰 ID: ${f.id} - OrigenId: ${f.origenId || 'N/A'} - Factura: ${f.factura || '(vacía)'} - Estado: ${f.estado}`);
          });
        });
        
        // 1. Recolectar todas las facturas que ya fueron pagadas en cualquier cuadre (por origenId Y por id)
        const facturasPagadasPorId = new Set();
        cuadres.forEach((c) => {
          if (c.sede === sede && c.fecha >= FECHA_MINIMA_FACTURAS && Array.isArray(c.facturas)) {
            c.facturas.forEach((f) => {
              const estadoLower = (f.estado || "").toLowerCase();
              if (estadoLower.includes("pagada") || estadoLower.includes("caja") || estadoLower.includes("transferencia")) {
                // Agregar AMBOS IDs para cubrir todos los casos de rastreo
                if (f.id) {
                  facturasPagadasPorId.add(String(f.id));
                }
                if (f.origenId) {
                  facturasPagadasPorId.add(String(f.origenId));
                }
              }
            });
          }
        });
        
        // 2. Recolectar IDs de facturas que YA están en el cuadre actual (para evitar duplicados)
        const idsYaEnCuadre = new Set();
        (facturasActuales || []).forEach((f) => {
          // Agregar tanto el origenId como el id para cubrir todos los casos
          if (f.origenId) {
            idsYaEnCuadre.add(String(f.origenId));
          }
          if (f.id) {
            idsYaEnCuadre.add(String(f.id));
          }
        });
        
        console.log("📋 IDs de facturas ya pagadas:", Array.from(facturasPagadasPorId));
        console.log("📋 IDs ya en cuadre actual:", Array.from(idsYaEnCuadre));
        
        // 3. Buscar facturas pendientes de días anteriores que NO estén pagadas NI ya en el cuadre (por ID único)
        cuadres.forEach((c) => {
          if (c.sede === sede && c.fecha < fecha && c.fecha >= FECHA_MINIMA_FACTURAS && Array.isArray(c.facturas)) {
            c.facturas.forEach((f) => {
              const estadoLower = (f.estado || "").toLowerCase();
              if (estadoLower === "pendiente" || estadoLower.includes("pendiente")) {
                // El ID único es el origenId si existe, sino el id de la factura
                const idUnico = f.origenId || f.id;
                
                // Solo agregar si:
                // - La factura tiene ID
                // - NO ha sido pagada en ningún cuadre (por ID)
                // - NO está ya en el cuadre actual (por ID)
                if (idUnico && !facturasPagadasPorId.has(String(idUnico)) && !idsYaEnCuadre.has(String(idUnico))) {
                  // IMPORTANTE: Preservar el origenId original si ya existe (factura que pasó por múltiples cuadres)
                  // Si no tiene origenId, usar su id como origenId
                  const origenIdReal = f.origenId || f.id;
                  pendientes.push({
                    id: uid(),
                    origenId: origenIdReal, // SIEMPRE el ID de la factura original
                    factura: f.factura,
                    valor: f.valor,
                    estado: "Pendiente",
                    origenFecha: f.origenFecha || c.fecha,
                  });
                  console.log(`⏳ Factura pendiente cargada: ${f.factura} (origenId: ${origenIdReal}) - Origen: ${f.origenFecha || c.fecha}`);
                } else if (idUnico && facturasPagadasPorId.has(String(idUnico))) {
                  console.log(`✅ Factura ${f.factura} (ID: ${idUnico}) ya fue pagada - NO se carga como pendiente`);
                } else if (idUnico && idsYaEnCuadre.has(String(idUnico))) {
                  console.log(`🔄 Factura ${f.factura} (ID: ${idUnico}) ya está en el cuadre actual - NO se duplica`);
                }
              }
            });
          }
        });
        
        return pendientes;
      };
      // ====================== FIN FUNCIÓN HELPER ======================
      
      if (existente && existente.estado !== "final") {
        // RETOMAR CUADRE EXISTENTE - pero verificar si hay nuevas facturas pendientes que agregar
        console.log("🔄 Retomando cuadre existente, verificando facturas pendientes faltantes...");
        
        const pendientesFaltantes = obtenerFacturasPendientesAnteriores(
          sedeSel, 
          fechaSel, 
          existente.facturas || []
        );
        
        if (pendientesFaltantes.length > 0) {
          console.log(`📥 Agregando ${pendientesFaltantes.length} facturas pendientes faltantes al cuadre existente`);
          
          // Agregar las facturas pendientes faltantes al inicio del array de facturas
          const facturasActualizadas = [...pendientesFaltantes, ...(existente.facturas || [])];
          
          // Actualizar el cuadre con las nuevas facturas
          actualizarCuadre(existente.id, {
            facturas: facturasActualizadas,
          });
          
          alert(
            `Ya existe un cuadre EN PROCESO para la sede "${sedeSel}" en la fecha ${fechaSel}. ` +
            `Se retomará y se agregaron ${pendientesFaltantes.length} factura(s) pendiente(s) de días anteriores.`
          );
        } else {
          alert(
            `Ya existe un cuadre EN PROCESO para la sede "${sedeSel}" en la fecha ${fechaSel}. Se retomará ese mismo.`
          );
        }
        
        setCuadreActivoId(existente.id);
        return;
      }
  
      // No existe ninguno → crear nuevo
      const base = crearOBuscarBorrador({ sede: sedeSel, fecha: fechaSel });
  
      actualizarCuadre(base.id, {
        empleadoId: empleadoSel,
        valorInicial: Number(valorInicial || 0),
      });
  
      // Cargar facturas pendientes de días anteriores de la misma sede
      const pendientes = obtenerFacturasPendientesAnteriores(sedeSel, fechaSel, base.facturas);
  
      let facturasInicial = base.facturas;
      if (!facturasInicial || !facturasInicial.length) {
        facturasInicial = [
          { id: uid(), factura: "", valor: "", estado: "Pagada de caja", origenFecha: fechaSel },
        ];
      }
      
      // Evitar duplicados por ID: solo agregar pendientes cuyos origenId no estén ya en facturasInicial
      const idsExistentes = new Set();
      facturasInicial.forEach(f => {
        if (f.id) idsExistentes.add(String(f.id));
        if (f.origenId) idsExistentes.add(String(f.origenId));
      });
      
      const pendientesSinDuplicar = pendientes.filter(p => {
        const idUnico = p.origenId || p.id;
        return idUnico && !idsExistentes.has(String(idUnico));
      });
      
      facturasInicial = [...pendientesSinDuplicar, ...facturasInicial];
  
      const domiciliosInicial =
        base.domicilios && base.domicilios.length
          ? base.domicilios
              .filter(d => d && d.id) // Solo conservar items con ID válido
              .map(d => ({
                ...d,
                pago_confirmado: d.pago_confirmado || false,
              }))
          : []; // NO crear filas vacías automáticamente - el usuario las agregará
  
      const transferInicial =
        base.transfer && base.transfer.length
          ? base.transfer
              .filter(t => t && t.id) // Solo conservar items con ID válido
              .map(t => ({
                ...t,
                bancolombia_confirmado: t.bancolombia_confirmado || false,
                davivienda_confirmado: t.davivienda_confirmado || false,
                daviplata_confirmado: t.daviplata_confirmado || false,
                nequi_confirmado: t.nequi_confirmado || false,
              }))
          : []; // NO crear filas vacías automáticamente
  
      const denomsBase =
        base.denoms && base.denoms.length
          ? base.denoms
          : DENOMINACIONES.map((v) => ({
              valor: v,
              cantidad: 0,
              subtotal: 0,
            }));
  
      actualizarCuadre(base.id, {
        facturas: facturasInicial,
        domicilios: domiciliosInicial,
        transfer: transferInicial,
        denoms: denomsBase,
      });
  
      setCuadreActivoId(base.id);
    }
  
    const bloqueado = cuadreActivo?.estado === "final";
    const empleado = cuadreActivo
      ? empleados.find((e) => e.id === cuadreActivo.empleadoId)
      : null;

    // Calcular totales de transferencias (debe estar antes de cualquier return)
    const totalesTransfer = useMemo(() => {
      const transfer = cuadreActivo?.transfer || [];
      let bancolombia = 0, davivienda = 0, daviplata = 0, nequi = 0;
      transfer.forEach((t) => {
        bancolombia += Number(t.bancolombia || 0);
        davivienda += Number(t.davivienda || 0);
        daviplata += Number(t.daviplata || 0);
        nequi += Number(t.nequi || 0);
      });
      return { bancolombia, davivienda, daviplata, nequi };
    }, [cuadreActivo?.transfer]);

    // Calcular total de datafonos (debe estar antes de cualquier return)
    const totalDatafonosCuadre = useMemo(() => {
      const datafonos = cuadreActivo?.datafonos || [];
      return datafonos.reduce((sum, d) => sum + Number(d.valor || 0), 0);
    }, [cuadreActivo?.datafonos]);

    // 🔹 Calcular total facturas pagadas de caja
    const totalFacturasCaja = useMemo(() => {
      const facturas = cuadreActivo?.facturas || [];
      return facturas
        .filter(f => f.estado === "Pagada de caja")
        .reduce((sum, f) => sum + Number(f.valor || 0), 0);
    }, [cuadreActivo?.facturas]);

    // 🔹 Calcular total facturas pendientes (quedan para el siguiente cuadre)
    const totalFacturasPendientes = useMemo(() => {
      const facturas = cuadreActivo?.facturas || [];
      return facturas
        .filter(f => f.estado === "Pendiente")
        .reduce((sum, f) => sum + Number(f.valor || 0), 0);
    }, [cuadreActivo?.facturas]);

    // 🔹 Calcular total domicilios (valor + cobro datafono)
    const totalDomiciliosCuadre = useMemo(() => {
      const domicilios = cuadreActivo?.domicilios || [];
      return domicilios.reduce((sum, d) => sum + Number(d.valor || 0) + Number(d.cobroDatafono || 0), 0);
    }, [cuadreActivo?.domicilios]);

    // 🔹 Calcular total ingresos varios
    const totalIngresosVariosCuadre = useMemo(() => {
      const ingresosVarios = cuadreActivo?.ingresosVarios || [];
      return ingresosVarios.reduce((sum, ing) => sum + Number(ing.valor || 0), 0);
    }, [cuadreActivo?.ingresosVarios]);

    // 🔹 Calcular total turnos
    const totalTurnosCuadre = useMemo(() => {
      const turnos = cuadreActivo?.turnos || [];
      return turnos.reduce((sum, t) => sum + Number(t.valor || 0), 0);
    }, [cuadreActivo?.turnos]);

    // 🔹 Calcular total adelantos
    const totalAdelantosCuadre = useMemo(() => {
      const adelantos = cuadreActivo?.adelantos || [];
      return adelantos.reduce((sum, a) => sum + Number(a.valor || 0), 0);
    }, [cuadreActivo?.adelantos]);

    // 🔹 Calcular sobrante en efectivo automático
    // Fórmula: Venta Efectivo + Total Ingresos Varios - Facturas de Caja - Total Domicilio - Adelantos - Total Turnos
    const sobranteEfectivoCalculado = useMemo(() => {
      const ventaEfectivo = Number(cuadreActivo?.ventas?.ventaEfectivo || 0);
      return ventaEfectivo + totalIngresosVariosCuadre - totalFacturasCaja - totalDomiciliosCuadre - totalAdelantosCuadre - totalTurnosCuadre;
    }, [cuadreActivo?.ventas?.ventaEfectivo, totalFacturasCaja, totalDomiciliosCuadre, totalIngresosVariosCuadre, totalAdelantosCuadre, totalTurnosCuadre]);

    // 🔹 Actualizar automáticamente el sobrante en las ventas cuando cambie el cálculo
    useEffect(() => {
      if (cuadreActivo && cuadreActivo.estado !== "final") {
        const sobranteActual = Number(cuadreActivo?.ventas?.sobrante || 0);
        // Solo actualizar si es diferente para evitar bucle infinito
        if (sobranteActual !== sobranteEfectivoCalculado) {
          const ventasActualizadas = { ...(cuadreActivo.ventas || {}), sobrante: sobranteEfectivoCalculado };
          actualizarCuadre(cuadreActivo.id, { ventas: ventasActualizadas });
        }
      }
    }, [sobranteEfectivoCalculado]);
  
    // Vista inicial: sin cuadre activo → formulario de "Abrir cuadre"
    if (!cuadreActivo) {
      return (
        <div className="card">
          <h2>Abrir cuadre diario</h2>
          <div className="grid-3">
            <Field label="Sede">
              <select
                value={sedeSel}
                onChange={(e) => setSedeSel(e.target.value)}
                className="input"
              >
                <option value="">-- Selecciona --</option>
                {sedes.map((s) => {
                  const sedeNombre = typeof s === 'string' ? s : (s?.nombre || s?.razonSocial || '');
                  return (
                    <option key={sedeNombre} value={sedeNombre}>
                      {sedeNombre}
                    </option>
                  );
                })}
              </select>
            </Field>
  
            <Field label="Empleado responsable">
              <select
                value={empleadoSel}
                onChange={(e) => setEmpleadoSel(e.target.value)}
                className="input"
                disabled={!esGerente}
                style={!esGerente ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
              >
                <option value="">-- Selecciona --</option>
                {[...(empleados || [])].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nombre}
                  </option>
                ))}
              </select>
              {!esGerente && <span style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Asignado automáticamente</span>}
            </Field>
  
            <Field label="Fecha del cuadre">
              <input
                className="input"
                type="date"
                value={fechaSel}
                onChange={(e) => setFechaSel(e.target.value)}
              />
            </Field>
  
            <Field label="Valor inicial de caja (COP)">
              <input
                className="input"
                type="number"
                value={valorInicial}
                onChange={(e) => setValorInicial(e.target.value)}
              />
            </Field>
          </div>
  
          {tienePermiso("admin_cuadre_abrir") && (
          <Button onClick={iniciarCuadre} style={{ marginTop: 12 }}>
            Abrir cuadre
          </Button>
          )}
        </div>
      );
    }
    
    // Verificar si el cuadre tiene precierre pendiente de confirmación
    // y si el usuario actual NO es el receptor asignado
    const usuarioActualId = empleadoActual?.id || usuarioActual?.id;
    const esReceptorPrecierre = cuadreActivo?.precierre?.asignadoAId === usuarioActualId;
    const precierrePendiente = cuadreActivo?.precierrePendienteConfirmacion;
    
    // Si hay precierre pendiente y el usuario NO es el receptor NI es gerencia, mostrar bloqueo
    if (precierrePendiente && !esReceptorPrecierre && !esGerente) {
      const empleadoAsignado = empleados.find(e => e.id === cuadreActivo?.precierre?.asignadoAId);
      return (
        <div className="card">
          <div style={{
            textAlign: 'center',
            padding: '40px 20px'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🔒</div>
            <h2 style={{ 
              margin: '0 0 16px 0', 
              color: '#dc2626',
              fontSize: '24px'
            }}>
              Cuadre en proceso de cambio de turno
            </h2>
            <div style={{
              padding: '20px',
              backgroundColor: '#fef2f2',
              border: '2px solid #f87171',
              borderRadius: '12px',
              marginBottom: '20px',
              maxWidth: '500px',
              margin: '0 auto 20px auto'
            }}>
              <p style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#991b1b' }}>
                Este cuadre está esperando que <strong>{empleadoAsignado?.nombre || cuadreActivo?.precierre?.asignadoANombre || "el empleado asignado"}</strong> confirme el precierre del turno anterior.
              </p>
              <p style={{ margin: '0', fontSize: '13px', color: '#666' }}>
                Mientras no se confirme la recepción del cuadre, no es posible editarlo.
              </p>
            </div>
            <div style={{
              padding: '16px',
              backgroundColor: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '10px',
              maxWidth: '500px',
              margin: '0 auto',
              fontSize: '13px',
              color: '#92400e'
            }}>
              <strong>📋 Información del precierre:</strong>
              <div style={{ marginTop: '8px', textAlign: 'left' }}>
                <div>• Realizado por: {cuadreActivo?.precierre?.porNombre || "N/A"}</div>
                <div>• Fecha: {cuadreActivo?.precierre?.fecha ? new Date(cuadreActivo.precierre.fecha).toLocaleString("es-CO") : "N/A"}</div>
                <div>• Asignado a: {empleadoAsignado?.nombre || cuadreActivo?.precierre?.asignadoANombre || "N/A"}</div>
              </div>
            </div>
            <button
              onClick={() => setCuadreActivoId(null)}
              style={{
                marginTop: '24px',
                padding: '12px 32px',
                backgroundColor: '#e5e7eb',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '15px'
              }}
            >
              ← Volver al listado
            </button>
          </div>
        </div>
      );
    }
  
    // Vista cuando YA hay un cuadre activo
    return (
      <div className="card" style={{ maxWidth: '100%', width: '100%' }}>
        <div className="cuadre-header">
          <div>
            <h2>
              Cuadre {cuadreActivo.sede} — {cuadreActivo.fecha}
            </h2>
            <p className="muted">
              Responsable: {empleado ? empleado.nombre : "SIN EMPLEADO"} — Valor
              inicial:{" "}
              {new Intl.NumberFormat("es-CO", {
                style: "currency",
                currency: "COP",
              }).format(cuadreActivo.valorInicial || 0)}
            </p>
            {cuadreActivo.estado === "final" && (
              <p className="badge-final">CUADRE CERRADO / NO MODIFICABLE</p>
            )}
          </div>
        </div>
        
        {/* ALERTA VISUAL SUPERPUESTA */}
        {alertaCuadre.visible && (
          <>
            {/* Fondo oscuro semi-transparente */}
            <div 
              onClick={() => setAlertaCuadre({ visible: false, tipo: '', mensaje: '' })}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                zIndex: 9998
              }}
            />
            {/* Alerta flotante centrada */}
            <div style={{
              position: 'fixed',
              top: '30%',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9999,
              padding: '24px 32px',
              borderRadius: '16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              fontSize: '16px',
              fontWeight: '600',
              backgroundColor: alertaCuadre.tipo === 'exito' ? '#dcfce7' : '#fee2e2',
              border: `3px solid ${alertaCuadre.tipo === 'exito' ? '#22c55e' : '#ef4444'}`,
              color: alertaCuadre.tipo === 'exito' ? '#166534' : '#991b1b',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              maxWidth: '550px',
              textAlign: 'left'
            }}>
              <span style={{ fontSize: '48px', alignSelf: 'center' }}>
                {alertaCuadre.tipo === 'exito' ? '✅' : '❌'}
              </span>
              <div style={{ lineHeight: '1.8', whiteSpace: 'pre-line', fontSize: '14px' }}>{alertaCuadre.mensaje}</div>
              <button
                onClick={() => setAlertaCuadre({ visible: false, tipo: '', mensaje: '' })}
                style={{
                  marginTop: '8px',
                  padding: '10px 32px',
                  backgroundColor: alertaCuadre.tipo === 'exito' ? '#22c55e' : '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  alignSelf: 'center'
                }}
              >
                Entendido
              </button>
            </div>
          </>
        )}
        
        {/* INDICADOR DE USUARIOS ACTIVOS - TRABAJO COLABORATIVO */}
        {usuariosActivosEnCuadre.length > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 16px',
            backgroundColor: '#ecfdf5',
            border: '1px solid #10b981',
            borderRadius: '8px',
            marginBottom: '16px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '18px' }}>👥</span>
              <strong style={{ color: '#059669', fontSize: '14px' }}>
                Trabajando en este cuadre:
              </strong>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {usuariosActivosEnCuadre.map((u, idx) => {
                const esSoyYo = u.usuario_id === (empleadoActual?.id || usuarioActual?.id);
                return (
                  <span 
                    key={u.usuario_id || idx}
                    style={{
                      padding: '4px 12px',
                      backgroundColor: esSoyYo ? '#10b981' : '#d1fae5',
                      color: esSoyYo ? 'white' : '#065f46',
                      borderRadius: '16px',
                      fontSize: '13px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <span style={{ 
                      width: '8px', 
                      height: '8px', 
                      backgroundColor: '#22c55e', 
                      borderRadius: '50%',
                      animation: 'pulse 2s infinite'
                    }}></span>
                    {u.usuario_nombre}{esSoyYo ? ' (Tú)' : ''}
                  </span>
                );
              })}
            </div>
            {ultimaActualizacionRemota && (
              <span style={{ 
                fontSize: '11px', 
                color: '#6b7280', 
                marginLeft: 'auto' 
              }}>
                🔄 Sincronizado: {new Date(ultimaActualizacionRemota).toLocaleTimeString('es-CO')}
              </span>
            )}
          </div>
        )}

      {/* MENÚ DE ACCIONES RÁPIDAS */}
      <MenuAccionesCuadre
        cuadreActivo={cuadreActivo}
        actualizarCuadre={actualizarCuadre}
        bloqueado={bloqueado}
        tienePermiso={tienePermiso}
      />
  
 
      <CuadroVentas
        ventas={cuadreActivo.ventas}
        onChange={(ventas) => {
          // 🔹 Siempre actualizar el sobrante calculado automáticamente
          const ventaEfectivo = Number(ventas.ventaEfectivo || 0);
          const sobranteAuto = ventaEfectivo + totalIngresosVariosCuadre - totalFacturasCaja - totalDomiciliosCuadre - totalAdelantosCuadre - totalTurnosCuadre;
          actualizarCuadre(cuadreActivo.id, { ventas: { ...ventas, sobrante: sobranteAuto } });
        }}
        disabled={bloqueado}
        totalesTransfer={totalesTransfer}
        totalDatafonos={totalDatafonosCuadre}
        totalFacturasCaja={totalFacturasCaja}
        totalDomicilios={totalDomiciliosCuadre}
        totalIngresosVarios={totalIngresosVariosCuadre}
        totalTurnos={totalTurnosCuadre}
        sobranteEfectivoCalculado={sobranteEfectivoCalculado}
        totalFacturasPendientes={totalFacturasPendientes}
        valorInicial={Number(cuadreActivo?.valorInicial || 0)}
        esGerente={esGerente}
        tienePermiso={tienePermiso}
        precierreRealizado={cuadreActivo?.precierreRealizado || false}
        precierreConfirmadoPorReceptor={cuadreActivo?.precierre?.confirmadoPorReceptor || false}
        efectivoFinalConfirmado={cuadreActivo?.efectivoFinalConfirmado || false}
        fecha={cuadreActivo.fecha}
        sede={cuadreActivo.sede}
      />
 
      <CuadroFacturas
        facturas={cuadreActivo.facturas}
        onChange={(facturas) =>
          actualizarCuadre(cuadreActivo.id, { facturas })
        }
        disabled={bloqueado}
        sede={cuadreActivo.sede}
        filtroEmpresa={filtroEmpresa}
        esGerente={esGerente}
        responsableId={cuadreActivo.empleadoId}
        empleados={empleados}
        facturasEliminadas={cuadreActivo.facturasEliminadas || []}
        onFacturasEliminadasChange={(facturasEliminadas) =>
          actualizarCuadre(cuadreActivo.id, { facturasEliminadas })
        }
        fechaCuadre={cuadreActivo.fecha}
        tienePermiso={tienePermiso}
      />
 
      {/* Contenedor de dos columnas para Domicilios y Transferencias */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1fr 1fr', 
        gap: '24px',
        marginBottom: '24px',
        alignItems: 'start'
      }}>
        <CuadroDomicilios
          domicilios={cuadreActivo.domicilios}
          onChange={(domicilios) =>
            actualizarCuadreParcial(cuadreActivo.id, 'domicilios', domicilios)
          }
          disabled={bloqueado}
          bloquearFila={(filaId) => bloquearFila('domicilios', filaId)}
          desbloquearFila={(filaId) => desbloquearFila('domicilios', filaId)}
          filaEstaBloqueadaPorOtro={(filaId) => filaEstaBloqueadaPorOtro('domicilios', filaId)}
          fecha={cuadreActivo.fecha}
          sede={cuadreActivo.sede}
          transferencias={cuadreActivo.transfer || []}
        />
 
        <CuadroTransfer
          transfer={cuadreActivo.transfer}
          onChange={(transfer) =>
            actualizarCuadreParcial(cuadreActivo.id, 'transfer', transfer)
          }
          disabled={bloqueado}
          bloquearFila={(filaId) => bloquearFila('transfer', filaId)}
          desbloquearFila={(filaId) => desbloquearFila('transfer', filaId)}
          filaEstaBloqueadaPorOtro={(filaId) => filaEstaBloqueadaPorOtro('transfer', filaId)}
          fecha={cuadreActivo.fecha}
          sede={cuadreActivo.sede}
        />
      </div>

      <CuadroDatafonos
        datafonos={cuadreActivo.datafonos || []}
        onChange={(datafonos) =>
          actualizarCuadre(cuadreActivo.id, { datafonos })
        }
        disabled={bloqueado}
      />
 
      <hr className="divider" />
 
      <div className="grid-2">
        <div>
          <h3>Comentario de modificación</h3>
          <p className="muted">
            No se permite cambiar los valores del cuadre una vez guardado,
            pero aquí puedes justificar ajustes contables o notas internas.
          </p>
          <textarea
            className="textarea"
            rows={4}
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            placeholder="Escribe comentarios de modificación (si aplica)"
          ></textarea>
          <Button
            variant="outline"
            style={{ marginTop: 8 }}
            onClick={() => {
              guardarComentario(cuadreActivo.id, comentario);
              alert("Comentario guardado para este cuadre.");
            }}
          >
            Guardar comentario
          </Button>
        </div>
 
        <div>
          <h3>Acciones sobre el cuadre</h3>
          <ul className="muted">
            <li>• <strong style={{color: '#dc2626'}}>Cierre Definitivo:</strong> Guarda y cierra el cuadre permanentemente.</li>
            <li>• Precierre: Cambio de turno (continúa otro empleado).</li>
            <li>• Imprimir cuadre completo (incluye totales y comentarios).</li>
          </ul>
          
          {/* Mostrar info de precierre si ya se realizó */}
          {cuadreActivo.precierreRealizado && cuadreActivo.precierre && (
            <div style={{
              padding: '12px',
              marginBottom: '12px',
              backgroundColor: '#fef3c7',
              border: '2px solid #f59e0b',
              borderRadius: '8px',
              fontSize: '12px'
            }}>
              <strong>📋 Precierre realizado:</strong>
              <div style={{ marginTop: '6px' }}>
                • {new Date(cuadreActivo.precierre.fecha).toLocaleString("es-CO")}<br/>
                • Por: {cuadreActivo.precierre.porNombre}<br/>
                • Asignado a: {cuadreActivo.precierre.asignadoANombre}
                {cuadreActivo.precierre.observaciones && (
                  <><br/>• Obs: {cuadreActivo.precierre.observaciones}</>
                )}
              </div>
            </div>
          )}
          
          {/* Botón de CIERRE DEFINITIVO - Primero y más llamativo */}
          {tienePermiso("admin_cuadre_guardar") && (
          <Button
            variant="solid"
            disabled={bloqueado}
            style={{
              marginRight: 8,
              marginBottom: 8,
              background: bloqueado ? '#9ca3af' : 'linear-gradient(135deg, #dc2626, #b91c1c)',
              color: 'white',
              fontWeight: 700,
              fontSize: '14px',
              padding: '12px 20px',
              border: 'none',
              boxShadow: bloqueado ? 'none' : '0 4px 6px rgba(185, 28, 28, 0.3)',
            }}
            onClick={() => {
              const ventas = cuadreActivo?.ventas || {};
              const facturas = cuadreActivo?.facturas || [];
              const errores = [];
              
              // 🔹 Validar que Venta en Efectivo tenga un valor (puede ser 0)
              // IMPORTANTE: 0 es un valor válido, no rechazar por ser falsy
              const ventaEfectivoValue = ventas.ventaEfectivo !== undefined && ventas.ventaEfectivo !== null 
                ? Number(ventas.ventaEfectivo) 
                : null;
              
              if (ventaEfectivoValue === null || isNaN(ventaEfectivoValue)) {
                errores.push("• Debes ingresar el valor de Venta en Efectivo (puede ser $0)");
              }
              
              // 🔹 Validar que Online Rappi tenga un valor (PUEDE SER 0 - DE FUDO)
              // IMPORTANTE: 0 es un valor válido, no rechazar por ser falsy
              const onlineRappiValue = ventas.onlineRappi !== undefined && ventas.onlineRappi !== null 
                ? Number(ventas.onlineRappi) 
                : null;
              
              if (onlineRappiValue === null || isNaN(onlineRappiValue)) {
                errores.push("• Debes ingresar el valor de Online Rappi (puede ser $0)");
              }
              
              // 🔹 Validar que se haya seleccionado Sí o No en descuadre de transferencia
              if (!ventas.huboDescuadreTransferencia || ventas.huboDescuadreTransferencia === "") {
                errores.push("• Debes indicar si hubo descuadre de caja en transferencia (Sí o No)");
              }
              
              // 🔹 Validar que se haya seleccionado Sí o No en descuadre de datáfono
              if (!ventas.huboDescuadreDatafono || ventas.huboDescuadreDatafono === "") {
                errores.push("• Debes indicar si hubo descuadre de caja en datáfono (Sí o No)");
              }
              
              // 🔹 Si hubo descuadre de transferencia, validar campos obligatorios
              if (ventas.huboDescuadreTransferencia === "Sí") {
                if (!ventas.valorDescuadreTransferencia || ventas.valorDescuadreTransferencia === "") {
                  errores.push("• Indicaste descuadre en transferencia: falta el valor del descuadre");
                }
                if (!ventas.numeroPedidoDescuadreTransferencia || ventas.numeroPedidoDescuadreTransferencia === "") {
                  errores.push("• Indicaste descuadre en transferencia: falta el número de pedido");
                }
                if (!ventas.bancoDescuadreTransferencia || ventas.bancoDescuadreTransferencia === "") {
                  errores.push("• Indicaste descuadre en transferencia: falta seleccionar el banco");
                }
              }
              
              // 🔹 Si hubo descuadre de datáfono, validar que tenga valor
              if (ventas.huboDescuadreDatafono === "Sí" && (!ventas.valorDescuadreDatafono || ventas.valorDescuadreDatafono === "")) {
                errores.push("• Indicaste descuadre en datáfono: falta el valor del descuadre");
              }
              
              // 🔹 Validar que se haya confirmado el efectivo en caja
              if (!ventas.efectivoEnCajaConfirmado) {
                errores.push("• Debes confirmar cuánto efectivo tienes en caja (presiona el botón 'Confirmar' en la sección de Verificación de Efectivo)");
              }
              
              // 🔹 Facturas "Pagada transferencia" que NO han sido enviadas a procesos
              const facturasTransferenciaSinEnviar = facturas.filter(f => 
                f.estado === "Pagada transferencia" && 
                !f.tareaEnviadaId
              );
              if (facturasTransferenciaSinEnviar.length > 0) {
                errores.push(`• Tienes ${facturasTransferenciaSinEnviar.length} factura(s) "Pagada transferencia" sin enviar a Procesos Administrativos`);
              }
              
              // 🔹 Facturas "Pagada transferencia" que están en estado Pendiente
              const facturasPendientes = facturas.filter(f => 
                f.estado === "Pagada transferencia" && 
                f.tareaEnviadaId && 
                f.estadoTarea !== "Procesada" && 
                f.estadoTarea !== "Negada"
              );
              if (facturasPendientes.length > 0) {
                errores.push(`• Tienes ${facturasPendientes.length} factura(s) por transferencia pendiente(s) de procesar`);
              }
              
              // Si hay errores, mostrar todos juntos
              if (errores.length > 0) {
                mostrarAlertaVisual('error', `⚠️ No se puede cerrar el cuadre:\n\n${errores.join('\n')}`);
                return;
              }
              
              // 🔹 Validar descuadre en efectivo (calculado automáticamente) - esto es solo confirmación, no bloquea
              if (sobranteEfectivoCalculado !== 0) {
                const tipoDesc = sobranteEfectivoCalculado > 0 ? "SOBRANTE" : "DESCUADRE";
                const valorDesc = Math.abs(sobranteEfectivoCalculado);
                const formatCOP = (val) => new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(val || 0);
                const confirmar = window.confirm(
                  `⚠️ ATENCIÓN: Hay un ${tipoDesc} EN EFECTIVO de ${formatCOP(valorDesc)}.\n\n¿Deseas continuar y cerrar el cuadre con este ${tipoDesc.toLowerCase()}?`
                );
                if (!confirmar) {
                  return;
                }
              }
              
              const t = calcularTotalesCuadre(cuadreActivo);
              setTotales(t);
              guardarFinal(cuadreActivo.id, t);
              mostrarAlertaVisual('exito', '✅ Cuadre guardado y cerrado correctamente. Ya no se puede modificar.');
              
              // 🖨️ IMPRESIÓN AUTOMÁTICA DEL RESUMEN DE TICKET + 💾 GUARDAR TIRILLA
               setTimeout(async () => {
                let resumenHTML = "";
                try {
                  resumenHTML = generarResumenTicketImpresion(cuadreActivo, empleados);
                } catch (e) {
                  console.error('❌ Error al generar resumen de impresión:', e);
                  return;
                }

                // 🖨️ Abrir ventana emergente e imprimir (siempre, aunque falle el guardado)
                try {
                  const ventana = window.open('', '_blank');
                  if (ventana) {
                    ventana.document.write(resumenHTML);
                    ventana.document.close();
                    ventana.focus();
                    setTimeout(() => { try { ventana.print(); } catch (err) {} }, 400);
                  } else {
                    mostrarAlertaVisual('error', '⚠️ El navegador bloqueó la ventana de impresión. Habilita las ventanas emergentes para imprimir la tirilla.');
                  }
                } catch (e) {
                  console.error('❌ Error al abrir ventana de impresión:', e);
                }

                // 💾 Guardar tirilla en Supabase para consultas posteriores
                try {
                  await guardarTirillaEnSupabase(cuadreActivo.id, resumenHTML, cuadreActivo, filtroEmpresa);
                } catch (e) {
                  console.error('❌ Error al guardar tirilla en Supabase:', e);
                }
              }, 1000);
            }}
          >
            🔒 Cierre Definitivo de Cuadre
          </Button>
          )}
          
          {/* Botón de Precierre - solo si NO está cerrado */}
          {!bloqueado && tienePermiso("admin_cuadre_guardar") && (
          <Button
            variant="outline"
            style={{ 
              marginRight: 8, 
              marginBottom: 8,
              backgroundColor: '#fef3c7',
              borderColor: '#f59e0b',
              color: '#92400e'
            }}
            onClick={() => setMostrarModalPrecierre(true)}
          >
            📋 Precierre de Turno
          </Button>
          )}

          {tienePermiso("admin_cuadre_imprimir") && (
          <Button
            variant="outline"
            style={{ marginLeft: 8, marginTop: 8 }}
            onClick={() =>
              imprimirCuadreCompleto(cuadreActivo, empleados)
            }
          >
            Imprimir cuadre
          </Button>
          )}
        </div>
      </div>
      
      {/* MODAL DE PRECIERRE */}
      {mostrarModalPrecierre && (() => {
        // Calcular todos los totales para mostrar en el modal
        const ventas = cuadreActivo?.ventas || {};
        const facturas = cuadreActivo?.facturas || [];
        const transfer = cuadreActivo?.transfer || [];
        const datafonos = cuadreActivo?.datafonos || [];
        
        // Totales de transferencias
        let bancolombia = 0, davivienda = 0, daviplata = 0, nequi = 0;
        transfer.forEach(t => {
          bancolombia += Number(t.bancolombia || 0);
          davivienda += Number(t.davivienda || 0);
          daviplata += Number(t.daviplata || 0);
          nequi += Number(t.nequi || 0);
        });
        const totalTransferencias = bancolombia + davivienda + daviplata + nequi;
        
        // Total datafonos
        const totalDatafonosCalc = datafonos.reduce((s, d) => s + Number(d.valor || 0), 0);
        
        // Ventas
        const ventaEfectivo = Number(ventas.ventaEfectivo || 0);
        const onlineRappi = Number(ventas.onlineRappi || 0);
        const sobrante = Number(ventas.sobrante || 0);
        const huboDescuadre = ventas.huboDescuadre || "No";
        
        // 🔹 Valor inicial de caja para mostrar dos valores
        const valorInicialCaja = Number(cuadreActivo?.valorInicial || 0);

        // Totales de facturas (calculado aquí para usar en el cuadre)
        const totalPendientes = facturas.filter(f => f.estado === "Pendiente").reduce((s, f) => s + Number(f.valor || 0), 0);

        // Las facturas pendientes quedan en caja para el siguiente cuadre;
        // el empleado las retiene aparte, por lo que se restan del valor inicial.
        const valorDebeQuedarConCaja = valorInicialCaja - totalPendientes;
        const valorDebeQuedarSinCaja = sobrante - totalPendientes;

        // 🔹 Efectivo en caja ingresado por el usuario
        const efectivoEnCaja = Number(ventas.efectivoEnCaja || 0);
        const diferenciaEfectivo = efectivoEnCaja - valorDebeQuedarConCaja;
        const estaCuadrado = Math.abs(diferenciaEfectivo) < 1;
        const estaDescuadrado = !estaCuadrado;

        // Total ventas
        const totalVentas = ventaEfectivo + totalTransferencias + totalDatafonosCalc + onlineRappi;
        const totalCaja = facturas.filter(f => f.estado === "Pagada de caja").reduce((s, f) => s + Number(f.valor || 0), 0);
        const totalTransf = facturas.filter(f => f.estado === "Pagada transferencia").reduce((s, f) => s + Number(f.valor || 0), 0);
        const totalGastos = totalPendientes + totalCaja + totalTransf;
        
        const formatCOP = (val) => new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(val || 0);
        
        return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          overflow: 'auto',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '100%',
            maxWidth: '650px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ 
              margin: '0 0 16px 0', 
              color: '#92400e',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📋 Precierre de Turno
            </h3>
            
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
              El precierre registra que tu turno terminó y asigna el cuadre a otro empleado para que continúe.
              <strong> El cuadre NO se cierra definitivamente</strong>, solo se registra el cambio de turno.
            </p>
            
            {/* 🔹 VERIFICACIÓN DE EFECTIVO - PRIMERO ANTES DE MOSTRAR VALORES */}
            {!ventas.efectivoEnCajaConfirmado && (
              <div style={{
                padding: '20px',
                backgroundColor: '#fef2f2',
                border: '3px solid #ef4444',
                borderRadius: '12px',
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
                <h4 style={{ margin: '0 0 12px 0', color: '#dc2626', fontSize: '16px' }}>
                  DEBES CONFIRMAR EL EFECTIVO EN CAJA PRIMERO
                </h4>
                <p style={{ fontSize: '13px', color: '#991b1b', marginBottom: '16px' }}>
                  Para hacer el precierre, primero debes contar e ingresar cuánto efectivo tienes en caja.
                  <br/><br/>
                  <strong>Los valores del cuadre están ocultos</strong> hasta que confirmes el efectivo.
                </p>
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#f1f5f9', 
                  borderRadius: '8px',
                  border: '2px dashed #94a3b8',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
                    🔒 Valores ocultos: "Debe quedar en caja", "Sobrante neto", etc.
                  </div>
                </div>
                <button
                  onClick={() => setMostrarModalPrecierre(false)}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '14px'
                  }}
                >
                  ← Ir a confirmar efectivo en caja
                </button>
              </div>
            )}
            
            {/* SOLO MOSTRAR SI YA CONFIRMÓ EL EFECTIVO */}
            {ventas.efectivoEnCajaConfirmado && (
              <>
                {/* RESUMEN DE VENTAS Y MEDIOS DE PAGO */}
                <div style={{
                  padding: '14px',
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #22c55e',
                  borderRadius: '10px',
                  marginBottom: '12px',
                  fontSize: '12px'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#166534', fontSize: '14px' }}>💰 Ventas y medios de pago</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: 'white', borderRadius: '4px' }}>
                      <span>Valor venta efectivo:</span>
                      <strong style={{ color: '#166534' }}>{formatCOP(ventaEfectivo)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: 'white', borderRadius: '4px' }}>
                      <span>Valor Bancolombia:</span>
                      <strong style={{ color: '#166534' }}>{formatCOP(bancolombia)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: 'white', borderRadius: '4px' }}>
                      <span>Valor Davivienda:</span>
                      <strong style={{ color: '#92400e' }}>{formatCOP(davivienda)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: 'white', borderRadius: '4px' }}>
                      <span>Valor Daviplata:</span>
                      <strong style={{ color: '#991b1b' }}>{formatCOP(daviplata)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: 'white', borderRadius: '4px' }}>
                      <span>Valor Nequi:</span>
                      <strong style={{ color: '#5b21b6' }}>{formatCOP(nequi)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: 'white', borderRadius: '4px' }}>
                      <span>Valor Datáfono:</span>
                      <strong style={{ color: '#0369a1' }}>{formatCOP(totalDatafonosCalc)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: 'white', borderRadius: '4px' }}>
                      <span>Online Rappi:</span>
                      <strong style={{ color: '#dc2626' }}>{formatCOP(onlineRappi)}</strong>
                    </div>
                  </div>
                  
                  {/* 🔹 CUATRO VALORES EN LÍNEA - Tienes, Debe quedar, Sobrante neto, Valor Total del Sobrante */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr 1fr 1fr', 
                    gap: '8px', 
                    marginTop: '12px',
                    padding: '8px',
                    backgroundColor: estaDescuadrado ? '#fef2f2' : '#f0fdf4',
                    borderRadius: '8px',
                    border: `2px solid ${estaDescuadrado ? '#ef4444' : '#22c55e'}`
                  }}>
                    {/* Efectivo en caja (ingresado) */}
                    <div style={{ 
                      padding: '8px', 
                      backgroundColor: '#eff6ff', 
                      borderRadius: '6px',
                      textAlign: 'center',
                      border: '2px solid #3b82f6'
                    }}>
                      <div style={{ fontSize: '10px', color: '#1e40af', marginBottom: '2px', fontWeight: '700' }}>💵 Tienes en caja</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e40af' }}>
                        {formatCOP(efectivoEnCaja)}
                      </div>
                      <div style={{ fontSize: '9px', color: '#6b7280' }}>Valor confirmado ✅</div>
                    </div>
                    <div style={{ 
                      padding: '8px', 
                      backgroundColor: valorDebeQuedarConCaja >= 0 ? '#d1fae5' : '#fef2f2', 
                      borderRadius: '6px',
                      textAlign: 'center',
                      border: `1px solid ${valorDebeQuedarConCaja >= 0 ? '#10b981' : '#ef4444'}`
                    }}>
                      <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>📦 Debe quedar en caja</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: valorDebeQuedarConCaja >= 0 ? '#047857' : '#dc2626' }}>
                        {formatCOP(valorDebeQuedarConCaja)}
                      </div>
                      <div style={{ fontSize: '9px', color: '#9ca3af' }}>Base: {formatCOP(valorInicialCaja)}</div>
                      {totalPendientes > 0 && (
                        <div style={{ fontSize: '9px', color: '#d97706', marginTop: '2px' }}>
                          Fact. pend. restadas: {formatCOP(totalPendientes)}
                        </div>
                      )}
                    </div>
                    <div style={{ 
                      padding: '8px', 
                      backgroundColor: valorDebeQuedarSinCaja >= 0 ? '#ecfdf5' : '#fef2f2', 
                      borderRadius: '6px',
                      textAlign: 'center',
                      border: `1px solid ${valorDebeQuedarSinCaja >= 0 ? '#059669' : '#ef4444'}`
                    }}>
                      <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>💵 Sobrante neto</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: valorDebeQuedarSinCaja >= 0 ? '#059669' : '#dc2626' }}>
                        {formatCOP(valorDebeQuedarSinCaja)}
                      </div>
                      <div style={{ fontSize: '9px', color: '#9ca3af' }}>Sin base</div>
                    </div>
                    <div style={{ 
                      padding: '8px', 
                      backgroundColor: '#f0f9ff', 
                      borderRadius: '6px',
                      textAlign: 'center',
                      border: '2px solid #0284c7'
                    }}>
                      <div style={{ fontSize: '10px', color: '#0369a1', marginBottom: '2px', fontWeight: '700' }}>💰 Valor Total</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#0c4a6e' }}>
                        {formatCOP(valorDebeQuedarSinCaja + diferenciaEfectivo)}
                      </div>
                      <div style={{ fontSize: '9px', color: '#0284c7' }}>del Sobrante</div>
                    </div>
                  </div>
                  
                  {/* Estado del cuadre - Comparación real */}
                  <div style={{ 
                    marginTop: '8px',
                    padding: '8px 12px',
                    backgroundColor: estaCuadrado ? '#d1fae5' : '#fef2f2',
                    borderRadius: '10px',
                    textAlign: 'center',
                    border: `3px solid ${estaCuadrado ? '#10b981' : '#ef4444'}`
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: '800', color: estaCuadrado ? '#065f46' : '#dc2626', marginBottom: '4px' }}>
                      {estaCuadrado 
                        ? "✅ ¡CUADRADO! El efectivo coincide"
                        : diferenciaEfectivo > 0 
                          ? `⚠️ SOBRANTE: Tienes ${formatCOP(diferenciaEfectivo)} de más`
                          : `🚨 DESCUADRADO: Te faltan ${formatCOP(Math.abs(diferenciaEfectivo))}`
                      }
                    </div>
                    <div style={{ fontSize: '10px', color: '#6b7280' }}>
                      Diferencia: <strong style={{ color: estaCuadrado ? '#065f46' : (diferenciaEfectivo > 0 ? '#d97706' : '#dc2626') }}>{diferenciaEfectivo >= 0 ? '+' : ''}{formatCOP(diferenciaEfectivo)}</strong>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', marginTop: '6px' }}>
                    <span>¿Hubo descuadre?:</span>
                    <strong style={{ color: huboDescuadre === "Sí" ? '#dc2626' : '#166534' }}>{huboDescuadre}</strong>
                  </div>
                  <div style={{ 
                    borderTop: '2px solid #22c55e', 
                    marginTop: '10px', 
                    paddingTop: '10px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '4px',
                    fontSize: '11px'
                  }}>
                    <span>Total efectivo: <strong style={{ color: '#166534' }}>{formatCOP(ventaEfectivo)}</strong></span>
                    <span>Total transferencias: <strong style={{ color: '#059669' }}>{formatCOP(totalTransferencias)}</strong></span>
                    <span>Total datáfonos: <strong style={{ color: '#0369a1' }}>{formatCOP(totalDatafonosCalc)}</strong></span>
                    <span>Total Online Rappi: <strong style={{ color: '#dc2626' }}>{formatCOP(onlineRappi)}</strong></span>
                  </div>
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '8px', 
                    backgroundColor: '#dcfce7', 
                    borderRadius: '6px', 
                    textAlign: 'center',
                    fontWeight: '700',
                    color: '#166534',
                    fontSize: '14px'
                  }}>
                    TOTAL VENTAS: {formatCOP(totalVentas)}
                  </div>
                </div>
                
                {/* RESUMEN DE GASTOS */}
                <div style={{
                  padding: '14px',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #f87171',
                  borderRadius: '10px',
                  marginBottom: '16px',
                  fontSize: '12px'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#991b1b', fontSize: '14px' }}>📋 Resumen de Facturas por Tipo de Pago</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    <div style={{ padding: '10px', backgroundColor: '#fef3c7', borderRadius: '6px', textAlign: 'center', border: '1px solid #f59e0b' }}>
                      <div style={{ fontSize: '11px', color: '#92400e', marginBottom: '4px' }}>⏳ Pendientes</div>
                      <strong style={{ color: '#ea580c', fontSize: '14px' }}>{formatCOP(totalPendientes)}</strong>
                    </div>
                    <div style={{ padding: '10px', backgroundColor: '#d1fae5', borderRadius: '6px', textAlign: 'center', border: '1px solid #10b981' }}>
                      <div style={{ fontSize: '11px', color: '#065f46', marginBottom: '4px' }}>💵 Pagadas Caja</div>
                      <strong style={{ color: '#166534', fontSize: '14px' }}>{formatCOP(totalCaja)}</strong>
                    </div>
                    <div style={{ padding: '10px', backgroundColor: '#dbeafe', borderRadius: '6px', textAlign: 'center', border: '1px solid #3b82f6' }}>
                      <div style={{ fontSize: '11px', color: '#1e40af', marginBottom: '4px' }}>🏦 Pagadas Transfer</div>
                      <strong style={{ color: '#0369a1', fontSize: '14px' }}>{formatCOP(totalTransf)}</strong>
                    </div>
                  </div>
                </div>
            
            {/* 🔹 ALERTA DE BASES DE DOMICILIOS PENDIENTES */}
            {(() => {
              const basesDom = cuadreActivo?.basesDomicilios || [];
              const basesPendientes = basesDom.filter(base => {
                const totalAbonos = (base.abonos || []).reduce((sum, a) => sum + Number(a.valor || 0), 0);
                return (Number(base.valorBase || 0) - totalAbonos) > 0;
              });
              const totalPendienteBases = basesPendientes.reduce((sum, base) => {
                const totalAbonos = (base.abonos || []).reduce((s, a) => s + Number(a.valor || 0), 0);
                return sum + (Number(base.valorBase || 0) - totalAbonos);
              }, 0);

              if (basesPendientes.length === 0) return null;

              return (
                <div style={{
                  padding: '14px',
                  backgroundColor: '#fef3c7',
                  border: '2px solid #f59e0b',
                  borderRadius: '10px',
                  marginBottom: '16px'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#92400e', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    ⚠️ Bases de Domicilios Pendientes
                  </h4>
                  <p style={{ fontSize: '12px', color: '#78350f', marginBottom: '10px' }}>
                    Hay <strong>{basesPendientes.length}</strong> base(s) con saldo pendiente por cobrar:
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {basesPendientes.map(base => {
                      const totalAbonos = (base.abonos || []).reduce((s, a) => s + Number(a.valor || 0), 0);
                      const saldo = Number(base.valorBase || 0) - totalAbonos;
                      return (
                        <div key={base.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #fbbf24'
                        }}>
                          <span style={{ fontWeight: '600', color: '#1f2937' }}>🚴 {base.empleado}</span>
                          <span style={{ fontWeight: '700', color: '#dc2626' }}>{formatCOP(saldo)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{
                    marginTop: '10px',
                    padding: '8px',
                    backgroundColor: '#fef9c3',
                    borderRadius: '6px',
                    textAlign: 'center',
                    fontWeight: '700',
                    color: '#92400e',
                    fontSize: '14px'
                  }}>
                    TOTAL PENDIENTE: {formatCOP(totalPendienteBases)}
                  </div>
                </div>
              );
            })()}
            
            {/* Selector de empleado */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px' }}>
                👤 Asignar cuadre a:
              </label>
              <select
                value={precierreAsignadoA}
                onChange={(e) => setPrecierreAsignadoA(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              >
                <option value="">-- Seleccionar empleado --</option>
                {empleados
                  .filter(e => e.id !== (empleadoActual?.id || usuarioActual?.id))
                  .map(e => (
                    <option key={e.id} value={e.id}>{e.nombre}</option>
                  ))
                }
              </select>
            </div>
            
            {/* Observaciones */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px' }}>
                📝 Observaciones del turno (opcional):
              </label>
              <textarea
                value={precierreObservaciones}
                onChange={(e) => setPrecierreObservaciones(e.target.value)}
                placeholder="Ej: Todo en orden, sin novedades..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>
            
            {/* Botones */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setMostrarModalPrecierre(false);
                  setPrecierreAsignadoA("");
                  setPrecierreObservaciones("");
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#e5e7eb',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (!precierreAsignadoA) {
                    alert("⚠️ Debes seleccionar un empleado para asignar el cuadre.");
                    return;
                  }
                  
                  // 🔹 Validar que el efectivo esté confirmado antes de hacer precierre
                  if (!ventas.efectivoEnCajaConfirmado) {
                    alert("⚠️ Para hacer el precierre, primero debes confirmar el efectivo en caja.\n\nVe a la sección 'Verificación de Efectivo en Caja' e ingresa cuánto efectivo tienes.");
                    setMostrarModalPrecierre(false);
                    return;
                  }
                  
                  // Pasar los totales calculados a la función (incluyendo efectivo confirmado)
                  realizarPrecierre(cuadreActivo.id, precierreAsignadoA, precierreObservaciones, {
                    ventaEfectivo,
                    bancolombia,
                    davivienda,
                    daviplata,
                    nequi,
                    totalTransferencias,
                    totalDatafonos: totalDatafonosCalc,
                    onlineRappi,
                    sobrante,
                    huboDescuadre,
                    totalVentas,
                    totalFacturasPendientes: totalPendientes,
                    totalFacturasCaja: totalCaja,
                    totalFacturasTransferencia: totalTransf,
                    totalGastos,
                    // 🔹 Agregar efectivo confirmado al precierre
                    efectivoEnCaja: Number(ventas.efectivoEnCaja || 0),
                    valorInicialCaja: valorInicialCaja,
                    debeQuedarEnCaja: valorDebeQuedarConCaja,
                    diferenciaEfectivo: diferenciaEfectivo,
                    estaCuadrado: estaCuadrado
                  });
                  setMostrarModalPrecierre(false);
                  setPrecierreAsignadoA("");
                  setPrecierreObservaciones("");
                }}
                style={{
                  padding: '10px 24px',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)'
                }}
              >
                ✅ Confirmar Precierre
              </button>
            </div>
              </>
            )}
          </div>
        </div>
        );
      })()}
      
      {/* MODAL DE CONFIRMACIÓN DE PRECIERRE RECIBIDO */}
      {mostrarAlertaConfirmarPrecierre && cuadreActivo?.precierre && (() => {
        const p = cuadreActivo.precierre;
        const totales = p.totales || {};
        const formatCOP = (val) => new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(val || 0);
        const fechaPrecierre = p.fecha ? new Date(p.fecha).toLocaleString("es-CO") : "N/A";
        
        return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            overflow: 'auto',
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '28px',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
              border: '3px solid #f59e0b',
              position: 'relative'
            }}>
              {/* Botón X para cerrar */}
              <button
                onClick={() => {
                  setMostrarAlertaConfirmarPrecierre(false);
                  // Opcional: volver al listado de cuadres
                  setCuadreActivoId(null);
                }}
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  fontSize: '20px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#fecaca';
                  e.target.style.transform = 'scale(1.1)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = '#fee2e2';
                  e.target.style.transform = 'scale(1)';
                }}
                title="Salir sin confirmar"
              >
                ✕
              </button>
              
              <div style={{
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '8px' }}>⚠️</div>
                <h2 style={{ 
                  margin: '0', 
                  color: '#92400e',
                  fontSize: '22px'
                }}>
                  PRECIERRE DE TURNO ANTERIOR
                </h2>
                <p style={{ 
                  margin: '8px 0 0 0', 
                  color: '#666',
                  fontSize: '14px'
                }}>
                  Debes confirmar que el turno anterior está correcto antes de continuar
                </p>
              </div>
              
              {/* Info del precierre */}
              <div style={{
                padding: '16px',
                backgroundColor: '#fef3c7',
                border: '2px solid #f59e0b',
                borderRadius: '12px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
                  <div><strong>📅 Fecha precierre:</strong> {fechaPrecierre}</div>
                  <div><strong>👤 Realizado por:</strong> {p.porNombre}</div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <strong>📝 Observaciones:</strong> {p.observaciones || "Sin observaciones"}
                  </div>
                </div>
              </div>
              
              {/* 🔹 VERIFICACIÓN DE EFECTIVO - PRIMERO ANTES DE MOSTRAR VALORES */}
              <div style={{
                padding: '16px',
                backgroundColor: efectivoReceptorConfirmado ? '#d1fae5' : '#eff6ff',
                border: `3px solid ${efectivoReceptorConfirmado ? '#22c55e' : '#3b82f6'}`,
                borderRadius: '12px',
                marginBottom: '16px'
              }}>
                <h4 style={{ margin: '0 0 12px 0', color: efectivoReceptorConfirmado ? '#166534' : '#1e40af', fontSize: '14px', textAlign: 'center' }}>
                  💵 PASO 1: VERIFICA EL EFECTIVO EN CAJA
                </h4>
                
                {!efectivoReceptorConfirmado ? (
                  <>
                    <div style={{ 
                      padding: '16px', 
                      backgroundColor: '#fff7ed', 
                      borderRadius: '10px',
                      textAlign: 'center',
                      border: '2px solid #f59e0b'
                    }}>
                      <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '8px', fontWeight: '700' }}>
                        📥 ¿Cuánto efectivo hay en caja? (Cuenta antes de ver los valores)
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center', maxWidth: '300px', margin: '0 auto' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                          <span style={{ 
                            position: 'absolute', 
                            left: '12px', 
                            top: '50%', 
                            transform: 'translateY(-50%)', 
                            color: '#1e40af', 
                            fontWeight: '700',
                            fontSize: '18px'
                          }}>$</span>
                          <input
                            type="number"
                            placeholder="0"
                            value={efectivoReceptor}
                            onChange={(e) => setEfectivoReceptor(e.target.value)}
                            style={{ 
                              width: '100%',
                              padding: '12px 12px 12px 30px',
                              borderRadius: '8px',
                              border: '2px solid #3b82f6',
                              backgroundColor: 'white',
                              fontSize: '20px',
                              fontWeight: '700',
                              textAlign: 'center',
                              color: '#1e40af'
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          disabled={!efectivoReceptor || Number(efectivoReceptor) < 0}
                          onClick={() => {
                            if (!efectivoReceptor || Number(efectivoReceptor) < 0) {
                              alert("⚠️ Ingresa un valor válido");
                              return;
                            }
                            const confirmar = window.confirm(`¿Confirmas que hay ${formatCOP(Number(efectivoReceptor))} en efectivo en caja?\n\nEste valor se comparará con lo que declaró el turno saliente.`);
                            if (confirmar) {
                              setEfectivoReceptorConfirmado(true);
                            }
                          }}
                          style={{ 
                            padding: '12px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: (!efectivoReceptor || Number(efectivoReceptor) < 0) ? '#94a3b8' : '#22c55e',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '700',
                            cursor: (!efectivoReceptor || Number(efectivoReceptor) < 0) ? 'not-allowed' : 'pointer'
                          }}
                        >
                          ✓ Confirmar
                        </button>
                      </div>
                      <div style={{ fontSize: '10px', color: '#f59e0b', marginTop: '10px', fontWeight: '600' }}>
                        ⚠️ Debes contar e ingresar el efectivo antes de ver los valores del turno anterior
                      </div>
                    </div>
                    
                    {/* Mensaje de que los valores están ocultos */}
                    <div style={{ 
                      marginTop: '16px',
                      padding: '12px', 
                      backgroundColor: '#f1f5f9', 
                      borderRadius: '8px',
                      textAlign: 'center',
                      border: '2px dashed #94a3b8'
                    }}>
                      <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>
                        🔒 Los valores del turno anterior están ocultos
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                        Se revelarán después de confirmar el efectivo en caja
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Ya confirmó - Mostrar comparación */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      <div style={{ 
                        padding: '12px', 
                        backgroundColor: '#d1fae5', 
                        borderRadius: '8px',
                        textAlign: 'center',
                        border: '2px solid #22c55e'
                      }}>
                        <div style={{ fontSize: '11px', color: '#166534', marginBottom: '4px', fontWeight: '700' }}>
                          📥 Tú contaste:
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#166534' }}>
                          {formatCOP(Number(efectivoReceptor))}
                        </div>
                        <button
                          type="button"
                          onClick={() => setEfectivoReceptorConfirmado(false)}
                          style={{
                            marginTop: '4px',
                            padding: '2px 8px',
                            fontSize: '9px',
                            backgroundColor: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          ✏️ Modificar
                        </button>
                      </div>
                      
                      <div style={{ 
                        padding: '12px', 
                        backgroundColor: '#dbeafe', 
                        borderRadius: '8px',
                        textAlign: 'center',
                        border: '2px solid #3b82f6'
                      }}>
                        <div style={{ fontSize: '11px', color: '#1e40af', marginBottom: '4px', fontWeight: '700' }}>
                          📤 Turno saliente dejó:
                        </div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e40af' }}>
                          {formatCOP(totales.efectivoEnCaja || 0)}
                        </div>
                        <div style={{ fontSize: '9px', color: totales.estaCuadrado ? '#22c55e' : '#f59e0b', marginTop: '4px' }}>
                          {totales.estaCuadrado ? '✅ Estaba cuadrado' : '⚠️ Tenía diferencia'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Resultado de la comparación */}
                    {(() => {
                      const efectivoSaliente = Number(totales.efectivoEnCaja || 0);
                      const efectivoEntrante = Number(efectivoReceptor || 0);
                      const diferencia = efectivoEntrante - efectivoSaliente;
                      const coincide = Math.abs(diferencia) < 1;
                      
                      return (
                        <div style={{
                          padding: '12px',
                          backgroundColor: coincide ? '#d1fae5' : '#fef2f2',
                          borderRadius: '8px',
                          textAlign: 'center',
                          border: `2px solid ${coincide ? '#22c55e' : '#ef4444'}`
                        }}>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: coincide ? '#166534' : '#dc2626' }}>
                            {coincide 
                              ? '✅ ¡Los valores coinciden!'
                              : diferencia > 0 
                                ? `⚠️ Hay ${formatCOP(diferencia)} MÁS de lo declarado`
                                : `🚨 Hay ${formatCOP(Math.abs(diferencia))} MENOS de lo declarado`
                            }
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
              
              {/* MOSTRAR RESÚMENES SOLO SI YA CONFIRMÓ EL EFECTIVO */}
              {efectivoReceptorConfirmado && (
                <>
                  {/* Resumen de ventas al precierre */}
                  <div style={{
                    padding: '14px',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #22c55e',
                    borderRadius: '10px',
                    marginBottom: '12px',
                    fontSize: '12px'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#166534', fontSize: '13px' }}>💰 VENTAS AL MOMENTO DEL PRECIERRE</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                      <span>Efectivo: <strong>{formatCOP(totales.ventaEfectivo)}</strong></span>
                      <span>Bancolombia: <strong>{formatCOP(totales.bancolombia)}</strong></span>
                      <span>Davivienda: <strong>{formatCOP(totales.davivienda)}</strong></span>
                      <span>Daviplata: <strong>{formatCOP(totales.daviplata)}</strong></span>
                      <span>Nequi: <strong>{formatCOP(totales.nequi)}</strong></span>
                      <span>Datáfono: <strong>{formatCOP(totales.totalDatafonos)}</strong></span>
                      <span>Online Rappi: <strong>{formatCOP(totales.onlineRappi)}</strong></span>
                    </div>
                    <div style={{ marginTop: '8px', fontWeight: '700', color: '#166534', fontSize: '14px', textAlign: 'right' }}>
                      TOTAL VENTAS: {formatCOP(totales.totalVentas)}
                    </div>
                  </div>
                  
                  {/* Resumen de facturas al precierre */}
                  <div style={{
                    padding: '14px',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #f87171',
                    borderRadius: '10px',
                    marginBottom: '16px',
                    fontSize: '12px'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#991b1b', fontSize: '13px' }}>📋 FACTURAS AL MOMENTO DEL PRECIERRE</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                      <div style={{ padding: '8px', backgroundColor: '#fef3c7', borderRadius: '6px', textAlign: 'center', border: '1px solid #f59e0b' }}>
                        <div style={{ fontSize: '10px', color: '#92400e', marginBottom: '2px' }}>⏳ Pendientes</div>
                        <strong style={{ color: '#ea580c', fontSize: '13px' }}>{formatCOP(totales.totalFacturasPendientes)}</strong>
                      </div>
                      <div style={{ padding: '8px', backgroundColor: '#d1fae5', borderRadius: '6px', textAlign: 'center', border: '1px solid #10b981' }}>
                        <div style={{ fontSize: '10px', color: '#065f46', marginBottom: '2px' }}>💵 Pagadas Caja</div>
                        <strong style={{ color: '#166534', fontSize: '13px' }}>{formatCOP(totales.totalFacturasCaja)}</strong>
                      </div>
                      <div style={{ padding: '8px', backgroundColor: '#dbeafe', borderRadius: '6px', textAlign: 'center', border: '1px solid #3b82f6' }}>
                        <div style={{ fontSize: '10px', color: '#1e40af', marginBottom: '2px' }}>🏦 Pagadas Transfer</div>
                        <strong style={{ color: '#0369a1', fontSize: '13px' }}>{formatCOP(totales.totalFacturasTransferencia)}</strong>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {/* Mensaje de confirmación */}
              <div style={{
                padding: '16px',
                backgroundColor: '#e0f2fe',
                border: '2px solid #0ea5e9',
                borderRadius: '12px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#0c4a6e' }}>
                  <strong>Al confirmar</strong>, asumes la responsabilidad del cuadre desde este momento.
                  <br/>
                  El turno de <strong>{p.porNombre}</strong> quedará registrado como cerrado correctamente.
                </p>
              </div>
              
              {/* Lista de empleados con acceso al cuadre */}
              {(() => {
                // Usar solo los empleados con acceso AUTORIZADO (solicitud aprobada)
                const empleadosConAccesoAutorizado = accesosDelCuadre
                  .filter(a => a.estado === 'aprobado')
                  .map(a => ({
                    id: a.solicitante_id,
                    nombre: a.solicitante_nombre,
                    fechaAprobacion: a.fecha_respuesta
                  }));
                  
                const empleadoQueHizoPrecierre = empleados.find(e => e.id === p.porId);
                const empleadoReceptor = empleados.find(e => e.id === p.asignadoAId);
                
                // Filtrar los que no son el que hizo precierre ni el receptor
                const otrosEmpleadosConAcceso = empleadosConAccesoAutorizado.filter(
                  e => e.id !== p.porId && e.id !== p.asignadoAId
                );
                
                return (
                  <div style={{
                    padding: '14px',
                    backgroundColor: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    borderRadius: '10px',
                    marginBottom: '16px',
                    fontSize: '12px'
                  }}>
                    <strong style={{ color: '#475569', fontSize: '13px' }}>👥 Información de empleados:</strong>
                    
                    {/* Quién hizo el precierre y quién recibe */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '1fr 1fr', 
                      gap: '10px', 
                      marginTop: '10px',
                      marginBottom: '12px'
                    }}>
                      <div style={{
                        padding: '8px 12px',
                        backgroundColor: '#fef3c7',
                        borderRadius: '8px',
                        border: '1px solid #f59e0b'
                      }}>
                        <div style={{ fontSize: '10px', color: '#92400e', fontWeight: '600' }}>📤 TURNO SALIENTE</div>
                        <div style={{ fontSize: '13px', color: '#78350f', fontWeight: '700', marginTop: '2px' }}>
                          {empleadoQueHizoPrecierre?.nombre || p.porNombre}
                        </div>
                        <div style={{ fontSize: '10px', color: '#a16207' }}>
                          {empleadoQueHizoPrecierre?.perfil || ""}
                        </div>
                      </div>
                      <div style={{
                        padding: '8px 12px',
                        backgroundColor: '#dcfce7',
                        borderRadius: '8px',
                        border: '1px solid #22c55e'
                      }}>
                        <div style={{ fontSize: '10px', color: '#166534', fontWeight: '600' }}>📥 TURNO ENTRANTE (TÚ)</div>
                        <div style={{ fontSize: '13px', color: '#14532d', fontWeight: '700', marginTop: '2px' }}>
                          {empleadoReceptor?.nombre || p.asignadoANombre}
                        </div>
                        <div style={{ fontSize: '10px', color: '#15803d' }}>
                          {empleadoReceptor?.perfil || ""}
                        </div>
                      </div>
                    </div>
                    
                    {/* Lista de otros empleados con acceso AUTORIZADO */}
                    <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '6px' }}>
                      🔐 Otros empleados con acceso autorizado a este cuadre:
                    </div>
                    {otrosEmpleadosConAcceso.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        {otrosEmpleadosConAcceso.slice(0, 8).map(e => (
                          <span key={e.id} style={{
                            padding: '3px 8px',
                            backgroundColor: '#e2e8f0',
                            borderRadius: '10px',
                            fontSize: '10px',
                            color: '#334155'
                          }}>
                            {e.nombre}
                          </span>
                        ))}
                        {otrosEmpleadosConAcceso.length > 8 && (
                          <span style={{ fontSize: '10px', color: '#64748b' }}>
                            +{otrosEmpleadosConAcceso.length - 8} más
                          </span>
                        )}
                      </div>
                    ) : (
                      <div style={{ 
                        padding: '8px 12px',
                        backgroundColor: '#fef2f2',
                        borderRadius: '6px',
                        fontSize: '11px',
                        color: '#991b1b',
                        textAlign: 'center'
                      }}>
                        ⚠️ No hay otros empleados con acceso autorizado a este cuadre
                      </div>
                    )}
                  </div>
                );
              })()}
              
              {/* Opción de reasignación - SOLO GERENCIA y NO el empleado receptor */}
              {esGerente && (empleadoActual?.id || usuarioActual?.id) !== cuadreActivo?.precierre?.asignadoAId && (
                <div style={{
                  padding: '14px',
                  backgroundColor: '#fef3c7',
                  border: '2px dashed #f59e0b',
                  borderRadius: '10px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#92400e', fontSize: '13px' }}>
                    🔄 Reasignar cuadre a otro empleado (Solo Gerencia)
                  </h4>
                  <select
                    value={reasignarACuadre}
                    onChange={(e) => setReasignarACuadre(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #f59e0b',
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">-- Mantener asignación actual --</option>
                    {empleados
                      .filter(e => e.id !== cuadreActivo?.empleadoId)
                      .map(e => (
                        <option key={e.id} value={e.id}>{e.nombre} ({e.perfil || 'Sin perfil'})</option>
                      ))
                    }
                  </select>
                  {reasignarACuadre && (
                    <button
                      onClick={() => {
                        const nuevoEmpleado = empleados.find(e => e.id === reasignarACuadre);
                        if (!nuevoEmpleado) return;
                        
                        const notaReasignacion = `\n\n🔄 **REASIGNACIÓN POR GERENCIA**\n` +
                          `• Fecha: ${new Date().toLocaleString("es-CO")}\n` +
                          `• Reasignado por: ${empleadoActual?.nombre || usuarioActual?.nombre || "Gerencia"}\n` +
                          `• Nuevo responsable: ${nuevoEmpleado.nombre}\n` +
                          `• Motivo: Reasignación durante confirmación de precierre`;
                        
                        actualizarCuadre(cuadreActivo.id, { 
                          empleadoId: reasignarACuadre,
                          precierre: {
                            ...cuadreActivo.precierre,
                            asignadoAId: reasignarACuadre,
                            asignadoANombre: nuevoEmpleado.nombre
                          },
                          comentarioModificacion: (cuadreActivo.comentarioModificacion || "") + notaReasignacion
                        });
                        
                        setReasignarACuadre("");
                        setMostrarAlertaConfirmarPrecierre(false);
                        setCuadreActivoId(null);
                        alert(`✅ Cuadre reasignado a: ${nuevoEmpleado.nombre}\n\nEl empleado deberá confirmar el precierre cuando abra el cuadre.`);
                      }}
                      style={{
                        marginTop: '10px',
                        padding: '10px 20px',
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        width: '100%'
                      }}
                    >
                      🔄 Reasignar a {empleados.find(e => e.id === reasignarACuadre)?.nombre || ""}
                    </button>
                  )}
                </div>
              )}
              
              {/* Botones */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  disabled={!efectivoReceptorConfirmado}
                  onClick={() => {
                    if (!efectivoReceptorConfirmado) {
                      alert("⚠️ Debes confirmar cuánto efectivo recibes antes de continuar.");
                      return;
                    }
                    
                    // Confirmar el precierre
                    const fechaConfirmacion = new Date().toISOString();
                    const nombreConfirmador = empleadoActual?.nombre || usuarioActual?.nombre || "Usuario";
                    
                    // Calcular diferencia entre efectivo saliente y entrante
                    const efectivoSaliente = Number(totales.efectivoEnCaja || 0);
                    const efectivoEntrante = Number(efectivoReceptor || 0);
                    const diferenciaRecepcion = efectivoEntrante - efectivoSaliente;
                    const coincide = Math.abs(diferenciaRecepcion) < 1;
                    
                    const notaConfirmacion = `\n\n✅ **PRECIERRE CONFIRMADO**\n` +
                      `• Fecha: ${new Date().toLocaleString("es-CO")}\n` +
                      `• Confirmado por: ${nombreConfirmador}\n` +
                      `• El turno de ${p.porNombre} fue aceptado como correcto.\n` +
                      `\n💵 **VERIFICACIÓN DE EFECTIVO EN RECEPCIÓN:**\n` +
                      `• Efectivo según turno saliente: ${formatCOP(efectivoSaliente)}\n` +
                      `• Efectivo contado por receptor: ${formatCOP(efectivoEntrante)}\n` +
                      `• Diferencia: ${coincide ? '✅ Coincide' : `${diferenciaRecepcion > 0 ? '+' : ''}${formatCOP(diferenciaRecepcion)}`}\n` +
                      `• Estado: ${coincide ? 'CUADRADO' : 'CON DIFERENCIA'}`;
                    
                    const precierreActualizado = {
                      ...cuadreActivo.precierre,
                      confirmadoPorReceptor: true,
                      fechaConfirmacion: fechaConfirmacion,
                      confirmadoPorNombre: nombreConfirmador,
                      // Guardar verificación de efectivo del receptor
                      verificacionReceptor: {
                        efectivoSaliente: efectivoSaliente,
                        efectivoRecibido: efectivoEntrante,
                        diferencia: diferenciaRecepcion,
                        coincide: coincide,
                        fechaVerificacion: fechaConfirmacion
                      }
                    };
                    
                    actualizarCuadre(cuadreActivo.id, { 
                      precierre: precierreActualizado,
                      precierrePendienteConfirmacion: false,
                      comentarioModificacion: (cuadreActivo.comentarioModificacion || "") + notaConfirmacion
                    });
                    
                    // Resetear estados
                    setEfectivoReceptor("");
                    setEfectivoReceptorConfirmado(false);
                    setMostrarAlertaConfirmarPrecierre(false);
                    
                    const mensajeAlerta = coincide 
                      ? "✅ Has confirmado que el turno anterior está correcto.\n\n✅ El efectivo coincide con lo declarado por el turno saliente.\n\nAhora eres responsable del cuadre desde este momento."
                      : `✅ Has confirmado que el turno anterior está correcto.\n\n⚠️ Hay una diferencia de ${formatCOP(Math.abs(diferenciaRecepcion))} en el efectivo.\n\nAhora eres responsable del cuadre desde este momento.`;
                    
                    alert(mensajeAlerta);
                  }}
                  style={{
                    padding: '14px 32px',
                    background: efectivoReceptorConfirmado 
                      ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                      : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: efectivoReceptorConfirmado ? 'pointer' : 'not-allowed',
                    fontWeight: '700',
                    fontSize: '16px',
                    boxShadow: efectivoReceptorConfirmado 
                      ? '0 4px 12px rgba(34, 197, 94, 0.4)'
                      : 'none',
                    opacity: efectivoReceptorConfirmado ? 1 : 0.7
                  }}
                >
                  {efectivoReceptorConfirmado ? '✅ CUADRE ANTERIOR CORRECTO' : '⏳ Confirma el efectivo primero'}
                </button>
              </div>
              
              <p style={{ 
                textAlign: 'center', 
                marginTop: '16px', 
                fontSize: '11px', 
                color: '#666' 
              }}>
                Si hay algún problema con el turno anterior, comunícalo a tu supervisor antes de confirmar.
              </p>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ====================== MODAL GENÉRICO ====================== */

function ModalCuadre({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        maxWidth: '90%',
        maxHeight: '85vh',
        overflowY: 'auto',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        minWidth: '400px'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '16px',
          borderBottom: '1px solid #e2e8f0',
          paddingBottom: '12px'
        }}>
          <h3 style={{ margin: 0, color: '#1e293b', fontWeight: '700' }}>{title}</h3>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#64748b',
              padding: '4px 8px',
              borderRadius: '4px'
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ====================== MENÚ ACCIONES RÁPIDAS ====================== */

function MenuAccionesCuadre({ 
  cuadreActivo, 
  actualizarCuadre, 
  bloqueado,
  tienePermiso
}) {
  const [modalActivo, setModalActivo] = useState(null);

  const acciones = [
    { 
      id: 'turnos', 
      label: 'Turnos', 
      icon: '👥', 
      color: '#8b5cf6',
      permiso: 'admin_cuadre_turnos',
      activo: cuadreActivo.turnosActivos
    },
    { 
      id: 'adelantos', 
      label: 'Adelantos', 
      icon: '💰', 
      color: '#f59e0b',
      permiso: 'admin_cuadre_adelantos',
      activo: cuadreActivo.adelantosActivos
    },
    { 
      id: 'denominaciones', 
      label: 'Denominaciones', 
      icon: '💵', 
      color: '#10b981',
      permiso: 'admin_cuadre_denominaciones',
      activo: cuadreActivo.denominacionesActivas
    },
    { 
      id: 'novedades', 
      label: 'Novedades', 
      icon: '📝', 
      color: '#ef4444',
      permiso: null, // Siempre visible
      activo: cuadreActivo.novedadesActivas
    },
    { 
      id: 'basesDomicilios', 
      label: 'Bases Domicilios', 
      icon: '🚴', 
      color: '#3b82f6',
      permiso: null, // Siempre visible
      activo: cuadreActivo.basesDomiciliosActivas
    },
    { 
      id: 'ingresosVarios', 
      label: 'Ingresos Varios', 
      icon: '💲', 
      color: '#059669',
      permiso: null, // Siempre visible
      activo: cuadreActivo.ingresosVariosActivos
    },
    { 
      id: 'descuadres', 
      label: 'Descuadres', 
      icon: '⚠️', 
      color: '#dc2626',
      permiso: null, // Siempre visible
      activo: cuadreActivo.ventas?.huboDescuadreTransferencia === 'Sí' || cuadreActivo.ventas?.huboDescuadreDatafono === 'Sí'
    }
  ];

  const renderModalContent = () => {
    switch(modalActivo) {
      case 'turnos':
        return (
          <CuadroTurnosModal
            turnos={cuadreActivo.turnos || []}
            onChange={(turnos) => {
              actualizarCuadre(cuadreActivo.id, { turnosActivos: true, turnos });
            }}
            onClose={() => setModalActivo(null)}
            disabled={bloqueado}
          />
        );
      case 'adelantos':
        return (
          <CuadroAdelantosModal
            adelantos={cuadreActivo.adelantos || []}
            onChange={(adelantos) => {
              actualizarCuadre(cuadreActivo.id, { adelantosActivos: true, adelantos });
            }}
            onClose={() => setModalActivo(null)}
            disabled={bloqueado}
          />
        );
      case 'denominaciones':
        return (
          <CuadroDenominacionesModal
            denoms={cuadreActivo.denoms || DENOMINACIONES.map((v) => ({ valor: v, cantidad: 0, subtotal: 0 }))}
            onChange={(denoms) => {
              actualizarCuadre(cuadreActivo.id, { denominacionesActivas: true, denoms });
            }}
            onClose={() => setModalActivo(null)}
            disabled={bloqueado}
          />
        );
      case 'novedades':
        return (
          <CuadroNovedadesModal
            novedades={cuadreActivo.novedades || []}
            onChange={(novedades) => {
              actualizarCuadre(cuadreActivo.id, { novedadesActivas: true, novedades });
            }}
            onClose={() => setModalActivo(null)}
            disabled={bloqueado}
          />
        );
      case 'basesDomicilios':
        return (
          <CuadroBasesDomiciliosModal
            basesDomicilios={cuadreActivo.basesDomicilios || []}
            onChange={(basesDomicilios) => {
              actualizarCuadre(cuadreActivo.id, { basesDomiciliosActivas: true, basesDomicilios });
            }}
            onClose={() => setModalActivo(null)}
            disabled={bloqueado}
          />
        );
      case 'ingresosVarios':
        return (
          <CuadroIngresosVariosModal
            ingresosVarios={cuadreActivo.ingresosVarios || []}
            onChange={(ingresosVarios) => {
              actualizarCuadre(cuadreActivo.id, { ingresosVariosActivos: true, ingresosVarios });
            }}
            onClose={() => setModalActivo(null)}
            disabled={bloqueado}
          />
        );
      case 'descuadres':
        return (
          <CuadroDescuadresModal
            cuadreActivo={cuadreActivo}
            actualizarCuadre={actualizarCuadre}
            onClose={() => setModalActivo(null)}
            disabled={bloqueado}
          />
        );
      default:
        return null;
    }
  };

  const getTituloModal = () => {
    switch(modalActivo) {
      case 'turnos': return '👥 Pago de Turnos';
      case 'adelantos': return '💰 Adelantos';
      case 'denominaciones': return '💵 Denominaciones de Efectivo';
      case 'novedades': return '📝 Novedades';
      case 'basesDomicilios': return '🚴 Bases Domicilios';
      case 'ingresosVarios': return '💲 Ingresos Varios';
      case 'descuadres': return '⚠️ Descuadres de Caja';
      default: return '';
    }
  };

  return (
    <>
      <div style={{
        marginBottom: '16px',
        padding: '16px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#64748b',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          ⚡ Acciones rápidas:
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '10px'
        }}>
          {acciones.map((accion) => {
            if (accion.permiso && !tienePermiso(accion.permiso)) return null;
            
            return (
              <button
                key={accion.id}
                onClick={() => !bloqueado && setModalActivo(accion.id)}
                disabled={bloqueado}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '12px 8px',
                  borderRadius: '10px',
                  border: `2px solid ${accion.activo ? accion.color : '#cbd5e1'}`,
                  backgroundColor: accion.activo ? `${accion.color}15` : 'white',
                  color: accion.activo ? accion.color : '#475569',
                  fontWeight: '600',
                  fontSize: '11px',
                  cursor: bloqueado ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: bloqueado ? 0.6 : 1,
                  minHeight: '70px',
                  position: 'relative'
                }}
              >
                <span style={{ fontSize: '20px' }}>{accion.icon}</span>
                <span style={{ textAlign: 'center' }}>{accion.label}</span>
                {accion.activo && (
                  <span style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    backgroundColor: accion.color,
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontSize: '9px'
                  }}>
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <ModalCuadre 
        isOpen={modalActivo !== null} 
        onClose={() => setModalActivo(null)}
        title={getTituloModal()}
      >
        {renderModalContent()}
      </ModalCuadre>
    </>
  );
}

/* ====================== COMPONENTES MODAL INTERNOS ====================== */

function CuadroTurnosModal({ turnos, onChange, onClose, disabled }) {
  const [data, setData] = useState(turnos.length > 0 ? turnos : [
    { id: uid(), empleado: "", valor: "", pagado: false }
  ]);

  const total = useMemo(
    () => data.reduce((s, t) => s + Number(t.valor || 0), 0),
    [data]
  );

  function cambiar(idx, campo, valor) {
    const copia = data.map((t, i) =>
      i === idx ? { ...t, [campo]: valor } : t
    );
    setData(copia);
  }

  function agregarFila() {
    setData([...data, { id: uid(), empleado: "", valor: "", pagado: false }]);
  }

  function guardar() {
    onChange(data);
    onClose();
  }

  return (
    <div>
      <table className="table" style={{ marginBottom: '16px' }}>
        <thead>
          <tr>
            <th>Empleado</th>
            <th>Valor turno</th>
            <th>¿Pagado?</th>
          </tr>
        </thead>
        <tbody>
          {data.map((t, idx) => (
            <tr key={t.id}>
              <td>
                <input
                  className="input"
                  disabled={disabled}
                  value={t.empleado}
                  onChange={(e) => cambiar(idx, "empleado", e.target.value)}
                  placeholder="Nombre empleado"
                />
              </td>
              <td>
                <input
                  className="input"
                  type="number"
                  disabled={disabled}
                  value={t.valor}
                  onChange={(e) => cambiar(idx, "valor", e.target.value)}
                  placeholder="0"
                />
              </td>
              <td>
                <select
                  className="input"
                  disabled={disabled}
                  value={t.pagado ? "Sí" : "No"}
                  onChange={(e) => cambiar(idx, "pagado", e.target.value === "Sí")}
                >
                  <option value="No">No</option>
                  <option value="Sí">Sí</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '12px',
        backgroundColor: '#f0fdf4',
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        <Button variant="outline" onClick={agregarFila} disabled={disabled}>
          + Agregar turno
        </Button>
        <span style={{ fontWeight: '700', color: '#166534' }}>
          Total: {new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(total)}
        </span>
      </div>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button variant="solid" onClick={guardar} disabled={disabled}>
          💾 Guardar turnos
        </Button>
      </div>
    </div>
  );
}

function CuadroAdelantosModal({ adelantos, onChange, onClose, disabled }) {
  const [data, setData] = useState(adelantos.length > 0 ? adelantos : [
    { id: uid(), empleado: "", valor: "" }
  ]);

  const total = useMemo(
    () => data.reduce((s, a) => s + Number(a.valor || 0), 0),
    [data]
  );

  function cambiar(idx, campo, valor) {
    const copia = data.map((a, i) =>
      i === idx ? { ...a, [campo]: valor } : a
    );
    setData(copia);
  }

  function agregarFila() {
    setData([...data, { id: uid(), empleado: "", valor: "" }]);
  }

  function guardar() {
    onChange(data);
    onClose();
  }

  return (
    <div>
      <table className="table" style={{ marginBottom: '16px' }}>
        <thead>
          <tr>
            <th>Empleado</th>
            <th>Valor adelanto</th>
          </tr>
        </thead>
        <tbody>
          {data.map((a, idx) => (
            <tr key={a.id}>
              <td>
                <input
                  className="input"
                  disabled={disabled}
                  value={a.empleado}
                  onChange={(e) => cambiar(idx, "empleado", e.target.value)}
                  placeholder="Nombre empleado"
                />
              </td>
              <td>
                <input
                  className="input"
                  type="number"
                  disabled={disabled}
                  value={a.valor}
                  onChange={(e) => cambiar(idx, "valor", e.target.value)}
                  placeholder="0"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '12px',
        backgroundColor: '#fef3c7',
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        <Button variant="outline" onClick={agregarFila} disabled={disabled}>
          + Agregar adelanto
        </Button>
        <span style={{ fontWeight: '700', color: '#92400e' }}>
          Total: {new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(total)}
        </span>
      </div>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button variant="solid" onClick={guardar} disabled={disabled}>
          💾 Guardar adelantos
        </Button>
      </div>
    </div>
  );
}

function CuadroDenominacionesModal({ denoms, onChange, onClose, disabled }) {
  const [data, setData] = useState(denoms);

  const total = useMemo(
    () => data.reduce((s, d) => s + Number(d.subtotal || 0), 0),
    [data]
  );

  function cambiar(idx, cantidad) {
    const cant = Number(cantidad) || 0;
    const copia = data.map((d, i) =>
      i === idx ? { ...d, cantidad: cant, subtotal: cant * d.valor } : d
    );
    setData(copia);
  }

  function guardar() {
    onChange(data);
    onClose();
  }

  return (
    <div>
      <p className="muted" style={{ marginBottom: '12px' }}>
        Ingresa la cantidad de billetes/monedas de cada denominación (COP).
      </p>
      <table className="table" style={{ marginBottom: '16px', maxWidth: '400px' }}>
        <thead>
          <tr>
            <th>Denominación</th>
            <th>Cantidad</th>
            <th>Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d, idx) => (
            <tr key={d.valor}>
              <td style={{ fontWeight: '600' }}>
                {new Intl.NumberFormat("es-CO").format(d.valor)}
              </td>
              <td>
                <input
                  className="input"
                  type="number"
                  disabled={disabled}
                  value={d.cantidad || ""}
                  onChange={(e) => cambiar(idx, e.target.value)}
                  style={{ width: '80px' }}
                />
              </td>
              <td style={{ fontWeight: '500' }}>
                {new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(d.subtotal || 0)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        alignItems: 'center',
        padding: '12px',
        backgroundColor: '#f0fdf4',
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        <span style={{ fontWeight: '700', color: '#166534', fontSize: '1.1em' }}>
          TOTAL EFECTIVO: {new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(total)}
        </span>
      </div>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button variant="solid" onClick={guardar} disabled={disabled}>
          💾 Guardar denominaciones
        </Button>
      </div>
    </div>
  );
}

function CuadroNovedadesModal({ novedades, onChange, onClose, disabled }) {
  const [data, setData] = useState(novedades.length > 0 ? novedades : [
    { id: uid(), descripcion: "", tipo: "Información" }
  ]);

  function cambiar(idx, campo, valor) {
    const copia = data.map((n, i) =>
      i === idx ? { ...n, [campo]: valor } : n
    );
    setData(copia);
  }

  function agregarFila() {
    setData([...data, { id: uid(), descripcion: "", tipo: "Información" }]);
  }

  function guardar() {
    onChange(data);
    onClose();
  }

  return (
    <div>
      <table className="table" style={{ marginBottom: '16px' }}>
        <thead>
          <tr>
            <th style={{ width: '70%' }}>Descripción</th>
            <th>Tipo</th>
          </tr>
        </thead>
        <tbody>
          {data.map((n, idx) => (
            <tr key={n.id}>
              <td>
                <textarea
                  className="textarea"
                  disabled={disabled}
                  value={n.descripcion}
                  onChange={(e) => cambiar(idx, "descripcion", e.target.value)}
                  placeholder="Describe la novedad..."
                  rows={2}
                  style={{ maxWidth: '100%' }}
                />
              </td>
              <td>
                <select
                  className="input"
                  disabled={disabled}
                  value={n.tipo}
                  onChange={(e) => cambiar(idx, "tipo", e.target.value)}
                >
                  <option>Información</option>
                  <option>Alerta</option>
                  <option>Urgente</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginBottom: '16px' }}>
        <Button variant="outline" onClick={agregarFila} disabled={disabled}>
          + Agregar novedad
        </Button>
      </div>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button variant="solid" onClick={guardar} disabled={disabled}>
          💾 Guardar novedades
        </Button>
      </div>
    </div>
  );
}

// 🔹 Modal para Bases de Domicilios
function CuadroBasesDomiciliosModal({ basesDomicilios, onChange, onClose, disabled }) {
  const [data, setData] = useState(basesDomicilios.length > 0 ? basesDomicilios : []);
  const [mostrarFormNuevo, setMostrarFormNuevo] = useState(false);
  const [nuevoEmpleado, setNuevoEmpleado] = useState("");
  const [nuevoValorBase, setNuevoValorBase] = useState("");
  const [mostrarAbono, setMostrarAbono] = useState(null); // ID del empleado para mostrar modal de abono
  const [valorAbono, setValorAbono] = useState("");

  // Formatear moneda
  const formatCOP = (val) => new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0
  }).format(val || 0);

  // Calcular saldo de cada base
  const calcularSaldo = (base) => {
    const totalAbonos = (base.abonos || []).reduce((sum, a) => sum + Number(a.valor || 0), 0);
    return Number(base.valorBase || 0) - totalAbonos;
  };

  // Total de bases pendientes
  const totalBasesPendientes = useMemo(() => {
    return data.reduce((sum, base) => sum + calcularSaldo(base), 0);
  }, [data]);

  // Agregar nueva base
  const agregarBase = () => {
    if (!nuevoEmpleado.trim()) {
      alert("⚠️ Debe ingresar el nombre del empleado domiciliario.");
      return;
    }
    if (!nuevoValorBase || Number(nuevoValorBase) <= 0) {
      alert("⚠️ Debe ingresar el valor de la base.");
      return;
    }

    const nuevaBase = {
      id: uid(),
      empleado: nuevoEmpleado.trim(),
      valorBase: Number(nuevoValorBase),
      fecha: new Date().toISOString(),
      abonos: []
    };

    setData([...data, nuevaBase]);
    setNuevoEmpleado("");
    setNuevoValorBase("");
    setMostrarFormNuevo(false);
  };

  // Agregar abono a una base
  const agregarAbono = (baseId) => {
    if (!valorAbono || Number(valorAbono) <= 0) {
      alert("⚠️ Debe ingresar el valor del abono.");
      return;
    }

    const base = data.find(b => b.id === baseId);
    const saldoActual = calcularSaldo(base);

    if (Number(valorAbono) > saldoActual) {
      alert(`⚠️ El abono no puede ser mayor al saldo pendiente (${formatCOP(saldoActual)}).`);
      return;
    }

    const nuevoAbono = {
      id: uid(),
      valor: Number(valorAbono),
      fecha: new Date().toISOString()
    };

    setData(data.map(b => 
      b.id === baseId 
        ? { ...b, abonos: [...(b.abonos || []), nuevoAbono] }
        : b
    ));

    setValorAbono("");
    setMostrarAbono(null);
  };

  // Saldar completamente una base
  const saldarBase = (baseId) => {
    const base = data.find(b => b.id === baseId);
    const saldoActual = calcularSaldo(base);

    if (saldoActual <= 0) {
      alert("✅ Esta base ya está saldada.");
      return;
    }

    const abonoTotal = {
      id: uid(),
      valor: saldoActual,
      fecha: new Date().toISOString(),
      esSaldoTotal: true
    };

    setData(data.map(b => 
      b.id === baseId 
        ? { ...b, abonos: [...(b.abonos || []), abonoTotal] }
        : b
    ));
  };

  // Eliminar base
  const eliminarBase = (baseId) => {
    if (!window.confirm("¿Eliminar esta base de domicilio?")) return;
    setData(data.filter(b => b.id !== baseId));
  };

  function guardar() {
    onChange(data);
    onClose();
  }

  return (
    <div>
      {/* Resumen de bases pendientes */}
      <div style={{
        padding: '12px',
        backgroundColor: totalBasesPendientes > 0 ? '#fef3c7' : '#d1fae5',
        borderRadius: '8px',
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{ fontWeight: '600', color: totalBasesPendientes > 0 ? '#92400e' : '#065f46' }}>
          {totalBasesPendientes > 0 
            ? `⚠️ Bases pendientes: ${formatCOP(totalBasesPendientes)}`
            : '✅ Todas las bases están saldadas'
          }
        </span>
        <span style={{ fontSize: '12px', color: '#666' }}>
          {data.length} base(s) registrada(s)
        </span>
      </div>

      {/* Lista de bases */}
      {data.length > 0 && (
        <div style={{ marginBottom: '16px', maxHeight: '300px', overflowY: 'auto' }}>
          {data.map(base => {
            const saldo = calcularSaldo(base);
            const estaSaldada = saldo <= 0;

            return (
              <div key={base.id} style={{
                padding: '12px',
                marginBottom: '8px',
                borderRadius: '8px',
                border: `2px solid ${estaSaldada ? '#10b981' : '#f59e0b'}`,
                backgroundColor: estaSaldada ? '#ecfdf5' : '#fffbeb'
              }}>
                {/* Header de la base */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div>
                    <span style={{ fontWeight: '700', color: '#1f2937' }}>🚴 {base.empleado}</span>
                    <span style={{ marginLeft: '12px', fontSize: '12px', color: '#6b7280' }}>
                      Base: {formatCOP(base.valorBase)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '700',
                      backgroundColor: estaSaldada ? '#10b981' : '#f59e0b',
                      color: 'white'
                    }}>
                      {estaSaldada ? '✓ Saldado' : `Saldo: ${formatCOP(saldo)}`}
                    </span>
                    {!disabled && !estaSaldada && (
                      <>
                        <button
                          onClick={() => {
                            setMostrarAbono(base.id);
                            setValorAbono("");
                          }}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: 'none',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            fontSize: '11px',
                            cursor: 'pointer'
                          }}
                        >
                          💵 Abonar
                        </button>
                        <button
                          onClick={() => saldarBase(base.id)}
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            border: 'none',
                            backgroundColor: '#10b981',
                            color: 'white',
                            fontSize: '11px',
                            cursor: 'pointer'
                          }}
                        >
                          ✓ Saldar Todo
                        </button>
                      </>
                    )}
                    {!disabled && (
                      <button
                        onClick={() => eliminarBase(base.id)}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: 'none',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          fontSize: '11px',
                          cursor: 'pointer'
                        }}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>

                {/* Modal de abono inline */}
                {mostrarAbono === base.id && (
                  <div style={{
                    padding: '10px',
                    backgroundColor: '#e0f2fe',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center'
                  }}>
                    <input
                      type="number"
                      placeholder="Valor del abono"
                      value={valorAbono}
                      onChange={(e) => setValorAbono(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ddd'
                      }}
                    />
                    <button
                      onClick={() => agregarAbono(base.id)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: 'none',
                        backgroundColor: '#10b981',
                        color: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      ✓ Confirmar
                    </button>
                    <button
                      onClick={() => setMostrarAbono(null)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '4px',
                        border: 'none',
                        backgroundColor: '#e5e7eb',
                        color: '#374151',
                        cursor: 'pointer'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* Historial de transacciones */}
                {(base.abonos || []).length > 0 && (
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #d1d5db' }}>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280' }}>📋 Transacciones:</span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                      {base.abonos.map((abono, idx) => (
                        <div key={abono.id} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '11px',
                          color: '#374151',
                          padding: '4px 8px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '4px'
                        }}>
                          <span>
                            {abono.esSaldoTotal ? '✓ Saldo total' : `Abono #${idx + 1}`}
                          </span>
                          <span style={{ fontWeight: '600', color: '#10b981' }}>
                            -{formatCOP(abono.valor)}
                          </span>
                          <span style={{ color: '#9ca3af' }}>
                            {new Date(abono.fecha).toLocaleTimeString("es-CO", { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Formulario para agregar nueva base */}
      {mostrarFormNuevo ? (
        <div style={{
          padding: '12px',
          backgroundColor: '#f0f9ff',
          borderRadius: '8px',
          marginBottom: '16px',
          border: '1px solid #bae6fd'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '12px', color: '#0369a1' }}>
            ➕ Nueva Base de Domicilio
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <input
              type="text"
              placeholder="Nombre del domiciliario"
              value={nuevoEmpleado}
              onChange={(e) => setNuevoEmpleado(e.target.value)}
              style={{
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid #ddd'
              }}
            />
            <input
              type="number"
              placeholder="Valor de la base ($)"
              value={nuevoValorBase}
              onChange={(e) => setNuevoValorBase(e.target.value)}
              style={{
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid #ddd'
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setMostrarFormNuevo(false);
                setNuevoEmpleado("");
                setNuevoValorBase("");
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={agregarBase}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#3b82f6',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              ✓ Agregar Base
            </button>
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: '16px' }}>
          <Button variant="outline" onClick={() => setMostrarFormNuevo(true)} disabled={disabled}>
            + Agregar base de domicilio
          </Button>
        </div>
      )}

      {/* Botones de acción */}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button variant="solid" onClick={guardar} disabled={disabled}>
          💾 Guardar bases
        </Button>
      </div>
    </div>
  );
}

// 🔹 Modal para Ingresos Varios (venta de materia prima, etc.)
function CuadroIngresosVariosModal({ ingresosVarios, onChange, onClose, disabled }) {
  const [data, setData] = useState(ingresosVarios.length > 0 ? ingresosVarios : []);
  const [mostrarFormNuevo, setMostrarFormNuevo] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  
  // Campos del formulario
  const [concepto, setConcepto] = useState("");
  const [cantidad, setCantidad] = useState("");
  const [cantidadLetras, setCantidadLetras] = useState("");
  const [valor, setValor] = useState("");

  // Formatear moneda
  const formatCOP = (val) => new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0
  }).format(val || 0);

  // Total de ingresos varios
  const totalIngresosVarios = useMemo(() => {
    return data.reduce((sum, ingreso) => sum + Number(ingreso.valor || 0), 0);
  }, [data]);

  // Convertir número a letras (simplificado)
  const numeroALetras = (num) => {
    const unidades = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
    const decenas = ['', 'diez', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
    const especiales = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
    
    const n = parseInt(num) || 0;
    if (n === 0) return 'cero';
    if (n < 10) return unidades[n];
    if (n < 20) return especiales[n - 10];
    if (n < 100) {
      const u = n % 10;
      const d = Math.floor(n / 10);
      return decenas[d] + (u ? ' y ' + unidades[u] : '');
    }
    return n.toString();
  };

  // Cuando cambia la cantidad, actualizar las letras
  const handleCantidadChange = (val) => {
    setCantidad(val);
    if (val && !isNaN(val)) {
      setCantidadLetras(numeroALetras(val));
    }
  };

  // Limpiar formulario
  const limpiarFormulario = () => {
    setConcepto("");
    setCantidad("");
    setCantidadLetras("");
    setValor("");
    setEditandoId(null);
    setMostrarFormNuevo(false);
  };

  // Agregar o actualizar ingreso
  const guardarIngreso = () => {
    if (!concepto.trim()) {
      alert("⚠️ Debe ingresar el concepto del ingreso.");
      return;
    }
    if (!cantidad || Number(cantidad) <= 0) {
      alert("⚠️ Debe ingresar la cantidad.");
      return;
    }
    if (!valor || Number(valor) <= 0) {
      alert("⚠️ Debe ingresar el valor.");
      return;
    }

    if (editandoId) {
      // Actualizar existente
      setData(data.map(ing => 
        ing.id === editandoId 
          ? { ...ing, concepto: concepto.trim(), cantidad: Number(cantidad), cantidadLetras: cantidadLetras, valor: Number(valor) }
          : ing
      ));
    } else {
      // Agregar nuevo
      const nuevoIngreso = {
        id: uid(),
        concepto: concepto.trim(),
        cantidad: Number(cantidad),
        cantidadLetras: cantidadLetras,
        valor: Number(valor),
        fecha: new Date().toISOString()
      };
      setData([...data, nuevoIngreso]);
    }

    limpiarFormulario();
  };

  // Editar ingreso
  const editarIngreso = (ingreso) => {
    setConcepto(ingreso.concepto);
    setCantidad(ingreso.cantidad.toString());
    setCantidadLetras(ingreso.cantidadLetras || numeroALetras(ingreso.cantidad));
    setValor(ingreso.valor.toString());
    setEditandoId(ingreso.id);
    setMostrarFormNuevo(true);
  };

  // Eliminar ingreso
  const eliminarIngreso = (ingresoId) => {
    if (!window.confirm("¿Eliminar este ingreso?")) return;
    setData(data.filter(ing => ing.id !== ingresoId));
  };

  function guardar() {
    onChange(data);
    onClose();
  }

  return (
    <div>
      {/* Resumen de ingresos */}
      <div style={{
        padding: '12px',
        backgroundColor: totalIngresosVarios > 0 ? '#d1fae5' : '#f3f4f6',
        borderRadius: '8px',
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{ fontWeight: '600', color: totalIngresosVarios > 0 ? '#065f46' : '#6b7280' }}>
          {totalIngresosVarios > 0 
            ? `💲 Total Ingresos Varios: ${formatCOP(totalIngresosVarios)}`
            : '📝 No hay ingresos varios registrados'
          }
        </span>
        <span style={{ fontSize: '12px', color: '#666' }}>
          {data.length} ingreso(s)
        </span>
      </div>

      {/* Lista de ingresos */}
      {data.length > 0 && (
        <div style={{ marginBottom: '16px', maxHeight: '250px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f1f5f9' }}>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Concepto</th>
                <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Cantidad</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>En letras</th>
                <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Valor</th>
                <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.map(ingreso => (
                <tr key={ingreso.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '8px' }}>{ingreso.concepto}</td>
                  <td style={{ padding: '8px', textAlign: 'center', fontWeight: '600' }}>{ingreso.cantidad}</td>
                  <td style={{ padding: '8px', fontStyle: 'italic', color: '#6b7280' }}>{ingreso.cantidadLetras || '-'}</td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#059669' }}>{formatCOP(ingreso.valor)}</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <button
                      onClick={() => editarIngreso(ingreso)}
                      disabled={disabled}
                      style={{
                        padding: '4px 8px',
                        marginRight: '4px',
                        border: '1px solid #3b82f6',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        color: '#3b82f6',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        fontSize: '11px'
                      }}
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => eliminarIngreso(ingreso.id)}
                      disabled={disabled}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #ef4444',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        color: '#ef4444',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        fontSize: '11px'
                      }}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Formulario para agregar/editar */}
      {mostrarFormNuevo ? (
        <div style={{
          padding: '16px',
          backgroundColor: '#f0fdf4',
          borderRadius: '8px',
          marginBottom: '16px',
          border: '2px solid #10b981'
        }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#065f46' }}>
            {editandoId ? '✏️ Modificar Ingreso' : '➕ Agregar Ingreso'}
          </h4>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>
              Concepto (ej: Venta de materia prima)
            </label>
            <input
              type="text"
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              placeholder="Describa el concepto del ingreso"
              disabled={disabled}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>
                Cantidad (número)
              </label>
              <input
                type="number"
                value={cantidad}
                onChange={(e) => handleCantidadChange(e.target.value)}
                placeholder="0"
                disabled={disabled}
                min="1"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>
                Cantidad en letras
              </label>
              <input
                type="text"
                value={cantidadLetras}
                onChange={(e) => setCantidadLetras(e.target.value)}
                placeholder="(automático o escriba manualmente)"
                disabled={disabled}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontStyle: 'italic'
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>
              Valor ($)
            </label>
            <input
              type="number"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder="0"
              disabled={disabled}
              min="1"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={limpiarFormulario}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={guardarIngreso}
              disabled={disabled}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#059669',
                color: 'white',
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontWeight: '600'
              }}
            >
              {editandoId ? '✓ Actualizar' : '✓ Agregar Ingreso'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: '16px' }}>
          <Button variant="outline" onClick={() => setMostrarFormNuevo(true)} disabled={disabled}>
            + Agregar ingreso
          </Button>
        </div>
      )}

      {/* Botones de acción */}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button variant="solid" onClick={guardar} disabled={disabled}>
          💾 Guardar ingresos
        </Button>
      </div>
    </div>
  );
}

// 🔹 Modal para gestionar Descuadres (Transferencia y Datáfono)
function CuadroDescuadresModal({ cuadreActivo, actualizarCuadre, onClose, disabled }) {
  const ventas = cuadreActivo.ventas || {};
  
  // Formatear moneda
  const formatCOP = (val) => new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0
  }).format(val || 0);

  // Calcular el sobrante automáticamente
  const totalFacturasCaja = useMemo(() => {
    return (cuadreActivo.facturas || [])
      .filter((f) => f.estado === "Pagada de caja")
      .reduce((s, f) => s + Number(f.valor || 0), 0);
  }, [cuadreActivo.facturas]);

  const totalDomicilios = useMemo(() => {
    // Los domicilios incluyen: valor del domicilio + cobro de datafono
    return (cuadreActivo.domicilios || [])
      .reduce((s, d) => s + Number(d.valor || 0) + Number(d.cobroDatafono || 0), 0);
  }, [cuadreActivo.domicilios]);

  const totalIngresosVarios = useMemo(() => {
    return (cuadreActivo.ingresosVarios || [])
      .reduce((s, ing) => s + Number(ing.valor || 0), 0);
  }, [cuadreActivo.ingresosVarios]);

  const sobranteEfectivoCalculado = Number(ventas.ventaEfectivo || 0) - totalFacturasCaja - totalDomicilios + totalIngresosVarios;

  const colorSobrante = sobranteEfectivoCalculado === 0 
    ? { bg: '#f0fdf4', color: '#166534' }
    : sobranteEfectivoCalculado > 0 
      ? { bg: '#ecfdf5', color: '#047857' }
      : { bg: '#fef2f2', color: '#b91c1c' };

  const descuadreEfectivoAuto = sobranteEfectivoCalculado === 0 ? "No" : "Sí";
  const tipoDescuadreEfectivo = sobranteEfectivoCalculado > 0 ? "Sobrante en efectivo" : sobranteEfectivoCalculado < 0 ? "Faltante en efectivo" : "";

  // Estado local para los campos
  const [huboDescuadreTransferencia, setHuboDescuadreTransferencia] = useState(ventas.huboDescuadreTransferencia || "");
  const [valorDescuadreTransferencia, setValorDescuadreTransferencia] = useState(ventas.valorDescuadreTransferencia || "");
  const [numeroPedidoDescuadreTransferencia, setNumeroPedidoDescuadreTransferencia] = useState(ventas.numeroPedidoDescuadreTransferencia || "");
  const [bancoDescuadreTransferencia, setBancoDescuadreTransferencia] = useState(ventas.bancoDescuadreTransferencia || "");
  const [huboDescuadreDatafono, setHuboDescuadreDatafono] = useState(ventas.huboDescuadreDatafono || "");
  const [valorDescuadreDatafono, setValorDescuadreDatafono] = useState(ventas.valorDescuadreDatafono || "");

  const guardar = () => {
    // Validar si hay descuadre de transferencia
    if (huboDescuadreTransferencia === "Sí") {
      if (!valorDescuadreTransferencia || Number(valorDescuadreTransferencia) <= 0) {
        alert("⚠️ Debe ingresar el valor del descuadre de transferencia.");
        return;
      }
      if (!numeroPedidoDescuadreTransferencia.trim()) {
        alert("⚠️ Debe ingresar el número de pedido del descuadre de transferencia.");
        return;
      }
      if (!bancoDescuadreTransferencia) {
        alert("⚠️ Debe seleccionar el banco del descuadre de transferencia.");
        return;
      }
    }

    // Validar si hay descuadre de datáfono
    if (huboDescuadreDatafono === "Sí") {
      if (!valorDescuadreDatafono || Number(valorDescuadreDatafono) <= 0) {
        alert("⚠️ Debe ingresar el valor del descuadre de datáfono.");
        return;
      }
    }

    // Actualizar el cuadre
    actualizarCuadre(cuadreActivo.id, {
      ventas: {
        ...ventas,
        huboDescuadreTransferencia,
        valorDescuadreTransferencia: huboDescuadreTransferencia === "Sí" ? Number(valorDescuadreTransferencia) : 0,
        numeroPedidoDescuadreTransferencia: huboDescuadreTransferencia === "Sí" ? numeroPedidoDescuadreTransferencia.trim() : "",
        bancoDescuadreTransferencia: huboDescuadreTransferencia === "Sí" ? bancoDescuadreTransferencia : "",
        huboDescuadreDatafono,
        valorDescuadreDatafono: huboDescuadreDatafono === "Sí" ? Number(valorDescuadreDatafono) : 0,
        sobrante: sobranteEfectivoCalculado
      }
    });
    onClose();
  };

  return (
    <div>
      {/* Resumen de descuadre en efectivo (automático) */}
      <div style={{
        padding: '16px',
        backgroundColor: colorSobrante.bg,
        borderRadius: '12px',
        marginBottom: '20px',
        border: `2px solid ${colorSobrante.color}`,
        textAlign: 'center'
      }}>
        <h4 style={{ margin: '0 0 8px 0', color: colorSobrante.color, fontSize: '14px', fontWeight: '600' }}>
          💰 Descuadre en Efectivo (Automático)
        </h4>
        <div style={{ fontSize: '22px', fontWeight: '700', color: colorSobrante.color, marginBottom: '8px' }}>
          {formatCOP(Math.abs(sobranteEfectivoCalculado))}
        </div>
        <div style={{ 
          padding: '6px 16px',
          backgroundColor: descuadreEfectivoAuto === "No" ? '#d1fae5' : '#fef3c7',
          borderRadius: '20px',
          display: 'inline-block',
          fontSize: '12px',
          fontWeight: '600',
          color: descuadreEfectivoAuto === "No" ? '#065f46' : '#92400e'
        }}>
          {descuadreEfectivoAuto === "No" 
            ? "✅ Sin descuadre en efectivo" 
            : `⚠️ ${tipoDescuadreEfectivo}`
          }
        </div>
        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '8px' }}>
          = Venta efectivo - Facturas caja - Domicilios + Ingresos varios
        </div>
      </div>

      {/* Detalle del cálculo */}
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#f0f9ff', 
        borderRadius: '8px',
        border: '1px solid #0ea5e9',
        marginBottom: '20px'
      }}>
        <h4 style={{ margin: '0 0 8px 0', color: '#0369a1', fontSize: '12px' }}>📊 Detalle del cálculo:</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', fontSize: '11px' }}>
          <div style={{ padding: '6px', backgroundColor: 'white', borderRadius: '6px', textAlign: 'center' }}>
            <div style={{ color: '#6b7280' }}>Venta Efectivo</div>
            <strong style={{ color: '#166534' }}>{formatCOP(Number(ventas.ventaEfectivo || 0))}</strong>
          </div>
          <div style={{ padding: '6px', backgroundColor: 'white', borderRadius: '6px', textAlign: 'center' }}>
            <div style={{ color: '#6b7280' }}>- Facturas Caja</div>
            <strong style={{ color: '#dc2626' }}>{formatCOP(totalFacturasCaja)}</strong>
          </div>
          <div style={{ padding: '6px', backgroundColor: 'white', borderRadius: '6px', textAlign: 'center' }}>
            <div style={{ color: '#6b7280' }}>- Domicilios</div>
            <strong style={{ color: '#dc2626' }}>{formatCOP(totalDomicilios)}</strong>
          </div>
          <div style={{ padding: '6px', backgroundColor: 'white', borderRadius: '6px', textAlign: 'center' }}>
            <div style={{ color: '#6b7280' }}>+ Ing. Varios</div>
            <strong style={{ color: '#059669' }}>{formatCOP(totalIngresosVarios)}</strong>
          </div>
        </div>
      </div>

      {/* Descuadres manuales */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '20px' }}>
        {/* Descuadre en Transferencia */}
        <div style={{ 
          backgroundColor: huboDescuadreTransferencia === "Sí" ? '#fef3c7' : '#f0fdf4', 
          padding: '16px', 
          borderRadius: '12px',
          border: `1px solid ${huboDescuadreTransferencia === "Sí" ? '#f59e0b' : '#10b981'}`
        }}>
          <h4 style={{ 
            margin: '0 0 12px 0', 
            color: huboDescuadreTransferencia === "Sí" ? '#92400e' : '#065f46', 
            fontSize: '13px', 
            fontWeight: '600',
            textAlign: 'center'
          }}>
            💸 Descuadre en Transferencia
          </h4>
          
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '12px' }}>
              ¿Hubo descuadre?
            </label>
            <select
              value={huboDescuadreTransferencia}
              onChange={(e) => setHuboDescuadreTransferencia(e.target.value)}
              disabled={disabled}
              style={{ 
                width: '100%',
                padding: '8px',
                borderRadius: '6px',
                border: huboDescuadreTransferencia === "" ? '2px solid #f97316' : '1px solid #d1d5db',
                backgroundColor: huboDescuadreTransferencia === "" ? '#fff7ed' : (huboDescuadreTransferencia === "No" ? '#d1fae5' : '#fef3c7')
              }}
            >
              <option value="">-- Seleccione --</option>
              <option value="No">No</option>
              <option value="Sí">Sí</option>
            </select>
          </div>
          
          {huboDescuadreTransferencia === "Sí" && (
            <>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '12px' }}>
                  Valor descuadre
                </label>
                <input
                  type="number"
                  value={valorDescuadreTransferencia}
                  onChange={(e) => setValorDescuadreTransferencia(e.target.value)}
                  disabled={disabled}
                  placeholder="0"
                  style={{ 
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: '#fff'
                  }}
                />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '12px' }}>
                  Número de pedido
                </label>
                <input
                  type="text"
                  value={numeroPedidoDescuadreTransferencia}
                  onChange={(e) => setNumeroPedidoDescuadreTransferencia(e.target.value)}
                  disabled={disabled}
                  placeholder="Ej: 12345"
                  style={{ 
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: '#fff'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '12px' }}>
                  Banco
                </label>
                <select
                  value={bancoDescuadreTransferencia}
                  onChange={(e) => setBancoDescuadreTransferencia(e.target.value)}
                  disabled={disabled}
                  style={{ 
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: bancoDescuadreTransferencia === "" ? '2px solid #f97316' : '1px solid #d1d5db',
                    backgroundColor: bancoDescuadreTransferencia === "" ? '#fff7ed' : '#fff'
                  }}
                >
                  <option value="">-- Seleccione banco --</option>
                  <option value="Bancolombia">Bancolombia</option>
                  <option value="Davivienda">Davivienda</option>
                  <option value="Daviplata">Daviplata</option>
                  <option value="Nequi">Nequi</option>
                </select>
              </div>
            </>
          )}
        </div>

        {/* Descuadre en Datáfono */}
        <div style={{ 
          backgroundColor: huboDescuadreDatafono === "Sí" ? '#fef3c7' : '#f0fdf4', 
          padding: '16px', 
          borderRadius: '12px',
          border: `1px solid ${huboDescuadreDatafono === "Sí" ? '#f59e0b' : '#10b981'}`
        }}>
          <h4 style={{ 
            margin: '0 0 12px 0', 
            color: huboDescuadreDatafono === "Sí" ? '#92400e' : '#065f46', 
            fontSize: '13px', 
            fontWeight: '600',
            textAlign: 'center'
          }}>
            💳 Descuadre en Datáfono
          </h4>
          
          <div style={{ marginBottom: '10px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '12px' }}>
              ¿Hubo descuadre?
            </label>
            <select
              value={huboDescuadreDatafono}
              onChange={(e) => setHuboDescuadreDatafono(e.target.value)}
              disabled={disabled}
              style={{ 
                width: '100%',
                padding: '8px',
                borderRadius: '6px',
                border: huboDescuadreDatafono === "" ? '2px solid #f97316' : '1px solid #d1d5db',
                backgroundColor: huboDescuadreDatafono === "" ? '#fff7ed' : (huboDescuadreDatafono === "No" ? '#d1fae5' : '#fef3c7')
              }}
            >
              <option value="">-- Seleccione --</option>
              <option value="No">No</option>
              <option value="Sí">Sí</option>
            </select>
          </div>
          
          {huboDescuadreDatafono === "Sí" && (
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '12px' }}>
                Valor descuadre
              </label>
              <input
                type="number"
                value={valorDescuadreDatafono}
                onChange={(e) => setValorDescuadreDatafono(e.target.value)}
                disabled={disabled}
                placeholder="0"
                style={{ 
                  width: '100%',
                  padding: '8px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#fff'
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Botones de acción */}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button variant="solid" onClick={guardar} disabled={disabled}>
          💾 Guardar descuadres
        </Button>
      </div>
    </div>
  );
}
 
/* ====================== SUB-CUADROS ====================== */
 
function CuadroVentas({ ventas, onChange, disabled, totalesTransfer, totalDatafonos, totalFacturasCaja, totalDomicilios, totalIngresosVarios, totalTurnos = 0, sobranteEfectivoCalculado, totalFacturasPendientes = 0, valorInicial = 0, esGerente = false, tienePermiso = () => true, precierreRealizado = false, precierreConfirmadoPorReceptor = false, efectivoFinalConfirmado = false, fecha, sede }) {
  // Estado local para el valor temporal antes de confirmar
  const [valorTemporal, setValorTemporal] = useState("");
  
  // 🔹 Estado para FUDO
  const [cargandoFudo, setCargandoFudo] = useState(false);
  const [mensajeFudo, setMensajeFudo] = useState(null);
  
  // 🔹 SEDES CONFIGURADAS PARA FUDO - Solo estas sedes usan la integración automática
  const SEDES_CON_FUDO = ['CORALES', 'VILLA DEL PRADO', 'AV SUR'];
  const sedeUsaFudo = SEDES_CON_FUDO.includes((sede || '').toUpperCase());
  
  // 🔹 EFECTO: Si hay precierre sin confirmar, pre-cargar el valor anterior
  useEffect(() => {
    if (precierreRealizado && !precierreConfirmadoPorReceptor && ventas.efectivoEnCaja) {
      setValorTemporal(String(ventas.efectivoEnCaja));
    }
  }, [precierreRealizado, precierreConfirmadoPorReceptor, ventas.efectivoEnCaja]);
  
  // 🔐 EFECTO: Cuando GERENCIA desbloqueó el efectivo (efectivoEnCajaConfirmado cambió a false), pre-cargar el valor
  useEffect(() => {
    if (ventas.efectivoEnCajaConfirmado === false && ventas.efectivoModificadoPorGerencia === true) {
      setValorTemporal(String(ventas.efectivoEnCaja || ""));
      console.log("🔐 GERENCIA desbloqueó el efectivo - precargando valor:", ventas.efectivoEnCaja);
    }
  }, [ventas.efectivoEnCajaConfirmado, ventas.efectivoModificadoPorGerencia]);
  
  function set(key, value) {
    onChange({ ...ventas, [key]: value });
  }
  
  // 🔹 Función para traer totales de FUDO (solo para sedes configuradas)
  async function traerTotalesDeFudo() {
    if (!sedeUsaFudo) return;
    if (disabled || !fecha || !sede) {
      setMensajeFudo({ tipo: 'error', texto: 'Faltan datos de fecha o sede' });
      return;
    }

    setCargandoFudo(true);
    setMensajeFudo(null);

    try {
      // Función para convertir fecha UTC a Colombia
      const convertirFechaUTCaColombia = (fechaISO) => {
        if (!fechaISO) return '';
        try {
          const fechaUTC = new Date(fechaISO);
          const fechaColombia = new Date(fechaUTC.getTime() - (5 * 60 * 60 * 1000));
          const year = fechaColombia.getUTCFullYear();
          const month = String(fechaColombia.getUTCMonth() + 1).padStart(2, '0');
          const day = String(fechaColombia.getUTCDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        } catch (e) {
          return fechaISO.split('T')[0] || '';
        }
      };

      // Agregar timestamp para evitar cache
      const timestamp = Date.now();
      const url = `https://intranetbigburguer.com/api/api_fudo_proxy?accion=traer_totales_ventas&sede=${encodeURIComponent(sede)}&fecha=${fecha}&_t=${timestamp}`;
      const response = await fetch(url);
      const data = await response.json();

      console.log('📡 FUDO Totales recibidos:', data.totales);

      if (!data.success) {
        setMensajeFudo({ tipo: 'error', texto: data.error || 'Error al consultar FUDO' });
        return;
      }

      // Verificar que la fecha coincida
      if (data.fechaBuscada !== fecha) {
        setMensajeFudo({ tipo: 'warning', texto: `⚠️ Fecha de FUDO (${data.fechaBuscada}) no coincide con cuadre (${fecha})` });
      }

      // Actualizar los valores de ventas con los totales de FUDO
      // IMPORTANTE: Siempre sobreescribir con los valores frescos de FUDO
      const nuevosValores = {
        ...ventas,
        ventaEfectivo: data.totales.efectivo || 0,
        ventaEfectivoFudo: data.totales.efectivo || 0,
        onlineRappi: data.totales.onlineRappi || 0,
        onlineRappiFudo: data.totales.onlineRappi || 0,
        datafonoFudo: data.totales.datafono || 0,
        totalesFudoFecha: new Date().toISOString(),
        totalesFudoPedidos: data.totalPedidosCerrados || 0
      };
      
      console.log('📊 Actualizando valores:', {
        efectivo: nuevosValores.ventaEfectivo,
        datafono: nuevosValores.datafonoFudo,
        onlineRappi: nuevosValores.onlineRappi
      });
      
      onChange(nuevosValores);

      // Formato de moneda para mostrar
      const formatearMoneda = (valor) => `$${Number(valor || 0).toLocaleString('es-CO')}`;

      setMensajeFudo({ 
        tipo: 'success', 
        texto: `✅ Actualizado (${data.totalPedidosCerrados} pedidos): Efectivo ${formatearMoneda(data.totales.efectivo)}, Datáfono ${formatearMoneda(data.totales.datafono)}, Rappi ${formatearMoneda(data.totales.onlineRappi)}` 
      });

    } catch (error) {
      console.error('Error trayendo totales de FUDO:', error);
      setMensajeFudo({ tipo: 'error', texto: 'Error de conexión con FUDO' });
    } finally {
      setCargandoFudo(false);
      setTimeout(() => setMensajeFudo(null), 5000);
    }
  }
  
  // 🔹 NUEVA LÓGICA: El efectivo debe estar desbloqueado SOLO si hay precierre no confirmado
  // O si el precierre fue confirmado pero aún no se ha confirmado el valor FINAL del segundo empleado
  // Si hay precierre realizado pero aún no confirmado por el receptor → desbloqueado (editable, pre-guardado)
  // Si ya se confirmó el precierre pero NO se ha confirmado el valor final → editable UNA vez más
  // Si efectivoFinalConfirmado = true → BLOQUEADO permanentemente
  const efectivoDesbloqueado = (precierreRealizado && !precierreConfirmadoPorReceptor) || 
                               (precierreRealizado && precierreConfirmadoPorReceptor && !efectivoFinalConfirmado);
  
  // 🔐 CORREGIDO: En fase de precierre, siempre permitir ingresar (el empleado B está recibiendo la caja)
  // El input se mostrará si:
  // 1. No está confirmado PERO hay precierre abierto (empleado B recibiendo caja)
  // 2. O simplemente no está confirmado aún
  const efectivoConfirmado = ventas.efectivoEnCajaConfirmado === true && !efectivoDesbloqueado;
  
  // 🔹 Determinar si el empleado B puede confirmar su valor final
  const puedeConfirmarValorFinal = precierreRealizado && precierreConfirmadoPorReceptor && !efectivoFinalConfirmado;
  
  // 🔹 Verificar si puede modificar el efectivo confirmado (solo gerencia/admin)
  const puedeModificarEfectivo = esGerente || tienePermiso("admin_cuadre_modificar_efectivo");
  
  // 🔹 Verificar si un empleado normal aún tiene oportunidad de modificar el valor final
  // Después de confirmar efectivoFinalConfirmado, permite UNA modificación más
  const tieneOportunidadModificacionFinal = !puedeModificarEfectivo && 
                                            efectivoFinalConfirmado && 
                                            ventas.efectivoFinalTieneOportunidadModificacion !== false;
  
  // 🔐 MEJORADA: Un empleado sin permiso está BLOQUEADO SOLO si:
  // - Ya confirmó un valor PERO
  // - NO está en situación especial (precierre, oportunidad final, desbloqueado)
  const empleadoSinPermisoYaBloqueado = !puedeModificarEfectivo && 
                                       ventas.efectivoEnCajaConfirmado === true &&  // 🔹 Confirmado antes
                                       !efectivoDesbloqueado &&  // 🔹 No está desbloqueado por precierre
                                       !tieneOportunidadModificacionFinal &&  // 🔹 Sin oportunidad final
                                       !puedeConfirmarValorFinal;  // 🔹 No está confirmando valor final
  
  // 🔹 Función para confirmar el efectivo en caja
  const confirmarEfectivo = () => {
    const valor = Number(valorTemporal);
    if (!valorTemporal || valor < 0) {
      alert("⚠️ Debes ingresar un valor válido (mayor o igual a 0)");
      return;
    }
    
    // 🔐 VALIDACIÓN FUERTE: Bloquear empleados sin permiso que ya confirmaron una vez
    if (empleadoSinPermisoYaBloqueado) {
      alert("⛔ Ya confirmaste el valor de efectivo y no tienes oportunidad de modificación adicional.\n\nSolo Gerencia o personal con permiso especial puede hacerlo.");
      return;
    }
    
    // Mensaje diferente según contexto
    let mensaje = "";
    if (puedeConfirmarValorFinal) {
      mensaje = `¿Confirmas que cierras tu jornada con $${valor.toLocaleString('es-CO')} en efectivo en caja?\n\n⚠️ Este es el VALOR FINAL. Solo podrás modificarlo UNA VEZ más.`;
    } else if (tieneOportunidadModificacionFinal) {
      mensaje = `¿Confirmas el nuevo valor de $${valor.toLocaleString('es-CO')} en efectivo?\n\n⚠️ ÚLTIMA OPORTUNIDAD de cambio. Después quedará bloqueado permanentemente.`;
    } else {
      mensaje = `¿Confirmas que tienes $${valor.toLocaleString('es-CO')} en efectivo en caja?\n\n⚠️ Una vez confirmado, quedará bloqueado hasta que finalices la jornada.`;
    }
    
    const confirmar = window.confirm(mensaje);
    if (!confirmar) return;
    
    // Guardar el valor y marcar como confirmado
    console.log("💾 Guardando efectivo en caja:", valor, puedeConfirmarValorFinal ? "(VALOR FINAL)" : tieneOportunidadModificacionFinal ? "(ÚLTIMA MODIFICACIÓN)" : "");
    
    const datosActualizados = { 
      ...ventas, 
      efectivoEnCaja: valor,
      efectivoEnCajaConfirmado: true,
      efectivoModificadoPorGerencia: false, // 🔐 Limpiar el flag después de confirmar el nuevo valor
      fechaConfirmacionEfectivo: new Date().toISOString()
    };
    
    // Si es el valor final del segundo empleado, marcar como finalizado permanentemente
    if (puedeConfirmarValorFinal) {
      datosActualizados.efectivoFinalConfirmado = true;
      datosActualizados.efectivoFinalTieneOportunidadModificacion = true; // 🔹 Permitir UNA modificación más
    } else if (tieneOportunidadModificacionFinal) {
      // Si el empleado está usando su oportunidad de modificación final, marcar como usada
      datosActualizados.efectivoFinalTieneOportunidadModificacion = false; // 🔹 Ya no hay más oportunidades
    }
    
    onChange(datosActualizados);
    setValorTemporal("");
  };
  
  // 🔹 Función para desbloquear (diferentes comportamientos según rol y contexto)
  const desbloquearEfectivo = () => {
    // Si es valor final confirmado
    if (efectivoFinalConfirmado) {
      // ✅ Si el empleado normal tiene oportunidad de modificación final
      if (tieneOportunidadModificacionFinal) {
        const confirmar = window.confirm(
          "⚠️ Esta es tu ÚLTIMA OPORTUNIDAD para modificar el valor de efectivo.\n\n" +
          "¿Deseas editarlo?\n\n" +
          "Después de confirmar, quedará bloqueado permanentemente."
        );
        if (!confirmar) return;
        
        setValorTemporal(String(ventas.efectivoEnCaja || ""));
        onChange({ 
          ...ventas, 
          efectivoEnCajaConfirmado: false
        });
        return;
      }
      
      // ❌ Si ya usó su oportunidad y no es GERENCIA
      if (!puedeModificarEfectivo) {
        alert("⛔ Este valor de efectivo ya está BLOQUEADO.\n\nYa usaste tu oportunidad de modificación.\n\nSolo Gerencia o personal con permiso especial puede editarlo ahora.");
        return;
      }
      
      // ✅ Si es GERENCIA, puede modificar con advertencia
      const confirmarFinal = window.confirm(
        "⚠️ ATENCIÓN: Este efectivo ya fue CONFIRMADO COMO FINAL.\n\n" +
        "¿Estás seguro de que deseas modificar este valor?\n\n" +
        "Esta acción quedará registrada como modificación de gerencia/admin."
      );
      if (!confirmarFinal) return;
      
      setValorTemporal(String(ventas.efectivoEnCaja || ""));
      onChange({ 
        ...ventas, 
        efectivoEnCajaConfirmado: false,
        efectivoFinalConfirmado: false,
        efectivoModificadoPorGerencia: true,
        fechaModificacionGerencia: new Date().toISOString()
      });
      return;
    }
    
    // Para valores no finales, solo GERENCIA puede desbloquear después de confirmado
    if (efectivoConfirmado && !puedeModificarEfectivo) {
      alert("⛔ Este valor ya está confirmado y bloqueado.\n\nSolo Gerencia o personal con permiso especial puede modificarlo.");
      return;
    }
    
    const confirmar = window.confirm("¿Deseas desbloquear el valor de efectivo en caja para modificarlo?\n\nDeberás volver a confirmar el nuevo valor.");
    if (!confirmar) return;
    
    setValorTemporal(String(ventas.efectivoEnCaja || ""));
    onChange({ 
      ...ventas, 
      efectivoEnCajaConfirmado: false,
      efectivoModificadoPorGerencia: puedeModificarEfectivo,
      ...(puedeModificarEfectivo && { fechaModificacionGerencia: new Date().toISOString() })
    });
  };

  // Formatear moneda
  const formatCOP = (val) => new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0
  }).format(val || 0);

  // 🔹 Determinar estado de descuadre en efectivo automáticamente
  // Si sobrante es 0 → No, si es positivo o negativo → Sí
  const descuadreEfectivoAuto = sobranteEfectivoCalculado === 0 ? "No" : "Sí";
  const tipoDescuadreEfectivo = sobranteEfectivoCalculado > 0 ? "Sobrante en efectivo" : sobranteEfectivoCalculado < 0 ? "Descuadre" : "";
  
  // Color para sobrante efectivo
  const colorSobrante = sobranteEfectivoCalculado === 0 
    ? { bg: '#f0fdf4', color: '#166534' } // verde suave
    : sobranteEfectivoCalculado > 0 
      ? { bg: '#ecfdf5', color: '#047857' } // verde para positivo
      : { bg: '#fef2f2', color: '#b91c1c' }; // rojo suave para negativo
 
  return (
    <section className="subcard">
      <h3>Ventas y medios de pago</h3>
      
      {/* 📊 SECCIÓN 1: Medios de pago - Grid 4 columnas */}
      <div style={{ 
        backgroundColor: '#f8fafc', 
        padding: '16px', 
        borderRadius: '12px', 
        marginBottom: '16px',
        border: '1px solid #e2e8f0'
      }}>
        {/* 🔹 Encabezado con botón FUDO */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '12px' 
        }}>
          <h4 style={{ margin: 0, color: '#475569', fontSize: '13px', fontWeight: '600' }}>
            💳 Medios de Pago {sedeUsaFudo && <span style={{ fontSize: '10px', color: '#3b82f6' }}>(FUDO)</span>}
          </h4>
          
          {/* Botón Traer de FUDO - Solo para sedes configuradas */}
          {sedeUsaFudo && fecha && sede && (
            <button
              onClick={traerTotalesDeFudo}
              disabled={disabled || cargandoFudo}
              style={{
                padding: '8px 16px',
                background: cargandoFudo ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: disabled || cargandoFudo ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)'
              }}
            >
              {cargandoFudo ? (
                <>⏳ Cargando...</>
              ) : (
                <>🔄 Traer de FUDO</>
              )}
            </button>
          )}
        </div>
        
        {/* Mensaje de FUDO - Solo para sedes configuradas */}
        {sedeUsaFudo && mensajeFudo && (
          <div style={{
            padding: '8px 12px',
            marginBottom: '12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: mensajeFudo.tipo === 'success' ? '#d1fae5' : 
                            mensajeFudo.tipo === 'error' ? '#fee2e2' : 
                            mensajeFudo.tipo === 'warning' ? '#fef3c7' : '#e0f2fe',
            color: mensajeFudo.tipo === 'success' ? '#065f46' : 
                   mensajeFudo.tipo === 'error' ? '#991b1b' : 
                   mensajeFudo.tipo === 'warning' ? '#92400e' : '#0369a1'
          }}>
            {mensajeFudo.texto}
          </div>
        )}
        
        {/* Info de última actualización FUDO - Solo para sedes configuradas */}
        {sedeUsaFudo && ventas.totalesFudoFecha && (
          <div style={{
            padding: '6px 10px',
            marginBottom: '12px',
            borderRadius: '6px',
            fontSize: '11px',
            backgroundColor: '#dbeafe',
            color: '#1e40af',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            📡 Datos de FUDO: {new Date(ventas.totalesFudoFecha).toLocaleString('es-CO')} 
            {ventas.totalesFudoPedidos && ` • ${ventas.totalesFudoPedidos} pedidos`}
          </div>
        )}
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
          {/* EFECTIVO - Si usa FUDO: no editable. Si no usa FUDO: editable */}
          <Field label={
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              Valor venta efectivo {!sedeUsaFudo && '*'}
              {sedeUsaFudo && ventas.ventaEfectivoFudo !== undefined && <span title="Valor de FUDO">📡</span>}
            </span>
          }>
            {sedeUsaFudo ? (
              <input
                className="input"
                type="text"
                disabled={true}
                value={formatCOP(ventas.ventaEfectivo || 0)}
                style={{ 
                  backgroundColor: '#f0fdf4', 
                  color: '#166534', 
                  fontWeight: '600',
                  border: '2px solid #22c55e'
                }}
              />
            ) : (
              <div style={{ position: 'relative' }}>
                <span style={{ 
                  position: 'absolute', 
                  left: '10px', 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  color: '#166534', 
                  fontWeight: '600',
                  fontSize: '14px'
                }}>$</span>
                <input
                  className="input"
                  type="number"
                  disabled={disabled}
                  placeholder="0"
                  value={ventas.ventaEfectivo !== undefined && ventas.ventaEfectivo !== null ? ventas.ventaEfectivo : ""}
                  onChange={(e) => set("ventaEfectivo", e.target.value === "" ? 0 : Number(e.target.value))}
                  style={{ 
                    paddingLeft: '24px',
                    backgroundColor: '#f0fdf4', 
                    color: '#166534', 
                    fontWeight: '600',
                    border: '2px solid #22c55e'
                  }}
                />
              </div>
            )}
          </Field>
          <Field label="Valor venta Bancolombia">
            <input
              className="input"
              type="text"
              disabled={true}
              value={formatCOP(totalesTransfer?.bancolombia || 0)}
              style={{ backgroundColor: '#f0fdf4', color: '#166534', fontWeight: '600' }}
            />
          </Field>
          <Field label="Valor venta Davivienda">
            <input
              className="input"
              type="text"
              disabled={true}
              value={formatCOP(totalesTransfer?.davivienda || 0)}
              style={{ backgroundColor: '#fef3c7', color: '#92400e', fontWeight: '600' }}
            />
          </Field>
          <Field label="Valor venta Daviplata">
            <input
              className="input"
              type="text"
              disabled={true}
              value={formatCOP(totalesTransfer?.daviplata || 0)}
              style={{ backgroundColor: '#fee2e2', color: '#991b1b', fontWeight: '600' }}
            />
          </Field>
          <Field label="Valor venta Nequi">
            <input
              className="input"
              type="text"
              disabled={true}
              value={formatCOP(totalesTransfer?.nequi || 0)}
              style={{ backgroundColor: '#ede9fe', color: '#5b21b6', fontWeight: '600' }}
            />
          </Field>
          {/* DATÁFONO - Si usa FUDO: muestra valor de FUDO con comparación. Si no: muestra total sección */}
          <Field label={
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              Valor venta Datáfono
              {sedeUsaFudo && ventas.datafonoFudo !== undefined && <span title="Valor de FUDO">📡</span>}
              {/* Indicador de comparación: ✅ verde si coincide, ⚠️ amarillo si no - Solo para sedes con FUDO */}
              {sedeUsaFudo && ventas.datafonoFudo !== undefined && (
                Number(ventas.datafonoFudo || 0) === Number(totalDatafonos || 0) ? (
                  <span title="Coincide con sección Datafonos" style={{ fontSize: '14px' }}>✅</span>
                ) : (
                  <span title="No coincide con sección Datafonos" style={{ fontSize: '14px' }}>⚠️</span>
                )
              )}
            </span>
          }>
            <div style={{ position: 'relative' }}>
              <input
                className="input"
                type="text"
                disabled={true}
                value={formatCOP(sedeUsaFudo && ventas.datafonoFudo !== undefined ? ventas.datafonoFudo : totalDatafonos || 0)}
                style={{ 
                  backgroundColor: sedeUsaFudo && ventas.datafonoFudo !== undefined 
                    ? (Number(ventas.datafonoFudo || 0) === Number(totalDatafonos || 0) ? '#d1fae5' : '#fef3c7')
                    : '#e0f2fe', 
                  color: sedeUsaFudo && ventas.datafonoFudo !== undefined 
                    ? (Number(ventas.datafonoFudo || 0) === Number(totalDatafonos || 0) ? '#065f46' : '#92400e')
                    : '#0369a1', 
                  fontWeight: '600',
                  border: sedeUsaFudo && ventas.datafonoFudo !== undefined 
                    ? (Number(ventas.datafonoFudo || 0) === Number(totalDatafonos || 0) ? '2px solid #22c55e' : '2px solid #f59e0b')
                    : '1px solid #e2e8f0'
                }}
              />
              {/* Mostrar comparación si hay diferencia - Solo para sedes con FUDO */}
              {sedeUsaFudo && ventas.datafonoFudo !== undefined && Number(ventas.datafonoFudo || 0) !== Number(totalDatafonos || 0) && (
                <div style={{ 
                  fontSize: '10px', 
                  color: '#92400e', 
                  marginTop: '2px',
                  textAlign: 'center'
                }}>
                  Sección: {formatCOP(totalDatafonos || 0)}
                </div>
              )}
            </div>
          </Field>
          {/* ONLINE RAPPI - Si usa FUDO: no editable. Si no usa FUDO: editable */}
          <Field label={
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              Valor Online Rappi {!sedeUsaFudo && '*'}
              {sedeUsaFudo && ventas.onlineRappiFudo !== undefined && <span title="Valor de FUDO">📡</span>}
            </span>
          }>
            {sedeUsaFudo ? (
              <input
                className="input"
                type="text"
                disabled={true}
                value={formatCOP(ventas.onlineRappi || 0)}
                style={{ 
                  backgroundColor: '#fef2f2', 
                  color: '#dc2626', 
                  fontWeight: '600',
                  border: '2px solid #ef4444'
                }}
              />
            ) : (
              <div style={{ position: 'relative' }}>
                <span style={{ 
                  position: 'absolute', 
                  left: '10px', 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  color: '#dc2626', 
                  fontWeight: '600',
                  fontSize: '14px'
                }}>$</span>
                <input
                  className="input"
                  type="number"
                  disabled={disabled}
                  placeholder="0"
                  value={ventas.onlineRappi !== undefined && ventas.onlineRappi !== null ? ventas.onlineRappi : ""}
                  onChange={(e) => set("onlineRappi", e.target.value === "" ? 0 : Number(e.target.value))}
                  style={{ 
                    paddingLeft: '24px',
                    backgroundColor: '#fef2f2', 
                    color: '#dc2626', 
                    fontWeight: '600',
                    border: '2px solid #ef4444'
                  }}
                />
              </div>
            )}
          </Field>
        </div>
      </div>
      
      {/* 💰 SECCIÓN 2: Efectivo en Caja - Con confirmación */}
      <div style={{ 
        backgroundColor: efectivoConfirmado ? '#f0fdf4' : '#f8fafc', 
        padding: '16px', 
        borderRadius: '12px', 
        marginBottom: '16px',
        border: efectivoConfirmado ? '3px solid #22c55e' : '2px solid #3b82f6'
      }}>
        <h4 style={{ margin: '0 0 12px 0', color: efectivoConfirmado ? '#166534' : '#1e40af', fontSize: '14px', fontWeight: '600', textAlign: 'center' }}>
          💰 Verificación de Efectivo en Caja {efectivoConfirmado && '✅'}
        </h4>
        
        {/* 🔹 SI NO ESTÁ CONFIRMADO: Mostrar campo de entrada y botón */}
        {!efectivoConfirmado && !empleadoSinPermisoYaBloqueado && (
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#eff6ff', 
            borderRadius: '12px',
            border: '2px solid #3b82f6',
            marginBottom: '12px'
          }}>
            <div style={{ fontSize: '12px', color: '#1e40af', marginBottom: '8px', fontWeight: '700', textAlign: 'center' }}>
              💵 ¿Cuánto tienes en efectivo en caja? *
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ position: 'relative', flex: 1, maxWidth: '200px' }}>
                <span style={{ 
                  position: 'absolute', 
                  left: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  color: '#1e40af', 
                  fontWeight: '700',
                  fontSize: '16px'
                }}>$</span>
                <input
                  type="number"
                  disabled={disabled}
                  placeholder="0"
                  value={valorTemporal}
                  onChange={(e) => setValorTemporal(e.target.value)}
                  style={{ 
                    width: '100%',
                    padding: '12px 12px 12px 28px',
                    borderRadius: '8px',
                    border: '2px solid #3b82f6',
                    backgroundColor: 'white',
                    fontSize: '18px',
                    fontWeight: '700',
                    textAlign: 'center',
                    color: '#1e40af'
                  }}
                />
              </div>
              <button
                type="button"
                disabled={disabled || !valorTemporal || Number(valorTemporal) < 0}
                onClick={confirmarEfectivo}
                style={{ 
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: (!valorTemporal || Number(valorTemporal) < 0) ? '#94a3b8' : '#22c55e',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: (!valorTemporal || Number(valorTemporal) < 0) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: (!valorTemporal || Number(valorTemporal) < 0) ? 'none' : '0 4px 12px rgba(34, 197, 94, 0.4)'
                }}
              >
                ✓ Confirmar
              </button>
            </div>
            <div style={{ fontSize: '10px', color: '#64748b', marginTop: '8px', textAlign: 'center' }}>
              {efectivoDesbloqueado 
                ? "⚠️ Pre-guardado. Puedes editarlo durante la jornada. Al finalizar deberá bloquearse."
                : "⚠️ Una vez confirmado, solo Gerencia o quien tenga permiso podrá modificarlo"
              }
            </div>
          </div>
        )}
        
        {/* � SI ESTÁ BLOQUEADO: Mostrar mensaje de bloqueo */}
        {empleadoSinPermisoYaBloqueado && (
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#fef2f2', 
            borderRadius: '12px',
            border: '2px solid #ef4444',
            marginBottom: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '14px', color: '#b91c1c', fontWeight: '700', marginBottom: '8px' }}>
              🔒 Efectivo Bloqueado
            </div>
            <div style={{ fontSize: '12px', color: '#991b1b', lineHeight: '1.5' }}>
              Ya confirmaste el valor de efectivo y no tienes oportunidad adicional de modificación.
              <br />
              <br />
              Solo Gerencia o personal con permiso especial puede editarlo.
            </div>
          </div>
        )}
        
        {/* �🔹 SI ESTÁ CONFIRMADO: Mostrar tres valores en línea */}
        {efectivoConfirmado && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              
              {/* 1️⃣ Valor confirmado */}
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#d1fae5', 
                borderRadius: '10px',
                textAlign: 'center',
                border: '2px solid #22c55e',
                position: 'relative'
              }}>
                <div style={{ fontSize: '11px', color: '#166534', marginBottom: '4px', fontWeight: '700' }}>
                  💵 Tienes en caja ✓
                </div>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#166534' }}>
                  {formatCOP(Number(ventas.efectivoEnCaja || 0))}
                </div>
                <div style={{ fontSize: '9px', color: '#22c55e', marginTop: '4px', fontWeight: '600' }}>
                  🔒 Valor confirmado
                </div>
                
                {/* Botón para modificar (solo gerencia/permiso O empleado con oportunidad final) */}
                {(puedeModificarEfectivo || tieneOportunidadModificacionFinal) && !disabled && (
                  <button
                    type="button"
                    onClick={desbloquearEfectivo}
                    style={{ 
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      padding: '4px 6px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      fontSize: '9px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                    title="Desbloquear para modificar"
                  >
                    ✏️
                  </button>
                )}
              </div>
              
              {/* 2️⃣ Debe quedar en caja (con base) */}
              <div style={{ 
                padding: '12px', 
                backgroundColor: (valorInicial - totalFacturasPendientes) >= 0 ? '#d1fae5' : '#fef2f2', 
                borderRadius: '10px',
                textAlign: 'center',
                border: `2px solid ${(valorInicial - totalFacturasPendientes) >= 0 ? '#10b981' : '#ef4444'}`
              }}>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>
                  📦 Debe quedar en caja
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: (valorInicial - totalFacturasPendientes) >= 0 ? '#047857' : '#dc2626' }}>
                  {formatCOP(valorInicial - totalFacturasPendientes)}
                </div>
                <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '4px' }}>
                  Base inicial: {formatCOP(valorInicial)}
                </div>
              </div>
              
              {/* 3️⃣ Sobrante neto (sin base) */}
              <div style={{ 
                padding: '12px', 
                backgroundColor: sobranteEfectivoCalculado >= 0 ? '#ecfdf5' : '#fef2f2', 
                borderRadius: '10px',
                textAlign: 'center',
                border: `2px solid ${sobranteEfectivoCalculado >= 0 ? '#059669' : '#ef4444'}`
              }}>
                <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>
                  💵 Sobrante neto
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: sobranteEfectivoCalculado >= 0 ? '#059669' : '#dc2626' }}>
                  {formatCOP(sobranteEfectivoCalculado)}
                </div>
                <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '4px' }}>
                  Sin base
                </div>
              </div>
              
              {/* 4️⃣ Valor Total del Sobrante */}
              {(() => {
                const tienes = Number(ventas.efectivoEnCaja || 0);
                const debeQuedar = valorInicial - totalFacturasPendientes;
                const diferencia = tienes - debeQuedar - sobranteEfectivoCalculado;
                const valorTotal = sobranteEfectivoCalculado + diferencia;
                return (
                  <div style={{ 
                    padding: '12px', 
                    backgroundColor: '#f0f9ff', 
                    borderRadius: '10px',
                    textAlign: 'center',
                    border: '2px solid #0284c7'
                  }}>
                    <div style={{ fontSize: '11px', color: '#0369a1', marginBottom: '4px', fontWeight: '600' }}>
                      💰 Valor Total
                    </div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#0c4a6e' }}>
                      {formatCOP(valorTotal)}
                    </div>
                    <div style={{ fontSize: '9px', color: '#0284c7', marginTop: '4px' }}>
                      del Sobrante
                    </div>
                  </div>
                );
              })()}
            </div>
            
            {/* Detalle del cálculo */}
            <div style={{ fontSize: '10px', color: '#6b7280', textAlign: 'center', marginBottom: '8px' }}>
              = Venta efectivo - Facturas caja - Domicilios + Ingresos varios - Turnos
            </div>
            
            {/* 🔹 COMPARACIÓN: Lo que tiene vs Lo que debería tener */}
            {(() => {
              const efectivoReal = Number(ventas.efectivoEnCaja || 0);
              // Las facturas pendientes quedan en caja para el siguiente cuadre,
              // por lo que se restan del "debe quedar" (el empleado las retiene aparte).
              const deberiaQuedar = valorInicial - totalFacturasPendientes;
              const sobranteNeto = Number(ventas.sobrante || 0);
              // Fórmula: Tienes en caja - Debe quedar en caja - Sobrante neto = Diferencia
              // Si es 0 está cuadrado, si > 0 hay sobrante, si < 0 hay faltante
              const diferencia = efectivoReal - deberiaQuedar - sobranteNeto;
              const estaCuadrado = Math.abs(diferencia) < 1;

              return (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: estaCuadrado ? '#d1fae5' : '#fef2f2',
                  borderRadius: '12px',
                  textAlign: 'center',
                  border: `3px solid ${estaCuadrado ? '#10b981' : '#ef4444'}`
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '800',
                    color: estaCuadrado ? '#065f46' : '#dc2626',
                    marginBottom: '8px'
                  }}>
                    {estaCuadrado
                      ? "✅ ¡CUADRADO! El efectivo coincide"
                      : diferencia > 0
                        ? `⚠️ SOBRANTE: Tienes ${formatCOP(diferencia)} de más`
                        : `🚨 DESCUADRADO: Te faltan ${formatCOP(Math.abs(diferencia))}`
                    }
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', fontSize: '11px', flexWrap: 'wrap' }}>
                    <span style={{ color: '#374151' }}>
                      <strong>Tienes:</strong> {formatCOP(efectivoReal)}
                    </span>
                    <span style={{ color: '#374151' }}>
                      <strong>Debe quedar:</strong> {formatCOP(deberiaQuedar)}
                      {totalFacturasPendientes > 0 && (
                        <span style={{ color: '#6b7280', fontStyle: 'italic' }}> (sin fact. pend. {formatCOP(totalFacturasPendientes)})</span>
                      )}
                    </span>
                    <span style={{ color: '#6b7280' }}>
                      <strong>Sobrante neto:</strong> {formatCOP(sobranteNeto)}
                    </span>
                    <span style={{ color: estaCuadrado ? '#065f46' : (diferencia > 0 ? '#d97706' : '#dc2626'), fontWeight: '700' }}>
                      <strong>Diferencia:</strong> {diferencia >= 0 ? '+' : ''}{formatCOP(diferencia)}
                    </span>
                  </div>
                </div>
              );
            })()}
          </>
        )}
        
        {/* Mensaje si no ha confirmado */}
        {!efectivoConfirmado && (
          <div style={{ 
            padding: '12px 16px',
            backgroundColor: '#fef3c7',
            borderRadius: '12px',
            textAlign: 'center',
            border: '2px solid #f59e0b'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#92400e' }}>
              ⚠️ Ingresa cuánto efectivo tienes en caja y presiona "Confirmar" para verificar el cuadre
            </div>
          </div>
        )}
      </div>

      {/* Resumen total de ventas */}
      <div style={{ 
        marginTop: '16px', 
        padding: '12px', 
        backgroundColor: '#f8fafc', 
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '8px' }}>
          <span style={{ fontWeight: '600', color: '#475569' }}>
            Total efectivo: <span style={{ color: '#166534' }}>{formatCOP(Number(ventas.ventaEfectivo || 0))}</span>
          </span>
          <span style={{ fontWeight: '600', color: '#475569' }}>
            Total transferencias: <span style={{ color: '#059669' }}>{formatCOP((totalesTransfer?.bancolombia || 0) + (totalesTransfer?.davivienda || 0) + (totalesTransfer?.daviplata || 0) + (totalesTransfer?.nequi || 0))}</span>
          </span>
          <span style={{ fontWeight: '600', color: '#475569' }}>
            Total datáfonos: <span style={{ color: '#0369a1' }}>{formatCOP(totalDatafonos || 0)}</span>
          </span>
          <span style={{ fontWeight: '600', color: '#475569' }}>
            Total Online Rappi: <span style={{ color: '#dc2626' }}>{formatCOP(Number(ventas.onlineRappi || 0))}</span>
          </span>
        </div>
        <div style={{ textAlign: 'right', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
          <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '1.1em' }}>
            TOTAL VENTAS: {formatCOP(
              Number(ventas.ventaEfectivo || 0) + 
              (totalesTransfer?.bancolombia || 0) + 
              (totalesTransfer?.davivienda || 0) + 
              (totalesTransfer?.daviplata || 0) + 
              (totalesTransfer?.nequi || 0) + 
              (totalDatafonos || 0) +
              Number(ventas.onlineRappi || 0)
            )}
          </span>
        </div>
      </div>
    </section>
  );
}
 
/* ---------- FACTURAS ---------- */
 
function CuadroFacturas({ facturas, onChange, disabled, sede = "", filtroEmpresa = null, esGerente = false, responsableId = null, empleados = [], facturasEliminadas = [], onFacturasEliminadasChange = null, fechaCuadre = "", tienePermiso = () => true }) {
  // Estados para el modal de envío a pago
  const [modalEnviarPago, setModalEnviarPago] = useState(false);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [envioForm, setEnvioForm] = useState({
    valorPedido: "",
    fechaRecibido: "",
    soporte: null,
    soporteNombre: "",
    datosPago: "",
  });
  const [enviando, setEnviando] = useState(false);
  
  // Estado para modal de consulta de tarea
  const [modalConsulta, setModalConsulta] = useState(false);
  const [tareaConsulta, setTareaConsulta] = useState(null);
  const [cargandoTarea, setCargandoTarea] = useState(false);

  // 🔧 NOTA: Se permite tener facturas con el mismo número (diferentes clientes pueden pagar la misma factura)
  // Las facturas se identifican por ID único, no por número

  // Efecto para actualizar automáticamente el estado de las facturas con tareas pendientes
  useEffect(() => {
    async function actualizarEstadosTareas() {
      const facturasConTarea = facturas.filter(f => f.tareaEnviadaId && f.estadoTarea === "Pendiente");
      if (facturasConTarea.length === 0) return;
      
      let huboCambios = false;
      const copiaFacturas = [...facturas];
      
      for (const factura of facturasConTarea) {
        try {
          const { data, error } = await supabase
            .from("procesos_pago")
            .select("estado, detalles")
            .eq("id", factura.tareaEnviadaId)
            .maybeSingle();
          
          if (!error && data) {
            const estadoTarea = data.estado;
            if (estadoTarea === "Procesado" || estadoTarea === "Negado") {
              const idx = copiaFacturas.findIndex(f => f.id === factura.id);
              if (idx !== -1) {
                copiaFacturas[idx] = {
                  ...copiaFacturas[idx],
                  estadoTarea: estadoTarea === "Procesado" ? "Procesada" : "Negada",
                  // Guardar info de respuesta para mostrar en revisión
                  tareaRespuesta: {
                    estado: estadoTarea,
                    comentariosGestion: data.detalles?.comentariosGestion || "",
                    adjuntoGestion: data.detalles?.adjuntoGestion || null,
                    fechaRespuesta: data.detalles?.fechaGestion || new Date().toISOString().slice(0, 10),
                  }
                };
                huboCambios = true;
              }
            }
          }
        } catch (err) {
          console.error("Error actualizando estado de tarea:", err);
        }
      }
      
      if (huboCambios) {
        onChange(copiaFacturas);
      }
    }
    
    // Actualizar al montar el componente y cada 30 segundos
    actualizarEstadosTareas();
    const intervalo = setInterval(actualizarEstadosTareas, 30000);
    return () => clearInterval(intervalo);
  }, [facturas.length]); // Solo re-ejecutar si cambia la cantidad de facturas

  // Total facturas pagadas de caja
  const totalCaja = useMemo(
    () =>
      facturas
        .filter((f) => f.estado === "Pagada de caja")
        .reduce((s, f) => s + Number(f.valor || 0), 0),
    [facturas]
  );

  // Total facturas pendientes
  const totalPendientes = useMemo(
    () =>
      facturas
        .filter((f) => f.estado === "Pendiente")
        .reduce((s, f) => s + Number(f.valor || 0), 0),
    [facturas]
  );

  // Total facturas pagadas con transferencia
  const totalTransferencia = useMemo(
    () =>
      facturas
        .filter((f) => f.estado === "Pagada transferencia")
        .reduce((s, f) => s + Number(f.valor || 0), 0),
    [facturas]
  );

  // Formatear moneda
  const formatCOP = (val) => new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0
  }).format(val || 0);

  // Helper de fecha
  function getTomorrowISO() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
  }

  function getTodayISO() {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
  }
 
  function cambiar(idx, campo, valor) {
    const copia = facturas.map((f, i) =>
      i === idx ? { ...f, [campo]: valor, updatedAt: Date.now() } : f
    );
    onChange(copia);
  }
 
  function agregarFilas() {
    if (disabled) return;
    const n = Number(
      prompt("¿Cuántas facturas nuevas deseas crear?")
    );
    if (!n || n <= 0) return;
    const nuevas = Array.from({ length: n }).map(() => ({
      id: uid(),
      factura: "",
      valor: "",
      estado: "Pagada de caja",
      origenFecha: fechaCuadre || getTodayISO(), // Guardar la fecha de origen para rastreo
    }));
    onChange([...facturas, ...nuevas]);
  }

  function eliminarFila(id) {
    if (disabled) return;
    // Marcar la factura como eliminada (para sincronización colaborativa)
    const facturaAEliminar = facturas.find(f => f.id === id);
    if (facturaAEliminar && onFacturasEliminadasChange) {
      // También agregar el número de factura para evitar re-sincronización por número
      const numFactura = String(facturaAEliminar.factura || "").trim().toLowerCase();
      onFacturasEliminadasChange([...facturasEliminadas, { id, numFactura, eliminadoEn: new Date().toISOString() }]);
      console.log(`🗑️ Factura marcada como eliminada: ID=${id}, Num=${numFactura}`);
    }
    onChange(facturas.filter((f) => f.id !== id));
  }

  // Abrir modal de envío a pago
  function abrirModalEnviarPago(factura, idx) {
    setFacturaSeleccionada({ ...factura, idx });
    setEnvioForm({
      valorPedido: factura.valor || "",
      fechaRecibido: getTodayISO(),
      soporte: null,
      soporteNombre: "",
      datosPago: "",
    });
    setModalEnviarPago(true);
  }

  // Manejar archivo de soporte
  async function handleSoporteEnvio(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setEnvioForm(prev => ({ ...prev, soporte: file, soporteNombre: file.name }));
  }

  // Enviar factura a pago (crear tarea automática)
  async function enviarFacturaAPago() {
    if (!facturaSeleccionada) return;
    if (!envioForm.valorPedido) {
      alert("Ingresa el valor del pedido");
      return;
    }
    if (!envioForm.fechaRecibido) {
      alert("Ingresa la fecha de recibido del pedido");
      return;
    }

    setEnviando(true);
    try {
      // Subir soporte si existe
      let soporteUrl = null;
      let soporteNombre = null;
      if (envioForm.soporte) {
        const uploaded = await subirSoporteProcesoPago(envioForm.soporte, {
          carpeta: "facturas-transferencia",
          nombre: envioForm.soporteNombre,
        });
        if (uploaded) {
          soporteUrl = uploaded.url;
          soporteNombre = uploaded.nombre || envioForm.soporteNombre;
        }
      }

      // Crear tarea automática en Procesos Administrativos
      const nuevaTareaId = uid();
      
      // Obtener nombre del radicador (responsable del cuadre)
      console.log("📋 DEBUG - responsableId:", responsableId);
      console.log("📋 DEBUG - empleados recibidos:", empleados?.length);
      
      let nombreRadicador = "Sistema (Cuadre Diario)";
      
      // Primero buscar en la lista local de empleados
      const responsableEmpleado = empleados.find(e => e.id === responsableId);
      
      if (responsableEmpleado?.nombre) {
        nombreRadicador = responsableEmpleado.nombre;
        console.log("📋 Radicador encontrado localmente:", nombreRadicador);
      } else if (responsableId) {
        // Si no está en la lista local, buscar en Supabase
        console.log("📋 Buscando radicador en Supabase...");
        try {
          const { data: empData } = await supabase
            .from("empleados")
            .select("nombre, nombres")
            .eq("id", responsableId)
            .single();
          
          if (empData) {
            nombreRadicador = empData.nombre || empData.nombres || "Empleado";
            console.log("📋 Radicador encontrado en Supabase:", nombreRadicador);
          }
        } catch (err) {
          console.warn("⚠️ No se pudo buscar radicador en Supabase:", err);
        }
      }
      
      console.log("📋 nombreRadicador final:", nombreRadicador);
      
      const nuevaTarea = {
        id: nuevaTareaId,
        fechaCreacion: getTodayISO(),
        fechaLimite: getTomorrowISO(), // Vencimiento: día siguiente
        sede: sede,
        tipo: "Factura a pagar por transferencia",
        estado: "En gestión", // Estado inicial
        proveedor: facturaSeleccionada.factura || "Proveedor sin nombre",
        valorServPublico: envioForm.valorPedido,
        fechaReciboPedido: envioForm.fechaRecibido,
        referenciaPago: "",
        datosPago: envioForm.datosPago || "",
        soportes: soporteUrl ? [{ id: uid(), nombre: soporteNombre, url: soporteUrl }] : [],
        comentariosGestion: `Factura enviada desde cuadre diario - Sede: ${sede}`,
        // Campo para vincular con la factura del cuadre
        facturaOrigenId: facturaSeleccionada.id,
        origenCuadre: true,
        // RADICADOR - quien envía la factura desde el cuadre
        radicador: nombreRadicador,
        radicadorId: responsableId,
      };

      // Guardar en Supabase
      const tareaGuardada = await guardarProcesoPagoEnSupabase(nuevaTarea, filtroEmpresa);
      
      if (tareaGuardada) {
        // Actualizar la factura con el estado de envío
        const copiaFacturas = facturas.map((f, i) =>
          i === facturaSeleccionada.idx 
            ? { 
                ...f, 
                tareaEnviadaId: tareaGuardada.id,
                estadoTarea: "Pendiente",
                fechaEnvio: getTodayISO(),
              } 
            : f
        );
        onChange(copiaFacturas);
        
        // Enviar SMS de notificación - buscar teléfono configurado desde configuracion_sistema
        try {
          // Buscar configuración de notificaciones (tabla configuracion_sistema)
          const { data: configData, error: configError } = await supabase
            .from("configuracion_sistema")
            .select("telefono_notificaciones_tareas, nombre_receptor_tareas")
            .eq("id", "principal")
            .single();
          
          console.log("📱 Config SMS encontrada:", configData, "Error:", configError);
          
          const telefonoNotif = configData?.telefono_notificaciones_tareas;
          
          if (telefonoNotif) {
            const formatCOP = (val) => new Intl.NumberFormat("es-CO", {
              style: "currency",
              currency: "COP",
              minimumFractionDigits: 0
            }).format(val || 0);
            
            const mensajeSMS = `Nueva Tarea
Tipo: Factura por transferencia
Sede: ${sede}
Proveedor: ${facturaSeleccionada.factura || "N/A"}
Valor: ${formatCOP(envioForm.valorPedido)}
Radicado por: ${nombreRadicador}
Revise Procesos Administrativos.`;

            const resultadoSMS = await enviarSMS(telefonoNotif, mensajeSMS);
            console.log("📱 SMS enviado para tarea de factura. Teléfono:", telefonoNotif, "Resultado:", resultadoSMS);
          } else {
            console.warn("⚠️ No hay teléfono de notificaciones configurado. Configure en Procesos Administrativos → Configurar SMS");
          }
        } catch (smsError) {
          console.warn("⚠️ No se pudo enviar SMS:", smsError);
        }
        
        alert("✅ Factura enviada a pago exitosamente.\n\nSe ha creado una tarea en Procesos Administrativos.");
        setModalEnviarPago(false);
        setFacturaSeleccionada(null);
      } else {
        alert("❌ Error al crear la tarea. Intenta de nuevo.");
      }
    } catch (err) {
      console.error("Error enviando factura a pago:", err);
      alert("❌ Error al enviar la factura: " + err.message);
    } finally {
      setEnviando(false);
    }
  }

  // Consultar tarea vinculada
  async function consultarTarea(facturaId, tareaId) {
    if (!tareaId) return;
    setCargandoTarea(true);
    setModalConsulta(true);
    
    try {
      const { data, error } = await supabase
        .from("procesos_pago")
        .select("*")
        .eq("id", tareaId)
        .maybeSingle();
      
      if (error) {
        console.error("Error consultando tarea:", error);
        setTareaConsulta(null);
      } else if (data) {
        const tareaMapeada = mapearProcesoPagoDesdeSupabase(data);
        setTareaConsulta(tareaMapeada);
        
        // Si la tarea está procesada, actualizar el estado en la factura
        if (tareaMapeada.estado === "Procesado") {
          const copiaFacturas = facturas.map((f) =>
            f.id === facturaId 
              ? { ...f, estadoTarea: "Procesada" } 
              : f
          );
          onChange(copiaFacturas);
        }
      }
    } catch (err) {
      console.error("Error:", err);
      setTareaConsulta(null);
    } finally {
      setCargandoTarea(false);
    }
  }
 
  return (
    <section className="subcard">
      <div className="subcard-header">
        <h3>Facturas</h3>
        <Button variant="outline" onClick={agregarFilas} disabled={disabled}>
          Crear nuevas facturas
        </Button>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Factura</th>
            <th>Valor</th>
            <th>Estado</th>
            <th>Origen (si venía pendiente)</th>
            <th>Acción</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {facturas.map((f, idx) => (
            <tr key={f.id}>
              <td>
                <input
                  className="input"
                  disabled={disabled}
                  value={f.factura}
                  onChange={(e) =>
                    cambiar(idx, "factura", e.target.value)
                  }
                />
              </td>
              <td>
                <input
                  className="input"
                  type="number"
                  disabled={disabled}
                  value={f.valor}
                  onChange={(e) =>
                    cambiar(idx, "valor", e.target.value)
                  }
                />
              </td>
              <td>
                <select
                  className="input"
                  disabled={disabled}
                  value={f.estado}
                  onChange={(e) =>
                    cambiar(idx, "estado", e.target.value)
                  }
                >
                  <option>Pagada de caja</option>
                  <option>Pendiente</option>
                  <option>Pagada transferencia</option>
                </select>
              </td>
              <td style={{ fontSize: 11, color: "#6b7280" }}>
                {f.origenFecha && f.origenFecha !== fechaCuadre
                  ? `Pendiente del ${f.origenFecha}`
                  : f.estado === "Pendiente" && !f.origenFecha
                    ? "(sin registro)"
                    : "—"}
              </td>
              <td style={{ minWidth: 140 }}>
                {/* Botón Enviar a Pago - solo si es transferencia y no se ha enviado */}
                {f.estado === "Pagada transferencia" && !f.tareaEnviadaId && (
                  <button
                    type="button"
                    onClick={() => abrirModalEnviarPago(f, idx)}
                    disabled={disabled}
                    style={{
                      padding: "6px 10px",
                      fontSize: 11,
                      fontWeight: 600,
                      border: "none",
                      borderRadius: 6,
                      cursor: disabled ? "not-allowed" : "pointer",
                      background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                      color: "white",
                    }}
                  >
                    📤 Enviar a pago
                  </button>
                )}
                
                {/* Estado Pendiente - tarea enviada pero no procesada */}
                {f.tareaEnviadaId && f.estadoTarea === "Pendiente" && (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <span style={{
                      padding: "4px 8px",
                      fontSize: 10,
                      fontWeight: 600,
                      borderRadius: 4,
                      backgroundColor: "#fef3c7",
                      color: "#92400e",
                    }}>
                      ⏳ Pendiente
                    </span>
                    <button
                      type="button"
                      onClick={() => consultarTarea(f.id, f.tareaEnviadaId)}
                      style={{
                        padding: "4px 8px",
                        fontSize: 10,
                        fontWeight: 600,
                        border: "1px solid #3b82f6",
                        borderRadius: 4,
                        cursor: "pointer",
                        background: "white",
                        color: "#3b82f6",
                      }}
                    >
                      🔍 Consultar
                    </button>
                  </div>
                )}
                
                {/* Estado Procesada */}
                {f.tareaEnviadaId && f.estadoTarea === "Procesada" && (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <span style={{
                      padding: "4px 8px",
                      fontSize: 10,
                      fontWeight: 600,
                      borderRadius: 4,
                      backgroundColor: "#d1fae5",
                      color: "#065f46",
                    }}>
                      ✅ Procesada
                    </span>
                    <button
                      type="button"
                      onClick={() => consultarTarea(f.id, f.tareaEnviadaId)}
                      style={{
                        padding: "4px 8px",
                        fontSize: 10,
                        fontWeight: 600,
                        border: "1px solid #10b981",
                        borderRadius: 4,
                        cursor: "pointer",
                        background: "white",
                        color: "#10b981",
                      }}
                    >
                      🔍 Consultar
                    </button>
                  </div>
                )}
              </td>
              <td>
                {/* 🔹 Lógica de eliminación de facturas:
                    - Si es factura por transferencia con tarea enviada (Pendiente/Procesada/Negada):
                      Solo puede eliminar si es gerente O tiene permiso admin_cuadre_facturas_eliminar_gestion
                    - Si no es factura en gestión: cualquiera puede eliminar (si no está disabled)
                */}
                {(() => {
                  const esFacturaEnGestion = f.estado === "Pagada transferencia" && f.tareaEnviadaId && 
                    (f.estadoTarea === "Pendiente" || f.estadoTarea === "Procesada" || f.estadoTarea === "Negada" || !f.estadoTarea);
                  const puedeEliminarGestion = esGerente || tienePermiso("admin_cuadre_facturas_eliminar_gestion");
                  
                  // Si es factura en gestión, solo mostrar botón si tiene permiso
                  if (esFacturaEnGestion && !puedeEliminarGestion) {
                    return (
                      <span style={{ 
                        fontSize: 10, 
                        color: '#9ca3af', 
                        fontStyle: 'italic',
                        display: 'block',
                        textAlign: 'center'
                      }}>
                        🔒 Protegida
                      </span>
                    );
                  }
                  
                  // Mostrar botón eliminar normalmente
                  return (
                    <Button
                      variant="danger"
                      onClick={() => eliminarFila(f.id)}
                      disabled={disabled}
                    >
                      X
                    </Button>
                  );
                })()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Resumen de gastos/facturas */}
      <div style={{ 
        marginTop: '16px', 
        padding: '12px', 
        backgroundColor: '#fef2f2', 
        borderRadius: '8px',
        border: '1px solid #fecaca'
      }}>
        <h4 style={{ margin: '0 0 12px 0', color: '#991b1b' }}>📋 Resumen de Facturas por Tipo de Pago</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
          {/* Facturas Pendientes */}
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#fef3c7', 
            borderRadius: '8px', 
            border: '1px solid #f59e0b',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '4px' }}>⏳ Facturas Pendientes</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc2626' }}>{formatCOP(totalPendientes)}</div>
          </div>
          
          {/* Facturas Pagadas de Caja */}
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#d1fae5', 
            borderRadius: '8px', 
            border: '1px solid #10b981',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '12px', color: '#065f46', marginBottom: '4px' }}>💵 Pagadas de Caja</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#059669' }}>{formatCOP(totalCaja)}</div>
          </div>
          
          {/* Facturas Pagadas por Transferencia */}
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#dbeafe', 
            borderRadius: '8px', 
            border: '1px solid #3b82f6',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '12px', color: '#1e40af', marginBottom: '4px' }}>🏦 Pagadas Transferencia</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#0369a1' }}>{formatCOP(totalTransferencia)}</div>
          </div>
        </div>
      </div>

      {/* MODAL: Enviar a Pago */}
      {modalEnviarPago && (
        <div style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: 12,
            padding: 24,
            width: 450,
            maxWidth: "95vw",
            boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
          }}>
            <h3 style={{ 
              margin: "0 0 16px 0", 
              fontSize: 16, 
              fontWeight: 700,
              color: "#1e40af",
              display: "flex",
              alignItems: "center",
              gap: 8,
              borderBottom: "2px solid #3b82f6",
              paddingBottom: 10,
            }}>
              📤 Enviar Factura a Pago por Transferencia
            </h3>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                Proveedor
              </label>
              <input
                type="text"
                disabled
                value={facturaSeleccionada?.factura || ""}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 13,
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  backgroundColor: "#f3f4f6",
                }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                Valor del pedido *
              </label>
              <input
                type="number"
                value={envioForm.valorPedido}
                onChange={(e) => setEnvioForm(prev => ({ ...prev, valorPedido: e.target.value }))}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 13,
                  border: "1px solid #3b82f6",
                  borderRadius: 6,
                }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                Fecha de recibido del pedido *
              </label>
              <input
                type="date"
                value={envioForm.fechaRecibido}
                onChange={(e) => setEnvioForm(prev => ({ ...prev, fechaRecibido: e.target.value }))}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 13,
                  border: "1px solid #3b82f6",
                  borderRadius: 6,
                }}
              />
            </div>

            {/* DATOS PARA EL PAGO */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                💳 Datos para el pago
              </label>
              <textarea
                value={envioForm.datosPago}
                onChange={(e) => setEnvioForm(prev => ({ ...prev, datosPago: e.target.value }))}
                placeholder="Ej: Banco Bancolombia, Cuenta Ahorros 123456789, NIT 900123456"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 13,
                  border: "1px solid #3b82f6",
                  borderRadius: 6,
                  minHeight: 70,
                  resize: "vertical",
                }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
                Adjuntar factura (opcional)
              </label>
              <input
                type="file"
                onChange={handleSoporteEnvio}
                style={{
                  width: "100%",
                  padding: "8px",
                  fontSize: 12,
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                }}
              />
              {envioForm.soporteNombre && (
                <div style={{ fontSize: 11, color: "#059669", marginTop: 4 }}>
                  📎 {envioForm.soporteNombre}
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  setModalEnviarPago(false);
                  setFacturaSeleccionada(null);
                }}
                style={{
                  padding: "10px 20px",
                  fontSize: 13,
                  fontWeight: 600,
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  cursor: "pointer",
                  background: "white",
                  color: "#374151",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={enviarFacturaAPago}
                disabled={enviando}
                style={{
                  padding: "10px 20px",
                  fontSize: 13,
                  fontWeight: 600,
                  border: "none",
                  borderRadius: 8,
                  cursor: enviando ? "not-allowed" : "pointer",
                  background: enviando ? "#9ca3af" : "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                  color: "white",
                }}
              >
                {enviando ? "Enviando..." : "✅ Guardar y Crear Tarea"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Consultar Tarea */}
      {modalConsulta && (
        <div style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: 12,
            padding: 24,
            width: 500,
            maxWidth: "95vw",
            maxHeight: "80vh",
            overflowY: "auto",
            boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
          }}>
            <h3 style={{ 
              margin: "0 0 16px 0", 
              fontSize: 16, 
              fontWeight: 700,
              color: "#065f46",
              display: "flex",
              alignItems: "center",
              gap: 8,
              borderBottom: "2px solid #10b981",
              paddingBottom: 10,
            }}>
              🔍 Consulta de Tarea
            </h3>

            {cargandoTarea ? (
              <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>
                Cargando información...
              </div>
            ) : tareaConsulta ? (
              <div>
                <div style={{ 
                  padding: 12, 
                  backgroundColor: tareaConsulta.estado === "Procesado" ? "#d1fae5" : "#fef3c7",
                  borderRadius: 8,
                  marginBottom: 16,
                }}>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Estado actual</div>
                  <div style={{ 
                    fontSize: 18, 
                    fontWeight: 700, 
                    color: tareaConsulta.estado === "Procesado" ? "#065f46" : "#92400e" 
                  }}>
                    {tareaConsulta.estado === "Procesado" ? "✅ Procesada" : "⏳ " + tareaConsulta.estado}
                  </div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Proveedor</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{tareaConsulta.proveedor || "—"}</div>
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Valor a pagar</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{formatCOP(tareaConsulta.valorServPublico || 0)}</div>
                </div>

                {tareaConsulta.referenciaPago && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>Referencia de pago</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{tareaConsulta.referenciaPago}</div>
                  </div>
                )}

                {tareaConsulta.comentariosGestion && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>Comentarios</div>
                    <div style={{ 
                      fontSize: 13, 
                      padding: 10, 
                      backgroundColor: "#f3f4f6", 
                      borderRadius: 6,
                      whiteSpace: "pre-wrap",
                    }}>
                      {tareaConsulta.comentariosGestion}
                    </div>
                  </div>
                )}

                {/* Soportes adjuntos */}
                {tareaConsulta.soportes && tareaConsulta.soportes.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Archivos adjuntos</div>
                    {tareaConsulta.soportes.map((s, i) => (
                      <a
                        key={i}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "block",
                          padding: "8px 12px",
                          backgroundColor: "#eff6ff",
                          borderRadius: 6,
                          marginBottom: 4,
                          fontSize: 12,
                          color: "#1d4ed8",
                          textDecoration: "none",
                        }}
                      >
                        📎 {s.nombre || `Archivo ${i + 1}`}
                      </a>
                    ))}
                  </div>
                )}

                {/* Adjunto de gestión */}
                {tareaConsulta.adjuntoGestion && tareaConsulta.adjuntoGestion.url && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>Comprobante de gestión</div>
                    <a
                      href={tareaConsulta.adjuntoGestion.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "block",
                        padding: "8px 12px",
                        backgroundColor: "#d1fae5",
                        borderRadius: 6,
                        fontSize: 12,
                        color: "#065f46",
                        textDecoration: "none",
                      }}
                    >
                      📎 {tareaConsulta.adjuntoGestion.nombre || "Comprobante"}
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: 40, color: "#dc2626" }}>
                No se encontró información de la tarea.
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
              <button
                onClick={() => {
                  setModalConsulta(false);
                  setTareaConsulta(null);
                }}
                style={{
                  padding: "10px 24px",
                  fontSize: 13,
                  fontWeight: 600,
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                  background: "#e5e7eb",
                  color: "#374151",
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
 
/* ---------- DOMICILIOS ---------- */
 
function CuadroDomicilios({ domicilios, onChange, disabled, bloquearFila, desbloquearFila, filaEstaBloqueadaPorOtro, fecha, sede, transferencias = [] }) {
  const [cargandoFudo, setCargandoFudo] = useState(false);
  const [mensajeFudo, setMensajeFudo] = useState(null);

  // 🔹 SEDES CONFIGURADAS PARA FUDO - Solo estas sedes usan la integración automática
  const SEDES_CON_FUDO = ['CORALES', 'VILLA DEL PRADO', 'AV SUR'];
  const sedeUsaFudo = SEDES_CON_FUDO.includes((sede || '').toUpperCase());

  // ✅ Set de pedidos que fueron pagados por transferencia (para marcar con chulo verde)
  const pedidosConTransferencia = useMemo(() => {
    const set = new Set();
    transferencias.forEach(t => {
      // Solo considerar transferencias que vienen de FUDO o tienen valores
      const pedido = String(t.pedido || '').trim();
      const tieneValor = Number(t.bancolombia || 0) > 0 || 
                        Number(t.davivienda || 0) > 0 || 
                        Number(t.daviplata || 0) > 0 || 
                        Number(t.nequi || 0) > 0;
      if (pedido && tieneValor) {
        set.add(pedido);
      }
    });
    return set;
  }, [transferencias]);

  // Total de valor domicilio + cobro datafono (para cálculos internos del cuadre)
  const total = useMemo(
    () =>
      domicilios
        .filter(d => d.estadoFudo !== 'eliminado_fudo')
        .reduce((s, d) => s + Number(d.valor || 0) + Number(d.cobroDatafono || 0), 0),
    [domicilios]
  );

  // Total solo de cobro datafono (para mostrar separado)
  const totalCobroDatafono = useMemo(
    () =>
      domicilios
        .filter(d => d.estadoFudo !== 'eliminado_fudo')
        .reduce((s, d) => s + Number(d.cobroDatafono || 0), 0),
    [domicilios]
  );

  // Total de valorPedido del FUDO (excluyendo eliminados)
  const totalValorPedido = useMemo(
    () =>
      domicilios
        .filter(d => d.estadoFudo !== 'eliminado_fudo')
        .reduce((s, d) => s + Number(d.valorPedido || 0), 0),
    [domicilios]
  );

  // Contar eliminados para mostrar
  const totalEliminados = useMemo(
    () => domicilios.filter(d => d.estadoFudo === 'eliminado_fudo').length,
    [domicilios]
  );

  // Contar confirmaciones de domicilios
  const confirmaciones = useMemo(() => {
    let confirmados = 0, pendientes = 0;
    domicilios.forEach((d) => {
      if (Number(d.valor || 0) > 0 && d.pago && d.pago !== '' && d.pago !== 'Pendiente') {
        if (d.pago_confirmado) confirmados++; else pendientes++;
      }
    });
    return { confirmados, pendientes };
  }, [domicilios]);
 
  function cambiar(idx, campo, valor) {
    const copia = domicilios.map((d, i) =>
      i === idx ? { ...d, [campo]: valor } : d
    );
    onChange(copia);
  }
 
  function agregarFilas() {
    if (disabled) return;
    const n = Number(prompt("¿Cuántas filas de domicilios deseas agregar?"));
    if (!n || n <= 0) return;
    const nuevas = Array.from({ length: n }).map(() => ({
      id: uid(),
      pedido: "",
      valor: "",
      valorPedido: "",
      pago: "",
      pago_confirmado: false,
      obs: "",
      fudoId: null,
      estadoFudo: null
    }));
    onChange([...domicilios, ...nuevas]);
  }
 
  function eliminarFila(id) {
    if (disabled) return;
    onChange(domicilios.filter((d) => d.id !== id));
  }

  // Función para traer domicilios desde FUDO
  async function traerDomiciliosDeFudo() {
    if (disabled || !fecha || !sede) {
      setMensajeFudo({ tipo: 'error', texto: 'Faltan datos de fecha o sede' });
      return;
    }

    setCargandoFudo(true);
    setMensajeFudo(null);

    try {
      // Agregar timestamp para evitar cache
      const timestamp = Date.now();
      const url = `https://intranetbigburguer.com/api/api_fudo_proxy?accion=traer_domicilios&sede=${encodeURIComponent(sede)}&fecha=${fecha}&_t=${timestamp}`;
      const response = await fetch(url);
      const data = await response.json();

      if (!data.success) {
        setMensajeFudo({ tipo: 'error', texto: data.error || 'Error al consultar FUDO' });
        return;
      }

      if (data.domicilios.length === 0) {
        setMensajeFudo({ tipo: 'info', texto: 'No se encontraron domicilios en FUDO para esta fecha' });
        return;
      }

      // 🕐 FILTRO DE SEGURIDAD: Convertir fecha UTC a Colombia y filtrar
      // FUDO devuelve fechas en UTC, Colombia está en UTC-5
      const convertirFechaUTCaColombia = (fechaISO) => {
        if (!fechaISO) return '';
        try {
          const fechaUTC = new Date(fechaISO);
          // Restar 5 horas (Colombia UTC-5)
          const fechaColombia = new Date(fechaUTC.getTime() - (5 * 60 * 60 * 1000));
          const year = fechaColombia.getUTCFullYear();
          const month = String(fechaColombia.getUTCMonth() + 1).padStart(2, '0');
          const day = String(fechaColombia.getUTCDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        } catch (e) {
          return fechaISO.split('T')[0] || '';
        }
      };

      // Filtrar domicilios que realmente corresponden a la fecha del cuadre
      const domiciliosFiltrados = data.domicilios.filter(d => {
        // Si el proxy ya envía la fecha convertida, usarla directamente
        // Si no, intentar convertir desde fechaOriginalUTC
        const fechaDomicilio = d.fechaOriginalUTC 
          ? convertirFechaUTCaColombia(d.fechaOriginalUTC) 
          : d.fecha;
        return fechaDomicilio === fecha;
      });

      console.log(`🕐 Domicilios: ${data.domicilios.length} recibidos, ${domiciliosFiltrados.length} de fecha ${fecha}`);

      if (domiciliosFiltrados.length === 0) {
        setMensajeFudo({ tipo: 'info', texto: `No se encontraron domicilios para ${fecha} (${data.domicilios.length} de otras fechas)` });
        return;
      }

      // Procesar domicilios filtrados: actualizar existentes o agregar nuevos
      // IMPORTANTE: Verificar duplicados tanto por fudoId como por número de pedido
      let nuevos = 0, actualizados = 0, sinCambios = 0, omitidosDuplicados = 0;
      const domiciliosActualizados = [...domicilios];

      for (const domFudo of domiciliosFiltrados) {
        const fudoId = domFudo.pedidoId;
        const pedidoStr = String(fudoId);
        
        // Buscar por fudoId primero
        let existenteIdx = domiciliosActualizados.findIndex(d => d.fudoId === fudoId);
        
        // Si no existe por fudoId, buscar por número de pedido (para detectar manuales)
        if (existenteIdx < 0) {
          existenteIdx = domiciliosActualizados.findIndex(d => 
            !d.fudoId && String(d.pedido) === pedidoStr
          );
        }

        if (existenteIdx >= 0) {
          // Actualizar pedido existente
          const existente = domiciliosActualizados[existenteIdx];
          const cambioValor = Number(existente.valorPedido || 0) !== Number(domFudo.valorPedido || 0);
          const eraManual = !existente.fudoId; // Era un registro manual
          // 🏠 Conservar la dirección editada manualmente; si no hay, tomar la de FUDO
          const direccionActualizada = existente.direccion || domFudo.direccion || '';
          
          if (cambioValor || eraManual) {
            domiciliosActualizados[existenteIdx] = {
              ...existente,
              fudoId: fudoId, // Vincular con FUDO si era manual
              valorPedido: domFudo.valorPedido,
              direccion: direccionActualizada,
              estadoFudo: eraManual ? 'vinculado' : 'actualizado'
            };
            actualizados++;
          } else if (!existente.direccion && domFudo.direccion) {
            // Solo llegó la dirección nueva desde FUDO
            domiciliosActualizados[existenteIdx] = { ...existente, direccion: domFudo.direccion };
            actualizados++;
          } else {
            sinCambios++;
          }
        } else {
          // Verificar que no exista ya con el mismo número de pedido (cualquier forma)
          const yaExiste = domiciliosActualizados.some(d => 
            String(d.pedido) === pedidoStr || String(d.fudoId) === pedidoStr
          );
          
          if (yaExiste) {
            omitidosDuplicados++;
            continue;
          }
          
          // Agregar nuevo domicilio
          domiciliosActualizados.push({
            id: uid(),
            fudoId: fudoId,
            pedido: fudoId,
            valor: domFudo.valorDomicilio || 0,
            valorPedido: domFudo.valorPedido || 0,
            pago: '',
            pago_confirmado: false,
            obs: domFudo.tipoDomicilio || '',
            direccion: domFudo.direccion || '',
            estadoFudo: 'nuevo',
            origenFudo: domFudo.origenDomicilio || 'producto'
          });
          nuevos++;
        }
      }

      // 🗑️ ELIMINAR de verdad los domicilios que se borraron en FUDO
      // (tenían fudoId pero ya no están en la respuesta de FUDO). Los manuales (sin fudoId) se conservan.
      const fudoIdsActuales = data.domicilios.map(d => d.pedidoId);
      const domiciliosFinales = domiciliosActualizados.filter(d => {
        const eliminadoEnFudo = d.fudoId && !fudoIdsActuales.includes(d.fudoId);
        return !eliminadoEnFudo;
      });
      const eliminados = domiciliosActualizados.length - domiciliosFinales.length;

      onChange(domiciliosFinales);
      
      const mensajes = [];
      if (nuevos > 0) mensajes.push(`${nuevos} nuevo${nuevos > 1 ? 's' : ''}`);
      if (actualizados > 0) mensajes.push(`${actualizados} actualizado${actualizados > 1 ? 's' : ''}`);
      if (sinCambios > 0) mensajes.push(`${sinCambios} sin cambios`);
      if (eliminados > 0) mensajes.push(`${eliminados} eliminado${eliminados > 1 ? 's' : ''} de FUDO`);
      if (omitidosDuplicados > 0) mensajes.push(`${omitidosDuplicados} omitido${omitidosDuplicados > 1 ? 's' : ''} (ya existían)`);
      if (data.pedidosRappiExcluidos > 0) mensajes.push(`${data.pedidosRappiExcluidos} Rappi excluidos`);
      
      setMensajeFudo({ 
        tipo: 'success', 
        texto: `✅ Sincronizado: ${mensajes.join(', ')}` 
      });

    } catch (error) {
      console.error('Error trayendo domicilios de FUDO:', error);
      setMensajeFudo({ tipo: 'error', texto: 'Error de conexión con FUDO' });
    } finally {
      setCargandoFudo(false);
      // Limpiar mensaje después de 5 segundos
      setTimeout(() => setMensajeFudo(null), 5000);
    }
  }
 
  return (
    <section className="subcard" style={{ borderLeft: '5px solid #f97316', background: 'linear-gradient(to right, rgba(249, 115, 22, 0.03), transparent)' }}>
      {/* Banner distintivo DOMICILIOS */}
      <div style={{
        background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
        color: 'white',
        padding: '12px 20px',
        marginBottom: '16px',
        borderRadius: '8px 8px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 2px 8px rgba(249, 115, 22, 0.3)'
      }}>
        <span style={{ fontSize: '32px' }}>🛵</span>
        <div>
          <div style={{ fontSize: '18px', fontWeight: '700', letterSpacing: '1px' }}>SECCIÓN DOMICILIOS</div>
          <div style={{ fontSize: '12px', opacity: 0.9 }}>Pagos por entrega a domicilio</div>
        </div>
      </div>
      <div className="subcard-header">
        <h3>Registro de domicilios</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
          {confirmaciones.pendientes > 0 && (
            <span style={{ 
              backgroundColor: '#fef3c7', 
              color: '#92400e', 
              padding: '4px 10px', 
              borderRadius: '12px', 
              fontSize: '12px',
              fontWeight: '600'
            }}>
              ⏳ {confirmaciones.pendientes} pendiente{confirmaciones.pendientes > 1 ? 's' : ''}
            </span>
          )}
          {confirmaciones.confirmados > 0 && (
            <span style={{ 
              backgroundColor: '#d1fae5', 
              color: '#065f46', 
              padding: '4px 10px', 
              borderRadius: '12px', 
              fontSize: '12px',
              fontWeight: '600'
            }}>
              ✓ {confirmaciones.confirmados} confirmado{confirmaciones.confirmados > 1 ? 's' : ''}
            </span>
          )}
          {/* Botón Traer de FUDO - Solo para sedes configuradas */}
          {sedeUsaFudo && fecha && sede && (
            <Button 
              variant="outline" 
              onClick={traerDomiciliosDeFudo} 
              disabled={disabled || cargandoFudo}
              style={{
                backgroundColor: cargandoFudo ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                fontWeight: '600'
              }}
            >
              {cargandoFudo ? '⏳ Cargando...' : '🔄 Traer de FUDO'}
            </Button>
          )}
          <Button variant="outline" onClick={agregarFilas} disabled={disabled}>
            Agregar filas (preguntar cuántas)
          </Button>
        </div>
      </div>
      {/* Mensaje de estado FUDO */}
      {mensajeFudo && (
        <div style={{
          padding: '10px 16px',
          marginBottom: '12px',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: '500',
          backgroundColor: mensajeFudo.tipo === 'success' ? '#d1fae5' : 
                          mensajeFudo.tipo === 'error' ? '#fee2e2' : '#e0f2fe',
          color: mensajeFudo.tipo === 'success' ? '#065f46' : 
                 mensajeFudo.tipo === 'error' ? '#991b1b' : '#0369a1',
          border: `1px solid ${mensajeFudo.tipo === 'success' ? '#a7f3d0' : 
                                mensajeFudo.tipo === 'error' ? '#fecaca' : '#bae6fd'}`
        }}>
          {mensajeFudo.texto}
        </div>
      )}
      <table className="table">
        <thead style={{ background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)' }}>
          <tr>
            <th style={{ color: '#c2410c' }}>📦 # Pedido</th>
            <th style={{ color: '#c2410c' }}>📊 Valor pedido</th>
            <th style={{ color: '#c2410c' }}>💵 Valor domicilio</th>
            <th style={{ color: '#c2410c' }}>🏧 Cobro datafono</th>
            <th style={{ color: '#c2410c', width: '200px', minWidth: '180px' }}>Dirección</th>
            <th style={{ color: '#c2410c', width: '90px' }}>Observaciones</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {domicilios.map((d, idx) => {
            // 🔒 Verificar si esta fila está bloqueada por otro usuario
            const bloqueoPorOtro = filaEstaBloqueadaPorOtro ? filaEstaBloqueadaPorOtro(d.id) : null;
            const filaBloqueada = !!bloqueoPorOtro;
            
            return (
            <tr key={d.id} style={{ 
              backgroundColor: filaBloqueada ? '#fef3c7' : 'transparent',
              position: 'relative'
            }}>
              {/* Indicador de bloqueo */}
              {filaBloqueada && (
                <td style={{ 
                  position: 'absolute', 
                  left: 0, 
                  right: 0, 
                  top: 0,
                  backgroundColor: 'rgba(251, 191, 36, 0.9)',
                  color: '#92400e',
                  fontSize: '11px',
                  padding: '2px 8px',
                  fontWeight: 600,
                  zIndex: 10,
                  textAlign: 'center'
                }}>
                  🔒 Editando: {bloqueoPorOtro.usuarioNombre}
                </td>
              )}
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input
                    className="input"
                    disabled={disabled || filaBloqueada}
                    value={d.pedido}
                    onChange={(e) =>
                      cambiar(idx, "pedido", e.target.value)
                    }
                    onFocus={() => bloquearFila && bloquearFila(d.id)}
                    onBlur={() => desbloquearFila && desbloquearFila(d.id)}
                    style={{ opacity: filaBloqueada ? 0.6 : 1, flex: 1 }}
                  />
                  {/* ✅ Indicador de pago por transferencia verificado */}
                  {pedidosConTransferencia.has(String(d.pedido || '').trim()) && (
                    <span 
                      title="Pagado por transferencia" 
                      style={{ 
                        color: '#22c55e', 
                        fontSize: '16px',
                        cursor: 'help'
                      }}
                    >
                      ✅
                    </span>
                  )}
                </div>
              </td>
              <td>
                <input
                  className="input"
                  type="number"
                  disabled={true}
                  value={d.valorPedido || ''}
                  placeholder="0"
                  style={{ opacity: 0.6, backgroundColor: '#f3f4f6' }}
                />
              </td>
              <td>
                <input
                  className="input"
                  type="number"
                  disabled={disabled || filaBloqueada}
                  value={d.valor}
                  onChange={(e) =>
                    cambiar(idx, "valor", e.target.value)
                  }
                  onFocus={() => bloquearFila && bloquearFila(d.id)}
                  onBlur={() => desbloquearFila && desbloquearFila(d.id)}
                  style={{ opacity: filaBloqueada ? 0.6 : 1 }}
                />
              </td>
              <td>
                <input
                  className="input"
                  type="number"
                  disabled={disabled || filaBloqueada}
                  placeholder="0"
                  value={d.cobroDatafono || ""}
                  onChange={(e) =>
                    cambiar(idx, "cobroDatafono", e.target.value)
                  }
                  onFocus={() => bloquearFila && bloquearFila(d.id)}
                  onBlur={() => desbloquearFila && desbloquearFila(d.id)}
                  style={{ 
                    opacity: filaBloqueada ? 0.6 : 1,
                    backgroundColor: '#f0fdf4'
                  }}
                />
              </td>
              <td>
                <textarea
                  className="textarea"
                  readOnly
                  rows={2}
                  value={d.direccion || ''}
                  placeholder="Sin dirección"
                  title={d.direccion || ''}
                  tabIndex={-1}
                  style={{
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    cursor: 'default',
                    resize: 'none',
                    width: '100%',
                    minWidth: '180px'
                  }}
                ></textarea>
              </td>
              <td>
                <textarea
                  className="textarea"
                  disabled={disabled || filaBloqueada}
                  rows={2}
                  value={d.obs}
                  onChange={(e) =>
                    cambiar(idx, "obs", e.target.value)
                  }
                  onFocus={() => bloquearFila && bloquearFila(d.id)}
                  onBlur={() => desbloquearFila && desbloquearFila(d.id)}
                  style={{ opacity: filaBloqueada ? 0.6 : 1, width: '100%', maxWidth: '90px' }}
                ></textarea>
              </td>
              <td>
                <Button
                  variant="danger"
                  onClick={() => eliminarFila(d.id)}
                  disabled={disabled || filaBloqueada}
                >
                  X
                </Button>
              </td>
            </tr>
          );
          })}
        </tbody>
        <tfoot>
          <tr style={{ backgroundColor: '#f8fafc' }}>
            <td style={{ textAlign: "right", fontWeight: 600, color: '#64748b' }}>
              Totales:
            </td>
            <td style={{ fontWeight: 700, color: '#0369a1' }}>
              {new Intl.NumberFormat("es-CO", {
                style: "currency",
                currency: "COP",
              }).format(totalValorPedido)}
            </td>
            <td style={{ fontWeight: 700, color: '#f97316' }}>
              {new Intl.NumberFormat("es-CO", {
                style: "currency",
                currency: "COP",
              }).format(total)}
            </td>
            <td style={{ fontWeight: 700, color: '#166534' }}>
              {new Intl.NumberFormat("es-CO", {
                style: "currency",
                currency: "COP",
              }).format(totalCobroDatafono)}
            </td>
            <td colSpan={3}>
              {totalEliminados > 0 && (
                <span style={{ 
                  fontSize: '11px', 
                  color: '#dc2626',
                  backgroundColor: '#fee2e2',
                  padding: '2px 8px',
                  borderRadius: '4px'
                }}>
                  ⚠️ {totalEliminados} eliminado{totalEliminados > 1 ? 's' : ''} (no suman)
                </span>
              )}
            </td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}
 
/* ---------- TRANSFERENCIAS ---------- */
 
function CuadroTransfer({ transfer, onChange, disabled, bloquearFila, desbloquearFila, filaEstaBloqueadaPorOtro, fecha, sede }) {
  const [cargandoFudo, setCargandoFudo] = useState(false);
  const [mensajeFudo, setMensajeFudo] = useState(null);

  // 🔹 SEDES CONFIGURADAS PARA FUDO - Solo estas sedes usan la integración automática
  const SEDES_CON_FUDO = ['CORALES', 'VILLA DEL PRADO', 'AV SUR'];
  const sedeUsaFudo = SEDES_CON_FUDO.includes((sede || '').toUpperCase());

  // Totales excluyendo eliminados
  const totales = useMemo(() => {
    let b = 0,
      d = 0,
      dp = 0,
      n = 0;
    transfer
      .filter(t => t.estadoFudo !== 'eliminado_fudo')
      .forEach((t) => {
        b += Number(t.bancolombia || 0);
        d += Number(t.davivienda || 0);
        dp += Number(t.daviplata || 0);
        n += Number(t.nequi || 0);
      });
    return { b, d, dp, n, total: b + d + dp + n };
  }, [transfer]);

  // Contar eliminados
  const totalEliminados = useMemo(() => 
    transfer.filter(t => t.estadoFudo === 'eliminado_fudo').length,
    [transfer]
  );

  // Contar confirmaciones (excluyendo eliminados)
  const confirmaciones = useMemo(() => {
    let confirmados = 0, pendientes = 0;
    transfer
      .filter(t => t.estadoFudo !== 'eliminado_fudo')
      .forEach((t) => {
        if (Number(t.bancolombia || 0) > 0) {
          if (t.bancolombia_confirmado) confirmados++; else pendientes++;
        }
        if (Number(t.davivienda || 0) > 0) {
          if (t.davivienda_confirmado) confirmados++; else pendientes++;
        }
        if (Number(t.daviplata || 0) > 0) {
          if (t.daviplata_confirmado) confirmados++; else pendientes++;
        }
        if (Number(t.nequi || 0) > 0) {
        if (t.nequi_confirmado) confirmados++; else pendientes++;
      }
    });
    return { confirmados, pendientes };
  }, [transfer]);
 
  function cambiar(idx, campo, valor) {
    const copia = transfer.map((t, i) =>
      i === idx ? { ...t, [campo]: valor } : t
    );
    onChange(copia);
  }
 
  function agregarFilas() {
    if (disabled) return;
    const n = Number(
      prompt("¿Cuántas filas de transferencias deseas agregar?")
    );
    if (!n || n <= 0) return;
    const nuevas = Array.from({ length: n }).map(() => ({
      id: uid(),
      pedido: "",
      bancolombia: "",
      bancolombia_confirmado: false,
      davivienda: "",
      davivienda_confirmado: false,
      daviplata: "",
      daviplata_confirmado: false,
      nequi: "",
      nequi_confirmado: false,
    }));
    onChange([...transfer, ...nuevas]);
  }
 
  function eliminarFila(id) {
    if (disabled) return;
    onChange(transfer.filter((t) => t.id !== id));
  }

  // Función para traer transferencias desde FUDO
  async function traerTransferenciasDeFudo() {
    if (disabled || !fecha || !sede) {
      setMensajeFudo({ tipo: 'error', texto: 'Faltan datos de fecha o sede' });
      return;
    }

    setCargandoFudo(true);
    setMensajeFudo(null);

    try {
      // Agregar timestamp para evitar cache
      const timestamp = Date.now();
      const url = `https://intranetbigburguer.com/api/api_fudo_proxy?accion=traer_transferencias&sede=${encodeURIComponent(sede)}&fecha=${fecha}&_t=${timestamp}`;
      const response = await fetch(url);
      const data = await response.json();

      if (!data.success) {
        setMensajeFudo({ tipo: 'error', texto: data.error || 'Error al consultar FUDO' });
        return;
      }

      if (data.transferencias.length === 0) {
        setMensajeFudo({ tipo: 'info', texto: 'No se encontraron transferencias en FUDO para esta fecha' });
        return;
      }

      // 🕐 FILTRO DE SEGURIDAD: Convertir fecha UTC a Colombia y filtrar
      // FUDO devuelve fechas en UTC, Colombia está en UTC-5
      const convertirFechaUTCaColombia = (fechaISO) => {
        if (!fechaISO) return '';
        try {
          const fechaUTC = new Date(fechaISO);
          // Restar 5 horas (Colombia UTC-5)
          const fechaColombia = new Date(fechaUTC.getTime() - (5 * 60 * 60 * 1000));
          const year = fechaColombia.getUTCFullYear();
          const month = String(fechaColombia.getUTCMonth() + 1).padStart(2, '0');
          const day = String(fechaColombia.getUTCDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        } catch (e) {
          return fechaISO.split('T')[0] || '';
        }
      };

      // Filtrar transferencias que realmente corresponden a la fecha del cuadre
      const transferenciasFiltradas = data.transferencias.filter(t => {
        // Si el proxy ya envía la fecha convertida, usarla directamente
        // Si no, intentar convertir desde fechaOriginalUTC
        const fechaTransferencia = t.fechaOriginalUTC 
          ? convertirFechaUTCaColombia(t.fechaOriginalUTC) 
          : t.fecha;
        return fechaTransferencia === fecha;
      });

      console.log(`🕐 Transferencias: ${data.transferencias.length} recibidas, ${transferenciasFiltradas.length} de fecha ${fecha}`);

      if (transferenciasFiltradas.length === 0) {
        setMensajeFudo({ tipo: 'info', texto: `No se encontraron transferencias para ${fecha} (${data.transferencias.length} de otras fechas)` });
        return;
      }

      // Procesar transferencias: cada PAGO de FUDO es una fila independiente.
      // - Cada pago se identifica por su paymentId único de FUDO (soporta varias transferencias por pedido).
      // - Lo que ya está en el sistema NO se modifica: se respetan las modificaciones manuales.
      let nuevos = 0, sinCambios = 0, vinculados = 0;
      const transferActualizadas = [...transfer];
      const usados = new Set(); // ids de filas existentes ya emparejadas en esta sincronización

      for (const trans of transferenciasFiltradas) {
        const legacyId = `${trans.pedidoId}_${trans.banco}_${trans.valor}`;
        const paymentKey = trans.paymentId ? String(trans.paymentId) : legacyId;
        const pedidoStr = String(trans.pedidoId).trim();
        const bancoKey = trans.banco.toLowerCase();

        // 1) ¿Ya existe una fila para este pago exacto (por paymentId de FUDO)?
        let existenteIdx = transferActualizadas.findIndex(t => t.fudoId && t.fudoId === paymentKey && !usados.has(t.id));

        // 2) Compatibilidad: filas antiguas guardadas con fudoId en formato "pedido_banco_valor"
        if (existenteIdx < 0) {
          existenteIdx = transferActualizadas.findIndex(t => t.fudoId === legacyId && !usados.has(t.id));
        }

        // 3) Vincular una fila MANUAL (sin fudoId) del mismo pedido y banco, preservando su valor
        if (existenteIdx < 0) {
          existenteIdx = transferActualizadas.findIndex(t =>
            !t.fudoId && !usados.has(t.id) &&
            String(t.pedido || '').trim() === pedidoStr &&
            Number(t[bancoKey] || 0) > 0
          );
        }

        if (existenteIdx >= 0) {
          const existente = transferActualizadas[existenteIdx];
          usados.add(existente.id);
          // 🔒 NO se modifica el valor existente: se respetan las modificaciones manuales
          if (!existente.fudoId) {
            transferActualizadas[existenteIdx] = { ...existente, fudoId: paymentKey, estadoFudo: 'vinculado' };
            vinculados++;
          } else {
            sinCambios++;
          }
          continue;
        }

        // Nueva fila: el valor va solo en la columna del banco correspondiente
        const valorBanco = {
          bancolombia: trans.banco === 'Bancolombia' ? trans.valor : "",
          davivienda: trans.banco === 'Davivienda' ? trans.valor : "",
          daviplata: trans.banco === 'Daviplata' ? trans.valor : "",
          nequi: trans.banco === 'Nequi' ? trans.valor : ""
        };

        transferActualizadas.push({
          id: uid(),
          fudoId: paymentKey,
          pedido: trans.pedidoId,
          bancolombia: valorBanco.bancolombia,
          bancolombia_confirmado: false,
          davivienda: valorBanco.davivienda,
          davivienda_confirmado: false,
          daviplata: valorBanco.daviplata,
          daviplata_confirmado: false,
          nequi: valorBanco.nequi,
          nequi_confirmado: false,
          estadoFudo: 'nuevo'
        });
        nuevos++;
      }

      // Marcar transferencias eliminadas en FUDO (compatibilidad: paymentId y formato antiguo)
      const fudoIdsActuales = [];
      data.transferencias.forEach(t => {
        fudoIdsActuales.push(`${t.pedidoId}_${t.banco}_${t.valor}`);
        if (t.paymentId) fudoIdsActuales.push(String(t.paymentId));
      });
      transferActualizadas.forEach((t, idx) => {
        if (t.fudoId && !fudoIdsActuales.includes(t.fudoId) && t.estadoFudo !== 'eliminado_fudo') {
          transferActualizadas[idx] = { ...t, estadoFudo: 'eliminado_fudo' };
        }
      });

      // 🚨 NUEVA VALIDACIÓN: Detectar transferencias en el sistema que NO corresponden a FUDO
      // Crear Set de pedidos que FUDO dice son transferencias
      const pedidosFudoTransferencia = new Set(
        transferenciasFiltradas.map(t => String(t.pedidoId).trim())
      );
      
      // Buscar transferencias manuales (sin fudoId) que no están en la lista de FUDO
      const transferenciasIncorrectas = [];
      transferActualizadas.forEach(t => {
        // Solo verificar registros que NO vienen de FUDO (manuales)
        if (t.fudoId) return;
        
        const pedidoStr = String(t.pedido || '').trim();
        if (!pedidoStr) return;
        
        // Si tiene algún valor de transferencia y no está en FUDO
        const tieneValor = Number(t.bancolombia || 0) > 0 || 
                          Number(t.davivienda || 0) > 0 || 
                          Number(t.daviplata || 0) > 0 || 
                          Number(t.nequi || 0) > 0;
        
        if (tieneValor && !pedidosFudoTransferencia.has(pedidoStr)) {
          // Determinar el banco donde tiene el valor
          let bancoUsado = '';
          if (Number(t.bancolombia || 0) > 0) bancoUsado = 'Bancolombia';
          else if (Number(t.davivienda || 0) > 0) bancoUsado = 'Davivienda';
          else if (Number(t.daviplata || 0) > 0) bancoUsado = 'Daviplata';
          else if (Number(t.nequi || 0) > 0) bancoUsado = 'Nequi';
          
          transferenciasIncorrectas.push({
            pedido: pedidoStr,
            banco: bancoUsado,
            valor: Number(t.bancolombia || 0) + Number(t.davivienda || 0) + Number(t.daviplata || 0) + Number(t.nequi || 0)
          });
        }
      });

      onChange(transferActualizadas);
      
      const mensajes = [];
      if (nuevos > 0) mensajes.push(`${nuevos} nueva${nuevos > 1 ? 's' : ''}`);
      if (vinculados > 0) mensajes.push(`${vinculados} vinculada${vinculados > 1 ? 's' : ''}`);
      if (sinCambios > 0) mensajes.push(`${sinCambios} sin cambios (respetadas)`);
      if (data.pedidosRappiExcluidos > 0) mensajes.push(`${data.pedidosRappiExcluidos} Rappi excluidos`);
      
      // Mostrar alerta si hay transferencias que no corresponden
      if (transferenciasIncorrectas.length > 0) {
        const listaIncorrectas = transferenciasIncorrectas
          .map(t => `• Pedido #${t.pedido} (${t.banco}: $${t.valor.toLocaleString('es-CO')})`)
          .join('\n');
        
        setMensajeFudo({ 
          tipo: 'warning', 
          texto: `⚠️ ${mensajes.join(', ')}. ALERTA: ${transferenciasIncorrectas.length} pedido(s) NO son transferencia en FUDO`
        });
        
        // Mostrar alert con el detalle
        setTimeout(() => {
          alert(`⚠️ ALERTA: Los siguientes pedidos están registrados como TRANSFERENCIA en el sistema, pero en FUDO tienen OTRO método de pago:\n\n${listaIncorrectas}\n\nVerifica si el método de pago es correcto.`);
        }, 500);
      } else {
        setMensajeFudo({ 
          tipo: 'success', 
          texto: `✅ Sincronizado: ${mensajes.join(', ')}` 
        });
      }

    } catch (error) {
      console.error('Error trayendo transferencias de FUDO:', error);
      setMensajeFudo({ tipo: 'error', texto: 'Error de conexión con FUDO' });
    } finally {
      setCargandoFudo(false);
      setTimeout(() => setMensajeFudo(null), 5000);
    }
  }
 
  return (
    <section className="subcard" style={{ borderLeft: '5px solid #7c3aed', background: 'linear-gradient(to right, rgba(124, 58, 237, 0.03), transparent)' }}>
      {/* Banner distintivo TRANSFERENCIAS */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
        color: 'white',
        padding: '12px 20px',
        marginBottom: '16px',
        borderRadius: '8px 8px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 2px 8px rgba(124, 58, 237, 0.3)'
      }}>
        <span style={{ fontSize: '32px' }}>💳</span>
        <div>
          <div style={{ fontSize: '18px', fontWeight: '700', letterSpacing: '1px' }}>SECCIÓN TRANSFERENCIAS</div>
          <div style={{ fontSize: '12px', opacity: 0.9 }}>Pagos bancarios y billeteras digitales</div>
        </div>
      </div>
      <div className="subcard-header">
        <h3>Transferencias</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          {confirmaciones.pendientes > 0 && (
            <span style={{ 
              backgroundColor: '#fef3c7', 
              color: '#92400e', 
              padding: '4px 10px', 
              borderRadius: '12px', 
              fontSize: '12px',
              fontWeight: '600'
            }}>
              ⏳ {confirmaciones.pendientes} pendiente{confirmaciones.pendientes > 1 ? 's' : ''}
            </span>
          )}
          {confirmaciones.confirmados > 0 && (
            <span style={{ 
              backgroundColor: '#d1fae5', 
              color: '#065f46', 
              padding: '4px 10px', 
              borderRadius: '12px', 
              fontSize: '12px',
              fontWeight: '600'
            }}>
              ✓ {confirmaciones.confirmados} confirmada{confirmaciones.confirmados > 1 ? 's' : ''}
            </span>
          )}
          {/* Botón Traer de FUDO - Solo para sedes configuradas */}
          {sedeUsaFudo && fecha && sede && (
            <Button 
              variant="outline" 
              onClick={traerTransferenciasDeFudo} 
              disabled={disabled || cargandoFudo}
              style={{
                backgroundColor: cargandoFudo ? '#9ca3af' : '#7c3aed',
                color: 'white',
                border: 'none',
                fontWeight: '600'
              }}
            >
              {cargandoFudo ? '⏳ Cargando...' : '🔄 Traer de FUDO'}
            </Button>
          )}
          <Button variant="outline" onClick={agregarFilas} disabled={disabled}>
            Agregar filas (preguntar cuántas)
          </Button>
        </div>
      </div>
      {/* Mensaje de estado FUDO */}
      {mensajeFudo && (
        <div style={{
          padding: '10px 16px',
          marginBottom: '12px',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: '500',
          backgroundColor: mensajeFudo.tipo === 'success' ? '#d1fae5' : 
                          mensajeFudo.tipo === 'error' ? '#fee2e2' : '#e0f2fe',
          color: mensajeFudo.tipo === 'success' ? '#065f46' : 
                 mensajeFudo.tipo === 'error' ? '#991b1b' : '#0369a1',
          border: `1px solid ${mensajeFudo.tipo === 'success' ? '#a7f3d0' : 
                                mensajeFudo.tipo === 'error' ? '#fecaca' : '#bae6fd'}`
        }}>
          {mensajeFudo.texto}
        </div>
      )}
      <table className="table">
        <thead style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' }}>
          <tr>
            <th style={{ color: '#5b21b6' }}>📦 # Pedido</th>
            <th style={{ color: '#5b21b6' }}>🏦 Bancolombia</th>
            <th style={{ color: '#5b21b6' }}>🏦 Davivienda</th>
            <th style={{ color: '#5b21b6' }}>📱 Daviplata</th>
            <th style={{ color: '#5b21b6' }}>📱 Nequi</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {transfer.map((t, idx) => {
            // 🔒 Verificar si esta fila está bloqueada por otro usuario
            const bloqueoPorOtro = filaEstaBloqueadaPorOtro ? filaEstaBloqueadaPorOtro(t.id) : null;
            const filaBloqueada = !!bloqueoPorOtro;
            // 🗑️ Verificar si está eliminada en FUDO
            const estaEliminada = t.estadoFudo === 'eliminado_fudo';
            
            return (
            <tr key={t.id} style={{ 
              backgroundColor: estaEliminada ? '#fee2e2' : filaBloqueada ? '#fef3c7' : 'transparent',
              position: 'relative',
              opacity: estaEliminada ? 0.6 : 1,
              textDecoration: estaEliminada ? 'line-through' : 'none'
            }}>
              {/* Indicador de eliminado */}
              {estaEliminada && (
                <td style={{ 
                  position: 'absolute', 
                  left: 0, 
                  right: 0, 
                  top: 0,
                  backgroundColor: 'rgba(239, 68, 68, 0.9)',
                  color: 'white',
                  fontSize: '11px',
                  padding: '2px 8px',
                  fontWeight: 600,
                  zIndex: 10,
                  textAlign: 'center'
                }}>
                  🗑️ Eliminado en FUDO (no suma)
                </td>
              )}
              {/* Indicador de bloqueo */}
              {filaBloqueada && !estaEliminada && (
                <td style={{ 
                  position: 'absolute', 
                  left: 0, 
                  right: 0, 
                  top: 0,
                  backgroundColor: 'rgba(251, 191, 36, 0.9)',
                  color: '#92400e',
                  fontSize: '11px',
                  padding: '2px 8px',
                  fontWeight: 600,
                  zIndex: 10,
                  textAlign: 'center'
                }}>
                  🔒 Editando: {bloqueoPorOtro.usuarioNombre}
                </td>
              )}
              <td>
                <input
                  className="input"
                  disabled={disabled || filaBloqueada}
                  value={t.pedido}
                  onChange={(e) =>
                    cambiar(idx, "pedido", e.target.value)
                  }
                  onFocus={() => bloquearFila && bloquearFila(t.id)}
                  onBlur={() => desbloquearFila && desbloquearFila(t.id)}
                  style={{ opacity: filaBloqueada ? 0.6 : 1 }}
                />
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    className="input"
                    type="number"
                    disabled={disabled || filaBloqueada}
                    value={t.bancolombia}
                    onChange={(e) =>
                      cambiar(idx, "bancolombia", e.target.value)
                    }
                    onFocus={() => bloquearFila && bloquearFila(t.id)}
                    onBlur={() => desbloquearFila && desbloquearFila(t.id)}
                    style={{ flex: 1, opacity: filaBloqueada ? 0.6 : 1 }}
                  />
                  {Number(t.bancolombia || 0) > 0 && (
                    <button
                      type="button"
                      onClick={() => cambiar(idx, "bancolombia_confirmado", !t.bancolombia_confirmado)}
                      disabled={disabled}
                      title={t.bancolombia_confirmado ? "Transferencia verificada" : "Click para confirmar transferencia"}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        border: t.bancolombia_confirmado ? '2px solid #10b981' : '2px dashed #9ca3af',
                        backgroundColor: t.bancolombia_confirmado ? '#10b981' : 'transparent',
                        color: t.bancolombia_confirmado ? 'white' : '#9ca3af',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      ✓
                    </button>
                  )}
                </div>
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    className="input"
                    type="number"
                    disabled={disabled}
                    value={t.davivienda}
                    onChange={(e) =>
                      cambiar(idx, "davivienda", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                  {Number(t.davivienda || 0) > 0 && (
                    <button
                      type="button"
                      onClick={() => cambiar(idx, "davivienda_confirmado", !t.davivienda_confirmado)}
                      disabled={disabled}
                      title={t.davivienda_confirmado ? "Transferencia verificada" : "Click para confirmar transferencia"}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        border: t.davivienda_confirmado ? '2px solid #10b981' : '2px dashed #9ca3af',
                        backgroundColor: t.davivienda_confirmado ? '#10b981' : 'transparent',
                        color: t.davivienda_confirmado ? 'white' : '#9ca3af',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      ✓
                    </button>
                  )}
                </div>
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    className="input"
                    type="number"
                    disabled={disabled}
                    value={t.daviplata}
                    onChange={(e) =>
                      cambiar(idx, "daviplata", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                  {Number(t.daviplata || 0) > 0 && (
                    <button
                      type="button"
                      onClick={() => cambiar(idx, "daviplata_confirmado", !t.daviplata_confirmado)}
                      disabled={disabled}
                      title={t.daviplata_confirmado ? "Transferencia verificada" : "Click para confirmar transferencia"}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        border: t.daviplata_confirmado ? '2px solid #10b981' : '2px dashed #9ca3af',
                        backgroundColor: t.daviplata_confirmado ? '#10b981' : 'transparent',
                        color: t.daviplata_confirmado ? 'white' : '#9ca3af',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      ✓
                    </button>
                  )}
                </div>
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    className="input"
                    type="number"
                    disabled={disabled}
                    value={t.nequi}
                    onChange={(e) =>
                      cambiar(idx, "nequi", e.target.value)
                    }
                    style={{ flex: 1 }}
                  />
                  {Number(t.nequi || 0) > 0 && (
                    <button
                      type="button"
                      onClick={() => cambiar(idx, "nequi_confirmado", !t.nequi_confirmado)}
                      disabled={disabled}
                      title={t.nequi_confirmado ? "Transferencia verificada" : "Click para confirmar transferencia"}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        border: t.nequi_confirmado ? '2px solid #10b981' : '2px dashed #9ca3af',
                        backgroundColor: t.nequi_confirmado ? '#10b981' : 'transparent',
                        color: t.nequi_confirmado ? 'white' : '#9ca3af',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      ✓
                    </button>
                  )}
                </div>
              </td>
              <td>
                <Button
                  variant="danger"
                  onClick={() => eliminarFila(t.id)}
                  disabled={disabled || filaBloqueada}
                >
                  X
                </Button>
              </td>
            </tr>
          );
          })}
        </tbody>
        <tfoot>
          {totalEliminados > 0 && (
            <tr style={{ backgroundColor: '#fee2e2' }}>
              <td colSpan="6" style={{ 
                textAlign: 'center', 
                color: '#dc2626', 
                fontWeight: 600,
                fontSize: '12px'
              }}>
                🗑️ {totalEliminados} transferencia{totalEliminados > 1 ? 's' : ''} eliminada{totalEliminados > 1 ? 's' : ''} en FUDO (no suman en totales)
              </td>
            </tr>
          )}
          <tr>
            <td style={{ fontWeight: 600 }}>Totales:</td>
            <td>
              {new Intl.NumberFormat("es-CO", {
                style: "currency",
                currency: "COP",
              }).format(totales.b)}
            </td>
            <td>
              {new Intl.NumberFormat("es-CO", {
                style: "currency",
                currency: "COP",
              }).format(totales.d)}
            </td>
            <td>
              {new Intl.NumberFormat("es-CO", {
                style: "currency",
                currency: "COP",
              }).format(totales.dp)}
            </td>
            <td>
              {new Intl.NumberFormat("es-CO", {
                style: "currency",
                currency: "COP",
              }).format(totales.n)}
            </td>
            <td style={{ fontWeight: 700 }}>
              {new Intl.NumberFormat("es-CO", {
                style: "currency",
                currency: "COP",
              }).format(totales.total)}
            </td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}
 
/* ---------- TURNOS ---------- */
 
function CuadroTurnos({
  turnosActivos,
  turnos,
  onChange,
  disabled,
}) {
  const total = useMemo(
    () => turnos.reduce((s, t) => s + Number(t.valor || 0), 0),
    [turnos]
  );
 
  function toggle() {
    if (disabled) return;
    if (turnosActivos) {
      onChange(false, []);
    } else {
      const nueva = { id: uid(), empleado: "", valor: "" };
      onChange(true, turnos.length ? turnos : [nueva]);
    }
  }
 
  function cambiar(idx, campo, valor) {
    const copia = turnos.map((t, i) =>
      i === idx ? { ...t, [campo]: valor } : t
    );
    onChange(turnosActivos, copia);
  }
 
  function agregar() {
    if (disabled || !turnosActivos) return;
    const nueva = { id: uid(), empleado: "", valor: "" };
    onChange(turnosActivos, [...turnos, nueva]);
  }
 
  function eliminar(id) {
    if (disabled) return;
    onChange(
      turnosActivos,
      turnos.filter((t) => t.id !== id)
    );
  }
 
  function imprimir() {
    if (!turnos.length) {
      alert("No hay turnos para imprimir.");
      return;
    }
    imprimirRecibo("TURNOS", turnos, total);
  }
 
  return (
    <section className="subcard">
      <div className="subcard-header">
        <h3>Turnos</h3>
        <Button variant="outline" onClick={toggle} disabled={disabled}>
          {turnosActivos ? "Desactivar turnos" : "Activar turnos"}
        </Button>
      </div>
 
      {!turnosActivos && (
        <p className="alert-rojo">SIN PAGO DE TURNOS</p>
      )}
 
      {turnosActivos && (
        <>
          <div style={{ marginBottom: 6 }}>
            <Button variant="outline" onClick={agregar} disabled={disabled}>
              Agregar fila
            </Button>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Nombre del empleado</th>
                <th>Valor (COP)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {turnos.map((t, idx) => (
                <tr key={t.id}>
                  <td>
                    <input
                      className="input"
                      disabled={disabled}
                      value={t.empleado}
                      onChange={(e) =>
                        cambiar(idx, "empleado", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="input"
                      type="number"
                      disabled={disabled}
                      value={t.valor}
                      onChange={(e) =>
                        cambiar(idx, "valor", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <Button
                      variant="danger"
                      onClick={() => eliminar(t.id)}
                      disabled={disabled}
                    >
                      X
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  Total turnos:
                </td>
                <td colSpan={2} style={{ fontWeight: 700 }}>
                  {new Intl.NumberFormat("es-CO", {
                    style: "currency",
                    currency: "COP",
                  }).format(total)}
                </td>
              </tr>
            </tfoot>
          </table>
          <Button
            variant="outline"
            onClick={imprimir}
            disabled={!turnos.length}
            style={{ marginTop: 4 }}
          >
            Imprimir
          </Button>
        </>
      )}
    </section>
  );
}
 
/* ---------- ADELANTOS ---------- */
 
function CuadroAdelantos({
  adelantosActivos,
  adelantos,
  onChange,
  disabled,
}) {
  const total = useMemo(
    () => adelantos.reduce((s, a) => s + Number(a.valor || 0), 0),
    [adelantos]
  );
 
  function toggle() {
    if (disabled) return;
    if (adelantosActivos) {
      onChange(false, []);
    } else {
      const nueva = {
        id: uid(),
        empleado: "",
        valor: "",
        registradoNomina: "No",
      };
      onChange(true, adelantos.length ? adelantos : [nueva]);
    }
  }
 
  function cambiar(idx, campo, valor) {
    const copia = adelantos.map((a, i) =>
      i === idx ? { ...a, [campo]: valor } : a
    );
    onChange(adelantosActivos, copia);
  }
 
  function agregar() {
    if (disabled || !adelantosActivos) return;
    const nueva = {
      id: uid(),
      empleado: "",
      valor: "",
      registradoNomina: "No",
    };
    onChange(adelantosActivos, [...adelantos, nueva]);
  }
 
  function eliminar(id) {
    if (disabled) return;
    onChange(
      adelantosActivos,
      adelantos.filter((a) => a.id !== id)
    );
  }
 
  function imprimir() {
    if (!adelantos.length) {
      alert("No hay adelantos para imprimir.");
      return;
    }
    imprimirRecibo("ADELANTOS", adelantos, total);
  }
 
  return (
    <section className="subcard">
      <div className="subcard-header">
        <h3>Adelantos</h3>
        <Button variant="outline" onClick={toggle} disabled={disabled}>
          {adelantosActivos ? "Desactivar adelantos" : "Activar adelantos"}
        </Button>
      </div>
 
      {!adelantosActivos && (
        <p className="alert-rojo">SIN ADELANTOS</p>
      )}
 
      {adelantosActivos && (
        <>
          <div style={{ marginBottom: 6 }}>
            <Button variant="outline" onClick={agregar} disabled={disabled}>
              Agregar fila
            </Button>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Nombre del empleado</th>
                <th>Valor (COP)</th>
                <th>Registrado en nómina</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {adelantos.map((a, idx) => (
                <tr key={a.id}>
                  <td>
                    <input
                      className="input"
                      disabled={disabled}
                      value={a.empleado}
                      onChange={(e) =>
                        cambiar(idx, "empleado", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="input"
                      type="number"
                      disabled={disabled}
                      value={a.valor}
                      onChange={(e) =>
                        cambiar(idx, "valor", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <select
                      className="input"
                      disabled={disabled}
                      value={a.registradoNomina}
                      onChange={(e) =>
                        cambiar(
                          idx,
                          "registradoNomina",
                          e.target.value
                        )
                      }
                    >
                      <option>No</option>
                      <option>Sí</option>
                    </select>
                  </td>
                  <td>
                    <Button
                      variant="danger"
                      onClick={() => eliminar(a.id)}
                      disabled={disabled}
                    >
                      X
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  Total adelantos:
                </td>
                <td colSpan={3} style={{ fontWeight: 700 }}>
                  {new Intl.NumberFormat("es-CO", {
                    style: "currency",
                    currency: "COP",
                  }).format(total)}
                </td>
              </tr>
            </tfoot>
          </table>
          <Button
            variant="outline"
            onClick={imprimir}
            disabled={!adelantos.length}
            style={{ marginTop: 4 }}
          >
            Imprimir
          </Button>
        </>
      )}
    </section>
  );
}
 
/* ---------- DENOMINACIONES ---------- */
 
function CuadroDenominaciones({ denoms, onChange, disabled, activo, onToggleActivo }) {
  const total = useMemo(
    () =>
      denoms.reduce((s, d) => s + Number(d.subtotal || 0), 0),
    [denoms]
  );
 
  function cambiarCantidad(idx, valor) {
    const cantidad = Number(valor || 0);
    const copia = denoms.map((d, i) =>
      i === idx
        ? { ...d, cantidad, subtotal: d.valor * cantidad }
        : d
    );
    onChange(copia);
  }

  // Si no está activo, mostrar solo el botón para activar
  if (!activo) {
    return (
      <section className="subcard">
        <div className="subcard-header" style={{ justifyContent: 'space-between' }}>
          <h3 style={{ color: '#000000', fontWeight: '700' }}>Denominaciones de efectivo</h3>
          <Button 
            variant="outline" 
            onClick={onToggleActivo}
            disabled={disabled}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              borderColor: '#000000',
              color: '#000000',
              fontWeight: '700'
            }}
          >
            Activar denominaciones de efectivo
          </Button>
        </div>
      </section>
    );
  }
 
  return (
    <section className="subcard">
      <div className="subcard-header" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ color: '#000000' }}>Denominaciones de efectivo</h3>
        <Button 
          variant="outline" 
          onClick={onToggleActivo}
          disabled={disabled}
          style={{ 
            borderColor: '#ef4444',
            color: '#ef4444'
          }}
        >
          Ocultar denominaciones
        </Button>
      </div>
      <p className="muted">
        Ingresa la cantidad de billetes/monedas que hay de cada denominación (COP).
      </p>
      <div className="denoms-box">
        <table className="table table-denoms">
          <thead>
            <tr>
              <th>Denominación</th>
              <th>Cant.</th>
              <th>Subtotal (COP)</th>
            </tr>
          </thead>
          <tbody>
            {denoms.map((d, idx) => (
              <tr key={d.valor}>
                <td>
                  {new Intl.NumberFormat("es-CO", {
                    style: "currency",
                    currency: "COP",
                  }).format(d.valor)}
                </td>
                <td>
                  <input
                    className="input"
                    type="number"
                    disabled={disabled}
                    value={d.cantidad}
                    onChange={(e) =>
                      cambiarCantidad(idx, e.target.value)
                    }
                    style={{ textAlign: "center" }}
                  />
                </td>
                <td>
                  {new Intl.NumberFormat("es-CO", {
                    style: "currency",
                    currency: "COP",
                  }).format(d.subtotal || 0)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ textAlign: "right", fontWeight: 600 }}>
                Total contado:
              </td>
              <td colSpan={2} style={{ fontWeight: 700 }}>
                {new Intl.NumberFormat("es-CO", {
                  style: "currency",
                  currency: "COP",
                }).format(total)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
 
/* ---------- NOVEDADES ---------- */
 
function CuadroNovedades({
  novedadesActivas,
  novedades,
  onChange,
  disabled,
}) {
  function toggle() {
    if (disabled) return;
    onChange(!novedadesActivas, novedadesActivas ? [] : []);
  }
 
  function cambiar(idx, campo, valor) {
    const copia = novedades.map((n, i) =>
      i === idx ? { ...n, [campo]: valor } : n
    );
    onChange(novedadesActivas, copia);
  }
 
  function agregar() {
    if (disabled) return;
    const nueva = { id: uid(), novedad: "", aQuien: "", comentarios: "" };
    onChange(novedadesActivas, [...novedades, nueva]);
  }
 
  function eliminar(id) {
    if (disabled) return;
    onChange(
      novedadesActivas,
      novedades.filter((n) => n.id !== id)
    );
  }
 
  return (
    <section className="subcard">
      <div className="subcard-header">
        <h3>Novedades</h3>
        <Button variant="outline" onClick={toggle} disabled={disabled}>
          {novedadesActivas
            ? "Desactivar novedades"
            : "Activar novedades"}
        </Button>
      </div>
 
      {!novedadesActivas && (
        <p className="alert-rojo">SIN NOVEDADES</p>
      )}
 
      {novedadesActivas && (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Novedad</th>
                <th>A quién se reportó</th>
                <th>Comentarios</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {novedades.map((n, idx) => (
                <tr key={n.id}>
                  <td>
                    <input
                      className="input"
                      disabled={disabled}
                      value={n.novedad}
                      onChange={(e) =>
                        cambiar(idx, "novedad", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="input"
                      disabled={disabled}
                      value={n.aQuien}
                      onChange={(e) =>
                        cambiar(idx, "aQuien", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <textarea
                      className="textarea"
                      disabled={disabled}
                      rows={2}
                      value={n.comentarios}
                      onChange={(e) =>
                        cambiar(
                          idx,
                          "comentarios",
                          e.target.value
                        )
                      }
                    ></textarea>
                  </td>
                  <td>
                    <Button
                      variant="danger"
                      onClick={() => eliminar(n.id)}
                      disabled={disabled}
                    >
                      X
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button variant="outline" onClick={agregar} disabled={disabled}>
            Agregar novedad
          </Button>
        </>
      )}
    </section>
  );
}

/* ---------- DATAFONOS ---------- */

function CuadroDatafonos({ datafonos, onChange, disabled }) {
  // Asegurarse de que siempre haya al menos una fila
  const datosDatafonos = datafonos && datafonos.length > 0 
    ? datafonos 
    : [{ id: uid(), terminal: "", valor: "" }];

  function cambiar(idx, campo, valor) {
    const copia = datosDatafonos.map((d, i) =>
      i === idx ? { ...d, [campo]: valor } : d
    );
    onChange(copia);
  }

  function agregar() {
    if (disabled) return;
    const nuevo = { id: uid(), terminal: "", valor: "" };
    onChange([...datosDatafonos, nuevo]);
  }

  function eliminar(id) {
    if (disabled) return;
    // No permitir eliminar si solo queda una fila
    if (datosDatafonos.length <= 1) return;
    onChange(datosDatafonos.filter((d) => d.id !== id));
  }

  const totalDatafonos = datosDatafonos.reduce(
    (sum, d) => sum + Number(d.valor || 0),
    0
  );

  return (
    <section className="subcard">
      <div className="subcard-header">
        <h3>Datafonos</h3>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#ff6b35' }}>
          Total: {new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP" }).format(totalDatafonos)}
        </span>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th>Terminal</th>
            <th>Valor</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {datosDatafonos.map((d, idx) => (
            <tr key={d.id}>
              <td>
                <input
                  className="input"
                  type="text"
                  disabled={disabled}
                  value={d.terminal || ""}
                  placeholder="Ej: Terminal 1, Visa, etc."
                  onChange={(e) => cambiar(idx, "terminal", e.target.value)}
                />
              </td>
              <td>
                <input
                  className="input"
                  type="number"
                  disabled={disabled}
                  value={d.valor || ""}
                  placeholder="0"
                  onChange={(e) => cambiar(idx, "valor", e.target.value)}
                />
              </td>
              <td>
                <Button
                  variant="danger"
                  onClick={() => eliminar(d.id)}
                  disabled={disabled || datosDatafonos.length <= 1}
                >
                  X
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Button variant="outline" onClick={agregar} disabled={disabled}>
        + Agregar datáfono
      </Button>
    </section>
  );
}
 
/* ====================== CONSULTAS ====================== */
 
function ConsultasCuadres({ sedes, empleados, cuadres, setCuadreActivoId, esGerente, onGestionarAccesos, onReabrirCuadre, tienePermiso = () => true }) {
  const [tipo, setTipo] = useState("porFecha");
  const [sede, setSede] = useState("");
  const [empleadoId, setEmpleadoId] = useState("");
  const [fecha, setFecha] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [filtroMes, setFiltroMes] = useState("");
  const [cuadreDetallesId, setCuadreDetallesId] = useState(null);
  const [accionesCuadreId, setAccionesCuadreId] = useState(null);

  // 🖨️ Consultar / imprimir la tirilla guardada de un cuadre (o generarla al momento)
  async function verTirillaCuadre(c) {
    try {
      let html = null;
      const guardada = await obtenerTirillaDeSupabase(c.id);
      if (guardada && guardada.contenido_html) {
        html = guardada.contenido_html;
      } else {
        // Cuadres antiguos sin tirilla guardada: generarla al momento
        html = generarResumenTicketImpresion(c, empleados);
      }
      const ventana = window.open('', '_blank');
      if (ventana) {
        ventana.document.write(html);
        ventana.document.close();
        ventana.focus();
        setTimeout(() => { try { ventana.print(); } catch (err) {} }, 400);
      } else {
        alert('⚠️ El navegador bloqueó la ventana emergente. Habilita las ventanas emergentes para ver la tirilla.');
      }
    } catch (e) {
      console.error('❌ Error al abrir tirilla:', e);
      alert('❌ No se pudo abrir la tirilla.');
    }
  }

  const monthsArr = useMemo(() => {
    const base = sede ? cuadres.filter((c) => c.sede === sede) : cuadres;
    const months = new Set();
    base.forEach((c) => {
      if (!c?.fecha) return;
      const clave = String(c.fecha).slice(0, 7);
      if (clave) months.add(clave);
    });
    return Array.from(months).sort().reverse();
  }, [cuadres, sede]);

  const monthLabel = useCallback((clave) => {
    if (!clave || typeof clave !== "string") return "";
    const [yearStr, monthStr] = clave.split("-");
    const year = Number(yearStr);
    const month = Number(monthStr) - 1;
    if (Number.isNaN(year) || Number.isNaN(month)) return clave;
    const formatter = new Intl.DateTimeFormat("es-CO", {
      month: "long",
      year: "numeric",
    });
    return formatter.format(new Date(year, month, 1));
  }, []);

  useEffect(() => {
    if (tipo !== "porSede" && tipo !== "sedeEntreFechas") {
      setFiltroMes("");
    }
  }, [tipo]);

  useEffect(() => {
    if (filtroMes && !monthsArr.includes(filtroMes)) {
      setFiltroMes("");
    }
  }, [monthsArr, filtroMes]);
 
  function filtrar() {
    // FILTRO: Excluir cuadres de prueba anteriores al 7 de febrero de 2026
    const FECHA_MINIMA_CUADRES = "2026-02-07";
    let lista = cuadres.filter(c => c.fecha >= FECHA_MINIMA_CUADRES);
    
    switch (tipo) {
      case "porFecha":
        lista = lista.filter((c) => c.fecha === fecha);
        break;
      case "porSede":
        lista = lista.filter((c) => c.sede === sede);
        break;
      case "porEmpleado":
        lista = lista.filter((c) => c.empleadoId === empleadoId);
        break;
      case "entreFechas":
        lista = lista.filter((c) => c.fecha >= desde && c.fecha <= hasta);
        break;
      case "empleadoEntreFechas":
        lista = lista.filter(
          (c) =>
            c.empleadoId === empleadoId &&
            c.fecha >= desde &&
            c.fecha <= hasta
        );
        break;
      case "sedeEntreFechas":
        lista = lista.filter(
          (c) =>
            c.sede === sede &&
            c.fecha >= desde &&
            c.fecha <= hasta
        );
        break;
      default:
        break;
    }

    if ((tipo === "porSede" || tipo === "sedeEntreFechas") && filtroMes) {
      lista = lista.filter((c) => String(c?.fecha || "").startsWith(filtroMes));
    }
    return lista.sort((a, b) => (a.fecha < b.fecha ? -1 : 1));
  }
 
  const resultados = filtrar();
 
  return (
    <div className="card">
      <h2>Consultas de cuadres guardados</h2>
      <div className="grid-3">
        <Field label="Tipo de consulta">
          <select
            className="input"
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
          >
            <option value="porFecha">Por fecha específica</option>
            <option value="porSede">Por sede</option>
            <option value="porEmpleado">Por empleado</option>
            <option value="entreFechas">Entre dos fechas</option>
            <option value="empleadoEntreFechas">
              Empleado entre fechas
            </option>
            <option value="sedeEntreFechas">Sede entre fechas</option>
          </select>
        </Field>
 
        {(tipo === "porSede" || tipo === "sedeEntreFechas") && (
          <>
            <Field label="Sede">
              <select
                className="input"
                value={sede}
                onChange={(e) => setSede(e.target.value)}
              >
                <option value="">-- Selecciona --</option>
                {sedes.map((s) => {
                  const sedeNombre = typeof s === 'string' ? s : (s?.nombre || s?.razonSocial || '');
                  const sedeId = typeof s === 'string' ? s : (s?.id || sedeNombre);
                  return (
                    <option key={sedeId} value={sedeNombre}>
                      {sedeNombre || "Sin nombre"}
                    </option>
                  );
                })}
              </select>
            </Field>
            <Field label="Mes">
              <select
                style={{
                  width: "100%",
                  fontSize: 12,
                padding: "4px",
                borderRadius: 4,
                border: "1px solid #cbd5e1",
              }}
              value={filtroMes}
              onChange={(e) => setFiltroMes(e.target.value)}
              disabled={monthsArr.length === 0}
            >
              <option value="">Todos</option>
              {monthsArr.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </select>
          </Field>
          </>
        )}
 
        {(tipo === "porEmpleado" ||
          tipo === "empleadoEntreFechas") && (
          <Field label="Empleado">
            <select
              className="input"
              value={empleadoId}
              onChange={(e) => setEmpleadoId(e.target.value)}
            >
              <option value="">-- Selecciona --</option>
              {[...(empleados || [])].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nombre}
                </option>
              ))}
            </select>
          </Field>
        )}
 
        {(tipo === "porFecha" ||
          tipo === "porSede" ||
          tipo === "porEmpleado") && (
          <Field label="Fecha">
            <input
              className="input"
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </Field>
        )}
 
        {(tipo === "entreFechas" ||
          tipo === "empleadoEntreFechas" ||
          tipo === "sedeEntreFechas") && (
          <>
            <Field label="Desde">
              <input
                className="input"
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
              />
            </Field>
            <Field label="Hasta">
              <input
                className="input"
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
              />
            </Field>
          </>
        )}
      </div>
 
      <h3 style={{ marginTop: 16 }}>Resultados</h3>
      {resultados.length === 0 && (
        <p className="muted">No hay cuadres que cumplan ese filtro.</p>
      )}
      {resultados.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: 16 }}>
          <table className="table" style={{ fontSize: '11px', minWidth: '2200px' }}>
            <thead style={{ backgroundColor: '#f3f4f6', position: 'sticky', top: 0 }}>
              <tr>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Fecha</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Responsable</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Venta Efectivo</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Bancolombia</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Davivienda</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Daviplata</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Nequi</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Datafonos</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Rappi Online</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Efectivo Caja</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Debía Quedar</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Fact. Pend</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Fact. Pagada Caja</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Fact. Pagada Trans</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Dom. Total</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Dom. c/Datafono</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Trans. Banc</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Trans. Davi</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Trans. Davip</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Trans. Nequ</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Estado Cuadre</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Valor Total Sobrante</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Monto Sobrante</th>
                <th style={{ padding: '8px 4px', borderRight: '1px solid #ddd' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {resultados.map((c) => {
                const empleado = empleados.find((e) => e.id === c.empleadoId);
                
                // Calcular valores
                const ventas = c.ventas || {};
                const transfer = Array.isArray(c.transfer) ? c.transfer : [];
                const facturas = Array.isArray(c.facturas) ? c.facturas : [];
                const domicilios = Array.isArray(c.domicilios) ? c.domicilios : [];
                
                // Medios de pago
                const ventaEfectivo = Number(ventas.ventaEfectivo || 0);
                let bancolombiaVentas = 0, daviviendaVentas = 0, daviplataVentas = 0, nequiVentas = 0;
                transfer.forEach(t => {
                  bancolombiaVentas += Number(t.bancolombia || 0);
                  daviviendaVentas += Number(t.davivienda || 0);
                  daviplataVentas += Number(t.daviplata || 0);
                  nequiVentas += Number(t.nequi || 0);
                });
                // 🔹 DATAFONOS - Calcular desde array de datafonos
                const totalDatafonos = Array.isArray(c.datafonos) ? c.datafonos.reduce((s, d) => s + Number(d.valor || 0), 0) : 0;
                const onlineRappi = Number(ventas.onlineRappi || 0);
                
                // Caja
                const efectivoEnCaja = Number(ventas.efectivoEnCaja || 0);
                const deberíaQuedar = Number(ventas.deberíaQuedarEnCaja || 0);
                
                // Facturas
                const factPendientes = facturas.filter(f => f.estado === 'Pendiente').length;
                const factPagadaCaja = facturas.filter(f => f.estado === 'Pagada de caja').length;
                const factPagadaTransfer = facturas.filter(f => f.estado === 'Pagada transferencia').length;
                
                // Domicilios
                const domiciliosConDatafono = domicilios.filter(d => Number(d.cobroDatafono || 0) > 0).length;
                
                // Transferencias por banco
                let transferenciasBanc = 0, transferenciasDavi = 0, transferenciasDavip = 0, transferenciasNequ = 0;
                transfer.forEach(t => {
                  if (Number(t.bancolombia || 0) > 0) transferenciasBanc++;
                  if (Number(t.davivienda || 0) > 0) transferenciasDavi++;
                  if (Number(t.daviplata || 0) > 0) transferenciasDavip++;
                  if (Number(t.nequi || 0) > 0) transferenciasNequ++;
                });
                
                // Estado cuadre
                const sobranteNeto = Number(ventas.sobrante || 0);
                const diferencia = efectivoEnCaja - deberíaQuedar - sobranteNeto;
                const valorTotalSobrante = sobranteNeto + diferencia;
                const diferenciaNeta = valorTotalSobrante - sobranteNeto;
                
                let estadoCuadre = 'CUADRADO';
                let montoCuadre = '$0';
                let colorEstado = '#16a34a';
                
                if (diferenciaNeta > 0) {
                  estadoCuadre = 'SOBRANTE';
                  montoCuadre = '+' + new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(diferenciaNeta);
                  colorEstado = '#f59e0b';
                } else if (diferenciaNeta < 0) {
                  estadoCuadre = 'FALTANTE';
                  montoCuadre = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(diferenciaNeta);
                  colorEstado = '#dc2626';
                }
                
                const formatCOP = (val) => new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(val || 0);
                
                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd' }}>{c.fecha}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd' }}>{empleado ? empleado.nombre : "—"}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'right' }}>{formatCOP(ventaEfectivo)}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'right' }}>{formatCOP(bancolombiaVentas)}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'right' }}>{formatCOP(daviviendaVentas)}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'right' }}>{formatCOP(daviplataVentas)}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'right' }}>{formatCOP(nequiVentas)}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'right' }}>{formatCOP(totalDatafonos)}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'right' }}>{formatCOP(onlineRappi)}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'right' }}>{formatCOP(efectivoEnCaja)}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'right' }}>{formatCOP(deberíaQuedar)}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'center' }}>{factPendientes}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'center' }}>{factPagadaCaja}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'center' }}>{factPagadaTransfer}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'center' }}>{domicilios.length}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'center' }}>{domiciliosConDatafono}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'center' }}>{transferenciasBanc}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'center' }}>{transferenciasDavi}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'center' }}>{transferenciasDavip}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', textAlign: 'center' }}>{transferenciasNequ}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', color: colorEstado, fontWeight: 'bold', textAlign: 'center' }}>{estadoCuadre}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', color: colorEstado, fontWeight: 'bold', textAlign: 'right' }}>{formatCOP(valorTotalSobrante)}</td>
                    <td style={{ padding: '6px 4px', borderRight: '1px solid #ddd', color: colorEstado, fontWeight: 'bold', textAlign: 'right' }}>{montoCuadre}</td>
                    <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                      <Button
                        variant="outline"
                        style={{ fontSize: '10px', padding: '4px 10px', whiteSpace: 'nowrap' }}
                        onClick={() => setAccionesCuadreId(c.id)}
                      >
                        Ver más
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 🔘 MODAL DE ACCIONES DEL CUADRE */}
      {accionesCuadreId && (() => {
        const cuadreAcciones = resultados.find(c => c.id === accionesCuadreId);
        if (!cuadreAcciones) return null;
        const empleadoAcc = empleados.find(e => e.id === cuadreAcciones.empleadoId);
        const puedeReabrir = (esGerente || tienePermiso("admin_cuadre_reabrir")) && cuadreAcciones.estado === 'final' && typeof onReabrirCuadre === 'function';

        return (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px'
            }}
            onClick={() => setAccionesCuadreId(null)}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '340px',
                padding: '20px',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <h3 style={{ margin: 0, fontSize: '16px' }}>Acciones del cuadre</h3>
                <button
                  onClick={() => setAccionesCuadreId(null)}
                  style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#666', lineHeight: 1 }}
                >×</button>
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
                {cuadreAcciones.fecha} · {empleadoAcc ? empleadoAcc.nombre : '—'}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Button
                  variant="outline"
                  style={{ width: '100%', padding: '10px', fontSize: '13px' }}
                  onClick={() => { setCuadreActivoId(cuadreAcciones.id); setAccionesCuadreId(null); }}
                >
                  👁️ Ver Detalles
                </Button>
                <Button
                  variant="outline"
                  style={{ width: '100%', padding: '10px', fontSize: '13px', color: '#2563eb', borderColor: '#2563eb' }}
                  onClick={() => { verTirillaCuadre(cuadreAcciones); setAccionesCuadreId(null); }}
                >
                  🖨️ Tirilla
                </Button>
                {puedeReabrir && (
                  <Button
                    variant="outline"
                    style={{ width: '100%', padding: '10px', fontSize: '13px', color: '#dc2626', borderColor: '#dc2626' }}
                    onClick={() => { onReabrirCuadre(cuadreAcciones.id); setAccionesCuadreId(null); }}
                  >
                    🔓 Reabrir
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* 📋 MODAL DE DETALLES DEL CUADRE */}
      {cuadreDetallesId && (() => {
        const cuadreDetalle = cuadres.find(c => c.id === cuadreDetallesId);
        if (!cuadreDetalle) return null;

        return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxHeight: '90vh',
              overflowY: 'auto',
              maxWidth: '1000px',
              width: '100%',
              padding: '24px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0 }}>📊 Detalles del Cuadre - {cuadreDetalle.fecha}</h2>
                <button onClick={() => setCuadreDetallesId(null)} style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}>×</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <strong>Sede:</strong> {cuadreDetalle.sede}
                </div>
                <div>
                  <strong>Responsable:</strong> {empleados.find(e => e.id === cuadreDetalle.empleadoId)?.nombre || '—'}
                </div>
                <div>
                  <strong>Cerrado por:</strong> {cuadreDetalle.cerradoPorNombre || '—'}
                </div>
                <div>
                  <strong>Estado:</strong> {cuadreDetalle.estado === 'final' ? '✅ Final' : '📝 Borrador'}
                </div>
              </div>

              <button onClick={() => {
                setCuadreActivoId(cuadreDetalle.id);
                setCuadreDetallesId(null);
              }} style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                padding: '10px 16px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                marginRight: '8px',
                fontWeight: '600'
              }}>
                👁️ Abrir Vista Completa
              </button>

              <button onClick={() => setCuadreDetallesId(null)} style={{
                backgroundColor: '#e5e7eb',
                color: '#374151',
                padding: '10px 16px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                fontWeight: '600'
              }}>
                ❌ Cerrar
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
 
/* ====================== SOLO LECTURA PARA CONSULTAS ====================== */
 
function CuadreSoloLectura({ cuadre, empleados, onGuardarComentario, tienePermiso = () => true }) {
  const empleado = empleados.find((e) => e.id === cuadre.empleadoId);
  const [comentario, setComentario] = useState(
    cuadre.comentarioModificacion || ""
  );
 
  useEffect(() => {
    setComentario(cuadre.comentarioModificacion || "");
  }, [cuadre]);
 
  const esFinal = cuadre.estado === "final";
 
  return (
    <div className="card">
      <h2>
        Cuadre {cuadre.sede} — {cuadre.fecha}
      </h2>
      <p className="muted">
        Responsable: {empleado ? empleado.nombre : "—"} | Estado:{" "}
        {esFinal ? "Final (cerrado)" : "Borrador / en proceso"}
      </p>
      {cuadre.comentarioModificacion && (
        <p className="comentario-label">
          CON COMENTARIOS DE MODIFICACIÓN
        </p>
      )}
 
      <div style={{ marginTop: 8, marginBottom: 8 }}>
        {tienePermiso("admin_cuadre_imprimir") && (
        <Button
          variant="outline"
          onClick={() => imprimirCuadreCompleto(cuadre, empleados)}
        >
          Imprimir cuadre
        </Button>
        )}
      </div>
 
      {esFinal && (
        <div style={{ marginTop: 8 }}>
          <h3>Comentario de modificación</h3>
          <p className="muted">
            Aquí puedes registrar notas sobre este cuadre cerrado (ajustes,
            observaciones, etc.).
          </p>
          <textarea
            className="textarea"
            rows={4}
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            placeholder="Escribe comentarios de modificación (si aplica)"
          ></textarea>
          <Button
            variant="outline"
            style={{ marginTop: 8 }}
            onClick={() => {
              onGuardarComentario(cuadre.id, comentario);
              alert("Comentario guardado para este cuadre.");
            }}
          >
            Guardar comentario
          </Button>
        </div>
      )}
 
      {!esFinal && (
        <p className="muted" style={{ marginTop: 8 }}>
          Los comentarios de modificación solo se registran cuando el cuadre
          está cerrado.
        </p>
      )}
    </div>
  );
}
 
/* ====================== TOTALES E IMPRESIÓN ====================== */

function generarResumenTicketImpresion(cuadre, empleados) {
  const formatCOP = (val) => new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(val || 0);

  // 🔹 DATOS BÁSICOS
  const fecha = new Date(cuadre.fecha).toLocaleDateString('es-CO', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  const sede = cuadre.sede || 'N/A';
  const empleadoFind = empleados && empleados.find(e => e.id === cuadre.empleadoId);
  const responsable = cuadre.cerradoPorNombre || (empleadoFind ? empleadoFind.nombre : 'N/A');

  // 🔹 TURNOS
  const turnos = Array.isArray(cuadre.turnos) ? cuadre.turnos : [];
  const totalTurnos = turnos.reduce((s, t) => s + Number(t.valor || 0), 0);
  const textTurnos = turnos.length > 0 
    ? '✅ Turnos: ' + formatCOP(totalTurnos)
    : '❌ Turnos: NO';

  // 🔹 ADELANTOS
  const adelantos = Array.isArray(cuadre.adelantos) ? cuadre.adelantos : [];
  const totalAdelantos = adelantos.reduce((s, a) => s + Number(a.valor || 0), 0);
  const adelantosTexto = adelantos.map(a => '- ' + (a.nombre || 'N/A')).join('\n   ');
  const textAdelantos = adelantos.length > 0 
    ? '✅ Adelantos: ' + formatCOP(totalAdelantos) + '\n   ' + adelantosTexto
    : '❌ Adelantos: NO';

  // 🔹 NOVEDADES
  const novedades = Array.isArray(cuadre.novedades) ? cuadre.novedades : [];
  const novedadesTexto = novedades.map(n => '• ' + (n.comentarios || 'Sin comentario')).join('\n   ');
  const textNovedades = novedades.length > 0 
    ? '✅ Novedades (' + novedades.length + '):\n   ' + novedadesTexto
    : '❌ Novedades: NO';

  // 🔹 INGRESOS VARIOS
  const ingresosVarios = Array.isArray(cuadre.ingresosVarios) ? cuadre.ingresosVarios : [];
  const totalIngresosVarios = ingresosVarios.reduce((s, i) => s + Number(i.valor || 0), 0);
  const ingresosVariosTexto = ingresosVarios.map(i => '- ' + (i.concepto || 'Sin concepto') + ': ' + formatCOP(i.valor || 0)).join('\n   ');
  const textIngresosVarios = ingresosVarios.length > 0 
    ? '✅ Ingresos Varios (' + ingresosVarios.length + '):\n   ' + ingresosVariosTexto + '\n   TOTAL: ' + formatCOP(totalIngresosVarios)
    : '❌ Ingresos Varios: NO';

  // 🔹 MEDIOS DE PAGO (DESDE VENTAS)
  const ventas = cuadre.ventas || {};
  const ventaEfectivo = Number(ventas.ventaEfectivo || 0);
  
  // 🔹 TRANSFERENCIAS POR BANCO - Se calculan desde el array transfer
  const transfer = Array.isArray(cuadre.transfer) ? cuadre.transfer : [];
  let bancolombiaVentas = 0, daviviendaVentas = 0, daviplataVentas = 0, nequiVentas = 0;
  transfer.forEach(t => {
    bancolombiaVentas += Number(t.bancolombia || 0);
    daviviendaVentas += Number(t.davivienda || 0);
    daviplataVentas += Number(t.daviplata || 0);
    nequiVentas += Number(t.nequi || 0);
  });
  
  const ventaDatafono = Number(ventas.ventaDatafono || 0);
  const onlineRappi = Number(ventas.onlineRappi || 0);

  // 🔹 CAJA
  const efectivoEnCaja = Number(ventas.efectivoEnCaja || 0);
  const deberíaQuedar = Number(ventas.deberíaQuedarEnCaja || 0);
  const diferenciaCaja = Number(ventas.diferenciaCaja || 0);
  const estadoCuadre = ventas.estadoCuadre || 'Cuadrado';

  // 🔹 FACTURAS (AGRUPADAS POR ESTADO)
  const facturas = Array.isArray(cuadre.facturas) ? cuadre.facturas : [];
  const factPendientes = facturas.filter(f => f.estado === 'Pendiente');
  const factPagadaCaja = facturas.filter(f => f.estado === 'Pagada de caja');
  const factPagadaTransfer = facturas.filter(f => f.estado === 'Pagada transferencia');
  
  const totalFactPendientes = factPendientes.reduce((s, f) => s + Number(f.valor || 0), 0);
  const totalFactCaja = factPagadaCaja.reduce((s, f) => s + Number(f.valor || 0), 0);
  const totalFactTransfer = factPagadaTransfer.reduce((s, f) => s + Number(f.valor || 0), 0);

  // 🔹 DOMICILIOS
  const domicilios = Array.isArray(cuadre.domicilios) ? cuadre.domicilios : [];
  const domiciliosConDatafono = domicilios.filter(d => Number(d.cobroDatafono || 0) > 0);
  const domiciliosSinDatafono = domicilios.filter(d => Number(d.cobroDatafono || 0) === 0);
  const valorDomicilios = domicilios.reduce((s, d) => s + Number(d.valor || 0), 0);
  const valorDatafonoDomicilios = domicilios.reduce((s, d) => s + Number(d.cobroDatafono || 0), 0);
  const totalDomicilios = valorDomicilios + valorDatafonoDomicilios;

  // 🔹 DATAFONOS
  const datafonos = Array.isArray(cuadre.datafonos) ? cuadre.datafonos : [];
  const totalDatafonos = datafonos.reduce((s, d) => s + Number(d.valor || 0), 0);

  // 🔹 SOBRANTE NETO
  const sobranteNeto = Number(ventas.sobrante || 0);

  // 🔹 VALOR TOTAL DEL SOBRANTE (Sobrante Neto + Diferencia)
  const diferencia = efectivoEnCaja - deberíaQuedar - sobranteNeto;
  const valorTotalSobrante = sobranteNeto + diferencia;

  // 🔹 GENERAR HTML PARA IMPRESIÓN (FORMATO TICKET 80MM)
  let html = '<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Resumen Cuadre</title><style>*{margin:0;padding:0}body{font-family:Courier,monospace;width:80mm;padding:5mm;line-height:1.8;color:#000;font-size:14px}h1{font-size:22px;font-weight:bold;border-bottom:3px solid #000;padding:10px 0;margin-bottom:8px;text-align:center}h2{font-size:16px;font-weight:bold;border:2px solid #000;padding:7px 5px;margin:12px 0 6px 0;background:#f5f5f5}.info{font-size:14px;text-align:left;margin:5px 0;font-weight:600;color:#000;line-height:1.6}.grande{font-size:18px;font-weight:bold;border:3px solid #000;padding:12px;background:#ffffff;margin:15px 0;text-align:center}</style></head><body><h1>RESUMEN CUADRE</h1>';
  html += '<div class="info"><b>Fecha:</b> ' + fecha + '</div>';
  html += '<div class="info"><b>Sede:</b> ' + sede + '</div>';
  html += '<div class="info"><b>Responsable:</b> ' + responsable + '</div>';
  html += '<h2>TURNOS</h2><div class="info">' + textTurnos + '</div>';
  html += '<h2>ADELANTOS</h2><div class="info">' + textAdelantos.replace(/\n/g, '<br>') + '</div>';
  html += '<h2>NOVEDADES</h2><div class="info">' + textNovedades.replace(/\n/g, '<br>') + '</div>';
  html += '<h2>INGRESOS VARIOS</h2><div class="info">' + textIngresosVarios.replace(/\n/g, '<br>') + '</div>';
  html += '<h2>MEDIOS DE PAGO</h2>';
  html += '<div class="info"><b>Efectivo:</b> ' + formatCOP(ventaEfectivo) + '</div>';
  html += '<div class="info"><b>Bancolombia:</b> ' + formatCOP(bancolombiaVentas) + '</div>';
  html += '<div class="info"><b>Davivienda:</b> ' + formatCOP(daviviendaVentas) + '</div>';
  html += '<div class="info"><b>Daviplata:</b> ' + formatCOP(daviplataVentas) + '</div>';
  html += '<div class="info"><b>Nequi:</b> ' + formatCOP(nequiVentas) + '</div>';
  html += '<div class="info"><b>Datafono:</b> ' + formatCOP(ventaDatafono) + '</div>';
  html += '<div class="info"><b>Online Rappi:</b> ' + formatCOP(onlineRappi) + '</div>';
  html += '<h2>VERIFICACIÓN DE CAJA</h2>';
  html += '<div class="info"><b>Efectivo en caja:</b> ' + formatCOP(efectivoEnCaja) + '</div>';
  html += '<div class="info"><b>Debería quedar:</b> ' + formatCOP(deberíaQuedar) + '</div>';
  html += '<div class="info"><b>Diferencia:</b> ' + formatCOP(diferenciaCaja) + '</div>';
  html += '<div class="info"><b>Estado:</b> ' + estadoCuadre + '</div>';
  html += '<div class="info"><b>Sobrante Neto:</b> ' + formatCOP(sobranteNeto) + '</div>';
  html += '<div class="info"><b>Valor Total del Sobrante:</b> ' + formatCOP(valorTotalSobrante) + '</div>';
  html += '<h2>FACTURAS</h2>';
  html += '<div class="info"><b>Pendientes:</b> ' + factPendientes.length + ' - ' + formatCOP(totalFactPendientes) + '</div>';
  html += '<div class="info"><b>Pagada Caja:</b> ' + factPagadaCaja.length + ' - ' + formatCOP(totalFactCaja) + '</div>';
  html += '<div class="info"><b>Pagada Transfer:</b> ' + factPagadaTransfer.length + ' - ' + formatCOP(totalFactTransfer) + '</div>';
  html += '<h2>DOMICILIOS</h2>';
  html += '<div class="info"><b>Total Domicilios:</b> ' + domicilios.length + '</div>';
  html += '<div class="info"><b>  - Con Datafono:</b> ' + domiciliosConDatafono.length + '</div>';
  html += '<div class="info"><b>  - Sin Datafono:</b> ' + domiciliosSinDatafono.length + '</div>';
  html += '<div class="info"><b>Valor Domicilios:</b> ' + formatCOP(valorDomicilios) + '</div>';
  html += '<div class="info"><b>TOTAL DOMICILIOS:</b> ' + formatCOP(totalDomicilios) + '</div>';
  
  html += '<h2>TRANSFERENCIAS</h2>';
  const totalTransferenciasBancos = bancolombiaVentas + daviviendaVentas + daviplataVentas + nequiVentas;
  html += '<div class="info"><b>Total Transfers:</b> ' + transfer.length + '</div>';
  html += '<div class="info"><b>Valor Total:</b> ' + formatCOP(totalTransferenciasBancos) + '</div>';
  if (bancolombiaVentas > 0) html += '<div class="info"><b>  🏦 Bancolombia:</b> ' + formatCOP(bancolombiaVentas) + '</div>';
  if (daviviendaVentas > 0) html += '<div class="info"><b>  🏦 Davivienda:</b> ' + formatCOP(daviviendaVentas) + '</div>';
  if (daviplataVentas > 0) html += '<div class="info"><b>  📱 Daviplata:</b> ' + formatCOP(daviplataVentas) + '</div>';
  if (nequiVentas > 0) html += '<div class="info"><b>  📱 Nequi:</b> ' + formatCOP(nequiVentas) + '</div>';
  html += '<h2>DATAFONOS</h2>';
  html += '<div class="info"><b>Valor Total:</b> ' + formatCOP(totalDatafonos) + '</div>';
  html += '<div class="grande" style="text-align:center;">VALOR TOTAL DEL SOBRANTE<br>' + formatCOP(valorTotalSobrante) + '</div>';
  
  // 🔹 Determinar si está cuadrado o descuadrado
  // Fórmula: Valor Total del Sobrante - Sobrante Neto
  const diferenciaNeta = valorTotalSobrante - sobranteNeto;
  let leyendaCuadre = '';
  let signoSobrante = '';
  
  if (diferenciaNeta > 0) {
    leyendaCuadre = 'SOBRANTE';
    signoSobrante = '+' + formatCOP(diferenciaNeta);
  } else if (diferenciaNeta < 0) {
    leyendaCuadre = 'FALTANTE';
    signoSobrante = formatCOP(diferenciaNeta);
  } else {
    leyendaCuadre = 'CUADRADO';
    signoSobrante = '$0';
  }
  
  html += '<div style="text-align:center;font-size:18px;font-weight:bold;margin:15px 0;padding:10px;border:3px solid #000;background:#fff;color:#000;line-height:1.6">' + leyendaCuadre + ' = ' + signoSobrante + '</div>';
  
  html += '<p style="font-size:12px;text-align:center;margin-top:20px;font-weight:600;color:#000;line-height:1.6">Impreso: ' + new Date(cuadre.fecha).toLocaleString('es-CO') + '<br>BigBurguer - Sistema Cuadre</p>';
  html += '</body></html>';

  return html;
}

/* MARCADOR - FIN DE FUNCIÓN GENERARRESUMENTICKETIMPRESION */

/* ========== FIN DE generarResumenTicketImpresion - Código corrupto eliminado ========== */
 
function calcularTotalesCuadre(c) {
  const totalFactCaja = (c.facturas || [])
    .filter((f) => f.estado === "Pagada de caja")
    .reduce((s, f) => s + Number(f.valor || 0), 0);
 
  const totalDomicilios = (c.domicilios || []).reduce(
    (s, d) => s + Number(d.valor || 0) + Number(d.cobroDatafono || 0),
    0
  );
 
  let tb = 0,
    td = 0,
    tdp = 0,
    tn = 0;
  (c.transfer || []).forEach((t) => {
    tb += Number(t.bancolombia || 0);
    td += Number(t.davivienda || 0);
    tdp += Number(t.daviplata || 0);
    tn += Number(t.nequi || 0);
  });
  const totalTransfer = tb + td + tdp + tn;
 
  const totalTurnos = (c.turnos || []).reduce(
    (s, t) => s + Number(t.valor || 0),
    0
  );
 
  const totalAdelantos = (c.adelantos || []).reduce(
    (s, a) => s + Number(a.valor || 0),
    0
  );
 
  const totalDenoms = (c.denoms || []).reduce(
    (s, d) => s + Number(d.subtotal || 0),
    0
  );

  const totalDatafonos = (c.datafonos || []).reduce(
    (s, d) => s + Number(d.valor || 0),
    0
  );

  // 🔹 Total ingresos varios
  const totalIngresosVarios = (c.ingresosVarios || []).reduce(
    (s, ing) => s + Number(ing.valor || 0),
    0
  );

  // 🔹 Sobrante en efectivo calculado automáticamente
  const ventaEfectivo = Number(c.ventas?.ventaEfectivo || 0);
  const sobranteEfectivo = ventaEfectivo - totalFactCaja - totalDomicilios + totalIngresosVarios;
 
  return {
    totalFactCaja,
    totalDomicilios,
    totalTransfer,
    tb,
    td,
    tdp,
    tn,
    totalTurnos,
    totalAdelantos,
    totalDenoms,
    totalDatafonos,
    totalIngresosVarios,
    sobranteEfectivo,
  };
}
 
function imprimirCuadreCompleto(c, empleados) {
  const empleado = empleados.find((e) => e.id === c.empleadoId);
  const tot = calcularTotalesCuadre(c);
  const fechaHoy = new Date().toLocaleString('es-CO');
  const formatCurrency = (val) => new Intl.NumberFormat("es-CO", {
	style: "currency",
	currency: "COP",
	minimumFractionDigits: 0,
	maximumFractionDigits: 0
  }).format(val || 0);
 
  let html = `
	<!DOCTYPE html>
	<html lang="es">
  	<head>
    	<meta charset="UTF-8" />
    	<meta name="viewport" content="width=device-width, initial-scale=1.0">
    	<title>Cuadre Diario - ${c.fecha} - ${c.sede}</title>
    	<style>
      	* {
        	margin: 0;
        	padding: 0;
        	box-sizing: border-box;
      	}
         
      	body {
        	font-family: 'Arial', 'Helvetica', sans-serif;
        	padding: 30px;
        	background: #f5f5f5;
        	color: #2c3e50;
      	}
         
      	@media print {
        	body {
          	background: white !important;
          	padding: 15px;
        	}
        	.no-print {
          	display: none !important;
        	}
        	.page-break {
          	page-break-after: always;
        	}
      	}
         
      	.container {
        	max-width: 1100px;
        	margin: 0 auto;
        	background: white;
        	box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      	}
         
      	.header {
        	background: #e31e24;
        	color: white;
        	padding: 20px 30px;
        	border-bottom: 4px solid #b91c1c;
        	display: flex;
        	align-items: center;
        	justify-content: space-between;
      	}
         
      	.header-left {
        	flex: 1;
      	}
         
      	.header-right {
        	display: flex;
        	align-items: center;
        	gap: 20px;
      	}
         
      	.logo-container {
        	width: 120px;
        	height: 120px;
        	background: white;
        	border-radius: 50%;
        	display: flex;
        	align-items: center;
        	justify-content: center;
        	padding: 10px;
        	box-shadow: 0 4px 8px rgba(0,0,0,0.2);
      	}
         
      	.logo-container img {
        	width: 100%;
        	height: 100%;
        	object-fit: contain;
      	}
         
      	.header h1 {
        	font-size: 28px;
        	font-weight: bold;
        	margin-bottom: 8px;
        	letter-spacing: -0.5px;
      	}
         
      	.header-subtitle {
        	font-size: 14px;
        	opacity: 0.95;
        	font-weight: normal;
      	}
         
      	.info-bar {
        	background: #34495e;
        	color: white;
        	padding: 15px 30px;
        	display: grid;
        	grid-template-columns: repeat(4, 1fr);
        	gap: 20px;
        	border-bottom: 2px solid #2c3e50;
      	}
         
      	.info-item {
        	text-align: center;
      	}
         
      	.info-label {
        	display: block;
        	font-size: 11px;
        	text-transform: uppercase;
        	letter-spacing: 0.5px;
        	opacity: 0.8;
        	margin-bottom: 5px;
      	}
         
      	.info-value {
        	display: block;
        	font-size: 16px;
        	font-weight: bold;
      	}
         
      	.content {
        	padding: 30px;
      	}
         
      	.section {
        	margin-bottom: 30px;
        	page-break-inside: avoid;
      	}
         
      	.section-title {
        	font-size: 16px;
        	font-weight: bold;
        	color: #2c3e50;
        	margin-bottom: 12px;
        	padding: 10px 15px;
        	background: #ecf0f1;
        	border-left: 4px solid #e31e24;
        	text-transform: uppercase;
        	letter-spacing: 0.5px;
      	}
         
      	table {
        	width: 100%;
        	border-collapse: collapse;
        	margin-top: 10px;
        	font-size: 13px;
      	}
         
      	thead {
        	background: #34495e;
        	color: white;
      	}
         
      	th {
        	padding: 12px;
        	text-align: left;
        	font-weight: 600;
        	font-size: 12px;
        	text-transform: uppercase;
        	letter-spacing: 0.3px;
      	}
         
      	td {
        	padding: 10px 12px;
        	border-bottom: 1px solid #e0e0e0;
        	color: #2c3e50;
      	}
         
      	tbody tr:nth-child(even) {
        	background-color: #f8f9fa;
      	}
         
      	tbody tr:hover {
        	background-color: #e8f4f8;
      	}
         
      	.totales {
        	background: #2c3e50;
        	color: white;
        	padding: 25px 30px;
        	margin-top: 30px;
      	}
         
      	.totales h2 {
        	font-size: 18px;
        	margin-bottom: 20px;
        	text-transform: uppercase;
        	letter-spacing: 1px;
        	border-bottom: 2px solid #e31e24;
        	padding-bottom: 10px;
      	}
         
      	.totales-grid {
        	display: grid;
        	grid-template-columns: repeat(3, 1fr);
        	gap: 15px;
      	}
         
      	.total-item {
        	background: rgba(255,255,255,0.1);
        	padding: 15px;
        	border-left: 3px solid #e31e24;
      	}
         
      	.total-label {
        	display: block;
        	font-size: 11px;
        	text-transform: uppercase;
        	opacity: 0.8;
        	margin-bottom: 8px;
        	letter-spacing: 0.5px;
      	}
         
      	.total-value {
        	display: block;
        	font-size: 20px;
        	font-weight: bold;
      	}
         
      	.comentario-box {
        	background: #fff3cd;
        	border-left: 4px solid #ffc107;
        	padding: 15px 20px;
        	margin-top: 20px;
      	}
         
      	.comentario-box h3 {
        	color: #856404;
        	font-size: 14px;
        	margin-bottom: 10px;
        	text-transform: uppercase;
      	}
         
      	.comentario-box p {
        	color: #856404;
        	line-height: 1.5;
      	}
         
      	.footer {
        	background: #ecf0f1;
        	padding: 20px 30px;
        	text-align: center;
        	border-top: 3px solid #e31e24;
      	}
         
      	.footer-content {
        	display: flex;
        	justify-content: space-between;
        	align-items: center;
        	color: #7f8c8d;
        	font-size: 11px;
      	}
         
      	.empty-state {
        	text-align: center;
        	padding: 30px;
        	color: #95a5a6;
        	font-style: italic;
        	background: #f8f9fa;
        	border: 1px dashed #dee2e6;
      	}
         
      	.badge {
        	display: inline-block;
        	padding: 4px 10px;
        	border-radius: 3px;
        	font-size: 11px;
        	font-weight: 600;
        	text-transform: uppercase;
      	}
         
      	.badge-success {
        	background: #27ae60;
        	color: white;
      	}
         
      	.badge-error {
        	background: #e74c3c;
        	color: white;
      	}
      	
      	.badge-warning {
        	background: #f39c12;
        	color: white;
      	}
         
      	.signature-section {
        	margin-top: 60px;
        	display: grid;
        	grid-template-columns: 1fr 1fr;
        	gap: 40px;
        	page-break-inside: avoid;
      	}
         
      	.signature-box {
        	text-align: center;
      	}
         
      	.signature-line {
        	border-top: 2px solid #2c3e50;
        	margin-top: 50px;
        	padding-top: 10px;
        	font-size: 12px;
        	color: #7f8c8d;
        	text-transform: uppercase;
      	}
    	</style>
  	</head>
  	<body>
    	<div class="container">
      	<div class="header">
        	<div class="header-left">
          	<h1>CUADRE DIARIO</h1>
          	<div class="header-subtitle">Informe de Cierre de Caja - ${c.sede}</div>
        	</div>
        	<div class="header-right">
          	<div class="logo-container">
            	<img src="data:image/png;base64,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" alt="Big Burguer Logo" />
          	</div>
        	</div>
      	</div>
         
      	<div class="info-bar">
        	<div class="info-item">
          	<span class="info-label">Fecha</span>
          	<span class="info-value">${c.fecha}</span>
        	</div>
        	<div class="info-item">
          	<span class="info-label">Responsable</span>
          	<span class="info-value">${empleado ? empleado.nombre : "—"}</span>
        	</div>
        	<div class="info-item">
          	<span class="info-label">Valor Inicial</span>
          	<span class="info-value">${formatCurrency(c.valorInicial)}</span>
        	</div>
        	<div class="info-item">
          	<span class="info-label">Generado</span>
          	<span class="info-value">${new Date().toLocaleDateString('es-CO')}</span>
        	</div>
      	</div>
         
      	<div class="content">
  `;
 
  // Facturas
  html += `
	<div class="section">
  	<h2 class="section-title">Facturas de Caja</h2>
  	${(c.facturas || []).length === 0 ?
    	'<div class="empty-state">No hay facturas registradas en este período</div>' :
    	`<table>
      	<thead>
        	<tr>
          	<th>N° Factura</th>
          	<th>Valor</th>
          	<th>Estado</th>
          	<th>Fecha Origen</th>
          	<th>Estado Tarea</th>
        	</tr>
      	</thead>
      	<tbody>
        	${(c.facturas || []).map(f => {
          	// Determinar el badge del estado
          	let badgeClass = 'badge-error';
          	if (f.estado === 'Pagada') badgeClass = 'badge-success';
          	else if (f.estado === 'Pagada transferencia') badgeClass = 'badge-warning';
          	
          	// Estado de la tarea para facturas por transferencia
          	let estadoTareaTexto = '—';
          	let estadoTareaBadge = '';
          	if (f.estado === 'Pagada transferencia' && f.tareaEnviadaId) {
            	if (f.estadoTarea === 'Procesada') {
              	estadoTareaTexto = 'Procesada';
              	estadoTareaBadge = 'badge-success';
            	} else if (f.estadoTarea === 'Pendiente') {
              	estadoTareaTexto = 'Pendiente';
              	estadoTareaBadge = 'badge-warning';
            	} else {
              	estadoTareaTexto = f.estadoTarea || 'Enviada';
              	estadoTareaBadge = 'badge-warning';
            	}
          	}
          	
          	// Mostrar fecha de origen si la factura viene de otro cuadre
          	let fechaOrigen = '—';
          	if (f.origenFecha && f.origenFecha !== c.fecha) {
            	fechaOrigen = f.origenFecha;
          	} else if (f.estado === 'Pendiente' && !f.origenFecha) {
            	// Para facturas pendientes sin fecha de origen registrada
            	fechaOrigen = '(sin registro)';
          	}
          	
          	return `
          	<tr>
            	<td><strong>${f.factura || "—"}</strong></td>
            	<td>${formatCurrency(f.valor)}</td>
            	<td><span class="badge ${badgeClass}">${f.estado || "—"}</span></td>
            	<td>${fechaOrigen}</td>
            	<td>${estadoTareaBadge ? `<span class="badge ${estadoTareaBadge}">${estadoTareaTexto}</span>` : estadoTareaTexto}</td>
          	</tr>
        	`;
        	}).join('')}
      	</tbody>
    	</table>`
  	}
	</div>
  `;
 
  // Domicilios
  html += `
	<div class="section">
  	<h2 class="section-title">Servicio a Domicilio</h2>
  	${(c.domicilios || []).length === 0 ?
    	'<div class="empty-state">No hay domicilios registrados en este período</div>' :
    	`<table>
      	<thead>
        	<tr>
          	<th>N° Pedido</th>
          	<th>Valor</th>
          	<th>Forma de Pago</th>
          	<th>Confirmado</th>
          	<th>Observaciones</th>
        	</tr>
      	</thead>
      	<tbody>
        	${(c.domicilios || []).map(d => `
          	<tr>
            	<td><strong>${d.pedido || "—"}</strong></td>
            	<td>${formatCurrency(d.valor)}</td>
            	<td>${d.pago || "—"}</td>
            	<td style="text-align: center; font-size: 18px;">${d.pago_confirmado ? '<span style="color: #22c55e;">✓</span>' : (d.pago === 'Pendiente' ? '<span style="color: #f59e0b;">⏳</span>' : '<span style="color: #ef4444;">✗</span>')}</td>
            	<td>${d.obs || "—"}</td>
          	</tr>
        	`).join('')}
      	</tbody>
    	</table>`
  	}
	</div>
  `;
 
  // Transferencias
  html += `
	<div class="section">
  	<h2 class="section-title">Transferencias Electrónicas</h2>
  	${(c.transfer || []).length === 0 ?
    	'<div class="empty-state">No hay transferencias registradas en este período</div>' :
    	`<table>
      	<thead>
        	<tr>
          	<th>N° Pedido</th>
          	<th>Bancolombia</th>
          	<th style="text-align:center;">✓</th>
          	<th>Davivienda</th>
          	<th style="text-align:center;">✓</th>
          	<th>Daviplata</th>
          	<th style="text-align:center;">✓</th>
          	<th>Nequi</th>
          	<th style="text-align:center;">✓</th>
        	</tr>
      	</thead>
      	<tbody>
        	${(c.transfer || []).map(t => `
          	<tr>
            	<td><strong>${t.pedido || "—"}</strong></td>
            	<td style="background-color: ${Number(t.bancolombia || 0) > 0 ? '#f0fdf4' : 'transparent'};">${formatCurrency(t.bancolombia)}</td>
            	<td style="text-align: center; font-size: 16px;">${Number(t.bancolombia || 0) > 0 ? (t.bancolombia_confirmado ? '<span style="color: #22c55e;">✓</span>' : '<span style="color: #ef4444;">✗</span>') : ''}</td>
            	<td style="background-color: ${Number(t.davivienda || 0) > 0 ? '#fef3c7' : 'transparent'};">${formatCurrency(t.davivienda)}</td>
            	<td style="text-align: center; font-size: 16px;">${Number(t.davivienda || 0) > 0 ? (t.davivienda_confirmado ? '<span style="color: #22c55e;">✓</span>' : '<span style="color: #ef4444;">✗</span>') : ''}</td>
            	<td style="background-color: ${Number(t.daviplata || 0) > 0 ? '#fee2e2' : 'transparent'};">${formatCurrency(t.daviplata)}</td>
            	<td style="text-align: center; font-size: 16px;">${Number(t.daviplata || 0) > 0 ? (t.daviplata_confirmado ? '<span style="color: #22c55e;">✓</span>' : '<span style="color: #ef4444;">✗</span>') : ''}</td>
            	<td style="background-color: ${Number(t.nequi || 0) > 0 ? '#ede9fe' : 'transparent'};">${formatCurrency(t.nequi)}</td>
            	<td style="text-align: center; font-size: 16px;">${Number(t.nequi || 0) > 0 ? (t.nequi_confirmado ? '<span style="color: #22c55e;">✓</span>' : '<span style="color: #ef4444;">✗</span>') : ''}</td>
          	</tr>
        	`).join('')}
      	</tbody>
    	</table>`
  	}
	</div>
  `;
 
  // Turnos
  html += `
	<div class="section">
  	<h2 class="section-title">Turnos de Personal</h2>
  	${(c.turnos || []).length === 0 ?
    	'<div class="empty-state">No hay turnos registrados en este período</div>' :
    	`<table>
      	<thead>
        	<tr>
          	<th>Empleado</th>
          	<th>Valor</th>
        	</tr>
      	</thead>
      	<tbody>
        	${(c.turnos || []).map(t => `
          	<tr>
            	<td>${t.empleado || "—"}</td>
            	<td>${formatCurrency(t.valor)}</td>
          	</tr>
        	`).join('')}
      	</tbody>
    	</table>`
  	}
	</div>
  `;
 
  // Adelantos
  html += `
	<div class="section">
  	<h2 class="section-title">Adelantos a Personal</h2>
  	${(c.adelantos || []).length === 0 ?
    	'<div class="empty-state">No hay adelantos registrados en este período</div>' :
    	`<table>
      	<thead>
        	<tr>
          	<th>Empleado</th>
          	<th>Valor</th>
          	<th>Registrado en Nómina</th>
        	</tr>
      	</thead>
      	<tbody>
        	${(c.adelantos || []).map(a => `
          	<tr>
            	<td>${a.empleado || "—"}</td>
            	<td>${formatCurrency(a.valor)}</td>
            	<td>${a.registradoNomina || "—"}</td>
          	</tr>
        	`).join('')}
      	</tbody>
    	</table>`
  	}
	</div>
  `;
 
  // Denominaciones
  html += `
	<div class="section">
  	<h2 class="section-title">Arqueode Caja - Denominaciones</h2>
  	${(c.denoms || []).length === 0 ?
    	'<div class="empty-state">No hay denominaciones registradas</div>' :
    	`<table>
      	<thead>
        	<tr>
          	<th>Denominación</th>
          	<th>Cantidad</th>
          	<th>Subtotal</th>
        	</tr>
      	</thead>
      	<tbody>
        	${(c.denoms || []).map(d => `
          	<tr>
            	<td><strong>${formatCurrency(d.valor)}</strong></td>
            	<td style="text-align: center;">${d.cantidad}</td>
            	<td style="text-align: right;"><strong>${formatCurrency(d.subtotal)}</strong></td>
          	</tr>
        	`).join('')}
      	</tbody>
    	</table>`
  	}
	</div>
  `;
 
  // Novedades
  html += `
	<div class="section">
  	<h2 class="section-title">Novedades del Día</h2>
  	${(c.novedades || []).length === 0 ?
    	'<div class="empty-state">No hay novedades registradas</div>' :
    	`<table>
      	<thead>
        	<tr>
          	<th>Tipo de Novedad</th>
          	<th>Reportado Por</th>
          	<th>Comentarios</th>
        	</tr>
      	</thead>
      	<tbody>
        	${(c.novedades || []).map(n => `
          	<tr>
            	<td><strong>${n.novedad || "—"}</strong></td>
            	<td>${n.aQuien || "—"}</td>
            	<td>${n.comentarios || "—"}</td>
          	</tr>
        	`).join('')}
      	</tbody>
    	</table>`
  	}
	</div>
  `;

  // Datafonos
  html += `
	<div class="section">
  	<h2 class="section-title">Datafonos</h2>
  	${(c.datafonos || []).length === 0 ?
    	'<div class="empty-state">No hay datafonos registrados</div>' :
    	`<table>
      	<thead>
        	<tr>
          	<th>Terminal</th>
          	<th>Valor</th>
        	</tr>
      	</thead>
      	<tbody>
        	${(c.datafonos || []).map(d => `
          	<tr>
            	<td><strong>${d.terminal || "—"}</strong></td>
            	<td>${formatCurrency(d.valor)}</td>
          	</tr>
        	`).join('')}
        	<tr style="background: #34495e;">
          	<td style="color: white;"><strong>TOTAL DATAFONOS</strong></td>
          	<td style="color: white;"><strong>${formatCurrency(tot.totalDatafonos)}</strong></td>
        	</tr>
      	</tbody>
    	</table>`
  	}
	</div>
  `;

  // Calcular totales de transferencias
  const totalBancolombia = (c.transfer || []).reduce((s, t) => s + Number(t.bancolombia || 0), 0);
  const totalDavivienda = (c.transfer || []).reduce((s, t) => s + Number(t.davivienda || 0), 0);
  const totalDaviplata = (c.transfer || []).reduce((s, t) => s + Number(t.daviplata || 0), 0);
  const totalNequi = (c.transfer || []).reduce((s, t) => s + Number(t.nequi || 0), 0);
  const totalTransferencias = totalBancolombia + totalDavivienda + totalDaviplata + totalNequi;

  // Ventas y medios de pago
  const sobrante = c.ventas?.sobrante || 0;
  const huboDescuadre = c.ventas?.huboDescuadre || "No";
  const valorDescuadre = c.ventas?.valorDescuadre || 0;
  const ventaEfectivo = c.ventas?.ventaEfectivo || 0;
  const onlineRappi = c.ventas?.onlineRappi || 0;
  const totalVentas = Number(ventaEfectivo) + totalTransferencias + tot.totalDatafonos + Number(onlineRappi);
  
  // Calcular totales de facturas por estado
  const facturasPagadasCaja = (c.facturas || []).filter(f => f.estado === "Pagada de caja").reduce((s, f) => s + Number(f.valor || 0), 0);
  const facturasPendientes = (c.facturas || []).filter(f => f.estado === "Pendiente").reduce((s, f) => s + Number(f.valor || 0), 0);
  const facturasPagadasTransfer = (c.facturas || []).filter(f => f.estado === "Pagada transferencia").reduce((s, f) => s + Number(f.valor || 0), 0);
  
  html += `
	<div class="section">
  	<h2 class="section-title">Ventas y Medios de Pago</h2>
  	<table>
    	<thead>
      	<tr>
        	<th>Medio de Pago</th>
        	<th style="text-align: right;">Valor</th>
      	</tr>
    	</thead>
    	<tbody>
      	<tr style="background-color: #f9f9f9;">
        	<td><strong>Efectivo</strong></td>
        	<td style="text-align: right; font-size: 14px;"><strong>${formatCurrency(ventaEfectivo)}</strong></td>
      	</tr>
      	<tr style="background-color: #ffffff;">
        	<td><strong>Bancolombia</strong></td>
        	<td style="text-align: right; font-size: 14px;"><strong>${formatCurrency(totalBancolombia)}</strong></td>
      	</tr>
      	<tr style="background-color: #f9f9f9;">
        	<td><strong>Davivienda</strong></td>
        	<td style="text-align: right; font-size: 14px;"><strong>${formatCurrency(totalDavivienda)}</strong></td>
      	</tr>
      	<tr style="background-color: #ffffff;">
        	<td><strong>Daviplata</strong></td>
        	<td style="text-align: right; font-size: 14px;"><strong>${formatCurrency(totalDaviplata)}</strong></td>
      	</tr>
      	<tr style="background-color: #f9f9f9;">
        	<td><strong>Nequi</strong></td>
        	<td style="text-align: right; font-size: 14px;"><strong>${formatCurrency(totalNequi)}</strong></td>
      	</tr>
      	<tr style="background-color: #ffffff;">
        	<td><strong>Datáfono</strong></td>
        	<td style="text-align: right; font-size: 14px;"><strong>${formatCurrency(tot.totalDatafonos)}</strong></td>
      	</tr>
      	<tr style="background-color: #f9f9f9;">
        	<td><strong>Online Rappi</strong></td>
        	<td style="text-align: right; font-size: 14px;"><strong>${formatCurrency(onlineRappi)}</strong></td>
      	</tr>
      	<tr style="background: #e5e7eb; color: #111;">
        	<td style="padding: 15px;"><strong style="font-size: 16px;">TOTAL VENTAS</strong></td>
        	<td style="text-align: right; padding: 15px;"><strong style="font-size: 18px;">${formatCurrency(totalVentas)}</strong></td>
      	</tr>
    	</tbody>
  	</table>
	</div>
  `;
  
  // Totales de Facturas por Estado
  html += `
	<div class="section">
  	<h2 class="section-title">Facturas por Estado</h2>
  	<table>
    	<thead>
      	<tr>
        	<th>Estado de Factura</th>
        	<th style="text-align: right;">Total</th>
      	</tr>
    	</thead>
    	<tbody>
      	<tr style="background-color: #f9f9f9;">
        	<td><strong>Pagadas de Caja</strong></td>
        	<td style="text-align: right; font-size: 14px;"><strong>${formatCurrency(facturasPagadasCaja)}</strong></td>
      	</tr>
      	<tr style="background-color: #ffffff;">
        	<td><strong>Pendientes</strong></td>
        	<td style="text-align: right; font-size: 14px;"><strong>${formatCurrency(facturasPendientes)}</strong></td>
      	</tr>
      	<tr style="background-color: #f9f9f9;">
        	<td><strong>Pagadas por Transferencia</strong></td>
        	<td style="text-align: right; font-size: 14px;"><strong>${formatCurrency(facturasPagadasTransfer)}</strong></td>
      	</tr>
    	</tbody>
  	</table>
	</div>
  `;

  // 🔹 Valores de verificación de caja (idénticos a la tirilla y a la tabla de consulta)
  const efectivoRealEnCajaPDF = Number(c.ventas?.efectivoEnCaja || 0);
  const debeQuedarEnCajaPDF = Number(c.ventas?.deberíaQuedarEnCaja || 0);
  const sobranteNetoPDF = Number(c.ventas?.sobrante || 0);
  const diferenciaEfectivoPDF = efectivoRealEnCajaPDF - debeQuedarEnCajaPDF - sobranteNetoPDF;
  const valorTotalSobrantePDF = sobranteNetoPDF + diferenciaEfectivoPDF;

  // Cuadre de Caja
  html += `
	<div class="section">
  	<h2 class="section-title">Cuadre de Caja</h2>
  	<table>
    	<tbody>
      	<tr style="background-color: #eff6ff;">
        	<td><strong>💵 Efectivo Real en Caja</strong></td>
        	<td style="text-align: right; font-size: 16px; color: #1e40af;"><strong>${formatCurrency(efectivoRealEnCajaPDF)}</strong></td>
      	</tr>
      	<tr style="background-color: #f3f4f6;">
        	<td><strong>📦 Debe Quedar en Caja</strong></td>
        	<td style="text-align: right; font-size: 16px; color: ${debeQuedarEnCajaPDF >= 0 ? '#047857' : '#dc2626'};"><strong>${formatCurrency(debeQuedarEnCajaPDF)}</strong></td>
      	</tr>
      	<tr style="background-color: #ecfdf5;">
        	<td><strong>💵 Sobrante Neto</strong></td>
        	<td style="text-align: right; font-size: 16px; color: ${sobranteNetoPDF >= 0 ? '#059669' : '#dc2626'};"><strong>${formatCurrency(sobranteNetoPDF)}</strong></td>
      	</tr>
      	<tr style="background-color: #f0f9ff;">
        	<td><strong>💰 Valor Total del Sobrante</strong></td>
        	<td style="text-align: right; font-size: 16px; color: #0c4a6e;"><strong>${formatCurrency(valorTotalSobrantePDF)}</strong></td>
      	</tr>
      	<tr style="background-color: ${Number(sobrante) >= 0 ? '#e8f5e9' : '#ffebee'};">
        	<td><strong>${Number(sobrante) >= 0 ? '📈 Sobrante Efectivo' : '📉 Faltante Efectivo'}</strong></td>
        	<td style="text-align: right; font-size: 16px; color: ${Number(sobrante) >= 0 ? '#4caf50' : '#f44336'};"><strong>${formatCurrency(Math.abs(sobrante))}</strong></td>
      	</tr>
      	<tr style="background-color: ${huboDescuadre === 'Sí' ? '#ffebee' : '#e8f5e9'};">
        	<td><strong>⚖️ Estado del Cuadre</strong></td>
        	<td style="text-align: right; font-size: 16px; color: ${huboDescuadre === 'Sí' ? '#f44336' : '#4caf50'};">
          	<strong>${huboDescuadre === 'Sí' ? '❌ HAY DESCUADRE' : '✅ CUADRE OK'}</strong>
        	</td>
      	</tr>
      	${huboDescuadre === 'Sí' ? `
      	<tr style="background-color: #ffebee;">
        	<td><strong>💢 Valor del Descuadre</strong></td>
        	<td style="text-align: right; font-size: 16px; color: #f44336;"><strong>${formatCurrency(valorDescuadre)}</strong></td>
      	</tr>
      	` : ''}
    	</tbody>
  	</table>
	</div>
  `;
 
  // Comentario de modificación
  if (c.comentarioModificacion) {
	html += `
  	<div class="comentario-box">
    	<h3>⚠ Comentario de Modificación</h3>
    	<p>${c.comentarioModificacion}</p>
  	</div>
	`;
  }
 
  // Totales
  html += `
	<div class="totales">
  	<h2>RESUMEN FINANCIERO</h2>
  	<div class="totales-grid">
    	<div class="total-item">
      	<span class="total-label">Venta Efectivo</span>
      	<span class="total-value">${formatCurrency(ventaEfectivo)}</span>
    	</div>
    	<div class="total-item">
      	<span class="total-label">Transferencias</span>
      	<span class="total-value">${formatCurrency(totalTransferencias)}</span>
    	</div>
    	<div class="total-item">
      	<span class="total-label">Datáfonos</span>
      	<span class="total-value">${formatCurrency(tot.totalDatafonos)}</span>
    	</div>
    	<div class="total-item">
      	<span class="total-label">Online Rappi</span>
      	<span class="total-value">${formatCurrency(onlineRappi)}</span>
    	</div>
    	<div class="total-item">
      	<span class="total-label">Turnos Pagados</span>
      	<span class="total-value">${formatCurrency(tot.totalTurnos)}</span>
    	</div>
    	<div class="total-item">
      	<span class="total-label">Adelantos</span>
      	<span class="total-value">${formatCurrency(tot.totalAdelantos)}</span>
    	</div>
    	<div class="total-item">
      	<span class="total-label">Facturas Pagadas</span>
      	<span class="total-value">${formatCurrency(tot.totalFactCaja)}</span>
    	</div>
    	<div class="total-item">
      	<span class="total-label">Domicilios</span>
      	<span class="total-value">${formatCurrency(tot.totalDomicilios)}</span>
    	</div>
    	<div class="total-item">
      	<span class="total-label">Sobrante Efectivo</span>
      	<span class="total-value">${formatCurrency(sobrante)}</span>
    	</div>
    	<div class="total-item">
      	<span class="total-label">Valor Total del Sobrante</span>
      	<span class="total-value">${formatCurrency(valorTotalSobrantePDF)}</span>
    	</div>
  	</div>
  	<div style="margin-top: 20px; padding-top: 15px; border-top: 2px solid #e31e24; text-align: center;">
    	<span style="font-size: 14px; opacity: 0.8;">TOTAL VENTAS DEL DÍA</span><br/>
    	<span style="font-size: 28px; font-weight: bold;">${formatCurrency(totalVentas)}</span>
  	</div>
	</div>
  `;
 
  // Sección de PRECIERRE si aplica
  if (c.precierreRealizado && c.precierre) {
    html += `
	<div class="section" style="background: #fef3c7; border: 2px solid #f59e0b; border-radius: 8px; padding: 15px; margin: 20px 30px;">
  	<h2 class="section-title" style="background: #f59e0b; color: white; border-left-color: #d97706;">📋 PRECIERRE DE TURNO</h2>
  	<table style="margin-top: 10px;">
    	<tbody>
      	<tr>
        	<td style="width: 40%; font-weight: bold;">Fecha del precierre:</td>
        	<td>${new Date(c.precierre.fecha).toLocaleString("es-CO")}</td>
      	</tr>
      	<tr>
        	<td style="font-weight: bold;">Realizado por:</td>
        	<td>${c.precierre.porNombre || "—"}</td>
      	</tr>
      	<tr>
        	<td style="font-weight: bold;">Asignado a:</td>
        	<td>${c.precierre.asignadoANombre || "—"}</td>
      	</tr>
      	${c.precierre.observaciones ? `
      	<tr>
        	<td style="font-weight: bold;">Observaciones:</td>
        	<td>${c.precierre.observaciones}</td>
      	</tr>
      	` : ''}
      	<tr style="background: #fef9c3;">
        	<td style="font-weight: bold;">Totales al precierre:</td>
        	<td>Efectivo: ${formatCurrency(c.precierre.totales?.efectivoTotal || 0)} | Transfer: ${formatCurrency(c.precierre.totales?.transferTotal || 0)}</td>
      	</tr>
    	</tbody>
  	</table>
	</div>
    `;
  }

  // Sección de firmas
  html += `
	<div class="content">
  	<div class="signature-section">
    	<div class="signature-box">
      	<div class="signature-line">
        	Elaborado Por<br/>
        	<strong>${empleado ? empleado.nombre : ""}</strong>
        	${c.precierreRealizado && c.precierre ? `<br/><small style="color: #666;">(Continuado por ${c.precierre.asignadoANombre})</small>` : ''}
      	</div>
    	</div>
    	<div class="signature-box">
      	<div class="signature-line">
        	${c.cerradoPorNombre ? `Cerrado Por<br/><strong>${c.cerradoPorNombre}</strong>` : 'Revisado Por<br/><strong>Gerente de Sede</strong>'}
        	${c.fechaCierre ? `<br/><small style="color: #666;">${new Date(c.fechaCierre).toLocaleString("es-CO")}</small>` : ''}
      	</div>
    	</div>
  	</div>
	</div>
  `;
 
  html += `
      	</div>
         
      	<div class="footer">
        	<div class="footer-content">
          	<div>Big Burguer® - Sistema de Gestión Empresarial</div>
          	<div>Documento generado: ${fechaHoy}</div>
          	<div>Confidencial</div>
        	</div>
      	</div>
    	</div>
  	</body>
	</html>
  `;
 
  const w = window.open("", "_blank");
  w.document.write(html);
  w.document.close();
  w.print();
}
 
/* -------- Recibos pequeños -------- */
 
function imprimirRecibo(tipo, filas, total) {
  const fechaStr = new Date().toLocaleDateString("es-CO");
 
  let titulo = "";
  let descripcion = "";
  let nota = "";
 
  if (tipo === "ADELANTOS") {
    titulo = "Recibo de adelanto de nómina";
    descripcion = "Detalle de adelantos de nómina pagados en la fecha indicada.";
    nota =
      "Declaro que he recibido a conformidad el adelanto de nómina por los valores aquí señalados y autorizo su descuento en la próxima quincena.";
  } else if (tipo === "TURNOS") {
    titulo = "Recibo de pago de turnos";
    descripcion =
      "Detalle de pagos por turnos de trabajo correspondientes a la fecha indicada.";
    nota =
      "Declaro que he recibido a conformidad el pago de turnos por los valores aquí señalados.";
  } else {
    titulo = "Recibo";
    descripcion = "";
    nota = "";
  }
 
  let html = `
    <html>
      <head>
        <meta charset="UTF-8" />
        <title>${titulo}</title>
        <style>
          @page { size: 80mm 60mm; margin: 4mm; }
          body { font-family: Arial, sans-serif; font-size: 10px; }
          h1 { font-size: 12px; text-align: center; margin: 4px 0; }
          p { margin: 2px 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 4px; }
          td, th { border: 1px solid #ddd; padding: 2px; }
          th { background: #f3f4f6; }
        </style>
      </head>
      <body>
        <h1>${titulo}</h1>
        <p><strong>Fecha:</strong> ${fechaStr}</p>
        ${
          descripcion
            ? `<p style="font-size:9px;">${descripcion}</p>`
            : ""
        }
        <table>
          <thead>
            <tr><th>Empleado</th><th>Valor (COP)</th></tr>
          </thead>
          <tbody>
  `;
 
  filas.forEach((f) => {
    html += `<tr><td>${f.empleado || ""}</td><td>${new Intl.NumberFormat(
      "es-CO",
      { style: "currency", currency: "COP" }
    ).format(Number(f.valor || 0))}</td></tr>`;
  });
 
  html += `</tbody></table>
        <p style="margin-top:4px;"><strong>Total:</strong> ${new Intl.NumberFormat(
          "es-CO",
          { style: "currency", currency: "COP" }
        ).format(total)}</p>
  `;
 
  if (nota) {
    html += `<p style="margin-top:6px;font-size:9px;text-align:justify;">${nota}</p>`;
  }
 
  html += `
        <p style="margin-top:14px;text-align:center;">______________________________</p>
        <p style="font-size:9px;text-align:center;">Firma del empleado</p>
      </body>
    </html>
  `;
 
  const w = window.open("", "_blank", "width=400,height=300");
  w.document.write(html);
  w.document.close();
  w.print();
}
 
/* ====================== ESTILOS INLINE (clases simples) ====================== */
 
const styleEl = document.createElement("style");
styleEl.innerHTML = `
.card {
  background: #ffffff;
  border-radius: 10px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  margin-bottom: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  max-width: 100%;
}
.subcard {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 10px;
  background: #fafbfc;
}
.subcard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
}
.cuadre-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e2e8f0;
}
.field {
  display: flex;
  flex-direction: column;
  margin-bottom: 6px;
  font-size: 12px;
}
.field-label {
  color: #64748b;
  margin-bottom: 3px;
  font-weight: 500;
  font-size: 11px;
}
.input {
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  padding: 6px 8px;
  font-size: 12px;
  width: 100%;
  max-width: 280px;
  transition: border-color 0.2s;
}
.input:focus {
  outline: none;
  border-color: #ff6b35;
  box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.15);
}
.textarea {
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  padding: 6px 8px;
  font-size: 12px;
  width: 100%;
  max-width: 100%;
  resize: vertical;
  transition: border-color 0.2s;
}
.textarea:focus {
  outline: none;
  border-color: #ff6b35;
  box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.15);
}
.btn {
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 11px;
  cursor: pointer;
  border: none;
  font-weight: 500;
  transition: all 0.2s;
}
.btn-solid {
  background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
  color: white;
}
.btn-solid:hover {
  box-shadow: 0 2px 8px rgba(255, 107, 53, 0.35);
}
.btn-outline {
  background: white;
  border: 1px solid #cbd5e1;
  color: #334155;
}
.btn-outline:hover {
  background: #f8fafc;
  border-color: #ff6b35;
}
.btn-danger {
  background: #dc2626;
  color: white;
}
.btn-danger:hover {
  background: #b91c1c;
}
.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  margin-top: 6px;
  border-radius: 6px;
  overflow: hidden;
}
.table th, .table td {
  border: 1px solid #e2e8f0;
  padding: 6px 8px;
}
.table th {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  font-weight: 600;
  color: #475569;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.table td {
  background: #fff;
}
.table .input {
  max-width: 100%;
  padding: 4px 6px;
  font-size: 11px;
}
.grid-3 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  max-width: 500px;
}
.grid-4 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  max-width: 600px;
}
.grid-2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
  margin-top: 10px;
}
.muted {
  color: #64748b;
  font-size: 11px;
  line-height: 1.4;
}
.badge-final {
  font-size: 10px;
  color: #dc2626;
  font-weight: 600;
  background: #fef2f2;
  padding: 3px 8px;
  border-radius: 4px;
  display: inline-block;
  margin-top: 4px;
}
.alert-rojo {
  color: #dc2626;
  text-decoration: underline;
  font-weight: 600;
}
.divider {
  margin-top: 14px;
  margin-bottom: 14px;
  border: none;
  border-top: 1px solid #e2e8f0;
}
.tabs {
  display: flex;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 12px;
  gap: 4px;
}
.tab {
  padding: 8px 14px;
  font-size: 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 6px 6px 0 0;
  color: #64748b;
  font-weight: 500;
  transition: all 0.2s;
}
.tab:hover {
  background: #f8fafc;
  color: #334155;
}
.tab.active {
  border-bottom: 2px solid #ff6b35;
  font-weight: 600;
  color: #ff6b35;
  background: #fff7ed;
}
.comentario-label {
  color: #dc2626;
  font-weight: 700;
  text-decoration: underline;
  font-size: 11px;
  margin: 4px 0;
}
.denoms-box {
  max-width: 350px;
  margin-top: 6px;
  background: #fafbfc;
  border-radius: 8px;
  padding: 10px;
  border: 1px solid #e2e8f0;
}
 
.table-denoms th,
.table-denoms td {
  font-size: 11px;
  padding: 4px 6px;
}
 
.table-denoms td:first-child {
  text-align: right;
  font-weight: 500;
}
 
.table-small {
  font-size: 11px;
}
.table-small th,
.table-small td {
  padding: 5px 8px;
}
.chip-borrador {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 999px;
  background: #fef2f2;
  color: #dc2626;
  font-size: 10px;
  font-weight: 500;
}
`;
document.head.appendChild(styleEl);
 
/* ====================== EMPLEADO (Form & View) ====================== */
function EmpleadoForm({ empleado, sedes, usuarios = [], onSave, onCancel, readOnly = false, valoresAnuales = {} }) {
  // ====== ESTADO BASE DEL EMPLEADO ======
  
  // Garantizar que sedes es siempre un array
  const sedesArray = Array.isArray(sedes) ? sedes : [];
  
  // Obtener el nombre de la primera sede
  const primeraSedeNombre = sedesArray.length > 0 
    ? (typeof sedesArray[0] === 'string' ? sedesArray[0] : (sedesArray[0]?.nombre || "")) 
    : "";
  
  // Usar el SMLV de valoresAnuales como salario base por defecto
  const smlvActual = valoresAnuales?.smlv || 1750905; // Valor por defecto actualizado
  
  console.log("🔍 EmpleadoForm recibió sedes:", sedes, "Convertido a array:", sedesArray, "Primera sede:", primeraSedeNombre);
  console.log("💰 EmpleadoForm usando SMLV:", smlvActual);
  
  // Valores por defecto
  const defaultValues = {
    id: uid(),
    activo: true,
    nombre: "",
    nombres: "",
    apellidos: "",
    tipoDoc: "Cédula de ciudadanía",
    documento: "",
    telefono: "",
    direccion: "",
    rh: "",
    fechaExpedicion: "",
    fechaIngreso: "",
    fechaNacimiento: "",
    cargo: "",
    sede: primeraSedeNombre,
    tipoContrato: "Indefinido",
    eps: "",
    arl: "",
    fondo: "",
    nacionalidad: "Colombiana",
    genero: "Masculino",
    banco: "",
    tipoCuenta: "Ahorros",
    numeroCuenta: "",
    hijos: [],
    tieneHijos: false,
    fechaRetiro: "",
    docs: [],
    salarioBase: smlvActual, // Usar SMLV dinámico como salario base por defecto
    salarioProm6m: 2000000,
    duracionContrato: "",
    valorBonificacion: 100000,
    perfil: "",
  };

  // Fusionar empleado con valores por defecto
  const empleadoInicial = empleado 
    ? {
        ...defaultValues,
        ...empleado,
        // Asegurar que tipoDoc y sede no sean vacíos
        tipoDoc: empleado.tipoDoc || "Cédula de ciudadanía",
        // Use la sede del empleado, o la primera disponible si no existe
        sede: empleado.sede || primeraSedeNombre,
      }
    : {
        ...defaultValues,
        // Para nuevo empleado, usar la primera sede disponible
        sede: primeraSedeNombre,
      };

  const [e, setE] = useState(empleadoInicial);
  const [folderId] = useState(() => empleado?.id || empleado?.documento || uid());
  const [cargandoDocs, setCargandoDocs] = useState(false);

  const dis = readOnly ? { disabled: true } : {};
  const [verDocs, setVerDocs] = useState(false);
  const [perfilAutomatico, setPerfilAutomatico] = useState("");

  // Lista de sedes que garantiza incluir la sede del empleado aunque no esté en catálogo
  const sedesOpciones = useMemo(() => {
    const lista = [];
    const pushIf = (valor) => {
      if (!valor) return;
      if (!lista.includes(valor)) lista.push(valor);
    };
    pushIf(e.sede);
    (sedesArray || []).forEach((s) => {
      if (typeof s === 'string') {
        pushIf(s);
      } else if (s && s.nombre) {
        pushIf(s.nombre);
      }
    });
    return lista;
  }, [sedesArray, e.sede]);

  function setCampo(campo, valor) {
    setE((prev) => ({ ...prev, [campo]: valor }));
  }

  function calcularEdad(fecha) {
    if (!fecha) return "";
    const [y, m, d] = fecha.split("-").map(Number);
    if (!y || !m || !d) return "";
    const hoy = new Date();
    const nac = new Date(y, m - 1, d);
    let edad = hoy.getFullYear() - nac.getFullYear();
    const mes = hoy.getMonth() - nac.getMonth();
    if (mes < 0 || (mes === 0 && hoy.getDate() < nac.getDate())) {
      edad--;
    }
    if (edad < 0 || isNaN(edad)) return "";
    return String(edad);
  }

  // Cargar el perfil directamente desde la tabla de usuarios (Configuraciones → Usuarios)
  React.useEffect(() => {
    if (perfilAutomatico) return; // ya mostrado

    let cancelado = false;

    const nombrePerfilDesdeId = async (perfilId) => {
      if (!perfilId) return "";
      try {
        const raw = window.localStorage.getItem("perfiles_app_v1");
        if (raw) {
          const perfilesLs = JSON.parse(raw);
          const encontrado = perfilesLs.find((p) => p.id === perfilId);
          if (encontrado?.nombre) return encontrado.nombre;
        }
      } catch (err) {
        console.warn("No se pudo leer perfiles_app_v1", err);
      }
      try {
        const { data, error } = await supabase
          .from("perfiles")
          .select("nombre")
          .eq("id", perfilId)
          .maybeSingle();
        if (!error && data?.nombre) return data.nombre;
      } catch (err) {
        console.warn("No se pudo consultar perfil en Supabase", err);
      }
      return perfilId;
    };

    const resolverPerfil = async () => {
      const doc = (e.documento || "").toString().trim();
      const nombreEmp = (e.nombre || "").toString().trim();

      const normalizar = (v = "") => v.toString().trim().toLowerCase();

      const usuarioAsociado =
        usuarios.find((u) => normalizar(u.usuario) === normalizar(doc)) ||
        usuarios.find((u) => normalizar(u.nombre) === normalizar(nombreEmp));

      let perfilId = usuarioAsociado?.perfil;

      // Si no se encontró en estado local, intentar traerlo directo desde Supabase
      if (!perfilId && (doc || nombreEmp)) {
        try {
          const filtros = [];
          if (doc) filtros.push(`usuario.eq.${doc}`);
          if (nombreEmp) filtros.push(`nombre.eq.${nombreEmp}`);
          const { data, error } = await supabase
            .from("usuarios")
            .select("perfil")
            .or(filtros.join(","))
            .maybeSingle();
          if (!error && data?.perfil) {
            perfilId = data.perfil;
          }
        } catch (err) {
          console.warn("No se pudo consultar usuario en Supabase", err);
        }
      }

      // Fallback: si el empleado ya trae un perfil guardado, úsalo
      if (!perfilId && e.perfil) {
        perfilId = e.perfil;
      }

      if (!perfilId) return;

      const nombre = await nombrePerfilDesdeId(perfilId);
      if (cancelado) return;
      setPerfilAutomatico(nombre);
      setCampo("perfil", perfilId);
    };

    resolverPerfil();
    return () => {
      cancelado = true;
    };
  }, [usuarios, e.documento, e.nombre, e.perfil, perfilAutomatico]);

  // Efecto para actualizar la edad cuando cambia la fecha de nacimiento
  React.useEffect(() => {
    if (e.fechaNacimiento) {
      const edad = calcularEdad(e.fechaNacimiento);
      if (edad && e.edad !== edad) {
        setCampo("edad", edad);
      }
    }
  }, [e.fechaNacimiento]);
 
  // nombres / apellidos UI
  const [nombresUI, setNombresUI] = useState(
    empleado?.nombres ||
      (empleado?.nombre || "").split(" ").slice(0, -1).join(" ")
  );
  const [apellidosUI, setApellidosUI] = useState(
    empleado?.apellidos ||
      (empleado?.nombre || "").split(" ").slice(-1).join(" ")
  );
 
  function handleSubmit(ev) {
    try {
      ev.preventDefault();
      console.log("📤 Enviando formulario...", "e:", e);
      console.log("   - nombresUI actual:", nombresUI);
      console.log("   - apellidosUI actual:", apellidosUI);
      
      const nombreCompleto = `${(nombresUI || "").trim()} ${(apellidosUI || "").trim()}`.trim();
      const payload = {
        ...e,
        nombres: nombresUI || "",
        apellidos: apellidosUI || "",
        nombre: nombreCompleto || e.nombre,
      };
      
      console.log("📦 Payload a guardar:", payload);
      console.log("   - payload.nombres:", payload.nombres);
      console.log("   - payload.apellidos:", payload.apellidos);
      onSave && onSave(payload);
    } catch (error) {
      console.error("❌ Error en handleSubmit:", error);
      alert("Error al guardar: " + error.message);
    }
  }
 
  // ====== DOCUMENTOS ======
  function slugify(texto) {
    if (!texto) return "documento";
    let value = texto.toString();
    if (typeof value.normalize === "function") {
      value = value.normalize("NFD");
    }
    return value
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase() || "documento";
  }

  async function onDocs(ev) {
    const input = ev.target;
    const files = Array.from(input.files || []);
    if (!files.length) return;

    setCargandoDocs(true);

    try {
      const nuevosDocs = [];
      const folderSafe = slugify(folderId || "empleado");

      for (const file of files) {
        const base = file.name.replace(/\.[^.]+$/, "");
        const nombrePrompt = typeof window !== "undefined"
          ? window.prompt(
              "Nombre del documento (como quieres que se vea en el perfil):",
              base
            )
          : base;

        if (nombrePrompt === null) {
          continue;
        }

        const nombreFinal = (nombrePrompt || base || "Documento").trim();
        if (!nombreFinal) {
          continue;
        }

        const extension = file.name.includes(".")
          ? file.name.substring(file.name.lastIndexOf("."))
          : "";
        const safeFileName = `${Date.now()}_${uid()}_${slugify(nombreFinal)}${extension}`;
        const path = `${folderSafe}/${safeFileName}`;

        const { error: uploadError } = await supabase.storage
          .from(SUPABASE_BUCKET_DOCS)
          .upload(path, file, {
            cacheControl: "3600",
            upsert: false,
            contentType: file.type || undefined,
          });

        if (uploadError) {
          throw new Error(uploadError.message || "Error al subir el archivo");
        }

        const { data: publicData, error: publicError } = supabase.storage
          .from(SUPABASE_BUCKET_DOCS)
          .getPublicUrl(path);

        if (publicError) {
          throw new Error(publicError.message || "No se pudo obtener la URL pública");
        }

        const publicUrl = publicData?.publicUrl;
        if (!publicUrl) {
          throw new Error("No se recibió la URL pública del documento.");
        }

        nuevosDocs.push({
          id: uid(),
          nombre: nombreFinal,
          size: file.size,
          type: file.type,
          fecha: toISO(new Date()),
          url: publicUrl,
          bucket: SUPABASE_BUCKET_DOCS,
          path,
        });
      }

      if (nuevosDocs.length) {
        setE((prev) => ({
          ...prev,
          docs: [...(prev.docs || []), ...nuevosDocs],
        }));
      }
    } catch (error) {
      console.error("Error subiendo documentos:", error);
      if (typeof window !== "undefined") {
        window.alert(
          error?.message
            ? `No se pudo subir el documento: ${error.message}`
            : "No se pudo subir el documento."
        );
      }
    } finally {
      setCargandoDocs(false);
      if (input) {
        input.value = "";
      }
    }
  }

  function verDocumento(doc) {
    let targetUrl = doc?.url;

    if (targetUrl && targetUrl.startsWith("blob:")) {
      targetUrl = null;
    }

    if (!targetUrl && doc?.bucket && doc?.path) {
      const { data, error } = supabase.storage
        .from(doc.bucket)
        .getPublicUrl(doc.path);

      if (!error) {
        targetUrl = data?.publicUrl || null;
      }
    }

    if (!targetUrl) {
      alert("Este registro no tiene un archivo disponible para visualizar.");
      return;
    }

    window.open(targetUrl, "_blank", "noopener,noreferrer");
  }
 
  function imprimirListadoDocs() {
    if (!e.docs || !e.docs.length) {
      alert("Este empleado no tiene documentos registrados.");
      return;
    }
 
    const win = window.open("", "_blank");
    if (!win) return;
 
    const filas = e.docs
      .map(
        (d) => `
        <tr>
          <td style='padding:4px;border:1px solid #ddd;'>${d.nombre}</td>
          <td style='padding:4px;border:1px solid #ddd;'>${Math.round(
            d.size / 1024
          )} kb</td>
          <td style='padding:4px;border:1px solid #ddd;'>${d.fecha || ""}</td>
        </tr>`
      )
      .join("");
 
    win.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charSet='utf-8' />
          <title>Documentos del empleado</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 12px; padding: 16px; }
            table { border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 4px; }
            th { background:#f3f3f3; }
          </style>
        </head>
        <body>
          <h3>Documentos del empleado</h3>
          <p>Empleado: <strong>${e.nombre || ""}</strong> – Doc: ${
      e.tipoDoc || ""
    } ${e.documento || ""}</p>
          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Tamaño</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>${filas}</tbody>
          </table>
          <script>window.print()</script>
        </body>
      </html>
    `);
    win.document.close();
  }
 
  const edadCalculada = calcularEdad(e.fechaNacimiento);
 
  // helpers grid por estilos (no Tailwind)
  const grid2 = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "6px 12px",
    marginBottom: 6,
  };
  const grid3 = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "6px 12px",
    marginBottom: 6,
  };
 
  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 rounded-lg"
      style={{
        background: "#f3f7fc",
        border: "1px solid #d1e3ff",
        padding: 12,
      }}
    >
      {/* CABECERA */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #1d4ed8 0%, #0ea5e9 60%, #22c55e 100%)",
          borderRadius: 8,
          padding: "6px 10px",
          marginBottom: 10,
          color: "white",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 className="text-sm font-semibold">Datos del empleado</h2>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                console.log("🔙 Botón Cerrar clickeado");
                onCancel();
              }}
              className="text-[11px] px-3 py-[4px] bg-white/10 border-white hover:bg-white/20"
            >
              Cerrar
            </Button>
            {!readOnly && (
              <Button
                type="submit"
                onClick={() => console.log("🔘 Botón Guardar clickeado")}
                className="text-[11px] px-3 py-[4px] bg-white text-blue-700 hover:bg-slate-100"
              >
                Guardar
              </Button>
            )}
          </div>
        </div>
      </div>
 
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* ===== DATOS PERSONALES ===== */}
        <div>
          <h3 className="text-xs font-semibold text-slate-700 mb-2">
            Datos personales
          </h3>
 
          {/* Fila: Tipo doc / Número doc */}
          <div style={grid2}>
            <Field label="Tipo documento">
              <select
                {...dis}
                style={{ width: "100%", fontSize: 12, padding: "4px", borderRadius: 4, border: "1px solid #cbd5e1" }}
                value={e.tipoDoc}
                onChange={(ev) => setCampo("tipoDoc", ev.target.value)}
              >
                <option>Cédula de ciudadanía</option>
                <option>Cédula de extranjería</option>
                <option>Tarjeta de identidad</option>
                <option>Registro civil</option>
                <option>Permiso de protección temporal PPT</option>
                <option>Pasaporte</option>
                <option>Cédula venezolana</option>
              </select>
            </Field>
 
            <Field label="Número de documento">
              <Input
                {...dis}
                value={e.documento}
                onChange={(ev) => setCampo("documento", ev.target.value)}
              />
            </Field>
          </div>
 
          {/* Fila: Nombres / Apellidos */}
          <div style={grid2}>
            <Field label="Nombres">
              <Input
                {...dis}
                value={nombresUI || ""}
                onChange={(ev) => {
                  console.log("✍️ Cambio en Nombres:", ev.target.value);
                  setNombresUI(ev.target.value);
                }}
              />
            </Field>
            <Field label="Apellidos">
              <Input
                {...dis}
                value={apellidosUI || ""}
                onChange={(ev) => {
                  console.log("✍️ Cambio en Apellidos:", ev.target.value);
                  setApellidosUI(ev.target.value);
                }}
              />
            </Field>
          </div>
 
          {/* Fila: Fecha nacimiento / Fecha expedición / Perfil */}
          <div style={grid3}>
            <Field label="Fecha de nacimiento">
              <Input
                {...dis}
                type="date"
                value={e.fechaNacimiento || ""}
                onChange={(ev) =>
                  setCampo("fechaNacimiento", ev.target.value)
                }
              />
            </Field>
 
            <Field label="Fecha expedición documento">
              <Input
                {...dis}
                type="date"
                value={e.fechaExpedicion || ""}
                onChange={(ev) =>
                  setCampo("fechaExpedicion", ev.target.value)
                }
              />
            </Field>

            <Field label="Perfil (desde Configuraciones)">
              <div style={{
                padding: "4px 6px",
                borderRadius: 4,
                border: "1px solid #cbd5e1",
                backgroundColor: "#f1f5f9",
                fontSize: 12,
                color: perfilAutomatico ? "#0ea5e9" : "#94a3b8",
                fontWeight: perfilAutomatico ? "500" : "normal"
              }}>
                {perfilAutomatico ? (
                  <span>{perfilAutomatico}</span>
                ) : (
                  <span style={{ fontStyle: "italic" }}>Sin perfil asignado</span>
                )}
              </div>
            </Field>
          </div>
 
          {/* Fila: RH / Edad / Nacionalidad / Género */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.5fr 0.8fr", gap: "6px 12px", marginBottom: 6 }}>
            <Field label="RH">
              <Input
                {...dis}
                value={e.rh || ""}
                onChange={(ev) => setCampo("rh", ev.target.value)}
              />
            </Field>
 
            <Field label="Edad (automática)">
              <Input
                disabled
                value={edadCalculada}
                className="bg-slate-100"
              />
            </Field>
 
            <Field label="Nacionalidad">
              <Input
                {...dis}
                value={e.nacionalidad || ""}
                onChange={(ev) =>
                  setCampo("nacionalidad", ev.target.value)
                }
              />
            </Field>

            <Field label="Género">
              <select
                {...dis}
                value={e.genero || "Masculino"}
                onChange={(ev) => setCampo("genero", ev.target.value)}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px' }}
              >
                <option value="Masculino">Masculino</option>
                <option value="Femenino">Femenino</option>
              </select>
            </Field>
          </div>
        </div>
 
        {/* ===== UBICACIÓN ===== */}
        <div>
          <h3 className="text-xs font-semibold text-slate-700 mb-2">
            Ubicación
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "0.25fr 1.15fr", gap: "6px 12px", marginBottom: 6 }}>
            <Field label="Teléfono">
              <Input
                {...dis}
                type="tel"
                value={e.telefono || ""}
                onChange={(ev) => setCampo("telefono", ev.target.value)}
              />
            </Field>
            <Field label="Dirección">
              <Input
                {...dis}
                style={{ minWidth: "350px" }}
                value={e.direccion || ""}
                onChange={(ev) => setCampo("direccion", ev.target.value)}
              />
            </Field>
          </div>
        </div>
 
        {/* ===== DATOS LABORALES ===== */}
        <div>
          <h3 className="text-xs font-semibold text-slate-700 mb-2">
            Datos laborales
          </h3>
          <div style={grid3}>
            <Field label="Fecha de ingreso">
              <Input
                {...dis}
                type="date"
                value={normalizarFechaISO(e.fechaIngreso) || ""}
                onChange={(ev) => setCampo("fechaIngreso", ev.target.value)}
              />
            </Field>
 
            <Field label="Tipo de contrato">
              <select
                {...dis}
                style={{ width: "100%", fontSize: 12, padding: "4px", borderRadius: 4, border: "1px solid #cbd5e1" }}
                value={e.tipoContrato || "Indefinido"}
                onChange={(ev) => setCampo("tipoContrato", ev.target.value)}
              >
                <option>Indefinido</option>
                <option>Fijo</option>
                <option>Obra o labor</option>
                <option>Aprendizaje</option>
              </select>
            </Field>
 
            <Field label="Sede">
              <select
                {...dis}
                style={{ width: "100%", fontSize: 12, padding: "4px", borderRadius: 4, border: "1px solid #cbd5e1" }}
                value={e.sede || ""}
                onChange={(ev) => setCampo("sede", ev.target.value)}
              >
                {sedesOpciones.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div style={grid2}>
            <Field label="Cargo">
              <Input
                {...dis}
                value={e.cargo || ""}
                onChange={(ev) => setCampo("cargo", ev.target.value)}
              />
            </Field>

            <Field label="Fecha de retiro">
              <Input
                {...dis}
                type="date"
                value={e.fechaRetiro || ""}
                onChange={(ev) => setCampo("fechaRetiro", ev.target.value)}
              />
            </Field>
          </div>
        </div>
 
        {/* ===== SEGURIDAD SOCIAL Y PAGOS ===== */}
        <div>
          <h3 className="text-xs font-semibold text-slate-700 mb-2">
            Seguridad social y pagos
          </h3>
          <div style={grid3}>
            <Field label="EPS">
              <Input
                {...dis}
                value={e.eps || ""}
                onChange={(ev) => setCampo("eps", ev.target.value)}
              />
            </Field>
            <Field label="ARL">
              <Input
                {...dis}
                value={e.arl || ""}
                onChange={(ev) => setCampo("arl", ev.target.value)}
              />
            </Field>
            <Field label="Fondo pensiones / cesantías">
              <Input
                {...dis}
                value={e.fondo || ""}
                onChange={(ev) => setCampo("fondo", ev.target.value)}
              />
            </Field>
          </div>
 
          <div style={grid3}>
            <Field label="Banco">
              <Input
                {...dis}
                value={e.banco || ""}
                onChange={(ev) => setCampo("banco", ev.target.value)}
              />
            </Field>
            <Field label="Tipo de cuenta">
              <select
                {...dis}
                style={{ width: "100%", fontSize: 12, padding: "4px", borderRadius: 4, border: "1px solid #cbd5e1" }}
                value={e.tipoCuenta || "Ahorros"}
                onChange={(ev) => setCampo("tipoCuenta", ev.target.value)}
              >
                <option>Ahorros</option>
                <option>Corriente</option>
              </select>
            </Field>
            <Field label="Número de cuenta">
              <Input
                {...dis}
                value={e.numeroCuenta || ""}
                onChange={(ev) =>
                  setCampo("numeroCuenta", ev.target.value)
                }
              />
            </Field>
          </div>
        </div>
 
        {/* ===== INFORMACIÓN SALARIAL ===== */}
        <div>
          <h3 className="text-xs font-semibold text-slate-700 mb-2">
            Información salarial
          </h3>
          <div style={grid3}>
            <Field label="Salario base">
              <Input
                {...dis}
                type="number"
                value={e.salarioBase || ""}
                onChange={(ev) =>
                  setCampo("salarioBase", Number(ev.target.value || 0))
                }
              />
            </Field>
            <Field label="Promedio últimos 6 meses">
              <Input
                {...dis}
                type="number"
                value={e.salarioProm6m || ""}
                onChange={(ev) =>
                  setCampo("salarioProm6m", Number(ev.target.value || 0))
                }
              />
            </Field>
            <Field label="Bonificación (si aplica)">
              <Input
                {...dis}
                type="number"
                value={e.valorBonificacion || ""}
                onChange={(ev) =>
                  setCampo("valorBonificacion", Number(ev.target.value || 0))
                }
              />
            </Field>
            <Field label="Afiliado a Seguridad Social">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                <input
                  {...dis}
                  type="checkbox"
                  checked={e.afiliadoSeguridadSocial !== false}
                  onChange={(ev) => setCampo("afiliadoSeguridadSocial", ev.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '11px', color: '#666' }}>
                  {e.afiliadoSeguridadSocial !== false ? '✅ Sí (descuenta salud, pensión y suma aux. transporte)' : '❌ No (sin descuentos de salud/pensión ni aux. transporte)'}
                </span>
              </div>
            </Field>
          </div>
        </div>
 
        {/* ===== DOCUMENTOS ===== */}
        <div style={{
          marginTop: '24px',
          background: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #2196F3 0%, #42A5F5 100%)',
            padding: '14px 18px',
            borderBottom: '2px solid #1976D2'
          }}>
            <h3 style={{ color: 'white', margin: 0, fontSize: '15px', fontWeight: '600' }}>
              📄 Documentos del Empleado
            </h3>
          </div>
          <div style={{ padding: '14px 18px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '14px'
            }}>
              <span style={{ fontSize: '13px', color: '#666' }}>
                Adjunta y consulta los documentos del perfil.
              </span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => setVerDocs((v) => !v)}
                  style={{
                    background: '#2196F3',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#1976D2'}
                  onMouseOut={(e) => e.target.style.background = '#2196F3'}
                >
                  {verDocs ? '✕ Ocultar' : '✚ Subir documentos'}
                </button>
              )}
            </div>

            {verDocs && !readOnly && (
              <div style={{
                background: '#F5F5F5',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '14px',
                border: '2px dashed #BBDEFB'
              }}>
                <input
                  type="file"
                  multiple
                  onChange={onDocs}
                  disabled={cargandoDocs}
                  style={{
                    display: 'block',
                    width: '100%',
                    marginBottom: '8px',
                    fontSize: '12px'
                  }}
                />
                <div style={{ fontSize: '12px', color: '#1976D2', fontStyle: 'italic' }}>
                  💡 Al adjuntar un archivo se te pedirá el nombre con el que quedará guardado.
                </div>
                {cargandoDocs && (
                  <div style={{ fontSize: '12px', color: '#2196F3', marginTop: '8px', fontWeight: '500' }}>
                    ⏳ Subiendo documentos, espera un momento...
                  </div>
                )}
              </div>
            )}

            {e.docs?.length > 0 ? (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#F5F5F5' }}>
                        <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '2px solid #eee' }}>Nombre</th>
                        <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '2px solid #eee' }}>Tamaño</th>
                        <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '2px solid #eee' }}>Fecha</th>
                        <th style={{ padding: '12px 14px', textAlign: 'center', fontWeight: '600', color: '#424242', borderBottom: '2px solid #eee' }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {e.docs.map((d, idx) => (
                        <tr key={d.id} style={{ background: idx % 2 === 0 ? 'white' : '#FAFAFA' }}>
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #eee', fontWeight: '500' }}>
                            {d.nombre}
                          </td>
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #eee', color: '#666' }}>
                            {Math.round(d.size / 1024)} KB
                          </td>
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #eee', color: '#666' }}>
                            {d.fecha || "—"}
                          </td>
                          <td style={{ padding: '10px 14px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={() => verDocumento(d)}
                              style={{
                                background: '#E3F2FD',
                                color: '#1976D2',
                                border: '1px solid #BBDEFB',
                                padding: '6px 10px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseOver={(e) => {
                                e.target.style.background = '#2196F3';
                                e.target.style.color = 'white';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.background = '#E3F2FD';
                                e.target.style.color = '#1976D2';
                              }}
                            >
                              👁️ Ver
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {!readOnly && (
                  <button
                    type="button"
                    onClick={imprimirListadoDocs}
                    style={{
                      marginTop: '14px',
                      background: 'white',
                      color: '#2196F3',
                      border: '1px solid #2196F3',
                      padding: '8px 14px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = '#E3F2FD';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = 'white';
                    }}
                  >
                    🖨️ Imprimir listado
                  </button>
                )}
              </>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '30px',
                color: '#9E9E9E',
                fontSize: '14px'
              }}>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>📭</div>
                No hay documentos registrados para este empleado.
              </div>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}
 
/* Vista de solo lectura con historial de ausencias */
function EmpleadoView({ empleado, sedes, usuarios = [], onClose, ausenciasAll = [], vacacionesAll = [], memorandosAll = [] }) {
  const ausencias = (ausenciasAll || []).filter((a) => a.empleadoId === empleado.id);
  const vacaciones = (vacacionesAll || []).filter((v) => v.empleadoId === empleado.id);
  const memorandos = (memorandosAll || []).filter((m) => m.empleadoId === empleado.id);
  const cantidadMemorandos = memorandos.length;
 
  return (
    <div>
      <EmpleadoForm
        empleado={empleado}
        sedes={sedes}
        usuarios={usuarios}
        onSave={() => {}}
        onCancel={onClose}
        readOnly
      />

      {/* Historial de Memorandos/Procesos Disciplinarios */}
      {cantidadMemorandos > 0 && (
      <div style={{
        marginTop: '14px',
        background: 'white',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #e0e0e0'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
          padding: '10px 14px',
          borderBottom: '2px solid #b91c1c'
        }}>
          <h3 style={{ color: 'white', margin: 0, fontSize: '14px', fontWeight: '600' }}>
            ⚖️ Procesos Disciplinarios ({cantidadMemorandos})
          </h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Tipo</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>F. Envío Carta</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>F. Descargos</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>F. Decisión</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Decisión</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {memorandos.map((m, idx) => (
                <tr key={m.id} style={{ background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '3px 6px',
                      borderRadius: '8px',
                      backgroundColor: m.tipoProceso === "PROCESO_DISCIPLINARIO" ? '#fef3c7' : '#e0f2fe',
                      color: m.tipoProceso === "PROCESO_DISCIPLINARIO" ? '#d97706' : '#0369a1',
                      fontSize: '10px',
                      fontWeight: '600'
                    }}>
                      {m.tipoProceso === "PROCESO_DISCIPLINARIO" ? "⚖️ Proceso Disciplinario" : "📧 Memorando"}
                    </span>
                  </td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#666', fontWeight: '500' }}>
                    {m.fechaEnvio || "—"}
                  </td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#666' }}>
                    {m.tipoProceso === "PROCESO_DISCIPLINARIO" ? (m.fechaDescargos || "—") : "N/A"}
                  </td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#666' }}>
                    {m.tipoProceso === "PROCESO_DISCIPLINARIO" ? (m.fechaDecision || "—") : "N/A"}
                  </td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#666' }}>
                    {m.tipoProceso === "PROCESO_DISCIPLINARIO" ? (m.decision || "—") : "N/A"}
                  </td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee' }}>
                    <span style={{
                      padding: '3px 8px',
                      borderRadius: '10px',
                      fontSize: '10px',
                      fontWeight: '600',
                      background: m.cerrado 
                        ? 'linear-gradient(135deg, #43a047, #66bb6a)' 
                        : 'linear-gradient(135deg, #ff9800, #ffb74d)',
                      color: 'white'
                    }}>
                      {m.cerrado ? '✅ Cerrado' : '🔄 En curso'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
      
      {/* Historial de ausencias */}
      <div style={{
        marginTop: '16px',
        background: 'white',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #e0e0e0'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
          padding: '10px 14px',
          borderBottom: '2px solid #ff5722'
        }}>
          <h3 style={{ color: 'white', margin: 0, fontSize: '14px', fontWeight: '600' }}>
            📋 Historial de Ausencias
          </h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Tipo</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Inicio</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Fin</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Reintegro</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Periodo</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Días</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {ausencias.length ? (
                ausencias.map((a, idx) => (
                  <tr key={a.id} style={{ background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', fontWeight: '500' }}>{a.tipo}</td>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#666' }}>{a.inicio || "—"}</td>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#666' }}>{a.fin || "—"}</td>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#666' }}>{a.reintegro || "—"}</td>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#666' }}>{a.periodo || "—"}</td>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#666' }}>{a.dias ?? "—"}</td>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#666' }}>{a.motivo || "—"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: '#9e9e9e', fontSize: '13px' }}>
                    <div style={{ fontSize: '24px', marginBottom: '4px' }}>📭</div>
                    Sin registros de ausencias
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vacaciones */}
      <div style={{
        marginTop: '14px',
        background: 'white',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #e0e0e0'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #43a047 0%, #66bb6a 100%)',
          padding: '10px 14px',
          borderBottom: '2px solid #2e7d32'
        }}>
          <h3 style={{ color: 'white', margin: 0, fontSize: '14px', fontWeight: '600' }}>
            🏖️ Vacaciones Solicitadas
          </h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Inicio</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Fin</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Días</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Estado</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '1px solid #eee' }}>Aprobado por</th>
              </tr>
            </thead>
            <tbody>
              {vacaciones.length ? (
                vacaciones.map((v, idx) => (
                  <tr key={v.id} style={{ background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#666' }}>{v.inicio || "—"}</td>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#666' }}>{v.fin || "—"}</td>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#666' }}>{v.dias ?? "—"}</td>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee' }}>
                      <span style={{
                        padding: '3px 8px',
                        borderRadius: '10px',
                        fontSize: '10px',
                        fontWeight: '600',
                        background: v.estado === 'Aprobada' ? 'linear-gradient(135deg, #43a047, #66bb6a)' 
                          : v.estado === 'Rechazada' ? 'linear-gradient(135deg, #e53935, #ef5350)' 
                          : 'linear-gradient(135deg, #ff9800, #ffb74d)',
                        color: 'white'
                      }}>
                        {v.estado || "Pendiente"}
                      </span>
                    </td>
                    <td style={{ padding: '6px 10px', borderBottom: '1px solid #eee', color: '#666' }}>{v.aprobadoPor || "—"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '20px', color: '#9e9e9e', fontSize: '13px' }}>
                    <div style={{ fontSize: '24px', marginBottom: '4px' }}>🏝️</div>
                    Sin vacaciones solicitadas
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
 
/* ====================== MEMORANDOS ====================== */
function MemorandoForm({ memorando, empleados, onSubmit, onCancel }) {
  const [form, setForm] = useState(() => ({
    empleadoId: memorando?.empleadoId || "",
    tipoProceso: memorando?.tipoProceso || "MEMORANDO",
    descripcion: memorando?.descripcion || "",
    fechaEnvio: memorando?.fechaEnvio || "",
    fechaDescargos: memorando?.fechaDescargos || "",
    fechaDecision: memorando?.fechaDecision || "",
    decision: memorando?.decision || "",
  }));

  const bloqueado = Boolean(memorando?.cerrado);

  useEffect(() => {
    setForm({
      empleadoId: memorando?.empleadoId || "",
      tipoProceso: memorando?.tipoProceso || "MEMORANDO",
      descripcion: memorando?.descripcion || "",
      fechaEnvio: memorando?.fechaEnvio || "",
      fechaDescargos: memorando?.fechaDescargos || "",
      fechaDecision: memorando?.fechaDecision || "",
      decision: memorando?.decision || "",
    });
  }, [memorando]);

  function handleChange(field) {
    return (event) => {
      const value = event.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (!form.empleadoId) {
      alert("Selecciona un empleado");
      return;
    }
    onSubmit({
      ...memorando,
      empleadoId: form.empleadoId,
      tipoProceso: form.tipoProceso,
      descripcion: form.tipoProceso === "MEMORANDO" ? "" : form.descripcion,
      fechaEnvio: form.fechaEnvio,
      fechaDescargos: form.tipoProceso === "MEMORANDO" ? "" : form.fechaDescargos,
      fechaDecision: form.tipoProceso === "MEMORANDO" ? "" : form.fechaDecision,
      decision: form.tipoProceso === "MEMORANDO" ? "" : form.decision,
    });
  }

  const selectStyle = {
    width: '100%',
    padding: '8px 12px',
    fontSize: '13px',
    border: '2px solid #ff6b35',
    borderRadius: '8px',
    backgroundColor: bloqueado ? '#f3f4f6' : '#fffbf5',
    color: '#333',
    cursor: bloqueado ? 'not-allowed' : 'pointer',
    outline: 'none',
    transition: 'all 0.2s ease',
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    fontSize: '13px',
    border: '2px solid #ffc53d',
    borderRadius: '8px',
    backgroundColor: bloqueado ? '#f3f4f6' : '#fffbf5',
    color: '#333',
    outline: 'none',
    transition: 'all 0.2s ease',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: '#ff6b35',
    marginBottom: '4px',
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label style={labelStyle}>📋 Tipo de Proceso</label>
          <select
            value={form.tipoProceso}
            onChange={handleChange("tipoProceso")}
            style={selectStyle}
            disabled={bloqueado}
          >
            <option value="MEMORANDO">📧 Memorando</option>
            <option value="PROCESO_DISCIPLINARIO">⚖️ Proceso Disciplinario</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>👤 Empleado</label>
          <select
            value={form.empleadoId}
            onChange={handleChange("empleadoId")}
            style={selectStyle}
            disabled={bloqueado}
          >
            <option value="">-- Selecciona --</option>
            {[...(empleados || [])].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombre} – {e.tipoDoc} {e.documento}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>📧 Fecha envío carta</label>
          <input type="date" value={form.fechaEnvio} onChange={handleChange("fechaEnvio")} disabled={bloqueado} style={inputStyle} />
        </div>
        {form.tipoProceso === "PROCESO_DISCIPLINARIO" && (
          <>
            <div>
              <label style={labelStyle}>📝 Fecha descargos</label>
              <input type="date" value={form.fechaDescargos} onChange={handleChange("fechaDescargos")} disabled={bloqueado} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>⚖️ Fecha decisión</label>
              <input type="date" value={form.fechaDecision} onChange={handleChange("fechaDecision")} disabled={bloqueado} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>📋 Decisión / Resultado</label>
              <input
                value={form.decision}
                onChange={handleChange("decision")}
                placeholder="Amonestación / Archivo / Suspensión..."
                disabled={bloqueado}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>📄 Descripción / Motivo</label>
              <input
                value={form.descripcion}
                onChange={handleChange("descripcion")}
                placeholder="Incumplimiento horario..."
                disabled={bloqueado}
                style={inputStyle}
              />
            </div>
          </>
        )}
      </div>
      {bloqueado && (
        <div style={{
          fontSize: '12px',
          color: '#dc2626',
          backgroundColor: '#fef2f2',
          border: '2px solid #fecaca',
          borderRadius: '8px',
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          🔒 Este proceso está cerrado y no admite modificaciones.
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: '2px solid #ff6b35',
            borderRadius: '8px',
            backgroundColor: 'transparent',
            color: '#ff6b35',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={e => { e.target.style.backgroundColor = '#fff5f0'; }}
          onMouseLeave={e => { e.target.style.backgroundColor = 'transparent'; }}
        >
          ❌ Cancelar
        </button>
        <button
          type="submit"
          disabled={bloqueado}
          style={{
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '8px',
            background: bloqueado ? '#d1d5db' : 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
            color: 'white',
            cursor: bloqueado ? 'not-allowed' : 'pointer',
            boxShadow: bloqueado ? 'none' : '0 2px 8px rgba(82, 196, 26, 0.3)',
            transition: 'all 0.2s ease',
          }}
        >
          💾 Guardar cambios
        </button>
      </div>
    </form>
  );
}

function MemorandoAdjuntoForm({ memorando, onSubmit, onCancel }) {
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [archivo, setArchivo] = useState(null);
  const [archivoInfo, setArchivoInfo] = useState(null);
  const [subiendo, setSubiendo] = useState(false);

  function slugifyLocal(texto) {
    if (!texto) return "documento";
    let value = texto.toString();
    if (typeof value.normalize === "function") {
      value = value.normalize("NFD");
    }
    return (
      value
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase() || "documento"
    );
  }

  function handleFileChange(event) {
    const file = event.target.files?.[0];
    if (!file) {
      setArchivo(null);
      setArchivoInfo(null);
      return;
    }
    setArchivo(file);
    setArchivoInfo({ nombre: file.name, tipo: file.type, size: file.size });
    setNombre((prev) => (prev ? prev : file.name.replace(/\.[^.]+$/, "")));
  }

  async function subirArchivo(file, nombreDeseado) {
    const extension = file.name.includes(".") ? file.name.substring(file.name.lastIndexOf(".")) : "";
    const carpeta = slugifyLocal(memorando?.id || memorando?.empleadoId || "memorando");
    const safeFileName = `${Date.now()}_${uid()}_${slugifyLocal(nombreDeseado)}${extension}`;
    const path = `memorandos/${carpeta}/${safeFileName}`;

    const { error: uploadError } = await supabase.storage
      .from(SUPABASE_BUCKET_MEMORANDOS)
      .upload(path, file, {
        cacheControl: "3600",
        upsert: false,
        contentType: file.type || undefined,
      });

    if (uploadError) {
      throw new Error(uploadError.message || "No se pudo subir el archivo");
    }

    const { data: publicData, error: publicError } = supabase.storage
      .from(SUPABASE_BUCKET_MEMORANDOS)
      .getPublicUrl(path);

    if (publicError || !publicData?.publicUrl) {
      throw new Error(publicError?.message || "No se pudo obtener la URL pública");
    }

    return { url: publicData.publicUrl, path };
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!archivo) {
      alert("Selecciona un archivo para adjuntar");
      return;
    }

    try {
      setSubiendo(true);
      const nombreFinal = (nombre || archivoInfo?.nombre || "Documento").trim() || "Documento";
      const { url, path } = await subirArchivo(archivo, nombreFinal);

      const adjunto = {
        id: uid(),
        nombre: nombreFinal,
        url,
        descripcion: descripcion.trim() || undefined,
        tipo: archivoInfo?.tipo,
        size: archivoInfo?.size,
        creadoEn: toISO(new Date()),
        bucket: SUPABASE_BUCKET_MEMORANDOS,
        path,
      };

      onSubmit(adjunto);
    } catch (error) {
      console.error("Error adjuntando documento de memorando:", error);
      alert(error?.message || "No fue posible adjuntar el documento. Intenta nuevamente.");
    } finally {
      setSubiendo(false);
    }
  }

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    fontSize: '13px',
    border: '2px solid #ffc53d',
    borderRadius: '8px',
    backgroundColor: '#fffbf5',
    color: '#333',
    outline: 'none',
    transition: 'all 0.2s ease',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: '#ff6b35',
    marginBottom: '6px',
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div>
          <label style={labelStyle}>📎 Nombre del documento</label>
          <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Memorando firmado" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>📝 Descripción (opcional)</label>
          <input
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Firmado por el colaborador"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>📁 Seleccionar archivo</label>
          <input
            type="file"
            onChange={handleFileChange}
            style={{
              padding: '10px',
              fontSize: '13px',
              border: '2px dashed #ff6b35',
              borderRadius: '8px',
              backgroundColor: '#fffbf5',
              width: '100%',
              cursor: 'pointer',
            }}
          />
          {archivoInfo && (
            <p style={{ fontSize: '11px', color: '#666', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              📄 {archivoInfo.nombre} · {(archivoInfo.size / 1024).toFixed(1)} KB
            </p>
          )}
        </div>
      </div>
      {Array.isArray(memorando?.adjuntos) && memorando.adjuntos.length > 0 && (
        <div style={{
          border: '2px solid #ffc53d',
          borderRadius: '8px',
          padding: '12px',
          backgroundColor: '#fffbf5',
        }}>
          <p style={{ fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '8px' }}>📂 Adjuntos existentes</p>
          <ul style={{ fontSize: '12px', listStyle: 'none', padding: 0, margin: 0 }}>
            {memorando.adjuntos.map((adj) => (
              <li key={adj.id || adj.nombre} style={{ padding: '4px 0', borderBottom: '1px solid #ffe4c9' }}>
                📄 {adj.nombre || "Documento"}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: '2px solid #ff6b35',
            borderRadius: '8px',
            backgroundColor: 'transparent',
            color: '#ff6b35',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={e => { e.target.style.backgroundColor = '#fff5f0'; }}
          onMouseLeave={e => { e.target.style.backgroundColor = 'transparent'; }}
        >
          ❌ Cancelar
        </button>
        <button
          type="submit"
          disabled={subiendo}
          style={{
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '8px',
            background: subiendo ? '#d1d5db' : 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
            color: 'white',
            cursor: subiendo ? 'not-allowed' : 'pointer',
            boxShadow: subiendo ? 'none' : '0 2px 8px rgba(24, 144, 255, 0.3)',
            transition: 'all 0.2s ease',
          }}
        >
          {subiendo ? "⏳ Guardando..." : "📎 Adjuntar"}
        </button>
      </div>
    </form>
  );
}

function Memorandos({ empleados, memorandos, onCreate, onDelete, onUpdate, onAttach, tienePermiso = () => true }) {
  const [tipoProceso, setTipoProceso] = useState("MEMORANDO"); // "MEMORANDO" | "PROCESO_DISCIPLINARIO"
  const [empleadoId, setEmpleadoId] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [fechaEnvio, setFechaEnvio] = useState("");
  const [fechaDescargos, setFechaDescargos] = useState("");
  const [fechaDecision, setFechaDecision] = useState("");
  const [decision, setDecision] = useState("");
  const [editarMemorando, setEditarMemorando] = useState(null);
  const [memorandoAdjuntar, setMemorandoAdjuntar] = useState(null);
 
  const memConEmpleado = memorandos.map((m) => ({
    ...m,
    empleado: empleados.find((e) => e.id === m.empleadoId),
  }));

  function limpiarMemorandoPayload(base) {
    if (!base) return null;
    const { empleado, ...rest } = base;
    if (typeof rest.cerrado === "undefined") {
      rest.cerrado = false;
    }
    return { ...rest };
  }
 
  function guardar() {
    if (!empleadoId) return alert("Selecciona un empleado");
    onCreate({
      id: uid(),
      empleadoId,
      tipoProceso,
      descripcion: tipoProceso === "MEMORANDO" ? "" : descripcion,
      fechaEnvio,
      fechaDescargos: tipoProceso === "MEMORANDO" ? "" : fechaDescargos,
      fechaDecision: tipoProceso === "MEMORANDO" ? "" : fechaDecision,
      decision: tipoProceso === "MEMORANDO" ? "" : decision,
      creadoEn: toISO(new Date()),
      adjuntos: [],
      cerrado: false,
      cerradoEn: null,
    });
    setDescripcion("");
    setFechaEnvio("");
    setFechaDescargos("");
    setFechaDecision("");
    setDecision("");
  }

  function abrirEditor(m) {
    if (!m || m.cerrado) return;
    const origen = memorandos.find((base) => base.id === m.id);
    setEditarMemorando(origen || { ...m });
  }

  function abrirAdjuntos(m) {
    if (!m || m.cerrado) return;
    const origen = memorandos.find((base) => base.id === m.id);
    setMemorandoAdjuntar(origen || { ...m });
  }

  function manejarActualizacion(memorandoActualizado) {
    if (!memorandoActualizado) return;
    const limpio = limpiarMemorandoPayload(memorandoActualizado);
    onUpdate?.(limpio);
    setEditarMemorando(null);
  }

  function manejarAdjunto(nuevoAdjunto) {
    if (!memorandoAdjuntar || !nuevoAdjunto) return;
    if (memorandoAdjuntar.cerrado) return;
    onAttach?.(memorandoAdjuntar.id, nuevoAdjunto);
    setMemorandoAdjuntar(null);
  }

  function manejarCierre(m) {
    if (!m || m.cerrado) return;
    const payload = limpiarMemorandoPayload({ ...m, cerrado: true, cerradoEn: toISO(new Date()) });
    onUpdate?.(payload);
  }

  function manejarReapertura(m) {
    if (!m || !m.cerrado) return;
    const payload = limpiarMemorandoPayload({ ...m, cerrado: false, cerradoEn: null });
    onUpdate?.(payload);
  }
 
  const selectStyleMemo = {
    width: '100%',
    padding: '10px 14px',
    fontSize: '13px',
    border: '2px solid #ff6b35',
    borderRadius: '8px',
    backgroundColor: '#fffbf5',
    color: '#333',
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.2s ease',
  };

  const inputStyleMemo = {
    width: '100%',
    padding: '10px 14px',
    fontSize: '13px',
    border: '2px solid #ffc53d',
    borderRadius: '8px',
    backgroundColor: '#fffbf5',
    color: '#333',
    outline: 'none',
    transition: 'all 0.2s ease',
  };

  const labelStyleMemo = {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: '#ff6b35',
    marginBottom: '6px',
  };

  return (
    <div style={{ padding: '16px' }}>
      <h2 style={{
        fontSize: '18px',
        fontWeight: '700',
        color: '#ff6b35',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        borderBottom: '3px solid #ffc53d',
        paddingBottom: '10px'
      }}>⚖️ Procesos Disciplinarios</h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: tipoProceso === "MEMORANDO" ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)',
        gap: '16px',
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: '#fffbf5',
        borderRadius: '12px',
        border: '2px solid #ffe4c9'
      }}>
        <div>
          <label style={labelStyleMemo}>📋 Tipo de Proceso</label>
          <select
            value={tipoProceso}
            onChange={(e) => setTipoProceso(e.target.value)}
            style={selectStyleMemo}
          >
            <option value="MEMORANDO">📧 Memorando</option>
            <option value="PROCESO_DISCIPLINARIO">⚖️ Proceso Disciplinario</option>
          </select>
        </div>
        <div>
          <label style={labelStyleMemo}>👤 Empleado</label>
          <select
            value={empleadoId}
            onChange={(e) => setEmpleadoId(e.target.value)}
            style={selectStyleMemo}
          >
            <option value="">-- Selecciona --</option>
            {[...(empleados || [])].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombre} – {e.tipoDoc} {e.documento}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyleMemo}>📧 Fecha envío carta</label>
          <input
            type="date"
            value={fechaEnvio}
            onChange={(e) => setFechaEnvio(e.target.value)}
            style={inputStyleMemo}
          />
        </div>
        {tipoProceso === "PROCESO_DISCIPLINARIO" && (
          <>
            <div>
              <label style={labelStyleMemo}>📝 Fecha descargos</label>
              <input
                type="date"
                value={fechaDescargos}
                onChange={(e) => setFechaDescargos(e.target.value)}
                style={inputStyleMemo}
              />
            </div>
            <div>
              <label style={labelStyleMemo}>⚖️ Fecha decisión</label>
              <input
                type="date"
                value={fechaDecision}
                onChange={(e) => setFechaDecision(e.target.value)}
                style={inputStyleMemo}
              />
            </div>
            <div>
              <label style={labelStyleMemo}>📋 Decisión / Resultado</label>
              <input
                value={decision}
                onChange={(e) => setDecision(e.target.value)}
                placeholder="Amonestación / Archivo / Suspensión..."
                style={inputStyleMemo}
              />
            </div>
            <div>
              <label style={labelStyleMemo}>📄 Descripción / Motivo</label>
              <input
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Incumplimiento horario..."
                style={inputStyleMemo}
              />
            </div>
          </>
        )}
      </div>
      {tienePermiso("rh_memorandos_crear") && (
      <button
        onClick={guardar}
        style={{
          padding: '12px 24px',
          fontSize: '14px',
          fontWeight: '600',
          border: 'none',
          borderRadius: '8px',
          background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
          color: 'white',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
        onMouseEnter={e => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 4px 12px rgba(82, 196, 26, 0.4)'; }}
        onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 2px 8px rgba(82, 196, 26, 0.3)'; }}
      >💾 Guardar {tipoProceso === "MEMORANDO" ? "memorando" : "proceso disciplinario"}</button>
      )}
 
      <h3 style={{
        marginTop: '24px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#ff6b35',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>📑 Todos los procesos disciplinarios</h3>
      <table style={{
        width: '100%',
        fontSize: '12px',
        marginTop: '12px',
        borderCollapse: 'collapse',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(255, 107, 53, 0.15)'
      }}>
        <thead>
          <tr style={{ background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' }}>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>📋 Tipo</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>👤 Empleado</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>📄 Motivo</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>📧 F. envío</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>📝 F. descargos</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>⚖️ F. decisión</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>📋 Decisión</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>🔄 Estado</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>📎 Adjuntos</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>⚙️ Acciones</th>
          </tr>
        </thead>
        <tbody>
          {memConEmpleado.map((m, idx) => (
            <tr key={m.id} style={{ backgroundColor: idx % 2 === 0 ? '#fffbf5' : '#fff', borderBottom: '1px solid #ffe4c9' }}>
              <td style={{ padding: '10px 12px' }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  backgroundColor: m.tipoProceso === "PROCESO_DISCIPLINARIO" ? '#fef3c7' : '#e0f2fe',
                  color: m.tipoProceso === "PROCESO_DISCIPLINARIO" ? '#d97706' : '#0369a1',
                  fontSize: '10px',
                  fontWeight: '600'
                }}>
                  {m.tipoProceso === "PROCESO_DISCIPLINARIO" ? "⚖️ Proceso Disciplinario" : "📧 Memorando"}
                </span>
              </td>
              <td style={{ padding: '10px 12px' }}>
                {m.empleado
                  ? `${m.empleado.nombre} – ${m.empleado.tipoDoc} ${m.empleado.documento}`
                  : "—"}
              </td>
              <td style={{ padding: '10px 12px' }}>{m.descripcion}</td>
              <td style={{ padding: '10px 12px' }}>{m.fechaEnvio || "—"}</td>
              <td style={{ padding: '10px 12px' }}>{m.fechaDescargos || "—"}</td>
              <td style={{ padding: '10px 12px' }}>{m.fechaDecision || "—"}</td>
              <td style={{ padding: '10px 12px' }}>{m.decision || "—"}</td>
              <td style={{ padding: '10px 12px' }}>
                {m.cerrado ? (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    backgroundColor: '#d1fae5',
                    color: '#047857',
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    ✅ Cerrado {m.cerradoEn ? `(${m.cerradoEn})` : ""}
                  </span>
                ) : (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    backgroundColor: '#fef3c7',
                    color: '#d97706',
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    🔄 En curso
                  </span>
                )}
              </td>
              <td style={{ padding: '10px 12px' }}>
                {Array.isArray(m.adjuntos) && m.adjuntos.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {m.adjuntos.map((adj) => (
                      <a
                        key={adj.id}
                        href={adj.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1890ff', textDecoration: 'underline', fontSize: '11px' }}
                      >
                        📄 {adj.nombre || "Documento"}
                      </a>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: '#9ca3af', fontSize: '11px' }}>📂 Sin archivos</span>
                )}
              </td>
              <td style={{ padding: '10px 12px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {tienePermiso("rh_memorandos_editar") && (
                  <button
                    onClick={() => abrirEditor(m)}
                    disabled={m.cerrado}
                    style={{
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      border: '2px solid #1890ff',
                      borderRadius: '6px',
                      backgroundColor: m.cerrado ? '#f3f4f6' : 'transparent',
                      color: m.cerrado ? '#9ca3af' : '#1890ff',
                      cursor: m.cerrado ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    ✏️ Editar
                  </button>
                  )}
                  {tienePermiso("rh_memorandos_adjuntar") && (
                  <button
                    onClick={() => abrirAdjuntos(m)}
                    disabled={m.cerrado}
                    style={{
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      border: '2px solid #ff6b35',
                      borderRadius: '6px',
                      backgroundColor: m.cerrado ? '#f3f4f6' : 'transparent',
                      color: m.cerrado ? '#9ca3af' : '#ff6b35',
                      cursor: m.cerrado ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    📎 Adjuntar
                  </button>
                  )}
                  {!m.cerrado ? (
                    tienePermiso("rh_memorandos_completar") && (
                    <button
                      onClick={() => manejarCierre(m)}
                      style={{
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        border: 'none',
                        borderRadius: '6px',
                        background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      ✅ Completado
                    </button>
                    )
                  ) : (
                    tienePermiso("rh_memorandos_reabrir") && (
                    <button
                      onClick={() => manejarReapertura(m)}
                      style={{
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        border: '2px solid #ffc53d',
                        borderRadius: '6px',
                        backgroundColor: 'transparent',
                        color: '#d97706',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      🔓 Reabrir
                    </button>
                    )
                  )}
                  {tienePermiso("rh_memorandos_eliminar") && (
                  <button
                    onClick={() => onDelete(m.id)}
                    style={{
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      border: 'none',
                      borderRadius: '6px',
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    🗑️ Eliminar
                  </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {!memConEmpleado.length && (
            <tr>
              <td colSpan={10} style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
                📭 Sin procesos disciplinarios registrados
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {editarMemorando && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(255, 107, 53, 0.3)',
            width: '100%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: '3px solid #ffc53d'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#ff6b35',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderBottom: '2px solid #ffe4c9',
              paddingBottom: '10px'
            }}>✏️ Editar memorando</h3>
            <MemorandoForm
              memorando={editarMemorando}
              empleados={empleados}
              onCancel={() => setEditarMemorando(null)}
              onSubmit={manejarActualizacion}
            />
          </div>
        </div>
      )}

      {memorandoAdjuntar && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(255, 107, 53, 0.3)',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '85vh',
            overflowY: 'auto',
            border: '3px solid #ffc53d'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#ff6b35',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderBottom: '2px solid #ffe4c9',
              paddingBottom: '10px'
            }}>📎 Adjuntar documento</h3>
            <MemorandoAdjuntoForm
              memorando={memorandoAdjuntar}
              onCancel={() => setMemorandoAdjuntar(null)}
              onSubmit={manejarAdjunto}
            />
          </div>
        </div>
      )}
    </div>
  );
}
 
/* ====================== AUSENCIAS ====================== */
function Ausencias({ 
  empleados, 
  ausencias, 
  onCreate, 
  onDelete, 
  nominasGuardadas = [], 
  tienePermiso = () => true,
  // 🔹 Props para liquidación automática de vacaciones
  onGuardarLiquidacion = null,
  liquidacionesRepo = [],
  valoresAnuales = {}
}) {
  const [empleadoId, setEmpleadoId] = useState("");
  const [tipo, setTipo] = useState("Permiso");
  const [origen, setOrigen] = useState("Enfermedad general");
  const [periodoVac, setPeriodoVac] = useState("");
  const [inicio, setInicio] = useState("");
  const [fin, setFin] = useState("");
  const [motivo, setMotivo] = useState("");
  const [cobroPagado, setCobroPagado] = useState(false);
  const [cobroFecha, setCobroFecha] = useState("");
  const [diasCantidad, setDiasCantidad] = useState("");
  const [vacDetalle, setVacDetalle] = useState([]);  
  const [periodoAuto, setPeriodoAuto] = useState(""); // texto auto sugerido
  const [editandoId, setEditandoId] = useState(null); // Para editar ausencias
  const [motivoModificacion, setMotivoModificacion] = useState(""); // Motivo cuando se edita

  // === ESTADOS DE FILTROS ===
  const [filtroAnio, setFiltroAnio] = useState(new Date().getFullYear().toString());
  const [filtroMes, setFiltroMes] = useState("");
  const [filtroEmpleado, setFiltroEmpleado] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
 
  const holidaysCache = useMemo(() => ({}), []);

  React.useEffect(() => {
    const n = Number(diasCantidad || 0);
    if (!inicio || n <= 0) return;
    if (tipo === "Vacaciones") {
      const finCalc = addBusinessDaysVac(inicio, n, holidaysCache);
      setFin(finCalc);
    } else {
      setFin(addDaysLocal(inicio, n - 1));
    }
  }, [tipo, inicio, diasCantidad]); // eslint-disable-line
 
  const conEmpleado = ausencias.map((a) => ({
    ...a,
    empleado: empleados.find((e) => e.id === a.empleadoId),
  }));
 
  function seTraslapa(aInicio, aFin, bInicio, bFin) {
    if (!aInicio || !aFin || !bInicio || !bFin) return false;
    const a0 = new Date(aInicio);
    const a1 = new Date(aFin);
    const b0 = new Date(bInicio);
    const b1 = new Date(bFin);
    return a0 <= b1 && b0 <= a1;
  }
 
  // Sugerir período de vacaciones a consumir (FIFO por períodos pendientes)
  React.useEffect(() => {
    if (tipo !== "Vacaciones") { setVacDetalle([]); setPeriodoAuto(""); return; }
    const n = Number(diasCantidad || 0);
    if (!empleadoId || !n) { setVacDetalle([]); setPeriodoAuto(""); return; }
 
    const emp = empleados.find(e => e.id === empleadoId);
    if (!emp) return;
 
    const periods = buildVacationPeriods(emp);
    const vacsEmp = (ausencias || []).filter(a => a.empleadoId === emp.id && a.tipo === "Vacaciones");
    const buckets = applyVacationUses(periods, vacsEmp, holidaysCache);
    const { detalle, faltantes } = allocateVacationFromBuckets(buckets, n);
 
    setVacDetalle(detalle);
    const texto = detalle.length
      ? detalle.map(d => `${d.periodo}: ${d.dias} día(s)`).join(" + ")
      : "";
    setPeriodoAuto(texto + (faltantes>0 ? ` (¡faltan ${faltantes} día(s)!)` : ""));
  }, [tipo, empleadoId, diasCantidad, inicio]); // eslint-disable-line
 
  function calcularDias(inicioISO, finISO, esVac) {
    if (!inicioISO) return 0;
    if (!finISO) finISO = inicioISO;
    if (esVac) {
      // L–S sin festivos
      let cur = parseISO(inicioISO);
      const end = parseISO(finISO);
      let count = 0;
      while (cur <= end) {
        const iso = toISO(cur);
        if (isBusinessDayVac(iso, holidaysCache)) count++;
        cur.setDate(cur.getDate() + 1);
      }
      return count;
    } else {
      // Corridos (incluye domingos y festivos)
      const a = parseISO(inicioISO), b = parseISO(finISO);
      return Math.floor((b - a) / (24 * 3600 * 1000)) + 1;
    }
  }
 
  function guardar() {
    if (!empleadoId) return alert("Selecciona un empleado");
    if (!inicio) return alert("Indica fecha de inicio");
    
    // Validar motivo de modificación para vacaciones cuando se edita
    if (editandoId && tipo === "Vacaciones" && !motivoModificacion.trim()) {
      return alert("Debe ingresar el motivo de la modificación para ausencias de vacaciones");
    }
 
    let finDef = fin;
    const n = Number(diasCantidad || 0);
 
    if (!finDef && n > 0) {
      finDef =
        tipo === "Vacaciones"
          ? addBusinessDaysVac(inicio, n, holidaysCache)
          : addDaysLocal(inicio, n - 1);
    }
 
    // (4) No permitir novedades si el período está liquidado/guardado en nóminas
    const bloqueadoPorNomina = (nominasGuardadas || []).some((nom) => {
      const r = nom?.rango || {};
      return seTraslapa(inicio, finDef || inicio, r.inicio, r.fin);
    });
    if (bloqueadoPorNomina) {
      alert("No puedes guardar ausencias en un período de nómina ya liquidado.");
      return;
    }
 
    // Validación de solapes con otras ausencias del empleado
    // Si estamos editando (editandoId), excluir esa ausencia de la validación
    const tieneSolape = ausencias.some(
      (a) =>
        a.empleadoId === empleadoId &&
        a.id !== editandoId && // Excluir la ausencia que estamos editando
        seTraslapa(
          inicio,
          finDef || inicio,
          a.inicio || a.fin || a.inicio,
          a.fin || a.inicio
        )
    );
    if (tieneSolape) {
      alert(
        "Ya existe una ausencia/permiso/vacación que cruza las fechas indicadas."
      );
      return;
    }
 
    // Cálculo de días
    const dias = calcularDias(
      inicio,
      finDef || inicio,
      tipo === "Vacaciones"
    );
 
    // Si es vacaciones, forzar periodo con el auto (FIFO)
    let periodoFinal = periodoVac;
    let detalleVac = [];
    if (tipo === "Vacaciones") {
      detalleVac = [...(vacDetalle || [])];
 
      // Por si vacDetalle está vacío pero sí hay días
      if (!detalleVac.length && Number(diasCantidad || 0) > 0) {
        const emp = empleados.find((e) => e.id === empleadoId);
        if (emp) {
          const periods = buildVacationPeriods(emp);
          const vacsEmp = (ausencias || []).filter(
            (a) => a.empleadoId === emp.id && a.tipo === "Vacaciones"
          );
          const buckets = applyVacationUses(periods, vacsEmp, holidaysCache);
          const r = allocateVacationFromBuckets(
            buckets,
            Number(diasCantidad || 0)
          );
          detalleVac = r.detalle;
        }
      }
 
      periodoFinal = detalleVac.length
        ? detalleVac.map((d) => `${d.periodo}:${d.dias}`).join(" + ")
        : periodoVac || "";
    }
 
    // Guardar AUSENCIA
    const registroAusencia = {
      id: uid(),
      empleadoId,
      tipo,
      origen,
      inicio,
      fin: finDef,
      periodo: tipo === "Vacaciones" ? periodoFinal : "",
      detalleVac: tipo === "Vacaciones" ? detalleVac : [],
      motivo,
      cobroPagado: tipo === "Incapacidad" ? cobroPagado : false,
      cobroFecha:
        tipo === "Incapacidad" && cobroPagado ? cobroFecha : "",
      reintegro:
        tipo === "Vacaciones" && finDef
          ? nextBusinessDayVac(finDef, holidaysCache)
          : "",
      dias, // número de días
    };

    // Si está editando, actualizar; si no, crear
    if (editandoId) {
      registroAusencia.id = editandoId;
      const ausenciaOriginal = ausencias.find(a => a.id === editandoId);
      registroAusencia.creadoEn = ausenciaOriginal?.creadoEn;
      
      // Guardar historial de modificaciones
      if (motivoModificacion) {
        const historialAnterior = ausenciaOriginal?.historialModificaciones || [];
        registroAusencia.historialModificaciones = [
          ...historialAnterior,
          {
            fecha: new Date().toISOString(),
            motivo: motivoModificacion,
            cambios: {
              inicioAnterior: ausenciaOriginal?.inicio,
              finAnterior: ausenciaOriginal?.fin,
              diasAnterior: ausenciaOriginal?.dias,
              inicioNuevo: inicio,
              finNuevo: finDef,
              diasNuevo: dias
            }
          }
        ];
        registroAusencia.motivoModificacion = motivoModificacion;
        registroAusencia.fechaModificacion = new Date().toISOString();
      }
    }

    onCreate(registroAusencia);

    // 🔹 Si es vacaciones y existe la función de liquidación, crear liquidación automáticamente
    // Usamos setTimeout para evitar conflictos de callbacks asíncronos con Supabase
    if (tipo === "Vacaciones" && typeof onGuardarLiquidacion === "function" && !editandoId) {
      const emp = empleados.find(e => e.id === empleadoId);
      if (emp && dias > 0) {
        // Usar valores anuales dinámicos o valores por defecto
        const SMLV = valoresAnuales.smlv || 1423500;
        const diasPeriodo = 15; // días de vacaciones por año
        const valorDia = (SMLV / 30);
        const totalVacaciones = Math.round(valorDia * dias);
        
        // Crear registro de liquidación
        const liquidacionVac = {
          id: uid(),
          tipo: `Liquidación de Vacaciones ${periodoFinal || ''}`.trim(),
          empleadoId,
          empleadoNombre: emp.nombre,
          empleadoDoc: emp.documento,
          fecha: toISO(new Date()),
          fechaInicio: inicio,
          fechaFin: finDef,
          periodo: `${inicio} → ${finDef}`,
          periodoAfectado: periodoFinal || periodoAuto || '',
          diasTomados: dias,
          detalle: [
            {
              nombre: dias === diasPeriodo 
                ? `Vacaciones período completo (${dias} días)` 
                : `Vacaciones parciales (${dias} días)`,
              valor: totalVacaciones,
            },
          ],
          total: totalVacaciones,
          empleado: emp,
          baseLiquidacion: SMLV,
        };
        
        // Verificar que no exista ya una liquidación para este período
        const yaExisteLiquidacion = (liquidacionesRepo || []).some((liq) => {
          if (liq.empleadoId !== empleadoId) return false;
          const tipoLiq = (liq.tipo || '').toLowerCase();
          if (!tipoLiq.includes('vacaciones')) return false;
          if (!liq.fechaInicio || !liq.fechaFin || !inicio || !finDef) return false;
          
          const a1 = parseISO(liq.fechaInicio);
          const a2 = parseISO(liq.fechaFin);
          const b1 = parseISO(inicio);
          const b2 = parseISO(finDef);
          
          return a1 <= b2 && b1 <= a2;
        });
        
        if (!yaExisteLiquidacion) {
          // Guardar liquidación con delay para evitar conflicto de callbacks de Supabase
          setTimeout(() => {
            try {
              onGuardarLiquidacion(liquidacionVac);
              console.log("✅ Liquidación de vacaciones enviada a guardar");
            } catch (err) {
              console.error("❌ Error al guardar liquidación de vacaciones:", err);
            }
          }, 500); // 500ms de delay para permitir que la ausencia se guarde primero
          
          // Preguntar si quiere imprimir (después del delay inicial)
          setTimeout(() => {
            const quiereImprimir = window.confirm(
              `✅ Ausencia de vacaciones registrada correctamente.\n\n` +
              `Se ha generado automáticamente la liquidación de vacaciones:\n` +
              `• Empleado: ${emp.nombre}\n` +
              `• Días: ${dias}\n` +
              `• Total: ${new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(totalVacaciones)}\n\n` +
              `¿Desea imprimir la liquidación ahora?`
            );
            
            if (quiereImprimir) {
              imprimirLiquidacion(liquidacionVac);
            }
          }, 600);
        } else {
          console.log("⚠️ Ya existe una liquidación de vacaciones para este período, no se crea duplicado");
        }
      }
    }

    // Limpiar formulario
    limpiarFormulario();
  }

  function limpiarFormulario() {
    setMotivo("");
    setInicio("");
    setFin("");
    setCobroPagado(false);
    setCobroFecha("");
    setPeriodoVac("");
    setDiasCantidad("");
    setVacDetalle([]);
    setPeriodoAuto("");
    setEmpleadoId("");
    setTipo("Permiso");
    setOrigen("Enfermedad general");
    setEditandoId(null);
    setMotivoModificacion("");
  }

  // 🔹 Función para imprimir liquidación de vacaciones (mismo formato que el botón Liquidaciones)
  function imprimirLiquidacion(liquidacion) {
    const e = liquidacion.empleado || {};
    const emp = {
      nombre: e?.nombre || liquidacion.empleadoNombre || "Sin nombre",
      tipoDoc: e?.tipoDoc || "CC",
      documento: e?.documento || liquidacion.empleadoDoc || "",
      cargo: e?.cargo || "—",
      fechaIngreso: e?.fechaIngreso || "—",
      salarioBase: e?.salarioBase || 0,
    };
    
    // Parsear detalle si es string JSON
    let detalle = [];
    if (liquidacion.detalle) {
      if (typeof liquidacion.detalle === 'string') {
        try {
          detalle = JSON.parse(liquidacion.detalle);
        } catch (err) {
          console.error("Error parseando detalle:", err);
          detalle = [];
        }
      } else if (Array.isArray(liquidacion.detalle)) {
        detalle = liquidacion.detalle;
      }
    }
    
    // Construir sección de información de salario
    let seccionSalario = '';
    if (liquidacion.baseLiquidacion) {
      seccionSalario = `<strong>Base de liquidación:</strong> $${(liquidacion.baseLiquidacion || 0).toLocaleString()}<br/>`;
    } else if (emp.salarioBase) {
      seccionSalario = `<strong>Salario base:</strong> $${(emp.salarioBase || 0).toLocaleString()}<br/>`;
    }
    
    const win = window.open("", "_blank", "width=850,height=700");
    if (!win) return;
    
    const firma = `
      <div style="display:flex;justify-content:space-between;margin-top:48px;">
        <div style="width:45%;text-align:center;">
          <div>RECIBO A CONFORMIDAD</div>
          <div style="margin-top:46px;border-top:1px solid #000;padding-top:4px;">
            ${emp.nombre}<br/>${emp.tipoDoc} ${emp.documento}
          </div>
        </div>
        <div style="width:45%;text-align:center;">
          <div>FIRMA AUTORIZADA</div>
          <div style="margin-top:46px;border-top:1px solid #000;padding-top:4px;">
            FIRMA AUTORIZADA
          </div>
        </div>
      </div>
    `;
    
    // Generar las filas de detalle
    const filasDetalle = (detalle || [])
      .filter(d => d && d.nombre && d.valor !== undefined && d.valor !== null)
      .map(d => `
        <tr style="border-bottom:1px solid #ccc;">
          <td style="padding:6px 8px;">${d.nombre}</td>
          <td style="padding:6px 8px;text-align:right;">$${(d.valor || 0).toLocaleString()}</td>
        </tr>
      `).join("");
    
    // Si no hay detalles, crear una fila con el tipo de liquidación
    const filasDetalleFinales = filasDetalle && filasDetalle.trim() ? filasDetalle : `
      <tr style="border-bottom:1px solid #ccc;">
        <td style="padding:6px 8px;">${liquidacion.tipo || "Vacaciones"}</td>
        <td style="padding:6px 8px;text-align:right;">$${(liquidacion.total || 0).toLocaleString()}</td>
      </tr>
    `;
    
    win.document.write(`
      <html>
        <head>
          <title>${liquidacion.tipo || "Liquidación de Vacaciones"}</title>
          <style>
            body{font-family:Arial,sans-serif;padding:18px;position:relative;}
            .logo-header{position:absolute;top:10px;right:15px;width:110px;height:110px;}
            .logo-header img{width:100%;height:100%;object-fit:contain;border-radius:10px;box-shadow:0 2px 8px rgba(0,0,0,0.15);}
            h1{font-size:18px;margin:0 130px 12px 0;text-align:center;text-transform:uppercase;font-weight:bold;border-bottom:2px solid #333;padding-bottom:10px;padding-top:20px;}
            table{border-collapse:collapse;width:100%;font-size:12px;margin-top:10px;}
            th,td{border:1px solid #ccc;padding:6px;text-align:left;}
            .meta{font-size:12px;color:#555;line-height:1.5;margin-top:6px;}
            @media print {
              .logo-header{position:fixed;top:0;right:0;width:120px;height:120px;}
              h1{margin-right:140px;}
            }
          </style>
        </head>
        <body>
          <div class="logo-header">
            <img src="${LOGO_BIGBURGUER}" alt="BigBurguer Logo" />
          </div>
          <h1>${liquidacion.tipo || "LIQUIDACIÓN DE VACACIONES"}</h1>
          <div class="meta">
            <strong>Empleado:</strong> ${emp.nombre}<br/>
            <strong>Documento:</strong> ${emp.tipoDoc} ${emp.documento}<br/>
            <strong>Cargo:</strong> ${emp.cargo}<br/>
            <strong>Fecha ingreso:</strong> ${emp.fechaIngreso}<br/>
            ${seccionSalario}
            ${liquidacion.fechaInicio ? `<strong>Período:</strong> ${liquidacion.fechaInicio} → ${liquidacion.fechaFin || ""}<br/>` : ""}
            ${liquidacion.periodoAfectado ? `<strong>Período(s) tomado(s):</strong> ${liquidacion.periodoAfectado}<br/>` : ""}
            ${liquidacion.diasTomados != null ? `<strong>Días tomados en esta liquidación:</strong> ${liquidacion.diasTomados}<br/>` : ""}
          </div>
          
          <table>
            <thead>
              <tr style="background:#f0f0f0;">
                <th style="text-align:left;">Concepto</th>
                <th style="text-align:right;">Valor</th>
              </tr>
            </thead>
            <tbody>
              ${filasDetalleFinales}
              <tr style="background:#f0f0f0;font-weight:bold;">
                <td style="text-align:right;padding:8px;">TOTAL A PAGAR</td>
                <td style="text-align:right;padding:8px;"><strong>$${(liquidacion.total || 0).toLocaleString()}</strong></td>
              </tr>
            </tbody>
          </table>
          
          ${firma}
          <script>window.print()</script>
        </body>
      </html>
    `);
    
    win.document.close();
  }

  function cargarParaEditar(ausencia) {
    setEditandoId(ausencia.id);
    setEmpleadoId(ausencia.empleadoId);
    setTipo(ausencia.tipo);
    setOrigen(ausencia.origen || "Enfermedad general");
    setInicio(ausencia.inicio);
    setFin(ausencia.fin);
    setMotivo(ausencia.motivo || "");
    setCobroPagado(ausencia.cobroPagado || false);
    setCobroFecha(ausencia.cobroFecha || "");
    setPeriodoVac(ausencia.periodo || "");
    setDiasCantidad(ausencia.dias?.toString() || "");
    setMotivoModificacion(""); // Limpiar motivo de modificación para nueva edición
  }

  function cancelarEdicion() {
    limpiarFormulario();
  }

  const selectStyleAus = {
    width: '100%',
    padding: '10px 14px',
    fontSize: '13px',
    border: '2px solid #ff6b35',
    borderRadius: '8px',
    backgroundColor: '#fffbf5',
    color: '#333',
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.2s ease',
  };

  const inputStyleAus = {
    width: '100%',
    padding: '10px 14px',
    fontSize: '13px',
    border: '2px solid #ffc53d',
    borderRadius: '8px',
    backgroundColor: '#fffbf5',
    color: '#333',
    outline: 'none',
    transition: 'all 0.2s ease',
  };

  const labelStyleAus = {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: '#ff6b35',
    marginBottom: '6px',
  };

  return (
    <div style={{ padding: '16px' }}>
      <h2 style={{
        fontSize: '18px',
        fontWeight: '700',
        color: '#ff6b35',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        borderBottom: '3px solid #ffc53d',
        paddingBottom: '10px'
      }}>📅 Ausencias / Incapacidades</h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '16px',
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: '#fffbf5',
        borderRadius: '12px',
        border: '2px solid #ffe4c9'
      }}>
        <div>
          <label style={labelStyleAus}>👤 Empleado</label>
          <select
            value={empleadoId}
            onChange={(e) => setEmpleadoId(e.target.value)}
            style={selectStyleAus}
          >
            <option value="">-- Selecciona --</option>
            {[...(empleados || [])].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombre} – {e.tipoDoc} {e.documento}
              </option>
            ))}
          </select>
        </div>
 
        <div>
          <label style={labelStyleAus}>📋 Tipo</label>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            style={selectStyleAus}
          >
            <option value="Permiso">🗓️ Permiso</option>
            <option value="Vacaciones">🏖️ Vacaciones</option>
            <option value="Incapacidad">🩺 Incapacidad</option>
            <option value="Suspensión">⛔ Suspensión</option>
            <option value="Otro">📝 Otro</option>
          </select>
        </div>
 
        <div>
          <label style={labelStyleAus}>🏥 Origen (para incapacidad)</label>
          <select
            value={origen}
            onChange={(e) => setOrigen(e.target.value)}
            style={{
              ...selectStyleAus,
              backgroundColor: tipo === "Vacaciones" ? '#f3f4f6' : '#fffbf5',
              cursor: tipo === "Vacaciones" ? 'not-allowed' : 'pointer',
            }}
            disabled={tipo === "Vacaciones"}
          >
            <option value="Enfermedad general">🤒 Enfermedad general</option>
            <option value="Accidente laboral">⚠️ Accidente laboral</option>
            <option value="Licencia maternidad">🤰 Licencia maternidad</option>
            <option value="No aplica">➖ No aplica</option>
          </select>
        </div>
 
        <div>
          <label style={labelStyleAus}>📆 Inicio</label>
          <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} style={inputStyleAus} />
        </div>
 
        <div>
          <label style={labelStyleAus}>🔢 Cantidad de días</label>
          <input
            type="number"
            value={diasCantidad}
            onChange={(e) => setDiasCantidad(e.target.value)}
            placeholder="Escribe días para autocalcular fin"
            style={inputStyleAus}
          />
        </div>
 
        <div>
          <label style={labelStyleAus}>📆 Fin</label>
          <input
            type="date"
            value={fin}
            onChange={(e) => setFin(e.target.value)}
            placeholder={tipo === "Vacaciones" ? "Se autocalcula con L–S y festivos CO" : "Se autocalcula corrido"}
            style={inputStyleAus}
          />
        </div>
 
        {tipo === "Vacaciones" && (
          <div>
            <label style={labelStyleAus}>🏖️ Periodo de vacaciones (auto)</label>
            <input
              value={periodoAuto || periodoVac}
              onChange={(e) => setPeriodoVac(e.target.value)}
              placeholder="Se sugiere automáticamente del período más antiguo"
              style={inputStyleAus}
            />
            {vacDetalle?.length > 0 && (
              <div style={{ fontSize: '11px', color: '#666', marginTop: '6px' }}>
                📊 Detalle: {vacDetalle.map(d => `${d.periodo} (${d.dias})`).join(" + ")}
              </div>
            )}
          </div>
        )}
 
        <div>
          <label style={labelStyleAus}>📝 Motivo / descripción</label>
          <input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="p. ej. cita médica, vacaciones..."
            style={inputStyleAus}
          />
        </div>
 
        {tipo === "Incapacidad" && (
          <>
            <div>
              <label style={labelStyleAus}>💰 ¿Ya se cobró la incapacidad?</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '10px', backgroundColor: '#fffbf5', border: '2px solid #ffc53d', borderRadius: '8px' }}>
                <input
                  type="checkbox"
                  checked={cobroPagado}
                  onChange={(e) => setCobroPagado(e.target.checked)}
                  style={{ width: '18px', height: '18px', accentColor: '#52c41a' }}
                />
                <span style={{ fontSize: '13px', fontWeight: '500' }}>{cobroPagado ? '✅ Sí, ya se cobró' : '⏳ Pendiente'}</span>
              </div>
            </div>
            {cobroPagado && (
              <div>
                <label style={labelStyleAus}>📅 Fecha de pago incapacidad</label>
                <input
                  type="date"
                  value={cobroFecha}
                  onChange={(e) => setCobroFecha(e.target.value)}
                  style={inputStyleAus}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Campo de motivo de modificación - solo visible cuando se edita */}
      {editandoId && (
        <div style={{ 
          marginTop: '16px', 
          marginBottom: '16px',
          padding: '16px', 
          backgroundColor: '#fff7ed', 
          border: '2px solid #f97316', 
          borderRadius: '10px' 
        }}>
          <label style={{ 
            display: 'block', 
            fontSize: '13px', 
            fontWeight: '700', 
            color: '#ea580c', 
            marginBottom: '8px' 
          }}>
            ✏️ Motivo de la Modificación {tipo === "Vacaciones" ? "(Requerido para vacaciones)" : "(Opcional)"}
          </label>
          <textarea
            value={motivoModificacion}
            onChange={(e) => setMotivoModificacion(e.target.value)}
            placeholder="Ingrese el motivo por el cual se está modificando esta ausencia (ej: Cambio de fechas por solicitud del empleado, Corrección de datos, etc.)"
            rows={3}
            style={{
              width: '100%',
              padding: '12px 14px',
              fontSize: '13px',
              border: '2px solid #fdba74',
              borderRadius: '8px',
              backgroundColor: 'white',
              color: '#333',
              resize: 'vertical',
              outline: 'none',
            }}
          />
          <p style={{ fontSize: '11px', color: '#9a3412', marginTop: '6px', margin: '6px 0 0' }}>
            ⚠️ Este motivo quedará registrado en el historial de la ausencia.
          </p>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px' }}>
        {(tienePermiso("rh_ausencias_guardar") || tienePermiso("rh_ausencias_editar")) && (
        <button
          onClick={guardar}
          style={{
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
            color: 'white',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseEnter={e => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 4px 12px rgba(82, 196, 26, 0.4)'; }}
          onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 2px 8px rgba(82, 196, 26, 0.3)'; }}
        >
          {editandoId ? "✏️ Actualizar ausencia" : "💾 Guardar ausencia"}
        </button>
        )}
        {editandoId && (
          <button
            onClick={cancelarEdicion}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: '600',
              border: '2px solid #ff6b35',
              borderRadius: '8px',
              backgroundColor: 'transparent',
              color: '#ff6b35',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => { e.target.style.backgroundColor = '#fff5f0'; }}
            onMouseLeave={e => { e.target.style.backgroundColor = 'transparent'; }}
          >
            ❌ Cancelar edición
          </button>
        )}
      </div>      <h3 style={{
        marginTop: '24px',
        fontSize: '15px',
        fontWeight: '600',
        color: '#ff6b35',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>📋 Todas las ausencias</h3>

      {/* === FILTROS DE BÚSQUEDA === */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '12px',
        marginTop: '12px',
        marginBottom: '16px',
        padding: '14px',
        backgroundColor: '#f8f9fa',
        borderRadius: '10px',
        border: '2px solid #e9ecef'
      }}>
        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#495057', marginBottom: '4px' }}>📅 Año</label>
          <select
            value={filtroAnio}
            onChange={(e) => setFiltroAnio(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: '12px',
              border: '1px solid #ced4da',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Todos</option>
            {(() => {
              // Obtener años de las ausencias existentes - parsear directamente del string YYYY-MM-DD
              const aniosAusencias = ausencias
                .map(a => {
                  if (!a.inicio) return null;
                  const partes = a.inicio.split('-');
                  return partes.length >= 1 ? parseInt(partes[0], 10) : null;
                })
                .filter(Boolean);
              
              // Siempre incluir el año actual y el anterior
              const anioActual = new Date().getFullYear();
              const aniosBase = [anioActual, anioActual - 1];
              
              // Combinar y eliminar duplicados, ordenar descendente
              const todosAnios = [...new Set([...aniosAusencias, ...aniosBase])].sort((a, b) => b - a);
              
              return todosAnios.map(anio => (
                <option key={anio} value={anio.toString()}>{anio}</option>
              ));
            })()}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#495057', marginBottom: '4px' }}>📆 Mes</label>
          <select
            value={filtroMes}
            onChange={(e) => setFiltroMes(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: '12px',
              border: '1px solid #ced4da',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Todos</option>
            <option value="1">Enero</option>
            <option value="2">Febrero</option>
            <option value="3">Marzo</option>
            <option value="4">Abril</option>
            <option value="5">Mayo</option>
            <option value="6">Junio</option>
            <option value="7">Julio</option>
            <option value="8">Agosto</option>
            <option value="9">Septiembre</option>
            <option value="10">Octubre</option>
            <option value="11">Noviembre</option>
            <option value="12">Diciembre</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#495057', marginBottom: '4px' }}>👤 Empleado</label>
          <select
            value={filtroEmpleado}
            onChange={(e) => setFiltroEmpleado(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: '12px',
              border: '1px solid #ced4da',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Todos</option>
            {[...(empleados || [])].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map(e => (
              <option key={e.id} value={e.id}>{e.nombre}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#495057', marginBottom: '4px' }}>📋 Tipo</label>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: '12px',
              border: '1px solid #ced4da',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Todos</option>
            <option value="Permiso">🗓️ Permiso</option>
            <option value="Vacaciones">🏖️ Vacaciones</option>
            <option value="Incapacidad">🩺 Incapacidad</option>
            <option value="Suspensión">⛔ Suspensión</option>
            <option value="Otro">📝 Otro</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button
            onClick={() => {
              setFiltroAnio(new Date().getFullYear().toString());
              setFiltroMes("");
              setFiltroEmpleado("");
              setFiltroTipo("");
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '12px',
              fontWeight: '600',
              border: '2px solid #6c757d',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              color: '#6c757d',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => { e.target.style.backgroundColor = '#6c757d'; e.target.style.color = 'white'; }}
            onMouseLeave={e => { e.target.style.backgroundColor = 'transparent'; e.target.style.color = '#6c757d'; }}
          >
            🔄 Limpiar filtros
          </button>
        </div>
      </div>

      {/* Mostrar resumen de filtros activos */}
      {(filtroAnio || filtroMes || filtroEmpleado || filtroTipo) && (
        <div style={{
          marginBottom: '12px',
          padding: '8px 12px',
          backgroundColor: '#e3f2fd',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#1565c0',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: '600' }}>🔍 Filtros activos:</span>
          {filtroAnio && <span style={{ backgroundColor: '#bbdefb', padding: '2px 8px', borderRadius: '12px' }}>Año: {filtroAnio}</span>}
          {filtroMes && <span style={{ backgroundColor: '#bbdefb', padding: '2px 8px', borderRadius: '12px' }}>Mes: {["", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"][parseInt(filtroMes)]}</span>}
          {filtroEmpleado && <span style={{ backgroundColor: '#bbdefb', padding: '2px 8px', borderRadius: '12px' }}>Empleado: {empleados.find(e => e.id === filtroEmpleado)?.nombre || "?"}</span>}
          {filtroTipo && <span style={{ backgroundColor: '#bbdefb', padding: '2px 8px', borderRadius: '12px' }}>Tipo: {filtroTipo}</span>}
          <span style={{ marginLeft: 'auto', fontWeight: '600' }}>
            ({(() => {
              const ausenciasFiltradas = conEmpleado.filter(a => {
                if (!a.inicio) return false;
                const fechaInicio = new Date(a.inicio);
                if (filtroAnio && fechaInicio.getFullYear() !== parseInt(filtroAnio)) return false;
                if (filtroMes && (fechaInicio.getMonth() + 1) !== parseInt(filtroMes)) return false;
                if (filtroEmpleado && a.empleadoId !== filtroEmpleado) return false;
                if (filtroTipo && a.tipo !== filtroTipo) return false;
                return true;
              });
              return ausenciasFiltradas.length;
            })()} resultados)
          </span>
        </div>
      )}

      <table style={{
        width: '100%',
        fontSize: '12px',
        marginTop: '12px',
        borderCollapse: 'collapse',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(255, 107, 53, 0.15)'
      }}>
        <thead>
          <tr style={{ background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' }}>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>👤 Empleado</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>📋 Tipo</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>📆 Inicio</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>📆 Fin</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>🔄 Reintegro</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>🗓️ Periodo</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>🔢 Días</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>📝 Motivo</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>✏️ Modificación</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>💰 Cobro</th>
            <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>⚙️ Acciones</th>
          </tr>
        </thead>
        <tbody>
          {conEmpleado
            .filter(a => {
              // Aplicar filtros
              if (!a.inicio) return !filtroAnio && !filtroMes; // Mostrar sin fecha solo si no hay filtros de fecha
              
              // Parsear fecha correctamente (formato YYYY-MM-DD)
              const partes = a.inicio.split('-');
              const anioAusencia = partes.length >= 1 ? parseInt(partes[0], 10) : null;
              const mesAusencia = partes.length >= 2 ? parseInt(partes[1], 10) : null;
              
              if (filtroAnio && anioAusencia !== parseInt(filtroAnio, 10)) return false;
              if (filtroMes && mesAusencia !== parseInt(filtroMes, 10)) return false;
              if (filtroEmpleado && a.empleadoId !== filtroEmpleado) return false;
              if (filtroTipo && a.tipo !== filtroTipo) return false;
              return true;
            })
            .sort((a, b) => {
              // Ordenar por fecha más reciente primero
              if (!a.inicio && !b.inicio) return 0;
              if (!a.inicio) return 1;
              if (!b.inicio) return -1;
              return new Date(b.inicio) - new Date(a.inicio);
            })
            .map((a, idx) => (
            <tr key={a.id} style={{ backgroundColor: idx % 2 === 0 ? '#fffbf5' : '#fff', borderBottom: '1px solid #ffe4c9' }}>
              <td style={{ padding: '10px 12px' }}>
                {a.empleado
                  ? `${a.empleado.nombre} – ${a.empleado.tipoDoc} ${a.empleado.documento}`
                  : "—"}
              </td>
              <td style={{ padding: '10px 12px' }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  borderRadius: '20px',
                  backgroundColor: a.tipo === 'Vacaciones' ? '#dbeafe' : a.tipo === 'Incapacidad' ? '#fce7f3' : a.tipo === 'Suspensión' ? '#fee2e2' : '#fef3c7',
                  color: a.tipo === 'Vacaciones' ? '#1d4ed8' : a.tipo === 'Incapacidad' ? '#be185d' : a.tipo === 'Suspensión' ? '#dc2626' : '#d97706',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  {a.tipo === 'Vacaciones' ? '🏖️' : a.tipo === 'Incapacidad' ? '🩺' : a.tipo === 'Suspensión' ? '⛔' : a.tipo === 'Permiso' ? '🗓️' : '📝'} {a.tipo}
                </span>
              </td>
              <td style={{ padding: '10px 12px' }}>{a.inicio || "—"}</td>
              <td style={{ padding: '10px 12px' }}>{a.fin || "—"}</td>
              <td style={{ padding: '10px 12px' }}>{a.reintegro || "—"}</td>
              <td style={{ padding: '10px 12px' }}>{a.periodo || "—"}</td>
              <td style={{ padding: '10px 12px', fontWeight: '600', color: '#ff6b35' }}>{a.dias ?? "—"}</td>
              <td style={{ padding: '10px 12px' }}>{a.motivo || "—"}</td>
              <td style={{ padding: '10px 12px' }}>
                {a.motivoModificacion || a.historialModificaciones?.length > 0 ? (
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => {
                        const historial = a.historialModificaciones || [];
                        const ultimaModif = historial[historial.length - 1];
                        let mensaje = `📝 ÚLTIMA MODIFICACIÓN:\n`;
                        mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━\n`;
                        mensaje += `📅 Fecha: ${a.fechaModificacion ? new Date(a.fechaModificacion).toLocaleString('es-CO') : 'N/A'}\n`;
                        mensaje += `💬 Motivo: ${a.motivoModificacion || 'No especificado'}\n\n`;
                        
                        if (historial.length > 0) {
                          mensaje += `📚 HISTORIAL COMPLETO (${historial.length} modificación${historial.length > 1 ? 'es' : ''}):\n`;
                          mensaje += `━━━━━━━━━━━━━━━━━━━━━━━━\n`;
                          historial.forEach((h, i) => {
                            mensaje += `\n${i + 1}. ${new Date(h.fecha).toLocaleString('es-CO')}\n`;
                            mensaje += `   Motivo: ${h.motivo}\n`;
                            if (h.cambios) {
                              mensaje += `   Cambios:\n`;
                              mensaje += `   • Inicio: ${h.cambios.inicioAnterior || 'N/A'} → ${h.cambios.inicioNuevo || 'N/A'}\n`;
                              mensaje += `   • Fin: ${h.cambios.finAnterior || 'N/A'} → ${h.cambios.finNuevo || 'N/A'}\n`;
                              mensaje += `   • Días: ${h.cambios.diasAnterior || 'N/A'} → ${h.cambios.diasNuevo || 'N/A'}\n`;
                            }
                          });
                        }
                        alert(mensaje);
                      }}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '20px',
                        border: 'none',
                        backgroundColor: '#fef3c7',
                        color: '#d97706',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title={`Motivo: ${a.motivoModificacion || 'Ver historial'}`}
                    >
                      ✏️ Modificado
                      {(a.historialModificaciones?.length || 0) > 1 && (
                        <span style={{
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          borderRadius: '50%',
                          width: 16,
                          height: 16,
                          fontSize: 10,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {a.historialModificaciones.length}
                        </span>
                      )}
                    </button>
                  </div>
                ) : (
                  <span style={{ color: '#9ca3af', fontSize: '11px' }}>Sin cambios</span>
                )}
              </td>
              <td style={{ padding: '10px 12px' }}>
                {a.tipo === "Incapacidad" ? (
                  a.cobroPagado ? (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      backgroundColor: '#d1fae5',
                      color: '#047857',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>✅ {a.cobroFecha || "Cobrado"}</span>
                  ) : (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>⏳ Pendiente</span>
                  )
                ) : (
                  <span style={{ color: '#9ca3af' }}>—</span>
                )}
              </td>
              <td style={{ padding: '10px 12px' }}>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {tienePermiso("rh_ausencias_editar") && (
                  <button 
                    onClick={() => cargarParaEditar(a)}
                    style={{
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      border: '2px solid #1890ff',
                      borderRadius: '6px',
                      backgroundColor: 'transparent',
                      color: '#1890ff',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    ✏️ Editar
                  </button>
                  )}
                  {tienePermiso("rh_ausencias_eliminar") && (
                  <button 
                    onClick={() => {
                      const confirmacion = window.confirm("¿Estás seguro de que deseas eliminar esta ausencia? Esta acción no se puede deshacer.");
                      if (confirmacion) {
                        onDelete(a.id);
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      border: 'none',
                      borderRadius: '6px',
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      color: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    🗑️ Eliminar
                  </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {conEmpleado.filter(a => {
              if (!a.inicio) return !filtroAnio && !filtroMes;
              const partes = a.inicio.split('-');
              const anioAusencia = partes.length >= 1 ? parseInt(partes[0], 10) : null;
              const mesAusencia = partes.length >= 2 ? parseInt(partes[1], 10) : null;
              if (filtroAnio && anioAusencia !== parseInt(filtroAnio, 10)) return false;
              if (filtroMes && mesAusencia !== parseInt(filtroMes, 10)) return false;
              if (filtroEmpleado && a.empleadoId !== filtroEmpleado) return false;
              if (filtroTipo && a.tipo !== filtroTipo) return false;
              return true;
            }).length === 0 && (
            <tr>
              <td colSpan={11} style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
                {(filtroAnio || filtroMes || filtroEmpleado || filtroTipo) 
                  ? "🔍 No se encontraron ausencias con los filtros seleccionados" 
                  : "📭 Sin ausencias registradas"}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
 
/* ====================== VACACIONES (resumen por períodos) ====================== */
function Vacaciones({ empleados, ausencias }) {
  const holidaysCache = React.useMemo(() => ({}), []);
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(""); // "" = ninguno, "TODOS" = todos

  function bucketsEmpleado(e) {
    try {
      const periods = buildVacationPeriods(e);  // ✅ CORRECTO
      const vacsEmp = (ausencias || []).filter(a => a.empleadoId === e.id && a.tipo === "Vacaciones");
      return applyVacationUses(periods, vacsEmp, holidaysCache);
    } catch (err) {
      console.warn('⚠️ Error calculando vacaciones para:', e?.nombre, err);
      return [];
    }
  }
 
  // Función para calcular datos de vacaciones de un empleado
  function calcularDatosEmpleado(empleado) {
    try {
      const buckets = bucketsEmpleado(empleado);
      const totalAcumulado = buckets.reduce((s, b) => s + (b.accrued || 0), 0);
      const totalUtilizado = buckets.reduce((s, b) => s + (b.used || 0), 0);
      const totalDisponible = buckets.reduce((s, b) => s + (b.pending || 0), 0);

      return {
        empleado,
        buckets,
        totalAcumulado,
        totalUtilizado,
        totalDisponible,
      };
    } catch (err) {
      console.warn('⚠️ Error calculando datos de empleado:', empleado?.nombre, err);
      return {
        empleado,
        buckets: [],
        totalAcumulado: 0,
        totalUtilizado: 0,
        totalDisponible: 0,
      };
    }
  }
 
  // Calcular empleados activos con sus datos (ordenados alfabéticamente)
  const empleadosActivos = empleados
    .filter((e) => e.activo !== false)
    .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", 'es', { sensitivity: 'base' }));
  const datosEmpleados = empleadosActivos.map(calcularDatosEmpleado);
 
  // Filtrar según selección
  const empleadosMostrar =
    empleadoSeleccionado === "TODOS"
      ? datosEmpleados
      : empleadoSeleccionado
      ? datosEmpleados.filter((d) => d.empleado.id === empleadoSeleccionado)
      : [];
 
  // Función para imprimir reporte individual
  function imprimirIndividual(datos) {
    const { empleado, buckets, totalAcumulado, totalUtilizado, totalDisponible } = datos;
 
    let html = `
      <html>
        <head>
          <title>Reporte de Vacaciones - ${empleado.nombre}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; border-bottom: 2px solid #d32f2f; padding-bottom: 10px; }
            h2 { color: #555; margin-top: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #d32f2f; color: white; }
            tr:nth-child(even) { background-color: #f2f2f2; }
            .resumen { background-color: #fff3e0; padding: 15px; border-left: 4px solid #ff9800; margin: 20px 0; }
            .resumen p { margin: 5px 0; }
            .footer { margin-top: 30px; font-size: 12px; color: #888; }
          </style>
        </head>
        <body>
          <h1>BIGBURGUER - Reporte de Vacaciones</h1>
          <h2>Empleado: ${empleado.nombre}</h2>
          <p><strong>Documento:</strong> ${empleado.documento || "N/A"}</p>
          <p><strong>Fecha de ingreso:</strong> ${empleado.fechaIngreso || "N/A"}</p>
          <p><strong>Fecha de reporte:</strong> ${toISO(new Date())}</p>
 
          <div class="resumen">
            <h3>Resumen</h3>
            <p><strong>Total acumulado:</strong> ${totalAcumulado} días</p>
            <p><strong>Total utilizado:</strong> ${totalUtilizado} días</p>
            <p><strong>Total disponible:</strong> ${totalDisponible} días</p>
          </div>
 
          <h3>Detalle por períodos</h3>
          <table>
            <thead>
              <tr>
                <th>Período</th>
                <th>Días acumulados</th>
                <th>Días utilizados</th>
                <th>Días disponibles</th>
              </tr>
            </thead>
            <tbody>
              ${buckets
                .map(
                  (b) => `
                <tr>
                  <td>${b.label || "N/A"}</td>
                  <td>${b.accrued || 0}</td>
                  <td>${b.used || 0}</td>
                  <td>${b.pending || 0}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
 
          <div class="footer">
            <p>Generado automáticamente por el sistema BIGBURGUER</p>
          </div>
        </body>
      </html>
    `;
 
    const ventana = window.open("", "_blank");
    ventana.document.write(html);
    ventana.document.close();
    ventana.print();
  }
 
  // Función para imprimir reporte de todos
  function imprimirTodos() {
    let html = `
      <html>
        <head>
          <title>Reporte General de Vacaciones</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; border-bottom: 2px solid #d32f2f; padding-bottom: 10px; }
            h2 { color: #555; margin-top: 30px; page-break-before: always; }
            h2:first-of-type { page-break-before: avoid; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #d32f2f; color: white; }
            tr:nth-child(even) { background-color: #f2f2f2; }
            .resumen { background-color: #fff3e0; padding: 10px; border-left: 4px solid #ff9800; margin: 15px 0; }
            .resumen p { margin: 5px 0; font-size: 14px; }
            .footer { margin-top: 30px; font-size: 12px; color: #888; }
            @media print {
              h2 { page-break-before: always; }
              h2:first-of-type { page-break-before: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>BIGBURGUER - Reporte General de Vacaciones</h1>
          <p><strong>Fecha de reporte:</strong> ${toISO(new Date())}</p>
          <p><strong>Total de empleados activos:</strong> ${datosEmpleados.length}</p>
 
          ${datosEmpleados
            .map(
              (datos) => `
            <h2>${datos.empleado.nombre}</h2>
            <p><strong>Documento:</strong> ${datos.empleado.documento || "N/A"} |
               <strong>Ingreso:</strong> ${datos.empleado.fechaIngreso || "N/A"}</p>
 
            <div class="resumen">
              <p><strong>Total acumulado:</strong> ${datos.totalAcumulado} días |
                 <strong>Utilizado:</strong> ${datos.totalUtilizado} días |
                 <strong>Disponible:</strong> ${datos.totalDisponible} días</p>
            </div>
 
            <table>
              <thead>
                <tr>
                  <th>Período</th>
                  <th>Acumulados</th>
                  <th>Utilizados</th>
                  <th>Disponibles</th>
                </tr>
              </thead>
              <tbody>
                ${datos.buckets
                  .map(
                    (b) => `
                  <tr>
                    <td>${b.label || "N/A"}</td>
                    <td>${b.accrued || 0}</td>
                    <td>${b.used || 0}</td>
                    <td>${b.pending || 0}</td>
                  </tr>
                `
                  )
                  .join("")}
              </tbody>
            </table>
          `
            )
            .join("")}
 
          <div class="footer">
            <p>Generado automáticamente por el sistema BIGBURGUER</p>
          </div>
        </body>
      </html>
    `;
 
    const ventana = window.open("", "_blank");
    ventana.document.write(html);
    ventana.document.close();
    ventana.print();
  }
 
  return (
    <div className="p-2">
      <h2 className="text-sm font-semibold mb-2">Vacaciones</h2>
 
      {/* Selector de empleado */}
      <div style={{ marginBottom: "20px" }}>
        <Field label="Seleccionar empleado:">
          <select
            value={empleadoSeleccionado}
            onChange={(e) => setEmpleadoSeleccionado(e.target.value)}
            style={{ padding: "8px", fontSize: "14px", width: "300px" }}
          >
            <option value="">-- Seleccione un empleado --</option>
            <option value="TODOS">VER TODOS LOS EMPLEADOS</option>
            {empleadosActivos.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombre} ({e.documento || "Sin doc"})
              </option>
            ))}
          </select>
        </Field>
 
        {/* Botones de impresión */}
        {empleadoSeleccionado && (
          <div style={{ marginTop: "10px" }}>
            {empleadoSeleccionado !== "TODOS" ? (
              <Button
                onClick={() => {
                  const datos = datosEmpleados.find(
                    (d) => d.empleado.id === empleadoSeleccionado
                  );
                  if (datos) imprimirIndividual(datos);
                }}
              >
                🖨️ Imprimir Reporte Individual
              </Button>
            ) : (
              <Button onClick={imprimirTodos}>
                🖨️ Imprimir Reporte General (Todos)
              </Button>
            )}
          </div>
        )}
      </div>
 
      {/* Mostrar datos según selección */}
      {empleadosMostrar.length === 0 && empleadoSeleccionado && (
        <p style={{ color: "#888", fontStyle: "italic" }}>
          No hay datos para mostrar.
        </p>
      )}
 
      {empleadosMostrar.map((datos) => {
        const { empleado, buckets, totalAcumulado, totalUtilizado, totalDisponible } = datos;
 
        return (
          <div
            key={empleado.id}
            style={{
              marginBottom: "30px",
              padding: "15px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              backgroundColor: "#fafafa",
            }}
          >
            <h3 style={{ marginTop: 0, color: "#d32f2f" }}>{empleado.nombre}</h3>
            <p>
              <strong>Documento:</strong> {empleado.documento || "N/A"} |{" "}
              <strong>Ingreso:</strong> {empleado.fechaIngreso || "N/A"}
            </p>
 
            <div
              style={{
                backgroundColor: "#fff3e0",
                padding: "10px",
                borderLeft: "4px solid #ff9800",
                marginBottom: "15px",
              }}
            >
              <p style={{ margin: "5px 0" }}>
                <strong>Total acumulado:</strong> {totalAcumulado} días
              </p>
              <p style={{ margin: "5px 0" }}>
                <strong>Total utilizado:</strong> {totalUtilizado} días
              </p>
              <p style={{ margin: "5px 0" }}>
                <strong>Total disponible:</strong> {totalDisponible} días
              </p>
            </div>
 
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: "#d32f2f", color: "white" }}>
                  <th style={{ padding: "8px", border: "1px solid #ddd" }}>Período</th>
                  <th style={{ padding: "8px", border: "1px solid #ddd" }}>Acumulados</th>
                  <th style={{ padding: "8px", border: "1px solid #ddd" }}>Utilizados</th>
                  <th style={{ padding: "8px", border: "1px solid #ddd" }}>Disponibles</th>
                </tr>
              </thead>
              <tbody>
                {buckets.map((b, idx) => (
                  <tr
                    key={idx}
                    style={{
                      backgroundColor: idx % 2 === 0 ? "#f9f9f9" : "white",
                    }}
                  >
                    <td style={{ padding: "8px", border: "1px solid #ddd" }}>
                      {b.label || "N/A"}
                    </td>
                    <td style={{ padding: "8px", border: "1px solid #ddd" }}>
                      {b.accrued || 0}
                    </td>
                    <td style={{ padding: "8px", border: "1px solid #ddd" }}>
                      {b.used || 0}
                    </td>
                    <td style={{ padding: "8px", border: "1px solid #ddd" }}>
                      {b.pending || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
 
/* ====================== PRESTAMOS (UI mejorada) ====================== */
 
function Prestamos({ empleados = [], prestamos = [], sedes = [], onCreate, onAbono, tienePermiso = () => true, esGerencia = false, onReactivarCuota, onEliminar, usuario }) {
  const [empleadoId, setEmpleadoId] = useState("");
  const [valor, setValor] = useState("");
  const [fecha, setFecha] = useState("");
  const [formaPago, setFormaPago] = useState("Quincenal");
  const [tipo, setTipo] = useState("Préstamo");
  const [cuotas, setCuotas] = useState("");
  const [motivo, setMotivo] = useState(""); // Nuevo campo motivo
 
  // Campos extra según tipo
  const [descFecha, setDescFecha] = useState("");
  const [descSede, setDescSede] = useState("");
  const [motivoDanio, setMotivoDanio] = useState("");

  // Estados para modal de eliminación
  const [modalEliminarOpen, setModalEliminarOpen] = useState(false);
  const [prestamoAEliminar, setPrestamoAEliminar] = useState(null);
  const [claveVerificacion, setClaveVerificacion] = useState("");
  const [motivoEliminacion, setMotivoEliminacion] = useState("");
  const [errorVerificacion, setErrorVerificacion] = useState("");
  const [eliminando, setEliminando] = useState(false);

  // Estados para modal de bitácora
  const [modalBitacoraOpen, setModalBitacoraOpen] = useState(false);
  const [bitacoraData, setBitacoraData] = useState([]);
  const [cargandoBitacora, setCargandoBitacora] = useState(false);

  // Estados para modal de abono a crédito
  const [modalAbonoOpen, setModalAbonoOpen] = useState(false);
  const [prestamoAbonar, setPrestamoAbonar] = useState(null);
  const [montoAbono, setMontoAbono] = useState("");
  const [claveAbono, setClaveAbono] = useState("");
  const [motivoAbono, setMotivoAbono] = useState("");
  const [errorAbono, setErrorAbono] = useState("");
  const [procesandoAbono, setProcesandoAbono] = useState(false);
 
  const [filtroEmp, setFiltroEmp] = useState("todos");
  const [filtroEstado, setFiltroEstado] = useState("todos"); // Mostrar todos por defecto
  const [prestamoExpandido, setPrestamoExpandido] = useState(null); // ID del préstamo expandido

  const [modalCuotasOpen, setModalCuotasOpen] = useState(false);
  // Determinar la vista inicial según permisos
  const vistaInicial = tienePermiso("rh_prestamos_registrar") ? "registrar" : 
                       tienePermiso("rh_prestamos_consultar") ? "consultar" : "registrar";
  const [vista, setVista] = useState(vistaInicial); // "registrar" | "consultar"
  const [tipoReporte, setTipoReporte] = useState("todos"); // "por-prestamo" | "por-empleado" | "activos" | "inactivos" | "todos"
  const [reporteOpen, setReporteOpen] = useState(false);
 
  // Protección contra datos undefined
  const prestamosArray = Array.isArray(prestamos) ? prestamos : [];
  const empleadosArray = Array.isArray(empleados) ? empleados : [];

  // Crear un mapa de empleados por ID para búsqueda rápida
  const empleadosMap = useMemo(() => {
    const map = new Map();
    empleadosArray.forEach(e => map.set(e.id, e));
    return map;
  }, [empleadosArray]);

  // Asociar cada préstamo con su empleado (si existe en la empresa actual)
  // NO filtramos aquí - el filtro por empresa ya se hace en cargarPrestamosDeSupabase
  const conEmpleado = useMemo(() => prestamosArray.map((p) => ({
    ...p,
    plan: Array.isArray(p.plan) ? p.plan : [],
    empleado: empleadosMap.get(p.empleadoId) || null,
  })), [prestamosArray, empleadosMap]);
 
  function guardar() {
    if (!empleadoId) return alert("Selecciona empleado");
    if (!valor) return alert("Valor requerido");
 
    const monto = Number(valor);
    const nCuotas = Math.max(1, Number(cuotas || 1));
 
    // Validaciones para tipos especiales
    if (tipo === "Descuadre de caja" || tipo === "Daños") {
      if (!descFecha) {
        return alert("Debes indicar la fecha del evento (descuadre / daño).");
      }
      if (!descSede) {
        return alert("Debes indicar la sede para este tipo de préstamo.");
      }
      if (tipo === "Daños" && !motivoDanio.trim()) {
        return alert("Debes indicar el motivo del daño.");
      }
    }
 
    const plan = Array.from({ length: nCuotas }).map((_, i) => ({
      id: uid(),
      nro: i + 1,
      fecha: "",
      valor: Math.round(monto / nCuotas),
      pagado: false,
      noDescontable: false,
    }));
 
    const extra = {};
    if (tipo === "Descuadre de caja" || tipo === "Daños") {
      extra.descFecha = descFecha;
      extra.descSede = descSede;
    }
    if (tipo === "Daños") {
      extra.motivoDanio = motivoDanio;
    }
 
    const nuevoPrestamo = {
      id: uid(),
      empleadoId,
      valor: monto,
      fecha,
      formaPago,
      tipo,
      cuotas: nCuotas,
      plan,
      motivo: motivo.trim() || null,
      ...extra,
    };
 
    onCreate(nuevoPrestamo);
 
    // Preguntar si quiere imprimir recibido
    const quiereImprimir = window.confirm(
      "Préstamo guardado.\n\n¿Desea imprimir el recibido de este préstamo ahora?"
    );
    if (quiereImprimir) {
      imprimirRecibo(nuevoPrestamo);
    }
 
    // Limpiar campos
    setValor("");
    setFecha("");
    setDescFecha("");
    setDescSede("");
    setMotivoDanio("");
    setMotivo("");
  }
 
  function togglePago(idPrestamo, cuotaId) {
    onAbono(idPrestamo, cuotaId);
  }

  // Funciones para eliminar préstamo
  function abrirModalEliminar(prestamo) {
    setPrestamoAEliminar(prestamo);
    setClaveVerificacion("");
    setMotivoEliminacion("");
    setErrorVerificacion("");
    setModalEliminarOpen(true);
  }

  function cerrarModalEliminar() {
    setModalEliminarOpen(false);
    setPrestamoAEliminar(null);
    setClaveVerificacion("");
    setMotivoEliminacion("");
    setErrorVerificacion("");
    setEliminando(false);
  }

  async function confirmarEliminacion() {
    if (!claveVerificacion.trim()) {
      setErrorVerificacion("Ingresa tu contraseña para verificar");
      return;
    }
    if (!motivoEliminacion.trim()) {
      setErrorVerificacion("Debes indicar el motivo de la eliminación");
      return;
    }

    setEliminando(true);
    setErrorVerificacion("");

    try {
      // Verificar la contraseña del usuario actual en la tabla usuarios
      // El sistema usa el campo 'usuario' (documento) como identificador, no el email
      const usuarioActual = usuario?.usuario || usuario?.email || "";
      
      if (!usuarioActual) {
        setErrorVerificacion("No se pudo identificar al usuario. Inicia sesión nuevamente.");
        setEliminando(false);
        return;
      }

      // Buscar el usuario en la tabla usuarios por el campo 'usuario' (documento)
      const { data: usuarioData, error: usuarioError } = await supabase
        .from("usuarios")
        .select("id, usuario, clave, nombre")
        .eq("usuario", usuarioActual)
        .single();

      if (usuarioError || !usuarioData) {
        setErrorVerificacion("No se encontró el usuario en el sistema.");
        setEliminando(false);
        return;
      }

      // Verificar que la contraseña coincida
      if (usuarioData.clave !== claveVerificacion) {
        setErrorVerificacion("Contraseña incorrecta. Verifica e intenta de nuevo.");
        setEliminando(false);
        return;
      }

      // Contraseña verificada, proceder con la eliminación
      if (onEliminar && prestamoAEliminar) {
        const exito = await onEliminar(prestamoAEliminar, motivoEliminacion);
        if (exito) {
          cerrarModalEliminar();
          alert("Préstamo eliminado correctamente. La información ha sido guardada en la bitácora.");
        } else {
          setErrorVerificacion("Error al eliminar el préstamo. Intenta de nuevo.");
        }
      }
    } catch (e) {
      console.error("Error en verificación:", e);
      setErrorVerificacion("Error al verificar. Intenta de nuevo.");
    }

    setEliminando(false);
  }

  // Función para abrir y cargar la bitácora
  async function abrirBitacora() {
    setCargandoBitacora(true);
    setModalBitacoraOpen(true);
    try {
      const datos = await cargarBitacoraPrestamosEliminados();
      setBitacoraData(datos);
    } catch (e) {
      console.error("Error cargando bitácora:", e);
      setBitacoraData([]);
    }
    setCargandoBitacora(false);
  }

  // Funciones para abono a crédito
  function abrirModalAbono(prestamo) {
    setPrestamoAbonar(prestamo);
    setMontoAbono("");
    setClaveAbono("");
    setMotivoAbono("");
    setErrorAbono("");
    setModalAbonoOpen(true);
  }

  function cerrarModalAbono() {
    setModalAbonoOpen(false);
    setPrestamoAbonar(null);
    setMontoAbono("");
    setClaveAbono("");
    setMotivoAbono("");
    setErrorAbono("");
    setProcesandoAbono(false);
  }

  async function confirmarAbono() {
    if (!montoAbono || Number(montoAbono) <= 0) {
      setErrorAbono("Ingresa un monto de abono válido");
      return;
    }
    if (!claveAbono.trim()) {
      setErrorAbono("Ingresa tu contraseña para verificar");
      return;
    }
    if (!motivoAbono.trim()) {
      setErrorAbono("Debes indicar el motivo del abono");
      return;
    }

    setProcesandoAbono(true);
    setErrorAbono("");

    try {
      // Verificar la contraseña del usuario actual
      const usuarioActual = usuario?.usuario || usuario?.email || "";
      
      if (!usuarioActual) {
        setErrorAbono("No se pudo identificar al usuario. Inicia sesión nuevamente.");
        setProcesandoAbono(false);
        return;
      }

      // Buscar el usuario en la tabla usuarios
      const { data: usuarioData, error: usuarioError } = await supabase
        .from("usuarios")
        .select("id, usuario, clave, nombre, perfil")
        .eq("usuario", usuarioActual)
        .single();

      if (usuarioError || !usuarioData) {
        setErrorAbono("No se encontró el usuario en el sistema.");
        setProcesandoAbono(false);
        return;
      }

      // Verificar que sea perfil GERENCIA (case-insensitive)
      const perfilUsuario = (usuarioData.perfil || "").toLowerCase();
      if (!perfilUsuario.includes("gerencia") && !perfilUsuario.includes("gerente") && !perfilUsuario.includes("admin")) {
        setErrorAbono("Solo usuarios con perfil GERENCIA pueden realizar abonos a crédito.");
        setProcesandoAbono(false);
        return;
      }

      // Verificar que la contraseña coincida
      if (usuarioData.clave !== claveAbono) {
        setErrorAbono("Contraseña incorrecta. Verifica e intenta de nuevo.");
        setProcesandoAbono(false);
        return;
      }

      // Calcular el abono
      const monto = Number(montoAbono);
      const planActual = prestamoAbonar.plan || [];
      const cuotasPendientes = planActual.filter(c => !c.noDescontable);
      
      if (cuotasPendientes.length === 0) {
        setErrorAbono("Este préstamo ya está completamente pagado.");
        setProcesandoAbono(false);
        return;
      }

      // Calcular saldo pendiente
      const saldoPendiente = cuotasPendientes.reduce((sum, c) => sum + (c.valor || 0), 0);
      
      if (monto > saldoPendiente) {
        setErrorAbono(`El abono ($${monto.toLocaleString("es-CO")}) supera el saldo pendiente ($${saldoPendiente.toLocaleString("es-CO")}).`);
        setProcesandoAbono(false);
        return;
      }

      // Procesar el abono: eliminar cuotas completas y dejar residuo en la última
      let montoRestante = monto;
      const nuevoPlan = [...planActual];
      const cuotasEliminadas = [];
      
      // Ordenar cuotas pendientes por número
      const indicesPendientes = planActual
        .map((c, idx) => ({ ...c, originalIndex: idx }))
        .filter(c => !c.noDescontable)
        .sort((a, b) => a.nro - b.nro);
      
      // Marcar cuotas como pagadas mientras alcance el monto
      for (let i = 0; i < indicesPendientes.length && montoRestante > 0; i++) {
        const cuota = indicesPendientes[i];
        const valorCuota = cuota.valor || 0;
        
        if (montoRestante >= valorCuota) {
          // Marcar cuota completa como pagada
          nuevoPlan[cuota.originalIndex] = {
            ...nuevoPlan[cuota.originalIndex],
            noDescontable: true,
            pagado: true,
            fechaPago: new Date().toISOString().split('T')[0],
            tipoPago: "Abono a crédito",
            motivoAbono: motivoAbono
          };
          cuotasEliminadas.push(cuota.nro);
          montoRestante -= valorCuota;
        } else {
          // Hay residuo: aplicarlo a esta cuota parcialmente y el resto queda para la última
          // Reducir el valor de esta cuota
          nuevoPlan[cuota.originalIndex] = {
            ...nuevoPlan[cuota.originalIndex],
            valor: valorCuota - montoRestante,
            abonoAplicado: montoRestante,
            fechaAbonoAplicado: new Date().toISOString().split('T')[0]
          };
          montoRestante = 0;
        }
      }

      // Actualizar el préstamo en Supabase
      const { error: updateError } = await supabase
        .from("prestamos")
        .update({ 
          plan: nuevoPlan,
          updated_at: new Date().toISOString()
        })
        .eq("id", prestamoAbonar.id);

      if (updateError) {
        console.error("Error actualizando préstamo:", updateError);
        setErrorAbono("Error al procesar el abono. Intenta de nuevo.");
        setProcesandoAbono(false);
        return;
      }

      // Registrar en bitácora (misma tabla que eliminaciones)
      try {
        const payloadBitacora = {
          prestamo_id: prestamoAbonar.id,
          empleado_id: prestamoAbonar.empleadoId,
          empleado_nombre: prestamoAbonar.empleado?.nombre || "Desconocido",
          valor: prestamoAbonar.valor || 0,
          cuotas: prestamoAbonar.cuotas || (prestamoAbonar.plan || []).length || 0,
          cuotas_descontadas: nuevoPlan.filter(c => c.noDescontable).length,
          saldo: saldoPendiente - monto,
          fecha_prestamo: prestamoAbonar.fecha || prestamoAbonar.fechaSolicitud || null,
          tipo_prestamo: prestamoAbonar.tipo || "Préstamo",
          estado_al_eliminar: "ABONO_CREDITO",
          plan_cuotas: JSON.stringify(nuevoPlan),
          usuario_eliminador_id: usuarioData.id,
          usuario_eliminador_nombre: usuarioData.nombre || usuarioActual,
          motivo_eliminacion: `[ABONO $${monto.toLocaleString("es-CO")}] ${motivoAbono} | Cuotas liquidadas: ${cuotasEliminadas.length > 0 ? cuotasEliminadas.join(", ") : "Ninguna completa (abono parcial)"}`,
          fecha_eliminacion: new Date().toISOString(),
          empresa_id: prestamoAbonar.empresa_id || null
        };
        
        console.log("📝 Guardando abono en bitácora:", payloadBitacora);
        
        const { error: bitacoraErr } = await supabase
          .from("bitacora_prestamos_eliminados")
          .insert([payloadBitacora]);
          
        if (bitacoraErr) {
          console.error("❌ Error guardando en bitácora:", bitacoraErr);
        } else {
          console.log("✅ Abono registrado en bitácora correctamente");
        }
      } catch (bitacoraError) {
        console.warn("No se pudo registrar en bitácora:", bitacoraError);
      }

      // Éxito
      cerrarModalAbono();
      alert(`Abono aplicado exitosamente.\n\nMonto: $${monto.toLocaleString("es-CO")}\nCuotas liquidadas: ${cuotasEliminadas.length > 0 ? cuotasEliminadas.join(", ") : "Ninguna completa"}\n\nEl préstamo ha sido actualizado.`);
      
      // Recargar datos (si hay función disponible)
      if (typeof window !== "undefined") {
        window.location.reload();
      }

    } catch (e) {
      console.error("Error en abono:", e);
      setErrorAbono("Error al procesar. Intenta de nuevo.");
    }

    setProcesandoAbono(false);
  }
 
  const prestamosFiltrados = conEmpleado.filter((p) => {
    // FILTRO POR EMPRESA: Solo mostrar préstamos cuyo empleado existe en la empresa actual
    // Si el empleado no existe (es de otra empresa), no mostrar el préstamo
    if (!p.empleado) return false;

    if (filtroEmp !== "todos" && p.empleadoId !== filtroEmp) return false;

    // Determinar si el préstamo está activo o pagado
    // Un préstamo está PAGADO cuando:
    // 1. Su estado es "Pagado" o "Completado" O
    // 2. Todas sus cuotas han sido descontadas (noDescontable = true en todas)
    const cuotasDescontadas = (p.plan || []).filter((c) => c.noDescontable).length;
    const totalCuotas = p.cuotas || (p.plan || []).length || 1;
    const todasCuotasDescontadas = totalCuotas > 0 && cuotasDescontadas >= totalCuotas;
    
    const estadoPagado = p.estado === "Pagado" || p.estado === "Completado";
    const estaPagado = estadoPagado || todasCuotasDescontadas;
    
    // Filtrar según el estado seleccionado
    if (filtroEstado === "activos" && estaPagado) return false;
    if (filtroEstado === "pagados" && !estaPagado) return false;
    // Si filtroEstado === "todos", mostrar todos

    return true;
  });

  // Préstamos con cuotas disponibles para descontar (para el modal de selección de cuotas)
  // Solo muestra préstamos que tienen al menos una cuota sin descontar
  const prestamosConCuotasDisponibles = conEmpleado.filter((p) => {
    // FILTRO POR EMPRESA: Solo préstamos cuyo empleado existe en la empresa actual
    if (!p.empleado) return false;

    // Solo préstamos activos (no pagados completamente)
    const cuotasDescontadas = (p.plan || []).filter((c) => c.noDescontable).length;
    const totalCuotas = p.cuotas || (p.plan || []).length || 1;
    
    // Solo mostrar si hay cuotas pendientes de descontar
    return cuotasDescontadas < totalCuotas;
  });
 
  const carteraTotal = prestamosFiltrados.reduce((acc, p) => {
    const abonado = (p.plan || [])
      .filter((c) => c.noDescontable)
      .reduce((s, c) => s + (c.valor || 0), 0);
    const saldo = (p.valor || 0) - abonado;
    return acc + Math.max(0, saldo);
  }, 0);
 
  function imprimirReporte(tipo = "todos") {
    let datosParaReporte = prestamosFiltrados;
    let titulo = "Reporte de préstamos / adelantos";

    // Filtrar según el tipo de reporte solicitado
    if (tipo === "activos") {
      datosParaReporte = prestamosFiltrados.filter((p) => {
        const cuotasDescontadas = (p.plan || []).filter(
          (c) => c.noDescontable
        ).length;
        return cuotasDescontadas < (p.cuotas || 0);
      });
      titulo = "Reporte de préstamos ACTIVOS";
    } else if (tipo === "inactivos") {
      datosParaReporte = prestamosFiltrados.filter((p) => {
        const cuotasDescontadas = (p.plan || []).filter(
          (c) => c.noDescontable
        ).length;
        return cuotasDescontadas >= (p.cuotas || 0);
      });
      titulo = "Reporte de préstamos INACTIVOS (Pagados)";
    } else if (tipo === "por-empleado") {
      titulo = "Reporte de préstamos por empleado";
    } else if (tipo === "por-prestamo") {
      titulo = "Reporte detallado de préstamos";
    }

    if (!datosParaReporte.length) {
      alert(`No hay datos para imprimir el reporte: ${titulo}`);
      return;
    }

    const filas = datosParaReporte
      .map((p) => {
        const e = p.empleado || {};
        const abonado = (p.plan || [])
          .filter((c) => c.noDescontable)
          .reduce((s, c) => s + (c.valor || 0), 0);
        const saldo = (p.valor || 0) - abonado;
        const cuotasPagadas = (p.plan || []).filter(
          (c) => c.noDescontable
        ).length;
        const estado =
          cuotasPagadas >= (p.cuotas || 0) ? "Pagado" : "Activo";

        return `
          <tr>
            <td style="padding:4px;border:1px solid #ddd;">${e.nombre || "—"}</td>
            <td style="padding:4px;border:1px solid #ddd;">${p.tipo}</td>
            <td style="padding:4px;border:1px solid #ddd;">${p.fecha || "—"}</td>
            <td style="padding:4px;border:1px solid #ddd;">$${(p.valor || 0).toLocaleString()}</td>
            <td style="padding:4px;border:1px solid #ddd;">${cuotasPagadas}/${p.cuotas}</td>
            <td style="padding:4px;border:1px solid #ddd;">$${abonado.toLocaleString()}</td>
            <td style="padding:4px;border:1px solid #ddd;">$${saldo.toLocaleString()}</td>
            <td style="padding:4px;border:1px solid #ddd;">${estado}</td>
          </tr>
        `;
      })
      .join("");

    const win = window.open("", "_blank");
    if (!win) return;

    win.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charSet="utf-8" />
          <title>${titulo}</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 12px; padding: 16px; }
            h1 { font-size: 16px; margin-bottom: 8px; }
            table { border-collapse: collapse; width: 100%; margin-top: 8px; }
            th, td { border: 1px solid #ddd; padding: 4px; text-align: left; }
            th { background:#f3f3f3; font-weight: bold; }
            .total { font-weight: bold; background: #f9f9f9; }
          </style>
        </head>
        <body>
          <h1>${titulo}</h1>
          <p>Fecha del reporte: ${new Date().toLocaleDateString("es-CO")}</p>
          <table>
            <thead>
              <tr>
                <th>Empleado</th>
                <th>Tipo</th>
                <th>Fecha</th>
                <th>Valor</th>
                <th>Cuotas desc.</th>
                <th>Abonado</th>
                <th>Saldo</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>${filas}</tbody>
          </table>
          <script>window.print()</script>
        </body>
      </html>
    `);

    win.document.close();
  }
 
  function imprimirRecibo(prestamo) {
    const e = empleadosArray.find((emp) => emp.id === prestamo.empleadoId) || {};
    const valorTotal = Number(prestamo.valor || 0);
    const nCuotas = Number(
      prestamo.cuotas || (prestamo.plan?.length || 1)
    );
    const valorCuota =
      nCuotas > 0 ? Math.round(valorTotal / nCuotas) : valorTotal;
    const fechaPrestamo = prestamo.fecha || toISO(new Date());
 
    const inicio = prestamo.fecha ? parseISO(prestamo.fecha) : new Date();
    const fin = new Date(inicio);
    fin.setDate(fin.getDate() + nCuotas * 15);
    const fechaFinISO = toISO(fin);
    const fechaFinLindo = fechaFinISO.split("-").reverse().join("/");
 
    const valorTexto = valorTotal.toLocaleString("es-CO", {
      style: "currency",
      currency: "COP",
    });
    const valorCuotaTexto = valorCuota.toLocaleString("es-CO", {
      style: "currency",
      currency: "COP",
    });
 
    const tipoPrestamo = prestamo.tipo || "Préstamo";
 
    const clausulaPrincipal = `
      Recibo a entera satisfacción la suma de ${valorTexto}, correspondiente a ${tipoPrestamo}.
      Este valor será descontado por nómina en ${nCuotas} cuota(s) de ${valorCuotaTexto} cada una,
      hasta la cancelación total de la obligación, autorizando de manera expresa a la empresa
      para realizar dichos descuentos en cada período de pago hasta completar el valor adeudado.
    `;
 
    const clausulaRetiro =
      tipoPrestamo === "Adelanto prima" || tipoPrestamo === "Adelanto cesantías"
        ? `
      En caso de terminación de mi contrato de trabajo por cualquier causa, autorizo a que el saldo
      pendiente de este ${tipoPrestamo} sea compensado y descontado de mi liquidación definitiva de
      prestaciones sociales (prima, cesantías, vacaciones y demás conceptos que haya lugar).
    `
        : "";
 
    const win = window.open("", "_blank");
    if (!win) return;
 
    win.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charSet="utf-8" />
          <title>Recibido de préstamo</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 12px; padding: 16px; line-height: 1.4; }
            h1 { font-size: 16px; text-align: center; margin-bottom: 12px; }
            .seccion { margin-bottom: 8px; }
            .label { font-weight: bold; }
            .firma { margin-top: 32px; text-align: center; }
            .firma div { border-top: 1px solid #000; display:inline-block; padding-top:4px; min-width:220px; }
          </style>
        </head>
        <body>
          <h1>Recibido de préstamo / adelanto</h1>
 
          <div class="seccion">
            <span class="label">Empleado: </span>${e.nombre || "—"} (${e.tipoDoc || "CC"} ${e.documento || ""})
          </div>
          <div class="seccion">
            <span class="label">Tipo de préstamo: </span>${tipoPrestamo}
          </div>
          <div class="seccion">
            <span class="label">Fecha del préstamo: </span>${fechaPrestamo}
          </div>
          <div class="seccion">
            <span class="label">Valor del préstamo: </span>${valorTexto}
          </div>
          <div class="seccion">
            <span class="label">Número de cuotas: </span>${nCuotas}
          </div>
          <div class="seccion">
            <span class="label">Valor de cada cuota: </span>${valorCuotaTexto}
          </div>
          <div class="seccion">
            <span class="label">Fecha estimada de cancelación: </span>${fechaFinLindo}
          </div>
 
          ${
            prestamo.descFecha || prestamo.descSede || prestamo.motivoDanio
              ? `<div class="seccion">
                  <span class="label">Detalle del evento: </span>
                  ${prestamo.descFecha || ""} ${prestamo.descSede ? " · " + prestamo.descSede : ""} ${
                  prestamo.motivoDanio || ""
                }
                </div>`
              : ""
          }
 
          <div class="seccion">
            ${clausulaPrincipal}
          </div>
          ${
            clausulaRetiro
              ? `<div class="seccion">${clausulaRetiro}</div>`
              : ""
          }
 
          <div class="firma">
            <div>${e.nombre || ""}</div>
            <div>Firma del trabajador</div>
          </div>
 
          <script>window.print()</script>
        </body>
      </html>
    `);
 
    win.document.close();
  }
 
  return (
    <div className="p-3">
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>💰</span>
        Préstamos / Adelantos
      </h2>

      {/* Pestañas ejecutivas - mostrar solo si tiene al menos un permiso */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        {tienePermiso("rh_prestamos_registrar") && (
        <button
          onClick={() => setVista("registrar")}
          style={{
            padding: '12px 24px',
            fontSize: 14,
            fontWeight: 600,
            borderRadius: 10,
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            transition: 'all 0.2s ease',
            background: vista === "registrar" 
              ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' 
              : '#f1f5f9',
            color: vista === "registrar" ? 'white' : '#64748b',
            boxShadow: vista === "registrar" 
              ? '0 4px 14px rgba(59, 130, 246, 0.4)' 
              : '0 1px 3px rgba(0,0,0,0.1)',
            transform: vista === "registrar" ? 'translateY(-1px)' : 'none'
          }}
        >
          <span style={{ fontSize: 16 }}>📝</span>
          Registrar préstamo
        </button>
        )}
        {tienePermiso("rh_prestamos_consultar") && (
        <button
          onClick={() => setVista("consultar")}
          style={{
            padding: '12px 24px',
            fontSize: 14,
            fontWeight: 600,
            borderRadius: 10,
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            transition: 'all 0.2s ease',
            background: vista === "consultar" 
              ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' 
              : '#f1f5f9',
            color: vista === "consultar" ? 'white' : '#64748b',
            boxShadow: vista === "consultar" 
              ? '0 4px 14px rgba(59, 130, 246, 0.4)' 
              : '0 1px 3px rgba(0,0,0,0.1)',
            transform: vista === "consultar" ? 'translateY(-1px)' : 'none'
          }}
        >
          <span style={{ fontSize: 16 }}>📊</span>
          Consultar / gestionar
        </button>
        )}
      </div>
 
      {/* === REGISTRAR (estilo panel azul mejorado) === */}
      {vista === "registrar" && tienePermiso("rh_prestamos_registrar") && (
        <div
          style={{
            background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
            borderRadius: 16,
            padding: 24,
            border: "1px solid #bfdbfe",
            boxShadow: '0 4px 20px rgba(59, 130, 246, 0.1)'
          }}
        >
          {/* Header mejorado */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>💰</div>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1e3a8a" }}>Registrar préstamo / adelanto</h3>
                <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>Diligencia los campos y guarda. Consulta en <strong>"Consultar / gestionar"</strong></p>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 24, alignItems: "stretch" }}>
            {/* Columna izquierda - Formulario */}
            <div style={{ flex: 2, background: 'white', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Empleado */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>👤 Empleado</label>
                  <select value={empleadoId} onChange={(e) => setEmpleadoId(e.target.value)} style={{ width: "100%", padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: 'white' }}>
                    <option value="">-- Selecciona --</option>
                    {empleadosArray.map((e) => (<option key={e.id} value={e.id}>{e.nombre} – {e.tipoDoc} {e.documento}</option>))}
                  </select>
                </div>
                {/* Valor */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>💵 Valor (COP)</label>
                  <input type="number" value={valor} onChange={(e) => setValor(e.target.value)} style={{ width: "100%", padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8 }} placeholder="0" />
                </div>
                {/* Fecha */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>📅 Fecha</label>
                  <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={{ width: "100%", padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8 }} />
                </div>
                {/* Forma pago */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>🔄 Forma de pago</label>
                  <select value={formaPago} onChange={(e) => setFormaPago(e.target.value)} style={{ width: "100%", padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: 'white' }}><option>Quincenal</option><option>Mensual</option></select>
                </div>
                {/* Tipo */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>📋 Tipo</label>
                  <select value={tipo} onChange={(e) => setTipo(e.target.value)} style={{ width: "100%", padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: 'white' }}><option>Préstamo</option><option>Adelanto nómina</option><option>Adelanto prima</option><option>Adelanto cesantías</option><option>Descuadre de caja</option><option>Daños</option></select>
                </div>
                {/* Cuotas */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>🔢 Cuotas</label>
                  <input type="number" value={cuotas} onChange={(e) => setCuotas(Number(e.target.value || 1))} style={{ width: "100%", padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8 }} min="1" />
                </div>
                {/* Motivo */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>📝 Motivo (opcional)</label>
                  <textarea rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} style={{ width: "100%", padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8, resize: 'vertical' }} placeholder="Describe el motivo del préstamo o adelanto..." />
                </div>
              </div>
              {/* Campos adicionales */}
              {(tipo === "Descuadre de caja" || tipo === "Daños") && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div><label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>📅 Fecha evento</label><input type="date" value={descFecha} onChange={(e) => setDescFecha(e.target.value)} style={{ width: "100%", padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8 }} /></div>
                    <div><label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>🏢 Sede</label><select value={descSede} onChange={(e) => setDescSede(e.target.value)} style={{ width: "100%", padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8, backgroundColor: 'white' }}><option value="">-- Selecciona --</option>{(sedes || []).map((s) => (<option key={s.id || s} value={s.nombre || s}>{s.nombre || s}</option>))}</select></div>
                  </div>
                  {tipo === "Daños" && (<div style={{ marginTop: 16 }}><label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>📝 Motivo</label><textarea rows={2} value={motivoDanio} onChange={(e) => setMotivoDanio(e.target.value)} style={{ width: "100%", padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8, resize: 'vertical' }} placeholder="Describa lo ocurrido..." /></div>)}
                </div>
              )}
            </div>
            {/* Columna derecha - Resumen */}
            <div style={{ width: 300, background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)", borderRadius: 12, border: "1px solid #bfdbfe", padding: 20, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #3b82f6' }}><span style={{ fontSize: 20 }}>📊</span><span style={{ fontWeight: 700, fontSize: 16, color: "#1e3a8a" }}>Resumen estimado</span></div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ background: '#eff6ff', padding: 12, borderRadius: 8, border: '1px solid #bfdbfe' }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Valor del préstamo</div><div style={{ fontSize: 20, fontWeight: 700, color: '#1e3a8a' }}>{Number(valor || 0).toLocaleString("es-CO", { style: "currency", currency: "COP" })}</div></div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ flex: 1, background: '#f0fdf4', padding: 12, borderRadius: 8, border: '1px solid #bbf7d0' }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Cuotas</div><div style={{ fontSize: 18, fontWeight: 700, color: '#166534' }}>{cuotas || 0}</div></div>
                    <div style={{ flex: 1, background: '#fef3c7', padding: 12, borderRadius: 8, border: '1px solid #fcd34d' }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Valor cuota</div><div style={{ fontSize: 16, fontWeight: 700, color: '#92400e' }}>{Number(valor || 0) > 0 && cuotas > 0 ? Math.round(Number(valor || 0) / Number(cuotas || 1)).toLocaleString("es-CO", { style: "currency", currency: "COP" }) : "$0"}</div></div>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 20 }}>
                {tienePermiso("rh_prestamos_registrar") && (<button onClick={guardar} style={{ width: "100%", padding: "14px 0", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)", color: "white", fontSize: 14, fontWeight: 700, cursor: "pointer", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 4px 14px rgba(34, 197, 94, 0.4)' }}><span>✓</span> Guardar préstamo</button>)}
              </div>
            </div>
          </div>
        </div>
      )}
 
      {/* === CONSULTAR / GESTIONAR (panel azul + tabla) === */}
      {vista === "consultar" && tienePermiso("rh_prestamos_consultar") && (
        <div
          style={{
            background: "#e5f3ff",
            borderRadius: 10,
            padding: 16,
            border: "1px solid #c3ddff",
          }}
        >
          <div style={{ marginBottom: 12 }}>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                color: "#1e3a8a",
              }}
            >
              Consultar y gestionar préstamos
            </h3>
            <p
              style={{
                margin: "4px 0 0",
                fontSize: 14,
                color: "#4b5563",
              }}
            >
              Consulta los préstamos existentes, marca las cuotas a descontar en
              la nómina actual y genera reportes o recibidos.
            </p>
          </div>
 
          {/* Filtros */}
          <div
            style={{
              background: "rgba(255,255,255,0.9)",
              borderRadius: 8,
              padding: 8,
              marginBottom: 10,
              border: "1px solid #dbeafe",
            }}
          >
            <div className="flex flex-wrap gap-3 items-end">
              <Field label="Filtrar por empleado">
                <select
                  className="border rounded-lg px-2 py-1.5 text-sm w-56 bg-white"
                  value={filtroEmp}
                  onChange={(e) => setFiltroEmp(e.target.value)}
                >
                  <option value="todos">Todos</option>
                  {empleadosArray.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nombre} – {e.documento}
                    </option>
                  ))}
                </select>
              </Field>
 
              <Field label="Estado">
                <select
                  className="border rounded-lg px-2 py-1.5 text-sm w-40 bg-white"
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value)}
                >
                  <option value="todos">Todos</option>
                  <option value="activos">Activos</option>
                  <option value="pagados">Pagados</option>
                </select>
              </Field>
 
              <div style={{ 
                marginLeft: 'auto', 
                display: 'flex', 
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'nowrap'
              }}>
                {tienePermiso("rh_prestamos_abonar") && (
                <button
                  onClick={() => setModalCuotasOpen(true)}
                  style={{
                    background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 20px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    boxShadow: '0 4px 14px rgba(96, 165, 250, 0.35)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(96, 165, 250, 0.45)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(96, 165, 250, 0.35)';
                  }}
                >
                  <span style={{ fontSize: 16 }}>💳</span>
                  Seleccionar cuotas a descontar
                </button>
                )}
                {tienePermiso("rh_prestamos_imprimir") && (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <button
                    onClick={() => setReporteOpen(!reporteOpen)}
                    style={{
                      background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      padding: '10px 20px',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      boxShadow: '0 4px 14px rgba(96, 165, 250, 0.35)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(96, 165, 250, 0.45)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 14px rgba(96, 165, 250, 0.35)';
                    }}
                  >
                    <span style={{ fontSize: 16 }}>📊</span>
                    Imprimir reporte ▼
                  </button>
                  {reporteOpen && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: 8,
                      width: 280,
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: 12,
                      boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                      zIndex: 9999,
                      overflow: 'hidden'
                    }}>
                      <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)', color: 'white', fontSize: 12, fontWeight: 600 }}>
                        Selecciona el tipo de reporte
                      </div>
                      <button
                        onClick={() => {
                          imprimirReporte("todos");
                          setReporteOpen(false);
                        }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', border: 'none', borderBottom: '1px solid #f1f5f9', background: 'white', fontSize: 13, cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                      >
                        📋 General (Todos los préstamos)
                      </button>
                      <button
                        onClick={() => {
                          imprimirReporte("activos");
                          setReporteOpen(false);
                        }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', border: 'none', borderBottom: '1px solid #f1f5f9', background: 'white', fontSize: 13, cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                      >
                        ✓ Activos (Con cuotas pendientes)
                      </button>
                      <button
                        onClick={() => {
                          imprimirReporte("inactivos");
                          setReporteOpen(false);
                        }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', border: 'none', borderBottom: '1px solid #f1f5f9', background: 'white', fontSize: 13, cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                      >
                        ✓ Inactivos (Completamente pagados)
                      </button>
                      <button
                        onClick={() => {
                          imprimirReporte("por-empleado");
                          setReporteOpen(false);
                        }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', border: 'none', borderBottom: '1px solid #f1f5f9', background: 'white', fontSize: 13, cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                      >
                        👤 Por empleado
                      </button>
                      <button
                        onClick={() => {
                          imprimirReporte("por-prestamo");
                          setReporteOpen(false);
                        }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', border: 'none', background: 'white', fontSize: 13, cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                      >
                        📄 Por préstamo (detallado)
                      </button>
                    </div>
                  )}
                </div>
                )}
                {tienePermiso("rh_prestamos_eliminar") && (
                <button
                  onClick={abrirBitacora}
                  style={{
                    background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 20px',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    boxShadow: '0 4px 14px rgba(96, 165, 250, 0.35)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(96, 165, 250, 0.45)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(96, 165, 250, 0.35)';
                  }}
                >
                  <span style={{ fontSize: 16 }}>📋</span>
                  Bitácora novedades a crédito
                </button>
                )}
              </div>
            </div>
 
            <div className="mt-2 text-xs text-slate-700">
              Cartera (según filtros):{" "}
              <strong>
                $
                {carteraTotal.toLocaleString("es-CO")}
              </strong>
            </div>
          </div>
 
          {/* Lista de préstamos - Vista ejecutiva */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {prestamosFiltrados.map((p) => {
              const e = p.empleado || {};
              const abonado = (p.plan || [])
                .filter((c) => c.noDescontable)
                .reduce((s, c) => s + (c.valor || 0), 0);
              const saldo = (p.valor || 0) - abonado;
              const cuotasPagadas = (p.plan || []).filter(
                (c) => c.noDescontable
              ).length;
              const valorCuota =
                (p.cuotas || 0) > 0
                  ? Math.round((p.valor || 0) / (p.cuotas || 1))
                  : p.valor || 0;
              const isExpanded = prestamoExpandido === p.id;
              const progreso = p.cuotas > 0 ? (cuotasPagadas / p.cuotas) * 100 : 0;

              return (
                <div
                  key={p.id}
                  style={{
                    background: 'white',
                    borderRadius: 12,
                    border: isExpanded ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                    overflow: 'hidden',
                    boxShadow: isExpanded ? '0 8px 25px rgba(59, 130, 246, 0.15)' : '0 2px 8px rgba(0,0,0,0.04)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {/* Fila principal compacta */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr auto',
                      alignItems: 'center',
                      padding: '16px 20px',
                      gap: 16,
                      background: isExpanded ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' : 'white'
                    }}
                  >
                    {/* Empleado */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 44,
                        height: 44,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: 16
                      }}>
                        {(e.nombre || 'U')[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{e.nombre || "—"}</div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>{e.tipoDoc || "CC"} {e.documento || ""}</div>
                      </div>
                    </div>
                    {/* Tipo */}
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{p.tipo}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.formaPago || "Quincenal"}</div>
                    </div>
                    {/* Valor */}
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#1e3a8a' }}>${(p.valor || 0).toLocaleString("es-CO")}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{p.fecha || "—"}</div>
                    </div>
                    {/* Progreso */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: '#64748b' }}>Cuotas</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{cuotasPagadas}/{p.cuotas || 0}</span>
                      </div>
                      <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${progreso}%`,
                          background: progreso >= 100 ? 'linear-gradient(90deg, #22c55e, #16a34a)' : 'linear-gradient(90deg, #3b82f6, #1d4ed8)',
                          borderRadius: 3,
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </div>
                    {/* Saldo */}
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 11, color: '#64748b' }}>Saldo</div>
                      <div style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: saldo === 0 ? '#16a34a' : '#dc2626'
                      }}>
                        ${saldo.toLocaleString("es-CO")}
                      </div>
                    </div>
                    {/* Botón Consultar */}
                    <button
                      onClick={() => setPrestamoExpandido(isExpanded ? null : p.id)}
                      style={{
                        padding: '10px 20px',
                        borderRadius: 8,
                        border: 'none',
                        background: isExpanded 
                          ? 'linear-gradient(135deg, #64748b 0%, #475569 100%)' 
                          : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: 'white',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {isExpanded ? '✕ Cerrar' : '🔍 Consultar'}
                    </button>
                  </div>

                  {/* Panel expandido con detalles */}
                  {isExpanded && (
                    <div style={{
                      borderTop: '1px solid #e2e8f0',
                      padding: 20,
                      background: '#f8fafc',
                      animation: 'fadeIn 0.3s ease'
                    }}>
                      {/* Resumen del préstamo */}
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 8, 
                          marginBottom: 16,
                          paddingBottom: 8,
                          borderBottom: '2px solid #3b82f6'
                        }}>
                          <span style={{ fontSize: 18 }}>📊</span>
                          <span style={{ fontWeight: 700, fontSize: 14, color: '#1e3a8a' }}>Resumen del préstamo</span>
                        </div>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(3, 1fr)', 
                          gap: 12,
                          background: 'white',
                          padding: 16,
                          borderRadius: 10,
                          border: '1px solid #e2e8f0'
                        }}>
                          {/* Empleado */}
                          <div style={{ background: '#eff6ff', padding: 12, borderRadius: 8, border: '1px solid #bfdbfe' }}>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span>👤</span> Empleado
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#1e3a8a' }}>{e.nombre || "—"}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{e.tipoDoc || "CC"} {e.documento || ""}</div>
                          </div>
                          {/* Valor */}
                          <div style={{ background: '#f0fdf4', padding: 12, borderRadius: 8, border: '1px solid #bbf7d0' }}>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span>💵</span> Valor (COP)
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: '#166534' }}>${(p.valor || 0).toLocaleString("es-CO")}</div>
                          </div>
                          {/* Fecha */}
                          <div style={{ background: '#fef3c7', padding: 12, borderRadius: 8, border: '1px solid #fcd34d' }}>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span>📅</span> Fecha
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#92400e' }}>{p.fecha || "—"}</div>
                          </div>
                          {/* Forma de pago */}
                          <div style={{ background: '#f5f3ff', padding: 12, borderRadius: 8, border: '1px solid #c4b5fd' }}>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span>🔄</span> Forma de pago
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#5b21b6' }}>{p.formaPago || "Quincenal"}</div>
                          </div>
                          {/* Tipo */}
                          <div style={{ background: '#fef2f2', padding: 12, borderRadius: 8, border: '1px solid #fecaca' }}>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span>📋</span> Tipo
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#991b1b' }}>{p.tipo || "Préstamo"}</div>
                          </div>
                          {/* Cuotas */}
                          <div style={{ background: '#ecfeff', padding: 12, borderRadius: 8, border: '1px solid #a5f3fc' }}>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span>🔢</span> Cuotas
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#0e7490' }}>{cuotasPagadas} de {p.cuotas || 0} pagadas</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>Valor cuota: ${valorCuota.toLocaleString("es-CO")}</div>
                          </div>
                        </div>
                        {/* Motivo del préstamo */}
                        {p.motivo && (
                          <div style={{ 
                            marginTop: 12,
                            padding: 12, 
                            background: '#FFF8E1', 
                            borderRadius: 8, 
                            border: '1px solid #FFECB3' 
                          }}>
                            <div style={{ fontSize: 11, color: '#F57C00', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                              <span>📝</span> Motivo
                            </div>
                            <div style={{ fontSize: 13, color: '#333' }}>{p.motivo}</div>
                          </div>
                        )}
                      </div>

                      {/* Sección de cuotas */}
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 8, 
                          marginBottom: 12,
                          paddingBottom: 8,
                          borderBottom: '2px solid #3b82f6'
                        }}>
                          <span style={{ fontSize: 18 }}>📋</span>
                          <span style={{ fontWeight: 700, fontSize: 14, color: '#1e3a8a' }}>Detalle de cuotas</span>
                          <span style={{ 
                            marginLeft: 'auto', 
                            fontSize: 12, 
                            color: '#64748b',
                            background: '#e2e8f0',
                            padding: '4px 10px',
                            borderRadius: 12
                          }}>
                            Cuota aprox: ${valorCuota.toLocaleString("es-CO")}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {(p.plan || []).map((c) => (
                            <div
                              key={c.id}
                              title={
                                c.noDescontable
                                  ? "🔒 Descontado en nómina" + (esGerencia ? " - Click para reactivar" : "")
                                  : c.pagado
                                  ? "✓ Marcado para descontar"
                                  : "○ Pendiente"
                              }
                              onClick={() => {
                                if (c.noDescontable && esGerencia && typeof onReactivarCuota === 'function') {
                                  onReactivarCuota(p.id, c.id);
                                }
                              }}
                              style={{
                                padding: '10px 14px',
                                borderRadius: 8,
                                fontSize: 13,
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                cursor: c.noDescontable && esGerencia ? 'pointer' : 'default',
                                background: c.noDescontable
                                  ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                                  : c.pagado
                                  ? '#dcfce7'
                                  : '#f1f5f9',
                                color: c.noDescontable
                                  ? 'white'
                                  : c.pagado
                                  ? '#166534'
                                  : '#475569',
                                border: c.noDescontable
                                  ? 'none'
                                  : c.pagado
                                  ? '1px solid #bbf7d0'
                                  : '1px solid #e2e8f0',
                                boxShadow: c.noDescontable ? '0 2px 8px rgba(34, 197, 94, 0.3)' : 'none',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <span>{c.noDescontable ? "🔒" : c.pagado ? "✓" : "○"}</span>
                              <span>#{c.nro || 0}</span>
                              <span>${(c.valor || 0).toLocaleString("es-CO")}</span>
                              {c.noDescontable && esGerencia && (
                                <span style={{ 
                                  marginLeft: 4, 
                                  fontSize: 10, 
                                  backgroundColor: '#ffc53d', 
                                  color: '#000', 
                                  padding: '2px 6px', 
                                  borderRadius: 4,
                                  fontWeight: 'bold'
                                }}>
                                  🔓
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Acciones */}
                      <div style={{ 
                        display: 'flex', 
                        gap: 12, 
                        paddingTop: 16, 
                        borderTop: '1px solid #e2e8f0',
                        justifyContent: 'flex-end'
                      }}>
                        {tienePermiso("rh_prestamos_recibo") && (
                          <button
                            onClick={() => imprimirRecibo(p)}
                            style={{
                              padding: '10px 20px',
                              borderRadius: 8,
                              border: 'none',
                              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                              color: 'white',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
                            }}
                          >
                            🧾 Imprimir recibido
                          </button>
                        )}
                        {tienePermiso("rh_prestamos_abono_credito") && esGerencia && cuotasPagadas < (p.cuotas || (p.plan || []).length || 1) && (
                          <button
                            onClick={() => abrirModalAbono(p)}
                            style={{
                              padding: '10px 20px',
                              borderRadius: 8,
                              border: 'none',
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: 'white',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                            }}
                          >
                            💰 Abono a crédito
                          </button>
                        )}
                        {tienePermiso("rh_prestamos_eliminar") && cuotasPagadas < (p.cuotas || (p.plan || []).length || 1) && (
                          <button
                            onClick={() => abrirModalEliminar(p)}
                            style={{
                              padding: '10px 20px',
                              borderRadius: 8,
                              border: 'none',
                              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                              color: 'white',
                              fontSize: 13,
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                            }}
                          >
                            🗑️ Eliminar préstamo
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {!prestamosFiltrados.length && (
              <div style={{
                background: 'white',
                borderRadius: 12,
                border: '1px solid #e2e8f0',
                padding: 40,
                textAlign: 'center',
                color: '#64748b'
              }}>
                <span style={{ fontSize: 40, display: 'block', marginBottom: 12 }}>📭</span>
                <span style={{ fontSize: 14 }}>Sin préstamos con los filtros actuales.</span>
              </div>
            )}
          </div>
 
          {/* Modal cuotas */}
          {modalCuotasOpen && (
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: 'rgba(15, 23, 42, 0.95)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 999999,
                backdropFilter: 'blur(8px)'
              }}
            >
              <div style={{
                backgroundColor: '#ffffff',
                borderRadius: 16,
                padding: 0,
                width: 920,
                maxWidth: '95vw',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.1)'
              }}>
                {/* Header del modal */}
                <div style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  padding: '20px 24px',
                  borderRadius: '16px 16px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 22
                    }}>
                      💳
                    </div>
                    <div>
                      <h3 style={{ color: 'white', fontSize: 18, fontWeight: 700, margin: 0 }}>
                        Selección de Cuotas
                      </h3>
                      <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, margin: 0 }}>
                        Marca las cuotas a descontar en esta quincena
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setModalCuotasOpen(false)}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: '50%',
                      width: 36,
                      height: 36,
                      color: 'white',
                      fontSize: 18,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                  >
                    ✕
                  </button>
                </div>

                {/* Nota informativa */}
                <div style={{
                  background: 'linear-gradient(90deg, #fef3c7 0%, #fef9c3 100%)',
                  padding: '12px 24px',
                  borderBottom: '1px solid #fcd34d',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10
                }}>
                  <span style={{ fontSize: 16 }}>⚠️</span>
                  <span style={{ fontSize: 12, color: '#92400e' }}>
                    Las cuotas con fondo <strong>gris oscuro</strong> ya fueron descontadas y están bloqueadas. Solo puedes marcar las cuotas pendientes.
                  </span>
                </div>

                {/* Contenido scrolleable */}
                <div style={{ padding: '20px 24px', flex: 1, overflowY: 'auto', maxHeight: '55vh' }}>
                  {prestamosConCuotasDisponibles.map((p) => {
                    const e = p.empleado || {};
                    const abonado = (p.plan || [])
                      .filter((c) => c.noDescontable)
                      .reduce((s, c) => s + (c.valor || 0), 0);
                    const saldo = (p.valor || 0) - abonado;
 
                    return (
                      <div
                        key={p.id}
                        style={{
                          backgroundColor: '#ffffff',
                          borderRadius: 10,
                          padding: '16px 20px',
                          marginBottom: 12,
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b' }}>
                              {e.nombre || "—"}
                            </div>
                            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                              {e.tipoDoc || "CC"} {e.documento || ""} · {p.tipo}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 11, color: '#64748b' }}>Total: <strong style={{ color: '#1e293b' }}>${(p.valor || 0).toLocaleString("es-CO")}</strong></div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>Saldo: <strong style={{ color: '#dc2626' }}>${saldo.toLocaleString("es-CO")}</strong></div>
                            <div style={{
                              fontSize: 10,
                              backgroundColor: '#dbeafe',
                              color: '#1d4ed8',
                              padding: '2px 8px',
                              borderRadius: 20,
                              marginTop: 4,
                              display: 'inline-block',
                              fontWeight: 600
                            }}>
                              {(p.plan || []).filter(c => c.noDescontable).length}/{p.cuotas || 0} cuotas
                            </div>
                          </div>
                        </div>
 
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {(p.plan || []).map((c) => {
                            const yaDescontada = !!c.noDescontable;
                            const marcada = !!c.pagado;
                            const checked = yaDescontada || marcada;
 
                            let estilo =
                              "inline-flex items-center gap-1 px-2 py-[2px] rounded-full border text-[10px] ";
                            if (yaDescontada) {
                              estilo +=
                                "bg-green-600 text-white border-green-700 cursor-not-allowed";
                            } else if (marcada) {
                              estilo +=
                                "bg-emerald-100 text-emerald-800 border-emerald-300";
                            } else {
                              estilo +=
                                "bg-slate-100 text-slate-700 border-slate-300";
                            }
 
                            return (
                              <label key={c.id} className={estilo}>
                                <input
                                  type="checkbox"
                                  className="w-3 h-3"
                                  checked={checked}
                                  disabled={yaDescontada}
                                  onChange={() => {
                                    if (yaDescontada) {
                                      alert("Esta cuota ya fue descontada en nómina. No se puede modificar.");
                                      return;
                                    }
                                    togglePago(p.id, c.id);
                                  }}
                                />
                                <span>
                                  #{c.nro || 0} $
                                  {(c.valor || 0).toLocaleString(
                                    "es-CO"
                                  )}
                                </span>
                                {yaDescontada && (
                                  <span className="text-[9px] ml-1">
                                    (desc. nómina)
                                  </span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
 
                  {!prestamosConCuotasDisponibles.length && (
                    <div style={{
                      padding: 40,
                      textAlign: 'center',
                      color: '#64748b'
                    }}>
                      <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>No hay préstamos con cuotas disponibles</div>
                      <div style={{ fontSize: 12, marginTop: 4 }}>Todos los préstamos ya fueron pagados o no tienen cuotas pendientes.</div>
                    </div>
                  )}
                </div>
 
                {/* Footer con botones */}
                <div style={{
                  padding: '16px 24px',
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 12,
                  backgroundColor: '#f8fafc',
                  borderRadius: '0 0 16px 16px'
                }}>
                  <button
                    onClick={() => setModalCuotasOpen(false)}
                    style={{
                      padding: '10px 20px',
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#64748b',
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      setModalCuotasOpen(false);
                      alert("Las cuotas marcadas quedarán listas para ser descontadas al guardar la nómina de esta quincena.");
                    }}
                    style={{
                      padding: '10px 24px',
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'white',
                      background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      boxShadow: '0 4px 14px rgba(34, 197, 94, 0.4)',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(34, 197, 94, 0.5)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 14px rgba(34, 197, 94, 0.4)';
                    }}
                  >
                    <span>✓</span> Confirmar Selección
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de confirmación para eliminar préstamo */}
      {modalEliminarOpen && prestamoAEliminar && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: '#000000',
            opacity: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999
          }}
        >
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: 12,
            padding: 20,
            width: 500,
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div className="flex items-center gap-3 mb-4">
              <div style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24
              }}>
                🗑️
              </div>
              <div>
                <h3 className="text-lg font-bold text-red-700">
                  Eliminar Préstamo
                </h3>
                <p className="text-sm text-slate-600">
                  Esta acción es irreversible
                </p>
              </div>
            </div>

            {/* Información del préstamo a eliminar */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <div className="text-sm">
                <div className="font-semibold text-red-800">
                  {prestamoAEliminar.empleado?.nombre || "Empleado desconocido"}
                </div>
                <div className="text-red-700">
                  {prestamoAEliminar.tipo || "Préstamo"} - ${(prestamoAEliminar.valor || 0).toLocaleString("es-CO")}
                </div>
                <div className="text-red-600 text-xs mt-1">
                  Cuotas: {(prestamoAEliminar.plan || []).filter(c => c.noDescontable).length}/{prestamoAEliminar.cuotas || 0} descontadas
                  {" | "}
                  Saldo: ${((prestamoAEliminar.valor || 0) - (prestamoAEliminar.plan || []).filter(c => c.noDescontable).reduce((s, c) => s + (c.valor || 0), 0)).toLocaleString("es-CO")}
                </div>
              </div>
            </div>

            {/* Campo de motivo */}
            <form onSubmit={(e) => { e.preventDefault(); confirmarEliminacion(); }}>
            {/* Campo de usuario oculto para accesibilidad */}
            <input type="text" name="username" autoComplete="username" value={usuario?.email || ""} readOnly style={{ display: 'none' }} />
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Motivo de la eliminación *
              </label>
              <textarea
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                rows={2}
                placeholder="Explica por qué se elimina este préstamo..."
                value={motivoEliminacion}
                onChange={(e) => setMotivoEliminacion(e.target.value)}
              />
            </div>

            {/* Campo de contraseña */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Ingresa tu contraseña para confirmar *
              </label>
              <input
                type="password"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Tu contraseña de inicio de sesión"
                value={claveVerificacion}
                onChange={(e) => setClaveVerificacion(e.target.value)}
                autoComplete="current-password"
              />
            </div>

            {/* Mensaje de error */}
            {errorVerificacion && (
              <div className="bg-red-100 border border-red-300 text-red-700 px-3 py-2 rounded-lg mb-4 text-sm">
                ⚠️ {errorVerificacion}
              </div>
            )}

            {/* Botones */}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={cerrarModalEliminar}
                disabled={eliminando}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                style={{
                  backgroundColor: '#dc2626',
                  color: 'white',
                  opacity: eliminando ? 0.7 : 1
                }}
                disabled={eliminando}
              >
                {eliminando ? "Eliminando..." : "🗑️ Confirmar Eliminación"}
              </Button>
            </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de abono a crédito */}
      {modalAbonoOpen && prestamoAbonar && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: '#000000',
            opacity: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999
          }}
        >
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: 12,
            padding: 20,
            width: 550,
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div className="flex items-center gap-3 mb-4">
              <div style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#ecfdf5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24
              }}>
                💰
              </div>
              <div>
                <h3 className="text-lg font-bold text-emerald-700">
                  Abono a Crédito
                </h3>
                <p className="text-sm text-slate-600">
                  Solo disponible para perfil GERENCIA
                </p>
              </div>
            </div>

            {/* Información del préstamo */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
              <div className="text-sm">
                <div className="font-semibold text-emerald-800">
                  {prestamoAbonar.empleado?.nombre || "Empleado desconocido"}
                </div>
                <div className="text-emerald-700">
                  {prestamoAbonar.tipo || "Préstamo"} - Valor total: ${(prestamoAbonar.valor || 0).toLocaleString("es-CO")}
                </div>
                <div className="text-emerald-600 text-xs mt-1">
                  Cuotas: {(prestamoAbonar.plan || []).filter(c => c.noDescontable).length}/{prestamoAbonar.cuotas || (prestamoAbonar.plan || []).length || 0} pagadas
                  {" | "}
                  Saldo pendiente: ${((prestamoAbonar.valor || 0) - (prestamoAbonar.plan || []).filter(c => c.noDescontable).reduce((s, c) => s + (c.valor || 0), 0)).toLocaleString("es-CO")}
                </div>
              </div>
            </div>

            {/* Detalle de cuotas pendientes */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
              <div className="text-xs font-semibold text-slate-600 mb-2">Cuotas pendientes:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(prestamoAbonar.plan || []).filter(c => !c.noDescontable).map((c, idx) => (
                  <span key={idx} style={{
                    padding: '4px 8px',
                    borderRadius: 4,
                    backgroundColor: '#e0f2fe',
                    color: '#0369a1',
                    fontSize: 11,
                    fontWeight: 500
                  }}>
                    #{c.nro} - ${(c.valor || 0).toLocaleString("es-CO")}
                  </span>
                ))}
              </div>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); confirmarAbono(); }}>
              {/* Campo de usuario oculto para accesibilidad */}
              <input type="text" name="username" autoComplete="username" value={usuario?.email || ""} readOnly style={{ display: 'none' }} />
              
              {/* Campo de monto de abono */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Monto del abono *
                </label>
                <input
                  type="number"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Ingrese el monto a abonar"
                  value={montoAbono}
                  onChange={(e) => setMontoAbono(e.target.value)}
                  min="1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Las cuotas completas se liquidarán y el residuo (si hay) reducirá el valor de la siguiente cuota pendiente.
                </p>
              </div>

              {/* Campo de motivo */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Motivo del abono *
                </label>
                <textarea
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  rows={2}
                  placeholder="Explica el motivo de este abono..."
                  value={motivoAbono}
                  onChange={(e) => setMotivoAbono(e.target.value)}
                />
              </div>

              {/* Campo de contraseña */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Ingresa tu contraseña para confirmar *
                </label>
                <input
                  type="password"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Tu contraseña de inicio de sesión"
                  value={claveAbono}
                  onChange={(e) => setClaveAbono(e.target.value)}
                  autoComplete="current-password"
                />
              </div>

              {/* Mensaje de error */}
              {errorAbono && (
                <div className="bg-red-100 border border-red-300 text-red-700 px-3 py-2 rounded-lg mb-4 text-sm">
                  ⚠️ {errorAbono}
                </div>
              )}

              {/* Botones */}
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={cerrarModalAbono}
                  disabled={procesandoAbono}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  style={{
                    backgroundColor: '#059669',
                    color: 'white',
                    opacity: procesandoAbono ? 0.7 : 1
                  }}
                  disabled={procesandoAbono}
                >
                  {procesandoAbono ? "Procesando..." : "💰 Confirmar Abono"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de bitácora de novedades a crédito */}
      {modalBitacoraOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: '#000000',
            opacity: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999
          }}
        >
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: 12,
            padding: 20,
            width: 1000,
            maxWidth: '95vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: '#f0fdf4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 24
                }}>
                  📋
                </div>
                <div>
                  <h3 className="text-lg font-bold text-emerald-700">
                    Bitácora Novedades a Crédito
                  </h3>
                  <p className="text-sm text-slate-600">
                    Registro histórico de eliminaciones y abonos a préstamos
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => setModalBitacoraOpen(false)}
              >
                ✕ Cerrar
              </Button>
            </div>

            {/* Contenido de la bitácora */}
            <div className="flex-1 overflow-auto border rounded-lg">
              {cargandoBitacora ? (
                <div className="p-8 text-center text-slate-500">
                  <div className="text-2xl mb-2">⏳</div>
                  Cargando bitácora...
                </div>
              ) : bitacoraData.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <div className="text-2xl mb-2">📭</div>
                  No hay registros de novedades a crédito
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left border-b font-semibold">Fecha</th>
                      <th className="px-3 py-2 text-left border-b font-semibold">Tipo</th>
                      <th className="px-3 py-2 text-left border-b font-semibold">Empleado</th>
                      <th className="px-3 py-2 text-left border-b font-semibold">Préstamo</th>
                      <th className="px-3 py-2 text-right border-b font-semibold">Valor/Abono</th>
                      <th className="px-3 py-2 text-center border-b font-semibold">Cuotas</th>
                      <th className="px-3 py-2 text-left border-b font-semibold">Ejecutado por</th>
                      <th className="px-3 py-2 text-left border-b font-semibold">Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bitacoraData.map((registro, idx) => {
                      const esAbono = registro.estado_al_eliminar === "ABONO_CREDITO" || (registro.motivo_eliminacion || "").startsWith("[ABONO");
                      // Extraer monto del abono del motivo si es abono
                      let montoAbono = 0;
                      if (esAbono && registro.motivo_eliminacion) {
                        const match = registro.motivo_eliminacion.match(/\[ABONO \$?([\d.,]+)\]/);
                        if (match) {
                          montoAbono = parseFloat(match[1].replace(/\./g, "").replace(",", ".")) || 0;
                        }
                      }
                      return (
                        <tr key={registro.id || idx} className={`hover:bg-slate-50 border-b ${esAbono ? 'bg-emerald-50/30' : 'bg-red-50/30'}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium">
                              {registro.fecha_eliminacion ? 
                                new Date(registro.fecha_eliminacion).toLocaleDateString('es-CO') : '—'}
                            </div>
                            <div className="text-xs text-slate-500">
                              {registro.fecha_eliminacion ? 
                                new Date(registro.fecha_eliminacion).toLocaleTimeString('es-CO', {hour: '2-digit', minute: '2-digit'}) : ''}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: 6,
                              fontSize: 11,
                              fontWeight: 600,
                              backgroundColor: esAbono ? '#d1fae5' : '#fee2e2',
                              color: esAbono ? '#047857' : '#dc2626'
                            }}>
                              {esAbono ? '💰 ABONO' : '🗑️ ELIMINADO'}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-medium">{registro.empleado_nombre || '—'}</div>
                            <div className="text-xs text-slate-500">ID: {registro.empleado_id?.substring(0,8) || '—'}</div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-medium">{registro.tipo_prestamo || 'Préstamo'}</div>
                            <div className="text-xs text-slate-500">Total: ${(registro.valor || 0).toLocaleString('es-CO')}</div>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {esAbono ? (
                              <div>
                                <div className="font-bold text-emerald-700">
                                  +${montoAbono.toLocaleString('es-CO')}
                                </div>
                                <div className="text-xs text-slate-500">
                                  Saldo restante: ${(registro.saldo || 0).toLocaleString('es-CO')}
                                </div>
                              </div>
                            ) : (
                              <div className="font-medium text-red-700">
                                ${(registro.valor || 0).toLocaleString('es-CO')}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className="text-xs">
                              {registro.cuotas_descontadas || 0}/{registro.cuotas || 0}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <div className={`font-medium ${esAbono ? 'text-emerald-700' : 'text-red-700'}`}>
                              {registro.usuario_eliminador_nombre || '—'}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="max-w-[180px] truncate" title={registro.motivo_eliminacion || ''}>
                              {esAbono ? (registro.motivo_eliminacion || "").replace(/\[ABONO \$?[\d.,]+\]\s*/, "") : (registro.motivo_eliminacion || '—')}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mt-4 flex justify-between items-center text-xs text-slate-500">
              <div>
                Total registros: {bitacoraData.length}
                {bitacoraData.length > 0 && (
                  <span className="ml-4">
                    (💰 Abonos: {bitacoraData.filter(r => r.estado_al_eliminar === 'ABONO_CREDITO' || (r.motivo_eliminacion || '').startsWith('[ABONO')).length} | 
                    🗑️ Eliminados: {bitacoraData.filter(r => r.estado_al_eliminar !== 'ABONO_CREDITO' && !(r.motivo_eliminacion || '').startsWith('[ABONO')).length})
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
 
/* ====================== HORARIOS (actualizado) ====================== */
function mondayOf(iso) {
  const d = parseISO(iso);
  const day = d.getDay(); // 0=Dom, 1=Lun
  const delta = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + delta);
  return toISO(d);
}
function sundayOf(iso) {
  const mon = parseISO(mondayOf(iso));
  const s = new Date(mon);
  s.setDate(s.getDate() + 6);
  return toISO(s);
}
const DIAS = ["Lunes","Martes","Miércoles","Jueves","Viernes","Sábado","Domingo"];
function dayIndexMon0(dateIso){ // L=0..D=6
  const d = parseISO(dateIso);
  const js = d.getDay();
  return (js + 6) % 7;
}
function isHolidayOrSundayCO(iso, cache){
  return parseISO(iso).getDay() === 0 || isHolidayCO(iso, cache);
}
 
const EVENTOS_BASE = [
  { id:"CONGELADOR", nombre:"CONGELADOR", color:"#fff3a6" },
  { id:"CAMPANA", nombre:"CAMPANA", color:"#d1f4ff" },
  { id:"REFRIGERADOR", nombre:"REFRIGERADOR", color:"#b3e5fc" },
  { id:"ASEO", nombre:"ASEO", color:"#e8ffe1" },
  { id:"INVENTARIO", nombre:"INVENTARIO", color:"#f9d6ff" },
  { id:"REUNION", nombre:"REUNIÓN", color:"#ffe0db" },
];

// ====================== ANÁLISIS COMPARATIVO DE HORARIOS ======================
function AnalisisComparativoHorarios({ 
  empleados, 
  horariosRepo, 
  marcacionesReales, 
  cargarMarcacionesReales,
  fechasSemana,
  semanaInicio,
  semanaFin,
  sedes
}) {
  const [fechaDesde, setFechaDesde] = React.useState(semanaInicio);
  const [fechaHasta, setFechaHasta] = React.useState(semanaFin);
  const [marcaciones, setMarcaciones] = React.useState([]);
  const [cargando, setCargando] = React.useState(false);
  const [vistaAnalisis, setVistaAnalisis] = React.useState("individual"); // "individual" | "grupal" | "resumen"
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = React.useState("");

  // Función para convertir hora 12h a minutos desde medianoche
  function horaAMinutos(horaStr) {
    if (!horaStr || horaStr === '-') return null;
    const texto = horaStr.toString().trim().toUpperCase();
    // Soporta: "12:00 PM", "12:00PM", "12 PM", "12PM", "8:30", "08:30"
    const match = texto.match(/^(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)?$/);
    if (!match) return null;
    let [, h, m, periodo] = match;
    let hh = parseInt(h, 10);
    const mm = parseInt(m || "0", 10);
    if (periodo === "PM" && hh !== 12) hh += 12;
    if (periodo === "AM" && hh === 12) hh = 0;
    return hh * 60 + mm;
  }

  // Función para formatear diferencia en minutos
  function formatearDiferencia(minutos) {
    if (minutos === null || minutos === undefined) return "-";
    const signo = minutos >= 0 ? "+" : "";
    const absMin = Math.abs(minutos);
    const horas = Math.floor(absMin / 60);
    const mins = absMin % 60;
    if (horas > 0) {
      return `${signo}${horas}h ${mins}m`;
    }
    return `${signo}${mins}m`;
  }

  // Función para describir el estado detallado
  function obtenerEstadoDetallado(dia) {
    if (dia.estado === 'falta') return { texto: '❌ FALTA', color: '#dc2626', bg: '#fef2f2' };
    if (dia.estado === 'descanso') return { texto: '😴 DESCANSO', color: '#6b7280', bg: '#f5f5f5' };
    if (dia.estado === 'sin_programar') return { texto: '⚠️ SIN PROG.', color: '#d97706', bg: '#fffbeb' };
    if (dia.estado !== 'presente') return { texto: '-', color: '#64748b', bg: 'white' };
    
    const estados = [];
    
    // Analizar E1
    if (dia.diferencias.entrada1 !== null) {
      if (dia.diferencias.entrada1 > 5) estados.push('E1 Tarde');
      else if (dia.diferencias.entrada1 < -5) estados.push('E1 Temprano');
      else estados.push('E1 OK');
    }
    
    // Analizar S1
    if (dia.diferencias.salida1 !== null) {
      if (dia.diferencias.salida1 < -5) estados.push('S1 Temprano');
      else if (dia.diferencias.salida1 > 5) estados.push('S1 Extra');
      else estados.push('S1 OK');
    }
    
    // Analizar E2 (solo si aplica)
    if (dia.diferencias.entrada2 !== null) {
      if (dia.diferencias.entrada2 > 5) estados.push('E2 Tarde');
      else if (dia.diferencias.entrada2 < -5) estados.push('E2 Temprano');
      else estados.push('E2 OK');
    }
    
    // Analizar S2 (solo si aplica)
    if (dia.diferencias.salida2 !== null) {
      if (dia.diferencias.salida2 < -5) estados.push('S2 Temprano');
      else if (dia.diferencias.salida2 > 5) estados.push('S2 Extra');
      else estados.push('S2 OK');
    }
    
    const tieneProblemas = estados.some(e => e.includes('Tarde') || e.includes('Temprano'));
    const todoOK = estados.every(e => e.includes('OK'));
    
    if (todoOK) {
      return { texto: '✅ PUNTUAL', color: '#16a34a', bg: '#f0fdf4' };
    } else if (tieneProblemas) {
      return { texto: estados.filter(e => !e.includes('OK')).join(' | '), color: '#d97706', bg: '#fffbeb' };
    }
    return { texto: '✅ OK', color: '#16a34a', bg: '#f0fdf4' };
  }

  // Cargar marcaciones al cambiar fechas
  async function cargarDatos() {
    setCargando(true);
    try {
      const data = await cargarMarcacionesReales(null, {
        fechaDesde: fechaDesde,
        fechaHasta: fechaHasta
      });
      setMarcaciones(data);
    } catch (e) {
      console.error("Error cargando marcaciones:", e);
    }
    setCargando(false);
  }

  React.useEffect(() => {
    cargarDatos();
  }, []);

  // Obtener horario programado para un empleado en una fecha
  function obtenerHorarioProgramado(empleadoId, fechaISO) {
    const lunes = mondayOf(fechaISO);
    const domingo = sundayOf(lunes);
    const horario = horariosRepo.find(h => h.semanaInicio === lunes && h.semanaFin === domingo);
    if (!horario || !horario.celdas) return null;
    
    const celdaEmpleado = horario.celdas[empleadoId];
    if (!celdaEmpleado) return null;
    
    const diaIdx = dayIndexMon0(fechaISO);
    const celda = celdaEmpleado[diaIdx];
    
    return celda || null;
  }

  // Analizar un empleado - MEJORADO
  function analizarEmpleado(emp) {
    const marcacionesEmp = marcaciones.filter(m => m.empleadoId === emp.id);
    const analisis = [];
    
    let fecha = parseISO(fechaDesde);
    const fin = parseISO(fechaHasta);
    
    while (fecha <= fin) {
      const fechaISO = toISO(fecha);
      const marcacion = marcacionesEmp.find(m => m.fecha === fechaISO);
      const programado = obtenerHorarioProgramado(emp.id, fechaISO);
      
      let estado = "sin_datos";
      let diferencias = { entrada1: null, salida1: null, entrada2: null, salida2: null };
      let minutosLlegadaTarde = 0;
      let minutosSalidaTemprana = 0;
      let detallesEstado = { e1: 'N/A', s1: 'N/A', e2: 'N/A', s2: 'N/A' };
      
      if (programado && programado.tipo && programado.tipo !== "DESCANSO" && programado.tipo !== "NO_TRABAJO") {
        if (!marcacion) {
          estado = "falta";
        } else {
          estado = "presente";
          
          // Comparar E1
          const progE1 = horaAMinutos(programado.e1);
          const realE1 = horaAMinutos(marcacion.entrada1);
          if (progE1 !== null && realE1 !== null) {
            diferencias.entrada1 = realE1 - progE1;
            if (diferencias.entrada1 > 0) {
              minutosLlegadaTarde += diferencias.entrada1;
              detallesEstado.e1 = 'TARDE';
            } else if (diferencias.entrada1 < 0) {
              detallesEstado.e1 = 'TEMPRANO';
            } else {
              detallesEstado.e1 = 'PUNTUAL';
            }
          } else if (progE1 !== null && realE1 === null) {
            detallesEstado.e1 = 'SIN MARCAR';
          }
          
          // Comparar S1
          const progS1 = horaAMinutos(programado.s1);
          const realS1 = horaAMinutos(marcacion.salida1);
          if (progS1 !== null && realS1 !== null) {
            diferencias.salida1 = realS1 - progS1;
            if (diferencias.salida1 < 0) {
              minutosSalidaTemprana += Math.abs(diferencias.salida1);
              detallesEstado.s1 = 'TEMPRANO';
            } else if (diferencias.salida1 > 0) {
              detallesEstado.s1 = 'EXTRA';
            } else {
              detallesEstado.s1 = 'NORMAL';
            }
          } else if (progS1 !== null && realS1 === null) {
            detallesEstado.s1 = 'SIN MARCAR';
          }
          
          // Comparar E2 y S2 (solo si es PARTIDO)
          if (programado.tipo === "PARTIDO") {
            const progE2 = horaAMinutos(programado.e2);
            const realE2 = horaAMinutos(marcacion.entrada2);
            if (progE2 !== null && realE2 !== null) {
              diferencias.entrada2 = realE2 - progE2;
              if (diferencias.entrada2 > 0) {
                minutosLlegadaTarde += diferencias.entrada2;
                detallesEstado.e2 = 'TARDE';
              } else if (diferencias.entrada2 < 0) {
                detallesEstado.e2 = 'TEMPRANO';
              } else {
                detallesEstado.e2 = 'PUNTUAL';
              }
            } else if (progE2 !== null && realE2 === null) {
              detallesEstado.e2 = 'SIN MARCAR';
            }
            
            const progS2 = horaAMinutos(programado.s2);
            const realS2 = horaAMinutos(marcacion.salida2);
            if (progS2 !== null && realS2 !== null) {
              diferencias.salida2 = realS2 - progS2;
              if (diferencias.salida2 < 0) {
                minutosSalidaTemprana += Math.abs(diferencias.salida2);
                detallesEstado.s2 = 'TEMPRANO';
              } else if (diferencias.salida2 > 0) {
                detallesEstado.s2 = 'EXTRA';
              } else {
                detallesEstado.s2 = 'NORMAL';
              }
            } else if (progS2 !== null && realS2 === null) {
              detallesEstado.s2 = 'SIN MARCAR';
            }
          }
        }
      } else if (programado && (programado.tipo === "DESCANSO" || programado.tipo === "NO_TRABAJO")) {
        estado = "descanso";
      } else if (!programado && marcacion) {
        estado = "sin_programar";
      }
      
      analisis.push({
        fecha: fechaISO,
        diaSemana: DIAS[dayIndexMon0(fechaISO)],
        diaIdx: dayIndexMon0(fechaISO),
        programado,
        marcacion,
        estado,
        diferencias,
        detallesEstado,
        minutosLlegadaTarde,
        minutosSalidaTemprana,
        totalMinutosPerdidos: minutosLlegadaTarde + minutosSalidaTemprana
      });
      
      fecha.setDate(fecha.getDate() + 1);
    }
    
    return analisis;
  }

  // Calcular estadísticas de un empleado
  function calcularEstadisticasEmpleado(analisis) {
    const stats = {
      diasTrabajados: 0,
      diasPuntuales: 0,
      faltas: 0,
      llegadasTarde: 0,
      salidasTempranas: 0,
      minutosLlegadaTarde: 0,
      minutosSalidaTemprana: 0,
      totalMinutosPerdidos: 0,
      diasConProblemas: [],
      diasPorSemana: { Lunes: 0, Martes: 0, Miércoles: 0, Jueves: 0, Viernes: 0, Sábado: 0, Domingo: 0 },
      llegadasTardePorDia: { Lunes: 0, Martes: 0, Miércoles: 0, Jueves: 0, Viernes: 0, Sábado: 0, Domingo: 0 },
      // NUEVO: Estadísticas de horarios incompletos
      diasIncompletos: 0,
      diasIncompletosList: [],
      marcacionesSinE1: 0,
      marcacionesSinS1: 0,
      marcacionesSinE2: 0,
      marcacionesSinS2: 0,
      porcentajeCompletitud: 100
    };
    
    let totalMarcacionesRequeridas = 0;
    let marcacionesFaltantes = 0;
    
    analisis.forEach(dia => {
      if (dia.estado === "presente") {
        stats.diasTrabajados++;
        stats.diasPorSemana[dia.diaSemana]++;
        
        const tieneProblema = dia.minutosLlegadaTarde > 0 || dia.minutosSalidaTemprana > 0;
        
        // NUEVO: Verificar completitud de marcaciones
        let esIncompleto = false;
        const prog = dia.programado;
        const marc = dia.marcacion;
        
        if (prog && marc) {
          // Verificar E1 y S1 (siempre requeridos si hay horario)
          if (prog.e1 && (!marc.entrada1 || marc.entrada1 === '-')) {
            stats.marcacionesSinE1++;
            esIncompleto = true;
            marcacionesFaltantes++;
          }
          if (prog.e1) totalMarcacionesRequeridas++;
          
          if (prog.s1 && (!marc.salida1 || marc.salida1 === '-')) {
            stats.marcacionesSinS1++;
            esIncompleto = true;
            marcacionesFaltantes++;
          }
          if (prog.s1) totalMarcacionesRequeridas++;
          
          // Verificar E2 y S2 (solo si es PARTIDO)
          if (prog.tipo === "PARTIDO") {
            if (prog.e2 && (!marc.entrada2 || marc.entrada2 === '-')) {
              stats.marcacionesSinE2++;
              esIncompleto = true;
              marcacionesFaltantes++;
            }
            if (prog.e2) totalMarcacionesRequeridas++;
            
            if (prog.s2 && (!marc.salida2 || marc.salida2 === '-')) {
              stats.marcacionesSinS2++;
              esIncompleto = true;
              marcacionesFaltantes++;
            }
            if (prog.s2) totalMarcacionesRequeridas++;
          }
        }
        
        if (esIncompleto) {
          stats.diasIncompletos++;
          stats.diasIncompletosList.push({
            fecha: dia.fecha,
            diaSemana: dia.diaSemana,
            faltantes: {
              e1: prog.e1 && (!marc.entrada1 || marc.entrada1 === '-'),
              s1: prog.s1 && (!marc.salida1 || marc.salida1 === '-'),
              e2: prog.tipo === "PARTIDO" && prog.e2 && (!marc.entrada2 || marc.entrada2 === '-'),
              s2: prog.tipo === "PARTIDO" && prog.s2 && (!marc.salida2 || marc.salida2 === '-')
            }
          });
        }
        
        if (tieneProblema) {
          stats.diasConProblemas.push(dia);
          if (dia.minutosLlegadaTarde > 0) {
            stats.llegadasTarde++;
            stats.minutosLlegadaTarde += dia.minutosLlegadaTarde;
            stats.llegadasTardePorDia[dia.diaSemana]++;
          }
          if (dia.minutosSalidaTemprana > 0) {
            stats.salidasTempranas++;
            stats.minutosSalidaTemprana += dia.minutosSalidaTemprana;
          }
        } else if (!esIncompleto) {
          stats.diasPuntuales++;
        }
        
        stats.totalMinutosPerdidos += dia.totalMinutosPerdidos;
      } else if (dia.estado === "falta") {
        stats.faltas++;
      }
    });
    
    // Calcular porcentaje de completitud
    if (totalMarcacionesRequeridas > 0) {
      stats.porcentajeCompletitud = Math.round(((totalMarcacionesRequeridas - marcacionesFaltantes) / totalMarcacionesRequeridas) * 100);
    }
    
    // Calcular día con más llegadas tarde
    const diasOrdenados = Object.entries(stats.llegadasTardePorDia)
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1]);
    stats.diaConMasTardanzas = diasOrdenados.length > 0 ? diasOrdenados[0][0] : null;
    
    // Porcentaje de puntualidad
    stats.porcentajePuntualidad = stats.diasTrabajados > 0 
      ? Math.round((stats.diasPuntuales / stats.diasTrabajados) * 100) 
      : 0;
    
    // NUEVO: Calcular score de riesgo (para ranking y toma de decisiones)
    stats.scoreRiesgo = (stats.faltas * 100) + (stats.diasIncompletos * 50) + (stats.llegadasTarde * 30) + 
                        (stats.salidasTempranas * 20) + Math.round(stats.totalMinutosPerdidos / 10);
    
    return stats;
  }

  // Calcular resumen grupal mejorado
  function calcularResumenGrupal() {
    const resumen = {
      totalEmpleados: empleados.length,
      totalDias: 0,
      totalFaltas: 0,
      totalLlegadasTarde: 0,
      totalMinutosLlegadaTarde: 0,
      totalSalidasTempranas: 0,
      totalMinutosSalidaTemprana: 0,
      empleadosConProblemas: [],
      empleadosPuntuales: [],
      porEmpleado: {},
      llegadasTardePorDia: { Lunes: 0, Martes: 0, Miércoles: 0, Jueves: 0, Viernes: 0, Sábado: 0, Domingo: 0 },
      // NUEVO: Estadísticas de horarios incompletos
      totalDiasIncompletos: 0,
      totalMarcacionesSinE1: 0,
      totalMarcacionesSinS1: 0,
      totalMarcacionesSinE2: 0,
      totalMarcacionesSinS2: 0,
      empleadosConIncompletos: [],
      rankingProblematicos: [],
      // NUEVO: Para análisis y toma de decisiones
      indicadores: {
        nivelCritico: [],     // Empleados críticos (score > 300)
        nivelAlerta: [],      // Empleados en alerta (score 150-300)
        nivelObservacion: [], // Empleados en observación (score 50-150)
        nivelOptimo: []       // Empleados óptimos (score < 50)
      },
      recomendaciones: []
    };
    
    empleados.forEach(emp => {
      const analisis = analizarEmpleado(emp);
      const stats = calcularEstadisticasEmpleado(analisis);
      
      const empResumen = {
        id: emp.id,
        nombre: emp.nombre,
        documento: emp.documento,
        sede: emp.sede || 'Sin sede',
        cargo: emp.cargo || 'Sin cargo',
        ...stats
      };
      
      resumen.totalFaltas += stats.faltas;
      resumen.totalLlegadasTarde += stats.llegadasTarde;
      resumen.totalMinutosLlegadaTarde += stats.minutosLlegadaTarde;
      resumen.totalSalidasTempranas += stats.salidasTempranas;
      resumen.totalMinutosSalidaTemprana += stats.minutosSalidaTemprana;
      
      // NUEVO: Sumar estadísticas de incompletos
      resumen.totalDiasIncompletos += stats.diasIncompletos;
      resumen.totalMarcacionesSinE1 += stats.marcacionesSinE1;
      resumen.totalMarcacionesSinS1 += stats.marcacionesSinS1;
      resumen.totalMarcacionesSinE2 += stats.marcacionesSinE2;
      resumen.totalMarcacionesSinS2 += stats.marcacionesSinS2;
      
      // Sumar llegadas tarde por día
      Object.keys(stats.llegadasTardePorDia).forEach(dia => {
        resumen.llegadasTardePorDia[dia] += stats.llegadasTardePorDia[dia];
      });
      
      // NUEVO: Clasificar por nivel de riesgo para toma de decisiones
      if (stats.scoreRiesgo >= 300) {
        resumen.indicadores.nivelCritico.push(empResumen);
      } else if (stats.scoreRiesgo >= 150) {
        resumen.indicadores.nivelAlerta.push(empResumen);
      } else if (stats.scoreRiesgo >= 50) {
        resumen.indicadores.nivelObservacion.push(empResumen);
      } else {
        resumen.indicadores.nivelOptimo.push(empResumen);
      }
      
      if (stats.faltas > 0 || stats.llegadasTarde > 0 || stats.salidasTempranas > 0) {
        resumen.empleadosConProblemas.push(empResumen);
      } else if (stats.diasTrabajados > 0 && stats.diasIncompletos === 0) {
        resumen.empleadosPuntuales.push(empResumen);
      }
      
      // NUEVO: Empleados con horarios incompletos
      if (stats.diasIncompletos > 0) {
        resumen.empleadosConIncompletos.push(empResumen);
      }
      
      resumen.porEmpleado[emp.id] = empResumen;
    });
    
    // Ordenar por más problemas
    resumen.empleadosConProblemas.sort((a, b) => 
      (b.totalMinutosPerdidos + b.faltas * 480) - (a.totalMinutosPerdidos + a.faltas * 480)
    );
    
    // NUEVO: Ordenar empleados con incompletos por cantidad de días
    resumen.empleadosConIncompletos.sort((a, b) => b.diasIncompletos - a.diasIncompletos);
    
    // NUEVO: Crear ranking general de empleados más problemáticos (top 10)
    const todosEmpleados = Object.values(resumen.porEmpleado);
    resumen.rankingProblematicos = todosEmpleados
      .filter(e => e.scoreRiesgo > 0)
      .sort((a, b) => b.scoreRiesgo - a.scoreRiesgo)
      .slice(0, 10);
    
    // Ordenar niveles por score
    resumen.indicadores.nivelCritico.sort((a, b) => b.scoreRiesgo - a.scoreRiesgo);
    resumen.indicadores.nivelAlerta.sort((a, b) => b.scoreRiesgo - a.scoreRiesgo);
    resumen.indicadores.nivelObservacion.sort((a, b) => b.scoreRiesgo - a.scoreRiesgo);
    
    // Día con más llegadas tarde global
    const diasOrdenados = Object.entries(resumen.llegadasTardePorDia)
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1]);
    resumen.diaConMasTardanzas = diasOrdenados.length > 0 ? diasOrdenados[0] : null;
    
    // NUEVO: Generar recomendaciones automáticas
    if (resumen.indicadores.nivelCritico.length > 0) {
      resumen.recomendaciones.push({
        tipo: 'critico',
        icono: '🚨',
        titulo: 'Atención Inmediata Requerida',
        mensaje: `${resumen.indicadores.nivelCritico.length} empleado(s) requieren atención inmediata por alto nivel de incidencias.`,
        accion: 'Revisar casos y programar reuniones de seguimiento'
      });
    }
    
    if (resumen.totalDiasIncompletos > 5) {
      resumen.recomendaciones.push({
        tipo: 'alerta',
        icono: '⚠️',
        titulo: 'Problemas de Marcación Detectados',
        mensaje: `Se detectaron ${resumen.totalDiasIncompletos} días con marcaciones incompletas. Posible problema con biométrico o disciplina.`,
        accion: 'Verificar funcionamiento del biométrico y recordar procedimiento a empleados'
      });
    }
    
    if (resumen.diaConMasTardanzas && resumen.diaConMasTardanzas[1] > 5) {
      resumen.recomendaciones.push({
        tipo: 'patron',
        icono: '📊',
        titulo: 'Patrón de Tardanzas Identificado',
        mensaje: `Los ${resumen.diaConMasTardanzas[0]} son el día con más tardanzas (${resumen.diaConMasTardanzas[1]} incidencias).`,
        accion: 'Investigar causa y considerar ajuste de horarios o refuerzo de control'
      });
    }
    
    if (resumen.empleadosPuntuales.length > resumen.totalEmpleados * 0.7) {
      resumen.recomendaciones.push({
        tipo: 'positivo',
        icono: '🎉',
        titulo: 'Buen Desempeño General',
        mensaje: `El ${Math.round((resumen.empleadosPuntuales.length / resumen.totalEmpleados) * 100)}% de empleados mantienen puntualidad óptima.`,
        accion: 'Considerar reconocimiento o incentivos para mantener la tendencia'
      });
    }
    
    return resumen;
  }

  const resumenGrupal = React.useMemo(() => calcularResumenGrupal(), [marcaciones, empleados, fechaDesde, fechaHasta]);

  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      padding: 20,
      border: '2px solid #e9ecef',
      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
    }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>
          📊 Análisis Comparativo de Horarios
        </h3>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
          Compara horarios programados vs marcaciones reales (E1, S1, E2, S2)
        </p>
      </div>

      {/* Filtros */}
      <div style={{
        display: 'flex',
        gap: 12,
        marginBottom: 20,
        padding: 16,
        background: '#f8fafc',
        borderRadius: 10,
        flexWrap: 'wrap',
        alignItems: 'flex-end'
      }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Desde</label>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Hasta</label>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
          />
        </div>
        <button
          onClick={cargarDatos}
          disabled={cargando}
          style={{
            background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: cargando ? 'not-allowed' : 'pointer',
            opacity: cargando ? 0.7 : 1
          }}
        >
          {cargando ? '⏳ Cargando...' : '🔍 Analizar'}
        </button>
      </div>

      {/* Tabs de vista */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {[
          { id: 'resumen', label: '📈 Resumen General' },
          { id: 'grupal', label: '👥 Vista Grupal' },
          { id: 'individual', label: '👤 Vista Individual' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setVistaAnalisis(tab.id)}
            style={{
              padding: '10px 16px',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              background: vistaAnalisis === tab.id ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' : '#f1f5f9',
              color: vistaAnalisis === tab.id ? 'white' : '#64748b',
              transition: 'all 0.2s ease'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {cargando ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
          <p>Cargando datos de análisis...</p>
        </div>
      ) : (
        <>
          {/* Vista: Resumen General */}
          {vistaAnalisis === "resumen" && (
            <div>
              {/* Cards de resumen principales */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
                <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', borderRadius: 12, padding: 16, border: '1px solid #bfdbfe' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#1e40af' }}>{resumenGrupal.totalEmpleados}</div>
                  <div style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600 }}>👥 Empleados</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderRadius: 12, padding: 16, border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#16a34a' }}>{resumenGrupal.empleadosPuntuales.length}</div>
                  <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 600 }}>✅ Puntuales</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', borderRadius: 12, padding: 16, border: '1px solid #fecaca' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#dc2626' }}>{resumenGrupal.totalFaltas}</div>
                  <div style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>❌ Faltas</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', borderRadius: 12, padding: 16, border: '1px solid #fcd34d' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#d97706' }}>{resumenGrupal.totalLlegadasTarde}</div>
                  <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>⏰ Llegadas Tarde</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)', borderRadius: 12, padding: 16, border: '1px solid #e879f9' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#a21caf' }}>{resumenGrupal.totalSalidasTempranas}</div>
                  <div style={{ fontSize: 11, color: '#c026d3', fontWeight: 600 }}>🚪 Salidas Temp.</div>
                </div>
              </div>

              {/* NUEVO: Análisis Separado de Minutos - Llegadas Tarde vs Salidas Tempranas */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 24 }}>
                {/* Card de Llegadas Tarde */}
                <div style={{ 
                  background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', 
                  borderRadius: 16, 
                  padding: 20, 
                  border: '2px solid #dc2626',
                  boxShadow: '0 4px 12px rgba(220, 38, 38, 0.15)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%', 
                      background: '#dc2626', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontSize: 24
                    }}>
                      ⏰
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#dc2626' }}>LLEGADAS TARDE</div>
                      <div style={{ fontSize: 11, color: '#ef4444' }}>Minutos de entrada después de la hora programada</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <div>
                      <div style={{ fontSize: 36, fontWeight: 800, color: '#b91c1c' }}>
                        {resumenGrupal.totalMinutosLlegadaTarde}
                      </div>
                      <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>minutos totales</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#7f1d1d' }}>
                        {Math.floor(resumenGrupal.totalMinutosLlegadaTarde / 60)}h {resumenGrupal.totalMinutosLlegadaTarde % 60}m
                      </div>
                      <div style={{ fontSize: 11, color: '#ef4444' }}>equivalente en horas</div>
                    </div>
                  </div>
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #fecaca' }}>
                    <div style={{ fontSize: 11, color: '#991b1b' }}>
                      📊 Promedio por empleado con tardanza: <strong>
                        {resumenGrupal.totalLlegadasTarde > 0 
                          ? Math.round(resumenGrupal.totalMinutosLlegadaTarde / resumenGrupal.totalLlegadasTarde) 
                          : 0}m
                      </strong>
                    </div>
                  </div>
                </div>

                {/* Card de Salidas Tempranas */}
                <div style={{ 
                  background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', 
                  borderRadius: 16, 
                  padding: 20, 
                  border: '2px solid #d97706',
                  boxShadow: '0 4px 12px rgba(217, 119, 6, 0.15)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '50%', 
                      background: '#d97706', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontSize: 24
                    }}>
                      🚪
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#d97706' }}>SALIDAS TEMPRANAS</div>
                      <div style={{ fontSize: 11, color: '#f59e0b' }}>Minutos de salida antes de la hora programada</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <div>
                      <div style={{ fontSize: 36, fontWeight: 800, color: '#92400e' }}>
                        {resumenGrupal.totalMinutosSalidaTemprana}
                      </div>
                      <div style={{ fontSize: 12, color: '#d97706', fontWeight: 600 }}>minutos totales</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: '#78350f' }}>
                        {Math.floor(resumenGrupal.totalMinutosSalidaTemprana / 60)}h {resumenGrupal.totalMinutosSalidaTemprana % 60}m
                      </div>
                      <div style={{ fontSize: 11, color: '#f59e0b' }}>equivalente en horas</div>
                    </div>
                  </div>
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #fcd34d' }}>
                    <div style={{ fontSize: 11, color: '#92400e' }}>
                      📊 Promedio por caso: <strong>
                        {resumenGrupal.totalSalidasTempranas > 0 
                          ? Math.round(resumenGrupal.totalMinutosSalidaTemprana / resumenGrupal.totalSalidasTempranas) 
                          : 0}m
                      </strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* Minutos perdidos totales COMBINADO */}
              <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #374151 100%)', borderRadius: 16, padding: 20, marginBottom: 20, color: 'white' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 14, color: '#fbbf24', fontWeight: 700, marginBottom: 4 }}>⏱️ TOTAL TIEMPO PERDIDO (COMBINADO)</div>
                    <div style={{ fontSize: 32, fontWeight: 800, color: 'white' }}>
                      {Math.floor((resumenGrupal.totalMinutosLlegadaTarde + resumenGrupal.totalMinutosSalidaTemprana) / 60)}h {(resumenGrupal.totalMinutosLlegadaTarde + resumenGrupal.totalMinutosSalidaTemprana) % 60}m
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>
                      = {resumenGrupal.totalMinutosLlegadaTarde + resumenGrupal.totalMinutosSalidaTemprana} minutos totales
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 20 }}>
                    <div style={{ textAlign: 'center', padding: '12px 20px', background: 'rgba(220, 38, 38, 0.2)', borderRadius: 10 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#fca5a5' }}>
                        {Math.floor(resumenGrupal.totalMinutosLlegadaTarde / 60)}h {resumenGrupal.totalMinutosLlegadaTarde % 60}m
                      </div>
                      <div style={{ fontSize: 10, color: '#fecaca' }}>🔴 Llegadas Tarde</div>
                    </div>
                    <div style={{ textAlign: 'center', padding: '12px 20px', background: 'rgba(217, 119, 6, 0.2)', borderRadius: 10 }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#fcd34d' }}>
                        {Math.floor(resumenGrupal.totalMinutosSalidaTemprana / 60)}h {resumenGrupal.totalMinutosSalidaTemprana % 60}m
                      </div>
                      <div style={{ fontSize: 10, color: '#fef3c7' }}>🟡 Salidas Temprana</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Análisis por día de la semana */}
              <div style={{ background: '#f8fafc', borderRadius: 12, padding: 16, marginBottom: 20, border: '1px solid #e2e8f0' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: '#374151' }}>
                  📅 Llegadas Tarde por Día de la Semana
                </h4>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Object.entries(resumenGrupal.llegadasTardePorDia).map(([dia, count]) => (
                    <div key={dia} style={{
                      flex: 1,
                      minWidth: 80,
                      padding: 12,
                      borderRadius: 8,
                      textAlign: 'center',
                      background: count > 0 ? '#fffbeb' : '#f0fdf4',
                      border: count > 0 ? '1px solid #fcd34d' : '1px solid #bbf7d0'
                    }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: count > 0 ? '#d97706' : '#16a34a' }}>{count}</div>
                      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{dia.substring(0, 3)}</div>
                    </div>
                  ))}
                </div>
                {resumenGrupal.diaConMasTardanzas && (
                  <div style={{ marginTop: 12, padding: 10, background: '#fffbeb', borderRadius: 8, border: '1px solid #fcd34d' }}>
                    <span style={{ fontSize: 12, color: '#92400e' }}>
                      ⚠️ El día con más llegadas tarde es <strong>{resumenGrupal.diaConMasTardanzas[0]}</strong> con <strong>{resumenGrupal.diaConMasTardanzas[1]}</strong> incidencias
                    </span>
                  </div>
                )}
                {!resumenGrupal.diaConMasTardanzas && resumenGrupal.empleadosPuntuales.length > 0 && (
                  <div style={{ marginTop: 12, padding: 10, background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                    <span style={{ fontSize: 12, color: '#16a34a' }}>
                      🎉 ¡Excelente! No hay llegadas tarde registradas en el período
                    </span>
                  </div>
                )}
              </div>

              {/* Top empleados con problemas */}
              {resumenGrupal.empleadosConProblemas.length > 0 && (
                <div style={{ background: '#fff7ed', borderRadius: 12, padding: 16, border: '1px solid #fed7aa', marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#c2410c' }}>
                    ⚠️ Empleados con Novedades ({resumenGrupal.empleadosConProblemas.length})
                  </h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: '#ffedd5' }}>
                        <th style={{ padding: 8, textAlign: 'left', fontWeight: 700 }}>Empleado</th>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>Faltas</th>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>Tardanzas</th>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>Min. Tarde</th>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>Sal. Temp.</th>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>Min. Temp.</th>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>Total Perdido</th>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>Día Crítico</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumenGrupal.empleadosConProblemas.slice(0, 10).map((emp, idx) => (
                        <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#fffbeb' }}>
                          <td style={{ padding: 8, fontWeight: 500 }}>{emp.nombre}</td>
                          <td style={{ padding: 8, textAlign: 'center', color: emp.faltas > 0 ? '#dc2626' : '#16a34a', fontWeight: 700 }}>
                            {emp.faltas}
                          </td>
                          <td style={{ padding: 8, textAlign: 'center', color: emp.llegadasTarde > 0 ? '#d97706' : '#16a34a', fontWeight: 700 }}>
                            {emp.llegadasTarde}
                          </td>
                          <td style={{ padding: 8, textAlign: 'center', color: '#64748b' }}>
                            {emp.minutosLlegadaTarde > 0 ? `${Math.floor(emp.minutosLlegadaTarde / 60)}h ${emp.minutosLlegadaTarde % 60}m` : '-'}
                          </td>
                          <td style={{ padding: 8, textAlign: 'center', color: emp.salidasTempranas > 0 ? '#a21caf' : '#16a34a', fontWeight: 700 }}>
                            {emp.salidasTempranas}
                          </td>
                          <td style={{ padding: 8, textAlign: 'center', color: '#64748b' }}>
                            {emp.minutosSalidaTemprana > 0 ? `${Math.floor(emp.minutosSalidaTemprana / 60)}h ${emp.minutosSalidaTemprana % 60}m` : '-'}
                          </td>
                          <td style={{ padding: 8, textAlign: 'center', color: '#dc2626', fontWeight: 700 }}>
                            {`${Math.floor(emp.totalMinutosPerdidos / 60)}h ${emp.totalMinutosPerdidos % 60}m`}
                          </td>
                          <td style={{ padding: 8, textAlign: 'center', fontSize: 10, color: '#64748b' }}>
                            {emp.diaConMasTardanzas || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Empleados puntuales */}
              {resumenGrupal.empleadosPuntuales.length > 0 && (
                <div style={{ background: '#f0fdf4', borderRadius: 12, padding: 16, border: '1px solid #bbf7d0', marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#16a34a' }}>
                    ✅ Empleados 100% Puntuales ({resumenGrupal.empleadosPuntuales.length})
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {resumenGrupal.empleadosPuntuales.map((emp, idx) => (
                      <span key={idx} style={{
                        padding: '6px 12px',
                        background: 'white',
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#16a34a',
                        border: '1px solid #bbf7d0'
                      }}>
                        🌟 {emp.nombre}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* NUEVO: Panel de Horarios Incompletos */}
              {resumenGrupal.totalDiasIncompletos > 0 && (
                <div style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)', borderRadius: 12, padding: 16, border: '2px solid #f59e0b', marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 20 }}>📋</span> Análisis de Horarios Incompletos
                  </h4>
                  
                  {/* Cards de estadísticas de incompletos */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
                    <div style={{ background: 'white', borderRadius: 10, padding: 12, textAlign: 'center', border: '1px solid #fcd34d' }}>
                      <div style={{ fontSize: 28, fontWeight: 800, color: '#d97706' }}>{resumenGrupal.totalDiasIncompletos}</div>
                      <div style={{ fontSize: 10, color: '#92400e', fontWeight: 600 }}>Días con Marcaciones Incompletas</div>
                    </div>
                    <div style={{ background: 'white', borderRadius: 10, padding: 12, textAlign: 'center', border: '1px solid #fcd34d' }}>
                      <div style={{ fontSize: 28, fontWeight: 800, color: '#059669' }}>{resumenGrupal.totalMarcacionesSinE1}</div>
                      <div style={{ fontSize: 10, color: '#92400e', fontWeight: 600 }}>Sin Entrada 1</div>
                    </div>
                    <div style={{ background: 'white', borderRadius: 10, padding: 12, textAlign: 'center', border: '1px solid #fcd34d' }}>
                      <div style={{ fontSize: 28, fontWeight: 800, color: '#dc2626' }}>{resumenGrupal.totalMarcacionesSinS1}</div>
                      <div style={{ fontSize: 10, color: '#92400e', fontWeight: 600 }}>Sin Salida 1</div>
                    </div>
                    <div style={{ background: 'white', borderRadius: 10, padding: 12, textAlign: 'center', border: '1px solid #fcd34d' }}>
                      <div style={{ fontSize: 28, fontWeight: 800, color: '#2563eb' }}>{resumenGrupal.totalMarcacionesSinE2}</div>
                      <div style={{ fontSize: 10, color: '#92400e', fontWeight: 600 }}>Sin Entrada 2</div>
                    </div>
                    <div style={{ background: 'white', borderRadius: 10, padding: 12, textAlign: 'center', border: '1px solid #fcd34d' }}>
                      <div style={{ fontSize: 28, fontWeight: 800, color: '#7c3aed' }}>{resumenGrupal.totalMarcacionesSinS2}</div>
                      <div style={{ fontSize: 10, color: '#92400e', fontWeight: 600 }}>Sin Salida 2</div>
                    </div>
                  </div>
                  
                  {/* Ranking de empleados con más días incompletos */}
                  {resumenGrupal.empleadosConIncompletos.length > 0 && (
                    <div style={{ background: 'white', borderRadius: 10, padding: 12, border: '1px solid #fcd34d' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 10 }}>
                        🏆 Ranking: Empleados con Más Días Incompletos
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr style={{ background: '#fef3c7' }}>
                            <th style={{ padding: 6, textAlign: 'center', fontWeight: 700, width: 30 }}>#</th>
                            <th style={{ padding: 6, textAlign: 'left', fontWeight: 700 }}>Empleado</th>
                            <th style={{ padding: 6, textAlign: 'center', fontWeight: 700 }}>Días Inc.</th>
                            <th style={{ padding: 6, textAlign: 'center', fontWeight: 700 }}>Sin E1</th>
                            <th style={{ padding: 6, textAlign: 'center', fontWeight: 700 }}>Sin S1</th>
                            <th style={{ padding: 6, textAlign: 'center', fontWeight: 700 }}>Sin E2</th>
                            <th style={{ padding: 6, textAlign: 'center', fontWeight: 700 }}>Sin S2</th>
                            <th style={{ padding: 6, textAlign: 'center', fontWeight: 700 }}>% Complet.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resumenGrupal.empleadosConIncompletos.slice(0, 10).map((emp, idx) => (
                            <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#fffbeb' }}>
                              <td style={{ padding: 6, textAlign: 'center', fontWeight: 700, color: idx < 3 ? '#dc2626' : '#64748b' }}>
                                {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                              </td>
                              <td style={{ padding: 6, fontWeight: 500 }}>{emp.nombre}</td>
                              <td style={{ padding: 6, textAlign: 'center', fontWeight: 700, color: '#d97706' }}>{emp.diasIncompletos}</td>
                              <td style={{ padding: 6, textAlign: 'center', color: emp.marcacionesSinE1 > 0 ? '#dc2626' : '#16a34a' }}>{emp.marcacionesSinE1}</td>
                              <td style={{ padding: 6, textAlign: 'center', color: emp.marcacionesSinS1 > 0 ? '#dc2626' : '#16a34a' }}>{emp.marcacionesSinS1}</td>
                              <td style={{ padding: 6, textAlign: 'center', color: emp.marcacionesSinE2 > 0 ? '#dc2626' : '#16a34a' }}>{emp.marcacionesSinE2}</td>
                              <td style={{ padding: 6, textAlign: 'center', color: emp.marcacionesSinS2 > 0 ? '#dc2626' : '#16a34a' }}>{emp.marcacionesSinS2}</td>
                              <td style={{ padding: 6, textAlign: 'center' }}>
                                <span style={{
                                  padding: '2px 6px',
                                  borderRadius: 10,
                                  fontSize: 10,
                                  fontWeight: 700,
                                  background: emp.porcentajeCompletitud >= 90 ? '#f0fdf4' : emp.porcentajeCompletitud >= 70 ? '#fffbeb' : '#fef2f2',
                                  color: emp.porcentajeCompletitud >= 90 ? '#16a34a' : emp.porcentajeCompletitud >= 70 ? '#d97706' : '#dc2626'
                                }}>
                                  {emp.porcentajeCompletitud}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* NUEVO: Semáforo de Indicadores de Riesgo */}
              <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', borderRadius: 12, padding: 20, marginBottom: 16, color: 'white' }}>
                <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: '#fbbf24', display: 'flex', alignItems: 'center', gap: 8 }}>
                  🚦 Semáforo de Riesgo - Clasificación para Toma de Decisiones
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                  {/* Nivel Crítico */}
                  <div style={{ background: 'rgba(220, 38, 38, 0.2)', borderRadius: 10, padding: 12, border: '2px solid #dc2626' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#dc2626', boxShadow: '0 0 10px #dc2626' }}></div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#fecaca' }}>CRÍTICO</span>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#fecaca' }}>{resumenGrupal.indicadores.nivelCritico.length}</div>
                    <div style={{ fontSize: 10, color: '#fca5a5', marginBottom: 8 }}>Requiere Acción Inmediata</div>
                    {resumenGrupal.indicadores.nivelCritico.slice(0, 3).map((emp, idx) => (
                      <div key={idx} style={{ fontSize: 10, color: '#fecaca', padding: '2px 0' }}>• {emp.nombre}</div>
                    ))}
                    {resumenGrupal.indicadores.nivelCritico.length > 3 && (
                      <div style={{ fontSize: 9, color: '#ef4444', marginTop: 4 }}>+{resumenGrupal.indicadores.nivelCritico.length - 3} más...</div>
                    )}
                  </div>
                  
                  {/* Nivel Alerta */}
                  <div style={{ background: 'rgba(245, 158, 11, 0.2)', borderRadius: 10, padding: 12, border: '2px solid #f59e0b' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#f59e0b', boxShadow: '0 0 10px #f59e0b' }}></div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#fef3c7' }}>ALERTA</span>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#fcd34d' }}>{resumenGrupal.indicadores.nivelAlerta.length}</div>
                    <div style={{ fontSize: 10, color: '#fcd34d', marginBottom: 8 }}>Seguimiento Necesario</div>
                    {resumenGrupal.indicadores.nivelAlerta.slice(0, 3).map((emp, idx) => (
                      <div key={idx} style={{ fontSize: 10, color: '#fef3c7', padding: '2px 0' }}>• {emp.nombre}</div>
                    ))}
                    {resumenGrupal.indicadores.nivelAlerta.length > 3 && (
                      <div style={{ fontSize: 9, color: '#f59e0b', marginTop: 4 }}>+{resumenGrupal.indicadores.nivelAlerta.length - 3} más...</div>
                    )}
                  </div>
                  
                  {/* Nivel Observación */}
                  <div style={{ background: 'rgba(59, 130, 246, 0.2)', borderRadius: 10, padding: 12, border: '2px solid #3b82f6' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#3b82f6', boxShadow: '0 0 10px #3b82f6' }}></div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#bfdbfe' }}>OBSERVACIÓN</span>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#93c5fd' }}>{resumenGrupal.indicadores.nivelObservacion.length}</div>
                    <div style={{ fontSize: 10, color: '#93c5fd', marginBottom: 8 }}>Monitoreo Preventivo</div>
                    {resumenGrupal.indicadores.nivelObservacion.slice(0, 3).map((emp, idx) => (
                      <div key={idx} style={{ fontSize: 10, color: '#bfdbfe', padding: '2px 0' }}>• {emp.nombre}</div>
                    ))}
                    {resumenGrupal.indicadores.nivelObservacion.length > 3 && (
                      <div style={{ fontSize: 9, color: '#3b82f6', marginTop: 4 }}>+{resumenGrupal.indicadores.nivelObservacion.length - 3} más...</div>
                    )}
                  </div>
                  
                  {/* Nivel Óptimo */}
                  <div style={{ background: 'rgba(34, 197, 94, 0.2)', borderRadius: 10, padding: 12, border: '2px solid #22c55e' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 10px #22c55e' }}></div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#bbf7d0' }}>ÓPTIMO</span>
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: '#86efac' }}>{resumenGrupal.indicadores.nivelOptimo.length}</div>
                    <div style={{ fontSize: 10, color: '#86efac', marginBottom: 8 }}>Desempeño Excelente</div>
                    {resumenGrupal.indicadores.nivelOptimo.slice(0, 3).map((emp, idx) => (
                      <div key={idx} style={{ fontSize: 10, color: '#bbf7d0', padding: '2px 0' }}>• {emp.nombre}</div>
                    ))}
                    {resumenGrupal.indicadores.nivelOptimo.length > 3 && (
                      <div style={{ fontSize: 9, color: '#22c55e', marginTop: 4 }}>+{resumenGrupal.indicadores.nivelOptimo.length - 3} más...</div>
                    )}
                  </div>
                </div>
              </div>

              {/* NUEVO: Panel de Recomendaciones para Toma de Decisiones */}
              {resumenGrupal.recomendaciones.length > 0 && (
                <div style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderRadius: 12, padding: 16, border: '2px solid #0ea5e9', marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: '#0369a1', display: 'flex', alignItems: 'center', gap: 8 }}>
                    💡 Recomendaciones para la Toma de Decisiones
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {resumenGrupal.recomendaciones.map((rec, idx) => (
                      <div key={idx} style={{
                        background: rec.tipo === 'critico' ? '#fef2f2' : rec.tipo === 'alerta' ? '#fffbeb' : rec.tipo === 'positivo' ? '#f0fdf4' : 'white',
                        borderRadius: 10,
                        padding: 14,
                        border: rec.tipo === 'critico' ? '1px solid #fecaca' : rec.tipo === 'alerta' ? '1px solid #fcd34d' : rec.tipo === 'positivo' ? '1px solid #bbf7d0' : '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 12
                      }}>
                        <div style={{ fontSize: 24 }}>{rec.icono}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: rec.tipo === 'critico' ? '#dc2626' : rec.tipo === 'alerta' ? '#d97706' : rec.tipo === 'positivo' ? '#16a34a' : '#374151', marginBottom: 4 }}>
                            {rec.titulo}
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                            {rec.mensaje}
                          </div>
                          <div style={{ fontSize: 11, color: '#0ea5e9', fontWeight: 600, padding: '6px 10px', background: '#e0f2fe', borderRadius: 6, display: 'inline-block' }}>
                            ▶ Acción sugerida: {rec.accion}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* NUEVO: Ranking General de Empleados Más Problemáticos */}
              {resumenGrupal.rankingProblematicos.length > 0 && (
                <div style={{ background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', borderRadius: 12, padding: 16, border: '2px solid #dc2626' }}>
                  <h4 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: '#991b1b', display: 'flex', alignItems: 'center', gap: 8 }}>
                    🏆 Ranking: Top 10 Empleados con Mayor Índice de Riesgo
                  </h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: '#fecaca' }}>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700, width: 40 }}>Pos.</th>
                        <th style={{ padding: 8, textAlign: 'left', fontWeight: 700 }}>Empleado</th>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>Score</th>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>Faltas</th>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>Días Inc.</th>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>Tardanzas</th>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>Sal. Temp.</th>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>Min. Perd.</th>
                        <th style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>Nivel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumenGrupal.rankingProblematicos.map((emp, idx) => {
                        const nivel = emp.scoreRiesgo >= 300 ? 'CRÍTICO' : emp.scoreRiesgo >= 150 ? 'ALERTA' : 'OBSERV.';
                        const nivelColor = emp.scoreRiesgo >= 300 ? '#dc2626' : emp.scoreRiesgo >= 150 ? '#d97706' : '#3b82f6';
                        const nivelBg = emp.scoreRiesgo >= 300 ? '#fef2f2' : emp.scoreRiesgo >= 150 ? '#fffbeb' : '#eff6ff';
                        return (
                          <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#fff5f5' }}>
                            <td style={{ padding: 8, textAlign: 'center', fontWeight: 700 }}>
                              {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                            </td>
                            <td style={{ padding: 8, fontWeight: 600 }}>
                              {emp.nombre}
                              <div style={{ fontSize: 9, color: '#64748b' }}>{emp.sede}</div>
                            </td>
                            <td style={{ padding: 8, textAlign: 'center', fontWeight: 800, color: '#dc2626', fontSize: 13 }}>
                              {emp.scoreRiesgo}
                            </td>
                            <td style={{ padding: 8, textAlign: 'center', fontWeight: 700, color: emp.faltas > 0 ? '#dc2626' : '#16a34a' }}>
                              {emp.faltas}
                            </td>
                            <td style={{ padding: 8, textAlign: 'center', fontWeight: 700, color: emp.diasIncompletos > 0 ? '#d97706' : '#16a34a' }}>
                              {emp.diasIncompletos}
                            </td>
                            <td style={{ padding: 8, textAlign: 'center', fontWeight: 700, color: emp.llegadasTarde > 0 ? '#d97706' : '#16a34a' }}>
                              {emp.llegadasTarde}
                            </td>
                            <td style={{ padding: 8, textAlign: 'center', fontWeight: 700, color: emp.salidasTempranas > 0 ? '#a21caf' : '#16a34a' }}>
                              {emp.salidasTempranas}
                            </td>
                            <td style={{ padding: 8, textAlign: 'center', color: '#64748b' }}>
                              {`${Math.floor(emp.totalMinutosPerdidos / 60)}h ${emp.totalMinutosPerdidos % 60}m`}
                            </td>
                            <td style={{ padding: 8, textAlign: 'center' }}>
                              <span style={{
                                padding: '3px 8px',
                                borderRadius: 12,
                                fontSize: 9,
                                fontWeight: 700,
                                background: nivelBg,
                                color: nivelColor,
                                border: `1px solid ${nivelColor}`
                              }}>
                                {nivel}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div style={{ marginTop: 12, padding: 10, background: '#fee2e2', borderRadius: 8, fontSize: 11, color: '#991b1b' }}>
                    <strong>📊 Cálculo del Score de Riesgo:</strong> Faltas (×100) + Días Incompletos (×50) + Tardanzas (×30) + Salidas Tempranas (×20) + Minutos Perdidos (÷10)
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Vista: Grupal */}
          {vistaAnalisis === "grupal" && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                    <th style={{ padding: 10, textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 700, position: 'sticky', left: 0, background: '#f8f9fa', zIndex: 1 }}>Empleado</th>
                    <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700 }}>Días Trab.</th>
                    <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700 }}>Puntuales</th>
                    <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700, color: '#dc2626' }}>Faltas</th>
                    <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700, color: '#d97706' }}>Días Inc.</th>
                    <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700, color: '#d97706' }}>Tardanzas</th>
                    <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700, color: '#a21caf' }}>Sal. Temp.</th>
                    <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700, color: '#dc2626' }}>Min. Perdidos</th>
                    <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700 }}>% Puntual</th>
                    <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700 }}>% Complet.</th>
                    <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700 }}>Score</th>
                    <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700 }}>Nivel</th>
                  </tr>
                </thead>
                <tbody>
                  {empleados.map((emp, idx) => {
                    const data = resumenGrupal.porEmpleado[emp.id] || {};
                    const tieneProblemas = data.faltas > 0 || data.llegadasTarde > 0 || data.salidasTempranas > 0 || data.diasIncompletos > 0;
                    const nivel = (data.scoreRiesgo || 0) >= 300 ? 'CRÍTICO' : (data.scoreRiesgo || 0) >= 150 ? 'ALERTA' : (data.scoreRiesgo || 0) >= 50 ? 'OBSERV.' : 'ÓPTIMO';
                    const nivelColor = (data.scoreRiesgo || 0) >= 300 ? '#dc2626' : (data.scoreRiesgo || 0) >= 150 ? '#d97706' : (data.scoreRiesgo || 0) >= 50 ? '#3b82f6' : '#16a34a';
                    const nivelBg = (data.scoreRiesgo || 0) >= 300 ? '#fef2f2' : (data.scoreRiesgo || 0) >= 150 ? '#fffbeb' : (data.scoreRiesgo || 0) >= 50 ? '#eff6ff' : '#f0fdf4';
                    return (
                      <tr key={emp.id} style={{ background: tieneProblemas ? '#fffbeb' : idx % 2 === 0 ? 'white' : '#fafafa' }}>
                        <td style={{ padding: 10, fontWeight: 500, position: 'sticky', left: 0, background: tieneProblemas ? '#fffbeb' : idx % 2 === 0 ? 'white' : '#fafafa', zIndex: 1 }}>
                          {emp.nombre}
                          <div style={{ fontSize: 10, color: '#64748b' }}>{emp.documento}</div>
                        </td>
                        <td style={{ padding: 10, textAlign: 'center', fontWeight: 600 }}>{data.diasTrabajados || 0}</td>
                        <td style={{ padding: 10, textAlign: 'center', fontWeight: 600, color: '#16a34a' }}>{data.diasPuntuales || 0}</td>
                        <td style={{ padding: 10, textAlign: 'center', fontWeight: 700, color: data.faltas > 0 ? '#dc2626' : '#16a34a' }}>
                          {data.faltas || 0}
                        </td>
                        <td style={{ padding: 10, textAlign: 'center', fontWeight: 700, color: (data.diasIncompletos || 0) > 0 ? '#d97706' : '#16a34a' }}>
                          {data.diasIncompletos || 0}
                        </td>
                        <td style={{ padding: 10, textAlign: 'center', fontWeight: 700, color: data.llegadasTarde > 0 ? '#d97706' : '#16a34a' }}>
                          {data.llegadasTarde || 0}
                        </td>
                        <td style={{ padding: 10, textAlign: 'center', fontWeight: 700, color: data.salidasTempranas > 0 ? '#a21caf' : '#16a34a' }}>
                          {data.salidasTempranas || 0}
                        </td>
                        <td style={{ padding: 10, textAlign: 'center', color: data.totalMinutosPerdidos > 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                          {data.totalMinutosPerdidos > 0 ? `${Math.floor(data.totalMinutosPerdidos / 60)}h ${data.totalMinutosPerdidos % 60}m` : '✓'}
                        </td>
                        <td style={{ padding: 10, textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: 20,
                            fontSize: 10,
                            fontWeight: 700,
                            background: (data.porcentajePuntualidad || 0) >= 90 ? '#f0fdf4' : (data.porcentajePuntualidad || 0) >= 70 ? '#fffbeb' : '#fef2f2',
                            color: (data.porcentajePuntualidad || 0) >= 90 ? '#16a34a' : (data.porcentajePuntualidad || 0) >= 70 ? '#d97706' : '#dc2626'
                          }}>
                            {data.porcentajePuntualidad || 0}%
                          </span>
                        </td>
                        <td style={{ padding: 10, textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: 20,
                            fontSize: 10,
                            fontWeight: 700,
                            background: (data.porcentajeCompletitud || 100) >= 90 ? '#f0fdf4' : (data.porcentajeCompletitud || 100) >= 70 ? '#fffbeb' : '#fef2f2',
                            color: (data.porcentajeCompletitud || 100) >= 90 ? '#16a34a' : (data.porcentajeCompletitud || 100) >= 70 ? '#d97706' : '#dc2626'
                          }}>
                            {data.porcentajeCompletitud || 100}%
                          </span>
                        </td>
                        <td style={{ padding: 10, textAlign: 'center', fontWeight: 700, color: '#64748b' }}>
                          {data.scoreRiesgo || 0}
                        </td>
                        <td style={{ padding: 10, textAlign: 'center' }}>
                          <span style={{
                            padding: '3px 8px',
                            borderRadius: 12,
                            fontSize: 9,
                            fontWeight: 700,
                            background: nivelBg,
                            color: nivelColor,
                            border: `1px solid ${nivelColor}`
                          }}>
                            {nivel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Vista: Individual */}
          {vistaAnalisis === "individual" && (
            <div>
              {/* Selector de empleado */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginRight: 12 }}>Seleccionar empleado:</label>
                <select
                  value={empleadoSeleccionado}
                  onChange={(e) => setEmpleadoSeleccionado(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, minWidth: 250 }}
                >
                  <option value="">-- Selecciona un empleado --</option>
                  {empleados.map(e => (
                    <option key={e.id} value={e.id}>{e.nombre} - {e.documento}</option>
                  ))}
                </select>
              </div>

              {empleadoSeleccionado ? (
                (() => {
                  const emp = empleados.find(e => e.id === empleadoSeleccionado);
                  if (!emp) return null;
                  const analisis = analizarEmpleado(emp);
                  const stats = calcularEstadisticasEmpleado(analisis);
                  
                  return (
                    <div>
                      {/* Info del empleado */}
                      <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 16, display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ width: 50, height: 50, borderRadius: '50%', background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 20, fontWeight: 700 }}>
                          {emp.nombre?.charAt(0) || '?'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>{emp.nombre}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>Documento: {emp.documento}</div>
                        </div>
                        <div style={{ 
                          padding: '8px 16px', 
                          borderRadius: 20, 
                          background: stats.porcentajePuntualidad >= 90 ? '#f0fdf4' : stats.porcentajePuntualidad >= 70 ? '#fffbeb' : '#fef2f2',
                          border: stats.porcentajePuntualidad >= 90 ? '1px solid #bbf7d0' : stats.porcentajePuntualidad >= 70 ? '1px solid #fcd34d' : '1px solid #fecaca'
                        }}>
                          <span style={{ 
                            fontSize: 14, 
                            fontWeight: 700, 
                            color: stats.porcentajePuntualidad >= 90 ? '#16a34a' : stats.porcentajePuntualidad >= 70 ? '#d97706' : '#dc2626' 
                          }}>
                            {stats.porcentajePuntualidad}% Puntual
                          </span>
                        </div>
                      </div>

                      {/* Resumen del empleado */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
                        <div style={{ padding: 12, background: '#eff6ff', borderRadius: 8, textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#1e40af' }}>{stats.diasTrabajados}</div>
                          <div style={{ fontSize: 10, color: '#3b82f6' }}>Días Trabajados</div>
                        </div>
                        <div style={{ padding: 12, background: '#f0fdf4', borderRadius: 8, textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#16a34a' }}>{stats.diasPuntuales}</div>
                          <div style={{ fontSize: 10, color: '#22c55e' }}>Días Puntuales</div>
                        </div>
                        <div style={{ padding: 12, background: '#fffbeb', borderRadius: 8, textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#d97706' }}>{stats.llegadasTarde}</div>
                          <div style={{ fontSize: 10, color: '#f59e0b' }}>Llegadas Tarde</div>
                        </div>
                        <div style={{ padding: 12, background: '#fef2f2', borderRadius: 8, textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#dc2626' }}>{stats.totalMinutosPerdidos}m</div>
                          <div style={{ fontSize: 10, color: '#ef4444' }}>Min. Perdidos</div>
                        </div>
                      </div>

                      {/* Análisis positivo si es puntual */}
                      {stats.porcentajePuntualidad === 100 && (
                        <div style={{ background: '#f0fdf4', borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid #bbf7d0', textAlign: 'center' }}>
                          <div style={{ fontSize: 40, marginBottom: 8 }}>🌟</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#16a34a' }}>¡Excelente! 100% de puntualidad</div>
                          <div style={{ fontSize: 12, color: '#22c55e' }}>Este empleado ha cumplido perfectamente con su horario</div>
                        </div>
                      )}

                      {/* Día con más tardanzas */}
                      {stats.diaConMasTardanzas && (
                        <div style={{ background: '#fffbeb', borderRadius: 10, padding: 12, marginBottom: 16, border: '1px solid #fcd34d' }}>
                          <span style={{ fontSize: 12, color: '#92400e' }}>
                            ⚠️ Día con más tardanzas: <strong>{stats.diaConMasTardanzas}</strong> ({stats.llegadasTardePorDia[stats.diaConMasTardanzas]} veces)
                          </span>
                        </div>
                      )}

                      {/* Tabla detallada con E1, S1, E2, S2 */}
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                          <thead>
                            <tr style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                              <th style={{ padding: 8, textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Fecha</th>
                              <th style={{ padding: 8, textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Día</th>
                              <th style={{ padding: 8, textAlign: 'center', borderBottom: '2px solid #dee2e6', background: '#dcfce7' }}>E1 Prog</th>
                              <th style={{ padding: 8, textAlign: 'center', borderBottom: '2px solid #dee2e6', background: '#d1fae5' }}>E1 Real</th>
                              <th style={{ padding: 8, textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Dif</th>
                              <th style={{ padding: 8, textAlign: 'center', borderBottom: '2px solid #dee2e6', background: '#fee2e2' }}>S1 Prog</th>
                              <th style={{ padding: 8, textAlign: 'center', borderBottom: '2px solid #dee2e6', background: '#fecaca' }}>S1 Real</th>
                              <th style={{ padding: 8, textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Dif</th>
                              <th style={{ padding: 8, textAlign: 'center', borderBottom: '2px solid #dee2e6', background: '#dbeafe' }}>E2 Prog</th>
                              <th style={{ padding: 8, textAlign: 'center', borderBottom: '2px solid #dee2e6', background: '#bfdbfe' }}>E2 Real</th>
                              <th style={{ padding: 8, textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Dif</th>
                              <th style={{ padding: 8, textAlign: 'center', borderBottom: '2px solid #dee2e6', background: '#fae8ff' }}>S2 Prog</th>
                              <th style={{ padding: 8, textAlign: 'center', borderBottom: '2px solid #dee2e6', background: '#f5d0fe' }}>S2 Real</th>
                              <th style={{ padding: 8, textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Dif</th>
                              <th style={{ padding: 8, textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Min Perd.</th>
                              <th style={{ padding: 8, textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Estado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analisis.map((dia, idx) => {
                              const estadoDetallado = obtenerEstadoDetallado(dia);
                              return (
                                <tr key={dia.fecha} style={{ 
                                  background: dia.estado === 'falta' ? '#fef2f2' : 
                                             dia.estado === 'descanso' ? '#f5f5f5' : 
                                             dia.totalMinutosPerdidos > 0 ? '#fffbeb' :
                                             idx % 2 === 0 ? 'white' : '#fafafa' 
                                }}>
                                  <td style={{ padding: 6, fontWeight: 500 }}>{dia.fecha}</td>
                                  <td style={{ padding: 6, color: '#64748b' }}>{dia.diaSemana.substring(0, 3)}</td>
                                  <td style={{ padding: 6, textAlign: 'center', color: '#16a34a', fontWeight: 600 }}>
                                    {dia.programado?.e1 || '-'}
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'center', color: '#059669', fontWeight: 700 }}>
                                    {dia.marcacion?.entrada1 || '-'}
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'center', fontWeight: 600, fontSize: 9, color: dia.diferencias.entrada1 > 0 ? '#dc2626' : dia.diferencias.entrada1 < 0 ? '#16a34a' : '#64748b' }}>
                                    {formatearDiferencia(dia.diferencias.entrada1)}
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'center', color: '#dc2626', fontWeight: 600 }}>
                                    {dia.programado?.s1 || '-'}
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'center', color: '#b91c1c', fontWeight: 700 }}>
                                    {dia.marcacion?.salida1 || '-'}
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'center', fontWeight: 600, fontSize: 9, color: dia.diferencias.salida1 < 0 ? '#dc2626' : dia.diferencias.salida1 > 0 ? '#16a34a' : '#64748b' }}>
                                    {formatearDiferencia(dia.diferencias.salida1)}
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'center', color: '#2563eb', fontWeight: 600 }}>
                                    {dia.programado?.e2 || '-'}
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'center', color: '#1d4ed8', fontWeight: 700 }}>
                                    {dia.marcacion?.entrada2 || '-'}
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'center', fontWeight: 600, fontSize: 9, color: dia.diferencias.entrada2 > 0 ? '#dc2626' : dia.diferencias.entrada2 < 0 ? '#16a34a' : '#64748b' }}>
                                    {formatearDiferencia(dia.diferencias.entrada2)}
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'center', color: '#a21caf', fontWeight: 600 }}>
                                    {dia.programado?.s2 || '-'}
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'center', color: '#86198f', fontWeight: 700 }}>
                                    {dia.marcacion?.salida2 || '-'}
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'center', fontWeight: 600, fontSize: 9, color: dia.diferencias.salida2 < 0 ? '#dc2626' : dia.diferencias.salida2 > 0 ? '#16a34a' : '#64748b' }}>
                                    {formatearDiferencia(dia.diferencias.salida2)}
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'center', fontWeight: 700, color: dia.totalMinutosPerdidos > 0 ? '#dc2626' : '#16a34a' }}>
                                    {dia.totalMinutosPerdidos > 0 ? `${dia.totalMinutosPerdidos}m` : '✓'}
                                  </td>
                                  <td style={{ padding: 6, textAlign: 'center' }}>
                                    <span style={{
                                      padding: '2px 6px',
                                      borderRadius: 4,
                                      fontSize: 8,
                                      fontWeight: 700,
                                      background: estadoDetallado.bg,
                                      color: estadoDetallado.color,
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {estadoDetallado.texto}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr style={{ background: '#fef2f2', fontWeight: 700 }}>
                              <td colSpan="14" style={{ padding: 10, textAlign: 'right', color: '#dc2626' }}>
                                TOTAL MINUTOS PERDIDOS:
                              </td>
                              <td style={{ padding: 10, textAlign: 'center', color: '#dc2626', fontSize: 12 }}>
                                {stats.totalMinutosPerdidos}m
                              </td>
                              <td style={{ padding: 10, textAlign: 'center', color: '#dc2626', fontSize: 10 }}>
                                ({Math.floor(stats.totalMinutosPerdidos / 60)}h {stats.totalMinutosPerdidos % 60}m)
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
                  <div style={{ fontSize: 50, marginBottom: 16 }}>👤</div>
                  <p>Selecciona un empleado para ver su análisis detallado</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Horarios({
  empleados,
  sedes,
  ausencias = [],
  horariosRepo = [],
  onGuardarHorario,       // (registro) => void  (push)
  onActualizarHorario,    // (id, patch) => void (merge)
  tienePermiso = () => true, // función para validar permisos
  filtroEmpresa = null,   // filtro de empresa para sedes
}) {
  const sedesLista = React.useMemo(
    () =>
      (sedes || [])
        .map((s) => {
          if (!s) return "";
          if (typeof s === "string") return s;
          return s.nombre || s.razonSocial || s.razonsocial || "";
        })
        .filter((nombre) => typeof nombre === "string" && nombre.trim().length > 0),
    [sedes]
  );
  const holidaysCache = React.useMemo(() => ({}), []);
  const hoyISO = toISO(new Date());
  const [fechaBase, setFechaBase] = useState(mondayOf(hoyISO)); // anclado a lunes
  const semanaInicio = fechaBase;
  const semanaFin = sundayOf(semanaInicio);
 
  // repo existente
  const horarioExistente =
    horariosRepo.find(h => h.semanaInicio === semanaInicio && h.semanaFin === semanaFin) || null;
 
  // ===== Estado principal =====
  const [estado, setEstado] = useState(horarioExistente?.estado || "temporal");
  
  // diasCerrados: array de fechas ISO que están cerradas definitivamente
  const [diasCerrados, setDiasCerrados] = useState(() => horarioExistente?.diasCerrados || []);
 
  // celdas: guardamos ahora {tipo, sede1, sede2, e1,s1,e2,s2, otro}
  const [celdas, setCeldas] = useState(() =>
    typeof structuredClone === "function"
      ? structuredClone(horarioExistente?.celdas || {})
      : JSON.parse(JSON.stringify(horarioExistente?.celdas || {}))
  );
 
  // eventosPorDia: { [empleadoId]: { [iso]: {eventoId, nombre, color} } }
  const [eventosPorDia, setEventosPorDia] = useState(() =>
    typeof structuredClone === "function"
      ? structuredClone(horarioExistente?.eventosPorDia || {})
      : JSON.parse(JSON.stringify(horarioExistente?.eventosPorDia || {}))
  );

  // Flag para evitar que el useEffect sobreescriba las celdas después de guardar desde Vista Gráfica
  const skipCeldasReloadRef = React.useRef(false);

  const [modalCopiarOpen, setModalCopiarOpen] = useState(false);
  const [copiarSeleccion, setCopiarSeleccion] = useState("");
 
 // selección de empleados visible en grilla
const [selOpen, setSelOpen] = useState(false);
 
// empleados activos (solo los que tengan e.activo === true)
const activos = React.useMemo(
  () => empleados.filter((e) => e.activo),
  [empleados]
);
 
// ids seleccionados por defecto: todos los activos
const [selectedIds, setSelectedIds] = useState([]);

// Sincronizar selectedIds cuando cambian los empleados activos
React.useEffect(() => {
  if (activos.length > 0 && selectedIds.length === 0) {
    setSelectedIds(activos.map((e) => e.id));
  }
}, [activos]);
 
React.useEffect(() => {
  // Si se acaba de guardar desde Vista Gráfica, no recargar las celdas
  // La bandera se mantiene hasta el próximo cambio manual de semana
  if (skipCeldasReloadRef.current) {
    console.log('[Horarios] useEffect bloqueado por bandera - celdas no recargadas');
    return;
  }
  
  console.log('[Horarios] useEffect ejecutado - cargando celdas de:', semanaInicio, '->', semanaFin);
  console.log('[Horarios] horariosRepo tiene:', horariosRepo.length, 'horarios');
  if (horariosRepo.length > 0) {
    console.log('[Horarios] Semanas en repo:', horariosRepo.map(h => `${h.semanaInicio} -> ${h.semanaFin}`).join(' | '));
  }
  console.log('[Horarios] Buscando coincidencia exacta con:', { semanaInicio, semanaFin });
  const encontrado = horariosRepo.find(h => h.semanaInicio === semanaInicio && h.semanaFin === semanaFin);
  console.log('[Horarios] horarioExistente:', encontrado?.id || 'null', '- celdas:', Object.keys(encontrado?.celdas || {}).length);
  
  // recarga al cambiar semana o repo
  setEstado(horarioExistente?.estado || "temporal");
  setDiasCerrados(horarioExistente?.diasCerrados || []);
  setCeldas(
    typeof structuredClone === "function"
      ? structuredClone(horarioExistente?.celdas || {})
      : JSON.parse(JSON.stringify(horarioExistente?.celdas || {}))
  );
  setEventosPorDia(
    typeof structuredClone === "function"
      ? structuredClone(horarioExistente?.eventosPorDia || {})
      : JSON.parse(JSON.stringify(horarioExistente?.eventosPorDia || {}))
  );
  setSelectedIds(activos.map((e) => e.id)); // por defecto todos activos visibles
}, [semanaInicio, semanaFin, horarioExistente?.id]);

  // ===== Estados para Marcaciones Reales (HikVision) =====
  const [vistaHorarios, setVistaHorarios] = useState("programacion"); // "programacion" | "vista_grafica" | "marcaciones_temporales" | "marcaciones_reales" | "analisis" | "horario_nomina" | "actualizar_horarios"
  
  // Estados para Vista Gráfica del Local
  const [vistaGraficaDia, setVistaGraficaDia] = useState(-1); // -1 = semana completa, 0-6 = día específico
  const [vistaGraficaTurno, setVistaGraficaTurno] = useState("todos"); // "todos" | "mañana" | "tarde" | "noche"
  const [modoVistaGrafica, setModoVistaGrafica] = useState("ninguno"); // "ninguno" | "consultar" | "crear"
  const [horarioCreacion, setHorarioCreacion] = useState({}); // {empleadoId: {diaIdx: [{sede, e1, s1, e2, s2, tipo, esDescanso}]}}
  const [empleadoSeleccionadoCreacion, setEmpleadoSeleccionadoCreacion] = useState(null);
  const [modalAsignarHorario, setModalAsignarHorario] = useState(false);
  const [diaCreacionSeleccionado, setDiaCreacionSeleccionado] = useState(0); // 0-6 para día de la semana en modo crear
  const [empleadoArrastrado, setEmpleadoArrastrado] = useState(null); // Empleado siendo arrastrado
  const [alertasHorario, setAlertasHorario] = useState([]); // Alertas de validación de horarios
  const [modalConfirmarGuardarHorario, setModalConfirmarGuardarHorario] = useState(false);
  const [sugerenciasHorarios, setSugerenciasHorarios] = useState(null); // Sugerencias basadas en historial
  const [fechaBaseVistaGrafica, setFechaBaseVistaGrafica] = useState(mondayOf(toISO(new Date()))); // Semana para Vista Gráfica
  const [mostrarMenuSugerencias, setMostrarMenuSugerencias] = useState(false); // Menú desplegable de sugerencias
  const [modalCopiarVistaGraficaOpen, setModalCopiarVistaGraficaOpen] = useState(false); // Modal para copiar horarios en Vista Gráfica
  const [copiarSeleccionVistaGrafica, setCopiarSeleccionVistaGrafica] = useState(""); // Horario seleccionado para copiar
  const [menuHorariosManuales, setMenuHorariosManuales] = useState(false); // Menú desplegable para Horarios Manuales
  const [menuHorarioBiometrico, setMenuHorarioBiometrico] = useState(false); // Menú desplegable para Horario Biométrico
  const [panelResumenSemanal, setPanelResumenSemanal] = useState(false); // Panel lateral con vista de tabla semanal
  
  // 🔒 Estados para Modal de Resumen antes de Cierre Definitivo (empleados con >7 horas)
  const [modalResumen7Horas, setModalResumen7Horas] = useState(false);
  const [empleadosMas7HorasLista, setEmpleadosMas7HorasLista] = useState([]);
  const [limiteQuincenaResumen, setLimiteQuincenaResumen] = useState("");
  
  // Calcular semana de vista gráfica
  const semanaInicioVistaGrafica = fechaBaseVistaGrafica;
  const semanaFinVistaGrafica = sundayOf(fechaBaseVistaGrafica);
  const fechasSemanaVistaGrafica = React.useMemo(() => {
    const arr = [];
    let cur = new Date(fechaBaseVistaGrafica + "T12:00:00");
    for (let i = 0; i < 7; i++) {
      arr.push(toISO(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return arr;
  }, [fechaBaseVistaGrafica]);
  
  // Obtener horario existente para la semana de Vista Gráfica
  const horarioExistenteVistaGrafica = horariosRepo.find(
    h => h.semanaInicio === semanaInicioVistaGrafica && h.semanaFin === semanaFinVistaGrafica
  ) || null;
  
  // Celdas del horario guardado en Vista Gráfica (para Consultar Personal)
  const celdasVistaGrafica = horarioExistenteVistaGrafica?.celdas || {};
  
  // Horarios disponibles para copiar en Vista Gráfica (excluye la semana actual de Vista Gráfica)
  const horariosCopiablesVistaGrafica = React.useMemo(() => {
    return (horariosRepo || [])
      .filter((h) => h.id && h.id !== (horarioExistenteVistaGrafica?.id || null))
      .sort((a, b) => parseISO(b.semanaInicio) - parseISO(a.semanaInicio));
  }, [horariosRepo, horarioExistenteVistaGrafica?.id]);
  
  // Efecto para establecer selección inicial cuando se abre el modal de copiar en Vista Gráfica
  React.useEffect(() => {
    if (modalCopiarVistaGraficaOpen) {
      setCopiarSeleccionVistaGrafica(horariosCopiablesVistaGrafica[0]?.id || "");
    }
  }, [modalCopiarVistaGraficaOpen, horariosCopiablesVistaGrafica]);
  
  // 🔹 NUEVA FUNCIÓN: Cargar horarios existentes de la semana actual para edición
  // Toma los horarios guardados (celdasVistaGrafica) y los carga en horarioCreacion
  // SOLO carga horarios que realmente estén guardados - celdas vacías quedan en blanco
  const cargarHorariosExistentesParaEdicion = () => {
    const horariosGuardados = celdasVistaGrafica || {};
    
    // Si no hay horarios guardados, retornar objeto vacío
    if (Object.keys(horariosGuardados).length === 0) {
      console.log("📋 No hay horarios guardados para cargar");
      return {};
    }
    
    const nuevosHorarios = {};
    let horariosCopiados = 0;
    
    Object.entries(horariosGuardados).forEach(([empId, dias]) => {
      // Solo procesar empleados activos
      const empleadoActivo = activos.find(emp => emp.id === empId);
      if (!empleadoActivo) return;
      
      Object.entries(dias).forEach(([diaIdx, celda]) => {
        // Verificar que la celda tenga datos reales
        if (!celda || typeof celda !== 'object') return;
        
        const diaIdxNum = parseInt(diaIdx);
        const fechaISO = fechasSemanaVistaGrafica[diaIdxNum];
        
        // No cargar días cerrados (no se pueden editar)
        if (isDiaCerradoVistaGrafica(fechaISO)) return;
        
        // Verificar si el empleado está bloqueado por novedad ese día
        if (empleadoBloqueadoPorNovedad(empId, diaIdxNum)) return;
        
        // 🔹 SOLO cargar si es explícitamente un descanso marcado
        const esDescansoExplicito = celda.descanso === true || celda.esDescanso === true || 
                          celda.tipo === 'DESCANSO';
        
        // 🔹 SOLO cargar si tiene horas definidas (e1 Y s1 con valores reales)
        const tieneHorasValidas = celda.e1 && celda.s1 && 
                                  String(celda.e1).trim() !== '' && 
                                  String(celda.s1).trim() !== '';
        
        // Si no es descanso explícito NI tiene horas válidas, NO cargar (dejar en blanco)
        if (!esDescansoExplicito && !tieneHorasValidas) {
          return; // Celda vacía o sin datos válidos - no cargar
        }
        
        // Inicializar el objeto del empleado si no existe
        if (!nuevosHorarios[empId]) {
          nuevosHorarios[empId] = {};
        }
        
        if (esDescansoExplicito) {
          nuevosHorarios[empId][diaIdxNum] = {
            esDescanso: true,
            sede: 'DESCANSO'
          };
          horariosCopiados++;
        } else if (tieneHorasValidas) {
          nuevosHorarios[empId][diaIdxNum] = {
            sede: celda.sede || celda.sede1 || '',
            sede2: celda.sede2 || celda.sede || celda.sede1 || '',
            e1: celda.e1 || '',
            s1: celda.s1 || '',
            e2: celda.e2 || '',
            s2: celda.s2 || '',
            horariosAdicionales: celda.horariosAdicionales || []
          };
          horariosCopiados++;
        }
      });
    });
    
    console.log(`📋 Horarios existentes cargados: ${horariosCopiados} turnos de ${Object.keys(nuevosHorarios).length} empleados`);
    return nuevosHorarios;
  };
  
  // Función para copiar horarios de otra semana a horarioCreacion
  // RESPETA horarios ya guardados en la semana actual (no los sobrescribe)
  const copiarHorarioAVistaGrafica = (origenId) => {
    if (!origenId) {
      alert("Selecciona un horario para copiar.");
      return;
    }
    const origen = (horariosRepo || []).find((h) => h.id === origenId);
    if (!origen) {
      alert("No se encontró el horario seleccionado.");
      return;
    }
    
    // Clonar las celdas del horario origen
    const celdasOrigen = typeof structuredClone === "function"
      ? structuredClone(origen.celdas || {})
      : JSON.parse(JSON.stringify(origen.celdas || {}));
    
    // Obtener horarios ya guardados en la semana actual (NO se deben sobrescribir)
    const horariosYaGuardados = celdasVistaGrafica || {};
    
    // Convertir las celdas al formato de horarioCreacion
    const nuevosHorarios = {};
    let horariosCopiados = 0;
    let horariosRespetados = 0;
    const empleadosRespetados = [];
    
    Object.entries(celdasOrigen).forEach(([empId, dias]) => {
      // Solo procesar empleados activos
      const empleadoActivo = activos.find(emp => emp.id === empId);
      if (!empleadoActivo) return;
      
      nuevosHorarios[empId] = {};
      
      Object.entries(dias).forEach(([diaIdx, celda]) => {
        const diaIdxNum = parseInt(diaIdx);
        const fechaISO = fechasSemanaVistaGrafica[diaIdxNum];
        
        // No copiar a días cerrados
        if (isDiaCerradoVistaGrafica(fechaISO)) return;
        
        // Verificar si el empleado está bloqueado por novedad ese día
        if (empleadoBloqueadoPorNovedad(empId, diaIdxNum)) return;
        
        // 🔒 VERIFICAR SI YA EXISTE UN HORARIO GUARDADO PARA ESTE EMPLEADO/DÍA
        const horarioExistente = horariosYaGuardados[empId]?.[diaIdxNum];
        if (horarioExistente && (horarioExistente.e1 || horarioExistente.s1 || horarioExistente.esDescanso || horarioExistente.descanso)) {
          // Ya existe un horario guardado - NO SOBRESCRIBIR
          horariosRespetados++;
          if (!empleadosRespetados.find(e => e.id === empId)) {
            empleadosRespetados.push({
              id: empId,
              nombre: empleadoActivo.nombre,
              dia: DIAS[diaIdxNum]
            });
          }
          // Mantener el horario existente en nuevosHorarios
          if (horarioExistente.esDescanso || horarioExistente.descanso) {
            nuevosHorarios[empId][diaIdxNum] = {
              esDescanso: true,
              sede: 'DESCANSO'
            };
          } else {
            nuevosHorarios[empId][diaIdxNum] = {
              sede: horarioExistente.sede || horarioExistente.sede1 || '',
              sede2: horarioExistente.sede2 || horarioExistente.sede || horarioExistente.sede1 || '',
              e1: horarioExistente.e1,
              s1: horarioExistente.s1,
              e2: horarioExistente.e2 || '',
              s2: horarioExistente.s2 || ''
            };
          }
          return; // No copiar el horario de origen
        }
        
        // Detectar si es descanso
        const esDescanso = celda.descanso || celda.esDescanso || 
                          celda.tipo === 'DESCANSO' ||
                          (!celda.e1 && !celda.s1);
        
        if (esDescanso) {
          nuevosHorarios[empId][diaIdxNum] = {
            esDescanso: true,
            sede: 'DESCANSO'
          };
          horariosCopiados++;
        } else if (celda.e1 && celda.s1) {
          nuevosHorarios[empId][diaIdxNum] = {
            sede: celda.sede || celda.sede1 || '',
            sede2: celda.sede2 || celda.sede || celda.sede1 || '',
            e1: celda.e1,
            s1: celda.s1,
            e2: celda.e2 || '',
            s2: celda.s2 || ''
          };
          horariosCopiados++;
        }
      });
    });
    
    setHorarioCreacion(nuevosHorarios);
    setModalCopiarVistaGraficaOpen(false);
    
    const totalEmpleados = Object.keys(nuevosHorarios).length;
    const totalDias = Object.values(nuevosHorarios).reduce((acc, dias) => acc + Object.keys(dias).length, 0);
    
    let mensaje = `✅ Horarios copiados de la semana ${origen.semanaInicio} → ${origen.semanaFin}.\n\n` +
          `• ${totalEmpleados} empleados\n` +
          `• ${horariosCopiados} horarios copiados de la semana origen\n`;
    
    if (horariosRespetados > 0) {
      mensaje += `\n🔒 ${horariosRespetados} horarios YA GUARDADOS fueron respetados (no sobrescritos):\n`;
      empleadosRespetados.slice(0, 5).forEach(e => {
        mensaje += `   • ${e.nombre} (${e.dia})\n`;
      });
      if (empleadosRespetados.length > 5) {
        mensaje += `   ... y ${empleadosRespetados.length - 5} más\n`;
      }
    }
    
    mensaje += `\nRecuerda guardar cuando termines de ajustar.`;
    
    alert(mensaje);
  };
  
  // Verificar si un día está cerrado en la semana de Vista Gráfica
  const isDiaCerradoVistaGrafica = (fechaISO) => {
    return horarioExistenteVistaGrafica?.diasCerrados?.includes(fechaISO) || false;
  };
  
  // Obtener empleados con novedades activas (incapacidad, vacaciones, suspensiones)
  const getNovedadesEmpleadoPorDia = (empleadoId, fechaISO) => {
    const novedades = [];
    
    // Buscar en ausencias
    (ausencias || []).forEach(aus => {
      if (aus.empleadoId !== empleadoId) return;
      
      const inicio = aus.inicio;
      const fin = aus.fin || aus.inicio;
      
      if (fechaISO >= inicio && fechaISO <= fin) {
        const tipoLower = (aus.tipo || '').toLowerCase();
        if (tipoLower.includes('incapacidad')) {
          novedades.push({ tipo: 'INCAPACIDAD', motivo: aus.motivo, inicio, fin, bloquea: true });
        } else if (tipoLower.includes('vacacion')) {
          novedades.push({ tipo: 'VACACIONES', motivo: aus.motivo, inicio, fin, bloquea: true });
        } else if (tipoLower.includes('suspension') || tipoLower.includes('suspensión')) {
          novedades.push({ tipo: 'SUSPENSIÓN', motivo: aus.motivo, inicio, fin, bloquea: true });
        } else if (tipoLower.includes('licencia')) {
          novedades.push({ tipo: 'LICENCIA', motivo: aus.motivo, inicio, fin, bloquea: true });
        }
      }
    });
    
    return novedades;
  };
  
  // Verificar si empleado tiene novedad que bloquea horario
  const empleadoBloqueadoPorNovedad = (empleadoId, diaIdx) => {
    const fechaISO = fechasSemanaVistaGrafica[diaIdx];
    const novedades = getNovedadesEmpleadoPorDia(empleadoId, fechaISO);
    return novedades.find(n => n.bloquea) || null;
  };
  
  // Filtrar sedes por empresa actual (filtroEmpresa viene del componente padre)
  const sedesListaFiltradas = React.useMemo(() => {
    return (sedes || [])
      .filter(s => {
        // Si hay filtro de empresa, solo mostrar sedes de esa empresa
        if (typeof s === 'object' && s !== null) {
          if (s.empresa_id && filtroEmpresa) {
            return s.empresa_id === filtroEmpresa;
          }
        }
        return true;
      })
      .map((s) => {
        if (!s) return "";
        if (typeof s === "string") return s;
        return s.nombre || s.razonSocial || s.razonsocial || "";
      })
      .filter((nombre) => typeof nombre === "string" && nombre.trim().length > 0);
  }, [sedes, filtroEmpresa]);
  
  // Función para convertir hora 24h a formato AM/PM
  const formatearHoraAMPM = (hora24) => {
    if (!hora24) return '';
    const [hh, mm] = hora24.split(':');
    let h = parseInt(hh);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${mm} ${ampm}`;
  };
  
  // Función para verificar si un horario cruza con un turno específico
  const horarioCruzaConTurno = (e1, s1, e2, s2, turno) => {
    const parseHora = (h) => h ? parseInt(h.split(':')[0]) + parseInt(h.split(':')[1]) / 60 : null;
    const entrada1 = parseHora(e1);
    const salida1 = parseHora(s1);
    const entrada2 = parseHora(e2);
    const salida2 = parseHora(s2);
    
    const rangos = {
      'mañana': { inicio: 6, fin: 15 },
      'tarde': { inicio: 14, fin: 19 },
      'noche': { inicio: 18, fin: 24 }
    };
    
    if (!rangos[turno]) return { cruza: true, tipo: 'completo' };
    
    const { inicio, fin } = rangos[turno];
    
    // Verificar si el primer turno cruza
    let cruzaTurno1 = false;
    let entradaEnTurno1 = false;
    let salidaEnTurno1 = false;
    if (entrada1 !== null && salida1 !== null) {
      // La salida cruza con el turno
      if (salida1 > inicio && entrada1 < fin) {
        cruzaTurno1 = true;
        entradaEnTurno1 = entrada1 >= inicio && entrada1 < fin;
        salidaEnTurno1 = salida1 > inicio && salida1 <= fin;
      }
    }
    
    // Verificar si el segundo turno cruza
    let cruzaTurno2 = false;
    let entradaEnTurno2 = false;
    if (entrada2 !== null && salida2 !== null) {
      if (salida2 > inicio && entrada2 < fin) {
        cruzaTurno2 = true;
        entradaEnTurno2 = entrada2 >= inicio && entrada2 < fin;
      }
    }
    
    if (!cruzaTurno1 && !cruzaTurno2) return { cruza: false };
    
    // Determinar tipo de cruce
    let tipo = 'completo';
    let horaLimite = null;
    if (cruzaTurno1 && !entradaEnTurno1 && salidaEnTurno1) {
      tipo = 'viene_de_otro_turno';
      horaLimite = salida1;
    } else if (cruzaTurno2 && entradaEnTurno2) {
      tipo = 'entra_en_turno';
    }
    
    return { cruza: true, tipo, horaLimite };
  };
  
  // Función para verificar si dos rangos de horas se cruzan
  const horasSeCruzan = (inicio1, fin1, inicio2, fin2) => {
    const parseHora = (h) => {
      if (!h) return null;
      const [hh, mm] = h.split(':').map(Number);
      return hh * 60 + (mm || 0);
    };
    const i1 = parseHora(inicio1);
    const f1 = parseHora(fin1);
    const i2 = parseHora(inicio2);
    const f2 = parseHora(fin2);
    if (i1 === null || f1 === null || i2 === null || f2 === null) return false;
    // Se cruzan si hay cualquier solapamiento
    return (i1 < f2 && f1 > i2);
  };
  
  // Función para verificar cruce de horarios completo (incluyendo turnos partidos)
  const verificarCruceHorarioCompleto = (horario1, horario2) => {
    if (!horario1 || !horario2) return false;
    if (horario1.esDescanso || horario2.esDescanso) return false;
    
    // Verificar todos los turnos
    const turnos1 = [];
    if (horario1.e1 && horario1.s1) turnos1.push({ e: horario1.e1, s: horario1.s1 });
    if (horario1.e2 && horario1.s2) turnos1.push({ e: horario1.e2, s: horario1.s2 });
    
    const turnos2 = [];
    if (horario2.e1 && horario2.s1) turnos2.push({ e: horario2.e1, s: horario2.s1 });
    if (horario2.e2 && horario2.s2) turnos2.push({ e: horario2.e2, s: horario2.s2 });
    
    for (const t1 of turnos1) {
      for (const t2 of turnos2) {
        if (horasSeCruzan(t1.e, t1.s, t2.e, t2.s)) {
          return true;
        }
      }
    }
    return false;
  };
  
  // Función para agregar horario a empleado con validación de cruce
  const agregarHorarioConValidacion = (empId, diaIdx, nuevoHorario, sedeNueva) => {
    const horarioActual = horarioCreacion[empId]?.[diaIdx];
    const horariosAdicionales = horarioActual?.horariosAdicionales || [];
    
    // Si no hay horario actual o es descanso, simplemente asignar
    if (!horarioActual || horarioActual.esDescanso) {
      return { permitido: true };
    }
    
    // Si la sede es la misma, es una actualización
    if (horarioActual.sede?.toUpperCase() === sedeNueva?.toUpperCase()) {
      return { permitido: true, esActualizacion: true };
    }
    
    // Si hay horario en otra sede, verificar cruce
    const todosLosHorarios = [horarioActual, ...horariosAdicionales];
    
    for (const h of todosLosHorarios) {
      if (verificarCruceHorarioCompleto(nuevoHorario, h)) {
        return { 
          permitido: false, 
          sedeConflicto: h.sede,
          mensaje: `⚠️ Las horas se cruzan con el horario asignado en ${h.sede}. No se permite cruzar ni una sola hora.`
        };
      }
    }
    
    // No hay cruce, se puede agregar como horario adicional
    return { permitido: true, esAdicional: true };
  };
  
  // Función para verificar cruce de horarios para un empleado en un día específico
  const verificarCruceHorariosEmpleado = (empId, diaIdx, nuevoHorario, horariosActuales) => {
    if (!nuevoHorario || nuevoHorario.esDescanso) return { hayCruce: false };
    
    // Obtener todos los horarios del mismo empleado en el mismo día pero en otras sedes
    const horariosDelDia = [];
    
    // Buscar en horarios existentes (de la base de datos)
    if (horarioExistenteVistaGrafica?.celdas?.[empId]?.[diaIdx]) {
      const hExistente = horarioExistenteVistaGrafica.celdas[empId][diaIdx];
      if (!hExistente.descanso && hExistente.e1 && hExistente.s1) {
        horariosDelDia.push({
          sede: hExistente.sede || hExistente.sede1,
          e1: hExistente.e1,
          s1: hExistente.s1,
          e2: hExistente.e2,
          s2: hExistente.s2
        });
      }
    }
    
    // Buscar en horarios en creación (para otras sedes del mismo día)
    const horarioCreacionDia = horarioCreacion[empId]?.[diaIdx];
    if (horarioCreacionDia && !horarioCreacionDia.esDescanso) {
      horariosDelDia.push({
        sede: horarioCreacionDia.sede,
        e1: horarioCreacionDia.e1,
        s1: horarioCreacionDia.s1,
        e2: horarioCreacionDia.e2,
        s2: horarioCreacionDia.s2
      });
      // También verificar horarios adicionales
      const adicionales = horarioCreacionDia.horariosAdicionales || [];
      adicionales.forEach(h => {
        if (h.e1 && h.s1) {
          horariosDelDia.push(h);
        }
      });
    }
    
    // Verificar cruce
    for (const h of horariosDelDia) {
      // Comparar turno 1 con turno 1
      if (horasSeCruzan(nuevoHorario.e1, nuevoHorario.s1, h.e1, h.s1)) {
        return { hayCruce: true, sedeConflicto: h.sede, tipoConflicto: 'Turno 1' };
      }
      // Comparar turno 1 con turno 2 (si existe)
      if (h.e2 && h.s2 && horasSeCruzan(nuevoHorario.e1, nuevoHorario.s1, h.e2, h.s2)) {
        return { hayCruce: true, sedeConflicto: h.sede, tipoConflicto: 'Turno 2' };
      }
      // Si el nuevo horario tiene turno 2
      if (nuevoHorario.e2 && nuevoHorario.s2) {
        if (horasSeCruzan(nuevoHorario.e2, nuevoHorario.s2, h.e1, h.s1)) {
          return { hayCruce: true, sedeConflicto: h.sede, tipoConflicto: 'Turno 1' };
        }
        if (h.e2 && h.s2 && horasSeCruzan(nuevoHorario.e2, nuevoHorario.s2, h.e2, h.s2)) {
          return { hayCruce: true, sedeConflicto: h.sede, tipoConflicto: 'Turno 2' };
        }
      }
    }
    
    return { hayCruce: false };
  };
  
  // TIPOS DE HORARIO PREDEFINIDOS - Todos los horarios disponibles
  const TIPOS_HORARIO_PREDEFINIDOS = [
    // Derechos
    { id: 'derecho_1', nombre: 'Derecho 1', e1: '11:30', s1: '23:00', e2: '', s2: '' },
    { id: 'derecho_2', nombre: 'Derecho 2', e1: '16:00', s1: '23:00', e2: '', s2: '' },
    { id: 'derecho_3', nombre: 'Derecho 3 Tarde', e1: '15:00', s1: '23:00', e2: '', s2: '' },
    { id: 'derecho_4', nombre: 'Derecho 4', e1: '16:00', s1: '23:00', e2: '', s2: '' },
    { id: 'derecho_5', nombre: 'Derecho 5', e1: '17:00', s1: '23:00', e2: '', s2: '' },
    { id: 'derecho_6', nombre: 'Derecho 6', e1: '18:00', s1: '23:00', e2: '', s2: '' },
    // Partidos
    { id: 'partido_1', nombre: 'Partido 1', e1: '11:00', s1: '15:00', e2: '19:00', s2: '23:00' },
    { id: 'partido_2', nombre: 'Partido 2', e1: '11:00', s1: '15:00', e2: '18:00', s2: '23:00' },
    // Partidos Aseo
    { id: 'partido_3_aseo', nombre: 'Partido 3 Aseo 1', e1: '09:30', s1: '14:00', e2: '18:00', s2: '23:00' },
    { id: 'partido_4_aseo', nombre: 'Partido 4 Aseo 2', e1: '09:30', s1: '15:00', e2: '19:00', s2: '23:00' },
    // Descanso
    { id: 'descanso', nombre: 'DESCANSO', e1: '', s1: '', e2: '', s2: '', esDescanso: true }
  ];
  
  // Función para mapear un horario histórico al tipo predefinido más cercano
  const mapearATipoPredefinido = (horarioHistorico) => {
    if (!horarioHistorico || !horarioHistorico.e1 || !horarioHistorico.s1) {
      return TIPOS_HORARIO_PREDEFINIDOS.find(t => t.esDescanso);
    }
    
    const parseHora = (h) => {
      if (!h) return 0;
      const [hh, mm] = h.split(':').map(Number);
      return hh * 60 + (mm || 0);
    };
    
    const e1Hist = parseHora(horarioHistorico.e1);
    const s1Hist = parseHora(horarioHistorico.s1);
    const e2Hist = parseHora(horarioHistorico.e2);
    const s2Hist = parseHora(horarioHistorico.s2);
    const esPartido = !!(horarioHistorico.e2 && horarioHistorico.s2);
    
    // Tipos de trabajo (sin descanso)
    const tiposTrabajo = TIPOS_HORARIO_PREDEFINIDOS.filter(t => !t.esDescanso);
    
    let mejorCoincidencia = null;
    let menorDiferencia = Infinity;
    
    tiposTrabajo.forEach(tipo => {
      const e1Tipo = parseHora(tipo.e1);
      const s1Tipo = parseHora(tipo.s1);
      const e2Tipo = parseHora(tipo.e2);
      const s2Tipo = parseHora(tipo.s2);
      const tipoEsPartido = !!(tipo.e2 && tipo.s2);
      
      // Penalizar si uno es partido y otro no
      let diferencia = 0;
      if (esPartido !== tipoEsPartido) {
        diferencia += 500; // Penalización por tipo diferente
      }
      
      // Diferencia en horarios principales
      diferencia += Math.abs(e1Hist - e1Tipo);
      diferencia += Math.abs(s1Hist - s1Tipo);
      
      // Diferencia en segundo turno (si aplica)
      if (esPartido && tipoEsPartido) {
        diferencia += Math.abs(e2Hist - e2Tipo);
        diferencia += Math.abs(s2Hist - s2Tipo);
      }
      
      if (diferencia < menorDiferencia) {
        menorDiferencia = diferencia;
        mejorCoincidencia = tipo;
      }
    });
    
    return mejorCoincidencia || tiposTrabajo[0];
  };
  
  // Calcular horas trabajadas de un horario
  const calcularHorasTrabajadas = (e1, s1, e2, s2) => {
    const parseHora = (h) => {
      if (!h) return null;
      const [hh, mm] = h.split(':');
      return parseInt(hh) + parseInt(mm) / 60;
    };
    
    let total = 0;
    const entrada1 = parseHora(e1);
    const salida1 = parseHora(s1);
    if (entrada1 !== null && salida1 !== null) {
      let diff = salida1 - entrada1;
      if (diff < 0) diff += 24; // Cruza medianoche
      total += diff;
    }
    
    const entrada2 = parseHora(e2);
    const salida2 = parseHora(s2);
    if (entrada2 !== null && salida2 !== null) {
      let diff = salida2 - entrada2;
      if (diff < 0) diff += 24;
      total += diff;
    }
    
    return total;
  };
  
  // Validar horarios antes de guardar - Validación completa
  const validarHorariosAntesDeGuardar = () => {
    const alertas = [];
    const empleadosConMas7Horas = [];
    const empleadosSinHorario = [];
    const empleadosSinDescanso = [];
    const empleadosConNovedades = [];
    const diasCerradosConflicto = [];
    
    // Verificar todos los empleados activos
    activos.forEach(emp => {
      const diasEmp = horarioCreacion[emp.id] || {};
      let tieneHorario = false;
      let tieneDescanso = false;
      
      // Revisar cada día de la semana
      for (let diaIdx = 0; diaIdx < 7; diaIdx++) {
        const horario = diasEmp[diaIdx];
        const fechaISO = fechasSemanaVistaGrafica[diaIdx];
        
        // Verificar si el día está cerrado
        if (isDiaCerradoVistaGrafica(fechaISO) && horario) {
          diasCerradosConflicto.push({
            empleado: emp.nombre,
            dia: DIAS[diaIdx],
            fecha: fechaISO
          });
        }
        
        // Verificar novedades que bloquean
        const novedad = empleadoBloqueadoPorNovedad(emp.id, diaIdx);
        if (novedad && horario && !horario.esDescanso) {
          empleadosConNovedades.push({
            empleado: emp.nombre,
            dia: DIAS[diaIdx],
            novedad: novedad.tipo
          });
        }
        
        if (horario) {
          if (horario.esDescanso) {
            tieneDescanso = true;
          } else if (horario.e1 && horario.s1) {
            tieneHorario = true;
            
            // Verificar horas trabajadas
            const horas = calcularHorasTrabajadas(horario.e1, horario.s1, horario.e2, horario.s2);
            if (horas > 7) {
              empleadosConMas7Horas.push({
                empleado: emp.nombre,
                dia: DIAS[diaIdx],
                horas: horas.toFixed(1)
              });
            }
          }
        }
      }
      
      // Verificar si tiene al menos un horario asignado
      if (!tieneHorario && Object.keys(diasEmp).length === 0) {
        empleadosSinHorario.push({ empleado: emp.nombre });
      }
      
      // Verificar si tiene descanso asignado
      if (tieneHorario && !tieneDescanso) {
        empleadosSinDescanso.push({ empleado: emp.nombre });
      }
    });
    
    // Agregar alertas
    if (diasCerradosConflicto.length > 0) {
      alertas.push({
        tipo: 'error',
        titulo: '🔒 No se puede modificar días cerrados',
        empleados: diasCerradosConflicto,
        bloquea: true
      });
    }
    
    if (empleadosConNovedades.length > 0) {
      alertas.push({
        tipo: 'error',
        titulo: '🚫 Empleados con novedades que bloquean horario',
        empleados: empleadosConNovedades,
        bloquea: true
      });
    }
    
    if (empleadosConMas7Horas.length > 0) {
      alertas.push({
        tipo: 'advertencia',
        titulo: '⚠️ Empleados con más de 7 horas de trabajo',
        empleados: empleadosConMas7Horas
      });
    }
    
    if (empleadosSinHorario.length > 0) {
      alertas.push({
        tipo: 'info',
        titulo: '📋 Empleados sin horario asignado',
        empleados: empleadosSinHorario
      });
    }
    
    if (empleadosSinDescanso.length > 0) {
      alertas.push({
        tipo: 'advertencia',
        titulo: '💤 Empleados sin descanso asignado',
        empleados: empleadosSinDescanso
      });
    }
    
    setAlertasHorario(alertas);
    return alertas;
  };
  
  // Generar sugerencias de horarios basadas en los últimos 2 meses
  // Respeta horarios ya guardados en la semana seleccionada
  // SOLO para empleados ACTIVOS - Sugerencias inteligentes basadas en patrones históricos
  // Parámetro diaEspecifico: si es un número (0-6), genera sugerencias considerando
  // que solo se está editando ese día, sin asignar descansos automáticos
  const generarSugerenciasHorarios = async (diaEspecifico = null) => {
    try {
      const esSoloDia = diaEspecifico !== null && diaEspecifico >= 0 && diaEspecifico <= 6;
      
      // Obtener IDs de empleados activos
      const idsEmpleadosActivos = new Set(activos.map(emp => emp.id));
      
      // Obtener horarios de los últimos 2 meses ordenados por fecha (más reciente primero)
      const hoy = new Date();
      const hace2Meses = new Date(hoy);
      hace2Meses.setMonth(hace2Meses.getMonth() - 2);
      
      const horariosHistoricos = horariosRepo
        .filter(h => {
          const fechaInicio = new Date(h.semanaInicio);
          return fechaInicio >= hace2Meses && fechaInicio <= hoy;
        })
        .sort((a, b) => new Date(b.semanaInicio) - new Date(a.semanaInicio)); // Más reciente primero
      
      if (horariosHistoricos.length === 0) {
        alert('⚠️ No hay horarios históricos en los últimos 2 meses para generar sugerencias.');
        return null;
      }
      
      // Horarios ya guardados en Vista Gráfica actual
      const horarioYaGuardado = horarioExistenteVistaGrafica?.celdas || {};
      
      // Horarios ya asignados manualmente en horarioCreacion
      const horarioYaAsignado = horarioCreacion || {};
      
      // Analizar patrones por empleado y día con pesos por recencia
      // Las semanas más recientes tienen más peso
      const patrones = {};
      const totalSemanas = horariosHistoricos.length;
      
      horariosHistoricos.forEach((horario, semanaIdx) => {
        const celdasHorario = horario.celdas || {};
        // Peso: semanas más recientes pesan más (peso de 1 a totalSemanas)
        const peso = totalSemanas - semanaIdx;
        
        Object.entries(celdasHorario).forEach(([empId, dias]) => {
          // Solo procesar empleados activos
          if (!idsEmpleadosActivos.has(empId)) return;
          
          if (!patrones[empId]) {
            patrones[empId] = {
              diasData: {},
              ultimaSemana: null
            };
          }
          
          // Guardar la última semana completa del empleado
          if (semanaIdx === 0 && Object.keys(dias).length > 0) {
            patrones[empId].ultimaSemana = dias;
          }
          
          Object.entries(dias).forEach(([diaIdx, celda]) => {
            if (!patrones[empId].diasData[diaIdx]) {
              patrones[empId].diasData[diaIdx] = {
                horarios: [],
                descansos: 0,
                trabajos: 0
              };
            }
            
            // Detectar si es descanso
            const esDescanso = celda.descanso || celda.esDescanso || 
                              (!celda.e1 && !celda.s1) ||
                              (celda.e1 === '' && celda.s1 === '');
            
            if (esDescanso) {
              patrones[empId].diasData[diaIdx].descansos += peso;
            } else if (celda.e1 && celda.s1) {
              patrones[empId].diasData[diaIdx].trabajos += peso;
              patrones[empId].diasData[diaIdx].horarios.push({
                sede: celda.sede || celda.sede1 || '',
                e1: celda.e1,
                s1: celda.s1,
                e2: celda.e2 || '',
                s2: celda.s2 || '',
                peso: peso
              });
            }
          });
        });
      });
      
      // Calcular sugerencias inteligentes para cada empleado y día
      const sugerencias = {};
      let totalEmpleados = 0;
      let empleadosConDescanso = 0;
      
      Object.entries(patrones).forEach(([empId, data]) => {
        // Verificar que el empleado esté activo
        if (!idsEmpleadosActivos.has(empId)) return;
        
        sugerencias[empId] = {};
        totalEmpleados++;
        let tieneAlgunDescanso = false;
        
        // Procesar los 7 días de la semana (0-6)
        for (let diaIdx = 0; diaIdx < 7; diaIdx++) {
          const diaStr = diaIdx.toString();
          
          // 1. Si ya está asignado en horarioCreacion, respetarlo
          const yaAsignado = horarioYaAsignado[empId]?.[diaIdx];
          if (yaAsignado && (yaAsignado.e1 || yaAsignado.esDescanso)) {
            sugerencias[empId][diaStr] = { 
              ...yaAsignado, 
              yaAsignado: true 
            };
            if (yaAsignado.esDescanso) tieneAlgunDescanso = true;
            continue;
          }
          
          // 2. Si ya está guardado en Vista Gráfica, respetarlo
          const yaGuardado = horarioYaGuardado[empId]?.[diaStr];
          if (yaGuardado && (yaGuardado.e1 || yaGuardado.descanso)) {
            if (yaGuardado.descanso) {
              sugerencias[empId][diaStr] = {
                esDescanso: true,
                sede: 'DESCANSO',
                yaGuardado: true
              };
              tieneAlgunDescanso = true;
            } else {
              sugerencias[empId][diaStr] = {
                sede: yaGuardado.sede || yaGuardado.sede1 || '',
                e1: yaGuardado.e1,
                s1: yaGuardado.s1,
                e2: yaGuardado.e2 || '',
                s2: yaGuardado.s2 || '',
                yaGuardado: true
              };
            }
            continue;
          }
          
          // 3. Usar datos históricos para sugerir
          const diaData = data.diasData[diaStr];
          
          if (!diaData) {
            // Si no hay datos históricos para este día, seguir el patrón de la última semana
            const ultimaSemanaData = data.ultimaSemana?.[diaStr];
            if (ultimaSemanaData) {
              const esDescansoUltima = ultimaSemanaData.descanso || ultimaSemanaData.esDescanso ||
                                       (!ultimaSemanaData.e1 && !ultimaSemanaData.s1);
              // Si es día específico y no hay datos, preferir horario de trabajo
              if (esDescansoUltima && !esSoloDia) {
                sugerencias[empId][diaStr] = {
                  esDescanso: true,
                  sede: 'DESCANSO'
                };
                tieneAlgunDescanso = true;
              } else if (ultimaSemanaData.e1 && ultimaSemanaData.s1) {
                // Mapear al tipo predefinido más cercano
                const tipoPredefinido = mapearATipoPredefinido(ultimaSemanaData);
                sugerencias[empId][diaStr] = {
                  sede: ultimaSemanaData.sede || ultimaSemanaData.sede1 || '',
                  e1: tipoPredefinido.e1,
                  s1: tipoPredefinido.s1,
                  e2: tipoPredefinido.e2 || '',
                  s2: tipoPredefinido.s2 || '',
                  tipoHorario: tipoPredefinido.nombre
                };
              } else if (esSoloDia && !esDescansoUltima) {
                // Si es día específico y la última semana no era descanso pero no tiene horario,
                // buscar el horario más frecuente del empleado en cualquier día
                const horarioMasFrecuente = Object.values(data.diasData)
                  .filter(d => d.horarios && d.horarios.length > 0)
                  .flatMap(d => d.horarios)
                  .sort((a, b) => (b.peso || 0) - (a.peso || 0))[0];
                
                if (horarioMasFrecuente) {
                  const tipoPredefinido = mapearATipoPredefinido(horarioMasFrecuente);
                  sugerencias[empId][diaStr] = {
                    sede: horarioMasFrecuente.sede || '',
                    e1: tipoPredefinido.e1,
                    s1: tipoPredefinido.s1,
                    e2: tipoPredefinido.e2 || '',
                    s2: tipoPredefinido.s2 || '',
                    tipoHorario: tipoPredefinido.nombre
                  };
                }
              }
            }
            continue;
          }
          
          // 4. Decidir si es día de trabajo o descanso basado en frecuencia ponderada
          const totalPesoDescanso = diaData.descansos;
          const totalPesoTrabajo = diaData.trabajos;
          const totalPeso = totalPesoDescanso + totalPesoTrabajo;
          
          // Si es día específico, solo sugerir descanso si históricamente es MUY frecuentemente descanso (>70%)
          // Si es semana completa, usar la lógica normal (descanso > trabajo)
          const umbralDescanso = esSoloDia ? 0.7 : 0.5;
          const porcentajeDescanso = totalPeso > 0 ? totalPesoDescanso / totalPeso : 0;
          
          // Si tiene más peso de descanso según el umbral, sugerir descanso
          if (porcentajeDescanso >= umbralDescanso) {
            sugerencias[empId][diaStr] = {
              esDescanso: true,
              sede: 'DESCANSO'
            };
            tieneAlgunDescanso = true;
          } else if (diaData.horarios.length > 0) {
            // 5. Mapear cada horario histórico a tipo predefinido y contar frecuencias
            const frecuenciasTipos = {};
            const sedesMasFrecuentes = {};
            
            diaData.horarios.forEach(h => {
              const tipoPredefinido = mapearATipoPredefinido(h);
              const tipoKey = tipoPredefinido.id;
              frecuenciasTipos[tipoKey] = (frecuenciasTipos[tipoKey] || 0) + h.peso;
              
              // Guardar la sede más frecuente para cada tipo
              if (!sedesMasFrecuentes[tipoKey]) sedesMasFrecuentes[tipoKey] = {};
              sedesMasFrecuentes[tipoKey][h.sede || ''] = (sedesMasFrecuentes[tipoKey][h.sede || ''] || 0) + h.peso;
            });
            
            // Obtener el tipo más frecuente
            const tipoMasFrecuente = Object.entries(frecuenciasTipos)
              .sort((a, b) => b[1] - a[1])[0];
            
            if (tipoMasFrecuente) {
              const tipoPredefinido = TIPOS_HORARIO_PREDEFINIDOS.find(t => t.id === tipoMasFrecuente[0]);
              
              // Obtener la sede más frecuente para ese tipo
              const sedesDelTipo = sedesMasFrecuentes[tipoMasFrecuente[0]] || {};
              const sedeMasFrecuente = Object.entries(sedesDelTipo)
                .sort((a, b) => b[1] - a[1])[0]?.[0] || '';
              
              if (tipoPredefinido) {
                sugerencias[empId][diaStr] = { 
                  sede: sedeMasFrecuente, 
                  e1: tipoPredefinido.e1, 
                  s1: tipoPredefinido.s1, 
                  e2: tipoPredefinido.e2 || '', 
                  s2: tipoPredefinido.s2 || '',
                  tipoHorario: tipoPredefinido.nombre
                };
              }
            }
          } else if (totalPesoDescanso > 0) {
            // Si no hay horarios de trabajo pero sí descansos registrados
            sugerencias[empId][diaStr] = {
              esDescanso: true,
              sede: 'DESCANSO'
            };
            tieneAlgunDescanso = true;
          }
        }
        
        if (tieneAlgunDescanso) empleadosConDescanso++;
      });
      
      // POST-PROCESAMIENTO: Asegurar descansos para empleados de tiempo completo
      // (Solo aplica cuando se genera para semana completa, NO para día específico)
      // (Los empleados que trabajan por días NO necesitan descanso en sugerencias)
      if (!esSoloDia) {
        Object.entries(sugerencias).forEach(([empId, dias]) => {
          const diasConHorario = Object.values(dias).filter(d => !d.esDescanso && d.e1).length;
          const diasConDescanso = Object.values(dias).filter(d => d.esDescanso).length;
          
          // Si tiene 6-7 días con horario y 0 descansos, es empleado de tiempo completo sin descanso
          // Buscar el día de lunes a jueves (0-3) con menos carga histórica para sugerir descanso
          if (diasConHorario >= 6 && diasConDescanso === 0) {
            const data = patrones[empId];
            if (!data) return;
            
            // Buscar entre lunes (0) y jueves (3) el día con más descansos históricos o menos trabajos
            let mejorDiaDescanso = null;
            let mejorPuntaje = -Infinity;
            
            for (let dia = 0; dia <= 3; dia++) {
              const diaStr = dia.toString();
              const diaData = data.diasData[diaStr];
              
              // Si ya tiene horario guardado o asignado, no cambiar
              if (sugerencias[empId][diaStr]?.yaGuardado || sugerencias[empId][diaStr]?.yaAsignado) continue;
              
              // Calcular puntaje: más descansos históricos = mejor candidato
              const puntaje = diaData ? (diaData.descansos - diaData.trabajos) : 0;
              if (puntaje > mejorPuntaje || mejorDiaDescanso === null) {
                mejorPuntaje = puntaje;
                mejorDiaDescanso = diaStr;
              }
            }
            
            // Asignar descanso si encontramos un día candidato
            if (mejorDiaDescanso !== null) {
              sugerencias[empId][mejorDiaDescanso] = {
                esDescanso: true,
                sede: 'DESCANSO',
                sugeridoAutomatico: true
              };
              empleadosConDescanso++;
            }
          }
        });
      }
      
      setSugerenciasHorarios(sugerencias);
      
      const diasConSugerencia = Object.values(sugerencias).reduce((acc, dias) => acc + Object.keys(dias).length, 0);
      alert(`✅ Sugerencias inteligentes generadas:\n` +
            `• ${totalEmpleados} empleados activos\n` +
            `• ${diasConSugerencia} días sugeridos\n` +
            `• ${empleadosConDescanso} empleados con días de descanso\n` +
            `• Basado en ${horariosHistoricos.length} semanas de historial\n\n` +
            `Las sugerencias siguen los patrones históricos y respetan horarios ya asignados.`);
      
      return sugerencias;
    } catch (error) {
      console.error('Error generando sugerencias:', error);
      alert('❌ Error al generar sugerencias: ' + error.message);
      return null;
    }
  };
  
  // Efecto para normalizar automáticamente celdas con sedes inconsistentes
  // Se ejecuta cuando se cargan los horarios y detecta inconsistencias
  React.useEffect(() => {
    if (!horarioExistenteVistaGrafica?.celdas) return;
    
    const celdasOriginales = horarioExistenteVistaGrafica.celdas;
    let tieneInconsistencias = false;
    
    // Detectar si hay inconsistencias
    Object.entries(celdasOriginales).forEach(([empId, dias]) => {
      Object.entries(dias).forEach(([diaIdx, celda]) => {
        // Detectar inconsistencia: sede y sede1 son diferentes
        if (celda.sede && celda.sede1 && 
            celda.sede.toUpperCase() !== celda.sede1.toUpperCase()) {
          tieneInconsistencias = true;
        }
      });
    });
    
    // Si hay inconsistencias, normalizar automáticamente
    if (tieneInconsistencias) {
      console.log('🔧 Detectadas inconsistencias en sedes, normalizando automáticamente...');
      
      const celdasNormalizadas = {};
      let celdasCorregidas = 0;
      
      Object.entries(celdasOriginales).forEach(([empId, dias]) => {
        celdasNormalizadas[empId] = {};
        
        Object.entries(dias).forEach(([diaIdx, celda]) => {
          // Normalizar: sede siempre debe ser igual a sede1
          const sedePrincipal = celda.sede1 || celda.sede || '';
          const sedeSecundaria = celda.sede2 || sedePrincipal;
          
          const inconsistente = celda.sede && celda.sede1 && 
                               celda.sede.toUpperCase() !== celda.sede1.toUpperCase();
          
          if (inconsistente) celdasCorregidas++;
          
          celdasNormalizadas[empId][diaIdx] = {
            ...celda,
            sede: sedePrincipal,
            sede1: sedePrincipal,
            sede2: sedeSecundaria
          };
        });
      });
      
      // Actualizar en memoria y guardar
      const payload = {
        ...horarioExistenteVistaGrafica,
        celdas: celdasNormalizadas,
        actualizadoEn: new Date().toISOString()
      };
      
      onActualizarHorario(horarioExistenteVistaGrafica.id, payload);
      console.log(`✅ Normalización automática completada: ${celdasCorregidas} celdas corregidas`);
    }
  }, [horarioExistenteVistaGrafica?.id]); // Solo se ejecuta cuando cambia el horario
  
  // Aplicar sugerencias a horarioCreacion
  // Respeta días cerrados, empleados bloqueados por novedades, horarios ya guardados y horarios ya asignados
  const aplicarSugerenciasHorarios = (solodia = null) => {
    if (!sugerenciasHorarios) {
      alert('⚠️ Primero debes generar las sugerencias presionando el botón 🤖');
      return;
    }
    
    const nuevosHorarios = { ...horarioCreacion };
    const horariosYaGuardados = celdasVistaGrafica || {};
    let aplicadosHorarios = 0;
    let aplicadosDescansos = 0;
    let omitidosCerrado = 0;
    let omitidosNovedad = 0;
    let omitidosYaAsignados = 0;
    let omitidosYaGuardados = 0;
    
    Object.entries(sugerenciasHorarios).forEach(([empId, dias]) => {
      if (!nuevosHorarios[empId]) nuevosHorarios[empId] = {};
      
      Object.entries(dias).forEach(([diaIdx, horario]) => {
        const diaIdxNum = parseInt(diaIdx);
        if (solodia !== null && diaIdxNum !== solodia) return;
        
        // 🔒 VERIFICAR SI YA EXISTE UN HORARIO GUARDADO EN LA BASE DE DATOS
        const horarioGuardado = horariosYaGuardados[empId]?.[diaIdxNum];
        if (horarioGuardado && (horarioGuardado.e1 || horarioGuardado.s1 || horarioGuardado.esDescanso || horarioGuardado.descanso)) {
          omitidosYaGuardados++;
          // Mantener el horario guardado en nuevosHorarios
          if (horarioGuardado.esDescanso || horarioGuardado.descanso) {
            nuevosHorarios[empId][diaIdxNum] = {
              esDescanso: true,
              sede: 'DESCANSO'
            };
          } else {
            nuevosHorarios[empId][diaIdxNum] = {
              sede: horarioGuardado.sede || horarioGuardado.sede1 || '',
              sede2: horarioGuardado.sede2 || horarioGuardado.sede || horarioGuardado.sede1 || '',
              e1: horarioGuardado.e1,
              s1: horarioGuardado.s1,
              e2: horarioGuardado.e2 || '',
              s2: horarioGuardado.s2 || ''
            };
          }
          return;
        }
        
        // Si ya tiene asignación previa (no vino de sugerencias), respetar
        const yaAsignado = nuevosHorarios[empId]?.[diaIdxNum];
        if (yaAsignado && !horario.yaAsignado && !horario.yaGuardado && 
            (yaAsignado.e1 || yaAsignado.esDescanso)) {
          omitidosYaAsignados++;
          return;
        }
        
        // Verificar si el día está cerrado
        const fechaISO = fechasSemanaVistaGrafica[diaIdxNum];
        if (isDiaCerradoVistaGrafica(fechaISO)) {
          omitidosCerrado++;
          return;
        }
        
        // Verificar si el empleado está bloqueado por novedad
        if (empleadoBloqueadoPorNovedad(empId, diaIdxNum)) {
          omitidosNovedad++;
          return;
        }
        
        // Aplicar sugerencia
        if (horario.esDescanso) {
          // Si es solo día y el descanso fue sugerido automáticamente, NO aplicarlo
          // Solo aplicar descanso si históricamente este día es descanso
          if (solodia !== null && horario.sugeridoAutomatico) {
            // No aplicar descanso sugerido automáticamente cuando es día específico
            return;
          }
          nuevosHorarios[empId][diaIdxNum] = { 
            esDescanso: true, 
            sede: 'DESCANSO' 
          };
          aplicadosDescansos++;
        } else if (horario.e1 && horario.s1) {
          nuevosHorarios[empId][diaIdxNum] = { 
            sede: horario.sede || '',
            e1: horario.e1,
            s1: horario.s1,
            e2: horario.e2 || '',
            s2: horario.s2 || ''
          };
          aplicadosHorarios++;
        }
      });
    });
    
    setHorarioCreacion(nuevosHorarios);
    
    const totalOmitidos = omitidosCerrado + omitidosNovedad + omitidosYaAsignados + omitidosYaGuardados;
    let mensaje = `✅ Sugerencias aplicadas:\n• ${aplicadosHorarios} horarios de trabajo\n• ${aplicadosDescansos} descansos`;
    
    if (totalOmitidos > 0) {
      mensaje += `\n\n⚠️ ${totalOmitidos} omitidos:`;
      if (omitidosYaGuardados > 0) mensaje += `\n• ${omitidosYaGuardados} ya guardados (permisos especiales)`;
      if (omitidosCerrado > 0) mensaje += `\n• ${omitidosCerrado} por días cerrados`;
      if (omitidosNovedad > 0) mensaje += `\n• ${omitidosNovedad} por novedades`;
      if (omitidosYaAsignados > 0) mensaje += `\n• ${omitidosYaAsignados} ya tenían asignación`;
    }
    
    alert(mensaje);
  };
  
  // Deshacer horarios por día o semana
  // RESPETA horarios ya guardados en la base de datos (no los borra)
  const deshacerHorarios = (solodia = null) => {
    const horariosYaGuardados = celdasVistaGrafica || {};
    let horariosRespetados = 0;
    
    if (solodia !== null) {
      // Deshacer solo un día específico
      const nuevosHorarios = { ...horarioCreacion };
      Object.keys(nuevosHorarios).forEach(empId => {
        // 🔒 Verificar si existe un horario guardado para este empleado/día
        const horarioGuardado = horariosYaGuardados[empId]?.[solodia];
        if (horarioGuardado && (horarioGuardado.e1 || horarioGuardado.s1 || horarioGuardado.esDescanso || horarioGuardado.descanso)) {
          // Mantener el horario guardado
          horariosRespetados++;
          if (horarioGuardado.esDescanso || horarioGuardado.descanso) {
            nuevosHorarios[empId][solodia] = {
              esDescanso: true,
              sede: 'DESCANSO'
            };
          } else {
            nuevosHorarios[empId][solodia] = {
              sede: horarioGuardado.sede || horarioGuardado.sede1 || '',
              sede2: horarioGuardado.sede2 || horarioGuardado.sede || horarioGuardado.sede1 || '',
              e1: horarioGuardado.e1,
              s1: horarioGuardado.s1,
              e2: horarioGuardado.e2 || '',
              s2: horarioGuardado.s2 || ''
            };
          }
        } else if (nuevosHorarios[empId][solodia]) {
          delete nuevosHorarios[empId][solodia];
        }
      });
      setHorarioCreacion(nuevosHorarios);
      
      if (horariosRespetados > 0) {
        alert(`↩️ Día deshecho.\n\n🔒 ${horariosRespetados} horarios ya guardados fueron respetados.`);
      }
    } else {
      // Deshacer toda la semana - pero mantener horarios ya guardados
      const nuevosHorarios = {};
      
      // Recorrer todos los empleados activos y sus horarios guardados
      activos.forEach(emp => {
        const celdasEmp = horariosYaGuardados[emp.id];
        if (!celdasEmp) return;
        
        nuevosHorarios[emp.id] = {};
        
        Object.entries(celdasEmp).forEach(([diaIdx, horarioGuardado]) => {
          if (horarioGuardado && (horarioGuardado.e1 || horarioGuardado.s1 || horarioGuardado.esDescanso || horarioGuardado.descanso)) {
            horariosRespetados++;
            if (horarioGuardado.esDescanso || horarioGuardado.descanso) {
              nuevosHorarios[emp.id][diaIdx] = {
                esDescanso: true,
                sede: 'DESCANSO'
              };
            } else {
              nuevosHorarios[emp.id][diaIdx] = {
                sede: horarioGuardado.sede || horarioGuardado.sede1 || '',
                sede2: horarioGuardado.sede2 || horarioGuardado.sede || horarioGuardado.sede1 || '',
                e1: horarioGuardado.e1,
                s1: horarioGuardado.s1,
                e2: horarioGuardado.e2 || '',
                s2: horarioGuardado.s2 || ''
              };
            }
          }
        });
        
        // Si el empleado no tiene horarios guardados, eliminar entrada vacía
        if (Object.keys(nuevosHorarios[emp.id]).length === 0) {
          delete nuevosHorarios[emp.id];
        }
      });
      
      setHorarioCreacion(nuevosHorarios);
      
      if (horariosRespetados > 0) {
        alert(`🗑️ Semana deshecha.\n\n🔒 ${horariosRespetados} horarios ya guardados fueron respetados.`);
      }
    }
  };
  
  // Guardar horarios creados a Programación de Horarios
  const guardarHorariosCreados = async () => {
    const alertas = validarHorariosAntesDeGuardar();
    
    if (alertas.length > 0) {
      setModalConfirmarGuardarHorario(true);
      return;
    }
    
    await ejecutarGuardarHorarios();
  };
  
  const ejecutarGuardarHorarios = async () => {
    try {
      // Copiar horarios creados a las celdas para la semana de Vista Gráfica
      const nuevasCeldas = { ...(horarioExistenteVistaGrafica?.celdas || {}) };
      
      Object.entries(horarioCreacion).forEach(([empId, dias]) => {
        if (!nuevasCeldas[empId]) nuevasCeldas[empId] = {};
        
        Object.entries(dias).forEach(([diaIdx, horario]) => {
          // Limpiar campos temporales
          const horarioLimpio = { ...horario };
          delete horarioLimpio.modoAdicional;
          delete horarioLimpio.sedeNueva;
          delete horarioLimpio.e1Nuevo;
          delete horarioLimpio.s1Nuevo;
          delete horarioLimpio.e2Nuevo;
          delete horarioLimpio.s2Nuevo;
          delete horarioLimpio.tipoHorarioNuevo;
          
          if (horarioLimpio.esDescanso) {
            nuevasCeldas[empId][diaIdx] = {
              tipo: 'DESCANSO',
              e1: '', s1: '', e2: '', s2: '',
              sede: '', sede1: '', sede2: '',
              descanso: true
            };
          } else {
            // Determinar el tipo basado en si tiene segundo turno
            const tieneSegundoTurno = horarioLimpio.e2 && horarioLimpio.s2;
            const tipoHorario = tieneSegundoTurno ? 'PARTIDO' : 'DERECHO';
            
            // IMPORTANTE: sede siempre debe ser igual a sede1 para evitar inconsistencias
            const sedePrincipal = horarioLimpio.sede || '';
            // sede2 solo tiene valor si es PARTIDO (tiene segundo turno)
            const sedeSegundoTurno = tieneSegundoTurno ? (horarioLimpio.sede2 || sedePrincipal) : sedePrincipal;
            
            nuevasCeldas[empId][diaIdx] = {
              tipo: tipoHorario,
              e1: horarioLimpio.e1 || '',
              s1: horarioLimpio.s1 || '',
              e2: horarioLimpio.e2 || '',
              s2: horarioLimpio.s2 || '',
              sede: sedePrincipal,  // Siempre igual a sede1
              sede1: sedePrincipal,
              sede2: sedeSegundoTurno,
              // Guardar horarios adicionales si existen
              horariosAdicionales: horarioLimpio.horariosAdicionales?.map(h => ({
                sede: h.sede || '',
                e1: h.e1 || '',
                s1: h.s1 || '',
                e2: h.e2 || '',
                s2: h.s2 || ''
              })) || []
            };
          }
        });
      });
      
      // Crear el payload para guardar en Supabase con las fechas de Vista Gráfica
      const nuevoId = horarioExistenteVistaGrafica?.id || crypto.randomUUID();
      const payload = {
        id: nuevoId,
        semanaInicio: semanaInicioVistaGrafica,
        semanaFin: semanaFinVistaGrafica,
        estado: 'temporal',
        diasCerrados: horarioExistenteVistaGrafica?.diasCerrados || [],
        celdas: nuevasCeldas,
        eventos: horarioExistenteVistaGrafica?.eventos || {},
        eventosPorDia: horarioExistenteVistaGrafica?.eventosPorDia || {},
        creadoEn: horarioExistenteVistaGrafica?.creadoEn || new Date().toISOString(),
        actualizadoEn: new Date().toISOString(),
      };
      
      // Guardar usando las funciones del repositorio (actualiza tanto local como Supabase)
      if (horarioExistenteVistaGrafica) {
        onActualizarHorario(horarioExistenteVistaGrafica.id, payload);
      } else {
        onGuardarHorario(payload);
      }
      
      // Limpiar estados de Vista Gráfica
      setModoVistaGrafica("ninguno");
      setHorarioCreacion({});
      setModalConfirmarGuardarHorario(false);
      
      // IMPORTANTE: Primero navegar a programación y luego actualizar los estados
      // para evitar condiciones de carrera con el useEffect
      setVistaHorarios("programacion");
      
      // Marcar que no se debe recargar las celdas automáticamente
      skipCeldasReloadRef.current = true;
      
      // Cambiar a la semana guardada
      setFechaBase(semanaInicioVistaGrafica);
      
      // Actualizar las celdas directamente
      console.log('[Guardar] Actualizando celdas directamente. Empleados:', Object.keys(nuevasCeldas).length);
      console.log('[Guardar] Celdas a guardar:', JSON.stringify(nuevasCeldas).substring(0, 500));
      setCeldas(nuevasCeldas);
      setEstado('temporal');
      setDiasCerrados(payload.diasCerrados);
      setEventosPorDia(payload.eventosPorDia);
      
      // Mostrar confirmación después de un pequeño delay para permitir que React procese los cambios
      setTimeout(() => {
        alert(`✅ Horarios guardados correctamente para la semana ${semanaInicioVistaGrafica} → ${semanaFinVistaGrafica}.`);
      }, 100);
      
    } catch (error) {
      console.error('Error guardando horarios:', error);
      alert("❌ Error al guardar los horarios: " + error.message);
    }
  };
  const [marcacionesTemporales, setMarcacionesTemporales] = useState([]);
  const [marcacionesReales, setMarcacionesReales] = useState([]);
  const [cargandoMarcaciones, setCargandoMarcaciones] = useState(false);
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);
  const [filtroMarcaciones, setFiltroMarcaciones] = useState({ fechaDesde: "", fechaHasta: "", empleadoId: "" });

  // ===== Estados para Filtros de Vista (Sede y Día) =====
  const [filtroSede, setFiltroSede] = useState(""); // "" = todas las sedes
  const [filtroDia, setFiltroDia] = useState(-1); // -1 = todos los días, 0-6 = lun-dom
  
  // ===== Estados para Horario Nómina =====
  const [horariosNomina, setHorariosNomina] = useState({}); // {empleadoId: {diaIdx: {e1,s1,e2,s2,sede1,sede2,tipo}}}
  const [filtroHorarioNomina, setFiltroHorarioNomina] = useState({ fechaDesde: semanaInicio, fechaHasta: semanaFin });
  
  // ===== Estados para Actualizar Horarios =====
  const [diferenciasHorarios, setDiferenciasHorarios] = useState([]); // Lista de diferencias encontradas
  const [seleccionActualizacion, setSeleccionActualizacion] = useState({
    empleadosIds: [], // IDs de empleados seleccionados
    actualizarTodo: false,
    actualizarE1: true,
    actualizarS1: true,
    actualizarE2: true,
    actualizarS2: true,
    actualizarSede: true
  });
  const [modalConfirmarActualizacion, setModalConfirmarActualizacion] = useState(false);

  // Cargar marcaciones al montar o cambiar de vista
  React.useEffect(() => {
    if (vistaHorarios === "marcaciones_temporales") {
      cargarMarcacionesTemporales();
    } else if (vistaHorarios === "marcaciones_reales") {
      cargarMarcacionesRealesConFiltros();
    }
  }, [vistaHorarios]);

  async function cargarMarcacionesTemporales() {
    setCargandoMarcaciones(true);
    try {
      const data = await cargarMarcacionesTemporalesDeSupabase();
      setMarcacionesTemporales(data.filter(m => m.estado === "pendiente"));
    } catch (e) {
      console.error("Error cargando marcaciones temporales:", e);
    }
    setCargandoMarcaciones(false);
  }

  async function cargarMarcacionesRealesConFiltros() {
    setCargandoMarcaciones(true);
    try {
      const data = await cargarMarcacionesRealesDeSupabase(null, {
        fechaDesde: filtroMarcaciones.fechaDesde || undefined,
        fechaHasta: filtroMarcaciones.fechaHasta || undefined,
        empleadoId: filtroMarcaciones.empleadoId || undefined
      });
      setMarcacionesReales(data);
    } catch (e) {
      console.error("Error cargando marcaciones reales:", e);
    }
    setCargandoMarcaciones(false);
  }

  // Manejar subida de archivo CSV
  async function handleSubirCSV(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setSubiendoArchivo(true);
    try {
      const texto = await file.text();
      const resultado = procesarCSVMarcaciones(texto, empleados);
      
      if (resultado.error) {
        alert(`❌ Error procesando archivo: ${resultado.error}`);
        setSubiendoArchivo(false);
        return;
      }
      
      if (resultado.empleadosNoEncontrados.length > 0) {
        const msg = `⚠️ Los siguientes empleados no se encontraron en el sistema:\n\n${resultado.empleadosNoEncontrados.join("\n")}\n\n¿Deseas continuar sin estos registros?`;
        if (!window.confirm(msg)) {
          setSubiendoArchivo(false);
          return;
        }
      }
      
      if (resultado.marcaciones.length === 0) {
        alert("⚠️ No se encontraron marcaciones válidas en el archivo.");
        setSubiendoArchivo(false);
        return;
      }
      
      // Guardar como temporales
      const guardado = await guardarMarcacionesTemporalesEnSupabase(
        resultado.marcaciones,
        file.name,
        null, // subidoPor (se puede pasar el usuario actual)
        null  // empresaId
      );
      
      if (guardado.success) {
        alert(`✅ Se procesaron ${resultado.totalProcesadas} marcaciones.\n\nEl archivo está pendiente de aprobación por gerencia.`);
        setVistaHorarios("marcaciones_temporales");
        cargarMarcacionesTemporales();
      } else {
        alert(`❌ Error guardando marcaciones: ${guardado.error}`);
      }
    } catch (e) {
      console.error("Error subiendo CSV:", e);
      alert(`❌ Error leyendo archivo: ${e.message}`);
    }
    setSubiendoArchivo(false);
    event.target.value = ""; // Reset input
  }

  // Aprobar archivo de marcaciones
  async function handleAprobarMarcaciones(archivoId) {
    if (!window.confirm("¿Estás seguro de aprobar estas marcaciones? Se agregarán a los registros reales.")) return;
    
    setCargandoMarcaciones(true);
    const resultado = await aprobarMarcacionesTemporales(archivoId, null, null);
    
    if (resultado.success) {
      alert(`✅ Marcaciones aprobadas.\n\n• Insertadas: ${resultado.insertadas}\n• Omitidas (ya existían): ${resultado.omitidas}`);
      cargarMarcacionesTemporales();
    } else {
      alert(`❌ Error aprobando marcaciones: ${resultado.error}`);
    }
    setCargandoMarcaciones(false);
  }

  // Rechazar archivo de marcaciones
  async function handleRechazarMarcaciones(archivoId) {
    if (!window.confirm("¿Estás seguro de rechazar y eliminar estas marcaciones?")) return;
    
    const ok = await rechazarMarcacionesTemporales(archivoId);
    if (ok) {
      alert("✅ Archivo de marcaciones eliminado.");
      cargarMarcacionesTemporales();
    } else {
      alert("❌ Error eliminando archivo.");
    }
  }

  // Obtener todas las sedes usadas en la semana actual (para Programación de Horarios)
  // SOLO considera sede2 si el empleado tiene segundo turno real (e2 && s2)
  const sedesUsadasEnSemana = React.useMemo(() => {
    const sedes = new Set();
    activos.forEach(emp => {
      for (let idx = 0; idx < 7; idx++) {
        const cel = celdas?.[emp.id]?.[idx];
        if (!cel) continue;
        // Agregar sede1 si existe
        if (cel.sede1) sedes.add(cel.sede1.toUpperCase().trim());
        // Solo agregar sede2 si tiene segundo turno real
        const tieneSegundoTurno = cel.e2 && cel.s2;
        if (tieneSegundoTurno && cel.sede2) sedes.add(cel.sede2.toUpperCase().trim());
      }
    });
    return Array.from(sedes).sort();
  }, [activos, celdas]);

  // Obtener todas las sedes usadas en Vista Gráfica
  // Basado en las celdas del horario guardado
  // SOLO usa sede1 y sede2 (solo si tiene segundo turno real)
  const sedesUsadasVistaGrafica = React.useMemo(() => {
    const sedes = new Set();
    activos.forEach(emp => {
      for (let idx = 0; idx < 7; idx++) {
        const cel = celdasVistaGrafica?.[emp.id]?.[idx];
        if (!cel) continue;
        // Agregar sede1 si existe
        const sedePrincipal = (cel.sede1 || '').toUpperCase().trim();
        if (sedePrincipal && sedePrincipal !== 'DESCANSO') {
          sedes.add(sedePrincipal);
        }
        // Solo agregar sede2 si tiene segundo turno real
        const tieneSegundoTurno = cel.e2 && cel.s2;
        if (tieneSegundoTurno) {
          const sedeSecundaria = (cel.sede2 || '').toUpperCase().trim();
          if (sedeSecundaria && sedeSecundaria !== 'DESCANSO' && sedeSecundaria !== sedePrincipal) {
            sedes.add(sedeSecundaria);
          }
        }
      }
    });
    return Array.from(sedes).sort();
  }, [activos, celdasVistaGrafica]);

  // Empleados visibles filtrados por selección, sede y día
  const empleadosVisibles = React.useMemo(() => {
    return activos.filter((e) => {
      // Primero verificar si está seleccionado
      if (!selectedIds.includes(e.id)) return false;
      
      // Si no hay filtro de sede ni día, mostrar todos los seleccionados
      if (!filtroSede && filtroDia === -1) return true;
      
      // Verificar si el empleado trabaja en la sede filtrada o en el día filtrado
      let cumpleFiltroSede = !filtroSede; // true si no hay filtro de sede
      let cumpleFiltroDia = filtroDia === -1; // true si no hay filtro de día
      
      for (let idx = 0; idx < 7; idx++) {
        const cel = celdas?.[e.id]?.[idx];
        if (!cel) continue;
        
        // Verificar sede - SOLO usar sede1, y sede2 solo si tiene segundo turno real
        if (filtroSede) {
          const sedeUpper = filtroSede.toUpperCase();
          const sedeCelda1 = (cel.sede1 || '').toUpperCase().trim();
          // Solo considerar sede2 si tiene segundo turno real (e2 && s2)
          const tieneSegundoTurno = cel.e2 && cel.s2;
          const sedeCelda2 = tieneSegundoTurno ? (cel.sede2 || '').toUpperCase().trim() : '';
          
          const coincideSede = sedeCelda1 === sedeUpper || 
                              (sedeCelda2 && sedeCelda2 === sedeUpper);
          
          if (coincideSede) {
            // Si también hay filtro de día, solo marcar como cumplido si coincide el día
            if (filtroDia === -1 || idx === filtroDia) {
              cumpleFiltroSede = true;
            }
          }
        }
        
        // Verificar día
        if (filtroDia !== -1 && idx === filtroDia) {
          if (cel.tipo && cel.tipo !== "") {
            // Si también hay filtro de sede, verificar que coincida
            if (!filtroSede) {
              cumpleFiltroDia = true;
            } else {
              const sedeUpper = filtroSede.toUpperCase();
              const sedeCelda1 = (cel.sede1 || '').toUpperCase().trim();
              // Solo considerar sede2 si tiene segundo turno real
              const tieneSegundoTurno = cel.e2 && cel.s2;
              const sedeCelda2 = tieneSegundoTurno ? (cel.sede2 || '').toUpperCase().trim() : '';
              
              const coincideSede = sedeCelda1 === sedeUpper || 
                                  (sedeCelda2 && sedeCelda2 === sedeUpper);
              if (coincideSede) {
                cumpleFiltroDia = true;
              }
            }
          }
        }
      }
      
      return cumpleFiltroSede && cumpleFiltroDia;
    });
  }, [activos, selectedIds, celdas, filtroSede, filtroDia]);

  const focusCampoDom = React.useCallback((empId, diaIdx, field) => {
    if (typeof document === "undefined") return false;
    const selector = `[data-horario-emp="${empId}"][data-horario-dia="${diaIdx}"][data-horario-field="${field}"]`;
    const element = document.querySelector(selector);
    if (!element) return false;
    const focusFn = () => {
      element.focus();
      if (typeof element.select === "function") {
        element.select();
      }
    };
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(focusFn);
    } else {
      setTimeout(focusFn, 0);
    }
    return true;
  }, []);

  const focusPrimerCampo = React.useCallback(
    (empId, diaIdx, preferLast = false) => {
      const celda = celdas?.[empId]?.[diaIdx] || null;
      const prioridad = [];
      if (celda?.tipo === "PARTIDO") {
        prioridad.push(
          ...(preferLast ? ["s2", "e2", "s1", "e1"] : ["e1", "s1", "e2", "s2"])
        );
      } else if (celda?.tipo === "DERECHO") {
        prioridad.push(...(preferLast ? ["s1", "e1"] : ["e1", "s1"]));
      } else if (celda?.tipo === "OTRO") {
        prioridad.push("otro");
      }
      prioridad.push("tipo");
      for (const campo of prioridad) {
        if (focusCampoDom(empId, diaIdx, campo)) {
          return true;
        }
      }
      return false;
    },
    [celdas, focusCampoDom]
  );

  const horariosCopiables = React.useMemo(() => {
    return (horariosRepo || [])
      .filter((h) => h.id && h.id !== (horarioExistente?.id || null))
      .sort((a, b) => parseISO(b.semanaInicio) - parseISO(a.semanaInicio));
  }, [horariosRepo, horarioExistente?.id]);

  React.useEffect(() => {
    if (modalCopiarOpen) {
      setCopiarSeleccion(horariosCopiables[0]?.id || "");
    }
  }, [modalCopiarOpen, horariosCopiables]);
  // util fechas L..D
  const fechasSemana = React.useMemo(() => {
    const arr = [];
    let d = parseISO(semanaInicio);
    for (let i=0;i<7;i++){
      arr.push(toISO(d));
      d = parseISO(addDaysLocal(toISO(d), 1));
    }
    return arr;
  }, [semanaInicio]);

  // Función helper para verificar si un día específico está cerrado
  function isDiaCerrado(iso) {
    return diasCerrados.includes(iso);
  }

  // Función helper para obtener el límite de quincena basado en una fecha
  function getLimiteQuincena(fechaISO) {
    const fecha = parseISO(fechaISO);
    const dia = fecha.getDate();
    const mes = fecha.getMonth();
    const anio = fecha.getFullYear();
    
    if (dia <= 15) {
      // Primera quincena: límite es el 15
      return toISO(new Date(anio, mes, 15));
    } else {
      // Segunda quincena: límite es el último día del mes
      const ultimoDia = new Date(anio, mes + 1, 0).getDate();
      return toISO(new Date(anio, mes, ultimoDia));
    }
  }

  // Función para obtener días de la semana que pueden cerrarse en la quincena actual
  function getDiasHastaLimiteQuincena() {
    if (fechasSemana.length === 0) return [];
    
    // Encontrar los días que aún NO están cerrados
    const diasNoCerrados = fechasSemana.filter(iso => !diasCerrados.includes(iso));
    
    // Si no hay días sin cerrar, devolver array vacío
    if (diasNoCerrados.length === 0) return [];
    
    // Usar el PRIMER día no cerrado para determinar la quincena actual a cerrar
    const primerDiaNoCerrado = diasNoCerrados[0];
    const limite = getLimiteQuincena(primerDiaNoCerrado);
    
    // Devolver solo los días no cerrados que estén dentro del límite de la quincena
    return diasNoCerrados.filter(iso => iso <= limite);
  }
 
  function handleFechaBaseChange(v){
    if (!v) return;
    const lunes = mondayOf(v);
    if (v !== lunes) {
      alert("El horario semanal debe iniciar un día Lunes. Se ajustará al Lunes de esa semana.");
    }
    // Resetear bandera para que se carguen las celdas del repositorio
    skipCeldasReloadRef.current = false;
    setFechaBase(lunes);
  }
 
  function aplicarSemana(direction){ // -1 anterior, 0 actual, +1 siguiente
    const base = parseISO(semanaInicio);
    base.setDate(base.getDate() + (direction*7));
    // Resetear bandera para que se carguen las celdas del repositorio
    skipCeldasReloadRef.current = false;
    setFechaBase(toISO(base));
  }
 
  // ===== helpers celdas =====
  function getCell(empId, diaIdx) {
    return celdas?.[empId]?.[diaIdx] || null;
  }
  function setCell(empId, diaIdx, patch) {
    setCeldas(prev => {
      // Verificar si este día específico está cerrado
      const iso = fechasSemana[diaIdx];
      if (isDiaCerrado(iso)) return prev;
      const next = { ...(prev || {}) };
      if (!next[empId]) next[empId] = {};
      next[empId] = { ...next[empId] };
      const merged = { ...(next[empId][diaIdx] || {}), ...patch };
      for (const key of Object.keys(merged)) {
        if (merged[key] === undefined) {
          delete merged[key];
        }
      }
      next[empId][diaIdx] = merged;
      return next;
    });
  }
  function setEventoDia(empId, iso, payload) {
    setEventosPorDia(prev => {
      // Verificar si este día específico está cerrado
      if (isDiaCerrado(iso)) return prev;
      const nxt = { ...(prev || {}) };
      if (!nxt[empId]) {
        if (!payload) return nxt;
        nxt[empId] = {};
      } else {
        nxt[empId] = { ...nxt[empId] };
      }
      if (payload) {
        nxt[empId][iso] = payload;
      } else if (nxt[empId]) {
        delete nxt[empId][iso];
      }
      return nxt;
    });
  }

  function copiarHorarioSeleccionado(origenId) {
    if (!origenId) {
      alert("Selecciona un horario para copiar.");
      return;
    }
    const origen = (horariosRepo || []).find((h) => h.id === origenId);
    if (!origen) {
      alert("No se encontró el horario seleccionado.");
      return;
    }
    const clonCeldas = typeof structuredClone === "function"
      ? structuredClone(origen.celdas || {})
      : JSON.parse(JSON.stringify(origen.celdas || {}));
    const clonEventos = typeof structuredClone === "function"
      ? structuredClone(origen.eventosPorDia || {})
      : JSON.parse(JSON.stringify(origen.eventosPorDia || {}));
    setCeldas(clonCeldas);
    setEventosPorDia(clonEventos);
    setEstado("temporal");
    setModalCopiarOpen(false);
    alert(`Horarios copiados desde la semana ${origen.semanaInicio} → ${origen.semanaFin}. Recuerda guardar.`);
  }

  function reabrirHorario() {
    if (!horarioExistente && diasCerrados.length === 0) {
      alert("Este horario aún no se ha guardado o no tiene días cerrados.");
      return;
    }
    const ok = window.confirm(
      "¿Deseas habilitar la edición de los días cerrados? Podrás modificar el horario nuevamente."
    );
    if (!ok) return;
    setEstado("temporal");
    setDiasCerrados([]);
    const payload = {
      ...horarioExistente,
      estado: "temporal",
      diasCerrados: [],
    };
    if (horarioExistente) {
      onActualizarHorario(horarioExistente.id, payload);
    }
    alert("Horario reabierto. Ya puedes modificar todos los días.");
  }

  const DISPLAY_FIELDS = ["e1Display", "s1Display", "e2Display", "s2Display"];
  function limpiarCeldasParaGuardar(origen) {
    if (!origen) return {};
    const resultado = {};
    for (const empId of Object.keys(origen)) {
      const dias = origen[empId];
      if (!dias) continue;
      const diasSanitizados = {};
      for (const diaIdx of Object.keys(dias)) {
        const celda = dias[diaIdx];
        if (!celda) continue;
        const limpio = {};
        for (const [clave, valor] of Object.entries(celda)) {
          if (DISPLAY_FIELDS.includes(clave)) continue;
          if (valor === undefined) continue;
          limpio[clave] = valor;
        }
        if (Object.keys(limpio).length > 0) {
          diasSanitizados[diaIdx] = limpio;
        }
      }
      if (Object.keys(diasSanitizados).length > 0) {
        resultado[empId] = diasSanitizados;
      }
    }
    return resultado;
  }
 
  // bloqueo por ausencias (no editar si cae en ausencia)
  function ausenciaDelDia(empId, iso) {
    const a = ausencias.find(x => x.empleadoId === empId && isDateInRange(iso, x.inicio, x.fin || x.inicio));
    return a ? a.tipo : null;
  }
  
  // 🔒 FUNCIÓN: Obtener empleados con más de 7 horas en algún día de la semana
  function obtenerEmpleadosMas7HorasPorDia() {
    const empleadosConExceso = [];
    const DIAS_SEMANA = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
    
    // Función auxiliar para calcular horas de una celda
    const calcularHorasCelda = (cel) => {
      if (!cel || !cel.e1 || !cel.s1) return 0;
      
      const parseHora = (h) => {
        if (!h) return null;
        const partes = h.toString().split(':');
        if (partes.length < 2) return null;
        const hh = parseInt(partes[0], 10);
        const mm = parseInt(partes[1], 10);
        if (isNaN(hh) || isNaN(mm)) return null;
        return hh + mm / 60;
      };
      
      let total = 0;
      const entrada1 = parseHora(cel.e1);
      const salida1 = parseHora(cel.s1);
      if (entrada1 !== null && salida1 !== null) {
        let diff = salida1 - entrada1;
        if (diff < 0) diff += 24; // Cruza medianoche
        total += diff;
      }
      
      // Segundo turno si existe (horario partido)
      const entrada2 = parseHora(cel.e2);
      const salida2 = parseHora(cel.s2);
      if (entrada2 !== null && salida2 !== null) {
        let diff = salida2 - entrada2;
        if (diff < 0) diff += 24;
        total += diff;
      }
      
      return total;
    };
    
    // Revisar todos los empleados seleccionados
    activos.filter(emp => selectedIds.includes(emp.id)).forEach(emp => {
      // Revisar cada día de la semana
      fechasSemana.forEach((iso, diaIdx) => {
        // Ignorar días cerrados
        if (isDiaCerrado(iso)) return;
        
        const cel = getCell(emp.id, diaIdx);
        if (!cel) return;
        
        // Ignorar si es descanso
        if (cel.tipo === 'DESCANSO' || cel.descanso || cel.esDescanso) return;
        
        const horas = calcularHorasCelda(cel);
        
        if (horas > 7) {
          empleadosConExceso.push({
            empleadoId: emp.id,
            empleado: emp.nombre || `${emp.nombres || ''} ${emp.apellidos || ''}`.trim(),
            documento: emp.documento || '',
            dia: DIAS_SEMANA[diaIdx],
            fecha: iso,
            horas: horas.toFixed(1),
            horasExactas: horas
          });
        }
      });
    });
    
    return empleadosConExceso;
  }
 
  // ===== Guardar / Imprimir =====
  function guardar(estadoTarget){
    const celdasClonadas =
      typeof structuredClone === "function"
        ? structuredClone(celdas || {})
        : JSON.parse(JSON.stringify(celdas || {}));
    
    // Calcular días a cerrar según el límite de quincena
    let nuevosDiasCerrados = [...diasCerrados];
    let mensajeExtra = "";
    
    if (estadoTarget === "definitivo") {
      const diasParaCerrar = getDiasHastaLimiteQuincena();
      
      // Agregar los días de la quincena actual a diasCerrados
      diasParaCerrar.forEach(iso => {
        if (!nuevosDiasCerrados.includes(iso)) {
          nuevosDiasCerrados.push(iso);
        }
      });
      
      // Verificar si quedaron días sin cerrar de la semana (para otra quincena)
      const diasRestantesSinCerrar = fechasSemana.filter(iso => !nuevosDiasCerrados.includes(iso));
      
      // Si hay días que quedan sin cerrar (de la siguiente quincena)
      if (diasRestantesSinCerrar.length > 0 && diasParaCerrar.length > 0) {
        const fechaLimite = getLimiteQuincena(diasParaCerrar[0]);
        mensajeExtra = `\\n\\n⚠️ Nota: Solo se cerraron los días hasta el ${fechaLimite} (límite de quincena).\\nLos días ${diasRestantesSinCerrar.join(", ")} quedan habilitados para edición.`;
      }
      
      // El estado será "parcial" si hay días sin cerrar, "definitivo" si todos están cerrados
      estadoTarget = nuevosDiasCerrados.length === fechasSemana.length ? "definitivo" : "parcial";
    }
    
    const payload = {
      id: ensureUuid(horarioExistente?.id),
      semanaInicio, semanaFin,
      estado: estadoTarget,
      diasCerrados: nuevosDiasCerrados,
      celdas: limpiarCeldasParaGuardar(celdasClonadas),
      // mantenemos compatibilidad pero guardamos por-día:
      eventos: structuredClone(horarioExistente?.eventos || {}), // legacy
      eventosPorDia:
        typeof structuredClone === "function"
          ? structuredClone(eventosPorDia || {})
          : JSON.parse(JSON.stringify(eventosPorDia || {})),
      creadoEn: horarioExistente?.creadoEn || new Date().toISOString(),
      actualizadoEn: new Date().toISOString(),
    };
    if (horarioExistente){
      onActualizarHorario(horarioExistente.id, payload);
    } else {
      onGuardarHorario(payload);
    }
    setEstado(estadoTarget);
    setDiasCerrados(nuevosDiasCerrados);
    
    const msgBase = estadoTarget === "definitivo" || estadoTarget === "parcial" 
      ? "guardado DEFINITIVO" 
      : "guardado temporalmente";
    alert(`Horario ${msgBase} para ${semanaInicio} → ${semanaFin}.${mensajeExtra}`);
  }
 
  function imprimir() {
    // Obtener fecha actual para el reporte
    const fechaImpresion = new Date().toLocaleDateString('es-CO', { 
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });

    // leyenda (colores usados esa semana + tipos de ausencia)
    const usados = [];
    const usedMap = new Map();
    const ausenciasUsadas = new Set();
    
    for (const empId of Object.keys(eventosPorDia||{})) {
      for (const iso of Object.keys(eventosPorDia[empId]||{})) {
        const ev = eventosPorDia[empId][iso];
        const key = `${ev.nombre}::${ev.color}`;
        if (!usedMap.has(key)) {
          usedMap.set(key, true);
          usados.push({ nombre: ev.nombre, color: ev.color });
        }
      }
    }
    
    // Detectar ausencias en los empleados seleccionados
    activos.filter(e => selectedIds.includes(e.id)).forEach(emp => {
      fechasSemana.forEach(iso => {
        const aus = ausenciaDelDia(emp.id, iso);
        if (aus) ausenciasUsadas.add(aus.toUpperCase());
      });
    });

    // Detectar todas las sedes únicas y asignarles colores
    const sedesUsadas = new Set();
    activos.filter(e => selectedIds.includes(e.id)).forEach(emp => {
      fechasSemana.forEach((iso, idx) => {
        const cel = getCell(emp.id, idx);
        if (cel?.sede1) sedesUsadas.add(cel.sede1.toUpperCase());
        if (cel?.sede2) sedesUsadas.add(cel.sede2.toUpperCase());
      });
    });
    
    // Paleta de colores para sedes (colores pastel con buen contraste)
    const coloresSedes = [
      '#FFE082', // Amarillo
      '#B2DFDB', // Verde agua
      '#F8BBD9', // Rosa
      '#C5CAE9', // Azul lavanda
      '#FFCCBC', // Naranja claro
      '#D7CCC8', // Beige
      '#B3E5FC', // Celeste
      '#DCEDC8', // Verde lima
      '#F0F4C3', // Lima claro
      '#E1BEE7', // Púrpura claro
    ];
    
    const sedesArray = Array.from(sedesUsadas);
    const mapaColorSedes = {};
    sedesArray.forEach((sede, i) => {
      mapaColorSedes[sede] = coloresSedes[i % coloresSedes.length];
    });
    
    // Función para obtener el HTML de una sede con su color
    const sedeConColor = (nombreSede) => {
      if (!nombreSede) return '';
      const sedeUpper = nombreSede.toUpperCase();
      const color = mapaColorSedes[sedeUpper] || '#f0f0f0';
      return `<span class="sede-badge" style="background:${color};">${nombreSede}</span>`;
    };

    const legendHTML = (usados.length || ausenciasUsadas.size || sedesArray.length)
      ? `<div class="legend">
          <span class="legend-title">📋 Leyenda:</span>
          ${ausenciasUsadas.has('VACACIONES') ? '<span class="legend-item vacaciones">🏖️ Vacaciones</span>' : ''}
          ${ausenciasUsadas.has('INCAPACIDAD') ? '<span class="legend-item incapacidad">🏥 Incapacidad</span>' : ''}
          ${ausenciasUsadas.has('LICENCIA') ? '<span class="legend-item licencia">📝 Licencia</span>' : ''}
          ${ausenciasUsadas.has('PERMISO') ? '<span class="legend-item permiso">✋ Permiso</span>' : ''}
          ${usados.map(u => `<span class="legend-item" style="background:${u.color};border-color:${u.color};">${u.nombre}</span>`).join("")}
        </div>
        ${sedesArray.length > 0 ? `
        <div class="legend sedes-legend">
          <span class="legend-title">🏢 Sedes:</span>
          ${sedesArray.map(sede => `<span class="legend-item sede-item" style="background:${mapaColorSedes[sede]};">${sede}</span>`).join("")}
        </div>` : ''}`
      : (sedesArray.length > 0 ? `
        <div class="legend sedes-legend">
          <span class="legend-title">🏢 Sedes:</span>
          ${sedesArray.map(sede => `<span class="legend-item sede-item" style="background:${mapaColorSedes[sede]};">${sede}</span>`).join("")}
        </div>` : '');

    // Función para convertir a formato 12h
    const hora12 = (value) => {
      if (!value) return "";
      const [hh, mm = "00"] = value.split(":");
      const h = parseInt(hh, 10);
      if (isNaN(h)) return value;
      const ampm = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 === 0 ? 12 : h % 12;
      return `${String(h12).padStart(2, "0")}:${mm} ${ampm}`;
    };

    // Función auxiliar para convertir hora (12h o 24h) a minutos desde medianoche
    const horaAMinutos = (valor) => {
      if (!valor) return null;
      const textoLimpio = valor.toString().trim().toUpperCase();
      
      // Intentar detectar formato 12h con AM/PM (más flexible)
      // Soporta: "12:00 PM", "12:00PM", "12 PM", "12PM", "12:00 am", etc.
      const match12h = textoLimpio.match(/^(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)$/);
      if (match12h) {
        let [, h, m, periodo] = match12h;
        let hh = parseInt(h, 10);
        if (isNaN(hh)) return null;
        const mm = parseInt(m || "0", 10);
        // Convertir a 24h
        if (periodo === "PM" && hh !== 12) hh += 12;
        if (periodo === "AM" && hh === 12) hh = 0;
        const minutos = hh * 60 + mm;
        return minutos;
      }
      
      // Intentar formato 24h (HH:MM o H:MM)
      const match24h = textoLimpio.match(/^(\d{1,2}):(\d{1,2})$/);
      if (match24h) {
        const [, h, m] = match24h;
        const hh = parseInt(h, 10);
        const mm = parseInt(m, 10);
        if (isNaN(hh) || isNaN(mm)) return null;
        if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) {
          return hh * 60 + mm;
        }
      }
      
      // Intentar solo número (asumiendo hora en formato 24h)
      const soloNumero = textoLimpio.match(/^(\d{1,2})$/);
      if (soloNumero) {
        const hh = parseInt(soloNumero[1], 10);
        if (hh >= 0 && hh <= 23) {
          return hh * 60;
        }
      }
      
      return null;
    };

    // Calcular total de horas por empleado
    const calcularHorasEmpleado = (empId) => {
      let totalMinutos = 0;
      fechasSemana.forEach((iso, idx) => {
        const cel = getCell(empId, idx);
        if (!cel) return;
        
        // Primer turno
        const e1Min = horaAMinutos(cel.e1);
        const s1Min = horaAMinutos(cel.s1);
        if (e1Min !== null && s1Min !== null) {
          if (s1Min > e1Min) {
            // Turno normal (ej: 8 AM a 5 PM)
            totalMinutos += s1Min - e1Min;
          } else if (s1Min < e1Min) {
            // Turno nocturno que cruza medianoche (ej: 3 PM a 12 AM o 1 AM)
            // Calcular: desde entrada hasta medianoche + desde medianoche hasta salida
            totalMinutos += (1440 - e1Min) + s1Min;
          }
        }
        
        // Segundo turno (horario partido)
        const e2Min = horaAMinutos(cel.e2);
        const s2Min = horaAMinutos(cel.s2);
        if (e2Min !== null && s2Min !== null) {
          if (s2Min > e2Min) {
            totalMinutos += s2Min - e2Min;
          } else if (s2Min < e2Min) {
            // Turno nocturno que cruza medianoche
            totalMinutos += (1440 - e2Min) + s2Min;
          }
        }
      });
      const horas = Math.floor(totalMinutos / 60);
      const mins = totalMinutos % 60;
      return { horas, mins, totalMinutos };
    };

    const filas = activos
      .filter(e => selectedIds.includes(e.id))
      .map(e => {
        const nombreMostradoRaw = (e.nombres || "").trim();
        const nombreMostrado = nombreMostradoRaw || (typeof e.nombre === "string" ? e.nombre.split(/\s+/)[0] || e.nombre : "");
        const horasEmp = calcularHorasEmpleado(e.id);
        
        const tds = fechasSemana.map((iso, idx) => {
          const fest = isHolidayOrSundayCO(iso, holidaysCache);
          const aus = ausenciaDelDia(e.id, iso);
          const cel = getCell(e.id, idx);
          const evDia = eventosPorDia?.[e.id]?.[iso] || null;
          const cerrado = isDiaCerrado(iso);

          const est = cel?.tipo || "";
          let texto = "";
          let classExtra = "";
          let iconoAusencia = "";

          // Primero verificar ausencias
          if (aus) {
            const ausUpper = aus.toUpperCase();
            if (ausUpper === "VACACIONES") {
              texto = `<div class="ausencia vacaciones"><span class="icono">🏖️</span><span class="texto">VACACIONES</span></div>`;
              classExtra = "celda-vacaciones";
            } else if (ausUpper === "INCAPACIDAD") {
              texto = `<div class="ausencia incapacidad"><span class="icono">🏥</span><span class="texto">INCAPACIDAD</span></div>`;
              classExtra = "celda-incapacidad";
            } else if (ausUpper === "LICENCIA") {
              texto = `<div class="ausencia licencia"><span class="icono">📝</span><span class="texto">LICENCIA</span></div>`;
              classExtra = "celda-licencia";
            } else if (ausUpper === "PERMISO") {
              texto = `<div class="ausencia permiso"><span class="icono">✋</span><span class="texto">PERMISO</span></div>`;
              classExtra = "celda-permiso";
            } else {
              texto = `<div class="ausencia otro"><span class="texto">${ausUpper}</span></div>`;
              classExtra = "celda-ausencia";
            }
          } else if (est === "PARTIDO") {
            const sede1 = cel?.sede1 || "";
            const sede2 = cel?.sede2 || "";
            texto = `<div class="horario-turno"><span class="turno-label">1°</span> ${hora12(cel?.e1)} - ${hora12(cel?.s1)}${sede1 ? ` ${sedeConColor(sede1)}` : ""}</div>`;
            if (cel?.e2 || cel?.s2) {
              texto += `<div class="horario-turno segundo"><span class="turno-label">2°</span> ${hora12(cel?.e2)} - ${hora12(cel?.s2)}${sede2 ? ` ${sedeConColor(sede2)}` : ""}</div>`;
            }
            classExtra = "celda-partido";
          } else if (est === "DERECHO") {
            const sede1 = cel?.sede1 || "";
            texto = `<div class="horario-turno"><span class="hora">${hora12(cel?.e1)} - ${hora12(cel?.s1)}</span>${sede1 ? ` ${sedeConColor(sede1)}` : ""}</div>`;
            classExtra = "celda-derecho";
          } else if (est === "OTRO") {
            texto = `<div class="otro-tipo">${cel?.otro || "OTRO"}</div>`;
            classExtra = "celda-otro";
          } else if (est === "DESCANSO") {
            texto = `<div class="descanso"><span class="icono">😴</span><span class="texto">DESCANSO</span></div>`;
            classExtra = "celda-descanso";
          } else if (est === "NO_TRABAJO") {
            texto = `<div class="no-trabajo"><span class="texto">NO TRABAJA</span></div>`;
            classExtra = "celda-no-trabajo";
          } else {
            texto = `<span class="sin-asignar">—</span>`;
            classExtra = "celda-vacia";
          }

          // Agregar evento si existe
          if (evDia && !aus) {
            texto += `<div class="evento" style="background:${evDia.color};">${evDia.nombre}</div>`;
          }

          const festClass = fest ? ' festivo' : '';
          const cerradoClass = cerrado ? ' cerrado' : '';

          return `<td class="${classExtra}${festClass}${cerradoClass}">${texto}</td>`;
        }).join("");
        
        return `<tr>
          <td class="nombre-empleado">
            <div class="emp-nombre">${nombreMostrado}</div>
            <div class="emp-doc">${e.tipoDoc || ''} ${e.documento || ''}</div>
            <div class="emp-horas">⏱️ ${horasEmp.totalMinutos > 0 ? `${horasEmp.horas}h${horasEmp.mins > 0 ? ' ' + horasEmp.mins + 'm' : ''}` : 'Sin horario'}/sem</div>
          </td>
          ${tds}
        </tr>`;
      }).join("");

    const thead = `<tr>
      <th class="col-nombre">👤 Empleado</th>
      ${fechasSemana.map((iso, i) => {
        const fest = isHolidayOrSundayCO(iso, holidaysCache);
        const cerrado = isDiaCerrado(iso);
        const diaNum = iso.split('-')[2];
        return `<th class="col-dia${fest ? ' festivo' : ''}${cerrado ? ' cerrado' : ''}">
          <div class="dia-nombre">${DIAS[i]}</div>
          <div class="dia-num">${diaNum}</div>
          <div class="dia-fecha">${iso}</div>
          ${cerrado ? '<div class="dia-cerrado">🔒</div>' : ''}
        </th>`;
      }).join("")}
    </tr>`;

    // Calcular totales generales
    const totalEmpleados = activos.filter(e => selectedIds.includes(e.id)).length;
    const horasTotalesGeneral = activos
      .filter(e => selectedIds.includes(e.id))
      .reduce((acc, e) => acc + calcularHorasEmpleado(e.id).totalMinutos, 0);
    const horasGeneral = Math.floor(horasTotalesGeneral / 60);
    const minsGeneral = horasTotalesGeneral % 60;

    const win = window.open("", "_blank", "width=1100,height=800");
    if (!win) return;
    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Horario Semanal - ${semanaInicio} al ${semanaFin}</title>
        <meta charset="UTF-8">
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 20px;
            background: #f5f5f5;
            color: #333;
            font-weight: 600;
          }
          
          /* Header */
          .header {
            background: linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%);
            color: white;
            padding: 20px 24px;
            border-radius: 12px;
            margin-bottom: 16px;
            box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
          }
          .header-top {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            flex-wrap: wrap;
            gap: 12px;
          }
          .header h1 {
            font-size: 22px;
            font-weight: 700;
            margin-bottom: 4px;
          }
          .header .periodo {
            font-size: 16px;
            opacity: 0.95;
          }
          .header .estado {
            background: rgba(255,255,255,0.25);
            padding: 6px 14px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
          }
          .header-stats {
            display: flex;
            gap: 20px;
            margin-top: 12px;
            font-size: 13px;
          }
          .header-stats .stat {
            background: rgba(255,255,255,0.2);
            padding: 6px 12px;
            border-radius: 8px;
          }
          .fecha-impresion {
            font-size: 11px;
            opacity: 0.8;
            margin-top: 8px;
          }
          
          /* Leyenda */
          .legend {
            background: white;
            padding: 12px 16px;
            border-radius: 8px;
            margin-bottom: 16px;
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            align-items: center;
            box-shadow: 0 2px 6px rgba(0,0,0,0.08);
          }
          .legend-title {
            font-weight: 600;
            color: #555;
            margin-right: 8px;
          }
          .legend-item {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 4px 10px;
            border-radius: 6px;
            font-size: 11px;
            font-weight: 500;
            border: 1px solid #ddd;
            background: #f8f9fa;
          }
          .legend-item.vacaciones { background: #e3f2fd; border-color: #2196f3; color: #1565c0; }
          .legend-item.incapacidad { background: #ffebee; border-color: #f44336; color: #c62828; }
          .legend-item.licencia { background: #fff3e0; border-color: #ff9800; color: #e65100; }
          .legend-item.permiso { background: #f3e5f5; border-color: #9c27b0; color: #6a1b9a; }
          
          /* Tabla */
          .tabla-container {
            background: white;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }
          th {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            padding: 10px 8px;
            text-align: center;
            border-bottom: 2px solid #dee2e6;
            font-weight: 600;
            color: #495057;
          }
          th.col-nombre {
            text-align: left;
            width: 160px;
            padding-left: 14px;
          }
          th.col-dia .dia-nombre { font-size: 11px; opacity: 0.7; }
          th.col-dia .dia-num { font-size: 16px; font-weight: 700; }
          th.col-dia .dia-fecha { font-size: 9px; color: #888; margin-top: 2px; }
          th.col-dia .dia-cerrado { font-size: 10px; margin-top: 2px; }
          th.festivo { background: #ffebee; color: #c62828; }
          th.cerrado { background: #e9ecef; color: #6c757d; }
          
          td {
            padding: 8px 6px;
            border-bottom: 1px solid #eee;
            vertical-align: top;
            text-align: center;
          }
          tr:hover { background: rgba(255, 107, 53, 0.03); }
          
          /* Celdas de empleado */
          td.nombre-empleado {
            text-align: left;
            padding-left: 14px;
            background: #fafafa;
            border-right: 2px solid #e9ecef;
          }
          .emp-nombre { font-weight: 600; color: #1a1a2e; font-size: 12px; }
          .emp-doc { font-size: 9px; color: #888; margin-top: 2px; }
          .emp-horas { 
            font-size: 9px; 
            color: #ff6b35; 
            background: rgba(255, 107, 53, 0.1);
            padding: 2px 6px;
            border-radius: 4px;
            display: inline-block;
            margin-top: 4px;
            font-weight: 500;
          }
          
          /* Celdas de horario */
          .horario-turno {
            font-size: 10px;
            padding: 2px 0;
          }
          .horario-turno.segundo { margin-top: 4px; padding-top: 4px; border-top: 1px dashed #ddd; }
          .turno-label { 
            font-weight: 600; 
            color: #ff6b35; 
            margin-right: 3px;
          }
          .sede { font-size: 9px; color: #888; }
          .sede-badge {
            display: inline-block;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 9px;
            font-weight: 700;
            color: #333;
            margin-left: 4px;
            border: 1px solid rgba(0,0,0,0.15);
          }
          .hora { font-weight: 500; }
          
          /* Leyenda de sedes */
          .sedes-legend {
            margin-top: 8px;
          }
          .sede-item {
            font-weight: 700;
            color: #333 !important;
          }
          
          /* Tipos de celda */
          .celda-partido { background: #e8f5e9; }
          .celda-derecho { background: #e3f2fd; }
          .celda-otro { background: #fff3e0; }
          .celda-descanso { background: #f5f5f5; }
          .celda-no-trabajo { background: #fafafa; color: #999; }
          .celda-vacia { color: #ccc; }
          
          /* Ausencias */
          .ausencia {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            padding: 4px;
            border-radius: 6px;
            font-weight: 600;
          }
          .ausencia .icono { font-size: 16px; }
          .ausencia .texto { font-size: 9px; text-transform: uppercase; }
          
          .celda-vacaciones { background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%); }
          .celda-vacaciones .ausencia { color: #1565c0; }
          
          .celda-incapacidad { background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%); }
          .celda-incapacidad .ausencia { color: #c62828; }
          
          .celda-licencia { background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%); }
          .celda-licencia .ausencia { color: #e65100; }
          
          .celda-permiso { background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%); }
          .celda-permiso .ausencia { color: #6a1b9a; }
          
          .descanso, .no-trabajo, .otro-tipo {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            font-size: 10px;
            color: #666;
          }
          .descanso .icono { font-size: 14px; }
          .sin-asignar { color: #ddd; font-size: 14px; }
          
          /* Eventos */
          .evento {
            font-size: 9px;
            padding: 2px 6px;
            border-radius: 4px;
            margin-top: 4px;
            font-weight: 500;
          }
          
          /* Festivos y cerrados */
          td.festivo { background: #fff5f5 !important; }
          td.cerrado { background: #f8f9fa !important; opacity: 0.8; }
          
          /* Footer */
          .footer {
            margin-top: 16px;
            padding: 12px 16px;
            background: white;
            border-radius: 8px;
            font-size: 10px;
            color: #888;
            text-align: center;
            box-shadow: 0 2px 6px rgba(0,0,0,0.08);
          }
          
          /* Print styles */
          @media print {
            body { 
              background: white; 
              padding: 10px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .header { 
              box-shadow: none;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .tabla-container { box-shadow: none; }
            .legend { box-shadow: none; }
            .footer { box-shadow: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-top">
            <div>
              <h1>📅 Programación de Horarios</h1>
              <div class="periodo"><strong>${semanaInicio}</strong> → <strong>${semanaFin}</strong></div>
            </div>
            <div class="estado">${(estado || 'TEMPORAL').toUpperCase()}</div>
          </div>
          <div class="header-stats">
            <span class="stat">👥 ${totalEmpleados} empleados</span>
            <span class="stat">⏱️ ${horasGeneral}h ${minsGeneral}m totales</span>
          </div>
          <div class="fecha-impresion">📄 Impreso: ${fechaImpresion}</div>
        </div>
        
        ${legendHTML}
        
        <div class="tabla-container">
          <table>
            <thead>${thead}</thead>
            <tbody>${filas}</tbody>
          </table>
        </div>
        
        <div class="footer">
          Sistema de Gestión de Horarios • Documento generado automáticamente
        </div>
        
        <script>
          setTimeout(() => window.print(), 300);
        </script>
      </body>
      </html>
    `);
    win.document.close();
  }
 
  // ===== Componente de Input con Autocompletado para Horas =====
  const HORARIOS_SUGERIDOS = [
    "12:00 AM", "12:30 AM",
    "1:00 AM", "1:30 AM", "2:00 AM", "2:30 AM", "3:00 AM", "3:30 AM", "4:00 AM", "4:30 AM",
    "5:00 AM", "5:30 AM", "6:00 AM", "6:30 AM", "7:00 AM", "7:30 AM", "8:00 AM", "8:30 AM", 
    "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM", 
    "12:00 PM", "12:30 PM",
    "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM",
    "5:00 PM", "5:30 PM", "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM", "8:00 PM", "8:30 PM",
    "9:00 PM", "9:30 PM", "10:00 PM", "10:30 PM", "11:00 PM", "11:30 PM"
  ];

  function InputHoraAutocompletado({ 
    value, 
    onChange, 
    onCommit, 
    placeholder, 
    disabled, 
    inputRef,
    onKeyDown,
    esEntrada,
    dataAttrs = {}
  }) {
    const [mostrarSugerencias, setMostrarSugerencias] = React.useState(false);
    const [inputValue, setInputValue] = React.useState(value || "");
    const [indiceSugerencia, setIndiceSugerencia] = React.useState(-1);
    const containerRef = React.useRef(null);

    React.useEffect(() => {
      setInputValue(value || "");
    }, [value]);

    // Filtrar sugerencias basadas en el input
    const sugerenciasFiltradas = React.useMemo(() => {
      if (!inputValue.trim()) return HORARIOS_SUGERIDOS.slice(0, 12); // Mostrar primeras 12 si está vacío
      const texto = inputValue.toLowerCase().replace(/[^0-9apm]/g, '');
      return HORARIOS_SUGERIDOS.filter(h => {
        const hLower = h.toLowerCase().replace(/[^0-9apm]/g, '');
        return hLower.includes(texto) || h.startsWith(inputValue);
      }).slice(0, 8);
    }, [inputValue]);

    // Cerrar sugerencias al hacer clic fuera
    React.useEffect(() => {
      const handleClickOutside = (e) => {
        if (containerRef.current && !containerRef.current.contains(e.target)) {
          setMostrarSugerencias(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const seleccionarSugerencia = (sugerencia) => {
      setInputValue(sugerencia);
      onChange(sugerencia);
      onCommit(sugerencia);
      setMostrarSugerencias(false);
      setIndiceSugerencia(-1);
    };

    const handleKeyDownInternal = (e) => {
      if (mostrarSugerencias && sugerenciasFiltradas.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setIndiceSugerencia(prev => Math.min(prev + 1, sugerenciasFiltradas.length - 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setIndiceSugerencia(prev => Math.max(prev - 1, -1));
        } else if (e.key === 'Enter' && indiceSugerencia >= 0) {
          e.preventDefault();
          seleccionarSugerencia(sugerenciasFiltradas[indiceSugerencia]);
          return;
        } else if (e.key === 'Escape') {
          setMostrarSugerencias(false);
          setIndiceSugerencia(-1);
        }
      }
      if (onKeyDown) onKeyDown(e);
    };

    return (
      <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
        <input
          ref={inputRef}
          type="text"
          inputMode="text"
          autoComplete="off"
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => {
            const val = e.target.value;
            setInputValue(val);
            onChange(val);
            setMostrarSugerencias(true);
            setIndiceSugerencia(-1);
          }}
          onFocus={() => setMostrarSugerencias(true)}
          onBlur={(e) => {
            // Delay para permitir clic en sugerencia
            setTimeout(() => {
              if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
                onCommit(inputValue);
                setMostrarSugerencias(false);
              }
            }, 150);
          }}
          onKeyDown={handleKeyDownInternal}
          {...dataAttrs}
          style={{
            width: '100%',
            padding: '8px 10px',
            fontSize: 12,
            fontWeight: 600,
            border: '2px solid ' + (disabled ? '#ddd' : esEntrada ? 'rgba(82, 196, 26, 0.5)' : 'rgba(255, 107, 53, 0.5)'),
            borderRadius: 10,
            background: disabled ? 'rgba(200,200,200,0.3)' : esEntrada ? 'linear-gradient(135deg, #f6ffed 0%, #ffffff 100%)' : 'linear-gradient(135deg, #fff7e6 0%, #ffffff 100%)',
            color: disabled ? '#999' : '#1a1a2e',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            transition: 'all 0.2s ease',
            outline: 'none'
          }}
        />
        
        {/* Dropdown de sugerencias */}
        {mostrarSugerencias && !disabled && sugerenciasFiltradas.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 9999,
            background: 'white',
            border: '2px solid rgba(255, 107, 53, 0.3)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            maxHeight: 200,
            overflowY: 'auto',
            marginTop: 4
          }}>
            {sugerenciasFiltradas.map((sug, idx) => (
              <div
                key={sug}
                onMouseDown={(e) => {
                  e.preventDefault();
                  seleccionarSugerencia(sug);
                }}
                style={{
                  padding: '10px 12px',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  background: indiceSugerencia === idx 
                    ? 'linear-gradient(135deg, rgba(255, 107, 53, 0.15) 0%, rgba(255, 197, 61, 0.15) 100%)' 
                    : 'white',
                  borderBottom: idx < sugerenciasFiltradas.length - 1 ? '1px solid #f0f0f0' : 'none',
                  transition: 'background 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, rgba(255, 197, 61, 0.1) 100%)';
                }}
                onMouseLeave={(e) => {
                  if (indiceSugerencia !== idx) {
                    e.currentTarget.style.background = 'white';
                  }
                }}
              >
                <span style={{ 
                  fontSize: 14,
                  width: 20,
                  textAlign: 'center'
                }}>
                  {sug.includes('AM') ? '🌅' : sug.includes('PM') && parseInt(sug) < 6 ? '☀️' : '🌙'}
                </span>
                <span style={{ color: '#1a1a2e' }}>{sug}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ===== UI editor de celda =====
  function EditorCelda({ emp, idxDia }) {
    const iso = fechasSemana[idxDia];
    const fest = isHolidayOrSundayCO(iso, holidaysCache);
    const aus = ausenciaDelDia(emp.id, iso);
    const cel = getCell(emp.id, idxDia) || {};
    // Bloquear si hay ausencia O si este día específico está cerrado
    const bloqueado = !!aus || isDiaCerrado(iso);
    const e1Ref = React.useRef(null);
    const s1Ref = React.useRef(null);
    const e2Ref = React.useRef(null);
    const s2Ref = React.useRef(null);

    const hora24a12 = (valor = "") => {
      if (!valor) return { display: "", raw: "" };
      const [hhStr = "", mmStr = "00"] = valor.split(":");
      const hh = parseInt(hhStr, 10);
      if (Number.isNaN(hh)) return { display: "", raw: "" };
      const mm = mmStr.padStart(2, "0").slice(0, 2);
      const ampm = hh >= 12 ? "PM" : "AM";
      const hora12 = hh % 12 === 0 ? 12 : hh % 12;
      const horaTexto = String(hora12).padStart(2, "0");
      return { display: `${horaTexto}:${mm} ${ampm}`, raw: `${hhStr.padStart(2, "0")}:${mm}` };
    };

    const hora12a24 = (texto = "") => {
      if (!texto) return { raw: "", display: "" };
      const match = texto.trim().toUpperCase().match(/^(\d{1,2})(?::?(\d{1,2}))?\s*(AM|PM)?$/);
      if (!match) return { raw: "", display: texto };
      let [, h, m, periodo = "AM"] = match;
      let hh = parseInt(h, 10);
      if (Number.isNaN(hh)) return { raw: "", display: texto };
      hh = Math.max(1, Math.min(12, hh));
      const minutos = (m || "00").padStart(2, "0").slice(0, 2);
      const period = periodo === "PM" ? "PM" : "AM";
      const hh24 = period === "PM" ? (hh % 12) + 12 : hh % 12;
      const raw = `${String(hh24).padStart(2, "0")}:${minutos}`;
      const display = `${String(hh).padStart(2, "0")}:${minutos} ${period}`;
      return { raw, display };
    };

    const baseDraft = React.useMemo(
      () => ({
        e1: cel.e1Display ?? hora24a12(cel.e1 || "").display,
        s1: cel.s1Display ?? hora24a12(cel.s1 || "").display,
        e2: cel.e2Display ?? hora24a12(cel.e2 || "").display,
        s2: cel.s2Display ?? hora24a12(cel.s2 || "").display,
      }),
      [cel.e1, cel.s1, cel.e2, cel.s2, cel.e1Display, cel.s1Display, cel.e2Display, cel.s2Display]
    );

    const [borrador, setBorrador] = React.useState(baseDraft);

    React.useEffect(() => {
      setBorrador(baseDraft);
    }, [baseDraft, cel.tipo]);

    const commitHora = React.useCallback(
      (field, valor) => {
        const trimmed = (valor ?? "").trim();
        if (!trimmed) {
          setCell(emp.id, idxDia, { [field]: undefined, [`${field}Display`]: undefined });
          setBorrador((prev) => ({ ...prev, [field]: "" }));
          return;
        }
        const { raw, display } = hora12a24(trimmed);
        if (!raw) {
          setBorrador((prev) => ({ ...prev, [field]: trimmed }));
          return;
        }
        setCell(emp.id, idxDia, { [field]: raw, [`${field}Display`]: undefined });
        setBorrador((prev) => ({ ...prev, [field]: display }));
      },
      [emp.id, idxDia]
    );

    const enfocarCampo = React.useCallback((field) => {
      const refs = { e1: e1Ref, s1: s1Ref, e2: e2Ref, s2: s2Ref };
      const target = refs[field]?.current;
      if (!target) return false;
      const focusFn = () => {
        target.focus();
        target.select?.();
      };
      if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(focusFn);
      } else {
        setTimeout(focusFn, 0);
      }
      return true;
    }, []);

    const ordenCampos =
      cel.tipo === "PARTIDO"
        ? ["e1", "s1", "e2", "s2"]
        : cel.tipo === "DERECHO"
        ? ["e1", "s1"]
        : [];

    const handleKeyDown = React.useCallback(
      (field) => (ev) => {
        const moverDentro = (direction) => {
          const idx = ordenCampos.indexOf(field);
          const nextIdx = idx + direction;
          if (idx === -1 || nextIdx < 0 || nextIdx >= ordenCampos.length) {
            return false;
          }
          const siguienteCampo = ordenCampos[nextIdx];
          // Usar setTimeout para asegurar que el focus ocurra después del blur
          setTimeout(() => {
            if (!enfocarCampo(siguienteCampo)) {
              focusCampoDom(emp.id, idxDia, siguienteCampo);
            }
          }, 10);
          return true;
        };

        const moverACelda = (direction) => {
          if (!empleadosVisibles.length) return false;
          const empIndex = empleadosVisibles.findIndex((item) => item.id === emp.id);
          if (empIndex === -1) return false;
          let siguienteEmp = empIndex;
          let siguienteDia = idxDia;
          if (direction > 0) {
            siguienteDia += 1;
            if (siguienteDia >= fechasSemana.length) {
              siguienteDia = 0;
              siguienteEmp += 1;
            }
          } else {
            siguienteDia -= 1;
            if (siguienteDia < 0) {
              siguienteDia = fechasSemana.length - 1;
              siguienteEmp -= 1;
            }
          }
          if (siguienteEmp < 0 || siguienteEmp >= empleadosVisibles.length) {
            return false;
          }
          const objetivo = empleadosVisibles[siguienteEmp];
          setTimeout(() => {
            focusPrimerCampo(objetivo.id, siguienteDia, direction < 0);
          }, 10);
          return true;
        };

        if (ev.key === "Enter") {
          ev.preventDefault();
          commitHora(field, borrador[field]);
          if (!moverDentro(1)) {
            moverACelda(1);
          }
          return;
        }

        if (ev.key === "Tab") {
          ev.preventDefault(); // Siempre prevenir el Tab por defecto
          commitHora(field, borrador[field]);
          const direction = ev.shiftKey ? -1 : 1;
          if (!moverDentro(direction)) {
            moverACelda(direction);
          }
        }
      },
      [commitHora, borrador, empleadosVisibles, enfocarCampo, emp.id, focusCampoDom, focusPrimerCampo, idxDia, ordenCampos, fechasSemana]
    );

    return (
      <div className="text-[11px]">
        {bloqueado && (
          <div style={{
            fontSize: 10,
            color: '#666',
            background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.1) 0%, rgba(255, 197, 61, 0.1) 100%)',
            border: '1px solid rgba(255, 107, 53, 0.2)',
            borderRadius: 6,
            padding: '3px 6px',
            marginBottom: 4
          }}>
            🔒 {aus} (bloqueado)
          </div>
        )}
        <select
          value={cel.tipo || ""}
          onChange={(e) => {
            const tipo = e.target.value || undefined;
            setCell(emp.id, idxDia, {
              tipo,
              ...(tipo !== "PARTIDO" ? { e2: undefined, s2: undefined, sede2: undefined } : {}),
              ...(tipo === "OTRO" || tipo === "DESCANSO" || tipo === "NO_TRABAJO" || !tipo
                ? { e1: undefined, s1: undefined, e2: undefined, s2: undefined, sede1: undefined, sede2: undefined, otro: undefined }
                : {}),
            });
            setBorrador({ e1: "", s1: "", e2: "", s2: "" });
          }}
          disabled={bloqueado}
          style={{
            width: '100%',
            padding: '6px 8px',
            fontSize: 11,
            fontWeight: 500,
            border: '2px solid ' + (bloqueado ? '#ddd' : 'rgba(255, 107, 53, 0.3)'),
            borderRadius: 8,
            background: bloqueado ? 'rgba(200,200,200,0.3)' : 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255, 250, 245, 0.98) 100%)',
            color: bloqueado ? '#999' : (fest ? '#c00' : '#1a1a2e'),
            textDecoration: fest ? 'underline' : 'none',
            cursor: bloqueado ? 'not-allowed' : 'pointer',
            marginBottom: 6,
            boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
            transition: 'all 0.2s ease',
            outline: 'none'
          }}
          data-horario-emp={emp.id}
          data-horario-dia={idxDia}
          data-horario-field="tipo"
        >
          <option value="">— Selecciona —</option>
          <option value="PARTIDO">🔄 PARTIDO</option>
          <option value="DERECHO">➡️ DERECHO</option>
          <option value="DESCANSO">😴 DESCANSO</option>
          <option value="NO_TRABAJO">🚫 NO TRABAJO</option>
          <option value="OTRO">📝 OTRO</option>
        </select>

        {(cel.tipo === "PARTIDO" || cel.tipo === "DERECHO") && (
          <>
            {cel.tipo === "PARTIDO" ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginBottom: 6 }}>
                <select
                  value={cel.sede1 || ""}
                  onChange={(e) => setCell(emp.id, idxDia, { sede1: e.target.value })}
                  disabled={bloqueado}
                  style={{
                    padding: '5px 6px',
                    fontSize: 10,
                    fontWeight: 500,
                    border: '2px solid ' + (bloqueado ? '#ddd' : 'rgba(255, 107, 53, 0.25)'),
                    borderRadius: 6,
                    background: bloqueado ? 'rgba(200,200,200,0.3)' : 'rgba(255,255,255,0.95)',
                    color: bloqueado ? '#999' : '#1a1a2e',
                    cursor: bloqueado ? 'not-allowed' : 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'all 0.2s ease',
                    outline: 'none'
                  }}
                >
                  <option value="">📍 Sede 1</option>
                  {sedesLista.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <select
                  value={cel.sede2 || ""}
                  onChange={(e) => setCell(emp.id, idxDia, { sede2: e.target.value })}
                  disabled={bloqueado}
                  style={{
                    padding: '5px 6px',
                    fontSize: 10,
                    fontWeight: 500,
                    border: '2px solid ' + (bloqueado ? '#ddd' : 'rgba(255, 107, 53, 0.25)'),
                    borderRadius: 6,
                    background: bloqueado ? 'rgba(200,200,200,0.3)' : 'rgba(255,255,255,0.95)',
                    color: bloqueado ? '#999' : '#1a1a2e',
                    cursor: bloqueado ? 'not-allowed' : 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'all 0.2s ease',
                    outline: 'none'
                  }}
                >
                  <option value="">📍 Sede 2</option>
                  {sedesLista.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <select
                value={cel.sede1 || ""}
                onChange={(e) => setCell(emp.id, idxDia, { sede1: e.target.value })}
                disabled={bloqueado}
                style={{
                  width: '100%',
                  padding: '5px 6px',
                  fontSize: 10,
                  fontWeight: 500,
                  border: '2px solid ' + (bloqueado ? '#ddd' : 'rgba(255, 107, 53, 0.25)'),
                  borderRadius: 6,
                  background: bloqueado ? 'rgba(200,200,200,0.3)' : 'rgba(255,255,255,0.95)',
                  color: bloqueado ? '#999' : '#1a1a2e',
                  cursor: bloqueado ? 'not-allowed' : 'pointer',
                  marginBottom: 6,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s ease',
                  outline: 'none'
                }}
              >
                <option value="">📍 Sede</option>
                {sedesLista.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <div>
                <div style={{ fontSize: 9, fontWeight: 600, color: '#52c41a', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span>🟢</span> Entrada
                </div>
                <InputHoraAutocompletado
                  inputRef={e1Ref}
                  value={borrador.e1}
                  placeholder="Ej: 8:00 AM"
                  onChange={(val) => setBorrador((prev) => ({ ...prev, e1: val }))}
                  onCommit={(val) => commitHora("e1", val)}
                  onKeyDown={handleKeyDown("e1")}
                  disabled={bloqueado}
                  esEntrada={true}
                  dataAttrs={{
                    'data-horario-emp': emp.id,
                    'data-horario-dia': idxDia,
                    'data-horario-field': 'e1'
                  }}
                />
              </div>

              <div>
                <div style={{ fontSize: 9, fontWeight: 600, color: '#ff6b35', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span>🔴</span> Salida
                </div>
                <InputHoraAutocompletado
                  inputRef={s1Ref}
                  value={borrador.s1}
                  placeholder="Ej: 2:00 PM"
                  onChange={(val) => setBorrador((prev) => ({ ...prev, s1: val }))}
                  onCommit={(val) => commitHora("s1", val)}
                  onKeyDown={handleKeyDown("s1")}
                  disabled={bloqueado}
                  esEntrada={false}
                  dataAttrs={{
                    'data-horario-emp': emp.id,
                    'data-horario-dia': idxDia,
                    'data-horario-field': 's1'
                  }}
                />
              </div>

              {cel.tipo === "PARTIDO" && (
                <>
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: 9, fontWeight: 600, color: '#52c41a', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span>🟢</span> Entrada 2
                    </div>
                    <InputHoraAutocompletado
                      inputRef={e2Ref}
                      value={borrador.e2}
                      placeholder="Ej: 4:00 PM"
                      onChange={(val) => setBorrador((prev) => ({ ...prev, e2: val }))}
                      onCommit={(val) => commitHora("e2", val)}
                      onKeyDown={handleKeyDown("e2")}
                      disabled={bloqueado}
                      esEntrada={true}
                      dataAttrs={{
                        'data-horario-emp': emp.id,
                        'data-horario-dia': idxDia,
                        'data-horario-field': 'e2'
                      }}
                    />
                  </div>

                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: 9, fontWeight: 600, color: '#ff6b35', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span>🔴</span> Salida 2
                    </div>
                    <InputHoraAutocompletado
                      inputRef={s2Ref}
                      value={borrador.s2}
                      placeholder="Ej: 10:00 PM"
                      onChange={(val) => setBorrador((prev) => ({ ...prev, s2: val }))}
                      onCommit={(val) => commitHora("s2", val)}
                      onKeyDown={handleKeyDown("s2")}
                      disabled={bloqueado}
                      esEntrada={false}
                      dataAttrs={{
                        'data-horario-emp': emp.id,
                        'data-horario-dia': idxDia,
                        'data-horario-field': 's2'
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {cel.tipo === "OTRO" && (
          <>
            <select
              value={cel.otro || ""}
              onChange={(e) => {
                const val = e.target.value;
                setCell(emp.id, idxDia, { 
                  otro: val,
                  // Limpiar horas si no es "Otro"
                  ...(val !== "Otro" ? { e1: undefined, s1: undefined, sede1: undefined } : {})
                });
              }}
              disabled={bloqueado}
              data-horario-emp={emp.id}
              data-horario-dia={idxDia}
              data-horario-field="otro"
              style={{
                width: '100%',
                padding: '5px 6px',
                fontSize: 10,
                fontWeight: 500,
                border: '2px solid ' + (bloqueado ? '#ddd' : 'rgba(255, 107, 53, 0.25)'),
                borderRadius: 6,
                background: bloqueado ? 'rgba(200,200,200,0.3)' : 'rgba(255,255,255,0.95)',
                color: bloqueado ? '#999' : '#1a1a2e',
                cursor: bloqueado ? 'not-allowed' : 'pointer',
                marginBottom: 6,
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease',
                outline: 'none'
              }}
            >
              <option value="">— Selecciona —</option>
              <option value="Vacaciones">🏖️ Vacaciones</option>
              <option value="Incapacidad">🩺 Incapacidad</option>
              <option value="Suspensión">⛔ Suspensión</option>
              <option value="Otro">📝 Otro</option>
            </select>
            
            {/* Mostrar campos de horas solo cuando se selecciona "Otro" */}
            {cel.otro === "Otro" && (
              <>
                <select
                  value={cel.sede1 || ""}
                  onChange={(e) => setCell(emp.id, idxDia, { sede1: e.target.value })}
                  disabled={bloqueado}
                  style={{
                    width: '100%',
                    padding: '5px 6px',
                    fontSize: 10,
                    fontWeight: 500,
                    border: '2px solid ' + (bloqueado ? '#ddd' : 'rgba(255, 107, 53, 0.25)'),
                    borderRadius: 6,
                    background: bloqueado ? 'rgba(200,200,200,0.3)' : 'rgba(255,255,255,0.95)',
                    color: bloqueado ? '#999' : '#1a1a2e',
                    cursor: bloqueado ? 'not-allowed' : 'pointer',
                    marginBottom: 6,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    transition: 'all 0.2s ease',
                    outline: 'none'
                  }}
                >
                  <option value="">📍 Sede</option>
                  {sedesLista.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 600, color: '#52c41a', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span>🟢</span> Entrada
                    </div>
                    <InputHoraAutocompletado
                      value={borrador.e1}
                      placeholder="Ej: 8:00 AM"
                      onChange={(val) => setBorrador((prev) => ({ ...prev, e1: val }))}
                      onCommit={(val) => commitHora("e1", val)}
                      onKeyDown={handleKeyDown("e1")}
                      disabled={bloqueado}
                      esEntrada={true}
                      dataAttrs={{
                        'data-horario-emp': emp.id,
                        'data-horario-dia': idxDia,
                        'data-horario-field': 'e1'
                      }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 600, color: '#ff6b35', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span>🔴</span> Salida
                    </div>
                    <InputHoraAutocompletado
                      value={borrador.s1}
                      placeholder="Ej: 2:00 PM"
                      onChange={(val) => setBorrador((prev) => ({ ...prev, s1: val }))}
                      onCommit={(val) => commitHora("s1", val)}
                      onKeyDown={handleKeyDown("s1")}
                      disabled={bloqueado}
                      esEntrada={false}
                      dataAttrs={{
                        'data-horario-emp': emp.id,
                        'data-horario-dia': idxDia,
                        'data-horario-field': 's1'
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {eventosPorDia?.[emp.id]?.[fechasSemana[idxDia]] && (
          <div
            style={{
              marginTop: 6,
              padding: '4px 8px',
              borderRadius: 6,
              border: '2px solid rgba(255,255,255,0.5)',
              fontSize: 10,
              fontWeight: 600,
              textAlign: 'center',
              background: eventosPorDia[emp.id][fechasSemana[idxDia]].color,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
            title="Evento aplicado a este día"
          >
            📌 {eventosPorDia[emp.id][fechasSemana[idxDia]].nombre}
          </div>
        )}
      </div>
    );
  }
 
  // ===== Modal de evento (elige FECHA dentro de la semana + tipo + color) =====
  const [modalEvento, setModalEvento] = useState(null); // { empId } | null
  const [evFecha, setEvFecha] = useState(semanaInicio);
  const [evTipo, setEvTipo] = useState("");
  const [evColor, setEvColor] = useState("#fff3a6");
 
  React.useEffect(() => {
    // al abrir modal, default = lunes
    if (modalEvento) setEvFecha(semanaInicio);
  }, [modalEvento, semanaInicio]);
 
  const eventosPosibles = EVENTOS_BASE;
 
  // ===== UI =====
  return (
    <div style={{ padding: 16 }}>
      {/* Header mejorado */}
      <div style={{
        background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
        borderRadius: 16,
        padding: '20px 24px',
        marginBottom: 20,
        boxShadow: '0 8px 24px rgba(255, 107, 53, 0.25)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h2 style={{ 
              margin: 0, 
              fontSize: 22, 
              fontWeight: 700, 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}>
              📅 Programación de Horarios
            </h2>
            <p style={{ margin: '6px 0 0 0', fontSize: 13, color: 'rgba(255,255,255,0.9)' }}>
              Semana: <strong>{semanaInicio}</strong> → <strong>{semanaFin}</strong>
              <span style={{ 
                marginLeft: 12, 
                padding: '3px 10px', 
                background: 'rgba(255,255,255,0.25)', 
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 600
              }}>
                {(estado||"temporal").toUpperCase()} {horarioExistente ? "✓" : ""}
              </span>
            </p>
          </div>
          
          {/* Navegación de semana */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={()=>aplicarSemana(-1)}
              style={{
                background: 'rgba(255,255,255,0.95)',
                border: 'none',
                borderRadius: 10,
                padding: '10px 16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                fontSize: 13,
                fontWeight: 600,
                color: '#ff6b35',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
            >
              ◀ Anterior
            </button>
            <button
              onClick={()=>aplicarSemana(0)}
              style={{
                background: 'white',
                border: '3px solid rgba(255,255,255,0.5)',
                borderRadius: 10,
                padding: '10px 20px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                fontSize: 13,
                fontWeight: 700,
                color: '#ff6b35',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
              }}
            >
              📍 Hoy
            </button>
            <button
              onClick={()=>aplicarSemana(1)}
              style={{
                background: 'rgba(255,255,255,0.95)',
                border: 'none',
                borderRadius: 10,
                padding: '10px 16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                fontSize: 13,
                fontWeight: 600,
                color: '#ff6b35',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
            >
              Siguiente ▶
            </button>
          </div>
        </div>
        
        {/* Selector de fecha */}
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ color: 'white', fontSize: 12, fontWeight: 600 }}>Ir a semana:</label>
          <input 
            type="date" 
            value={semanaInicio} 
            onChange={(e)=>handleFechaBaseChange(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: 'none',
              fontSize: 13,
              fontWeight: 500,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              cursor: 'pointer'
            }}
          />
          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11 }}>
            (Selecciona cualquier día y se ajustará al lunes)
          </span>
        </div>
      </div>

      {/* Pestañas de navegación con menús desplegables */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 16,
        background: '#f1f5f9',
        padding: 6,
        borderRadius: 12,
        width: 'fit-content',
        position: 'relative'
      }}>
        {/* Menú Horarios Manuales */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => {
              setMenuHorariosManuales(!menuHorariosManuales);
              setMenuHorarioBiometrico(false);
            }}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              background: (vistaHorarios === "programacion" || vistaHorarios === "vista_grafica") 
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' 
                : 'white',
              color: (vistaHorarios === "programacion" || vistaHorarios === "vista_grafica") 
                ? 'white' 
                : '#64748b',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            ✏️ Horarios Manuales ▼
          </button>
          
          {menuHorariosManuales && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 4,
              background: 'white',
              borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              border: '1px solid #e2e8f0',
              zIndex: 100,
              minWidth: 220,
              overflow: 'hidden'
            }}>
              <button
                onClick={() => {
                  setVistaHorarios("programacion");
                  setMenuHorariosManuales(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: vistaHorarios === "programacion" ? '#fff7ed' : 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: vistaHorarios === "programacion" ? 600 : 500,
                  color: vistaHorarios === "programacion" ? '#ff6b35' : '#374151',
                  borderBottom: '1px solid #f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = vistaHorarios === "programacion" ? '#fff7ed' : '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = vistaHorarios === "programacion" ? '#fff7ed' : 'white'}
              >
                📅 Programación de Horarios
                {vistaHorarios === "programacion" && <span style={{ marginLeft: 'auto' }}>✓</span>}
              </button>
              <button
                onClick={() => {
                  setVistaHorarios("vista_grafica");
                  setMenuHorariosManuales(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: vistaHorarios === "vista_grafica" ? '#ecfeff' : 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: vistaHorarios === "vista_grafica" ? 600 : 500,
                  color: vistaHorarios === "vista_grafica" ? '#0891b2' : '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = vistaHorarios === "vista_grafica" ? '#ecfeff' : '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = vistaHorarios === "vista_grafica" ? '#ecfeff' : 'white'}
              >
                🏪 Vista Gráfica
                {vistaHorarios === "vista_grafica" && <span style={{ marginLeft: 'auto' }}>✓</span>}
              </button>
            </div>
          )}
        </div>

        {/* Menú Horario Biométrico - Solo si tiene algún permiso relacionado */}
        {(tienePermiso("rh_horarios_subir_marcaciones") || tienePermiso("rh_horarios_ver_marcaciones_reales")) && (
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => {
              setMenuHorarioBiometrico(!menuHorarioBiometrico);
              setMenuHorariosManuales(false);
            }}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              background: (vistaHorarios === "marcaciones_temporales" || vistaHorarios === "marcaciones_reales" || 
                          vistaHorarios === "analisis" || vistaHorarios === "horario_nomina" || 
                          vistaHorarios === "actualizar_horarios") 
                ? 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' 
                : 'white',
              color: (vistaHorarios === "marcaciones_temporales" || vistaHorarios === "marcaciones_reales" || 
                     vistaHorarios === "analisis" || vistaHorarios === "horario_nomina" || 
                     vistaHorarios === "actualizar_horarios") 
                ? 'white' 
                : '#64748b',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            🔐 Horario Biométrico ▼
            {marcacionesTemporales.length > 0 && (
              <span style={{
                background: '#ef4444',
                color: 'white',
                fontSize: 10,
                fontWeight: 700,
                padding: '2px 6px',
                borderRadius: 10
              }}>
                {marcacionesTemporales.length}
              </span>
            )}
          </button>
          
          {menuHorarioBiometrico && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: 4,
              background: 'white',
              borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
              border: '1px solid #e2e8f0',
              zIndex: 100,
              minWidth: 280,
              overflow: 'hidden'
            }}>
              {tienePermiso("rh_horarios_subir_marcaciones") && (
              <button
                onClick={() => {
                  setVistaHorarios("marcaciones_temporales");
                  setMenuHorarioBiometrico(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: vistaHorarios === "marcaciones_temporales" ? '#fff7ed' : 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: vistaHorarios === "marcaciones_temporales" ? 600 : 500,
                  color: vistaHorarios === "marcaciones_temporales" ? '#ff6b35' : '#374151',
                  borderBottom: '1px solid #f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = vistaHorarios === "marcaciones_temporales" ? '#fff7ed' : '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = vistaHorarios === "marcaciones_temporales" ? '#fff7ed' : 'white'}
              >
                ⏳ Horario Temporal
                {marcacionesTemporales.length > 0 && (
                  <span style={{
                    background: '#ef4444',
                    color: 'white',
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '2px 6px',
                    borderRadius: 10,
                    marginLeft: 8
                  }}>
                    {marcacionesTemporales.length}
                  </span>
                )}
                {vistaHorarios === "marcaciones_temporales" && <span style={{ marginLeft: 'auto' }}>✓</span>}
              </button>
              )}
              
              {tienePermiso("rh_horarios_ver_marcaciones_reales") && (
              <>
              <button
                onClick={() => {
                  setVistaHorarios("marcaciones_reales");
                  setMenuHorarioBiometrico(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: vistaHorarios === "marcaciones_reales" ? '#ecfdf5' : 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: vistaHorarios === "marcaciones_reales" ? 600 : 500,
                  color: vistaHorarios === "marcaciones_reales" ? '#10b981' : '#374151',
                  borderBottom: '1px solid #f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = vistaHorarios === "marcaciones_reales" ? '#ecfdf5' : '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = vistaHorarios === "marcaciones_reales" ? '#ecfdf5' : 'white'}
              >
                ✅ Horas de Entrada y Salida Real
                {vistaHorarios === "marcaciones_reales" && <span style={{ marginLeft: 'auto' }}>✓</span>}
              </button>
              
              <button
                onClick={() => {
                  setVistaHorarios("analisis");
                  setMenuHorarioBiometrico(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: vistaHorarios === "analisis" ? '#eff6ff' : 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: vistaHorarios === "analisis" ? 600 : 500,
                  color: vistaHorarios === "analisis" ? '#3b82f6' : '#374151',
                  borderBottom: '1px solid #f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = vistaHorarios === "analisis" ? '#eff6ff' : '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = vistaHorarios === "analisis" ? '#eff6ff' : 'white'}
              >
                📊 Análisis Comparativo
                {vistaHorarios === "analisis" && <span style={{ marginLeft: 'auto' }}>✓</span>}
              </button>
              
              <button
                onClick={() => {
                  setVistaHorarios("horario_nomina");
                  setMenuHorarioBiometrico(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: vistaHorarios === "horario_nomina" ? '#fef3c7' : 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: vistaHorarios === "horario_nomina" ? 600 : 500,
                  color: vistaHorarios === "horario_nomina" ? '#d97706' : '#374151',
                  borderBottom: '1px solid #f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = vistaHorarios === "horario_nomina" ? '#fef3c7' : '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = vistaHorarios === "horario_nomina" ? '#fef3c7' : 'white'}
              >
                💰 Horario Nómina
                {vistaHorarios === "horario_nomina" && <span style={{ marginLeft: 'auto' }}>✓</span>}
              </button>
              
              <button
                onClick={() => {
                  setVistaHorarios("actualizar_horarios");
                  setMenuHorarioBiometrico(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: vistaHorarios === "actualizar_horarios" ? '#f3e8ff' : 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: vistaHorarios === "actualizar_horarios" ? 600 : 500,
                  color: vistaHorarios === "actualizar_horarios" ? '#7c3aed' : '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = vistaHorarios === "actualizar_horarios" ? '#f3e8ff' : '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = vistaHorarios === "actualizar_horarios" ? '#f3e8ff' : 'white'}
              >
                🔄 Actualizar Horarios
                {vistaHorarios === "actualizar_horarios" && <span style={{ marginLeft: 'auto' }}>✓</span>}
              </button>
              </>
              )}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Cerrar menús al hacer clic fuera */}
      {(menuHorariosManuales || menuHorarioBiometrico) && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 50
          }}
          onClick={() => {
            setMenuHorariosManuales(false);
            setMenuHorarioBiometrico(false);
          }}
        />
      )}

      {/* Vista: Marcaciones Temporales (Pendientes de Aprobación) */}
      {vistaHorarios === "marcaciones_temporales" && tienePermiso("rh_horarios_subir_marcaciones") && (
        <div style={{
          background: 'white',
          borderRadius: 12,
          padding: 20,
          border: '2px solid #e9ecef',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>
                ⏳ Marcaciones Temporales
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                Archivos de marcaciones pendientes de aprobación por gerencia
              </p>
            </div>
            {tienePermiso("rh_horarios_subir_marcaciones") && (
            <label style={{
              background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
              color: 'white',
              padding: '10px 20px',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: subiendoArchivo ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
              opacity: subiendoArchivo ? 0.7 : 1
            }}>
              <span>📤</span> {subiendoArchivo ? "Procesando..." : "Subir Archivo CSV"}
              <input
                type="file"
                accept=".csv,.txt"
                onChange={handleSubirCSV}
                disabled={subiendoArchivo}
                style={{ display: 'none' }}
              />
            </label>
            )}
          </div>

          {cargandoMarcaciones ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              ⏳ Cargando marcaciones...
            </div>
          ) : marcacionesTemporales.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 40,
              background: '#f8fafc',
              borderRadius: 10,
              border: '2px dashed #e2e8f0'
            }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
              <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
                No hay archivos de marcaciones pendientes de aprobación
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#94a3b8' }}>
                Sube un archivo CSV del dispositivo HikVision para comenzar
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {marcacionesTemporales.map(archivo => (
                <div key={archivo.id} style={{
                  background: '#fffbeb',
                  border: '2px solid #fcd34d',
                  borderRadius: 12,
                  padding: 16
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>
                        📁 {archivo.nombreArchivo}
                      </h4>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>
                        Subido: {new Date(archivo.fechaSubida).toLocaleString('es-CO')} • {archivo.totalRegistros} registros
                      </p>
                    </div>
                    {tienePermiso("rh_horarios_aprobar_marcaciones") && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => handleRechazarMarcaciones(archivo.id)}
                          style={{
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: '1px solid #fecaca',
                            padding: '8px 16px',
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          ❌ Rechazar
                        </button>
                        <button
                          onClick={() => handleAprobarMarcaciones(archivo.id)}
                          style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                          }}
                        >
                          ✅ Aprobar
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Vista previa de marcaciones */}
                  <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead>
                        <tr style={{ background: '#fef3c7' }}>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #fcd34d' }}>Empleado</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #fcd34d' }}>Sede 1</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #fcd34d' }}>Fecha</th>
                          <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #fcd34d' }}>E1</th>
                          <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #fcd34d' }}>S1</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #fcd34d' }}>Sede 2</th>
                          <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #fcd34d' }}>E2</th>
                          <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #fcd34d' }}>S2</th>
                          <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #fcd34d' }}>Tipo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {archivo.marcaciones.slice(0, 20).map((m, idx) => (
                          <tr key={idx} style={{ background: m.incompleto ? '#fef2f2' : 'white' }}>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>
                              {m.nombreEmpleado || m.documento}
                            </td>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', fontWeight: 600, color: '#0891b2', fontSize: 10 }}>
                              {m.sede1 || m.sede || '-'}
                            </td>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }}>
                              {m.fecha}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #f3f4f6', color: '#16a34a', fontWeight: 600 }}>
                              {m.entrada1 || '-'}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #f3f4f6', color: '#dc2626', fontWeight: 600 }}>
                              {m.salida1 || '-'}
                            </td>
                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f4f6', fontWeight: 600, color: '#7c3aed', fontSize: 10 }}>
                              {m.sede2 || (m.tipoHorario === 'PARTIDO' ? '-' : '')}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #f3f4f6', color: '#16a34a', fontWeight: 600 }}>
                              {m.entrada2 || '-'}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #f3f4f6', color: '#dc2626', fontWeight: 600 }}>
                              {m.salida2 || '-'}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #f3f4f6' }}>
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: 4,
                                fontSize: 10,
                                fontWeight: 600,
                                background: m.tipoHorario === 'INCOMPLETO' ? '#fef2f2' : m.tipoHorario === 'PARTIDO' ? '#f0fdf4' : '#eff6ff',
                                color: m.tipoHorario === 'INCOMPLETO' ? '#dc2626' : m.tipoHorario === 'PARTIDO' ? '#16a34a' : '#2563eb'
                              }}>
                                {m.tipoHorario}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {archivo.marcaciones.length > 20 && (
                      <div style={{ textAlign: 'center', padding: 8, fontSize: 11, color: '#64748b' }}>
                        ... y {archivo.marcaciones.length - 20} registros más
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Vista: Marcaciones Reales Aprobadas */}
      {vistaHorarios === "marcaciones_reales" && tienePermiso("rh_horarios_ver_marcaciones_reales") && (
        <div style={{
          background: 'white',
          borderRadius: 12,
          padding: 20,
          border: '2px solid #e9ecef',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>
                ✅ Horas de Entrada y Salida Real
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                Registro de marcaciones aprobadas del dispositivo HikVision
              </p>
            </div>
          </div>

          {/* Filtros */}
          <div style={{
            display: 'flex',
            gap: 12,
            marginBottom: 16,
            padding: 12,
            background: '#f8fafc',
            borderRadius: 8,
            flexWrap: 'wrap',
            alignItems: 'flex-end'
          }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Desde</label>
              <input
                type="date"
                value={filtroMarcaciones.fechaDesde}
                onChange={(e) => setFiltroMarcaciones(prev => ({ ...prev, fechaDesde: e.target.value }))}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Hasta</label>
              <input
                type="date"
                value={filtroMarcaciones.fechaHasta}
                onChange={(e) => setFiltroMarcaciones(prev => ({ ...prev, fechaHasta: e.target.value }))}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Empleado</label>
              <select
                value={filtroMarcaciones.empleadoId}
                onChange={(e) => setFiltroMarcaciones(prev => ({ ...prev, empleadoId: e.target.value }))}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, minWidth: 200 }}
              >
                <option value="">Todos los empleados</option>
                {activos.map(e => (
                  <option key={e.id} value={e.id}>{e.nombre} - {e.documento}</option>
                ))}
              </select>
            </div>
            <button
              onClick={cargarMarcacionesRealesConFiltros}
              style={{
                background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              🔍 Buscar
            </button>
          </div>

          {cargandoMarcaciones ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
              ⏳ Cargando marcaciones...
            </div>
          ) : marcacionesReales.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 40,
              background: '#f8fafc',
              borderRadius: 10,
              border: '2px dashed #e2e8f0'
            }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
              <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
                No hay marcaciones registradas para los filtros seleccionados
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                    <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 700 }}>Empleado</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 700 }}>Documento</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 700 }}>Fecha</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 700, background: '#e0f2fe' }}>Sede 1</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700, background: '#dcfce7' }}>E1</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700, background: '#fee2e2' }}>S1</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 700, background: '#f3e8ff' }}>Sede 2</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700, background: '#dcfce7' }}>E2</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700, background: '#fee2e2' }}>S2</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 700 }}>Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  {marcacionesReales.map((m, idx) => {
                    const emp = empleados.find(e => e.id === m.empleadoId);
                    return (
                      <tr key={m.id || idx} style={{ background: m.tipoHorario === 'INCOMPLETO' ? '#fef2f2' : idx % 2 === 0 ? 'white' : '#fafafa' }}>
                        <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', fontWeight: 500 }}>
                          {emp?.nombre || 'Desconocido'}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', color: '#64748b' }}>
                          {m.documento}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', fontWeight: 500 }}>
                          {m.fecha}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', color: '#0891b2', fontWeight: 600, fontSize: 10 }}>
                          {m.sede1 || m.sede || '-'}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #f3f4f6', color: '#16a34a', fontWeight: 700, fontSize: 12 }}>
                          {m.entrada1 || '-'}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #f3f4f6', color: '#dc2626', fontWeight: 700, fontSize: 12 }}>
                          {m.salida1 || '-'}
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #f3f4f6', color: '#7c3aed', fontWeight: 600, fontSize: 10 }}>
                          {m.sede2 || (m.tipoHorario === 'PARTIDO' ? '-' : '')}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #f3f4f6', color: '#16a34a', fontWeight: 700, fontSize: 12 }}>
                          {m.entrada2 || '-'}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #f3f4f6', color: '#dc2626', fontWeight: 700, fontSize: 12 }}>
                          {m.salida2 || '-'}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #f3f4f6' }}>
                          <span style={{
                            padding: '3px 10px',
                            borderRadius: 6,
                            fontSize: 10,
                            fontWeight: 700,
                            background: m.tipoHorario === 'INCOMPLETO' ? '#fef2f2' : m.tipoHorario === 'PARTIDO' ? '#f0fdf4' : '#eff6ff',
                            color: m.tipoHorario === 'INCOMPLETO' ? '#dc2626' : m.tipoHorario === 'PARTIDO' ? '#16a34a' : '#2563eb',
                            border: m.tipoHorario === 'INCOMPLETO' ? '1px solid #fecaca' : m.tipoHorario === 'PARTIDO' ? '1px solid #bbf7d0' : '1px solid #bfdbfe'
                          }}>
                            {m.tipoHorario}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Vista: Análisis Comparativo */}
      {vistaHorarios === "analisis" && tienePermiso("rh_horarios_ver_marcaciones_reales") && (
        <AnalisisComparativoHorarios
          empleados={activos}
          horariosRepo={horariosRepo}
          marcacionesReales={marcacionesReales}
          cargarMarcacionesReales={cargarMarcacionesRealesDeSupabase}
          fechasSemana={fechasSemana}
          semanaInicio={semanaInicio}
          semanaFin={semanaFin}
          sedes={sedesLista}
        />
      )}

      {/* Vista: Horario Nómina */}
      {vistaHorarios === "horario_nomina" && tienePermiso("rh_horarios_ver_marcaciones_reales") && (
        <div style={{
          background: 'white',
          borderRadius: 12,
          padding: 20,
          border: '2px solid #10b981',
          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.15)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>
                💰 Horario Nómina
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                Horarios actualizados para liquidación de nómina - Semana {semanaInicio} → {semanaFin}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => {
                  // Inicializar horariosNomina con los horarios programados
                  const nominaInicial = {};
                  activos.forEach(emp => {
                    if (celdas[emp.id]) {
                      nominaInicial[emp.id] = JSON.parse(JSON.stringify(celdas[emp.id]));
                    }
                  });
                  setHorariosNomina(nominaInicial);
                  alert("✅ Horarios de nómina inicializados desde la programación");
                }}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
              >
                📥 Cargar desde Programación
              </button>
            </div>
          </div>

          {/* Tabla de Horario Nómina */}
          <div style={{ 
            background: 'white', 
            borderRadius: 12, 
            border: '2px solid #e9ecef',
            overflow: 'hidden',
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
          }}>
            <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
              <thead style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' }}>
                <tr>
                  <th style={{ 
                    padding: '14px 16px', 
                    textAlign: 'left', 
                    fontWeight: 700,
                    color: '#065f46',
                    borderBottom: '2px solid #10b981',
                    width: 200
                  }}>
                    👤 Empleado
                  </th>
                  {fechasSemana.map((iso, i) => {
                    const diaNum = iso.split('-')[2];
                    return (
                      <th key={i} style={{ 
                        padding: '14px 12px', 
                        textAlign: 'center',
                        fontWeight: 600,
                        color: '#065f46',
                        borderBottom: '2px solid #10b981',
                        background: 'transparent'
                      }}>
                        <div style={{ fontSize: 11, opacity: 0.7 }}>{DIAS[i]}</div>
                        <div style={{ fontSize: 16, fontWeight: 700 }}>{diaNum}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {activos.filter(e => selectedIds.includes(e.id)).map((emp, empIdx) => {
                  const nominaEmp = horariosNomina[emp.id] || celdas[emp.id] || {};
                  return (
                    <tr key={emp.id} style={{ 
                      borderTop: '1px solid #e9ecef',
                      background: empIdx % 2 === 0 ? 'white' : '#f0fdf4'
                    }}>
                      <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
                        <div style={{ fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{emp.nombre}</div>
                        <div style={{ fontSize: 10, color: '#6c757d' }}>{emp.tipoDoc} {emp.documento}</div>
                      </td>
                      {fechasSemana.map((iso, idx) => {
                        const cel = nominaEmp[idx] || {};
                        const tipoAbrev = cel.tipo === "PARTIDO" ? "P" : cel.tipo === "CONTINUO" ? "C" : cel.tipo === "DESCANSO" ? "D" : "-";
                        return (
                          <td key={idx} style={{ 
                            padding: '8px 6px', 
                            verticalAlign: 'top',
                            borderLeft: '1px solid #f0f0f0',
                            fontSize: 10
                          }}>
                            {cel.tipo && cel.tipo !== "DESCANSO" && cel.tipo !== "NO_TRABAJO" ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: '#16a34a', fontWeight: 600 }}>E1: {cel.e1 || '-'}</span>
                                  <span style={{ color: '#dc2626', fontWeight: 600 }}>S1: {cel.s1 || '-'}</span>
                                </div>
                                {cel.tipo === "PARTIDO" && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: '#2563eb', fontWeight: 600 }}>E2: {cel.e2 || '-'}</span>
                                    <span style={{ color: '#7c3aed', fontWeight: 600 }}>S2: {cel.s2 || '-'}</span>
                                  </div>
                                )}
                                <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>
                                  📍 {cel.sede1 || cel.sede || emp.sede || '-'}
                                </div>
                              </div>
                            ) : (
                              <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: 18 }}>
                                {cel.tipo === "DESCANSO" ? '😴' : '-'}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {Object.keys(horariosNomina).length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: 40,
              background: '#f0fdf4',
              borderRadius: 10,
              marginTop: 16,
              border: '2px dashed #10b981'
            }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
              <p style={{ margin: 0, fontSize: 14, color: '#065f46', fontWeight: 600 }}>
                No hay horarios de nómina cargados
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#10b981' }}>
                Haz clic en "Cargar desde Programación" o utiliza "Actualizar Horarios" para cargar desde las marcaciones reales
              </p>
            </div>
          )}
        </div>
      )}

      {/* Vista: Actualizar Horarios */}
      {vistaHorarios === "actualizar_horarios" && tienePermiso("rh_horarios_ver_marcaciones_reales") && (
        <div style={{
          background: 'white',
          borderRadius: 12,
          padding: 20,
          border: '2px solid #7c3aed',
          boxShadow: '0 4px 12px rgba(124, 58, 237, 0.15)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>
                🔄 Actualizar Horarios
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                Selecciona las diferencias a aplicar desde las marcaciones reales al Horario Nómina
              </p>
            </div>
          </div>

          {/* Filtros */}
          <div style={{
            display: 'flex',
            gap: 12,
            marginBottom: 16,
            padding: 16,
            background: '#faf5ff',
            borderRadius: 10,
            flexWrap: 'wrap',
            alignItems: 'flex-end'
          }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#7c3aed', marginBottom: 4 }}>Desde</label>
              <input
                type="date"
                value={filtroHorarioNomina.fechaDesde}
                onChange={(e) => setFiltroHorarioNomina(prev => ({ ...prev, fechaDesde: e.target.value }))}
                style={{ padding: '8px 12px', borderRadius: 8, border: '2px solid #e9d5ff', fontSize: 12 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#7c3aed', marginBottom: 4 }}>Hasta</label>
              <input
                type="date"
                value={filtroHorarioNomina.fechaHasta}
                onChange={(e) => setFiltroHorarioNomina(prev => ({ ...prev, fechaHasta: e.target.value }))}
                style={{ padding: '8px 12px', borderRadius: 8, border: '2px solid #e9d5ff', fontSize: 12 }}
              />
            </div>
            <button
              onClick={async () => {
                setCargandoMarcaciones(true);
                try {
                  const marcaciones = await cargarMarcacionesRealesDeSupabase(null, {
                    fechaDesde: filtroHorarioNomina.fechaDesde,
                    fechaHasta: filtroHorarioNomina.fechaHasta
                  });
                  
                  // Encontrar diferencias entre programado y real
                  const diffs = [];
                  marcaciones.forEach(m => {
                    const emp = activos.find(e => e.id === m.empleadoId);
                    if (!emp) return;
                    
                    const fechaISO = m.fecha;
                    const lunes = mondayOf(fechaISO);
                    const domingo = sundayOf(lunes);
                    const horario = horariosRepo.find(h => h.semanaInicio === lunes && h.semanaFin === domingo);
                    if (!horario || !horario.celdas || !horario.celdas[emp.id]) return;
                    
                    const diaIdx = dayIndexMon0(fechaISO);
                    const prog = horario.celdas[emp.id][diaIdx];
                    if (!prog || prog.tipo === "DESCANSO" || prog.tipo === "NO_TRABAJO") return;
                    
                    // Comparar horarios
                    const horaAMinutos = (horaStr) => {
                      if (!horaStr || horaStr === '-') return null;
                      const texto = horaStr.toString().trim().toUpperCase();
                      const match = texto.match(/^(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)?$/);
                      if (!match) return null;
                      let [, h, min, periodo] = match;
                      let hh = parseInt(h, 10);
                      const mm = parseInt(min || "0", 10);
                      if (periodo === "PM" && hh !== 12) hh += 12;
                      if (periodo === "AM" && hh === 12) hh = 0;
                      return hh * 60 + mm;
                    };
                    
                    const difE1 = horaAMinutos(m.entrada1) !== null && horaAMinutos(prog.e1) !== null
                      ? horaAMinutos(m.entrada1) - horaAMinutos(prog.e1) : null;
                    const difS1 = horaAMinutos(m.salida1) !== null && horaAMinutos(prog.s1) !== null
                      ? horaAMinutos(m.salida1) - horaAMinutos(prog.s1) : null;
                    const difE2 = horaAMinutos(m.entrada2) !== null && horaAMinutos(prog.e2) !== null
                      ? horaAMinutos(m.entrada2) - horaAMinutos(prog.e2) : null;
                    const difS2 = horaAMinutos(m.salida2) !== null && horaAMinutos(prog.s2) !== null
                      ? horaAMinutos(m.salida2) - horaAMinutos(prog.s2) : null;
                    
                    const tieneDiferencia = 
                      (difE1 !== null && difE1 > 0) || // Llegada tarde E1
                      (difS1 !== null && difS1 < 0) || // Salida temprana S1
                      (difE2 !== null && difE2 > 0) || // Llegada tarde E2
                      (difS2 !== null && difS2 < 0) || // Salida temprana S2
                      (m.sede1 && prog.sede1 && m.sede1 !== prog.sede1) || // Cambio de sede
                      (m.sede2 && prog.sede2 && m.sede2 !== prog.sede2);
                    
                    if (tieneDiferencia) {
                      diffs.push({
                        id: `${m.empleadoId}_${m.fecha}`,
                        empleadoId: m.empleadoId,
                        empleadoNombre: emp.nombre,
                        documento: emp.documento,
                        fecha: m.fecha,
                        diaIdx: diaIdx,
                        diaSemana: DIAS[diaIdx],
                        programado: prog,
                        real: {
                          e1: m.entrada1,
                          s1: m.salida1,
                          e2: m.entrada2,
                          s2: m.salida2,
                          sede1: m.sede1 || m.sede,
                          sede2: m.sede2,
                          tipo: m.tipoHorario
                        },
                        diferencias: {
                          e1: difE1,
                          s1: difS1,
                          e2: difE2,
                          s2: difS2
                        },
                        minutosTarde: Math.max(0, difE1 || 0) + Math.max(0, difE2 || 0),
                        minutosSalidaTemprana: Math.abs(Math.min(0, difS1 || 0)) + Math.abs(Math.min(0, difS2 || 0)),
                        seleccionado: false
                      });
                    }
                  });
                  
                  setDiferenciasHorarios(diffs);
                  setSeleccionActualizacion(prev => ({
                    ...prev,
                    empleadosIds: [...new Set(diffs.map(d => d.empleadoId))]
                  }));
                  
                } catch (e) {
                  console.error("Error buscando diferencias:", e);
                  alert("Error al buscar diferencias: " + e.message);
                }
                setCargandoMarcaciones(false);
              }}
              style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)'
              }}
              disabled={cargandoMarcaciones}
            >
              {cargandoMarcaciones ? '⏳ Buscando...' : '🔍 Buscar Diferencias'}
            </button>
          </div>

          {/* Opciones de actualización */}
          {diferenciasHorarios.length > 0 && (
            <div style={{
              background: '#faf5ff',
              borderRadius: 10,
              padding: 16,
              marginBottom: 16,
              border: '1px solid #e9d5ff'
            }}>
              <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#7c3aed' }}>
                ⚙️ Opciones de Actualización
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={seleccionActualizacion.actualizarTodo}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSeleccionActualizacion(prev => ({
                        ...prev,
                        actualizarTodo: checked,
                        actualizarE1: checked,
                        actualizarS1: checked,
                        actualizarE2: checked,
                        actualizarS2: checked,
                        actualizarSede: checked
                      }));
                    }}
                    style={{ width: 16, height: 16 }}
                  />
                  <span style={{ fontWeight: 600 }}>Seleccionar Todo</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={seleccionActualizacion.actualizarE1}
                    onChange={(e) => setSeleccionActualizacion(prev => ({ ...prev, actualizarE1: e.target.checked }))}
                    style={{ width: 16, height: 16 }}
                  />
                  <span style={{ color: '#16a34a' }}>✓ Entrada 1</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={seleccionActualizacion.actualizarS1}
                    onChange={(e) => setSeleccionActualizacion(prev => ({ ...prev, actualizarS1: e.target.checked }))}
                    style={{ width: 16, height: 16 }}
                  />
                  <span style={{ color: '#dc2626' }}>✓ Salida 1</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={seleccionActualizacion.actualizarE2}
                    onChange={(e) => setSeleccionActualizacion(prev => ({ ...prev, actualizarE2: e.target.checked }))}
                    style={{ width: 16, height: 16 }}
                  />
                  <span style={{ color: '#2563eb' }}>✓ Entrada 2</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={seleccionActualizacion.actualizarS2}
                    onChange={(e) => setSeleccionActualizacion(prev => ({ ...prev, actualizarS2: e.target.checked }))}
                    style={{ width: 16, height: 16 }}
                  />
                  <span style={{ color: '#7c3aed' }}>✓ Salida 2</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={seleccionActualizacion.actualizarSede}
                    onChange={(e) => setSeleccionActualizacion(prev => ({ ...prev, actualizarSede: e.target.checked }))}
                    style={{ width: 16, height: 16 }}
                  />
                  <span style={{ color: '#0891b2' }}>✓ Sede</span>
                </label>
              </div>
            </div>
          )}

          {/* Tabla de diferencias */}
          {cargandoMarcaciones ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#7c3aed' }}>
              ⏳ Buscando diferencias...
            </div>
          ) : diferenciasHorarios.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 40,
              background: '#faf5ff',
              borderRadius: 10,
              border: '2px dashed #e9d5ff'
            }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
              <p style={{ margin: 0, fontSize: 14, color: '#7c3aed', fontWeight: 600 }}>
                No hay diferencias encontradas
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#a78bfa' }}>
                Selecciona un rango de fechas y haz clic en "Buscar Diferencias"
              </p>
            </div>
          ) : (
            <>
              {/* Resumen de diferencias */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 12,
                marginBottom: 16
              }}>
                <div style={{ padding: 16, background: '#fef2f2', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#dc2626' }}>
                    {diferenciasHorarios.reduce((sum, d) => sum + d.minutosTarde, 0)}m
                  </div>
                  <div style={{ fontSize: 11, color: '#ef4444' }}>Total Minutos Llegada Tarde</div>
                </div>
                <div style={{ padding: 16, background: '#fffbeb', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#d97706' }}>
                    {diferenciasHorarios.reduce((sum, d) => sum + d.minutosSalidaTemprana, 0)}m
                  </div>
                  <div style={{ fontSize: 11, color: '#f59e0b' }}>Total Minutos Salida Temprana</div>
                </div>
                <div style={{ padding: 16, background: '#f0f9ff', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#0891b2' }}>
                    {diferenciasHorarios.length}
                  </div>
                  <div style={{ fontSize: 11, color: '#06b6d4' }}>Registros con Diferencias</div>
                </div>
                <div style={{ padding: 16, background: '#faf5ff', borderRadius: 10, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#7c3aed' }}>
                    {new Set(diferenciasHorarios.map(d => d.empleadoId)).size}
                  </div>
                  <div style={{ fontSize: 11, color: '#a78bfa' }}>Empleados Afectados</div>
                </div>
              </div>

              {/* Tabla de diferencias */}
              <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)' }}>
                      <th style={{ padding: 10, borderBottom: '2px solid #7c3aed', textAlign: 'center', width: 40 }}>
                        <input
                          type="checkbox"
                          checked={diferenciasHorarios.every(d => d.seleccionado)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setDiferenciasHorarios(prev => prev.map(d => ({ ...d, seleccionado: checked })));
                          }}
                          style={{ width: 16, height: 16 }}
                        />
                      </th>
                      <th style={{ padding: 10, textAlign: 'left', borderBottom: '2px solid #7c3aed' }}>Empleado</th>
                      <th style={{ padding: 10, textAlign: 'left', borderBottom: '2px solid #7c3aed' }}>Fecha</th>
                      <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #7c3aed', background: '#dcfce7' }}>E1 Prog → Real</th>
                      <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #7c3aed', background: '#fee2e2' }}>S1 Prog → Real</th>
                      <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #7c3aed', background: '#dbeafe' }}>E2 Prog → Real</th>
                      <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #7c3aed', background: '#fae8ff' }}>S2 Prog → Real</th>
                      <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #7c3aed', color: '#dc2626' }}>Min. Tarde</th>
                      <th style={{ padding: 10, textAlign: 'center', borderBottom: '2px solid #7c3aed', color: '#d97706' }}>Min. Sal. Temp.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diferenciasHorarios.map((dif, idx) => (
                      <tr key={dif.id} style={{ background: dif.seleccionado ? '#f5f3ff' : idx % 2 === 0 ? 'white' : '#fafafa' }}>
                        <td style={{ padding: 8, textAlign: 'center', borderBottom: '1px solid #f3f4f6' }}>
                          <input
                            type="checkbox"
                            checked={dif.seleccionado}
                            onChange={(e) => {
                              setDiferenciasHorarios(prev => prev.map(d => 
                                d.id === dif.id ? { ...d, seleccionado: e.target.checked } : d
                              ));
                            }}
                            style={{ width: 14, height: 14 }}
                          />
                        </td>
                        <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>
                          <div style={{ fontWeight: 600 }}>{dif.empleadoNombre}</div>
                          <div style={{ fontSize: 10, color: '#64748b' }}>{dif.documento}</div>
                        </td>
                        <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>
                          <div style={{ fontWeight: 500 }}>{dif.fecha}</div>
                          <div style={{ fontSize: 10, color: '#64748b' }}>{dif.diaSemana}</div>
                        </td>
                        <td style={{ padding: 8, textAlign: 'center', borderBottom: '1px solid #f3f4f6' }}>
                          <span style={{ color: '#64748b' }}>{convertir24hA12h(dif.programado.e1) || '-'}</span>
                          <span> → </span>
                          <span style={{ color: dif.diferencias.e1 > 0 ? '#dc2626' : '#16a34a', fontWeight: 700 }}>
                            {dif.real.e1 || '-'}
                          </span>
                          {dif.diferencias.e1 > 0 && (
                            <span style={{ fontSize: 9, color: '#dc2626', marginLeft: 4 }}>+{dif.diferencias.e1}m</span>
                          )}
                        </td>
                        <td style={{ padding: 8, textAlign: 'center', borderBottom: '1px solid #f3f4f6' }}>
                          <span style={{ color: '#64748b' }}>{convertir24hA12h(dif.programado.s1) || '-'}</span>
                          <span> → </span>
                          <span style={{ color: dif.diferencias.s1 < 0 ? '#d97706' : '#16a34a', fontWeight: 700 }}>
                            {dif.real.s1 || '-'}
                          </span>
                          {dif.diferencias.s1 < 0 && (
                            <span style={{ fontSize: 9, color: '#d97706', marginLeft: 4 }}>{dif.diferencias.s1}m</span>
                          )}
                        </td>
                        <td style={{ padding: 8, textAlign: 'center', borderBottom: '1px solid #f3f4f6' }}>
                          <span style={{ color: '#64748b' }}>{convertir24hA12h(dif.programado.e2) || '-'}</span>
                          <span> → </span>
                          <span style={{ color: dif.diferencias.e2 > 0 ? '#dc2626' : '#16a34a', fontWeight: 700 }}>
                            {dif.real.e2 || '-'}
                          </span>
                          {dif.diferencias.e2 > 0 && (
                            <span style={{ fontSize: 9, color: '#dc2626', marginLeft: 4 }}>+{dif.diferencias.e2}m</span>
                          )}
                        </td>
                        <td style={{ padding: 8, textAlign: 'center', borderBottom: '1px solid #f3f4f6' }}>
                          <span style={{ color: '#64748b' }}>{convertir24hA12h(dif.programado.s2) || '-'}</span>
                          <span> → </span>
                          <span style={{ color: dif.diferencias.s2 < 0 ? '#d97706' : '#16a34a', fontWeight: 700 }}>
                            {dif.real.s2 || '-'}
                          </span>
                          {dif.diferencias.s2 < 0 && (
                            <span style={{ fontSize: 9, color: '#d97706', marginLeft: 4 }}>{dif.diferencias.s2}m</span>
                          )}
                        </td>
                        <td style={{ padding: 8, textAlign: 'center', borderBottom: '1px solid #f3f4f6', fontWeight: 700, color: '#dc2626' }}>
                          {dif.minutosTarde > 0 ? `${dif.minutosTarde}m` : '-'}
                        </td>
                        <td style={{ padding: 8, textAlign: 'center', borderBottom: '1px solid #f3f4f6', fontWeight: 700, color: '#d97706' }}>
                          {dif.minutosSalidaTemprana > 0 ? `${dif.minutosSalidaTemprana}m` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Botón actualizar */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button
                  onClick={() => setModalConfirmarActualizacion(true)}
                  disabled={!diferenciasHorarios.some(d => d.seleccionado)}
                  style={{
                    background: diferenciasHorarios.some(d => d.seleccionado)
                      ? 'linear-gradient(135deg, #10b981 0%, #34d399 100%)'
                      : '#e5e7eb',
                    color: diferenciasHorarios.some(d => d.seleccionado) ? 'white' : '#9ca3af',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: 10,
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: diferenciasHorarios.some(d => d.seleccionado) ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    boxShadow: diferenciasHorarios.some(d => d.seleccionado) 
                      ? '0 4px 12px rgba(16, 185, 129, 0.3)' 
                      : 'none'
                  }}
                >
                  💾 Actualizar Horario Nómina ({diferenciasHorarios.filter(d => d.seleccionado).length} seleccionados)
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Modal de confirmación de actualización */}
      {modalConfirmarActualizacion && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 16,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            padding: 24,
            width: 500,
            maxWidth: '95vw'
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>
              🔄 Confirmar Actualización de Horarios
            </h3>
            
            <div style={{ background: '#faf5ff', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#374151' }}>
                Vas a actualizar <strong>{diferenciasHorarios.filter(d => d.seleccionado).length}</strong> registros 
                de <strong>{new Set(diferenciasHorarios.filter(d => d.seleccionado).map(d => d.empleadoId)).size}</strong> empleados
                en el <strong>Horario Nómina</strong>.
              </p>
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#7c3aed' }}>
                Se actualizarán los siguientes campos:
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {seleccionActualizacion.actualizarE1 && (
                  <span style={{ padding: '4px 10px', background: '#dcfce7', color: '#16a34a', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                    ✓ Entrada 1
                  </span>
                )}
                {seleccionActualizacion.actualizarS1 && (
                  <span style={{ padding: '4px 10px', background: '#fee2e2', color: '#dc2626', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                    ✓ Salida 1
                  </span>
                )}
                {seleccionActualizacion.actualizarE2 && (
                  <span style={{ padding: '4px 10px', background: '#dbeafe', color: '#2563eb', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                    ✓ Entrada 2
                  </span>
                )}
                {seleccionActualizacion.actualizarS2 && (
                  <span style={{ padding: '4px 10px', background: '#fae8ff', color: '#7c3aed', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                    ✓ Salida 2
                  </span>
                )}
                {seleccionActualizacion.actualizarSede && (
                  <span style={{ padding: '4px 10px', background: '#e0f2fe', color: '#0891b2', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                    ✓ Sede
                  </span>
                )}
              </div>
            </div>
            
            <div style={{ 
              background: '#fffbeb', 
              borderRadius: 10, 
              padding: 12, 
              marginBottom: 16,
              border: '1px solid #fcd34d'
            }}>
              <p style={{ margin: 0, fontSize: 12, color: '#92400e' }}>
                ⚠️ Esta acción actualizará los horarios de nómina con los datos de las marcaciones reales seleccionadas.
              </p>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                onClick={() => setModalConfirmarActualizacion(false)}
                style={{
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  padding: '10px 20px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  // Aplicar actualizaciones al horariosNomina
                  const seleccionados = diferenciasHorarios.filter(d => d.seleccionado);
                  const nuevoHorarioNomina = { ...horariosNomina };
                  
                  seleccionados.forEach(dif => {
                    if (!nuevoHorarioNomina[dif.empleadoId]) {
                      // Inicializar con el horario programado si no existe
                      nuevoHorarioNomina[dif.empleadoId] = celdas[dif.empleadoId] 
                        ? JSON.parse(JSON.stringify(celdas[dif.empleadoId])) 
                        : {};
                    }
                    if (!nuevoHorarioNomina[dif.empleadoId][dif.diaIdx]) {
                      nuevoHorarioNomina[dif.empleadoId][dif.diaIdx] = { ...dif.programado };
                    }
                    
                    const celda = nuevoHorarioNomina[dif.empleadoId][dif.diaIdx];
                    
                    if (seleccionActualizacion.actualizarE1 && dif.real.e1) {
                      celda.e1 = dif.real.e1;
                    }
                    if (seleccionActualizacion.actualizarS1 && dif.real.s1) {
                      celda.s1 = dif.real.s1;
                    }
                    if (seleccionActualizacion.actualizarE2 && dif.real.e2) {
                      celda.e2 = dif.real.e2;
                    }
                    if (seleccionActualizacion.actualizarS2 && dif.real.s2) {
                      celda.s2 = dif.real.s2;
                    }
                    if (seleccionActualizacion.actualizarSede && dif.real.sede1) {
                      celda.sede1 = dif.real.sede1;
                      celda.sede = dif.real.sede1;
                    }
                    if (seleccionActualizacion.actualizarSede && dif.real.sede2) {
                      celda.sede2 = dif.real.sede2;
                    }
                  });
                  
                  setHorariosNomina(nuevoHorarioNomina);
                  setModalConfirmarActualizacion(false);
                  
                  // Limpiar selección
                  setDiferenciasHorarios(prev => prev.map(d => ({ ...d, seleccionado: false })));
                  
                  alert(`✅ Se actualizaron ${seleccionados.length} registros en el Horario Nómina`);
                }}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
              >
                ✅ Confirmar Actualización
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vista: Vista Gráfica del Local */}
      {vistaHorarios === "vista_grafica" && (
        <div style={{
          background: 'white',
          borderRadius: 16,
          padding: 24,
          border: '2px solid #06b6d4',
          boxShadow: '0 8px 24px rgba(6, 182, 212, 0.15)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>
                🏪 Vista Gráfica - Distribución del Personal
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
                {modoVistaGrafica === "ninguno" 
                  ? "Seleccione una opción para continuar"
                  : `Semana ${semanaInicioVistaGrafica} → ${semanaFinVistaGrafica}`}
              </p>
            </div>
            {/* Botones Consultar Personal y Crear Horario */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                onClick={() => setModoVistaGrafica("consultar")}
                style={{
                  padding: '12px 24px',
                  borderRadius: 10,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  background: modoVistaGrafica === "consultar" 
                    ? 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)' 
                    : 'linear-gradient(135deg, #e2e8f0 0%, #f1f5f9 100%)',
                  color: modoVistaGrafica === "consultar" ? 'white' : '#475569',
                  boxShadow: modoVistaGrafica === "consultar" 
                    ? '0 4px 15px rgba(8, 145, 178, 0.4)' 
                    : '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <span style={{ fontSize: 16 }}>👥</span> Consultar Personal
              </button>
              <button
                onClick={() => {
                  setModoVistaGrafica("crear");
                  setDiaCreacionSeleccionado(0);
                  // 🔹 Cargar horarios existentes para edición en lugar de limpiar
                  const horariosExistentes = cargarHorariosExistentesParaEdicion();
                  setHorarioCreacion(horariosExistentes);
                }}
                style={{
                  padding: '12px 24px',
                  borderRadius: 10,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  background: modoVistaGrafica === "crear" 
                    ? 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' 
                    : 'linear-gradient(135deg, #e2e8f0 0%, #f1f5f9 100%)',
                  color: modoVistaGrafica === "crear" ? 'white' : '#475569',
                  boxShadow: modoVistaGrafica === "crear" 
                    ? '0 4px 15px rgba(16, 185, 129, 0.4)' 
                    : '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <span style={{ fontSize: 16 }}>📝</span> {Object.keys(celdasVistaGrafica).length > 0 ? 'Modificar Horario' : 'Crear Horario'}
              </button>
            </div>
          </div>

          {/* PANTALLA INICIAL - Sin selección */}
          {modoVistaGrafica === "ninguno" && (
            <div style={{
              textAlign: 'center',
              padding: 60,
              background: 'linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%)',
              borderRadius: 16,
              border: '2px dashed #14b8a6'
            }}>
              <div style={{ fontSize: 60, marginBottom: 20 }}>🏪</div>
              <h3 style={{ margin: '0 0 12px', fontSize: 22, fontWeight: 700, color: '#0f766e' }}>
                Vista Gráfica de Horarios
              </h3>
              <p style={{ margin: 0, fontSize: 14, color: '#64748b', maxWidth: 400, marginInline: 'auto' }}>
                Seleccione <strong>"Consultar Personal"</strong> para ver la distribución actual del personal,
                o <strong>"Crear Horario"</strong> para programar nuevos horarios.
              </p>
            </div>
          )}

          {/* Selector de Semana - Común para ambos modos */}
          {modoVistaGrafica !== "ninguno" && (
            <div style={{ 
              display: 'flex', 
              gap: 16, 
              marginBottom: 20, 
              padding: 16, 
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', 
              borderRadius: 12,
              border: '1px solid #7dd3fc',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, color: '#0369a1', fontSize: 13 }}>📆 Semana:</span>
                <button
                  onClick={() => {
                    const prev = new Date(fechaBaseVistaGrafica + "T12:00:00");
                    prev.setDate(prev.getDate() - 7);
                    setFechaBaseVistaGrafica(mondayOf(toISO(prev)));
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: '1px solid #0ea5e9',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#0369a1'
                  }}
                >
                  ◀ Anterior
                </button>
                <input
                  type="date"
                  value={fechaBaseVistaGrafica}
                  onChange={(e) => {
                    if (e.target.value) {
                      setFechaBaseVistaGrafica(mondayOf(e.target.value));
                    }
                  }}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '2px solid #0ea5e9',
                    background: 'white',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#0369a1',
                    cursor: 'pointer'
                  }}
                />
                <button
                  onClick={() => {
                    const next = new Date(fechaBaseVistaGrafica + "T12:00:00");
                    next.setDate(next.getDate() + 7);
                    setFechaBaseVistaGrafica(mondayOf(toISO(next)));
                  }}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: '1px solid #0ea5e9',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#0369a1'
                  }}
                >
                  Siguiente ▶
                </button>
              </div>
              
              {/* Indicador de estado del horario */}
              {horarioExistenteVistaGrafica && (
                <div style={{
                  padding: '6px 12px',
                  borderRadius: 20,
                  background: horarioExistenteVistaGrafica.estado === 'definitivo' ? '#fef3c7' : '#d1fae5',
                  border: horarioExistenteVistaGrafica.estado === 'definitivo' ? '1px solid #f59e0b' : '1px solid #10b981',
                  fontSize: 11,
                  fontWeight: 600,
                  color: horarioExistenteVistaGrafica.estado === 'definitivo' ? '#92400e' : '#065f46'
                }}>
                  {horarioExistenteVistaGrafica.estado === 'definitivo' ? '🔒 Horario Cerrado' : '📝 Horario Guardado'}
                </div>
              )}
              
              {/* 🔹 Botón MODIFICAR - Solo en modo consultar cuando hay horarios guardados */}
              {modoVistaGrafica === "consultar" && Object.keys(celdasVistaGrafica).length > 0 && (
                <button
                  onClick={() => {
                    const horariosExistentes = cargarHorariosExistentesParaEdicion();
                    setHorarioCreacion(horariosExistentes);
                    setModoVistaGrafica("crear");
                    setDiaCreacionSeleccionado(0);
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                    color: 'white',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
                  }}
                >
                  <span>✏️</span> Modificar Horario
                </button>
              )}
            </div>
          )}

          {/* Filtros: Día y Turno - Solo para modo CONSULTAR */}
          {modoVistaGrafica === "consultar" && (
          <div style={{ 
            display: 'flex', 
            gap: 16, 
            marginBottom: 24, 
            padding: 16, 
            background: 'linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%)', 
            borderRadius: 12,
            border: '1px solid #99f6e4',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, color: '#0f766e', fontSize: 13 }}>📅 Día:</span>
              <select
                value={vistaGraficaDia}
                onChange={(e) => setVistaGraficaDia(parseInt(e.target.value))}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '2px solid #14b8a6',
                  background: 'white',
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#0d9488',
                  cursor: 'pointer'
                }}
              >
                <option value={-1}>📊 Semana Completa</option>
                {DIAS.map((dia, idx) => (
                  <option key={idx} value={idx}>{dia} ({fechasSemanaVistaGrafica[idx]})</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontWeight: 600, color: '#0f766e', fontSize: 13 }}>⏰ Turno:</span>
              <select
                value={vistaGraficaTurno}
                onChange={(e) => setVistaGraficaTurno(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '2px solid #14b8a6',
                  background: 'white',
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#0d9488',
                  cursor: 'pointer'
                }}
              >
                <option value="todos">🌐 Todos los turnos</option>
                <option value="mañana">🌅 Mañana (7:00 - 15:00)</option>
                <option value="tarde">🌆 Tarde (15:00 - 19:00)</option>
                <option value="noche">🌙 Noche (19:00 - 24:00)</option>
              </select>
            </div>

            {/* Leyenda de áreas */}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 16, height: 16, background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderRadius: 4, border: '1px solid #f59e0b' }}></div>
                <span style={{ fontSize: 11, color: '#78350f', fontWeight: 500 }}>COCINA</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 16, height: 16, background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', borderRadius: 4, border: '1px solid #3b82f6' }}></div>
                <span style={{ fontSize: 11, color: '#1e3a8a', fontWeight: 500 }}>CAJA/MESAS</span>
              </div>
            </div>
          </div>
          )}

          {/* MODO CONSULTAR PERSONAL */}
          {modoVistaGrafica === "consultar" && (
          <>
          {/* Grid de todas las sedes */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
            gap: 24 
          }}>
            {sedesUsadasVistaGrafica.map(sedeActual => {
              // Función para categorizar empleado por cargo
              const getAreaPorCargo = (cargo) => {
                if (!cargo) return 'CAJA_MESAS';
                const cargoLower = cargo.toLowerCase();
                if (cargoLower.includes('cocina') || cargoLower.includes('chef') || cargoLower.includes('cocinero')) {
                  return 'COCINA';
                }
                return 'CAJA_MESAS';
              };

              // Función mejorada para verificar si un horario cruza con el filtro de turno
              // Ahora incluye empleados que vienen de otros turnos
              // Las alertas solo se muestran para horarios PARTIDO (con segundo turno)
              const turnoEnFiltroMejorado = (e1, s1, e2, s2) => {
                if (vistaGraficaTurno === 'todos') return { incluir: true, alerta: null };
                if (!e1 && !e2) return { incluir: false, alerta: null };
                
                const parseHora = (h) => h ? parseInt(h.split(':')[0]) + parseInt(h.split(':')[1] || 0) / 60 : null;
                const entrada1 = parseHora(e1);
                const salida1 = parseHora(s1);
                const entrada2 = parseHora(e2);
                const salida2 = parseHora(s2);
                
                // Determinar si es horario PARTIDO (tiene segundo turno)
                const esHorarioPartido = entrada2 !== null && salida2 !== null;
                
                const rangos = {
                  'mañana': { inicio: 6, fin: 15 },
                  'tarde': { inicio: 14, fin: 19 },
                  'noche': { inicio: 18, fin: 24 }
                };
                
                const { inicio, fin } = rangos[vistaGraficaTurno];
                let incluir = false;
                let alerta = null;
                
                // Verificar primer turno
                if (entrada1 !== null && salida1 !== null) {
                  // Empleado trabaja durante este turno (su rango se cruza con el turno)
                  const sal1Ajustada = salida1 < entrada1 ? salida1 + 24 : salida1;
                  if (sal1Ajustada > inicio && entrada1 < fin) {
                    incluir = true;
                    // Solo mostrar alertas para horarios PARTIDO
                    if (esHorarioPartido) {
                      // Verificar si viene de otro turno (entrada antes del rango)
                      if (entrada1 < inicio) {
                        alerta = `⚠️ Viene de turno anterior - Sale a las ${formatearHoraAMPM(s1)}`;
                      }
                      // Verificar si se extiende a otro turno (salida después del rango)
                      if (sal1Ajustada > fin) {
                        alerta = `⚠️ Se extiende al siguiente turno - Hasta las ${formatearHoraAMPM(s1)}`;
                      }
                    }
                  }
                }
                
                // Verificar segundo turno (solo aplica a horarios PARTIDO)
                if (esHorarioPartido) {
                  const sal2Ajustada = salida2 < entrada2 ? salida2 + 24 : salida2;
                  if (sal2Ajustada > inicio && entrada2 < fin) {
                    incluir = true;
                    if (entrada2 < inicio) {
                      alerta = `⚠️ Turno 2 viene de anterior - Sale a las ${formatearHoraAMPM(s2)}`;
                    }
                  }
                }
                
                return { incluir, alerta };
              };

              // Obtener empleados para esta sede con información de alerta
              // SOLO usa sede1 y sede2 - IGNORA el campo legacy 'sede' para evitar datos antiguos
              const empleadosEnSedeConAlerta = activos.map(emp => {
                const celdasEmp = celdasVistaGrafica[emp.id];
                if (!celdasEmp) return null;

                let alerta = null;
                let incluir = false;

                // Si es semana completa (-1), revisar todos los días
                if (vistaGraficaDia === -1) {
                  for (let idx = 0; idx < 7; idx++) {
                    const cel = celdasEmp[idx];
                    if (!cel) continue;
                    
                    // SOLO usar sede1 - y sede2 SOLO si tiene segundo turno real
                    const sedeCelda1 = (cel.sede1 || '').toUpperCase().trim();
                    const tieneSegundoTurno = cel.e2 && cel.s2;
                    const sedeCelda2 = tieneSegundoTurno ? (cel.sede2 || '').toUpperCase().trim() : '';
                    const sedeActualUpper = sedeActual.toUpperCase().trim();
                    
                    const estaEnSede = sedeCelda1 === sedeActualUpper || 
                                      (sedeCelda2 && sedeCelda2 === sedeActualUpper);
                    
                    if (estaEnSede) {
                      const resultado = turnoEnFiltroMejorado(cel.e1, cel.s1, cel.e2, cel.s2);
                      if (resultado.incluir) {
                        incluir = true;
                        if (resultado.alerta) alerta = resultado.alerta;
                      }
                    }
                  }
                } else {
                  // Día específico
                  const cel = celdasEmp[vistaGraficaDia];
                  if (!cel) return null;
                  
                  // SOLO usar sede1 - y sede2 SOLO si tiene segundo turno real (e2/s2)
                  const sedeCelda1 = (cel.sede1 || '').toUpperCase().trim();
                  // Solo considerar sede2 si realmente tiene segundo turno
                  const tieneSegundoTurno = cel.e2 && cel.s2;
                  const sedeCelda2 = tieneSegundoTurno ? (cel.sede2 || '').toUpperCase().trim() : '';
                  const sedeActualUpper = sedeActual.toUpperCase().trim();
                  
                  // Convertir explícitamente a booleano para evitar valores vacíos
                  const estaEnSede = Boolean(sedeCelda1 === sedeActualUpper) || 
                                    Boolean(sedeCelda2 && sedeCelda2 === sedeActualUpper);
                  
                  if (!estaEnSede) return null;
                  
                  const resultado = turnoEnFiltroMejorado(cel.e1, cel.s1, cel.e2, cel.s2);
                  incluir = resultado.incluir;
                  alerta = resultado.alerta;
                }

                return incluir ? { ...emp, alertaHorario: alerta } : null;
              }).filter(Boolean);

              // Separar por área
              const empleadosCocina = empleadosEnSedeConAlerta.filter(emp => getAreaPorCargo(emp.cargo) === 'COCINA');
              const empleadosCajaMesas = empleadosEnSedeConAlerta.filter(emp => getAreaPorCargo(emp.cargo) === 'CAJA_MESAS');

              // Componente para mostrar un empleado con horario en AM/PM
              const EmpleadoAvatar = ({ emp }) => {
                const iniciales = emp.nombre.split(' ').slice(0, 2).map(n => n[0]).join('');
                const colorHash = emp.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 360;
                
                // Obtener horario del día seleccionado en formato AM/PM
                let horarioTexto = '';
                let tieneAlerta = emp.alertaHorario;
                if (vistaGraficaDia !== -1 && celdasVistaGrafica[emp.id]?.[vistaGraficaDia]) {
                  const cel = celdasVistaGrafica[emp.id][vistaGraficaDia];
                  if (cel.e1 && cel.s1) {
                    horarioTexto = `${formatearHoraAMPM(cel.e1)} - ${formatearHoraAMPM(cel.s1)}`;
                    if (cel.e2 && cel.s2) {
                      horarioTexto += `\n${formatearHoraAMPM(cel.e2)} - ${formatearHoraAMPM(cel.s2)}`;
                    }
                  }
                }

                return (
                  <div 
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: 10,
                      background: tieneAlerta ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : 'white',
                      borderRadius: 12,
                      boxShadow: tieneAlerta ? '0 2px 12px rgba(245, 158, 11, 0.4)' : '0 2px 8px rgba(0,0,0,0.1)',
                      border: tieneAlerta ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                      minWidth: 100,
                      transition: 'transform 0.2s ease',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                    title={`${emp.nombre}\n${emp.cargo || 'Sin cargo'}\n${horarioTexto}${tieneAlerta ? '\n' + tieneAlerta : ''}`}
                  >
                    {tieneAlerta && (
                      <div style={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        width: 20,
                        height: 20,
                        background: '#f59e0b',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        color: 'white',
                        fontWeight: 700,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}>
                        ⚠️
                      </div>
                    )}
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, hsl(${colorHash}, 70%, 60%) 0%, hsl(${colorHash}, 70%, 40%) 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: 14,
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {iniciales}
                    </div>
                    <span style={{ 
                      fontSize: 11, 
                      fontWeight: 600, 
                      color: '#374151', 
                      marginTop: 6,
                      textAlign: 'center',
                      maxWidth: 90,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {emp.nombre.split(' ')[0]}
                    </span>
                    {horarioTexto && (
                      <div style={{ 
                        fontSize: 11, 
                        fontWeight: 600,
                        color: tieneAlerta ? '#92400e' : '#0891b2', 
                        marginTop: 4,
                        textAlign: 'center',
                        lineHeight: 1.3,
                        whiteSpace: 'pre-line'
                      }}>
                        {horarioTexto}
                      </div>
                    )}
                    {tieneAlerta && (
                      <div style={{
                        marginTop: 4,
                        padding: '3px 6px',
                        background: '#fef3c7',
                        borderRadius: 4,
                        fontSize: 9,
                        color: '#92400e',
                        fontWeight: 600,
                        textAlign: 'center',
                        maxWidth: 90,
                        wordBreak: 'break-word'
                      }}>
                        {tieneAlerta.replace('⚠️ ', '')}
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <div 
                  key={sedeActual}
                  style={{
                    background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                    borderRadius: 16,
                    border: '2px solid #e2e8f0',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }}
                >
                  {/* Header de la sede */}
                  <div style={{
                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                    padding: '12px 16px',
                    color: 'white'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 700, fontSize: 16 }}>📍 {sedeActual}</span>
                      <span style={{ 
                        background: 'rgba(255,255,255,0.2)', 
                        padding: '4px 10px', 
                        borderRadius: 20, 
                        fontSize: 11, 
                        fontWeight: 600 
                      }}>
                        {empleadosEnSedeConAlerta.length} empleados
                      </span>
                    </div>
                  </div>

                  {/* Plano del local */}
                  <div style={{ padding: 16 }}>
                    {/* Área COCINA */}
                    <div style={{
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      borderRadius: 12,
                      padding: 16,
                      marginBottom: 12,
                      border: '2px dashed #f59e0b',
                      minHeight: 100
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 8, 
                        marginBottom: 12,
                        paddingBottom: 8,
                        borderBottom: '1px solid rgba(245, 158, 11, 0.3)'
                      }}>
                        <span style={{ fontSize: 18 }}>🍳</span>
                        <span style={{ fontWeight: 700, color: '#92400e', fontSize: 14 }}>COCINA</span>
                        <span style={{ 
                          marginLeft: 'auto', 
                          background: '#f59e0b', 
                          color: 'white', 
                          padding: '2px 8px', 
                          borderRadius: 10, 
                          fontSize: 10, 
                          fontWeight: 600 
                        }}>
                          {empleadosCocina.length}
                        </span>
                      </div>
                      
                      {empleadosCocina.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {empleadosCocina.map(emp => (
                            <EmpleadoAvatar key={emp.id} emp={emp} />
                          ))}
                        </div>
                      ) : (
                        <div style={{ 
                          textAlign: 'center', 
                          color: '#92400e', 
                          fontSize: 12, 
                          fontStyle: 'italic',
                          opacity: 0.7,
                          padding: 16
                        }}>
                          Sin personal asignado
                        </div>
                      )}
                    </div>

                    {/* Área CAJA/MESAS */}
                    <div style={{
                      background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                      borderRadius: 12,
                      padding: 16,
                      border: '2px dashed #3b82f6',
                      minHeight: 100
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 8, 
                        marginBottom: 12,
                        paddingBottom: 8,
                        borderBottom: '1px solid rgba(59, 130, 246, 0.3)'
                      }}>
                        <span style={{ fontSize: 18 }}>🍽️</span>
                        <span style={{ fontWeight: 700, color: '#1e40af', fontSize: 14 }}>CAJA / MESAS</span>
                        <span style={{ 
                          marginLeft: 'auto', 
                          background: '#3b82f6', 
                          color: 'white', 
                          padding: '2px 8px', 
                          borderRadius: 10, 
                          fontSize: 10, 
                          fontWeight: 600 
                        }}>
                          {empleadosCajaMesas.length}
                        </span>
                      </div>
                      
                      {empleadosCajaMesas.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {empleadosCajaMesas.map(emp => (
                            <EmpleadoAvatar key={emp.id} emp={emp} />
                          ))}
                        </div>
                      ) : (
                        <div style={{ 
                          textAlign: 'center', 
                          color: '#1e40af', 
                          fontSize: 12, 
                          fontStyle: 'italic',
                          opacity: 0.7,
                          padding: 16
                        }}>
                          Sin personal asignado
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sección de Empleados en Descanso */}
          {(() => {
            // Calcular empleados en descanso
            const empleadosEnDescanso = [];
            
            activos.forEach(emp => {
              const celdasEmp = celdasVistaGrafica[emp.id];
              if (!celdasEmp) return;
              
              if (vistaGraficaDia === -1) {
                // Semana completa: buscar qué días descansa cada empleado
                const diasDescanso = [];
                for (let idx = 0; idx < 7; idx++) {
                  const cel = celdasEmp[idx];
                  if (cel && (cel.descanso || cel.esDescanso || cel.tipo === 'DESCANSO' || 
                      (!cel.e1 && !cel.s1 && !cel.sede && !cel.sede1))) {
                    diasDescanso.push(DIAS[idx]);
                  }
                }
                if (diasDescanso.length > 0) {
                  empleadosEnDescanso.push({
                    ...emp,
                    diasDescanso,
                    diaTexto: diasDescanso.join(', ')
                  });
                }
              } else {
                // Día específico: verificar si descansa ese día
                const cel = celdasEmp[vistaGraficaDia];
                if (cel && (cel.descanso || cel.esDescanso || cel.tipo === 'DESCANSO' || 
                    (!cel.e1 && !cel.s1 && !cel.sede && !cel.sede1))) {
                  empleadosEnDescanso.push({
                    ...emp,
                    diasDescanso: [DIAS[vistaGraficaDia]],
                    diaTexto: DIAS[vistaGraficaDia]
                  });
                }
              }
            });
            
            if (empleadosEnDescanso.length === 0) return null;
            
            return (
              <div style={{
                marginTop: 24,
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                borderRadius: 16,
                border: '3px solid #f59e0b',
                overflow: 'hidden',
                boxShadow: '0 4px 16px rgba(245, 158, 11, 0.2)'
              }}>
                {/* Header */}
                <div style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                  padding: '14px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 22 }}>😴</span>
                    <span style={{ fontWeight: 700, fontSize: 16, color: 'white' }}>
                      Empleados en Descanso
                    </span>
                  </div>
                  <span style={{
                    background: 'rgba(255,255,255,0.3)',
                    padding: '5px 14px',
                    borderRadius: 20,
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'white'
                  }}>
                    {empleadosEnDescanso.length} {empleadosEnDescanso.length === 1 ? 'empleado' : 'empleados'}
                  </span>
                </div>
                
                {/* Contenido */}
                <div style={{ padding: 16 }}>
                  {vistaGraficaDia === -1 ? (
                    /* Vista Semana Completa - Tabla con día de descanso */
                    <div style={{
                      background: 'white',
                      borderRadius: 10,
                      overflow: 'hidden',
                      border: '1px solid #fde68a'
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#fffbeb' }}>
                            <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 12, color: '#92400e', borderBottom: '2px solid #fde68a' }}>
                              Empleado
                            </th>
                            <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 12, color: '#92400e', borderBottom: '2px solid #fde68a' }}>
                              Cargo
                            </th>
                            <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 12, color: '#92400e', borderBottom: '2px solid #fde68a' }}>
                              Día(s) de Descanso
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {empleadosEnDescanso.map((emp, idx) => (
                            <tr key={emp.id} style={{ background: idx % 2 === 0 ? 'white' : '#fffbeb' }}>
                              <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600, color: '#1e293b', borderBottom: '1px solid #fef3c7' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <div style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    background: `linear-gradient(135deg, hsl(${emp.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360}, 70%, 60%) 0%, hsl(${emp.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360}, 70%, 40%) 100%)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: 11,
                                    fontWeight: 700
                                  }}>
                                    {emp.nombre.split(' ').slice(0, 2).map(n => n[0]).join('')}
                                  </div>
                                  {emp.nombre}
                                </div>
                              </td>
                              <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748b', borderBottom: '1px solid #fef3c7' }}>
                                {emp.cargo || 'Sin cargo'}
                              </td>
                              <td style={{ padding: '10px 14px', borderBottom: '1px solid #fef3c7' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                  {emp.diasDescanso.map((dia, i) => (
                                    <span key={i} style={{
                                      background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                                      color: 'white',
                                      padding: '3px 10px',
                                      borderRadius: 12,
                                      fontSize: 11,
                                      fontWeight: 600
                                    }}>
                                      {dia}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    /* Vista Día Específico - Mostrar avatares */
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {empleadosEnDescanso.map(emp => {
                        const iniciales = emp.nombre.split(' ').slice(0, 2).map(n => n[0]).join('');
                        const colorHash = emp.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 360;
                        
                        return (
                          <div 
                            key={emp.id}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              padding: 12,
                              background: 'white',
                              borderRadius: 12,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                              border: '2px solid #fde68a',
                              minWidth: 110
                            }}
                            title={`${emp.nombre}\n${emp.cargo || 'Sin cargo'}\nDescanso: ${emp.diaTexto}`}
                          >
                            <div style={{
                              width: 44,
                              height: 44,
                              borderRadius: '50%',
                              background: `linear-gradient(135deg, hsl(${colorHash}, 70%, 60%) 0%, hsl(${colorHash}, 70%, 40%) 100%)`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: 14,
                              textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                              marginBottom: 6
                            }}>
                              {iniciales}
                            </div>
                            <span style={{ 
                              fontSize: 11, 
                              fontWeight: 600, 
                              color: '#374151', 
                              textAlign: 'center',
                              maxWidth: 90,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {emp.nombre.split(' ')[0]}
                            </span>
                            <span style={{
                              marginTop: 4,
                              background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                              color: 'white',
                              padding: '3px 8px',
                              borderRadius: 10,
                              fontSize: 10,
                              fontWeight: 700
                            }}>
                              😴 Descanso
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Mensaje si no hay sedes */}
          {sedesUsadasVistaGrafica.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: 40,
              color: '#64748b'
            }}>
              <span style={{ fontSize: 40, display: 'block', marginBottom: 12 }}>🏢</span>
              <p style={{ fontSize: 14, fontWeight: 500 }}>No hay horarios guardados para esta semana</p>
              <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>Use "Crear Horario" para programar turnos</p>
            </div>
          )}
          </>
          )}

          {/* MODO CREAR HORARIO */}
          {modoVistaGrafica === "crear" && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Advertencia si hay días cerrados */}
            {horarioExistenteVistaGrafica?.diasCerrados?.length > 0 && (
              <div style={{
                padding: 16,
                background: '#fef2f2',
                borderRadius: 10,
                border: '2px solid #fecaca'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 18 }}>🔒</span>
                  <strong style={{ color: '#dc2626' }}>Días Cerrados</strong>
                </div>
                <p style={{ margin: 0, fontSize: 12, color: '#7f1d1d' }}>
                  Los siguientes días están cerrados y no se pueden modificar: {horarioExistenteVistaGrafica.diasCerrados.join(', ')}
                </p>
              </div>
            )}

            {/* Barra de herramientas de creación */}
            <div style={{
              display: 'flex',
              gap: 12,
              padding: 16,
              background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
              borderRadius: 12,
              border: '2px solid #34d399',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              {/* Selector de día */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, color: '#065f46', fontSize: 13 }}>📅 Día:</span>
                <select
                  value={diaCreacionSeleccionado}
                  onChange={(e) => setDiaCreacionSeleccionado(parseInt(e.target.value))}
                  style={{
                    padding: '10px 14px',
                    borderRadius: 8,
                    border: '2px solid #10b981',
                    background: isDiaCerradoVistaGrafica(fechasSemanaVistaGrafica[diaCreacionSeleccionado]) ? '#fee2e2' : 'white',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#047857',
                    cursor: 'pointer'
                  }}
                >
                  {DIAS.map((dia, idx) => {
                    const fechaISO = fechasSemanaVistaGrafica[idx];
                    const cerrado = isDiaCerradoVistaGrafica(fechaISO);
                    return (
                      <option key={idx} value={idx}>{cerrado ? '🔒 ' : ''}{dia} ({fechaISO})</option>
                    );
                  })}
                </select>
              </div>

              {/* Botón Sugerencias de Horario - Con menú desplegable */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => {
                    setMostrarMenuSugerencias(!mostrarMenuSugerencias);
                  }}
                  style={{
                    padding: '10px 16px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                    color: 'white',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    boxShadow: '0 3px 10px rgba(124, 58, 237, 0.3)'
                  }}
                >
                  🤖 Sugerencia de horario ▼
                </button>
                
                {mostrarMenuSugerencias && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: 4,
                    background: 'white',
                    borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                    border: '1px solid #e2e8f0',
                    zIndex: 100,
                    minWidth: 260,
                    overflow: 'hidden'
                  }}>
                    <div style={{ padding: '8px 12px', background: '#f3f4f6', fontWeight: 600, fontSize: 11, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                      📅 Solo {DIAS[diaCreacionSeleccionado]}
                    </div>
                    <button
                      onClick={async () => {
                        await generarSugerenciasHorarios(diaCreacionSeleccionado);
                        aplicarSugerenciasHorarios(diaCreacionSeleccionado);
                        setMostrarMenuSugerencias(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: 'white',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 500,
                        color: '#374151',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      📥 Generar y Aplicar para {DIAS[diaCreacionSeleccionado]}
                    </button>
                    <div style={{ padding: '8px 12px', background: '#f3f4f6', fontWeight: 600, fontSize: 11, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                      📆 Semana Completa
                    </div>
                    <button
                      onClick={async () => {
                        await generarSugerenciasHorarios();
                        aplicarSugerenciasHorarios();
                        setMostrarMenuSugerencias(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: 'white',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 500,
                        color: '#374151'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      📥 Generar y Aplicar Semana Completa
                    </button>
                  </div>
                )}
              </div>

              {/* Separador */}
              <div style={{ width: 1, height: 30, background: '#34d399', margin: '0 8px' }} />

              {/* Botón Copiar de otra semana */}
              <button
                onClick={() => setModalCopiarVistaGraficaOpen(true)}
                disabled={horariosCopiablesVistaGrafica.length === 0}
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: 'none',
                  background: horariosCopiablesVistaGrafica.length === 0 
                    ? 'rgba(200,200,200,0.5)' 
                    : 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                  color: horariosCopiablesVistaGrafica.length === 0 ? '#999' : 'white',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: horariosCopiablesVistaGrafica.length === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  opacity: horariosCopiablesVistaGrafica.length === 0 ? 0.6 : 1,
                  boxShadow: horariosCopiablesVistaGrafica.length > 0 ? '0 3px 10px rgba(59, 130, 246, 0.3)' : 'none'
                }}
              >
                📋 Copiar de otra semana {horariosCopiablesVistaGrafica.length > 0 && `(${horariosCopiablesVistaGrafica.length})`}
              </button>

              {/* Separador */}
              <div style={{ width: 1, height: 30, background: '#34d399', margin: '0 8px' }} />

              {/* Botón Deshacer Día */}
              <button
                onClick={() => deshacerHorarios(diaCreacionSeleccionado)}
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: '2px solid #f59e0b',
                  background: 'white',
                  color: '#d97706',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                ↩️ Deshacer Día
              </button>

              {/* Botón Deshacer Semana */}
              <button
                onClick={() => deshacerHorarios()}
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: '2px solid #ef4444',
                  background: 'white',
                  color: '#dc2626',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                🗑️ Deshacer Semana
              </button>

              {/* Separador */}
              <div style={{ width: 1, height: 30, background: '#34d399', margin: '0 8px' }} />

              {/* Botón Ver Semana Completa */}
              <button
                onClick={() => setPanelResumenSemanal(!panelResumenSemanal)}
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: panelResumenSemanal ? '2px solid #0ea5e9' : '2px solid #0891b2',
                  background: panelResumenSemanal 
                    ? 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)' 
                    : 'white',
                  color: panelResumenSemanal ? 'white' : '#0891b2',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: panelResumenSemanal ? '0 3px 10px rgba(14, 165, 233, 0.3)' : 'none'
                }}
              >
                📊 {panelResumenSemanal ? 'Cerrar Tabla' : 'Ver Semana'}
              </button>

              {/* Botones Guardar y Modificar */}
              <button
                onClick={guardarHorariosCreados}
                disabled={Object.keys(horarioCreacion).length === 0}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: Object.keys(horarioCreacion).length === 0 
                    ? '#d1d5db' 
                    : 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                  color: 'white',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: Object.keys(horarioCreacion).length === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  boxShadow: Object.keys(horarioCreacion).length === 0 
                    ? 'none' 
                    : '0 4px 15px rgba(16, 185, 129, 0.4)'
                }}
              >
                💾 Guardar Horario
              </button>
            </div>

            {/* Contenedor principal de creación de horarios */}
            <div style={{ display: 'flex', gap: 24 }}>
              {/* Panel izquierdo: Lista de empleados disponibles por área */}
              <div style={{
                width: 320,
                minWidth: 320,
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                borderRadius: 12,
                border: '2px solid #e2e8f0',
                padding: 16,
                maxHeight: 700,
                overflowY: 'auto'
              }}>
                <h4 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                  👥 Empleados Disponibles
                </h4>
                
                {/* Función para categorizar por área */}
                {(() => {
                  const getAreaPorCargo = (cargo) => {
                    if (!cargo) return 'CAJA_MESAS';
                    const cargoLower = cargo.toLowerCase();
                    if (cargoLower.includes('cocina') || cargoLower.includes('chef') || cargoLower.includes('cocinero')) {
                      return 'COCINA';
                    }
                    return 'CAJA_MESAS';
                  };
                  
                  const empleadosCocina = activos.filter(emp => getAreaPorCargo(emp.cargo) === 'COCINA');
                  const empleadosCajaMesas = activos.filter(emp => getAreaPorCargo(emp.cargo) === 'CAJA_MESAS');
                  
                  const renderEmpleado = (emp) => {
                    const horarioDelDia = horarioCreacion[emp.id]?.[diaCreacionSeleccionado];
                    const tieneHorarioAsignado = horarioDelDia && (horarioDelDia.e1 || horarioDelDia.esDescanso);
                    const fechaDelDia = fechasSemanaVistaGrafica[diaCreacionSeleccionado];
                    const bloqueado = fechaDelDia ? empleadoBloqueadoPorNovedad(emp.id, diaCreacionSeleccionado) : false;
                    const novedadesDelDia = fechaDelDia ? getNovedadesEmpleadoPorDia(emp.id, fechaDelDia) : [];
                    const tipoNovedad = novedadesDelDia.length > 0 ? novedadesDelDia[0].tipo : null;
                    
                    return (
                      <div
                        key={emp.id}
                        draggable={!bloqueado}
                        onDragStart={(e) => {
                          if (!bloqueado) {
                            setEmpleadoArrastrado(emp);
                            e.currentTarget.style.opacity = '0.5';
                          }
                        }}
                        onDragEnd={(e) => {
                          setEmpleadoArrastrado(null);
                          e.currentTarget.style.opacity = '1';
                        }}
                        onClick={() => {
                          if (bloqueado) {
                            alert(`⚠️ ${emp.nombre} está bloqueado por ${tipoNovedad || 'novedad'} y no puede recibir horario este día.`);
                            return;
                          }
                          setEmpleadoSeleccionadoCreacion(emp);
                          setModalAsignarHorario(true);
                        }}
                        style={{
                          padding: 10,
                          background: bloqueado
                            ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'
                            : tieneHorarioAsignado 
                              ? horarioDelDia.esDescanso 
                                ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' 
                                : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                              : 'white',
                          borderRadius: 10,
                          border: bloqueado 
                            ? '2px solid #ef4444'
                            : tieneHorarioAsignado ? '2px solid #10b981' : '1px solid #e2e8f0',
                          cursor: bloqueado ? 'not-allowed' : 'grab',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          transition: 'all 0.2s ease',
                          opacity: bloqueado ? 0.7 : 1
                        }}
                      >
                        <div style={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          background: bloqueado 
                            ? '#ef4444'
                            : `linear-gradient(135deg, hsl(${emp.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360}, 70%, 60%) 0%, hsl(${emp.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360}, 70%, 40%) 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: 12
                        }}>
                          {bloqueado ? '🚫' : emp.nombre.split(' ').slice(0, 2).map(n => n[0]).join('')}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontSize: 13, 
                            fontWeight: 600, 
                            color: bloqueado ? '#991b1b' : '#1e293b',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {emp.nombre.split(' ').slice(0, 2).join(' ')}
                          </div>
                          {bloqueado ? (
                            <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 500 }}>
                              🚫 {tipoNovedad || 'BLOQUEADO'}
                            </div>
                          ) : tieneHorarioAsignado && (
                            <div style={{ fontSize: 11, color: '#059669', fontWeight: 500 }}>
                              {horarioDelDia.esDescanso 
                                ? '💤 DESCANSO' 
                                : `${formatearHoraAMPM(horarioDelDia.e1)} - ${formatearHoraAMPM(horarioDelDia.s1)}`}
                            </div>
                          )}
                        </div>
                        {bloqueado ? (
                          <span style={{ fontSize: 14 }}>🚫</span>
                        ) : tieneHorarioAsignado && (
                          <span style={{ fontSize: 14 }}>{horarioDelDia.esDescanso ? '💤' : '✅'}</span>
                        )}
                      </div>
                    );
                  };
                  
                  return (
                    <>
                      {/* Área COCINA */}
                      <div style={{ marginBottom: 20 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 10,
                          padding: '8px 12px',
                          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                          borderRadius: 8,
                          border: '1px solid #f59e0b'
                        }}>
                          <span style={{ fontSize: 16 }}>🍳</span>
                          <span style={{ fontWeight: 700, color: '#78350f', fontSize: 14 }}>COCINA ({empleadosCocina.length})</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {empleadosCocina.map(emp => renderEmpleado(emp))}
                          {empleadosCocina.length === 0 && (
                            <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: 12 }}>
                              Sin empleados de cocina
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Área CAJA/MESAS */}
                      <div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 10,
                          padding: '8px 12px',
                          background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                          borderRadius: 8,
                          border: '1px solid #3b82f6'
                        }}>
                          <span style={{ fontSize: 16 }}>💼</span>
                          <span style={{ fontWeight: 700, color: '#1e3a8a', fontSize: 14 }}>CAJA/MESAS ({empleadosCajaMesas.length})</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {empleadosCajaMesas.map(emp => renderEmpleado(emp))}
                          {empleadosCajaMesas.length === 0 && (
                            <div style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', padding: 12 }}>
                              Sin empleados de caja/mesas
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Panel derecho: Sedes para asignar */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Zona de DESCANSO - Compacta arriba */}
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.style.borderColor = '#10b981';
                    e.currentTarget.style.background = 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)';
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.style.borderColor = '#f59e0b';
                    e.currentTarget.style.background = 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)';
                  }}
                  onDrop={(e) => {
                    e.currentTarget.style.borderColor = '#f59e0b';
                    e.currentTarget.style.background = 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)';
                    if (empleadoArrastrado) {
                      // Verificar si el empleado ya tiene descansos en la semana
                      const descansosExistentes = Object.entries(horarioCreacion[empleadoArrastrado.id] || {})
                        .filter(([dia, h]) => h?.esDescanso && dia !== String(diaCreacionSeleccionado));
                      
                      const asignarDescanso = () => {
                        setHorarioCreacion(prev => ({
                          ...prev,
                          [empleadoArrastrado.id]: {
                            ...(prev[empleadoArrastrado.id] || {}),
                            [diaCreacionSeleccionado]: {
                              esDescanso: true,
                              sede: 'DESCANSO'
                            }
                          }
                        }));
                        setEmpleadoArrastrado(null);
                      };
                      
                      if (descansosExistentes.length > 0) {
                        const diasDescanso = descansosExistentes.map(([dia]) => DIAS[parseInt(dia)]).join(', ');
                        if (window.confirm(`⚠️ Este empleado ya tiene ${descansosExistentes.length} descanso(s) asignado(s) esta semana (${diasDescanso}). ¿Desea agregar otro descanso?`)) {
                          asignarDescanso();
                        }
                      } else {
                        asignarDescanso();
                      }
                    }
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    borderRadius: 10,
                    padding: '10px 16px',
                    border: '2px dashed #f59e0b',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 20 }}>💤</span>
                    <span style={{ fontWeight: 700, color: '#92400e', fontSize: 14 }}>DESCANSO</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, flex: 1 }}>
                    {Object.entries(horarioCreacion)
                      .filter(([_, dias]) => dias[diaCreacionSeleccionado]?.esDescanso)
                      .map(([empId]) => {
                        const emp = empleados.find(e => e.id === empId);
                        if (!emp) return null;
                        return (
                          <div 
                            key={empId}
                            draggable
                            onDragStart={(e) => {
                              setEmpleadoArrastrado(emp);
                              e.currentTarget.style.opacity = '0.5';
                            }}
                            onDragEnd={(e) => {
                              setEmpleadoArrastrado(null);
                              e.currentTarget.style.opacity = '1';
                            }}
                            onClick={() => {
                              setEmpleadoSeleccionadoCreacion(emp);
                              setModalAsignarHorario(true);
                            }}
                            style={{
                              padding: '4px 10px',
                              background: 'white',
                              borderRadius: 16,
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#92400e',
                              border: '1px solid #f59e0b',
                              cursor: 'grab',
                              transition: 'transform 0.1s ease'
                            }}
                          >
                            {emp.nombre.split(' ')[0]}
                          </div>
                        );
                      })}
                    {Object.entries(horarioCreacion).filter(([_, dias]) => dias[diaCreacionSeleccionado]?.esDescanso).length === 0 && (
                      <span style={{ fontSize: 11, color: '#92400e', opacity: 0.7, fontStyle: 'italic' }}>Arrastra empleados aquí</span>
                    )}
                  </div>
                </div>

                {/* Grid de Sedes - Ocupa el resto del espacio */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
                  gap: 16,
                  flex: 1
                }}>

                  {/* Sedes disponibles */}
                  {sedesListaFiltradas.map(sede => {
                    // Función para categorizar empleado por cargo
                    const getAreaPorCargo = (cargo) => {
                      if (!cargo) return 'CAJA_MESAS';
                      const cargoLower = cargo.toLowerCase();
                      if (cargoLower.includes('cocina') || cargoLower.includes('chef') || cargoLower.includes('cocinero')) {
                        return 'COCINA';
                      }
                      return 'CAJA_MESAS';
                    };
                    
                    // Obtener empleados asignados a esta sede (incluyendo horarios adicionales)
                    const empleadosEnSede = [];
                    Object.entries(horarioCreacion).forEach(([empId, dias]) => {
                      const horarioDia = dias[diaCreacionSeleccionado];
                      if (!horarioDia || horarioDia.esDescanso) return;
                      
                      const emp = empleados.find(e => e.id === empId);
                      if (!emp) return;
                      
                      // Verificar sede principal
                      if (horarioDia.sede?.toUpperCase() === sede.toUpperCase()) {
                        empleadosEnSede.push({ emp, horario: horarioDia, esAdicional: false });
                      }
                      
                      // Verificar horarios adicionales
                      const adicionales = horarioDia.horariosAdicionales || [];
                      adicionales.forEach(hAd => {
                        if (hAd.sede?.toUpperCase() === sede.toUpperCase()) {
                          empleadosEnSede.push({ emp, horario: hAd, esAdicional: true });
                        }
                      });
                    });
                    
                    const empleadosCocina = empleadosEnSede.filter(item => getAreaPorCargo(item.emp.cargo) === 'COCINA');
                    const empleadosCajaMesas = empleadosEnSede.filter(item => getAreaPorCargo(item.emp.cargo) === 'CAJA_MESAS');
                    
                    return (
                      <div
                        key={sede}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.borderColor = '#10b981';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)';
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.style.borderColor = '#0ea5e9';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)';
                        }}
                        onDrop={(e) => {
                          e.currentTarget.style.borderColor = '#0ea5e9';
                          e.currentTarget.style.background = 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)';
                          if (empleadoArrastrado) {
                            const empId = empleadoArrastrado.id;
                            const horarioExistente = horarioCreacion[empId]?.[diaCreacionSeleccionado];
                            
                            // Si ya tiene horario en otra sede, guardamos la sede destino para el modal
                            setEmpleadoSeleccionadoCreacion(empleadoArrastrado);
                            
                            // Si es un nuevo horario o está en la misma sede, simplemente actualizar
                            if (!horarioExistente || horarioExistente.esDescanso || 
                                horarioExistente.sede?.toUpperCase() === sede.toUpperCase()) {
                              setHorarioCreacion(prev => ({
                                ...prev,
                                [empId]: {
                                  ...(prev[empId] || {}),
                                  [diaCreacionSeleccionado]: {
                                    ...(prev[empId]?.[diaCreacionSeleccionado] || {}),
                                    sede: sede.toUpperCase(),
                                    esAdicional: false
                                  }
                                }
                              }));
                            } else {
                              // Ya tiene horario en otra sede, abrir modal para horario adicional
                              setHorarioCreacion(prev => ({
                                ...prev,
                                [empId]: {
                                  ...(prev[empId] || {}),
                                  [diaCreacionSeleccionado]: {
                                    ...(prev[empId]?.[diaCreacionSeleccionado] || {}),
                                    sedeNueva: sede.toUpperCase(),
                                    modoAdicional: true
                                  }
                                }
                              }));
                            }
                            
                            setModalAsignarHorario(true);
                            setEmpleadoArrastrado(null);
                          }
                        }}
                        style={{
                          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                          borderRadius: 12,
                          padding: 16,
                          border: '2px solid #0ea5e9',
                          minHeight: 180,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          marginBottom: 14,
                          paddingBottom: 10,
                          borderBottom: '2px solid #7dd3fc'
                        }}>
                          <div>
                            <span style={{ fontSize: 20 }}>📍</span>
                            <span style={{ fontWeight: 700, color: '#0369a1', fontSize: 16, marginLeft: 8 }}>{sede}</span>
                          </div>
                          <span style={{
                            background: '#0ea5e9',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: 12,
                            fontSize: 12,
                            fontWeight: 600
                          }}>
                            {empleadosEnSede.length} asignados
                          </span>
                        </div>
                        
                        {/* Área COCINA */}
                        <div style={{ marginBottom: 14 }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            marginBottom: 8,
                            padding: '6px 10px',
                            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                            borderRadius: 8,
                            border: '1px solid #f59e0b'
                          }}>
                            <span style={{ fontSize: 14 }}>🍳</span>
                            <span style={{ fontWeight: 600, color: '#78350f', fontSize: 12 }}>COCINA ({empleadosCocina.length})</span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, minHeight: 36 }}>
                            {empleadosCocina.map(({ emp, horario }) => (
                              <div 
                                key={emp.id}
                                draggable
                                onDragStart={(e) => {
                                  setEmpleadoArrastrado(emp);
                                  e.currentTarget.style.opacity = '0.5';
                                }}
                                onDragEnd={(e) => {
                                  setEmpleadoArrastrado(null);
                                  e.currentTarget.style.opacity = '1';
                                }}
                                onClick={() => {
                                  setEmpleadoSeleccionadoCreacion(emp);
                                  setModalAsignarHorario(true);
                                }}
                                style={{
                                  padding: '6px 10px',
                                  background: 'white',
                                  borderRadius: 8,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: '#78350f',
                                  cursor: 'pointer',
                                  border: '1px solid #f59e0b',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  transition: 'transform 0.1s ease'
                                }}
                              >
                                <span>{emp.nombre.split(' ')[0]}</span>
                                <span style={{ fontSize: 9, opacity: 0.8, textAlign: 'center' }}>
                                  {formatearHoraAMPM(horario.e1)}-{formatearHoraAMPM(horario.s1)}
                                  {horario.e2 && horario.s2 && (
                                    <><br/>{formatearHoraAMPM(horario.e2)}-{formatearHoraAMPM(horario.s2)}</>
                                  )}
                                </span>
                              </div>
                            ))}
                            {empleadosCocina.length === 0 && (
                              <span style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>Arrastra empleados aquí</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Área CAJA/MESAS */}
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            marginBottom: 8,
                            padding: '6px 10px',
                            background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                            borderRadius: 8,
                            border: '1px solid #3b82f6'
                          }}>
                            <span style={{ fontSize: 14 }}>💼</span>
                            <span style={{ fontWeight: 600, color: '#1e3a8a', fontSize: 12 }}>CAJA/MESAS ({empleadosCajaMesas.length})</span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, minHeight: 36 }}>
                            {empleadosCajaMesas.map(({ emp, horario }) => (
                              <div 
                                key={emp.id}
                                draggable
                                onDragStart={(e) => {
                                  setEmpleadoArrastrado(emp);
                                  e.currentTarget.style.opacity = '0.5';
                                }}
                                onDragEnd={(e) => {
                                  setEmpleadoArrastrado(null);
                                  e.currentTarget.style.opacity = '1';
                                }}
                                onClick={() => {
                                  setEmpleadoSeleccionadoCreacion(emp);
                                  setModalAsignarHorario(true);
                                }}
                                style={{
                                  padding: '6px 10px',
                                  background: 'white',
                                  borderRadius: 8,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: '#1e3a8a',
                                  cursor: 'pointer',
                                  border: '1px solid #3b82f6',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  transition: 'transform 0.1s ease'
                                }}
                              >
                                <span>{emp.nombre.split(' ')[0]}</span>
                                <span style={{ fontSize: 9, opacity: 0.8, textAlign: 'center' }}>
                                  {formatearHoraAMPM(horario.e1)}-{formatearHoraAMPM(horario.s1)}
                                  {horario.e2 && horario.s2 && (
                                    <><br/>{formatearHoraAMPM(horario.e2)}-{formatearHoraAMPM(horario.s2)}</>
                                  )}
                                </span>
                              </div>
                            ))}
                            {empleadosCajaMesas.length === 0 && (
                              <span style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>Arrastra empleados aquí</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Panel lateral de resumen semanal (tipo tabla) */}
          {panelResumenSemanal && modoVistaGrafica === "crear" && (() => {
            // Colores pastel suaves estilo Excel
            const coloresPorSede = {};
            const paletaColores = [
              { bg: '#e8f4fd', text: '#1565c0', border: '#90caf9' }, // Azul suave
              { bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' }, // Verde suave
              { bg: '#fff3e0', text: '#e65100', border: '#ffcc80' }, // Naranja suave
              { bg: '#f3e5f5', text: '#7b1fa2', border: '#ce93d8' }, // Púrpura suave
              { bg: '#e0f7fa', text: '#00838f', border: '#80deea' }, // Cyan suave
              { bg: '#fce4ec', text: '#c2185b', border: '#f48fb1' }, // Rosa suave
              { bg: '#fff8e1', text: '#ff8f00', border: '#ffca28' }, // Ámbar suave
              { bg: '#e8eaf6', text: '#303f9f', border: '#9fa8da' }, // Índigo suave
            ];
            
            // Recolectar todas las sedes usadas
            const sedesUsadasSet = new Set();
            Object.values(horarioCreacion).forEach(dias => {
              Object.values(dias).forEach(h => {
                if (h && !h.esDescanso) {
                  if (h.sede) sedesUsadasSet.add(h.sede.toUpperCase());
                  (h.horariosAdicionales || []).forEach(ad => {
                    if (ad.sede) sedesUsadasSet.add(ad.sede.toUpperCase());
                  });
                }
              });
            });
            const sedesArray = Array.from(sedesUsadasSet);
            sedesArray.forEach((sede, i) => {
              coloresPorSede[sede] = paletaColores[i % paletaColores.length];
            });
            
            const getColorSede = (nombreSede) => {
              if (!nombreSede) return paletaColores[0];
              return coloresPorSede[nombreSede.toUpperCase()] || paletaColores[0];
            };
            
            // Función para formatear hora a AM/PM corto
            const formatoHoraCorto = (hora) => {
              if (!hora) return '';
              const [h, m] = hora.split(':').map(Number);
              const ampm = h >= 12 ? 'PM' : 'AM';
              const hora12 = h % 12 || 12;
              return `${hora12}:${m.toString().padStart(2,'0')}${ampm}`;
            };
            
            // Calcular altura para que quepan TODOS los empleados sin scroll
            const numEmpleados = activos.length;
            const alturaHeader = 70;
            const alturaFooter = 50;
            const alturaFilaHeader = 35;
            const padding = 16;
            const alturaDisponible = window.innerHeight - alturaHeader - alturaFooter - alturaFilaHeader - padding;
            const alturaFila = Math.max(28, Math.floor(alturaDisponible / Math.max(numEmpleados, 1)));
            
            return (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              background: 'white',
              zIndex: 9998,
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Header estilo Excel */}
              <div style={{
                padding: '8px 16px',
                background: '#217346',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: alturaHeader
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 22 }}>📅</span>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'white' }}>
                      Horarios Semanales
                    </h3>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)' }}>
                      {fechasSemanaVistaGrafica[0]} - {fechasSemanaVistaGrafica[6]} | {activos.length} empleados
                    </span>
                  </div>
                </div>
                
                {/* Leyenda de sedes */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {sedesArray.map((sede, i) => (
                    <div 
                      key={sede}
                      style={{
                        padding: '3px 8px',
                        background: paletaColores[i % paletaColores.length].bg,
                        border: `1px solid ${paletaColores[i % paletaColores.length].border}`,
                        borderRadius: 3,
                        fontSize: 11,
                        fontWeight: 600,
                        color: paletaColores[i % paletaColores.length].text
                      }}
                    >
                      {sede}
                    </div>
                  ))}
                </div>
                
                <button
                  onClick={() => setPanelResumenSemanal(false)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: 4,
                    border: 'none',
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  ✕ Cerrar
                </button>
              </div>

              {/* Tabla estilo Excel */}
              <div style={{ flex: 1, padding: '4px 8px', overflow: 'hidden' }}>
                <table style={{ 
                  width: '100%', 
                  height: '100%',
                  borderCollapse: 'collapse',
                  tableLayout: 'fixed',
                  border: '1px solid #d0d0d0'
                }}>
                  <thead>
                    <tr style={{ height: alturaFilaHeader }}>
                      <th style={{ 
                        padding: '4px 6px', 
                        textAlign: 'left', 
                        fontWeight: 700, 
                        fontSize: 13,
                        color: '#333',
                        background: '#f0f0f0',
                        border: '1px solid #d0d0d0',
                        width: 120
                      }}>
                        Empleado
                      </th>
                      {DIAS.map((dia, idx) => (
                        <th 
                          key={idx} 
                          onClick={() => {
                            setDiaCreacionSeleccionado(idx);
                            setPanelResumenSemanal(false);
                          }}
                          style={{ 
                            padding: '4px 2px', 
                            textAlign: 'center', 
                            fontWeight: 700, 
                            fontSize: 12,
                            color: diaCreacionSeleccionado === idx ? 'white' : '#333',
                            background: diaCreacionSeleccionado === idx ? '#217346' : '#f0f0f0',
                            border: '1px solid #d0d0d0',
                            cursor: 'pointer'
                          }}
                        >
                          <div>{dia}</div>
                          <div style={{ fontSize: 10, fontWeight: 500, opacity: 0.7 }}>
                            {fechasSemanaVistaGrafica[idx]}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activos.map((emp, empIdx) => {
                      const diasEmp = horarioCreacion[emp.id] || {};
                      const rowBg = empIdx % 2 === 0 ? 'white' : '#fafafa';
                      
                      return (
                        <tr key={emp.id} style={{ height: alturaFila }}>
                          <td style={{ 
                            padding: '2px 6px', 
                            fontWeight: 600,
                            fontSize: 12,
                            color: '#333',
                            background: rowBg,
                            border: '1px solid #e0e0e0',
                            verticalAlign: 'middle'
                          }}>
                            <div style={{ lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {emp.nombre.split(' ').slice(0, 2).join(' ')}
                            </div>
                          </td>
                          {DIAS.map((_, diaIdx) => {
                            const horario = diasEmp[diaIdx];
                            const cerrado = isDiaCerradoVistaGrafica(fechasSemanaVistaGrafica[diaIdx]);
                            
                            if (cerrado) {
                              return (
                                <td key={diaIdx} style={{ 
                                  padding: 1, 
                                  textAlign: 'center',
                                  background: '#ffebee',
                                  border: '1px solid #e0e0e0',
                                  verticalAlign: 'middle'
                                }}>
                                  <span style={{ fontSize: 13, color: '#c62828', fontWeight: 700 }}>CERRADO</span>
                                </td>
                              );
                            }
                            
                            if (!horario) {
                              return (
                                <td 
                                  key={diaIdx} 
                                  onClick={() => {
                                    setDiaCreacionSeleccionado(diaIdx);
                                    setEmpleadoSeleccionadoCreacion(emp);
                                    setModalAsignarHorario(true);
                                  }}
                                  style={{ 
                                    padding: 1, 
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    background: rowBg,
                                    border: '1px solid #e0e0e0',
                                    verticalAlign: 'middle'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = '#e3f2fd'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = rowBg}
                                >
                                  <span style={{ fontSize: 16, color: '#bdbdbd' }}>+</span>
                                </td>
                              );
                            }
                            
                            if (horario.esDescanso) {
                              return (
                                <td 
                                  key={diaIdx} 
                                  onClick={() => {
                                    setDiaCreacionSeleccionado(diaIdx);
                                    setEmpleadoSeleccionadoCreacion(emp);
                                    setModalAsignarHorario(true);
                                  }}
                                  style={{ 
                                    padding: 2, 
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    background: '#fff8e1',
                                    border: '1px solid #e0e0e0',
                                    verticalAlign: 'middle'
                                  }}
                                >
                                  <div style={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: '#f57c00'
                                  }}>
                                    DESCANSO
                                  </div>
                                </td>
                              );
                            }
                            
                            // Horario normal
                            const adicionales = horario.horariosAdicionales || [];
                            const colorPrincipal = getColorSede(horario.sede);
                            const tienePartido = horario.e2 && horario.s2;
                            
                            return (
                              <td 
                                key={diaIdx}
                                onClick={() => {
                                  setDiaCreacionSeleccionado(diaIdx);
                                  setEmpleadoSeleccionadoCreacion(emp);
                                  setModalAsignarHorario(true);
                                }}
                                style={{ 
                                  padding: 1, 
                                  verticalAlign: 'middle',
                                  cursor: 'pointer',
                                  border: '1px solid #e0e0e0',
                                  background: rowBg
                                }}
                              >
                                <div style={{ display: 'flex', gap: 1, height: '100%' }}>
                                  {/* Horario principal */}
                                  <div style={{
                                    flex: 1,
                                    background: colorPrincipal.bg,
                                    border: `1px solid ${colorPrincipal.border}`,
                                    borderRadius: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    padding: '1px 2px',
                                    minHeight: alturaFila - 4
                                  }}>
                                    <div style={{ fontWeight: 700, color: colorPrincipal.text, fontSize: 13, lineHeight: 1 }}>
                                      {horario.sede || '?'}
                                    </div>
                                    {tienePartido ? (
                                      <div style={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                        <span style={{ color: colorPrincipal.text, fontSize: 12, fontWeight: 700 }}>
                                          {formatoHoraCorto(horario.e1)}-{formatoHoraCorto(horario.s1)}
                                        </span>
                                        <span style={{ color: colorPrincipal.text, fontSize: 10, fontWeight: 600 }}>|</span>
                                        <span style={{ color: colorPrincipal.text, fontSize: 12, fontWeight: 700 }}>
                                          {formatoHoraCorto(horario.e2)}-{formatoHoraCorto(horario.s2)}
                                        </span>
                                      </div>
                                    ) : (
                                      <div style={{ color: colorPrincipal.text, fontSize: 14, fontWeight: 700, lineHeight: 1.1 }}>
                                        {formatoHoraCorto(horario.e1)} - {formatoHoraCorto(horario.s1)}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Horarios adicionales */}
                                  {adicionales.map((hAd, adIdx) => {
                                    const colorAd = getColorSede(hAd.sede);
                                    const tienePartidoAd = hAd.e2 && hAd.s2;
                                    return (
                                      <div 
                                        key={adIdx}
                                        style={{
                                          flex: 1,
                                          background: colorAd.bg,
                                          border: `1px solid ${colorAd.border}`,
                                          borderRadius: 2,
                                          display: 'flex',
                                          flexDirection: 'column',
                                          justifyContent: 'center',
                                          alignItems: 'center',
                                          padding: '1px 2px',
                                          minHeight: alturaFila - 4
                                        }}
                                      >
                                        <div style={{ fontWeight: 700, color: colorAd.text, fontSize: 13, lineHeight: 1 }}>
                                          {hAd.sede || '?'}
                                        </div>
                                        {tienePartidoAd ? (
                                          <div style={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                            <span style={{ color: colorAd.text, fontSize: 12, fontWeight: 700 }}>
                                              {formatoHoraCorto(hAd.e1)}-{formatoHoraCorto(hAd.s1)}
                                            </span>
                                            <span style={{ color: colorAd.text, fontSize: 10, fontWeight: 600 }}>|</span>
                                            <span style={{ color: colorAd.text, fontSize: 12, fontWeight: 700 }}>
                                              {formatoHoraCorto(hAd.e2)}-{formatoHoraCorto(hAd.s2)}
                                            </span>
                                          </div>
                                        ) : (
                                          <div style={{ color: colorAd.text, fontSize: 14, fontWeight: 700, lineHeight: 1.1 }}>
                                            {formatoHoraCorto(hAd.e1)} - {formatoHoraCorto(hAd.s1)}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer estilo Excel */}
              <div style={{
                padding: '6px 16px',
                background: '#f5f5f5',
                borderTop: '1px solid #d0d0d0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: alturaFooter
              }}>
                <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#666' }}>
                  <span>✓ <strong>{Object.keys(horarioCreacion).length}</strong> asignados</span>
                  <span>|</span>
                  <span>💤 <strong>{
                    Object.values(horarioCreacion).reduce((total, dias) => 
                      total + Object.values(dias).filter(h => h?.esDescanso).length, 0
                    )
                  }</strong> descansos</span>
                  <span>|</span>
                  <span>📍 <strong>{sedesArray.length}</strong> sedes</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => setPanelResumenSemanal(false)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 3,
                      border: '1px solid #ccc',
                      background: 'white',
                      color: '#666',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Volver
                  </button>
                  <button
                    onClick={() => {
                      guardarHorariosCreados();
                      setPanelResumenSemanal(false);
                    }}
                    disabled={Object.keys(horarioCreacion).length === 0}
                    style={{
                      padding: '6px 16px',
                      borderRadius: 3,
                      border: 'none',
                      background: Object.keys(horarioCreacion).length === 0 ? '#ccc' : '#217346',
                      color: 'white',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: Object.keys(horarioCreacion).length === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    💾 Guardar
                  </button>
                </div>
              </div>
            </div>
            );
          })()}

          {/* Modal para asignar horario a empleado */}
          {modalAsignarHorario && empleadoSeleccionadoCreacion && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <div style={{
                background: 'white',
                borderRadius: 16,
                padding: 24,
                width: '90%',
                maxWidth: 500,
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1e293b' }}>
                    📝 Asignar Horario - {empleadoSeleccionadoCreacion.nombre.split(' ').slice(0, 2).join(' ')}
                  </h3>
                  <button
                    onClick={() => {
                      // Limpiar flags de modo adicional
                      const empId = empleadoSeleccionadoCreacion.id;
                      setHorarioCreacion(prev => {
                        const nuevo = { ...prev };
                        if (nuevo[empId]?.[diaCreacionSeleccionado]) {
                          delete nuevo[empId][diaCreacionSeleccionado].modoAdicional;
                          delete nuevo[empId][diaCreacionSeleccionado].sedeNueva;
                        }
                        return nuevo;
                      });
                      setModalAsignarHorario(false);
                      setEmpleadoSeleccionadoCreacion(null);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: 24,
                      cursor: 'pointer',
                      color: '#64748b'
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Mostrar horarios existentes en otras sedes */}
                {(() => {
                  const empId = empleadoSeleccionadoCreacion.id;
                  const horarioActual = horarioCreacion[empId]?.[diaCreacionSeleccionado];
                  const horariosExistentes = [];
                  
                  if (horarioActual && !horarioActual.esDescanso && horarioActual.e1 && horarioActual.s1) {
                    horariosExistentes.push(horarioActual);
                  }
                  const adicionales = horarioActual?.horariosAdicionales || [];
                  horariosExistentes.push(...adicionales.filter(h => h.e1 && h.s1));
                  
                  if (horariosExistentes.length > 0 && horarioActual?.modoAdicional) {
                    return (
                      <div style={{
                        padding: 12,
                        background: '#fef3c7',
                        borderRadius: 8,
                        marginBottom: 16,
                        border: '1px solid #f59e0b'
                      }}>
                        <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 8, fontSize: 13 }}>
                          ⚠️ Este empleado ya tiene horario(s) asignado(s) este día:
                        </div>
                        {horariosExistentes.map((h, idx) => (
                          <div key={idx} style={{ fontSize: 12, color: '#78350f', marginBottom: 4 }}>
                            • <strong>{h.sede}:</strong> {formatearHoraAMPM(h.e1)} - {formatearHoraAMPM(h.s1)}
                            {h.e2 && h.s2 && ` / ${formatearHoraAMPM(h.e2)} - ${formatearHoraAMPM(h.s2)}`}
                          </div>
                        ))}
                        <div style={{ fontSize: 11, color: '#92400e', marginTop: 8, fontStyle: 'italic' }}>
                          El nuevo horario en <strong>{horarioActual.sedeNueva}</strong> NO puede cruzar horas con los anteriores.
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 13 }}>
                    📅 Día: {DIAS[diaCreacionSeleccionado]} ({fechasSemana[diaCreacionSeleccionado]})
                  </label>
                </div>

                {/* Tipos de horario predefinidos */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 13 }}>
                    ⏰ Tipo de Horario:
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                    {TIPOS_HORARIO_PREDEFINIDOS.map(tipo => (
                      <button
                        key={tipo.id}
                        onClick={() => {
                          const empId = empleadoSeleccionadoCreacion.id;
                          const horarioActual = horarioCreacion[empId]?.[diaCreacionSeleccionado];
                          const sedeActual = horarioActual?.sedeNueva || horarioActual?.sede || sedesLista[0]?.toUpperCase() || '';
                          const esModoAdicional = horarioActual?.modoAdicional;
                          
                          const asignarHorario = () => {
                            if (esModoAdicional) {
                              // En modo adicional, guardar valores nuevos en campos separados
                              setHorarioCreacion(prev => ({
                                ...prev,
                                [empId]: {
                                  ...(prev[empId] || {}),
                                  [diaCreacionSeleccionado]: {
                                    ...(prev[empId]?.[diaCreacionSeleccionado] || {}),
                                    e1Nuevo: tipo.e1,
                                    s1Nuevo: tipo.s1,
                                    e2Nuevo: tipo.e2,
                                    s2Nuevo: tipo.s2,
                                    tipoHorarioNuevo: tipo.id
                                  }
                                }
                              }));
                            } else {
                              // Modo normal, reemplazar horario
                              setHorarioCreacion(prev => ({
                                ...prev,
                                [empId]: {
                                  ...(prev[empId] || {}),
                                  [diaCreacionSeleccionado]: {
                                    sede: tipo.esDescanso ? 'DESCANSO' : sedeActual,
                                    e1: tipo.e1,
                                    s1: tipo.s1,
                                    e2: tipo.e2,
                                    s2: tipo.s2,
                                    esDescanso: tipo.esDescanso || false,
                                    tipoHorario: tipo.id
                                  }
                                }
                              }));
                            }
                          };
                          
                          // Si es descanso, verificar si ya tiene otros descansos esta semana
                          if (tipo.esDescanso && !esModoAdicional) {
                            const descansosExistentes = Object.entries(horarioCreacion[empId] || {})
                              .filter(([dia, h]) => h?.esDescanso && dia !== String(diaCreacionSeleccionado));
                            
                            if (descansosExistentes.length > 0) {
                              const diasDescanso = descansosExistentes.map(([dia]) => DIAS[parseInt(dia)]).join(', ');
                              if (window.confirm(`⚠️ Este empleado ya tiene ${descansosExistentes.length} descanso(s) asignado(s) esta semana (${diasDescanso}). ¿Desea agregar otro descanso?`)) {
                                asignarHorario();
                              }
                            } else {
                              asignarHorario();
                            }
                          } else if (!tipo.esDescanso || esModoAdicional) {
                            asignarHorario();
                          }
                        }}
                        style={{
                          padding: '10px 12px',
                          borderRadius: 8,
                          border: '2px solid #e2e8f0',
                          background: tipo.esDescanso 
                            ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
                            : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                          cursor: 'pointer',
                          textAlign: 'left',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: 12, color: '#1e293b' }}>{tipo.nombre}</div>
                        {!tipo.esDescanso && tipo.e1 && (
                          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                            {formatearHoraAMPM(tipo.e1)} - {formatearHoraAMPM(tipo.s1)}
                            {tipo.e2 && ` / ${formatearHoraAMPM(tipo.e2)} - ${formatearHoraAMPM(tipo.s2)}`}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sede */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 13 }}>
                    📍 Sede:
                  </label>
                  <select
                    value={horarioCreacion[empleadoSeleccionadoCreacion.id]?.[diaCreacionSeleccionado]?.sede || ''}
                    onChange={(e) => {
                      const empId = empleadoSeleccionadoCreacion.id;
                      setHorarioCreacion(prev => ({
                        ...prev,
                        [empId]: {
                          ...(prev[empId] || {}),
                          [diaCreacionSeleccionado]: {
                            ...(prev[empId]?.[diaCreacionSeleccionado] || {}),
                            sede: e.target.value
                          }
                        }
                      }));
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '2px solid #e2e8f0',
                      fontSize: 13
                    }}
                  >
                    <option value="">Seleccionar sede...</option>
                    {sedesListaFiltradas.map(sede => (
                      <option key={sede} value={sede.toUpperCase()}>{sede}</option>
                    ))}
                  </select>
                </div>

                {/* Inputs de hora - Siempre editables */}
                {horarioCreacion[empleadoSeleccionadoCreacion.id]?.[diaCreacionSeleccionado] && 
                 !horarioCreacion[empleadoSeleccionadoCreacion.id]?.[diaCreacionSeleccionado]?.esDescanso && (
                  <>
                    <div style={{ marginBottom: 8 }}>
                      <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, color: '#374151', fontSize: 12 }}>
                        ✏️ Modificar horarios (puede editar las horas):
                      </label>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#374151', fontSize: 12 }}>
                          Entrada 1:
                        </label>
                        <input
                          type="time"
                          value={horarioCreacion[empleadoSeleccionadoCreacion.id]?.[diaCreacionSeleccionado]?.e1 || ''}
                          onChange={(e) => {
                            const empId = empleadoSeleccionadoCreacion.id;
                            setHorarioCreacion(prev => ({
                              ...prev,
                              [empId]: {
                                ...(prev[empId] || {}),
                                [diaCreacionSeleccionado]: {
                                  ...(prev[empId]?.[diaCreacionSeleccionado] || {}),
                                  e1: e.target.value
                                }
                              }
                            }));
                          }}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: 6,
                            border: '2px solid #e2e8f0',
                            fontSize: 13
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#374151', fontSize: 12 }}>
                          Salida 1:
                        </label>
                        <input
                          type="time"
                          value={horarioCreacion[empleadoSeleccionadoCreacion.id]?.[diaCreacionSeleccionado]?.s1 || ''}
                          onChange={(e) => {
                            const empId = empleadoSeleccionadoCreacion.id;
                            setHorarioCreacion(prev => ({
                              ...prev,
                              [empId]: {
                                ...(prev[empId] || {}),
                                [diaCreacionSeleccionado]: {
                                  ...(prev[empId]?.[diaCreacionSeleccionado] || {}),
                                  s1: e.target.value
                                }
                              }
                            }));
                          }}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: 6,
                            border: '2px solid #e2e8f0',
                            fontSize: 13
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#374151', fontSize: 12 }}>
                          Entrada 2 (opcional):
                        </label>
                        <input
                          type="time"
                          value={horarioCreacion[empleadoSeleccionadoCreacion.id]?.[diaCreacionSeleccionado]?.e2 || ''}
                          onChange={(e) => {
                            const empId = empleadoSeleccionadoCreacion.id;
                            setHorarioCreacion(prev => ({
                              ...prev,
                              [empId]: {
                                ...(prev[empId] || {}),
                                [diaCreacionSeleccionado]: {
                                  ...(prev[empId]?.[diaCreacionSeleccionado] || {}),
                                  e2: e.target.value
                                }
                              }
                            }));
                          }}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: 6,
                            border: '2px solid #e2e8f0',
                            fontSize: 13
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#374151', fontSize: 12 }}>
                          Salida 2 (opcional):
                        </label>
                        <input
                          type="time"
                          value={horarioCreacion[empleadoSeleccionadoCreacion.id]?.[diaCreacionSeleccionado]?.s2 || ''}
                          onChange={(e) => {
                            const empId = empleadoSeleccionadoCreacion.id;
                            setHorarioCreacion(prev => ({
                              ...prev,
                              [empId]: {
                                ...(prev[empId] || {}),
                                [diaCreacionSeleccionado]: {
                                  ...(prev[empId]?.[diaCreacionSeleccionado] || {}),
                                  s2: e.target.value
                                }
                              }
                            }));
                          }}
                          style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: 6,
                            border: '2px solid #e2e8f0',
                            fontSize: 13
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Resumen del horario asignado */}
                {horarioCreacion[empleadoSeleccionadoCreacion.id]?.[diaCreacionSeleccionado] && (
                  <div style={{
                    padding: 16,
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    borderRadius: 10,
                    marginBottom: 16
                  }}>
                    <div style={{ fontWeight: 600, color: '#065f46', marginBottom: 8, fontSize: 13 }}>
                      📋 Resumen del Horario:
                    </div>
                    {(() => {
                      const h = horarioCreacion[empleadoSeleccionadoCreacion.id][diaCreacionSeleccionado];
                      if (h.esDescanso) {
                        return <div style={{ color: '#92400e', fontWeight: 600 }}>💤 DESCANSO</div>;
                      }
                      const horas = calcularHorasTrabajadas(h.e1, h.s1, h.e2, h.s2);
                      return (
                        <>
                          <div style={{ fontSize: 12, color: '#047857' }}>
                            <strong>Sede:</strong> {h.sede || 'No asignada'}
                          </div>
                          {h.e1 && h.s1 && (
                            <div style={{ fontSize: 12, color: '#047857' }}>
                              <strong>Turno 1:</strong> {formatearHoraAMPM(h.e1)} - {formatearHoraAMPM(h.s1)}
                            </div>
                          )}
                          {h.e2 && h.s2 && (
                            <div style={{ fontSize: 12, color: '#047857' }}>
                              <strong>Turno 2:</strong> {formatearHoraAMPM(h.e2)} - {formatearHoraAMPM(h.s2)}
                            </div>
                          )}
                          <div style={{ 
                            fontSize: 12, 
                            color: horas > 7 ? '#dc2626' : '#047857',
                            fontWeight: horas > 7 ? 700 : 400,
                            marginTop: 4
                          }}>
                            <strong>Total:</strong> {horas.toFixed(1)} horas
                            {horas > 7 && ' ⚠️ Más de 7 horas'}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Botones de acción */}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      const empId = empleadoSeleccionadoCreacion.id;
                      setHorarioCreacion(prev => {
                        const nuevo = { ...prev };
                        if (nuevo[empId]) {
                          delete nuevo[empId][diaCreacionSeleccionado];
                          if (Object.keys(nuevo[empId]).length === 0) {
                            delete nuevo[empId];
                          }
                        }
                        return nuevo;
                      });
                      setModalAsignarHorario(false);
                      setEmpleadoSeleccionadoCreacion(null);
                    }}
                    style={{
                      padding: '10px 20px',
                      borderRadius: 8,
                      border: '2px solid #ef4444',
                      background: 'white',
                      color: '#dc2626',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    🗑️ Quitar Horario
                  </button>
                  <button
                    onClick={() => {
                      const empId = empleadoSeleccionadoCreacion.id;
                      const horarioActual = horarioCreacion[empId]?.[diaCreacionSeleccionado];
                      
                      // Validar cruce de horas si estamos en modo adicional
                      if (horarioActual?.modoAdicional && horarioActual?.sedeNueva) {
                        const nuevoHorario = {
                          sede: horarioActual.sedeNueva,
                          e1: horarioActual.e1Nuevo || horarioActual.e1,
                          s1: horarioActual.s1Nuevo || horarioActual.s1,
                          e2: horarioActual.e2Nuevo || horarioActual.e2,
                          s2: horarioActual.s2Nuevo || horarioActual.s2
                        };
                        
                        // Verificar cruce con horarios existentes
                        const horariosExistentes = [horarioActual];
                        const adicionales = horarioActual.horariosAdicionales || [];
                        horariosExistentes.push(...adicionales);
                        
                        for (const h of horariosExistentes) {
                          if (!h.sede || h.sede === 'DESCANSO') continue;
                          if (verificarCruceHorarioCompleto(nuevoHorario, h)) {
                            alert(`❌ No se puede asignar este horario. Las horas se cruzan con el horario en ${h.sede}.\n\nNo se permite que ni una sola hora se cruce entre sedes.`);
                            return;
                          }
                        }
                        
                        // No hay cruce, agregar como horario adicional
                        setHorarioCreacion(prev => {
                          const horarioPrevio = prev[empId]?.[diaCreacionSeleccionado] || {};
                          const adicionalesPrevios = horarioPrevio.horariosAdicionales || [];
                          
                          return {
                            ...prev,
                            [empId]: {
                              ...(prev[empId] || {}),
                              [diaCreacionSeleccionado]: {
                                ...horarioPrevio,
                                horariosAdicionales: [
                                  ...adicionalesPrevios,
                                  nuevoHorario
                                ],
                                modoAdicional: false,
                                sedeNueva: undefined,
                                e1Nuevo: undefined,
                                s1Nuevo: undefined,
                                e2Nuevo: undefined,
                                s2Nuevo: undefined
                              }
                            }
                          };
                        });
                      }
                      
                      setModalAsignarHorario(false);
                      setEmpleadoSeleccionadoCreacion(null);
                    }}
                    style={{
                      padding: '10px 24px',
                      borderRadius: 8,
                      border: 'none',
                      background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                      color: 'white',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                    }}
                  >
                    ✅ Confirmar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal de confirmación para guardar con alertas */}
          {modalConfirmarGuardarHorario && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <div style={{
                background: 'white',
                borderRadius: 16,
                padding: 24,
                width: '90%',
                maxWidth: 500,
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
              }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: '#dc2626' }}>
                  ⚠️ Alertas de Validación
                </h3>
                
                {alertasHorario.map((alerta, idx) => {
                  const bgColor = alerta.tipo === 'error' ? '#fef2f2' : alerta.tipo === 'advertencia' ? '#fffbeb' : '#f0f9ff';
                  const borderColor = alerta.tipo === 'error' ? '#fecaca' : alerta.tipo === 'advertencia' ? '#fde68a' : '#bae6fd';
                  const textColor = alerta.tipo === 'error' ? '#dc2626' : alerta.tipo === 'advertencia' ? '#d97706' : '#0369a1';
                  const subTextColor = alerta.tipo === 'error' ? '#7f1d1d' : alerta.tipo === 'advertencia' ? '#92400e' : '#0c4a6e';
                  
                  return (
                    <div key={idx} style={{
                      padding: 16,
                      background: bgColor,
                      borderRadius: 10,
                      marginBottom: 12,
                      border: `1px solid ${borderColor}`
                    }}>
                      <div style={{ fontWeight: 600, color: textColor, marginBottom: 8 }}>
                        {alerta.titulo} {alerta.bloquea && '⛔'}
                      </div>
                      {alerta.empleados?.map((emp, i) => (
                        <div key={i} style={{ fontSize: 12, color: subTextColor, marginBottom: 4 }}>
                          • {emp.empleado}{emp.dia ? ` - ${emp.dia}` : ''}{emp.horas ? `: ${emp.horas} horas` : ''}{emp.novedad ? ` (${emp.novedad})` : ''}{emp.fecha ? ` [${emp.fecha}]` : ''}
                        </div>
                      ))}
                    </div>
                  );
                })}

                {alertasHorario.some(a => a.bloquea) ? (
                  <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 20, fontWeight: 600 }}>
                    ⛔ Hay errores que impiden guardar. Corrige los problemas antes de continuar.
                  </p>
                ) : (
                  <p style={{ color: '#64748b', fontSize: 13, marginBottom: 20 }}>
                    ¿Deseas guardar de todos modos?
                  </p>
                )}

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setModalConfirmarGuardarHorario(false)}
                    style={{
                      padding: '10px 20px',
                      borderRadius: 8,
                      border: '2px solid #e2e8f0',
                      background: 'white',
                      color: '#64748b',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {alertasHorario.some(a => a.bloquea) ? 'Cerrar' : 'Cancelar'}
                  </button>
                  {!alertasHorario.some(a => a.bloquea) && (
                    <button
                      onClick={ejecutarGuardarHorarios}
                      style={{
                        padding: '10px 24px',
                        borderRadius: 8,
                        border: 'none',
                        background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                        color: 'white',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
                      }}
                    >
                      ⚠️ Guardar de todos modos
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Modal copiar horarios de otra semana a Vista Gráfica */}
          {modalCopiarVistaGraficaOpen && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: 12,
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                padding: 20,
                width: 450,
                maxWidth: '95vw'
              }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: '#1e40af' }}>
                  📋 Copiar horarios de otra semana
                </h3>
                <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
                  Selecciona la semana origen. Se copiarán los turnos a la semana de Vista Gráfica 
                  ({semanaInicioVistaGrafica} → {semanaFinVistaGrafica}).
                </p>
                {horariosCopiablesVistaGrafica.length ? (
                  <>
                    <select
                      style={{ 
                        border: '2px solid #3b82f6', 
                        borderRadius: 8, 
                        padding: '10px 12px', 
                        fontSize: 14, 
                        width: '100%', 
                        marginBottom: 16,
                        fontWeight: 500
                      }}
                      value={copiarSeleccionVistaGrafica}
                      onChange={(e) => setCopiarSeleccionVistaGrafica(e.target.value)}
                    >
                      {horariosCopiablesVistaGrafica.map((h) => (
                        <option key={h.id} value={h.id}>
                          📅 {h.semanaInicio} → {h.semanaFin} ({(h.estado || "temporal").toUpperCase()})
                        </option>
                      ))}
                    </select>
                    <div style={{ 
                      padding: 12, 
                      background: '#f0f9ff', 
                      borderRadius: 8, 
                      marginBottom: 16,
                      border: '1px solid #bae6fd',
                      fontSize: 12,
                      color: '#0369a1'
                    }}>
                      💡 <strong>Nota:</strong> Se respetarán los días cerrados, empleados con novedades (incapacidad, vacaciones, etc.) y <strong>horarios ya guardados</strong> (permisos especiales) que no serán sobrescritos.
                    </div>
                  </>
                ) : (
                  <div style={{ 
                    fontSize: 13, 
                    color: '#64748b', 
                    marginBottom: 16,
                    padding: 20,
                    background: '#f8fafc',
                    borderRadius: 8,
                    textAlign: 'center'
                  }}>
                    <span style={{ fontSize: 32, display: 'block', marginBottom: 8 }}>📭</span>
                    No hay horarios guardados para copiar.
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                  <button
                    onClick={() => setModalCopiarVistaGraficaOpen(false)}
                    style={{
                      padding: '10px 20px',
                      borderRadius: 8,
                      border: '2px solid #e2e8f0',
                      background: 'white',
                      color: '#64748b',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => copiarHorarioAVistaGrafica(copiarSeleccionVistaGrafica)}
                    disabled={!copiarSeleccionVistaGrafica}
                    style={{
                      padding: '10px 24px',
                      borderRadius: 8,
                      border: 'none',
                      background: copiarSeleccionVistaGrafica 
                        ? 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)' 
                        : 'rgba(200,200,200,0.5)',
                      color: 'white',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: copiarSeleccionVistaGrafica ? 'pointer' : 'not-allowed',
                      boxShadow: copiarSeleccionVistaGrafica ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none'
                    }}
                  >
                    📋 Copiar aquí
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vista: Programación de Horarios (original) */}
      {vistaHorarios === "programacion" && (
      <>
      {/* Barra de acciones */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginBottom: 16,
        padding: '12px 16px',
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
        borderRadius: 12,
        border: '2px solid #e9ecef',
        overflowX: 'auto'
      }}>
        {/* Verificar si hay días editables (no cerrados) en la semana */}
        {(() => {
          const diasEditables = fechasSemana.filter(iso => !isDiaCerrado(iso));
          const todosLosDiasCerrados = diasEditables.length === 0;
          const diasQuincenaActual = getDiasHastaLimiteQuincena();
          const diasQuincenaSinCerrar = diasQuincenaActual.filter(iso => !isDiaCerrado(iso));
          const todosLosDiasQuincenaCerrados = diasQuincenaSinCerrar.length === 0;
          
          return (
            <>
              {tienePermiso("rh_horarios_guardar") && (
              <button
                onClick={()=>guardar("temporal")}
                disabled={todosLosDiasCerrados}
                style={{
                  background: todosLosDiasCerrados ? 'rgba(200,200,200,0.5)' : 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: todosLosDiasCerrados ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: todosLosDiasCerrados ? 'none' : '0 4px 12px rgba(82, 196, 26, 0.35)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: todosLosDiasCerrados ? '#999' : '#ffffff',
                  opacity: todosLosDiasCerrados ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!todosLosDiasCerrados) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(82, 196, 26, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!todosLosDiasCerrados) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(82, 196, 26, 0.35)';
                  }
                }}
              >
                <span style={{ fontSize: 16 }}>💾</span> Guardar
              </button>
              )}
              {tienePermiso("rh_horarios_cierre") && (
              <button
                onClick={() => {
                  if (todosLosDiasQuincenaCerrados) return;
                  const limite = getLimiteQuincena(fechasSemana[0]);
                  
                  // 🔒 VERIFICAR empleados con más de 7 horas antes de cerrar
                  const empleadosExceso = obtenerEmpleadosMas7HorasPorDia();
                  
                  if (empleadosExceso.length > 0) {
                    // Hay empleados con más de 7 horas - mostrar modal de resumen
                    setEmpleadosMas7HorasLista(empleadosExceso);
                    setLimiteQuincenaResumen(limite);
                    setModalResumen7Horas(true);
                  } else {
                    // No hay empleados con exceso - confirmar normalmente
                    const diasACerrar = diasQuincenaSinCerrar.length;
                    const msg = diasACerrar < diasQuincenaActual.length
                      ? `¿Seguro que deseas cerrar ${diasACerrar} día(s) hasta ${limite}? Los días ya cerrados se mantienen.`
                      : `¿Seguro que deseas cerrar el horario hasta ${limite}? No se podrá modificar después del cierre.`;
                    const ok = window.confirm(msg);
                    if (ok) guardar("definitivo");
                  }
                }}
                disabled={todosLosDiasQuincenaCerrados}
                style={{
                  background: todosLosDiasQuincenaCerrados ? 'rgba(200,200,200,0.5)' : 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: todosLosDiasQuincenaCerrados ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: todosLosDiasQuincenaCerrados ? 'none' : '0 4px 12px rgba(255, 107, 53, 0.35)',
                  fontSize: 13,
                  fontWeight: 600,
                  color: todosLosDiasQuincenaCerrados ? '#999' : '#ffffff',
                  opacity: todosLosDiasQuincenaCerrados ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!todosLosDiasQuincenaCerrados) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 107, 53, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!todosLosDiasQuincenaCerrados) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.35)';
                  }
                }}
              >
                <span style={{ fontSize: 16 }}>🔒</span> Cerrar Horario
              </button>
              )}
              {tienePermiso("rh_horarios_modificar_cerrado") && diasCerrados.length > 0 && (
                <button
                  onClick={reabrirHorario}
                  style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)',
                    border: 'none',
                    borderRadius: 10,
                    padding: '10px 18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 4px 12px rgba(24, 144, 255, 0.35)',
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#ffffff'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(24, 144, 255, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.35)';
                  }}
                >
                  <span style={{ fontSize: 16 }}>🔓</span> Reabrir
                </button>
              )}
            </>
          );
        })()}
        
        {/* Separador visual */}
        <div style={{ width: 1, height: 30, background: '#dee2e6', margin: '0 4px' }} />
        
        {tienePermiso("rh_horarios_copiar") && (
        <button
          onClick={() => setModalCopiarOpen(true)}
          disabled={horariosCopiables.length === 0}
          style={{
            background: 'white',
            border: '2px solid #e9ecef',
            borderRadius: 10,
            padding: '10px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: horariosCopiables.length === 0 ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            fontSize: 13,
            fontWeight: 600,
            color: horariosCopiables.length === 0 ? '#999' : '#495057',
            opacity: horariosCopiables.length === 0 ? 0.6 : 1
          }}
          onMouseEnter={(e) => {
            if (horariosCopiables.length > 0) {
              e.currentTarget.style.borderColor = '#ff6b35';
              e.currentTarget.style.color = '#ff6b35';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e9ecef';
            e.currentTarget.style.color = '#495057';
          }}
        >
          <span style={{ fontSize: 16 }}>📋</span> Copiar {horariosCopiables.length > 0 && `(${horariosCopiables.length})`}
        </button>
        )}
        {tienePermiso("rh_horarios_imprimir") && (
        <button
          onClick={imprimir}
          style={{
            background: 'white',
            border: '2px solid #e9ecef',
            borderRadius: 10,
            padding: '10px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontSize: 13,
            fontWeight: 600,
            color: '#495057'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#ff6b35';
            e.currentTarget.style.color = '#ff6b35';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e9ecef';
            e.currentTarget.style.color = '#495057';
          }}
        >
          <span style={{ fontSize: 16 }}>🖨️</span> Imprimir
        </button>
        )}
        {tienePermiso("rh_horarios_elegir_empleados") && (
        <button
          onClick={() => setSelOpen(true)}
          style={{
            background: 'white',
            border: '2px solid #e9ecef',
            borderRadius: 10,
            padding: '10px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontSize: 13,
            fontWeight: 600,
            color: '#495057'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#ff6b35';
            e.currentTarget.style.color = '#ff6b35';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#e9ecef';
            e.currentTarget.style.color = '#495057';
          }}
        >
          <span style={{ fontSize: 16 }}>👥</span> Empleados ({selectedIds.length}/{activos.length})
        </button>
        )}
        
        {/* Separador visual */}
        <div style={{ width: 1, height: 32, background: '#e9ecef', margin: '0 4px' }} />
        
        {/* Filtro por Sede */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 14 }}>🏢</span>
          <select
            value={filtroSede}
            onChange={(e) => setFiltroSede(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: filtroSede ? '2px solid #ff6b35' : '2px solid #e9ecef',
              fontSize: 13,
              fontWeight: 500,
              color: '#495057',
              background: filtroSede ? '#fff7ed' : 'white',
              cursor: 'pointer',
              minWidth: 140
            }}
          >
            <option value="">Todas las sedes</option>
            {sedesUsadasEnSemana.map(sede => (
              <option key={sede} value={sede}>{sede}</option>
            ))}
            {/* También mostrar sedes del sistema que no estén usadas */}
            {sedesLista.filter(s => !sedesUsadasEnSemana.includes(s.toUpperCase())).map(sede => (
              <option key={sede} value={sede.toUpperCase()}>{sede}</option>
            ))}
          </select>
        </div>
        
        {/* Filtro por Día */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 14 }}>📅</span>
          <select
            value={filtroDia}
            onChange={(e) => setFiltroDia(parseInt(e.target.value))}
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: filtroDia !== -1 ? '2px solid #ff6b35' : '2px solid #e9ecef',
              fontSize: 13,
              fontWeight: 500,
              color: '#495057',
              background: filtroDia !== -1 ? '#fff7ed' : 'white',
              cursor: 'pointer',
              minWidth: 130
            }}
          >
            <option value={-1}>Semana completa</option>
            {DIAS.map((dia, idx) => (
              <option key={idx} value={idx}>{dia}</option>
            ))}
          </select>
        </div>
        
        {/* Botón limpiar filtros (solo si hay filtros activos) */}
        {(filtroSede || filtroDia !== -1) && (
          <button
            onClick={() => {
              setFiltroSede("");
              setFiltroDia(-1);
            }}
            style={{
              background: '#fee2e2',
              border: '2px solid #fecaca',
              borderRadius: 8,
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              color: '#dc2626'
            }}
          >
            <span>✕</span> Limpiar filtros
          </button>
        )}
        
        {/* Indicador de filtro activo con descripción clara */}
        {(filtroSede || filtroDia !== -1) && (
          <div style={{
            padding: '6px 12px',
            background: filtroSede && filtroDia === -1 ? '#dcfce7' : filtroDia !== -1 && !filtroSede ? '#dbeafe' : '#fef3c7',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            color: filtroSede && filtroDia === -1 ? '#166534' : filtroDia !== -1 && !filtroSede ? '#1e40af' : '#92400e',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}>
            <span>{filtroSede && filtroDia === -1 ? '🏢' : filtroDia !== -1 && !filtroSede ? '📅' : '🔍'}</span>
            {filtroSede && filtroDia === -1 && (
              <>Sede <strong>{filtroSede}</strong> - Semana completa ({empleadosVisibles.length} empleados)</>
            )}
            {filtroDia !== -1 && !filtroSede && (
              <><strong>{DIAS[filtroDia]}</strong> - Todas las sedes ({empleadosVisibles.length} empleados)</>
            )}
            {filtroSede && filtroDia !== -1 && (
              <>Sede <strong>{filtroSede}</strong> el <strong>{DIAS[filtroDia]}</strong> ({empleadosVisibles.length} empleados)</>
            )}
          </div>
        )}
      </div>
 
      {/* Tabla de horarios */}
      <div style={{ 
        background: 'white', 
        borderRadius: 12, 
        border: '2px solid #e9ecef',
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(0,0,0,0.06)'
      }}>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
            <tr>
              <th style={{ 
                padding: '14px 16px', 
                textAlign: 'left', 
                fontWeight: 700,
                color: '#495057',
                borderBottom: '2px solid #dee2e6',
                width: 200
              }}>
                👤 Empleado
              </th>
              {fechasSemana.map((iso, i) => {
                // Si hay filtro de día activo, solo mostrar ese día
                if (filtroDia !== -1 && i !== filtroDia) return null;
                
                const fest = isHolidayOrSundayCO(iso, holidaysCache);
                const cerrado = isDiaCerrado(iso);
                const diaNum = iso.split('-')[2];
                const esFiltroDia = filtroDia === i;
                return (
                  <th key={i} style={{ 
                    padding: '14px 12px', 
                    textAlign: 'center',
                    fontWeight: 600,
                    color: fest ? '#dc3545' : cerrado ? '#6c757d' : '#495057',
                    borderBottom: '2px solid #dee2e6',
                    background: esFiltroDia ? 'rgba(255, 107, 53, 0.1)' : cerrado ? 'rgba(108, 117, 125, 0.1)' : fest ? 'rgba(220, 53, 69, 0.05)' : 'transparent',
                    minWidth: filtroDia !== -1 ? 300 : 'auto' // Más ancho si solo se muestra un día
                  }}>
                    <div style={{ fontSize: 11, opacity: 0.7 }}>{DIAS[i]}</div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{diaNum}</div>
                    {cerrado && <span style={{ fontSize: 10 }}>🔒</span>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {empleadosVisibles.map((emp) => {
              // Calcular horas totales del empleado (maneja turnos nocturnos)
              const horasTotales = fechasSemana.reduce((acc, iso, idx) => {
                const cel = celdas?.[emp.id]?.[idx];
                if (!cel) return acc;
                // Calcular horas de este día
                let minutos = 0;
                // e1 y s1 son entrada1 y salida1, e2 y s2 son entrada2 y salida2
                if (cel.e1 && cel.s1) {
                  const [h1, m1] = cel.e1.split(':').map(Number);
                  const [h2, m2] = cel.s1.split(':').map(Number);
                  if (!isNaN(h1) && !isNaN(h2)) {
                    const entradaMin = h1 * 60 + (m1 || 0);
                    const salidaMin = h2 * 60 + (m2 || 0);
                    // Si salida < entrada, el turno cruza medianoche
                    if (salidaMin < entradaMin) {
                      minutos += (1440 - entradaMin) + salidaMin; // 1440 = 24*60 minutos
                    } else {
                      minutos += salidaMin - entradaMin;
                    }
                  }
                }
                if (cel.e2 && cel.s2) {
                  const [h3, m3] = cel.e2.split(':').map(Number);
                  const [h4, m4] = cel.s2.split(':').map(Number);
                  if (!isNaN(h3) && !isNaN(h4)) {
                    const entrada2Min = h3 * 60 + (m3 || 0);
                    const salida2Min = h4 * 60 + (m4 || 0);
                    // Si salida < entrada, el turno cruza medianoche
                    if (salida2Min < entrada2Min) {
                      minutos += (1440 - entrada2Min) + salida2Min;
                    } else {
                      minutos += salida2Min - entrada2Min;
                    }
                  }
                }
                return acc + Math.max(0, minutos);
              }, 0);
              const horas = Math.floor(horasTotales / 60);
              const mins = horasTotales % 60;
              
              return (
              <tr key={emp.id} style={{ 
                borderTop: '1px solid #e9ecef',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 107, 53, 0.03)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <td style={{ 
                  padding: '12px 16px', 
                  verticalAlign: 'top'
                }}>
                  <div style={{ fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{emp.nombre}</div>
                  <div style={{ fontSize: 10, color: '#6c757d', marginBottom: 6 }}>{emp.tipoDoc} {emp.documento}</div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8,
                    flexWrap: 'wrap'
                  }}>
                    {(() => {
                      const hayDiasEditables = fechasSemana.some(iso => !isDiaCerrado(iso));
                      return (
                        <>
                        <button
                          onClick={()=> hayDiasEditables && setModalEvento({ empId: emp.id })}
                          disabled={!hayDiasEditables}
                          style={{
                            background: hayDiasEditables ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e9ecef',
                            border: 'none',
                            borderRadius: 6,
                            padding: '4px 10px',
                            fontSize: 10,
                            fontWeight: 600,
                            color: hayDiasEditables ? 'white' : '#999',
                            cursor: hayDiasEditables ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4
                          }}
                        >
                          📅 Evento
                        </button>
                        <span style={{ 
                          fontSize: 10, 
                          color: '#6c757d',
                          background: 'rgba(255, 107, 53, 0.1)',
                          padding: '3px 8px',
                          borderRadius: 4,
                          fontWeight: 500
                        }}>
                          ⏱️ {horas}h {mins > 0 ? `${mins}m` : ''}/sem
                        </span>
                        </>
                      );
                    })()}
                  </div>
                </td>
                {fechasSemana.map((iso, idx) => {
                  // Si hay filtro de día activo, solo mostrar ese día
                  if (filtroDia !== -1 && idx !== filtroDia) return null;
                  
                  const ev = eventosPorDia?.[emp.id]?.[iso];
                  const cerrado = isDiaCerrado(iso);
                  const esFiltroDia = filtroDia === idx;
                  const bg = cerrado ? 'rgba(108, 117, 125, 0.05)' : ev ? ev.color : esFiltroDia ? 'rgba(255, 107, 53, 0.03)' : 'transparent';
                  return (
                    <td key={idx} style={{ 
                      padding: '8px 10px', 
                      verticalAlign: 'top',
                      background: bg,
                      borderLeft: '1px solid #f0f0f0',
                      minWidth: filtroDia !== -1 ? 300 : 'auto' // Más ancho si solo se muestra un día
                    }}>
                      <EditorCelda emp={emp} idxDia={idx} />
                    </td>
                  );
                })}
              </tr>
              );
            })}
            {!empleadosVisibles.length && (
              <tr>
                <td colSpan={filtroDia !== -1 ? 2 : 8} style={{ 
                  textAlign: 'center', 
                  padding: 24, 
                  color: '#6c757d',
                  fontStyle: 'italic'
                }}>
                  👥 {filtroSede || filtroDia !== -1 
                    ? `No hay empleados que coincidan con los filtros${filtroSede ? ` (Sede: ${filtroSede})` : ''}${filtroDia !== -1 ? ` (Día: ${DIAS[filtroDia]})` : ''}.` 
                    : 'No hay empleados seleccionados para mostrar.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </>
      )}

      {/* Modal copiar horarios */}
      {modalCopiarOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            padding: 16,
            width: 400,
            maxWidth: '95vw'
          }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Copiar horarios de otra semana</h3>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
              Selecciona la semana origen. Se copiarán los turnos y eventos sobre la semana actual.
            </p>
            {horariosCopiables.length ? (
              <select
                style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 14, width: '100%', marginBottom: 12 }}
                value={copiarSeleccion}
                onChange={(e) => setCopiarSeleccion(e.target.value)}
              >
                {horariosCopiables.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.semanaInicio} → {h.semanaFin} ({(h.estado || "temporal").toUpperCase()})
                  </option>
                ))}
              </select>
            ) : (
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                No hay horarios guardados para copiar.
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="outline" onClick={()=> setModalCopiarOpen(false)}>Cancelar</Button>
              <Button
                onClick={()=> copiarHorarioSeleccionado(copiarSeleccion)}
                disabled={!copiarSeleccion}
              >
                Copiar aquí
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 🔒 Modal Resumen Empleados con Más de 7 Horas - Antes de Cierre Definitivo */}
      {modalResumen7Horas && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 16,
            boxShadow: '0 8px 40px rgba(0,0,0,0.35)',
            padding: 24,
            width: 600,
            maxWidth: '95vw',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Encabezado */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12, 
              marginBottom: 16,
              paddingBottom: 16,
              borderBottom: '2px solid #fee2e2'
            }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24
              }}>
                ⚠️
              </div>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#dc2626', margin: 0 }}>
                  Atención: Empleados con más de 7 horas
                </h3>
                <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0 0' }}>
                  Se detectaron empleados con jornadas superiores a 7 horas diarias
                </p>
              </div>
            </div>

            {/* Información del cierre */}
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderRadius: 10,
              padding: 12,
              marginBottom: 16,
              border: '1px solid #f59e0b'
            }}>
              <div style={{ fontSize: 13, color: '#92400e' }}>
                <strong>🔒 Cierre hasta:</strong> {limiteQuincenaResumen}
              </div>
              <div style={{ fontSize: 12, color: '#a16207', marginTop: 4 }}>
                Revisa el resumen antes de confirmar el cierre definitivo.
              </div>
            </div>

            {/* Lista de empleados */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              marginBottom: 16,
              border: '1px solid #e5e7eb',
              borderRadius: 10
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>
                      👤 Empleado
                    </th>
                    <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>
                      📅 Día
                    </th>
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb' }}>
                      ⏱️ Horas
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {empleadosMas7HorasLista.map((item, idx) => (
                    <tr 
                      key={`${item.empleadoId}-${item.dia}-${idx}`}
                      style={{ 
                        background: idx % 2 === 0 ? 'white' : '#f9fafb',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                    >
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ fontWeight: 500, color: '#1f2937' }}>{item.empleado}</div>
                        {item.documento && (
                          <div style={{ fontSize: 11, color: '#9ca3af' }}>{item.documento}</div>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ fontWeight: 500, color: '#374151' }}>{item.dia}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>{item.fecha}</div>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          borderRadius: 20,
                          background: item.horasExactas > 8 ? '#fecaca' : '#fed7aa',
                          color: item.horasExactas > 8 ? '#dc2626' : '#ea580c',
                          fontWeight: 600,
                          fontSize: 12
                        }}>
                          {item.horas}h
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Resumen */}
            <div style={{
              background: '#f1f5f9',
              borderRadius: 8,
              padding: 10,
              marginBottom: 16,
              fontSize: 12,
              color: '#475569'
            }}>
              <strong>📊 Resumen:</strong> {empleadosMas7HorasLista.length} registro(s) con más de 7 horas detectados
              en {[...new Set(empleadosMas7HorasLista.map(e => e.empleadoId))].length} empleado(s).
            </div>

            {/* Botones de acción */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                onClick={() => {
                  setModalResumen7Horas(false);
                  setEmpleadosMas7HorasLista([]);
                  setLimiteQuincenaResumen("");
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: '2px solid #d1d5db',
                  background: 'white',
                  color: '#374151',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#9ca3af';
                  e.currentTarget.style.background = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.background = 'white';
                }}
              >
                ✏️ Cancelar y Editar
              </button>
              <button
                onClick={() => {
                  setModalResumen7Horas(false);
                  setEmpleadosMas7HorasLista([]);
                  setLimiteQuincenaResumen("");
                  guardar("definitivo");
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: 14,
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(255, 107, 53, 0.35)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(255, 107, 53, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.35)';
                }}
              >
                🔒 Aceptar y Cerrar Horario
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal evento (por EMPLEADO y DÍA) */}
      {modalEvento && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            padding: 20,
            width: 480,
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1a1a2e' }}>🎉 Evento del empleado</h3>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
              Semana {semanaInicio} → {semanaFin}
            </div>
 
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#374151' }}>Fecha de la semana</label>
                <select
                  style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 10px', fontSize: 14, width: '100%' }}
                  value={evFecha}
                  onChange={(e)=> setEvFecha(e.target.value)}
                >
                  {fechasSemana.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#374151' }}>Tipo de evento</label>
                <select
                  style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 10px', fontSize: 14, width: '100%' }}
                  value={evTipo}
                  onChange={(e)=> {
                    const v = e.target.value;
                    setEvTipo(v);
                    const def = EVENTOS_BASE.find(x => x.id === v);
                    if (def) setEvColor(def.color);
                  }}
                >
                  <option value="">— Selecciona —</option>
                  {eventosPosibles.map(ev => <option key={ev.id} value={ev.id}>{ev.nombre}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: '#374151' }}>Color</label>
                <input
                  type="color"
                  style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: '4px', width: '100%', height: 38, cursor: 'pointer' }}
                  value={evColor}
                  onChange={(e)=> setEvColor(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <span style={{ fontSize: 11, color: '#64748b' }}>La celda completa se pintará con el color.</span>
              </div>
            </div>
 
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 20 }}>
              <button
                onClick={()=>{
                  setEventoDia(modalEvento.empId, evFecha, null);
                  alert("Evento eliminado para ese día.");
                }}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                🗑️ Quitar evento
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button 
                  onClick={()=> setModalEvento(null)}
                  style={{
                    background: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    padding: '10px 16px',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Cerrar
                </button>
                <button
                  onClick={()=>{
                    if (!evFecha || !evTipo) { alert("Elige fecha y tipo de evento."); return; }
                    const def = EVENTOS_BASE.find(x => x.id === evTipo);
                    const nombre = def ? def.nombre : evTipo;
                    setEventoDia(modalEvento.empId, evFecha, { eventoId: evTipo, nombre, color: evColor || "#fff3a6" });
                    setModalEvento(null);
                    alert("Evento configurado. Recuerda guardar el horario.");
                  }}
                  style={{
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    padding: '10px 16px',
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  ✓ Guardar evento
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
 
      {/* Modal selección de empleados (para ver/editar horarios) */}
      {selOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 8,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            padding: 16,
            width: 520,
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Elegir empleados (activos)</h3>
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, padding: 8 }}>
              {activos.map((e) => (
                <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, borderBottom: '1px solid #f3f4f6', padding: '6px 0', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(e.id)}
                    onChange={(ev) => {
                      const checked = ev.target.checked;
                      setSelectedIds((prev) =>
                        checked ? Array.from(new Set([...prev, e.id])) : prev.filter((x) => x !== e.id)
                      );
                    }}
                  />
                  <span>{e.nombre} – {e.tipoDoc} {e.documento}</span>
                </label>
              ))}
              {!activos.length && <div style={{ color: '#64748b', fontSize: 14 }}>No hay empleados activos.</div>}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="outline" onClick={() => setSelectedIds(activos.map(e=>e.id))}>
                  Seleccionar todos
                </Button>
                <Button variant="outline" onClick={() => setSelectedIds([])}>
                  Limpiar
                </Button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="outline" onClick={() => setSelOpen(false)}>Cancelar</Button>
                <Button onClick={() => setSelOpen(false)}>Aplicar</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
 
/* ====================== NÓMINA ====================== */
function Nomina({
  empleados,
  prestamos,
  liquidaciones,
  onGuardarNomina,
  nominasGuardadas = [],
  onMarcarCuotasUsadas,
  onAbono, // Para seleccionar cuotas a descontar
  onReabrirNomina,
  ausencias = [],
  horariosRepo = [], // <-- NUEVO
  tienePermiso = () => true,
  valoresAnuales = {}, // <-- NUEVOS VALORES DINÁMICOS
  empresaId = null, // <-- ID de la empresa actual
  sedes = [], // <-- Sedes para mostrar en desprendibles
}) {

  // Usar valores anuales dinámicos o valores por defecto
  const TARIFAS = {
    horaNormal: valoresAnuales.valorHoraNormal || 6471,
    horaExtraDiurna: valoresAnuales.valorExtraDiurna || 8088,
    horaNocturna: valoresAnuales.valorHoraNocturna || 8355,
    horaExtraNocturna: valoresAnuales.valorExtraNocturna || 10422,
    horaFestiva: valoresAnuales.valorHoraFestiva || 11323,
    horaExtraFestiva: valoresAnuales.valorHoraExtraFestiva || 13587,
  };
  // Auxiliar de transporte quincenal = auxTransporte mensual / 2
  const AUX_TRANSP_QUINCENAL_BASE_2025 = Math.round((valoresAnuales.auxTransporte || 200000) / 2);
  // Descuentos de salud y pensión basados en SMLV y porcentajes
  const SMLV = valoresAnuales.smlv || 1423500;
  const PORCENTAJE_SALUD = valoresAnuales.salud || 4;
  const PORCENTAJE_PENSION = valoresAnuales.pension || 4;
  const DESCUENTO_SALUD_QUINCENA = Math.round((SMLV * (PORCENTAJE_SALUD / 100)) / 2);
  const DESCUENTO_PENSION_QUINCENA = Math.round((SMLV * (PORCENTAJE_PENSION / 100)) / 2);
 
  const holidaysCache = React.useMemo(() => ({}), []);
 
  // ==== Mapa de "comidas" por quincena (persistente) ====
  const COMIDAS_KEY = "nomina_comidas_v1";
  const [comidas, setComidas] = React.useState(() => {
    try {
      const raw = localStorage.getItem(COMIDAS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  // Cargar comidas desde Supabase al iniciar
  React.useEffect(() => {
    async function cargarComidasDesdeSupabase() {
      try {
        const comidasSupabase = await cargarComidasQuincenaDeSupabase(empresaId);
        if (comidasSupabase && Object.keys(comidasSupabase).length > 0) {
          console.log("✅ Comidas cargadas desde Supabase:", Object.keys(comidasSupabase).length, "períodos");
          setComidas(prev => {
            // Combinar con datos locales, priorizando Supabase
            const merged = { ...prev, ...comidasSupabase };
            localStorage.setItem(COMIDAS_KEY, JSON.stringify(merged));
            return merged;
          });
        }
      } catch (e) {
        console.error("Error cargando comidas desde Supabase:", e);
      }
    }
    cargarComidasDesdeSupabase();
  }, [empresaId]);

  const [modalComidasOpen, setModalComidasOpen] = React.useState(false);
  const [modalCuotasNominaOpen, setModalCuotasNominaOpen] = React.useState(false); // Modal para seleccionar cuotas a descontar
  const [comidasModoHistorial, setComidasModoHistorial] = React.useState(false);
  const [comidasPeriodoConsulta, setComidasPeriodoConsulta] = React.useState("");
  
  // Función para toggle de cuotas (gemela de Prestamos)
  function togglePagoCuota(idPrestamo, cuotaId) {
    if (typeof onAbono === 'function') {
      onAbono(idPrestamo, cuotaId);
    }
  }

  // Préstamos con cuotas disponibles para descontar (igual que en Prestamos)
  const prestamosArray = Array.isArray(prestamos) ? prestamos : [];
  const prestamosConEmpleado = prestamosArray.map((p) => {
    const emp = empleados.find((e) => e.id === p.empleadoId);
    return { ...p, empleado: emp || null };
  }).filter((p) => p.empleado !== null);

  const prestamosConCuotasDisponiblesNomina = prestamosConEmpleado.filter((p) => {
    if (!p.empleado) return false;
    const cuotasDescontadas = (p.plan || []).filter((c) => c.noDescontable).length;
    const totalCuotas = p.cuotas || (p.plan || []).length || 1;
    return cuotasDescontadas < totalCuotas;
  });

  function claveComidas(mes, quin) { return `${mes}-Q${quin}`; }
  function getComidaEmpleado(empId) {
    // Solo devolver valor si el empleado pertenece a la empresa actual
    const emp = empleados.find(e => e.id === empId);
    if (!emp) return 0;
    const key = claveComidas(mesNomina, quincena);
    return Number(comidas?.[key]?.[empId] || 0);
  }
  
  // Debounce para sincronizar comidas con Supabase
  const comidasSyncTimeout = React.useRef(null);
  
  function setComidaEmpleado(empId, valor) {
    // Solo permitir setear si el empleado pertenece a la empresa actual
    const emp = empleados.find(e => e.id === empId);
    if (!emp) return;
    const v = Math.max(0, Number(valor || 0));
    const key = claveComidas(mesNomina, quincena);
    
    setComidas((prev) => {
      const next = { ...(prev || {}) };
      if (!next[key]) next[key] = {};
      next[key][empId] = v;
      localStorage.setItem(COMIDAS_KEY, JSON.stringify(next));
      
      // Sincronizar con Supabase después de un delay (debounce)
      if (comidasSyncTimeout.current) {
        clearTimeout(comidasSyncTimeout.current);
      }
      comidasSyncTimeout.current = setTimeout(() => {
        const comidasPeriodo = next[key] || {};
        const comidasConValor = {};
        Object.entries(comidasPeriodo).forEach(([empId, valor]) => {
          if (Number(valor) > 0) {
            comidasConValor[empId] = Number(valor);
          }
        });
        if (Object.keys(comidasConValor).length > 0) {
          guardarComidasQuincenaEnSupabase(key, comidasConValor, empresaId).catch(e => {
            console.error("Error sincronizando comidas:", e);
          });
        }
      }, 2000); // Esperar 2 segundos antes de sincronizar
      
      return next;
    });
  }

  // Función para guardar comidas de la quincena actual en Supabase
  async function guardarComidasEnSupabase() {
    const periodo = claveComidas(mesNomina, quincena);
    const comidasQuincena = comidas[periodo] || {};
    
    // Filtrar solo empleados con valores > 0
    const comidasConValor = {};
    Object.entries(comidasQuincena).forEach(([empId, valor]) => {
      if (Number(valor) > 0) {
        comidasConValor[empId] = Number(valor);
      }
    });
    
    if (Object.keys(comidasConValor).length === 0) {
      alert("No hay comidas registradas para guardar en esta quincena.");
      return;
    }
    
    try {
      const exito = await guardarComidasQuincenaEnSupabase(periodo, comidasConValor, empresaId);
      if (exito) {
        console.log(`✅ Comidas guardadas en Supabase para período ${periodo}`);
        alert(`✅ Comidas guardadas correctamente para ${periodo}\n${Object.keys(comidasConValor).length} empleados registrados.`);
      } else {
        alert("❌ Error al guardar las comidas en Supabase");
      }
    } catch (e) {
      console.error("Error guardando comidas:", e);
      alert("❌ Error al guardar las comidas: " + e.message);
    }
  }

  // Obtener lista de períodos de comidas disponibles para consultar
  function getPeriodosComidas() {
    return Object.keys(comidas).filter(k => k.match(/^\d{4}-\d{2}-Q[12]$/)).sort().reverse();
  }

  // Obtener comidas de un período específico
  function getComidasPeriodo(periodo) {
    return comidas[periodo] || {};
  }

  // Calcular total de comidas de un período (solo empleados de la empresa actual)
  function getTotalComidasPeriodo(periodo) {
    const comidasPeriodo = getComidasPeriodo(periodo);
    // Filtrar solo empleados de la empresa actual
    return Object.entries(comidasPeriodo)
      .filter(([empId]) => empleados.find(e => e.id === empId))
      .reduce((sum, [_, v]) => sum + Number(v || 0), 0);
  }

  // Obtener comida de un empleado para la quincena actual (solo si es de esta empresa)
  function getComidaEmpleadoFiltrado(empId) {
    // Verificar que el empleado pertenece a la empresa actual
    const emp = empleados.find(e => e.id === empId);
    if (!emp) return 0;
    const key = claveComidas(mesNomina, quincena);
    return Number(comidas?.[key]?.[empId] || 0);
  }

  // ==== OTROS DESCUENTOS (programados) ====
  const [otrosDescuentos, setOtrosDescuentos] = React.useState([]);
  const [modalOtrosDescuentosOpen, setModalOtrosDescuentosOpen] = React.useState(false);
  const [nuevoDescuento, setNuevoDescuento] = React.useState({
    empleadoId: "",
    concepto: "",
    tipo: "indefinido", // "meses", "indefinido" o "1_quincena"
    valorMensual: "",
    valorQuincenalDirecto: "", // Para cuando es 1 sola quincena
    valorTotal: "",
    meses: "",
  });

  // ==== AJUSTES DE NÓMINA ====
  const [ajustesNomina, setAjustesNomina] = React.useState([]);
  const [modalAjustesNominaOpen, setModalAjustesNominaOpen] = React.useState(false);
  const [nuevoAjuste, setNuevoAjuste] = React.useState({
    empleadoId: "",
    tipoBeneficiario: "empleado", // "empleado" o "empresa"
    tipoAjuste: "horario", // "horario", "descanso" o "horas"
    fechasAjuste: [], // Array de { fecha, tipoHora, cantidadHoras, descripcion, horario? }
    concepto: "",
  });
  const [fechaAjusteTemp, setFechaAjusteTemp] = React.useState({
    fecha: "",
    // Para tipo "horario" - ingresar horario y calcular automáticamente
    e1: "", // entrada turno 1
    s1: "", // salida turno 1
    e2: "", // entrada turno 2
    s2: "", // salida turno 2
    // Para tipo "horas" - ingresar manualmente
    tipoHora: "normal", // "normal", "nocturna", "festiva"
    cantidadHoras: "",
    descripcion: "",
  });

  // Horas de descanso configuradas (según se ve en calcularHorasQuincenaEmpleado usa 7)
  const HORAS_DESCANSO = 7;

  // Función para calcular horas de un horario (igual que en calcularHorasDiaEmpleado)
  function calcularHorasDeHorario(e1, s1, e2, s2, fecha) {
    const esFestivo = parseISO(fecha).getDay() === 0 || isHolidayCO(fecha, holidaysCache);
    
    const m1 = diffMinutes(e1, s1);
    const m2 = diffMinutes(e2, s2);
    const totMin = m1 + m2;
    
    if (totMin <= 0) {
      return { hNorm: 0, hNoct: 0, hFest: 0, esFestivo };
    }
    
    // Si es festivo, todas las horas son festivas
    if (esFestivo) {
      return { hNorm: 0, hNoct: 0, hFest: totMin / 60, esFestivo };
    }
    
    // Calcular horas normales y nocturnas
    let normMin = 0, noctMin = 0;
    if (m1 > 0 && e1 && s1) {
      const seg1 = segmentarNormalNocturna(hhmmToMinutes(e1), hhmmToMinutes(s1));
      normMin += seg1.normMin; noctMin += seg1.noctMin;
    }
    if (m2 > 0 && e2 && s2) {
      const seg2 = segmentarNormalNocturna(hhmmToMinutes(e2), hhmmToMinutes(s2));
      normMin += seg2.normMin; noctMin += seg2.noctMin;
    }
    
    return {
      hNorm: normMin / 60,
      hNoct: noctMin / 60,
      hFest: 0,
      esFestivo
    };
  }

  // Generar concepto automático basado en el tipo y las fechas
  function generarConceptoAjuste(tipoAjuste, fechasAjuste) {
    if (tipoAjuste === "descanso") {
      const fechasStr = fechasAjuste.map(f => f.fecha.slice(5).replace("-", "/")).join(", ");
      return `Descanso compensado ${fechasStr}`;
    }
    
    // Para horario y horas: mostrar resumen de horas por tipo
    const { totalNormales, totalNocturnas, totalFestivas } = calcularValorAjuste(fechasAjuste);
    const partes = [];
    if (totalNormales > 0) partes.push(`${totalNormales}h normales`);
    if (totalNocturnas > 0) partes.push(`${totalNocturnas}h nocturnas`);
    if (totalFestivas > 0) partes.push(`${totalFestivas}h festivas`);
    
    const fechasStr = fechasAjuste.map(f => f.fecha.slice(5).replace("-", "/")).join(", ");
    return `${partes.join(", ")} compensadas de ${fechasStr}`;
  }

  // Cargar ajustes de nómina desde Supabase
  React.useEffect(() => {
    async function cargarAjustes() {
      if (!empresaId) return;
      try {
        const ajustesSupabase = await cargarAjustesNominaDeSupabase(empresaId);
        if (ajustesSupabase && ajustesSupabase.length > 0) {
          console.log("✅ Ajustes de nómina cargados desde Supabase:", ajustesSupabase.length);
          setAjustesNomina(ajustesSupabase);
        }
      } catch (e) {
        console.error("Error cargando ajustes de nómina:", e);
      }
    }
    cargarAjustes();
  }, [empresaId]);

  // Función para obtener ajustes del período actual (solo pendientes)
  function getAjustesPeriodoActual() {
    const [anioMes, mesNum] = mesNomina.split("-");
    const periodoActual = `${anioMes}-${mesNum}-Q${quincena}`;
    
    // Filtrar por mesNomina/quincena O por periodoAplicacion (compatibilidad)
    return ajustesNomina.filter(a => {
      if (a.estado !== "pendiente") return false;
      
      // Método 1: Por mesNomina y quincena directamente
      if (a.mesNomina === mesNomina && String(a.quincena) === String(quincena)) {
        return true;
      }
      
      // Método 2: Por periodoAplicacion (para ajustes antiguos)
      if (a.periodoAplicacion === periodoActual) {
        return true;
      }
      
      return false;
    });
  }

  // Función para obtener ajustes por empleado
  function getAjustesPorEmpleado(empleadoId) {
    return getAjustesPeriodoActual().filter(a => a.empleadoId === empleadoId);
  }

  // Calcular valor total de un ajuste según tipo de horas y tarifas
  function calcularValorAjuste(fechasAjuste) {
    let totalNormales = 0, totalNocturnas = 0, totalFestivas = 0, valorTotal = 0;
    
    fechasAjuste.forEach(f => {
      const horas = Number(f.cantidadHoras) || 0;
      let valorHora = 0;
      
      switch (f.tipoHora) {
        case "normal":
          valorHora = TARIFAS.horaNormal;
          totalNormales += horas;
          break;
        case "nocturna":
          valorHora = TARIFAS.horaNocturna;
          totalNocturnas += horas;
          break;
        case "festiva":
          valorHora = TARIFAS.horaFestiva;
          totalFestivas += horas;
          break;
        default:
          valorHora = TARIFAS.horaNormal;
          totalNormales += horas;
      }
      
      valorTotal += horas * valorHora;
    });
    
    return { totalNormales, totalNocturnas, totalFestivas, valorTotal };
  }

  // Agregar fecha al ajuste según el tipo seleccionado
  function agregarFechaAjuste() {
    if (!fechaAjusteTemp.fecha) {
      alert("⚠️ Debes ingresar una fecha");
      return;
    }

    // Verificar si ya existe esa fecha
    if (nuevoAjuste.fechasAjuste.some(f => f.fecha === fechaAjusteTemp.fecha)) {
      alert("⚠️ Ya existe una entrada para esa fecha. Elimínala primero si deseas modificarla.");
      return;
    }

    let nuevaFecha = null;

    // === TIPO HORARIO: Calcular horas automáticamente desde el horario ===
    if (nuevoAjuste.tipoAjuste === "horario") {
      if (!fechaAjusteTemp.e1 || !fechaAjusteTemp.s1) {
        alert("⚠️ Debes ingresar al menos el primer turno (entrada y salida)");
        return;
      }

      const { hNorm, hNoct, hFest, esFestivo } = calcularHorasDeHorario(
        fechaAjusteTemp.e1, fechaAjusteTemp.s1,
        fechaAjusteTemp.e2, fechaAjusteTemp.s2,
        fechaAjusteTemp.fecha
      );

      const totalHoras = hNorm + hNoct + hFest;
      if (totalHoras <= 0) {
        alert("⚠️ El horario ingresado no genera horas");
        return;
      }

      // Crear entradas separadas por tipo de hora
      const fechasAgregar = [];
      const horarioStr = `${fechaAjusteTemp.e1}-${fechaAjusteTemp.s1}${fechaAjusteTemp.e2 ? ` / ${fechaAjusteTemp.e2}-${fechaAjusteTemp.s2}` : ''}`;
      
      if (hNorm > 0) {
        fechasAgregar.push({
          fecha: fechaAjusteTemp.fecha,
          tipoHora: "normal",
          cantidadHoras: Math.round(hNorm * 100) / 100,
          valorHora: TARIFAS.horaNormal,
          valorTotal: Math.round(hNorm * TARIFAS.horaNormal),
          descripcion: `Horario: ${horarioStr}`,
          horario: { e1: fechaAjusteTemp.e1, s1: fechaAjusteTemp.s1, e2: fechaAjusteTemp.e2, s2: fechaAjusteTemp.s2 },
        });
      }
      if (hNoct > 0) {
        fechasAgregar.push({
          fecha: fechaAjusteTemp.fecha,
          tipoHora: "nocturna",
          cantidadHoras: Math.round(hNoct * 100) / 100,
          valorHora: TARIFAS.horaNocturna,
          valorTotal: Math.round(hNoct * TARIFAS.horaNocturna),
          descripcion: `Horario: ${horarioStr}`,
          horario: { e1: fechaAjusteTemp.e1, s1: fechaAjusteTemp.s1, e2: fechaAjusteTemp.e2, s2: fechaAjusteTemp.s2 },
        });
      }
      if (hFest > 0) {
        fechasAgregar.push({
          fecha: fechaAjusteTemp.fecha,
          tipoHora: "festiva",
          cantidadHoras: Math.round(hFest * 100) / 100,
          valorHora: TARIFAS.horaFestiva,
          valorTotal: Math.round(hFest * TARIFAS.horaFestiva),
          descripcion: `${esFestivo ? 'Festivo' : ''} Horario: ${horarioStr}`,
          horario: { e1: fechaAjusteTemp.e1, s1: fechaAjusteTemp.s1, e2: fechaAjusteTemp.e2, s2: fechaAjusteTemp.s2 },
        });
      }

      setNuevoAjuste(prev => ({
        ...prev,
        fechasAjuste: [...prev.fechasAjuste, ...fechasAgregar]
      }));

      // Limpiar campos temporales
      setFechaAjusteTemp({
        fecha: "", e1: "", s1: "", e2: "", s2: "",
        tipoHora: "normal", cantidadHoras: "", descripcion: "",
      });
      return;
    }

    // === TIPO DESCANSO: Usar horas de descanso configuradas (7 horas normales) ===
    if (nuevoAjuste.tipoAjuste === "descanso") {
      nuevaFecha = {
        fecha: fechaAjusteTemp.fecha,
        tipoHora: "normal",
        cantidadHoras: HORAS_DESCANSO,
        valorHora: TARIFAS.horaNormal,
        valorTotal: HORAS_DESCANSO * TARIFAS.horaNormal,
        descripcion: `Descanso compensado`,
        esDescanso: true,
      };
    }

    // === TIPO HORAS: Ingreso manual de horas (sistema anterior) ===
    if (nuevoAjuste.tipoAjuste === "horas") {
      if (!fechaAjusteTemp.cantidadHoras) {
        alert("⚠️ Debes ingresar la cantidad de horas");
        return;
      }

      const horas = Number(fechaAjusteTemp.cantidadHoras);
      if (horas <= 0 || horas > 24) {
        alert("⚠️ La cantidad de horas debe ser entre 0.5 y 24");
        return;
      }

      let valorHora = TARIFAS.horaNormal;
      if (fechaAjusteTemp.tipoHora === "nocturna") valorHora = TARIFAS.horaNocturna;
      if (fechaAjusteTemp.tipoHora === "festiva") valorHora = TARIFAS.horaFestiva;

      nuevaFecha = {
        fecha: fechaAjusteTemp.fecha,
        tipoHora: fechaAjusteTemp.tipoHora,
        cantidadHoras: horas,
        valorHora,
        valorTotal: horas * valorHora,
        descripcion: fechaAjusteTemp.descripcion || "Ajuste manual",
      };
    }

    if (nuevaFecha) {
      setNuevoAjuste(prev => ({
        ...prev,
        fechasAjuste: [...prev.fechasAjuste, nuevaFecha]
      }));
    }

    // Limpiar campos temporales
    setFechaAjusteTemp({
      fecha: "", e1: "", s1: "", e2: "", s2: "",
      tipoHora: "normal", cantidadHoras: "", descripcion: "",
    });
  }

  // Eliminar fecha del ajuste temporal
  function eliminarFechaAjuste(fecha) {
    setNuevoAjuste(prev => ({
      ...prev,
      fechasAjuste: prev.fechasAjuste.filter(f => f.fecha !== fecha)
    }));
  }

  // Guardar ajuste de nómina
  async function guardarAjusteNomina() {
    if (!nuevoAjuste.empleadoId) {
      alert("⚠️ Debes seleccionar un empleado");
      return;
    }
    if (nuevoAjuste.fechasAjuste.length === 0) {
      alert("⚠️ Debes agregar al menos una fecha");
      return;
    }

    const empleado = empleados.find(e => e.id === nuevoAjuste.empleadoId);
    const { totalNormales, totalNocturnas, totalFestivas, valorTotal } = calcularValorAjuste(nuevoAjuste.fechasAjuste);
    const fechas = [...new Set(nuevoAjuste.fechasAjuste.map(f => f.fecha))]; // Fechas únicas

    // Verificar si ya existe un ajuste para este empleado y alguna de estas fechas
    const ajustesExistentes = await verificarAjusteExistente(empresaId, nuevoAjuste.empleadoId, fechas);
    
    if (ajustesExistentes.length > 0) {
      const fechasConflicto = ajustesExistentes.flatMap(a => 
        (a.fechasAjuste || []).filter(fa => fechas.includes(fa.fecha)).map(fa => fa.fecha)
      );
      const continuar = window.confirm(
        `⚠️ AJUSTE EXISTENTE DETECTADO\n\n` +
        `Ya existe un ajuste para ${empleado?.nombre || "este empleado"} en las siguientes fechas:\n` +
        `${[...new Set(fechasConflicto)].join(", ")}\n\n` +
        `Período: ${ajustesExistentes[0].periodoAplicacion}\n` +
        `Estado: ${ajustesExistentes[0].estado}\n\n` +
        `¿Deseas CONTINUAR de todas formas?`
      );
      if (!continuar) return;
    }

    const [anioMes] = mesNomina.split("-");
    const periodoActual = `${anioMes}-${String(mesNomina.split("-")[1]).padStart(2, "0")}-Q${quincena}`;
    
    // Generar concepto automático si no se proporcionó uno manual
    const conceptoFinal = nuevoAjuste.concepto || generarConceptoAjuste(nuevoAjuste.tipoAjuste, nuevoAjuste.fechasAjuste);
    
    const ajuste = {
      id: ensureUuid(),
      empleadoId: nuevoAjuste.empleadoId,
      empleadoNombre: empleado?.nombre || "Desconocido",
      tipoBeneficiario: nuevoAjuste.tipoBeneficiario,
      tipoAjuste: nuevoAjuste.tipoAjuste,
      fechasAjuste: nuevoAjuste.fechasAjuste,
      totalHorasNormales: totalNormales,
      totalHorasNocturnas: totalNocturnas,
      totalHorasFestivas: totalFestivas,
      valorTotal,
      concepto: conceptoFinal,
      periodoAplicacion: periodoActual,
      mesNomina: mesNomina,
      quincena: quincena,
      anio: parseInt(anioMes),
      estado: "pendiente",
      aplicadoEnNomina: false,
    };

    // Guardar en Supabase
    const resultado = await guardarAjusteNominaEnSupabase(ajuste, empresaId);
    
    if (resultado) {
      setAjustesNomina(prev => [ajuste, ...prev]);
      setNuevoAjuste({
        empleadoId: "",
        tipoBeneficiario: "empleado",
        tipoAjuste: "horario",
        fechasAjuste: [],
        concepto: "",
      });
      alert(`✅ Ajuste guardado correctamente\n\n${nuevoAjuste.tipoBeneficiario === "empleado" ? "A FAVOR DEL EMPLEADO" : "A FAVOR DE LA EMPRESA"}\nValor: $${valorTotal.toLocaleString()}`);
    } else {
      alert("❌ Error al guardar el ajuste. Intenta nuevamente.");
    }
  }

  // Cancelar un ajuste
  async function cancelarAjuste(ajusteId) {
    const ajuste = ajustesNomina.find(a => a.id === ajusteId);
    if (!ajuste) return;

    const confirmar = window.confirm(
      `⚠️ ¿Cancelar este ajuste?\n\n` +
      `Empleado: ${ajuste.empleadoNombre}\n` +
      `Valor: $${ajuste.valorTotal?.toLocaleString()}\n` +
      `Tipo: ${ajuste.tipoBeneficiario === "empleado" ? "A favor del empleado" : "A favor de la empresa"}`
    );
    if (!confirmar) return;

    const resultado = await cancelarAjusteNomina(ajusteId);
    if (resultado) {
      setAjustesNomina(prev => prev.map(a => 
        a.id === ajusteId ? { ...a, estado: "cancelado" } : a
      ));
      alert("✅ Ajuste cancelado");
    } else {
      alert("❌ Error al cancelar el ajuste");
    }
  }

  // ==== RESÚMENES EJECUTIVOS DE NÓMINA ====
  const [modalResumenOpen, setModalResumenOpen] = React.useState(false);
  const [resumenActual, setResumenActual] = React.useState(null);
  const [modalBuscarResumenesOpen, setModalBuscarResumenesOpen] = React.useState(false);
  const [resumenesHistoricos, setResumenesHistoricos] = React.useState([]);
  const [filtroResumenes, setFiltroResumenes] = React.useState({
    quincena: "",
    mesNumero: "",
    anio: new Date().getFullYear(),
  });
  const [cargandoResumenes, setCargandoResumenes] = React.useState(false);

  // Función para cargar todos los resúmenes (sin filtros)
  async function cargarTodosLosResumenes() {
    setCargandoResumenes(true);
    try {
      const resultados = await cargarResumenesNominaDeSupabase(empresaId, { anio: filtroResumenes.anio });
      setResumenesHistoricos(resultados);
    } catch (e) {
      console.error("Error cargando resúmenes:", e);
    } finally {
      setCargandoResumenes(false);
    }
  }

  // Función para eliminar un resumen ejecutivo
  async function eliminarResumenEjecutivo(resumen) {
    const confirmacion = window.confirm(
      `⚠️ ELIMINAR RESUMEN EJECUTIVO\n\n` +
      `Período: ${resumen.mes || resumen.periodo}\n` +
      `Quincena: Q${resumen.quincena}\n` +
      `Empleados: ${resumen.totalEmpleados}\n` +
      `Total Neto: $${(resumen.totalNeto || 0).toLocaleString()}\n\n` +
      `Esta acción no se puede deshacer.\n¿Deseas continuar?`
    );
    if (!confirmacion) return;

    try {
      const exito = await eliminarResumenNominaDeSupabase(resumen.id);
      if (exito) {
        setResumenesHistoricos(prev => prev.filter(r => r.id !== resumen.id));
        alert("✅ Resumen eliminado correctamente");
      } else {
        alert("❌ Error al eliminar el resumen");
      }
    } catch (e) {
      console.error("Error eliminando resumen:", e);
      alert("❌ Error al eliminar: " + e.message);
    }
  }

  // Cargar resúmenes automáticamente cuando se abre el modal
  React.useEffect(() => {
    if (modalBuscarResumenesOpen && resumenesHistoricos.length === 0) {
      cargarTodosLosResumenes();
    }
  }, [modalBuscarResumenesOpen]);

  // Función para buscar resúmenes históricos
  async function buscarResumenesHistoricos() {
    setCargandoResumenes(true);
    try {
      const filtros = {};
      if (filtroResumenes.quincena) filtros.quincena = parseInt(filtroResumenes.quincena);
      if (filtroResumenes.mesNumero) filtros.mesNumero = parseInt(filtroResumenes.mesNumero);
      if (filtroResumenes.anio) filtros.anio = parseInt(filtroResumenes.anio);
      
      const resultados = await cargarResumenesNominaDeSupabase(empresaId, filtros);
      setResumenesHistoricos(resultados);
    } catch (e) {
      console.error("Error buscando resúmenes:", e);
      alert("Error al buscar resúmenes de nómina");
    } finally {
      setCargandoResumenes(false);
    }
  }

  // Función para ver detalle de un resumen
  function verDetalleResumen(resumen) {
    setResumenActual(resumen);
    setModalResumenOpen(true);
  }

  // Función para imprimir resumen ejecutivo
  function imprimirResumenEjecutivo(resumen) {
    if (!resumen) return;
    
    const win = window.open("", "_blank");
    if (!win) {
      alert("Por favor habilite las ventanas emergentes para imprimir");
      return;
    }
    
    win.document.write(`
      <html>
      <head>
        <title>Resumen Ejecutivo - ${resumen.mes} Q${resumen.quincena}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          h1 { color: #ff6b35; font-size: 22px; border-bottom: 3px solid #ffc53d; padding-bottom: 10px; }
          h2 { color: #333; font-size: 16px; margin-top: 20px; }
          .info { background: #f8f9fa; padding: 15px; border-radius: 8px; margin: 15px 0; }
          .info p { margin: 5px 0; font-size: 14px; }
          .totales { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin: 20px 0; }
          .total-box { background: linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%); color: white; padding: 15px; border-radius: 8px; text-align: center; }
          .total-box.verde { background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%); }
          .total-box.rojo { background: linear-gradient(135deg, #dc2626 0%, #f87171 100%); }
          .total-box h3 { margin: 0; font-size: 14px; opacity: 0.9; }
          .total-box p { margin: 5px 0 0; font-size: 22px; font-weight: bold; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 13px; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background: #f8f8f8; font-weight: 600; }
          .right { text-align: right; }
          .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #ddd; font-size: 11px; color: #666; }
        </style>
      </head>
      <body>
        <h1>📊 Resumen Ejecutivo de Nómina</h1>
        
        <div class="info">
          <p><strong>📅 Período:</strong> ${resumen.mes} - Quincena ${resumen.quincena}</p>
          <p><strong>📆 Rango:</strong> ${resumen.rangoInicio || 'N/A'} a ${resumen.rangoFin || 'N/A'}</p>
          <p><strong>👥 Total Empleados:</strong> ${resumen.totalEmpleados}</p>
          <p><strong>🕐 Generado:</strong> ${new Date(resumen.fechaGeneracion).toLocaleString()}</p>
        </div>
        
        <div class="totales">
          <div class="total-box">
            <h3>💰 Total Devengado</h3>
            <p>$${resumen.totalDevengado?.toLocaleString() || 0}</p>
          </div>
          <div class="total-box rojo">
            <h3>📉 Total Descuentos</h3>
            <p>$${resumen.totalDescuentos?.toLocaleString() || 0}</p>
          </div>
          <div class="total-box verde" style="grid-column: span 2;">
            <h3>💵 Total Neto a Pagar</h3>
            <p>$${resumen.totalNeto?.toLocaleString() || 0}</p>
          </div>
        </div>
        
        <h2>📋 Desglose de Devengados</h2>
        <table>
          <tr><td>Salarios Base</td><td class="right">$${(resumen.totalDevengado - (resumen.totalAuxTransporte || 0) - (resumen.totalBonificaciones || 0) - (resumen.totalIncapacidades || 0)).toLocaleString()}</td></tr>
          <tr><td>Auxilio de Transporte</td><td class="right">$${(resumen.totalAuxTransporte || 0).toLocaleString()}</td></tr>
          <tr><td>Bonificaciones</td><td class="right">$${(resumen.totalBonificaciones || 0).toLocaleString()}</td></tr>
          <tr><td>Incapacidades</td><td class="right">$${(resumen.totalIncapacidades || 0).toLocaleString()}</td></tr>
          <tr style="font-weight: bold; background: #fff3e0;"><td>TOTAL DEVENGADO</td><td class="right">$${resumen.totalDevengado?.toLocaleString() || 0}</td></tr>
        </table>
        
        <h2>📋 Desglose de Descuentos</h2>
        <table>
          <tr><td>Salud (4%)</td><td class="right">$${(resumen.totalSalud || 0).toLocaleString()}</td></tr>
          <tr><td>Pensión (4%)</td><td class="right">$${(resumen.totalPension || 0).toLocaleString()}</td></tr>
          <tr><td>Préstamos</td><td class="right">$${(resumen.totalPrestamos || 0).toLocaleString()}</td></tr>
          <tr><td>Comidas</td><td class="right">$${(resumen.totalComidas || 0).toLocaleString()}</td></tr>
          <tr><td>Otros Descuentos</td><td class="right">$${(resumen.totalOtrosDescuentos || 0).toLocaleString()}</td></tr>
          <tr style="font-weight: bold; background: #fee2e2;"><td>TOTAL DESCUENTOS</td><td class="right">$${resumen.totalDescuentos?.toLocaleString() || 0}</td></tr>
        </table>
        
        <h2>👥 Detalle por Empleado</h2>
        <table>
          <thead>
            <tr><th>Empleado</th><th>Documento</th><th>Cargo</th><th class="right">Bruto</th><th class="right">Neto</th></tr>
          </thead>
          <tbody>
            ${(resumen.empleadosDetalle || []).map(emp => `
              <tr>
                <td>${emp.nombre}</td>
                <td>${emp.documento}</td>
                <td>${emp.cargo || 'N/A'}</td>
                <td class="right">$${(emp.bruto || 0).toLocaleString()}</td>
                <td class="right">$${(emp.neto || 0).toLocaleString()}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          <p>Resumen ejecutivo generado automáticamente por el Sistema de Nómina</p>
          <p>ID: ${resumen.id} | Fecha de impresión: ${new Date().toLocaleString()}</p>
        </div>
        
        <script>window.print()</script>
      </body>
      </html>
    `);
    win.document.close();
  }

  // Cargar otros descuentos al iniciar - primero desde Supabase, luego localStorage como fallback
  React.useEffect(() => {
    async function cargarDescuentos() {
      try {
        // Intentar cargar desde Supabase primero
        const descuentosSupabase = await cargarOtrosDescuentosDeSupabase(empresaId);
        if (descuentosSupabase && descuentosSupabase.length > 0) {
          console.log("✅ Otros descuentos cargados desde Supabase:", descuentosSupabase.length);
          setOtrosDescuentos(descuentosSupabase);
          // Sincronizar con localStorage usando clave por empresa
          const localStorageKey = empresaId ? `otros_descuentos_${empresaId}` : "otros_descuentos_v1";
          localStorage.setItem(localStorageKey, JSON.stringify(descuentosSupabase));
        } else {
          // Fallback a localStorage con clave por empresa
          const localStorageKey = empresaId ? `otros_descuentos_${empresaId}` : "otros_descuentos_v1";
          const saved = localStorage.getItem(localStorageKey);
          if (saved) {
            console.log("📦 Otros descuentos cargados desde localStorage para empresa:", empresaId);
            const parsed = JSON.parse(saved);
            // Filtrar solo los de esta empresa por seguridad
            const filtrados = empresaId ? parsed.filter(d => d.empresaId === empresaId) : parsed;
            setOtrosDescuentos(filtrados);
          } else {
            // Si no hay datos para esta empresa, limpiar el estado
            setOtrosDescuentos([]);
          }
        }
      } catch (e) {
        console.error("Error cargando otros descuentos:", e);
        // Fallback a localStorage en caso de error
        try {
          const saved = localStorage.getItem("otros_descuentos_v1");
          if (saved) {
            setOtrosDescuentos(JSON.parse(saved));
          }
        } catch (e2) {
          console.error("Error cargando desde localStorage:", e2);
        }
      }
    }
    cargarDescuentos();
  }, [empresaId]);

  function getOtrosDescuentosEmpleado(empId) {
    // Obtener todos los descuentos activos para este empleado de la empresa actual SOLAMENTE
    // No incluir descuentos sin empresaId para evitar mezclar datos entre empresas
    if (!empresaId) return []; // Si no hay empresa seleccionada, no mostrar nada
    return otrosDescuentos.filter(d => 
      d.empleadoId === empId && 
      d.activo !== false && 
      d.empresaId === empresaId // Solo descuentos de esta empresa específica
    );
  }

  // Obtener descuentos de la empresa actual (para mostrar en el modal)
  function getOtrosDescuentosEmpresaActual() {
    // Solo mostrar descuentos que pertenecen a la empresa actual
    // No incluir descuentos sin empresaId para evitar mezclar datos entre empresas
    if (!empresaId) return []; // Si no hay empresa seleccionada, no mostrar nada
    return otrosDescuentos.filter(d => d.empresaId === empresaId);
  }

  function getTotalOtrosDescuentosQuincenal(empId) {
    // Suma de todos los descuentos quincenales activos
    const descuentosEmp = getOtrosDescuentosEmpleado(empId);
    return descuentosEmp.reduce((sum, d) => sum + (d.valorQuincenal || Math.round(d.valorMensual / 2)), 0);
  }

  function agregarOtroDescuento() {
    // Validar campos según el tipo
    if (nuevoDescuento.tipo === "1_quincena") {
      if (!nuevoDescuento.empleadoId || !nuevoDescuento.concepto || !nuevoDescuento.valorQuincenalDirecto) {
        alert("Por favor complete todos los campos obligatorios: Empleado, Concepto y Valor");
        return;
      }
    } else {
      if (!nuevoDescuento.empleadoId || !nuevoDescuento.concepto || !nuevoDescuento.valorMensual) {
        alert("Por favor complete todos los campos obligatorios: Empleado, Concepto y Valor Mensual");
        return;
      }
    }

    let valorMensual = Number(nuevoDescuento.valorMensual) || 0;
    let valorQuincenal = Math.round(valorMensual / 2);
    let mesesOriginales = 0;
    let mesesRestantes = 0;
    let valorTotal = 0;
    let tipoFinal = nuevoDescuento.tipo;

    // Tipo: 1 sola quincena
    if (nuevoDescuento.tipo === "1_quincena") {
      valorQuincenal = Number(nuevoDescuento.valorQuincenalDirecto) || 0;
      valorMensual = valorQuincenal; // El valor mensual es igual al quincenal (no se divide)
      mesesOriginales = 1; // 1 quincena = 1 "mes" en el sistema
      mesesRestantes = 1;
      valorTotal = valorQuincenal;
      tipoFinal = "meses"; // Se maneja como tipo meses con 1 restante
    }
    // Tipo: Por meses
    else if (nuevoDescuento.tipo === "meses") {
      if (nuevoDescuento.valorTotal) {
        // Tiene valor total neto
        valorTotal = Number(nuevoDescuento.valorTotal) || 0;
        mesesOriginales = Math.ceil(valorTotal / valorMensual);
        mesesRestantes = mesesOriginales;
      } else if (nuevoDescuento.meses) {
        // Tiene número de meses específico
        mesesOriginales = Number(nuevoDescuento.meses) || 1;
        mesesRestantes = mesesOriginales;
        valorTotal = valorMensual * mesesOriginales;
      } else {
        alert("Para descuento por meses, debe especificar el valor total neto o el número de meses");
        return;
      }
    }

    const nuevo = {
      id: ensureUuid(), // Generar UUID válido para Supabase
      empleadoId: nuevoDescuento.empleadoId,
      empresaId: empresaId, // <-- Guardar ID de la empresa actual
      concepto: nuevoDescuento.concepto,
      tipo: tipoFinal,
      valorMensual: valorMensual,
      valorQuincenal: valorQuincenal,
      valorTotal: valorTotal,
      mesesOriginales: mesesOriginales,
      mesesRestantes: mesesRestantes,
      activo: true,
      fechaCreacion: new Date().toISOString(),
      esQuincenaUnica: nuevoDescuento.tipo === "1_quincena", // Marcar si es solo 1 quincena
    };

    setOtrosDescuentos(prev => {
      const updated = [...prev, nuevo];
      // Usar clave de localStorage por empresa
      const localStorageKey = empresaId ? `otros_descuentos_${empresaId}` : "otros_descuentos_v1";
      localStorage.setItem(localStorageKey, JSON.stringify(updated));
      return updated;
    });

    // Guardar en Supabase para persistencia
    guardarOtroDescuentoEnSupabase(nuevo, empresaId)
      .then((guardado) => {
        if (guardado) {
          console.log("✅ Otro descuento guardado en Supabase:", nuevo.concepto);
        } else {
          console.error("❌ Error al guardar otro descuento en Supabase");
        }
      })
      .catch((err) => {
        console.error("❌ Error al guardar otro descuento:", err);
      });

    // Limpiar formulario
    setNuevoDescuento({
      empleadoId: "",
      concepto: "",
      tipo: "indefinido",
      valorMensual: "",
      valorQuincenalDirecto: "",
      valorTotal: "",
      meses: "",
    });

    const mensajeQuincena = nuevo.esQuincenaUnica 
      ? `Valor: $${nuevo.valorQuincenal.toLocaleString()} (se descontará completo en 1 sola quincena)`
      : `Valor quincenal: $${nuevo.valorQuincenal.toLocaleString()}`;
    alert(`✅ Descuento "${nuevo.concepto}" agregado para ${empleados.find(e => e.id === nuevo.empleadoId)?.nombre || "Empleado"}\n${mensajeQuincena}`);
  }

  function eliminarOtroDescuento(id) {
    if (!window.confirm("¿Está seguro de eliminar este descuento?")) return;
    
    setOtrosDescuentos(prev => {
      const updated = prev.map(d => d.id === id ? { ...d, activo: false } : d);
      const localStorageKey = empresaId ? `otros_descuentos_${empresaId}` : "otros_descuentos_v1";
      localStorage.setItem(localStorageKey, JSON.stringify(updated));
      return updated;
    });

    // Eliminar (marcar inactivo) en Supabase
    eliminarOtroDescuentoEnSupabase(id)
      .then((exito) => {
        if (exito) {
          console.log("✅ Otro descuento marcado como inactivo en Supabase");
        }
      })
      .catch((err) => {
        console.error("❌ Error al eliminar otro descuento:", err);
      });
  }

  function reactivarDescuento(id) {
    setOtrosDescuentos(prev => {
      const updated = prev.map(d => d.id === id ? { ...d, activo: true } : d);
      const localStorageKey = empresaId ? `otros_descuentos_${empresaId}` : "otros_descuentos_v1";
      localStorage.setItem(localStorageKey, JSON.stringify(updated));
      return updated;
    });

    // Reactivar en Supabase
    const descuento = otrosDescuentos.find(d => d.id === id);
    if (descuento) {
      guardarOtroDescuentoEnSupabase({ ...descuento, activo: true }, empresaId)
        .then((guardado) => {
          if (guardado) {
            console.log("✅ Otro descuento reactivado en Supabase");
          }
        })
        .catch((err) => {
          console.error("❌ Error al reactivar otro descuento:", err);
        });
    }
  }
 
  const [horas, setHoras] = React.useState(
    empleados.map((e) => ({
      empleadoId: e.id,
      hNorm: 0,
      hNoct: 0,
      hFest: 0,
      hExDia: 0,
      hExNoc: 0,
      hExFest: 0,
    }))
  );
 
  // === Controles de período de nómina
  const [mesNomina, setMesNomina] = React.useState(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
  });
  const [quincena, setQuincena] = React.useState(1);
 
  // Helpers de período
  function daysInMonth(yyyy, mm) { return new Date(yyyy, mm, 0).getDate(); }
  function getFechasQuincena(yyyy, mm, half) {
    const last = daysInMonth(yyyy, mm);
    const start = half === 1 ? 1 : 16;
    const end = half === 1 ? 15 : last;
    const arr = [];
    for (let d = start; d <= end; d++) arr.push(toISO(new Date(yyyy, mm - 1, d)));
    return arr;
  }

  // Reconstruir el rango de quincena desde una fecha
  function getRangoFromFecha(fechaISO) {
    if (!fechaISO) return { inicio: "", fin: "" };
    const date = new Date(fechaISO);
    const yyyy = date.getFullYear();
    const mm = date.getMonth() + 1;
    const dd = date.getDate();
    const half = dd <= 15 ? 1 : 2;
    const fechas = getFechasQuincena(yyyy, mm, half);
    return {
      inicio: fechas[0],
      fin: fechas[fechas.length - 1]
    };
  }

  const fechasQuincena = React.useMemo(() => {
    const [y, m] = mesNomina.split("-").map(Number);
    return getFechasQuincena(y, m, quincena);
  }, [mesNomina, quincena]);
  const periodoActualKey = React.useMemo(() => {
    if (!fechasQuincena.length) return "";
    return `${fechasQuincena[0]}::${fechasQuincena[fechasQuincena.length - 1]}`;
  }, [fechasQuincena]);
 
  // === Empleados ya liquidados en este periodo (según repositorio)
  const liquidadosSet = React.useMemo(() => {
    const set = new Set();
    
    console.log("🔍 Calculando liquidadosSet:");
    console.log("  periodoActualKey:", periodoActualKey);
    console.log("  mesNomina:", mesNomina, "quincena:", quincena);
    console.log("  nominasGuardadas:", JSON.stringify(nominasGuardadas, null, 2));
    console.log("  liquidaciones:", JSON.stringify(liquidaciones, null, 2));
    
    // 1. Empleados que ya tienen nómina guardada para este período
    for (const n of nominasGuardadas || []) {
      const key = n?.rango?.inicio && n?.rango?.fin ? `${n.rango.inicio}::${n.rango.fin}` : "";
      console.log(`  Comparando nómina key "${key}" con periodoActualKey "${periodoActualKey}"`);
      if (key && key === periodoActualKey) {
        for (const it of n.items || []) {
          console.log(`    ✓ Agregando empleado ${it.empleadoId} a liquidadosSet (por nómina guardada)`);
          set.add(it.empleadoId);
        }
      }
    }
    
    // 2. Empleados que ya tienen una liquidación de nómina para este período
    const mesQuincenaActual = `${mesNomina}-Q${quincena}`;
    console.log("  Buscando liquidaciones para mesQuincenaActual:", mesQuincenaActual);
    for (const liq of liquidaciones || []) {
      console.log(`    Liquidación: periodo="${liq.periodo}", empleadoId="${liq.empleadoId}"`);
      // Verificar si la liquidación es de una nómina de este período
      if (liq.periodo && liq.periodo.includes(mesQuincenaActual)) {
        console.log(`    ✓ Agregando empleado ${liq.empleadoId} a liquidadosSet (por liquidación)`);
        set.add(liq.empleadoId);
      }
    }
    
    console.log("  liquidadosSet final:", Array.from(set));
    return set;
  }, [nominasGuardadas, periodoActualKey, liquidaciones, mesNomina, quincena]);
 
  // Selección de empleados (activos y no liquidados)
  const [selectOpen, setSelectOpen] = React.useState(false);
  const activosIds = React.useMemo(
    () => empleados.filter((e) => e.activo && !liquidadosSet.has(e.id)).map((e) => e.id),
    [empleados, liquidadosSet]
  );
  const [selectedIds, setSelectedIds] = React.useState(activosIds);
 
  React.useEffect(() => {
    setSelectedIds(activosIds);
  }, [activosIds, mesNomina, quincena]);

  // Log cuando nominasGuardadas cambia
  React.useEffect(() => {
    console.log("📋 nominasGuardadas actualizado:", JSON.stringify(nominasGuardadas, null, 2));
  }, [nominasGuardadas]);
  
  const empleadosSeleccionados = React.useMemo(
    () => empleados.filter((e) => e.activo && !liquidadosSet.has(e.id) && selectedIds.includes(e.id)),
    [empleados, selectedIds, liquidadosSet]
  );
 
  // planilla[empId][iso] = { e1, s1, e2, s2, descanso, noTrabajado }
  const [planilla, setPlanilla] = React.useState({});

  // Estado para expandir detalle de cuotas por empleado
  const [expandidosCuotas, setExpandidosCuotas] = React.useState({});
 
  // ====== Borrador en localStorage ======
  const DRAFT_KEY = "nomina_draft_v3";
  const draftLoadedRef = React.useRef(false);
  const [autoSavedAt, setAutoSavedAt] = React.useState("");
 
  React.useEffect(() => {
    if (draftLoadedRef.current) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.horas) setHoras(d.horas);
        if (d.planilla) setPlanilla(d.planilla);
        if (d.mesNomina) setMesNomina(d.mesNomina);
        if (d.quincena) setQuincena(d.quincena);
        if (d.autoSavedAt) setAutoSavedAt(d.autoSavedAt);
      }
    } catch {}
    draftLoadedRef.current = true;
  }, []);
 
  React.useEffect(() => {
    if (!draftLoadedRef.current) return;
    const payload = {
      horas,
      planilla,
      mesNomina,
      quincena,
      autoSavedAt: new Date().toISOString(),
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
    setAutoSavedAt(payload.autoSavedAt);
  }, [horas, planilla, mesNomina, quincena]);
 
  function guardarBorradorManual() {
    const payload = {
      horas,
      planilla,
      mesNomina,
      quincena,
      autoSavedAt: new Date().toISOString(),
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
    setAutoSavedAt(payload.autoSavedAt);
    alert("Borrador de nómina guardado.");
  }
  function descartarBorrador() {
    localStorage.removeItem(DRAFT_KEY);
    setAutoSavedAt("");
    alert("Borrador descartado de este navegador.");
  }
 
  // ----- Utilidades de fecha/hora y bloqueos -----
  function isSunday(iso) { return parseISO(iso).getDay() === 0; }
  function getPlanDia(empId, iso) {
    return (planilla?.[empId]?.[iso]) || { e1: "", s1: "", e2: "", s2: "", descanso: false, noTrabajado: false, incapacidad: false };
  }
  function setPlanDia(empId, iso, field, value) {
    setPlanilla((prev) => {
      const p = { ...(prev || {}) };
      if (!p[empId]) p[empId] = {};
      if (!p[empId][iso]) p[empId][iso] = { e1: "", s1: "", e2: "", s2: "", descanso: false, noTrabajado: false, incapacidad: false };
      p[empId][iso][field] = value;
      
      // Si se marca noTrabajado, incapacidad o descanso, limpiar las horas automáticamente
      if ((field === "noTrabajado" || field === "incapacidad") && value === true) {
        p[empId][iso].e1 = "";
        p[empId][iso].s1 = "";
        p[empId][iso].e2 = "";
        p[empId][iso].s2 = "";
        p[empId][iso].descanso = false;
      }
      if (field === "descanso" && value === true) {
        p[empId][iso].e1 = "";
        p[empId][iso].s1 = "";
        p[empId][iso].e2 = "";
        p[empId][iso].s2 = "";
        p[empId][iso].noTrabajado = false;
        p[empId][iso].incapacidad = false;
      }
      
      return p;
    });
  }
 
  function getAbsenceBlock(empId, iso) {
    const a = ausencias.find(
      (x) =>
        x.empleadoId === empId &&
        (x.tipo === "Vacaciones" || x.tipo === "Suspensión" || x.tipo === "Incapacidad") &&
        isDateInRange(iso, x.inicio, x.fin || x.inicio)
    );
    if (!a) return null;
    return a.tipo;
  }
  function hhmmToMinutes(hhmm) {
    if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return null;
    const [hh, mm] = hhmm.split(":").map(Number);
    return (hh * 60 + mm) % 1440;
  }
  function diffMinutes(hhmmStart, hhmmEnd) {
    const s = hhmmToMinutes(hhmmStart);
    const e = hhmmToMinutes(hhmmEnd);
    if (s == null || e == null) return 0;
    const d = e - s;
    return d >= 0 ? d : d + 1440;
  }
  function overlapMinutes(a, b, c, d) {
    const start = Math.max(a, c);
    const end = Math.min(b, d);
    return Math.max(0, end - start);
  }
  function segmentarNormalNocturna(startMin, endMin) {
    const parts =
      endMin >= startMin
        ? [[startMin, endMin]]
        : [[startMin, 1440], [0, endMin]];
 
    const NORM_START = 7 * 60;
    const NORM_END   = 19 * 60;
    const NOCT_1     = [0, 7 * 60];
    const NOCT_2     = [19 * 60, 1440];
    const DIUR       = [NORM_START, NORM_END];
 
    let normMin = 0, noctMin = 0;
    for (const [a, b] of parts) {
      normMin += overlapMinutes(a, b, DIUR[0], DIUR[1]);
      noctMin += overlapMinutes(a, b, NOCT_1[0], NOCT_1[1]);
      noctMin += overlapMinutes(a, b, NOCT_2[0], NOCT_2[1]);
    }
    return { normMin, noctMin };
  }
 
  // === cálculo por quincena ===
  function calcularHorasQuincenaEmpleado(empId) {
    let hNorm = 0, hNoct = 0, hFest = 0;
 
    for (const iso of fechasQuincena) {
      const d = getPlanDia(empId, iso);
      const bloqueo = getAbsenceBlock(empId, iso);
      const esFestivo = isSunday(iso) || isHolidayCO(iso, holidaysCache);
 
      // INCAPACIDAD desde ausencias O desde la planilla => NO suma horas
      if (bloqueo === "Incapacidad" || d.incapacidad) {
        continue;
      }
 
      // Bloqueo (vacaciones/suspensión) o noTrabajado marcado => NO suma horas
      if (bloqueo || d.noTrabajado) continue;

      if (d.descanso) { hNorm += 7; continue; }

      const m1 = diffMinutes(d.e1, d.s1);
      const m2 = diffMinutes(d.e2, d.s2);
      const totMin = m1 + m2;
      if (totMin <= 0) continue;

      if (esFestivo) {
        hFest += totMin / 60;
      } else {
        let normMin = 0, noctMin = 0;
        if (m1 > 0) {
          const seg1 = segmentarNormalNocturna(hhmmToMinutes(d.e1), hhmmToMinutes(d.s1));
          normMin += seg1.normMin; noctMin += seg1.noctMin;
        }
        if (m2 > 0) {
          const seg2 = segmentarNormalNocturna(hhmmToMinutes(d.e2), hhmmToMinutes(d.s2));
          normMin += seg2.normMin; noctMin += seg2.noctMin;
        }
        hNorm += normMin / 60;
        hNoct += noctMin / 60;
      }
    }

    let hExDia = 0, hExNoc = 0, hExFest = 0;
    const baseTotal = hNorm + hNoct + hFest;
    if (baseTotal > 110) {
      let exced = baseTotal - 110;
      const take = (from, need) => {
        const moved = Math.min(from, need);
        return [from - moved, need - moved, moved];
      };
      let moved;
      [hNorm, exced, moved] = take(hNorm, exced); hExDia += moved;
      if (exced > 0) { [hFest, exced, moved] = take(hFest, exced); hExDia += moved; }
      if (exced > 0) { [hNoct, exced, moved] = take(hNoct, exced); hExDia += moved; }
    }
 
    const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
    return {
      hNorm: r2(hNorm),
      hNoct: r2(hNoct),
      hFest: r2(hFest),
      hExDia: r2(hExDia),
      hExNoc: r2(hExNoc),
      hExFest: r2(hExFest),
    };
  }
 
  function contarDiasTrabajados(empId) {
    // (1) Auxilio de transporte: NO contar días de incapacidad
    let dias = 0;
    for (const iso of fechasQuincena) {
      const d = getPlanDia(empId, iso);
      const bloqueo = getAbsenceBlock(empId, iso);
      // No sumar días de incapacidad (desde ausencias O planilla)
      if (bloqueo === "Incapacidad" || d.incapacidad) continue;
      if (bloqueo && bloqueo !== "Incapacidad") continue;
 
      if (d.noTrabajado) continue;
      if (d.descanso) { dias += 1; continue; }
      const m1 = diffMinutes(d.e1, d.s1);
      const m2 = diffMinutes(d.e2, d.s2);
      const totMin = m1 + m2;
      if (totMin > 0) dias += 1;
    }
    return dias;
  }
 
  React.useEffect(() => {
    const nuevos = empleados.map((e) => {
      try { return { empleadoId: e.id, ...calcularHorasQuincenaEmpleado(e.id) }; }
      catch { return { empleadoId: e.id, hNorm: 0, hNoct: 0, hFest: 0, hExDia: 0, hExNoc: 0, hExFest: 0 }; }
    });
    setHoras(nuevos);
  }, [planilla, mesNomina, quincena, empleados, ausencias]); // eslint-disable-line
 
    // === APLICAR HORARIOS SEMANALES A LA PLANILLA (solo PARTIDO/DERECHO; OTRO no toca)
    React.useEffect(() => {
      if (!Array.isArray(horariosRepo) || !horariosRepo.length) return;
  
      const intersecta = (a0,a1,b0,b1) => !(parseISO(a1) < parseISO(b0) || parseISO(b1) < parseISO(a0));
      const rangQ = { ini: fechasQuincena[0], fin: fechasQuincena[fechasQuincena.length-1] };
  
      // Incluir horarios "definitivo" O "parcial" (para semanas que abarcan dos quincenas)
      const aplicables = horariosRepo
        .filter(h => (h.estado === "definitivo" || h.estado === "parcial") && intersecta(h.semanaInicio, h.semanaFin, rangQ.ini, rangQ.fin))
        .sort((a,b)=> (parseISO(b.semanaInicio)-parseISO(a.semanaInicio)));
  
      if (!aplicables.length) return;
  
      setPlanilla(prev => {
        const next = { ...(prev || {}) };
        
        // Primero: limpiar días de la planilla que corresponden a semanas de horarios aplicables
        // para que al re-aplicar, se reflejen los cambios (si un horario fue eliminado)
        for (const h of aplicables) {
          const fechasSemana = [];
          let d = parseISO(h.semanaInicio);
          for (let i=0;i<7;i++){ fechasSemana.push(toISO(d)); d = parseISO(addDaysLocal(toISO(d),1)); }
          const diasCerradosHorario = h.diasCerrados || [];
          
          for (const emp of empleados) {
            for (let idx=0; idx<7; idx++) {
              const iso = fechasSemana[idx];
              if (parseISO(iso) < parseISO(rangQ.ini) || parseISO(iso) > parseISO(rangQ.fin)) continue;
              if (h.estado === "parcial" && !diasCerradosHorario.includes(iso)) continue;
              
              const bloqueo = ausencias.find(
                (x) => x.empleadoId === emp.id && isDateInRange(iso, x.inicio, x.fin || x.inicio)
              );
              if (bloqueo) continue;
              
              // Limpiar el día para que se re-aplique desde el horario
              if (!next[emp.id]) next[emp.id] = {};
              next[emp.id][iso] = { e1:"",s1:"",e2:"",s2:"",descanso:false,noTrabajado:false,incapacidad:false };
            }
          }
        }
  
        // Segundo: aplicar los horarios
        for (const h of aplicables) {
          const fechasSemana = [];
          let d = parseISO(h.semanaInicio);
          for (let i=0;i<7;i++){ fechasSemana.push(toISO(d)); d = parseISO(addDaysLocal(toISO(d),1)); }
          
          // Obtener los días cerrados de este horario
          const diasCerradosHorario = h.diasCerrados || [];

          for (const emp of empleados) {
            const empCells = h.celdas?.[emp.id] || {};
            for (let idx=0; idx<7; idx++) {
              const cel = empCells[idx];
              if (!cel) continue;
  
              const iso = fechasSemana[idx];
              if (parseISO(iso) < parseISO(rangQ.ini) || parseISO(iso) > parseISO(rangQ.fin)) continue;
              
              // Para horarios parciales, solo aplicar días que estén cerrados
              if (h.estado === "parcial" && !diasCerradosHorario.includes(iso)) continue;
  
              const bloqueo = ausencias.find(
                (x) => x.empleadoId === emp.id && isDateInRange(iso, x.inicio, x.fin || x.inicio)
              );
              if (bloqueo) continue;
  
              if (cel.tipo === "PARTIDO" || cel.tipo === "DERECHO") {
                if (!next[emp.id]) next[emp.id] = {};
                if (!next[emp.id][iso]) next[emp.id][iso] = { e1:"",s1:"",e2:"",s2:"",descanso:false,noTrabajado:false };
                next[emp.id][iso].e1 = cel.e1 || "";
                next[emp.id][iso].s1 = cel.s1 || "";
                next[emp.id][iso].e2 = cel.tipo === "PARTIDO" ? (cel.e2 || "") : "";
                next[emp.id][iso].s2 = cel.tipo === "PARTIDO" ? (cel.s2 || "") : "";
                next[emp.id][iso].descanso = false;
                next[emp.id][iso].noTrabajado = false;
              }
              // tipo DESCANSO: marcar día como descanso
              if (cel.tipo === "DESCANSO") {
                if (!next[emp.id]) next[emp.id] = {};
                if (!next[emp.id][iso]) next[emp.id][iso] = { e1:"",s1:"",e2:"",s2:"",descanso:false,noTrabajado:false };
                next[emp.id][iso].descanso = true;
                next[emp.id][iso].noTrabajado = false;
                next[emp.id][iso].e1 = "";
                next[emp.id][iso].s1 = "";
                next[emp.id][iso].e2 = "";
                next[emp.id][iso].s2 = "";
              }
              // tipo NO_TRABAJO: marcar día como no trabajado
              if (cel.tipo === "NO_TRABAJO") {
                if (!next[emp.id]) next[emp.id] = {};
                if (!next[emp.id][iso]) next[emp.id][iso] = { e1:"",s1:"",e2:"",s2:"",descanso:false,noTrabajado:false };
                next[emp.id][iso].noTrabajado = true;
                next[emp.id][iso].descanso = false;
                next[emp.id][iso].e1 = "";
                next[emp.id][iso].s1 = "";
                next[emp.id][iso].e2 = "";
                next[emp.id][iso].s2 = "";
              }
              // tipo OTRO: aplicar Otro con horas u otras opciones
              if (cel.tipo === "OTRO") {
                if (!next[emp.id]) next[emp.id] = {};
                if (!next[emp.id][iso]) next[emp.id][iso] = { e1:"",s1:"",e2:"",s2:"",descanso:false,noTrabajado:false,incapacidad:false };
                
                if (cel.otro === "Otro" && cel.e1 && cel.s1) {
                  // Otro con horas personalizadas
                  next[emp.id][iso].e1 = cel.e1 || "";
                  next[emp.id][iso].s1 = cel.s1 || "";
                  next[emp.id][iso].e2 = "";
                  next[emp.id][iso].s2 = "";
                  next[emp.id][iso].descanso = false;
                  next[emp.id][iso].noTrabajado = false;
                  next[emp.id][iso].incapacidad = false;
                }
                // Incapacidad: marcar día sin horas y como incapacidad
                if (cel.otro === "Incapacidad") {
                  next[emp.id][iso].e1 = "";
                  next[emp.id][iso].s1 = "";
                  next[emp.id][iso].e2 = "";
                  next[emp.id][iso].s2 = "";
                  next[emp.id][iso].descanso = false;
                  next[emp.id][iso].noTrabajado = true;
                  next[emp.id][iso].incapacidad = true;
                }
                // Vacaciones, Suspensión: marcar día sin horas
                if (cel.otro === "Vacaciones" || cel.otro === "Suspensión") {
                  next[emp.id][iso].e1 = "";
                  next[emp.id][iso].s1 = "";
                  next[emp.id][iso].e2 = "";
                  next[emp.id][iso].s2 = "";
                  next[emp.id][iso].descanso = false;
                  next[emp.id][iso].noTrabajado = true;
                  next[emp.id][iso].incapacidad = false;
                }
              }
            }
          }
        }
        return next;
      });
    }, [horariosRepo, mesNomina, quincena, empleados, ausencias]);
  
 
  // === CSV (igual) ===
  function parseCSVText(text) {
    const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim().length);
    if (!lines.length) return [];
    if (lines[0].charCodeAt(0) === 0xfeff) lines[0] = lines[0].slice(1);
    const cand = [",", ";", "\t", "|"];
    let sep = ",", best = -1;
    for (const c of cand) {
      const n = (lines[0].match(new RegExp("\\" + c, "g")) || []).length;
      if (n > best) { best = n; sep = c; }
    }
    const split = (line) => (sep === "\t" ? line.split("\t") : line.split(sep)).map((x) => x.trim());
    const headers = split(lines[0]).map((h) => h.toLowerCase());
    const idx = (name) => headers.findIndex((h) => h === name.toLowerCase());
 
    const iDoc = idx("documento");
    const iHNorm = idx("hnorm");
    const iHNoct = idx("hnoct");
    const iHFest = idx("hfest");
    const iHExD  = idx("hexdiur");
 
    if (iDoc === -1) { alert('El archivo debe incluir "documento".'); return []; }
 
    const toNum = (v) => {
      const t = (v ?? "").toString().replace(/\./g, "").replace(",", ".").trim();
      const n = Number(t);
      return Number.isFinite(n) ? n : 0;
    };
 
    return lines.slice(1).map((ln) => {
      const cols = split(ln);
      return {
        documento: (cols[iDoc] || "").toString().trim(),
        hNorm: toNum(cols[iHNorm]),
        hNoct: toNum(cols[iHNoct]),
        hFest: toNum(cols[iHFest]),
        hExDia: toNum(cols[iHExD]),
      };
    });
  }
  function handleCSVFile(ev) {
    const file = ev.target.files?.[0];
    if (!file) return;
    const fr = new FileReader();
    fr.onload = () => {
      const rows = parseCSVText(String(fr.result || ""));
      if (!rows.length) return;
      const byDoc = new Map(rows.map((r) => [r.documento, r]));
      setHoras((prev) =>
        prev.map((h) => {
          const emp = empleados.find((e) => e.id === h.empleadoId);
          if (!emp || !byDoc.has(emp.documento)) return h;
          const r = byDoc.get(emp.documento);
          return { ...h,
            hNorm: r.hNorm ?? h.hNorm,
            hNoct: r.hNoct ?? h.hNoct,
            hFest: r.hFest ?? h.hFest,
            hExDia: r.hExDia ?? h.hExDia,
          };
        })
      );
      alert("Archivo cargado. Horas aplicadas por documento.");
      ev.target.value = "";
    };
    fr.readAsText(file);
  }
 
  // ======= BONIFICACIÓN (con condiciones) =======
  function calcularBonificacion(empId) {
    const emp = empleados.find((e) => e.id === empId);
    if (!emp) return 0;
    const valor = Number(emp.valorBonificacion || 0);
    if (!valor) return 0;
 
    // Conteo de días por condición dentro de la quincena
    let vacHabiles = 0;
    let incapacidadDias = 0;
    let suspensionDias = 0;
    let noTrabajoMarcado = 0;
 
    for (const iso of fechasQuincena) {
      const ausencia = ausencias.find(
        (x) =>
          x.empleadoId === empId &&
          (x.tipo === "Vacaciones" || x.tipo === "Incapacidad" || x.tipo === "Suspensión") &&
          isDateInRange(iso, x.inicio, x.fin || x.inicio)
      );
 
      if (ausencia?.tipo === "Vacaciones") {
        if (isBusinessDayVac(iso, holidaysCache)) vacHabiles += 1; // solo L–S y sin festivos
      } else if (ausencia?.tipo === "Incapacidad") {
        incapacidadDias += 1; // corrido
      } else if (ausencia?.tipo === "Suspensión") {
        suspensionDias += 1;
      }
 
      const d = getPlanDia(empId, iso);
      if (!ausencia && d?.noTrabajado) noTrabajoMarcado += 1;
    }
 
    if (vacHabiles > 5) return 0;
    if (incapacidadDias > 5) return 0;
    if (suspensionDias >= 1) return 0;
    if (noTrabajoMarcado > 3) return 0;
 
    return valor;
  }

  // ======= INCAPACIDAD (cálculo de días y valor) =======
  // Valor diario de incapacidad = SMLV / 30 días
  function calcularIncapacidad(empId) {
    let diasIncapacidad = 0;
    
    for (const iso of fechasQuincena) {
      const ausencia = ausencias.find(
        (x) =>
          x.empleadoId === empId &&
          x.tipo === "Incapacidad" &&
          isDateInRange(iso, x.inicio, x.fin || x.inicio)
      );
      
      if (ausencia) {
        diasIncapacidad += 1;
      }
    }
    
    // Valor diario = SMLV / 30
    const valorDiario = Math.round(SMLV / 30);
    const valorTotal = diasIncapacidad * valorDiario;
    
    return {
      dias: diasIncapacidad,
      valorDiario,
      valorTotal
    };
  }
 
  // Cálculo monetario
  function calcBruto(hEmp) {
    return Math.round(
      hEmp.hNorm * TARIFAS.horaNormal +
      hEmp.hNoct * TARIFAS.horaNocturna +
      hEmp.hFest * TARIFAS.horaFestiva +
      hEmp.hExDia * TARIFAS.horaExtraDiurna +
      hEmp.hExNoc * TARIFAS.horaExtraNocturna +
      hEmp.hExFest * TARIFAS.horaExtraFestiva
    );
  }
  
  // Cálculo monetario SIN RECARGOS EXTRAS - Horas normales, nocturnas y festivas con recargo
  // Las horas extras se suman a su categoría correspondiente (extras diurnas → normales, extras nocturnas → nocturnas, extras festivas → festivas)
  function calcBrutoSinRecargos(hEmp) {
    // Horas normales = normales + extras diurnas (se pagan como hora normal)
    const horasNormales = (hEmp.hNorm || 0) + (hEmp.hExDia || 0);
    // Horas nocturnas = nocturnas + extras nocturnas (mantienen recargo nocturno)
    const horasNocturnas = (hEmp.hNoct || 0) + (hEmp.hExNoc || 0);
    // Horas festivas = festivas + extras festivas (mantienen recargo festivo)
    const horasFestivas = (hEmp.hFest || 0) + (hEmp.hExFest || 0);
    return Math.round(
      horasNormales * TARIFAS.horaNormal +
      horasNocturnas * TARIFAS.horaNocturna +
      horasFestivas * TARIFAS.horaFestiva
    );
  }
  
  // Función auxiliar para calcular horas agrupadas para modo sin recargos extras
  function calcularHorasAgrupadasSinRecargos(horas) {
    // Horas normales = normales + extras diurnas
    const horasNormales = (horas.hNorm || 0) + (horas.hExDia || 0);
    // Horas nocturnas = nocturnas + extras nocturnas
    const horasNocturnas = (horas.hNoct || 0) + (horas.hExNoc || 0);
    // Horas festivas = festivas + extras festivas
    const horasFestivas = (horas.hFest || 0) + (horas.hExFest || 0);
    return {
      horasNormales: horasNormales,
      horasNocturnas: horasNocturnas,
      horasFestivas: horasFestivas,
      totalHoras: horasNormales + horasNocturnas + horasFestivas
    };
  }
  
  function cuotasMarcadasParaDescuento(empleadoId) {
    const pres = prestamos.filter((p) => p.empleadoId === empleadoId);
    return pres.reduce((acc, p) => {
      const usado = p.plan.filter((c) => c.pagado && !c.noDescontable).reduce((s, c) => s + c.valor, 0);
      return acc + usado;
    }, 0);
  }

  // Obtener detalle completo de cuotas para guardar en nómina
  function obtenerDetallePrestamosEmpleado(empleadoId) {
    const pres = prestamos.filter((p) => p.empleadoId === empleadoId);
    const detalle = [];
    const cuotasIds = [];

    pres.forEach((p) => {
      const cuotasMarcadas = (p.plan || []).filter((c) => c.pagado && !c.noDescontable);
      if (cuotasMarcadas.length > 0) {
        const totalCuotas = cuotasMarcadas.reduce((s, c) => s + c.valor, 0);
        detalle.push({
          prestamoId: p.id,
          tipo: p.tipo,
          valorPrestamo: p.valor,
          cuotas: cuotasMarcadas.map((c) => ({
            id: c.id,
            numero: c.nro,
            valor: c.valor,
          })),
          totalDescontado: totalCuotas,
        });
        
        cuotasMarcadas.forEach((c) => {
          cuotasIds.push({
            prestamoId: p.id,
            cuotaId: c.id,
          });
        });
      }
    });

    return { detalle, cuotasIds };
  }
   // Recolectar las cuotas marcadas para descontar en esta nómina
  function recolectarCuotasMarcadas() {
    const cuotasParaMarcar = [];
    empleadosSeleccionados.forEach((e) => {
      const pres = prestamos.filter((p) => p.empleadoId === e.id);
      pres.forEach((p) => {
        (p.plan || []).forEach((c) => {
          if (c.pagado && !c.noDescontable) {
            cuotasParaMarcar.push({
              prestamoId: p.id,
              cuotaId: c.id,
              empleadoId: e.id,
            });
          }
        });
      });
    });
    return cuotasParaMarcar;
  }
 
  // ==== por día (para impresión individual) ====
  function calcularHorasDiaEmpleado(empId, iso) {
    const d = getPlanDia(empId, iso);
    const bloqueo = getAbsenceBlock(empId, iso);
    const esFestivo = parseISO(iso).getDay() === 0 || isHolidayCO(iso, holidaysCache);    const rangos = [
      d.e1 && d.s1 ? `${d.e1}–${d.s1}` : "",
      d.e2 && d.s2 ? `${d.e2}–${d.s2}` : "",
    ].filter(Boolean);
 
    let turnosTexto = "";
    if (bloqueo) turnosTexto = bloqueo;
    else if (d.incapacidad) turnosTexto = "Incapacidad";
    else if (d.noTrabajado) turnosTexto = "No trabajó";
    else if (d.descanso) turnosTexto = "Descanso";
    else turnosTexto = rangos.join(" / ");
 
    // Incapacidad desde ausencias O desde planilla => 0 horas
    if (bloqueo === "Incapacidad" || d.incapacidad) {
      return { fecha: iso, horario: turnosTexto, esFestivo, esDescanso: false, esNoTrabajado: false, esIncapacidad: true, hNorm: 0, hNoct: 0, hFest: 0, hExDia: 0, hExNoc: 0, hExFest: 0 };
    }
 
    if (bloqueo || d.noTrabajado) {
      return { fecha: iso, horario: turnosTexto, esFestivo, esDescanso: false, esNoTrabajado: !bloqueo, esIncapacidad: false, hNorm: 0, hNoct: 0, hFest: 0, hExDia: 0, hExNoc: 0, hExFest: 0 };
    }
    if (d.descanso) {
      return { fecha: iso, horario: turnosTexto, esFestivo, esDescanso: true, esNoTrabajado: false, esIncapacidad: false, hNorm: 8, hNoct: 0, hFest: 0, hExDia: 0, hExNoc: 0, hExFest: 0 };
    }
 
    const m1 = diffMinutes(d.e1, d.s1);
    const m2 = diffMinutes(d.e2, d.s2);
    const totMin = m1 + m2;
 
    if (totMin <= 0) {
      return { fecha: iso, horario: turnosTexto, esFestivo, esDescanso: false, esNoTrabajado: true, esIncapacidad: false, hNorm: 0, hNoct: 0, hFest: 0, hExDia: 0, hExNoc: 0, hExFest: 0 };
    }
 
    if (esFestivo) {
      return { fecha: iso, horario: turnosTexto, esFestivo, esDescanso: false, esNoTrabajado: false, esIncapacidad: false, hNorm: 0, hNoct: 0, hFest: totMin / 60, hExDia: 0, hExNoc: 0, hExFest: 0 };
    }
 
    let normMin = 0, noctMin = 0;
    if (m1 > 0 && d.e1 && d.s1) {
      const seg1 = segmentarNormalNocturna(hhmmToMinutes(d.e1), hhmmToMinutes(d.s1));
      normMin += seg1.normMin; noctMin += seg1.noctMin;
    }
    if (m2 > 0 && d.e2 && d.s2) {
      const seg2 = segmentarNormalNocturna(hhmmToMinutes(d.e2), hhmmToMinutes(d.s2));
      normMin += seg2.normMin; noctMin += seg2.noctMin;
    }
 
    return {
      fecha: iso,
      horario: turnosTexto,
      esFestivo,
      esDescanso: false,
      esNoTrabajado: false,
      esIncapacidad: false,
      hNorm: normMin / 60,
      hNoct: noctMin / 60,
      hFest: 0,
      hExDia: 0,
      hExNoc: 0,
      hExFest: 0
    };
  }
 
  // ==== VALIDACIÓN: sin días pendientes ====
  function hayDiasSinDiligenciar() {
    const diasFaltantes = [];
    for (const emp of empleadosSeleccionados) {
      for (const iso of fechasQuincena) {
        const d = getPlanDia(emp.id, iso);
        const bloqueo = getAbsenceBlock(emp.id, iso);
        if (bloqueo) continue;
        if (d.noTrabajado || d.descanso) continue;
        const m1 = diffMinutes(d.e1, d.s1);
        const m2 = diffMinutes(d.e2, d.s2);
        const tot = m1 + m2;
        if (tot <= 0) {
          diasFaltantes.push({
            empleado: emp.nombre,
            empleadoId: emp.id,
            fecha: iso,
            dia: DIAS[dayIndexMon0(iso)],
            datoPlanilla: d
          });
        }
      }
    }
    if (diasFaltantes.length > 0) {
      console.log('⚠️ DÍAS SIN DILIGENCIAR:', diasFaltantes.length);
      console.table(diasFaltantes);
      return true;
    }
    return false;
  }

  // ===== Impresión (total) =====
  function imprimirNomina(nomina) {
    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) return;
    
    // Verificar si la nómina fue liquidada sin recargos
    const sinRecargos = nomina.metodoLiquidacion === "sin_recargos";
    
    const rows = nomina.items.map((it) => {
      // Manejar detalleHoras faltante
      const horas = it.detalleHoras || it.horasTotales || {};
      const emp = empleados.find((e) => e.id === it.empleadoId);
      const empleadoNombre = it.empleadoNombre || emp?.nombre || "";
      const documento = it.documento || emp?.documento || "";
      const incap = it.incapacidad || { dias: 0, valorTotal: 0 };
      
      // Calcular horas según método de liquidación
      let columnasHoras;
      if (sinRecargos) {
        // En modo sin recargos extras: mostrar horas normales, nocturnas y festivas agrupadas
        const horasAgrupadas = calcularHorasAgrupadasSinRecargos(horas);
        columnasHoras = `<td style="text-align:right">${horasAgrupadas.horasNormales.toFixed(2)}</td>
        <td style="text-align:right">${horasAgrupadas.horasNocturnas.toFixed(2)}</td>
        <td style="text-align:right">${horasAgrupadas.horasFestivas.toFixed(2)}</td>`;
      } else {
        columnasHoras = `<td style="text-align:right">${(horas.hNorm || 0).toFixed(2)}</td>
        <td style="text-align:right">${(horas.hNoct || 0).toFixed(2)}</td>
        <td style="text-align:right">${(horas.hFest || 0).toFixed(2)}</td>
        <td style="text-align:right">${(horas.hExDia || 0).toFixed(2)}</td>
        <td style="text-align:right">${(horas.hExNoc || 0).toFixed(2)}</td>
        <td style="text-align:right">${(horas.hExFest || 0).toFixed(2)}</td>`;
      }
      
      return `
      <tr>
        <td>${empleadoNombre}</td>
        <td>${documento}</td>
        ${columnasHoras}
        <td style="text-align:right">$${(it.bruto || 0).toLocaleString()}</td>
        <td style="text-align:right; color: ${incap.dias > 0 ? '#be185d' : 'inherit'}">${incap.dias > 0 ? `${incap.dias}d / $${incap.valorTotal.toLocaleString()}` : '—'}</td>
        <td style="text-align:right">$${(it.adicionales?.bonificacion || 0).toLocaleString()}</td>
        <td style="text-align:right">$${(it.auxTransp || 0).toLocaleString()}</td>
        <td style="text-align:right">$${(it.descuentos?.prestamos || 0).toLocaleString()}</td>
        <td style="text-align:right">$${(it.descuentos?.salud || 0).toLocaleString()}</td>
        <td style="text-align:right">$${(it.descuentos?.pension || 0).toLocaleString()}</td>
        <td style="text-align:right">$${(it.descuentos?.comida || 0).toLocaleString()}</td>
        <td style="text-align:right">$${(it.descuentos?.otrosDescuentos || 0).toLocaleString()}</td>
        <td style="text-align:right">${it.ajustes ? (it.ajustes.sumaEmpleado > 0 && it.ajustes.restaEmpresa > 0 ? `$${(it.ajustes.sumaEmpleado || 0).toLocaleString()} / $${(it.ajustes.restaEmpresa || 0).toLocaleString()}` : it.ajustes.sumaEmpleado > 0 ? `$${(it.ajustes.sumaEmpleado || 0).toLocaleString()}` : it.ajustes.restaEmpresa > 0 ? `$${(it.ajustes.restaEmpresa || 0).toLocaleString()}` : '—') : '—'}</td>
        <td style="text-align:right"><strong>$${(it.neto || 0).toLocaleString()}</strong></td>
      </tr>
    `;
    }).join("");

    // Encabezados de horas según método de liquidación
    const encabezadosHoras = sinRecargos 
      ? `<th>H. Normales</th>
              <th>H. Nocturnas</th>
              <th>H. Festivas</th>`
      : `<th>H. norm</th>
              <th>H. noct</th>
              <th>H. fest</th>
              <th>H. ex diur</th>
              <th>H. ex noct</th>
              <th>H. ex fest</th>`;

    win.document.write(`
      <html>
      <head>
        <title>${nomina.nombre}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 16px; }
          h1 { font-size: 18px; margin: 0 0 8px; }
          .meta { font-size: 12px; color: #555; margin-bottom: 12px; }
          table { border-collapse: collapse; width: 100%; font-size: 12px; }
          th, td { border: 1px solid #ccc; padding: 6px; }
          th { background: #f2f2f2; text-align: left; }
        </style>
      </head>
      <body>
        <h1>${nomina.nombre}${sinRecargos ? ' (Sin Recargos Extras)' : ''}</h1>
        <div class="meta">
          Rango: ${nomina.rango.inicio} a ${nomina.rango.fin} &nbsp;|&nbsp;
          Generado: ${nomina.fechaCreacion}
        </div>
        <table>
          <thead>
            <tr>
              <th>Empleado</th>
              <th>Documento</th>
              ${encabezadosHoras}
              <th>Bruto</th>
              <th>Incap.</th>
              <th>Bonif.</th>
              <th>Aux. Transp.</th>
              <th>Préstamos</th>
              <th>Salud</th>
              <th>Pensión</th>
              <th>Comida</th>
              <th>Otros Desc.</th>
              <th>Ajuste</th>
              <th>Neto</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <script>window.print()</script>
      </body>
      </html>
    `);
    win.document.close();
  }
 
  function imprimirNominaIndividual(reg) {
    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) return;
 
    const detalle = reg.detallePorFecha || [];
 
    const filas = detalle
      .map((d) => {
        const totalDia =
          (d.hNorm ?? 0) +
          (d.hNoct ?? 0) +
          (d.hFest ?? 0) +
          (d.hExDia ?? 0) +
          (d.hExNoc ?? 0) +
          (d.hExFest ?? 0);
 
        const clase =
          d.horario === "Vacaciones"
            ? "vac"
            : d.horario === "Suspensión"
            ? "alerta"
            : d.horario === "Incapacidad"
            ? "alerta"
            : d.horario === "No trabajó"
            ? "alerta"
            : d.esFestivo
            ? "fest"
            : "";
 
        // Si es incapacidad, no mostramos las "8 h" para no confundir
        const totalVisible =
          d.horario === "Incapacidad"
            ? "—"
            : `${totalDia.toFixed(2)} h`;
 
        return `
          <tr class="${clase}">
            <td>${d.fecha}</td>
            <td>${d.horario || "—"}</td>
            <td style="text-align:right">${totalVisible}</td>
          </tr>
        `;
      })
      .join("");
 
    const tot = reg.horasTotales || {
      hNorm: 0,
      hNoct: 0,
      hFest: 0,
      hExDia: 0,
      hExNoc: 0,
      hExFest: 0,
    };
 
    // Verificar si fue liquidada sin recargos
    const sinRecargos = reg.metodoLiquidacion === "sin_recargos";
    const totalHorasTrabajadas = tot.hNorm + tot.hNoct + tot.hFest + tot.hExDia + tot.hExNoc + tot.hExFest;

    const bruto = reg.bruto || 0;
    const bonif = reg.adicionales?.bonificacion || 0;
    const auxT = reg.auxTransp || 0;
    const ajustes = reg.ajustes || { sumaEmpleado: 0, restaEmpresa: 0, detalle: [] };
    const dsc = reg.descuentos || {
      prestamos: 0,
      salud: 0,
      pension: 0,
      comida: 0,
    };
    const descTotal =
      (dsc.prestamos || 0) +
      (dsc.salud || 0) +
      (dsc.pension || 0) +
      (dsc.comida || 0);
    const neto = reg.neto || Math.round(bruto + auxT + bonif - descTotal);

    // Resumen de horas según método de liquidación
    let resumenHoras;
    if (sinRecargos) {
      const horasAgrupadas = calcularHorasAgrupadasSinRecargos(tot);
      resumenHoras = `<tr><td><strong>Horas trabajadas normales:</strong></td><td>${horasAgrupadas.horasNormales.toFixed(2)}</td></tr>
              <tr><td><strong>Horas trabajadas nocturnas:</strong></td><td>${horasAgrupadas.horasNocturnas.toFixed(2)}</td></tr>
              <tr><td><strong>Horas trabajadas festivas:</strong></td><td>${horasAgrupadas.horasFestivas.toFixed(2)}</td></tr>`;
    } else {
      resumenHoras = `<tr><td>Horas normales:</td><td>${tot.hNorm.toFixed(2)}</td></tr>
              <tr><td>Horas nocturnas:</td><td>${tot.hNoct.toFixed(2)}</td></tr>
              <tr><td>Horas festivas:</td><td>${tot.hFest.toFixed(2)}</td></tr>
              <tr><td>Horas extra diurnas:</td><td>${tot.hExDia.toFixed(2)}</td></tr>
              <tr><td>Horas extra nocturnas:</td><td>${tot.hExNoc.toFixed(2)}</td></tr>
              <tr><td>Horas extra festivas:</td><td>${tot.hExFest.toFixed(2)}</td></tr>`;
    }

    win.document.write(`
      <html>
        <head>
          <title>Desprendible de nómina${sinRecargos ? ' (Sin Recargos Extras)' : ''}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 16px; }
            h1 { font-size: 18px; margin: 0 0 8px; }
            .meta { font-size: 12px; color: #555; margin-bottom: 12px; }
            table { border-collapse: collapse; width: 100%; font-size: 12px; }
            th, td { border: 1px solid #ccc; padding: 6px; }
            th { background: #f2f2f2; text-align: left; }
            tr.vac { background: #e8fff0; }
            tr.fest { background: #fff5e0; }
            tr.alerta { background: #ffe0e0; }
            .resumen td { border: none; padding: 4px; }
          </style>
        </head>
        <body>
          <h1>Desprendible de nómina${sinRecargos ? ' (Sin Recargos Extras)' : ''}</h1>
          <div class="meta">
            <strong>Empleado:</strong> ${reg.empleadoNombre || "—"} – ${
      reg.documento || ""
    }<br/>
            <strong>Periodo:</strong> ${reg.rango?.inicio || ""} a ${
      reg.rango?.fin || ""
    }
          </div>

          <h3>Detalle por día</h3>
          <table>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Horario / Novedad</th>
                <th>Total horas visibles</th>
              </tr>
            </thead>
            <tbody>
              ${filas}
            </tbody>
          </table>

          <h3>Resumen de horas</h3>
          <table class="resumen">
            <tbody>
              ${resumenHoras}
              <tr><td>Descuento préstamos:</td><td>$${(dsc.prestamos || 0).toLocaleString()}</td></tr>
              <tr><td>Descuento salud:</td><td>$${(dsc.salud || 0).toLocaleString()}</td></tr>
              <tr><td>Descuento pensión:</td><td>$${(dsc.pension || 0).toLocaleString()}</td></tr>
              <tr><td>Descuento comida:</td><td>$${(dsc.comida || 0).toLocaleString()}</td></tr>
              <tr><td>Otros descuentos:</td><td>$${(dsc.otrosDescuentos || 0).toLocaleString()}</td></tr>
              ${ajustes.sumaEmpleado > 0 ? `<tr><td>Ajuste de nómina:</td><td>$${(ajustes.sumaEmpleado || 0).toLocaleString()}</td></tr>
              ${(ajustes.detalle || []).filter(a => a.beneficiario === 'empleado').map(a => `<tr style="font-size: 10px; color: #666;"><td colspan="2" style="padding-left: 12px;">↳ ${a.concepto}</td></tr>`).join('')}` : ''}
              ${ajustes.restaEmpresa > 0 ? `<tr><td>Ajuste de nómina:</td><td>$${(ajustes.restaEmpresa || 0).toLocaleString()}</td></tr>
              ${(ajustes.detalle || []).filter(a => a.beneficiario === 'empresa').map(a => `<tr style="font-size: 10px; color: #666;"><td colspan="2" style="padding-left: 12px;">↳ ${a.concepto}</td></tr>`).join('')}` : ''}
              <tr><td><strong>Neto a pagar:</strong></td><td><strong>$${neto.toLocaleString()}</strong></td></tr>
            </tbody>
          </table>
 
          <script>window.print()</script>
        </body>
      </html>
    `);
    win.document.close();
  }
 
  // ====== NUEVO: Desprendible de pago (TOTAL) ======
  function imprimirDesprendibleTotal(nomina) {
    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) return;
 
    // Verificar si la nómina fue liquidada sin recargos
    const sinRecargos = nomina.metodoLiquidacion === "sin_recargos";

    const bloques = nomina.items.map((it) => {
      // Buscar empleado actual, pero usar datos guardados en el item como fallback
      const empActual = empleados.find((e) => e.id === it.empleadoId);
      
      // Combinar datos: priorizar datos actuales del empleado, pero usar guardados si no existen
      const tipoDoc = empActual?.tipoDoc || it.empleadoTipoDoc || "CC";
      const cargo = empActual?.cargo || it.empleadoCargo || "";
      const fechaIngreso = empActual?.fechaIngreso || it.empleadoFechaIngreso || "";
      const sedeNombre = empActual?.sede || it.empleadoSede || "";
      
      // Obtener datos de la sede del empleado
      const sedeData = sedes.find(s => s.nombre === sedeNombre || s.id === sedeNombre) || {};
      
      // Usar detalleHoras como fuente principal ya que contiene las horas extras correctas
      const horasBase = it.horasTotales || { hNorm:0,hNoct:0,hFest:0,hExDia:0,hExNoc:0,hExFest:0 };
      const detalleH = it.detalleHoras || {};
      // Combinar: usar horasTotales para horas base, pero detalleHoras para extras si están disponibles
      const horas = {
        hNorm: horasBase.hNorm || 0,
        hNoct: horasBase.hNoct || 0,
        hFest: horasBase.hFest || 0,
        hExDia: detalleH.hExDia || horasBase.hExDia || 0,
        hExNoc: detalleH.hExNoc || horasBase.hExNoc || 0,
        hExFest: detalleH.hExFest || horasBase.hExFest || 0,
      };
      const extras = (horas.hExDia||0)+(horas.hExNoc||0)+(horas.hExFest||0);
      const totalHoras = (horas.hNorm||0)+(horas.hNoct||0)+(horas.hFest||0)+extras;
      const dsc = it.descuentos || { prestamos:0, salud:0, pension:0, comida:0, otrosDescuentos:0 };
      const otrosDesc = dsc.otrosDescuentos || 0;
      const descTotal = (dsc.prestamos||0)+(dsc.salud||0)+(dsc.pension||0)+(dsc.comida||0)+(otrosDesc);
      const bonif = it.adicionales?.bonificacion || 0;
      const incap = it.incapacidad || { dias: 0, valorTotal: 0 };
      
      // Ajustes de nómina
      const ajustes = it.ajustes || { sumaEmpleado: 0, restaEmpresa: 0, netoAjuste: 0, detalle: [], cantidad: 0 };
      const tieneAjustes = (ajustes.sumaEmpleado > 0 || ajustes.restaEmpresa > 0 || ajustes.cantidad > 0);
      
      // Calcular subtotales (incluyendo ajustes)
      const totalDevengado = (it.bruto||0) + (incap.valorTotal||0) + bonif + (it.auxTransp||0) + ajustes.sumaEmpleado;
      const totalDeducciones = descTotal + ajustes.restaEmpresa;
      const neto = it.neto ?? Math.round(totalDevengado - totalDeducciones);

      // Contenido de horas según método de liquidación
      let filasHoras;
      if (sinRecargos) {
        const horasAgrupadas = calcularHorasAgrupadasSinRecargos(horas);
        filasHoras = `
              <tr><td>Horas normales</td><td class="right">${horasAgrupadas.horasNormales.toFixed(2)} h</td></tr>
              <tr><td>Horas nocturnas</td><td class="right">${horasAgrupadas.horasNocturnas.toFixed(2)} h</td></tr>
              <tr><td>Horas festivas</td><td class="right">${horasAgrupadas.horasFestivas.toFixed(2)} h</td></tr>`;
      } else {
        filasHoras = `
              <tr><td>Horas normales</td><td class="right">${(horas.hNorm||0).toFixed(2)} h</td></tr>
              <tr><td>Horas nocturnas</td><td class="right">${(horas.hNoct||0).toFixed(2)} h</td></tr>
              <tr><td>Horas festivas</td><td class="right">${(horas.hFest||0).toFixed(2)} h</td></tr>
              <tr><td>Horas extras diurnas</td><td class="right">${(horas.hExDia||0).toFixed(2)} h</td></tr>
              <tr><td>Horas extras nocturnas</td><td class="right">${(horas.hExNoc||0).toFixed(2)} h</td></tr>
              <tr><td>Horas extras festivas</td><td class="right">${(horas.hExFest||0).toFixed(2)} h</td></tr>`;
      }

      return `
        <section class="slip">
          <div class="header-row">
            <div class="header-info">
              <div class="title">${nomina.nombre}${sinRecargos ? ' (Sin Recargos Extras)' : ''}</div>
              <div class="meta">Generado: ${nomina.fechaCreacion} · Rango: ${nomina.rango.inicio} a ${nomina.rango.fin}</div>
            </div>
            <div class="header-logo">
              <img src="/logo BIGBURGUER.jpg.jpg" alt="Logo" class="logo-img" onerror="this.style.display='none'" />
            </div>
          </div>
          
          ${sedeData.nombre ? `
          <div class="sede-info">
            <strong>${sedeData.nombre}</strong>
            ${sedeData.razonSocial ? `<span>| ${sedeData.razonSocial}</span>` : ''}
            ${sedeData.nit ? `<span>| NIT: ${sedeData.nit}</span>` : ''}
            ${sedeData.direccion ? `<span>| ${sedeData.direccion}</span>` : ''}
            ${sedeData.telefono ? `<span>| Tel: ${sedeData.telefono}</span>` : ''}
          </div>
          ` : ''}

          <div class="info-empleado">
            <table class="tabla-info">
              <tr>
                <td><strong>Empleado:</strong> ${it.empleadoNombre}</td>
                <td><strong>Documento:</strong> ${tipoDoc} ${it.documento}</td>
              </tr>
              <tr>
                <td><strong>Cargo:</strong> ${cargo || "—"}</td>
                <td><strong>Fecha ingreso:</strong> ${fechaIngreso || "—"}</td>
              </tr>
              <tr>
                <td colspan="2"><strong>Sede:</strong> ${sedeNombre || "—"}</td>
              </tr>
            </table>
          </div>

          <div class="grid2">
            <div class="box">
              <h4 class="seccion-titulo">📊 Resumen de Horas</h4>
              <table class="tabla-detalle">
                <tbody>
                  ${filasHoras}
                </tbody>
              </table>
            </div>
            <div class="box">
              <h4 class="seccion-titulo">💰 Devengado</h4>
              <table class="tabla-detalle">
                <tbody>
                  <tr><td>Salario bruto</td><td class="right">$${(it.bruto||0).toLocaleString()}</td></tr>
                  ${incap.dias > 0 ? `<tr><td>Incapacidad (${incap.dias} días)</td><td class="right">$${(incap.valorTotal||0).toLocaleString()}</td></tr>` : ''}
                  <tr><td>Bonificación</td><td class="right">$${bonif.toLocaleString()}</td></tr>
                  <tr><td>Aux. transporte</td><td class="right">$${(it.auxTransp||0).toLocaleString()}</td></tr>
                  ${tieneAjustes && ajustes.sumaEmpleado > 0 ? `
                  <tr>
                    <td>Ajuste de nómina</td>
                    <td class="right">$${ajustes.sumaEmpleado.toLocaleString()}</td>
                  </tr>
                  ${ajustes.detalle.filter(a => a.beneficiario === 'empleado').map(a => `
                  <tr style="font-size: 9px; color: #666;">
                    <td colspan="2" style="padding-left: 16px;">↳ ${a.concepto}</td>
                  </tr>
                  `).join('')}
                  ` : ''}
                  <tr class="subtotal"><td><strong>Total Devengado</strong></td><td class="right"><strong>$${totalDevengado.toLocaleString()}</strong></td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="box" style="margin-top: 10px;">
            <h4 class="seccion-titulo">📉 Deducciones</h4>
            <table class="tabla-detalle">
              <tbody>
                <tr><td>Préstamos</td><td class="right">$${(dsc.prestamos||0).toLocaleString()}</td></tr>
                <tr><td>Salud</td><td class="right">$${(dsc.salud||0).toLocaleString()}</td></tr>
                <tr><td>Pensión</td><td class="right">$${(dsc.pension||0).toLocaleString()}</td></tr>
                <tr><td>Comida</td><td class="right">$${(dsc.comida||0).toLocaleString()}</td></tr>
                <tr><td>Otros descuentos</td><td class="right">$${otrosDesc.toLocaleString()}</td></tr>
                ${tieneAjustes && ajustes.restaEmpresa > 0 ? `
                <tr>
                  <td>Ajuste de nómina</td>
                  <td class="right">$${ajustes.restaEmpresa.toLocaleString()}</td>
                </tr>
                ${ajustes.detalle.filter(a => a.beneficiario === 'empresa').map(a => `
                <tr style="font-size: 9px; color: #666;">
                  <td colspan="2" style="padding-left: 16px;">↳ ${a.concepto}</td>
                </tr>
                `).join('')}
                ` : ''}
                <tr class="subtotal"><td><strong>Total Deducciones</strong></td><td class="right"><strong>$${totalDeducciones.toLocaleString()}</strong></td></tr>
              </tbody>
            </table>
          </div>

          <div class="total-neto">
            <table class="tabla-neto">
              <tr>
                <td>Total Devengado</td>
                <td class="right">$${totalDevengado.toLocaleString()}</td>
              </tr>
              <tr>
                <td>(-) Total Deducciones</td>
                <td class="right">$${totalDeducciones.toLocaleString()}</td>
              </tr>
              <tr class="fila-neto">
                <td><strong>NETO A PAGAR</strong></td>
                <td class="right"><strong>$${neto.toLocaleString()}</strong></td>
              </tr>
            </table>
          </div>

          <div class="firma-conformidad">
            <p><strong>Recibí a conformidad:</strong></p>
            <div class="linea-firma"></div>
            <p class="nombre-empleado">${it.empleadoNombre || ""}</p>
            <p class="documento-empleado">C.C. ${it.documento || ""}</p>
          </div>
        </section>
      `;
    }).join('<div class="page-break"></div>');
 
    win.document.write(`
      <html>
      <head>
        <title>Desprendibles - ${nomina.nombre}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 16px; font-size: 12px; }
          .header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; }
          .header-info { flex: 1; }
          .header-logo { width: 80px; text-align: right; }
          .logo-img { width: 70px; height: 70px; border-radius: 50%; object-fit: cover; border: 2px solid #ffc53d; }
          .title { font-size: 16px; font-weight: bold; color: #333; }
          .meta { font-size: 11px; color: #555; margin-top: 2px; }
          .sede-info { border: 1px solid #ccc; border-radius: 6px; padding: 8px 12px; margin-bottom: 10px; font-size: 11px; color: #444; background-color: #f5f5f5; }
          .sede-info span { margin-left: 8px; }
          .info-empleado { margin-bottom: 10px; }
          .tabla-info { width: 100%; border-collapse: collapse; font-size: 12px; }
          .tabla-info td { padding: 4px 8px; border: 1px solid #ddd; }
          .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 10px 0; }
          .box { border: 1px solid #ddd; border-radius: 6px; padding: 10px; font-size: 12px; background: #fafafa; }
          .seccion-titulo { margin: 0 0 8px 0; font-size: 12px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
          .tabla-detalle { width: 100%; border-collapse: collapse; font-size: 11px; }
          .tabla-detalle td { padding: 4px 6px; border-bottom: 1px solid #eee; }
          .tabla-detalle tr.subtotal td { border-top: 2px solid #333; background: #f0f0f0; font-weight: bold; }
          .right { text-align: right; }
          .total-neto { margin-top: 12px; background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%); border-radius: 8px; padding: 12px; border: 1px solid #ccc; }
          .tabla-neto { width: 100%; border-collapse: collapse; color: #333; font-size: 12px; }
          .tabla-neto td { padding: 4px 8px; }
          .tabla-neto tr.fila-neto td { font-size: 14px; border-top: 2px solid #999; padding-top: 8px; }
          .firma-conformidad { margin-top: 25px; text-align: center; padding: 15px; }
          .firma-conformidad p { margin: 5px 0; font-size: 11px; color: #333; }
          .linea-firma { width: 60%; height: 1px; background: #333; margin: 20px auto 8px auto; }
          .nombre-empleado { font-weight: bold; font-size: 12px; }
          .documento-empleado { font-size: 10px; color: #666; }
          .page-break { page-break-after: always; }
          .slip { break-inside: avoid; padding-bottom: 20px; border-bottom: 2px dashed #ccc; margin-bottom: 20px; }
          @media print { 
            .page-break { display: block; } 
            .slip { border-bottom: none; }
            .total-neto { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        ${bloques}
        <script>window.print()</script>
      </body>
      </html>
    `);
    win.document.close();
  }
 
  // ====== NUEVO: Desprendible de pago (INDIVIDUAL, RESUMEN) ======
  function imprimirDesprendibleIndividual(registroIndividual) {
    const reg = registroIndividual;
    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) return;

    // Verificar si fue liquidada sin recargos
    const sinRecargos = reg.metodoLiquidacion === "sin_recargos";

    const bruto = reg.bruto || 0;
    const bonif = reg.adicionales?.bonificacion || 0;
    const auxT = reg.auxTransp || 0;
    const dsc = reg.descuentos || { prestamos: 0, salud: 0, pension: 0, comida: 0, otrosDescuentos: 0 };
    const otrosDesc = dsc.otrosDescuentos || 0;
    const descTotal = (dsc.prestamos || 0) + (dsc.salud || 0) + (dsc.pension || 0) + (dsc.comida || 0) + otrosDesc;
    const incap = reg.incapacidad || { dias: 0, valorTotal: 0 };
    
    // Ajustes de nómina
    const ajustes = reg.ajustes || { sumaEmpleado: 0, restaEmpresa: 0, netoAjuste: 0, detalle: [], cantidad: 0 };
    const tieneAjustes = (ajustes.sumaEmpleado > 0 || ajustes.restaEmpresa > 0 || ajustes.cantidad > 0);
    
    // Calcular total devengado (incluyendo ajustes)
    const totalDevengado = bruto + (incap.valorTotal || 0) + bonif + auxT + ajustes.sumaEmpleado;
    const totalDeducciones = descTotal + ajustes.restaEmpresa;
    const neto = reg.neto || Math.round(totalDevengado - totalDeducciones);

    // Usar detalleHoras como fuente principal ya que contiene las horas extras correctas
    const horasBase = reg.horasTotales || { hNorm: 0, hNoct: 0, hFest: 0, hExDia: 0, hExNoc: 0, hExFest: 0 };
    const detalleH = reg.detalleHoras || {};
    // Combinar: usar horasTotales para horas base, pero detalleHoras para extras si están disponibles
    const horas = {
      hNorm: horasBase.hNorm || 0,
      hNoct: horasBase.hNoct || 0,
      hFest: horasBase.hFest || 0,
      hExDia: detalleH.hExDia || horasBase.hExDia || 0,
      hExNoc: detalleH.hExNoc || horasBase.hExNoc || 0,
      hExFest: detalleH.hExFest || horasBase.hExFest || 0,
    };
    const extras = (horas.hExDia || 0) + (horas.hExNoc || 0) + (horas.hExFest || 0);
    const totalHoras = (horas.hNorm || 0) + (horas.hNoct || 0) + (horas.hFest || 0) + extras;
    
    // Obtener datos de la sede del empleado
    const sedeNombre = reg.empleado?.sede || "";
    const sedeData = sedes.find(s => s.nombre === sedeNombre || s.id === sedeNombre) || {};

    // Filas de horas según método de liquidación
    let filasHoras;
    if (sinRecargos) {
      const horasAgrupadas = calcularHorasAgrupadasSinRecargos(horas);
      filasHoras = `
            <tr><td>Horas normales</td><td class="right">${horasAgrupadas.horasNormales.toFixed(2)} h</td></tr>
            <tr><td>Horas nocturnas</td><td class="right">${horasAgrupadas.horasNocturnas.toFixed(2)} h</td></tr>
            <tr><td>Horas festivas</td><td class="right">${horasAgrupadas.horasFestivas.toFixed(2)} h</td></tr>`;
    } else {
      filasHoras = `
            <tr><td>Horas normales</td><td class="right">${(horas.hNorm || 0).toFixed(2)} h</td></tr>
            <tr><td>Horas nocturnas</td><td class="right">${(horas.hNoct || 0).toFixed(2)} h</td></tr>
            <tr><td>Horas festivas</td><td class="right">${(horas.hFest || 0).toFixed(2)} h</td></tr>
            <tr><td>Horas extras diurnas</td><td class="right">${(horas.hExDia || 0).toFixed(2)} h</td></tr>
            <tr><td>Horas extras nocturnas</td><td class="right">${(horas.hExNoc || 0).toFixed(2)} h</td></tr>
            <tr><td>Horas extras festivas</td><td class="right">${(horas.hExFest || 0).toFixed(2)} h</td></tr>`;
    }

    win.document.write(`
      <html>
      <head>
        <title>Desprendible - ${reg.empleado?.nombre || "Empleado"}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 16px; max-width: 700px; margin: 0 auto; font-size: 12px; }
          .header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 12px; }
          .header-info { flex: 1; }
          .header-logo { width: 80px; text-align: right; }
          .logo-img { width: 70px; height: 70px; border-radius: 50%; object-fit: cover; border: 2px solid #ffc53d; }
          h1 { font-size: 18px; margin: 0 0 6px; color: #333; }
          .meta { font-size: 11px; color: #555; line-height: 1.4; }
          .sede-info { border: 1px solid #ccc; border-radius: 6px; padding: 10px 12px; margin-bottom: 12px; font-size: 11px; color: #444; background-color: #f5f5f5; }
          .sede-info span { margin-left: 8px; }
          .info-empleado { margin-bottom: 10px; }
          .tabla-info { width: 100%; border-collapse: collapse; font-size: 12px; }
          .tabla-info td { padding: 4px 8px; border: 1px solid #ddd; }
          .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 10px 0; }
          .box { border: 1px solid #ddd; border-radius: 6px; padding: 10px; font-size: 12px; background: #fafafa; }
          .seccion-titulo { margin: 0 0 8px 0; font-size: 12px; color: #333; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
          .tabla-detalle { width: 100%; border-collapse: collapse; font-size: 11px; }
          .tabla-detalle td { padding: 4px 6px; border-bottom: 1px solid #eee; }
          .tabla-detalle tr.subtotal td { border-top: 2px solid #333; background: #f0f0f0; font-weight: bold; }
          .right { text-align: right; }
          .total-neto { margin-top: 12px; background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%); border-radius: 8px; padding: 12px; border: 1px solid #ccc; }
          .tabla-neto { width: 100%; border-collapse: collapse; color: #333; font-size: 12px; }
          .tabla-neto td { padding: 4px 8px; }
          .tabla-neto tr.fila-neto td { font-size: 14px; border-top: 2px solid #999; padding-top: 8px; }
          @media print { 
            .total-neto { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header-row">
          <div class="header-info">
            <h1>${reg.nombre}${sinRecargos ? ' (Sin Recargos Extras)' : ''}</h1>
            <div class="meta">Rango: ${reg.rango?.inicio || ""} a ${reg.rango?.fin || ""} · Generado: ${reg.fechaCreacion || ""}</div>
          </div>
          <div class="header-logo">
            <img src="/logo BIGBURGUER.jpg.jpg" alt="Logo" class="logo-img" onerror="this.style.display='none'" />
          </div>
        </div>
        
        ${sedeData.nombre ? `
        <div class="sede-info">
          <strong>${sedeData.nombre}</strong>
          ${sedeData.razonSocial ? `<span>| ${sedeData.razonSocial}</span>` : ''}
          ${sedeData.nit ? `<span>| NIT: ${sedeData.nit}</span>` : ''}
          ${sedeData.direccion ? `<span>| ${sedeData.direccion}</span>` : ''}
          ${sedeData.telefono ? `<span>| Tel: ${sedeData.telefono}</span>` : ''}
        </div>
        ` : ''}

        <div class="info-empleado">
          <table class="tabla-info">
            <tr>
              <td><strong>Empleado:</strong> ${reg.empleado?.nombre || "—"}</td>
              <td><strong>Documento:</strong> ${reg.empleado?.tipoDoc || "CC"} ${reg.empleado?.documento || ""}</td>
            </tr>
            <tr>
              <td><strong>Cargo:</strong> ${reg.empleado?.cargo || "—"}</td>
              <td><strong>Fecha ingreso:</strong> ${reg.empleado?.fechaIngreso || "—"}</td>
            </tr>
            <tr>
              <td colspan="2"><strong>Sede:</strong> ${sedeNombre || "—"}</td>
            </tr>
          </table>
        </div>

        <div class="grid2">
          <div class="box">
            <h4 class="seccion-titulo">📊 Resumen de Horas</h4>
            <table class="tabla-detalle">
              <tbody>
                ${filasHoras}
              </tbody>
            </table>
          </div>
          <div class="box">
            <h4 class="seccion-titulo">💰 Devengado</h4>
            <table class="tabla-detalle">
              <tbody>
                <tr><td>Salario bruto</td><td class="right">$${bruto.toLocaleString()}</td></tr>
                ${incap.dias > 0 ? `<tr><td>Incapacidad (${incap.dias} días)</td><td class="right">$${(incap.valorTotal || 0).toLocaleString()}</td></tr>` : ''}
                <tr><td>Bonificación</td><td class="right">$${bonif.toLocaleString()}</td></tr>
                <tr><td>Aux. transporte</td><td class="right">$${auxT.toLocaleString()}</td></tr>
                ${tieneAjustes && ajustes.sumaEmpleado > 0 ? `
                <tr>
                  <td>Ajuste de nómina</td>
                  <td class="right">$${ajustes.sumaEmpleado.toLocaleString()}</td>
                </tr>
                ${(ajustes.detalle || []).filter(a => a.beneficiario === 'empleado').map(a => `
                <tr style="font-size: 9px; color: #666;">
                  <td colspan="2" style="padding-left: 16px;">↳ ${a.concepto}</td>
                </tr>
                `).join('')}
                ` : ''}
                <tr class="subtotal"><td><strong>Total Devengado</strong></td><td class="right"><strong>$${totalDevengado.toLocaleString()}</strong></td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="box" style="margin-top: 10px;">
          <h4 class="seccion-titulo">📉 Deducciones</h4>
          <table class="tabla-detalle">
            <tbody>
              <tr><td>Préstamos</td><td class="right">$${(dsc.prestamos || 0).toLocaleString()}</td></tr>
              <tr><td>Salud</td><td class="right">$${(dsc.salud || 0).toLocaleString()}</td></tr>
              <tr><td>Pensión</td><td class="right">$${(dsc.pension || 0).toLocaleString()}</td></tr>
              <tr><td>Comida</td><td class="right">$${(dsc.comida || 0).toLocaleString()}</td></tr>
              <tr><td>Otros descuentos</td><td class="right">$${otrosDesc.toLocaleString()}</td></tr>
              ${tieneAjustes && ajustes.restaEmpresa > 0 ? `
              <tr>
                <td>Ajuste de nómina</td>
                <td class="right">$${ajustes.restaEmpresa.toLocaleString()}</td>
              </tr>
              ${(ajustes.detalle || []).filter(a => a.beneficiario === 'empresa').map(a => `
              <tr style="font-size: 9px; color: #666;">
                <td colspan="2" style="padding-left: 16px;">↳ ${a.concepto}</td>
              </tr>
              `).join('')}
              ` : ''}
              <tr class="subtotal"><td><strong>Total Deducciones</strong></td><td class="right"><strong>$${totalDeducciones.toLocaleString()}</strong></td></tr>
            </tbody>
          </table>
        </div>

        <div class="total-neto">
          <table class="tabla-neto">
            <tr>
              <td>Total Devengado</td>
              <td class="right">$${totalDevengado.toLocaleString()}</td>
            </tr>
            <tr>
              <td>(-) Total Deducciones</td>
              <td class="right">$${totalDeducciones.toLocaleString()}</td>
            </tr>
            <tr class="fila-neto">
              <td><strong>NETO A PAGAR</strong></td>
              <td class="right"><strong>$${neto.toLocaleString()}</strong></td>
            </tr>
          </table>
        </div>

        <script>window.print()</script>
      </body>
      </html>
    `);

    win.document.close();
  }
 
  // ====== Guardar nómina ======
  function guardarNominaActual() {
    if ((empleados.filter((e) => e.activo).length > 0) && empleadosSeleccionados.length === 0 && liquidadosSet.size > 0) {
      const liquidados = Array.from(liquidadosSet)
        .map(id => empleados.find(e => e.id === id)?.nombre || id)
        .join(", ");
      alert(
        `⚠️ LA NÓMINA DE ESTA QUINCENA YA FUE PROCESADA Y LIQUIDADA\n\n` +
        `Período: ${mesNomina} - Quincena ${quincena} (${periodoActualKey})\n\n` +
        `Empleados ya liquidados: ${liquidados}\n\n` +
        `❌ No se puede modificar una nómina que ya ha sido liquidada. ` +
        `Si necesitas hacer cambios, contacta al administrador.`
      );
      return;
    }

    if (empleadosSeleccionados.length === 0) {
      alert("No hay empleados seleccionados para esta quincena.");
      return;
    }
    
    // Verificar que ninguno de los empleados seleccionados ya fue liquidado
    const empleadosYaLiquidados = empleadosSeleccionados.filter(e => liquidadosSet.has(e.id));
    if (empleadosYaLiquidados.length > 0) {
      const nombres = empleadosYaLiquidados.map(e => e.nombre).join(", ");
      alert(
        `❌ OPERACIÓN NO PERMITIDA - NÓMINA YA LIQUIDADA\n\n` +
        `Período: ${mesNomina} - Quincena ${quincena}\n\n` +
        `Los siguientes empleados ya tienen nómina liquidada: ${nombres}\n\n` +
        `Una nómina liquidada no puede ser modificada o guardada nuevamente. ` +
        `Si necesitas hacer cambios, contacta con administración.`
      );
      return;
    }

    if (hayDiasSinDiligenciar()) {
      alert("No se puede guardar la nómina: hay días sin diligenciar (ni horario, ni 'No trabajó', ni 'Descanso') en esta quincena. Puedes guardar como borrador mientras completas los datos.");
      return;
    }
 
    const totalDiasQuincena = fechasQuincena.length;
    const rango = { inicio: fechasQuincena[0], fin: fechasQuincena[fechasQuincena.length - 1] };
    const nombreAuto = `Nómina ${mesNomina} Q${quincena}`;
 
    // (3) Preguntar el método de liquidación: Con recargos o Sin recargos
    const opcionIngresada = window.prompt(
      `📋 MÉTODO DE LIQUIDACIÓN - ${nombreAuto}\n\n` +
      `Período: ${rango.inicio} a ${rango.fin}\n\n` +
      `Escriba el número de la opción que desea:\n\n` +
      `1️⃣  LIQUIDAR CON RECARGOS\n` +
      `     (Horas nocturnas, extras, festivas con sus respectivos recargos)\n\n` +
      `2️⃣  LIQUIDAR SIN RECARGOS EXTRAS\n` +
      `     (Horas normales y nocturnas con recargo, extras y festivas como normales)\n\n` +
      `Ingrese 1 o 2:`,
      "1"
    );
    
    // Si el usuario canceló el prompt
    if (opcionIngresada === null) {
      return;
    }
    
    // Validar la opción ingresada
    const opcionLimpia = opcionIngresada.trim();
    if (opcionLimpia !== "1" && opcionLimpia !== "2") {
      alert("❌ Opción no válida. Por favor ingrese 1 para liquidar CON RECARGOS o 2 para liquidar SIN RECARGOS EXTRAS.");
      return;
    }
    
    const sinRecargos = opcionLimpia === "2";
    
    // Confirmar la selección
    const textoMetodo = sinRecargos ? "SIN RECARGOS EXTRAS (horas normales y nocturnas con recargo, extras y festivas como normales)" : "CON RECARGOS (horas nocturnas, extras y festivas con recargo)";
    const confirmar = window.confirm(
      `✅ CONFIRMAR LIQUIDACIÓN\n\n` +
      `Nómina: ${nombreAuto}\n` +
      `Período: ${rango.inicio} a ${rango.fin}\n` +
      `Empleados: ${empleadosSeleccionados.length}\n\n` +
      `Método seleccionado:\n` +
      `➡️ ${textoMetodo}\n\n` +
      `¿Desea continuar con la liquidación?`
    );
    
    if (!confirmar) return;
 
    const items = empleadosSeleccionados.map((e) => {
      const hEmp = horas.find((h) => h.empleadoId === e.id) || {
        hNorm: 0, hNoct: 0, hFest: 0, hExDia: 0, hExNoc: 0, hExFest: 0
      };
    
      // ¿Incapacidad total en toda la quincena?
      const incapDias = fechasQuincena.filter(iso => {
        const a = ausencias.find(x =>
          x.empleadoId === e.id &&
          x.tipo === "Incapacidad" &&
          (parseISO(iso) >= parseISO(x.inicio)) &&
          (parseISO(iso) <= parseISO(x.fin || x.inicio))
        );
        return !!a;
      }).length;
      const incapTotalQuincena = incapDias === fechasQuincena.length;
    
      // Calcular bruto según método de liquidación (con o sin recargos)
      const bruto = sinRecargos ? calcBrutoSinRecargos(hEmp) : calcBruto(hEmp);
    
      // Verificar si el empleado está afiliado a seguridad social
      const estaAfiliado = e.afiliadoSeguridadSocial !== false;
      
      // Auxilio de transporte solo si está afiliado a seguridad social
      const diasTrabajados = contarDiasTrabajados(e.id);
      const auxTransp = estaAfiliado ? Math.round((diasTrabajados / totalDiasQuincena) * AUX_TRANSP_QUINCENAL_BASE_2025) : 0;
      const cuotaPrestamos = cuotasMarcadasParaDescuento(e.id);
      // Salud y pensión solo si está afiliado a seguridad social
      const salud = estaAfiliado ? DESCUENTO_SALUD_QUINCENA : 0;
      const pension = estaAfiliado ? DESCUENTO_PENSION_QUINCENA : 0;
      const comida = getComidaEmpleado(e.id) || 0;
      const bonificacion = calcularBonificacion(e.id);
      
      // Otros descuentos programados (seguros, ahorros, suscripciones, etc.)
      const otrosDescuentosQuincenal = getTotalOtrosDescuentosQuincenal(e.id);
      const detalleOtrosDescuentos = getOtrosDescuentosEmpleado(e.id);
      
      // Calcular valor de incapacidad
      const incapacidadData = calcularIncapacidad(e.id);
      
      // ==== AJUSTES DE NÓMINA ====
      const ajustesEmpleado = getAjustesPorEmpleado(e.id);
      let ajusteSumaEmpleado = 0; // Ajustes a favor del empleado (SUMAN)
      let ajusteRestaEmpresa = 0; // Ajustes a favor de la empresa (RESTAN)
      const detalleAjustes = [];
      
      ajustesEmpleado.forEach(aj => {
        if (aj.tipoBeneficiario === "empleado") {
          ajusteSumaEmpleado += aj.valorTotal;
        } else {
          ajusteRestaEmpresa += aj.valorTotal;
        }
        detalleAjustes.push({
          id: aj.id,
          tipo: aj.tipoAjuste,
          beneficiario: aj.tipoBeneficiario,
          valor: aj.valorTotal,
          concepto: aj.concepto,
          fechas: aj.fechasAjuste,
          horasNormales: aj.totalHorasNormales,
          horasNocturnas: aj.totalHorasNocturnas,
          horasFestivas: aj.totalHorasFestivas,
        });
      });
      
      const ajusteNetoTotal = ajusteSumaEmpleado - ajusteRestaEmpresa;
    
      const descuentosTotal = salud + pension + cuotaPrestamos + comida + otrosDescuentosQuincenal;
      const neto = Math.round(bruto + auxTransp + bonificacion + incapacidadData.valorTotal + ajusteNetoTotal - descuentosTotal);
    
      // Detalle por fecha (para vistas detalladas)
      const detallePorFecha = fechasQuincena.map((f) => calcularHorasDiaEmpleado(e.id, f));
    
      // Totales para imprimir: si toda la quincena es incapacidad, usar el total 110h ya calculado en hEmp
      const horasTotales = incapTotalQuincena
        ? { ...hEmp }
        : detallePorFecha.reduce(
            (acc, d) => ({
              hNorm: acc.hNorm + d.hNorm,
              hNoct: acc.hNoct + d.hNoct,
              hFest: acc.hFest + d.hFest,
              hExDia: acc.hExDia + d.hExDia,
              hExNoc: acc.hExNoc + d.hExNoc,
              hExFest: acc.hExFest + d.hExFest,
            }),
            { hNorm: 0, hNoct: 0, hFest: 0, hExDia: 0, hExNoc: 0, hExFest: 0 }
          );
    
      return {
        empleadoId: e.id,
        empleadoNombre: e.nombre,
        documento: e.documento,
        empleadoTipoDoc: e.tipoDoc,
        empleadoCargo: e.cargo,
        empleadoFechaIngreso: e.fechaIngreso,
        empleadoSede: e.sede,
        detalleHoras: { ...hEmp },
        detallePorFecha,
        horasTotales,
        incapTotalQuincena, // <-- NUEVO flag guardado
        incapacidad: incapacidadData, // Datos de incapacidad: { dias, valorDiario, valorTotal }
        bruto,
        metodoLiquidacion: sinRecargos ? "sin_recargos" : "con_recargos", // Guardar método usado
        auxTransp,
        adicionales: { bonificacion },
        descuentos: { prestamos: cuotaPrestamos, salud, pension, comida, otrosDescuentos: otrosDescuentosQuincenal, detalleOtros: detalleOtrosDescuentos },
        // ==== AJUSTES DE NÓMINA ====
        ajustes: {
          sumaEmpleado: ajusteSumaEmpleado,
          restaEmpresa: ajusteRestaEmpresa,
          netoAjuste: ajusteNetoTotal,
          detalle: detalleAjustes,
          cantidad: ajustesEmpleado.length,
        },
        neto,
      };
    });
    
 
    const record = {
      id: uid(),
      nombre: nombreAuto,
      fechaCreacion: toISO(new Date()),
      rango,
      items,
      metodoLiquidacion: sinRecargos ? "sin_recargos" : "con_recargos", // Guardar método de liquidación en el registro
      // Datos de comidas de esta quincena
      periodoComidas: claveComidas(mesNomina, quincena),
      comidasQuincena: empleadosSeleccionados.reduce((acc, e) => {
        acc[e.id] = getComidaEmpleado(e.id) || 0;
        return acc;
      }, {}),
      // Información de préstamos para Supabase
      detallesPrestamos: items.map((item) => {
        const { detalle, cuotasIds } = obtenerDetallePrestamosEmpleado(item.empleadoId);
        return {
          empleadoId: item.empleadoId,
          totalPrestamos: item.descuentos.prestamos,
          detallePrestamosFull: detalle,
          cuotasProcesadas: cuotasIds,
        };
      }),
    };

    const metodoTexto = sinRecargos ? "SIN RECARGOS EXTRAS" : "CON RECARGOS";
    console.log(`\n✅ NÓMINA GUARDADA EXITOSAMENTE (${metodoTexto})\n Período: ${mesNomina} Q${quincena}\n Empleados: ${empleadosSeleccionados.length}\n Rango: ${rango.inicio} a ${rango.fin}\n`);
    
    onGuardarNomina(record);
    
    // === MARCAR AJUSTES COMO APLICADOS ===
    const todosLosAjustes = items.flatMap(item => item.ajustes?.detalle || []);
    if (todosLosAjustes.length > 0) {
      console.log(`📝 Marcando ${todosLosAjustes.length} ajuste(s) como aplicados...`);
      for (const ajuste of todosLosAjustes) {
        marcarAjusteComoAplicado(ajuste.id, record.id).then(() => {
          // Actualizar estado local
          setAjustesNomina(prev => prev.map(a => 
            a.id === ajuste.id ? { ...a, estado: "aplicado", aplicadoEnNomina: true, nominaId: record.id } : a
          ));
        });
      }
    }
    
    // === DETECTAR NETOS NEGATIVOS Y CREAR SALDOS PENDIENTES ===
    const empleadosConNetoNegativo = items.filter(item => item.neto < 0);
    if (empleadosConNetoNegativo.length > 0) {
      console.log(`⚠️ ${empleadosConNetoNegativo.length} empleado(s) con neto negativo detectado(s)`);
      
      // Crear saldos pendientes automáticamente para cada empleado con neto negativo
      for (const item of empleadosConNetoNegativo) {
        const valorNegativo = Math.abs(item.neto);
        console.log(`💰 Creando saldo pendiente para ${item.empleadoNombre}: $${valorNegativo.toLocaleString()}`);
        
        crearSaldoPendienteNomina(
          item.empleadoId,
          item.neto, // Valor negativo
          quincena,
          mesNomina,
          record.id,
          empresaId
        ).then((descuentoCreado) => {
          if (descuentoCreado) {
            // Actualizar el estado local de otros descuentos
            setOtrosDescuentos(prev => [...prev, {
              id: descuentoCreado.id,
              empleadoId: item.empleadoId,
              concepto: descuentoCreado.concepto,
              tipo: "meses",
              valorMensual: valorNegativo * 2,
              valorQuincenal: valorNegativo,
              mesesRestantes: 1,
              activo: true,
              empresaId: empresaId,
              origenNominaId: record.id,
            }]);
          }
        });
      }
      
      // Notificar al usuario sobre los saldos pendientes creados
      const nombresNegativos = empleadosConNetoNegativo.map(e => `${e.empleadoNombre}: -$${Math.abs(e.neto).toLocaleString()}`).join("\n");
      setTimeout(() => {
        alert(`⚠️ SALDOS PENDIENTES CREADOS AUTOMÁTICAMENTE\n\nLos siguientes empleados quedaron con neto negativo:\n\n${nombresNegativos}\n\n✅ Se han creado descuentos automáticos en "Otros Descuentos" que se aplicarán en la siguiente quincena (Q${quincena === 1 ? 2 : 1} ${quincena === 2 ? "del próximo mes" : mesNomina}).`);
      }, 500);
    }
    
    // Generar y guardar resumen ejecutivo
    const resumen = generarResumenEjecutivo(record, mesNomina, quincena);
    guardarResumenNominaEnSupabase(resumen, empresaId)
      .then((exito) => {
        if (exito) {
          console.log("✅ Resumen ejecutivo guardado en Supabase");
        }
      })
      .catch((err) => console.error("Error guardando resumen:", err));
    
    // Mostrar el resumen ejecutivo al usuario
    setResumenActual(resumen);
    setModalResumenOpen(true);
    
    // Recolectar y marcar cuotas como descontadas
    const cuotasDescontadas = recolectarCuotasMarcadas();
    if (typeof onMarcarCuotasUsadas === "function" && cuotasDescontadas.length > 0) {
      onMarcarCuotasUsadas(cuotasDescontadas);
      console.log(`✅ ${cuotasDescontadas.length} cuotas marcadas como descontadas en nómina`);
    }
  }
 
  // ====== Repositorio (como venía) ======
  const [repoOpen, setRepoOpen] = React.useState(false);
  const [repoNominaId, setRepoNominaId] = React.useState("");
  const [repoTipo, setRepoTipo] = React.useState("total");
  const [repoEmpleadoId, setRepoEmpleadoId] = React.useState("");
 
  function buildRegistroIndividual(nomina, empleadoId) {
    const it = nomina.items.find((x) => x.empleadoId === empleadoId);
    // Si no hay item, no podemos generar el registro
    if (!it) return null;
    
    // Buscar empleado actual (puede no existir si fue desactivado o es de otra empresa)
    const empActual = empleados.find((e) => e.id === empleadoId);
    
    // Combinar datos: priorizar datos actuales del empleado, pero usar guardados en el item si no existen
    const nombre = empActual?.nombre || it.empleadoNombre || "Empleado";
    const documento = empActual?.documento || it.documento || "";
    const tipoDoc = empActual?.tipoDoc || it.empleadoTipoDoc || "CC";
    const cargo = empActual?.cargo || it.empleadoCargo || "";
    const fechaIngreso = empActual?.fechaIngreso || it.empleadoFechaIngreso || "";
    const sede = empActual?.sede || it.empleadoSede || "";
    
    // Completar datos faltantes con valores por defecto
    const horasTotales = it.detalleHoras || it.horasTotales || {
      hNorm: 0,
      hNoct: 0,
      hFest: 0,
      hExDia: 0,
      hExNoc: 0,
      hExFest: 0,
    };
    
    return {
      nombre: nomina.nombre,
      rango: nomina.rango,
      fechaCreacion: nomina.fechaCreacion,
      metodoLiquidacion: it.metodoLiquidacion || nomina.metodoLiquidacion || "con_recargos",
      empleado: { nombre, documento, tipoDoc, cargo, fechaIngreso, sede },
      detallePorFecha: it.detallePorFecha || [],
      horasTotales: horasTotales,
      incapacidad: it.incapacidad || { dias: 0, valorDiario: 0, valorTotal: 0 },
      bruto: it.bruto || 0,
      auxTransp: it.auxTransp || 0,
      adicionales: it.adicionales || { bonificacion: 0 },
      descuentos: it.descuentos || { prestamos:0, salud:0, pension:0, comida:0, otrosDescuentos:0 },
      ajustes: it.ajustes || { sumaEmpleado: 0, restaEmpresa: 0, netoAjuste: 0, detalle: [], cantidad: 0 },
      neto: it.neto || 0,
      empleadoNombre: nombre,
      documento: documento,
    };
  }
 
  // ====== Modal externo de Desprendible ======
  const [slipOpen, setSlipOpen] = React.useState(false);
  const [slipNominaId, setSlipNominaId] = React.useState("");
  const [slipTipo, setSlipTipo] = React.useState("total");
  const [slipEmpleadoId, setSlipEmpleadoId] = React.useState("");

  // ====== Modal Planilla de Pagos ======
  const [planillaPagosOpen, setPlanillaPagosOpen] = React.useState(false);
  const [planillaPagosNominaId, setPlanillaPagosNominaId] = React.useState("");
  const [planillaPagosEmpleados, setPlanillaPagosEmpleados] = React.useState([]);
  const [planillaPagosModoMetodo, setPlanillaPagosModoMetodo] = React.useState("mismo"); // "mismo" o "individual"
  const [planillaPagosMetodoGeneral, setPlanillaPagosMetodoGeneral] = React.useState("efectivo");
  const [planillaPagosMetodos, setPlanillaPagosMetodos] = React.useState({}); // {empleadoId: "efectivo" | "transferencia"}
  const [planillaPagosGenerada, setPlanillaPagosGenerada] = React.useState(false);
 
  const guardadoLabel = autoSavedAt
    ? `Borrador auto-guardado: ${new Date(autoSavedAt).toLocaleString()}`
    : "Borrador no guardado";
 
  // =========== UI ===========
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px' }}>
      <div style={{ width: '100%', maxWidth: '100%' }}>
        <h2 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#ff6b35',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          borderBottom: '3px solid #ffc53d',
          paddingBottom: '10px'
        }}>💰 Nómina quincenal</h2>

        {/* Indicador de valores anuales usados */}
        <div style={{
          backgroundColor: '#e6f7ff',
          border: '1px solid #91d5ff',
          borderRadius: '8px',
          padding: '10px 16px',
          marginBottom: '16px',
          fontSize: '12px',
          color: '#0050b3',
          maxWidth: '800px',
          margin: '0 auto 16px auto'
        }}>
          <strong>📊 Valores configurados:</strong>
          <span style={{ marginLeft: '8px' }}>SMLV: ${SMLV.toLocaleString('es-CO')}</span>
          <span style={{ marginLeft: '12px' }}>Aux. Transp. Quincenal: ${AUX_TRANSP_QUINCENAL_BASE_2025.toLocaleString('es-CO')}</span>
          <span style={{ marginLeft: '12px' }}>Salud: {PORCENTAJE_SALUD}%</span>
          <span style={{ marginLeft: '12px' }}>Pensión: {PORCENTAJE_PENSION}%</span>
        </div>
 
        {/* === CONTROLES (ordenados y centrados) === */}
        <div style={{
          padding: '20px',
          marginBottom: '20px',
          backgroundColor: '#fffbf5',
          borderRadius: '12px',
          border: '2px solid #ffe4c9',
          maxWidth: '800px',
          margin: '0 auto 20px auto'
        }}>
          {/* Layout de dos columnas */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            
            {/* === COLUMNA IZQUIERDA === */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Seleccionar empleados */}
              <button
                onClick={() => setSelectOpen(true)}
                style={{
                  padding: '12px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%'
                }}
              >👥 Seleccionar empleados</button>
              
              {/* Mes de la nómina */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📅 Mes de la nómina</label>
                <input type="month" value={mesNomina} onChange={(e) => setMesNomina(e.target.value)} style={{
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                  color: '#333',
                  outline: 'none',
                  width: '100%',
                  boxSizing: 'border-box'
                }} />
              </div>
              
              {/* Quincena */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📆 Quincena</label>
                <select
                  value={quincena}
                  onChange={(e) => setQuincena(Number(e.target.value))}
                  style={{
                    padding: '10px 14px',
                    fontSize: '13px',
                    border: '2px solid #ff6b35',
                    borderRadius: '8px',
                    backgroundColor: '#fffbf5',
                    color: '#333',
                    cursor: 'pointer',
                    outline: 'none',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value={1}>➀ Primera (1–15)</option>
                  <option value={2}>➁ Segunda (16–fin)</option>
                </select>
              </div>

              {/* Separador */}
              <div style={{ borderTop: '1px solid #ffe4c9', margin: '8px 0' }}></div>
              
              {/* Guardar borrador */}
              {tienePermiso("rh_nomina_guardar_borrador") && (
              <button
                onClick={guardarBorradorManual}
                style={{
                  padding: '12px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: '#d97706',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%'
                }}
              >📝 Guardar borrador</button>
              )}
              
              {/* Descartar borrador */}
              {tienePermiso("rh_nomina_descartar_borrador") && (
              <button
                onClick={descartarBorrador}
                style={{
                  padding: '12px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: '2px solid #ef4444',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: '#ef4444',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%'
                }}
              >🗑️ Descartar borrador</button>
              )}
            </div>

            {/* === COLUMNA DERECHA === */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Comandas */}
              <button
                onClick={() => setModalComidasOpen(true)}
                style={{
                  padding: '12px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: '2px solid #ff6b35',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: '#ff6b35',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%'
                }}
              >🍽️ Comandas</button>
              
              {/* Cuotas préstamos */}
              <button
                onClick={() => setModalCuotasNominaOpen(true)}
                style={{
                  padding: '12px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%'
                }}
              >💳 Cuotas préstamos</button>
              
              {/* Otros Descuentos */}
              <button
                onClick={() => setModalOtrosDescuentosOpen(true)}
                style={{
                  padding: '12px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: '2px solid #dc2626',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: '#dc2626',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%'
                }}
              >📋 Otros Descuentos</button>

              {/* Ajustes de Nómina */}
              <button
                onClick={() => setModalAjustesNominaOpen(true)}
                style={{
                  padding: '12px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: '2px solid #059669',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: '#059669',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%'
                }}
              >
                ⚖️ Ajustes
              </button>

              {/* Separador */}
              <div style={{ borderTop: '1px solid #ffe4c9', margin: '8px 0' }}></div>
              
              {/* Resúmenes */}
              <button
                onClick={() => setModalBuscarResumenesOpen(true)}
                style={{
                  padding: '12px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: '2px solid #8b5cf6',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: '#8b5cf6',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%'
                }}
              >📊 Resúmenes</button>
              
              {/* Ver repositorio */}
              {tienePermiso("rh_nomina_repositorio") && (
              <button
                onClick={() => setRepoOpen(true)}
                style={{
                  padding: '12px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: '2px solid #ff6b35',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: '#ff6b35',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%'
                }}
              >📂 Ver repositorio</button>
              )}
              
              {/* Desprendible */}
              {tienePermiso("rh_nomina_desprendible") && (
              <button
                onClick={() => setSlipOpen(true)}
                style={{
                  padding: '12px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: '2px solid #1890ff',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: '#1890ff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%'
                }}
              >🧾 Desprendible</button>
              )}

              {/* Generar Planilla Pagos */}
              <button
                onClick={() => {
                  setPlanillaPagosOpen(true);
                  setPlanillaPagosGenerada(false);
                  setPlanillaPagosEmpleados([]);
                  setPlanillaPagosNominaId("");
                  setPlanillaPagosModoMetodo("mismo");
                  setPlanillaPagosMetodoGeneral("efectivo");
                  setPlanillaPagosMetodos({});
                }}
                style={{
                  padding: '12px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: '2px solid #059669',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: '#059669',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%'
                }}
              >💵 Generar Planilla Pagos</button>
            </div>
          </div>
          
          {/* Botón Guardar nómina centrado */}
          {tienePermiso("rh_nomina_guardar") && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <button
              onClick={guardarNominaActual}
              style={{
                padding: '14px 48px',
                fontSize: '15px',
                fontWeight: '700',
                border: 'none',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                color: 'white',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(82, 196, 26, 0.4)',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                minWidth: '280px'
              }}
            >💾 Guardar nómina</button>
          </div>
          )}
          
          {/* Estado de guardado */}
          <div style={{ textAlign: 'center', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #ffe4c9' }}>
            <span style={{ fontSize: '11px', color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>💾 {guardadoLabel}</span>
          </div>
        </div>
 
        {/* Planillas por empleado */}
        {empleadosSeleccionados.map((emp) => (
          <div key={emp.id} style={{
            marginTop: '16px',
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '2px solid #ffe4c9',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(255, 107, 53, 0.1)'
          }}>
            <div style={{
              padding: '10px 16px',
              fontSize: '13px',
              fontWeight: '600',
              textAlign: 'center',
              background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
              color: 'white'
            }}>
              👤 {emp.nombre} – {emp.tipoDoc} {emp.documento}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#fffbf5' }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '2px solid #ffe4c9', color: '#ff6b35', fontWeight: '600' }}>📅 Fecha</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #ffe4c9', color: '#ff6b35', fontWeight: '600' }}>⬇️ E1</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #ffe4c9', color: '#ff6b35', fontWeight: '600' }}>⬆️ S1</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #ffe4c9', color: '#ff6b35', fontWeight: '600' }}>⬇️ E2</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #ffe4c9', color: '#ff6b35', fontWeight: '600' }}>⬆️ S2</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #ffe4c9', color: '#ff6b35', fontWeight: '600' }}>😴 Descanso</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #ffe4c9', color: '#ff6b35', fontWeight: '600' }}>❌ No trabajó</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', borderBottom: '2px solid #ffe4c9', color: '#ff6b35', fontWeight: '600' }}>📌 Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {fechasQuincena.map((iso) => {
                    const d = (planilla?.[emp.id]?.[iso]) || { e1: "", s1: "", e2: "", s2: "", descanso: false, noTrabajado: false, incapacidad: false };
                    const bloqueo =
                      ausencias.find(
                        (x) =>
                          x.empleadoId === emp.id &&
                          (x.tipo === "Vacaciones" || x.tipo === "Suspensión" || x.tipo === "Incapacidad") &&
                          (parseISO(iso) >= parseISO(x.inicio)) &&
                          (parseISO(iso) <= parseISO(x.fin || x.inicio))
                      )?.tipo || null;
                    const disabled = Boolean(bloqueo) || d.noTrabajado || d.incapacidad;
                    const timeInputStyle = {
                      padding: '6px 8px',
                      fontSize: '12px',
                      border: disabled || d.descanso ? '1px solid #e5e7eb' : '2px solid #ffc53d',
                      borderRadius: '6px',
                      backgroundColor: disabled || d.descanso ? '#f3f4f6' : '#fffbf5',
                      color: '#333',
                      outline: 'none',
                      width: '115px',
                      minWidth: '115px',
                    };
                    return (
                      <tr key={iso} style={{ backgroundColor: disabled ? '#f9fafb' : 'white', borderBottom: '1px solid #ffe4c9' }}>
                        <td style={{ padding: '8px 10px', fontWeight: '500' }}>{iso}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          <input type="time" value={d.e1} onChange={(e) => setPlanDia(emp.id, iso, "e1", e.target.value)} style={timeInputStyle} disabled={disabled || d.descanso}/>
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          <input type="time" value={d.s1} onChange={(e) => setPlanDia(emp.id, iso, "s1", e.target.value)} style={timeInputStyle} disabled={disabled || d.descanso}/>
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          <input type="time" value={d.e2} onChange={(e) => setPlanDia(emp.id, iso, "e2", e.target.value)} style={timeInputStyle} disabled={disabled || d.descanso}/>
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          <input type="time" value={d.s2} onChange={(e) => setPlanDia(emp.id, iso, "s2", e.target.value)} style={timeInputStyle} disabled={disabled || d.descanso}/>
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={d.descanso || false}
                            onChange={(e) => setPlanDia(emp.id, iso, "descanso", e.target.checked)}
                            disabled={disabled}
                            style={{ width: '18px', height: '18px', accentColor: '#52c41a' }}
                          />
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={d.noTrabajado || false}
                            onChange={(e) => setPlanDia(emp.id, iso, "noTrabajado", e.target.checked)}
                            disabled={Boolean(bloqueo) || d.incapacidad}
                            title={bloqueo ? "Bloqueado por ausencia" : d.incapacidad ? "Incapacidad desde horarios" : "Marcar si no trabajó (bloquea el día)"}
                            style={{ width: '18px', height: '18px', accentColor: '#ef4444' }}
                          />
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          {bloqueo ? (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              backgroundColor: bloqueo === 'Vacaciones' ? '#dbeafe' : bloqueo === 'Incapacidad' ? '#fce7f3' : '#fee2e2',
                              color: bloqueo === 'Vacaciones' ? '#1d4ed8' : bloqueo === 'Incapacidad' ? '#be185d' : '#dc2626',
                              fontSize: '10px',
                              fontWeight: '600'
                            }}>
                              {bloqueo === 'Vacaciones' ? '🏖️' : bloqueo === 'Incapacidad' ? '🩺' : '⛔'} {bloqueo}
                            </span>
                          ) : d.incapacidad ? (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              backgroundColor: '#fce7f3',
                              color: '#be185d',
                              fontSize: '10px',
                              fontWeight: '600'
                            }}>
                              🩺 Incapacidad
                            </span>
                          ) : d.noTrabajado ? (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '4px 10px',
                              borderRadius: '20px',
                              backgroundColor: '#f3f4f6',
                              color: '#6b7280',
                              fontSize: '10px',
                              fontWeight: '600'
                            }}>
                              ❌ No trabajó
                            </span>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
 
            <div style={{
              padding: '12px 16px',
              fontSize: '12px',
              color: '#333',
              borderTop: '2px solid #ffe4c9',
              backgroundColor: '#fffbf5'
            }}>
              {(() => {
                let r = { hNorm: 0, hNoct: 0, hFest: 0, hExDia: 0, hExNoc: 0, hExFest: 0 };
                try { r = calcularHorasQuincenaEmpleado(emp.id); } catch {}
                const diasTrab = contarDiasTrabajados(emp.id);
                return (
                  <>
                    <div style={{ marginBottom: '4px' }}>
                      ⏰ Normales: <strong style={{ color: '#ff6b35' }}>{r.hNorm.toFixed(2)}h</strong> · 
                      🌙 Nocturnas: <strong style={{ color: '#8b5cf6' }}>{r.hNoct.toFixed(2)}h</strong> · 
                      🎉 Festivas: <strong style={{ color: '#f59e0b' }}>{r.hFest.toFixed(2)}h</strong>
                    </div>
                    <div style={{ marginBottom: '4px' }}>
                      ⭐ Extras diurnas: <strong style={{ color: '#10b981' }}>{r.hExDia.toFixed(2)}h</strong> · 
                      🌃 Extras nocturnas: <strong style={{ color: '#6366f1' }}>{r.hExNoc.toFixed(2)}h</strong> · 
                      🌟 Extras festivas: <strong style={{ color: '#ec4899' }}>{r.hExFest.toFixed(2)}h</strong>
                    </div>
                    <div style={{ color: '#666' }}>
                      📅 Días trabajados para aux. transporte: <strong style={{ color: '#ff6b35' }}>{diasTrab}/{fechasQuincena.length}</strong>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        ))}
 
        {!empleadosSeleccionados.length && (liquidadosSet.size > 0) && (
          <div style={{
            fontSize: '14px',
            color: '#d97706',
            backgroundColor: '#fef3c7',
            border: '2px solid #fcd34d',
            borderRadius: '10px',
            padding: '16px',
            marginTop: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            ✅ Todos los empleados activos ya están liquidados en esta quincena.
          </div>
        )}
 
        {/* Tabla resumen */}
        <h3 style={{
          marginTop: '24px',
          fontSize: '15px',
          fontWeight: '600',
          color: '#ff6b35',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>📊 Resumen de nómina</h3>
        <table style={{
          width: '100%',
          fontSize: '11px',
          marginTop: '12px',
          borderCollapse: 'collapse',
          borderRadius: '10px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(255, 107, 53, 0.15)'
        }}>
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' }}>
              <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600' }}>👤 Empleado</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>H. norm</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>H. noct</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>H. fest</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>H. ex diur</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>H. ex noct</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>H. ex fest</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>💰 Bruto</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>🩺 Incap.</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>⭐ Bonif.</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>🚌 Aux.</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>💳 Prést.</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>🏥 Salud</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>💼 Pens.</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>🍽️ Comida</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>📦 Otros</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>⚖️ Ajuste</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>💵 Neto</th>
            </tr>
          </thead>
          <tbody>
            {empleadosSeleccionados.map((e) => {
              const hEmp = horas.find((h) => h.empleadoId === e.id) || { hNorm: 0, hNoct: 0, hFest: 0, hExDia: 0, hExNoc: 0, hExFest: 0 };
              const bruto = calcBruto(hEmp);
              const diasTrabajados = contarDiasTrabajados(e.id);
              // Verificar si el empleado está afiliado a seguridad social
              const estaAfiliado = e.afiliadoSeguridadSocial !== false;
              // Aux transporte solo si está afiliado
              const auxTransp = estaAfiliado ? Math.round((diasTrabajados / fechasQuincena.length) * AUX_TRANSP_QUINCENAL_BASE_2025) : 0;
              const pres = prestamos.filter((p) => p.empleadoId === e.id);
              const cuotaPrestamos = pres.reduce((acc, p) => acc + p.plan.filter((c) => c.pagado && !c.noDescontable).reduce((s, c) => s + c.valor, 0), 0);
              const comidaVal = getComidaEmpleado(e.id) || 0;
              const bonif = calcularBonificacion(e.id);
              const incapacidadData = calcularIncapacidad(e.id);
              const otrosDescVal = getTotalOtrosDescuentosQuincenal(e.id);
              // Calcular ajustes del empleado
              const ajustesEmp = getAjustesPorEmpleado(e.id);
              const ajusteSumaEmpleado = ajustesEmp.filter(a => a.tipoBeneficiario === 'empleado').reduce((acc, a) => acc + (a.valorTotal || 0), 0);
              const ajusteRestaEmpresa = ajustesEmp.filter(a => a.tipoBeneficiario === 'empresa').reduce((acc, a) => acc + (a.valorTotal || 0), 0);
              const ajusteNetoTotal = ajusteSumaEmpleado - ajusteRestaEmpresa;
              // Salud y pensión solo si está afiliado
              const saludDesc = estaAfiliado ? DESCUENTO_SALUD_QUINCENA : 0;
              const pensionDesc = estaAfiliado ? DESCUENTO_PENSION_QUINCENA : 0;
              const neto = Math.round(bruto + bonif + auxTransp + incapacidadData.valorTotal + ajusteNetoTotal - saludDesc - pensionDesc - cuotaPrestamos - comidaVal - otrosDescVal);

              return (
                <tr key={e.id} style={{ backgroundColor: 'white', borderBottom: '1px solid #ffe4c9' }}>
                  <td style={{ padding: '10px 8px' }}>{e.nombre} – {e.tipoDoc} {e.documento}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{hEmp.hNorm.toFixed(2)}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{hEmp.hNoct.toFixed(2)}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{hEmp.hFest.toFixed(2)}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{hEmp.hExDia.toFixed(2)}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{hEmp.hExNoc.toFixed(2)}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{hEmp.hExFest.toFixed(2)}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center', color: '#ff6b35', fontWeight: '600' }}>${bruto.toLocaleString()}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center', color: '#be185d' }}>{incapacidadData.dias > 0 ? `${incapacidadData.dias}d / $${incapacidadData.valorTotal.toLocaleString()}` : "—"}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center', color: '#10b981' }}>{bonif ? `$${bonif.toLocaleString()}` : "—"}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{estaAfiliado && auxTransp ? `$${auxTransp.toLocaleString()}` : "—"}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center', color: '#ef4444' }}>{cuotaPrestamos ? `$${cuotaPrestamos.toLocaleString()}` : "—"}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{estaAfiliado ? `$${saludDesc.toLocaleString()}` : "—"}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{estaAfiliado ? `$${pensionDesc.toLocaleString()}` : "—"}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>{comidaVal ? `$${comidaVal.toLocaleString()}` : "—"}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center', color: '#dc2626' }}>{otrosDescVal ? `$${otrosDescVal.toLocaleString()}` : "—"}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: '600' }}>{ajusteNetoTotal !== 0 ? `$${Math.abs(ajusteNetoTotal).toLocaleString()}` : "—"}</td>
                  <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: '700', color: '#52c41a', fontSize: '12px' }}>${neto.toLocaleString()}</td>
                </tr>
              );
            })}
            {!empleadosSeleccionados.length && (
              <tr>
                <td colSpan={18} style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
                  📭 No hay empleados seleccionados para esta quincena.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <p style={{
          fontSize: '11px',
          color: '#888',
          marginTop: '12px',
          padding: '10px',
          backgroundColor: '#fffbf5',
          borderRadius: '8px',
          border: '1px solid #ffe4c9'
        }}>
          💡 Las horas se recalculan automáticamente. Ausencias (Vacaciones/Suspensión/Incapacidad) bloquean días según reglas. Auxilio de transporte prorrateado por días trabajados.
        </p>
      </div>
 
      {/* Modal Repositorio */}
      {repoOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 10px 40px rgba(255, 107, 53, 0.3)',
            padding: '24px',
            width: '800px',
            maxWidth: '95vw',
            border: '3px solid #ffc53d'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#ff6b35',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderBottom: '2px solid #ffe4c9',
              paddingBottom: '10px'
            }}>📂 Repositorio de nóminas</h3>
 
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📋 Nómina</label>
                <select
                  value={repoNominaId}
                  onChange={(e) => {
                    setRepoNominaId(e.target.value);
                    setRepoEmpleadoId("");
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    fontSize: '13px',
                    border: '2px solid #ff6b35',
                    borderRadius: '8px',
                    backgroundColor: '#fffbf5',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">-- Selecciona --</option>
                  {nominasGuardadas.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.nombre} ({n.rango?.inicio} → {n.rango?.fin})
                    </option>
                  ))}
                </select>
              </div>
 
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>👁️ Tipo de vista</label>
                <select
                  value={repoTipo}
                  onChange={(e) => setRepoTipo(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    fontSize: '13px',
                    border: '2px solid #ffc53d',
                    borderRadius: '8px',
                    backgroundColor: '#fffbf5',
                    cursor: 'pointer',
                  }}
                >
                  <option value="total">👥 Total (todos los empleados)</option>
                  <option value="individual">👤 Individual (por empleado)</option>
                </select>
              </div>
 
              {repoTipo === "individual" && (
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>👤 Empleado</label>
                  <select
                    value={repoEmpleadoId}
                    onChange={(e) => setRepoEmpleadoId(e.target.value)}
                    disabled={!repoNominaId}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      fontSize: '13px',
                      border: '2px solid #ffc53d',
                      borderRadius: '8px',
                      backgroundColor: !repoNominaId ? '#f3f4f6' : '#fffbf5',
                      cursor: !repoNominaId ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <option value="">-- Selecciona --</option>
                    {(() => {
                      const nom = nominasGuardadas.find((x) => x.id === repoNominaId);
                      const items = nom?.items || [];
                      return items.map((it) => {
                        const nombreMostrar = it.empleadoNombre && it.empleadoNombre.trim() 
                          ? it.empleadoNombre 
                          : `Empleado ${it.empleadoId}`;
                        const docMostrar = it.documento && it.documento.trim()
                          ? it.documento
                          : "S/D";
                        return (
                          <option key={it.empleadoId} value={it.empleadoId}>
                            {nombreMostrar} – {docMostrar}
                          </option>
                        );
                      });
                    })()}
                  </select>
                </div>
              )}
            </div>
            {repoTipo === "liquidaciones" && (
  <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#fffbf5', borderRadius: '8px', border: '1px solid #ffe4c9' }}>
    <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#ff6b35', marginBottom: '12px' }}>📂 Repositorio de liquidaciones de prestaciones</h3>
    <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#333' }}>
    <span style={{ fontWeight: '600', color: '#ff6b35' }}>👤 Empleado:</span>
    <select
      value={repoEmpleadoId}
      onChange={(e) => setRepoEmpleadoId(e.target.value)}
      style={{
        padding: '8px 12px',
        fontSize: '13px',
        border: '2px solid #ffc53d',
        borderRadius: '8px',
        backgroundColor: '#fffbf5',
        cursor: 'pointer',
      }}
    >
      <option value="">-- Todos --</option>
      {[...(empleados || [])].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map((emp) => (
        <option key={emp.id} value={emp.id}>
          {emp.nombre} ({emp.documento})
        </option>
      ))}
    </select>
  </label>
 
  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#333' }}>
    <span style={{ fontWeight: '600', color: '#ff6b35' }}>🎯 Prestación:</span>
    <select
      value={filtroTipo}
      onChange={(e) => setFiltroTipo(e.target.value)}
      style={{
        padding: '8px 12px',
        fontSize: '13px',
        border: '2px solid #ffc53d',
        borderRadius: '8px',
        backgroundColor: '#fffbf5',
        cursor: 'pointer',
      }}
    >
      <option value="">-- Todas --</option>
      <option value="Vacaciones">🏖️ Vacaciones</option>
      <option value="Cesantías">💰 Cesantías</option>
      <option value="Prima">🎁 Prima</option>
      <option value="LIQUIDACIÓN DE CONTRATO">📄 Liquidación de contrato</option>
    </select>
  </label>
</div>
 
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginTop: '8px' }}>
      <thead>
        <tr style={{ background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' }}>
          <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Empleado</th>
          <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Documento</th>
          <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Prestación</th>
          <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Periodo / Fecha</th>
          <th style={{ padding: '10px 8px', textAlign: 'right', color: 'white', fontWeight: '600' }}>Valor</th>
          <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>Acción</th>
        </tr>
      </thead>
      <tbody>
        {repo
          .filter((liq) =>
            repoEmpleadoId ? liq.empleadoId === repoEmpleadoId : true
          )
          .map((liq, idx) => {
            const emp = empleados.find((e) => e.id === liq.empleadoId);
            if (!emp) return null;
            return (
              <tr key={liq.id} style={{ backgroundColor: idx % 2 === 0 ? '#fffbf5' : '#fff5eb', borderBottom: '1px solid #ffe4c9' }}>
                <td style={{ padding: '10px 8px', color: '#333' }}>{emp.nombre}</td>
                <td style={{ padding: '10px 8px', color: '#666' }}>{emp.documento}</td>
                <td style={{ padding: '10px 8px' }}>
                  <span style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#fff3e0', color: '#ff6b35' }}>
                    {liq.tipoPrestacion}
                  </span>
                </td>
                <td style={{ padding: '10px 8px', color: '#666' }}>{liq.periodoTexto || liq.fechaLiquidacion}</td>
                <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '700', color: '#52c41a' }}>{money(liq.valorNeto || liq.valorTotal || 0)}</td>
                <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={() => handleImprimirLiquidacion(liq.id)}
                    style={{
                      padding: '6px 12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      border: 'none',
                      borderRadius: '6px',
                      background: 'linear-gradient(135deg, #1890ff 0%, #52c41a 100%)',
                      color: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    🖨️ Imprimir
                  </button>
                </td>
              </tr>
            );
          })}
      </tbody>
    </table>
  </div>
)}
 
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setRepoOpen(false)}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#ff6b35',
                  cursor: 'pointer',
                }}
              >
                ❌ Cerrar
              </button>
              {tienePermiso("rh_nomina_reabrir") && (
              <button
                onClick={() => {
                  const nom = nominasGuardadas.find((x) => x.id === repoNominaId);
                  if (!nom) { alert("Selecciona la nómina a reabrir."); return; }
                  const confirmacion = window.confirm(
                    `⚠️ REABRIR NÓMINA\n\n` +
                    `Nómina: ${nom.nombre}\n` +
                    `Período: ${nom.rango?.inicio} → ${nom.rango?.fin}\n` +
                    `Empleados: ${nom.items?.length || 0}\n\n` +
                    `Esta acción:\n` +
                    `• Eliminará la nómina del repositorio para que puedas editarla\n` +
                    `• Reactivará las cuotas de préstamos descontadas en esta quincena\n` +
                    `• Las cuotas quedarán disponibles para volver a seleccionar o deseleccionar\n\n` +
                    `¿Deseas continuar?`
                  );
                  if (!confirmacion) return;
                  
                  if (typeof onReabrirNomina === "function") {
                    onReabrirNomina(nom);
                    setRepoOpen(false);
                    alert(`✅ Nómina "${nom.nombre}" reabierta.\n\n• Ahora puedes editarla y volver a guardarla.\n• Las cuotas de préstamos fueron reactivadas y pueden volver a seleccionarse.`);
                  } else {
                    alert("❌ Error: Función de reabrir no disponible.");
                  }
                }}
                disabled={!repoNominaId}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '8px',
                  background: !repoNominaId ? '#e2e8f0' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: !repoNominaId ? '#94a3b8' : 'white',
                  cursor: !repoNominaId ? 'not-allowed' : 'pointer',
                  boxShadow: repoNominaId ? '0 2px 4px rgba(245, 158, 11, 0.3)' : 'none',
                }}
              >
                🔓 Reabrir para editar
              </button>
              )}
              <button
                onClick={() => {
                  const nom = nominasGuardadas.find((x) => x.id === repoNominaId);
                  if (!nom) { alert("Selecciona la nómina a visualizar."); return; }
                  if (repoTipo === "total") {
                    imprimirNomina(nom);
                  } else {
                    if (!repoEmpleadoId) { alert("Selecciona el empleado para impresión individual."); return; }
                    const reg = buildRegistroIndividual(nom, repoEmpleadoId);
                    if (!reg) { alert("No se encontró información para ese empleado."); return; }
                    // DETALLADO (con colores + leyenda)
                    imprimirNominaIndividual(reg);
                  }
                }}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(255, 107, 53, 0.3)',
                }}
              >
                🖨️ Imprimir
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* Modal Planilla de Pagos */}
      {planillaPagosOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 8px 24px rgba(5, 150, 105, 0.25)', padding: '24px', width: planillaPagosGenerada ? '95vw' : '700px', maxWidth: '98vw', maxHeight: '95vh', overflow: 'auto', border: '2px solid #059669' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid #d1fae5', paddingBottom: '12px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#059669', margin: 0 }}>💵 Generar Planilla de Pagos</h3>
              <button onClick={() => setPlanillaPagosOpen(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666' }}>✕</button>
            </div>

            {!planillaPagosGenerada ? (
              <>
                {/* Seleccionar nómina */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#059669', marginBottom: '6px' }}>📅 Seleccionar Nómina/Quincena</label>
                  <select
                    value={planillaPagosNominaId}
                    onChange={(e) => {
                      setPlanillaPagosNominaId(e.target.value);
                      setPlanillaPagosEmpleados([]);
                    }}
                    style={{ width: '100%', padding: '10px 14px', fontSize: '13px', border: '2px solid #d1fae5', borderRadius: '8px', backgroundColor: '#f0fdf4' }}
                  >
                    <option value="">-- Seleccionar nómina --</option>
                    {nominasGuardadas.map((n) => {
                      // Determinar quincena 1 o 2 basado en el rango
                      const rangoInicio = n.rango?.inicio || "";
                      const dia = rangoInicio ? parseInt(rangoInicio.split("-")[2] || "1") : 1;
                      const esQuincena1 = dia <= 15;
                      const quincenaLabel = esQuincena1 ? "Q1" : "Q2";
                      const rangoTexto = n.rango?.inicio && n.rango?.fin 
                        ? ` (${n.rango.inicio} al ${n.rango.fin})`
                        : "";
                      return (
                        <option key={n.id} value={n.id}>{n.nombre} - {quincenaLabel}{rangoTexto} - {n.items?.length || 0} empleados</option>
                      );
                    })}
                  </select>
                </div>

                {/* Seleccionar empleados */}
                {planillaPagosNominaId && (() => {
                  const nominaSel = nominasGuardadas.find(n => n.id === planillaPagosNominaId);
                  const itemsNomina = nominaSel?.items || [];
                  return (
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#059669', marginBottom: '6px' }}>👥 Seleccionar Empleados ({itemsNomina.length} liquidados en esta nómina)</label>
                      <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #d1fae5', borderRadius: '8px', padding: '10px', backgroundColor: '#f0fdf4' }}>
                        <div style={{ marginBottom: '8px', display: 'flex', gap: '10px' }}>
                          <button
                            type="button"
                            onClick={() => setPlanillaPagosEmpleados(itemsNomina.map(item => item.empleadoId).filter(Boolean))}
                            style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #059669', borderRadius: '6px', background: 'white', color: '#059669', cursor: 'pointer' }}
                          >✅ Todos</button>
                          <button
                            type="button"
                            onClick={() => setPlanillaPagosEmpleados([])}
                            style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #dc2626', borderRadius: '6px', background: 'white', color: '#dc2626', cursor: 'pointer' }}
                          >🗑️ Ninguno</button>
                        </div>
                        {itemsNomina.length === 0 ? (
                          <p style={{ color: '#999', fontSize: '13px', textAlign: 'center', padding: '16px' }}>⚠️ No hay empleados liquidados en esta nómina</p>
                        ) : (
                          itemsNomina.map((item) => (
                            <label key={item.empleadoId} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', padding: '6px 0', borderBottom: '1px solid #e5e7eb' }}>
                              <input
                                type="checkbox"
                                checked={planillaPagosEmpleados.includes(item.empleadoId)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setPlanillaPagosEmpleados(prev => [...prev, item.empleadoId]);
                                  } else {
                                    setPlanillaPagosEmpleados(prev => prev.filter(id => id !== item.empleadoId));
                                  }
                                }}
                                style={{ accentColor: '#059669' }}
                              />
                              <span>{item.empleadoNombre || 'Sin nombre'}</span>
                              <span style={{ marginLeft: 'auto', color: '#666', fontSize: '11px' }}>Neto: ${(item.neto || 0).toLocaleString('es-CO')}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Método de pago */}
                {planillaPagosEmpleados.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#059669', marginBottom: '6px' }}>💳 Método de Pago</label>
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="modoMetodoPago"
                          checked={planillaPagosModoMetodo === "mismo"}
                          onChange={() => setPlanillaPagosModoMetodo("mismo")}
                          style={{ accentColor: '#059669' }}
                        />
                        Mismo método para todos
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="modoMetodoPago"
                          checked={planillaPagosModoMetodo === "individual"}
                          onChange={() => setPlanillaPagosModoMetodo("individual")}
                          style={{ accentColor: '#059669' }}
                        />
                        Diferente por empleado
                      </label>
                    </div>

                    {planillaPagosModoMetodo === "mismo" ? (
                      <select
                        value={planillaPagosMetodoGeneral}
                        onChange={(e) => setPlanillaPagosMetodoGeneral(e.target.value)}
                        style={{ width: '100%', padding: '10px 14px', fontSize: '13px', border: '2px solid #d1fae5', borderRadius: '8px', backgroundColor: '#f0fdf4' }}
                      >
                        <option value="efectivo">💵 Efectivo</option>
                        <option value="transferencia">🏦 Transferencia</option>
                      </select>
                    ) : (
                      <div style={{ maxHeight: '150px', overflow: 'auto', border: '1px solid #d1fae5', borderRadius: '8px', padding: '10px', backgroundColor: '#f0fdf4' }}>
                        {(() => {
                          const nominaSel = nominasGuardadas.find(n => n.id === planillaPagosNominaId);
                          const itemsNomina = nominaSel?.items || [];
                          return itemsNomina
                            .filter(item => planillaPagosEmpleados.includes(item.empleadoId))
                            .map((item) => (
                              <div key={item.empleadoId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e5e7eb' }}>
                                <span style={{ fontSize: '13px' }}>{item.empleadoNombre}</span>
                                <select
                                  value={planillaPagosMetodos[item.empleadoId] || "efectivo"}
                                  onChange={(e) => setPlanillaPagosMetodos(prev => ({ ...prev, [item.empleadoId]: e.target.value }))}
                                  style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid #d1fae5', borderRadius: '6px', backgroundColor: 'white' }}
                                >
                                  <option value="efectivo">💵 Efectivo</option>
                                  <option value="transferencia">🏦 Transferencia</option>
                                </select>
                              </div>
                            ));
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Botón generar */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                  <button
                    onClick={() => setPlanillaPagosOpen(false)}
                    style={{ padding: '10px 20px', fontSize: '13px', fontWeight: '600', border: '2px solid #e5e7eb', borderRadius: '8px', background: 'white', color: '#666', cursor: 'pointer' }}
                  >Cancelar</button>
                  <button
                    onClick={() => {
                      if (!planillaPagosNominaId) { alert("Selecciona una nómina"); return; }
                      if (planillaPagosEmpleados.length === 0) { alert("Selecciona al menos un empleado"); return; }
                      setPlanillaPagosGenerada(true);
                    }}
                    disabled={!planillaPagosNominaId || planillaPagosEmpleados.length === 0}
                    style={{
                      padding: '10px 24px',
                      fontSize: '13px',
                      fontWeight: '600',
                      border: 'none',
                      borderRadius: '8px',
                      background: (!planillaPagosNominaId || planillaPagosEmpleados.length === 0) ? '#ccc' : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                      color: 'white',
                      cursor: (!planillaPagosNominaId || planillaPagosEmpleados.length === 0) ? 'not-allowed' : 'pointer',
                      boxShadow: '0 2px 4px rgba(5, 150, 105, 0.3)'
                    }}
                  >📋 Generar Planilla</button>
                </div>
              </>
            ) : (
              /* Vista de planilla generada */
              (() => {
                const nominaSel = nominasGuardadas.find(n => n.id === planillaPagosNominaId);
                const itemsNomina = nominaSel?.items || [];
                const itemsSeleccionados = itemsNomina.filter(item => planillaPagosEmpleados.includes(item.empleadoId));
                
                // Determinar quincena
                const rangoInicio = nominaSel?.rango?.inicio || "";
                const dia = rangoInicio ? parseInt(rangoInicio.split("-")[2] || "1") : 1;
                const esQuincena1 = dia <= 15;
                const quincenaLabel = esQuincena1 ? "Quincena 1" : "Quincena 2";

                const totalGeneral = itemsSeleccionados.reduce((acc, item) => {
                  return {
                    devengados: acc.devengados + (item.totalDevengado || 0),
                    deducciones: acc.deducciones + (item.totalDescuentos || 0),
                    netoPagar: acc.netoPagar + (item.neto || 0)
                  };
                }, { devengados: 0, deducciones: 0, netoPagar: 0 });

                return (
                  <>
                    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #d1fae5' }}>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#059669' }}>📅 {nominaSel?.nombre} - {quincenaLabel}</p>
                      <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>
                        {nominaSel?.rango?.inicio && nominaSel?.rango?.fin ? `Del ${nominaSel.rango.inicio} al ${nominaSel.rango.fin} | ` : ''}
                        {itemsSeleccionados.length} empleado(s) seleccionado(s)
                      </p>
                    </div>

                    {/* Tabla horizontal */}
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '900px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#059669', color: 'white' }}>
                            <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #047857', whiteSpace: 'nowrap' }}>Empleado</th>
                            <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #047857', whiteSpace: 'nowrap' }}>Documento</th>
                            <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #047857', whiteSpace: 'nowrap' }}>Total Devengado</th>
                            <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #047857', whiteSpace: 'nowrap' }}>Total Descuentos</th>
                            <th style={{ padding: '10px 8px', textAlign: 'right', borderBottom: '2px solid #047857', whiteSpace: 'nowrap' }}>Neto a Pagar</th>
                            <th style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '2px solid #047857', whiteSpace: 'nowrap' }}>Método Pago</th>
                            <th style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '2px solid #047857', whiteSpace: 'nowrap', minWidth: '150px' }}>Acepto a Conformidad</th>
                          </tr>
                        </thead>
                        <tbody>
                          {itemsSeleccionados.map((item, idx) => {
                            const metodo = planillaPagosModoMetodo === "mismo" ? planillaPagosMetodoGeneral : (planillaPagosMetodos[item.empleadoId] || "efectivo");
                            return (
                              <tr key={item.empleadoId} style={{ backgroundColor: idx % 2 === 0 ? '#f9fafb' : 'white' }}>
                                <td style={{ padding: '10px 8px', borderBottom: '1px solid #e5e7eb', fontWeight: '500' }}>{item.empleadoNombre || 'N/A'}</td>
                                <td style={{ padding: '10px 8px', borderBottom: '1px solid #e5e7eb' }}>{item.documento || 'N/A'}</td>
                                <td style={{ padding: '10px 8px', borderBottom: '1px solid #e5e7eb', textAlign: 'right', color: '#059669', fontWeight: '600' }}>${(item.totalDevengado || 0).toLocaleString('es-CO')}</td>
                                <td style={{ padding: '10px 8px', borderBottom: '1px solid #e5e7eb', textAlign: 'right', color: '#dc2626', fontWeight: '600' }}>${(item.totalDescuentos || 0).toLocaleString('es-CO')}</td>
                                <td style={{ padding: '10px 8px', borderBottom: '1px solid #e5e7eb', textAlign: 'right', color: '#1d4ed8', fontWeight: '700' }}>${(item.neto || 0).toLocaleString('es-CO')}</td>
                                <td style={{ padding: '10px 8px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                                  <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: metodo === 'efectivo' ? '#fef3c7' : '#dbeafe', color: metodo === 'efectivo' ? '#92400e' : '#1e40af' }}>
                                    {metodo === 'efectivo' ? '💵 Efectivo' : '🏦 Transferencia'}
                                  </span>
                                </td>
                                <td style={{ padding: '10px 8px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>
                                  <div style={{ borderBottom: '1px solid #333', width: '120px', height: '30px', margin: '0 auto' }}></div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr style={{ backgroundColor: '#d1fae5', fontWeight: '700' }}>
                            <td colSpan="2" style={{ padding: '12px 8px', borderTop: '2px solid #059669' }}>TOTALES</td>
                            <td style={{ padding: '12px 8px', borderTop: '2px solid #059669', textAlign: 'right', color: '#059669' }}>${totalGeneral.devengados.toLocaleString('es-CO')}</td>
                            <td style={{ padding: '12px 8px', borderTop: '2px solid #059669', textAlign: 'right', color: '#dc2626' }}>${totalGeneral.deducciones.toLocaleString('es-CO')}</td>
                            <td style={{ padding: '12px 8px', borderTop: '2px solid #059669', textAlign: 'right', color: '#1d4ed8' }}>${totalGeneral.netoPagar.toLocaleString('es-CO')}</td>
                            <td colSpan="2" style={{ padding: '12px 8px', borderTop: '2px solid #059669' }}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* Botones */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', gap: '10px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => setPlanillaPagosGenerada(false)}
                        style={{ padding: '10px 20px', fontSize: '13px', fontWeight: '600', border: '2px solid #e5e7eb', borderRadius: '8px', background: 'white', color: '#666', cursor: 'pointer' }}
                      >⬅️ Volver</button>
                      <button
                        onClick={() => {
                          const printWindow = window.open('', '_blank');
                          printWindow.document.write(`
                            <!DOCTYPE html>
                            <html>
                            <head>
                              <title>Planilla de Pagos - ${nominaSel?.nombre} - ${quincenaLabel}</title>
                              <style>
                                @page { size: landscape; margin: 5mm; }
                                body { font-family: 'Segoe UI', Arial, sans-serif; padding: 8px; color: #1a1a1a; margin: 0; }
                                .header { text-align: center; margin-bottom: 6px; border-bottom: 2px solid #1a1a1a; padding-bottom: 4px; }
                                h1 { color: #1a1a1a; font-size: 20px; margin: 0; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; }
                                .subtitle { font-size: 12px; color: #555; margin-top: 2px; }
                                .info { margin-bottom: 6px; font-size: 11px; color: #333; display: flex; justify-content: space-between; gap: 10px; padding: 4px 8px; background: #f5f5f5; border: 1px solid #ddd; }
                                .info-label { font-weight: 600; color: #1a1a1a; }
                                table { width: 100%; border-collapse: collapse; font-size: 13px; border: 1px solid #999; }
                                th { background-color: #2c3e50; color: white; padding: 5px 4px; text-align: left; font-weight: 600; font-size: 11px; text-transform: uppercase; border: 1px solid #1a252f; }
                                td { padding: 4px 4px; border: 1px solid #ccc; vertical-align: middle; }
                                tr:nth-child(even) { background-color: #f9f9f9; }
                                .text-right { text-align: right; }
                                .text-center { text-align: center; }
                                .currency { font-size: 13px; font-weight: 600; font-family: 'Consolas', monospace; }
                                .currency-positive { color: #1a1a1a; }
                                .currency-negative { color: #c0392b; }
                                .currency-total { color: #1a1a1a; font-weight: 700; font-size: 14px; }
                                .metodo { padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 600; }
                                .metodo-efectivo { background: #eee; border: 1px solid #ccc; color: #333; }
                                .metodo-transferencia { background: #e3f2fd; border: 1px solid #90caf9; color: #1565c0; }
                                .firma-container { min-width: 120px; text-align: center; }
                                .firma-linea { border-bottom: 1px solid #333; width: 110px; height: 25px; margin: 0 auto 2px auto; }
                                .firma-texto { font-size: 8px; color: #666; }
                                tfoot tr { background-color: #2c3e50 !important; color: white; }
                                tfoot td { padding: 5px 4px; font-weight: 700; font-size: 13px; border: 1px solid #1a252f; }
                                tfoot .currency { color: white; font-size: 14px; }
                                @media print { 
                                  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                                }
                              </style>
                            </head>
                            <body>
                              <div class="header">
                                <h1>Planilla de Pagos</h1>
                                <div class="subtitle">${nominaSel?.nombre} - ${quincenaLabel}</div>
                              </div>
                              <div class="info">
                                <span><span class="info-label">Período:</span> ${nominaSel?.rango?.inicio || ''} al ${nominaSel?.rango?.fin || ''}</span>
                                <span><span class="info-label">Emisión:</span> ${new Date().toLocaleDateString('es-CO')}</span>
                                <span><span class="info-label">Empleados:</span> ${itemsSeleccionados.length}</span>
                              </div>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Empleado</th>
                                    <th>Documento</th>
                                    <th class="text-right">Total Devengado</th>
                                    <th class="text-right">Total Descuentos</th>
                                    <th class="text-right">Neto a Pagar</th>
                                    <th class="text-center">Método de Pago</th>
                                    <th class="text-center">Firma - Acepto a Conformidad</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  ${itemsSeleccionados.map((item, idx) => {
                                    const metodo = planillaPagosModoMetodo === "mismo" ? planillaPagosMetodoGeneral : (planillaPagosMetodos[item.empleadoId] || "efectivo");
                                    return `
                                      <tr>
                                        <td style="font-weight:500">${item.empleadoNombre || 'N/A'}</td>
                                        <td>${item.documento || 'N/A'}</td>
                                        <td class="text-right currency currency-positive">$${(item.totalDevengado || 0).toLocaleString('es-CO')}</td>
                                        <td class="text-right currency currency-negative">$${(item.totalDescuentos || 0).toLocaleString('es-CO')}</td>
                                        <td class="text-right currency currency-total">$${(item.neto || 0).toLocaleString('es-CO')}</td>
                                        <td class="text-center"><span class="metodo ${metodo === 'efectivo' ? 'metodo-efectivo' : 'metodo-transferencia'}">${metodo === 'efectivo' ? 'Efectivo' : 'Transferencia'}</span></td>
                                        <td class="firma-container"><div class="firma-linea"></div><div class="firma-texto">Firma del empleado</div></td>
                                      </tr>
                                    `;
                                  }).join('')}
                                </tbody>
                                <tfoot>
                                  <tr>
                                    <td colspan="2"><strong>TOTALES GENERALES</strong></td>
                                    <td class="text-right currency">$${totalGeneral.devengados.toLocaleString('es-CO')}</td>
                                    <td class="text-right currency">$${totalGeneral.deducciones.toLocaleString('es-CO')}</td>
                                    <td class="text-right currency">$${totalGeneral.netoPagar.toLocaleString('es-CO')}</td>
                                    <td colspan="2"></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </body>
                            </html>
                          `);
                          printWindow.document.close();
                          printWindow.focus();
                          setTimeout(() => { printWindow.print(); }, 500);
                        }}
                        style={{
                          padding: '10px 24px',
                          fontSize: '13px',
                          fontWeight: '600',
                          border: 'none',
                          borderRadius: '8px',
                          background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                          color: 'white',
                          cursor: 'pointer',
                          boxShadow: '0 2px 4px rgba(5, 150, 105, 0.3)'
                        }}
                      >🖨️ Imprimir Planilla</button>
                    </div>
                  </>
                );
              })()
            )}
          </div>
        </div>
      )}

      {/* Modal Desprendible (externo) */}
      {slipOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 8px 24px rgba(255, 107, 53, 0.25)', padding: '20px', width: '760px', maxWidth: '95vw', border: '2px solid #ffc53d' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: '#ff6b35', borderBottom: '2px solid #ffe4c9', paddingBottom: '10px' }}>📋 Desprendible de pago</h3>
 
<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📄 Nómina</label>
                <select
                  value={slipNominaId}
                  onChange={(e) => {
                    setSlipNominaId(e.target.value);
                    setSlipEmpleadoId("");
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    fontSize: '13px',
                    border: '2px solid #ffc53d',
                    borderRadius: '8px',
                    backgroundColor: '#fffbf5',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">-- Selecciona --</option>
                  {nominasGuardadas.map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.nombre} ({n.rango?.inicio} → {n.rango?.fin})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>👁️ Tipo de desprendible</label>
                <select
                  value={slipTipo}
                  onChange={(e) => setSlipTipo(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    fontSize: '13px',
                    border: '2px solid #ffc53d',
                    borderRadius: '8px',
                    backgroundColor: '#fffbf5',
                    cursor: 'pointer',
                  }}
                >
                  <option value="total">👥 Total (todos los empleados)</option>
                  <option value="individual">👤 Individual (por empleado)</option>
                </select>
              </div>
 
              {slipTipo === "individual" && (
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>👤 Empleado</label>
                  <select
                    value={slipEmpleadoId}
                    onChange={(e) => setSlipEmpleadoId(e.target.value)}
                    disabled={!slipNominaId}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      fontSize: '13px',
                      border: '2px solid #ffc53d',
                      borderRadius: '8px',
                      backgroundColor: !slipNominaId ? '#f3f4f6' : '#fffbf5',
                      cursor: !slipNominaId ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <option value="">-- Selecciona --</option>
                    {(() => {
                      const nom = nominasGuardadas.find((x) => x.id === slipNominaId);
                      const items = nom?.items || [];
                      return items.map((it) => (
                        <option key={it.empleadoId} value={it.empleadoId}>
                          {it.empleadoNombre} – {it.documento}
                        </option>
                      ));
                    })()}
                  </select>
                </div>
              )}
            </div>
 
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setSlipOpen(false)}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#ff6b35',
                  cursor: 'pointer',
                }}
              >
                ❌ Cerrar
              </button>
              <button
                onClick={() => {
                  const nom = nominasGuardadas.find((x) => x.id === slipNominaId);
                  if (!nom) { alert("Selecciona la nómina para generar desprendible."); return; }
                  if (slipTipo === "total") {
                    imprimirDesprendibleTotal(nom);
                  } else {
                    if (!slipEmpleadoId) { alert("Selecciona el empleado (vista individual)."); return; }
                    const reg = buildRegistroIndividual(nom, slipEmpleadoId);
                    if (!reg) { alert("No se encontró información para ese empleado."); return; }
                    // RESUMEN sin fechas/horarios
                    imprimirDesprendibleIndividual(reg);
                  }
                }}
                style={{
                  padding: '10px 20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  boxShadow: '0 2px 4px rgba(255, 107, 53, 0.3)',
                }}
              >
                📋 Generar desprendible
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* Modal selección de empleados */}
      {selectOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 8px 24px rgba(255, 107, 53, 0.25)', padding: '20px', width: '520px', maxWidth: '95vw', border: '2px solid #ffc53d' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: '#ff6b35', borderBottom: '2px solid #ffe4c9', paddingBottom: '10px' }}>👥 Seleccionar empleados (activos y no liquidados en esta quincena)</h3>
            <div style={{ maxHeight: '320px', overflow: 'auto', border: '1px solid #ffe4c9', borderRadius: '8px', padding: '12px', backgroundColor: '#fffbf5' }}>
              {empleados.filter((e) => e.activo && !liquidadosSet.has(e.id)).map((e) => (
                <label key={e.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', borderBottom: '1px solid #ffe4c9', padding: '8px 0' }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(e.id)}
                    onChange={(ev) => {
                      const checked = ev.target.checked;
                      setSelectedIds((prev) =>
                        checked ? Array.from(new Set([...prev, e.id])) : prev.filter((x) => x !== e.id)
                      );
                    }}
                    style={{ accentColor: '#ff6b35', width: '16px', height: '16px' }}
                  />
                  <span style={{ color: '#333' }}>{e.nombre} – {e.tipoDoc} {e.documento}</span>
                </label>
              ))}
              {!empleados.filter((e) => e.activo && !liquidadosSet.has(e.id)).length && (
                <div style={{ color: '#999', fontSize: '13px', textAlign: 'center', padding: '16px' }}>💭 No hay empleados disponibles para esta quincena.</div>
              )}
            </div>
 
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setSelectedIds(empleados.filter((e) => e.activo && !liquidadosSet.has(e.id)).map((e) => e.id))}
                  style={{
                    padding: '10px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    border: '2px solid #ffc53d',
                    borderRadius: '8px',
                    background: 'white',
                    color: '#ff6b35',
                    cursor: 'pointer',
                  }}
                >
                  ✅ Seleccionar todos
                </button>
                <button
                  onClick={() => setSelectedIds([])}
                  style={{
                    padding: '10px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    border: '2px solid #ffc53d',
                    borderRadius: '8px',
                    background: 'white',
                    color: '#ff6b35',
                    cursor: 'pointer',
                  }}
                >
                  🗑️ Limpiar
                </button>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => setSelectOpen(false)}
                  style={{
                    padding: '10px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    border: '2px solid #ffc53d',
                    borderRadius: '8px',
                    background: 'white',
                    color: '#ff6b35',
                    cursor: 'pointer',
                  }}
                >
                  ❌ Cancelar
                </button>
                <button
                  onClick={() => setSelectOpen(false)}
                  style={{
                    padding: '10px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    border: 'none',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
                    color: 'white',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(255, 107, 53, 0.3)',
                  }}
                >
                  💾 Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
 
     
      {/* Modal Comidas */}
{modalComidasOpen && (
  <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
    <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 8px 24px rgba(255, 107, 53, 0.25)', padding: '20px', width: '650px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', border: '2px solid #ffc53d' }}>
      <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: '#ff6b35', borderBottom: '2px solid #ffe4c9', paddingBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>🍝 Comidas – {comidasModoHistorial ? 'Historial' : `${mesNomina} Q${quincena}`}</span>
        <button
          onClick={() => setComidasModoHistorial(!comidasModoHistorial)}
          style={{
            padding: '6px 12px',
            fontSize: '11px',
            fontWeight: '600',
            border: '1px solid #ffc53d',
            borderRadius: '6px',
            background: comidasModoHistorial ? '#ff6b35' : 'white',
            color: comidasModoHistorial ? 'white' : '#ff6b35',
            cursor: 'pointer',
          }}
        >
          {comidasModoHistorial ? '📝 Editar Quincena Actual' : '📋 Ver Historial'}
        </button>
      </h3>

      {/* Modo: Editar quincena actual */}
      {!comidasModoHistorial && (
        <>
          <div style={{ maxHeight: '320px', overflow: 'auto', border: '1px solid #ffe4c9', borderRadius: '8px', padding: '12px', backgroundColor: '#fffbf5' }}>
            {empleados
              .filter((e) => e.activo && !liquidadosSet.has(e.id) && selectedIds.includes(e.id))
              .map((e) => (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #ffe4c9' }}>
                  <div style={{ fontSize: '13px', color: '#333' }}>
                    {e.nombre} – {e.tipoDoc} {e.documento}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#52c41a', fontWeight: '600' }}>$</span>
                    <input
                      type="number"
                      value={getComidaEmpleado(e.id)}
                      onChange={(ev) => setComidaEmpleado(e.id, ev.target.value)}
                      style={{
                        width: '120px',
                        padding: '8px 12px',
                        fontSize: '13px',
                        border: '2px solid #ffc53d',
                        borderRadius: '8px',
                        backgroundColor: '#fffbf5',
                        textAlign: 'right',
                      }}
                    />
                  </div>
                </div>
              ))}

            {!empleados
              .filter((e) => e.activo && !liquidadosSet.has(e.id) && selectedIds.includes(e.id))
              .length && (
              <div style={{ color: '#999', fontSize: '13px', textAlign: 'center', padding: '16px' }}>💭 No hay empleados seleccionados.</div>
            )}
          </div>
          
          {/* Total de la quincena actual */}
          <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#fef3cd', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#856404' }}>📊 Total Quincena Actual:</span>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#ff6b35' }}>
              ${getTotalComidasPeriodo(claveComidas(mesNomina, quincena)).toLocaleString()}
            </span>
          </div>
        </>
      )}

      {/* Modo: Historial de comidas */}
      {comidasModoHistorial && (
        <>
          {/* Selector de período */}
          <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
            <label style={{ fontSize: '13px', fontWeight: '600', color: '#666' }}>📅 Período:</label>
            <select
              value={comidasPeriodoConsulta}
              onChange={(e) => setComidasPeriodoConsulta(e.target.value)}
              style={{
                padding: '8px 12px',
                fontSize: '13px',
                border: '2px solid #ffc53d',
                borderRadius: '8px',
                minWidth: '180px',
              }}
            >
              <option value="">Seleccionar período...</option>
              {getPeriodosComidas().map((periodo) => (
                <option key={periodo} value={periodo}>
                  {periodo} - Total: ${getTotalComidasPeriodo(periodo).toLocaleString()}
                </option>
              ))}
            </select>
          </div>

          {/* Tabla de comidas del período seleccionado */}
          {comidasPeriodoConsulta && (
            <div style={{ border: '1px solid #ffe4c9', borderRadius: '8px', overflow: 'auto', maxHeight: '300px' }}>
              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#ffc53d', color: 'white' }}>
                    <th style={{ padding: '10px', textAlign: 'left', position: 'sticky', top: 0, backgroundColor: '#ffc53d' }}>Empleado</th>
                    <th style={{ padding: '10px', textAlign: 'right', position: 'sticky', top: 0, backgroundColor: '#ffc53d' }}>Valor Comidas</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(getComidasPeriodo(comidasPeriodoConsulta))
                    .filter(([empId, valor]) => {
                      // Solo mostrar empleados de la empresa actual
                      const emp = empleados.find((e) => e.id === empId);
                      return emp && Number(valor) > 0;
                    })
                    .map(([empId, valor]) => {
                      const emp = empleados.find((e) => e.id === empId);
                      return (
                        <tr key={empId} style={{ borderBottom: '1px solid #ffe4c9' }}>
                          <td style={{ padding: '10px' }}>{emp?.nombre || `ID: ${empId}`}</td>
                          <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#ff6b35' }}>
                            ${Number(valor).toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: '#fef3cd', fontWeight: 'bold' }}>
                    <td style={{ padding: '10px' }}>📊 TOTAL (esta empresa)</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: '#ff6b35' }}>
                      ${(() => {
                        // Calcular total solo para empleados de esta empresa
                        const comidasPeriodo = getComidasPeriodo(comidasPeriodoConsulta);
                        return Object.entries(comidasPeriodo)
                          .filter(([empId]) => empleados.find(e => e.id === empId))
                          .reduce((sum, [_, v]) => sum + Number(v || 0), 0)
                          .toLocaleString();
                      })()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {!comidasPeriodoConsulta && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
              📋 Seleccione un período para ver el historial de comidas
            </div>
          )}

          {getPeriodosComidas().length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
              💭 No hay historial de comidas registrado
            </div>
          )}
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', gap: '10px' }}>
        <button
          onClick={() => { setModalComidasOpen(false); setComidasModoHistorial(false); }}
          style={{
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: '2px solid #ffc53d',
            borderRadius: '8px',
            background: 'white',
            color: '#ff6b35',
            cursor: 'pointer',
          }}
        >
          ❌ Cerrar
        </button>
        {!comidasModoHistorial && (
          <button
            onClick={async () => { await guardarComidasEnSupabase(); }}
            style={{
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
              color: 'white',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(255, 107, 53, 0.3)',
            }}
          >
            💾 Guardar en Base de Datos
          </button>
        )}
      </div>
    </div>
  </div>
)}

{/* Modal Otros Descuentos */}
{modalOtrosDescuentosOpen && (
  <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
    <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 8px 24px rgba(220, 38, 38, 0.25)', padding: '20px', width: '700px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', border: '2px solid #dc2626' }}>
      <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: '#dc2626', borderBottom: '2px solid #fecaca', paddingBottom: '10px' }}>
        📋 Otros Descuentos Programados
      </h3>

      {/* Formulario para nuevo descuento */}
      <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#dc2626', marginBottom: '12px' }}>➕ Agregar nuevo descuento</h4>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>👤 Empleado *</label>
            <select
              value={nuevoDescuento.empleadoId}
              onChange={(e) => setNuevoDescuento(prev => ({ ...prev, empleadoId: e.target.value }))}
              style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #fca5a5', borderRadius: '6px' }}
            >
              <option value="">Seleccionar empleado...</option>
              {[...(empleados || [])].filter(e => e.activo).sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map(e => (
                <option key={e.id} value={e.id}>{e.nombre}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>📝 Concepto *</label>
            <input
              type="text"
              placeholder="Ej: Seguro adicional, Ahorro, Daño..."
              value={nuevoDescuento.concepto}
              onChange={(e) => setNuevoDescuento(prev => ({ ...prev, concepto: e.target.value }))}
              style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #fca5a5', borderRadius: '6px' }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>� Tipo de descuento</label>
            <select
              value={nuevoDescuento.tipo}
              onChange={(e) => setNuevoDescuento(prev => ({ ...prev, tipo: e.target.value, valorTotal: "", meses: "", valorMensual: "", valorQuincenalDirecto: "" }))}
              style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #fca5a5', borderRadius: '6px' }}
            >
              <option value="indefinido">♾️ Indefinido (mensual dividido en 2 quincenas)</option>
              <option value="1_quincena">1️⃣ Solo 1 quincena (valor completo)</option>
              <option value="meses">📆 Por meses (valor total o meses fijos)</option>
            </select>
          </div>
          
          {nuevoDescuento.tipo === "1_quincena" ? (
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>💰 Valor a descontar * ($)</label>
              <input
                type="number"
                placeholder="Valor total a descontar en 1 quincena"
                value={nuevoDescuento.valorQuincenalDirecto}
                onChange={(e) => setNuevoDescuento(prev => ({ ...prev, valorQuincenalDirecto: e.target.value, valorMensual: e.target.value }))}
                style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #fca5a5', borderRadius: '6px' }}
              />
              {nuevoDescuento.valorQuincenalDirecto && (
                <span style={{ fontSize: '10px', color: '#dc2626', fontWeight: '600' }}>⚡ Se descontará completo en la próxima quincena</span>
              )}
            </div>
          ) : (
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>💰 Valor Mensual * ($)</label>
              <input
                type="number"
                placeholder="Valor a descontar cada mes"
                value={nuevoDescuento.valorMensual}
                onChange={(e) => setNuevoDescuento(prev => ({ ...prev, valorMensual: e.target.value }))}
                style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #fca5a5', borderRadius: '6px' }}
              />
              {nuevoDescuento.valorMensual && (
                <span style={{ fontSize: '10px', color: '#16a34a' }}>Quincenal: ${Math.round(Number(nuevoDescuento.valorMensual) / 2).toLocaleString()}</span>
              )}
            </div>
          )}
          
          {nuevoDescuento.tipo === "meses" && (
            <>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>💵 Valor Total Neto (opcional)</label>
                <input
                  type="number"
                  placeholder="Deuda total a pagar"
                  value={nuevoDescuento.valorTotal}
                  onChange={(e) => setNuevoDescuento(prev => ({ ...prev, valorTotal: e.target.value, meses: "" }))}
                  style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #fca5a5', borderRadius: '6px' }}
                />
                {nuevoDescuento.valorTotal && nuevoDescuento.valorMensual && (
                  <span style={{ fontSize: '10px', color: '#666' }}>
                    ≈ {Math.ceil(Number(nuevoDescuento.valorTotal) / Number(nuevoDescuento.valorMensual))} meses
                  </span>
                )}
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>🔢 O número de meses</label>
                <input
                  type="number"
                  placeholder="Cantidad de meses"
                  value={nuevoDescuento.meses}
                  onChange={(e) => setNuevoDescuento(prev => ({ ...prev, meses: e.target.value, valorTotal: "" }))}
                  style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #fca5a5', borderRadius: '6px' }}
                  disabled={nuevoDescuento.valorTotal}
                />
              </div>
            </>
          )}
        </div>
        
        <button
          onClick={agregarOtroDescuento}
          style={{
            marginTop: '12px',
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
            color: 'white',
            cursor: 'pointer',
            width: '100%',
          }}
        >
          ➕ Agregar Descuento
        </button>
      </div>

      {/* Lista de descuentos activos */}
      <div style={{ marginBottom: '16px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>✅ Descuentos Activos</h4>
        <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          {getOtrosDescuentosEmpresaActual().filter(d => d.activo).length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
              💭 No hay descuentos activos
            </div>
          ) : (
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Empleado</th>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Concepto</th>
                  <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Quincenal</th>
                  <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Tipo</th>
                  <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Restante</th>
                  <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {getOtrosDescuentosEmpresaActual().filter(d => d.activo).map(d => {
                  const emp = empleados.find(e => e.id === d.empleadoId);
                  return (
                    <tr key={d.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px' }}>{emp?.nombre || 'Desconocido'}</td>
                      <td style={{ padding: '8px' }}>{d.concepto}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#dc2626' }}>
                        ${d.valorQuincenal?.toLocaleString() || Math.round(d.valorMensual / 2).toLocaleString()}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: '600',
                          backgroundColor: d.tipo === 'indefinido' ? '#dbeafe' : d.esQuincenaUnica ? '#f0fdf4' : '#fef3c7',
                          color: d.tipo === 'indefinido' ? '#1d4ed8' : d.esQuincenaUnica ? '#16a34a' : '#d97706'
                        }}>
                          {d.tipo === 'indefinido' ? '♾️ Indefinido' : d.esQuincenaUnica ? '1️⃣ 1 Quincena' : `📆 ${d.mesesRestantes}/${d.mesesOriginales} meses`}
                        </span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px' }}>
                        {d.tipo === 'meses' 
                          ? (d.origenNominaId || d.concepto?.startsWith('Saldo Q')
                              ? `$${(d.valorQuincenal || Math.round(d.valorMensual / 2)).toLocaleString()}`
                              : `$${(d.mesesRestantes * d.valorMensual).toLocaleString()}`)
                          : '—'}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <button
                          onClick={() => eliminarOtroDescuento(d.id)}
                          style={{
                            padding: '4px 8px',
                            fontSize: '11px',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            cursor: 'pointer'
                          }}
                        >
                          🗑️ Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Lista de descuentos inactivos */}
      {getOtrosDescuentosEmpresaActual().filter(d => !d.activo).length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#9ca3af', marginBottom: '8px' }}>📦 Descuentos Finalizados/Eliminados</h4>
          <div style={{ maxHeight: '120px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
              <tbody>
                {getOtrosDescuentosEmpresaActual().filter(d => !d.activo).map(d => {
                  const emp = empleados.find(e => e.id === d.empleadoId);
                  return (
                    <tr key={d.id} style={{ borderBottom: '1px solid #f3f4f6', color: '#9ca3af' }}>
                      <td style={{ padding: '6px 8px' }}>{emp?.nombre || 'Desconocido'}</td>
                      <td style={{ padding: '6px 8px' }}>{d.concepto}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>${d.valorQuincenal?.toLocaleString()}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <button
                          onClick={() => reactivarDescuento(d.id)}
                          style={{
                            padding: '3px 6px',
                            fontSize: '10px',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: '#d1fae5',
                            color: '#059669',
                            cursor: 'pointer'
                          }}
                        >
                          ♻️ Reactivar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button
          onClick={() => setModalOtrosDescuentosOpen(false)}
          style={{
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: '2px solid #dc2626',
            borderRadius: '8px',
            background: 'white',
            color: '#dc2626',
            cursor: 'pointer',
          }}
        >
          ❌ Cerrar
        </button>
      </div>
    </div>
  </div>
)}

{/* Modal Ajustes de Nómina */}
{modalAjustesNominaOpen && (
  <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
    <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 8px 24px rgba(5, 150, 105, 0.25)', padding: '20px', width: '900px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', border: '2px solid #059669' }}>
      <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: '#059669', borderBottom: '2px solid #d1fae5', paddingBottom: '10px' }}>
        ⚖️ Ajustes de Nómina
      </h3>

      {/* Formulario para nuevo ajuste */}
      <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#059669', marginBottom: '12px' }}>➕ Crear nuevo ajuste</h4>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          {/* Empleado */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>👤 Empleado *</label>
            <select
              value={nuevoAjuste.empleadoId}
              onChange={(e) => setNuevoAjuste(prev => ({ ...prev, empleadoId: e.target.value }))}
              style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #86efac', borderRadius: '6px' }}
            >
              <option value="">Seleccionar empleado...</option>
              {[...(empleados || [])].filter(e => e.activo).sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map(e => (
                <option key={e.id} value={e.id}>{e.nombre}</option>
              ))}
            </select>
          </div>
          
          {/* A favor de */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>🎯 A favor de *</label>
            <select
              value={nuevoAjuste.tipoBeneficiario}
              onChange={(e) => setNuevoAjuste(prev => ({ ...prev, tipoBeneficiario: e.target.value }))}
              style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #86efac', borderRadius: '6px' }}
            >
              <option value="empleado">👤 Empleado (SUMA al pago)</option>
              <option value="empresa">🏢 Empresa (RESTA del pago)</option>
            </select>
          </div>
          
          {/* Tipo de ajuste */}
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>📋 Tipo de ajuste *</label>
            <select
              value={nuevoAjuste.tipoAjuste}
              onChange={(e) => {
                setNuevoAjuste(prev => ({ ...prev, tipoAjuste: e.target.value, fechasAjuste: [] }));
                setFechaAjusteTemp({ fecha: "", e1: "", s1: "", e2: "", s2: "", tipoHora: "normal", cantidadHoras: "", descripcion: "" });
              }}
              style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #86efac', borderRadius: '6px' }}
            >
              <option value="horario">🕐 Ajuste de Horario (ingresar horario)</option>
              <option value="descanso">😴 Descanso Compensado ({HORAS_DESCANSO}h)</option>
              <option value="horas">🔢 Cantidad de Horas (manual)</option>
            </select>
          </div>
        </div>

        {/* Concepto general (opcional) */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>📝 Concepto/Observación (opcional - se genera automáticamente)</label>
          <input
            type="text"
            placeholder="Dejar vacío para generar automáticamente..."
            value={nuevoAjuste.concepto}
            onChange={(e) => setNuevoAjuste(prev => ({ ...prev, concepto: e.target.value }))}
            style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #86efac', borderRadius: '6px' }}
          />
        </div>

        {/* === SECCIÓN PARA TIPO HORARIO === */}
        {nuevoAjuste.tipoAjuste === "horario" && (
          <div style={{ backgroundColor: '#ecfdf5', border: '1px dashed #10b981', borderRadius: '6px', padding: '12px', marginBottom: '12px' }}>
            <h5 style={{ fontSize: '12px', fontWeight: '600', color: '#047857', marginBottom: '10px' }}>🕐 Ingresar horario del día (se calculan horas automáticamente)</h5>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
              {/* Fecha */}
              <div>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: '600', color: '#666', marginBottom: '3px' }}>📅 Fecha *</label>
                <input
                  type="date"
                  value={fechaAjusteTemp.fecha}
                  onChange={(e) => setFechaAjusteTemp(prev => ({ ...prev, fecha: e.target.value }))}
                  style={{ width: '100%', padding: '6px', fontSize: '11px', border: '1px solid #a7f3d0', borderRadius: '4px' }}
                />
              </div>
              
              {/* Turno 1 - Entrada */}
              <div>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: '600', color: '#666', marginBottom: '3px' }}>⏰ Entrada T1 *</label>
                <input
                  type="time"
                  value={fechaAjusteTemp.e1}
                  onChange={(e) => setFechaAjusteTemp(prev => ({ ...prev, e1: e.target.value }))}
                  style={{ width: '100%', padding: '6px', fontSize: '11px', border: '1px solid #a7f3d0', borderRadius: '4px' }}
                />
              </div>
              
              {/* Turno 1 - Salida */}
              <div>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: '600', color: '#666', marginBottom: '3px' }}>⏰ Salida T1 *</label>
                <input
                  type="time"
                  value={fechaAjusteTemp.s1}
                  onChange={(e) => setFechaAjusteTemp(prev => ({ ...prev, s1: e.target.value }))}
                  style={{ width: '100%', padding: '6px', fontSize: '11px', border: '1px solid #a7f3d0', borderRadius: '4px' }}
                />
              </div>
              
              {/* Turno 2 - Entrada (opcional) */}
              <div>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: '600', color: '#666', marginBottom: '3px' }}>⏰ Entrada T2</label>
                <input
                  type="time"
                  value={fechaAjusteTemp.e2}
                  onChange={(e) => setFechaAjusteTemp(prev => ({ ...prev, e2: e.target.value }))}
                  style={{ width: '100%', padding: '6px', fontSize: '11px', border: '1px solid #a7f3d0', borderRadius: '4px' }}
                />
              </div>
              
              {/* Turno 2 - Salida (opcional) */}
              <div>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: '600', color: '#666', marginBottom: '3px' }}>⏰ Salida T2</label>
                <input
                  type="time"
                  value={fechaAjusteTemp.s2}
                  onChange={(e) => setFechaAjusteTemp(prev => ({ ...prev, s2: e.target.value }))}
                  style={{ width: '100%', padding: '6px', fontSize: '11px', border: '1px solid #a7f3d0', borderRadius: '4px' }}
                />
              </div>
              
              {/* Botón agregar */}
              <button
                onClick={agregarFechaAjuste}
                style={{
                  padding: '6px 12px',
                  fontSize: '11px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >➕</button>
            </div>
            
            {/* Preview de horas calculadas */}
            {fechaAjusteTemp.fecha && fechaAjusteTemp.e1 && fechaAjusteTemp.s1 && (
              <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#d1fae5', borderRadius: '4px', fontSize: '11px' }}>
                {(() => {
                  const { hNorm, hNoct, hFest, esFestivo } = calcularHorasDeHorario(
                    fechaAjusteTemp.e1, fechaAjusteTemp.s1,
                    fechaAjusteTemp.e2, fechaAjusteTemp.s2,
                    fechaAjusteTemp.fecha
                  );
                  const total = hNorm + hNoct + hFest;
                  const valorEst = (hNorm * TARIFAS.horaNormal) + (hNoct * TARIFAS.horaNocturna) + (hFest * TARIFAS.horaFestiva);
                  return (
                    <span>
                      <strong>Vista previa:</strong> {esFestivo && '🎉 Día festivo - '}
                      {hNorm > 0 && <span style={{ color: '#d97706' }}>{hNorm.toFixed(1)}h norm. </span>}
                      {hNoct > 0 && <span style={{ color: '#4338ca' }}>{hNoct.toFixed(1)}h noct. </span>}
                      {hFest > 0 && <span style={{ color: '#be185d' }}>{hFest.toFixed(1)}h fest. </span>}
                      | Total: {total.toFixed(1)}h ≈ <strong style={{ color: '#059669' }}>${Math.round(valorEst).toLocaleString()}</strong>
                    </span>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* === SECCIÓN PARA TIPO DESCANSO === */}
        {nuevoAjuste.tipoAjuste === "descanso" && (
          <div style={{ backgroundColor: '#fef3c7', border: '1px dashed #f59e0b', borderRadius: '6px', padding: '12px', marginBottom: '12px' }}>
            <h5 style={{ fontSize: '12px', fontWeight: '600', color: '#d97706', marginBottom: '10px' }}>😴 Descanso Compensado ({HORAS_DESCANSO} horas normales por fecha)</h5>
            
            <div style={{ display: 'flex', gap: '8px', alignItems: 'end' }}>
              {/* Fecha */}
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: '600', color: '#666', marginBottom: '3px' }}>📅 Fecha del descanso *</label>
                <input
                  type="date"
                  value={fechaAjusteTemp.fecha}
                  onChange={(e) => setFechaAjusteTemp(prev => ({ ...prev, fecha: e.target.value }))}
                  style={{ width: '100%', padding: '6px', fontSize: '11px', border: '1px solid #fcd34d', borderRadius: '4px' }}
                />
              </div>
              
              {/* Botón agregar */}
              <button
                onClick={agregarFechaAjuste}
                style={{
                  padding: '6px 16px',
                  fontSize: '11px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >➕ Agregar descanso</button>
            </div>
            
            <div style={{ marginTop: '8px', fontSize: '11px', color: '#92400e' }}>
              💡 Se agregarán <strong>{HORAS_DESCANSO} horas normales</strong> (${(HORAS_DESCANSO * TARIFAS.horaNormal).toLocaleString()}) por cada fecha
            </div>
          </div>
        )}

        {/* === SECCIÓN PARA TIPO HORAS (MANUAL) === */}
        {nuevoAjuste.tipoAjuste === "horas" && (
          <div style={{ backgroundColor: '#e0e7ff', border: '1px dashed #6366f1', borderRadius: '6px', padding: '12px', marginBottom: '12px' }}>
            <h5 style={{ fontSize: '12px', fontWeight: '600', color: '#4338ca', marginBottom: '10px' }}>🔢 Ingresar cantidad de horas manualmente</h5>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
              {/* Fecha */}
              <div>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: '600', color: '#666', marginBottom: '3px' }}>📅 Fecha</label>
                <input
                  type="date"
                  value={fechaAjusteTemp.fecha}
                  onChange={(e) => setFechaAjusteTemp(prev => ({ ...prev, fecha: e.target.value }))}
                  style={{ width: '100%', padding: '6px', fontSize: '11px', border: '1px solid #a5b4fc', borderRadius: '4px' }}
                />
              </div>
              
              {/* Tipo de hora */}
              <div>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: '600', color: '#666', marginBottom: '3px' }}>Tipo hora</label>
                <select
                  value={fechaAjusteTemp.tipoHora}
                  onChange={(e) => setFechaAjusteTemp(prev => ({ ...prev, tipoHora: e.target.value }))}
                  style={{ width: '100%', padding: '6px', fontSize: '11px', border: '1px solid #a5b4fc', borderRadius: '4px' }}
                >
                  <option value="normal">☀️ Normal (${TARIFAS.horaNormal.toLocaleString()}/h)</option>
                  <option value="nocturna">🌙 Nocturna (${TARIFAS.horaNocturna.toLocaleString()}/h)</option>
                  <option value="festiva">🎉 Festiva (${TARIFAS.horaFestiva.toLocaleString()}/h)</option>
                </select>
              </div>
              
              {/* Cantidad de horas */}
              <div>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: '600', color: '#666', marginBottom: '3px' }}>Horas</label>
                <input
                  type="number"
                  min="0.5"
                  max="24"
                  step="0.5"
                  placeholder="Ej: 8"
                  value={fechaAjusteTemp.cantidadHoras}
                  onChange={(e) => setFechaAjusteTemp(prev => ({ ...prev, cantidadHoras: e.target.value }))}
                  style={{ width: '100%', padding: '6px', fontSize: '11px', border: '1px solid #a5b4fc', borderRadius: '4px' }}
                />
              </div>
              
              {/* Descripción */}
              <div>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: '600', color: '#666', marginBottom: '3px' }}>Desc. (opc.)</label>
                <input
                  type="text"
                  placeholder="Detalle..."
                  value={fechaAjusteTemp.descripcion}
                  onChange={(e) => setFechaAjusteTemp(prev => ({ ...prev, descripcion: e.target.value }))}
                  style={{ width: '100%', padding: '6px', fontSize: '11px', border: '1px solid #a5b4fc', borderRadius: '4px' }}
                />
              </div>
              
              {/* Botón agregar */}
              <button
                onClick={agregarFechaAjuste}
                style={{
                  padding: '6px 12px',
                  fontSize: '11px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#6366f1',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >➕</button>
            </div>
          </div>
        )}

        {/* Lista de fechas agregadas */}
        {nuevoAjuste.fechasAjuste.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <h5 style={{ fontSize: '12px', fontWeight: '600', color: '#047857', marginBottom: '8px' }}>
              📋 Detalle del ajuste ({[...new Set(nuevoAjuste.fechasAjuste.map(f => f.fecha))].length} fecha(s))
            </h5>
            <div style={{ border: '1px solid #d1fae5', borderRadius: '6px', overflow: 'hidden' }}>
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#ecfdf5' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1fae5' }}>Fecha</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #d1fae5' }}>Tipo</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #d1fae5' }}>Horas</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #d1fae5' }}>$/Hora</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #d1fae5' }}>Subtotal</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #d1fae5' }}>Descripción</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #d1fae5' }}>Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {nuevoAjuste.fechasAjuste.map((f, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f0fdf4' }}>
                      <td style={{ padding: '6px 8px' }}>{f.fecha}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontWeight: '600',
                          backgroundColor: f.tipoHora === 'normal' ? '#fef3c7' : f.tipoHora === 'nocturna' ? '#e0e7ff' : '#fce7f3',
                          color: f.tipoHora === 'normal' ? '#d97706' : f.tipoHora === 'nocturna' ? '#4338ca' : '#be185d'
                        }}>
                          {f.tipoHora === 'normal' ? '☀️ Normal' : f.tipoHora === 'nocturna' ? '🌙 Nocturna' : '🎉 Festiva'}
                        </span>
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '600' }}>{f.cantidadHoras}h</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>${f.valorHora?.toLocaleString()}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '600', color: '#059669' }}>${f.valorTotal?.toLocaleString()}</td>
                      <td style={{ padding: '6px 8px', fontSize: '10px', color: '#666' }}>{f.descripcion || '—'}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <button
                          onClick={() => eliminarFechaAjuste(f.fecha)}
                          style={{
                            padding: '2px 6px',
                            fontSize: '10px',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            cursor: 'pointer'
                          }}
                        >🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ backgroundColor: '#d1fae5' }}>
                    <td colSpan="4" style={{ padding: '8px', fontWeight: '700', textAlign: 'right' }}>TOTAL:</td>
                    <td style={{ padding: '8px', fontWeight: '700', textAlign: 'right', color: '#059669', fontSize: '13px' }}>
                      ${calcularValorAjuste(nuevoAjuste.fechasAjuste).valorTotal.toLocaleString()}
                    </td>
                    <td colSpan="2" style={{ padding: '8px', fontSize: '10px', color: '#666' }}>
                      {calcularValorAjuste(nuevoAjuste.fechasAjuste).totalNormales > 0 && `${calcularValorAjuste(nuevoAjuste.fechasAjuste).totalNormales}h norm. `}
                      {calcularValorAjuste(nuevoAjuste.fechasAjuste).totalNocturnas > 0 && `${calcularValorAjuste(nuevoAjuste.fechasAjuste).totalNocturnas}h noct. `}
                      {calcularValorAjuste(nuevoAjuste.fechasAjuste).totalFestivas > 0 && `${calcularValorAjuste(nuevoAjuste.fechasAjuste).totalFestivas}h fest.`}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Resumen y botón guardar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: '13px' }}>
            {nuevoAjuste.empleadoId && nuevoAjuste.fechasAjuste.length > 0 && (
              <div style={{ 
                padding: '8px 12px', 
                borderRadius: '6px',
                backgroundColor: '#f0fdf4',
                color: '#047857',
                fontWeight: '600'
              }}>
                Ajuste de nómina: ${ calcularValorAjuste(nuevoAjuste.fechasAjuste).valorTotal.toLocaleString() } 
                {nuevoAjuste.tipoBeneficiario === 'empleado' ? ' (a favor del empleado)' : ' (a favor de la empresa)'}
              </div>
            )}
          </div>
          <button
            onClick={guardarAjusteNomina}
            disabled={!nuevoAjuste.empleadoId || nuevoAjuste.fechasAjuste.length === 0}
            style={{
              padding: '10px 24px',
              fontSize: '13px',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              background: (!nuevoAjuste.empleadoId || nuevoAjuste.fechasAjuste.length === 0) 
                ? '#e5e7eb' 
                : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
              color: (!nuevoAjuste.empleadoId || nuevoAjuste.fechasAjuste.length === 0) ? '#9ca3af' : 'white',
              cursor: (!nuevoAjuste.empleadoId || nuevoAjuste.fechasAjuste.length === 0) ? 'not-allowed' : 'pointer',
              boxShadow: (!nuevoAjuste.empleadoId || nuevoAjuste.fechasAjuste.length === 0) ? 'none' : '0 2px 8px rgba(5, 150, 105, 0.3)',
            }}
          >
            💾 Guardar Ajuste
          </button>
        </div>
      </div>

      {/* Lista de ajustes pendientes del período */}
      <div style={{ marginBottom: '16px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
          📋 Ajustes Pendientes (Q{quincena} {mesNomina}) 
          <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#666', marginLeft: '8px' }}>
            Se aplicarán al liquidar la nómina
          </span>
        </h4>
        <div style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
          {getAjustesPeriodoActual().length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
              💭 No hay ajustes pendientes para este período
            </div>
          ) : (
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Empleado</th>
                  <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Tipo</th>
                  <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Beneficiario</th>
                  <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Fechas</th>
                  <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Horas</th>
                  <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Valor</th>
                  <th style={{ padding: '8px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {getAjustesPeriodoActual().map(ajuste => {
                  const totalHoras = ajuste.totalHorasNormales + ajuste.totalHorasNocturnas + ajuste.totalHorasFestivas;
                  return (
                    <tr key={ajuste.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '8px' }}>{ajuste.empleadoNombre}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: '600',
                          backgroundColor: ajuste.tipoAjuste === 'horario' ? '#dbeafe' : ajuste.tipoAjuste === 'descanso' ? '#fef3c7' : '#e0e7ff',
                          color: ajuste.tipoAjuste === 'horario' ? '#1d4ed8' : ajuste.tipoAjuste === 'descanso' ? '#d97706' : '#4338ca'
                        }}>
                          {ajuste.tipoAjuste === 'horario' ? '🕐 Horario' : ajuste.tipoAjuste === 'descanso' ? '😴 Descanso' : '🔢 Horas'}
                        </span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: '600',
                          backgroundColor: ajuste.tipoBeneficiario === 'empleado' ? '#d1fae5' : '#fee2e2',
                          color: ajuste.tipoBeneficiario === 'empleado' ? '#059669' : '#dc2626'
                        }}>
                          {ajuste.tipoBeneficiario === 'empleado' ? '👤 Empleado (+)' : '🏢 Empresa (-)'}
                        </span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center', fontSize: '11px' }}>
                        {[...new Set(ajuste.fechasAjuste?.map(f => f.fecha) || [])].length} fecha(s)
                        <br/>
                        <span style={{ color: '#9ca3af', fontSize: '10px' }}>
                          {ajuste.fechasAjuste?.map(f => f.fecha.slice(5)).join(', ')}
                        </span>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', fontSize: '11px' }}>
                        {totalHoras}h
                        {ajuste.totalHorasNormales > 0 && <span style={{ color: '#d97706' }}> ({ajuste.totalHorasNormales}n)</span>}
                        {ajuste.totalHorasNocturnas > 0 && <span style={{ color: '#4338ca' }}> ({ajuste.totalHorasNocturnas}nc)</span>}
                        {ajuste.totalHorasFestivas > 0 && <span style={{ color: '#be185d' }}> ({ajuste.totalHorasFestivas}f)</span>}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600' }}>
                        ${ajuste.valorTotal?.toLocaleString()}
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <button
                          onClick={() => cancelarAjuste(ajuste.id)}
                          style={{
                            padding: '4px 8px',
                            fontSize: '11px',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            cursor: 'pointer'
                          }}
                        >
                          ❌ Cancelar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Lista de ajustes aplicados (historial) */}
      {ajustesNomina.filter(a => a.estado === 'aplicado').length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#9ca3af', marginBottom: '8px' }}>📦 Ajustes Aplicados (Historial)</h4>
          <div style={{ maxHeight: '150px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
              <tbody>
                {ajustesNomina.filter(a => a.estado === 'aplicado').slice(0, 20).map(ajuste => (
                  <tr key={ajuste.id} style={{ borderBottom: '1px solid #f3f4f6', color: '#6b7280' }}>
                    <td style={{ padding: '6px 8px' }}>{ajuste.empleadoNombre}</td>
                    <td style={{ padding: '6px 8px' }}>{ajuste.periodoAplicacion}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      {ajuste.tipoBeneficiario === 'empleado' ? '👤' : '🏢'}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                      ${ajuste.valorTotal?.toLocaleString()}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <span style={{ padding: '2px 6px', borderRadius: '10px', fontSize: '9px', backgroundColor: '#d1fae5', color: '#059669' }}>
                        ✓ Aplicado
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button
          onClick={() => setModalAjustesNominaOpen(false)}
          style={{
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: '2px solid #059669',
            borderRadius: '8px',
            background: 'white',
            color: '#059669',
            cursor: 'pointer',
          }}
        >
          ✅ Cerrar
        </button>
      </div>
    </div>
  </div>
)}

{/* Modal Resumen Ejecutivo de Nómina */}
{modalResumenOpen && resumenActual && (
  <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
    <div style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 8px 32px rgba(255, 107, 53, 0.3)', padding: '24px', width: '800px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', border: '3px solid #ff6b35' }}>
      <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', color: '#ff6b35', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '3px solid #ffc53d', paddingBottom: '12px' }}>
        📊 Resumen Ejecutivo de Nómina
      </h3>
      
      {/* Información del período */}
      <div style={{ backgroundColor: '#fef7ed', border: '2px solid #ffc53d', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', fontSize: '13px' }}>
          <div><strong>📅 Período:</strong> {resumenActual.mes} - Quincena {resumenActual.quincena}</div>
          <div><strong>📆 Rango:</strong> {resumenActual.rangoInicio} a {resumenActual.rangoFin}</div>
          <div><strong>👥 Total Empleados:</strong> {resumenActual.totalEmpleados}</div>
          <div><strong>🕐 Generado:</strong> {new Date(resumenActual.fechaGeneracion).toLocaleString()}</div>
        </div>
      </div>
      
      {/* Totales principales */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>
        <div style={{ background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)', color: 'white', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: '12px', opacity: 0.9 }}>💰 Total Devengado</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>${resumenActual.totalDevengado?.toLocaleString() || 0}</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)', color: 'white', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: '12px', opacity: 0.9 }}>📉 Total Descuentos</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>${resumenActual.totalDescuentos?.toLocaleString() || 0}</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)', color: 'white', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: '12px', opacity: 0.9 }}>💵 Total Neto</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>${resumenActual.totalNeto?.toLocaleString() || 0}</div>
        </div>
      </div>
      
      {/* Desglose */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px' }}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#16a34a', marginBottom: '8px' }}>📈 Devengados</h4>
          <table style={{ width: '100%', fontSize: '12px' }}>
            <tbody>
              <tr><td>Salarios</td><td style={{ textAlign: 'right' }}>${(resumenActual.totalDevengado - (resumenActual.totalAuxTransporte || 0) - (resumenActual.totalBonificaciones || 0) - (resumenActual.totalIncapacidades || 0)).toLocaleString()}</td></tr>
              <tr><td>Aux. Transporte</td><td style={{ textAlign: 'right' }}>${(resumenActual.totalAuxTransporte || 0).toLocaleString()}</td></tr>
              <tr><td>Bonificaciones</td><td style={{ textAlign: 'right' }}>${(resumenActual.totalBonificaciones || 0).toLocaleString()}</td></tr>
              <tr><td>Incapacidades</td><td style={{ textAlign: 'right' }}>${(resumenActual.totalIncapacidades || 0).toLocaleString()}</td></tr>
            </tbody>
          </table>
        </div>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px' }}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#dc2626', marginBottom: '8px' }}>📉 Descuentos</h4>
          <table style={{ width: '100%', fontSize: '12px' }}>
            <tbody>
              <tr><td>Salud (4%)</td><td style={{ textAlign: 'right' }}>${(resumenActual.totalSalud || 0).toLocaleString()}</td></tr>
              <tr><td>Pensión (4%)</td><td style={{ textAlign: 'right' }}>${(resumenActual.totalPension || 0).toLocaleString()}</td></tr>
              <tr><td>Préstamos</td><td style={{ textAlign: 'right' }}>${(resumenActual.totalPrestamos || 0).toLocaleString()}</td></tr>
              <tr><td>Comidas</td><td style={{ textAlign: 'right' }}>${(resumenActual.totalComidas || 0).toLocaleString()}</td></tr>
              <tr><td>Otros</td><td style={{ textAlign: 'right' }}>${(resumenActual.totalOtrosDescuentos || 0).toLocaleString()}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Detalle por empleado */}
      {resumenActual.empleadosDetalle && resumenActual.empleadosDetalle.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>👥 Detalle por Empleado</h4>
          <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0 }}>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Empleado</th>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Documento</th>
                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Cargo</th>
                  <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Bruto</th>
                  <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>Neto</th>
                </tr>
              </thead>
              <tbody>
                {resumenActual.empleadosDetalle.map((emp, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px' }}>{emp.nombre}</td>
                    <td style={{ padding: '8px' }}>{emp.documento}</td>
                    <td style={{ padding: '8px' }}>{emp.cargo || 'N/A'}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>${(emp.bruto || 0).toLocaleString()}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>${(emp.neto || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Botones de acción */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
        <button
          onClick={() => imprimirResumenEjecutivo(resumenActual)}
          style={{
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '8px',
            background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
            color: 'white',
            cursor: 'pointer',
          }}
        >
          🖨️ Imprimir Resumen
        </button>
        <button
          onClick={() => { setModalResumenOpen(false); setResumenActual(null); }}
          style={{
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: '2px solid #ff6b35',
            borderRadius: '8px',
            background: 'white',
            color: '#ff6b35',
            cursor: 'pointer',
          }}
        >
          ✓ Aceptar
        </button>
      </div>
    </div>
  </div>
)}

{/* Modal Buscar Resúmenes Históricos */}
{modalBuscarResumenesOpen && (
  <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
    <div style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 8px 32px rgba(255, 107, 53, 0.3)', padding: '24px', width: '900px', maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', border: '3px solid #ff6b35' }}>
      <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', color: '#ff6b35', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '3px solid #ffc53d', paddingBottom: '12px' }}>
        🔍 Buscar Resúmenes de Nómina
      </h3>
      
      {/* Filtros de búsqueda */}
      <div style={{ backgroundColor: '#fef7ed', border: '2px solid #ffc53d', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>📅 Año</label>
            <select
              value={filtroResumenes.anio}
              onChange={(e) => setFiltroResumenes(prev => ({ ...prev, anio: e.target.value }))}
              style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #ffc53d', borderRadius: '6px' }}
            >
              {[2024, 2025, 2026, 2027].map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>📆 Mes</label>
            <select
              value={filtroResumenes.mesNumero}
              onChange={(e) => setFiltroResumenes(prev => ({ ...prev, mesNumero: e.target.value }))}
              style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #ffc53d', borderRadius: '6px' }}
            >
              <option value="">Todos los meses</option>
              {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'].map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#666', marginBottom: '4px' }}>🔢 Quincena</label>
            <select
              value={filtroResumenes.quincena}
              onChange={(e) => setFiltroResumenes(prev => ({ ...prev, quincena: e.target.value }))}
              style={{ width: '100%', padding: '8px', fontSize: '12px', border: '1px solid #ffc53d', borderRadius: '6px' }}
            >
              <option value="">Ambas</option>
              <option value="1">Primera (1-15)</option>
              <option value="2">Segunda (16-30)</option>
            </select>
          </div>
          <button
            onClick={buscarResumenesHistoricos}
            disabled={cargandoResumenes}
            style={{
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
              color: 'white',
              cursor: cargandoResumenes ? 'wait' : 'pointer',
              opacity: cargandoResumenes ? 0.7 : 1,
            }}
          >
            {cargandoResumenes ? '⏳ Buscando...' : '🔍 Buscar'}
          </button>
        </div>
      </div>
      
      {/* Resultados */}
      <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
        {cargandoResumenes ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#ff6b35', fontSize: '14px' }}>
            ⏳ Cargando resúmenes...
          </div>
        ) : resumenesHistoricos.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
            📭 No hay resúmenes guardados para el año {filtroResumenes.anio}.
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0 }}>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Período</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Quincena</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Empleados</th>
                <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>Devengado</th>
                <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>Descuentos</th>
                <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>Neto</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #e5e7eb' }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {resumenesHistoricos.map((r, idx) => (
                <tr key={r.id || idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '10px' }}>{r.mes || r.periodo}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <span style={{
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      backgroundColor: r.quincena === 1 ? '#dbeafe' : '#fef3c7',
                      color: r.quincena === 1 ? '#1d4ed8' : '#d97706'
                    }}>
                      Q{r.quincena}
                    </span>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{r.totalEmpleados}</td>
                  <td style={{ padding: '10px', textAlign: 'right' }}>${(r.totalDevengado || 0).toLocaleString()}</td>
                  <td style={{ padding: '10px', textAlign: 'right', color: '#dc2626' }}>${(r.totalDescuentos || 0).toLocaleString()}</td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>${(r.totalNeto || 0).toLocaleString()}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <button
                      onClick={() => verDetalleResumen(r)}
                      style={{
                        padding: '4px 10px',
                        fontSize: '11px',
                        border: 'none',
                        borderRadius: '4px',
                        backgroundColor: '#dbeafe',
                        color: '#1d4ed8',
                        cursor: 'pointer',
                        marginRight: '4px'
                      }}
                    >
                      👁️ Ver
                    </button>
                    <button
                      onClick={() => imprimirResumenEjecutivo(r)}
                      style={{
                        padding: '4px 10px',
                        fontSize: '11px',
                        border: 'none',
                        borderRadius: '4px',
                        backgroundColor: '#d1fae5',
                        color: '#059669',
                        cursor: 'pointer',
                        marginRight: '4px'
                      }}
                    >
                      🖨️
                    </button>
                    <button
                      onClick={() => eliminarResumenEjecutivo(r)}
                      style={{
                        padding: '4px 10px',
                        fontSize: '11px',
                        border: 'none',
                        borderRadius: '4px',
                        backgroundColor: '#fee2e2',
                        color: '#dc2626',
                        cursor: 'pointer'
                      }}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Botón cerrar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
        <button
          onClick={() => { setModalBuscarResumenesOpen(false); }}
          style={{
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: '2px solid #ff6b35',
            borderRadius: '8px',
            background: 'white',
            color: '#ff6b35',
            cursor: 'pointer',
          }}
        >
          ❌ Cerrar
        </button>
      </div>
    </div>
  </div>
)}

{/* Modal Cuotas de Préstamos (gemelo de Prestamos) */}
{modalCuotasNominaOpen && (
  <div 
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(15, 23, 42, 0.95)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 999999,
      backdropFilter: 'blur(8px)'
    }}
  >
    <div style={{
      backgroundColor: '#ffffff',
      borderRadius: 16,
      padding: 0,
      width: 920,
      maxWidth: '95vw',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255,255,255,0.1)'
    }}>
      {/* Header del modal */}
      <div style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
        padding: '20px 24px',
        borderRadius: '16px 16px 0 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 22
          }}>
            💳
          </div>
          <div>
            <h3 style={{ color: 'white', fontSize: 18, fontWeight: 700, margin: 0 }}>
              Selección de Cuotas a Descontar
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12, margin: 0 }}>
              Marca las cuotas que se descontarán en la nómina actual
            </p>
          </div>
        </div>
        <button
          onClick={() => setModalCuotasNominaOpen(false)}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '50%',
            width: 36,
            height: 36,
            color: 'white',
            fontSize: 18,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
          onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
        >
          ✕
        </button>
      </div>

      {/* Nota informativa */}
      <div style={{
        background: 'linear-gradient(90deg, #fef3c7 0%, #fef9c3 100%)',
        padding: '12px 24px',
        borderBottom: '1px solid #fcd34d',
        display: 'flex',
        alignItems: 'center',
        gap: 10
      }}>
        <span style={{ fontSize: 16 }}>⚠️</span>
        <span style={{ fontSize: 12, color: '#92400e' }}>
          Las cuotas con fondo <strong>gris oscuro</strong> ya fueron descontadas y están bloqueadas. Solo puedes marcar las cuotas pendientes.
        </span>
      </div>

      {/* Contenido scrolleable */}
      <div style={{ padding: '20px 24px', flex: 1, overflowY: 'auto', maxHeight: '55vh' }}>
        {prestamosConCuotasDisponiblesNomina.map((p) => {
          const e = p.empleado || {};
          const abonado = (p.plan || [])
            .filter((c) => c.noDescontable)
            .reduce((s, c) => s + (c.valor || 0), 0);
          const saldo = (p.valor || 0) - abonado;

          return (
            <div
              key={p.id}
              style={{
                backgroundColor: '#ffffff',
                borderRadius: 10,
                padding: '16px 20px',
                marginBottom: 12,
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.04)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b' }}>
                    {e.nombre || "—"}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {e.tipoDoc || "CC"} {e.documento || ""} · {p.tipo}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Total: <strong style={{ color: '#1e293b' }}>${(p.valor || 0).toLocaleString("es-CO")}</strong></div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Saldo: <strong style={{ color: '#dc2626' }}>${saldo.toLocaleString("es-CO")}</strong></div>
                  <div style={{
                    fontSize: 10,
                    backgroundColor: '#dbeafe',
                    color: '#1d4ed8',
                    padding: '2px 8px',
                    borderRadius: 20,
                    marginTop: 4,
                    display: 'inline-block',
                    fontWeight: 600
                  }}>
                    {(p.plan || []).filter(c => c.noDescontable).length}/{p.cuotas || 0} cuotas
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(p.plan || []).map((c) => {
                  const yaDescontada = !!c.noDescontable;
                  const marcada = !!c.pagado;
                  const checked = yaDescontada || marcada;

                  let estiloBase = {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    borderRadius: 20,
                    fontSize: 11,
                    cursor: yaDescontada ? 'not-allowed' : 'pointer',
                    border: '1px solid',
                    transition: 'all 0.2s'
                  };

                  if (yaDescontada) {
                    estiloBase.background = 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)';
                    estiloBase.color = 'white';
                    estiloBase.borderColor = '#16a34a';
                  } else if (marcada) {
                    estiloBase.background = '#dcfce7';
                    estiloBase.color = '#166534';
                    estiloBase.borderColor = '#86efac';
                  } else {
                    estiloBase.background = '#f1f5f9';
                    estiloBase.color = '#475569';
                    estiloBase.borderColor = '#cbd5e1';
                  }

                  return (
                    <label key={c.id} style={estiloBase}>
                      <input
                        type="checkbox"
                        style={{ width: 12, height: 12 }}
                        checked={checked}
                        disabled={yaDescontada}
                        onChange={() => {
                          if (yaDescontada) {
                            alert("Esta cuota ya fue descontada en nómina. No se puede modificar.");
                            return;
                          }
                          togglePagoCuota(p.id, c.id);
                        }}
                      />
                      <span>#{c.nro || 0} ${(c.valor || 0).toLocaleString("es-CO")}</span>
                      {yaDescontada && (
                        <span style={{ fontSize: 9, marginLeft: 4 }}>(desc.)</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}

        {!prestamosConCuotasDisponiblesNomina.length && (
          <div style={{
            padding: 40,
            textAlign: 'center',
            color: '#64748b'
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>No hay préstamos con cuotas disponibles</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Todos los préstamos ya fueron pagados o no tienen cuotas pendientes.</div>
          </div>
        )}
      </div>

      {/* Footer con botones */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 12,
        backgroundColor: '#f8fafc',
        borderRadius: '0 0 16px 16px'
      }}>
        <button
          onClick={() => setModalCuotasNominaOpen(false)}
          style={{
            padding: '12px 24px',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            background: 'white',
            color: '#64748b',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          Cerrar
        </button>
        <button
          onClick={() => setModalCuotasNominaOpen(false)}
          style={{
            padding: '12px 24px',
            borderRadius: 8,
            border: 'none',
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            color: 'white',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
            transition: 'all 0.2s'
          }}
        >
          ✓ Confirmar selección
        </button>
      </div>
    </div>
  </div>
)}
 
    </div>
  );
}
/** Calcula el total de días de vacaciones disponibles para un empleado */
function calcularDiasTotalesDisponibles(empleado, ausencias, feriados) {
  const periods = buildVacationPeriods(empleado);
  const ausenciasVac = ausencias.filter(
    a => a.empleadoId === empleado.id && a.tipo === "Vacaciones"
  );
  const conUsos = applyVacationUses(periods, ausenciasVac, feriados || {});
  return conUsos.reduce((sum, p) => sum + (p.pending || 0), 0);
}
/* ====================== LIQUIDACIONES CORREGIDAS ====================== */

function normalizarClaveTipo(valor) {
  return (valor || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function extraerFechasDeTexto(texto, inicioFallback, finFallback) {
  if (!texto) return [inicioFallback || null, finFallback || null];
  const coincidencias = texto.match(/\d{4}-\d{2}-\d{2}/g) || [];
  if (coincidencias.length >= 2) {
    return [coincidencias[0], coincidencias[1]];
  }
  if (coincidencias.length === 1) {
    return [coincidencias[0], coincidencias[0]];
  }
  return [inicioFallback || null, finFallback || null];
}

function inferirDetalleContratoClave(nombre) {
  const clave = normalizarClaveTipo(nombre);
  if (!clave) return null;
  if (clave.includes("cesantia")) return "cesantias";
  if (clave.includes("prima")) return "prima";
  if (clave.includes("vacacion")) return "vacaciones";
  return null;
}

const CLAVE_LIQ_VACACIONES = normalizarClaveTipo("Liquidación de VACACIONES");
const CLAVE_LIQ_CESANTIAS = normalizarClaveTipo("Liquidación de CESANTÍAS");
const CLAVE_LIQ_PRIMA = normalizarClaveTipo("Liquidación de PRIMA");
const CLAVE_LIQ_CONTRATO = normalizarClaveTipo("Liquidación CONTRATO");

// Función corregida de Liquidaciones con validación de duplicados y filtros
// CAMBIO PRINCIPAL: Liquidación de Contrato ahora usa días acumulados para vacaciones
function Liquidaciones({
  empleados = [],
  ausencias = [],
  onGuardarLiquidacion,
  repo = [],
  onCrearAusencia,
  onInactivarEmpleado,  // 🔹 NUEVO: función para inactivar empleado
  onEliminarLiquidacion, // 🔹 NUEVO: función para eliminar liquidación
  tienePermiso = () => true,
  valoresAnuales = {}, // <-- NUEVOS VALORES DINÁMICOS
  sedes = [], // 🔹 NUEVO: lista de sedes para filtros
  empresaId = null, // 🔹 NUEVO: ID de empresa para filtrar informes grupales
}) {
  // Valores dinámicos para liquidaciones
  const SMLV_DINAMICO = valoresAnuales.smlv || 1423500;
  const AUX_TRANSP_DINAMICO = valoresAnuales.auxTransporte || 200000;
  const INTERES_CES_DINAMICO = (valoresAnuales.interesCesantias || 12) / 100; // Convertir porcentaje a decimal
  const VAC_DIAS_ANO_DINAMICO = 15;
  const PRIMA_DIAS_SEM_DINAMICO = 15;

  const [tipo, setTipo] = useState("Vacaciones");
  const [empleadoId, setEmpleadoId] = useState("");
  const [yearCes, setYearCes] = useState(new Date().getFullYear());
  const [finPrima, setFinPrima] = useState(""); // 2025-06-30 o 2025-12-31
  const [primaDesde, setPrimaDesde] = useState("");
  const [primaHasta, setPrimaHasta] = useState("");
  const [calc, setCalc] = useState(null);       // resultado de cálculo para previsualizar
  const [filtroEmpleado, setFiltroEmpleado] = useState(""); // para filtrar histórico
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroFechaDesde, setFiltroFechaDesde] = useState("");
  const [filtroFechaHasta, setFiltroFechaHasta] = useState("");
  const [filtroSede, setFiltroSede] = useState("");
  const [cesDesde, setCesDesde] = useState("");
  const [cesHasta, setCesHasta] = useState("");
 
  // 🔹 estados para vacaciones
  const [vacPeriodo, setVacPeriodo] = useState("");
  const [vacDias, setVacDias] = useState("");
  const [vacDesde, setVacDesde] = useState("");
  const [vacHasta, setVacHasta] = useState("");
 
  // 🔹 NUEVO: estados para Liquidación de Contrato
  const [contratoVacDias, setContratoVacDias] = useState(""); // días acumulados de vacaciones

  // 🔹 NUEVO: estados para liquidación GRUPAL
  const [modoGrupal, setModoGrupal] = useState(false);
  const [empleadosSeleccionados, setEmpleadosSeleccionados] = useState([]);
  const [resultadosGrupales, setResultadosGrupales] = useState([]);

  // 🔹 NUEVO: estados para Intereses a las Cesantías
  const [intCesDesde, setIntCesDesde] = useState("");
  const [intCesHasta, setIntCesHasta] = useState("");
  const [intCesYear, setIntCesYear] = useState(new Date().getFullYear());

  // 🔹 NUEVO: estado para tipo de salario en Prima (promedio vs base)
  const [primaSalarioTipo, setPrimaSalarioTipo] = useState("promedio"); // "promedio" o "base"

  // 🔹 NUEVO: estados para historial de informes grupales
  const [informesGrupales, setInformesGrupales] = useState([]);
  const [mostrarHistorialInformes, setMostrarHistorialInformes] = useState(false);
  const [cargandoInformes, setCargandoInformes] = useState(false);

  // 🔹 NUEVO: estado para controlar la vista activa (pestañas)
  const [vistaActiva, setVistaActiva] = useState("liquidar"); // "liquidar" o "historico"

  // Cargar informes grupales al montar y cuando cambie la empresa
  React.useEffect(() => {
    cargarInformesGrupales();
  }, [empresaId]);

  async function cargarInformesGrupales() {
    setCargandoInformes(true);
    try {
      let query = supabase
        .from("informes_grupales")
        .select("*")
        .order("fecha", { ascending: false })
        .limit(50);
      
      // 🔹 Filtrar por empresa si no es "todas"
      // Incluir también los que tienen empresa_id NULL (informes antiguos)
      if (empresaId && empresaId !== "todas") {
        query = query.or(`empresa_id.eq.${empresaId},empresa_id.is.null`);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error("Error cargando informes grupales:", error);
      } else {
        setInformesGrupales(data || []);
      }
    } catch (err) {
      console.error("Error cargando informes grupales:", err);
    }
    setCargandoInformes(false);
  }

  async function guardarInformeGrupal(tipoLiq, periodoDesde, periodoHasta, empleadosData, totalGeneral, htmlUnificado, htmlResumen) {
    try {
      // 🔹 Preparar datos con empresa_id
      const datosInforme = {
        tipo: tipoLiq,
        periodo_desde: periodoDesde,
        periodo_hasta: periodoHasta,
        cantidad_empleados: empleadosData.length,
        total_general: totalGeneral,
        detalle_empleados: empleadosData,
        html_unificado: htmlUnificado,
        html_resumen: htmlResumen,
      };
      
      // 🔹 Agregar empresa_id si está definido
      if (empresaId && empresaId !== "todas") {
        datosInforme.empresa_id = empresaId;
      }
      
      const { data, error } = await supabase
        .from("informes_grupales")
        .insert(datosInforme)
        .select()
        .single();
      
      if (error) {
        console.error("Error guardando informe grupal:", error);
        return null;
      }
      
      // Recargar lista de informes
      cargarInformesGrupales();
      return data;
    } catch (err) {
      console.error("Error guardando informe grupal:", err);
      return null;
    }
  }

  async function eliminarInformeGrupal(id) {
    if (!window.confirm("¿Está seguro de eliminar este informe grupal?")) return;
    
    try {
      const { error } = await supabase
        .from("informes_grupales")
        .delete()
        .eq("id", id);
      
      if (error) {
        console.error("Error eliminando informe:", error);
        alert("Error al eliminar el informe");
      } else {
        cargarInformesGrupales();
      }
    } catch (err) {
      console.error("Error eliminando informe:", err);
    }
  }
 
  React.useEffect(() => {
    if (tipo !== "Vacaciones") return;
 
    const n = Number(vacDias || 0);
    if (!vacDesde || n <= 0) {
      setVacHasta("");
      return;
    }
 
    const finCalc = addBusinessDaysVac(vacDesde, n, holidaysCache);
    setVacHasta(finCalc);
  }, [tipo, vacDesde, vacDias]);
 
  // Cache de festivos para usar la misma lógica de vacaciones que el botón VACACIONES
  const holidaysCache = useMemo(() => ({}), []);

  const indiceLiquidaciones = useMemo(() => {
    const porEmpleado = {};

    const agregarEntrada = (empId, claveTipo, entrada) => {
      if (!empId || !claveTipo) return;
      if (!porEmpleado[empId]) {
        porEmpleado[empId] = {};
      }
      if (!porEmpleado[empId][claveTipo]) {
        porEmpleado[empId][claveTipo] = [];
      }
      porEmpleado[empId][claveTipo].push(entrada);
    };

    (repo || []).forEach((liq) => {
      const empId = liq?.empleadoId;
      if (!empId) return;

      const claveTipo = normalizarClaveTipo(liq?.tipo);
      const baseEntrada = {
        id: liq?.id,
        tipo: liq?.tipo || "",
        fechaInicio: liq?.fechaInicio || null,
        fechaFin: liq?.fechaFin || null,
        periodo: liq?.periodo || "",
      };

      agregarEntrada(empId, claveTipo, baseEntrada);

      if (claveTipo === CLAVE_LIQ_CONTRATO && Array.isArray(liq?.detalle)) {
        liq.detalle.forEach((item) => {
          const claveDetalle = inferirDetalleContratoClave(item?.nombre || "");
          if (!claveDetalle) return;
          const [desdeDet, hastaDet] = extraerFechasDeTexto(
            item?.nombre || "",
            liq?.fechaInicio || null,
            liq?.fechaFin || null
          );

          agregarEntrada(empId, `sub:${claveDetalle}`, {
            ...baseEntrada,
            fechaInicio: desdeDet || baseEntrada.fechaInicio,
            fechaFin: hastaDet || baseEntrada.fechaFin,
            detalleNombre: item?.nombre || "",
          });
        });
      }
    });

    return porEmpleado;
  }, [repo]);

  const buscarConflictos = useCallback(
    (claves, desde, hasta) => {
      if (!empleadoId || !claves || !claves.length) return [];
      if (!desde || !hasta) return [];
      const indiceEmpleado = indiceLiquidaciones[empleadoId];
      if (!indiceEmpleado) return [];

      const conflictos = [];
      claves.forEach((clave) => {
        const lista = indiceEmpleado[clave];
        if (!lista) return;
        lista.forEach((entrada) => {
          if (!entrada?.fechaInicio || !entrada?.fechaFin) return;
          if (
            seTraslapaRangoFechas(
              desde,
              hasta,
              entrada.fechaInicio,
              entrada.fechaFin
            )
          ) {
            conflictos.push({ ...entrada, clave });
          }
        });
      });

      return conflictos;
    },
    [empleadoId, indiceLiquidaciones]
  );

  const formatearMensajeBloqueo = useCallback((lista) => {
    if (!lista || !lista.length) {
      return "Ya existe una liquidación registrada para este empleado y período.";
    }
    const detalle = lista
      .map((item) => {
        const inicio = item?.fechaInicio || "—";
        const fin = item?.fechaFin || "—";
        const etiqueta = item?.tipo || "Liquidación previa";
        return `• ${etiqueta}: ${inicio} → ${fin}`;
      })
      .join("\n");
    return (
      "No es posible continuar porque ya existe una liquidación registrada:\n" +
      detalle
    );
  }, []);

  const infoBloqueo = useMemo(() => {
    const respuestaBase = {
      activo: false,
      razones: [],
      contrato: {
        general: [],
        cesantias: [],
        prima: [],
        vacaciones: [],
      },
    };

    if (!empleadoId) {
      return respuestaBase;
    }

    if (tipo === "Vacaciones") {
      if (!vacDesde || !vacHasta) return respuestaBase;
      const conflictos = buscarConflictos(
        [CLAVE_LIQ_VACACIONES, "sub:vacaciones"],
        vacDesde,
        vacHasta
      );
      return {
        ...respuestaBase,
        activo: conflictos.length > 0,
        razones: conflictos,
      };
    }

    if (tipo === "Cesantías") {
      if (!cesDesde || !cesHasta) return respuestaBase;
      const conflictos = buscarConflictos(
        [CLAVE_LIQ_CESANTIAS, "sub:cesantias"],
        cesDesde,
        cesHasta
      );
      return {
        ...respuestaBase,
        activo: conflictos.length > 0,
        razones: conflictos,
      };
    }

    if (tipo === "Prima") {
      if (!primaDesde || !primaHasta) return respuestaBase;
      const conflictos = buscarConflictos(
        [CLAVE_LIQ_PRIMA, "sub:prima"],
        primaDesde,
        primaHasta
      );
      return {
        ...respuestaBase,
        activo: conflictos.length > 0,
        razones: conflictos,
      };
    }

    if (tipo === "Liquidación CONTRATO") {
      const conflictosCesantias =
        cesDesde && cesHasta
          ? buscarConflictos(
              [CLAVE_LIQ_CESANTIAS, "sub:cesantias"],
              cesDesde,
              cesHasta
            )
          : [];

      const conflictosPrima =
        primaDesde && primaHasta
          ? buscarConflictos(
              [CLAVE_LIQ_PRIMA, "sub:prima"],
              primaDesde,
              primaHasta
            )
          : [];

      const conflictosVacaciones = (() => {
        if (!contratoVacDias || Number(contratoVacDias) <= 0) {
          return [];
        }
        const indiceEmpleado = indiceLiquidaciones[empleadoId];
        if (!indiceEmpleado) return [];
        return indiceEmpleado["sub:vacaciones"] || [];
      })();

      let contratoInicio = null;
      let contratoFin = null;
      [
        ...(cesDesde && cesHasta
          ? [{ desde: cesDesde, hasta: cesHasta }]
          : []),
        ...(primaDesde && primaHasta
          ? [{ desde: primaDesde, hasta: primaHasta }]
          : []),
      ].forEach((r) => {
        if (!r.desde || !r.hasta) return;
        contratoInicio = contratoInicio && contratoInicio < r.desde ? contratoInicio : r.desde;
        contratoFin = contratoFin && contratoFin > r.hasta ? contratoFin : r.hasta;
      });

      const conflictosContrato =
        contratoInicio && contratoFin
          ? buscarConflictos(
              [CLAVE_LIQ_CONTRATO],
              contratoInicio,
              contratoFin
            )
          : [];

      const razones = [
        ...conflictosContrato,
        ...conflictosCesantias,
        ...conflictosPrima,
        ...conflictosVacaciones,
      ];

      return {
        activo: razones.length > 0,
        razones,
        contrato: {
          general: conflictosContrato,
          cesantias: conflictosCesantias,
          prima: conflictosPrima,
          vacaciones: conflictosVacaciones,
        },
      };
    }

    return respuestaBase;
  }, [
    empleadoId,
    tipo,
    vacDesde,
    vacHasta,
    cesDesde,
    cesHasta,
    primaDesde,
    primaHasta,
    contratoVacDias,
    buscarConflictos,
    indiceLiquidaciones,
  ]);
 
  // 🔹 Períodos de vacaciones disponibles para un empleado (mismo criterio del botón Vacaciones)
  function bucketsEmpleado(e) {
    if (!e) return [];
    const periods = buildVacationPeriods(e);
    const vacsEmp = (ausencias || []).filter(
      (a) => a.empleadoId === e.id && a.tipo === "Vacaciones"
    );
    return applyVacationUses(periods, vacsEmp, holidaysCache);
  }
 
  // 🔹 NUEVO: Calcular total de días de vacaciones disponibles
  function getTotalDiasVacacionesDisponibles(empId) {
    const emp = empleados.find((x) => x.id === empId);
    if (!emp) return 0;
    const buckets = bucketsEmpleado(emp);
    return buckets.reduce((sum, b) => sum + (b.pending || 0), 0);
  }
 
  // 🔹 NUEVO: Cuando cambia el empleado o el tipo a "Liquidación CONTRATO", cargar días disponibles
  React.useEffect(() => {
    if (tipo === "Liquidación CONTRATO" && empleadoId) {
      const totalDias = getTotalDiasVacacionesDisponibles(empleadoId);
      setContratoVacDias(String(Math.round(totalDias * 100) / 100));
    }
  }, [tipo, empleadoId]);
 
  // helpers de impresión
  function imprimirIndividual(rec) {
    console.log("📄 Iniciando impresión individual:", {
      id: rec.id,
      tipo: rec.tipo,
      empleadoId: rec.empleadoId,
      detalle: rec.detalle,
      detalleType: typeof rec.detalle,
      detalleLength: Array.isArray(rec.detalle) ? rec.detalle.length : "no es array",
      total: rec.total,
      // Debug campos de salario
      auxTransporte: rec.auxTransporte,
      baseLiquidacion: rec.baseLiquidacion,
      tipoSalarioUsado: rec.tipoSalarioUsado,
    });
    
    // Enriquecer con datos del empleado si es necesario
    let e = rec.empleado;
    if (!e || !e.nombre) {
      // Si no tiene empleado, buscar en la lista
      const empEncontrado = empleados.find((emp) => emp.id === rec.empleadoId);
      e = empEncontrado || {
        id: rec.empleadoId,
        nombre: rec.empleadoNombre || "Sin nombre",
        documento: rec.empleadoDoc || "",
        tipoDoc: "CC",
        cargo: "—",
        fechaIngreso: "—",
        salarioBase: 0,
      };
    }
    
    // Asegurar que e tiene todas las propiedades necesarias
    e = {
      nombre: e?.nombre || rec.empleadoNombre || "Sin nombre",
      tipoDoc: e?.tipoDoc || "CC",
      documento: e?.documento || rec.empleadoDoc || "",
      cargo: e?.cargo || "—",
      fechaIngreso: e?.fechaIngreso || "—",
      salarioBase: e?.salarioBase || 0,
      ...e,
    };
    
    // Parsear detalle si es string JSON
    let detalle = [];
    if (rec.detalle) {
      if (typeof rec.detalle === 'string') {
        try {
          detalle = JSON.parse(rec.detalle);
        } catch (err) {
          console.error("Error parseando detalle:", err);
          detalle = [];
        }
      } else if (Array.isArray(rec.detalle)) {
        detalle = rec.detalle;
      }
    }
    
    console.log("📋 Detalle parseado:", detalle);
    
    // Parsear vacacionesBuckets si es string JSON
    let vacacionesBuckets = [];
    if (rec.vacacionesBuckets) {
      if (typeof rec.vacacionesBuckets === 'string') {
        try {
          vacacionesBuckets = JSON.parse(rec.vacacionesBuckets);
        } catch (err) {
          console.error("Error parseando vacacionesBuckets:", err);
          vacacionesBuckets = [];
        }
      } else if (Array.isArray(rec.vacacionesBuckets)) {
        vacacionesBuckets = rec.vacacionesBuckets;
      }
    }
    
    // Construir sección de información de salario ANTES del template
    let seccionSalario = '';
    console.log("💰 Datos para sección salario:", {
      tipoSalarioUsado: rec.tipoSalarioUsado,
      auxTransporte: rec.auxTransporte,
      baseLiquidacion: rec.baseLiquidacion,
      salarioBaseEmpleado: e.salarioBase,
    });
    
    if (rec.tipoSalarioUsado) {
      // Prima: mostrar tipo de salario usado
      seccionSalario = `<strong>${rec.tipoSalarioUsado}:</strong> $${(rec.baseLiquidacion || 0).toLocaleString()}<br/>`;
    } else if (rec.auxTransporte !== undefined && rec.auxTransporte !== null && rec.baseLiquidacion) {
      // Cesantías/Intereses: mostrar salario base + aux + total
      seccionSalario = `<strong>Salario base:</strong> $${(e.salarioBase || 0).toLocaleString()}<br/>
                        <strong>Auxilio de transporte:</strong> $${(rec.auxTransporte || 0).toLocaleString()}<br/>
                        <strong>Base de liquidación:</strong> $${(rec.baseLiquidacion || 0).toLocaleString()}<br/>`;
    } else if (rec.baseLiquidacion) {
      // Vacaciones u otros: solo base
      seccionSalario = `<strong>Base de liquidación:</strong> $${(rec.baseLiquidacion || 0).toLocaleString()}<br/>`;
    } else {
      // Default: solo salario base del empleado
      seccionSalario = `<strong>Salario base:</strong> $${(e.salarioBase || 0).toLocaleString()}<br/>`;
    }
    
    const win = window.open("", "_blank", "width=850,height=700");
    if (!win) return;
  
    const firma = `
      <div style="display:flex;justify-content:space-between;margin-top:48px;">
        <div style="width:45%;text-align:center;">
          <div>RECIBO A CONFORMIDAD</div>
          <div style="margin-top:46px;border-top:1px solid #000;padding-top:4px;">
            ${e.nombre}<br/>${e.tipoDoc} ${e.documento}
          </div>
        </div>
        <div style="width:45%;text-align:center;">
          <div>FIRMA AUTORIZADA</div>
          <div style="margin-top:46px;border-top:1px solid #000;padding-top:4px;">
            FIRMA AUTORIZADA
          </div>
        </div>
      </div>
    `;
 
    // NUEVO: Generar sección de desglose de vacaciones por período si existe
    let desgloseVacacionesHTML = "";
    if (vacacionesBuckets && vacacionesBuckets.length > 0 && rec.diasVacacionesLiquidados > 0) {
      const filasDesglose = vacacionesBuckets
        .filter(b => b.pending > 0)
        .map(b => `
          <tr>
            <td style="padding:4px 8px;">${b.label}</td>
            <td style="padding:4px 8px;">${b.fromISO} → ${b.toISO}</td>
            <td style="padding:4px 8px;text-align:right;">${b.pending} día(s)</td>
          </tr>
        `).join("");
 
      desgloseVacacionesHTML = `
        <h3 style="font-size:13px;margin:16px 0 8px;">Desglose de vacaciones por período</h3>
        <table style="border-collapse:collapse;width:100%;font-size:12px;margin-bottom:12px;">
          <thead>
            <tr style="background:#f5f5f5;">
              <th style="border:1px solid #ccc;padding:6px;text-align:left;">Período</th>
              <th style="border:1px solid #ccc;padding:6px;text-align:left;">Rango</th>
              <th style="border:1px solid #ccc;padding:6px;text-align:right;">Días</th>
            </tr>
          </thead>
          <tbody>
            ${filasDesglose}
            <tr style="background:#e8f4e8;font-weight:bold;">
              <td colspan="2" style="border:1px solid #ccc;padding:6px;text-align:right;">Total días liquidados:</td>
              <td style="border:1px solid #ccc;padding:6px;text-align:right;">${rec.diasVacacionesLiquidados}</td>
            </tr>
          </tbody>
        </table>
      `;
    }
    
    // Generar las filas de detalle
    const filasDetalle = (detalle || [])
      .filter(d => d && d.nombre && d.valor !== undefined && d.valor !== null)
      .map(d => `
        <tr style="border-bottom:1px solid #ccc;">
          <td style="padding:6px 8px;">${d.nombre}</td>
          <td style="padding:6px 8px;text-align:right;">$${(d.valor || 0).toLocaleString()}</td>
        </tr>
      `).join("");
    
    // Si no hay detalles, crear una fila con al menos el tipo de liquidación
    const filasDetalleFinales = filasDetalle && filasDetalle.trim() ? filasDetalle : `
      <tr style="border-bottom:1px solid #ccc;">
        <td style="padding:6px 8px;">${rec.tipo || "Concepto"}</td>
        <td style="padding:6px 8px;text-align:right;">$${(rec.total || 0).toLocaleString()}</td>
      </tr>
    `;
    
    console.log("📋 Detalle parseado:", {
      detalleArray: detalle,
      filasDetalleGeneradas: filasDetalle,
      filasDetalleFinales: filasDetalleFinales,
    });
  
    win.document.write(`
      <html>
        <head>
          <title>${rec.tipo}</title>
          <style>
            body{font-family:Arial,sans-serif;padding:18px;position:relative;}
            .logo-header{position:absolute;top:10px;right:15px;width:110px;height:110px;}
            .logo-header img{width:100%;height:100%;object-fit:contain;border-radius:10px;box-shadow:0 2px 8px rgba(0,0,0,0.15);}
            h1{font-size:18px;margin:0 130px 12px 0;text-align:center;text-transform:uppercase;font-weight:bold;border-bottom:2px solid #333;padding-bottom:10px;padding-top:20px;}
            table{border-collapse:collapse;width:100%;font-size:12px;margin-top:10px;}
            th,td{border:1px solid #ccc;padding:6px;text-align:left;}
            .meta{font-size:12px;color:#555;line-height:1.5;margin-top:6px;}
            @media print {
              .logo-header{position:fixed;top:0;right:0;width:120px;height:120px;}
              h1{margin-right:140px;}
            }
          </style>
        </head>
        <body>
          <div class="logo-header">
            <img src="${LOGO_BIGBURGUER}" alt="BigBurguer Logo" />
          </div>
          <h1>${rec.tipo === "Liquidación CONTRATO" ? "LIQUIDACIÓN DEFINITIVA DE CONTRATO" : rec.tipo}</h1>
          <div class="meta">
            <strong>Empleado:</strong> ${e.nombre}<br/>
            <strong>Documento:</strong> ${e.tipoDoc} ${e.documento}<br/>
            <strong>Cargo:</strong> ${e.cargo}<br/>
            <strong>Fecha ingreso:</strong> ${e.fechaIngreso}<br/>
            ${seccionSalario}
            ${rec.fechaInicio ? `<strong>Período:</strong> ${rec.fechaInicio} → ${rec.fechaFin || ""}<br/>` : ""}
            ${
              rec.periodoAfectado
                ? `<strong>Período(s) tomado(s):</strong> ${rec.periodoAfectado}<br/>`
                : ""
            }
            ${
              rec.diasTomados != null
                ? `<strong>Días tomados en esta liquidación:</strong> ${rec.diasTomados}<br/>`
                : ""
            }
          </div>
 
          ${desgloseVacacionesHTML}
  
          <table>
            <thead>
              <tr style="background:#f0f0f0;">
                <th style="text-align:left;">Concepto</th>
                <th style="text-align:right;">Valor</th>
              </tr>
            </thead>
            <tbody>
              ${filasDetalleFinales}
              <tr style="background:#f0f0f0;font-weight:bold;">
                <td style="text-align:right;padding:8px;">TOTAL A PAGAR</td>
                <td style="text-align:right;padding:8px;"><strong>$${(rec.total || 0).toLocaleString()}</strong></td>
              </tr>
            </tbody>
          </table>
  
          ${firma}
          <script>window.print()</script>
        </body>
      </html>
    `);
  
    win.document.close();
  }
  
 
  function imprimirTodos(lista) {
    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) return;
    const rows = lista.map(r => `
      <tr>
        <td>${r.empleado?.nombre || r.empleadoNombre || "—"}</td>
        <td>${r.empleado?.tipoDoc || "CC"} ${r.empleado?.documento || r.empleadoDoc || ""}</td>
        <td>${r.tipo}</td>
        <td>${r.fechaInicio || "—"}</td>
        <td>${r.fechaFin || "—"}</td>
        <td style="text-align:right;">$${(r.total||0).toLocaleString()}</td>
      </tr>
    `).join("");
    win.document.write(`
      <html><head><title>Liquidaciones</title>
      <style>
        body{font-family:Arial,sans-serif;padding:16px;position:relative;}
        .logo-header{position:absolute;top:10px;right:15px;width:110px;height:110px;}
        .logo-header img{width:100%;height:100%;object-fit:contain;border-radius:10px;box-shadow:0 2px 8px rgba(0,0,0,0.15);}
        h1{text-align:center;padding-top:20px;text-transform:uppercase;font-weight:bold;margin-right:130px;}
        table{border-collapse:collapse;width:100%;font-size:12px;}
        th,td{border:1px solid #ccc;padding:6px;}
        th{background:#f2f2f2;text-align:left;}
        @media print {
          .logo-header{position:fixed;top:0;right:0;width:120px;height:120px;}
          h1{margin-right:140px;}
        }
      </style></head>
      <body>
        <div class="logo-header">
          <img src="${LOGO_BIGBURGUER}" alt="BigBurguer Logo" />
        </div>
        <h1>Liquidaciones – ${new Date().toLocaleDateString()}</h1>
        <table>
          <thead>
            <tr>
              <th>Empleado</th>
              <th>Documento</th>
              <th>Tipo</th>
              <th>Fecha inicial</th>
              <th>Fecha final</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <script>window.print()</script>
      </body></html>
    `);
    win.document.close();
  }
 
  // === VACACIONES ===
  function calcVacaciones(e, diasTomar) {
    const base = SMLV_DINAMICO; // Usar valor dinámico
    const diasPeriodo = VAC_DIAS_ANO_DINAMICO;
    const valorPeriodo = Math.round((base / 30) * diasPeriodo);
    const valorDia = valorPeriodo / diasPeriodo;
 
    const desde = e.ultimasVacaciones || e.fechaIngreso;
    if (!desde)
      return { error: "Falta fecha de ingreso o últimas vacaciones" };
 
    const desdeISO = toISO(new Date(desde));
    const fin = toISO(
      new Date(
        new Date(desdeISO).setFullYear(
          parseISO(desdeISO).getFullYear() + 1
        )
      )
    );
 
    const dias = Math.max(1, Number(diasTomar || diasPeriodo));
    const total = Math.round(valorDia * dias);
 
    return {
      tipo: "Liquidación de VACACIONES",
      fechaInicio: desdeISO,
      fechaFin: addDaysLocal(fin, -1),
      // Datos para metadatos (vacaciones usa SMLV, no aux transporte)
      baseLiquidacion: base,
      detalle: [
        {
          nombre:
            dias === diasPeriodo
              ? `Vacaciones período completo (${dias} días)`
              : `Vacaciones parciales (${dias} días)`,
          valor: total,
        },
      ],
      total,
      actualizarCampo: "ultimasVacaciones",
      nuevaFechaCampo: fin,
    };
  }
 
  function calcCesantias(e, anio) {
    const y = Number(anio);
    if (!y) return { error:"Año inválido" };
    const inicioAnio = `${y}-01-01`;
    const finAnio = `${y}-12-31`;
 
    let fechaIni = e.fechaIngreso && parseISO(e.fechaIngreso) > parseISO(inicioAnio)
      ? e.fechaIngreso
      : inicioAnio;
    const fechaFin = finAnio;
 
    const dTrab = Math.max(0, diasEntre(fechaIni, fechaFin));
    const dAnio = diasEntre(inicioAnio, finAnio);
 
    const base = SMLV_DINAMICO + AUX_TRANSP_DINAMICO; // Usar valores dinámicos
    const ces = Math.round((base) * (dTrab / dAnio));
    const intereses = Math.round(ces * INTERES_CES_DINAMICO * (dTrab / dAnio)); // Usar porcentaje dinámico
 
    return {
      tipo: "Liquidación de CESANTÍAS",
      fechaInicio: fechaIni,
      fechaFin: fechaFin,
      detalle: [
        { nombre: `Cesantías año ${y}`, valor: ces },
        { nombre: `Intereses (12%) año ${y}`, valor: intereses },
      ],
      total: ces + intereses,
      actualizarCampo: "ultimasCesantias",
      nuevaFechaCampo: finAnio,
    };
  }
 
  // ========= PRIMA POR RANGO DE FECHAS (SEMESTRE) =========
  function calcPrimaRango(e, desdeISO, hastaISO, usarSalarioBase = false) {
    if (!desdeISO || !hastaISO) {
      return { error: "Ingresa la fecha inicial y la fecha final del período de prima." };
    }
 
    const fIni = parseISO(desdeISO);
    const fFin = parseISO(hastaISO);
 
    if (!(fIni instanceof Date) || isNaN(fIni) || !(fFin instanceof Date) || isNaN(fFin)) {
      return { error: "Fechas de prima no válidas." };
    }
 
    if (fIni > fFin) {
      return { error: "La fecha inicial de prima no puede ser mayor que la fecha final." };
    }
 
    const year = fIni.getFullYear();
    if (fFin.getFullYear() !== year) {
      return { error: "Las primas solo se liquidan dentro del mismo año calendario." };
    }
 
    const mIni = fIni.getMonth();
    const mFin = fFin.getMonth();
 
    let semInicio, semFin, etiqueta;
    if (mIni <= 5 && mFin <= 5) {
      semInicio = `${year}-01-01`;
      semFin = `${year}-06-30`;
      etiqueta = "enero-junio";
    } else if (mIni >= 6 && mFin >= 6) {
      semInicio = `${year}-07-01`;
      semFin = `${year}-12-31`;
      etiqueta = "julio-diciembre";
    } else {
      return {
        error:
          "El período de prima debe estar completamente dentro de enero-junio o dentro de julio-diciembre."
      };
    }
 
    if (desdeISO < semInicio || hastaISO > semFin) {
      return {
        error:
          "El período de prima debe estar dentro de los rangos 01/01-30/06 o 01/07-31/12 del mismo año."
      };
    }
 
    // Determinar base según tipo de salario seleccionado
    let salarioBase, auxTransporte, baseTotal, tipoSalarioTexto;
    
    if (usarSalarioBase) {
      // Usar salario base + auxilio de transporte
      salarioBase = Number(e.salarioBase || e.salariobase || 0);
      auxTransporte = AUX_TRANSP_DINAMICO;
      baseTotal = salarioBase + auxTransporte;
      tipoSalarioTexto = "Salario base + Aux. Transporte";
    } else {
      // Usar salario promedio de últimos 6 meses SIN auxilio de transporte
      // IMPORTANTE: El campo se llama 'promedioultimos6meses' (actualizado en liquidación masiva)
      salarioBase = Number(e.promedioultimos6meses || e.salarioProm6m || e.salarioBase || e.salariobase || 0);
      auxTransporte = 0;
      baseTotal = salarioBase;
      tipoSalarioTexto = "Salario promedio últimos 6 meses (mensual)";
    }
    
    if (!baseTotal) {
      return { error: "El empleado no tiene salario registrado." };
    }
 
    let desdeTrab = desdeISO;
    if (e.fechaIngreso && e.fechaIngreso > desdeTrab) {
      desdeTrab = e.fechaIngreso;
    }
 
    if (desdeTrab > hastaISO) {
      return {
        tipo: "Liquidación de PRIMA",
        fechaInicio: desdeISO,
        fechaFin: hastaISO,
        detalle: [
          { nombre: `Prima de servicios (${etiqueta})`, valor: 0 }
        ],
        total: 0,
        actualizarCampo: "ultimaPrima",
        nuevaFechaCampo: hastaISO
      };
    }
 
    const diasPeriodo = daysBetweenInclusive(semInicio, semFin);
    const diasTrab = daysBetweenInclusive(desdeTrab, hastaISO);
 
    const valorDia = baseTotal / 30;
    const primaSemestreCompleta = valorDia * PRIMA_DIAS_SEM_DINAMICO; // Usar valor dinámico
    const factor = diasPeriodo > 0 ? diasTrab / diasPeriodo : 0;
    const primaValor = Math.round(primaSemestreCompleta * factor);

    return {
      tipo: "Liquidación de PRIMA",
      fechaInicio: desdeISO,
      fechaFin: hastaISO,
      // Datos para mostrar en metadatos (solo si usa salario base + aux)
      auxTransporte: usarSalarioBase ? auxTransporte : null,
      baseLiquidacion: baseTotal,
      tipoSalarioUsado: tipoSalarioTexto,
      detalle: [
        {
          nombre: `Prima de servicios ${etiqueta} (${diasTrab} días sobre ${diasPeriodo})`,
          valor: primaValor
        }
      ],
      total: primaValor,
      actualizarCampo: "ultimaPrima",
      nuevaFechaCampo: hastaISO
    };
  }
 
  // ================== CESANTÍAS POR RANGO DE FECHAS ==================
  // CORREGIDO: Usa salarioBase del perfil del empleado
  // En Colombia se usa año de 360 días (12 meses × 30 días)
  function calcCesantiasRango(empleado, desdeISO, hastaISO) {
    if (!empleado || !desdeISO || !hastaISO) {
      return { dias: 0, meses: 0, cesantias: 0, intereses: 0, total: 0 };
    }
 
    // Usar promedioultimos6meses (salario mensual ya calculado y validado)
    // Si no existe, usar salarioBase como fallback
    const salario = Number(empleado.promedioultimos6meses || empleado.salarioBase || empleado.salariobase || 0);
    const auxTransporte = AUX_TRANSP_DINAMICO; // Usar valor dinámico
    const base = salario + auxTransporte;
 
    if (!base) {
      return { dias: 0, meses: 0, cesantias: 0, intereses: 0, total: 0, error: "El empleado no tiene salario base registrado." };
    }
 
    // Calcular días laborales (cada mes = 30 días, máximo 360 por año)
    const fechaDesde = parseISO(desdeISO);
    const fechaHasta = parseISO(hastaISO);
    
    // Calcular meses completos y días adicionales usando método de 30 días por mes
    let diasLaborales = 0;
    
    // Si es el mismo mes
    if (fechaDesde.getFullYear() === fechaHasta.getFullYear() && fechaDesde.getMonth() === fechaHasta.getMonth()) {
      diasLaborales = Math.min(fechaHasta.getDate(), 30) - fechaDesde.getDate() + 1;
      diasLaborales = Math.min(diasLaborales, 30);
    } else {
      // Días del primer mes (desde el día de inicio hasta el 30)
      const diasPrimerMes = Math.min(30, 30 - fechaDesde.getDate() + 1);
      
      // Días del último mes (desde el 1 hasta el día final, máximo 30)
      const diasUltimoMes = Math.min(fechaHasta.getDate(), 30);
      
      // Meses completos entre medio
      let mesesCompletos = 0;
      let fechaTemp = new Date(fechaDesde.getFullYear(), fechaDesde.getMonth() + 1, 1);
      while (fechaTemp < new Date(fechaHasta.getFullYear(), fechaHasta.getMonth(), 1)) {
        mesesCompletos++;
        fechaTemp.setMonth(fechaTemp.getMonth() + 1);
      }
      
      diasLaborales = diasPrimerMes + (mesesCompletos * 30) + diasUltimoMes;
    }
    
    // Limitar a 360 días máximo (un año completo)
    diasLaborales = Math.min(diasLaborales, 360);
    
    if (diasLaborales <= 0) {
      return { dias: 0, meses: 0, cesantias: 0, intereses: 0, total: 0 };
    }
 
    // Fórmula Colombia:
    // Cesantías = (Salario + Aux) × días / 360
    const cesantias = Math.round(base * diasLaborales / 360);
    // Intereses = Cesantías × 12% × días / 360
    const intereses = Math.round(cesantias * INTERES_CES_DINAMICO * diasLaborales / 360);
    const total = cesantias + intereses;
 
    return { 
      dias: diasLaborales, 
      meses: diasLaborales / 30, 
      cesantias, 
      intereses, 
      total,
      salarioBase: salario,
      auxTransporte: auxTransporte,
      baseTotal: base
    };
  }

  // ================== INTERESES A LAS CESANTÍAS (SOLO INTERESES) ==================
  // Fórmula Colombia:
  // - Cesantías = (Salario + Aux) × días / 360
  // - Intereses = Cesantías × 12% × días / 360
  // En Colombia se usa año de 360 días (12 meses × 30 días)
  function calcInteresesCesantiasSolo(empleado, desdeISO, hastaISO) {
    if (!empleado || !desdeISO || !hastaISO) {
      return { dias: 0, meses: 0, baseCesantias: 0, intereses: 0, total: 0, diasTrabajados: 0 };
    }

    // Usar promedioultimos6meses si existe, sino usar salarioBase
    const salario = Number(empleado.promedioultimos6meses || empleado.salarioBase || empleado.salariobase || 0);
    const auxTransporte = AUX_TRANSP_DINAMICO;
    const base = salario + auxTransporte;

    if (!base) {
      return { dias: 0, meses: 0, baseCesantias: 0, intereses: 0, total: 0, diasTrabajados: 0, error: "El empleado no tiene salario base registrado." };
    }

    // Calcular días laborales (cada mes = 30 días, máximo 360 por año)
    const fechaDesde = parseISO(desdeISO);
    const fechaHasta = parseISO(hastaISO);
    
    let diasLaborales = 0;
    
    if (fechaDesde.getFullYear() === fechaHasta.getFullYear() && fechaDesde.getMonth() === fechaHasta.getMonth()) {
      diasLaborales = Math.min(fechaHasta.getDate(), 30) - fechaDesde.getDate() + 1;
      diasLaborales = Math.min(diasLaborales, 30);
    } else {
      const diasPrimerMes = Math.min(30, 30 - fechaDesde.getDate() + 1);
      const diasUltimoMes = Math.min(fechaHasta.getDate(), 30);
      
      let mesesCompletos = 0;
      let fechaTemp = new Date(fechaDesde.getFullYear(), fechaDesde.getMonth() + 1, 1);
      while (fechaTemp < new Date(fechaHasta.getFullYear(), fechaHasta.getMonth(), 1)) {
        mesesCompletos++;
        fechaTemp.setMonth(fechaTemp.getMonth() + 1);
      }
      
      diasLaborales = diasPrimerMes + (mesesCompletos * 30) + diasUltimoMes;
    }
    
    diasLaborales = Math.min(diasLaborales, 360);
    
    if (diasLaborales <= 0) {
      return { dias: 0, meses: 0, baseCesantias: 0, intereses: 0, total: 0, diasTrabajados: 0 };
    }

    // Calcular cesantías proporcionales: (Salario + Aux) × días / 360
    const baseCesantias = Math.round(base * diasLaborales / 360);
    
    // Calcular intereses: Cesantías × 12% × días / 360
    const intereses = Math.round(baseCesantias * INTERES_CES_DINAMICO * diasLaborales / 360);

    return { 
      dias: diasLaborales, 
      meses: diasLaborales / 30, 
      baseCesantias, // Base de cálculo (cesantías acumuladas)
      intereses, // Solo esto se paga
      total: intereses, // Solo intereses
      diasTrabajados: diasLaborales,
      porcentaje: (INTERES_CES_DINAMICO * 100).toFixed(1),
      salarioBase: salario,
      auxTransporte: auxTransporte,
      baseTotal: base
    };
  }
 
  // ================== LIQUIDACIÓN DE CONTRATO (MODIFICADA) ==================
  // - Prima: Solo permite fechas del año en curso (01/01-30/06 o 01/07-31/12)
  // - Vacaciones: Muestra desglose por período en la impresión
  function calcContrato(empleado, opciones, bucketsEmpleadoFn) {
    const {
      cesDesde,
      cesHasta,
      primaDesde,
      primaHasta,
      vacDiasAcumulados, // días acumulados de vacaciones
      vacacionesBuckets, // NUEVO: detalle de días por período
    } = opciones || {};
 
    if (!empleado) {
      return { error: "Selecciona un empleado para liquidar el contrato." };
    }
 
    if (!empleado.fechaIngreso) {
      return { error: "El empleado no tiene fecha de ingreso registrada." };
    }
 
    const detalle = [];
    let total = 0;
    let fechaInicio = null;
    let fechaFin = null;
 
    // ---------- CESANTÍAS + INTERESES ----------
    if (cesDesde && cesHasta) {
      const res = calcCesantiasRango(empleado, cesDesde, cesHasta);
 
      if (res.error) {
        return { error: res.error };
      }
 
      if (res.total && res.total > 0) {
        detalle.push({
          nombre: `Cesantías del ${cesDesde} al ${cesHasta}`,
          valor: res.cesantias,
        });
        detalle.push({
          nombre: `Intereses cesantías (12%) del ${cesDesde} al ${cesHasta}`,
          valor: res.intereses,
        });
 
        total += res.total;
 
        fechaInicio = fechaInicio ? (fechaInicio < cesDesde ? fechaInicio : cesDesde) : cesDesde;
        fechaFin    = fechaFin    ? (fechaFin    > cesHasta ? fechaFin    : cesHasta) : cesHasta;
      }
    }
 
    // ---------- PRIMA (con restricción de año en curso) ----------
    if (primaDesde && primaHasta) {
      const anioActual = new Date().getFullYear();
      const fIni = parseISO(primaDesde);
      const fFin = parseISO(primaHasta);
 
      // Validar que sea del año en curso
      if (fIni.getFullYear() !== anioActual || fFin.getFullYear() !== anioActual) {
        return {
          error: `La prima solo puede liquidarse con fechas del año en curso (${anioActual}). Las fechas ingresadas corresponden a otro año.`
        };
      }
 
      const mIni = fIni.getMonth();
      const mFin = fFin.getMonth();
 
      // Validar rangos permitidos: 01/01-30/06 o 01/07-31/12
      let rangoValido = false;
      let etiqueta = "";
 
      if (mIni <= 5 && mFin <= 5) {
        // Primer semestre
        const inicioPermitido = `${anioActual}-01-01`;
        const finPermitido = `${anioActual}-06-30`;
        
        if (primaDesde >= inicioPermitido && primaHasta <= finPermitido) {
          rangoValido = true;
          etiqueta = "enero-junio";
        }
      } else if (mIni >= 6 && mFin >= 6) {
        // Segundo semestre
        const inicioPermitido = `${anioActual}-07-01`;
        const finPermitido = `${anioActual}-12-31`;
        
        if (primaDesde >= inicioPermitido && primaHasta <= finPermitido) {
          rangoValido = true;
          etiqueta = "julio-diciembre";
        }
      }
 
      if (!rangoValido) {
        return {
          error: `La prima debe estar dentro de los rangos permitidos del año ${anioActual}:\n• 01/01/${anioActual} al 30/06/${anioActual} (primer semestre)\n• 01/07/${anioActual} al 31/12/${anioActual} (segundo semestre)\n\nNo se permiten rangos que crucen semestres.`
        };
      }
 
      // Calcular prima
      const base = Number(empleado.promedioultimos6meses || empleado.salarioProm6m || empleado.salarioBase || empleado.salariobase || 0);
      if (!base) {
        return { error: "El empleado no tiene salario registrado para calcular la prima." };
      }
 
      let desdeTrab = primaDesde;
      if (empleado.fechaIngreso && empleado.fechaIngreso > desdeTrab) {
        desdeTrab = empleado.fechaIngreso;
      }
 
      if (desdeTrab > primaHasta) {
        // No hay derecho
        detalle.push({
          nombre: `Prima de servicios (${etiqueta}) - Sin derecho (ingreso posterior)`,
          valor: 0
        });
      } else {
        const semInicio = mIni <= 5 ? `${anioActual}-01-01` : `${anioActual}-07-01`;
        const semFin = mIni <= 5 ? `${anioActual}-06-30` : `${anioActual}-12-31`;
        
        const diasPeriodo = daysBetweenInclusive(semInicio, semFin);
        const diasTrab = daysBetweenInclusive(desdeTrab, primaHasta);
 
        const valorDia = base / 30;
        const primaSemestreCompleta = valorDia * PRIMA_DIAS_SEM;
        const factor = diasPeriodo > 0 ? diasTrab / diasPeriodo : 0;
        const primaValor = Math.round(primaSemestreCompleta * factor);
 
        detalle.push({
          nombre: `Prima de servicios ${etiqueta} (${diasTrab} días sobre ${diasPeriodo})`,
          valor: primaValor
        });
 
        total += primaValor;
 
        fechaInicio = fechaInicio ? (fechaInicio < primaDesde ? fechaInicio : primaDesde) : primaDesde;
        fechaFin    = fechaFin    ? (fechaFin    > primaHasta ? fechaFin    : primaHasta) : primaHasta;
      }
    }
 
    // ---------- VACACIONES (por días acumulados con desglose por período) ----------
    const diasVac = Number(vacDiasAcumulados || 0);
    if (diasVac > 0) {
      const base = baseVac(empleado);
      if (base) {
        const valorDia = base / 30;
        const valorVac = Math.round(valorDia * diasVac);
 
        // Crear descripción con desglose por período
        let descripcionVac = `Vacaciones acumuladas (${diasVac} días)`;
        
        if (vacacionesBuckets && vacacionesBuckets.length > 0) {
          const desglose = vacacionesBuckets
            .filter(b => b.pending > 0)
            .map(b => `${b.label}: ${b.pending} día(s)`)
            .join(" | ");
          
          if (desglose) {
            descripcionVac = `Vacaciones: ${desglose} = ${diasVac} días total`;
          }
        }
 
        detalle.push({
          nombre: descripcionVac,
          valor: valorVac,
        });
 
        total += valorVac;
      }
    }
 
    // Si no se diligenció ningún concepto, error
    if (!detalle.length) {
      return {
        error:
          "No se ingresó ningún dato para cesantías, primas o vacaciones. Diligencia al menos uno.",
      };
    }
 
    return {
      tipo: "Liquidación CONTRATO",
      fechaInicio,
      fechaFin,
      detalle,
      total,
      diasVacacionesLiquidados: diasVac,
      vacacionesBuckets, // Para la impresión
    };
  }
 
  function previsualizar() {
    // Modo grupal: procesar múltiples empleados
    if (modoGrupal && (tipo === "Prima" || tipo === "Cesantías" || tipo === "Intereses Cesantías")) {
      if (empleadosSeleccionados.length === 0) {
        alert("Selecciona al menos un empleado para liquidar en grupo");
        return;
      }

      const resultados = [];
      const errores = [];

      empleadosSeleccionados.forEach(empId => {
        const emp = empleados.find(x => x.id === empId);
        if (!emp) return;

        try {
          let resultado = null;

          if (tipo === "Prima") {
            if (!primaDesde || !primaHasta) {
              errores.push(`${emp.nombre}: Falta fecha desde/hasta`);
              return;
            }
            // Ajustar fecha inicial según fecha de ingreso del empleado
            let fechaInicioReal = primaDesde;
            if (emp.fechaIngreso) {
              const fechaIngreso = parseISO(emp.fechaIngreso);
              const fechaPeriodoInicio = parseISO(primaDesde);
              if (fechaIngreso > fechaPeriodoInicio) {
                fechaInicioReal = emp.fechaIngreso;
              }
            }
            // Usar salario según opción seleccionada (true = salario base + aux, false = promedio sin aux)
            const usarBase = primaSalarioTipo === "base";
            const res = calcPrimaRango(emp, fechaInicioReal, primaHasta, usarBase);
            if (res) {
              // Determinar semestre para el título
              const yearPrima = parseISO(primaHasta).getFullYear();
              const mesPrima = parseISO(primaHasta).getMonth();
              const semestrePrima = mesPrima <= 5 ? "1er Semestre" : "2do Semestre";
              resultado = {
                ...res,
                tipo: `Liquidación de Prima ${semestrePrima} ${yearPrima}`,
                fechaInicio: fechaInicioReal,
                fechaFin: primaHasta,
              };
            }
          } else if (tipo === "Cesantías") {
            if (!cesDesde || !cesHasta) {
              errores.push(`${emp.nombre}: Falta fecha desde/hasta`);
              return;
            }
            // Ajustar fecha inicial según fecha de ingreso del empleado
            let fechaInicioReal = cesDesde;
            if (emp.fechaIngreso) {
              const fechaIngreso = parseISO(emp.fechaIngreso);
              const fechaPeriodoInicio = parseISO(cesDesde);
              // Si el empleado ingresó después del inicio del período, usar fecha de ingreso
              if (fechaIngreso > fechaPeriodoInicio) {
                fechaInicioReal = emp.fechaIngreso;
              }
            }
            const res = calcCesantiasRango(emp, fechaInicioReal, cesHasta);
            if (res && res.cesantias) {
              // En modo grupal, solo mostrar cesantías base SIN intereses
              const yearCesantias = parseISO(cesHasta).getFullYear();
              resultado = {
                tipo: `Liquidación de Cesantías ${yearCesantias}`,
                fechaInicio: fechaInicioReal,
                fechaFin: cesHasta,
                // Datos para mostrar en metadatos
                auxTransporte: res.auxTransporte,
                baseLiquidacion: res.baseTotal,
                detalle: [
                  { nombre: `Cesantías del ${fechaInicioReal} al ${cesHasta} (${res.dias} días)`, valor: res.cesantias },
                ],
                total: res.cesantias, // Solo cesantías, sin intereses
              };
            }
          } else if (tipo === "Intereses Cesantías") {
            if (!intCesDesde || !intCesHasta) {
              errores.push(`${emp.nombre}: Falta fecha desde/hasta`);
              return;
            }
            // Ajustar fecha inicial según fecha de ingreso del empleado
            let fechaInicioReal = intCesDesde;
            if (emp.fechaIngreso) {
              const fechaIngreso = parseISO(emp.fechaIngreso);
              const fechaPeriodoInicio = parseISO(intCesDesde);
              if (fechaIngreso > fechaPeriodoInicio) {
                fechaInicioReal = emp.fechaIngreso;
              }
            }
            const res = calcInteresesCesantiasSolo(emp, fechaInicioReal, intCesHasta);
            const yearIntCes = parseISO(intCesHasta).getFullYear();
            if (res && res.intereses !== undefined) {
              resultado = {
                tipo: `Liquidación de Intereses de Cesantías ${yearIntCes}`,
                fechaInicio: fechaInicioReal,
                fechaFin: intCesHasta,
                // Datos para mostrar en metadatos
                auxTransporte: res.auxTransporte,
                baseLiquidacion: res.baseTotal,
                detalle: [
                  { nombre: `Base cesantías (${res.diasTrabajados} días)`, valor: res.baseCesantias },
                  { nombre: `Intereses (12% - ${res.diasTrabajados} días)`, valor: res.intereses },
                ],
                total: res.intereses,
              };
            }
          } else if (tipo === "Vacaciones") {
            const buckets = bucketsEmpleado(emp);
            if (buckets.length > 0) {
              // Tomar el primer período disponible y todos los días
              const bucket = buckets[0];
              const diasNum = bucket.pending;
              const calcVac = calcVacaciones(emp, diasNum);
              if (calcVac && !calcVac.error) {
                resultado = {
                  ...calcVac,
                  tipo: "VACACIONES",
                  periodoAfectado: bucket.label,
                  diasTomados: diasNum,
                };
              }
            } else {
              errores.push(`${emp.nombre}: Sin días de vacaciones disponibles`);
              return;
            }
          }

          if (resultado && !resultado.error) {
            resultados.push({
              ...resultado,
              empleado: emp,
              empleadoId: emp.id,
            });
          } else if (resultado?.error) {
            errores.push(`${emp.nombre}: ${resultado.error}`);
          }
        } catch (err) {
          errores.push(`${emp.nombre}: Error al calcular - ${err.message}`);
        }
      });

      if (errores.length > 0 && resultados.length === 0) {
        alert("Errores al calcular:\n" + errores.join("\n"));
        return;
      }

      setResultadosGrupales(resultados);
      setCalc(null); // Limpiar cálculo individual
      
      if (errores.length > 0) {
        console.warn("⚠️ Algunos empleados tuvieron errores:", errores);
      }
      
      console.log("📊 Resultados grupales calculados:", resultados.length, "liquidaciones");
      return;
    }

    // Modo individual (código original)
    const e = empleados.find((x) => x.id === empleadoId);
    if (!e) {
      alert("Selecciona un empleado");
      return;
    }
  
    let r;
  
    if (tipo === "Vacaciones") {
      const buckets = bucketsEmpleado(e);
      if (!buckets.length) {
        alert("No hay períodos de vacaciones con días disponibles para este empleado");
        return;
      }
  
      if (!vacPeriodo) {
        alert("Selecciona el período afectado");
        return;
      }
  
      const bucketSel = buckets.find((b) => b.label === vacPeriodo);
      if (!bucketSel) {
        alert("El período seleccionado no es válido");
        return;
      }
  
      const diasNum = Number(vacDias || bucketSel.pending);
      if (!diasNum || diasNum <= 0) {
        alert("Ingresa los días a liquidar (o deja vacío para todo el período)");
        return;
      }
  
      if (diasNum > bucketSel.pending) {
        alert(`Solo hay ${bucketSel.pending} día(s) disponibles en ese período`);
        return;
      }
  
      if (!vacDesde || !vacHasta) {
        alert("Debes indicar la fecha inicial para que se calcule la fecha final.");
        return;
      }
  
      r = calcVacaciones(e, diasNum);
  
      if (!r.error) {
        r.fechaInicio = vacDesde;
        r.fechaFin = vacHasta;
        r.periodoAfectado = vacPeriodo;
        r.diasTomados = diasNum;
        // Actualizar título con el período
        r.tipo = `Liquidación de Vacaciones ${vacPeriodo}`;
      }
    } else if (tipo === "Cesantías") {
      if (!cesDesde || !cesHasta) {
        alert("Ingresa la fecha inicial y la fecha final del período a liquidar para cesantías.");
        return;
      }
 
      if (parseISO(cesDesde) > parseISO(cesHasta)) {
        alert("En cesantías, la fecha inicial no puede ser mayor que la fecha final.");
        return;
      }
 
      // Ajustar fecha inicial según fecha de ingreso del empleado
      let fechaInicioRealCes = cesDesde;
      if (e.fechaIngreso) {
        const fechaIngreso = parseISO(e.fechaIngreso);
        const fechaPeriodoInicio = parseISO(cesDesde);
        // Si el empleado ingresó después del inicio del período, usar fecha de ingreso
        if (fechaIngreso > fechaPeriodoInicio) {
          fechaInicioRealCes = e.fechaIngreso;
        }
      }
 
      const res = calcCesantiasRango(e, fechaInicioRealCes, cesHasta);
 
      if (!res || !res.total) {
        alert("No se pudo calcular las cesantías. Verifica salario y fechas.");
        return;
      }
 
      const yearCesInd = parseISO(cesHasta).getFullYear();
      r = {
        tipo: `Liquidación de Cesantías ${yearCesInd}`,
        fechaInicio: fechaInicioRealCes,
        fechaFin: cesHasta,
        // Datos para mostrar en metadatos
        auxTransporte: res.auxTransporte,
        baseLiquidacion: res.baseTotal,
        detalle: [
          {
            nombre: `Cesantías del ${fechaInicioRealCes} al ${cesHasta} (${res.dias} días)`,
            valor: res.cesantias,
          },
          {
            nombre: `Intereses (12%) del ${fechaInicioRealCes} al ${cesHasta}`,
            valor: res.intereses,
          },
        ],
        total: res.total,
        actualizarCampo: "ultimasCesantias",
        nuevaFechaCampo: cesHasta,
      };
 
    } else if (tipo === "Prima") {
      if (!primaDesde || !primaHasta) {
        alert("Ingresa la fecha inicial y la fecha final del período a liquidar para prima.");
        return;
      }
 
      // Ajustar fecha inicial según fecha de ingreso del empleado
      let fechaInicioRealPrima = primaDesde;
      if (e.fechaIngreso) {
        const fechaIngreso = parseISO(e.fechaIngreso);
        const fechaPeriodoInicio = parseISO(primaDesde);
        // Si el empleado ingresó después del inicio del período, usar fecha de ingreso
        if (fechaIngreso > fechaPeriodoInicio) {
          fechaInicioRealPrima = e.fechaIngreso;
        }
      }
 
      // Usar salario según opción seleccionada (true = salario base + aux, false = promedio sin aux)
      const usarBase = primaSalarioTipo === "base";
      const rPrima = calcPrimaRango(e, fechaInicioRealPrima, primaHasta, usarBase);
      if (rPrima && !rPrima.error) {
        // Determinar semestre para el título
        const yearPrimaInd = parseISO(primaHasta).getFullYear();
        const mesPrimaInd = parseISO(primaHasta).getMonth();
        const semestrePrimaInd = mesPrimaInd <= 5 ? "1er Semestre" : "2do Semestre";
        rPrima.tipo = `Liquidación de Prima ${semestrePrimaInd} ${yearPrimaInd}`;
        // Actualizar fechas para reflejar el ajuste por fecha de ingreso
        rPrima.fechaInicio = fechaInicioRealPrima;
        rPrima.fechaFin = primaHasta;
      }
      r = rPrima;
    } else if (tipo === "Intereses Cesantías") {
      // Liquidación solo de intereses a las cesantías
      if (!intCesDesde || !intCesHasta) {
        alert("Ingresa la fecha inicial y la fecha final del período para calcular intereses.");
        return;
      }
 
      if (parseISO(intCesDesde) > parseISO(intCesHasta)) {
        alert("La fecha inicial no puede ser mayor que la fecha final.");
        return;
      }
 
      // Ajustar fecha inicial según fecha de ingreso del empleado
      let fechaInicioRealIntCes = intCesDesde;
      if (e.fechaIngreso) {
        const fechaIngreso = parseISO(e.fechaIngreso);
        const fechaPeriodoInicio = parseISO(intCesDesde);
        // Si el empleado ingresó después del inicio del período, usar fecha de ingreso
        if (fechaIngreso > fechaPeriodoInicio) {
          fechaInicioRealIntCes = e.fechaIngreso;
        }
      }
 
      const res = calcInteresesCesantiasSolo(e, fechaInicioRealIntCes, intCesHasta);
 
      if (!res || res.intereses === undefined) {
        alert("No se pudo calcular los intereses. Verifica salario y fechas.");
        return;
      }
 
      const yearIntCesInd = parseISO(intCesHasta).getFullYear();
      r = {
        tipo: `Liquidación de Intereses de Cesantías ${yearIntCesInd}`,
        fechaInicio: fechaInicioRealIntCes,
        fechaFin: intCesHasta,
        // Datos para mostrar en metadatos
        auxTransporte: res.auxTransporte,
        baseLiquidacion: res.baseTotal,
        detalle: [
          {
            nombre: `Base cesantías del ${fechaInicioRealIntCes} al ${intCesHasta} (${res.diasTrabajados} días)`,
            valor: res.baseCesantias,
          },
          {
            nombre: `Intereses (12% anual - ${res.diasTrabajados} días)`,
            valor: res.intereses,
          },
        ],
        total: res.intereses,
      };
    } else if (tipo === "Liquidación CONTRATO") {
      // NUEVO: Liquidación de contrato con días acumulados de vacaciones
      // Obtener los buckets para el desglose en la impresión
      const buckets = bucketsEmpleado(e);
      
      r = calcContrato(e, {
        cesDesde,
        cesHasta,
        primaDesde,
        primaHasta,
        vacDiasAcumulados: contratoVacDias,
        vacacionesBuckets: buckets.filter(b => b.pending > 0), // Solo los que tienen días disponibles
      });
    } else {
      alert("Tipo de liquidación no reconocido");
      return;
    }
 
    if (!r) {
      alert("No se pudo calcular la liquidación.");
      return;
    }
  
    if (r.error) {
      alert(r.error);
      return;
    }

    if (infoBloqueo.activo) {
      alert(formatearMensajeBloqueo(infoBloqueo.razones));
      return;
    }
  
    setCalc({
      ...r,
      tipo: r.tipo || tipo,
      empleado: e,
    });
    
    console.log("🖨️ Previsualización calculada:", {
      tipo: r.tipo || tipo,
      empleado: e.nombre,
      detalle: r.detalle,
      total: r.total,
      // Debug campos de salario
      auxTransporte: r.auxTransporte,
      baseLiquidacion: r.baseLiquidacion,
      tipoSalarioUsado: r.tipoSalarioUsado,
    });
  }
      
  function guardarActual() {
    if (!calc) {
      alert("Primero calcula la liquidación (botón Calcular / Previsualizar).");
      return;
    }
  
    if (!empleadoId || !tipo) {
      alert("Falta seleccionar empleado o tipo de prestación.");
      return;
    }

    if (infoBloqueo.activo) {
      alert(formatearMensajeBloqueo(infoBloqueo.razones));
      return;
    }
  
    const rec = {
      id: uid(),
      tipo: calc.tipo || tipo,
      empleadoId,
      empleadoNombre: calc.empleado?.nombre,
      empleadoDoc: calc.empleado?.documento,
      fecha: toISO(new Date()),
      fechaInicio: calc.fechaInicio || null,
      fechaFin: calc.fechaFin || null,
      periodo: `${calc.fechaInicio || ""} → ${calc.fechaFin || ""}`,
      detalle: calc.detalle || [],
      total: calc.total || 0,
      empleado: calc.empleado,
      periodoAfectado: calc.periodoAfectado,
      diasTomados: calc.diasTomados,
      diasVacacionesLiquidados: calc.diasVacacionesLiquidados,
      vacacionesBuckets: calc.vacacionesBuckets,
      // Campos para mostrar en impresión
      auxTransporte: calc.auxTransporte || null,
      baseLiquidacion: calc.baseLiquidacion || null,
      tipoSalarioUsado: calc.tipoSalarioUsado || null,
    };
  
    const lista = Array.isArray(repo) ? repo : [];
  
    const conflicto = lista.some((l) => {
      if (l.empleadoId !== rec.empleadoId) return false;
  
      const tipoExistente = l.tipo || l.tipoPrestacion;
      if (tipoExistente !== rec.tipo) return false;
  
      if (!l.fechaInicio || !l.fechaFin || !rec.fechaInicio || !rec.fechaFin) {
        return (
          l.fechaInicio === rec.fechaInicio &&
          l.fechaFin === rec.fechaFin &&
          l.fechaInicio != null &&
          l.fechaFin != null
        );
      }
  
      const a1 = parseISO(l.fechaInicio);
      const a2 = parseISO(l.fechaFin);
      const b1 = parseISO(rec.fechaInicio);
      const b2 = parseISO(rec.fechaFin);
  
      return a1 <= b2 && b1 <= a2;
    });
  
    if (conflicto) {
      alert(
        `Ya existe una liquidación de ${rec.tipo} para este empleado con fechas iguales o que se cruzan con este período. ` +
          "Modifica las fechas antes de guardar."
      );
      return;
    }
  
    if (onGuardarLiquidacion) {
      onGuardarLiquidacion(rec);
    }
  
    // 🔹 NUEVO: Si es liquidación de contrato con vacaciones, crear ausencia para descontar días
    if (
      calc.tipo === "Liquidación CONTRATO" &&
      calc.diasVacacionesLiquidados &&
      calc.diasVacacionesLiquidados > 0 &&
      typeof onCrearAusencia === "function"
    ) {
      const fechaLiquidacion = toISO(new Date());
  
      // Construir detalle de períodos afectados
      const detalleVac = (calc.vacacionesBuckets || [])
        .filter((b) => b.pending > 0)
        .map((b) => ({
          periodo: b.label,
          dias: b.pending, // días disponibles del período
        }));
  
      // Construir cadena de período para referencia
      const periodoStr = detalleVac
        .map((d) => `${d.periodo}:${d.dias}`)
        .join(" + ");
  
      // Crear ausencia de vacaciones que descuenta los días liquidados
      const ausenciaVacaciones = {
        id: uid(),
        empleadoId,
        tipo: "Vacaciones",
        origen: "No aplica",
        inicio: fechaLiquidacion,
        fin: fechaLiquidacion,
        periodo: periodoStr,
        detalleVac,
        motivo: "Liquidación de contrato - Vacaciones compensadas",
        cobroPagado: false,
        cobroFecha: "",
        reintegro: "",
        dias: calc.diasVacacionesLiquidados, // 🔹 Días totales liquidados
        liquidacionDirecta: true, // 🔹 Flag especial para que applyVacationUses use el campo dias
      };
    // 🔹 NUEVO: Verificar si el empleado debe ser inactivado
    if (
      calc.tipo === "Liquidación CONTRATO" &&
      typeof onInactivarEmpleado === "function"
    ) {
      // Calcular días totales disponibles después de la liquidación
      const empleado = empleados.find((e) => e.id === empleadoId);
      if (empleado) {
        // Incluir la nueva ausencia en el cálculo
        const ausenciasActualizadas = [...ausencias];
        if (calc.diasVacacionesLiquidados > 0) {
          ausenciasActualizadas.push({
            id: uid(),
            empleadoId,
            tipo: "Vacaciones",
            inicio: toISO(new Date()),
            dias: calc.diasVacacionesLiquidados,
            liquidacionDirecta: true,
          });
        }
  
        const holidaysCache = {};
        const diasDisponibles = calcularDiasTotalesDisponibles(
          empleado,
          ausenciasActualizadas,
          holidaysCache
        );
  
        if (diasDisponibles === 0) {
          onInactivarEmpleado(empleadoId);
          alert(
            `El empleado ${empleado.nombre} ha sido marcado como INACTIVO ` +
              `porque todos sus días de vacaciones han sido liquidados.`
          );
        }
      }
    }
      // Crear la ausencia (esto descuenta automáticamente en el botón Vacaciones)
      onCrearAusencia(ausenciaVacaciones);
  
      alert(
        `Liquidación guardada correctamente.\n\n` +
          `Se descontaron ${calc.diasVacacionesLiquidados} día(s) de vacaciones:\n` +
          detalleVac.map((d) => `• ${d.periodo}: ${d.dias} día(s)`).join("\n")
      );
    } else {
      alert("Liquidación guardada correctamente.");
    }
  
    if (calc.actualizarCampo && calc.nuevaFechaCampo) {
      try {
        const campo = calc.actualizarCampo;
        const nuevo = calc.nuevaFechaCampo;
        const e = empleados.find((x) => x.id === empleadoId);
        if (e) {
          e[campo] = nuevo;
        }
      } catch (err) {
        console.error(err);
      }
    }
  
    setCalc(null);
    setEmpleadoId("");
  }
  
 
  const repoFiltrado = useMemo(() => {
    return (repo || []).map((r) => {
      // Enriquecer la liquidación con datos completos del empleado
      const empleado = empleados.find((e) => e.id === r.empleadoId);
      
      return {
        ...r,
        empleado: empleado || {
          id: r.empleadoId,
          nombre: r.empleadoNombre || "Sin nombre",
          documento: r.empleadoDoc || "",
          tipoDoc: "CC",
          sede: "",
        },
      };
    }).filter((r) => {
      if (filtroEmpleado && r.empleadoId !== filtroEmpleado) return false;
    
      // Filtro por tipo - buscar si el tipo contiene el valor seleccionado
      if (filtroTipo) {
        const tipoLower = (r.tipo || '').toLowerCase();
        const filtroLower = filtroTipo.toLowerCase();
        // Verificar si el tipo contiene el filtro o coincide exactamente
        const coincide = tipoLower.includes(filtroLower) || 
                         (r.tipoPrestacion && r.tipoPrestacion.toLowerCase().includes(filtroLower));
        if (!coincide) return false;
      }

      // Filtro por sede del empleado
      if (filtroSede && r.empleado?.sede !== filtroSede) {
        return false;
      }

      // Filtro por fecha de liquidación
      if (filtroFechaDesde && r.fecha) {
        const fechaLiq = parseISO(r.fecha);
        const fechaDesde = parseISO(filtroFechaDesde);
        if (fechaLiq < fechaDesde) return false;
      }
      if (filtroFechaHasta && r.fecha) {
        const fechaLiq = parseISO(r.fecha);
        const fechaHasta = parseISO(filtroFechaHasta);
        if (fechaLiq > fechaHasta) return false;
      }

      if (r.empleadoId && !empleados.some((e) => e.id === r.empleadoId)) {
        return false;
      }
    
      return true;
    });
  }, [repo, empleados, filtroEmpleado, filtroTipo, filtroSede, filtroFechaDesde, filtroFechaHasta]);
  
 
  return (
    <div style={{ padding: '16px' }}>
      <h2 style={{
        fontSize: '18px',
        fontWeight: '700',
        color: '#ff6b35',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        borderBottom: '3px solid #ffc53d',
        paddingBottom: '10px'
      }}>💰 Liquidaciones de Prestaciones</h2>

      {/* ============ PESTAÑAS DE NAVEGACIÓN ============ */}
      <div style={{ 
        display: 'flex', 
        gap: '0', 
        marginBottom: '20px',
        backgroundColor: '#f1f5f9',
        borderRadius: '12px',
        padding: '4px',
        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)'
      }}>
        <button
          onClick={() => setVistaActiva("liquidar")}
          style={{
            flex: 1,
            padding: '14px 20px',
            fontSize: '14px',
            fontWeight: '700',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            background: vistaActiva === "liquidar" 
              ? 'linear-gradient(135deg, #ff6b35 0%, #f97316 100%)' 
              : 'transparent',
            color: vistaActiva === "liquidar" ? 'white' : '#64748b',
            boxShadow: vistaActiva === "liquidar" ? '0 4px 12px rgba(249, 115, 22, 0.3)' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          💳 Liquidar Prestaciones
        </button>
        <button
          onClick={() => setVistaActiva("historico")}
          style={{
            flex: 1,
            padding: '14px 20px',
            fontSize: '14px',
            fontWeight: '700',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            background: vistaActiva === "historico" 
              ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' 
              : 'transparent',
            color: vistaActiva === "historico" ? 'white' : '#64748b',
            boxShadow: vistaActiva === "historico" ? '0 4px 12px rgba(139, 92, 246, 0.3)' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          📚 Archivos Históricos
        </button>
      </div>

      {/* ============ CONTENIDO DE LIQUIDAR PRESTACIONES ============ */}
      {vistaActiva === "liquidar" && (
        <>
      {/* Toggle Modo Grupal - Siempre visible */}
      <div style={{ 
        marginBottom: '16px', 
        padding: '12px 16px', 
        backgroundColor: modoGrupal ? '#e8f5e9' : '#fff3e0', 
        borderRadius: '10px', 
        border: modoGrupal ? '2px solid #4caf50' : '2px solid #ff9800',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: modoGrupal ? '#2e7d32' : '#e65100' }}>
            {modoGrupal ? '👥 Modo Grupal ACTIVADO' : '👤 Modo Individual'}
          </span>
          <span style={{ fontSize: '12px', color: '#666' }}>
            {modoGrupal 
              ? 'Selecciona múltiples empleados para liquidar (Prima, Cesantías, Intereses Cesantías)' 
              : 'Liquida un empleado a la vez'}
          </span>
        </div>
        <button
          onClick={() => {
            const nuevoModo = !modoGrupal;
            setModoGrupal(nuevoModo);
            // Si cambia a modo grupal y tiene un tipo no permitido, cambiar a Prima
            if (nuevoModo && (tipo === "Vacaciones" || tipo === "Liquidación CONTRATO")) {
              setTipo("Prima");
            }
            setEmpleadosSeleccionados([]);
            setResultadosGrupales([]);
            setCalc(null);
          }}
          style={{
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            background: modoGrupal ? 'linear-gradient(135deg, #ff9800, #f57c00)' : 'linear-gradient(135deg, #4caf50, #388e3c)',
            color: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          {modoGrupal ? '🔄 Cambiar a Individual' : '🔄 Cambiar a Grupal'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px', padding: '16px', backgroundColor: '#fffbf5', borderRadius: '12px', border: '2px solid #ffe4c9' }}>
        
        {/* Selector de empleado individual (solo si NO está en modo grupal) */}
        {!modoGrupal && (
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>👤 Empleado (activos)</label>
          <select
            value={empleadoId}
            onChange={(e) => setEmpleadoId(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              fontSize: '13px',
              border: '2px solid #ffc53d',
              borderRadius: '8px',
              backgroundColor: '#fffbf5',
              cursor: 'pointer',
            }}
          >
            <option value="">-- Selecciona --</option>
            {[...(empleados || [])].filter(e => e.activo).sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map(e => (
              <option key={e.id} value={e.id}>{e.nombre} – {e.tipoDoc} {e.documento}</option>
            ))}
          </select>
        </div>
        )}

        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>🎯 Tipo de liquidación</label>
          <select
            value={tipo}
            onChange={(e) => {
              setTipo(e.target.value);
              // NO resetear modoGrupal aquí para mantener el estado
              setResultadosGrupales([]);
              setCalc(null);
            }}
            style={{
              width: '100%',
              padding: '10px 14px',
              fontSize: '13px',
              border: '2px solid #ffc53d',
              borderRadius: '8px',
              backgroundColor: '#fffbf5',
              cursor: 'pointer',
            }}
          >
            {/* En modo grupal solo mostrar Prima, Cesantías, Intereses Cesantías */}
            {!modoGrupal && <option value="Vacaciones">🏖️ Vacaciones</option>}
            <option value="Cesantías">💰 Cesantías</option>
            <option value="Prima">🎁 Prima</option>
            <option value="Intereses Cesantías">📈 Intereses a las Cesantías</option>
            {!modoGrupal && <option value="Liquidación CONTRATO">📄 Liquidación CONTRATO</option>}
          </select>
        </div>
 
        {tipo === "Cesantías" && (
          <>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📅 Período a liquidar (año)</label>
              <select
                value={yearCes}
                onChange={(e) => {
                  const y = Number(e.target.value || 0);
                  setYearCes(y);
                  setCesDesde("");
                  setCesHasta("");
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                  cursor: 'pointer',
                }}
              >
                {(() => {
                  const actual = new Date().getFullYear();
                  const anterior = actual - 1;
                  return (
                    <>
                      <option value={actual}>{actual} (año en curso)</option>
                      <option value={anterior}>{anterior} (año anterior)</option>
                    </>
                  );
                })()}
              </select>
            </div>
 
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📆 Fecha inicial del período</label>
              <input
                type="date"
                value={cesDesde}
                onChange={(e) => setCesDesde(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                }}
              />
            </div>
 
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📆 Fecha final del período</label>
              <input
                type="date"
                value={cesHasta}
                onChange={(e) => setCesHasta(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                }}
              />
            </div>
          </>
        )}
 
        {tipo === "Prima" && (
          <>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>� Salario a usar para prima</label>
              <select
                value={primaSalarioTipo}
                onChange={(e) => setPrimaSalarioTipo(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                  cursor: 'pointer',
                }}
              >
                <option value="promedio">📊 Salario promedio últimos 6 meses</option>
                <option value="base">💵 Salario base actual</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📆 Fecha inicial del período de prima</label>
              <input
                type="date"
                value={primaDesde}
                onChange={(e) => setPrimaDesde(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                }}
              />
            </div>
 
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📆 Fecha final del período de prima</label>
              <input
                type="date"
                value={primaHasta}
                onChange={(e) => setPrimaHasta(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                }}
              />
            </div>
          </>
        )}
 
        {tipo === "Vacaciones" && (
          <>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📅 Período afectado</label>
              <select
                value={vacPeriodo}
                onChange={(e) => {
                  const label = e.target.value;
                  setVacPeriodo(label);
 
                  const emp = empleados.find((x) => x.id === empleadoId);
                  if (!emp || !label) return;
 
                  const buckets = bucketsEmpleado(emp).filter((b) => b.pending > 0);
                  const bucketSel = buckets.find((b) => b.label === label);
                  if (bucketSel) {
                    setVacDias(String(bucketSel.pending));
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                  cursor: 'pointer',
                }}
              >
                <option value="">Seleccione un período con días disponibles</option>
                {(() => {
                  const emp = empleados.find((x) => x.id === empleadoId);
                  if (!emp) return null;
 
                  const buckets = bucketsEmpleado(emp).filter((b) => b.pending > 0);
                  if (!buckets.length) {
                    return (
                      <option value="" disabled>
                        No hay períodos con días disponibles
                      </option>
                    );
                  }
 
                  return buckets.map((b) => (
                    <option key={b.label} value={b.label}>
                      {b.label} – {b.fromISO} → {b.toISO} ({Number(b.pending).toFixed(1)} día(s) disponibles)
                    </option>
                  ));
                })()}
              </select>
            </div>
 
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📊 Días a liquidar de ese período</label>
              <input
                type="number"
                min="1"
                value={vacDias}
                onChange={(e) => setVacDias(e.target.value)}
                placeholder="Ej: 5"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                }}
              />
            </div>
 
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📆 Fecha inicial de vacaciones</label>
              <input
                type="date"
                value={vacDesde}
                onChange={(e) => setVacDesde(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                }}
              />
            </div>
 
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📆 Fecha final (calculada L–S sin festivos)</label>
              <input
                type="date"
                value={vacHasta}
                onChange={(e) => setVacHasta(e.target.value)}
                readOnly
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffe4c9',
                  borderRadius: '8px',
                  backgroundColor: '#f5f5f5',
                  color: '#666',
                }}
              />
            </div>
          </>
        )}

        {/* NUEVO: Campos para Intereses a las Cesantías */}
        {tipo === "Intereses Cesantías" && (
          <>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📅 Año del período</label>
              <select
                value={intCesYear}
                onChange={(e) => {
                  const y = Number(e.target.value);
                  setIntCesYear(y);
                  setIntCesDesde(`${y}-01-01`);
                  setIntCesHasta(`${y}-12-31`);
                }}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                  cursor: 'pointer',
                }}
              >
                {[...Array(6)].map((_, i) => {
                  const y = new Date().getFullYear() - i;
                  return <option key={y} value={y}>{y}</option>;
                })}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📆 Fecha inicial</label>
              <input
                type="date"
                value={intCesDesde}
                onChange={(e) => setIntCesDesde(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📆 Fecha final</label>
              <input
                type="date"
                value={intCesHasta}
                onChange={(e) => setIntCesHasta(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                }}
              />
            </div>
          </>
        )}
 
        {/* NUEVO: Campos para Liquidación de Contrato */}
        {tipo === "Liquidación CONTRATO" && (
          <>
            <div style={{ gridColumn: 'span 3', borderTop: '2px solid #ffc53d', paddingTop: '12px', marginTop: '8px' }}>
              <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#ff6b35', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                💰 Cesantías + Intereses
              </h4>
            </div>
            
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📆 Fecha inicial cesantías</label>
              <input
                type="date"
                value={cesDesde}
                onChange={(e) => setCesDesde(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                }}
              />
            </div>
 
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📆 Fecha final cesantías</label>
              <input
                type="date"
                value={cesHasta}
                onChange={(e) => setCesHasta(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                }}
              />
            </div>
 
            {infoBloqueo.contrato.cesantias.length > 0 && (
              <div style={{ gridColumn: 'span 3', fontSize: '11px', color: '#ef4444', backgroundColor: '#fef2f2', padding: '8px 12px', borderRadius: '6px', border: '1px solid #fecaca' }}>
                ⚠️ Cesantías ya liquidadas:
                {infoBloqueo.contrato.cesantias.map((c, idx) => (
                  <span key={`${c.id || c.detalleNombre || c.periodo}`}> {idx ? "| " : ""}{c.fechaInicio || "—"} → {c.fechaFin || "—"}</span>
                ))}
              </div>
            )}

            <div></div>
 
            <div style={{ gridColumn: 'span 3', borderTop: '2px solid #ffc53d', paddingTop: '12px', marginTop: '8px' }}>
              <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#ff6b35', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                🎁 Prima
              </h4>
            </div>
 
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📆 Fecha inicial prima</label>
              <input
                type="date"
                value={primaDesde}
                onChange={(e) => setPrimaDesde(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                }}
              />
            </div>
 
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📆 Fecha final prima</label>
              <input
                type="date"
                value={primaHasta}
                onChange={(e) => setPrimaHasta(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                }}
              />
            </div>
 
            {infoBloqueo.contrato.prima.length > 0 && (
              <div style={{ gridColumn: 'span 3', fontSize: '11px', color: '#ef4444', backgroundColor: '#fef2f2', padding: '8px 12px', borderRadius: '6px', border: '1px solid #fecaca' }}>
                ⚠️ Prima ya liquidada:
                {infoBloqueo.contrato.prima.map((c, idx) => (
                  <span key={`${c.id || c.detalleNombre || c.periodo}`}> {idx ? "| " : ""}{c.fechaInicio || "—"} → {c.fechaFin || "—"}</span>
                ))}
              </div>
            )}

            <div></div>
 
            <div style={{ gridColumn: 'span 3', borderTop: '2px solid #ffc53d', paddingTop: '12px', marginTop: '8px' }}>
              <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#ff6b35', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                🏖️ Vacaciones (días acumulados)
              </h4>
            </div>
 
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>📊 Días de vacaciones disponibles</label>
              <input
                type="number"
                value={contratoVacDias}
                onChange={(e) => setContratoVacDias(e.target.value)}
                placeholder="Se carga automáticamente"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                }}
              />
              <p style={{ fontSize: '10px', color: '#888', marginTop: '4px' }}>
                Total de días disponibles de todos los períodos. Se prorratea por días (no por fechas).
              </p>
            </div>
 
            {infoBloqueo.contrato.vacaciones.length > 0 && (
              <div style={{ gridColumn: 'span 3', fontSize: '11px', color: '#ef4444', backgroundColor: '#fef2f2', padding: '8px 12px', borderRadius: '6px', border: '1px solid #fecaca' }}>
                ⚠️ Vacaciones ya liquidadas previamente.
              </div>
            )}

            {empleadoId && (
              <div style={{ gridColumn: 'span 2', fontSize: '12px', color: '#1890ff', backgroundColor: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: '8px', padding: '12px' }}>
                <strong>📝 Resumen de vacaciones:</strong><br/>
                {(() => {
                  const emp = empleados.find((x) => x.id === empleadoId);
                  if (!emp) return "Selecciona un empleado";
                  const buckets = bucketsEmpleado(emp);
                  if (!buckets.length) return "Sin períodos de vacaciones";
                  const totalDisp = buckets.reduce((s, b) => s + (b.pending || 0), 0);
                  return (
                    <>
                      {buckets.map((b) => (
                        <div key={b.label}>
                          • {b.label}: <span style={{ fontWeight: '600', color: '#52c41a' }}>{b.pending} día(s)</span> disponibles
                        </div>
                      ))}
                      <div style={{ marginTop: '8px', fontWeight: '700', color: '#ff6b35' }}>
                        Total disponible: {Math.round(totalDisp * 100) / 100} días
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </>
        )}
      </div>

      {/* SECCIÓN DE SELECCIÓN GRUPAL DE EMPLEADOS */}
      {modoGrupal && (tipo === "Prima" || tipo === "Cesantías" || tipo === "Intereses Cesantías") && (
        <div style={{ 
          marginBottom: '16px', 
          padding: '16px', 
          backgroundColor: '#f0f9ff', 
          borderRadius: '12px', 
          border: '2px solid #0ea5e9'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#0369a1' }}>
              👥 Seleccionar Empleados ({empleadosSeleccionados.length} seleccionados)
            </h4>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setEmpleadosSeleccionados(empleados.filter(e => e.activo).map(e => e.id))}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  border: '1px solid #0ea5e9',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#0369a1',
                  cursor: 'pointer'
                }}
              >
                ✓ Seleccionar Todos
              </button>
              <button
                onClick={() => setEmpleadosSeleccionados([])}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  border: '1px solid #dc2626',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#dc2626',
                  cursor: 'pointer'
                }}
              >
                ✕ Quitar Todos
              </button>
            </div>
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
            gap: '8px',
            maxHeight: '300px',
            overflowY: 'auto',
            padding: '8px',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            {[...(empleados || [])].filter(e => e.activo).sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map((emp) => (
              <label 
                key={emp.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: empleadosSeleccionados.includes(emp.id) ? '#dbeafe' : '#f9fafb',
                  border: empleadosSeleccionados.includes(emp.id) ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                  transition: 'all 0.2s'
                }}
              >
                <input
                  type="checkbox"
                  checked={empleadosSeleccionados.includes(emp.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setEmpleadosSeleccionados([...empleadosSeleccionados, emp.id]);
                    } else {
                      setEmpleadosSeleccionados(empleadosSeleccionados.filter(id => id !== emp.id));
                    }
                  }}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: '#1f2937' }}>{emp.nombre}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>{emp.tipoDoc} {emp.documento} • {emp.cargo}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}
 
      {infoBloqueo.activo && (
        <div style={{ marginBottom: '16px', fontSize: '12px', color: '#dc2626', backgroundColor: '#fef2f2', border: '2px solid #fecaca', borderRadius: '8px', padding: '12px 16px' }}>
          <div style={{ fontWeight: '700', marginBottom: '6px' }}>⛔ Esta liquidación ya fue registrada.</div>
          {(infoBloqueo.razones || []).map((item) => {
            const key =
              item?.id ||
              item?.detalleNombre ||
              item?.periodo ||
              `${item?.clave || ""}-${item?.fechaInicio || ""}-${item?.fechaFin || ""}`;
            return (
              <div key={key}>
                {item?.tipo || "Liquidación previa"}: {item?.fechaInicio || "—"} → {item?.fechaFin || "—"}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {tienePermiso("rh_liquidaciones_calcular") && (
        <button
          onClick={previsualizar}
          disabled={infoBloqueo.activo}
          style={{
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '8px',
            background: infoBloqueo.activo ? '#ccc' : 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
            color: 'white',
            cursor: infoBloqueo.activo ? 'not-allowed' : 'pointer',
            boxShadow: infoBloqueo.activo ? 'none' : '0 2px 8px rgba(24, 144, 255, 0.3)',
          }}
        >
          📊 Calcular / Previsualizar
        </button>
        )}
        {tienePermiso("rh_liquidaciones_guardar") && (
        <button
          onClick={guardarActual}
          disabled={!calc || infoBloqueo.activo}
          style={{
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: 'none',
            borderRadius: '8px',
            background: (!calc || infoBloqueo.activo) ? '#ccc' : 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
            color: 'white',
            cursor: (!calc || infoBloqueo.activo) ? 'not-allowed' : 'pointer',
            boxShadow: (!calc || infoBloqueo.activo) ? 'none' : '0 2px 8px rgba(82, 196, 26, 0.3)',
          }}
        >
          💾 Guardar liquidación
        </button>
        )}
        {tienePermiso("rh_liquidaciones_imprimir_historico") && (
        <button
          onClick={()=> repoFiltrado?.length ? imprimirTodos(repoFiltrado) : alert("No hay registros para imprimir")}
          style={{
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: '2px solid #ffc53d',
            borderRadius: '8px',
            background: 'white',
            color: '#ff6b35',
            cursor: 'pointer',
          }}
        >
          🖨️ Imprimir histórico
        </button>
        )}
        {tienePermiso("rh_liquidaciones_imprimir_individual") && (
        <button
          onClick={()=> calc ? imprimirIndividual(calc) : alert("Calcula primero para imprimir individual")}
          style={{
            padding: '10px 20px',
            fontSize: '13px',
            fontWeight: '600',
            border: '2px solid #ffc53d',
            borderRadius: '8px',
            background: 'white',
            color: '#ff6b35',
            cursor: 'pointer',
          }}
        >
          📄 Imprimir individual
        </button>
        )}
      </div>
 
      {calc && (
        <div style={{ border: '2px solid #ffc53d', borderRadius: '12px', padding: '16px', backgroundColor: 'white', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', marginBottom: '12px', color: '#ff6b35' }}>👁️ Previsualización</h3>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
            <div><strong>Empleado:</strong> {calc.empleado?.nombre} – {calc.empleado?.tipoDoc} {calc.empleado?.documento}</div>
            <div><strong>Periodo:</strong> {calc.fechaInicio || "—"} → {calc.fechaFin || "—"}</div>
          </div>
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Concepto</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', color: 'white', fontWeight: '600' }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {calc.detalle?.map((d,i)=>(
                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fffbf5' : '#fff5eb', borderBottom: '1px solid #ffe4c9' }}>
                  <td style={{ padding: '10px 12px' }}>{d.nombre}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600', color: '#52c41a' }}>${(d.valor||0).toLocaleString()}</td>
                </tr>
              ))}
              <tr style={{ backgroundColor: '#fff3e0' }}>
                <td style={{ padding: '12px', textAlign: 'right', fontWeight: '700', color: '#ff6b35' }}>TOTAL</td>
                <td style={{ padding: '12px', textAlign: 'right', fontWeight: '700', color: '#52c41a', fontSize: '14px' }}>${(calc.total||0).toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* RESULTADOS GRUPALES */}
      {resultadosGrupales.length > 0 && (
        <div style={{ border: '2px solid #0ea5e9', borderRadius: '12px', padding: '16px', backgroundColor: '#f0f9ff', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#0369a1', margin: 0 }}>
              📊 Liquidación Grupal - {resultadosGrupales.length} empleado{resultadosGrupales.length !== 1 ? 's' : ''}
            </h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              {tienePermiso("rh_liquidaciones_guardar") && (
                <button
                  onClick={() => {
                    // Validar duplicados antes de guardar
                    const duplicados = [];
                    resultadosGrupales.forEach(r => {
                      const yaExiste = (repo || []).some(liq => {
                        if (liq.empleadoId !== r.empleadoId) return false;
                        const tipoExistente = liq.tipo || liq.tipoPrestacion;
                        const tipoNuevo = r.tipo || tipo;
                        if (tipoExistente !== tipoNuevo) return false;
                        // Verificar mismo período
                        if (liq.fechaInicio === r.fechaInicio && liq.fechaFin === r.fechaFin) {
                          return true;
                        }
                        return false;
                      });
                      if (yaExiste) {
                        duplicados.push(r.empleado?.nombre || 'Empleado');
                      }
                    });

                    if (duplicados.length > 0) {
                      alert(`⚠️ No se puede guardar. Los siguientes empleados ya tienen una liquidación registrada para el mismo período y tipo:\n\n${duplicados.join('\n')}`);
                      return;
                    }

                    if (window.confirm(`¿Guardar ${resultadosGrupales.length} liquidaciones?`)) {
                      let guardados = 0;
                      const periodoSeleccionado = tipo === 'Cesantías' ? `${cesDesde} → ${cesHasta}` 
                        : tipo === 'Prima' ? `${primaDesde} → ${primaHasta}` 
                        : tipo === 'Intereses Cesantías' ? `${intCesDesde} → ${intCesHasta}` 
                        : '';
                      
                      const liquidacionesGuardadas = [];
                      
                      resultadosGrupales.forEach(r => {
                        const rec = {
                          id: uid(),
                          tipo: r.tipo || tipo,
                          empleadoId: r.empleadoId,
                          empleadoNombre: r.empleado?.nombre,
                          empleadoDoc: r.empleado?.documento,
                          empleadoSede: r.empleado?.sede,
                          fecha: toISO(new Date()),
                          fechaInicio: r.fechaInicio || null,
                          fechaFin: r.fechaFin || null,
                          periodo: `${r.fechaInicio || ""} → ${r.fechaFin || ""}`,
                          detalle: r.detalle || [],
                          total: r.total || 0,
                          empleado: r.empleado,
                          periodoAfectado: r.periodoAfectado,
                          diasTomados: r.diasTomados,
                          esGrupal: true,
                          // Campos para mostrar en impresión
                          auxTransporte: r.auxTransporte || null,
                          baseLiquidacion: r.baseLiquidacion || null,
                          tipoSalarioUsado: r.tipoSalarioUsado || null,
                        };
                        if (onGuardarLiquidacion) {
                          onGuardarLiquidacion(rec);
                          liquidacionesGuardadas.push(rec);
                          guardados++;
                        }
                      });
                      
                      alert(`✅ Se guardaron ${guardados} liquidaciones correctamente.`);
                      
                      // Preguntar si desea descargar todos los reportes en un solo documento
                      if (window.confirm(`¿Desea descargar los ${guardados} reportes de liquidación en un solo documento?`)) {
                        // Generar documento unificado con todos los reportes
                        const fechaActual = new Date().toLocaleDateString('es-CO', { 
                          year: 'numeric', month: 'long', day: 'numeric' 
                        });
                        const periodoDoc = tipo === 'Cesantías' ? `${cesDesde} → ${cesHasta}` 
                          : tipo === 'Prima' ? `${primaDesde} → ${primaHasta}` 
                          : tipo === 'Intereses Cesantías' ? `${intCesDesde} → ${intCesHasta}` 
                          : '';
                        
                        // Generar HTML de cada liquidación individual
                        const paginasHTML = liquidacionesGuardadas.map((rec, idx) => {
                          const emp = rec.empleado || {};
                          let detalle = [];
                          if (rec.detalle) {
                            if (typeof rec.detalle === 'string') {
                              try { detalle = JSON.parse(rec.detalle); } catch(e) { detalle = []; }
                            } else if (Array.isArray(rec.detalle)) {
                              detalle = rec.detalle;
                            }
                          }
                          
                          const filasDetalle = (detalle || []).filter(d => d && d.nombre).map(d => `
                            <tr style="border-bottom:1px solid #ccc;">
                              <td style="padding:6px 8px;">${d.nombre}</td>
                              <td style="padding:6px 8px;text-align:right;">$${(d.valor || 0).toLocaleString()}</td>
                            </tr>
                          `).join('') || `<tr><td style="padding:6px 8px;">${rec.tipo}</td><td style="padding:6px 8px;text-align:right;">$${(rec.total || 0).toLocaleString()}</td></tr>`;
                          
                          // Construir sección de información de salario (igual que individual)
                          let seccionSalarioGrupal = '';
                          if (rec.tipoSalarioUsado) {
                            // Prima: mostrar tipo de salario usado
                            seccionSalarioGrupal = `<strong>${rec.tipoSalarioUsado}:</strong> $${(rec.baseLiquidacion || 0).toLocaleString()}<br/>`;
                          } else if (rec.auxTransporte !== undefined && rec.auxTransporte !== null && rec.baseLiquidacion) {
                            // Cesantías/Intereses: mostrar salario base + aux + total
                            seccionSalarioGrupal = `<strong>Salario base:</strong> $${(emp.salarioBase || 0).toLocaleString()}<br/>
                                                    <strong>Auxilio de transporte:</strong> $${(rec.auxTransporte || 0).toLocaleString()}<br/>
                                                    <strong>Base de liquidación:</strong> $${(rec.baseLiquidacion || 0).toLocaleString()}<br/>`;
                          } else if (rec.baseLiquidacion) {
                            // Vacaciones u otros: solo base
                            seccionSalarioGrupal = `<strong>Base de liquidación:</strong> $${(rec.baseLiquidacion || 0).toLocaleString()}<br/>`;
                          } else {
                            // Default: solo salario base del empleado
                            seccionSalarioGrupal = `<strong>Salario base:</strong> $${(emp.salarioBase || 0).toLocaleString()}<br/>`;
                          }
                          
                          return `
                            <div class="page" style="page-break-after: always; padding: 20px; border: 1px solid #ddd; margin-bottom: 20px;">
                              <div style="text-align:right;margin-bottom:10px;">
                                <img src="${LOGO_BIGBURGUER}" style="width:80px;height:80px;object-fit:contain;" />
                              </div>
                              <h2 style="text-align:center;font-size:16px;border-bottom:2px solid #333;padding-bottom:8px;margin:0 0 15px 0;">
                                ${rec.tipo}
                              </h2>
                              <div style="font-size:12px;line-height:1.6;margin-bottom:15px;">
                                <strong>Empleado:</strong> ${emp.nombre || rec.empleadoNombre || ''}<br/>
                                <strong>Documento:</strong> ${emp.tipoDoc || 'CC'} ${emp.documento || rec.empleadoDoc || ''}<br/>
                                <strong>Cargo:</strong> ${emp.cargo || '—'}<br/>
                                <strong>Fecha ingreso:</strong> ${emp.fechaIngreso || '—'}<br/>
                                ${seccionSalarioGrupal}
                                <strong>Período:</strong> ${rec.fechaInicio || ''} → ${rec.fechaFin || ''}
                              </div>
                              <table style="width:100%;border-collapse:collapse;font-size:12px;">
                                <thead>
                                  <tr style="background:#f0f0f0;">
                                    <th style="border:1px solid #ccc;padding:6px;text-align:left;">Concepto</th>
                                    <th style="border:1px solid #ccc;padding:6px;text-align:right;">Valor</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  ${filasDetalle}
                                  <tr style="background:#f0f0f0;font-weight:bold;">
                                    <td style="border:1px solid #ccc;padding:8px;text-align:right;">TOTAL A PAGAR</td>
                                    <td style="border:1px solid #ccc;padding:8px;text-align:right;">$${(rec.total || 0).toLocaleString()}</td>
                                  </tr>
                                </tbody>
                              </table>
                              <div style="display:flex;justify-content:space-between;margin-top:40px;">
                                <div style="width:45%;text-align:center;">
                                  <div style="font-size:11px;">RECIBO A CONFORMIDAD</div>
                                  <div style="margin-top:35px;border-top:1px solid #000;padding-top:4px;font-size:11px;">
                                    ${emp.nombre || ''}<br/>${emp.tipoDoc || 'CC'} ${emp.documento || ''}
                                  </div>
                                </div>
                                <div style="width:45%;text-align:center;">
                                  <div style="font-size:11px;">FIRMA AUTORIZADA</div>
                                  <div style="margin-top:35px;border-top:1px solid #000;padding-top:4px;font-size:11px;">
                                    FIRMA AUTORIZADA
                                  </div>
                                </div>
                              </div>
                            </div>
                          `;
                        }).join('');
                        
                        const totalGeneral = liquidacionesGuardadas.reduce((sum, r) => sum + (r.total || 0), 0);
                        
                        const htmlUnificado = `
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <title>Liquidaciones Grupales - ${tipo}</title>
                            <style>
                              body { font-family: Arial, sans-serif; padding: 20px; }
                              .cover { text-align: center; padding: 50px 20px; page-break-after: always; }
                              .cover h1 { color: #0369a1; font-size: 28px; margin-bottom: 20px; }
                              .cover .info { font-size: 14px; color: #666; margin: 10px 0; }
                              .cover .total { font-size: 24px; color: #16a34a; font-weight: bold; margin-top: 30px; }
                              .page { background: white; }
                              @media print {
                                .page { page-break-after: always; }
                                .page:last-child { page-break-after: avoid; }
                              }
                            </style>
                          </head>
                          <body>
                            <div class="cover">
                              <img src="${LOGO_BIGBURGUER}" style="width:120px;height:120px;object-fit:contain;margin-bottom:20px;" />
                              <h1>LIQUIDACIONES GRUPALES</h1>
                              <div class="info"><strong>Tipo:</strong> ${tipo}</div>
                              <div class="info"><strong>Período:</strong> ${periodoDoc}</div>
                              <div class="info"><strong>Fecha de generación:</strong> ${fechaActual}</div>
                              <div class="info"><strong>Cantidad de empleados:</strong> ${liquidacionesGuardadas.length}</div>
                              <div class="total">Total General: $${totalGeneral.toLocaleString('es-CO')}</div>
                            </div>
                            ${paginasHTML}
                          </body>
                          </html>
                        `;
                        
                        // Guardar el informe grupal en Supabase
                        const empleadosData = liquidacionesGuardadas.map(r => ({
                          empleadoId: r.empleadoId,
                          empleadoNombre: r.empleado?.nombre || r.empleadoNombre,
                          empleadoDoc: r.empleado?.documento || r.empleadoDoc,
                          tipo: r.tipo,
                          total: r.total,
                          fechaInicio: r.fechaInicio,
                          fechaFin: r.fechaFin,
                        }));
                        
                        const periodoDesdeGuardar = tipo === 'Cesantías' ? cesDesde 
                          : tipo === 'Prima' ? primaDesde 
                          : tipo === 'Intereses Cesantías' ? intCesDesde 
                          : null;
                        const periodoHastaGuardar = tipo === 'Cesantías' ? cesHasta 
                          : tipo === 'Prima' ? primaHasta 
                          : tipo === 'Intereses Cesantías' ? intCesHasta 
                          : null;
                        
                        // Generar el HTML del informe ejecutivo grupal para guardarlo
                        const htmlResumenEjecutivo = `
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <title>Informe Ejecutivo - Liquidación Grupal</title>
                            <style>
                              body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                              .header { text-align: center; border-bottom: 3px solid #0369a1; padding-bottom: 20px; margin-bottom: 30px; }
                              .header h1 { color: #0369a1; margin: 0; font-size: 24px; }
                              .header p { color: #666; margin: 10px 0 0; }
                              .summary-box { background: linear-gradient(135deg, #f0f9ff, #e0f2fe); padding: 20px; border-radius: 12px; margin-bottom: 25px; border: 2px solid #0ea5e9; }
                              .summary-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed #bae6fd; }
                              .summary-row:last-child { border-bottom: none; }
                              .summary-label { font-weight: 600; color: #334155; }
                              .summary-value { font-weight: 700; color: #0369a1; }
                              .total-box { background: linear-gradient(135deg, #16a34a, #15803d); color: white; padding: 20px; border-radius: 12px; text-align: center; margin: 25px 0; }
                              .total-box h2 { margin: 0 0 10px; font-size: 18px; }
                              .total-box .amount { font-size: 32px; font-weight: 700; }
                              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                              th { background: #0369a1; color: white; padding: 12px; text-align: left; }
                              td { padding: 10px 12px; border-bottom: 1px solid #e0e0e0; }
                              tr:nth-child(even) { background: #f8fafc; }
                              .tipo-badge { display: inline-block; padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 600; background: #dbeafe; color: #1d4ed8; }
                              .amount-cell { text-align: right; font-weight: 600; color: #16a34a; }
                              .footer { margin-top: 40px; text-align: center; color: #888; font-size: 12px; border-top: 1px solid #e0e0e0; padding-top: 20px; }
                              @media print { body { padding: 20px; } }
                            </style>
                          </head>
                          <body>
                            <div class="header">
                              <h1>📊 INFORME EJECUTIVO DE LIQUIDACIÓN GRUPAL</h1>
                              <p>Generado el ${fechaActual}</p>
                            </div>

                            <div class="summary-box">
                              <div class="summary-row">
                                <span class="summary-label">📅 Tipo de Liquidación:</span>
                                <span class="summary-value">${tipo}</span>
                              </div>
                              <div class="summary-row">
                                <span class="summary-label">👥 Total de Empleados Liquidados:</span>
                                <span class="summary-value">${liquidacionesGuardadas.length}</span>
                              </div>
                              <div class="summary-row">
                                <span class="summary-label">📆 Período:</span>
                                <span class="summary-value">${periodoDesdeGuardar || ''} → ${periodoHastaGuardar || ''}</span>
                              </div>
                            </div>

                            <div class="total-box">
                              <h2>💰 TOTAL GENERAL A PAGAR</h2>
                              <div class="amount">$${totalGeneral.toLocaleString('es-CO')}</div>
                            </div>

                            <h3 style="color: #0369a1; margin-top: 30px;">📋 Detalle por Empleado</h3>
                            <table>
                              <thead>
                                <tr>
                                  <th style="width: 40px">#</th>
                                  <th>Empleado</th>
                                  <th>Documento</th>
                                  <th>Concepto</th>
                                  <th style="text-align: right">Valor</th>
                                </tr>
                              </thead>
                              <tbody>
                                ${liquidacionesGuardadas.map((r, idx) => `
                                  <tr>
                                    <td>${idx + 1}</td>
                                    <td><strong>${r.empleado?.nombre || r.empleadoNombre || ''}</strong></td>
                                    <td>${r.empleado?.documento || r.empleadoDoc || ''}</td>
                                    <td><span class="tipo-badge">${(r.tipo || '').replace('Liquidación de ', '')}</span></td>
                                    <td class="amount-cell">$${(r.total || 0).toLocaleString('es-CO')}</td>
                                  </tr>
                                `).join('')}
                                <tr style="background: #f0f9ff; font-weight: 700;">
                                  <td colspan="4" style="text-align: right; color: #0369a1;">TOTAL:</td>
                                  <td class="amount-cell" style="font-size: 16px;">$${totalGeneral.toLocaleString('es-CO')}</td>
                                </tr>
                              </tbody>
                            </table>

                            <div class="footer">
                              <p>Este informe fue generado automáticamente por el sistema de gestión de nómina.</p>
                              <p>BigBurguer - Sistema de Recursos Humanos</p>
                            </div>
                          </body>
                          </html>
                        `;
                        
                        // Guardar informe en segundo plano (no bloqueante)
                        guardarInformeGrupal(
                          tipo,
                          periodoDesdeGuardar,
                          periodoHastaGuardar,
                          empleadosData,
                          totalGeneral,
                          htmlUnificado,
                          htmlResumenEjecutivo // Ahora guardamos el informe ejecutivo
                        ).then(result => {
                          if (result) {
                            console.log("✅ Informe grupal guardado:", result.id);
                          }
                        });
                        
                        const printWindow = window.open('', '_blank');
                        printWindow.document.write(htmlUnificado);
                        printWindow.document.close();
                        printWindow.focus();
                        setTimeout(() => printWindow.print(), 500);
                      }
                      
                      setResultadosGrupales([]);
                      setEmpleadosSeleccionados([]);
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontWeight: '600',
                    border: 'none',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                    color: 'white',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)',
                  }}
                >
                  💾 Guardar Todas ({resultadosGrupales.length})
                </button>
              )}
              <button
                onClick={() => {
                  setResultadosGrupales([]);
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '12px',
                  fontWeight: '600',
                  border: '1px solid #dc2626',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#dc2626',
                  cursor: 'pointer',
                }}
              >
                ✕ Limpiar
              </button>
            </div>
          </div>
          
          <div style={{ 
            fontSize: '13px', 
            fontWeight: '600', 
            color: '#0369a1', 
            marginBottom: '12px',
            padding: '8px 12px',
            backgroundColor: '#dbeafe',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <span>💰 Total General:</span>
            <span style={{ color: '#16a34a', fontSize: '15px' }}>
              ${resultadosGrupales.reduce((sum, r) => sum + (r.total || 0), 0).toLocaleString()}
            </span>
          </div>

          <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #bae6fd' }}>
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', backgroundColor: 'white', tableLayout: 'fixed' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' }}>
                  <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600', width: '40px' }}>#</th>
                  <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600', width: '25%' }}>Empleado</th>
                  <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600', width: '18%' }}>Documento</th>
                  <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600', width: '15%' }}>Tipo</th>
                  <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600', width: '22%' }}>Periodo</th>
                  <th style={{ padding: '10px 8px', textAlign: 'right', color: 'white', fontWeight: '600', width: '15%' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {resultadosGrupales.map((r, idx) => (
                  <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#f0f9ff' : 'white', borderBottom: '1px solid #e0f2fe' }}>
                    <td style={{ padding: '8px', textAlign: 'center', fontWeight: '500', color: '#64748b' }}>{idx + 1}</td>
                    <td style={{ padding: '8px', fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.empleado?.nombre}</td>
                    <td style={{ padding: '8px', color: '#64748b', fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.empleado?.documento}</td>
                    <td style={{ padding: '8px' }}>
                      <span style={{ 
                        padding: '3px 6px', 
                        borderRadius: '4px', 
                        fontSize: '10px', 
                        fontWeight: '600', 
                        backgroundColor: '#dbeafe', 
                        color: '#1d4ed8',
                        display: 'inline-block',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {(r.tipo || '').replace('Liquidación de ', '')}
                      </span>
                    </td>
                    <td style={{ padding: '8px', fontSize: '10px', color: '#64748b', whiteSpace: 'nowrap' }}>{r.fechaInicio} → {r.fechaFin}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: '700', color: '#16a34a', fontSize: '12px' }}>
                      ${(r.total || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* INFORME EJECUTIVO DE LIQUIDACIÓN GRUPAL */}
          <div style={{ 
            marginTop: '20px', 
            padding: '20px', 
            backgroundColor: 'white', 
            borderRadius: '12px', 
            border: '2px solid #0ea5e9',
            boxShadow: '0 4px 12px rgba(14, 165, 233, 0.15)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#0369a1' }}>
                📋 INFORME EJECUTIVO DE LIQUIDACIÓN
              </h4>
              <button
                onClick={() => {
                  // Imprimir informe ejecutivo
                  const totalGeneral = resultadosGrupales.reduce((sum, r) => sum + (r.total || 0), 0);
                  const tiposAgrupados = {};
                  resultadosGrupales.forEach(r => {
                    const tipoKey = (r.tipo || 'Otro').replace('Liquidación de ', '');
                    if (!tiposAgrupados[tipoKey]) {
                      tiposAgrupados[tipoKey] = { count: 0, total: 0 };
                    }
                    tiposAgrupados[tipoKey].count++;
                    tiposAgrupados[tipoKey].total += (r.total || 0);
                  });
                  
                  const fechaActual = new Date().toLocaleDateString('es-CO', { 
                    year: 'numeric', month: 'long', day: 'numeric' 
                  });

                  const htmlContent = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                      <title>Informe Ejecutivo - Liquidación Grupal</title>
                      <style>
                        body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
                        .header { text-align: center; border-bottom: 3px solid #0369a1; padding-bottom: 20px; margin-bottom: 30px; }
                        .header h1 { color: #0369a1; margin: 0; font-size: 24px; }
                        .header p { color: #666; margin: 10px 0 0; }
                        .summary-box { background: linear-gradient(135deg, #f0f9ff, #e0f2fe); padding: 20px; border-radius: 12px; margin-bottom: 25px; border: 2px solid #0ea5e9; }
                        .summary-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed #bae6fd; }
                        .summary-row:last-child { border-bottom: none; }
                        .summary-label { font-weight: 600; color: #334155; }
                        .summary-value { font-weight: 700; color: #0369a1; }
                        .total-box { background: linear-gradient(135deg, #16a34a, #15803d); color: white; padding: 20px; border-radius: 12px; text-align: center; margin: 25px 0; }
                        .total-box h2 { margin: 0 0 10px; font-size: 18px; }
                        .total-box .amount { font-size: 32px; font-weight: 700; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th { background: #0369a1; color: white; padding: 12px; text-align: left; }
                        td { padding: 10px 12px; border-bottom: 1px solid #e0e0e0; }
                        tr:nth-child(even) { background: #f8fafc; }
                        .tipo-badge { display: inline-block; padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 600; background: #dbeafe; color: #1d4ed8; }
                        .amount-cell { text-align: right; font-weight: 600; color: #16a34a; }
                        .footer { margin-top: 40px; text-align: center; color: #888; font-size: 12px; border-top: 1px solid #e0e0e0; padding-top: 20px; }
                        @media print { body { padding: 20px; } }
                      </style>
                    </head>
                    <body>
                      <div class="header">
                        <h1>📊 INFORME EJECUTIVO DE LIQUIDACIÓN GRUPAL</h1>
                        <p>Generado el ${fechaActual}</p>
                      </div>

                      <div class="summary-box">
                        <div class="summary-row">
                          <span class="summary-label">📅 Tipo de Liquidación:</span>
                          <span class="summary-value">${tipo}</span>
                        </div>
                        <div class="summary-row">
                          <span class="summary-label">👥 Total de Empleados Liquidados:</span>
                          <span class="summary-value">${resultadosGrupales.length}</span>
                        </div>
                        <div class="summary-row">
                          <span class="summary-label">📆 Período:</span>
                          <span class="summary-value">${tipo === 'Cesantías' ? cesDesde : tipo === 'Prima' ? primaDesde : tipo === 'Intereses Cesantías' ? intCesDesde : ''} → ${tipo === 'Cesantías' ? cesHasta : tipo === 'Prima' ? primaHasta : tipo === 'Intereses Cesantías' ? intCesHasta : ''}</span>
                        </div>
                      </div>

                      <div class="total-box">
                        <h2>💰 TOTAL GENERAL A PAGAR</h2>
                        <div class="amount">$${totalGeneral.toLocaleString('es-CO')}</div>
                      </div>

                      <h3 style="color: #0369a1; margin-top: 30px;">📋 Detalle por Empleado</h3>
                      <table>
                        <thead>
                          <tr>
                            <th style="width: 40px">#</th>
                            <th>Empleado</th>
                            <th>Documento</th>
                            <th>Concepto</th>
                            <th style="text-align: right">Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${resultadosGrupales.map((r, idx) => `
                            <tr>
                              <td>${idx + 1}</td>
                              <td><strong>${r.empleado?.nombre || ''}</strong></td>
                              <td>${r.empleado?.documento || ''}</td>
                              <td><span class="tipo-badge">${(r.tipo || '').replace('Liquidación de ', '')}</span></td>
                              <td class="amount-cell">$${(r.total || 0).toLocaleString('es-CO')}</td>
                            </tr>
                          `).join('')}
                          <tr style="background: #f0f9ff; font-weight: 700;">
                            <td colspan="4" style="text-align: right; color: #0369a1;">TOTAL:</td>
                            <td class="amount-cell" style="font-size: 16px;">$${totalGeneral.toLocaleString('es-CO')}</td>
                          </tr>
                        </tbody>
                      </table>

                      <div class="footer">
                        <p>Este informe fue generado automáticamente por el sistema de gestión de nómina.</p>
                        <p>BigBurguer - Sistema de Recursos Humanos</p>
                      </div>
                    </body>
                    </html>
                  `;
                  
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(htmlContent);
                  printWindow.document.close();
                  printWindow.focus();
                  setTimeout(() => printWindow.print(), 500);
                }}
                style={{
                  padding: '8px 16px',
                  fontSize: '12px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
                }}
              >
                🖨️ Imprimir Informe
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f0f9ff', 
                borderRadius: '10px', 
                textAlign: 'center',
                border: '1px solid #bae6fd'
              }}>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>👥 Empleados</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#0369a1' }}>{resultadosGrupales.length}</div>
              </div>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f0fdf4', 
                borderRadius: '10px', 
                textAlign: 'center',
                border: '1px solid #bbf7d0'
              }}>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>💰 Total a Pagar</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#16a34a' }}>
                  ${resultadosGrupales.reduce((sum, r) => sum + (r.total || 0), 0).toLocaleString()}
                </div>
              </div>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#fffbeb', 
                borderRadius: '10px', 
                textAlign: 'center',
                border: '1px solid #fde68a'
              }}>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>📊 Promedio</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#d97706' }}>
                  ${resultadosGrupales.length > 0 
                    ? Math.round(resultadosGrupales.reduce((sum, r) => sum + (r.total || 0), 0) / resultadosGrupales.length).toLocaleString() 
                    : 0}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', fontSize: '12px', color: '#64748b' }}>
              <strong>📅 Período liquidado:</strong> {tipo === 'Cesantías' ? cesDesde : tipo === 'Prima' ? primaDesde : tipo === 'Intereses Cesantías' ? intCesDesde : '—'} → {tipo === 'Cesantías' ? cesHasta : tipo === 'Prima' ? primaHasta : tipo === 'Intereses Cesantías' ? intCesHasta : '—'}
              <span style={{ marginLeft: '20px' }}><strong>🎯 Tipo:</strong> {tipo}</span>
            </div>
          </div>
        </div>
      )}
        </>
      )}

      {/* ============ CONTENIDO DE ARCHIVOS HISTÓRICOS ============ */}
      {vistaActiva === "historico" && (
        <>
      {/* ============ HISTORIAL DE INFORMES GRUPALES ============ */}
      <div style={{ 
        marginBottom: '20px',
        padding: '16px', 
        backgroundColor: 'white', 
        borderRadius: '12px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid #c4b5fd'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '8px 0',
          borderBottom: '2px solid #c4b5fd',
          marginBottom: '16px'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#7c3aed', margin: 0 }}>
            📊 Informes de Liquidaciones Grupales ({informesGrupales.length})
          </h3>
          <button
            onClick={cargarInformesGrupales}
            style={{
              padding: '6px 12px',
              fontSize: '11px',
              border: '1px solid #7c3aed',
              borderRadius: '6px',
              background: 'white',
              color: '#7c3aed',
              cursor: 'pointer'
            }}
          >
            🔄 Actualizar
          </button>
        </div>
        
        <div>
            {cargandoInformes ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
                Cargando informes...
              </div>
            ) : informesGrupales.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#64748b', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                No hay informes grupales guardados. Los informes se guardan automáticamente cuando guardas una liquidación grupal.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
                      <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Fecha</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Tipo</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600' }}>Período</th>
                      <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>Empleados</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right', color: 'white', fontWeight: '600' }}>Total</th>
                      <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {informesGrupales.map((inf, idx) => (
                      <tr key={inf.id} style={{ backgroundColor: idx % 2 === 0 ? '#faf5ff' : 'white', borderBottom: '1px solid #e9d5ff' }}>
                        <td style={{ padding: '10px 8px', color: '#64748b' }}>
                          {new Date(inf.fecha).toLocaleDateString('es-CO', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <span style={{ 
                            padding: '3px 8px', 
                            borderRadius: '4px', 
                            fontSize: '11px', 
                            fontWeight: '600', 
                            backgroundColor: '#ddd6fe', 
                            color: '#6d28d9' 
                          }}>
                            {inf.tipo}
                          </span>
                        </td>
                        <td style={{ padding: '10px 8px', fontSize: '11px', color: '#64748b' }}>
                          {inf.periodo_desde} → {inf.periodo_hasta}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: '600', color: '#0369a1' }}>
                          {inf.cantidad_empleados}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '700', color: '#16a34a' }}>
                          ${(inf.total_general || 0).toLocaleString()}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <button
                              onClick={() => {
                                // Abrir informe ejecutivo resumen
                                if (inf.html_resumen) {
                                  const printWindow = window.open('', '_blank');
                                  printWindow.document.write(inf.html_resumen);
                                  printWindow.document.close();
                                  printWindow.focus();
                                } else {
                                  alert("Este informe no tiene resumen ejecutivo guardado. Solo los informes nuevos incluyen el resumen ejecutivo.");
                                }
                              }}
                              style={{
                                padding: '4px 8px',
                                fontSize: '11px',
                                border: 'none',
                                borderRadius: '4px',
                                background: '#16a34a',
                                color: 'white',
                                cursor: 'pointer'
                              }}
                              title="Ver informe ejecutivo resumen"
                            >
                              📊
                            </button>
                            <button
                              onClick={() => {
                                // Abrir documento unificado
                                if (inf.html_unificado) {
                                  const printWindow = window.open('', '_blank');
                                  printWindow.document.write(inf.html_unificado);
                                  printWindow.document.close();
                                  printWindow.focus();
                                } else {
                                  alert("Este informe no tiene documento unificado guardado");
                                }
                              }}
                              style={{
                                padding: '4px 8px',
                                fontSize: '11px',
                                border: 'none',
                                borderRadius: '4px',
                                background: '#8b5cf6',
                                color: 'white',
                                cursor: 'pointer'
                              }}
                              title="Ver documento unificado (comprobantes)"
                            >
                              📄
                            </button>
                            <button
                              onClick={() => {
                                // Mostrar detalle de empleados
                                const detalle = inf.detalle_empleados || [];
                                if (detalle.length === 0) {
                                  alert("No hay detalle de empleados");
                                  return;
                                }
                                const htmlDetalle = `
                                  <!DOCTYPE html>
                                  <html>
                                  <head>
                                    <title>Detalle - ${inf.tipo}</title>
                                    <style>
                                      body { font-family: Arial, sans-serif; padding: 20px; }
                                      table { width: 100%; border-collapse: collapse; }
                                      th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                                      th { background: #7c3aed; color: white; }
                                      tr:nth-child(even) { background: #f8f4ff; }
                                      .total { font-weight: bold; background: #f0fdf4 !important; }
                                    </style>
                                  </head>
                                  <body>
                                    <h1 style="color:#7c3aed;">Detalle de Informe Grupal</h1>
                                    <p><strong>Tipo:</strong> ${inf.tipo}</p>
                                    <p><strong>Período:</strong> ${inf.periodo_desde} → ${inf.periodo_hasta}</p>
                                    <p><strong>Fecha generación:</strong> ${new Date(inf.fecha).toLocaleDateString('es-CO')}</p>
                                    <table>
                                      <thead>
                                        <tr>
                                          <th>#</th>
                                          <th>Empleado</th>
                                          <th>Documento</th>
                                          <th style="text-align:right">Total</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        ${detalle.map((d, i) => `
                                          <tr>
                                            <td>${i + 1}</td>
                                            <td>${d.empleadoNombre || ''}</td>
                                            <td>${d.empleadoDoc || ''}</td>
                                            <td style="text-align:right">$${(d.total || 0).toLocaleString()}</td>
                                          </tr>
                                        `).join('')}
                                        <tr class="total">
                                          <td colspan="3" style="text-align:right">TOTAL:</td>
                                          <td style="text-align:right">$${(inf.total_general || 0).toLocaleString()}</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </body>
                                  </html>
                                `;
                                const win = window.open('', '_blank');
                                win.document.write(htmlDetalle);
                                win.document.close();
                              }}
                              style={{
                                padding: '4px 8px',
                                fontSize: '11px',
                                border: 'none',
                                borderRadius: '4px',
                                background: '#0ea5e9',
                                color: 'white',
                                cursor: 'pointer'
                              }}
                              title="Ver detalle de empleados"
                            >
                              👥
                            </button>
                            <button
                              onClick={() => eliminarInformeGrupal(inf.id)}
                              style={{
                                padding: '4px 8px',
                                fontSize: '11px',
                                border: 'none',
                                borderRadius: '4px',
                                background: '#ef4444',
                                color: 'white',
                                cursor: 'pointer'
                              }}
                              title="Eliminar informe"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      </div>
 
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '20px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#ff6b35', margin: 0 }}>📚 Histórico de Liquidaciones Individuales</h3>
        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#ff6b35', marginBottom: '4px' }}>� Empleado</label>
          <select
            value={filtroEmpleado}
            onChange={(e) => setFiltroEmpleado(e.target.value)}
            style={{
              padding: '8px 12px',
              fontSize: '12px',
              border: '2px solid #ffc53d',
              borderRadius: '8px',
              backgroundColor: '#fffbf5',
              cursor: 'pointer',
              minWidth: '180px',
            }}
          >
            <option value="">-- Todos --</option>
            {[...(empleados || [])].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map(e => (
              <option key={e.id} value={e.id}>{e.nombre}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#ff6b35', marginBottom: '4px' }}>🎯 Tipo</label>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            style={{
              padding: '8px 12px',
              fontSize: '12px',
              border: '2px solid #ffc53d',
              borderRadius: '8px',
              backgroundColor: '#fffbf5',
              cursor: 'pointer',
              minWidth: '150px',
            }}
          >
            <option value="">-- Todos --</option>
            <option value="Vacaciones">Vacaciones</option>
            <option value="Cesantías">Cesantías</option>
            <option value="Prima">Prima</option>
            <option value="Intereses Cesantías">Intereses Cesantías</option>
            <option value="Liquidación CONTRATO">Liquidación CONTRATO</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#ff6b35', marginBottom: '4px' }}>🏢 Sede</label>
          <select
            value={filtroSede}
            onChange={(e) => setFiltroSede(e.target.value)}
            style={{
              padding: '8px 12px',
              fontSize: '12px',
              border: '2px solid #ffc53d',
              borderRadius: '8px',
              backgroundColor: '#fffbf5',
              cursor: 'pointer',
              minWidth: '150px',
            }}
          >
            <option value="">-- Todas --</option>
            {sedes.map(s => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#ff6b35', marginBottom: '4px' }}>📅 Desde</label>
          <input
            type="date"
            value={filtroFechaDesde}
            onChange={(e) => setFiltroFechaDesde(e.target.value)}
            style={{
              padding: '8px 12px',
              fontSize: '12px',
              border: '2px solid #ffc53d',
              borderRadius: '8px',
              backgroundColor: '#fffbf5',
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#ff6b35', marginBottom: '4px' }}>📅 Hasta</label>
          <input
            type="date"
            value={filtroFechaHasta}
            onChange={(e) => setFiltroFechaHasta(e.target.value)}
            style={{
              padding: '8px 12px',
              fontSize: '12px',
              border: '2px solid #ffc53d',
              borderRadius: '8px',
              backgroundColor: '#fffbf5',
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button
            onClick={() => {
              setFiltroEmpleado("");
              setFiltroTipo("");
              setFiltroSede("");
              setFiltroFechaDesde("");
              setFiltroFechaHasta("");
            }}
            style={{
              padding: '8px 12px',
              fontSize: '11px',
              fontWeight: '600',
              border: '1px solid #ff6b35',
              borderRadius: '8px',
              backgroundColor: 'white',
              color: '#ff6b35',
              cursor: 'pointer',
            }}
          >
            🔄 Limpiar
          </button>
        </div>

        <span style={{ fontSize: '12px', color: '#888', backgroundColor: '#fff3e0', padding: '4px 10px', borderRadius: '12px', alignSelf: 'flex-end' }}>
          📊 {repoFiltrado?.length || 0} registro{repoFiltrado?.length !== 1 ? "s" : ""}
        </span>
      </div>
      
      <div style={{ overflowX: 'auto', border: '2px solid #ffc53d', borderRadius: '12px' }}>
        <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' }}>
              <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600', width: '18%' }}>Empleado</th>
              <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600', width: '18%' }}>Tipo</th>
              <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600', width: '10%' }}>Fecha</th>
              <th style={{ padding: '10px 8px', textAlign: 'left', color: 'white', fontWeight: '600', width: '14%' }}>Período</th>
              <th style={{ padding: '10px 8px', textAlign: 'right', color: 'white', fontWeight: '600', width: '12%' }}>Total</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: 'white', fontWeight: '600', width: '28%' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {repoFiltrado?.length ? repoFiltrado.map((r, idx)=>(
              <tr key={r.id} style={{ backgroundColor: idx % 2 === 0 ? '#fffbf5' : '#fff5eb', borderBottom: '1px solid #ffe4c9' }}>
                <td style={{ padding: '10px 8px', color: '#333', fontSize: '12px' }}>
                  <div style={{ fontWeight: '600' }}>{r.empleado?.nombre || r.empleadoNombre}</div>
                  <div style={{ fontSize: '11px', color: '#666' }}>({r.empleado?.documento || r.empleadoDoc})</div>
                </td>
                <td style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                  <span style={{ 
                    display: 'inline-block',
                    padding: '5px 8px', 
                    borderRadius: '6px', 
                    fontSize: '11px', 
                    fontWeight: '600', 
                    backgroundColor: '#fff3e0', 
                    color: '#ff6b35',
                    lineHeight: '1.3',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal'
                  }}>
                    {r.tipo}
                  </span>
                </td>
                <td style={{ padding: '10px 8px', color: '#666', fontSize: '12px' }}>{r.fecha}</td>
                <td style={{ padding: '10px 8px', color: '#666', fontSize: '11px' }}>{r.periodo}</td>
                <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '700', color: '#52c41a', fontSize: '13px' }}>${(r.total||0).toLocaleString()}</td>
                <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'nowrap' }}>
                    <button
                      onClick={() => imprimirIndividual(r)}
                      style={{
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        border: 'none',
                        borderRadius: '5px',
                        background: 'linear-gradient(135deg, #1890ff 0%, #52c41a 100%)',
                        color: 'white',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      🖨️ Imprimir
                    </button>
                    <button
                      onClick={async () => {
                        const confirmar = window.confirm(
                          `⚠️ ¿Estás seguro de eliminar esta liquidación?\n\n` +
                          `Empleado: ${r.empleado?.nombre || r.empleadoNombre}\n` +
                          `Tipo: ${r.tipo}\n` +
                          `Total: $${(r.total||0).toLocaleString()}\n\n` +
                          `Esta acción no se puede deshacer.`
                        );
                        if (!confirmar) return;
                        
                        const exito = await eliminarLiquidacionDeSupabase(r.id);
                        if (exito) {
                          if (onEliminarLiquidacion) {
                            onEliminarLiquidacion(r.id);
                          }
                          alert("✅ Liquidación eliminada correctamente.");
                        } else {
                          alert("❌ Error al eliminar la liquidación. Intenta nuevamente.");
                        }
                      }}
                      style={{
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        border: 'none',
                        borderRadius: '5px',
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: 'white',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      🗑️ Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: '#999' }}>
                  💭 Sin registros{filtroEmpleado ? " para este empleado" : ""}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
        </>
      )}
    </div>
  );
}
 
/* ====================== APP ====================== */
 
// Constantes necesarias - Se actualizan dinámicamente desde Valores Anuales
const SMLV_2025 = 1750905; // SMLV 2025-2026 actualizado
const AUX_TRANSP_2025 = 200000; // Aux transporte actualizado
const VAC_DIAS_ANO = 15;
const PRIMA_DIAS_SEM = 15;
const INTERES_CES = 0.12;
 
function diasEntre(fechaIni, fechaFin) {
  if (!fechaIni || !fechaFin) return 0;
  const ini = new Date(fechaIni);
  const fin = new Date(fechaFin);
  return Math.max(0, Math.ceil((fin - ini) / (1000 * 60 * 60 * 24)));
}

/* ====================== CALENDARIO DE EVENTOS ====================== */

function CalendarioEventos({ empleados = [], sedes = [], usuarioActual = null, empleadoActual = null, filtroEmpresa = null, tienePermiso = () => true }) {
  const [eventos, setEventos] = useState([]);
  const [mesActual, setMesActual] = useState(new Date());
  const [modoVista, setModoVista] = useState("calendario"); // "calendario" | "lista" | "crear"
  const [eventoSeleccionado, setEventoSeleccionado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [notificaciones, setNotificaciones] = useState([]);
  const [mostrarNotificaciones, setMostrarNotificaciones] = useState(false);
  
  // Estado para edición de invitados en eventos de vencimiento (solo Gerencia)
  const [editandoInvitados, setEditandoInvitados] = useState(false);
  const [invitadosTemp, setInvitadosTemp] = useState([]);
  
  // Verificar si el usuario tiene perfil Gerencia
  const esGerencia = useMemo(() => {
    if (!usuarioActual) return false;
    const perfil = (usuarioActual.perfil || usuarioActual.rol || "").toLowerCase();
    return perfil.includes("gerencia") || perfil.includes("gerente") || perfil.includes("admin");
  }, [usuarioActual]);
  
  // Formulario nuevo evento
  const [nuevoEvento, setNuevoEvento] = useState({
    titulo: "",
    tipo: "reunion",
    fecha: new Date().toISOString().slice(0, 10),
    hora_inicio: "09:00",
    hora_fin: "10:00",
    sede_id: "",
    sede_nombre: "",
    descripcion: "",
    observaciones: "",
    invitados: []
  });

  // Cargar eventos
  useEffect(() => {
    async function cargar() {
      setCargando(true);
      const data = await cargarEventosCalendarioDeSupabase(filtroEmpresa);
      console.log("📅 Eventos cargados del calendario:", data.length, "eventos");
      console.log("📅 Tipos de eventos:", [...new Set(data.map(e => e.tipo))]);
      console.log("📅 Eventos de plan_trabajo:", data.filter(e => e.tipo === "plan_trabajo").length);
      console.log("📅 Eventos de vencimiento_proceso:", data.filter(e => e.tipo === "vencimiento_proceso").length);
      setEventos(data);
      setCargando(false);
    }
    cargar();
  }, [filtroEmpresa]);
  
  // Filtrar eventos según el perfil del usuario
  // - Cada empleado solo ve los eventos donde está invitado o es el creador
  // - Eventos de tipo "vencimiento_proceso" solo se muestran a los empleados seleccionados (invitados)
  // - Eventos de tipo "plan_trabajo" se muestran al empleado asignado
  const eventosFiltrados = useMemo(() => {
    // Usar el ID del empleado actual (de la tabla empleados), no el ID del usuario autenticado
    const empleadoId = empleadoActual?.id;
    const usuarioId = usuarioActual?.id;
    
    console.log("📅 Filtrando eventos - esGerencia:", esGerencia, "empleadoId:", empleadoId, "usuarioId:", usuarioId);
    console.log("📅 Total eventos antes de filtrar:", eventos.length);
    
    // DEBUG: Mostrar todos los eventos con sus creadores e invitados
    eventos.forEach((ev, i) => {
      console.log(`📅 Evento ${i+1}: "${ev.titulo?.substring(0, 25)}" tipo=${ev.tipo} creador_id=${ev.creador_id} invitados=${ev.invitados?.length || 0}`);
    });
    
    // Función auxiliar para verificar si el usuario está invitado a un evento
    const estaInvitadoAlEvento = (evento) => {
      if (!evento.invitados || !Array.isArray(evento.invitados) || evento.invitados.length === 0) {
        return false;
      }
      return evento.invitados.some(inv => 
        (empleadoId && String(inv.id) === String(empleadoId)) || 
        (usuarioId && String(inv.id) === String(usuarioId))
      );
    };
    
    // Función auxiliar para verificar si el usuario es el creador del evento
    const esCreadorDelEvento = (evento) => {
      if (!evento.creador_id) return false;
      const creadorIdStr = String(evento.creador_id);
      // Verificar contra empleadoId Y usuarioId (ambos pueden ser el creador)
      const esCreador = (empleadoId && creadorIdStr === String(empleadoId)) || 
                        (usuarioId && creadorIdStr === String(usuarioId));
      
      // DEBUG: Log para diagnóstico
      if (!esCreador && (empleadoId || usuarioId)) {
        console.log(`📅 esCreadorDelEvento: creador_id=${creadorIdStr}, empleadoId=${empleadoId}, usuarioId=${usuarioId} => ${esCreador}`);
      }
      
      return esCreador;
    };
    
    const resultado = eventos.filter(evento => {
      // DEBUG: Mostrar info del evento para identificar por qué se filtra
      const debugInfo = {
        id: evento.id,
        titulo: evento.titulo?.substring(0, 20),
        tipo: evento.tipo,
        creador_id: evento.creador_id,
        invitados: evento.invitados?.length || 0,
        esInvitado: false,
        esCreador: false
      };
      debugInfo.esInvitado = estaInvitadoAlEvento(evento);
      debugInfo.esCreador = esCreadorDelEvento(evento);
      
      // Si es evento de vencimiento de proceso
      if (evento.tipo === "vencimiento_proceso" || evento.es_vencimiento) {
        // Si está marcado como solo_gerencia y el usuario no es gerencia, no mostrar
        if (evento.solo_gerencia && !esGerencia) {
          // Pero si el usuario está en la lista de invitados, sí mostrar
          if (!estaInvitadoAlEvento(evento)) {
            return false;
          }
        }
        // Gerencia siempre ve todos los eventos de vencimiento
        if (esGerencia) return true;
        // Otros usuarios solo ven si están en la lista de invitados
        return estaInvitadoAlEvento(evento);
      }
      
      // Si es evento de plan de trabajo, mostrarlo SOLO al empleado asignado
      if (evento.tipo === "plan_trabajo") {
        // Verificar si el usuario es el empleado asignado
        if (evento.empleado_asignado_id) {
          if (empleadoId && String(evento.empleado_asignado_id) === String(empleadoId)) return true;
          if (usuarioId && String(evento.empleado_asignado_id) === String(usuarioId)) return true;
        }
        
        // Verificar si el usuario está en la lista de invitados
        return estaInvitadoAlEvento(evento);
      }
      
      // Para otros eventos (reunion, visita, evento), mostrar si:
      // 1. El usuario está invitado
      // 2. El usuario es el creador
      // 3. El evento no tiene invitados específicos (evento general)
      if (estaInvitadoAlEvento(evento)) return true;
      if (esCreadorDelEvento(evento)) return true;
      
      // Si no hay invitados, es un evento general visible para todos
      if (!evento.invitados || evento.invitados.length === 0) return true;
      
      // DEBUG: Si llegamos aquí, el evento fue filtrado (excluido)
      console.log("📅 Evento EXCLUIDO:", debugInfo);
      return false;
    });
    
    console.log("📅 Eventos después de filtrar:", resultado.length, "de", eventos.length);
    
    return resultado;
  }, [eventos, esGerencia, empleadoActual, usuarioActual]);

  // Cargar notificaciones del usuario
  useEffect(() => {
    async function cargarNotifs() {
      if (usuarioActual?.id) {
        const notifs = await cargarNotificacionesCalendarioDeSupabase(usuarioActual.id, filtroEmpresa);
        setNotificaciones(notifs);
      }
    }
    cargarNotifs();
  }, [usuarioActual?.id, filtroEmpresa, eventos]);

  // Funciones de calendario
  const getDiasDelMes = () => {
    const year = mesActual.getFullYear();
    const month = mesActual.getMonth();
    const primerDia = new Date(year, month, 1);
    const ultimoDia = new Date(year, month + 1, 0);
    const diasEnMes = ultimoDia.getDate();
    const primerDiaSemana = primerDia.getDay();
    
    const dias = [];
    // Días vacíos antes del primer día
    for (let i = 0; i < primerDiaSemana; i++) {
      dias.push({ dia: null, fecha: null });
    }
    // Días del mes
    for (let d = 1; d <= diasEnMes; d++) {
      const fecha = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      dias.push({ dia: d, fecha });
    }
    return dias;
  };

  const eventosDelDia = (fecha) => {
    return eventosFiltrados.filter(e => e.fecha === fecha);
  };

  const mesAnterior = () => {
    setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() - 1, 1));
  };

  const mesSiguiente = () => {
    setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + 1, 1));
  };

  const nombresMeses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", 
                        "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

  const toggleInvitado = (emp) => {
    setNuevoEvento(prev => {
      const existe = prev.invitados.find(i => i.id === emp.id);
      if (existe) {
        return { ...prev, invitados: prev.invitados.filter(i => i.id !== emp.id) };
      } else {
        return { ...prev, invitados: [...prev.invitados, { id: emp.id, nombre: emp.nombre || `${emp.nombres} ${emp.apellidos}`, confirmado: false }] };
      }
    });
  };

  const guardarEvento = async () => {
    if (!nuevoEvento.titulo || !nuevoEvento.fecha) {
      alert("Debe ingresar título y fecha");
      return;
    }
    
    // Verificar permisos: solo Gerencia puede crear/editar eventos de vencimiento
    if ((nuevoEvento.tipo === "vencimiento_proceso" || eventoSeleccionado?.tipo === "vencimiento_proceso") && !esGerencia) {
      alert("⚠️ Solo usuarios con perfil Gerencia pueden crear o modificar eventos de vencimiento.");
      return;
    }
    
    setCargando(true);
    
    // Determinar empresa_id: usar filtroEmpresa o la empresa del usuario
    const empresaIdParaEvento = filtroEmpresa || usuarioActual?.empresaId || usuarioActual?.empresa_id || null;
    console.log("📅 Guardando evento con empresa_id:", empresaIdParaEvento, "filtroEmpresa:", filtroEmpresa);
    
    // Determinar el ID del creador (usar empleadoActual primero, luego usuarioActual)
    const creadorId = empleadoActual?.id || usuarioActual?.id || null;
    const creadorNombre = empleadoActual?.nombre || 
                          (empleadoActual?.nombres && empleadoActual?.apellidos ? `${empleadoActual.nombres} ${empleadoActual.apellidos}` : null) ||
                          usuarioActual?.nombre || 
                          usuarioActual?.usuario || 
                          "Usuario";
    
    // Agregar automáticamente al creador como invitado si no está ya en la lista
    let invitadosFinales = [...(nuevoEvento.invitados || [])];
    if (creadorId) {
      const creadorYaInvitado = invitadosFinales.some(inv => String(inv.id) === String(creadorId));
      if (!creadorYaInvitado) {
        invitadosFinales.unshift({
          id: creadorId,
          nombre: creadorNombre,
          confirmado: true,
          es_creador: true
        });
        console.log("📅 Creador agregado automáticamente como invitado:", creadorId, creadorNombre);
      }
    }
    
    const eventoGuardar = {
      ...nuevoEvento,
      id: eventoSeleccionado?.id || null,
      creador_id: creadorId,
      creador_nombre: creadorNombre,
      invitados: invitadosFinales,
      empresa_id: empresaIdParaEvento // Incluir empresa_id en el evento
    };
    
    console.log("📅 Evento a guardar:", { id: eventoGuardar.id, titulo: eventoGuardar.titulo, creador_id: eventoGuardar.creador_id, invitados: eventoGuardar.invitados?.length });
    
    const guardado = await guardarEventoCalendarioEnSupabase(eventoGuardar, empresaIdParaEvento);
    if (guardado) {
      setEventos(prev => {
        const sinEste = prev.filter(e => e.id !== guardado.id);
        return [...sinEste, guardado].sort((a, b) => a.fecha.localeCompare(b.fecha));
      });
      setNuevoEvento({
        titulo: "", tipo: "reunion", fecha: new Date().toISOString().slice(0, 10),
        hora_inicio: "09:00", hora_fin: "10:00", sede_id: "", sede_nombre: "",
        descripcion: "", observaciones: "", invitados: []
      });
      setEventoSeleccionado(null);
      setModoVista("calendario");
      alert("✅ Evento guardado correctamente");
    } else {
      alert("❌ Error al guardar el evento");
    }
    setCargando(false);
  };

  const eliminarEvento = async (id) => {
    // Buscar el evento para verificar permisos
    const evento = eventos.find(e => e.id === id);
    
    // Solo Gerencia puede eliminar eventos de vencimiento
    if (evento && (evento.tipo === "vencimiento_proceso" || evento.es_vencimiento) && !esGerencia) {
      alert("⚠️ Solo usuarios con perfil Gerencia pueden eliminar eventos de vencimiento.");
      return;
    }
    
    // Verificar si el usuario es el creador o tiene permiso para eliminar
    const empleadoId = empleadoActual?.id;
    const usuarioId = usuarioActual?.id;
    const esCreador = evento && (
      (empleadoId && String(evento.creador_id) === String(empleadoId)) ||
      (usuarioId && String(evento.creador_id) === String(usuarioId))
    );
    
    if (!esGerencia && !esCreador) {
      alert("⚠️ Solo puedes eliminar eventos que hayas creado o si tienes perfil Gerencia.");
      return;
    }
    
    if (!window.confirm("¿Eliminar este evento?")) return;
    const ok = await eliminarEventoCalendarioEnSupabase(id);
    if (ok) {
      setEventos(prev => prev.filter(e => e.id !== id));
      setEventoSeleccionado(null);
    }
  };

  const editarEvento = (evento) => {
    // Verificar si el usuario es el creador del evento
    const empleadoId = empleadoActual?.id;
    const usuarioId = usuarioActual?.id;
    const esCreador = (
      (empleadoId && String(evento.creador_id) === String(empleadoId)) ||
      (usuarioId && String(evento.creador_id) === String(usuarioId))
    );
    
    // Si es evento de plan de trabajo o vencimiento, solo permitir consulta (no edición)
    const esEventoVencimiento = evento.tipo === "vencimiento_proceso" || evento.es_vencimiento;
    const esEventoPlanTrabajo = evento.tipo === "plan_trabajo";
    
    // Eventos de vencimiento SIEMPRE son solo lectura - NADIE puede editarlos
    // Se editan desde Directorio → Info Sede → Datos Importantes
    if (esEventoVencimiento) {
      setEventoSeleccionado({ ...evento, soloLectura: true });
      setModoVista("consulta");
      return;
    }
    
    // Eventos de plan de trabajo siempre son solo lectura (se editan desde Planes de Trabajo)
    if (esEventoPlanTrabajo) {
      setEventoSeleccionado({ ...evento, soloLectura: true });
      setModoVista("consulta");
      return;
    }
    
    // Si no es gerencia y no es el creador, solo puede ver (no editar)
    if (!esGerencia && !esCreador) {
      setEventoSeleccionado({ ...evento, soloLectura: true });
      setModoVista("consulta");
      return;
    }
    
    setNuevoEvento({
      titulo: evento.titulo || "",
      tipo: evento.tipo || "reunion",
      fecha: evento.fecha || new Date().toISOString().slice(0, 10),
      hora_inicio: evento.hora_inicio || "09:00",
      hora_fin: evento.hora_fin || "10:00",
      sede_id: evento.sede_id || "",
      sede_nombre: evento.sede_nombre || "",
      descripcion: evento.descripcion || "",
      observaciones: evento.observaciones || "",
      invitados: evento.invitados || []
    });
    setEventoSeleccionado(evento);
    setModoVista("crear");
  };

  const marcarNotifLeida = async (notifId) => {
    await marcarNotificacionLeidaEnSupabase(notifId);
    setNotificaciones(prev => prev.filter(n => n.id !== notifId));
  };

  const tipoIcono = { reunion: "👥", visita: "🏢", evento: "🎉", vencimiento_proceso: "⚠️", plan_trabajo: "📋" };
  const tipoColor = { reunion: "#4CAF50", visita: "#2196F3", evento: "#FF9800", vencimiento_proceso: "#f44336", plan_trabajo: "#9C27B0" };

  return (
    <div style={{ padding: "16px", maxWidth: "900px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
        <h2 style={{ margin: 0, fontSize: "20px", color: "#1a1a2e" }}>📅 Calendario</h2>
        
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {/* Notificaciones */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setMostrarNotificaciones(!mostrarNotificaciones)}
              style={{
                background: notificaciones.length > 0 ? "#ff6b35" : "#f0f0f0",
                border: "none", borderRadius: "50%", width: "36px", height: "36px",
                cursor: "pointer", position: "relative"
              }}
            >
              🔔
              {notificaciones.length > 0 && (
                <span style={{
                  position: "absolute", top: "-5px", right: "-5px",
                  background: "red", color: "white", borderRadius: "50%",
                  width: "18px", height: "18px", fontSize: "11px",
                  display: "flex", alignItems: "center", justifyContent: "center"
                }}>{notificaciones.length}</span>
              )}
            </button>
            
            {mostrarNotificaciones && notificaciones.length > 0 && (
              <div style={{
                position: "absolute", right: 0, top: "40px", background: "white",
                border: "1px solid #ddd", borderRadius: "8px", width: "280px",
                maxHeight: "300px", overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 100
              }}>
                <div style={{ padding: "10px", borderBottom: "1px solid #eee", fontWeight: "bold" }}>
                  Notificaciones
                </div>
                {notificaciones.map(n => (
                  <div key={n.id} style={{ padding: "10px", borderBottom: "1px solid #f0f0f0" }}>
                    <div style={{ fontSize: "13px", fontWeight: "500" }}>
                      {tipoIcono[n.calendario_eventos?.tipo] || "📅"} {n.calendario_eventos?.titulo || "Evento"}
                    </div>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {n.calendario_eventos?.fecha} - {n.calendario_eventos?.hora_inicio || ""}
                    </div>
                    <button
                      onClick={() => marcarNotifLeida(n.id)}
                      style={{ fontSize: "11px", color: "#ff6b35", background: "none", border: "none", cursor: "pointer", marginTop: "4px" }}
                    >Marcar como leída</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Botones de vista */}
          <button
            onClick={() => setModoVista("calendario")}
            style={{
              padding: "8px 12px", borderRadius: "6px", border: "none", cursor: "pointer",
              background: modoVista === "calendario" ? "linear-gradient(135deg, #ff6b35, #ffc53d)" : "#f0f0f0",
              color: modoVista === "calendario" ? "white" : "#333"
            }}
          >📅 Calendario</button>
          <button
            onClick={() => setModoVista("lista")}
            style={{
              padding: "8px 12px", borderRadius: "6px", border: "none", cursor: "pointer",
              background: modoVista === "lista" ? "linear-gradient(135deg, #ff6b35, #ffc53d)" : "#f0f0f0",
              color: modoVista === "lista" ? "white" : "#333"
            }}
          >📋 Lista</button>
          <button
            onClick={() => { setModoVista("crear"); setEventoSeleccionado(null); setNuevoEvento({
              titulo: "", tipo: "reunion", fecha: new Date().toISOString().slice(0, 10),
              hora_inicio: "09:00", hora_fin: "10:00", sede_id: "", sede_nombre: "",
              descripcion: "", observaciones: "", invitados: []
            }); }}
            style={{
              padding: "8px 12px", borderRadius: "6px", border: "none", cursor: "pointer",
              background: "linear-gradient(135deg, #4CAF50, #8BC34A)", color: "white"
            }}
          >➕ Nuevo</button>
        </div>
      </div>

      {cargando && <div style={{ textAlign: "center", padding: "20px" }}>Cargando...</div>}

      {/* Vista Calendario */}
      {modoVista === "calendario" && !cargando && (
        <div style={{ background: "white", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          {/* Navegación del mes */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <button onClick={mesAnterior} style={{ background: "#f0f0f0", border: "none", padding: "8px 16px", borderRadius: "6px", cursor: "pointer" }}>◀ Anterior</button>
            <h3 style={{ margin: 0 }}>{nombresMeses[mesActual.getMonth()]} {mesActual.getFullYear()}</h3>
            <button onClick={mesSiguiente} style={{ background: "#f0f0f0", border: "none", padding: "8px 16px", borderRadius: "6px", cursor: "pointer" }}>Siguiente ▶</button>
          </div>

          {/* Días de la semana */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", marginBottom: "8px" }}>
            {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map(d => (
              <div key={d} style={{ textAlign: "center", fontWeight: "bold", color: "#666", fontSize: "12px", padding: "8px" }}>{d}</div>
            ))}
          </div>

          {/* Días del mes */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px" }}>
            {getDiasDelMes().map((item, idx) => {
              const eventosHoy = item.fecha ? eventosDelDia(item.fecha) : [];
              const esHoy = item.fecha === new Date().toISOString().slice(0, 10);
              return (
                <div
                  key={idx}
                  style={{
                    minHeight: "70px", padding: "4px", borderRadius: "6px",
                    background: esHoy ? "#fff3cd" : (item.dia ? "#f8f9fa" : "transparent"),
                    border: esHoy ? "2px solid #ff6b35" : "1px solid #e0e0e0",
                    cursor: item.dia ? "pointer" : "default"
                  }}
                  onClick={() => {
                    if (item.fecha) {
                      setNuevoEvento(prev => ({ ...prev, fecha: item.fecha }));
                      setModoVista("crear");
                    }
                  }}
                >
                  {item.dia && (
                    <>
                      <div style={{ fontWeight: esHoy ? "bold" : "normal", fontSize: "12px", marginBottom: "4px" }}>{item.dia}</div>
                      {eventosHoy.slice(0, 2).map(ev => (
                        <div
                          key={ev.id}
                          onClick={(e) => { e.stopPropagation(); editarEvento(ev); }}
                          style={{
                            fontSize: "10px", padding: "2px 4px", marginBottom: "2px",
                            background: tipoColor[ev.tipo] || "#666", color: "white",
                            borderRadius: "3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                          }}
                        >{tipoIcono[ev.tipo]} {ev.titulo}</div>
                      ))}
                      {eventosHoy.length > 2 && (
                        <div style={{ fontSize: "10px", color: "#666" }}>+{eventosHoy.length - 2} más</div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Vista Lista */}
      {modoVista === "lista" && !cargando && (
        <div style={{ background: "white", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          <h3 style={{ marginTop: 0 }}>Próximos Eventos</h3>
          {eventosFiltrados.length === 0 ? (
            <p style={{ color: "#666", textAlign: "center" }}>No hay eventos programados</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {eventosFiltrados.sort((a, b) => a.fecha.localeCompare(b.fecha)).map(ev => (
                <div key={ev.id} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "12px", background: "#f8f9fa", borderRadius: "8px",
                  borderLeft: `4px solid ${tipoColor[ev.tipo] || "#666"}`
                }}>
                  <div>
                    <div style={{ fontWeight: "bold" }}>{tipoIcono[ev.tipo]} {ev.titulo}</div>
                    <div style={{ fontSize: "13px", color: "#666" }}>
                      📆 {ev.fecha} {ev.hora_inicio && `⏰ ${ev.hora_inicio}`}
                      {ev.sede_nombre && ` 📍 ${ev.sede_nombre}`}
                    </div>
                    {ev.invitados && ev.invitados.length > 0 && (
                      <div style={{ fontSize: "12px", color: "#888" }}>
                        👥 {ev.invitados.map(i => i.nombre).join(", ")}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {(() => {
                      // Determinar permisos para este evento
                      const empleadoId = empleadoActual?.id;
                      const usuarioId = usuarioActual?.id;
                      const esCreador = (
                        (empleadoId && String(ev.creador_id) === String(empleadoId)) ||
                        (usuarioId && String(ev.creador_id) === String(usuarioId))
                      );
                      const esEventoVencimiento = ev.tipo === "vencimiento_proceso" || ev.es_vencimiento;
                      const esEventoPlanTrabajo = ev.tipo === "plan_trabajo";
                      
                      // Eventos de vencimiento de proceso SIEMPRE son solo de consulta (nadie puede editar)
                      if (esEventoVencimiento) {
                        return <button onClick={() => editarEvento(ev)} style={{ background: "#607D8B", color: "white", border: "none", padding: "6px 12px", borderRadius: "4px", cursor: "pointer" }}>👁️ Ver</button>;
                      }
                      
                      // Solo mostrar botón Ver para eventos de plan_trabajo (no editables)
                      if (esEventoPlanTrabajo) {
                        return <button onClick={() => editarEvento(ev)} style={{ background: "#607D8B", color: "white", border: "none", padding: "6px 12px", borderRadius: "4px", cursor: "pointer" }}>👁️ Ver</button>;
                      }
                      
                      // Si es Gerencia o es el creador, mostrar botones de edición
                      if (esGerencia || esCreador) {
                        return (
                          <>
                            <button onClick={() => editarEvento(ev)} style={{ background: "#2196F3", color: "white", border: "none", padding: "6px 12px", borderRadius: "4px", cursor: "pointer" }}>✏️</button>
                            <button onClick={() => eliminarEvento(ev.id)} style={{ background: "#f44336", color: "white", border: "none", padding: "6px 12px", borderRadius: "4px", cursor: "pointer" }}>🗑️</button>
                          </>
                        );
                      }
                      
                      // Si no tiene permisos, solo mostrar Ver
                      return <button onClick={() => editarEvento(ev)} style={{ background: "#607D8B", color: "white", border: "none", padding: "6px 12px", borderRadius: "4px", cursor: "pointer" }}>👁️ Ver</button>;
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Vista Crear/Editar */}
      {modoVista === "crear" && (
        <div style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          <h3 style={{ marginTop: 0 }}>{eventoSeleccionado ? "✏️ Editar Evento" : "➕ Nuevo Evento"}</h3>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: "500" }}>Título *</label>
              <input
                type="text"
                value={nuevoEvento.titulo}
                onChange={e => setNuevoEvento(prev => ({ ...prev, titulo: e.target.value }))}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
                placeholder="Título del evento"
              />
            </div>
            
            <div>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: "500" }}>Tipo</label>
              <select
                value={nuevoEvento.tipo}
                onChange={e => setNuevoEvento(prev => ({ ...prev, tipo: e.target.value }))}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
              >
                <option value="reunion">👥 Reunión</option>
                <option value="visita">🏢 Visita</option>
                <option value="evento">🎉 Evento</option>
              </select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: "500" }}>Fecha *</label>
              <input
                type="date"
                value={nuevoEvento.fecha}
                onChange={e => setNuevoEvento(prev => ({ ...prev, fecha: e.target.value }))}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: "500" }}>Sede</label>
              <select
                value={nuevoEvento.sede_nombre}
                onChange={e => {
                  const sede = sedes.find(s => s.nombre === e.target.value);
                  setNuevoEvento(prev => ({ ...prev, sede_id: sede?.id || "", sede_nombre: e.target.value }));
                }}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
              >
                <option value="">-- Seleccionar sede --</option>
                {sedes.map(s => <option key={s.id} value={s.nombre}>{s.nombre}</option>)}
              </select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: "500" }}>Hora Inicio</label>
              <input
                type="time"
                value={nuevoEvento.hora_inicio}
                onChange={e => setNuevoEvento(prev => ({ ...prev, hora_inicio: e.target.value }))}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: "500" }}>Hora Fin</label>
              <input
                type="time"
                value={nuevoEvento.hora_fin}
                onChange={e => setNuevoEvento(prev => ({ ...prev, hora_fin: e.target.value }))}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
              />
            </div>
          </div>

          <div style={{ marginTop: "16px" }}>
            <label style={{ display: "block", marginBottom: "4px", fontWeight: "500" }}>Descripción</label>
            <textarea
              value={nuevoEvento.descripcion}
              onChange={e => setNuevoEvento(prev => ({ ...prev, descripcion: e.target.value }))}
              style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd", minHeight: "60px" }}
              placeholder="Descripción del evento..."
            />
          </div>

          <div style={{ marginTop: "16px" }}>
            <label style={{ display: "block", marginBottom: "4px", fontWeight: "500" }}>Observaciones</label>
            <textarea
              value={nuevoEvento.observaciones}
              onChange={e => setNuevoEvento(prev => ({ ...prev, observaciones: e.target.value }))}
              style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd", minHeight: "60px" }}
              placeholder="Observaciones adicionales..."
            />
          </div>

          {/* Invitados */}
          <div style={{ marginTop: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>👥 Invitados (les llegará notificación)</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", maxHeight: "150px", overflowY: "auto", padding: "10px", background: "#f8f9fa", borderRadius: "6px" }}>
              {[...(empleados || [])].filter(e => e.activo !== false).sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map(emp => {
                const nombre = emp.nombre || `${emp.nombres || ""} ${emp.apellidos || ""}`.trim();
                const seleccionado = nuevoEvento.invitados.some(i => i.id === emp.id);
                return (
                  <div
                    key={emp.id}
                    onClick={() => toggleInvitado(emp)}
                    style={{
                      padding: "6px 12px", borderRadius: "20px", cursor: "pointer",
                      background: seleccionado ? "#4CAF50" : "white",
                      color: seleccionado ? "white" : "#333",
                      border: seleccionado ? "none" : "1px solid #ddd",
                      fontSize: "13px"
                    }}
                  >
                    {seleccionado ? "✓ " : ""}{nombre}
                  </div>
                );
              })}
            </div>
            {nuevoEvento.invitados.length > 0 && (
              <div style={{ marginTop: "8px", fontSize: "13px", color: "#666" }}>
                Seleccionados: {nuevoEvento.invitados.map(i => i.nombre).join(", ")}
              </div>
            )}
          </div>

          {/* Botones */}
          <div style={{ display: "flex", gap: "10px", marginTop: "20px", justifyContent: "flex-end" }}>
            <button
              onClick={() => { setModoVista("calendario"); setEventoSeleccionado(null); }}
              style={{ padding: "10px 20px", borderRadius: "6px", border: "1px solid #ddd", background: "white", cursor: "pointer" }}
            >Cancelar</button>
            <button
              onClick={guardarEvento}
              disabled={cargando}
              style={{
                padding: "10px 20px", borderRadius: "6px", border: "none", cursor: "pointer",
                background: "linear-gradient(135deg, #ff6b35, #ffc53d)", color: "white", fontWeight: "bold"
              }}
            >{cargando ? "Guardando..." : "💾 Guardar Evento"}</button>
          </div>
        </div>
      )}

      {/* Vista Consulta (Solo Lectura) */}
      {modoVista === "consulta" && eventoSeleccionado && (
        <div style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          {/* Encabezado especial para vencimientos */}
          {(eventoSeleccionado.tipo === "vencimiento_proceso" || eventoSeleccionado.es_vencimiento) ? (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", background: "linear-gradient(135deg, #f44336, #e91e63)", margin: "-20px -20px 16px -20px", padding: "20px", borderRadius: "12px 12px 0 0" }}>
                <h2 style={{ margin: 0, color: "white", fontSize: "22px", display: "flex", alignItems: "center", gap: "10px" }}>
                  ⚠️ VENCIMIENTO DE PROCESO
                </h2>
                <span style={{ background: "rgba(255,255,255,0.2)", color: "white", padding: "6px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: "bold" }}>
                  Evento Automático
                </span>
              </div>
              
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "16px" }}>
                <div style={{ background: "linear-gradient(135deg, #fff3e0, #ffe0b2)", padding: "20px", borderRadius: "10px", border: "2px solid #ff9800" }}>
                  <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#e65100", fontSize: "14px" }}>📋 PROCESO</label>
                  <div style={{ fontSize: "20px", fontWeight: "bold", color: "#bf360c" }}>
                    {eventoSeleccionado.titulo?.replace("⚠️ Vence: ", "") || "Sin título"}
                  </div>
                </div>
                
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <div style={{ background: "#ffebee", padding: "16px", borderRadius: "10px", border: "1px solid #ffcdd2" }}>
                    <label style={{ display: "block", marginBottom: "6px", fontWeight: "600", color: "#c62828", fontSize: "13px" }}>📅 FECHA DE VENCIMIENTO</label>
                    <div style={{ fontSize: "18px", fontWeight: "bold", color: "#b71c1c" }}>
                      {eventoSeleccionado.fecha}
                    </div>
                  </div>
                  
                  <div style={{ background: "#e3f2fd", padding: "16px", borderRadius: "10px", border: "1px solid #bbdefb" }}>
                    <label style={{ display: "block", marginBottom: "6px", fontWeight: "600", color: "#1565c0", fontSize: "13px" }}>📍 SEDE</label>
                    <div style={{ fontSize: "18px", fontWeight: "bold", color: "#0d47a1" }}>
                      {eventoSeleccionado.sede_nombre || "N/A"}
                    </div>
                  </div>
                </div>
                
                {eventoSeleccionado.descripcion && (
                  <div style={{ background: "#f5f5f5", padding: "16px", borderRadius: "10px" }}>
                    <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#424242", fontSize: "14px" }}>📝 DETALLES</label>
                    <div style={{ fontSize: "15px", color: "#616161", whiteSpace: "pre-wrap", lineHeight: "1.5" }}>
                      {eventoSeleccionado.descripcion}
                    </div>
                  </div>
                )}
                
                {eventoSeleccionado.observaciones && (
                  <div style={{ background: "#fafafa", padding: "16px", borderRadius: "10px", border: "1px solid #e0e0e0" }}>
                    <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", color: "#616161", fontSize: "14px" }}>📋 OBSERVACIONES</label>
                    <div style={{ fontSize: "14px", color: "#757575", whiteSpace: "pre-wrap" }}>
                      {eventoSeleccionado.observaciones}
                    </div>
                  </div>
                )}
                
                {/* Sección de invitados - SOLO LECTURA (evento informativo) */}
                <div style={{ background: "#e8f5e9", padding: "16px", borderRadius: "10px", border: "2px solid #4CAF50" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <label style={{ fontWeight: "600", color: "#2e7d32", fontSize: "14px" }}>
                      👥 EMPLEADOS NOTIFICADOS ({eventoSeleccionado.invitados?.length || 0})
                    </label>
                    <span style={{ background: "#9E9E9E", color: "white", padding: "4px 10px", borderRadius: "12px", fontSize: "11px" }}>
                      🔒 Solo lectura
                    </span>
                  </div>
                  
                  {/* Modo visualización - SIEMPRE solo lectura */}
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    {eventoSeleccionado.invitados && eventoSeleccionado.invitados.length > 0 ? (
                      eventoSeleccionado.invitados.map((inv, idx) => (
                        <span key={idx} style={{ background: "#4CAF50", color: "white", padding: "6px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: "500" }}>
                          {inv.nombre}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: "#999", fontStyle: "italic" }}>Sin empleados asignados</span>
                    )}
                  </div>
                  <p style={{ margin: "12px 0 0 0", fontSize: "12px", color: "#666", fontStyle: "italic" }}>
                    ℹ️ Este evento es informativo. Para modificar los empleados notificados, edite el proceso en Directorio → Info Sede.
                  </p>
                </div>
              </div>
            </>
          ) : (
            /* Vista normal para otros eventos */
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
                  {tipoIcono[eventoSeleccionado.tipo] || "📌"} 
                  {eventoSeleccionado.tipo === "plan_trabajo" ? "Plan de Trabajo" : "Evento"}
                </h3>
                <span style={{ background: eventoSeleccionado.tipo === "plan_trabajo" ? "#9C27B0" : "#607D8B", color: "white", padding: "4px 12px", borderRadius: "20px", fontSize: "12px" }}>
                  Solo consulta
                </span>
              </div>
              
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", color: "#666" }}>Título</label>
                  <div style={{ padding: "12px", background: "#f5f5f5", borderRadius: "6px", fontWeight: "bold", fontSize: "16px" }}>
                    {eventoSeleccionado.titulo}
                  </div>
                </div>
                
                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", color: "#666" }}>📅 Fecha</label>
                  <div style={{ padding: "12px", background: "#f5f5f5", borderRadius: "6px" }}>
                    {eventoSeleccionado.fecha}
                  </div>
                </div>
                
                <div>
                  <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", color: "#666" }}>⏰ Horario</label>
                  <div style={{ padding: "12px", background: "#f5f5f5", borderRadius: "6px" }}>
                    {eventoSeleccionado.hora_inicio || "N/A"} - {eventoSeleccionado.hora_fin || "N/A"}
                  </div>
                </div>
                
                {eventoSeleccionado.sede_nombre && (
                  <div>
                    <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", color: "#666" }}>📍 Sede</label>
                    <div style={{ padding: "12px", background: "#f5f5f5", borderRadius: "6px" }}>
                      {eventoSeleccionado.sede_nombre}
                    </div>
                  </div>
                )}
                
                {eventoSeleccionado.descripcion && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", color: "#666" }}>📝 Descripción</label>
                    <div style={{ padding: "12px", background: "#f5f5f5", borderRadius: "6px", whiteSpace: "pre-wrap" }}>
                      {eventoSeleccionado.descripcion}
                    </div>
                  </div>
                )}
                
                {eventoSeleccionado.observaciones && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", color: "#666" }}>📋 Observaciones</label>
                    <div style={{ padding: "12px", background: "#f5f5f5", borderRadius: "6px", whiteSpace: "pre-wrap" }}>
                      {eventoSeleccionado.observaciones}
                    </div>
                  </div>
                )}
                
                {eventoSeleccionado.invitados && eventoSeleccionado.invitados.length > 0 && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", color: "#666" }}>👥 Involucrados</label>
                    <div style={{ padding: "12px", background: "#f5f5f5", borderRadius: "6px" }}>
                      {eventoSeleccionado.invitados.map(i => i.nombre).join(", ")}
                    </div>
                  </div>
                )}
                
                {eventoSeleccionado.creador_nombre && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={{ display: "block", marginBottom: "4px", fontWeight: "500", color: "#666" }}>✍️ Creado por</label>
                    <div style={{ padding: "12px", background: "#f5f5f5", borderRadius: "6px" }}>
                      {eventoSeleccionado.creador_nombre}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Botón Cerrar */}
          <div style={{ display: "flex", gap: "10px", marginTop: "20px", justifyContent: "flex-end" }}>
            <button
              onClick={() => { 
                setModoVista("calendario"); 
                setEventoSeleccionado(null); 
                setEditandoInvitados(false);
                setInvitadosTemp([]);
              }}
              style={{ 
                padding: "12px 28px", 
                borderRadius: "6px", 
                border: "none", 
                background: "linear-gradient(135deg, #607D8B, #455A64)", 
                color: "white", 
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "14px"
              }}
            >✓ Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================== DIRECTORIO (PROVEEDORES + INFO SEDES) ====================== */

function DirectorioContactos({ sedes = [], empleados = [], filtroEmpresa = null, tienePermiso = () => true, usuarioActual = null, empleadoActual = null }) {
  const [tab, setTab] = useState("proveedores"); // "proveedores" | "sedes"
  const [proveedores, setProveedores] = useState([]);
  const [infoSedes, setInfoSedes] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(null); // null | "nuevo" | id
  const [busqueda, setBusqueda] = useState("");
  
  // Verificar si el usuario tiene perfil Gerencia
  const esGerencia = useMemo(() => {
    if (!usuarioActual) return false;
    const perfil = (usuarioActual.perfil || usuarioActual.rol || "").toLowerCase();
    return perfil.includes("gerencia") || perfil.includes("gerente") || perfil.includes("admin");
  }, [usuarioActual]);
  
  // Formulario proveedor
  const [formProveedor, setFormProveedor] = useState({
    nombre: "", telefono: "", nit: "",
    productos_servicios: "", formas_pago: [], banco: "", tipo_cuenta: "",
    numero_cuenta: "", observaciones: ""
  });

  // Formulario info sede
  const [formSede, setFormSede] = useState({
    sede_nombre: "", nit: "", representante_legal: "", correo_notificaciones: "",
    telefono_local: "", telefono_admin: "", direccion: "",
    bancos: [], // Array de {id, banco, tipo_cuenta, numero_cuenta}
    claves: [], 
    datos_importantes: [], // Array de {id, proceso, fecha_realizacion, fecha_vencimiento, valor, cantidad_empleados, empleados_notificar: []}
    observaciones: ""
  });
  
  // Estado para mostrar/ocultar selector de empleados por proceso
  const [mostrarSelectorEmpleados, setMostrarSelectorEmpleados] = useState({});
  
  // 🔹 Estado para el nombre del documento que se va a subir (por proceso)
  const [nombreDocumento, setNombreDocumento] = useState({});

  const [sedeEditandoId, setSedeEditandoId] = useState(null);

  // Cargar datos
  useEffect(() => {
    async function cargar() {
      setCargando(true);
      const [provs, infos] = await Promise.all([
        cargarProveedoresDeSupabase(filtroEmpresa),
        cargarInfoSedesDeSupabase(filtroEmpresa)
      ]);
      setProveedores(provs);
      setInfoSedes(infos);
      setCargando(false);
    }
    cargar();
  }, [filtroEmpresa]);

  // Proveedores
  const guardarProveedor = async () => {
    if (!formProveedor.nombre) {
      alert("El nombre es requerido");
      return;
    }
    setCargando(true);
    const payload = { ...formProveedor, id: modoEdicion !== "nuevo" ? modoEdicion : null };
    const guardado = await guardarProveedorEnSupabase(payload, filtroEmpresa);
    if (guardado) {
      setProveedores(prev => {
        const sin = prev.filter(p => p.id !== guardado.id);
        return [...sin, guardado].sort((a, b) => a.nombre.localeCompare(b.nombre));
      });
      resetFormProveedor();
      alert("✅ Proveedor guardado");
    } else {
      alert("❌ Error al guardar");
    }
    setCargando(false);
  };

  const editarProveedor = (prov) => {
    setFormProveedor({
      nombre: prov.nombre || "", telefono: prov.telefono || "", nit: prov.nit || "",
      productos_servicios: prov.productos_servicios || "",
      formas_pago: prov.formas_pago || [],
      banco: prov.banco || "", tipo_cuenta: prov.tipo_cuenta || "",
      numero_cuenta: prov.numero_cuenta || "",
      observaciones: prov.observaciones || ""
    });
    setModoEdicion(prov.id);
  };

  const eliminarProveedor = async (id) => {
    if (!window.confirm("¿Eliminar este proveedor?")) return;
    const ok = await eliminarProveedorEnSupabase(id);
    if (ok) {
      setProveedores(prev => prev.filter(p => p.id !== id));
    }
  };

  const resetFormProveedor = () => {
    setFormProveedor({
      nombre: "", telefono: "", nit: "",
      productos_servicios: "", formas_pago: [], banco: "", tipo_cuenta: "",
      numero_cuenta: "", observaciones: ""
    });
    setModoEdicion(null);
  };

  // Info Sedes
  const guardarInfoSede = async () => {
    if (!esGerencia) {
      alert("⚠️ Solo usuarios con perfil Gerencia pueden modificar la información de sedes.");
      return;
    }
    if (!formSede.sede_nombre) {
      alert("La sede es requerida");
      return;
    }
    setCargando(true);
    const payload = { ...formSede, id: sedeEditandoId };
    const guardado = await guardarInfoSedeEnSupabase(payload, filtroEmpresa);
    if (guardado) {
      setInfoSedes(prev => {
        const sin = prev.filter(s => s.id !== guardado.id);
        return [...sin, guardado].sort((a, b) => (a.sede_nombre || "").localeCompare(b.sede_nombre || ""));
      });
      
      // Crear eventos de calendario para fechas de vencimiento (solo para empleados seleccionados)
      const datosConVencimiento = (formSede.datos_importantes || []).filter(d => d.fecha_vencimiento);
      console.log("📅 Datos con fecha de vencimiento:", datosConVencimiento.length);
      
      // 🔹 PRIMERO: Eliminar eventos de vencimiento anteriores para esta sede
      // Buscar por sede_nombre y título que contenga "⚠️ Vence:" (ya que el tipo se guarda como "evento" en BD)
      try {
        const { data: eventosAnteriores } = await supabase
          .from("calendario_eventos")
          .select("id, titulo")
          .eq("sede_nombre", formSede.sede_nombre)
          .like("titulo", "⚠️ Vence:%");
        
        if (eventosAnteriores && eventosAnteriores.length > 0) {
          console.log("🗑️ Eliminando", eventosAnteriores.length, "eventos de vencimiento anteriores para sede:", formSede.sede_nombre);
          const idsAEliminar = eventosAnteriores.map(e => e.id);
          await supabase
            .from("calendario_eventos")
            .delete()
            .in("id", idsAEliminar);
        }
      } catch (err) {
        console.error("Error eliminando eventos anteriores:", err);
      }
      
      if (datosConVencimiento.length > 0) {
        // Crear evento de calendario para cada fecha de vencimiento
        for (const dato of datosConVencimiento) {
          // Obtener empleados a notificar (puede estar vacío, el evento siempre se crea)
          const empleadosANotificar = dato.empleados_notificar || [];
          
          console.log("📅 Creando evento de vencimiento para proceso:", dato.proceso);
          if (empleadosANotificar.length > 0) {
            console.log("📅 Empleados seleccionados para notificar:", empleadosANotificar.length);
          } else {
            console.log("📅 Evento solo visible para Gerencia (sin empleados específicos)");
          }
          
          // 🔹 Usar UUID válido para el ID (la tabla calendario_eventos requiere UUID)
          const eventoVencimiento = {
            id: uid(), // UUID válido
            titulo: `⚠️ Vence: ${dato.proceso || "Proceso"}`,
            tipo: "vencimiento_proceso",
            fecha: dato.fecha_vencimiento,
            hora_inicio: "08:00",
            hora_fin: "09:00",
            sede_id: null,
            sede_nombre: formSede.sede_nombre,
            descripcion: `Vencimiento de proceso: ${dato.proceso || "Sin nombre"}. Sede: ${formSede.sede_nombre}. Valor: $${Number(dato.valor || 0).toLocaleString()}`,
            observaciones: `Fecha realización: ${dato.fecha_realizacion || "N/A"}. Empleados involucrados: ${dato.cantidad_empleados || "N/A"}. RefProceso: ${dato.id}`,
            creador_id: null,
            creador_nombre: "Sistema - Directorio",
            invitados: empleadosANotificar.map(emp => ({ 
              id: emp.id, 
              nombre: emp.nombre || `${emp.nombres || ""} ${emp.apellidos || ""}`.trim()
            })),
            estado: "pendiente",
            es_vencimiento: true,
            solo_gerencia: true  // Marcar como evento exclusivo para Gerencia
          };
          
          console.log("📅 Creando evento de vencimiento:", eventoVencimiento.titulo, "fecha:", eventoVencimiento.fecha);
          
          try {
            const resultado = await guardarEventoCalendarioEnSupabase(eventoVencimiento, filtroEmpresa);
            console.log("📅 Resultado guardar evento vencimiento:", resultado ? "OK" : "ERROR");
          } catch (err) {
            console.error("Error creando evento de vencimiento:", err);
          }
        }
      }
      
      resetFormSede();
      alert("✅ Información guardada" + (datosConVencimiento.length > 0 ? " y eventos de vencimiento agregados al calendario" : ""));
    } else {
      alert("❌ Error al guardar");
    }
    setCargando(false);
  };

  const editarInfoSede = (info) => {
    if (!esGerencia) {
      alert("⚠️ Solo usuarios con perfil Gerencia pueden editar la información de sedes.");
      return;
    }
    // Convertir banco antiguo a nuevo formato si existe
    let bancosArray = info.bancos || [];
    if ((!bancosArray || bancosArray.length === 0) && info.banco) {
      bancosArray = [{ id: Date.now(), banco: info.banco, tipo_cuenta: info.tipo_cuenta || "", numero_cuenta: info.numero_cuenta || "" }];
    }
    // Asegurar que cada dato_importante tenga empleados_notificar y documentos
    const datosConEmpleados = (info.datos_importantes || []).map(d => ({
      ...d,
      empleados_notificar: d.empleados_notificar || [],
      documentos: d.documentos || []  // 🔹 Asegurar que documentos exista
    }));
    setFormSede({
      sede_nombre: info.sede_nombre || "", 
      nit: info.nit || "",
      representante_legal: info.representante_legal || "",
      correo_notificaciones: info.correo_notificaciones || "",
      telefono_local: info.telefono_local || "",
      telefono_admin: info.telefono_admin || "", direccion: info.direccion || "",
      bancos: bancosArray,
      claves: info.claves || [], datos_importantes: datosConEmpleados,
      observaciones: info.observaciones || ""
    });
    setSedeEditandoId(info.id);
    setMostrarSelectorEmpleados({});
  };
  
  // Función para toggle de empleados en la lista de notificación
  const toggleEmpleadoNotificar = (datoId, empleado) => {
    setFormSede(prev => ({
      ...prev,
      datos_importantes: prev.datos_importantes.map(d => {
        if (d.id !== datoId) return d;
        const empleadosActuales = d.empleados_notificar || [];
        const existe = empleadosActuales.some(e => e.id === empleado.id);
        const nuevosEmpleados = existe
          ? empleadosActuales.filter(e => e.id !== empleado.id)
          : [...empleadosActuales, { id: empleado.id, nombre: empleado.nombre || `${empleado.nombres || ""} ${empleado.apellidos || ""}`.trim() }];
        return { ...d, empleados_notificar: nuevosEmpleados };
      })
    }));
  };

  const resetFormSede = () => {
    setFormSede({
      sede_nombre: "", nit: "", representante_legal: "", correo_notificaciones: "",
      telefono_local: "", telefono_admin: "", direccion: "",
      bancos: [], claves: [], datos_importantes: [], observaciones: ""
    });
    setSedeEditandoId(null);
    setMostrarSelectorEmpleados({});
  };

  // Funciones para manejar múltiples bancos
  const agregarBanco = () => {
    setFormSede(prev => ({
      ...prev,
      bancos: [...prev.bancos, { id: Date.now(), banco: "", tipo_cuenta: "", numero_cuenta: "" }]
    }));
  };

  const actualizarBanco = (id, campo, valor) => {
    setFormSede(prev => ({
      ...prev,
      bancos: prev.bancos.map(b => b.id === id ? { ...b, [campo]: valor } : b)
    }));
  };

  const eliminarBanco = (id) => {
    setFormSede(prev => ({
      ...prev,
      bancos: prev.bancos.filter(b => b.id !== id)
    }));
  };

  const agregarClave = () => {
    setFormSede(prev => ({
      ...prev,
      claves: [...prev.claves, { id: Date.now(), nombre: "", valor: "" }]
    }));
  };

  const actualizarClave = (id, campo, valor) => {
    setFormSede(prev => ({
      ...prev,
      claves: prev.claves.map(c => c.id === id ? { ...c, [campo]: valor } : c)
    }));
  };

  const eliminarClave = (id) => {
    setFormSede(prev => ({
      ...prev,
      claves: prev.claves.filter(c => c.id !== id)
    }));
  };

  const agregarDato = () => {
    if (!esGerencia) {
      alert("⚠️ Solo usuarios con perfil Gerencia pueden agregar procesos.");
      return;
    }
    setFormSede(prev => ({
      ...prev,
      datos_importantes: [...prev.datos_importantes, { 
        id: Date.now(), 
        proceso: "", 
        fecha_realizacion: "", 
        fecha_vencimiento: "", 
        valor: "", 
        cantidad_empleados: "",
        empleados_notificar: [],
        documentos: []  // 🔹 Campo para documentos adjuntos
      }]
    }));
  };

  // 🔹 Función para subir documentos a un proceso
  const subirDocumentoProceso = async (datoId, file, nombrePersonalizado = null) => {
    if (!esGerencia) {
      alert("⚠️ Solo usuarios con perfil Gerencia pueden adjuntar documentos.");
      return;
    }
    
    // Validar que se haya ingresado un nombre
    const nombreFinal = nombrePersonalizado || nombreDocumento[datoId] || "";
    if (!nombreFinal.trim()) {
      alert("⚠️ Por favor, ingrese un nombre/tipo de documento antes de adjuntar.");
      return;
    }
    
    // Validar tamaño (máximo 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert("El archivo es demasiado grande. Máximo 10MB.");
      return;
    }
    
    setCargando(true);
    try {
      const extension = file.name.includes(".") ? file.name.substring(file.name.lastIndexOf(".")) : "";
      const nombreArchivo = `procesos-sedes/${formSede.sede_nombre || 'sede'}/${datoId}/${Date.now()}_${uid()}${extension}`;
      
      const { error: uploadError } = await supabase.storage
        .from(SUPABASE_BUCKET_DOCS)
        .upload(nombreArchivo, file, {
          cacheControl: "3600",
          upsert: false,
          contentType: file.type
        });
      
      if (uploadError) {
        throw new Error(uploadError.message);
      }
      
      const { data: urlData } = supabase.storage
        .from(SUPABASE_BUCKET_DOCS)
        .getPublicUrl(nombreArchivo);
      
      const nuevoDocumento = {
        id: uid(),
        nombre: nombreFinal.trim(),  // 🔹 Usar el nombre personalizado
        nombreArchivo: file.name,     // 🔹 Guardar también el nombre original del archivo
        url: urlData.publicUrl,
        tipo: file.type,
        path: nombreArchivo,
        fechaSubida: new Date().toISOString()
      };
      
      setFormSede(prev => ({
        ...prev,
        datos_importantes: prev.datos_importantes.map(d => 
          d.id === datoId 
            ? { ...d, documentos: [...(d.documentos || []), nuevoDocumento] }
            : d
        )
      }));
      
      // 🔹 Limpiar el input del nombre después de subir
      setNombreDocumento(prev => ({ ...prev, [datoId]: "" }));
      
      alert("✅ Documento adjuntado correctamente");
    } catch (error) {
      console.error("Error subiendo documento:", error);
      alert("❌ Error al subir el documento: " + error.message);
    }
    setCargando(false);
  };

  // 🔹 Función para eliminar documento de un proceso
  const eliminarDocumentoProceso = async (datoId, documentoId) => {
    if (!esGerencia) {
      alert("⚠️ Solo usuarios con perfil Gerencia pueden eliminar documentos.");
      return;
    }
    
    if (!window.confirm("¿Eliminar este documento?")) return;
    
    const dato = formSede.datos_importantes.find(d => d.id === datoId);
    const documento = dato?.documentos?.find(doc => doc.id === documentoId);
    
    if (documento?.path) {
      try {
        await supabase.storage.from(SUPABASE_BUCKET_DOCS).remove([documento.path]);
      } catch (error) {
        console.error("Error eliminando archivo del storage:", error);
      }
    }
    
    setFormSede(prev => ({
      ...prev,
      datos_importantes: prev.datos_importantes.map(d => 
        d.id === datoId 
          ? { ...d, documentos: (d.documentos || []).filter(doc => doc.id !== documentoId) }
          : d
      )
    }));
  };

  const actualizarDato = (id, campo, valor) => {
    setFormSede(prev => ({
      ...prev,
      datos_importantes: prev.datos_importantes.map(d => d.id === id ? { ...d, [campo]: valor } : d)
    }));
  };

  const eliminarDato = (id) => {
    setFormSede(prev => ({
      ...prev,
      datos_importantes: prev.datos_importantes.filter(d => d.id !== id)
    }));
  };

  const proveedoresFiltrados = proveedores.filter(p => 
    p.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
    p.productos_servicios?.toLowerCase().includes(busqueda.toLowerCase())
  );

  const inputStyle = { width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd", fontSize: "14px" };
  const labelStyle = { display: "block", marginBottom: "4px", fontWeight: "500", fontSize: "13px" };

  return (
    <div style={{ padding: "16px", maxWidth: "900px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
        <h2 style={{ margin: 0, fontSize: "20px", color: "#1a1a2e" }}>📇 Directorio</h2>
        
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={() => setTab("proveedores")}
            style={{
              padding: "8px 16px", borderRadius: "6px", border: "none", cursor: "pointer",
              background: tab === "proveedores" ? "linear-gradient(135deg, #ff6b35, #ffc53d)" : "#f0f0f0",
              color: tab === "proveedores" ? "white" : "#333"
            }}
          >🏪 Proveedores</button>
          <button
            onClick={() => setTab("sedes")}
            style={{
              padding: "8px 16px", borderRadius: "6px", border: "none", cursor: "pointer",
              background: tab === "sedes" ? "linear-gradient(135deg, #ff6b35, #ffc53d)" : "#f0f0f0",
              color: tab === "sedes" ? "white" : "#333"
            }}
          >🏢 Info Sedes</button>
        </div>
      </div>

      {cargando && <div style={{ textAlign: "center", padding: "20px" }}>Cargando...</div>}

      {/* TAB PROVEEDORES */}
      {tab === "proveedores" && !cargando && (
        <div>
          {/* Barra de búsqueda y botón nuevo */}
          <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
            <input
              type="text"
              placeholder="🔍 Buscar proveedor..."
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              style={{ flex: 1, padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
            />
            <button
              onClick={() => { resetFormProveedor(); setModoEdicion("nuevo"); }}
              style={{
                padding: "10px 16px", borderRadius: "6px", border: "none", cursor: "pointer",
                background: "linear-gradient(135deg, #4CAF50, #8BC34A)", color: "white"
              }}
            >➕ Nuevo Proveedor</button>
          </div>

          {/* Formulario Proveedor */}
          {modoEdicion && (
            <div style={{ background: "white", borderRadius: "12px", padding: "20px", marginBottom: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <h3 style={{ marginTop: 0 }}>{modoEdicion === "nuevo" ? "➕ Nuevo Proveedor" : "✏️ Editar Proveedor"}</h3>
              
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div><label style={labelStyle}>Nombre *</label><input style={inputStyle} value={formProveedor.nombre} onChange={e => setFormProveedor(p => ({ ...p, nombre: e.target.value }))} /></div>
                <div><label style={labelStyle}>NIT</label><input style={inputStyle} value={formProveedor.nit} onChange={e => setFormProveedor(p => ({ ...p, nit: e.target.value }))} /></div>
                <div><label style={labelStyle}>Teléfono</label><input style={inputStyle} value={formProveedor.telefono} onChange={e => setFormProveedor(p => ({ ...p, telefono: e.target.value }))} /></div>
                <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>¿Qué se les compra?</label><textarea style={{ ...inputStyle, minHeight: "60px" }} value={formProveedor.productos_servicios} onChange={e => setFormProveedor(p => ({ ...p, productos_servicios: e.target.value }))} /></div>
                <div>
                  <label style={labelStyle}>Formas de Pago</label>
                  <div style={{ display: "flex", gap: "16px", padding: "10px", background: "#f8f9fa", borderRadius: "6px", border: "1px solid #ddd" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={formProveedor.formas_pago.includes("Efectivo")}
                        onChange={e => {
                          setFormProveedor(p => ({
                            ...p,
                            formas_pago: e.target.checked
                              ? [...p.formas_pago, "Efectivo"]
                              : p.formas_pago.filter(f => f !== "Efectivo")
                          }));
                        }}
                      />
                      💵 Efectivo
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={formProveedor.formas_pago.includes("Transferencia")}
                        onChange={e => {
                          setFormProveedor(p => ({
                            ...p,
                            formas_pago: e.target.checked
                              ? [...p.formas_pago, "Transferencia"]
                              : p.formas_pago.filter(f => f !== "Transferencia")
                          }));
                        }}
                      />
                      🏦 Transferencia
                    </label>
                  </div>
                </div>
                <div><label style={labelStyle}>Banco</label><input style={inputStyle} value={formProveedor.banco} onChange={e => setFormProveedor(p => ({ ...p, banco: e.target.value }))} /></div>
                <div><label style={labelStyle}>Tipo Cuenta</label>
                  <select style={inputStyle} value={formProveedor.tipo_cuenta} onChange={e => setFormProveedor(p => ({ ...p, tipo_cuenta: e.target.value }))}>
                    <option value="">-- Seleccionar --</option>
                    <option value="Ahorros">Ahorros</option>
                    <option value="Corriente">Corriente</option>
                  </select>
                </div>
                <div><label style={labelStyle}>Número de Cuenta</label><input style={inputStyle} value={formProveedor.numero_cuenta} onChange={e => setFormProveedor(p => ({ ...p, numero_cuenta: e.target.value }))} /></div>
                <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Observaciones</label><textarea style={{ ...inputStyle, minHeight: "60px" }} value={formProveedor.observaciones} onChange={e => setFormProveedor(p => ({ ...p, observaciones: e.target.value }))} /></div>
              </div>

              <div style={{ display: "flex", gap: "10px", marginTop: "16px", justifyContent: "flex-end" }}>
                <button onClick={resetFormProveedor} style={{ padding: "10px 20px", borderRadius: "6px", border: "1px solid #ddd", background: "white", cursor: "pointer" }}>Cancelar</button>
                <button onClick={guardarProveedor} style={{ padding: "10px 20px", borderRadius: "6px", border: "none", background: "linear-gradient(135deg, #ff6b35, #ffc53d)", color: "white", cursor: "pointer", fontWeight: "bold" }}>💾 Guardar</button>
              </div>
            </div>
          )}

          {/* Lista de Proveedores */}
          <div style={{ background: "white", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            {proveedoresFiltrados.length === 0 ? (
              <p style={{ textAlign: "center", color: "#666" }}>No hay proveedores registrados</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {proveedoresFiltrados.map(prov => (
                  <div key={prov.id} style={{
                    padding: "12px", background: "#f8f9fa", borderRadius: "8px",
                    borderLeft: "4px solid #ff6b35"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontWeight: "bold", fontSize: "15px" }}>🏪 {prov.nombre}</div>
                        {prov.telefono && <div style={{ fontSize: "13px", color: "#666" }}>📞 {prov.telefono}</div>}
                        {prov.productos_servicios && <div style={{ fontSize: "13px", color: "#888" }}>📦 {prov.productos_servicios}</div>}
                        {prov.formas_pago && prov.formas_pago.length > 0 && <div style={{ fontSize: "12px", color: "#888" }}>💳 {prov.formas_pago.join(", ")}</div>}
                        {prov.banco && <div style={{ fontSize: "12px", color: "#888" }}>🏦 {prov.banco} - {prov.tipo_cuenta} - {prov.numero_cuenta}</div>}
                      </div>
                      <div style={{ display: "flex", gap: "6px" }}>
                        <button onClick={() => editarProveedor(prov)} style={{ background: "#2196F3", color: "white", border: "none", padding: "6px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>✏️</button>
                        <button onClick={() => eliminarProveedor(prov.id)} style={{ background: "#f44336", color: "white", border: "none", padding: "6px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>🗑️</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB INFO SEDES */}
      {tab === "sedes" && !cargando && (
        <div>
          {/* Mensaje para usuarios sin perfil Gerencia */}
          {!esGerencia && (
            <div style={{ 
              background: "linear-gradient(135deg, #fff3e0, #ffe0b2)", 
              padding: "12px 16px", 
              borderRadius: "8px", 
              marginBottom: "16px",
              border: "1px solid #ffb74d",
              display: "flex",
              alignItems: "center",
              gap: "10px"
            }}>
              <span style={{ fontSize: "20px" }}>🔒</span>
              <span style={{ color: "#e65100", fontSize: "14px" }}>
                <strong>Modo solo lectura:</strong> Solo usuarios con perfil Gerencia pueden crear o modificar información de sedes y procesos.
              </span>
            </div>
          )}
          
          {/* Formulario Info Sede - Solo para Gerencia */}
          {esGerencia && (
          <div style={{ background: "white", borderRadius: "12px", padding: "20px", marginBottom: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <h3 style={{ marginTop: 0 }}>{sedeEditandoId ? "✏️ Editar Info Sede" : "➕ Nueva Info de Sede"}</h3>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div>
                <label style={labelStyle}>Sede *</label>
                <select style={inputStyle} value={formSede.sede_nombre} onChange={e => setFormSede(p => ({ ...p, sede_nombre: e.target.value }))}>
                  <option value="">-- Seleccionar sede --</option>
                  {sedes.map(s => <option key={s.id} value={s.nombre}>{s.nombre}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>NIT</label><input style={inputStyle} placeholder="Ej: 900.123.456-7" value={formSede.nit || ""} onChange={e => setFormSede(p => ({ ...p, nit: e.target.value }))} /></div>
              <div><label style={labelStyle}>Representante Legal</label><input style={inputStyle} placeholder="Nombre completo" value={formSede.representante_legal || ""} onChange={e => setFormSede(p => ({ ...p, representante_legal: e.target.value }))} /></div>
              <div><label style={labelStyle}>Correo de Notificaciones</label><input style={inputStyle} type="email" placeholder="correo@ejemplo.com" value={formSede.correo_notificaciones || ""} onChange={e => setFormSede(p => ({ ...p, correo_notificaciones: e.target.value }))} /></div>
              <div><label style={labelStyle}>Teléfono Local</label><input style={inputStyle} value={formSede.telefono_local} onChange={e => setFormSede(p => ({ ...p, telefono_local: e.target.value }))} /></div>
              <div><label style={labelStyle}>Teléfono Admin</label><input style={inputStyle} value={formSede.telefono_admin} onChange={e => setFormSede(p => ({ ...p, telefono_admin: e.target.value }))} /></div>
              <div style={{ gridColumn: "span 2" }}><label style={labelStyle}>Dirección</label><input style={inputStyle} value={formSede.direccion} onChange={e => setFormSede(p => ({ ...p, direccion: e.target.value }))} /></div>
            </div>

            {/* Bancos - Múltiples */}
            <div style={{ marginTop: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <label style={{ fontWeight: "500" }}>🏦 Bancos y Cuentas</label>
                <button onClick={agregarBanco} style={{ background: "#4CAF50", color: "white", border: "none", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>+ Agregar Banco</button>
              </div>
              {formSede.bancos && formSede.bancos.map(banco => (
                <div key={banco.id} style={{ display: "flex", gap: "8px", marginBottom: "6px", flexWrap: "wrap", padding: "10px", backgroundColor: "#f0f8ff", borderRadius: "6px" }}>
                  <input placeholder="Nombre del Banco" style={{ ...inputStyle, flex: 1, minWidth: "150px" }} value={banco.banco} onChange={e => actualizarBanco(banco.id, "banco", e.target.value)} />
                  <select style={{ ...inputStyle, minWidth: "120px", flex: "0 0 auto" }} value={banco.tipo_cuenta} onChange={e => actualizarBanco(banco.id, "tipo_cuenta", e.target.value)}>
                    <option value="">Tipo Cuenta</option>
                    <option value="Ahorros">Ahorros</option>
                    <option value="Corriente">Corriente</option>
                  </select>
                  <input placeholder="Número de Cuenta" style={{ ...inputStyle, flex: 1, minWidth: "150px" }} value={banco.numero_cuenta} onChange={e => actualizarBanco(banco.id, "numero_cuenta", e.target.value)} />
                  <button onClick={() => eliminarBanco(banco.id)} style={{ background: "#f44336", color: "white", border: "none", padding: "6px 10px", borderRadius: "4px", cursor: "pointer" }}>🗑️</button>
                </div>
              ))}
              {(!formSede.bancos || formSede.bancos.length === 0) && (
                <p style={{ color: "#888", fontSize: "13px", fontStyle: "italic" }}>No hay bancos agregados. Haga clic en "+ Agregar Banco" para añadir uno.</p>
              )}
            </div>

            {/* Claves */}
            <div style={{ marginTop: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <label style={{ fontWeight: "500" }}>🔐 Claves Importantes</label>
                <button onClick={agregarClave} style={{ background: "#4CAF50", color: "white", border: "none", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>+ Agregar</button>
              </div>
              {formSede.claves.map(clave => (
                <div key={clave.id} style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
                  <input placeholder="Nombre (ej: WiFi)" style={{ ...inputStyle, flex: 1 }} value={clave.nombre} onChange={e => actualizarClave(clave.id, "nombre", e.target.value)} />
                  <input placeholder="Valor/Clave" style={{ ...inputStyle, flex: 1 }} value={clave.valor} onChange={e => actualizarClave(clave.id, "valor", e.target.value)} />
                  <button onClick={() => eliminarClave(clave.id)} style={{ background: "#f44336", color: "white", border: "none", padding: "6px 10px", borderRadius: "4px", cursor: "pointer" }}>🗑️</button>
                </div>
              ))}
            </div>

            {/* Datos Importantes - Procesos */}
            <div style={{ marginTop: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <label style={{ fontWeight: "500" }}>📋 Datos Importantes (Procesos)</label>
                {esGerencia && (
                  <button onClick={agregarDato} style={{ background: "#4CAF50", color: "white", border: "none", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>+ Agregar Proceso</button>
                )}
              </div>
              {!esGerencia && formSede.datos_importantes.length === 0 && (
                <p style={{ color: "#888", fontSize: "13px", fontStyle: "italic" }}>Solo usuarios con perfil Gerencia pueden agregar/modificar procesos.</p>
              )}
              {formSede.datos_importantes.map(dato => (
                <div key={dato.id} style={{ marginBottom: "10px", padding: "12px", backgroundColor: "#fff8e1", borderRadius: "8px", border: "1px solid #ffe082" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "8px" }}>
                    <div>
                      <label style={{ fontSize: "11px", color: "#666" }}>Proceso</label>
                      <input placeholder="Nombre del proceso" style={inputStyle} value={dato.proceso || ""} onChange={e => actualizarDato(dato.id, "proceso", e.target.value)} disabled={!esGerencia} />
                    </div>
                    <div>
                      <label style={{ fontSize: "11px", color: "#666" }}>Fecha Realización</label>
                      <input type="date" style={inputStyle} value={dato.fecha_realizacion || ""} onChange={e => actualizarDato(dato.id, "fecha_realizacion", e.target.value)} disabled={!esGerencia} />
                    </div>
                    <div>
                      <label style={{ fontSize: "11px", color: "#666" }}>Fecha Vencimiento</label>
                      <input type="date" style={inputStyle} value={dato.fecha_vencimiento || ""} onChange={e => actualizarDato(dato.id, "fecha_vencimiento", e.target.value)} disabled={!esGerencia} />
                    </div>
                    <div>
                      <label style={{ fontSize: "11px", color: "#666" }}>Valor ($)</label>
                      <input type="number" placeholder="0" style={inputStyle} value={dato.valor || ""} onChange={e => actualizarDato(dato.id, "valor", e.target.value)} disabled={!esGerencia} />
                    </div>
                    <div>
                      <label style={{ fontSize: "11px", color: "#666" }}>Cant. Empleados</label>
                      <input type="number" placeholder="0" style={inputStyle} value={dato.cantidad_empleados || ""} onChange={e => actualizarDato(dato.id, "cantidad_empleados", e.target.value)} disabled={!esGerencia} />
                    </div>
                    {esGerencia && (
                      <div style={{ display: "flex", alignItems: "flex-end" }}>
                        <button onClick={() => eliminarDato(dato.id)} style={{ background: "#f44336", color: "white", border: "none", padding: "10px 12px", borderRadius: "4px", cursor: "pointer", width: "100%" }}>🗑️ Eliminar</button>
                      </div>
                    )}
                  </div>
                  
                  {/* Selector de empleados para notificar - SOLO GERENCIA */}
                  {esGerencia && (
                    <div style={{ marginTop: "10px", padding: "10px", background: "#e8f5e9", borderRadius: "6px", border: "1px solid #a5d6a7" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                        <label style={{ fontSize: "12px", fontWeight: "500", color: "#2e7d32" }}>
                          👥 Empleados que verán este evento en su calendario ({(dato.empleados_notificar || []).length} seleccionados)
                        </label>
                        <button 
                          onClick={() => setMostrarSelectorEmpleados(prev => ({ ...prev, [dato.id]: !prev[dato.id] }))}
                          style={{ background: "#2196F3", color: "white", border: "none", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}
                        >
                          {mostrarSelectorEmpleados[dato.id] ? "▲ Ocultar" : "▼ Seleccionar"}
                        </button>
                      </div>
                      
                      {/* Lista de empleados seleccionados */}
                      {(dato.empleados_notificar || []).length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "8px" }}>
                          {(dato.empleados_notificar || []).map(emp => (
                            <span key={emp.id} style={{ background: "#4CAF50", color: "white", padding: "2px 8px", borderRadius: "12px", fontSize: "11px", display: "flex", alignItems: "center", gap: "4px" }}>
                              {emp.nombre}
                              <button 
                                onClick={() => toggleEmpleadoNotificar(dato.id, emp)}
                                style={{ background: "transparent", border: "none", color: "white", cursor: "pointer", padding: 0, fontSize: "12px" }}
                              >×</button>
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Selector de empleados desplegable */}
                      {mostrarSelectorEmpleados[dato.id] && (
                        <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #ddd", borderRadius: "4px", background: "white" }}>
                          {[...(empleados || [])].filter(emp => emp.activo !== false).sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map(emp => {
                            const nombreEmpleado = emp.nombre || `${emp.nombres || ""} ${emp.apellidos || ""}`.trim();
                            const estaSeleccionado = (dato.empleados_notificar || []).some(e => e.id === emp.id);
                            return (
                              <div 
                                key={emp.id}
                                onClick={() => toggleEmpleadoNotificar(dato.id, emp)}
                                style={{ 
                                  padding: "8px 12px", 
                                  cursor: "pointer", 
                                  borderBottom: "1px solid #eee",
                                  background: estaSeleccionado ? "#e3f2fd" : "white",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center"
                                }}
                              >
                                <span style={{ fontSize: "13px" }}>
                                  {nombreEmpleado}
                                  {emp.cargo && <span style={{ color: "#666", fontSize: "11px", marginLeft: "8px" }}>({emp.cargo})</span>}
                                </span>
                                {estaSeleccionado && <span style={{ color: "#4CAF50", fontWeight: "bold" }}>✓</span>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {(dato.empleados_notificar || []).length === 0 && !mostrarSelectorEmpleados[dato.id] && (
                        <p style={{ color: "#666", fontSize: "11px", margin: 0, fontStyle: "italic" }}>
                          ⚠️ Seleccione los empleados que verán este evento de vencimiento en su calendario.
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* 🔹 Sección de Documentos Adjuntos */}
                  <div style={{ marginTop: "10px", padding: "10px", background: "#fff3e0", borderRadius: "6px", border: "1px solid #ffcc80" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                      <label style={{ fontSize: "12px", fontWeight: "500", color: "#e65100" }}>
                        📎 Documentos Adjuntos ({(dato.documentos || []).length})
                      </label>
                    </div>
                    
                    {/* 🔹 Formulario para adjuntar documento (dos columnas) */}
                    {esGerencia && (
                      <div style={{ 
                        display: "grid", 
                        gridTemplateColumns: "1fr auto", 
                        gap: "8px", 
                        marginBottom: "10px",
                        alignItems: "center"
                      }}>
                        <input
                          type="text"
                          placeholder="Tipo/Nombre del documento (ej: Contrato, Licencia, Permiso...)"
                          value={nombreDocumento[dato.id] || ""}
                          onChange={(e) => setNombreDocumento(prev => ({ ...prev, [dato.id]: e.target.value }))}
                          style={{
                            padding: "8px 10px",
                            borderRadius: "4px",
                            border: "1px solid #ddd",
                            fontSize: "12px",
                            width: "100%"
                          }}
                        />
                        <label style={{ 
                          background: (nombreDocumento[dato.id] || "").trim() ? "#ff9800" : "#bdbdbd",
                          color: "white", 
                          border: "none", 
                          padding: "8px 12px", 
                          borderRadius: "4px", 
                          cursor: (nombreDocumento[dato.id] || "").trim() ? "pointer" : "not-allowed", 
                          fontSize: "11px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          whiteSpace: "nowrap"
                        }}>
                          📤 Adjuntar Archivo
                          <input
                            type="file"
                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                subirDocumentoProceso(dato.id, e.target.files[0]);
                                e.target.value = "";
                              }
                            }}
                            disabled={cargando || !(nombreDocumento[dato.id] || "").trim()}
                          />
                        </label>
                      </div>
                    )}
                    
                    {/* Lista de documentos adjuntos - Tabla con dos columnas */}
                    {(dato.documentos || []).length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        {/* Encabezado de tabla */}
                        <div style={{ 
                          display: "grid", 
                          gridTemplateColumns: "1fr 1fr auto", 
                          gap: "8px", 
                          padding: "6px 10px",
                          background: "#ffe0b2",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: "bold",
                          color: "#e65100"
                        }}>
                          <span>📋 Tipo/Nombre</span>
                          <span>📎 Archivo</span>
                          <span></span>
                        </div>
                        {/* Filas de documentos */}
                        {(dato.documentos || []).map(doc => (
                          <div key={doc.id} style={{ 
                            display: "grid", 
                            gridTemplateColumns: "1fr 1fr auto", 
                            gap: "8px",
                            alignItems: "center",
                            background: "white", 
                            padding: "6px 10px", 
                            borderRadius: "4px",
                            border: "1px solid #e0e0e0"
                          }}>
                            {/* Columna 1: Nombre/Tipo del documento */}
                            <span style={{ fontSize: "12px", fontWeight: "500", color: "#333" }}>
                              {doc.nombre}
                            </span>
                            {/* Columna 2: Archivo adjunto (enlace) */}
                            <a 
                              href={doc.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                fontSize: "12px", 
                                color: "#1976d2", 
                                textDecoration: "none",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                overflow: "hidden"
                              }}
                            >
                              {doc.tipo?.includes("image") ? "🖼️" : doc.tipo?.includes("pdf") ? "📄" : "📁"}
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {doc.nombreArchivo || doc.nombre}
                              </span>
                            </a>
                            {/* Columna 3: Botón eliminar */}
                            {esGerencia && (
                              <button 
                                onClick={() => eliminarDocumentoProceso(dato.id, doc.id)}
                                style={{ 
                                  background: "#f44336", 
                                  color: "white", 
                                  border: "none", 
                                  padding: "2px 6px", 
                                  borderRadius: "3px", 
                                  cursor: "pointer",
                                  fontSize: "11px"
                                }}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: "#888", fontSize: "11px", margin: 0, fontStyle: "italic" }}>
                        No hay documentos adjuntos. {esGerencia ? "Haga clic en 'Adjuntar' para añadir documentos." : ""}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              {formSede.datos_importantes.length === 0 && esGerencia && (
                <p style={{ color: "#888", fontSize: "13px", fontStyle: "italic" }}>No hay procesos agregados. Haga clic en "+ Agregar Proceso" para añadir uno.</p>
              )}
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>Observaciones</label>
              <textarea style={{ ...inputStyle, minHeight: "60px" }} value={formSede.observaciones} onChange={e => setFormSede(p => ({ ...p, observaciones: e.target.value }))} />
            </div>

            <div style={{ display: "flex", gap: "10px", marginTop: "16px", justifyContent: "flex-end" }}>
              <button onClick={resetFormSede} style={{ padding: "10px 20px", borderRadius: "6px", border: "1px solid #ddd", background: "white", cursor: "pointer" }}>Limpiar</button>
              <button onClick={guardarInfoSede} style={{ padding: "10px 20px", borderRadius: "6px", border: "none", background: "linear-gradient(135deg, #ff6b35, #ffc53d)", color: "white", cursor: "pointer", fontWeight: "bold" }}>💾 Guardar</button>
            </div>
          </div>
          )}

          {/* Lista de Sedes con Info */}
          <div style={{ background: "white", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <h4 style={{ marginTop: 0 }}>Información Guardada por Sede</h4>
            {infoSedes.length === 0 ? (
              <p style={{ textAlign: "center", color: "#666" }}>No hay información de sedes registrada</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {infoSedes.map(info => (
                  <div key={info.id} style={{
                    padding: "14px", background: "#f8f9fa", borderRadius: "8px",
                    borderLeft: "4px solid #2196F3"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "bold", fontSize: "16px", marginBottom: "8px" }}>🏢 {info.sede_nombre}</div>
                        {/* NIT, Representante Legal, Correo */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", fontSize: "13px", marginBottom: "8px" }}>
                          {info.nit && <div>🆔 NIT: <strong>{info.nit}</strong></div>}
                          {info.representante_legal && <div>👤 Rep. Legal: <strong>{info.representante_legal}</strong></div>}
                          {info.correo_notificaciones && <div style={{ gridColumn: "span 2" }}>📧 Correo: <a href={`mailto:${info.correo_notificaciones}`} style={{ color: "#1976d2" }}>{info.correo_notificaciones}</a></div>}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", fontSize: "13px" }}>
                          {info.telefono_local && <div>📞 Local: {info.telefono_local}</div>}
                          {info.telefono_admin && <div>📞 Admin: {info.telefono_admin}</div>}
                        </div>
                        {/* Bancos - Múltiples */}
                        {((info.bancos && info.bancos.length > 0) || info.banco) && (
                          <div style={{ marginTop: "8px" }}>
                            <strong style={{ fontSize: "12px" }}>🏦 Bancos:</strong>
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "4px" }}>
                              {info.bancos && info.bancos.length > 0 ? (
                                info.bancos.map((b, i) => (
                                  <span key={i} style={{ background: "#e3f2fd", padding: "4px 8px", borderRadius: "4px", fontSize: "12px" }}>
                                    {b.banco} - {b.tipo_cuenta} - {b.numero_cuenta}
                                  </span>
                                ))
                              ) : info.banco && (
                                <span style={{ background: "#e3f2fd", padding: "4px 8px", borderRadius: "4px", fontSize: "12px" }}>
                                  {info.banco} - {info.tipo_cuenta} - {info.numero_cuenta}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {info.claves && info.claves.length > 0 && (
                          <div style={{ marginTop: "8px" }}>
                            <strong style={{ fontSize: "12px" }}>🔐 Claves:</strong>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                              {info.claves.map((c, i) => (
                                <span key={i} style={{ background: "#e3f2fd", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }}>{c.nombre}: {c.valor}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {info.datos_importantes && info.datos_importantes.length > 0 && (
                          <div style={{ marginTop: "8px" }}>
                            <strong style={{ fontSize: "12px" }}>📋 Procesos:</strong>
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "4px" }}>
                              {info.datos_importantes.map((d, i) => {
                                const fechaVenc = d.fecha_vencimiento ? new Date(d.fecha_vencimiento) : null;
                                const hoy = new Date();
                                const diasRestantes = fechaVenc ? Math.ceil((fechaVenc - hoy) / (1000 * 60 * 60 * 24)) : null;
                                const colorVencimiento = diasRestantes !== null && diasRestantes <= 30 ? (diasRestantes <= 7 ? "#f44336" : "#ff9800") : "#4CAF50";
                                
                                return (
                                  <div key={i} style={{ background: "#fff8e1", padding: "8px 10px", borderRadius: "6px", fontSize: "12px", borderLeft: `3px solid ${colorVencimiento}` }}>
                                    <div style={{ fontWeight: "bold", marginBottom: "4px" }}>{d.proceso || d.titulo || "Sin nombre"}</div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", color: "#666" }}>
                                      {d.fecha_realizacion && <span>📅 Realizado: {d.fecha_realizacion}</span>}
                                      {d.fecha_vencimiento && (
                                        <span style={{ color: colorVencimiento, fontWeight: "bold" }}>
                                          ⏰ Vence: {d.fecha_vencimiento} 
                                          {diasRestantes !== null && ` (${diasRestantes > 0 ? diasRestantes + " días" : "VENCIDO"})`}
                                        </span>
                                      )}
                                      {d.valor && <span>💰 ${Number(d.valor).toLocaleString()}</span>}
                                      {d.cantidad_empleados && <span>👥 {d.cantidad_empleados} empleados</span>}
                                    </div>
                                    {/* Mostrar empleados asignados para notificación */}
                                    {d.empleados_notificar && d.empleados_notificar.length > 0 && (
                                      <div style={{ marginTop: "4px", fontSize: "11px", color: "#2e7d32" }}>
                                        📬 Notifica a: {d.empleados_notificar.map(e => e.nombre).join(", ")}
                                      </div>
                                    )}
                                    {/* 🔹 Mostrar documentos adjuntos */}
                                    {d.documentos && d.documentos.length > 0 && (
                                      <div style={{ marginTop: "4px", fontSize: "11px" }}>
                                        <span style={{ color: "#e65100" }}>📎 Documentos: </span>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "2px" }}>
                                          {d.documentos.map((doc, idx) => (
                                            <a 
                                              key={idx}
                                              href={doc.url} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              style={{ 
                                                background: "#fff3e0", 
                                                color: "#e65100", 
                                                padding: "2px 6px", 
                                                borderRadius: "3px", 
                                                textDecoration: "none",
                                                fontSize: "10px",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "3px"
                                              }}
                                            >
                                              {doc.tipo?.includes("image") ? "🖼️" : doc.tipo?.includes("pdf") ? "📄" : "📁"}
                                              {doc.nombre?.length > 15 ? doc.nombre.substring(0, 15) + "..." : doc.nombre}
                                            </a>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      {esGerencia && (
                        <button onClick={() => editarInfoSede(info)} style={{ background: "#2196F3", color: "white", border: "none", padding: "6px 10px", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}>✏️ Editar</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================== PLANES DE TRABAJO (CHECKLISTS) ====================== */

// Categorías predefinidas de tareas para planes de trabajo
const CATEGORIAS_TAREAS_PLAN = [
  "REVISION DE PARTE EXTERNA (LIMPIEZA DE VIDRIOS, LIMPIEZA DE COMEDOR, LIMPIEZA DE BAÑOS, BASURAS, TELEVISORES)",
  "REVISION DE NEVERAS COMEDOR (LIMPIEZA DE NEVERAS, ORGANIZACION DE PRODUCTOS, LIMPIEZA DE CAUCHOS)",
  "REVISION DE INVENTARIO COMEDOR (REVISION DE GASEOSAS, CERVEZAS, AGUAS)",
  "REVISION DE FECHAS DE VENCIMIENTO (PULPAS, HELADOS, MILO, CAFE)",
  "REVISION DE ASEO (BAÑOS, PISOS, CANECAS, MESONES, BARRA)",
  "REVISION DE WHATSAPP (QUEJAS, CANCELACIONES, TIEMPOS DE RESPUESTA)",
  "REALIZACION DE ARQUEO (ARQUEO SORPRESA)",
  "REVISION DE BODEGA EXTERNA (BODEGA CON INVENTARIO EXTERNA, ORGANIZACION Y ASEO)",
  "REVISION DE ATENCION A CLIENTES (SERVICIO AL CLIENTE, PEDIDOS CANCELADOS O ANULADOS)",
  "REVISION USO DE CELULAR (USO EXCESIVO DE CELULAR)",
  "VERIFICACION DE FACTURAS PENDIENTES (FACTURAS DE DIAS ANTERIORES Y FACTURAS FIRMADAS)",
  "REVISION NEVERAS COCINA (LIMPIEZA DE NEVERAS, ORGANIZACION DE PRODUCTOS, LIMPIEZA DE CAUCHOS)",
  "REVISION INVENTARIO COCINA (CARNES, VERDURAS, ESPECIES, ACEITE, MARGARINA, PANES, ASEO)",
  "REVISION DE INVENTARIO DE UTENSILIOS DE COCINA (CUCHILLOS, COCAS DE PAPA, ESPATULAS, OLLAS, LADRILLOS PARILLA)",
  "REVISION FECHAS DE VENCIMIENTO (CARNES, VERDURAS, JAMON, PANES, ACEITES, MARGARINA, AZUCARES, SALCHICHAS, RIPIO, LECHE, HUEVOS, SALSAS)",
  "REVISION DE ASEO (ASEO PLANCHA, PARILLA, UTENSILIOS DE COCINA, PISOS, MESONES, FREIDOR, MAQUINAS, COCINA GENERAL)",
  "REVISION BODEGA INTERNA (ASEO Y ORGANIZACION DE BODEGA)",
  "REVISION DE ROTACION PRODUCTOS (REVISAR PRODUCTOS ANTIGUOS VS NUEVOS)",
  "REVISION DE PROCESOS (REVISION DE PROCESOS DE PRODUCTOS)",
  "REVISION DE CAMARAS DE SEGURIDAD (CAMARAS DE SEGURIDAD)"
];

function PlanesTrabajoAdmin({ sedes = [], empleados = [], filtroEmpresa = null, tienePermiso = () => true, usuarioActual = null, empleadoActual = null }) {
  const [planes, setPlanes] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [modo, setModo] = useState("lista"); // "lista" | "crear" | "editar" | "diligenciar" | "revisar" | "bitacora"
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [mensaje, setMensaje] = useState({ tipo: "", texto: "" });
  const [notificacionesPlan, setNotificacionesPlan] = useState([]);
  
  // Filtros para bitácora y lista
  const [filtros, setFiltros] = useState({
    sede_id: "",
    empleado_id: "",
    mes: "",
    anio: new Date().getFullYear().toString(),
    estado: ""
  });
  const [bitacora, setBitacora] = useState([]);
  
  // Formulario para nuevo/editar plan
  const [formPlan, setFormPlan] = useState({
    nombre: "",
    descripcion: "",
    sede_id: "",
    empleado_asignado_id: "",
    fecha_limite: "",
    tareas: [],
    observaciones_generales: "",
    observaciones_gerencia: ""
  });

  // Verificar si el usuario tiene perfil Gerencia
  const esGerencia = useMemo(() => {
    if (!usuarioActual) return false;
    const perfil = (usuarioActual.perfil || usuarioActual.rol || "").toLowerCase();
    return perfil.includes("gerencia") || perfil.includes("gerente") || perfil.includes("admin");
  }, [usuarioActual]);

  // Verificar si el usuario es el empleado asignado al plan
  const esEmpleadoAsignado = useCallback((plan) => {
    if (!plan) return false;
    // Verificar por ID de empleado (tabla empleados)
    if (empleadoActual?.id && plan.empleado_asignado_id === empleadoActual.id) return true;
    // Verificar por ID de usuario (tabla usuarios)
    if (usuarioActual?.id && plan.empleado_asignado_id === usuarioActual.id) return true;
    // Verificar por nombre del empleado
    const nombreEmpleado = (empleadoActual?.nombre || empleadoActual?.nombres || "").toLowerCase().trim();
    const nombreAsignado = (plan.empleado_asignado_nombre || "").toLowerCase().trim();
    if (nombreEmpleado && nombreAsignado && nombreAsignado.includes(nombreEmpleado)) return true;
    return false;
  }, [usuarioActual, empleadoActual]);

  useEffect(() => {
    cargarPlanes();
    cargarNotificaciones();
  }, [filtroEmpresa]);

  const cargarPlanes = async () => {
    setCargando(true);
    try {
      const data = await cargarPlanesTrabajoDeSupabase(filtroEmpresa);
      setPlanes(data || []);
    } catch (error) {
      console.error("Error cargando planes:", error);
      setMensaje({ tipo: "error", texto: "Error al cargar planes de trabajo" });
    }
    setCargando(false);
  };

  const cargarNotificaciones = async () => {
    if (usuarioActual?.id) {
      const notifs = await cargarNotificacionesPlanesDeSupabase(usuarioActual.id, filtroEmpresa);
      setNotificacionesPlan(notifs);
    }
  };

  const cargarBitacora = async () => {
    setCargando(true);
    try {
      const data = await cargarBitacoraPlanesDeSupabase(filtros, filtroEmpresa);
      setBitacora(data || []);
    } catch (error) {
      console.error("Error cargando bitácora:", error);
      setMensaje({ tipo: "error", texto: "Error al cargar bitácora" });
    }
    setCargando(false);
  };

  const iniciarNuevoPlan = () => {
    // Solo usuarios con perfil Gerencia pueden crear planes
    if (!esGerencia) {
      setMensaje({ tipo: "error", texto: "No tiene permisos para crear planes de trabajo. Solo usuarios con perfil Gerencia pueden crear planes." });
      return;
    }
    setFormPlan({
      nombre: "",
      descripcion: "",
      sede_id: "",
      empleado_asignado_id: "",
      fecha_limite: new Date().toISOString().split('T')[0],
      tareas: [
        { id: uid(), nombre: "", estado: "pendiente", observacion: "", evidencias: [] }
      ],
      observaciones_generales: ""
    });
    setPlanSeleccionado(null);
    setModo("crear");
  };

  const agregarTarea = () => {
    setFormPlan({
      ...formPlan,
      tareas: [...formPlan.tareas, { id: uid(), nombre: "", estado: "pendiente", observacion: "", evidencias: [] }]
    });
  };

  // Función para subir evidencias de una tarea
  const subirEvidenciaTarea = async (index, file) => {
    if (!file) return;
    
    // Validar tipo de archivo
    const tiposPermitidos = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!tiposPermitidos.includes(file.type)) {
      setMensaje({ tipo: "error", texto: "Tipo de archivo no permitido. Use imágenes o documentos (PDF, DOC)." });
      return;
    }
    
    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMensaje({ tipo: "error", texto: "El archivo es demasiado grande. Máximo 5MB." });
      return;
    }
    
    setCargando(true);
    try {
      const tareaId = formPlan.tareas[index].id || uid();
      const extension = file.name.includes(".") ? file.name.substring(file.name.lastIndexOf(".")) : "";
      const nombreArchivo = `planes-trabajo/${tareaId}/${Date.now()}_${uid()}${extension}`;
      
      const { error: uploadError } = await supabase.storage
        .from(SUPABASE_BUCKET_DOCS)
        .upload(nombreArchivo, file, {
          cacheControl: "3600",
          upsert: false,
          contentType: file.type
        });
      
      if (uploadError) {
        throw new Error(uploadError.message);
      }
      
      const { data: urlData } = supabase.storage
        .from(SUPABASE_BUCKET_DOCS)
        .getPublicUrl(nombreArchivo);
      
      const nuevaEvidencia = {
        id: uid(),
        nombre: file.name,
        url: urlData.publicUrl,
        tipo: file.type,
        path: nombreArchivo,
        fechaSubida: new Date().toISOString()
      };
      
      const nuevasTareas = [...formPlan.tareas];
      nuevasTareas[index] = {
        ...nuevasTareas[index],
        evidencias: [...(nuevasTareas[index].evidencias || []), nuevaEvidencia]
      };
      setFormPlan({ ...formPlan, tareas: nuevasTareas });
      setMensaje({ tipo: "exito", texto: "Evidencia subida correctamente" });
    } catch (error) {
      console.error("Error subiendo evidencia:", error);
      setMensaje({ tipo: "error", texto: "Error al subir la evidencia" });
    }
    setCargando(false);
  };

  // Función para eliminar evidencia de una tarea
  const eliminarEvidenciaTarea = async (tareaIndex, evidenciaId) => {
    if (!window.confirm("¿Eliminar esta evidencia?")) return;
    
    const tarea = formPlan.tareas[tareaIndex];
    const evidencia = tarea.evidencias?.find(e => e.id === evidenciaId);
    
    if (evidencia?.path) {
      try {
        await supabase.storage.from(SUPABASE_BUCKET_DOCS).remove([evidencia.path]);
      } catch (error) {
        console.error("Error eliminando archivo:", error);
      }
    }
    
    const nuevasTareas = [...formPlan.tareas];
    nuevasTareas[tareaIndex] = {
      ...nuevasTareas[tareaIndex],
      evidencias: (nuevasTareas[tareaIndex].evidencias || []).filter(e => e.id !== evidenciaId)
    };
    setFormPlan({ ...formPlan, tareas: nuevasTareas });
  };

  const eliminarTarea = (index) => {
    const nuevasTareas = formPlan.tareas.filter((_, i) => i !== index);
    setFormPlan({ ...formPlan, tareas: nuevasTareas });
  };

  const actualizarTarea = (index, campo, valor) => {
    const nuevasTareas = [...formPlan.tareas];
    nuevasTareas[index] = { ...nuevasTareas[index], [campo]: valor };
    setFormPlan({ ...formPlan, tareas: nuevasTareas });
  };

  const guardarPlan = async () => {
    if (!formPlan.nombre.trim()) {
      setMensaje({ tipo: "error", texto: "El nombre del plan es requerido" });
      return;
    }
    if (!formPlan.sede_id) {
      setMensaje({ tipo: "error", texto: "Debe seleccionar una sede" });
      return;
    }
    if (!formPlan.empleado_asignado_id) {
      setMensaje({ tipo: "error", texto: "Debe asignar un empleado al plan" });
      return;
    }
    if (formPlan.tareas.filter(t => t.nombre.trim()).length === 0) {
      setMensaje({ tipo: "error", texto: "Debe agregar al menos una tarea" });
      return;
    }

    setCargando(true);
    try {
      const tareasValidas = formPlan.tareas.filter(t => t.nombre.trim());
      const tareasCompletadas = tareasValidas.filter(t => t.completada).length;
      const totalTareas = tareasValidas.length;
      const porcentaje = totalTareas > 0 ? Math.round((tareasCompletadas / totalTareas) * 100) : 0;
      
      let estado = "pendiente";
      if (porcentaje === 100) estado = "completado";
      else if (porcentaje > 0) estado = "en_progreso";

      const empleadoAsignado = empleados.find(e => e.id === formPlan.empleado_asignado_id);
      const sedeSeleccionada = sedes.find(s => s.id === formPlan.sede_id);

      const planData = {
        id: planSeleccionado?.id || uid(),
        nombre: formPlan.nombre,
        descripcion: formPlan.descripcion,
        sede_id: formPlan.sede_id,
        sede_nombre: sedeSeleccionada?.nombre || "",
        empleado_asignado_id: formPlan.empleado_asignado_id,
        empleado_asignado_nombre: empleadoAsignado ? `${empleadoAsignado.nombres || ""} ${empleadoAsignado.apellidos || ""}`.trim() : "",
        fecha_limite: formPlan.fecha_limite,
        tareas: JSON.stringify(tareasValidas),
        estado: estado,
        porcentaje_completado: porcentaje,
        observaciones_generales: formPlan.observaciones_generales,
        cerrado: false,
        cerrado_por: null,
        fecha_cierre: null,
        created_at: planSeleccionado?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const result = await guardarPlanTrabajoEnSupabase(planData, filtroEmpresa);
      
      if (result) {
        // Registrar en bitácora
        await guardarBitacoraPlanTrabajo({
          plan_id: planData.id,
          plan_nombre: planData.nombre,
          accion: planSeleccionado ? "actualizado" : "creado",
          descripcion: planSeleccionado 
            ? `Plan actualizado. Asignado a: ${planData.empleado_asignado_nombre}` 
            : `Plan creado y asignado a: ${planData.empleado_asignado_nombre}`,
          empleado_id: formPlan.empleado_asignado_id,
          empleado_nombre: planData.empleado_asignado_nombre,
          sede_id: formPlan.sede_id,
          sede_nombre: planData.sede_nombre,
          datos_adicionales: { tareas_count: tareasValidas.length, fecha_limite: formPlan.fecha_limite }
        }, filtroEmpresa);

        // Crear notificación para el empleado asignado
        if (!planSeleccionado || planSeleccionado.empleado_asignado_id !== formPlan.empleado_asignado_id) {
          await crearNotificacionPlanTrabajo({
            plan_id: planData.id,
            plan_nombre: planData.nombre,
            empleado_id: formPlan.empleado_asignado_id,
            tipo: "asignacion",
            mensaje: `📋 Se te ha asignado el plan de trabajo: "${planData.nombre}". Fecha límite: ${formPlan.fecha_limite}`
          }, filtroEmpresa);
        }

        // Siempre crear/actualizar evento al calendario del empleado asignado (usando upsert)
        const empleadoAsignado = empleados.find(e => e.id === formPlan.empleado_asignado_id);
        const eventoCalendario = {
          id: planData.id, // Usar el mismo UUID del plan como ID del evento
          titulo: `📋 Vence Plan: ${planData.nombre}`,
          tipo: "plan_trabajo",
          fecha: formPlan.fecha_limite,
          hora_inicio: "08:00",
          hora_fin: "17:00",
          sede_id: formPlan.sede_id,
          sede_nombre: planData.sede_nombre,
          descripcion: `Plan de trabajo asignado: ${planData.nombre}. ${planData.descripcion || ""}. Fecha límite: ${formPlan.fecha_limite}`,
          observaciones: `Total tareas: ${tareasValidas.length}. Asignado a: ${planData.empleado_asignado_nombre}`,
          creador_id: usuarioActual?.id || null,
          creador_nombre: usuarioActual?.nombre || "Sistema",
          empleado_asignado_id: formPlan.empleado_asignado_id,
          invitados: [{ id: formPlan.empleado_asignado_id, nombre: planData.empleado_asignado_nombre }],
          estado: "pendiente"
        };
        console.log("📅 Creando evento de calendario para plan:", eventoCalendario);
        await guardarEventoCalendarioEnSupabase(eventoCalendario, filtroEmpresa);

        setMensaje({ tipo: "exito", texto: planSeleccionado ? "Plan actualizado correctamente" : "Plan creado y notificación enviada al empleado" });
        await cargarPlanes();
        setModo("lista");
      } else {
        setMensaje({ tipo: "error", texto: "Error al guardar el plan" });
      }
    } catch (error) {
      console.error("Error guardando plan:", error);
      setMensaje({ tipo: "error", texto: "Error al guardar el plan" });
    }
    setCargando(false);
  };

  const abrirParaDiligenciar = (plan) => {
    const tareas = typeof plan.tareas === 'string' ? JSON.parse(plan.tareas) : (plan.tareas || []);
    // Normalizar tareas para compatibilidad
    const tareasNormalizadas = tareas.map(t => ({
      ...t,
      estado: t.estado || (t.completada ? "terminado" : "pendiente"),
      evidencias: t.evidencias || []
    }));
    setFormPlan({
      nombre: plan.nombre || "",
      descripcion: plan.descripcion || "",
      sede_id: plan.sede_id || "",
      empleado_asignado_id: plan.empleado_asignado_id || "",
      fecha_limite: plan.fecha_limite || "",
      tareas: tareasNormalizadas,
      observaciones_generales: plan.observaciones_generales || ""
    });
    setPlanSeleccionado(plan);
    setModo("diligenciar");
  };

  const guardarDiligenciamiento = async (enviarARevision = false) => {
    setCargando(true);
    try {
      const tareasValidas = formPlan.tareas.filter(t => t.nombre.trim());
      // Calcular progreso basado en estados de tareas
      const tareasTerminadas = tareasValidas.filter(t => t.estado === "terminado" || t.completada).length;
      const tareasEnProceso = tareasValidas.filter(t => t.estado === "en_proceso").length;
      const totalTareas = tareasValidas.length;
      
      // El porcentaje máximo es 95% hasta que Gerencia apruebe (100% solo con aprobación definitiva)
      let porcentajeReal = totalTareas > 0 ? Math.round((tareasTerminadas / totalTareas) * 100) : 0;
      let porcentajeMostrado = porcentajeReal === 100 ? 95 : porcentajeReal;
      
      let estado = planSeleccionado.estado || "pendiente";
      
      // Si está devuelto, permitir editarlo
      if (planSeleccionado.estado === "devuelto") {
        estado = "en_progreso";
      } else if (enviarARevision) {
        // Verificar que todas las tareas estén terminadas para enviar a revisión
        if (porcentajeReal < 100) {
          setMensaje({ tipo: "error", texto: "Debe completar todas las tareas antes de enviar a revisión" });
          setCargando(false);
          return;
        }
        estado = "pendiente_aprobacion";
        porcentajeMostrado = 95;
      } else {
        // Solo guardar progreso sin enviar a revisión
        if (porcentajeReal === 100) estado = "pendiente_aprobacion";
        else if (porcentajeReal > 0 || tareasEnProceso > 0) estado = "en_progreso";
        else estado = "pendiente";
      }

      const planData = {
        ...planSeleccionado,
        tareas: JSON.stringify(tareasValidas),
        estado: estado,
        porcentaje_completado: porcentajeMostrado,
        observaciones_generales: formPlan.observaciones_generales,
        updated_at: new Date().toISOString()
      };

      const result = await guardarPlanTrabajoEnSupabase(planData, filtroEmpresa);
      
      if (result) {
        // Notificar a gerencia si se envió a revisión
        if (enviarARevision) {
          await crearNotificacionPlanTrabajo({
            plan_id: planData.id,
            plan_nombre: planData.nombre,
            empleado_id: "gerencia", // Notificación general para gerencia
            tipo: "revision",
            mensaje: `📋 El plan "${planData.nombre}" está listo para revisión. Empleado: ${planSeleccionado.empleado_asignado_nombre}`
          }, filtroEmpresa);
          setMensaje({ tipo: "exito", texto: "Plan enviado a revisión. Esperando aprobación de Gerencia." });
        } else {
          setMensaje({ tipo: "exito", texto: "Plan guardado correctamente" });
        }
        
        await cargarPlanes();
        setModo("lista");
      } else {
        setMensaje({ tipo: "error", texto: "Error al guardar el plan" });
      }
    } catch (error) {
      console.error("Error guardando diligenciamiento:", error);
      setMensaje({ tipo: "error", texto: "Error al guardar" });
    }
    setCargando(false);
  };

  // Aprobar plan definitivamente (solo Gerencia)
  const aprobarPlanDefinitivo = async () => {
    if (!formPlan.observaciones_gerencia?.trim()) {
      if (!window.confirm("¿Aprobar sin observaciones de gerencia?")) return;
    }
    
    setCargando(true);
    try {
      const tareasValidas = formPlan.tareas.filter(t => t.nombre.trim());
      
      const planData = {
        ...planSeleccionado,
        tareas: JSON.stringify(tareasValidas),
        estado: "aprobado",
        porcentaje_completado: 100,
        cerrado: true,
        cerrado_por: usuarioActual?.id || null,
        cerrado_por_nombre: usuarioActual?.nombre || "",
        fecha_cierre: new Date().toISOString(),
        observaciones_gerencia: formPlan.observaciones_gerencia || "",
        aprobado_por: usuarioActual?.id || null,
        aprobado_por_nombre: usuarioActual?.nombre || "",
        fecha_aprobacion: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const result = await guardarPlanTrabajoEnSupabase(planData, filtroEmpresa);
      
      if (result) {
        // Registrar en bitácora SOLO cuando se aprueba definitivamente
        await guardarBitacoraPlanTrabajo({
          plan_id: planData.id,
          plan_nombre: planData.nombre,
          accion: "aprobado",
          descripcion: `Plan aprobado definitivamente por: ${usuarioActual?.nombre || "Gerencia"}`,
          empleado_id: planSeleccionado.empleado_asignado_id,
          empleado_nombre: planSeleccionado.empleado_asignado_nombre,
          sede_id: planSeleccionado.sede_id,
          sede_nombre: planSeleccionado.sede_nombre,
          datos_adicionales: { 
            aprobado_por: usuarioActual?.nombre,
            observaciones_gerencia: formPlan.observaciones_gerencia,
            tareas: tareasValidas,
            observaciones_empleado: formPlan.observaciones_generales,
            fecha_limite: planSeleccionado.fecha_limite
          }
        }, filtroEmpresa);

        // Notificar al empleado
        await crearNotificacionPlanTrabajo({
          plan_id: planData.id,
          plan_nombre: planData.nombre,
          empleado_id: planSeleccionado.empleado_asignado_id,
          tipo: "aprobacion",
          mensaje: `✅ ¡Excelente! El plan "${planData.nombre}" ha sido APROBADO por gerencia.`
        }, filtroEmpresa);

        setMensaje({ tipo: "exito", texto: "Plan aprobado definitivamente" });
        await cargarPlanes();
        setModo("lista");
      } else {
        setMensaje({ tipo: "error", texto: "Error al aprobar el plan" });
      }
    } catch (error) {
      console.error("Error aprobando plan:", error);
      setMensaje({ tipo: "error", texto: "Error al aprobar el plan" });
    }
    setCargando(false);
  };

  // Devolver plan para corrección (solo Gerencia)
  const devolverPlan = async () => {
    if (!formPlan.observaciones_gerencia?.trim()) {
      setMensaje({ tipo: "error", texto: "Debe agregar observaciones indicando qué corregir" });
      return;
    }
    
    setCargando(true);
    try {
      const tareasValidas = formPlan.tareas.filter(t => t.nombre.trim());
      
      const planData = {
        ...planSeleccionado,
        tareas: JSON.stringify(tareasValidas),
        estado: "devuelto",
        porcentaje_completado: 80, // Reducir porcentaje al devolver
        observaciones_gerencia: formPlan.observaciones_gerencia,
        devuelto_por: usuarioActual?.id || null,
        devuelto_por_nombre: usuarioActual?.nombre || "",
        fecha_devolucion: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const result = await guardarPlanTrabajoEnSupabase(planData, filtroEmpresa);
      
      if (result) {
        // Notificar al empleado que debe corregir
        await crearNotificacionPlanTrabajo({
          plan_id: planData.id,
          plan_nombre: planData.nombre,
          empleado_id: planSeleccionado.empleado_asignado_id,
          tipo: "devolucion",
          mensaje: `⚠️ El plan "${planData.nombre}" fue DEVUELTO para corrección. Revisa las observaciones de gerencia.`
        }, filtroEmpresa);

        setMensaje({ tipo: "exito", texto: "Plan devuelto al empleado para corrección" });
        await cargarPlanes();
        setModo("lista");
      } else {
        setMensaje({ tipo: "error", texto: "Error al devolver el plan" });
      }
    } catch (error) {
      console.error("Error devolviendo plan:", error);
      setMensaje({ tipo: "error", texto: "Error al devolver el plan" });
    }
    setCargando(false);
  };

  const abrirParaRevisar = (plan) => {
    const tareas = typeof plan.tareas === 'string' ? JSON.parse(plan.tareas) : (plan.tareas || []);
    // Normalizar tareas para compatibilidad
    const tareasNormalizadas = tareas.map(t => ({
      ...t,
      estado: t.estado || (t.completada ? "terminado" : "pendiente"),
      evidencias: t.evidencias || []
    }));
    setFormPlan({
      nombre: plan.nombre || "",
      descripcion: plan.descripcion || "",
      sede_id: plan.sede_id || "",
      empleado_asignado_id: plan.empleado_asignado_id || "",
      fecha_limite: plan.fecha_limite || "",
      tareas: tareasNormalizadas,
      observaciones_generales: plan.observaciones_generales || "",
      observaciones_gerencia: plan.observaciones_gerencia || ""
    });
    setPlanSeleccionado(plan);
    setModo("revisar");
  };

  // Función para descargar el plan aprobado como PDF/HTML
  const descargarPlanAprobado = (plan) => {
    let tareas = [];
    try {
      tareas = typeof plan.tareas === 'string' ? JSON.parse(plan.tareas) : (plan.tareas || []);
      // Asegurarse de que sea un array
      if (!Array.isArray(tareas)) tareas = [];
    } catch (e) {
      console.error("Error parseando tareas:", e);
      tareas = [];
    }
    
    const getEstadoInfo = (estado) => {
      switch(estado) {
        case "terminado": return { texto: "✓ Terminado", color: "#27ae60", bg: "#e8f8f0" };
        case "en_proceso": return { texto: "⏳ En Proceso", color: "#f39c12", bg: "#fff8e1" };
        default: return { texto: "○ Pendiente", color: "#95a5a6", bg: "#f8f9fa" };
      }
    };
    
    const htmlContent = `
<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Plan de Trabajo - ${plan.nombre}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 40px; max-width: 900px; margin: 0 auto; line-height: 1.5; }
    .header { text-align: center; border-bottom: 3px solid #27ae60; padding-bottom: 20px; margin-bottom: 30px; }
    .logo { font-size: 24px; font-weight: bold; color: #27ae60; }
    .aprobado-badge { background: #27ae60; color: white; padding: 8px 20px; border-radius: 20px; display: inline-block; margin-top: 10px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 30px; }
    .info-item { padding: 12px; background: #f8f9fa; border-radius: 5px; border-left: 3px solid #3498db; }
    .info-label { font-weight: bold; color: #666; font-size: 12px; text-transform: uppercase; }
    .info-value { font-size: 14px; margin-top: 5px; color: #333; }
    .section { margin-bottom: 25px; }
    .section-title { font-size: 16px; font-weight: bold; color: #333; border-bottom: 2px solid #3498db; padding-bottom: 8px; margin-bottom: 15px; }
    .tarea { padding: 15px; margin-bottom: 12px; border-radius: 8px; background: #f8f9fa; page-break-inside: avoid; }
    .tarea-header { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 10px; }
    .tarea-nombre { font-weight: bold; flex: 1; font-size: 14px; }
    .tarea-estado { padding: 4px 12px; border-radius: 12px; font-size: 11px; font-weight: bold; white-space: nowrap; }
    .tarea-obs { margin-top: 12px; padding: 12px; background: white; border-radius: 5px; font-size: 13px; border: 1px solid #e0e0e0; }
    .tarea-obs strong { color: #2c3e50; }
    .evidencias-lista { margin-top: 8px; padding-left: 20px; }
    .evidencia-item { margin-bottom: 5px; color: #7f8c8d; }
    .observaciones { padding: 15px; background: #e8f5e9; border-radius: 8px; border-left: 4px solid #27ae60; }
    .observaciones-gerencia { background: #e3f2fd; border-left-color: #2196f3; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #ddd; text-align: center; color: #666; font-size: 12px; }
    .firma { margin-top: 30px; display: grid; grid-template-columns: 1fr 1fr; gap: 30px; }
    .firma-item { text-align: center; padding-top: 40px; border-top: 1px solid #333; }
    .resumen { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; border-radius: 10px; margin-bottom: 25px; }
    .resumen h3 { margin: 0 0 15px 0; }
    .resumen-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
    .resumen-item { text-align: center; }
    .resumen-num { font-size: 24px; font-weight: bold; }
    .resumen-label { font-size: 12px; opacity: 0.9; }
    @media print { 
      body { padding: 20px; } 
      .tarea { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo">📋 PLAN DE TRABAJO - BIGBURGUER</div>
    <h1 style="margin: 10px 0;">${plan.nombre || "Sin nombre"}</h1>
    <div class="aprobado-badge">✅ APROBADO</div>
  </div>

  <div class="info-grid">
    <div class="info-item">
      <div class="info-label">SEDE</div>
      <div class="info-value">${plan.sede_nombre || "N/A"}</div>
    </div>
    <div class="info-item">
      <div class="info-label">EMPLEADO ASIGNADO</div>
      <div class="info-value">${plan.empleado_asignado_nombre || "N/A"}</div>
    </div>
    <div class="info-item">
      <div class="info-label">FECHA LÍMITE</div>
      <div class="info-value">${plan.fecha_limite || "N/A"}</div>
    </div>
    <div class="info-item">
      <div class="info-label">FECHA DE APROBACIÓN</div>
      <div class="info-value">${plan.fecha_aprobacion ? new Date(plan.fecha_aprobacion).toLocaleDateString("es-CO") : (plan.fecha_cierre ? new Date(plan.fecha_cierre).toLocaleDateString("es-CO") : "N/A")}</div>
    </div>
    <div class="info-item">
      <div class="info-label">APROBADO POR</div>
      <div class="info-value">${plan.aprobado_por_nombre || plan.cerrado_por_nombre || "Gerencia"}</div>
    </div>
    <div class="info-item">
      <div class="info-label">PROGRESO FINAL</div>
      <div class="info-value" style="color: #27ae60; font-weight: bold;">100% Completado</div>
    </div>
  </div>

  <div class="resumen">
    <h3>📊 Resumen del Plan</h3>
    <div class="resumen-grid">
      <div class="resumen-item">
        <div class="resumen-num">${tareas.length}</div>
        <div class="resumen-label">Total Tareas</div>
      </div>
      <div class="resumen-item">
        <div class="resumen-num">${tareas.filter(t => t.estado === "terminado" || t.completada).length}</div>
        <div class="resumen-label">Completadas</div>
      </div>
      <div class="resumen-item">
        <div class="resumen-num">${tareas.filter(t => t.observacion).length}</div>
        <div class="resumen-label">Con Observaciones</div>
      </div>
    </div>
  </div>

  ${plan.descripcion ? `
  <div class="section">
    <div class="section-title">📝 Descripción del Plan</div>
    <p style="margin: 0; padding: 10px; background: #f8f9fa; border-radius: 5px;">${plan.descripcion}</p>
  </div>
  ` : ""}

  <div class="section">
    <div class="section-title">✅ Detalle de Tareas (${tareas.length})</div>
    ${tareas.length > 0 ? tareas.map((t, i) => {
      const estadoInfo = getEstadoInfo(t.estado);
      return `
    <div class="tarea" style="border-left: 4px solid ${estadoInfo.color}; background: ${estadoInfo.bg};">
      <div class="tarea-header">
        <span class="tarea-nombre">${i + 1}. ${t.nombre || "Tarea sin nombre"}</span>
        <span class="tarea-estado" style="background: ${estadoInfo.color}; color: white;">${estadoInfo.texto}</span>
      </div>
      ${t.observacion ? `
      <div class="tarea-obs">
        <strong>📝 Observación del empleado:</strong><br>
        ${t.observacion}
      </div>` : ""}
      ${(t.evidencias && t.evidencias.length > 0) ? `
      <div class="tarea-obs">
        <strong>📎 Evidencias adjuntas (${t.evidencias.length}):</strong>
        <ul class="evidencias-lista">
          ${t.evidencias.map(ev => `<li class="evidencia-item">📄 ${ev.nombre || "Archivo"} - ${ev.fechaSubida ? new Date(ev.fechaSubida).toLocaleDateString("es-CO") : ""}</li>`).join("")}
        </ul>
      </div>` : ""}
    </div>`;
    }).join("") : `<p style="color: #7f8c8d; text-align: center; padding: 20px;">No hay tareas registradas en este plan.</p>`}
  </div>

  ${plan.observaciones_generales ? `
  <div class="section">
    <div class="section-title">💬 Observaciones Generales del Empleado</div>
    <div class="observaciones">${plan.observaciones_generales}</div>
  </div>
  ` : ""}

  ${plan.observaciones_gerencia ? `
  <div class="section">
    <div class="section-title">👔 Observaciones de Gerencia (Aprobación)</div>
    <div class="observaciones observaciones-gerencia">${plan.observaciones_gerencia}</div>
  </div>
  ` : ""}

  <div class="firma">
    <div class="firma-item">
      <strong>${plan.empleado_asignado_nombre || "Empleado"}</strong><br>
      <span style="font-size: 11px; color: #666;">Responsable del Plan</span>
    </div>
    <div class="firma-item">
      <strong>${plan.aprobado_por_nombre || plan.cerrado_por_nombre || "Gerencia"}</strong><br>
      <span style="font-size: 11px; color: #666;">Aprobación Gerencia</span>
    </div>
  </div>

  <div class="footer">
    <p>Documento generado el ${new Date().toLocaleString("es-CO")}</p>
    <p>Este plan de trabajo ha sido completado y aprobado satisfactoriamente.</p>
    <p style="font-size: 10px; color: #999;">Sistema de Gestión BigBurguer</p>
  </div>
</body>
</html>
    `;

    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Plan_${(plan.nombre || "plan").replace(/[^a-zA-Z0-9]/g, "_")}_${new Date().toISOString().split("T")[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const reabrirPlan = async (plan) => {
    if (!window.confirm("¿Está seguro de reabrir este plan de trabajo?")) return;
    
    setCargando(true);
    try {
      const planData = {
        ...plan,
        cerrado: false,
        cerrado_por: null,
        cerrado_por_nombre: null,
        fecha_cierre: null,
        estado: "en_progreso",
        updated_at: new Date().toISOString()
      };

      const result = await guardarPlanTrabajoEnSupabase(planData, filtroEmpresa);
      
      if (result) {
        await guardarBitacoraPlanTrabajo({
          plan_id: planData.id,
          plan_nombre: planData.nombre,
          accion: "reabierto",
          descripcion: `Plan reabierto por: ${usuarioActual?.nombre || "Usuario"}`,
          empleado_id: plan.empleado_asignado_id,
          empleado_nombre: plan.empleado_asignado_nombre,
          sede_id: plan.sede_id,
          sede_nombre: plan.sede_nombre
        }, filtroEmpresa);

        setMensaje({ tipo: "exito", texto: "Plan reabierto correctamente" });
        await cargarPlanes();
      }
    } catch (error) {
      console.error("Error reabriendo plan:", error);
    }
    setCargando(false);
  };

  const editarPlan = (plan) => {
    // Solo usuarios con perfil Gerencia pueden editar planes
    if (!esGerencia) {
      setMensaje({ tipo: "error", texto: "No tiene permisos para editar planes de trabajo. Solo usuarios con perfil Gerencia pueden editar planes." });
      return;
    }
    const tareas = typeof plan.tareas === 'string' ? JSON.parse(plan.tareas) : (plan.tareas || []);
    // Asegurar que cada tarea tenga la estructura nueva
    const tareasNormalizadas = tareas.map(t => ({
      ...t,
      estado: t.estado || (t.completada ? "terminado" : "pendiente"),
      evidencias: t.evidencias || []
    }));
    setFormPlan({
      nombre: plan.nombre || "",
      descripcion: plan.descripcion || "",
      sede_id: plan.sede_id || "",
      empleado_asignado_id: plan.empleado_asignado_id || "",
      fecha_limite: plan.fecha_limite || "",
      tareas: tareasNormalizadas.length > 0 ? tareasNormalizadas : [{ id: uid(), nombre: "", estado: "pendiente", observacion: "", evidencias: [] }],
      observaciones_generales: plan.observaciones_generales || ""
    });
    setPlanSeleccionado(plan);
    setModo("editar");
  };

  const eliminarPlan = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este plan de trabajo?")) return;
    
    setCargando(true);
    try {
      const plan = planes.find(p => p.id === id);
      await eliminarPlanTrabajoEnSupabase(id);
      
      // Registrar en bitácora
      if (plan) {
        await guardarBitacoraPlanTrabajo({
          plan_id: id,
          plan_nombre: plan.nombre,
          accion: "eliminado",
          descripcion: `Plan eliminado por: ${usuarioActual?.nombre || "Usuario"}`,
          empleado_id: plan.empleado_asignado_id,
          empleado_nombre: plan.empleado_asignado_nombre,
          sede_id: plan.sede_id,
          sede_nombre: plan.sede_nombre
        }, filtroEmpresa);
      }
      
      setMensaje({ tipo: "exito", texto: "Plan eliminado" });
      await cargarPlanes();
    } catch (error) {
      console.error("Error eliminando plan:", error);
      setMensaje({ tipo: "error", texto: "Error al eliminar el plan" });
    }
    setCargando(false);
  };

  const marcarNotificacionLeida = async (notifId) => {
    await marcarNotificacionPlanLeidaEnSupabase(notifId);
    setNotificacionesPlan(prev => prev.filter(n => n.id !== notifId));
  };

  const getNombreSede = (sedeId) => {
    const sede = sedes.find(s => s.id === sedeId);
    return sede ? sede.nombre : "Sin sede";
  };

  const getNombreEmpleado = (empleadoId) => {
    const emp = empleados.find(e => e.id === empleadoId);
    return emp ? `${emp.nombres || ""} ${emp.apellidos || ""}`.trim() : "Sin asignar";
  };

  const getEstadoColor = (estado, cerrado) => {
    if (cerrado || estado === "aprobado") return "#27ae60";
    switch (estado) {
      case "aprobado": return "#27ae60";
      case "pendiente_aprobacion": return "#3498db";
      case "devuelto": return "#e74c3c";
      case "completado": return "#27ae60";
      case "cerrado": return "#8e44ad";
      case "en_progreso": return "#f39c12";
      default: return "#95a5a6";
    }
  };

  const getEstadoLabel = (estado, cerrado) => {
    if (cerrado || estado === "aprobado") return "✅ Aprobado";
    switch (estado) {
      case "aprobado": return "✅ Aprobado";
      case "pendiente_aprobacion": return "🔍 Pendiente Aprobación";
      case "devuelto": return "↩️ Devuelto";
      case "completado": return "✅ Completado";
      case "cerrado": return "🔒 Cerrado";
      case "en_progreso": return "🔄 En Progreso";
      default: return "⏳ Pendiente";
    }
  };

  // Filtrar planes según los filtros aplicados y permisos del usuario
  // Solo se muestran planes a: 1) Usuarios con perfil Gerencia, 2) Empleados asignados al plan
  const planesFiltrados = useMemo(() => {
    return planes.filter(plan => {
      // RESTRICCIÓN DE VISIBILIDAD: Solo gerencia o empleado asignado pueden ver el plan
      if (!esGerencia && !esEmpleadoAsignado(plan)) {
        return false;
      }
      
      // Filtros adicionales
      if (filtros.sede_id && plan.sede_id !== filtros.sede_id) return false;
      if (filtros.empleado_id && plan.empleado_asignado_id !== filtros.empleado_id) return false;
      if (filtros.estado && plan.estado !== filtros.estado && !(filtros.estado === "cerrado" && plan.cerrado)) return false;
      if (filtros.mes || filtros.anio) {
        const fechaPlan = new Date(plan.fecha_limite || plan.created_at);
        if (filtros.mes && (fechaPlan.getMonth() + 1) !== parseInt(filtros.mes)) return false;
        if (filtros.anio && fechaPlan.getFullYear() !== parseInt(filtros.anio)) return false;
      }
      return true;
    });
  }, [planes, filtros, esGerencia, esEmpleadoAsignado]);

  const meses = [
    { value: "1", label: "Enero" }, { value: "2", label: "Febrero" }, { value: "3", label: "Marzo" },
    { value: "4", label: "Abril" }, { value: "5", label: "Mayo" }, { value: "6", label: "Junio" },
    { value: "7", label: "Julio" }, { value: "8", label: "Agosto" }, { value: "9", label: "Septiembre" },
    { value: "10", label: "Octubre" }, { value: "11", label: "Noviembre" }, { value: "12", label: "Diciembre" }
  ];

  const anios = [];
  const anioActual = new Date().getFullYear();
  for (let i = anioActual - 2; i <= anioActual + 1; i++) {
    anios.push(i.toString());
  }

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Notificaciones del usuario */}
      {notificacionesPlan.length > 0 && modo === "lista" && (
        <div style={{ marginBottom: "20px", padding: "15px", backgroundColor: "#fff3cd", borderRadius: "8px", border: "1px solid #ffc107" }}>
          <h4 style={{ margin: "0 0 10px 0", color: "#856404" }}>🔔 Notificaciones de Planes de Trabajo</h4>
          {notificacionesPlan.map(notif => (
            <div key={notif.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px", backgroundColor: "#fff", borderRadius: "5px", marginBottom: "5px" }}>
              <span style={{ fontSize: "14px" }}>{notif.mensaje}</span>
              <button
                onClick={() => marcarNotificacionLeida(notif.id)}
                style={{ padding: "4px 10px", backgroundColor: "#28a745", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}
              >
                Marcar leída
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "10px" }}>
        <h2 style={{ margin: 0 }}>📋 Planes de Trabajo</h2>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {modo === "lista" && (
            <>
              {esGerencia && tienePermiso("admin_planes_crear") && (
                <button
                  onClick={iniciarNuevoPlan}
                  style={{ padding: "10px 20px", backgroundColor: "#3498db", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }}
                >
                  ➕ Nuevo Plan
                </button>
              )}
              <button
                onClick={() => { setModo("bitacora"); cargarBitacora(); }}
                style={{ padding: "10px 20px", backgroundColor: "#9b59b6", color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}
              >
                📚 Ver Bitácora
              </button>
            </>
          )}
          {modo !== "lista" && (
            <button
              onClick={() => setModo("lista")}
              style={{ padding: "10px 20px", backgroundColor: "#95a5a6", color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}
            >
              ← Volver a lista
            </button>
          )}
        </div>
      </div>

      {mensaje.texto && (
        <div style={{
          padding: "12px",
          marginBottom: "15px",
          borderRadius: "6px",
          backgroundColor: mensaje.tipo === "exito" ? "#d4edda" : "#f8d7da",
          color: mensaje.tipo === "exito" ? "#155724" : "#721c24"
        }}>
          {mensaje.texto}
          <button onClick={() => setMensaje({ tipo: "", texto: "" })} style={{ float: "right", background: "none", border: "none", cursor: "pointer", fontSize: "16px" }}>×</button>
        </div>
      )}

      {cargando && <p style={{ textAlign: "center" }}>Cargando...</p>}

      {/* FILTROS - Visibles en lista y bitácora */}
      {(modo === "lista" || modo === "bitacora") && (
        <div style={{ backgroundColor: "#f8f9fa", padding: "15px", borderRadius: "8px", marginBottom: "20px" }}>
          <h4 style={{ margin: "0 0 15px 0" }}>🔍 Filtros</h4>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "15px" }}>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold" }}>Sede</label>
              <select
                value={filtros.sede_id}
                onChange={(e) => setFiltros({ ...filtros, sede_id: e.target.value })}
                style={{ width: "100%", padding: "8px", borderRadius: "5px", border: "1px solid #ddd" }}
              >
                <option value="">Todas las sedes</option>
                {sedes.map(sede => (
                  <option key={sede.id} value={sede.id}>{sede.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold" }}>Empleado</label>
              <select
                value={filtros.empleado_id}
                onChange={(e) => setFiltros({ ...filtros, empleado_id: e.target.value })}
                style={{ width: "100%", padding: "8px", borderRadius: "5px", border: "1px solid #ddd" }}
              >
                <option value="">Todos los empleados</option>
                {[...(empleados || [])].sort((a, b) => ((a.nombres || a.nombre || "").localeCompare((b.nombres || b.nombre || ""), 'es', { sensitivity: 'base' }))).map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.nombres || emp.nombre} {emp.apellidos || ""}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold" }}>Mes</label>
              <select
                value={filtros.mes}
                onChange={(e) => setFiltros({ ...filtros, mes: e.target.value })}
                style={{ width: "100%", padding: "8px", borderRadius: "5px", border: "1px solid #ddd" }}
              >
                <option value="">Todos los meses</option>
                {meses.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold" }}>Año</label>
              <select
                value={filtros.anio}
                onChange={(e) => setFiltros({ ...filtros, anio: e.target.value })}
                style={{ width: "100%", padding: "8px", borderRadius: "5px", border: "1px solid #ddd" }}
              >
                <option value="">Todos los años</option>
                {anios.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            {modo === "lista" && (
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold" }}>Estado</label>
                <select
                  value={filtros.estado}
                  onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
                  style={{ width: "100%", padding: "8px", borderRadius: "5px", border: "1px solid #ddd" }}
                >
                  <option value="">Todos</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="en_progreso">En Progreso</option>
                  <option value="completado">Completado</option>
                  <option value="cerrado">Cerrado</option>
                </select>
              </div>
            )}
            {modo === "bitacora" && (
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <button
                  onClick={cargarBitacora}
                  style={{ padding: "8px 20px", backgroundColor: "#3498db", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }}
                >
                  🔄 Aplicar Filtros
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* LISTA DE PLANES */}
      {modo === "lista" && !cargando && (
        <div>
          {planesFiltrados.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#7f8c8d" }}>
              <p style={{ fontSize: "48px", margin: "0" }}>📋</p>
              <p>No hay planes de trabajo {Object.values(filtros).some(f => f) ? "con los filtros aplicados" : "creados"}</p>
            </div>
          ) : (
            <div style={{ display: "grid", gap: "15px" }}>
              {planesFiltrados.map(plan => (
                <div
                  key={plan.id}
                  style={{
                    backgroundColor: "white",
                    borderRadius: "10px",
                    padding: "20px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    borderLeft: `5px solid ${getEstadoColor(plan.estado, plan.cerrado)}`
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "15px" }}>
                    <div style={{ flex: 1, minWidth: "250px" }}>
                      <h3 style={{ margin: "0 0 8px 0" }}>{plan.nombre}</h3>
                      <p style={{ margin: "0 0 5px 0", color: "#7f8c8d", fontSize: "14px" }}>
                        🏢 {plan.sede_nombre || getNombreSede(plan.sede_id)}
                      </p>
                      <p style={{ margin: "0 0 5px 0", color: "#7f8c8d", fontSize: "14px" }}>
                        👤 Asignado a: <strong>{plan.empleado_asignado_nombre || getNombreEmpleado(plan.empleado_asignado_id)}</strong>
                      </p>
                      <p style={{ margin: "0 0 10px 0", color: "#7f8c8d", fontSize: "14px" }}>
                        📅 Fecha límite: {plan.fecha_limite || "Sin fecha"}
                      </p>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                        <span style={{
                          padding: "4px 10px",
                          borderRadius: "15px",
                          backgroundColor: getEstadoColor(plan.estado, plan.cerrado),
                          color: "white",
                          fontSize: "12px"
                        }}>
                          {getEstadoLabel(plan.estado, plan.cerrado)}
                        </span>
                        <div style={{ flex: 1, maxWidth: "200px", minWidth: "100px" }}>
                          <div style={{
                            height: "8px",
                            backgroundColor: "#ecf0f1",
                            borderRadius: "4px",
                            overflow: "hidden"
                          }}>
                            <div style={{
                              width: `${plan.porcentaje_completado || 0}%`,
                              height: "100%",
                              backgroundColor: getEstadoColor(plan.estado, plan.cerrado),
                              transition: "width 0.3s"
                            }} />
                          </div>
                        </div>
                        <span style={{ fontSize: "12px", color: "#7f8c8d" }}>
                          {plan.porcentaje_completado || 0}%
                        </span>
                      </div>
                      {/* Mostrar razón de devolución si el plan está devuelto */}
                      {plan.estado === "devuelto" && plan.observaciones_gerencia && (
                        <div style={{ 
                          marginTop: "12px", 
                          padding: "10px 12px", 
                          backgroundColor: "#ffebee", 
                          borderRadius: "6px", 
                          border: "1px solid #e74c3c" 
                        }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                            <span style={{ fontSize: "16px" }}>⚠️</span>
                            <div>
                              <strong style={{ color: "#c0392b", fontSize: "13px" }}>Razón de la devolución:</strong>
                              <p style={{ margin: "4px 0 0 0", color: "#333", fontSize: "13px" }}>{plan.observaciones_gerencia}</p>
                              {plan.devuelto_por_nombre && (
                                <p style={{ margin: "6px 0 0 0", color: "#7f8c8d", fontSize: "11px", fontStyle: "italic" }}>
                                  Devuelto por: {plan.devuelto_por_nombre} {plan.fecha_devolucion ? `• ${new Date(plan.fecha_devolucion).toLocaleDateString()}` : ""}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {/* Botón Diligenciar - Para empleado asignado si no está cerrado/aprobado y no está pendiente aprobación */}
                      {!plan.cerrado && plan.estado !== "aprobado" && plan.estado !== "pendiente_aprobacion" && (esEmpleadoAsignado(plan) || (plan.estado === "devuelto" && esEmpleadoAsignado(plan))) && (
                        <button
                          onClick={() => abrirParaDiligenciar(plan)}
                          style={{ padding: "8px 12px", backgroundColor: "#27ae60", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }}
                        >
                          ✏️ Diligenciar
                        </button>
                      )}
                      {/* Botón Revisar/Aprobar - Solo para Gerencia cuando está pendiente aprobación */}
                      {esGerencia && !plan.cerrado && plan.estado === "pendiente_aprobacion" && (
                        <button
                          onClick={() => abrirParaRevisar(plan)}
                          style={{ padding: "8px 12px", backgroundColor: "#9b59b6", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }}
                        >
                          👁️ Revisar/Aprobar
                        </button>
                      )}
                      {/* Botón Descargar - Si está aprobado */}
                      {(plan.cerrado || plan.estado === "aprobado") && (
                        <button
                          onClick={() => descargarPlanAprobado(plan)}
                          style={{ padding: "8px 12px", backgroundColor: "#27ae60", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }}
                        >
                          📥 Descargar
                        </button>
                      )}
                      {/* Botón Reabrir - Solo para Gerencia si está cerrado/aprobado */}
                      {esGerencia && (plan.cerrado || plan.estado === "aprobado") && (
                        <button
                          onClick={() => reabrirPlan(plan)}
                          style={{ padding: "8px 12px", backgroundColor: "#f39c12", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }}
                        >
                          🔓 Reabrir
                        </button>
                      )}
                      {/* Botón Editar - Solo para Gerencia (empleados asignados sin perfil gerencia solo pueden diligenciar) */}
                      {esGerencia && tienePermiso("admin_planes_editar") && !plan.cerrado && plan.estado !== "aprobado" && plan.estado !== "pendiente_aprobacion" && (
                        <button
                          onClick={() => editarPlan(plan)}
                          style={{ padding: "8px 12px", backgroundColor: "#3498db", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }}
                        >
                          ✏️ Editar
                        </button>
                      )}
                      {esGerencia && tienePermiso("admin_planes_eliminar") && (
                        <button
                          onClick={() => eliminarPlan(plan.id)}
                          style={{ padding: "8px 12px", backgroundColor: "#e74c3c", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* BITÁCORA */}
      {modo === "bitacora" && !cargando && (
        <div>
          <h3 style={{ marginBottom: "15px" }}>📚 Bitácora de Planes de Trabajo</h3>
          {bitacora.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#7f8c8d" }}>
              <p>No hay registros en la bitácora con los filtros aplicados</p>
            </div>
          ) : (
            <div style={{ backgroundColor: "white", borderRadius: "10px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#34495e", color: "white" }}>
                    <th style={{ padding: "12px", textAlign: "left" }}>Fecha</th>
                    <th style={{ padding: "12px", textAlign: "left" }}>Plan</th>
                    <th style={{ padding: "12px", textAlign: "left" }}>Aprobado Por</th>
                    <th style={{ padding: "12px", textAlign: "left" }}>Empleado</th>
                    <th style={{ padding: "12px", textAlign: "left" }}>Sede</th>
                    <th style={{ padding: "12px", textAlign: "center" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {bitacora.map((reg, idx) => {
                    // Obtener datos adicionales del plan
                    const datos = reg.datos_adicionales || {};
                    const tareas = datos.tareas || [];
                    
                    return (
                      <tr key={reg.id} style={{ backgroundColor: idx % 2 === 0 ? "#f8f9fa" : "white" }}>
                        <td style={{ padding: "10px", fontSize: "13px" }}>
                          {new Date(reg.fecha).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" })}
                        </td>
                        <td style={{ padding: "10px", fontSize: "13px", fontWeight: "bold" }}>{reg.plan_nombre}</td>
                        <td style={{ padding: "10px" }}>
                          <span style={{
                            padding: "3px 8px",
                            borderRadius: "10px",
                            fontSize: "11px",
                            backgroundColor: "#27ae60",
                            color: "white"
                          }}>
                            ✅ APROBADO
                          </span>
                        </td>
                        <td style={{ padding: "10px", fontSize: "13px" }}>{datos.aprobado_por || reg.empleado_nombre || "—"}</td>
                        <td style={{ padding: "10px", fontSize: "13px" }}>{reg.sede_nombre || "—"}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          <button
                            onClick={() => {
                              // Crear objeto de plan para descargar desde los datos de la bitácora
                              const planParaDescargar = {
                                id: reg.plan_id,
                                nombre: reg.plan_nombre,
                                sede_nombre: reg.sede_nombre,
                                empleado_asignado_nombre: reg.empleado_nombre,
                                fecha_limite: datos.fecha_limite || "",
                                fecha_aprobacion: reg.fecha,
                                aprobado_por_nombre: datos.aprobado_por || "",
                                descripcion: "",
                                tareas: tareas,
                                observaciones_generales: datos.observaciones_empleado || "",
                                observaciones_gerencia: datos.observaciones_gerencia || "",
                                cerrado: true
                              };
                              descargarPlanAprobado(planParaDescargar);
                            }}
                            style={{ padding: "6px 12px", backgroundColor: "#27ae60", color: "white", border: "none", borderRadius: "5px", cursor: "pointer", fontSize: "12px" }}
                          >
                            📥 Descargar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* FORMULARIO CREAR/EDITAR - Solo accesible para usuarios con perfil Gerencia */}
      {(modo === "crear" || modo === "editar") && esGerencia && (
        <div style={{ backgroundColor: "white", borderRadius: "10px", padding: "25px", boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}>
          <h3 style={{ marginTop: 0 }}>{modo === "crear" ? "➕ Nuevo Plan de Trabajo" : "✏️ Editar Plan"}</h3>
          
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px", marginBottom: "20px" }}>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Nombre del Plan *</label>
              <input
                type="text"
                value={formPlan.nombre}
                onChange={(e) => setFormPlan({ ...formPlan, nombre: e.target.value })}
                style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ddd" }}
                placeholder="Ej: Revisión semanal de inventario"
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Sede *</label>
              <select
                value={formPlan.sede_id}
                onChange={(e) => setFormPlan({ ...formPlan, sede_id: e.target.value })}
                style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ddd" }}
              >
                <option value="">Seleccionar sede...</option>
                {sedes.map(sede => (
                  <option key={sede.id} value={sede.id}>{sede.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Asignar a Empleado *</label>
              <select
                value={formPlan.empleado_asignado_id}
                onChange={(e) => setFormPlan({ ...formPlan, empleado_asignado_id: e.target.value })}
                style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ddd" }}
              >
                <option value="">Seleccionar empleado...</option>
                {[...(empleados || [])].sort((a, b) => ((a.nombres || a.nombre || "").localeCompare((b.nombres || b.nombre || ""), 'es', { sensitivity: 'base' }))).map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.nombres || emp.nombre} {emp.apellidos || ""}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Fecha Límite *</label>
              <input
                type="date"
                value={formPlan.fecha_limite}
                onChange={(e) => setFormPlan({ ...formPlan, fecha_limite: e.target.value })}
                style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ddd" }}
              />
            </div>
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Descripción</label>
            <textarea
              value={formPlan.descripcion}
              onChange={(e) => setFormPlan({ ...formPlan, descripcion: e.target.value })}
              style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ddd", minHeight: "80px" }}
              placeholder="Descripción opcional del plan..."
            />
          </div>

          {/* TAREAS */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
              <h4 style={{ margin: 0 }}>📝 Tareas del Plan</h4>
              <button
                onClick={agregarTarea}
                style={{ padding: "6px 12px", backgroundColor: "#27ae60", color: "white", border: "none", borderRadius: "5px", cursor: "pointer", fontSize: "13px" }}
              >
                ➕ Agregar Tarea
              </button>
            </div>

            {formPlan.tareas.map((tarea, index) => (
              <div key={tarea.id || index} style={{ marginBottom: "15px", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid #ddd", overflow: "hidden" }}>
                <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "10px" }}>
                  <span style={{ color: "#7f8c8d", fontSize: "14px", minWidth: "25px", fontWeight: "bold", flexShrink: 0 }}>{index + 1}.</span>
                  <select
                    value={CATEGORIAS_TAREAS_PLAN.includes(tarea.nombre) ? tarea.nombre : "__custom__"}
                    onChange={(e) => {
                      if (e.target.value === "__custom__") {
                        actualizarTarea(index, "nombre", "");
                      } else {
                        actualizarTarea(index, "nombre", e.target.value);
                      }
                    }}
                    style={{ flex: 1, minWidth: 0, maxWidth: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ddd", backgroundColor: "white", boxSizing: "border-box" }}
                  >
                    <option value="">-- Seleccionar categoría de tarea --</option>
                    {CATEGORIAS_TAREAS_PLAN.map((cat, i) => (
                      <option key={i} value={cat}>{cat}</option>
                    ))}
                    <option value="__custom__">📝 Escribir tarea personalizada...</option>
                  </select>
                  {formPlan.tareas.length > 1 && (
                    <button
                      onClick={() => eliminarTarea(index)}
                      style={{ padding: "8px 12px", backgroundColor: "#e74c3c", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }}
                    >
                      ✕
                    </button>
                  )}
                </div>
                {/* Input para tarea personalizada */}
                {!CATEGORIAS_TAREAS_PLAN.includes(tarea.nombre) && (
                  <div style={{ marginLeft: "35px", marginTop: "8px" }}>
                    <input
                      type="text"
                      value={tarea.nombre}
                      onChange={(e) => actualizarTarea(index, "nombre", e.target.value)}
                      placeholder="Escriba la descripción de la tarea personalizada..."
                      style={{ width: "calc(100% - 20px)", padding: "10px", borderRadius: "5px", border: "1px solid #3498db", backgroundColor: "#e8f4fc", boxSizing: "border-box" }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <button
              onClick={() => setModo("lista")}
              style={{ padding: "12px 25px", backgroundColor: "#95a5a6", color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}
            >
              Cancelar
            </button>
            <button
              onClick={guardarPlan}
              disabled={cargando}
              style={{ padding: "12px 25px", backgroundColor: "#27ae60", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }}
            >
              💾 {modo === "crear" ? "Crear y Notificar" : "Guardar Cambios"}
            </button>
          </div>
        </div>
      )}

      {/* FORMULARIO DILIGENCIAR (para empleado asignado) */}
      {modo === "diligenciar" && planSeleccionado && (
        <div style={{ backgroundColor: "white", borderRadius: "10px", padding: "25px", boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}>
          <h3 style={{ marginTop: 0 }}>📝 Diligenciar Plan: {planSeleccionado.nombre}</h3>
          <p style={{ color: "#7f8c8d", marginBottom: "20px" }}>
            Sede: {planSeleccionado.sede_nombre} | Fecha límite: {planSeleccionado.fecha_limite}
          </p>
          
          {planSeleccionado.descripcion && (
            <div style={{ marginBottom: "20px", padding: "10px", backgroundColor: "#f8f9fa", borderRadius: "5px" }}>
              <strong>Descripción:</strong> {planSeleccionado.descripcion}
            </div>
          )}

          {/* Leyenda de estados */}
          <div style={{ display: "flex", gap: "15px", marginBottom: "15px", flexWrap: "wrap" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "13px" }}>
              <span style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#95a5a6" }}></span> Pendiente
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "13px" }}>
              <span style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#f39c12" }}></span> En Proceso
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "13px" }}>
              <span style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#27ae60" }}></span> Terminado
            </span>
          </div>

          <h4>✅ Tareas</h4>
          {formPlan.tareas.map((tarea, index) => {
            const estadoColor = tarea.estado === "terminado" ? "#27ae60" : tarea.estado === "en_proceso" ? "#f39c12" : "#95a5a6";
            const estadoBg = tarea.estado === "terminado" ? "#e8f8f0" : tarea.estado === "en_proceso" ? "#fff8e1" : "#f8f9fa";
            
            return (
              <div key={tarea.id || index} style={{ marginBottom: "20px", padding: "15px", backgroundColor: estadoBg, borderRadius: "8px", border: `2px solid ${estadoColor}` }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "15px", flexWrap: "wrap" }}>
                  {/* Estado de la tarea */}
                  <div style={{ minWidth: "140px" }}>
                    <label style={{ display: "block", fontSize: "12px", color: "#7f8c8d", marginBottom: "5px", fontWeight: "bold" }}>Estado:</label>
                    <select
                      value={tarea.estado || "pendiente"}
                      onChange={(e) => actualizarTarea(index, "estado", e.target.value)}
                      style={{ 
                        width: "100%", 
                        padding: "8px", 
                        borderRadius: "5px", 
                        border: `2px solid ${estadoColor}`,
                        backgroundColor: estadoBg,
                        fontWeight: "bold",
                        cursor: "pointer"
                      }}
                    >
                      <option value="pendiente">⏳ Pendiente</option>
                      <option value="en_proceso">🔄 En Proceso</option>
                      <option value="terminado">✅ Terminado</option>
                    </select>
                  </div>
                  
                  <div style={{ flex: 1, minWidth: "250px" }}>
                    <span style={{ fontWeight: "bold", fontSize: "15px", textDecoration: tarea.estado === "terminado" ? "line-through" : "none" }}>
                      {index + 1}. {tarea.nombre}
                    </span>
                    
                    {/* Observación */}
                    <div style={{ marginTop: "10px" }}>
                      <label style={{ display: "block", fontSize: "12px", color: "#7f8c8d", marginBottom: "5px" }}>📝 Observación:</label>
                      <textarea
                        value={tarea.observacion || ""}
                        onChange={(e) => actualizarTarea(index, "observacion", e.target.value)}
                        placeholder="Escribe tus observaciones sobre esta tarea..."
                        style={{ width: "100%", padding: "8px", borderRadius: "5px", border: "1px solid #ddd", minHeight: "60px", fontSize: "13px" }}
                      />
                    </div>

                    {/* Evidencias */}
                    <div style={{ marginTop: "15px" }}>
                      <label style={{ display: "block", fontSize: "12px", color: "#7f8c8d", marginBottom: "5px" }}>📎 Evidencias (fotos/documentos):</label>
                      
                      {/* Lista de evidencias existentes */}
                      {(tarea.evidencias || []).length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "10px" }}>
                          {(tarea.evidencias || []).map((ev) => (
                            <div key={ev.id} style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              gap: "8px", 
                              padding: "8px 12px", 
                              backgroundColor: "#fff", 
                              borderRadius: "6px", 
                              border: "1px solid #ddd",
                              fontSize: "13px"
                            }}>
                              {ev.tipo?.startsWith("image/") ? (
                                <a href={ev.url} target="_blank" rel="noopener noreferrer">
                                  <img src={ev.url} alt={ev.nombre} style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }} />
                                </a>
                              ) : (
                                <span>📄</span>
                              )}
                              <a href={ev.url} target="_blank" rel="noopener noreferrer" style={{ color: "#3498db", textDecoration: "none", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {ev.nombre}
                              </a>
                              <button
                                onClick={() => eliminarEvidenciaTarea(index, ev.id)}
                                style={{ padding: "2px 6px", backgroundColor: "#e74c3c", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}
                                title="Eliminar evidencia"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Botón para subir nueva evidencia */}
                      <input
                        type="file"
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            subirEvidenciaTarea(index, e.target.files[0]);
                            e.target.value = "";
                          }
                        }}
                        style={{ display: "none" }}
                        id={`evidencia-input-${index}`}
                      />
                      <label
                        htmlFor={`evidencia-input-${index}`}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          padding: "8px 12px",
                          backgroundColor: "#3498db",
                          color: "white",
                          borderRadius: "5px",
                          cursor: "pointer",
                          fontSize: "13px"
                        }}
                      >
                        📤 Subir Evidencia
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <div style={{ marginTop: "20px" }}>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>📋 Observaciones Generales</label>
            <textarea
              value={formPlan.observaciones_generales}
              onChange={(e) => setFormPlan({ ...formPlan, observaciones_generales: e.target.value })}
              style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ddd", minHeight: "100px" }}
              placeholder="Observaciones generales del plan de trabajo..."
            />
          </div>

          {/* Mostrar observaciones de gerencia si el plan fue devuelto */}
          {planSeleccionado.estado === "devuelto" && planSeleccionado.observaciones_gerencia && (
            <div style={{ marginTop: "20px", padding: "15px", backgroundColor: "#ffebee", borderRadius: "8px", border: "2px solid #e74c3c" }}>
              <h4 style={{ margin: "0 0 10px 0", color: "#c0392b" }}>⚠️ Observaciones de Gerencia (Correcciones Solicitadas)</h4>
              <p style={{ margin: 0, color: "#333" }}>{planSeleccionado.observaciones_gerencia}</p>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px", flexWrap: "wrap" }}>
            <button
              onClick={() => setModo("lista")}
              style={{ padding: "12px 25px", backgroundColor: "#95a5a6", color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}
            >
              Cancelar
            </button>
            <button
              onClick={() => guardarDiligenciamiento(false)}
              disabled={cargando}
              style={{ padding: "12px 25px", backgroundColor: "#3498db", color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}
            >
              💾 Guardar Progreso
            </button>
            <button
              onClick={() => guardarDiligenciamiento(true)}
              disabled={cargando}
              style={{ padding: "12px 25px", backgroundColor: "#27ae60", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }}
            >
              📤 Enviar a Revisión
            </button>
          </div>
        </div>
      )}

      {/* FORMULARIO REVISAR (para Gerencia) */}
      {modo === "revisar" && planSeleccionado && (
        <div style={{ backgroundColor: "white", borderRadius: "10px", padding: "25px", boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}>
          <h3 style={{ marginTop: 0 }}>👁️ Revisar Plan: {planSeleccionado.nombre}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "20px", padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
            <div><strong>Sede:</strong> {planSeleccionado.sede_nombre}</div>
            <div><strong>Asignado a:</strong> {planSeleccionado.empleado_asignado_nombre}</div>
            <div><strong>Fecha límite:</strong> {planSeleccionado.fecha_limite}</div>
            <div><strong>Progreso:</strong> {planSeleccionado.porcentaje_completado || 0}%</div>
          </div>
          
          {planSeleccionado.descripcion && (
            <div style={{ marginBottom: "20px", padding: "10px", backgroundColor: "#e3f2fd", borderRadius: "5px" }}>
              <strong>Descripción:</strong> {planSeleccionado.descripcion}
            </div>
          )}

          {/* Leyenda de estados */}
          <div style={{ display: "flex", gap: "15px", marginBottom: "15px", flexWrap: "wrap" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "13px" }}>
              <span style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#95a5a6" }}></span> Pendiente
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "13px" }}>
              <span style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#f39c12" }}></span> En Proceso
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "13px" }}>
              <span style={{ width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#27ae60" }}></span> Terminado
            </span>
          </div>

          <h4>✅ Tareas y Observaciones</h4>
          {formPlan.tareas.map((tarea, index) => {
            const estadoTarea = tarea.estado || (tarea.completada ? "terminado" : "pendiente");
            const estadoColor = estadoTarea === "terminado" ? "#27ae60" : estadoTarea === "en_proceso" ? "#f39c12" : "#95a5a6";
            const estadoBg = estadoTarea === "terminado" ? "#e8f8f0" : estadoTarea === "en_proceso" ? "#fff8e1" : "#fff3cd";
            const estadoIcon = estadoTarea === "terminado" ? "✅" : estadoTarea === "en_proceso" ? "🔄" : "⏳";
            const estadoLabel = estadoTarea === "terminado" ? "Terminado" : estadoTarea === "en_proceso" ? "En Proceso" : "Pendiente";

            return (
              <div key={tarea.id || index} style={{ marginBottom: "15px", padding: "15px", backgroundColor: estadoBg, borderRadius: "8px", border: `2px solid ${estadoColor}` }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  <span style={{ fontSize: "20px" }}>{estadoIcon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "8px" }}>
                      <span style={{ fontWeight: "bold" }}>
                        {index + 1}. {tarea.nombre}
                      </span>
                      <span style={{ 
                        padding: "2px 10px", 
                        borderRadius: "12px", 
                        backgroundColor: estadoColor, 
                        color: "white", 
                        fontSize: "11px",
                        fontWeight: "bold"
                      }}>
                        {estadoLabel}
                      </span>
                    </div>
                    
                    {tarea.observacion && (
                      <div style={{ marginTop: "10px", padding: "10px", backgroundColor: "rgba(255,255,255,0.8)", borderRadius: "5px", fontSize: "13px" }}>
                        <strong>📝 Observación del empleado:</strong><br />
                        {tarea.observacion}
                      </div>
                    )}

                    {/* Mostrar evidencias adjuntas */}
                    {(tarea.evidencias || []).length > 0 && (
                      <div style={{ marginTop: "10px", padding: "10px", backgroundColor: "rgba(255,255,255,0.8)", borderRadius: "5px" }}>
                        <strong>📎 Evidencias adjuntas:</strong>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "8px" }}>
                          {(tarea.evidencias || []).map((ev) => (
                            <a 
                              key={ev.id} 
                              href={ev.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                display: "flex", 
                                alignItems: "center", 
                                gap: "5px", 
                                padding: "6px 10px", 
                                backgroundColor: "#e3f2fd", 
                                borderRadius: "5px", 
                                textDecoration: "none",
                                color: "#1976d2",
                                fontSize: "12px"
                              }}
                            >
                              {ev.tipo?.startsWith("image/") ? (
                                <img src={ev.url} alt={ev.nombre} style={{ width: "30px", height: "30px", objectFit: "cover", borderRadius: "3px" }} />
                              ) : (
                                <span>📄</span>
                              )}
                              <span style={{ maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {ev.nombre}
                              </span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {formPlan.observaciones_generales && (
            <div style={{ marginTop: "20px", padding: "15px", backgroundColor: "#e8f5e9", borderRadius: "8px" }}>
              <h4 style={{ margin: "0 0 10px 0" }}>📋 Observaciones Generales del Empleado</h4>
              <p style={{ margin: 0 }}>{formPlan.observaciones_generales}</p>
            </div>
          )}

          {/* Observaciones de Gerencia */}
          <div style={{ marginTop: "20px" }}>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>👔 Observaciones de Gerencia</label>
            <textarea
              value={formPlan.observaciones_gerencia || ""}
              onChange={(e) => setFormPlan({ ...formPlan, observaciones_gerencia: e.target.value })}
              style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "2px solid #9b59b6", minHeight: "100px" }}
              placeholder="Escriba sus observaciones como gerencia (requerido para devolver el plan)..."
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "25px", flexWrap: "wrap" }}>
            <button
              onClick={() => setModo("lista")}
              style={{ padding: "12px 25px", backgroundColor: "#95a5a6", color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}
            >
              Volver
            </button>
            <button
              onClick={devolverPlan}
              disabled={cargando}
              style={{ padding: "12px 25px", backgroundColor: "#e74c3c", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }}
            >
              ↩️ Devolver para Corrección
            </button>
            <button
              onClick={aprobarPlanDefinitivo}
              disabled={cargando}
              style={{ padding: "12px 25px", backgroundColor: "#27ae60", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }}
            >
              ✅ Aprobar Definitivamente
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================== ARQUEOS DE CAJA ====================== */

function ArqueosCajaAdmin({ sedes = [], empleados = [], filtroEmpresa = null, tienePermiso = () => true, esGerente = false }) {
  const [arqueos, setArqueos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [modo, setModo] = useState("lista"); // "lista" | "crear" | "editar" | "ver"
  const [arqueoSeleccionado, setArqueoSeleccionado] = useState(null);
  const [mensaje, setMensaje] = useState({ tipo: "", texto: "" });

  // Filtros
  const [filtroMes, setFiltroMes] = useState("");
  const [filtroAnio, setFiltroAnio] = useState(new Date().getFullYear().toString());
  const [filtroSede, setFiltroSede] = useState("");
  const [filtroCajero, setFiltroCajero] = useState("");

  // Función para obtener hora actual formateada en AM/PM
  const obtenerHoraActual = () => {
    const ahora = new Date();
    let horas = ahora.getHours();
    const minutos = ahora.getMinutes().toString().padStart(2, '0');
    const ampm = horas >= 12 ? 'PM' : 'AM';
    horas = horas % 12;
    horas = horas ? horas : 12; // 0 debe ser 12
    return `${horas}:${minutos} ${ampm}`;
  };

  // Formulario de arqueo
  const [formArqueo, setFormArqueo] = useState({
    tipo: "caja", // "caja" | "sencilla"
    sede_id: "",
    cajero_id: "",
    fecha: new Date().toISOString().split('T')[0],
    hora: obtenerHoraActual(),
    valor_inicial: 0,
    // Efectivo por denominación
    denominaciones: {
      billete_100000: 0,
      billete_50000: 0,
      billete_20000: 0,
      billete_10000: 0,
      billete_5000: 0,
      billete_2000: 0,
      moneda_1000: 0,
      moneda_500: 0,
      moneda_200: 0,
      moneda_100: 0,
      moneda_50: 0
    },
    // Otros medios
    datafono: 0,
    transferencia_bancolombia: 0,
    transferencia_davivienda: 0,
    transferencia_daviplata: 0,
    transferencia_nequi: 0,
    ventas_proceso: 0, // Ventas que no se han cobrado aún
    gastos: 0,
    notas: ""
  });

  useEffect(() => {
    cargarArqueos();
  }, [filtroEmpresa]);

  const cargarArqueos = async () => {
    setCargando(true);
    try {
      const data = await cargarArqueosCajaDeSupabase(filtroEmpresa);
      setArqueos(data || []);
    } catch (error) {
      console.error("Error cargando arqueos:", error);
      setMensaje({ tipo: "error", texto: "Error al cargar arqueos" });
    }
    setCargando(false);
  };

  // Filtrar arqueos según los criterios seleccionados
  const arqueosFiltrados = useMemo(() => {
    return arqueos.filter(arqueo => {
      // Filtro por año
      if (filtroAnio && arqueo.fecha) {
        const anioArqueo = arqueo.fecha.substring(0, 4);
        if (anioArqueo !== filtroAnio) return false;
      }
      // Filtro por mes
      if (filtroMes && arqueo.fecha) {
        const mesArqueo = arqueo.fecha.substring(5, 7);
        if (mesArqueo !== filtroMes) return false;
      }
      // Filtro por sede
      if (filtroSede && arqueo.sede_id !== filtroSede) return false;
      // Filtro por cajero
      if (filtroCajero && arqueo.cajero_id !== filtroCajero) return false;
      
      return true;
    }).sort((a, b) => b.fecha.localeCompare(a.fecha)); // Ordenar por fecha descendente
  }, [arqueos, filtroAnio, filtroMes, filtroSede, filtroCajero]);

  // Obtener años disponibles para el filtro
  const aniosDisponibles = useMemo(() => {
    const anios = new Set();
    arqueos.forEach(a => {
      if (a.fecha) anios.add(a.fecha.substring(0, 4));
    });
    // Agregar año actual si no existe
    anios.add(new Date().getFullYear().toString());
    return Array.from(anios).sort((a, b) => b - a);
  }, [arqueos]);

  const iniciarNuevoArqueo = () => {
    setFormArqueo({
      tipo: "caja",
      sede_id: "",
      cajero_id: "",
      fecha: new Date().toISOString().split('T')[0],
      hora: obtenerHoraActual(),
      valor_inicial: 0,
      denominaciones: {
        billete_100000: 0, billete_50000: 0, billete_20000: 0, billete_10000: 0,
        billete_5000: 0, billete_2000: 0,
        moneda_1000: 0, moneda_500: 0, moneda_200: 0, moneda_100: 0, moneda_50: 0
      },
      datafono: 0,
      transferencia_bancolombia: 0,
      transferencia_davivienda: 0,
      transferencia_daviplata: 0,
      transferencia_nequi: 0,
      ventas_proceso: 0,
      gastos: 0,
      notas: ""
    });
    setArqueoSeleccionado(null);
    setModo("crear");
  };

  const editarArqueo = (arqueo) => {
    setFormArqueo({
      tipo: arqueo.tipo || "caja",
      sede_id: arqueo.sede_id || "",
      cajero_id: arqueo.cajero_id || "",
      fecha: arqueo.fecha || new Date().toISOString().split('T')[0],
      hora: arqueo.hora || obtenerHoraActual(),
      valor_inicial: arqueo.valor_inicial || 0,
      denominaciones: arqueo.denominaciones || {
        billete_100000: 0, billete_50000: 0, billete_20000: 0, billete_10000: 0,
        billete_5000: 0, billete_2000: 0,
        moneda_1000: 0, moneda_500: 0, moneda_200: 0, moneda_100: 0, moneda_50: 0
      },
      datafono: arqueo.datafono || 0,
      transferencia_bancolombia: arqueo.transferencia_bancolombia || 0,
      transferencia_davivienda: arqueo.transferencia_davivienda || 0,
      transferencia_daviplata: arqueo.transferencia_daviplata || 0,
      transferencia_nequi: arqueo.transferencia_nequi || 0,
      ventas_proceso: arqueo.ventas_proceso || 0,
      gastos: arqueo.gastos || 0,
      notas: arqueo.notas || ""
    });
    setArqueoSeleccionado(arqueo);
    setModo("editar");
  };

  const verArqueo = (arqueo) => {
    setArqueoSeleccionado(arqueo);
    setModo("ver");
  };

  // Calcular total efectivo desde denominaciones
  const calcularTotalEfectivo = (denominaciones = formArqueo.denominaciones) => {
    const d = denominaciones || {};
    return (
      (d.billete_100000 || 0) * 100000 +
      (d.billete_50000 || 0) * 50000 +
      (d.billete_20000 || 0) * 20000 +
      (d.billete_10000 || 0) * 10000 +
      (d.billete_5000 || 0) * 5000 +
      (d.billete_2000 || 0) * 2000 +
      (d.moneda_1000 || 0) * 1000 +
      (d.moneda_500 || 0) * 500 +
      (d.moneda_200 || 0) * 200 +
      (d.moneda_100 || 0) * 100 +
      (d.moneda_50 || 0) * 50
    );
  };

  const calcularTotalTransferencias = (data = formArqueo) => {
    return (
      (parseFloat(data.transferencia_bancolombia) || 0) +
      (parseFloat(data.transferencia_davivienda) || 0) +
      (parseFloat(data.transferencia_daviplata) || 0) +
      (parseFloat(data.transferencia_nequi) || 0)
    );
  };

  // Calcular lo que DEBERÍA QUEDAR en caja (Valor Inicial - Gastos)
  const calcularDeberiaQuedar = (data = formArqueo) => {
    const valorInicial = parseFloat(data.valor_inicial) || 0;
    const gastos = parseFloat(data.gastos) || 0;
    return valorInicial - gastos;
  };

  // Calcular diferencia: Efectivo Contado - Debería Quedar
  const calcularDiferencia = (data = formArqueo) => {
    const efectivoContado = calcularTotalEfectivo(data.denominaciones);
    const deberiaQuedar = calcularDeberiaQuedar(data);
    return efectivoContado - deberiaQuedar;
  };

  // Obtener estado del cuadre
  const getEstadoCuadre = (diferencia) => {
    if (diferencia > 0) return { texto: "SOBRANTE", color: "#27ae60", icono: "📈" };
    if (diferencia < 0) return { texto: "FALTANTE", color: "#e74c3c", icono: "📉" };
    return { texto: "CUADRADO", color: "#3498db", icono: "✅" };
  };

  const actualizarDenominacion = (key, valor) => {
    setFormArqueo({
      ...formArqueo,
      denominaciones: {
        ...formArqueo.denominaciones,
        [key]: parseInt(valor) || 0
      }
    });
  };

  const guardarArqueo = async () => {
    if (!formArqueo.sede_id) {
      setMensaje({ tipo: "error", texto: "Debe seleccionar una sede" });
      return;
    }
    if (!formArqueo.cajero_id) {
      setMensaje({ tipo: "error", texto: "Debe seleccionar un cajero" });
      return;
    }

    setCargando(true);
    try {
      const totalEfectivo = calcularTotalEfectivo();
      const totalTransferencias = calcularTotalTransferencias();
      const deberiaQuedar = calcularDeberiaQuedar();
      const diferencia = calcularDiferencia();
      const estadoCuadre = getEstadoCuadre(diferencia);

      const arqueoData = {
        id: arqueoSeleccionado?.id,
        tipo: formArqueo.tipo,
        sede_id: formArqueo.sede_id,
        cajero_id: formArqueo.cajero_id,
        fecha: formArqueo.fecha,
        hora: formArqueo.hora,
        valor_inicial: parseFloat(formArqueo.valor_inicial) || 0,
        denominaciones: formArqueo.denominaciones,
        total_efectivo: totalEfectivo,
        datafono: parseFloat(formArqueo.datafono) || 0,
        transferencia_bancolombia: parseFloat(formArqueo.transferencia_bancolombia) || 0,
        transferencia_davivienda: parseFloat(formArqueo.transferencia_davivienda) || 0,
        transferencia_daviplata: parseFloat(formArqueo.transferencia_daviplata) || 0,
        transferencia_nequi: parseFloat(formArqueo.transferencia_nequi) || 0,
        total_transferencias: totalTransferencias,
        ventas_proceso: parseFloat(formArqueo.ventas_proceso) || 0,
        gastos: parseFloat(formArqueo.gastos) || 0,
        total_general: totalEfectivo,
        diferencia: diferencia,
        estado_cuadre: estadoCuadre.texto,
        notas: formArqueo.notas,
        estado: "cerrado"
      };

      await guardarArqueoCajaEnSupabase(arqueoData, filtroEmpresa);
      setMensaje({ tipo: "exito", texto: arqueoSeleccionado ? "Arqueo actualizado" : "Arqueo guardado exitosamente" });
      await cargarArqueos();
      setModo("lista");
    } catch (error) {
      console.error("Error guardando arqueo:", error);
      setMensaje({ tipo: "error", texto: "Error al guardar el arqueo" });
    }
    setCargando(false);
  };

  const eliminarArqueo = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este arqueo?")) return;

    setCargando(true);
    try {
      await eliminarArqueoCajaEnSupabase(id);
      setMensaje({ tipo: "exito", texto: "Arqueo eliminado" });
      await cargarArqueos();
    } catch (error) {
      console.error("Error eliminando arqueo:", error);
      setMensaje({ tipo: "error", texto: "Error al eliminar el arqueo" });
    }
    setCargando(false);
  };

  // Función de impresión
  const imprimirArqueo = (arqueo) => {
    const sede = sedes.find(s => s.id === arqueo.sede_id);
    const cajero = empleados.find(e => e.id === arqueo.cajero_id);
    const valorInicial = arqueo.valor_inicial || 0;
    const gastos = arqueo.gastos || 0;
    const efectivoContado = arqueo.total_efectivo || 0;
    const deberiaQuedar = valorInicial - gastos;
    const diferencia = efectivoContado - deberiaQuedar;
    const estado = getEstadoCuadre(diferencia);
    
    const contenido = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Arqueo de Caja - ${arqueo.fecha}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { margin: 0; color: #333; }
          .header p { margin: 5px 0; color: #666; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 20px; }
          .info-box { background: #f5f5f5; padding: 12px; border-radius: 6px; }
          .info-box label { font-weight: bold; color: #555; font-size: 12px; display: block; margin-bottom: 4px; }
          .info-box span { font-size: 14px; color: #333; }
          .section { margin-bottom: 20px; }
          .section h3 { background: #34495e; color: white; padding: 8px 12px; margin: 0 0 10px 0; border-radius: 4px; font-size: 14px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { padding: 8px 12px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background: #f8f9fa; font-weight: bold; }
          .text-right { text-align: right; }
          .total-row { background: #e8f4f8; font-weight: bold; }
          .cuadre-box { padding: 20px; text-align: center; border-radius: 8px; margin-top: 20px; }
          .cuadre-sobrante { background: #d4edda; color: #155724; }
          .cuadre-faltante { background: #f8d7da; color: #721c24; }
          .cuadre-cuadrado { background: #cce5ff; color: #004085; }
          .footer { margin-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
          .firma { border-top: 1px solid #333; padding-top: 10px; text-align: center; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>💰 ARQUEO DE CAJA</h1>
          <p><strong>${sede?.nombre || "Sin sede"}</strong></p>
          <p>Fecha: ${arqueo.fecha} | Hora: ${arqueo.hora || "N/A"}</p>
        </div>

        <div class="info-grid">
          <div class="info-box">
            <label>Cajero</label>
            <span>${cajero ? `${cajero.nombre} ${cajero.apellido || ""}`.trim() : "Sin asignar"}</span>
          </div>
          <div class="info-box">
            <label>Tipo de Arqueo</label>
            <span>${arqueo.tipo === "caja" ? "Caja General" : "Sencilla"}</span>
          </div>
          <div class="info-box">
            <label>Valor Inicial (Base)</label>
            <span>$${valorInicial.toLocaleString('es-CO')}</span>
          </div>
          <div class="info-box">
            <label>Estado</label>
            <span>${arqueo.estado || "Cerrado"}</span>
          </div>
        </div>

        <div class="section">
          <h3>💵 CONTEO DE EFECTIVO</h3>
          <table>
            <thead>
              <tr><th>Denominación</th><th class="text-right">Cantidad</th><th class="text-right">Subtotal</th></tr>
            </thead>
            <tbody>
              ${Object.entries(arqueo.denominaciones || {}).filter(([key]) => key !== 'billete_1000').map(([key, cant]) => {
                const valores = { billete_100000: 100000, billete_50000: 50000, billete_20000: 20000, billete_10000: 10000, billete_5000: 5000, billete_2000: 2000, moneda_1000: 1000, moneda_500: 500, moneda_200: 200, moneda_100: 100, moneda_50: 50 };
                const nombres = { billete_100000: "$100.000", billete_50000: "$50.000", billete_20000: "$20.000", billete_10000: "$10.000", billete_5000: "$5.000", billete_2000: "$2.000", moneda_1000: "$1.000", moneda_500: "$500", moneda_200: "$200", moneda_100: "$100", moneda_50: "$50" };
                const subtotal = (cant || 0) * (valores[key] || 0);
                return cant > 0 ? `<tr><td>${nombres[key] || key}</td><td class="text-right">${cant}</td><td class="text-right">$${subtotal.toLocaleString('es-CO')}</td></tr>` : "";
              }).join("")}
              <tr class="total-row"><td colspan="2"><strong>TOTAL EFECTIVO CONTADO</strong></td><td class="text-right"><strong>$${efectivoContado.toLocaleString('es-CO')}</strong></td></tr>
            </tbody>
          </table>
        </div>

        <div class="section">
          <h3>💳 OTROS MEDIOS DE PAGO</h3>
          <table>
            <tbody>
              <tr><td>Datáfono</td><td class="text-right">$${(arqueo.datafono || 0).toLocaleString('es-CO')}</td></tr>
              <tr><td>Bancolombia</td><td class="text-right">$${(arqueo.transferencia_bancolombia || 0).toLocaleString('es-CO')}</td></tr>
              <tr><td>Davivienda</td><td class="text-right">$${(arqueo.transferencia_davivienda || 0).toLocaleString('es-CO')}</td></tr>
              <tr><td>Daviplata</td><td class="text-right">$${(arqueo.transferencia_daviplata || 0).toLocaleString('es-CO')}</td></tr>
              <tr><td>Nequi</td><td class="text-right">$${(arqueo.transferencia_nequi || 0).toLocaleString('es-CO')}</td></tr>
              <tr class="total-row"><td><strong>TOTAL TRANSFERENCIAS</strong></td><td class="text-right"><strong>$${(arqueo.total_transferencias || 0).toLocaleString('es-CO')}</strong></td></tr>
            </tbody>
          </table>
        </div>

        <div class="section">
          <h3>📊 RESUMEN DEL CUADRE</h3>
          <table>
            <tbody>
              <tr><td>Valor Inicial (Base)</td><td class="text-right">$${valorInicial.toLocaleString('es-CO')}</td></tr>
              <tr style="color: #e74c3c;"><td>(-) Gastos</td><td class="text-right">-$${gastos.toLocaleString('es-CO')}</td></tr>
              <tr class="total-row"><td><strong>= DEBERÍA QUEDAR</strong></td><td class="text-right"><strong>$${deberiaQuedar.toLocaleString('es-CO')}</strong></td></tr>
              <tr><td colspan="2" style="height: 10px;"></td></tr>
              <tr style="background: #e3f2fd;"><td><strong>EFECTIVO CONTADO</strong></td><td class="text-right"><strong>$${efectivoContado.toLocaleString('es-CO')}</strong></td></tr>
            </tbody>
          </table>
        </div>

        <div class="cuadre-box ${diferencia > 0 ? 'cuadre-sobrante' : diferencia < 0 ? 'cuadre-faltante' : 'cuadre-cuadrado'}">
          <h2 style="margin: 0;">${estado.icono} ${estado.texto}</h2>
          <p style="margin: 10px 0 0 0; font-size: 24px;">
            <strong>Diferencia: ${diferencia >= 0 ? '+' : ''}$${diferencia.toLocaleString('es-CO')}</strong>
          </p>
          <p style="margin: 5px 0 0 0; font-size: 14px; opacity: 0.8;">
            Debería Quedar: $${deberiaQuedar.toLocaleString('es-CO')} | Efectivo Contado: $${efectivoContado.toLocaleString('es-CO')}
          </p>
        </div>

        ${arqueo.notas ? `
        <div class="section" style="margin-top: 20px;">
          <h3>📋 OBSERVACIONES</h3>
          <p style="background: #f8f9fa; padding: 12px; border-radius: 6px;">${arqueo.notas}</p>
        </div>
        ` : ""}

        <div class="footer">
          <div class="firma">
            <p><strong>Cajero</strong></p>
            <p>${cajero ? `${cajero.nombre} ${cajero.apellido || ""}`.trim() : "_________________"}</p>
          </div>
          <div class="firma">
            <p><strong>Supervisor</strong></p>
            <p>_________________</p>
          </div>
        </div>

        <p style="text-align: center; margin-top: 30px; color: #999; font-size: 11px;">
          Impreso el ${new Date().toLocaleString('es-CO')}
        </p>
      </body>
      </html>
    `;

    const ventana = window.open("", "_blank");
    ventana.document.write(contenido);
    ventana.document.close();
    ventana.print();
  };

  const getNombreSede = (sedeId) => {
    const sede = sedes.find(s => s.id === sedeId);
    return sede ? sede.nombre : "Sin sede";
  };

  const getNombreCajero = (cajeroId) => {
    const emp = empleados.find(e => e.id === cajeroId);
    return emp ? `${emp.nombre} ${emp.apellido || ""}`.trim() : "Sin asignar";
  };

  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(valor || 0);
  };

  const getTurnoLabel = (turno) => {
    switch (turno) {
      case "mañana": return "🌅 Mañana";
      case "tarde": return "🌞 Tarde";
      case "noche": return "🌙 Noche";
      default: return turno;
    }
  };

  const meses = [
    { value: "", label: "Todos los meses" },
    { value: "01", label: "Enero" }, { value: "02", label: "Febrero" }, { value: "03", label: "Marzo" },
    { value: "04", label: "Abril" }, { value: "05", label: "Mayo" }, { value: "06", label: "Junio" },
    { value: "07", label: "Julio" }, { value: "08", label: "Agosto" }, { value: "09", label: "Septiembre" },
    { value: "10", label: "Octubre" }, { value: "11", label: "Noviembre" }, { value: "12", label: "Diciembre" }
  ];

  // Resumen de totales filtrados
  const resumenFiltrado = useMemo(() => {
    const total = arqueosFiltrados.reduce((acc, a) => {
      const valorInicial = a.valor_inicial || 0;
      const gastos = a.gastos || 0;
      const efectivoContado = a.total_efectivo || 0;
      const deberiaQuedar = valorInicial - gastos;
      const dif = efectivoContado - deberiaQuedar;
      return {
        valorInicial: acc.valorInicial + valorInicial,
        gastos: acc.gastos + gastos,
        deberiaQuedar: acc.deberiaQuedar + deberiaQuedar,
        efectivo: acc.efectivo + efectivoContado,
        diferencia: acc.diferencia + dif
      };
    }, { valorInicial: 0, gastos: 0, deberiaQuedar: 0, efectivo: 0, diferencia: 0 });
    return total;
  }, [arqueosFiltrados]);

  return (
    <div style={{ padding: "20px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "20px",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "20px",
        borderRadius: "12px",
        color: "white"
      }}>
        <div>
          <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: "10px" }}>
            💰 Arqueos de Caja
          </h2>
          <p style={{ margin: "5px 0 0 0", opacity: 0.9, fontSize: "14px" }}>
            Gestión de cuadres y arqueos diarios
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          {modo !== "lista" && (
            <button
              onClick={() => setModo("lista")}
              style={{
                padding: "10px 20px",
                backgroundColor: "rgba(255,255,255,0.2)",
                color: "white",
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: "6px",
                cursor: "pointer"
              }}
            >
              ← Volver
            </button>
          )}
          {modo === "lista" && tienePermiso("admin_arqueos_crear") && (
            <button
              onClick={iniciarNuevoArqueo}
              style={{
                padding: "10px 20px",
                backgroundColor: "#27ae60",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
            >
              ➕ Nuevo Arqueo
            </button>
          )}
        </div>
      </div>

      {mensaje.texto && (
        <div style={{
          padding: "12px 16px",
          marginBottom: "15px",
          borderRadius: "8px",
          backgroundColor: mensaje.tipo === "exito" ? "#d4edda" : "#f8d7da",
          color: mensaje.tipo === "exito" ? "#155724" : "#721c24",
          display: "flex",
          alignItems: "center",
          gap: "10px"
        }}>
          {mensaje.tipo === "exito" ? "✅" : "❌"} {mensaje.texto}
          <button onClick={() => setMensaje({ tipo: "", texto: "" })} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: "16px" }}>×</button>
        </div>
      )}

      {cargando && (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div style={{ fontSize: "40px", marginBottom: "10px" }}>⏳</div>
          <p>Cargando...</p>
        </div>
      )}

      {/* LISTA DE ARQUEOS */}
      {modo === "lista" && !cargando && (
        <div>
          {/* Filtros */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "15px",
            marginBottom: "20px",
            backgroundColor: "#f8f9fa",
            padding: "20px",
            borderRadius: "10px"
          }}>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold", color: "#555" }}>📅 Año</label>
              <select
                value={filtroAnio}
                onChange={(e) => setFiltroAnio(e.target.value)}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
              >
                {aniosDisponibles.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold", color: "#555" }}>📆 Mes</label>
              <select
                value={filtroMes}
                onChange={(e) => setFiltroMes(e.target.value)}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
              >
                {meses.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold", color: "#555" }}>🏢 Sede</label>
              <select
                value={filtroSede}
                onChange={(e) => setFiltroSede(e.target.value)}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
              >
                <option value="">Todas las sedes</option>
                {sedes.map(s => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold", color: "#555" }}>👤 Cajero</label>
              <select
                value={filtroCajero}
                onChange={(e) => setFiltroCajero(e.target.value)}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
              >
                <option value="">Todos los cajeros</option>
                {[...(empleados || [])].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map(e => (
                  <option key={e.id} value={e.id}>{e.nombre} {e.apellido || ""}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button
                onClick={() => { setFiltroMes(""); setFiltroSede(""); setFiltroCajero(""); }}
                style={{
                  padding: "10px 15px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  width: "100%"
                }}
              >
                🔄 Limpiar filtros
              </button>
            </div>
          </div>

          {/* Resumen de totales */}
          {arqueosFiltrados.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "15px",
              marginBottom: "20px"
            }}>
              <div style={{ backgroundColor: "#fff8e1", padding: "15px", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#ff8f00", fontWeight: "bold" }}>VALOR INICIAL</div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#e65100" }}>{formatearMoneda(resumenFiltrado.valorInicial)}</div>
              </div>
              <div style={{ backgroundColor: "#ffebee", padding: "15px", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#c62828", fontWeight: "bold" }}>(-) GASTOS</div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#b71c1c" }}>{formatearMoneda(resumenFiltrado.gastos)}</div>
              </div>
              <div style={{ backgroundColor: "#fff3e0", padding: "15px", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#e65100", fontWeight: "bold" }}>DEBERÍA QUEDAR</div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#bf360c" }}>{formatearMoneda(resumenFiltrado.deberiaQuedar)}</div>
              </div>
              <div style={{ backgroundColor: "#e3f2fd", padding: "15px", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#1565c0", fontWeight: "bold" }}>EFECTIVO CONTADO</div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#0d47a1" }}>{formatearMoneda(resumenFiltrado.efectivo)}</div>
              </div>
              <div style={{ 
                backgroundColor: resumenFiltrado.diferencia > 0 ? "#c8e6c9" : resumenFiltrado.diferencia < 0 ? "#ffcdd2" : "#bbdefb", 
                padding: "15px", 
                borderRadius: "8px", 
                textAlign: "center" 
              }}>
                <div style={{ fontSize: "12px", fontWeight: "bold", color: resumenFiltrado.diferencia > 0 ? "#1b5e20" : resumenFiltrado.diferencia < 0 ? "#b71c1c" : "#0d47a1" }}>
                  {resumenFiltrado.diferencia > 0 ? "📈 SOBRANTE" : resumenFiltrado.diferencia < 0 ? "📉 FALTANTE" : "✅ CUADRADO"}
                </div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: resumenFiltrado.diferencia > 0 ? "#1b5e20" : resumenFiltrado.diferencia < 0 ? "#b71c1c" : "#0d47a1" }}>
                  {resumenFiltrado.diferencia >= 0 ? "+" : ""}{formatearMoneda(resumenFiltrado.diferencia)}
                </div>
              </div>
            </div>
          )}

          {/* Info de resultados */}
          <p style={{ marginBottom: "10px", color: "#666" }}>
            Mostrando <strong>{arqueosFiltrados.length}</strong> arqueo(s) de <strong>{arqueos.length}</strong> total
          </p>

          {arqueosFiltrados.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#7f8c8d", backgroundColor: "white", borderRadius: "10px" }}>
              <p style={{ fontSize: "48px", margin: "0" }}>💰</p>
              <p>No hay arqueos que coincidan con los filtros</p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "white", borderRadius: "10px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                <thead>
                  <tr style={{ backgroundColor: "#34495e", color: "white" }}>
                    <th style={{ padding: "12px", textAlign: "left" }}>Fecha</th>
                    <th style={{ padding: "12px", textAlign: "left" }}>Sede</th>
                    <th style={{ padding: "12px", textAlign: "left" }}>Cajero</th>
                    <th style={{ padding: "12px", textAlign: "center" }}>Hora</th>
                    <th style={{ padding: "12px", textAlign: "right" }}>Efectivo</th>
                    <th style={{ padding: "12px", textAlign: "center" }}>Estado</th>
                    <th style={{ padding: "12px", textAlign: "center" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {arqueosFiltrados.map((arqueo, idx) => {
                    const valorInicial = arqueo.valor_inicial || 0;
                    const gastos = arqueo.gastos || 0;
                    const efectivoContado = arqueo.total_efectivo || 0;
                    const deberiaQuedar = valorInicial - gastos;
                    const diferencia = efectivoContado - deberiaQuedar;
                    const estado = getEstadoCuadre(diferencia);
                    
                    return (
                      <tr key={arqueo.id} style={{ backgroundColor: idx % 2 === 0 ? "#f9f9f9" : "white" }}>
                        <td style={{ padding: "12px" }}>{arqueo.fecha}</td>
                        <td style={{ padding: "12px" }}>{getNombreSede(arqueo.sede_id)}</td>
                        <td style={{ padding: "12px" }}>{getNombreCajero(arqueo.cajero_id)}</td>
                        <td style={{ padding: "12px", textAlign: "center" }}>{arqueo.hora || "-"}</td>
                        <td style={{ padding: "12px", textAlign: "right", fontWeight: "bold" }}>{formatearMoneda(efectivoContado)}</td>
                        <td style={{ padding: "12px", textAlign: "center" }}>
                          <span style={{
                            padding: "4px 10px",
                            borderRadius: "15px",
                            backgroundColor: estado.color,
                            color: "white",
                            fontSize: "12px",
                            fontWeight: "bold"
                          }}>
                            {estado.icono} {estado.texto}
                          </span>
                          <div style={{ fontSize: "11px", color: "#666", marginTop: "3px" }}>
                            {diferencia >= 0 ? "+" : ""}{formatearMoneda(diferencia)}
                          </div>
                        </td>
                        <td style={{ padding: "12px", textAlign: "center" }}>
                          <div style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                            <button
                              onClick={() => verArqueo(arqueo)}
                              title="Ver detalle"
                              style={{
                                padding: "6px 10px",
                                backgroundColor: "#17a2b8",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "14px"
                              }}
                            >
                              👁️
                            </button>
                            <button
                              onClick={() => imprimirArqueo(arqueo)}
                              title="Imprimir"
                              style={{
                                padding: "6px 10px",
                                backgroundColor: "#6c757d",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "14px"
                              }}
                            >
                              🖨️
                            </button>
                            {tienePermiso("admin_arqueos_editar") && esGerente && (
                              <button
                                onClick={() => editarArqueo(arqueo)}
                                title="Editar (Solo Gerencia)"
                                style={{
                                  padding: "6px 10px",
                                  backgroundColor: "#3498db",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  fontSize: "14px"
                                }}
                              >
                                ✏️
                              </button>
                            )}
                            {tienePermiso("admin_arqueos_eliminar") && esGerente && (
                              <button
                                onClick={() => eliminarArqueo(arqueo.id)}
                                title="Eliminar (Solo Gerencia)"
                                style={{
                                  padding: "6px 10px",
                                  backgroundColor: "#e74c3c",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  fontSize: "14px"
                                }}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* FORMULARIO CREAR/EDITAR */}
      {(modo === "crear" || modo === "editar") && (
        <div style={{ backgroundColor: "white", borderRadius: "10px", padding: "25px", boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}>
          <h3 style={{ marginTop: 0, borderBottom: "2px solid #667eea", paddingBottom: "10px" }}>
            {modo === "crear" ? "➕ Nuevo Arqueo de Caja" : "✏️ Editar Arqueo de Caja"}
          </h3>

          {/* Datos básicos */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "25px" }}>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", color: "#555" }}>Tipo *</label>
              <select
                value={formArqueo.tipo}
                onChange={(e) => setFormArqueo({ ...formArqueo, tipo: e.target.value })}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
              >
                <option value="caja">💵 Caja General</option>
                <option value="sencilla">🪙 Sencilla</option>
              </select>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", color: "#555" }}>Sede *</label>
              <select
                value={formArqueo.sede_id}
                onChange={(e) => setFormArqueo({ ...formArqueo, sede_id: e.target.value })}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
              >
                <option value="">Seleccionar sede...</option>
                {sedes.map(sede => (
                  <option key={sede.id} value={sede.id}>{sede.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", color: "#555" }}>Cajero *</label>
              <select
                value={formArqueo.cajero_id}
                onChange={(e) => setFormArqueo({ ...formArqueo, cajero_id: e.target.value })}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
              >
                <option value="">Seleccionar cajero...</option>
                {[...(empleados || [])].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.nombre} {emp.apellido || ""}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", color: "#555" }}>Fecha *</label>
              <input
                type="date"
                value={formArqueo.fecha}
                onChange={(e) => setFormArqueo({ ...formArqueo, fecha: e.target.value })}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", color: "#555" }}>🕐 Hora</label>
              <input
                type="text"
                value={formArqueo.hora}
                readOnly
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd", backgroundColor: "#f5f5f5", fontWeight: "bold", color: "#2c3e50" }}
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", color: "#555" }}>💰 Valor Inicial (Base)</label>
              <input
                type="number"
                value={formArqueo.valor_inicial}
                onChange={(e) => setFormArqueo({ ...formArqueo, valor_inicial: e.target.value })}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
                placeholder="0"
              />
            </div>
          </div>

          {/* Denominaciones de efectivo */}
          <div style={{ marginBottom: "25px", backgroundColor: "#f8f9fa", padding: "20px", borderRadius: "10px" }}>
            <h4 style={{ marginTop: 0, marginBottom: "15px", display: "flex", alignItems: "center", gap: "10px" }}>
              💵 Conteo de Efectivo
            </h4>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px" }}>
              {[
                { key: "billete_100000", label: "$100.000", valor: 100000, color: "#27ae60" },
                { key: "billete_50000", label: "$50.000", valor: 50000, color: "#2ecc71" },
                { key: "billete_20000", label: "$20.000", valor: 20000, color: "#f39c12" },
                { key: "billete_10000", label: "$10.000", valor: 10000, color: "#e67e22" },
                { key: "billete_5000", label: "$5.000", valor: 5000, color: "#9b59b6" },
                { key: "billete_2000", label: "$2.000", valor: 2000, color: "#3498db" },
                { key: "moneda_1000", label: "$1.000 (moneda)", valor: 1000, color: "#95a5a6" },
                { key: "moneda_500", label: "$500", valor: 500, color: "#7f8c8d" },
                { key: "moneda_200", label: "$200", valor: 200, color: "#bdc3c7" },
                { key: "moneda_100", label: "$100", valor: 100, color: "#95a5a6" },
                { key: "moneda_50", label: "$50", valor: 50, color: "#7f8c8d" }
              ].map(denom => (
                <div key={denom.key} style={{ backgroundColor: "white", padding: "12px", borderRadius: "8px", borderLeft: `4px solid ${denom.color}` }}>
                  <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold", color: "#333" }}>{denom.label}</label>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input
                      type="number"
                      min="0"
                      value={formArqueo.denominaciones[denom.key] || 0}
                      onChange={(e) => actualizarDenominacion(denom.key, e.target.value)}
                      style={{ width: "70px", padding: "8px", borderRadius: "4px", border: "1px solid #ddd", textAlign: "center" }}
                    />
                    <span style={{ fontSize: "12px", color: "#666", flex: 1 }}>
                      = {formatearMoneda((formArqueo.denominaciones[denom.key] || 0) * denom.valor)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: "15px", padding: "15px", backgroundColor: "#e3f2fd", borderRadius: "8px", textAlign: "right" }}>
              <span style={{ fontSize: "18px", fontWeight: "bold", color: "#1565c0" }}>
                Total Efectivo: {formatearMoneda(calcularTotalEfectivo())}
              </span>
            </div>
          </div>

          {/* Otros medios de pago */}
          <div style={{ marginBottom: "25px", backgroundColor: "#fff8e1", padding: "20px", borderRadius: "10px" }}>
            <h4 style={{ marginTop: 0, marginBottom: "15px" }}>💳 Otros Medios de Pago</h4>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "15px" }}>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold" }}>📟 Datáfono</label>
                <input
                  type="number"
                  value={formArqueo.datafono}
                  onChange={(e) => setFormArqueo({ ...formArqueo, datafono: e.target.value })}
                  style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
                  placeholder="0"
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold" }}>🏦 Bancolombia</label>
                <input
                  type="number"
                  value={formArqueo.transferencia_bancolombia}
                  onChange={(e) => setFormArqueo({ ...formArqueo, transferencia_bancolombia: e.target.value })}
                  style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
                  placeholder="0"
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold" }}>🏦 Davivienda</label>
                <input
                  type="number"
                  value={formArqueo.transferencia_davivienda}
                  onChange={(e) => setFormArqueo({ ...formArqueo, transferencia_davivienda: e.target.value })}
                  style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
                  placeholder="0"
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold" }}>📱 Daviplata</label>
                <input
                  type="number"
                  value={formArqueo.transferencia_daviplata}
                  onChange={(e) => setFormArqueo({ ...formArqueo, transferencia_daviplata: e.target.value })}
                  style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
                  placeholder="0"
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold" }}>📱 Nequi</label>
                <input
                  type="number"
                  value={formArqueo.transferencia_nequi}
                  onChange={(e) => setFormArqueo({ ...formArqueo, transferencia_nequi: e.target.value })}
                  style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
                  placeholder="0"
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold" }}>⏳ Ventas en Proceso</label>
                <input
                  type="number"
                  value={formArqueo.ventas_proceso}
                  onChange={(e) => setFormArqueo({ ...formArqueo, ventas_proceso: e.target.value })}
                  style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd" }}
                  placeholder="0"
                />
              </div>
            </div>
            <div style={{ marginTop: "15px", padding: "15px", backgroundColor: "#fff3e0", borderRadius: "8px", textAlign: "right" }}>
              <span style={{ fontSize: "16px", fontWeight: "bold", color: "#e65100" }}>
                Total Transferencias: {formatearMoneda(calcularTotalTransferencias())}
              </span>
            </div>
          </div>

          {/* Gastos */}
          <div style={{ marginBottom: "25px", backgroundColor: "#ffebee", padding: "20px", borderRadius: "10px" }}>
            <h4 style={{ marginTop: 0, marginBottom: "15px" }}>📤 Gastos</h4>
            <div>
              <label style={{ display: "block", marginBottom: "5px", fontSize: "13px", fontWeight: "bold" }}>💸 Valor Total Gastos</label>
              <input
                type="number"
                value={formArqueo.gastos}
                onChange={(e) => setFormArqueo({ ...formArqueo, gastos: e.target.value })}
                style={{ width: "100%", padding: "10px", borderRadius: "6px", border: "1px solid #ddd", maxWidth: "300px" }}
                placeholder="0"
              />
            </div>
          </div>

          {/* Notas */}
          <div style={{ marginBottom: "25px" }}>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", color: "#555" }}>📋 Observaciones</label>
            <textarea
              value={formArqueo.notas}
              onChange={(e) => setFormArqueo({ ...formArqueo, notas: e.target.value })}
              style={{ width: "100%", padding: "12px", borderRadius: "6px", border: "1px solid #ddd", minHeight: "80px" }}
              placeholder="Observaciones adicionales del arqueo..."
            />
          </div>

          {/* RESUMEN DEL CUADRE */}
          <div style={{ marginBottom: "25px", border: "2px solid #2c3e50", borderRadius: "12px", overflow: "hidden" }}>
            <div style={{ backgroundColor: "#2c3e50", color: "white", padding: "12px 20px" }}>
              <h4 style={{ margin: 0 }}>📊 RESUMEN DEL CUADRE</h4>
            </div>
            <div style={{ padding: "20px", backgroundColor: "#ecf0f1" }}>
              {/* Lo que DEBERÍA QUEDAR */}
              <div style={{ marginBottom: "15px", padding: "15px", backgroundColor: "#fff8e1", borderRadius: "8px", border: "2px solid #ffc107" }}>
                <div style={{ textAlign: "center", marginBottom: "10px", fontSize: "14px", color: "#ff8f00", fontWeight: "bold" }}>
                  📋 LO QUE DEBERÍA QUEDAR EN CAJA
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #ffe082" }}>
                  <span>💰 Valor Inicial (Base):</span>
                  <strong style={{ color: "#5d4037" }}>{formatearMoneda(parseFloat(formArqueo.valor_inicial) || 0)}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #ffe082", color: "#c62828" }}>
                  <span>📤 (-) Gastos:</span>
                  <strong>- {formatearMoneda(parseFloat(formArqueo.gastos) || 0)}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", backgroundColor: "#ffecb3", margin: "10px -15px -15px", borderRadius: "0 0 6px 6px" }}>
                  <span style={{ fontWeight: "bold", color: "#e65100" }}>= DEBERÍA QUEDAR:</span>
                  <strong style={{ fontSize: "18px", color: "#e65100" }}>{formatearMoneda(calcularDeberiaQuedar())}</strong>
                </div>
              </div>
              
              {/* Lo que REALMENTE HAY */}
              <div style={{ marginBottom: "15px", padding: "15px", backgroundColor: "#e3f2fd", borderRadius: "8px", border: "2px solid #2196f3" }}>
                <div style={{ textAlign: "center", marginBottom: "10px", fontSize: "14px", color: "#1565c0", fontWeight: "bold" }}>
                  💵 LO QUE REALMENTE HAY (EFECTIVO CONTADO)
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: "#0d47a1" }}>{formatearMoneda(calcularTotalEfectivo())}</div>
                </div>
              </div>
              
              {/* Comparación */}
              <div style={{ marginBottom: "15px", padding: "15px", backgroundColor: "#f5f5f5", borderRadius: "8px" }}>
                <div style={{ textAlign: "center", marginBottom: "10px", fontSize: "14px", color: "#666" }}>
                  ⚖️ COMPARACIÓN
                </div>
                <div style={{ display: "flex", justifyContent: "space-around", alignItems: "center" }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "12px", color: "#666" }}>Efectivo Contado</div>
                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#0d47a1" }}>{formatearMoneda(calcularTotalEfectivo())}</div>
                  </div>
                  <div style={{ fontSize: "24px", color: "#9e9e9e" }}>vs</div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "12px", color: "#666" }}>Debería Quedar</div>
                    <div style={{ fontSize: "20px", fontWeight: "bold", color: "#e65100" }}>{formatearMoneda(calcularDeberiaQuedar())}</div>
                  </div>
                </div>
              </div>
              
              {/* Estado del cuadre */}
              {(() => {
                const diferencia = calcularDiferencia();
                const estado = getEstadoCuadre(diferencia);
                const efectivoContado = calcularTotalEfectivo();
                return (
                  <>
                    <div style={{ 
                      padding: "20px", 
                      backgroundColor: estado.color, 
                      color: "white", 
                      borderRadius: "8px", 
                      textAlign: "center",
                      marginBottom: "15px"
                    }}>
                      <div style={{ fontSize: "28px", marginBottom: "5px" }}>{estado.icono}</div>
                      <div style={{ fontSize: "22px", fontWeight: "bold", marginBottom: "10px" }}>{estado.texto}</div>
                      <div style={{ fontSize: "28px", fontWeight: "bold" }}>
                        {diferencia >= 0 ? "+" : ""}{formatearMoneda(diferencia)}
                      </div>
                    </div>
                    
                    {/* Total en Caja */}
                    <div style={{ 
                      padding: "15px", 
                      backgroundColor: "#1a237e", 
                      color: "white", 
                      borderRadius: "8px", 
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "14px", marginBottom: "5px" }}>💰 EFECTIVO EN CAJA</div>
                      <div style={{ fontSize: "26px", fontWeight: "bold" }}>{formatearMoneda(efectivoContado)}</div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <button
              onClick={() => setModo("lista")}
              style={{
                padding: "12px 25px",
                backgroundColor: "#95a5a6",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer"
              }}
            >
              Cancelar
            </button>
            <button
              onClick={guardarArqueo}
              disabled={cargando}
              style={{
                padding: "12px 25px",
                backgroundColor: "#27ae60",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "bold"
              }}
            >
              💾 Guardar Arqueo
            </button>
          </div>
        </div>
      )}

      {/* VISTA DETALLE COMPLETA */}
      {modo === "ver" && arqueoSeleccionado && (() => {
        const valorInicial = arqueoSeleccionado.valor_inicial || 0;
        const gastos = arqueoSeleccionado.gastos || 0;
        const efectivoContado = arqueoSeleccionado.total_efectivo || 0;
        const deberiaQuedar = valorInicial - gastos;
        const diferencia = efectivoContado - deberiaQuedar;
        const estado = getEstadoCuadre(diferencia);
        
        return (
          <div style={{ backgroundColor: "white", borderRadius: "10px", padding: "25px", boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}>
            {/* Header con acciones */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", borderBottom: "2px solid #667eea", paddingBottom: "15px" }}>
              <h3 style={{ margin: 0 }}>📋 Detalle del Arqueo de Caja</h3>
              <button
                onClick={() => imprimirArqueo(arqueoSeleccionado)}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px"
                }}
              >
                🖨️ Imprimir
              </button>
            </div>

            {/* Info básica */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", marginBottom: "25px" }}>
              <div style={{ backgroundColor: "#f8f9fa", padding: "15px", borderRadius: "8px" }}>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}>📅 Fecha</div>
                <div style={{ fontSize: "16px", fontWeight: "bold" }}>{arqueoSeleccionado.fecha}</div>
              </div>
              <div style={{ backgroundColor: "#f8f9fa", padding: "15px", borderRadius: "8px" }}>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}>🏢 Sede</div>
                <div style={{ fontSize: "16px", fontWeight: "bold" }}>{getNombreSede(arqueoSeleccionado.sede_id)}</div>
              </div>
              <div style={{ backgroundColor: "#f8f9fa", padding: "15px", borderRadius: "8px" }}>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}>👤 Cajero</div>
                <div style={{ fontSize: "16px", fontWeight: "bold" }}>{getNombreCajero(arqueoSeleccionado.cajero_id)}</div>
              </div>
              <div style={{ backgroundColor: "#f8f9fa", padding: "15px", borderRadius: "8px" }}>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}>🕐 Hora</div>
                <div style={{ fontSize: "16px", fontWeight: "bold" }}>{arqueoSeleccionado.hora || "-"}</div>
              </div>
              <div style={{ backgroundColor: "#f8f9fa", padding: "15px", borderRadius: "8px" }}>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}>📌 Tipo</div>
                <div style={{ fontSize: "16px", fontWeight: "bold" }}>{arqueoSeleccionado.tipo === "caja" ? "Caja General" : "Sencilla"}</div>
              </div>
              <div style={{ backgroundColor: "#e3f2fd", padding: "15px", borderRadius: "8px" }}>
                <div style={{ fontSize: "12px", color: "#1565c0", marginBottom: "5px" }}>💰 Valor Inicial</div>
                <div style={{ fontSize: "16px", fontWeight: "bold", color: "#0d47a1" }}>{formatearMoneda(arqueoSeleccionado.valor_inicial)}</div>
              </div>
            </div>

            {/* Conteo de efectivo */}
            <div style={{ marginBottom: "25px", backgroundColor: "#f8f9fa", padding: "20px", borderRadius: "10px" }}>
              <h4 style={{ marginTop: 0, marginBottom: "15px" }}>💵 Conteo de Efectivo</h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "10px" }}>
                {[
                  { key: "billete_100000", label: "$100.000", valor: 100000 },
                  { key: "billete_50000", label: "$50.000", valor: 50000 },
                  { key: "billete_20000", label: "$20.000", valor: 20000 },
                  { key: "billete_10000", label: "$10.000", valor: 10000 },
                  { key: "billete_5000", label: "$5.000", valor: 5000 },
                  { key: "billete_2000", label: "$2.000", valor: 2000 },
                  { key: "moneda_1000", label: "$1.000 (M)", valor: 1000 },
                  { key: "moneda_500", label: "$500", valor: 500 },
                  { key: "moneda_200", label: "$200", valor: 200 },
                  { key: "moneda_100", label: "$100", valor: 100 },
                  { key: "moneda_50", label: "$50", valor: 50 }
                ].filter(d => (arqueoSeleccionado.denominaciones?.[d.key] || 0) > 0).map(denom => {
                  const cant = arqueoSeleccionado.denominaciones?.[denom.key] || 0;
                  return (
                    <div key={denom.key} style={{ backgroundColor: "white", padding: "10px", borderRadius: "6px", textAlign: "center" }}>
                      <div style={{ fontSize: "12px", color: "#666" }}>{denom.label}</div>
                      <div style={{ fontSize: "16px", fontWeight: "bold" }}>{cant} x</div>
                      <div style={{ fontSize: "13px", color: "#2196f3" }}>{formatearMoneda(cant * denom.valor)}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: "15px", padding: "12px", backgroundColor: "#e3f2fd", borderRadius: "8px", textAlign: "right" }}>
                <strong style={{ fontSize: "18px", color: "#1565c0" }}>Total Efectivo: {formatearMoneda(arqueoSeleccionado.total_efectivo)}</strong>
              </div>
            </div>

            {/* Otros medios */}
            <div style={{ marginBottom: "25px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "15px" }}>
              <div style={{ backgroundColor: "#fce4ec", padding: "15px", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#c2185b" }}>📟 Datáfono</div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#880e4f" }}>{formatearMoneda(arqueoSeleccionado.datafono)}</div>
              </div>
              <div style={{ backgroundColor: "#e8f5e9", padding: "15px", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#2e7d32" }}>🏦 Bancolombia</div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#1b5e20" }}>{formatearMoneda(arqueoSeleccionado.transferencia_bancolombia)}</div>
              </div>
              <div style={{ backgroundColor: "#fff3e0", padding: "15px", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#e65100" }}>🏦 Davivienda</div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#bf360c" }}>{formatearMoneda(arqueoSeleccionado.transferencia_davivienda)}</div>
              </div>
              <div style={{ backgroundColor: "#e0f7fa", padding: "15px", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#00838f" }}>📱 Daviplata</div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#006064" }}>{formatearMoneda(arqueoSeleccionado.transferencia_daviplata)}</div>
              </div>
              <div style={{ backgroundColor: "#f3e5f5", padding: "15px", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#7b1fa2" }}>📱 Nequi</div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#4a148c" }}>{formatearMoneda(arqueoSeleccionado.transferencia_nequi)}</div>
              </div>
              <div style={{ backgroundColor: "#fffde7", padding: "15px", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "12px", color: "#f57f17" }}>⏳ Ventas Proceso</div>
                <div style={{ fontSize: "18px", fontWeight: "bold", color: "#ff6f00" }}>{formatearMoneda(arqueoSeleccionado.ventas_proceso)}</div>
              </div>
            </div>

            {/* Gastos */}
            {(arqueoSeleccionado.gastos > 0) && (
              <div style={{ marginBottom: "25px", backgroundColor: "#ffebee", padding: "20px", borderRadius: "10px" }}>
                <h4 style={{ marginTop: 0, marginBottom: "10px", color: "#c62828" }}>📤 Gastos</h4>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: "20px", fontWeight: "bold", color: "#c62828" }}>
                    -{formatearMoneda(arqueoSeleccionado.gastos)}
                  </div>
                </div>
              </div>
            )}

            {/* CUADRE FINAL */}
            <div style={{ border: "3px solid #2c3e50", borderRadius: "12px", overflow: "hidden", marginBottom: "20px" }}>
              <div style={{ backgroundColor: "#2c3e50", color: "white", padding: "15px 20px" }}>
                <h4 style={{ margin: 0, textAlign: "center" }}>📊 RESULTADO DEL CUADRE</h4>
              </div>
              <div style={{ padding: "20px" }}>
                {/* Lo que DEBERÍA QUEDAR */}
                <div style={{ marginBottom: "15px", padding: "15px", backgroundColor: "#fff8e1", borderRadius: "8px", border: "2px solid #ffc107" }}>
                  <div style={{ textAlign: "center", marginBottom: "10px", fontSize: "14px", color: "#ff8f00", fontWeight: "bold" }}>
                    📋 LO QUE DEBERÍA QUEDAR EN CAJA
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #ffe082" }}>
                    <span>💰 Valor Inicial (Base):</span>
                    <strong style={{ color: "#5d4037" }}>{formatearMoneda(valorInicial)}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #ffe082", color: "#c62828" }}>
                    <span>📤 (-) Gastos:</span>
                    <strong>- {formatearMoneda(gastos)}</strong>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "12px", backgroundColor: "#ffecb3", margin: "10px -15px -15px", borderRadius: "0 0 6px 6px" }}>
                    <span style={{ fontWeight: "bold", color: "#e65100" }}>= DEBERÍA QUEDAR:</span>
                    <strong style={{ fontSize: "18px", color: "#e65100" }}>{formatearMoneda(deberiaQuedar)}</strong>
                  </div>
                </div>

                {/* Lo que REALMENTE HAY */}
                <div style={{ marginBottom: "15px", padding: "15px", backgroundColor: "#e3f2fd", borderRadius: "8px", border: "2px solid #2196f3" }}>
                  <div style={{ textAlign: "center", marginBottom: "10px", fontSize: "14px", color: "#1565c0", fontWeight: "bold" }}>
                    💵 LO QUE REALMENTE HAY (EFECTIVO CONTADO)
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "28px", fontWeight: "bold", color: "#0d47a1" }}>{formatearMoneda(efectivoContado)}</div>
                  </div>
                </div>

                {/* Comparación */}
                <div style={{ marginBottom: "15px", padding: "15px", backgroundColor: "#f5f5f5", borderRadius: "8px" }}>
                  <div style={{ textAlign: "center", marginBottom: "10px", fontSize: "14px", color: "#666" }}>
                    ⚖️ COMPARACIÓN
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-around", alignItems: "center" }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "12px", color: "#666" }}>Efectivo Contado</div>
                      <div style={{ fontSize: "22px", fontWeight: "bold", color: "#0d47a1" }}>{formatearMoneda(efectivoContado)}</div>
                    </div>
                    <div style={{ fontSize: "24px", color: "#9e9e9e" }}>vs</div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "12px", color: "#666" }}>Debería Quedar</div>
                      <div style={{ fontSize: "22px", fontWeight: "bold", color: "#e65100" }}>{formatearMoneda(deberiaQuedar)}</div>
                    </div>
                  </div>
                </div>
                
                {/* Estado */}
                <div style={{ 
                  padding: "20px", 
                  backgroundColor: estado.color, 
                  color: "white", 
                  borderRadius: "10px", 
                  textAlign: "center",
                  marginBottom: "15px"
                }}>
                  <div style={{ fontSize: "40px", marginBottom: "10px" }}>{estado.icono}</div>
                  <div style={{ fontSize: "28px", fontWeight: "bold", marginBottom: "10px" }}>{estado.texto}</div>
                  <div style={{ fontSize: "28px", fontWeight: "bold" }}>
                    {diferencia >= 0 ? "+" : ""}{formatearMoneda(diferencia)}
                  </div>
                </div>

                {/* Total en Caja */}
                <div style={{ 
                  padding: "15px", 
                  backgroundColor: "#1a237e", 
                  color: "white", 
                  borderRadius: "8px", 
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "14px", marginBottom: "5px" }}>💰 EFECTIVO EN CAJA</div>
                  <div style={{ fontSize: "26px", fontWeight: "bold" }}>{formatearMoneda(efectivoContado)}</div>
                </div>
              </div>
            </div>

            {/* Notas */}
            {arqueoSeleccionado.notas && (
              <div style={{ marginBottom: "20px", backgroundColor: "#f5f5f5", padding: "15px", borderRadius: "8px" }}>
                <strong>📋 Observaciones:</strong>
                <p style={{ margin: "10px 0 0 0" }}>{arqueoSeleccionado.notas}</p>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

/* ====================== REVISAR CUADRE ====================== */

function RevisarCuadreRoot({ sedes, empleados, usuarioActual = null, empleadoActual = null, esGerente = false, tienePermiso = () => true, filtroEmpresa = null }) {
  const [modo, setModo] = useState("seleccionar"); // "seleccionar" | "revisar" | "consultar" | "sobrantes"
  const [cuadresDisponibles, setCuadresDisponibles] = useState([]);
  const [revisionesGuardadas, setRevisionesGuardadas] = useState([]);
  const [revisionesCerradas, setRevisionesCerradas] = useState([]); // TODAS las revisiones cerradas (aprobadas/descuadre) - sin filtrar por permisos
  const [cuadreSeleccionado, setCuadreSeleccionado] = useState(null);
  const [revisionActual, setRevisionActual] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [asignaciones, setAsignaciones] = useState([]);
  const [todasLasAsignaciones, setTodasLasAsignaciones] = useState([]); // Todas las asignaciones del usuario
  const [sobrantesPorSede, setSobrantesPorSede] = useState({}); // Sobrantes agrupados por sede y semana
  const [sobrantesFiltrados, setSobrantesFiltrados] = useState([]); // Sobrantes filtrados para mostrar
  
  // Filtros para consulta
  const [filtroSede, setFiltroSede] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroFechaDesde, setFiltroFechaDesde] = useState("");
  const [filtroFechaHasta, setFiltroFechaHasta] = useState("");
  const [filtroEmpleado, setFiltroEmpleado] = useState("");
  
  // Filtros para sobrantes
  const [filtroSobranteSede, setFiltroSobranteSede] = useState("");
  const [filtroSobranteFechaDesde, setFiltroSobranteFechaDesde] = useState("");
  const [filtroSobranteFechaHasta, setFiltroSobranteFechaHasta] = useState("");
  
  // Modal de asignación
  const [mostrarModalAsignacion, setMostrarModalAsignacion] = useState(false);
  const [seccionParaAsignar, setSeccionParaAsignar] = useState("");
  const [empleadoAsignacion, setEmpleadoAsignacion] = useState("");
  
  // Modal para asignar revisión del historial a empleados
  const [mostrarModalAsignarRevision, setMostrarModalAsignarRevision] = useState(false);
  const [revisionParaAsignar, setRevisionParaAsignar] = useState(null);
  const [empleadosAsignadosRevision, setEmpleadosAsignadosRevision] = useState([]);
  const [empleadoNuevoAsignacion, setEmpleadoNuevoAsignacion] = useState("");
  
  // Modal para agregar sobrante manual
  const [mostrarModalSobranteManual, setMostrarModalSobranteManual] = useState(false);
  const [sobranteManualSede, setSobranteManualSede] = useState("");
  const [sobranteManualFecha, setSobranteManualFecha] = useState("");
  const [sobranteManualValor, setSobranteManualValor] = useState("");
  const [sobranteManualConcepto, setSobranteManualConcepto] = useState("");
  
  // Modo solo lectura para empleados viendo revisiones cerradas
  const [modoSoloLectura, setModoSoloLectura] = useState(false);
  
  // Modal para resumen ejecutivo
  const [mostrarModalResumen, setMostrarModalResumen] = useState(false);
  const [resumenTipo, setResumenTipo] = useState("todas"); // "todas" o "sede"
  const [resumenSedeSeleccionada, setResumenSedeSeleccionada] = useState("");
  const [resumenPeriodo, setResumenPeriodo] = useState("fechas"); // "fechas" o "mes"
  const [resumenFechaDesde, setResumenFechaDesde] = useState("");
  const [resumenFechaHasta, setResumenFechaHasta] = useState("");
  const [resumenMes, setResumenMes] = useState("");
  const [resumenAnio, setResumenAnio] = useState(new Date().getFullYear().toString());
  const [resumenGenerado, setResumenGenerado] = useState(null);
  const [resumenesGuardados, setResumenesGuardados] = useState([]);
  const [mostrarResumenesGuardados, setMostrarResumenesGuardados] = useState(false);

  const miId = empleadoActual?.id || usuarioActual?.id || "";
  const miNombre = empleadoActual?.nombre || usuarioActual?.nombre || "Usuario";

  // Secciones del cuadre para revisar (sin denominaciones)
  const SECCIONES = [
    { key: 'facturas', nombre: 'Facturas', icono: '🧾', tieneItems: true },
    { key: 'domicilios', nombre: 'Domicilios', icono: '🛵', tieneItems: true },
    { key: 'transferencias', nombre: 'Transferencias', icono: '💸', tieneItems: true },
    { key: 'turnos', nombre: 'Turnos', icono: '👥', tieneItems: true },
    { key: 'adelantos', nombre: 'Adelantos', icono: '💵', tieneItems: true },
    { key: 'novedades', nombre: 'Novedades', icono: '📝', tieneItems: true },
    { key: 'datafonos', nombre: 'Datáfonos', icono: '💳', tieneItems: true },
    { key: 'ingresos_varios', nombre: 'Ingresos Varios', icono: '💲', tieneItems: true },
    { key: 'ventas_descuadre', nombre: 'Ventas y Descuadre', icono: '💰', tieneItems: false }
  ];

  // Función para formatear valores como moneda
  const formatearMoneda = (valor) => {
    const num = parseFloat(valor) || 0;
    return num.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // Cargar cuadres disponibles para revisar
  useEffect(() => {
    cargarCuadresDisponibles();
    cargarMisAsignaciones(); // Primero cargar asignaciones
    cargarSobrantes();
  }, [filtroEmpresa]);

  // Cuando las asignaciones estén listas, cargar las revisiones
  useEffect(() => {
    // Solo cargar revisiones cuando ya tengamos el miId (para empleados) 
    // o si es gerente (no necesita filtrar)
    if (esGerente || tienePermiso("admin_revisar_cuadre") || miId) {
      cargarRevisiones();
    }
  }, [todasLasAsignaciones, esGerente]);

  // Cargar todas las asignaciones del usuario actual
  const cargarMisAsignaciones = async () => {
    if (!miId) return;
    try {
      // Cargar todas las asignaciones donde el usuario es el empleado asignado
      const { data, error } = await supabase
        .from('asignaciones_revision')
        .select('*')
        .eq('empleado_id', miId);
      
      if (!error && data) {
        setTodasLasAsignaciones(data);
      }
    } catch (e) {
      console.warn("Error cargando mis asignaciones:", e);
    }
  };

  // Cargar sobrantes por sede con filtros
  const cargarSobrantes = async (filtros = {}) => {
    try {
      let query = supabase
        .from('sobrantes_efectivo')
        .select('*')
        .order('semana_inicio', { ascending: false });
      
      // Aplicar filtro de sede
      if (filtros.sede) {
        query = query.eq('sede', filtros.sede);
      }
      
      // Aplicar filtro de fecha desde (semana_inicio >= fecha)
      if (filtros.fechaDesde) {
        query = query.gte('semana_inicio', filtros.fechaDesde);
      }
      
      // Aplicar filtro de fecha hasta (semana_fin <= fecha)
      if (filtros.fechaHasta) {
        query = query.lte('semana_fin', filtros.fechaHasta);
      }
      
      const { data, error } = await query;
      
      if (!error && data) {
        // Agrupar por sede
        const agrupado = {};
        data.forEach(s => {
          if (!agrupado[s.sede]) agrupado[s.sede] = [];
          agrupado[s.sede].push(s);
        });
        setSobrantesPorSede(agrupado);
        setSobrantesFiltrados(data);
      }
    } catch (e) {
      console.warn("Error cargando sobrantes:", e);
    }
  };
  
  // Aplicar filtros de sobrantes
  const aplicarFiltrosSobrantes = () => {
    const filtros = {};
    if (filtroSobranteSede) filtros.sede = filtroSobranteSede;
    if (filtroSobranteFechaDesde) filtros.fechaDesde = filtroSobranteFechaDesde;
    if (filtroSobranteFechaHasta) filtros.fechaHasta = filtroSobranteFechaHasta;
    cargarSobrantes(filtros);
  };
  
  // Limpiar filtros de sobrantes
  const limpiarFiltrosSobrantes = () => {
    setFiltroSobranteSede("");
    setFiltroSobranteFechaDesde("");
    setFiltroSobranteFechaHasta("");
    cargarSobrantes();
  };

  const cargarCuadresDisponibles = async () => {
    setCargando(true);
    try {
      const cuadres = await cargarCuadresDiariosDeSupabase(filtroEmpresa);
      // Filtrar solo cuadres finales
      const finales = cuadres.filter(c => c.estado === "final");
      setCuadresDisponibles(finales);
      
      // 🔹 Cargar TODAS las revisiones cerradas (aprobadas/descuadre) sin filtrar por permisos
      // Esto es para saber qué cuadres ya fueron revisados y no deben aparecer en pendientes
      try {
        const { data: revCerradas, error } = await supabase
          .from('revisiones_cuadre')
          .select('id, cuadre_id, estado')
          .in('estado', ['aprobado', 'descuadre', 'con_descuadre']);
        
        if (!error && revCerradas) {
          setRevisionesCerradas(revCerradas);
          console.log("📋 Revisiones cerradas cargadas:", revCerradas.length);
        } else if (error) {
          console.warn("Error cargando revisiones cerradas:", error);
        }
      } catch (e2) {
        console.warn("Error cargando revisiones cerradas:", e2);
      }
    } catch (e) {
      console.error("Error cargando cuadres:", e);
    }
    setCargando(false);
  };

  const cargarRevisiones = async () => {
    try {
      const filtros = {};
      if (filtroSede) filtros.sede = filtroSede;
      if (filtroEstado) filtros.estado = filtroEstado;
      if (filtroFechaDesde) filtros.fechaDesde = filtroFechaDesde;
      if (filtroFechaHasta) filtros.fechaHasta = filtroFechaHasta;
      if (filtroEmpleado) filtros.empleadoId = filtroEmpleado;
      
      let revisiones = await cargarRevisionesCuadresDeSupabase(filtroEmpresa, filtros);
      
      // Verificar si el usuario puede ver el historial completo
      // IMPORTANTE: admin_revisar_cuadre solo da acceso al módulo, NO al historial completo
      // Para ver historial completo se necesita: esGerente O admin_revisar_cuadre_ver_historial
      const puedeVerHistorialCompleto = esGerente || 
        tienePermiso("admin_revisar_cuadre_ver_historial");
      
      console.log("🔍 Verificación historial - esGerente:", esGerente, 
        "| tienePermiso ver_historial:", tienePermiso("admin_revisar_cuadre_ver_historial"),
        "| puedeVerHistorialCompleto:", puedeVerHistorialCompleto);
      
      // Si NO puede ver historial completo, filtrar SOLO las revisiones asignadas
      if (!puedeVerHistorialCompleto) {
        // Obtener IDs de revisiones asignadas al usuario actual
        const revisionesAsignadasIds = (todasLasAsignaciones || [])
          .filter(a => a.revision_id) // Solo las que tienen revision_id
          .map(a => a.revision_id);
        
        console.log("📋 Asignaciones del usuario:", todasLasAsignaciones);
        console.log("📋 IDs de revisiones asignadas:", revisionesAsignadasIds);
        console.log("📋 Total revisiones antes del filtro:", revisiones.length);
        
        // Filtrar: solo mostrar revisiones donde el usuario tiene asignación
        revisiones = revisiones.filter(r => {
          const tieneAsignacion = revisionesAsignadasIds.includes(r.id);
          return tieneAsignacion;
        });
        
        console.log("Revisiones filtradas para empleado:", revisiones.length);
      }
      
      setRevisionesGuardadas(revisiones);
    } catch (e) {
      console.error("Error cargando revisiones:", e);
    }
  };

  // Iniciar revisión de un cuadre
  const iniciarRevision = async (cuadre) => {
    // Verificar permisos antes de continuar
    const tieneAsignacionParaRevision = todasLasAsignaciones.some(a => {
      // La asignación puede ser por revision_id o por cuadre_id
      return a.cuadre_id === cuadre.id || a.revision_id;
    });
    
    if (!esGerente && !tienePermiso("admin_revisar_cuadre") && !tieneAsignacionParaRevision) {
      alert("No tiene permisos para revisar este cuadre. Debe tener una asignación específica.");
      return;
    }
    
    // Resetear modo solo lectura al iniciar revisión normal
    setModoSoloLectura(false);
    
    setCargando(true);
    
    // Verificar si ya existe una revisión para este cuadre
    let revisionExistente = null;
    try {
      revisionExistente = await verificarRevisionExistente(cuadre.id);
    } catch (e) {
      console.warn("No se pudo verificar revisión existente:", e);
    }
    
    // Si existe una revisión, verificar que el usuario tenga asignación para esa revisión específica
    if (revisionExistente && !esGerente && !tienePermiso("admin_revisar_cuadre")) {
      const tieneAsignacionRevision = todasLasAsignaciones.some(a => a.revision_id === revisionExistente.id);
      if (!tieneAsignacionRevision) {
        alert("No tiene asignación para esta revisión específica.");
        setCargando(false);
        return;
      }
    }
    
    if (revisionExistente) {
      // Cargar revisión existente
      let revisionCompleta = null;
      try {
        revisionCompleta = await cargarRevisionCuadrePorId(revisionExistente.id);
      } catch (e) {
        console.warn("No se pudo cargar revisión existente:", e);
      }
      
      if (revisionCompleta) {
        setRevisionActual(revisionCompleta);
        let asignacionesRev = [];
        try {
          asignacionesRev = await cargarAsignacionesRevision(revisionCompleta.id);
        } catch (e) {
          console.warn("No se pudieron cargar asignaciones:", e);
        }
        setAsignaciones(asignacionesRev);
        setCuadreSeleccionado(cuadre);
        setModo("revisar");
        setCargando(false);
        return;
      }
    }
    
    // Crear nueva revisión
    const nuevaRevision = {
      id: `rev_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      cuadre_id: cuadre.id,
      fecha_revision: new Date().toISOString(),
      estado: 'en_revision',
      revisor_principal_id: miId,
      revisor_principal_nombre: miNombre,
      sede: cuadre.sede,
      fecha_cuadre: cuadre.fecha,
      empleado_cuadre_id: cuadre.empleadoId,
      empleado_cuadre_nombre: obtenerNombreEmpleado(cuadre.empleadoId),
      observaciones_generales: '',
      revision_facturas: { aprobado: null, observaciones: '', items: [] },
      revision_domicilios: { aprobado: null, observaciones: '', items: [] },
      revision_transferencias: { aprobado: null, observaciones: '', items: [] },
      revision_turnos: { aprobado: null, observaciones: '', items: [] },
      revision_adelantos: { aprobado: null, observaciones: '', items: [] },
      revision_denominaciones: { aprobado: null, observaciones: '' },
      revision_novedades: { aprobado: null, observaciones: '', items: [] },
      revision_datafonos: { aprobado: null, observaciones: '', items: [] },
      revision_ventas_descuadre: { aprobado: null, observaciones: '' },
      total_cuadre: 0,
      diferencia: 0
    };
    
    // Intentar guardar en Supabase, pero si falla, trabajar localmente
    let guardada = null;
    try {
      guardada = await guardarRevisionCuadreEnSupabase(nuevaRevision, filtroEmpresa);
    } catch (e) {
      console.error("Error guardando en Supabase, trabajando localmente:", e);
    }
    
    // Usar la versión guardada o la local si Supabase falló
    const revisionFinal = guardada || nuevaRevision;
    setRevisionActual(revisionFinal);
    setAsignaciones([]);
    setCuadreSeleccionado(cuadre);
    setModo("revisar");
    
    if (!guardada) {
      console.warn("⚠️ Trabajando en modo local. La tabla revisiones_cuadre puede no existir en Supabase.");
    }
    setCargando(false);
  };

  const obtenerNombreEmpleado = (id) => {
    const emp = empleados.find(e => String(e.id) === String(id));
    return emp?.nombre || "Desconocido";
  };

  // Guardar cambios de revisión
  const guardarRevision = async () => {
    if (!revisionActual) return;
    setGuardando(true);
    
    let resultado = null;
    try {
      resultado = await guardarRevisionCuadreEnSupabase(revisionActual, filtroEmpresa);
    } catch (e) {
      console.error("Error guardando revisión:", e);
    }
    
    if (resultado) {
      alert("✅ Revisión guardada correctamente");
      await cargarRevisiones();
    } else {
      alert("⚠️ No se pudo guardar en la base de datos. Verifique que la tabla 'revisiones_cuadre' exista en Supabase.");
    }
    setGuardando(false);
  };

  // Cerrar revisión (solo gerencia)
  const cerrarRevision = async (estadoFinal) => {
    if (!esGerente && !tienePermiso("admin_revisar_cuadre_cerrar")) {
      alert("Solo gerencia puede cerrar la revisión");
      return;
    }
    
    if (!revisionActual) return;
    
    const confirmacion = window.confirm(
      `¿Está seguro de cerrar esta revisión como "${estadoFinal.toUpperCase()}"?\n\nEsta acción quedará registrada.`
    );
    
    if (!confirmacion) return;
    
    setGuardando(true);
    
    // Agregar al historial de acciones
    const historialActual = revisionActual.historial_acciones || [];
    const nuevaAccion = {
      accion: 'cerrar',
      estado_nuevo: estadoFinal,
      fecha: new Date().toISOString(),
      usuario_id: miId,
      usuario_nombre: miNombre
    };
    
    const revisionCerrada = {
      ...revisionActual,
      estado: estadoFinal,
      fecha_cierre: new Date().toISOString(),
      cerrado_por_id: miId,
      cerrado_por_nombre: miNombre,
      historial_acciones: [...historialActual, nuevaAccion]
    };
    
    const resultado = await guardarRevisionCuadreEnSupabase(revisionCerrada, filtroEmpresa);
    
    if (resultado) {
      alert(`✅ Revisión cerrada como: ${estadoFinal.toUpperCase()}`);
      
      // 🔹 Actualizar la lista de revisiones cerradas inmediatamente
      setRevisionesCerradas(prev => [...prev, {
        id: revisionCerrada.id,
        cuadre_id: revisionCerrada.cuadre_id,
        estado: estadoFinal
      }]);
      
      setRevisionActual(null);
      setCuadreSeleccionado(null);
      setModo("consultar");
      await cargarRevisiones();
      await cargarCuadresDisponibles(); // Recargar para actualizar lista de pendientes
    } else {
      alert("❌ Error al cerrar la revisión");
    }
    setGuardando(false);
  };
  
  // Ver revisión en modo solo lectura (para empleados con asignación)
  const verRevisionSoloLectura = async (revision) => {
    setCargando(true);
    try {
      // Cargar la revisión completa
      const revisionCompleta = await cargarRevisionCuadrePorId(revision.id);
      
      if (revisionCompleta) {
        setRevisionActual(revisionCompleta);
        setModoSoloLectura(true); // Activar modo solo lectura
        
        // Buscar el cuadre correspondiente
        const cuadre = cuadresDisponibles.find(c => c.id === revision.cuadre_id);
        if (cuadre) {
          setCuadreSeleccionado(cuadre);
        }
        
        // Cargar asignaciones de la revisión
        let asignacionesRev = [];
        try {
          asignacionesRev = await cargarAsignacionesRevision(revisionCompleta.id);
        } catch (e) {
          console.warn("No se pudieron cargar asignaciones:", e);
        }
        setAsignaciones(asignacionesRev);
        
        setModo("revisar");
      } else {
        alert("No se pudo cargar la revisión");
      }
    } catch (error) {
      console.error("Error al cargar revisión:", error);
      alert("Error al cargar la revisión");
    }
    setCargando(false);
  };
  
  // Reabrir revisión (solo gerencia)
  const reabrirRevision = async (revision) => {
    if (!esGerente && !tienePermiso("admin_revisar_cuadre_reabrir")) {
      alert("Solo gerencia puede reabrir revisiones");
      return;
    }
    
    const confirmacion = window.confirm(
      `¿Está seguro de REABRIR esta revisión?\n\nLa revisión volverá al estado "En Revisión" y quedará registrado este cambio.`
    );
    
    if (!confirmacion) return;
    
    setGuardando(true);
    
    // Agregar al historial de acciones
    const historialActual = revision.historial_acciones || [];
    const nuevaAccion = {
      accion: 'reabrir',
      estado_anterior: revision.estado,
      estado_nuevo: 'en_revision',
      fecha: new Date().toISOString(),
      usuario_id: miId,
      usuario_nombre: miNombre
    };
    
    const revisionReabierta = {
      ...revision,
      estado: 'en_revision',
      fecha_reapertura: new Date().toISOString(),
      reabierto_por_id: miId,
      reabierto_por_nombre: miNombre,
      historial_acciones: [...historialActual, nuevaAccion]
    };
    
    const resultado = await guardarRevisionCuadreEnSupabase(revisionReabierta, filtroEmpresa);
    
    if (resultado) {
      alert(`✅ Revisión reabierta correctamente. Ahora puede editarla y volver a cerrarla.`);
      
      // 🔹 Remover de la lista de revisiones cerradas para que aparezca en pendientes
      setRevisionesCerradas(prev => prev.filter(r => r.cuadre_id !== revision.cuadre_id));
      
      await cargarRevisiones();
      await cargarCuadresDisponibles(); // Recargar para actualizar lista
      
      // Cargar automáticamente el cuadre y la revisión reabierta para edición
      const cuadreAsociado = cuadresDisponibles.find(c => c.id === revision.cuadre_id);
      if (cuadreAsociado) {
        setCuadreSeleccionado(cuadreAsociado);
        setRevisionActual(revisionReabierta);
        setModo("revisar");
      }
    } else {
      alert("❌ Error al reabrir la revisión");
    }
    setGuardando(false);
  };
  
  // Asignar empleado a ver una revisión específica
  const asignarEmpleadoARevision = async () => {
    if (!revisionParaAsignar || !empleadoNuevoAsignacion) {
      alert("Seleccione un empleado");
      return;
    }
    
    const empleadoSeleccionado = empleados.find(e => String(e.id) === empleadoNuevoAsignacion);
    
    const nuevaAsignacion = {
      id: `asig_rev_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      revision_id: revisionParaAsignar.id,
      empleado_id: empleadoNuevoAsignacion,
      empleado_nombre: empleadoSeleccionado?.nombre || "Desconocido",
      seccion: 'todas', // Acceso a todas las secciones
      estado: 'asignado',
      asignado_por_id: miId,
      asignado_por_nombre: miNombre,
      fecha_asignacion: new Date().toISOString(),
      tipo: 'vista_revision' // Tipo especial para ver revisión completa
    };
    
    try {
      await guardarAsignacionRevision(nuevaAsignacion, filtroEmpresa);
      
      // Actualizar lista de empleados asignados
      setEmpleadosAsignadosRevision([...empleadosAsignadosRevision, nuevaAsignacion]);
      setEmpleadoNuevoAsignacion("");
      alert(`✅ ${empleadoSeleccionado?.nombre} puede ver esta revisión`);
    } catch (e) {
      console.error("Error asignando empleado:", e);
      alert("❌ Error al asignar empleado");
    }
  };
  
  // Cargar empleados asignados a una revisión
  const cargarEmpleadosAsignadosRevision = async (revisionId) => {
    try {
      const asignados = await cargarAsignacionesRevision(revisionId);
      const asignadosVista = asignados.filter(a => a.tipo === 'vista_revision' || a.seccion === 'todas');
      setEmpleadosAsignadosRevision(asignadosVista);
    } catch (e) {
      console.warn("Error cargando asignaciones:", e);
      setEmpleadosAsignadosRevision([]);
    }
  };
  
  // Abrir modal para asignar revisión
  const abrirModalAsignarRevision = async (revision) => {
    setRevisionParaAsignar(revision);
    await cargarEmpleadosAsignadosRevision(revision.id);
    setMostrarModalAsignarRevision(true);
  };
  
  // Eliminar asignación de revisión
  const eliminarAsignacionRevision = async (asignacionId) => {
    try {
      await supabase
        .from('asignaciones_revision')
        .delete()
        .eq('id', asignacionId);
      
      setEmpleadosAsignadosRevision(empleadosAsignadosRevision.filter(a => a.id !== asignacionId));
      alert("✅ Asignación eliminada");
    } catch (e) {
      console.error("Error eliminando asignación:", e);
    }
  };

  // Festivos de Colombia (lunes festivos) - Se actualiza cada año
  // Incluye los festivos que caen en lunes o se trasladan al lunes
  const getFestivosColombiaLunes = (year) => {
    // Festivos fijos que se trasladan al siguiente lunes si no caen en lunes
    // y festivos móviles (Ley Emiliani)
    const festivos = [];
    
    // Función auxiliar para agregar festivo trasladado al lunes
    const agregarFestivoLunes = (fecha) => {
      const d = new Date(fecha);
      const day = d.getDay();
      if (day === 1) {
        // Ya es lunes
        festivos.push(d.toISOString().split('T')[0]);
      } else if (day === 0) {
        // Domingo, se traslada al lunes siguiente
        d.setDate(d.getDate() + 1);
        festivos.push(d.toISOString().split('T')[0]);
      } else if (day >= 2 && day <= 6) {
        // Martes a sábado, se traslada al siguiente lunes
        d.setDate(d.getDate() + (8 - day));
        festivos.push(d.toISOString().split('T')[0]);
      }
    };
    
    // Festivos fijos en Colombia
    festivos.push(`${year}-01-01`); // Año Nuevo (si cae lunes)
    agregarFestivoLunes(`${year}-01-06`); // Reyes Magos - Ley Emiliani
    agregarFestivoLunes(`${year}-03-19`); // San José - Ley Emiliani
    festivos.push(`${year}-05-01`); // Día del Trabajo (si cae lunes)
    agregarFestivoLunes(`${year}-06-29`); // San Pedro y San Pablo - Ley Emiliani
    festivos.push(`${year}-07-20`); // Día de la Independencia (si cae lunes)
    festivos.push(`${year}-08-07`); // Batalla de Boyacá (si cae lunes)
    agregarFestivoLunes(`${year}-08-15`); // Asunción de la Virgen - Ley Emiliani
    agregarFestivoLunes(`${year}-10-12`); // Día de la Raza - Ley Emiliani
    agregarFestivoLunes(`${year}-11-01`); // Todos los Santos - Ley Emiliani
    agregarFestivoLunes(`${year}-11-11`); // Independencia de Cartagena - Ley Emiliani
    festivos.push(`${year}-12-08`); // Inmaculada Concepción (si cae lunes)
    festivos.push(`${year}-12-25`); // Navidad (si cae lunes)
    
    // Festivos móviles (dependen de Semana Santa)
    // Calcular Pascua usando el algoritmo de computus
    const calcularPascua = (year) => {
      const a = year % 19;
      const b = Math.floor(year / 100);
      const c = year % 100;
      const d = Math.floor(b / 4);
      const e = b % 4;
      const f = Math.floor((b + 8) / 25);
      const g = Math.floor((b - f + 1) / 3);
      const h = (19 * a + b - d - g + 15) % 30;
      const i = Math.floor(c / 4);
      const k = c % 4;
      const l = (32 + 2 * e + 2 * i - h - k) % 7;
      const m = Math.floor((a + 11 * h + 22 * l) / 451);
      const month = Math.floor((h + l - 7 * m + 114) / 31);
      const day = ((h + l - 7 * m + 114) % 31) + 1;
      return new Date(year, month - 1, day);
    };
    
    const pascua = calcularPascua(year);
    
    // Jueves Santo (3 días antes de Pascua)
    const juevesSanto = new Date(pascua);
    juevesSanto.setDate(pascua.getDate() - 3);
    
    // Viernes Santo (2 días antes de Pascua)
    const viernesSanto = new Date(pascua);
    viernesSanto.setDate(pascua.getDate() - 2);
    
    // Ascensión del Señor (39 días después de Pascua, trasladado al lunes)
    const ascension = new Date(pascua);
    ascension.setDate(pascua.getDate() + 39);
    agregarFestivoLunes(ascension.toISOString().split('T')[0]);
    
    // Corpus Christi (60 días después de Pascua, trasladado al lunes)
    const corpus = new Date(pascua);
    corpus.setDate(pascua.getDate() + 60);
    agregarFestivoLunes(corpus.toISOString().split('T')[0]);
    
    // Sagrado Corazón (68 días después de Pascua, trasladado al lunes)
    const sagradoCorazon = new Date(pascua);
    sagradoCorazon.setDate(pascua.getDate() + 68);
    agregarFestivoLunes(sagradoCorazon.toISOString().split('T')[0]);
    
    // Verificar si los festivos fijos caen en lunes
    const verificarLunes = (fechaStr) => {
      const d = new Date(fechaStr + 'T00:00:00');
      return d.getDay() === 1;
    };
    
    // Filtrar solo los lunes festivos
    const lunesFestivos = festivos.filter(f => verificarLunes(f));
    
    return lunesFestivos;
  };
  
  // Verificar si una fecha es lunes festivo en Colombia
  const esLunesFestivo = (fecha) => {
    const d = new Date(fecha + 'T00:00:00');
    const year = d.getFullYear();
    const festivos = getFestivosColombiaLunes(year);
    return festivos.includes(fecha);
  };

  // Obtener inicio de semana (lunes o martes si lunes es festivo) para una fecha
  const obtenerInicioSemana = (fecha) => {
    const d = new Date(fecha);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Ajustar para lunes
    const lunes = new Date(d.setDate(diff));
    lunes.setHours(0, 0, 0, 0);
    const lunesStr = lunes.toISOString().split('T')[0];
    
    // Verificar si el lunes es festivo en Colombia
    if (esLunesFestivo(lunesStr)) {
      // Si es festivo, la semana comienza el martes
      const martes = new Date(lunes);
      martes.setDate(martes.getDate() + 1);
      return martes.toISOString().split('T')[0];
    }
    
    return lunesStr;
  };

  // Obtener fin de semana (domingo) para una fecha
  const obtenerFinSemana = (fecha) => {
    const d = new Date(fecha);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Obtener lunes base
    const lunes = new Date(d.setDate(diff));
    const domingo = new Date(lunes);
    domingo.setDate(domingo.getDate() + 6);
    return domingo.toISOString().split('T')[0];
  };

  // Guardar o actualizar sobrante en la tabla
  const guardarSobranteEnTabla = async (sede, fecha, sobrante, cuadreId) => {
    const sobranteNum = parseFloat(sobrante) || 0;
    if (sobranteNum === 0) return; // No guardar si no hay sobrante
    
    const semanaInicio = obtenerInicioSemana(fecha);
    const semanaFin = obtenerFinSemana(fecha);
    
    try {
      // Buscar si ya existe un registro para esta sede y semana
      const { data: existente, error: errorBuscar } = await supabase
        .from('sobrantes_efectivo')
        .select('*')
        .eq('sede', sede)
        .eq('semana_inicio', semanaInicio)
        .single();
      
      if (existente && !errorBuscar) {
        // Actualizar registro existente
        const detallesActuales = existente.detalles || [];
        // Verificar si ya existe este cuadre
        const yaExiste = detallesActuales.some(d => d.cuadre_id === cuadreId);
        if (yaExiste) {
          console.log("Sobrante ya registrado para este cuadre");
          return;
        }
        
        const nuevosDetalles = [...detallesActuales, {
          cuadre_id: cuadreId,
          fecha: fecha,
          sobrante: sobranteNum,
          registrado_por: miNombre,
          registrado_fecha: new Date().toISOString()
        }];
        
        const nuevoTotal = nuevosDetalles.reduce((sum, d) => sum + (parseFloat(d.sobrante) || 0), 0);
        
        await supabase
          .from('sobrantes_efectivo')
          .update({
            total_sobrante: nuevoTotal,
            detalles: nuevosDetalles,
            updated_at: new Date().toISOString()
          })
          .eq('id', existente.id);
          
      } else {
        // Crear nuevo registro
        await supabase
          .from('sobrantes_efectivo')
          .insert({
            sede: sede,
            semana_inicio: semanaInicio,
            semana_fin: semanaFin,
            total_sobrante: sobranteNum,
            detalles: [{
              cuadre_id: cuadreId,
              fecha: fecha,
              sobrante: sobranteNum,
              registrado_por: miNombre,
              registrado_fecha: new Date().toISOString()
            }],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
      }
      
      // Recargar sobrantes
      await cargarSobrantes();
      console.log(`✅ Sobrante de ${formatearMoneda(sobranteNum)} guardado para sede ${sede}`);
      
    } catch (e) {
      console.error("Error guardando sobrante:", e);
    }
  };

  // Guardar sobrante manual (adicional al cuadre)
  const guardarSobranteManual = async () => {
    if (!sobranteManualSede || !sobranteManualFecha || !sobranteManualValor || !sobranteManualConcepto) {
      alert("Complete todos los campos para agregar el sobrante");
      return;
    }
    
    const valorNum = parseFloat(sobranteManualValor) || 0;
    if (valorNum === 0) {
      alert("El valor debe ser diferente de cero");
      return;
    }
    
    const semanaInicio = obtenerInicioSemana(sobranteManualFecha);
    const semanaFin = obtenerFinSemana(sobranteManualFecha);
    const idManual = `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    setGuardando(true);
    
    try {
      // Buscar si ya existe un registro para esta sede y semana
      const { data: existente, error: errorBuscar } = await supabase
        .from('sobrantes_efectivo')
        .select('*')
        .eq('sede', sobranteManualSede)
        .eq('semana_inicio', semanaInicio)
        .maybeSingle();
      
      if (existente && !errorBuscar) {
        // Actualizar registro existente
        const detallesActuales = existente.detalles || [];
        
        const nuevosDetalles = [...detallesActuales, {
          id: idManual,
          tipo: 'manual',
          fecha: sobranteManualFecha,
          sobrante: valorNum,
          concepto: sobranteManualConcepto,
          registrado_por: miNombre,
          registrado_fecha: new Date().toISOString()
        }];
        
        const nuevoTotal = nuevosDetalles.reduce((sum, d) => sum + (parseFloat(d.sobrante) || 0), 0);
        
        const { error: errorUpdate } = await supabase
          .from('sobrantes_efectivo')
          .update({
            total_sobrante: nuevoTotal,
            detalles: nuevosDetalles,
            updated_at: new Date().toISOString()
          })
          .eq('id', existente.id);
          
        if (errorUpdate) {
          console.error("Error actualizando sobrante:", errorUpdate);
          alert("Error al guardar el sobrante: " + errorUpdate.message);
        } else {
          alert(`✅ Sobrante manual de ${formatearMoneda(valorNum)} agregado correctamente`);
          cerrarModalSobranteManual();
          await cargarSobrantes();
        }
          
      } else {
        // Crear nuevo registro
        const { error: errorInsert } = await supabase
          .from('sobrantes_efectivo')
          .insert({
            sede: sobranteManualSede,
            semana_inicio: semanaInicio,
            semana_fin: semanaFin,
            total_sobrante: valorNum,
            detalles: [{
              id: idManual,
              tipo: 'manual',
              fecha: sobranteManualFecha,
              sobrante: valorNum,
              concepto: sobranteManualConcepto,
              registrado_por: miNombre,
              registrado_fecha: new Date().toISOString()
            }],
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
          
        if (errorInsert) {
          console.error("Error insertando sobrante:", errorInsert);
          alert("Error al guardar el sobrante: " + errorInsert.message);
        } else {
          alert(`✅ Sobrante manual de ${formatearMoneda(valorNum)} agregado correctamente`);
          cerrarModalSobranteManual();
          await cargarSobrantes();
        }
      }
      
    } catch (e) {
      console.error("Error guardando sobrante manual:", e);
      alert("Error inesperado al guardar el sobrante");
    }
    
    setGuardando(false);
  };
  
  // Cerrar modal de sobrante manual
  const cerrarModalSobranteManual = () => {
    setMostrarModalSobranteManual(false);
    setSobranteManualSede("");
    setSobranteManualFecha("");
    setSobranteManualValor("");
    setSobranteManualConcepto("");
  };

  // Generar resumen ejecutivo
  const generarResumenEjecutivo = async () => {
    let fechaInicio, fechaFin;
    
    if (resumenPeriodo === "mes") {
      const mes = parseInt(resumenMes);
      const anio = parseInt(resumenAnio);
      fechaInicio = `${anio}-${String(mes).padStart(2, '0')}-01`;
      const ultimoDia = new Date(anio, mes, 0).getDate();
      fechaFin = `${anio}-${String(mes).padStart(2, '0')}-${String(ultimoDia).padStart(2, '0')}`;
    } else {
      fechaInicio = resumenFechaDesde;
      fechaFin = resumenFechaHasta;
    }
    
    if (!fechaInicio || !fechaFin) {
      alert("Seleccione el período para el resumen");
      return;
    }
    
    setGuardando(true);
    
    try {
      // Obtener sobrantes filtrados
      let query = supabase
        .from('sobrantes_efectivo')
        .select('*')
        .gte('semana_inicio', fechaInicio)
        .lte('semana_fin', fechaFin)
        .order('sede', { ascending: true })
        .order('semana_inicio', { ascending: true });
      
      if (resumenTipo === "sede" && resumenSedeSeleccionada) {
        query = query.eq('sede', resumenSedeSeleccionada);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error("Error obteniendo datos:", error);
        alert("Error al obtener los datos");
        setGuardando(false);
        return;
      }
      
      // Procesar datos por sede
      const porSede = {};
      let totalGeneral = 0;
      let totalCuadres = 0;
      let totalManuales = 0;
      
      (data || []).forEach(semana => {
        if (!porSede[semana.sede]) {
          porSede[semana.sede] = {
            sede: semana.sede,
            total: 0,
            semanas: [],
            cuadres: 0,
            manuales: 0
          };
        }
        
        const totalSemana = parseFloat(semana.total_sobrante) || 0;
        porSede[semana.sede].total += totalSemana;
        totalGeneral += totalSemana;
        
        const cuadresSemana = (semana.detalles || []).filter(d => d.tipo !== 'manual').length;
        const manualesSemana = (semana.detalles || []).filter(d => d.tipo === 'manual').length;
        
        porSede[semana.sede].cuadres += cuadresSemana;
        porSede[semana.sede].manuales += manualesSemana;
        totalCuadres += cuadresSemana;
        totalManuales += manualesSemana;
        
        porSede[semana.sede].semanas.push({
          semana_inicio: semana.semana_inicio,
          semana_fin: semana.semana_fin,
          total: totalSemana,
          detalles: semana.detalles || []
        });
      });
      
      const resumen = {
        id: `resumen_${Date.now()}`,
        tipo: resumenTipo,
        sede: resumenTipo === "sede" ? resumenSedeSeleccionada : "Todas las sedes",
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        periodo: resumenPeriodo === "mes" ? `${obtenerNombreMes(parseInt(resumenMes))} ${resumenAnio}` : `${fechaInicio} al ${fechaFin}`,
        total_general: totalGeneral,
        total_cuadres: totalCuadres,
        total_manuales: totalManuales,
        por_sede: Object.values(porSede),
        generado_por_id: miId,
        generado_por_nombre: miNombre,
        fecha_generacion: new Date().toISOString(),
        empresa_id: filtroEmpresa
      };
      
      setResumenGenerado(resumen);
      
    } catch (e) {
      console.error("Error generando resumen:", e);
      alert("Error al generar el resumen");
    }
    
    setGuardando(false);
  };
  
  // Obtener nombre del mes
  const obtenerNombreMes = (mes) => {
    const meses = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return meses[mes] || '';
  };
  
  // Guardar resumen ejecutivo
  const guardarResumenEjecutivo = async () => {
    if (!resumenGenerado) return;
    
    setGuardando(true);
    
    try {
      const { error } = await supabase
        .from('resumenes_ejecutivos_sobrantes')
        .insert({
          ...resumenGenerado,
          created_at: new Date().toISOString()
        });
      
      if (error) {
        console.error("Error guardando resumen:", error);
        alert("Error al guardar el resumen: " + error.message);
      } else {
        alert("✅ Resumen guardado correctamente");
        await cargarResumenesGuardados();
      }
    } catch (e) {
      console.error("Error:", e);
      alert("Error inesperado al guardar");
    }
    
    setGuardando(false);
  };
  
  // Cargar resúmenes guardados
  const cargarResumenesGuardados = async () => {
    try {
      const { data, error } = await supabase
        .from('resumenes_ejecutivos_sobrantes')
        .select('*')
        .eq('empresa_id', filtroEmpresa)
        .order('fecha_generacion', { ascending: false })
        .limit(50);
      
      if (!error && data) {
        setResumenesGuardados(data);
      }
    } catch (e) {
      console.warn("Error cargando resúmenes:", e);
    }
  };
  
  // Imprimir resumen ejecutivo
  const imprimirResumen = () => {
    if (!resumenGenerado) return;
    
    const contenido = generarHTMLResumen(resumenGenerado);
    const ventana = window.open('', '_blank');
    ventana.document.write(contenido);
    ventana.document.close();
    ventana.print();
  };
  
  // Generar HTML para impresión
  const generarHTMLResumen = (resumen) => {
    const sedesHTML = resumen.por_sede.map(s => `
      <div style="margin-bottom: 20px; border: 1px solid #ddd; border-radius: 8px; overflow: hidden;">
        <div style="background: #4caf50; color: white; padding: 12px;">
          <strong style="font-size: 16px;">🏪 ${s.sede}</strong>
          <span style="float: right; font-size: 18px; font-weight: bold;">
            ${formatearMoneda(s.total)}
          </span>
        </div>
        <div style="padding: 12px;">
          <p style="margin: 0 0 10px; color: #666;">
            📋 ${s.cuadres} cuadre(s) | ✏️ ${s.manuales} manual(es)
          </p>
          ${s.semanas.map(sem => `
            <div style="margin-bottom: 10px; padding: 10px; background: #f5f5f5; border-radius: 4px;">
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                <span>📅 ${new Date(sem.semana_inicio + 'T00:00:00').toLocaleDateString('es-CO')} - ${new Date(sem.semana_fin + 'T00:00:00').toLocaleDateString('es-CO')}</span>
                <strong style="color: ${sem.total >= 0 ? '#4caf50' : '#f44336'};">
                  ${formatearMoneda(sem.total)}
                </strong>
              </div>
              ${sem.detalles.map(d => `
                <div style="padding: 4px 0; border-bottom: 1px solid #eee; display: flex; justify-content: space-between;">
                  <span>
                    <span style="background: ${d.tipo === 'manual' ? '#fff3e0' : '#e3f2fd'}; padding: 2px 6px; border-radius: 4px; font-size: 11px;">
                      ${d.tipo === 'manual' ? '✏️ Manual' : '📋 Cuadre'}
                    </span>
                    <strong style="margin-left: 8px;">
                      ${new Date(d.fecha + 'T00:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                    </strong>
                    ${d.concepto ? `<em style="margin-left: 8px; color: #666;">"${d.concepto}"</em>` : ''}
                  </span>
                  <strong style="color: ${(parseFloat(d.sobrante) || 0) >= 0 ? '#4caf50' : '#f44336'};">
                    ${formatearMoneda(d.sobrante || 0)}
                  </strong>
                </div>
              `).join('')}
            </div>
          `).join('')}
        </div>
      </div>
    `).join('');
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Resumen Ejecutivo de Sobrantes</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="margin: 0; color: #1a237e;">📊 Resumen Ejecutivo de Sobrantes</h1>
          <p style="color: #666; margin: 10px 0;">
            ${resumen.sede} | Período: ${resumen.periodo}
          </p>
          <p style="font-size: 12px; color: #999;">
            Generado el ${new Date(resumen.fecha_generacion).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })} por ${resumen.generado_por_nombre}
          </p>
        </div>
        
        <div style="background: #4caf50; color: white; padding: 20px; border-radius: 12px; text-align: center; margin-bottom: 30px;">
          <p style="margin: 0; font-size: 14px;">TOTAL GENERAL</p>
          <p style="margin: 10px 0 0; font-size: 32px; font-weight: bold;">
            ${formatearMoneda(resumen.total_general)}
          </p>
          <p style="margin: 10px 0 0; font-size: 12px; opacity: 0.9;">
            📋 ${resumen.total_cuadres} cuadres | ✏️ ${resumen.total_manuales} manuales
          </p>
        </div>
        
        <h2 style="color: #1a237e; border-bottom: 2px solid #1a237e; padding-bottom: 10px;">
          Detalle por Sede
        </h2>
        
        ${sedesHTML}
        
        <div style="margin-top: 30px; text-align: center; color: #999; font-size: 11px;">
          Documento generado automáticamente - Sistema de Gestión de Cuadres
        </div>
      </body>
      </html>
    `;
  };
  
  // Cerrar modal de resumen
  const cerrarModalResumen = () => {
    setMostrarModalResumen(false);
    setResumenGenerado(null);
    setResumenTipo("todas");
    setResumenSedeSeleccionada("");
    setResumenPeriodo("fechas");
    setResumenFechaDesde("");
    setResumenFechaHasta("");
    setResumenMes("");
  };

  // Actualizar sección de revisión
  const actualizarSeccionRevision = async (seccion, datos) => {
    if (!revisionActual) return;
    
    // Si se está aprobando la sección de ventas_descuadre, guardar el sobrante
    if (seccion === 'ventas_descuadre' && datos.aprobado === true && cuadreSeleccionado) {
      const sobrante = parseFloat(cuadreSeleccionado.ventas?.sobrante) || 0;
      if (sobrante !== 0) {
        await guardarSobranteEnTabla(
          cuadreSeleccionado.sede,
          cuadreSeleccionado.fecha,
          sobrante,
          cuadreSeleccionado.id
        );
      }
    }
    
    setRevisionActual(prev => ({
      ...prev,
      [`revision_${seccion}`]: {
        ...prev[`revision_${seccion}`],
        ...datos
      }
    }));
  };

  // Aprobar/Rechazar item individual
  const toggleItemAprobado = (seccion, itemIndex, aprobado) => {
    if (!revisionActual) return;
    const seccionKey = `revision_${seccion}`;
    const itemsActuales = revisionActual[seccionKey]?.items || [];
    
    // Buscar si el item ya existe
    const itemExistente = itemsActuales.find(i => i.index === itemIndex);
    
    let nuevosItems;
    if (itemExistente) {
      nuevosItems = itemsActuales.map(i => 
        i.index === itemIndex ? { ...i, aprobado } : i
      );
    } else {
      nuevosItems = [...itemsActuales, { index: itemIndex, aprobado, observacion: '' }];
    }
    
    actualizarSeccionRevision(seccion, { items: nuevosItems });
  };

  // Agregar observación a item
  const agregarObservacionItem = (seccion, itemIndex, observacion) => {
    if (!revisionActual) return;
    const seccionKey = `revision_${seccion}`;
    const itemsActuales = revisionActual[seccionKey]?.items || [];
    
    const itemExistente = itemsActuales.find(i => i.index === itemIndex);
    
    let nuevosItems;
    if (itemExistente) {
      nuevosItems = itemsActuales.map(i => 
        i.index === itemIndex ? { ...i, observacion } : i
      );
    } else {
      nuevosItems = [...itemsActuales, { index: itemIndex, aprobado: null, observacion }];
    }
    
    actualizarSeccionRevision(seccion, { items: nuevosItems });
  };

  // Asignar sección a empleado
  const asignarSeccion = async () => {
    if (!seccionParaAsignar || !empleadoAsignacion || !revisionActual) {
      alert("Seleccione una sección y un empleado");
      return;
    }
    
    const empleadoSeleccionado = empleados.find(e => String(e.id) === empleadoAsignacion);
    
    const nuevaAsignacion = {
      id: `asig_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      revision_id: revisionActual.id,
      empleado_id: empleadoAsignacion,
      empleado_nombre: empleadoSeleccionado?.nombre || "Desconocido",
      seccion: seccionParaAsignar,
      estado: 'pendiente',
      asignado_por_id: miId,
      asignado_por_nombre: miNombre
    };
    
    const resultado = await guardarAsignacionRevision(nuevaAsignacion);
    
    if (resultado) {
      setAsignaciones(prev => [...prev, resultado]);
      setMostrarModalAsignacion(false);
      setSeccionParaAsignar("");
      setEmpleadoAsignacion("");
      alert("✅ Sección asignada correctamente");
    } else {
      alert("❌ Error al asignar la sección");
    }
  };

  // Eliminar asignación
  const eliminarAsignacion = async (asignacionId) => {
    if (!window.confirm("¿Eliminar esta asignación?")) return;
    
    const resultado = await eliminarAsignacionRevision(asignacionId);
    if (resultado) {
      setAsignaciones(prev => prev.filter(a => a.id !== asignacionId));
    }
  };

  // Obtener estado de la sección
  const getEstadoSeccion = (seccion) => {
    if (!revisionActual) return { color: '#9e9e9e', texto: 'Pendiente', icono: '⏳' };
    
    const revSeccion = revisionActual[`revision_${seccion}`];
    if (!revSeccion) return { color: '#9e9e9e', texto: 'Pendiente', icono: '⏳' };
    
    if (revSeccion.aprobado === true) {
      return { color: '#4caf50', texto: 'Aprobado', icono: '✅' };
    } else if (revSeccion.aprobado === false) {
      return { color: '#f44336', texto: 'Con observaciones', icono: '⚠️' };
    }
    return { color: '#9e9e9e', texto: 'Pendiente', icono: '⏳' };
  };

  // Obtener color de estado general
  const getColorEstado = (estado) => {
    switch(estado) {
      case 'aprobado': return '#4caf50';
      case 'descuadre': return '#f44336';
      case 'en_revision': return '#ff9800';
      case 'pendiente_gerencia': return '#2196f3';
      default: return '#9e9e9e';
    }
  };

  // Obtener datos de items según la sección del cuadre
  const getItemsSeccion = (seccion) => {
    if (!cuadreSeleccionado) return [];
    
    switch(seccion) {
      case 'facturas':
        // Facturas: {numero, proveedor, valor, estado...} - Solo pagadas (caja o transferencia), NO pendientes
        const todasFacturas = cuadreSeleccionado.facturas || [];
        return todasFacturas.filter(f => f.estado === "Pagada de caja" || f.estado === "Pagada transferencia");
      case 'domicilios':
        // Domicilios: {pedido, valor, tipoPago, ...}
        return cuadreSeleccionado.domicilios || [];
      case 'transferencias':
        // Transferencias: array de {banco, valor}
        const transferArray = cuadreSeleccionado.transfer || [];
        return transferArray;
      case 'turnos':
        // Turnos: {empleadoId, empleadoNombre, valor}
        return cuadreSeleccionado.turnos || [];
      case 'adelantos':
        // Adelantos: {empleadoId, empleadoNombre, valor, motivo}
        return cuadreSeleccionado.adelantos || [];
      case 'novedades':
        // Novedades: {descripcion, ...} o strings
        return cuadreSeleccionado.novedades || [];
      case 'datafonos':
        // Datafonos: {terminal, valor}
        return cuadreSeleccionado.datafonos || [];
      case 'ingresos_varios':
        // Ingresos Varios: {concepto, cantidad, cantidadLetras, valor}
        return cuadreSeleccionado.ingresosVarios || [];
      case 'ventas_descuadre':
        // Ventas y Descuadre: retorna objeto con los valores
        return [{
          ventaEfectivo: cuadreSeleccionado.ventas?.ventaEfectivo || 0,
          sobrante: cuadreSeleccionado.ventas?.sobrante || 0,
          huboDescuadre: cuadreSeleccionado.ventas?.huboDescuadre || false,
          valorDescuadre: cuadreSeleccionado.ventas?.valorDescuadre || 0
        }];
      default:
        return [];
    }
  };

  // Obtener descripción legible de un item según la sección
  const getDescripcionItem = (seccion, item, idx) => {
    if (!item) return `Item ${idx + 1}`;
    
    switch(seccion) {
      case 'facturas':
        // Factura: mostrar número, estado y proveedor
        const numFactura = item.numero || item.numeroFactura || item.factura || '';
        const proveedor = item.proveedor || item.descripcion || '';
        const estadoFactura = item.estado || '';
        let descFactura = '';
        if (numFactura) descFactura = `Factura #${numFactura}`;
        else descFactura = `Factura ${idx + 1}`;
        if (proveedor) descFactura += ` - ${proveedor}`;
        if (estadoFactura) descFactura += ` [${estadoFactura}]`;
        return descFactura;
      case 'domicilios':
        // Domicilio: mostrar número de pedido, tipo de pago y confirmación
        const pedido = item.pedido || item.numeroPedido || '';
        const tipoPago = item.tipoPago || item.pago || '';
        const confirmado = item.pago_confirmado ? '✓' : '';
        let descDom = pedido ? `Pedido #${pedido}` : `Domicilio ${idx + 1}`;
        if (tipoPago) descDom += ` (${tipoPago} ${confirmado})`;
        return descDom;
      case 'transferencias':
        // Transferencia: mostrar pedido y los bancos usados (sin valores - el valor va en columna separada)
        const pedidoT = item.pedido || '';
        let bancosUsados = [];
        if (Number(item.bancolombia || 0) > 0) bancosUsados.push(`Bancolombia${item.bancolombia_confirmado ? ' ✓' : ''}`);
        if (Number(item.davivienda || 0) > 0) bancosUsados.push(`Davivienda${item.davivienda_confirmado ? ' ✓' : ''}`);
        if (Number(item.daviplata || 0) > 0) bancosUsados.push(`Daviplata${item.daviplata_confirmado ? ' ✓' : ''}`);
        if (Number(item.nequi || 0) > 0) bancosUsados.push(`Nequi${item.nequi_confirmado ? ' ✓' : ''}`);
        let descTransfer = pedidoT ? `Pedido #${pedidoT}` : `Transferencia ${idx + 1}`;
        if (bancosUsados.length > 0) descTransfer += ` → ${bancosUsados.join(', ')}`;
        return descTransfer;
      case 'turnos':
        // Turno: nombre del empleado y si está pagado
        const empTurno = item.empleadoNombre || item.nombre || item.empleado || '';
        const pagado = item.pagado ? ' (Pagado ✓)' : ' (Pendiente)';
        return (empTurno || `Turno ${idx + 1}`) + pagado;
      case 'adelantos':
        // Adelanto: nombre del empleado y motivo
        const empAdelanto = item.empleadoNombre || item.nombre || item.empleado || '';
        const motivo = item.motivo || '';
        if (empAdelanto && motivo) return `${empAdelanto} - ${motivo}`;
        if (empAdelanto) return empAdelanto;
        return `Adelanto ${idx + 1}`;
      case 'novedades':
        // Novedad: puede ser string o objeto con descripcion
        if (typeof item === 'string') return item || `Novedad ${idx + 1}`;
        const tipoNov = item.tipo ? `[${item.tipo}] ` : '';
        return tipoNov + (item.descripcion || item.texto || item.novedad || item.observacion || `Novedad ${idx + 1}`);
      case 'datafonos':
        // Datafono: terminal
        return item.terminal || item.nombre || item.datafono || `Datáfono ${idx + 1}`;
      case 'ingresos_varios':
        // Ingresos Varios: concepto y cantidad
        const concepto = item.concepto || '';
        const cantidad = item.cantidad || 0;
        const cantidadLetras = item.cantidadLetras || '';
        if (concepto) return `${concepto} (${cantidad} - ${cantidadLetras})`;
        return `Ingreso ${idx + 1}`;
      default:
        return item.descripcion || item.nombre || item.concepto || `Item ${idx + 1}`;
    }
  };

  // Obtener valor de un item según la sección
  const getValorItem = (seccion, item) => {
    if (!item) return 0;
    
    // Caso especial para transferencias: sumar todos los bancos
    if (seccion === 'transferencias') {
      return (
        parseFloat(item.bancolombia || 0) + 
        parseFloat(item.davivienda || 0) + 
        parseFloat(item.daviplata || 0) + 
        parseFloat(item.nequi || 0)
      );
    }
    
    return parseFloat(item.valor || item.monto || item.total || item.subtotal || 0) || 0;
  };

  // Verificar si el usuario tiene asignación para esta sección
  const tieneAsignacionSeccion = (seccion) => {
    return asignaciones.some(a => 
      a.seccion === seccion && 
      String(a.empleado_id) === String(miId) &&
      a.estado !== 'completada'
    );
  };

  // Verificar si el usuario tiene alguna asignación en esta revisión
  const tieneAsignacionesEnRevision = () => {
    return asignaciones.some(a => String(a.empleado_id) === String(miId));
  };

  // Obtener las secciones que el usuario puede ver
  const getSeccionesVisibles = () => {
    // Si es gerente, ve todas las secciones
    if (esGerente || tienePermiso("admin_revisar_cuadre_cerrar")) {
      return SECCIONES;
    }
    
    // Si tiene asignaciones, solo ve las secciones asignadas
    const misAsignaciones = asignaciones.filter(a => String(a.empleado_id) === String(miId));
    if (misAsignaciones.length > 0) {
      const seccionesAsignadas = misAsignaciones.map(a => a.seccion);
      return SECCIONES.filter(s => seccionesAsignadas.includes(s.key));
    }
    
    // Si no tiene asignaciones específicas pero tiene permiso de revisar, ve todas
    if (tienePermiso("admin_revisar_cuadre_revisar")) {
      return SECCIONES;
    }
    
    // Por defecto, solo puede ver (no editar)
    return SECCIONES;
  };

  // Verificar si el usuario es solo revisor de secciones específicas (sin permisos de administrador)
  const esSoloRevisorAsignado = () => {
    // Si es gerente o tiene permisos de administración, ve todo
    if (esGerente || tienePermiso("admin_revisar_cuadre_cerrar") || tienePermiso("admin_revisar_cuadre_asignar")) {
      return false;
    }
    // Si tiene asignaciones específicas, es solo revisor asignado
    return todasLasAsignaciones.length > 0;
  };

  // Filtrar cuadres: no mostrar los que ya tienen revisión aprobada o descuadre
  // Si es solo revisor asignado, mostrar solo cuadres donde tiene asignaciones
  const cuadresPendientesRevision = cuadresDisponibles.filter(cuadre => {
    // 🔹 PRIMERO: Verificar si el cuadre tiene una revisión CERRADA (aprobada/descuadre)
    // Usamos revisionesCerradas que contiene TODAS las revisiones cerradas sin filtrar por permisos
    const revisionCerrada = revisionesCerradas.find(r => r.cuadre_id === cuadre.id);
    if (revisionCerrada) {
      // El cuadre ya fue revisado y cerrado - NO debe aparecer en pendientes
      return false;
    }
    
    // Verificar en revisionesGuardadas también (por si acaso)
    const revision = revisionesGuardadas.find(r => r.cuadre_id === cuadre.id);
    
    // No mostrar cuadres ya aprobados o con descuadre
    const esPendiente = !revision || revision.estado === 'en_revision';
    if (!esPendiente) return false;
    
    // Si es solo revisor asignado, verificar que tenga asignación para este cuadre
    if (esSoloRevisorAsignado()) {
      // Buscar si tiene asignaciones para la revisión de este cuadre
      const tieneAsignacionParaCuadre = todasLasAsignaciones.some(a => {
        // Buscar la revisión correspondiente para obtener el cuadre_id
        const revisionAsignacion = revisionesGuardadas.find(r => r.id === a.revision_id);
        return revisionAsignacion && revisionAsignacion.cuadre_id === cuadre.id;
      });
      return tieneAsignacionParaCuadre;
    }
    
    return true;
  });

  // Renderizar vista de selección de cuadre
  const renderSeleccionCuadre = () => (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 20 
      }}>
        <h3 style={{ margin: 0, color: '#1a237e' }}>📋 Seleccionar Cuadre para Revisar</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {(esGerente || tienePermiso("admin_revisar_cuadre_cerrar") || tienePermiso("admin_revisar_cuadre_ver_sobrantes")) && (
            <button
              onClick={() => { setModo("sobrantes"); cargarSobrantes(); }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer'
              }}
            >
              💰 Sobrantes
            </button>
          )}
          <button
            onClick={() => { setModo("consultar"); cargarRevisiones(); }}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1a237e',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer'
            }}
          >
            📊 Ver Historial
          </button>
        </div>
      </div>
      
      {cargando ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div className="spinner" />
          <p>Cargando cuadres...</p>
        </div>
      ) : cuadresPendientesRevision.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: 40, 
          backgroundColor: '#f5f5f5', 
          borderRadius: 12 
        }}>
          <p style={{ fontSize: 18, color: '#666' }}>No hay cuadres pendientes de revisión</p>
          <p style={{ fontSize: 14, color: '#999' }}>Los cuadres aprobados o con descuadre están en el historial</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {cuadresPendientesRevision.map(cuadre => {
            const tieneRevision = revisionesGuardadas.some(r => r.cuadre_id === cuadre.id);
            const revisionEstado = revisionesGuardadas.find(r => r.cuadre_id === cuadre.id)?.estado;
            
            return (
              <div
                key={cuadre.id}
                onClick={() => iniciarRevision(cuadre)}
                style={{
                  padding: 16,
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: 12,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: 16, color: '#1a237e' }}>
                    {cuadre.sede} - {cuadre.fecha}
                  </div>
                  <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
                    Responsable: {obtenerNombreEmpleado(cuadre.empleadoId)}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {tieneRevision && (
                    <span style={{
                      padding: '4px 12px',
                      backgroundColor: getColorEstado(revisionEstado),
                      color: 'white',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 'bold'
                    }}>
                      {revisionEstado?.replace('_', ' ').toUpperCase() || 'EN REVISIÓN'}
                    </span>
                  )}
                  <span style={{ fontSize: 20 }}>➡️</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Renderizar panel de revisión de sección
  const renderSeccionRevision = (seccion) => {
    const config = SECCIONES.find(s => s.key === seccion.key);
    if (!config) return null;
    
    const estadoSeccion = getEstadoSeccion(seccion.key);
    const items = getItemsSeccion(seccion.key);
    const revSeccion = revisionActual?.[`revision_${seccion.key}`] || {};
    const asignacionSeccion = asignaciones.find(a => a.seccion === seccion.key);
    // En modo solo lectura nadie puede editar
    const puedeEditar = !modoSoloLectura && (esGerente || tieneAsignacionSeccion(seccion.key) || !asignacionSeccion);
    
    return (
      <div
        key={seccion.key}
        style={{
          backgroundColor: 'white',
          border: `2px solid ${estadoSeccion.color}`,
          borderRadius: 12,
          marginBottom: 16,
          overflow: 'hidden'
        }}
      >
        {/* Header de sección */}
        <div style={{
          padding: '12px 16px',
          backgroundColor: estadoSeccion.color + '15',
          borderBottom: `1px solid ${estadoSeccion.color}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 24 }}>{config.icono}</span>
            <span style={{ fontWeight: 'bold', fontSize: 16 }}>{config.nombre}</span>
            <span style={{ 
              padding: '2px 8px', 
              backgroundColor: estadoSeccion.color, 
              color: 'white', 
              borderRadius: 10, 
              fontSize: 11 
            }}>
              {estadoSeccion.icono} {estadoSeccion.texto}
            </span>
          </div>
          
          <div style={{ display: 'flex', gap: 8 }}>
            {asignacionSeccion && (
              <span style={{
                padding: '4px 8px',
                backgroundColor: '#e3f2fd',
                borderRadius: 4,
                fontSize: 11,
                color: '#1565c0'
              }}>
                👤 {asignacionSeccion.empleado_nombre}
              </span>
            )}
            
            {/* Ocultar botón Asignar en modo solo lectura */}
            {!modoSoloLectura && (esGerente || tienePermiso("admin_revisar_cuadre_asignar")) && !asignacionSeccion && (
              <button
                onClick={() => {
                  setSeccionParaAsignar(seccion.key);
                  setMostrarModalAsignacion(true);
                }}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#2196f3',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 11,
                  cursor: 'pointer'
                }}
              >
                ➕ Asignar
              </button>
            )}
            
            {puedeEditar && (
              <>
                <button
                  onClick={() => actualizarSeccionRevision(seccion.key, { aprobado: true })}
                  style={{
                    padding: '4px 12px',
                    backgroundColor: revSeccion.aprobado === true ? '#4caf50' : '#e8f5e9',
                    color: revSeccion.aprobado === true ? 'white' : '#4caf50',
                    border: `1px solid #4caf50`,
                    borderRadius: 4,
                    fontSize: 12,
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  ✅ Aprobar
                </button>
                <button
                  onClick={() => actualizarSeccionRevision(seccion.key, { aprobado: false })}
                  style={{
                    padding: '4px 12px',
                    backgroundColor: revSeccion.aprobado === false ? '#f44336' : '#ffebee',
                    color: revSeccion.aprobado === false ? 'white' : '#f44336',
                    border: `1px solid #f44336`,
                    borderRadius: 4,
                    fontSize: 12,
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  ⚠️ Observar
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Contenido - Items */}
        <div style={{ padding: 16 }}>
          {config.tieneItems && items.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: 8, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Item</th>
                  <th style={{ padding: 8, textAlign: 'right', borderBottom: '1px solid #ddd' }}>Valor</th>
                  <th style={{ padding: 8, textAlign: 'center', borderBottom: '1px solid #ddd', width: 100 }}>Estado</th>
                  <th style={{ padding: 8, textAlign: 'center', borderBottom: '1px solid #ddd', width: 180 }}>Observación</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const itemRevision = revSeccion.items?.find(i => i.index === idx) || {};
                  const descripcion = getDescripcionItem(seccion.key, item, idx);
                  const valor = getValorItem(seccion.key, item);
                  
                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: 8 }}>{descripcion}</td>
                      <td style={{ padding: 8, textAlign: 'right', fontWeight: 'bold' }}>
                        {formatearMoneda(valor)}
                      </td>
                      <td style={{ padding: 8, textAlign: 'center' }}>
                        {puedeEditar && (
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                            <button
                              onClick={() => toggleItemAprobado(seccion.key, idx, true)}
                              style={{
                                padding: '2px 8px',
                                backgroundColor: itemRevision.aprobado === true ? '#4caf50' : '#e8f5e9',
                                color: itemRevision.aprobado === true ? 'white' : '#4caf50',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                fontSize: 14
                              }}
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => toggleItemAprobado(seccion.key, idx, false)}
                              style={{
                                padding: '2px 8px',
                                backgroundColor: itemRevision.aprobado === false ? '#f44336' : '#ffebee',
                                color: itemRevision.aprobado === false ? 'white' : '#f44336',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                fontSize: 14
                              }}
                            >
                              ✗
                            </button>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: 4 }}>
                        <input
                          type="text"
                          placeholder="Observación..."
                          value={itemRevision.observacion || ''}
                          onChange={(e) => agregarObservacionItem(seccion.key, idx, e.target.value)}
                          disabled={!puedeEditar}
                          style={{
                            width: '100%',
                            padding: '4px 8px',
                            border: '1px solid #ddd',
                            borderRadius: 4,
                            fontSize: 12
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : config.tieneItems ? (
            <p style={{ color: '#999', textAlign: 'center', margin: 0 }}>
              Sin items en esta sección
            </p>
          ) : seccion.key === 'ventas_descuadre' ? (
            // Renderizar sección especial de Ventas y Descuadre con todos los métodos de pago
            (() => {
              // Calcular totales de transferencias - el campo se llama "transfer" no "transferencias"
              const transferencias = Array.isArray(cuadreSeleccionado?.transfer) ? cuadreSeleccionado.transfer : [];
              let totalBancolombia = 0, totalDavivienda = 0, totalDaviplata = 0, totalNequi = 0;
              transferencias.forEach(t => {
                totalBancolombia += Number(t.bancolombia || 0);
                totalDavivienda += Number(t.davivienda || 0);
                totalDaviplata += Number(t.daviplata || 0);
                totalNequi += Number(t.nequi || 0);
              });
              const totalTransferencias = totalBancolombia + totalDavivienda + totalDaviplata + totalNequi;
              
              // Calcular total de datáfonos
              const datafonos = Array.isArray(cuadreSeleccionado?.datafonos) ? cuadreSeleccionado.datafonos : [];
              const totalDatafonos = datafonos.reduce((sum, d) => sum + Number(d.valor || 0), 0);
              
              // Online Rappi
              const onlineRappi = Number(cuadreSeleccionado?.ventas?.onlineRappi || 0);
              
              // Total ventas
              const ventaEfectivo = Number(cuadreSeleccionado?.ventas?.ventaEfectivo || 0);
              const totalVentas = ventaEfectivo + totalTransferencias + totalDatafonos + onlineRappi;

              return (
                <div style={{ backgroundColor: '#f8f9fa', padding: 16, borderRadius: 8 }}>
                  {/* Título: Resumen de Ventas por Medio de Pago */}
                  <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #1a237e' }}>
                    <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1a237e' }}>📊 Resumen de Ventas por Medio de Pago</div>
                  </div>
                  
                  {/* Grid de métodos de pago */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                    {/* Efectivo */}
                    <div style={{ padding: 12, backgroundColor: 'white', borderRadius: 8, border: '1px solid #ddd' }}>
                      <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>💵 Efectivo</div>
                      <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1a237e' }}>
                        {formatearMoneda(ventaEfectivo)}
                      </div>
                    </div>
                    
                    {/* Bancolombia */}
                    <div style={{ padding: 12, backgroundColor: 'white', borderRadius: 8, border: '1px solid #FDDA25' }}>
                      <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>🏦 Bancolombia</div>
                      <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1a237e' }}>
                        {formatearMoneda(totalBancolombia)}
                      </div>
                    </div>
                    
                    {/* Davivienda */}
                    <div style={{ padding: 12, backgroundColor: 'white', borderRadius: 8, border: '1px solid #ED1C24' }}>
                      <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>🏦 Davivienda</div>
                      <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1a237e' }}>
                        {formatearMoneda(totalDavivienda)}
                      </div>
                    </div>
                    
                    {/* Daviplata */}
                    <div style={{ padding: 12, backgroundColor: 'white', borderRadius: 8, border: '1px solid #ED1C24' }}>
                      <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>📱 Daviplata</div>
                      <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1a237e' }}>
                        {formatearMoneda(totalDaviplata)}
                      </div>
                    </div>
                    
                    {/* Nequi */}
                    <div style={{ padding: 12, backgroundColor: 'white', borderRadius: 8, border: '1px solid #E6007E' }}>
                      <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>📱 Nequi</div>
                      <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1a237e' }}>
                        {formatearMoneda(totalNequi)}
                      </div>
                    </div>
                    
                    {/* Datáfonos */}
                    <div style={{ padding: 12, backgroundColor: 'white', borderRadius: 8, border: '1px solid #4caf50' }}>
                      <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>💳 Datáfonos</div>
                      <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1a237e' }}>
                        {formatearMoneda(totalDatafonos)}
                      </div>
                    </div>
                    
                    {/* Online Rappi */}
                    <div style={{ padding: 12, backgroundColor: 'white', borderRadius: 8, border: '1px solid #FF5722' }}>
                      <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>🛵 Online Rappi</div>
                      <div style={{ fontSize: 18, fontWeight: 'bold', color: '#FF5722' }}>
                        {formatearMoneda(onlineRappi)}
                      </div>
                    </div>
                    
                    {/* Total Transferencias */}
                    <div style={{ padding: 12, backgroundColor: '#e3f2fd', borderRadius: 8, border: '1px solid #2196f3' }}>
                      <div style={{ fontSize: 11, color: '#1976d2', marginBottom: 4 }}>🔄 Total Transferencias</div>
                      <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1976d2' }}>
                        {formatearMoneda(totalTransferencias)}
                      </div>
                    </div>
                    
                    {/* TOTAL VENTAS - Ocupa toda la fila */}
                    <div style={{ 
                      padding: 16, 
                      backgroundColor: '#1a237e', 
                      borderRadius: 8, 
                      border: '2px solid #0d47a1',
                      gridColumn: '1 / span 3',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginBottom: 4 }}>💰 TOTAL VENTAS</div>
                      <div style={{ fontSize: 24, fontWeight: 'bold', color: 'white' }}>
                        {formatearMoneda(totalVentas)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Sección de Sobrante/Faltante y Descuadre */}
                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: '2px solid #ddd' }}>
                    <div style={{ fontSize: 14, fontWeight: 'bold', color: '#666', marginBottom: 12 }}>⚖️ Estado del Cuadre de Efectivo</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                      {/* Sobrante/Faltante */}
                      <div style={{ 
                        padding: 12, 
                        backgroundColor: (cuadreSeleccionado?.ventas?.sobrante || 0) >= 0 ? '#e8f5e9' : '#ffebee', 
                        borderRadius: 8, 
                        border: `1px solid ${(cuadreSeleccionado?.ventas?.sobrante || 0) >= 0 ? '#4caf50' : '#f44336'}` 
                      }}>
                        <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>
                          {(cuadreSeleccionado?.ventas?.sobrante || 0) >= 0 ? '📈 Sobrante Efectivo' : '📉 Faltante Efectivo'}
                        </div>
                        <div style={{ 
                          fontSize: 18, 
                          fontWeight: 'bold', 
                          color: (cuadreSeleccionado?.ventas?.sobrante || 0) >= 0 ? '#4caf50' : '#f44336' 
                        }}>
                          {formatearMoneda(Math.abs(cuadreSeleccionado?.ventas?.sobrante || 0))}
                        </div>
                      </div>
                      
                      {/* Estado Cuadre Efectivo - Ahora automático */}
                      <div style={{ 
                        padding: 12, 
                        backgroundColor: (cuadreSeleccionado?.ventas?.sobrante || 0) === 0 ? '#e8f5e9' : '#fef3c7', 
                        borderRadius: 8, 
                        border: `1px solid ${(cuadreSeleccionado?.ventas?.sobrante || 0) === 0 ? '#4caf50' : '#f59e0b'}`
                      }}>
                        <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>Estado Cuadre Efectivo</div>
                        <div style={{ 
                          fontSize: 14, 
                          fontWeight: 'bold', 
                          color: (cuadreSeleccionado?.ventas?.sobrante || 0) === 0 ? '#4caf50' : '#92400e' 
                        }}>
                          {(cuadreSeleccionado?.ventas?.sobrante || 0) === 0 
                            ? '✅ Cuadre OK' 
                            : (cuadreSeleccionado?.ventas?.sobrante || 0) > 0 
                              ? '⚠️ Sobrante en efectivo' 
                              : '⚠️ Descuadre en efectivo'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Sección de Descuadre en Transferencia */}
                    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '2px solid #ddd' }}>
                      <div style={{ fontSize: 14, fontWeight: 'bold', color: '#666', marginBottom: 12 }}>💸 Descuadre en Transferencia</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                        <div style={{ 
                          padding: 12, 
                          backgroundColor: cuadreSeleccionado?.ventas?.huboDescuadreTransferencia === 'Sí' ? '#ffebee' : '#e8f5e9', 
                          borderRadius: 8, 
                          border: `1px solid ${cuadreSeleccionado?.ventas?.huboDescuadreTransferencia === 'Sí' ? '#f44336' : '#4caf50'}`
                        }}>
                          <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>¿Hubo descuadre?</div>
                          <div style={{ 
                            fontSize: 16, 
                            fontWeight: 'bold', 
                            color: cuadreSeleccionado?.ventas?.huboDescuadreTransferencia === 'Sí' ? '#f44336' : '#4caf50' 
                          }}>
                            {cuadreSeleccionado?.ventas?.huboDescuadreTransferencia || 'Sin respuesta'}
                          </div>
                        </div>
                        
                        {cuadreSeleccionado?.ventas?.huboDescuadreTransferencia === 'Sí' && (
                          <>
                            <div style={{ padding: 12, backgroundColor: '#ffebee', borderRadius: 8, border: '1px solid #f44336' }}>
                              <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>💢 Valor Descuadre</div>
                              <div style={{ fontSize: 18, fontWeight: 'bold', color: '#f44336' }}>
                                {formatearMoneda(cuadreSeleccionado?.ventas?.valorDescuadreTransferencia || 0)}
                              </div>
                            </div>
                            <div style={{ padding: 12, backgroundColor: '#fff3e0', borderRadius: 8, border: '1px solid #ff9800' }}>
                              <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>📝 Número de Pedido</div>
                              <div style={{ fontSize: 16, fontWeight: 'bold', color: '#e65100' }}>
                                {cuadreSeleccionado?.ventas?.numeroPedidoDescuadreTransferencia || '-'}
                              </div>
                            </div>
                            <div style={{ padding: 12, backgroundColor: '#e3f2fd', borderRadius: 8, border: '1px solid #2196f3' }}>
                              <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>🏦 Banco</div>
                              <div style={{ fontSize: 16, fontWeight: 'bold', color: '#1565c0' }}>
                                {cuadreSeleccionado?.ventas?.bancoDescuadreTransferencia || '-'}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Sección de Descuadre en Datáfono */}
                    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '2px solid #ddd' }}>
                      <div style={{ fontSize: 14, fontWeight: 'bold', color: '#666', marginBottom: 12 }}>💳 Descuadre en Datáfono</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                        <div style={{ 
                          padding: 12, 
                          backgroundColor: cuadreSeleccionado?.ventas?.huboDescuadreDatafono === 'Sí' ? '#ffebee' : '#e8f5e9', 
                          borderRadius: 8, 
                          border: `1px solid ${cuadreSeleccionado?.ventas?.huboDescuadreDatafono === 'Sí' ? '#f44336' : '#4caf50'}`
                        }}>
                          <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>¿Hubo descuadre?</div>
                          <div style={{ 
                            fontSize: 16, 
                            fontWeight: 'bold', 
                            color: cuadreSeleccionado?.ventas?.huboDescuadreDatafono === 'Sí' ? '#f44336' : '#4caf50' 
                          }}>
                            {cuadreSeleccionado?.ventas?.huboDescuadreDatafono || 'Sin respuesta'}
                          </div>
                        </div>
                        
                        {cuadreSeleccionado?.ventas?.huboDescuadreDatafono === 'Sí' && (
                          <div style={{ padding: 12, backgroundColor: '#ffebee', borderRadius: 8, border: '1px solid #f44336' }}>
                            <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>💢 Valor Descuadre</div>
                            <div style={{ fontSize: 18, fontWeight: 'bold', color: '#f44336' }}>
                              {formatearMoneda(cuadreSeleccionado?.ventas?.valorDescuadreDatafono || 0)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()
          ) : (
            <p style={{ color: '#666', textAlign: 'center', margin: 0 }}>
              Revisar los valores de {config.nombre.toLowerCase()} del cuadre
            </p>
          )}
          
          {/* Observaciones de la sección */}
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>
              Observaciones de {config.nombre}:
            </label>
            <textarea
              value={revSeccion.observaciones || ''}
              onChange={(e) => actualizarSeccionRevision(seccion.key, { observaciones: e.target.value })}
              disabled={!puedeEditar}
              placeholder={`Agregar observaciones sobre ${config.nombre.toLowerCase()}...`}
              style={{
                width: '100%',
                padding: 8,
                border: '1px solid #ddd',
                borderRadius: 4,
                fontSize: 13,
                minHeight: 60,
                resize: 'vertical'
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  // Componente para mostrar detalle de factura con tarea de transferencia
  const FacturaTransferenciaDetalle = ({ factura }) => {
    const [expandido, setExpandido] = useState(false);
    const [cargando, setCargando] = useState(false);
    const [tareaInfo, setTareaInfo] = useState(factura.tareaRespuesta || null);
    
    const consultarTarea = async () => {
      if (tareaInfo || !factura.tareaEnviadaId) return;
      setCargando(true);
      try {
        const { data, error } = await supabase
          .from("procesos_pago")
          .select("*")
          .eq("id", factura.tareaEnviadaId)
          .maybeSingle();
        
        if (!error && data) {
          const detalles = typeof data.detalles === 'string' ? JSON.parse(data.detalles) : data.detalles || {};
          setTareaInfo({
            estado: data.estado,
            datosPago: detalles.datosPago || "",
            valorPedido: detalles.valorServPublico || "",
            fechaRecibido: detalles.fechaReciboPedido || "",
            comentariosGestion: detalles.comentariosGestion || "",
            adjuntoGestion: detalles.adjuntoGestion || null,
          });
        }
      } catch (err) {
        console.error("Error consultando tarea:", err);
      } finally {
        setCargando(false);
      }
    };

    const getEstadoColor = () => {
      if (factura.estadoTarea === "Procesada") return { bg: '#d1fae5', color: '#065f46', texto: '✅ Procesada' };
      if (factura.estadoTarea === "Negada") return { bg: '#fee2e2', color: '#b91c1c', texto: '❌ Negada' };
      return { bg: '#fef3c7', color: '#92400e', texto: '⏳ Pendiente' };
    };
    const estadoStyle = getEstadoColor();

    return (
      <div style={{
        marginBottom: 8,
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: 'white',
      }}>
        {/* Header clickeable */}
        <div
          onClick={() => {
            setExpandido(!expandido);
            if (!expandido && !tareaInfo) consultarTarea();
          }}
          style={{
            padding: '10px 12px',
            backgroundColor: estadoStyle.bg,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16 }}>📄</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                {factura.factura || "Factura sin nombre"}
              </div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                Valor: ${Number(factura.valor || 0).toLocaleString()}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              padding: '3px 8px',
              backgroundColor: estadoStyle.color,
              color: 'white',
              borderRadius: 10,
              fontSize: 10,
              fontWeight: 600,
            }}>
              {estadoStyle.texto}
            </span>
            <span style={{ fontSize: 12, color: '#64748b' }}>
              {expandido ? '▲' : '▼'}
            </span>
          </div>
        </div>

        {/* Contenido expandible */}
        {expandido && (
          <div style={{ padding: 12, backgroundColor: '#f8fafc' }}>
            {cargando ? (
              <div style={{ textAlign: 'center', padding: 16, color: '#64748b' }}>
                Cargando información...
              </div>
            ) : tareaInfo ? (
              <div style={{ fontSize: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                  <div>
                    <div style={{ color: '#64748b', fontSize: 11 }}>Valor del pedido:</div>
                    <div style={{ fontWeight: 600 }}>${Number(tareaInfo.valorPedido || 0).toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ color: '#64748b', fontSize: 11 }}>Fecha recibido:</div>
                    <div style={{ fontWeight: 600 }}>{tareaInfo.fechaRecibido || "—"}</div>
                  </div>
                </div>
                
                {tareaInfo.datosPago && (
                  <div style={{ marginBottom: 10, padding: 8, backgroundColor: '#f0f9ff', borderRadius: 6, border: '1px solid #bae6fd' }}>
                    <div style={{ color: '#0369a1', fontSize: 11, fontWeight: 600 }}>💳 Datos para el pago:</div>
                    <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{tareaInfo.datosPago}</div>
                  </div>
                )}
                
                {tareaInfo.comentariosGestion && (
                  <div style={{ 
                    marginBottom: 10, 
                    padding: 8, 
                    backgroundColor: factura.estadoTarea === "Procesada" ? '#d1fae5' : '#fee2e2', 
                    borderRadius: 6 
                  }}>
                    <div style={{ color: '#374151', fontSize: 11, fontWeight: 600 }}>📝 Respuesta de gestión:</div>
                    <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{tareaInfo.comentariosGestion}</div>
                  </div>
                )}
                
                {tareaInfo.adjuntoGestion?.url && (
                  <div style={{ marginTop: 8 }}>
                    <a
                      href={tareaInfo.adjuntoGestion.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        borderRadius: 6,
                        textDecoration: 'none',
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      📎 Ver comprobante adjunto
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: '#64748b', fontSize: 12 }}>
                No se pudo cargar la información de la tarea
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Renderizar vista de revisión
  const renderRevisionCuadre = () => {
    if (!cuadreSeleccionado || !revisionActual) {
      return (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h3 style={{ color: '#1a237e', marginBottom: 12 }}>No se pudo cargar la revisión</h3>
          <p style={{ color: '#666', marginBottom: 20 }}>
            Hubo un problema al cargar los datos. Esto puede ocurrir si las tablas de Supabase no están creadas.
          </p>
          <button
            onClick={() => {
              setModo("seleccionar");
              setCuadreSeleccionado(null);
              setRevisionActual(null);
            }}
            style={{
              padding: '10px 24px',
              backgroundColor: '#1a237e',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            ← Volver a selección
          </button>
        </div>
      );
    }
    
    const seccionesAprobadas = SECCIONES.filter(s => 
      revisionActual[`revision_${s.key}`]?.aprobado === true
    ).length;
    const seccionesObservadas = SECCIONES.filter(s => 
      revisionActual[`revision_${s.key}`]?.aprobado === false
    ).length;
    const seccionesPendientes = SECCIONES.length - seccionesAprobadas - seccionesObservadas;
    
    return (
      <div>
        {/* Banner de Solo Lectura */}
        {modoSoloLectura && (
          <div style={{
            padding: '12px 20px',
            backgroundColor: '#607d8b',
            color: 'white',
            borderRadius: 12,
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 24 }}>👁️</span>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: 16 }}>MODO SOLO LECTURA</div>
                <div style={{ fontSize: 12, opacity: 0.9 }}>
                  Esta revisión está cerrada. Solo puede visualizarla, no modificarla.
                </div>
              </div>
            </div>
            <div style={{
              padding: '6px 16px',
              backgroundColor: revisionActual.estado === 'aprobado' ? '#4caf50' : '#f44336',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 'bold'
            }}>
              {revisionActual.estado?.toUpperCase()}
            </div>
          </div>
        )}
        
        {/* Header con info del cuadre */}
        <div style={{
          padding: 16,
          backgroundColor: '#1a237e',
          color: 'white',
          borderRadius: 12,
          marginBottom: 20
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <button
                onClick={() => {
                  setModo(modoSoloLectura ? "consultar" : "seleccionar");
                  setCuadreSeleccionado(null);
                  setRevisionActual(null);
                  setModoSoloLectura(false);
                }}
                style={{
                  padding: '4px 12px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  marginBottom: 8
                }}
              >
                ← Volver
              </button>
              <h3 style={{ margin: '8px 0' }}>
                {modoSoloLectura ? '👁️' : '🔍'} Revisión: {cuadreSeleccionado.sede} - {cuadreSeleccionado.fecha}
              </h3>
              <p style={{ margin: 0, opacity: 0.9, fontSize: 14 }}>
                Responsable: {obtenerNombreEmpleado(cuadreSeleccionado.empleadoId)} | 
                Revisor: {revisionActual.revisor_principal_nombre}
              </p>
            </div>
            
            <div style={{ textAlign: 'right' }}>
              <div style={{
                padding: '8px 16px',
                backgroundColor: getColorEstado(revisionActual.estado),
                borderRadius: 20,
                fontWeight: 'bold',
                fontSize: 14
              }}>
                {revisionActual.estado?.replace('_', ' ').toUpperCase()}
              </div>
            </div>
          </div>
          
          {/* Progreso */}
          <div style={{ 
            display: 'flex', 
            gap: 16, 
            marginTop: 16, 
            paddingTop: 12, 
            borderTop: '1px solid rgba(255,255,255,0.2)' 
          }}>
            <div>
              <span style={{ fontSize: 20 }}>✅</span> Aprobadas: <strong>{seccionesAprobadas}</strong>
            </div>
            <div>
              <span style={{ fontSize: 20 }}>⚠️</span> Observadas: <strong>{seccionesObservadas}</strong>
            </div>
            <div>
              <span style={{ fontSize: 20 }}>⏳</span> Pendientes: <strong>{seccionesPendientes}</strong>
            </div>
          </div>
        </div>
        
        {/* Mensaje si el empleado tiene secciones asignadas específicas */}
        {!esGerente && tieneAsignacionesEnRevision() && (
          <div style={{
            padding: 12,
            backgroundColor: '#e3f2fd',
            border: '1px solid #2196f3',
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 14,
            color: '#1565c0'
          }}>
            👤 <strong>Tienes secciones asignadas para revisar.</strong> Solo puedes ver y modificar las secciones que te fueron asignadas.
          </div>
        )}

        {/* Secciones de revisión - filtradas según permisos/asignaciones */}
        {getSeccionesVisibles().map(seccion => renderSeccionRevision(seccion))}
        
        {/* Observaciones del cuadre original */}
        {cuadreSeleccionado.comentarioModificacion && (
          <div style={{
            padding: 16,
            backgroundColor: '#fff8e1',
            border: '2px solid #ffc107',
            borderRadius: 12,
            marginBottom: 16
          }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#ff8f00' }}>📋 Observaciones del Cuadre Original</h4>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#5d4037' }}>
              {cuadreSeleccionado.comentarioModificacion}
            </p>
          </div>
        )}

        {/* Sección de Facturas Pagadas - Solo Caja y Transferencia */}
        {(() => {
          const facturas = cuadreSeleccionado?.facturas || [];
          const facturasCaja = facturas.filter(f => f.estado === "Pagada de caja");
          const facturasTransferencia = facturas.filter(f => f.estado === "Pagada transferencia");
          const totalCaja = facturasCaja.reduce((s, f) => s + (parseFloat(f.valor) || 0), 0);
          const totalTransferencia = facturasTransferencia.reduce((s, f) => s + (parseFloat(f.valor) || 0), 0);
          
          if (facturasCaja.length === 0 && facturasTransferencia.length === 0) return null;
          
          // Estado de la sección de facturas pagadas
          const revSeccionFacturasPagadas = revisionActual?.['revision_facturas_pagadas'] || {};
          const asignacionFacturasPagadas = asignaciones.find(a => a.seccion === 'facturas_pagadas');
          const puedeEditarFacturasPagadas = !modoSoloLectura && (esGerente || asignaciones.some(a => a.seccion === 'facturas_pagadas' && a.empleado_id === miId) || !asignacionFacturasPagadas);
          
          // Determinar estado visual
          const getEstadoSeccionFacturasPagadas = () => {
            if (revSeccionFacturasPagadas.aprobado === true) return { color: '#4caf50', icono: '✅', texto: 'Aprobado' };
            if (revSeccionFacturasPagadas.aprobado === false) return { color: '#f44336', icono: '⚠️', texto: 'Observado' };
            return { color: '#ff9800', icono: '⏳', texto: 'Pendiente' };
          };
          const estadoFacturasPagadas = getEstadoSeccionFacturasPagadas();
          
          return (
            <div style={{
              backgroundColor: 'white',
              border: `2px solid ${estadoFacturasPagadas.color}`,
              borderRadius: 12,
              marginBottom: 16,
              overflow: 'hidden'
            }}>
              {/* Header de sección */}
              <div style={{
                padding: '12px 16px',
                backgroundColor: estadoFacturasPagadas.color + '15',
                borderBottom: `1px solid ${estadoFacturasPagadas.color}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 24 }}>💸</span>
                  <span style={{ fontWeight: 'bold', fontSize: 16 }}>Facturas Pagadas</span>
                  <span style={{ 
                    padding: '2px 8px', 
                    backgroundColor: estadoFacturasPagadas.color, 
                    color: 'white', 
                    borderRadius: 10, 
                    fontSize: 11 
                  }}>
                    {estadoFacturasPagadas.icono} {estadoFacturasPagadas.texto}
                  </span>
                </div>
                
                <div style={{ display: 'flex', gap: 8 }}>
                  {asignacionFacturasPagadas && (
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: '#e3f2fd',
                      borderRadius: 4,
                      fontSize: 11,
                      color: '#1565c0'
                    }}>
                      👤 {asignacionFacturasPagadas.empleado_nombre}
                    </span>
                  )}
                  
                  {/* Botón Asignar */}
                  {!modoSoloLectura && (esGerente || tienePermiso("admin_revisar_cuadre_asignar")) && !asignacionFacturasPagadas && (
                    <button
                      onClick={() => {
                        setSeccionParaAsignar('facturas_pagadas');
                        setMostrarModalAsignacion(true);
                      }}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#2196f3',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        fontSize: 11,
                        cursor: 'pointer'
                      }}
                    >
                      ➕ Asignar
                    </button>
                  )}
                  
                  {puedeEditarFacturasPagadas && (
                    <>
                      <button
                        onClick={() => setRevisionActual(prev => ({
                          ...prev,
                          revision_facturas_pagadas: { ...revSeccionFacturasPagadas, aprobado: true }
                        }))}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: revSeccionFacturasPagadas.aprobado === true ? '#4caf50' : '#e8f5e9',
                          color: revSeccionFacturasPagadas.aprobado === true ? 'white' : '#4caf50',
                          border: `1px solid #4caf50`,
                          borderRadius: 4,
                          fontSize: 12,
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        ✅ Aprobar
                      </button>
                      <button
                        onClick={() => setRevisionActual(prev => ({
                          ...prev,
                          revision_facturas_pagadas: { ...revSeccionFacturasPagadas, aprobado: false }
                        }))}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: revSeccionFacturasPagadas.aprobado === false ? '#f44336' : '#ffebee',
                          color: revSeccionFacturasPagadas.aprobado === false ? 'white' : '#f44336',
                          border: `1px solid #f44336`,
                          borderRadius: 4,
                          fontSize: 12,
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        ⚠️ Observar
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Contenido */}
              <div style={{ padding: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                  {/* Total Facturas Pagadas de Caja */}
                  <div style={{ backgroundColor: '#e3f2fd', padding: 16, borderRadius: 8, textAlign: 'center' }}>
                    <div style={{ fontSize: 13, color: '#1565c0', marginBottom: 8 }}>💵 Total Pagadas de Caja</div>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#0d47a1' }}>
                      {formatearMoneda(totalCaja)}
                    </div>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                      {facturasCaja.length} factura{facturasCaja.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  
                  {/* Total Facturas Pagadas por Transferencia */}
                  <div style={{ backgroundColor: '#e8eaf6', padding: 16, borderRadius: 8, textAlign: 'center' }}>
                    <div style={{ fontSize: 13, color: '#3949ab', marginBottom: 8 }}>🏦 Total Pagadas Transferencia</div>
                    <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1a237e' }}>
                      {formatearMoneda(totalTransferencia)}
                    </div>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                      {facturasTransferencia.length} factura{facturasTransferencia.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>

                {/* Detalle de Facturas con Tareas de Transferencia */}
                {facturasTransferencia.filter(f => f.tareaEnviadaId).length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a237e', marginBottom: 8 }}>
                      📋 Facturas enviadas a Procesos Administrativos:
                    </div>
                    <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                      {facturasTransferencia.filter(f => f.tareaEnviadaId).map((factura, idx) => (
                        <FacturaTransferenciaDetalle key={factura.id || idx} factura={factura} />
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Observaciones de la sección */}
                <div style={{ marginTop: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>
                    Observaciones de Facturas Pagadas:
                  </label>
                  <textarea
                    value={revSeccionFacturasPagadas.observaciones || ''}
                    onChange={(e) => setRevisionActual(prev => ({
                      ...prev,
                      revision_facturas_pagadas: { ...revSeccionFacturasPagadas, observaciones: e.target.value }
                    }))}
                    disabled={!puedeEditarFacturasPagadas}
                    placeholder="Agregar observaciones sobre facturas pagadas..."
                    style={{
                      width: '100%',
                      padding: 8,
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      fontSize: 13,
                      minHeight: 60,
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })()}

        {/* Observaciones generales de la revisión */}
        <div style={{
          padding: 16,
          backgroundColor: modoSoloLectura ? '#f5f5f5' : 'white',
          border: '2px solid #1a237e',
          borderRadius: 12,
          marginBottom: 16
        }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#1a237e' }}>📝 Observaciones de la Revisión</h4>
          <textarea
            value={revisionActual.observaciones_generales || ''}
            onChange={(e) => !modoSoloLectura && setRevisionActual(prev => ({ ...prev, observaciones_generales: e.target.value }))}
            placeholder={modoSoloLectura ? "(Sin observaciones)" : "Agregar observaciones generales sobre la revisión..."}
            readOnly={modoSoloLectura}
            style={{
              width: '100%',
              padding: 12,
              border: '1px solid #ddd',
              borderRadius: 8,
              fontSize: 14,
              minHeight: 100,
              resize: modoSoloLectura ? 'none' : 'vertical',
              backgroundColor: modoSoloLectura ? '#eceff1' : 'white',
              cursor: modoSoloLectura ? 'default' : 'text'
            }}
          />
        </div>
        
        {/* Botones de acción - Solo si NO está en modo solo lectura */}
        {!modoSoloLectura ? (
        <div style={{
          display: 'flex',
          gap: 12,
          justifyContent: 'center',
          padding: 16,
          backgroundColor: '#f5f5f5',
          borderRadius: 12
        }}>
          <button
            onClick={guardarRevision}
            disabled={guardando}
            style={{
              padding: '12px 24px',
              backgroundColor: '#2196f3',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: 14,
              opacity: guardando ? 0.6 : 1
            }}
          >
            💾 {guardando ? 'Guardando...' : 'Guardar Avance'}
          </button>
          
          {(esGerente || tienePermiso("admin_revisar_cuadre_cerrar")) && revisionActual.estado !== 'aprobado' && revisionActual.estado !== 'descuadre' && (
            <>
              <button
                onClick={() => cerrarRevision('aprobado')}
                disabled={guardando || seccionesPendientes > 0}
                style={{
                  padding: '12px 24px',
                  backgroundColor: seccionesPendientes > 0 ? '#ccc' : '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: seccionesPendientes > 0 ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  fontSize: 14
                }}
                title={seccionesPendientes > 0 ? 'Debe revisar todas las secciones' : ''}
              >
                ✅ Cerrar como APROBADO
              </button>
              
              <button
                onClick={() => cerrarRevision('descuadre')}
                disabled={guardando}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: 14
                }}
              >
                ❌ Cerrar como DESCUADRE
              </button>
            </>
          )}
        </div>
        ) : (
          /* Botón para volver al historial en modo solo lectura */
          <div style={{
            display: 'flex',
            gap: 12,
            justifyContent: 'center',
            padding: 16,
            backgroundColor: '#eceff1',
            borderRadius: 12
          }}>
            <button
              onClick={() => {
                setModo("consultar");
                setCuadreSeleccionado(null);
                setRevisionActual(null);
                setModoSoloLectura(false);
              }}
              style={{
                padding: '12px 24px',
                backgroundColor: '#607d8b',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: 14
              }}
            >
              ← Volver al Historial
            </button>
          </div>
        )}
      </div>
    );
  };

  // Renderizar vista de sobrantes por sede
  const renderSobrantes = () => {
    const sedesConSobrantes = Object.keys(sobrantesPorSede);
    
    // Calcular totales acumulados
    const totalAcumuladoGeneral = sobrantesFiltrados.reduce((sum, s) => sum + (parseFloat(s.total_sobrante) || 0), 0);
    
    return (
      <div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 10
        }}>
          <h3 style={{ margin: 0, color: '#1a237e' }}>💰 Sobrantes por Sede</h3>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={() => setMostrarModalSobranteManual(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              ➕ Agregar Sobrante
            </button>
            <button
              onClick={() => {
                setMostrarModalResumen(true);
                cargarResumenesGuardados();
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              📊 Resumen Ejecutivo
            </button>
            <button
              onClick={() => setModo("seleccionar")}
              style={{
                padding: '8px 16px',
                backgroundColor: '#757575',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer'
              }}
            >
              ← Volver
            </button>
          </div>
        </div>
        
        {/* Filtros de sobrantes */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12,
          marginBottom: 16,
          padding: 16,
          backgroundColor: '#f5f5f5',
          borderRadius: 12
        }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Sede</label>
            <select
              value={filtroSobranteSede}
              onChange={(e) => setFiltroSobranteSede(e.target.value)}
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
            >
              <option value="">Todas las sedes</option>
              {sedes.map(s => (
                <option key={s.id || s.nombre} value={s.nombre}>{s.nombre}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Desde</label>
            <input
              type="date"
              value={filtroSobranteFechaDesde}
              onChange={(e) => setFiltroSobranteFechaDesde(e.target.value)}
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Hasta</label>
            <input
              type="date"
              value={filtroSobranteFechaHasta}
              onChange={(e) => setFiltroSobranteFechaHasta(e.target.value)}
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
            />
          </div>
          
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <button
              onClick={aplicarFiltrosSobrantes}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                flex: 1
              }}
            >
              🔍 Filtrar
            </button>
            <button
              onClick={limpiarFiltrosSobrantes}
              style={{
                padding: '8px 16px',
                backgroundColor: '#757575',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              ✖
            </button>
          </div>
        </div>
        
        {/* Total acumulado */}
        <div style={{ 
          backgroundColor: '#4caf50', 
          padding: 16, 
          borderRadius: 12, 
          marginBottom: 20,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontSize: 16, fontWeight: 'bold' }}>
            💵 TOTAL ACUMULADO {filtroSobranteSede ? `(${filtroSobranteSede})` : '(TODAS LAS SEDES)'}
          </span>
          <span style={{ fontSize: 24, fontWeight: 'bold' }}>
            {formatearMoneda(totalAcumuladoGeneral)}
          </span>
        </div>
        
        {/* Información de la semana actual */}
        {(() => {
          const hoy = new Date().toISOString().split('T')[0];
          const inicioSemanaActual = obtenerInicioSemana(hoy);
          const finSemanaActual = obtenerFinSemana(hoy);
          const lunesBase = new Date(hoy);
          const dayOfWeek = lunesBase.getDay();
          const diffToMonday = lunesBase.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
          lunesBase.setDate(diffToMonday);
          const lunesStr = lunesBase.toISOString().split('T')[0];
          const esFestivoElLunes = esLunesFestivo(lunesStr);
          
          return (
            <div style={{ 
              backgroundColor: '#e3f2fd', 
              padding: 16, 
              borderRadius: 12, 
              marginBottom: 20,
              border: '1px solid #1976d2'
            }}>
              <p style={{ margin: 0, color: '#1565c0', fontSize: 14, fontWeight: 'bold' }}>
                📅 SEMANA ACTUAL: {new Date(inicioSemanaActual + 'T00:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: '2-digit', month: 'long' })} al {new Date(finSemanaActual + 'T00:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
              </p>
              {esFestivoElLunes && (
                <p style={{ margin: '8px 0 0', color: '#e65100', fontSize: 12 }}>
                  🎉 El lunes {new Date(lunesStr + 'T00:00:00').toLocaleDateString('es-CO', { day: '2-digit', month: 'long' })} es festivo en Colombia. La semana de cuadre inicia el martes.
                </p>
              )}
            </div>
          );
        })()}
        
        <div style={{ 
          backgroundColor: '#e8f5e9', 
          padding: 16, 
          borderRadius: 12, 
          marginBottom: 20,
          border: '1px solid #4caf50'
        }}>
          <p style={{ margin: 0, color: '#2e7d32', fontSize: 14 }}>
            ℹ️ Los sobrantes se acumulan semanalmente (martes a domingo cuando el lunes es festivo en Colombia, de lo contrario lunes a domingo) por cada sede. 
            Al aprobar la sección "Ventas y Descuadre" de un cuadre, el sobrante se suma automáticamente a la semana correspondiente.
          </p>
        </div>
        
        {sedesConSobrantes.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: 60, 
            backgroundColor: '#f5f5f5', 
            borderRadius: 12 
          }}>
            <span style={{ fontSize: 48 }}>💰</span>
            <p style={{ fontSize: 18, color: '#666', marginTop: 16 }}>No hay sobrantes registrados</p>
            <p style={{ fontSize: 14, color: '#999' }}>
              Los sobrantes aparecerán aquí cuando se aprueben cuadres con sobrante de efectivo
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: 20 }}>
            {sedes.map(sede => {
              const sobrantesSede = sobrantesPorSede[sede.nombre] || [];
              const totalGeneral = sobrantesSede.reduce((sum, s) => sum + (parseFloat(s.total_sobrante) || 0), 0);
              
              return (
                <div 
                  key={sede.id} 
                  style={{ 
                    backgroundColor: 'white', 
                    borderRadius: 12, 
                    border: '2px solid #4caf50',
                    overflow: 'hidden'
                  }}
                >
                  {/* Encabezado de la sede con el rango de la semana actual */}
                  {(() => {
                    // Obtener la semana más reciente para mostrar en el encabezado
                    const semanaReciente = sobrantesSede.length > 0 ? sobrantesSede[0] : null;
                    const inicioSemanaReciente = semanaReciente ? new Date(semanaReciente.semana_inicio + 'T00:00:00') : null;
                    const finSemanaReciente = semanaReciente ? new Date(semanaReciente.semana_fin + 'T00:00:00') : null;
                    const diaInicio = inicioSemanaReciente ? inicioSemanaReciente.getDay() : 1;
                    const esSemanConFestivo = diaInicio === 2;
                    
                    return (
                      <div style={{ 
                        backgroundColor: '#4caf50', 
                        color: 'white', 
                        padding: '12px 16px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <span style={{ fontWeight: 'bold', fontSize: 18 }}>🏪 {sede.nombre}</span>
                          <span style={{ 
                            backgroundColor: 'rgba(255,255,255,0.25)', 
                            padding: '6px 14px', 
                            borderRadius: 12,
                            fontSize: 16,
                            fontWeight: 'bold'
                          }}>
                            Total: {formatearMoneda(totalGeneral)}
                          </span>
                        </div>
                        {semanaReciente && (
                          <div style={{ 
                            backgroundColor: 'rgba(255,255,255,0.15)', 
                            padding: '8px 12px', 
                            borderRadius: 8,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div>
                              <span style={{ fontSize: 12, opacity: 0.9 }}>📅 Semana actual:</span>
                              <div style={{ fontWeight: 'bold', fontSize: 14, marginTop: 2 }}>
                                {inicioSemanaReciente.toLocaleDateString('es-CO', { weekday: 'long', day: '2-digit', month: 'long' })} → {finSemanaReciente.toLocaleDateString('es-CO', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
                              </div>
                            </div>
                            {esSemanConFestivo && (
                              <span style={{ 
                                backgroundColor: '#ff9800', 
                                padding: '4px 8px', 
                                borderRadius: 6, 
                                fontSize: 11 
                              }}>
                                🎉 Lunes Festivo
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  
                  <div style={{ padding: 16, maxHeight: 400, overflowY: 'auto' }}>
                    {sobrantesSede.length === 0 ? (
                      <p style={{ color: '#999', textAlign: 'center', margin: 0 }}>
                        Sin sobrantes registrados
                      </p>
                    ) : (
                      <div>
                        {sobrantesSede.map((semana, idx) => {
                          const cuadresCount = (semana.detalles || []).filter(d => d.tipo !== 'manual').length;
                          const manualesCount = (semana.detalles || []).filter(d => d.tipo === 'manual').length;
                          
                          // Determinar si la semana tiene festivo
                          const inicioSemana = new Date(semana.semana_inicio + 'T00:00:00');
                          const finSemana = new Date(semana.semana_fin + 'T00:00:00');
                          const diaInicio = inicioSemana.getDay();
                          const esSemanConFestivo = diaInicio === 2;
                          
                          return (
                            <div key={idx} style={{ 
                              marginBottom: 16, 
                              border: esSemanConFestivo ? '2px solid #ff9800' : '1px solid #e0e0e0', 
                              borderRadius: 8,
                              overflow: 'hidden'
                            }}>
                              {/* Encabezado compacto de la semana */}
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                padding: '8px 12px',
                                backgroundColor: esSemanConFestivo ? '#fff8e1' : '#f5f5f5',
                                borderBottom: '1px solid #e0e0e0'
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ fontWeight: 'bold', fontSize: 13, color: '#333' }}>
                                    Semana {idx + 1}
                                  </span>
                                  {esSemanConFestivo && (
                                    <span style={{ 
                                      backgroundColor: '#ff9800', 
                                      color: 'white', 
                                      padding: '2px 6px', 
                                      borderRadius: 4, 
                                      fontSize: 9 
                                    }}>
                                      🎉 Lunes Festivo
                                    </span>
                                  )}
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <span style={{ fontWeight: 'bold', fontSize: 14, color: (parseFloat(semana.total_sobrante) || 0) >= 0 ? '#4caf50' : '#f44336' }}>
                                    {formatearMoneda(semana.total_sobrante || 0)}
                                  </span>
                                  <span style={{ fontSize: 10, color: '#999', marginLeft: 8 }}>
                                    ({cuadresCount + manualesCount} registro{(cuadresCount + manualesCount) !== 1 ? 's' : ''})
                                  </span>
                                </div>
                              </div>
                              
                              {/* Detalles de cuadres y manuales */}
                              {semana.detalles && semana.detalles.length > 0 && (
                                <div style={{ backgroundColor: '#fafafa' }}>
                                  {semana.detalles.map((detalle, dIdx) => {
                                    const fechaDetalle = new Date(detalle.fecha + 'T00:00:00');
                                    // Formatear el día de la semana en mayúsculas
                                    const diaSemana = fechaDetalle.toLocaleDateString('es-CO', { weekday: 'long' }).toUpperCase();
                                    const fechaCompleta = fechaDetalle.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' }).toUpperCase();
                                    
                                    return (
                                      <div key={dIdx} style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        padding: '12px 14px',
                                        borderBottom: dIdx < semana.detalles.length - 1 ? '1px solid #eee' : 'none'
                                      }}>
                                        <div style={{ flex: 1 }}>
                                          {/* Fecha grande y en negrita con día de la semana */}
                                          <div style={{ 
                                            fontSize: 16, 
                                            fontWeight: 'bold', 
                                            color: '#1a237e',
                                            marginBottom: 6
                                          }}>
                                            📅 {diaSemana} {fechaCompleta}
                                          </div>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                                            <span style={{ 
                                              display: 'inline-block',
                                              backgroundColor: detalle.tipo === 'manual' ? '#fff3e0' : '#e3f2fd',
                                              color: detalle.tipo === 'manual' ? '#e65100' : '#1565c0',
                                              padding: '3px 8px',
                                              borderRadius: 4,
                                              fontSize: 11,
                                              fontWeight: 'bold'
                                            }}>
                                              {detalle.tipo === 'manual' ? '✏️ Manual' : '📋 Cuadre'}
                                            </span>
                                          </div>
                                          {detalle.concepto && (
                                            <p style={{ 
                                              margin: '4px 0 0 0', 
                                              fontSize: 13, 
                                              color: '#666', 
                                              fontStyle: 'italic',
                                              paddingLeft: 4
                                            }}>
                                              💬 "{detalle.concepto}"
                                            </p>
                                          )}
                                          {detalle.registrado_por && (
                                            <p style={{ 
                                              margin: '2px 0 0 0', 
                                              fontSize: 10, 
                                              color: '#999',
                                              paddingLeft: 4
                                            }}>
                                              Registrado por: {detalle.registrado_por}
                                            </p>
                                          )}
                                        </div>
                                        <div style={{ textAlign: 'right', minWidth: 100 }}>
                                          <span style={{ 
                                            fontSize: 18, 
                                            fontWeight: 'bold', 
                                            color: (parseFloat(detalle.sobrante) || 0) >= 0 ? '#4caf50' : '#f44336' 
                                          }}>
                                            {formatearMoneda(detalle.sobrante || 0)}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Modal para agregar sobrante manual */}
        {mostrarModalSobranteManual && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: 16,
              padding: 24,
              width: '90%',
              maxWidth: 500,
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, color: '#1a237e' }}>➕ Agregar Sobrante Manual</h3>
                <button
                  onClick={cerrarModalSobranteManual}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 24,
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ✕
                </button>
              </div>
              
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#e3f2fd', borderRadius: 8 }}>
                <p style={{ margin: 0, fontSize: 13, color: '#1565c0' }}>
                  💡 Use esta opción para agregar sobrantes adicionales que no provienen de un cuadre específico pero deben sumarse al total semanal de la sede.
                </p>
              </div>
              
              <div style={{ display: 'grid', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6, color: '#333' }}>
                    Sede *
                  </label>
                  <select
                    value={sobranteManualSede}
                    onChange={(e) => setSobranteManualSede(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: 12, 
                      borderRadius: 8, 
                      border: '1px solid #ddd',
                      fontSize: 14
                    }}
                  >
                    <option value="">Seleccione una sede</option>
                    {sedes.map(s => (
                      <option key={s.id || s.nombre} value={s.nombre}>{s.nombre}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6, color: '#333' }}>
                    Fecha *
                  </label>
                  <input
                    type="date"
                    value={sobranteManualFecha}
                    onChange={(e) => setSobranteManualFecha(e.target.value)}
                    style={{ 
                      width: '100%', 
                      padding: 12, 
                      borderRadius: 8, 
                      border: '1px solid #ddd',
                      fontSize: 14
                    }}
                  />
                  <p style={{ margin: '4px 0 0', fontSize: 11, color: '#999' }}>
                    El sobrante se agregará a la semana (lunes-domingo) correspondiente a esta fecha
                  </p>
                </div>
                
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6, color: '#333' }}>
                    Valor del Sobrante *
                  </label>
                  <input
                    type="number"
                    value={sobranteManualValor}
                    onChange={(e) => setSobranteManualValor(e.target.value)}
                    placeholder="Ej: 50000"
                    style={{ 
                      width: '100%', 
                      padding: 12, 
                      borderRadius: 8, 
                      border: '1px solid #ddd',
                      fontSize: 14
                    }}
                  />
                  <p style={{ margin: '4px 0 0', fontSize: 11, color: '#999' }}>
                    Use valores negativos para faltantes (ej: -25000)
                  </p>
                </div>
                
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6, color: '#333' }}>
                    Concepto / Descripción *
                  </label>
                  <textarea
                    value={sobranteManualConcepto}
                    onChange={(e) => setSobranteManualConcepto(e.target.value)}
                    placeholder="Ej: Sobrante encontrado en caja menor, Ajuste por diferencia de cierre, etc."
                    rows={3}
                    style={{ 
                      width: '100%', 
                      padding: 12, 
                      borderRadius: 8, 
                      border: '1px solid #ddd',
                      fontSize: 14,
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: 12, 
                marginTop: 24, 
                justifyContent: 'flex-end' 
              }}>
                <button
                  onClick={cerrarModalSobranteManual}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#757575',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={guardarSobranteManual}
                  disabled={guardando || !sobranteManualSede || !sobranteManualFecha || !sobranteManualValor || !sobranteManualConcepto}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: guardando ? '#9e9e9e' : '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: guardando ? 'wait' : 'pointer',
                    fontSize: 14,
                    fontWeight: 'bold'
                  }}
                >
                  {guardando ? '⏳ Guardando...' : '💾 Guardar Sobrante'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal para resumen ejecutivo */}
        {mostrarModalResumen && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: 16,
              padding: 24,
              width: '95%',
              maxWidth: 900,
              maxHeight: '95vh',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, color: '#1a237e' }}>📊 Resumen Ejecutivo de Sobrantes</h3>
                <button
                  onClick={cerrarModalResumen}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 24,
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  ✕
                </button>
              </div>
              
              {/* Tabs: Generar nuevo o ver guardados */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                <button
                  onClick={() => setMostrarResumenesGuardados(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: !mostrarResumenesGuardados ? '#1976d2' : '#e0e0e0',
                    color: !mostrarResumenesGuardados ? 'white' : '#333',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  📝 Generar Nuevo
                </button>
                <button
                  onClick={() => setMostrarResumenesGuardados(true)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: mostrarResumenesGuardados ? '#1976d2' : '#e0e0e0',
                    color: mostrarResumenesGuardados ? 'white' : '#333',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  📁 Resúmenes Guardados ({resumenesGuardados.length})
                </button>
              </div>
              
              {!mostrarResumenesGuardados ? (
                <>
                  {/* Formulario de generación */}
                  {!resumenGenerado ? (
                    <div style={{ display: 'grid', gap: 16 }}>
                      {/* Tipo de resumen */}
                      <div>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 8 }}>Tipo de Resumen</label>
                        <div style={{ display: 'flex', gap: 10 }}>
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 6, 
                            padding: '10px 16px',
                            backgroundColor: resumenTipo === 'todas' ? '#e3f2fd' : '#f5f5f5',
                            borderRadius: 8,
                            cursor: 'pointer',
                            border: resumenTipo === 'todas' ? '2px solid #1976d2' : '2px solid transparent'
                          }}>
                            <input 
                              type="radio" 
                              name="tipoResumen" 
                              value="todas" 
                              checked={resumenTipo === 'todas'}
                              onChange={() => setResumenTipo('todas')}
                            />
                            🏢 Todas las sedes
                          </label>
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 6, 
                            padding: '10px 16px',
                            backgroundColor: resumenTipo === 'sede' ? '#e3f2fd' : '#f5f5f5',
                            borderRadius: 8,
                            cursor: 'pointer',
                            border: resumenTipo === 'sede' ? '2px solid #1976d2' : '2px solid transparent'
                          }}>
                            <input 
                              type="radio" 
                              name="tipoResumen" 
                              value="sede" 
                              checked={resumenTipo === 'sede'}
                              onChange={() => setResumenTipo('sede')}
                            />
                            🏪 Sede específica
                          </label>
                        </div>
                      </div>
                      
                      {/* Selector de sede si es específica */}
                      {resumenTipo === 'sede' && (
                        <div>
                          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>Seleccionar Sede</label>
                          <select
                            value={resumenSedeSeleccionada}
                            onChange={(e) => setResumenSedeSeleccionada(e.target.value)}
                            style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}
                          >
                            <option value="">Seleccione una sede</option>
                            {sedes.map(s => (
                              <option key={s.id || s.nombre} value={s.nombre}>{s.nombre}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      
                      {/* Período */}
                      <div>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 8 }}>Período</label>
                        <div style={{ display: 'flex', gap: 10 }}>
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 6, 
                            padding: '10px 16px',
                            backgroundColor: resumenPeriodo === 'fechas' ? '#e3f2fd' : '#f5f5f5',
                            borderRadius: 8,
                            cursor: 'pointer',
                            border: resumenPeriodo === 'fechas' ? '2px solid #1976d2' : '2px solid transparent'
                          }}>
                            <input 
                              type="radio" 
                              name="periodo" 
                              value="fechas" 
                              checked={resumenPeriodo === 'fechas'}
                              onChange={() => setResumenPeriodo('fechas')}
                            />
                            📅 Rango de fechas
                          </label>
                          <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 6, 
                            padding: '10px 16px',
                            backgroundColor: resumenPeriodo === 'mes' ? '#e3f2fd' : '#f5f5f5',
                            borderRadius: 8,
                            cursor: 'pointer',
                            border: resumenPeriodo === 'mes' ? '2px solid #1976d2' : '2px solid transparent'
                          }}>
                            <input 
                              type="radio" 
                              name="periodo" 
                              value="mes" 
                              checked={resumenPeriodo === 'mes'}
                              onChange={() => setResumenPeriodo('mes')}
                            />
                            📆 Mes completo
                          </label>
                        </div>
                      </div>
                      
                      {/* Campos de fecha según el período */}
                      {resumenPeriodo === 'fechas' ? (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div>
                            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>Desde</label>
                            <input
                              type="date"
                              value={resumenFechaDesde}
                              onChange={(e) => setResumenFechaDesde(e.target.value)}
                              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>Hasta</label>
                            <input
                              type="date"
                              value={resumenFechaHasta}
                              onChange={(e) => setResumenFechaHasta(e.target.value)}
                              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div>
                            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>Mes</label>
                            <select
                              value={resumenMes}
                              onChange={(e) => setResumenMes(e.target.value)}
                              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}
                            >
                              <option value="">Seleccione un mes</option>
                              <option value="1">Enero</option>
                              <option value="2">Febrero</option>
                              <option value="3">Marzo</option>
                              <option value="4">Abril</option>
                              <option value="5">Mayo</option>
                              <option value="6">Junio</option>
                              <option value="7">Julio</option>
                              <option value="8">Agosto</option>
                              <option value="9">Septiembre</option>
                              <option value="10">Octubre</option>
                              <option value="11">Noviembre</option>
                              <option value="12">Diciembre</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6 }}>Año</label>
                            <select
                              value={resumenAnio}
                              onChange={(e) => setResumenAnio(e.target.value)}
                              style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #ddd', fontSize: 14 }}
                            >
                              {[2024, 2025, 2026, 2027].map(a => (
                                <option key={a} value={a}>{a}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                      
                      <div style={{ display: 'flex', gap: 12, marginTop: 16, justifyContent: 'flex-end' }}>
                        <button
                          onClick={cerrarModalResumen}
                          style={{
                            padding: '12px 24px',
                            backgroundColor: '#757575',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            cursor: 'pointer',
                            fontSize: 14
                          }}
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={generarResumenEjecutivo}
                          disabled={guardando || (resumenTipo === 'sede' && !resumenSedeSeleccionada) || (resumenPeriodo === 'fechas' && (!resumenFechaDesde || !resumenFechaHasta)) || (resumenPeriodo === 'mes' && !resumenMes)}
                          style={{
                            padding: '12px 24px',
                            backgroundColor: guardando ? '#9e9e9e' : '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            cursor: guardando ? 'wait' : 'pointer',
                            fontSize: 14,
                            fontWeight: 'bold'
                          }}
                        >
                          {guardando ? '⏳ Generando...' : '📊 Generar Resumen'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Vista del resumen generado */
                    <div>
                      <div style={{ 
                        backgroundColor: '#4caf50', 
                        color: 'white', 
                        padding: 20, 
                        borderRadius: 12, 
                        textAlign: 'center',
                        marginBottom: 20
                      }}>
                        <p style={{ margin: 0, fontSize: 14, opacity: 0.9 }}>
                          {resumenGenerado.sede} | {resumenGenerado.periodo}
                        </p>
                        <p style={{ margin: '10px 0 0', fontSize: 32, fontWeight: 'bold' }}>
                          {formatearMoneda(resumenGenerado.total_general)}
                        </p>
                        <p style={{ margin: '8px 0 0', fontSize: 12, opacity: 0.9 }}>
                          📋 {resumenGenerado.total_cuadres} cuadres | ✏️ {resumenGenerado.total_manuales} manuales
                        </p>
                      </div>
                      
                      {/* Detalle por sede */}
                      <div style={{ maxHeight: 400, overflowY: 'auto', marginBottom: 20 }}>
                        {resumenGenerado.por_sede.map((s, idx) => (
                          <div key={idx} style={{ 
                            marginBottom: 12, 
                            border: '1px solid #e0e0e0', 
                            borderRadius: 8,
                            overflow: 'hidden'
                          }}>
                            <div style={{ 
                              backgroundColor: '#4caf50', 
                              color: 'white', 
                              padding: 12,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span style={{ fontWeight: 'bold' }}>🏪 {s.sede}</span>
                              <span style={{ fontWeight: 'bold', fontSize: 18 }}>{formatearMoneda(s.total)}</span>
                            </div>
                            <div style={{ padding: 12, backgroundColor: '#fafafa' }}>
                              <p style={{ margin: '0 0 8px', color: '#666', fontSize: 13 }}>
                                📋 {s.cuadres} cuadre(s) | ✏️ {s.manuales} manual(es) | 📆 {s.semanas.length} semana(s)
                              </p>
                              {s.semanas.map((sem, sIdx) => (
                                <div key={sIdx} style={{ 
                                  padding: 8, 
                                  marginBottom: 8, 
                                  backgroundColor: 'white', 
                                  borderRadius: 4,
                                  border: '1px solid #eee'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ fontSize: 12, color: '#666' }}>
                                      📅 {new Date(sem.semana_inicio + 'T00:00:00').toLocaleDateString('es-CO')} - {new Date(sem.semana_fin + 'T00:00:00').toLocaleDateString('es-CO')}
                                    </span>
                                    <span style={{ fontWeight: 'bold', color: sem.total >= 0 ? '#4caf50' : '#f44336' }}>
                                      {formatearMoneda(sem.total)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Botones de acción */}
                      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setResumenGenerado(null)}
                          style={{
                            padding: '12px 24px',
                            backgroundColor: '#757575',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            cursor: 'pointer',
                            fontSize: 14
                          }}
                        >
                          ← Volver
                        </button>
                        <button
                          onClick={imprimirResumen}
                          style={{
                            padding: '12px 24px',
                            backgroundColor: '#ff9800',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: 'bold'
                          }}
                        >
                          🖨️ Imprimir
                        </button>
                        <button
                          onClick={guardarResumenEjecutivo}
                          disabled={guardando}
                          style={{
                            padding: '12px 24px',
                            backgroundColor: guardando ? '#9e9e9e' : '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            cursor: guardando ? 'wait' : 'pointer',
                            fontSize: 14,
                            fontWeight: 'bold'
                          }}
                        >
                          {guardando ? '⏳ Guardando...' : '💾 Guardar Resumen'}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Lista de resúmenes guardados */
                <div>
                  {resumenesGuardados.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                      <span style={{ fontSize: 48 }}>📁</span>
                      <p style={{ fontSize: 16, marginTop: 16 }}>No hay resúmenes guardados</p>
                    </div>
                  ) : (
                    <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                      {resumenesGuardados.map((r, idx) => (
                        <div key={idx} style={{ 
                          padding: 16, 
                          marginBottom: 12, 
                          border: '1px solid #e0e0e0', 
                          borderRadius: 8,
                          backgroundColor: '#fafafa'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <p style={{ margin: 0, fontWeight: 'bold', fontSize: 16, color: '#1a237e' }}>
                                📊 {r.sede}
                              </p>
                              <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>
                                📅 {r.periodo}
                              </p>
                              <p style={{ margin: '4px 0 0', color: '#999', fontSize: 11 }}>
                                Generado por {r.generado_por_nombre} el {new Date(r.fecha_generacion).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <p style={{ 
                                margin: 0, 
                                fontWeight: 'bold', 
                                fontSize: 20, 
                                color: (r.total_general || 0) >= 0 ? '#4caf50' : '#f44336' 
                              }}>
                                {formatearMoneda(r.total_general)}
                              </p>
                              <p style={{ margin: '4px 0 0', fontSize: 11, color: '#999' }}>
                                📋 {r.total_cuadres} | ✏️ {r.total_manuales}
                              </p>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                            <button
                              onClick={() => {
                                setResumenGenerado(r);
                                setMostrarResumenesGuardados(false);
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#1976d2',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                fontSize: 12
                              }}
                            >
                              👁️ Ver
                            </button>
                            <button
                              onClick={() => {
                                setResumenGenerado(r);
                                setMostrarResumenesGuardados(false);
                                setTimeout(() => imprimirResumen(), 100);
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#ff9800',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                fontSize: 12
                              }}
                            >
                              🖨️ Imprimir
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Renderizar consulta de revisiones
  const renderConsultaRevisiones = () => {
    // Verificar si puede ver historial completo
    // IMPORTANTE: admin_revisar_cuadre solo da acceso al módulo, NO al historial completo
    const puedeVerHistorialCompleto = esGerente || 
      tienePermiso("admin_revisar_cuadre_ver_historial");
    
    return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 20 
      }}>
        <h3 style={{ margin: 0, color: '#1a237e' }}>
          📊 {puedeVerHistorialCompleto ? 'Historial de Revisiones' : 'Mis Revisiones Asignadas'}
        </h3>
        {/* Solo mostrar Nueva Revisión a usuarios autorizados */}
        {puedeVerHistorialCompleto && (
          <button
            onClick={() => setModo("seleccionar")}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1a237e',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer'
            }}
          >
            🔍 Nueva Revisión
          </button>
        )}
        {/* Empleados solo ven botón Volver */}
        {!puedeVerHistorialCompleto && (
          <button
            onClick={() => setModo("seleccionar")}
            style={{
              padding: '8px 16px',
              backgroundColor: '#757575',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer'
            }}
          >
            ← Volver
          </button>
        )}
      </div>
      
      {/* Filtros - solo mostrar a usuarios autorizados */}
      {puedeVerHistorialCompleto && (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 12,
        marginBottom: 16,
        padding: 16,
        backgroundColor: '#f5f5f5',
        borderRadius: 12
      }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Sede</label>
          <select
            value={filtroSede}
            onChange={(e) => { setFiltroSede(e.target.value); }}
            style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
          >
            <option value="">Todas</option>
            {sedes.map(s => (
              <option key={s.id || s.nombre} value={s.nombre}>{s.nombre}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Estado</label>
          <select
            value={filtroEstado}
            onChange={(e) => { setFiltroEstado(e.target.value); }}
            style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
          >
            <option value="">Todos</option>
            <option value="en_revision">En Revisión</option>
            <option value="aprobado">Aprobado</option>
            <option value="descuadre">Descuadre</option>
          </select>
        </div>
        
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Desde</label>
          <input
            type="date"
            value={filtroFechaDesde}
            onChange={(e) => { setFiltroFechaDesde(e.target.value); }}
            style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
          />
        </div>
        
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Hasta</label>
          <input
            type="date"
            value={filtroFechaHasta}
            onChange={(e) => { setFiltroFechaHasta(e.target.value); }}
            style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
          />
        </div>
        
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button
            onClick={cargarRevisiones}
            style={{
              padding: '8px 16px',
              backgroundColor: '#1a237e',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              width: '100%'
            }}
          >
            🔍 Filtrar
          </button>
        </div>
      </div>
      )}
      
      {/* Mensaje para empleados sin asignaciones */}
      {!puedeVerHistorialCompleto && revisionesGuardadas.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: 40, 
          backgroundColor: '#fff3e0', 
          borderRadius: 12,
          border: '1px solid #ffb74d'
        }}>
          <p style={{ fontSize: 18, color: '#e65100' }}>
            📋 No tienes revisiones asignadas actualmente
          </p>
          <p style={{ fontSize: 14, color: '#666', marginTop: 8 }}>
            Cuando un gerente te asigne una revisión, aparecerá aquí.
          </p>
        </div>
      )}
      
      {/* Lista de revisiones - solo mostrar si hay revisiones O si puede ver historial completo */}
      {(puedeVerHistorialCompleto || revisionesGuardadas.length > 0) && revisionesGuardadas.length === 0 && puedeVerHistorialCompleto ? (
        <div style={{ 
          textAlign: 'center', 
          padding: 40, 
          backgroundColor: 'white', 
          borderRadius: 12,
          border: '1px solid #e0e0e0'
        }}>
          <p style={{ fontSize: 18, color: '#666' }}>No hay revisiones que mostrar</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {revisionesGuardadas.map(revision => (
            <div
              key={revision.id}
              style={{
                padding: 16,
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: 12,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: 16, color: '#1a237e' }}>
                    {revision.sede} - {revision.fecha_cuadre}
                  </div>
                  <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                    Cuadre de: {revision.empleado_cuadre_nombre} | 
                    Revisado por: {revision.revisor_principal_nombre}
                  </div>
                  <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                    Fecha revisión: {new Date(revision.fecha_revision).toLocaleDateString()}
                    {revision.fecha_cierre && ` | Cerrado: ${new Date(revision.fecha_cierre).toLocaleDateString()}`}
                    {revision.cerrado_por_nombre && ` por ${revision.cerrado_por_nombre}`}
                  </div>
                  
                  {/* Mostrar si fue reabierta */}
                  {revision.historial_acciones && revision.historial_acciones.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <details style={{ fontSize: 11, color: '#666' }}>
                        <summary style={{ cursor: 'pointer', color: '#1a237e' }}>
                          📜 Ver historial de acciones ({revision.historial_acciones.length})
                        </summary>
                        <div style={{ 
                          marginTop: 8, 
                          padding: 8, 
                          backgroundColor: '#f5f5f5', 
                          borderRadius: 4,
                          maxHeight: 150,
                          overflowY: 'auto'
                        }}>
                          {revision.historial_acciones.map((accion, idx) => (
                            <div key={idx} style={{ 
                              padding: '4px 0', 
                              borderBottom: idx < revision.historial_acciones.length - 1 ? '1px solid #ddd' : 'none' 
                            }}>
                              <span style={{ 
                                padding: '2px 6px', 
                                backgroundColor: accion.accion === 'reabrir' ? '#ff9800' : '#4caf50',
                                color: 'white',
                                borderRadius: 4,
                                fontSize: 10,
                                marginRight: 8
                              }}>
                                {accion.accion === 'reabrir' ? '🔓 Reabierta' : '🔒 Cerrada'}
                              </span>
                              <span>{accion.usuario_nombre}</span>
                              <span style={{ color: '#999', marginLeft: 8 }}>
                                {new Date(accion.fecha).toLocaleString('es-CO')}
                              </span>
                              {accion.estado_nuevo && (
                                <span style={{ color: '#666', marginLeft: 8 }}>
                                  → {accion.estado_nuevo.toUpperCase()}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                  <span style={{
                    padding: '6px 16px',
                    backgroundColor: getColorEstado(revision.estado),
                    color: 'white',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 'bold'
                  }}>
                    {revision.estado?.replace('_', ' ').toUpperCase()}
                  </span>
                  
                  <div style={{ display: 'flex', gap: 8 }}>
                    {/* Botón Continuar - solo para usuarios autorizados */}
                    {revision.estado === 'en_revision' && 
                     (esGerente || tienePermiso("admin_revisar_cuadre") || 
                      todasLasAsignaciones.some(a => a.revision_id === revision.id)) && (
                      <button
                        onClick={async () => {
                          // Buscar el cuadre correspondiente
                          const cuadre = cuadresDisponibles.find(c => c.id === revision.cuadre_id);
                          if (cuadre) {
                            await iniciarRevision(cuadre);
                          } else {
                            alert("No se encontró el cuadre original");
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#ff9800',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                      >
                        ✏️ Continuar
                      </button>
                    )}
                    
                    {/* Botón Ver - para revisiones cerradas asignadas a empleados */}
                    {(revision.estado === 'aprobado' || revision.estado === 'descuadre') && 
                     (esGerente || tienePermiso("admin_revisar_cuadre") || 
                      todasLasAsignaciones.some(a => a.revision_id === revision.id)) && (
                      <button
                        onClick={() => verRevisionSoloLectura(revision)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#607d8b',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                      >
                        👁️ Ver
                      </button>
                    )}
                    
                    {/* Botón Reabrir - solo para revisiones cerradas y gerencia */}
                    {(revision.estado === 'aprobado' || revision.estado === 'descuadre') && 
                     (esGerente || tienePermiso("admin_revisar_cuadre_reabrir")) && (
                      <button
                        onClick={() => reabrirRevision(revision)}
                        disabled={guardando}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#ff9800',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                      >
                        🔓 Reabrir
                      </button>
                    )}
                    
                    {/* Botón Asignar empleados para ver esta revisión */}
                    {(esGerente || tienePermiso("admin_revisar_cuadre_asignar_revision")) && (
                      <button
                        onClick={() => abrirModalAsignarRevision(revision)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#2196f3',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                      >
                        👥 Asignar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  };

  // Modal de asignación
  const renderModalAsignacion = () => (
    mostrarModalAsignacion && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: 24,
          borderRadius: 12,
          width: '90%',
          maxWidth: 400
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#1a237e' }}>
            👤 Asignar Sección: {SECCIONES.find(s => s.key === seccionParaAsignar)?.nombre}
          </h3>
          
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold' }}>
              Empleado Responsable:
            </label>
            <select
              value={empleadoAsignacion}
              onChange={(e) => setEmpleadoAsignacion(e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                borderRadius: 8,
                border: '1px solid #ddd',
                fontSize: 14
              }}
            >
              <option value="">Seleccionar empleado...</option>
              {[...(empleados || [])].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map(emp => (
                <option key={emp.id} value={emp.id}>{emp.nombre}</option>
              ))}
            </select>
          </div>
          
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setMostrarModalAsignacion(false);
                setSeccionParaAsignar("");
                setEmpleadoAsignacion("");
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f5f5f5',
                border: '1px solid #ddd',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
            <button
              onClick={asignarSeccion}
              style={{
                padding: '8px 16px',
                backgroundColor: '#1a237e',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              ✅ Asignar
            </button>
          </div>
        </div>
      </div>
    )
  );
  
  // Modal para asignar empleados a ver una revisión específica
  const renderModalAsignarRevision = () => (
    mostrarModalAsignarRevision && revisionParaAsignar && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: 24,
          borderRadius: 12,
          width: '90%',
          maxWidth: 500
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#1a237e' }}>
            👥 Asignar Empleados a Revisión
          </h3>
          
          <div style={{ 
            padding: 12, 
            backgroundColor: '#f5f5f5', 
            borderRadius: 8, 
            marginBottom: 16 
          }}>
            <div style={{ fontWeight: 'bold' }}>{revisionParaAsignar.sede} - {revisionParaAsignar.fecha_cuadre}</div>
            <div style={{ fontSize: 12, color: '#666' }}>
              Estado: {revisionParaAsignar.estado?.toUpperCase()}
            </div>
          </div>
          
          {/* Lista de empleados asignados */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 'bold', marginBottom: 8 }}>
              Empleados con acceso a esta revisión:
            </label>
            {empleadosAsignadosRevision.length === 0 ? (
              <p style={{ color: '#999', fontSize: 13 }}>No hay empleados asignados</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {empleadosAsignadosRevision.map(asig => (
                  <span 
                    key={asig.id}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#e3f2fd',
                      borderRadius: 20,
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    👤 {asig.empleado_nombre}
                    {(esGerente || tienePermiso("admin_revisar_cuadre_asignar_revision")) && (
                      <button
                        onClick={() => eliminarAsignacionRevision(asig.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#f44336',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: 14
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>
          
          {/* Agregar nuevo empleado */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 'bold', marginBottom: 8 }}>
              Agregar empleado:
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                value={empleadoNuevoAsignacion}
                onChange={(e) => setEmpleadoNuevoAsignacion(e.target.value)}
                style={{ flex: 1, padding: 10, borderRadius: 4, border: '1px solid #ddd' }}
              >
                <option value="">Seleccionar empleado...</option>
                {empleados
                  .filter(e => !empleadosAsignadosRevision.some(a => String(a.empleado_id) === String(e.id)))
                  .map(e => (
                    <option key={e.id} value={e.id}>{e.nombre}</option>
                  ))
                }
              </select>
              <button
                onClick={asignarEmpleadoARevision}
                disabled={!empleadoNuevoAsignacion}
                style={{
                  padding: '10px 16px',
                  backgroundColor: empleadoNuevoAsignacion ? '#4caf50' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: empleadoNuevoAsignacion ? 'pointer' : 'not-allowed'
                }}
              >
                ➕ Agregar
              </button>
            </div>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setMostrarModalAsignarRevision(false);
                setRevisionParaAsignar(null);
                setEmpleadosAsignadosRevision([]);
                setEmpleadoNuevoAsignacion("");
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#757575',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    )
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Indicador de carga global */}
      {cargando && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255,255,255,0.9)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            width: 50,
            height: 50,
            border: '4px solid #e0e0e0',
            borderTop: '4px solid #1a237e',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ marginTop: 16, color: '#1a237e', fontSize: 16 }}>Cargando revisión...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      {/* Solo gerentes o con permiso ver_historial pueden crear nuevas revisiones */}
      {modo === "seleccionar" && (esGerente || tienePermiso("admin_revisar_cuadre_ver_historial")) && renderSeleccionCuadre()}
      {/* Empleados sin permisos que intenten acceder a seleccionar ven un mensaje */}
      {modo === "seleccionar" && !esGerente && !tienePermiso("admin_revisar_cuadre_ver_historial") && (
        <div style={{ 
          textAlign: 'center', 
          padding: 40, 
          backgroundColor: '#ffebee', 
          borderRadius: 12,
          border: '1px solid #ef5350'
        }}>
          <p style={{ fontSize: 18, color: '#c62828' }}>
            🚫 No tienes permisos para iniciar nuevas revisiones
          </p>
          <p style={{ fontSize: 14, color: '#666', marginTop: 8 }}>
            Solo puedes acceder a las revisiones que te han sido asignadas.
          </p>
          <button
            onClick={() => { setModo("consultar"); cargarRevisiones(); }}
            style={{
              marginTop: 16,
              padding: '10px 20px',
              backgroundColor: '#1a237e',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer'
            }}
          >
            📋 Ver Mis Asignaciones
          </button>
        </div>
      )}
      {modo === "revisar" && renderRevisionCuadre()}
      {modo === "consultar" && renderConsultaRevisiones()}
      {modo === "sobrantes" && renderSobrantes()}
      {renderModalAsignacion()}
      {renderModalAsignarRevision()}
    </div>
  );
}

/* ====================== MÓDULO DE GASTOS ====================== */

// Lista de gastos recurrentes predefinidos
const GASTOS_RECURRENTES_CONFIG = [
  // ENERGÍA
  { id: "energia_corales_1", nombre: "ENERGÍA LOCAL 1 MATRÍCULA 964817", sede: "SEDE CORALES", categoria: "Energía", matricula: "964817" },
  { id: "energia_corales_2", nombre: "ENERGÍA LOCAL 2 MATRÍCULA 964809", sede: "SEDE CORALES", categoria: "Energía", matricula: "964809" },
  { id: "energia_av_sur", nombre: "ENERGÍA MATRÍCULA 2083520", sede: "SEDE AV SUR", categoria: "Energía", matricula: "2083520" },
  { id: "energia_villa_local", nombre: "ENERGÍA LOCAL MATRÍCULA 1676014", sede: "SEDE VILLA DEL PRADO", categoria: "Energía", matricula: "1676014" },
  { id: "energia_villa_apto", nombre: "ENERGÍA APARTAMENTO MATRÍCULA 1645746", sede: "SEDE VILLA DEL PRADO", categoria: "Energía", matricula: "1645746" },
  // AGUA
  { id: "agua_corales_1", nombre: "AGUA LOCAL 1 MATRÍCULA 964817", sede: "SEDE CORALES", categoria: "Agua", matricula: "964817" },
  { id: "agua_corales_2", nombre: "AGUA LOCAL 2 MATRÍCULA 964809", sede: "SEDE CORALES", categoria: "Agua", matricula: "964809" },
  { id: "agua_av_sur", nombre: "AGUA MATRÍCULA 1774488", sede: "SEDE AV SUR", categoria: "Agua", matricula: "1774488" },
  { id: "agua_villa_local", nombre: "AGUA LOCAL MATRÍCULA 1676014", sede: "SEDE VILLA DEL PRADO", categoria: "Agua", matricula: "1676014" },
  { id: "agua_villa_apto", nombre: "AGUA APARTAMENTO MATRÍCULA 1645746", sede: "SEDE VILLA DEL PRADO", categoria: "Agua", matricula: "1645746" },
  // GAS
  { id: "gas_corales", nombre: "GAS MATRÍCULA 343783", sede: "SEDE CORALES", categoria: "Gas", matricula: "343783" },
  { id: "gas_av_sur", nombre: "GAS MATRÍCULA 316683", sede: "SEDE AV SUR", categoria: "Gas", matricula: "316683" },
  { id: "gas_villa_local", nombre: "GAS LOCAL MATRÍCULA 1374658", sede: "SEDE VILLA DEL PRADO", categoria: "Gas", matricula: "1374658" },
  { id: "gas_villa_apto", nombre: "GAS APARTAMENTO MATRÍCULA 298205", sede: "SEDE VILLA DEL PRADO", categoria: "Gas", matricula: "298205" },
  // INTERNET
  { id: "internet_corales", nombre: "INTERNET REFERENCIA 6075089570", sede: "SEDE CORALES", categoria: "Internet", referencia: "6075089570" },
  { id: "internet_av_sur", nombre: "INTERNET LÍNEA FIJA 6063396763", sede: "SEDE AV SUR", categoria: "Internet", referencia: "6063396763" },
  { id: "internet_villa", nombre: "INTERNET PAGO AUTOMATICO LINEA 6063391595", sede: "SEDE VILLA DEL PRADO", categoria: "Internet" },
  // SALUD
  { id: "salud_corales", nombre: "SALUD", sede: "SEDE CORALES", categoria: "Salud" },
  { id: "salud_av_sur", nombre: "SALUD", sede: "SEDE AV SUR", categoria: "Salud" },
  { id: "salud_villa", nombre: "SALUD", sede: "SEDE VILLA DEL PRADO", categoria: "Salud" },
  // ARRIENDO
  { id: "arriendo_corales", nombre: "ARRIENDO", sede: "SEDE CORALES", categoria: "Arriendo" },
  { id: "arriendo_av_sur", nombre: "ARRIENDO", sede: "SEDE AV SUR", categoria: "Arriendo" },
  { id: "arriendo_villa_local", nombre: "ARRIENDO LOCAL", sede: "SEDE VILLA DEL PRADO", categoria: "Arriendo" },
  { id: "arriendo_villa_apto", nombre: "ARRIENDO APARTAMENTO NEQUI 3007395201", sede: "SEDE VILLA DEL PRADO", categoria: "Arriendo" },
  // NÓMINA
  { id: "nomina_corales_15", nombre: "NÓMINA CORTE 15", sede: "SEDE CORALES", categoria: "Nómina" },
  { id: "nomina_corales_30", nombre: "NÓMINA CORTE 30", sede: "SEDE CORALES", categoria: "Nómina" },
  { id: "nomina_av_sur_15", nombre: "NÓMINA CORTE 15", sede: "SEDE AV SUR", categoria: "Nómina" },
  { id: "nomina_av_sur_30", nombre: "NÓMINA CORTE 30", sede: "SEDE AV SUR", categoria: "Nómina" },
  // REGALÍAS
  { id: "regalias_corales", nombre: "REGALÍAS MARCA", sede: "SEDE CORALES", categoria: "Regalías" },
  { id: "regalias_av_sur", nombre: "REGALÍAS MARCA", sede: "SEDE AV SUR", categoria: "Regalías" },
  { id: "regalias_villa", nombre: "REGALÍAS MARCA", sede: "SEDE VILLA DEL PRADO", categoria: "Regalías" }
];

function GastosRoot({ sedes, empleados, usuarioActual = null, empleadoActual = null, esGerente = false, tienePermiso = () => true, filtroEmpresa = null }) {
  const [modo, setModo] = useState("consultar"); // "consultar" | "manual" | "resumen" | "recurrentes"
  const [cargando, setCargando] = useState(false);
  const [gastosPorSede, setGastosPorSede] = useState({});
  const [gastosDetalle, setGastosDetalle] = useState([]);
  const [resumenGenerado, setResumenGenerado] = useState(null);
  const [resumenesGuardados, setResumenesGuardados] = useState([]);
  
  // ========== ESTADOS PARA GASTOS RECURRENTES ==========
  const [gastosRecurrentes, setGastosRecurrentes] = useState([]);
  const [cargandoRecurrentes, setCargandoRecurrentes] = useState(false);
  const [mesRecurrente, setMesRecurrente] = useState(() => {
    const hoy = new Date();
    return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
  });
  const [modalRecurrente, setModalRecurrente] = useState(null); // { gasto, accion: 'vencimiento' | 'pago' | 'confirmar' }
  const [formRecurrente, setFormRecurrente] = useState({
    fechaVencimiento: '',
    valor: '',
    fechaPago: '',
    adjuntos: []
  });
  const [subiendoAdjunto, setSubiendoAdjunto] = useState(false);
  const [sedeRecurrente, setSedeRecurrente] = useState("todas"); // "todas" | sede específica
  const [modalDetallesGasto, setModalDetallesGasto] = useState(null); // Para ver detalles del gasto con adjuntos
  const [vistaVencimientos, setVistaVencimientos] = useState(false); // Vista simplificada de vencimientos
  
  // ========== ESTADOS PARA SMS DE GASTOS RECURRENTES ==========
  const [mostrarConfigSMSGastos, setMostrarConfigSMSGastos] = useState(false);
  const [configSMSGastos, setConfigSMSGastos] = useState({ telefono: "", nombre: "" });
  const [guardandoConfigSMSGastos, setGuardandoConfigSMSGastos] = useState(false);
  
  // Sedes únicas de los gastos recurrentes
  const sedesRecurrentes = useMemo(() => {
    const sedesSet = new Set();
    GASTOS_RECURRENTES_CONFIG.forEach(g => sedesSet.add(g.sede));
    return Array.from(sedesSet).sort();
  }, []);
  
  // Fechas de consulta actualmente mostradas
  const [fechaConsultaDesde, setFechaConsultaDesde] = useState("");
  const [fechaConsultaHasta, setFechaConsultaHasta] = useState("");
  const [fechasPorSede, setFechasPorSede] = useState({});
  const [cargaInicial, setCargaInicial] = useState(false);
  
  // Filtros de consulta - inicializar con mes en curso
  const [tipoConsulta, setTipoConsulta] = useState("todas"); // "todas" | "sede"
  const [sedeSeleccionada, setSedeSeleccionada] = useState("");
  const [tipoFecha, setTipoFecha] = useState("rango"); // "especifica" | "rango"
  const [fechaEspecifica, setFechaEspecifica] = useState("");
  const [fechaDesde, setFechaDesde] = useState(() => {
    const hoy = new Date();
    return new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().slice(0, 10);
  });
  const [fechaHasta, setFechaHasta] = useState(() => new Date().toISOString().slice(0, 10));
  
  // Gastos manuales
  const [gastosManualeas, setGastosManuales] = useState([]);
  const [mostrarModalGastoManual, setMostrarModalGastoManual] = useState(false);
  const [gastoManualSede, setGastoManualSede] = useState("");
  const [gastoManualFecha, setGastoManualFecha] = useState("");
  const [gastoManualProveedor, setGastoManualProveedor] = useState("");
  const [gastoManualConcepto, setGastoManualConcepto] = useState("");
  const [gastoManualValor, setGastoManualValor] = useState("");
  const [guardandoGasto, setGuardandoGasto] = useState(false);
  const [gastoEditandoId, setGastoEditandoId] = useState(null); // Para editar gastos

  // Conceptos predefinidos para gastos manuales
  const conceptosGastos = [
    "Energía",
    "Agua",
    "Gas",
    "Internet",
    "Arriendo",
    "Nómina",
    "Salud",
    "Arreglos",
    "Solomos",
    "Mayonesa",
    "House",
    "Coca-Cola",
    "Devolución a Clientes",
    "Pago de Regalías Julian",
    "Pago de Regalías Jonnier",
    "Pago de Regalías Andres",
    "Pago de Regalías Daniel",
    "Otros Gastos"
  ];

  const formatearMoneda = (valor) => {
    const num = parseFloat(valor) || 0;
    return num.toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // Cargar gastos manuales
  const cargarGastosManuales = async (filtros = {}) => {
    try {
      let query = supabase.from('gastos_manuales').select('*');
      // Filtrar por empresa
      if (filtroEmpresa) query = query.eq('empresa_id', filtroEmpresa);
      if (filtros.sede) query = query.eq('sede', filtros.sede);
      if (filtros.fechaDesde) query = query.gte('fecha', filtros.fechaDesde);
      if (filtros.fechaHasta) query = query.lte('fecha', filtros.fechaHasta);
      
      const { data, error } = await query.order('fecha', { ascending: false });
      
      if (error) {
        console.error("❌ Error cargando gastos manuales:", error);
        return [];
      }
      
      console.log("📦 Gastos manuales cargados:", data?.length || 0, data);
      setGastosManuales(data || []);
      return data || [];
    } catch (e) {
      console.warn("Tabla gastos_manuales no disponible:", e);
      return [];
    }
  };

  // Cargar gastos desde cuadres cerrados
  const cargarGastosDesdeCuadres = async () => {
    setCargando(true);
    try {
      // Construir filtros de fecha
      let fechaInicio, fechaFin;
      if (tipoFecha === "especifica" && fechaEspecifica) {
        fechaInicio = fechaEspecifica;
        fechaFin = fechaEspecifica;
      } else if (tipoFecha === "rango" && fechaDesde && fechaHasta) {
        fechaInicio = fechaDesde;
        fechaFin = fechaHasta;
      } else {
        // Por defecto, último mes
        const hoy = new Date();
        fechaFin = hoy.toISOString().slice(0, 10);
        hoy.setMonth(hoy.getMonth() - 1);
        fechaInicio = hoy.toISOString().slice(0, 10);
      }

      // Cargar cuadres diarios cerrados (estado Final)
      // Nota: si agregas columna fue_reabierto, puedes filtrar con: .or('fue_reabierto.is.null,fue_reabierto.eq.false')
      let query = supabase
        .from('cuadres_diarios')
        .select('*')
        .eq('estado', 'Final')
        .gte('fecha', fechaInicio)
        .lte('fecha', fechaFin);
      
      if (filtroEmpresa) query = query.eq('empresa_id', filtroEmpresa);
      if (tipoConsulta === "sede" && sedeSeleccionada) {
        query = query.eq('sede', sedeSeleccionada);
      }

      const { data: cuadresCerrados, error } = await query;
      
      if (error) {
        console.error("Error cargando cuadres:", error);
        setCargando(false);
        return;
      }

      console.log("✅ Cuadres cerrados cargados:", cuadresCerrados?.length || 0);

      // Cargar gastos manuales con los mismos filtros
      const filtrosGastos = {};
      if (tipoConsulta === "sede" && sedeSeleccionada) filtrosGastos.sede = sedeSeleccionada;
      filtrosGastos.fechaDesde = fechaInicio;
      filtrosGastos.fechaHasta = fechaFin;
      const gastosManualesData = await cargarGastosManuales(filtrosGastos);

      // Función helper para parsear JSON si es string
      const parseJsonSafe = (data, defaultVal = []) => {
        if (!data) return defaultVal;
        if (typeof data === 'string') {
          try { return JSON.parse(data); } catch { return defaultVal; }
        }
        return data;
      };

      // Procesar datos de cuadres cerrados
      const gastosPorSedeTemp = {};
      const detalleTemp = [];

      console.log("📋 Procesando", cuadresCerrados?.length || 0, "cuadres cerrados");

      (cuadresCerrados || []).forEach(cuadre => {
        const sede = cuadre.sede || "Sin sede";
        if (!gastosPorSedeTemp[sede]) {
          gastosPorSedeTemp[sede] = {
            facturasCaja: 0,
            facturasTransferencia: 0,
            domicilios: 0,
            turnos: 0,
            adelantos: 0,
            manuales: 0,
            manualesPorConcepto: {},
            total: 0,
            detalle: []
          };
        }

        // Parsear datos siguiendo la misma estructura que mapearRowCuadreDesdeSupabase
        const ventasRaw = parseJsonSafe(cuadre.ventas, {});
        const facturasRaw = parseJsonSafe(cuadre.facturas, []);
        const domiciliosRaw = parseJsonSafe(cuadre.domicilios, {});
        const descuentosRaw = parseJsonSafe(cuadre.descuentos, {});

        // 1. Facturas - separadas por tipo de pago (caja vs transferencia)
        // Estructura: {proveedor, valor, fechaFactura, facturaEntregada, estado}
        const facturas = Array.isArray(facturasRaw) ? facturasRaw : [];
        const facturasCaja = facturas
          .filter(f => f.estado === "Pagada de caja")
          .reduce((sum, f) => sum + (parseFloat(f.valor) || 0), 0);
        const facturasTransferencia = facturas
          .filter(f => f.estado === "Pagada transferencia")
          .reduce((sum, f) => sum + (parseFloat(f.valor) || 0), 0);
        
        // 2. Domicilios - puede estar en domiciliosRaw.domicilios o ser array directo
        const domiciliosArray = Array.isArray(domiciliosRaw?.domicilios) 
          ? domiciliosRaw.domicilios 
          : Array.isArray(domiciliosRaw) 
            ? domiciliosRaw 
            : [];
        const costoDomicilios = domiciliosArray.reduce((sum, d) => {
          const valor = parseFloat(d.valor_domicilio) || parseFloat(d.valorDomicilio) || parseFloat(d.costo) || parseFloat(d.valor) || 0;
          return sum + valor;
        }, 0);
        
        // 3. Turnos - puede estar en domiciliosRaw.turnos o ventasRaw.turnos
        const turnosArray = Array.isArray(domiciliosRaw?.turnos) 
          ? domiciliosRaw.turnos 
          : Array.isArray(ventasRaw?.turnos) 
            ? ventasRaw.turnos 
            : [];
        const costoTurnos = turnosArray.reduce((sum, t) => sum + (parseFloat(t.valor) || 0), 0);
        
        // 4. Adelantos - puede estar en domiciliosRaw.adelantos o ventasRaw.adelantos
        const adelantosArray = Array.isArray(domiciliosRaw?.adelantos) 
          ? domiciliosRaw.adelantos 
          : Array.isArray(ventasRaw?.adelantos) 
            ? ventasRaw.adelantos 
            : [];
        const costoAdelantos = adelantosArray.reduce((sum, a) => sum + (parseFloat(a.valor) || 0), 0);

        console.log(`  📊 ${sede} - Fecha: ${cuadre.fecha} | Facturas Caja: ${facturasCaja} | Facturas Transf: ${facturasTransferencia} | Dom: ${costoDomicilios} | Turnos: ${costoTurnos} | Adelantos: ${costoAdelantos}`);

        gastosPorSedeTemp[sede].facturasCaja += facturasCaja;
        gastosPorSedeTemp[sede].facturasTransferencia += facturasTransferencia;
        gastosPorSedeTemp[sede].domicilios += costoDomicilios;
        gastosPorSedeTemp[sede].turnos += costoTurnos;
        gastosPorSedeTemp[sede].adelantos += costoAdelantos;

        detalleTemp.push({
          fecha: cuadre.fecha,
          sede: sede,
          facturasCaja,
          domicilios: costoDomicilios,
          turnos: costoTurnos,
          adelantos: costoAdelantos,
          tipo: 'cuadre'
        });
      });

      // Agregar gastos manuales agrupados por concepto
      console.log("📝 Procesando gastos manuales:", gastosManualesData?.length || 0);
      (gastosManualesData || []).forEach(gm => {
        console.log("  📌 Gasto manual:", gm.sede, gm.concepto, gm.valor);
        const sede = gm.sede || "Sin sede";
        const concepto = gm.concepto || "Sin concepto";
        
        if (!gastosPorSedeTemp[sede]) {
          gastosPorSedeTemp[sede] = {
            facturasCaja: 0,
            facturasTransferencia: 0,
            domicilios: 0,
            turnos: 0,
            adelantos: 0,
            manuales: 0,
            manualesPorConcepto: {},
            total: 0,
            detalle: []
          };
        }
        
        // Inicializar manualesPorConcepto si no existe
        if (!gastosPorSedeTemp[sede].manualesPorConcepto) {
          gastosPorSedeTemp[sede].manualesPorConcepto = {};
        }
        
        const valor = parseFloat(gm.valor) || 0;
        gastosPorSedeTemp[sede].manuales += valor;
        
        // Agrupar por concepto
        if (!gastosPorSedeTemp[sede].manualesPorConcepto[concepto]) {
          gastosPorSedeTemp[sede].manualesPorConcepto[concepto] = 0;
        }
        gastosPorSedeTemp[sede].manualesPorConcepto[concepto] += valor;
        
        detalleTemp.push({
          fecha: gm.fecha,
          sede: sede,
          proveedor: gm.proveedor_beneficiario,
          concepto: concepto,
          valor: valor,
          tipo: 'manual'
        });
      });

      // Calcular totales por sede y fechas min/max por sede
      const fechasPorSedeTemp = {};
      Object.keys(gastosPorSedeTemp).forEach(sede => {
        const g = gastosPorSedeTemp[sede];
        g.total = g.facturasCaja + g.facturasTransferencia + g.domicilios + g.turnos + g.adelantos + g.manuales;
      });

      // Calcular fechas min/max por sede desde el detalle
      detalleTemp.forEach(d => {
        const sede = d.sede;
        if (!fechasPorSedeTemp[sede]) {
          fechasPorSedeTemp[sede] = { min: d.fecha, max: d.fecha };
        } else {
          if (d.fecha < fechasPorSedeTemp[sede].min) fechasPorSedeTemp[sede].min = d.fecha;
          if (d.fecha > fechasPorSedeTemp[sede].max) fechasPorSedeTemp[sede].max = d.fecha;
        }
      });

      setGastosPorSede(gastosPorSedeTemp);
      setGastosDetalle(detalleTemp);
      setFechaConsultaDesde(fechaInicio);
      setFechaConsultaHasta(fechaFin);
      setFechasPorSede(fechasPorSedeTemp);
      
    } catch (e) {
      console.error("Error cargando gastos:", e);
    }
    setCargando(false);
  };

  // Guardar gasto manual
  const guardarGastoManual = async () => {
    if (!gastoManualSede || !gastoManualFecha || !gastoManualConcepto || !gastoManualValor) {
      alert("Por favor completa todos los campos obligatorios: Sede, Fecha, Concepto y Valor");
      return;
    }
    
    setGuardandoGasto(true);
    try {
      const datosGasto = {
        sede: gastoManualSede,
        fecha: gastoManualFecha,
        concepto: gastoManualConcepto,
        valor: parseFloat(gastoManualValor) || 0,
        proveedor_beneficiario: gastoManualProveedor || gastoManualConcepto, // Si no hay proveedor, usa el concepto
        categoria: 'manual',
        empresa_id: filtroEmpresa || null,
        created_by_nombre: usuarioActual?.nombre || empleadoActual?.nombre || 'Sistema'
      };
      
      console.log("📝 Guardando gasto manual:", datosGasto);
      
      const { data, error } = await supabase.from('gastos_manuales').insert(datosGasto).select();

      if (error) {
        console.error("❌ Error Supabase completo:", JSON.stringify(error, null, 2));
        console.error("❌ Código:", error.code);
        console.error("❌ Mensaje:", error.message);
        console.error("❌ Detalles:", error.details);
        console.error("❌ Hint:", error.hint);
        
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          alert("❌ La tabla 'gastos_manuales' no existe en Supabase.\n\nDebes crearla ejecutando el SQL en Supabase.");
        } else {
          alert(`❌ Error: ${error.message || 'Error desconocido'}`);
        }
        throw error;
      }
      
      console.log("✅ Gasto guardado:", data);

      alert("✅ Gasto registrado correctamente");
      setMostrarModalGastoManual(false);
      setGastoManualSede("");
      setGastoManualFecha("");
      setGastoManualProveedor("");
      setGastoManualConcepto("");
      setGastoManualValor("");
      cargarGastosDesdeCuadres();
    } catch (e) {
      console.error("Error guardando gasto:", e);
      alert("❌ Error al guardar el gasto");
    }
    setGuardandoGasto(false);
  };

  // Eliminar gasto manual
  const eliminarGastoManual = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este gasto?")) return;
    
    try {
      const { error } = await supabase.from('gastos_manuales').delete().eq('id', id);
      if (error) throw error;
      alert("✅ Gasto eliminado correctamente");
      cargarGastosDesdeCuadres();
    } catch (e) {
      console.error("Error eliminando gasto:", e);
      alert("❌ Error al eliminar el gasto");
    }
  };

  // Editar gasto manual (abrir modal con datos)
  const editarGastoManual = (gasto) => {
    setGastoEditandoId(gasto.id);
    setGastoManualSede(gasto.sede || "");
    setGastoManualFecha(gasto.fecha || "");
    setGastoManualConcepto(gasto.concepto || "");
    setGastoManualProveedor(gasto.proveedor_beneficiario || "");
    setGastoManualValor(gasto.valor?.toString() || "");
    setMostrarModalGastoManual(true);
  };

  // Actualizar gasto manual existente
  const actualizarGastoManual = async () => {
    if (!gastoManualSede || !gastoManualFecha || !gastoManualConcepto || !gastoManualValor) {
      alert("Por favor completa todos los campos obligatorios");
      return;
    }
    
    setGuardandoGasto(true);
    try {
      const datosGasto = {
        sede: gastoManualSede,
        fecha: gastoManualFecha,
        concepto: gastoManualConcepto,
        valor: parseFloat(gastoManualValor) || 0,
        proveedor_beneficiario: gastoManualProveedor || gastoManualConcepto
      };
      
      const { error } = await supabase.from('gastos_manuales')
        .update(datosGasto)
        .eq('id', gastoEditandoId);

      if (error) throw error;

      alert("✅ Gasto actualizado correctamente");
      setMostrarModalGastoManual(false);
      setGastoEditandoId(null);
      setGastoManualSede("");
      setGastoManualFecha("");
      setGastoManualProveedor("");
      setGastoManualConcepto("");
      setGastoManualValor("");
      cargarGastosDesdeCuadres();
    } catch (e) {
      console.error("Error actualizando gasto:", e);
      alert("❌ Error al actualizar el gasto");
    }
    setGuardandoGasto(false);
  };

  // Generar resumen ejecutivo
  const generarResumenEjecutivo = async () => {
    if (Object.keys(gastosPorSede).length === 0) {
      alert("Primero debes consultar los gastos");
      return;
    }

    const detallePorDia = {};
    const detallePorCategoria = { facturasCaja: 0, domicilios: 0, turnos: 0, adelantos: 0, manuales: 0 };
    const manualesPorConcepto = {}; // Desglose de manuales por concepto
    let totalGeneral = 0;

    // Agrupar por día de la semana
    gastosDetalle.forEach(g => {
      const fecha = new Date(g.fecha + 'T12:00:00');
      const diaSemana = fecha.toLocaleDateString('es-CO', { weekday: 'long' });
      
      if (!detallePorDia[diaSemana]) detallePorDia[diaSemana] = 0;
      
      if (g.tipo === 'cuadre') {
        detallePorDia[diaSemana] += (g.facturasCaja || 0) + (g.domicilios || 0) + (g.turnos || 0) + (g.adelantos || 0);
        detallePorCategoria.facturasCaja += g.facturasCaja || 0;
        detallePorCategoria.domicilios += g.domicilios || 0;
        detallePorCategoria.turnos += g.turnos || 0;
        detallePorCategoria.adelantos += g.adelantos || 0;
      } else {
        detallePorDia[diaSemana] += g.valor || 0;
        detallePorCategoria.manuales += g.valor || 0;
        // Agrupar por concepto
        const concepto = g.concepto || 'Sin concepto';
        if (!manualesPorConcepto[concepto]) manualesPorConcepto[concepto] = 0;
        manualesPorConcepto[concepto] += g.valor || 0;
      }
    });

    // Calcular totales
    Object.values(gastosPorSede).forEach(s => totalGeneral += s.total);

    // Encontrar día con más gastos
    let diaMaxGasto = '';
    let maxGasto = 0;
    Object.entries(detallePorDia).forEach(([dia, valor]) => {
      if (valor > maxGasto) {
        maxGasto = valor;
        diaMaxGasto = dia;
      }
    });

    // Encontrar categoría con más gastos
    let categoriaMax = '';
    let maxCategoria = 0;
    Object.entries(detallePorCategoria).forEach(([cat, valor]) => {
      if (valor > maxCategoria) {
        maxCategoria = valor;
        categoriaMax = cat;
      }
    });

    const categoriasNombres = {
      facturasCaja: 'Facturas pagadas por caja',
      domicilios: 'Costo domicilios',
      turnos: 'Turnos',
      adelantos: 'Adelantos',
      manuales: 'Gastos manuales'
    };

    // Generar análisis
    let analisis = `📊 RESUMEN EJECUTIVO DE GASTOS\n`;
    analisis += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;
    analisis += `📅 Período: ${tipoFecha === 'especifica' ? fechaEspecifica : `${fechaDesde} al ${fechaHasta}`}\n`;
    analisis += `🏢 Alcance: ${tipoConsulta === 'todas' ? 'Todas las sedes' : sedeSeleccionada}\n\n`;
    
    analisis += `💰 TOTAL GASTOS: ${formatearMoneda(totalGeneral)}\n\n`;
    
    analisis += `📈 DESGLOSE POR CATEGORÍA:\n`;
    analisis += `   • Facturas Caja: ${formatearMoneda(detallePorCategoria.facturasCaja)} (${((detallePorCategoria.facturasCaja/totalGeneral)*100 || 0).toFixed(1)}%)\n`;
    analisis += `   • Costo Domicilios: ${formatearMoneda(detallePorCategoria.domicilios)} (${((detallePorCategoria.domicilios/totalGeneral)*100 || 0).toFixed(1)}%)\n`;
    analisis += `   • Turnos: ${formatearMoneda(detallePorCategoria.turnos)} (${((detallePorCategoria.turnos/totalGeneral)*100 || 0).toFixed(1)}%)\n`;
    analisis += `   • Adelantos: ${formatearMoneda(detallePorCategoria.adelantos)} (${((detallePorCategoria.adelantos/totalGeneral)*100 || 0).toFixed(1)}%)\n`;
    analisis += `   • Gastos Manuales: ${formatearMoneda(detallePorCategoria.manuales)} (${((detallePorCategoria.manuales/totalGeneral)*100 || 0).toFixed(1)}%)\n`;
    
    // Desglose de gastos manuales por concepto
    if (Object.keys(manualesPorConcepto).length > 0) {
      analisis += `\n📝 DESGLOSE GASTOS MANUALES POR CONCEPTO:\n`;
      Object.entries(manualesPorConcepto)
        .sort((a, b) => b[1] - a[1])
        .forEach(([concepto, valor]) => {
          const porcentaje = ((valor/detallePorCategoria.manuales)*100 || 0).toFixed(1);
          analisis += `      → ${concepto}: ${formatearMoneda(valor)} (${porcentaje}% de manuales)\n`;
        });
    }
    analisis += `\n`;
    
    analisis += `📅 GASTOS POR DÍA DE LA SEMANA:\n`;
    const diasOrdenados = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
    diasOrdenados.forEach(dia => {
      if (detallePorDia[dia]) {
        analisis += `   • ${dia.charAt(0).toUpperCase() + dia.slice(1)}: ${formatearMoneda(detallePorDia[dia])}\n`;
      }
    });
    
    analisis += `\n🔍 ANÁLISIS:\n`;
    analisis += `   • El día con más gastos es: ${diaMaxGasto.toUpperCase()} (${formatearMoneda(maxGasto)})\n`;
    analisis += `   • La categoría con mayor gasto es: ${categoriasNombres[categoriaMax]} (${formatearMoneda(maxCategoria)})\n`;
    
    if (tipoConsulta === 'todas' && Object.keys(gastosPorSede).length > 1) {
      analisis += `\n🏢 GASTOS POR SEDE:\n`;
      Object.entries(gastosPorSede)
        .sort((a, b) => b[1].total - a[1].total)
        .forEach(([sede, datos]) => {
          analisis += `   • ${sede}: ${formatearMoneda(datos.total)}\n`;
        });
    }

    const resumen = {
      periodo: tipoFecha === 'especifica' ? fechaEspecifica : `${fechaDesde} al ${fechaHasta}`,
      tipo: tipoConsulta,
      sede: sedeSeleccionada,
      totalGeneral,
      detallePorCategoria,
      manualesPorConcepto,
      detallePorDia,
      analisis,
      gastosPorSede: { ...gastosPorSede }
    };

    setResumenGenerado(resumen);
    setModo("resumen");
  };

  // Guardar resumen
  const guardarResumen = async () => {
    if (!resumenGenerado) return;
    
    try {
      const { error } = await supabase.from('resumenes_gastos').insert({
        empresa_id: filtroEmpresa,
        tipo: tipoConsulta,
        sede: sedeSeleccionada || null,
        fecha_desde: tipoFecha === 'especifica' ? fechaEspecifica : fechaDesde,
        fecha_hasta: tipoFecha === 'especifica' ? fechaEspecifica : fechaHasta,
        resumen: resumenGenerado,
        analisis: resumenGenerado.analisis,
        total_gastos: resumenGenerado.totalGeneral,
        created_by: usuarioActual?.id || empleadoActual?.id,
        created_by_nombre: usuarioActual?.nombre || empleadoActual?.nombre
      });

      if (error) throw error;
      alert("✅ Resumen guardado correctamente");
      cargarResumenesGuardados();
    } catch (e) {
      console.error("Error guardando resumen:", e);
      alert("❌ Error al guardar el resumen");
    }
  };

  // Cargar resúmenes guardados
  const cargarResumenesGuardados = async () => {
    try {
      let query = supabase.from('resumenes_gastos').select('*').order('created_at', { ascending: false }).limit(20);
      if (filtroEmpresa) query = query.eq('empresa_id', filtroEmpresa);
      const { data } = await query;
      setResumenesGuardados(data || []);
    } catch (e) {
      console.warn("Tabla resumenes_gastos no disponible:", e);
    }
  };

  useEffect(() => {
    cargarResumenesGuardados();
  }, [filtroEmpresa]);

  // Cargar automáticamente al abrir el módulo
  useEffect(() => {
    if (!cargaInicial && fechaDesde && fechaHasta) {
      setCargaInicial(true);
      cargarGastosDesdeCuadres();
    }
  }, [fechaDesde, fechaHasta]);

  // Calcular total general
  const totalGeneral = Object.values(gastosPorSede).reduce((sum, s) => sum + s.total, 0);
  
  // Función para formatear fecha legible
  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const f = new Date(fecha + 'T12:00:00');
    return f.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // ========== FUNCIONES PARA GASTOS RECURRENTES ==========
  
  // Cargar configuración de SMS para gastos recurrentes (desde tabla empresas)
  const cargarConfigSMSGastos = async () => {
    if (!filtroEmpresa) {
      console.warn("📱 No hay empresa seleccionada para cargar config SMS");
      return;
    }
    try {
      const { data, error } = await supabase
        .from('empresas')
        .select('telefono_sms_gastos, nombre_receptor_sms')
        .eq('id', filtroEmpresa)
        .single();
      
      console.log("📱 Cargando config SMS gastos desde empresas:", data, error);
      
      if (!error && data) {
        setConfigSMSGastos({
          telefono: data.telefono_sms_gastos || "",
          nombre: data.nombre_receptor_sms || ""
        });
      }
    } catch (e) {
      console.warn("Error cargando config SMS gastos:", e);
    }
  };

  // Guardar configuración de SMS para gastos recurrentes (en tabla empresas)
  const guardarConfigSMSGastos = async () => {
    if (!filtroEmpresa) {
      alert("❌ No hay empresa seleccionada");
      return;
    }
    setGuardandoConfigSMSGastos(true);
    try {
      const { error } = await supabase
        .from('empresas')
        .update({
          telefono_sms_gastos: configSMSGastos.telefono,
          nombre_receptor_sms: configSMSGastos.nombre
        })
        .eq('id', filtroEmpresa);
      
      if (error) {
        console.error("Error actualizando empresa:", error);
        throw error;
      }
      
      console.log("✅ Config SMS gastos guardada en empresa:", filtroEmpresa, configSMSGastos);
      alert("✅ Configuración de SMS guardada correctamente");
      setMostrarConfigSMSGastos(false);
    } catch (e) {
      console.error("Error guardando config SMS gastos:", e);
      alert("❌ Error al guardar: " + e.message);
    } finally {
      setGuardandoConfigSMSGastos(false);
    }
  };

  // Enviar SMS de vencimiento de gasto (1 día antes)
  const enviarSMSVencimientoGasto = async (gasto, telefono, fechaVencimiento) => {
    if (!telefono) {
      console.warn("📱 No hay teléfono configurado para notificaciones de gastos");
      return { ok: false, error: "Teléfono no configurado" };
    }

    const valor = gasto.valor || 0;
    // Formatear valor sin símbolo $ (usar solo números con separadores)
    const valorFormateado = new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(valor);
    const nombreGasto = gasto.nombre_gasto || gasto.nombre || 'Gasto sin nombre';
    const sedeGasto = gasto.sede || 'Sin sede';
    const categoriaGasto = gasto.categoria || 'General';
    
    const mensaje = `Recordatorio de Pago
${nombreGasto}
Sede: ${sedeGasto}
Valor: ${valorFormateado} pesos
Vence: ${fechaVencimiento}
Categoria: ${categoriaGasto}
Recuerde pagar manana.`;

    console.log("📱 Enviando SMS de vencimiento:", { nombreGasto, sedeGasto, valor, telefono });
    return enviarSMS(telefono, mensaje);
  };

  // Verificar y notificar vencimientos del DÍA SIGUIENTE (1 día antes)
  // Se ejecuta automáticamente cuando alguien abre el sistema después de las 8 AM
  const verificarVencimientosManana = async () => {
    const ahora = new Date();
    const horaActual = ahora.getHours();
    
    // Solo ejecutar si ya son las 8 AM o más tarde
    if (horaActual < 8) {
      console.log("📅 Aún no son las 8 AM, no se verifican vencimientos");
      return { esperando: true };
    }
    
    const hoyISO = ahora.toISOString().slice(0, 10);
    
    // Calcular fecha de MAÑANA
    const manana = new Date(ahora);
    manana.setDate(manana.getDate() + 1);
    const mananaISO = manana.toISOString().slice(0, 10);
    
    console.log("📅 Verificando vencimientos para MAÑANA:", mananaISO, "(hoy es", hoyISO, ")");
    
    // Verificar si ya se notificó hoy
    const ultimaNotificacion = localStorage.getItem('ultimaNotificacionGastosVencimiento');
    if (ultimaNotificacion === hoyISO) {
      console.log("📅 Ya se verificaron los vencimientos hoy");
      return { yaNotificado: true };
    }

    try {
      // Buscar gastos que vencen MAÑANA
      const { data: gastosVencenManana, error } = await supabase
        .from('gastos_recurrentes')
        .select('*')
        .eq('fecha_vencimiento', mananaISO)
        .neq('estado', 'pagado');
      
      if (error) {
        console.error("Error buscando vencimientos:", error);
        return { error: error.message };
      }

      if (!gastosVencenManana || gastosVencenManana.length === 0) {
        console.log("📅 No hay gastos que venzan mañana:", mananaISO);
        localStorage.setItem('ultimaNotificacionGastosVencimiento', hoyISO);
        return { encontrados: 0, mensaje: "No hay gastos que venzan mañana" };
      }

      // Obtener teléfono configurado
      const { data: configData, error: configError } = await supabase
        .from('configuracion_sistema')
        .select('telefono_notificaciones_gastos')
        .limit(1);
      
      console.log("📱 Config SMS gastos:", configData, configError);
      
      const telefonoNotif = configData?.[0]?.telefono_notificaciones_gastos;
      
      if (!telefonoNotif) {
        console.warn("📱 No hay teléfono configurado para notificaciones de gastos");
        localStorage.setItem('ultimaNotificacionGastosVencimiento', hoyISO);
        return { encontrados: gastosVencenManana.length, sinTelefono: true, mensaje: "No hay teléfono configurado" };
      }

      console.log(`📅 Se encontraron ${gastosVencenManana.length} gastos que vencen MAÑANA, enviando a: ${telefonoNotif}`);

      let enviados = 0;
      let errores = [];
      
      // Enviar SMS por cada gasto que vence mañana
      for (const gasto of gastosVencenManana) {
        console.log("📤 Enviando SMS para gasto:", gasto);
        const resultado = await enviarSMSVencimientoGasto(gasto, telefonoNotif, mananaISO);
        if (resultado.ok) {
          console.log(`✅ SMS enviado para: ${gasto.nombre_gasto}`);
          enviados++;
        } else {
          console.warn(`❌ Error enviando SMS para: ${gasto.nombre_gasto}`, resultado.error);
          errores.push({ gasto: gasto.nombre_gasto, error: resultado.error });
        }
        // Pequeña pausa entre SMS para no saturar
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Marcar como notificado hoy
      localStorage.setItem('ultimaNotificacionGastosVencimiento', hoyISO);
      
      return { encontrados: gastosVencenManana.length, enviados, errores };
      
    } catch (e) {
      console.error("Error en verificarVencimientosManana:", e);
      return { error: e.message };
    }
  };

  // Cargar config SMS al montar el componente y verificar vencimientos automáticamente
  useEffect(() => {
    if (filtroEmpresa) {
      cargarConfigSMSGastos();
    }
    // Verificar vencimientos del DÍA SIGUIENTE automáticamente (solo después de las 8 AM)
    verificarVencimientosManana();
  }, [filtroEmpresa]);
  
  // Cargar gastos recurrentes del mes seleccionado
  const cargarGastosRecurrentes = async () => {
    setCargandoRecurrentes(true);
    try {
      const [anio, mes] = mesRecurrente.split('-');
      const primerDia = `${anio}-${mes}-01`;
      
      // Calcular el último día del mes correctamente
      const ultimoDiaDate = new Date(parseInt(anio), parseInt(mes), 0);
      const ultimoDia = ultimoDiaDate.toISOString().slice(0, 10);
      
      console.log("📅 Buscando gastos recurrentes:", { primerDia, ultimoDia });
      
      // Consultar gastos recurrentes del mes
      const { data, error } = await supabase
        .from('gastos_recurrentes')
        .select('*')
        .gte('periodo_mes', primerDia)
        .lte('periodo_mes', ultimoDia);
      
      if (error) {
        console.error("❌ Error cargando gastos recurrentes:", error);
        setGastosRecurrentes([]);
      } else {
        console.log("✅ Gastos recurrentes cargados:", data?.length || 0, data);
        setGastosRecurrentes(data || []);
      }
    } catch (e) {
      console.error("❌ Error:", e);
      setGastosRecurrentes([]);
    }
    setCargandoRecurrentes(false);
  };

  // Cargar gastos recurrentes cuando cambia el mes
  useEffect(() => {
    if (modo === "recurrentes") {
      cargarGastosRecurrentes();
    }
  }, [mesRecurrente, modo]);

  // Mapear sede desde config a sede real
  const mapearSedeAReal = (sedeConfig) => {
    const mapeo = {
      "SEDE CORALES": "Corales",
      "SEDE AV SUR": "Av Sur",
      "SEDE VILLA DEL PRADO": "Villa del Prado"
    };
    // Buscar en las sedes reales
    const sedeReal = sedes.find(s => 
      s.nombre?.toLowerCase().includes(sedeConfig.replace("SEDE ", "").toLowerCase()) ||
      sedeConfig.toLowerCase().includes(s.nombre?.toLowerCase())
    );
    return sedeReal?.nombre || mapeo[sedeConfig] || sedeConfig;
  };

  // Obtener gasto recurrente del mes actual por id_gasto
  const obtenerGastoMes = (idGasto) => {
    const encontrado = gastosRecurrentes.find(g => g.id_gasto === idGasto);
    return encontrado;
  };

  // Guardar/actualizar información de vencimiento y valor
  const guardarVencimientoGasto = async () => {
    if (!modalRecurrente?.gasto) return;
    
    const gastoConfig = modalRecurrente.gasto;
    const gastoExistente = obtenerGastoMes(gastoConfig.id);
    const [anio, mes] = mesRecurrente.split('-');
    
    try {
      const datosGasto = {
        id_gasto: gastoConfig.id,
        nombre_gasto: gastoConfig.nombre,
        sede: gastoConfig.sede,
        categoria: gastoConfig.categoria,
        matricula: gastoConfig.matricula || null,
        referencia: gastoConfig.referencia || null,
        periodo_mes: `${anio}-${mes}-01`,
        fecha_vencimiento: formRecurrente.fechaVencimiento || null,
        valor: parseFloat(formRecurrente.valor) || 0,
        empresa_id: filtroEmpresa,
        updated_at: new Date().toISOString(),
        updated_by: usuarioActual?.nombre || empleadoActual?.nombre || 'Sistema'
      };
      
      if (gastoExistente) {
        // Actualizar existente
        const { error } = await supabase.from('gastos_recurrentes')
          .update(datosGasto)
          .eq('id', gastoExistente.id);
        
        if (error) throw error;
      } else {
        // Crear nuevo
        datosGasto.estado = 'pendiente';
        datosGasto.created_at = new Date().toISOString();
        datosGasto.created_by = usuarioActual?.nombre || empleadoActual?.nombre || 'Sistema';
        
        const { error } = await supabase.from('gastos_recurrentes').insert(datosGasto);
        if (error) throw error;
      }
      
      alert("✅ Información guardada correctamente");
      setModalRecurrente(null);
      setFormRecurrente({ fechaVencimiento: '', valor: '', fechaPago: '', adjuntos: [] });
      cargarGastosRecurrentes();
    } catch (e) {
      console.error("Error guardando vencimiento:", e);
      alert("❌ Error al guardar: " + (e.message || "Error desconocido"));
    }
  };

  // Guardar fecha de pago y adjuntos
  const guardarPagoGasto = async () => {
    if (!modalRecurrente?.gasto) return;
    
    const gastoConfig = modalRecurrente.gasto;
    const gastoExistente = obtenerGastoMes(gastoConfig.id);
    
    if (!gastoExistente) {
      alert("⚠️ Primero debes registrar el vencimiento y valor del gasto");
      return;
    }
    
    try {
      const datosActualizar = {
        fecha_pago: formRecurrente.fechaPago || null,
        adjuntos: formRecurrente.adjuntos || [],
        updated_at: new Date().toISOString(),
        updated_by: usuarioActual?.nombre || empleadoActual?.nombre || 'Sistema'
      };
      
      const { error } = await supabase.from('gastos_recurrentes')
        .update(datosActualizar)
        .eq('id', gastoExistente.id);
      
      if (error) throw error;
      
      alert("✅ Información de pago guardada");
      setModalRecurrente(null);
      setFormRecurrente({ fechaVencimiento: '', valor: '', fechaPago: '', adjuntos: [] });
      cargarGastosRecurrentes();
    } catch (e) {
      console.error("Error guardando pago:", e);
      alert("❌ Error al guardar: " + (e.message || "Error desconocido"));
    }
  };

  // Subir adjunto para gasto recurrente
  const subirAdjuntoRecurrente = async (archivo) => {
    if (!archivo) return null;
    
    setSubiendoAdjunto(true);
    try {
      const gastoConfig = modalRecurrente?.gasto;
      const extension = archivo.name.split('.').pop();
      const nombreArchivo = `recurrentes/${gastoConfig?.id || 'gasto'}_${Date.now()}.${extension}`;
      
      const { data, error } = await supabase.storage
        .from(SUPABASE_BUCKET_DOCS)
        .upload(nombreArchivo, archivo, { upsert: true });
      
      if (error) throw error;
      
      const { data: urlData } = supabase.storage
        .from(SUPABASE_BUCKET_DOCS)
        .getPublicUrl(nombreArchivo);
      
      const nuevoAdjunto = {
        nombre: archivo.name,
        url: urlData.publicUrl,
        tipo: archivo.type,
        fecha: new Date().toISOString()
      };
      
      setFormRecurrente(prev => ({
        ...prev,
        adjuntos: [...(prev.adjuntos || []), nuevoAdjunto]
      }));
      
      return nuevoAdjunto;
    } catch (e) {
      console.error("Error subiendo archivo:", e);
      alert("❌ Error al subir archivo: " + e.message);
      return null;
    } finally {
      setSubiendoAdjunto(false);
    }
  };

  // Confirmar pago y enviar a gastos manuales
  const confirmarPagoYEnviar = async () => {
    if (!modalRecurrente?.gasto) return;
    
    const gastoConfig = modalRecurrente.gasto;
    const gastoExistente = obtenerGastoMes(gastoConfig.id);
    
    if (!gastoExistente) {
      alert("⚠️ Primero debes registrar la información del gasto");
      return;
    }
    
    if (!gastoExistente.fecha_pago) {
      alert("⚠️ Debes registrar la fecha de pago antes de confirmar");
      return;
    }
    
    if (!gastoExistente.valor || gastoExistente.valor <= 0) {
      alert("⚠️ El valor del gasto debe ser mayor a 0");
      return;
    }
    
    try {
      // 1. Crear gasto manual correspondiente
      const sedeReal = mapearSedeAReal(gastoConfig.sede);
      const datosGastoManual = {
        sede: sedeReal,
        fecha: gastoExistente.fecha_pago,
        concepto: gastoConfig.categoria,
        valor: gastoExistente.valor,
        proveedor_beneficiario: `${gastoConfig.nombre} - ${gastoConfig.sede} - Período: ${mesRecurrente}`,
        categoria: 'recurrente',
        created_by_nombre: usuarioActual?.nombre || empleadoActual?.nombre || 'Sistema'
      };
      
      // Solo agregar empresa_id si existe
      if (filtroEmpresa) {
        datosGastoManual.empresa_id = filtroEmpresa;
      }
      
      const { error: errorManual } = await supabase.from('gastos_manuales').insert(datosGastoManual);
      if (errorManual) throw errorManual;
      
      // 2. Marcar gasto recurrente como pagado
      const { error: errorUpdate } = await supabase.from('gastos_recurrentes')
        .update({
          estado: 'pagado',
          enviado_a_manuales: true,
          fecha_envio_manuales: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          updated_by: usuarioActual?.nombre || empleadoActual?.nombre || 'Sistema'
        })
        .eq('id', gastoExistente.id);
      
      if (errorUpdate) throw errorUpdate;
      
      alert("✅ Pago confirmado y enviado a Gastos Manuales de " + sedeReal);
      setModalRecurrente(null);
      setFormRecurrente({ fechaVencimiento: '', valor: '', fechaPago: '', adjuntos: [] });
      cargarGastosRecurrentes();
    } catch (e) {
      console.error("Error confirmando pago:", e);
      alert("❌ Error: " + (e.message || "Error desconocido"));
    }
  };

  // Abrir modal con datos existentes
  const abrirModalRecurrente = (gasto, accion) => {
    const gastoExistente = obtenerGastoMes(gasto.id);
    
    setFormRecurrente({
      fechaVencimiento: gastoExistente?.fecha_vencimiento || '',
      valor: gastoExistente?.valor?.toString() || '',
      fechaPago: gastoExistente?.fecha_pago || '',
      adjuntos: gastoExistente?.adjuntos || []
    });
    
    setModalRecurrente({ gasto, accion });
  };

  // Agrupar gastos por sede
  const gastosAgrupados = useMemo(() => {
    const agrupados = {};
    GASTOS_RECURRENTES_CONFIG.forEach(g => {
      if (!agrupados[g.sede]) agrupados[g.sede] = [];
      agrupados[g.sede].push(g);
    });
    return agrupados;
  }, []);

  // Separar gastos en pendientes y pagados (con filtro de sede)
  const gastosSeparados = useMemo(() => {
    const pendientes = [];
    const pagados = [];
    
    GASTOS_RECURRENTES_CONFIG.forEach(config => {
      // Aplicar filtro de sede
      if (sedeRecurrente !== "todas" && config.sede !== sedeRecurrente) {
        return; // Saltar este gasto si no coincide con la sede filtrada
      }
      
      const gastoMes = obtenerGastoMes(config.id);
      const item = { ...config, datosDelMes: gastoMes };
      
      if (gastoMes?.estado === 'pagado') {
        pagados.push(item);
      } else {
        pendientes.push(item);
      }
    });
    
    return { pendientes, pagados };
  }, [gastosRecurrentes, sedeRecurrente]);

  return (
    <div style={{ padding: 16 }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7b1fa2 0%, #9c27b0 100%)',
        color: 'white',
        padding: 20,
        borderRadius: 16,
        marginBottom: 24
      }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 28 }}>💸</span>
          Gestión de Gastos
        </h2>
        <p style={{ margin: '8px 0 0', opacity: 0.9, fontSize: 14 }}>
          Análisis de gastos por sede basado en cuadres cerrados
        </p>
      </div>

      {/* Tabs de navegación */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <button
          onClick={() => setModo("consultar")}
          style={{
            padding: '12px 20px',
            backgroundColor: modo === "consultar" ? '#7b1fa2' : '#f5f5f5',
            color: modo === "consultar" ? 'white' : '#333',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          📊 Consultar Gastos
        </button>
        <button
          onClick={() => setModo("manual")}
          style={{
            padding: '12px 20px',
            backgroundColor: modo === "manual" ? '#7b1fa2' : '#f5f5f5',
            color: modo === "manual" ? 'white' : '#333',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          ➕ Gastos Manuales
        </button>
        <button
          onClick={() => { cargarResumenesGuardados(); setModo("resumen"); }}
          style={{
            padding: '12px 20px',
            backgroundColor: modo === "resumen" ? '#7b1fa2' : '#f5f5f5',
            color: modo === "resumen" ? 'white' : '#333',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          📈 Resumen Ejecutivo
        </button>
        {(tienePermiso("admin_gastos_recurrentes") || tienePermiso("admin_gastos")) && (
        <button
          onClick={() => { cargarGastosRecurrentes(); setModo("recurrentes"); }}
          style={{
            padding: '12px 20px',
            backgroundColor: modo === "recurrentes" ? '#2e7d32' : '#f5f5f5',
            color: modo === "recurrentes" ? 'white' : '#333',
            border: modo === "recurrentes" ? 'none' : '2px solid #2e7d32',
            borderRadius: 8,
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          🔄 Gastos Recurrentes
        </button>
        )}
      </div>

      {/* Vista: Consultar Gastos */}
      {modo === "consultar" && (
        <div>
          {/* Filtros */}
          <div style={{ 
            backgroundColor: 'white', 
            padding: 20, 
            borderRadius: 12, 
            marginBottom: 24,
            border: '1px solid #e0e0e0'
          }}>
            <h3 style={{ margin: '0 0 16px', color: '#7b1fa2' }}>🔍 Filtros de Consulta</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              {/* Tipo de consulta */}
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', fontSize: 13 }}>
                  Consultar por:
                </label>
                <select
                  value={tipoConsulta}
                  onChange={(e) => setTipoConsulta(e.target.value)}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                >
                  <option value="todas">📊 Todas las sedes</option>
                  <option value="sede">🏢 Sede específica</option>
                </select>
              </div>

              {/* Sede */}
              {tipoConsulta === "sede" && (
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', fontSize: 13 }}>
                    Sede:
                  </label>
                  <select
                    value={sedeSeleccionada}
                    onChange={(e) => setSedeSeleccionada(e.target.value)}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                  >
                    <option value="">Seleccionar...</option>
                    {sedes.map(s => (
                      <option key={s.id || s.nombre} value={s.nombre}>{s.nombre}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Tipo de fecha */}
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', fontSize: 13 }}>
                  Período:
                </label>
                <select
                  value={tipoFecha}
                  onChange={(e) => setTipoFecha(e.target.value)}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                >
                  <option value="rango">📅 Rango de fechas</option>
                  <option value="especifica">📆 Fecha específica</option>
                </select>
              </div>

              {/* Fechas */}
              {tipoFecha === "especifica" ? (
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', fontSize: 13 }}>
                    Fecha:
                  </label>
                  <input
                    type="date"
                    value={fechaEspecifica}
                    onChange={(e) => setFechaEspecifica(e.target.value)}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box' }}
                  />
                </div>
              ) : (
                <>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', fontSize: 13 }}>
                      Desde:
                    </label>
                    <input
                      type="date"
                      value={fechaDesde}
                      onChange={(e) => setFechaDesde(e.target.value)}
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', fontSize: 13 }}>
                      Hasta:
                    </label>
                    <input
                      type="date"
                      value={fechaHasta}
                      onChange={(e) => setFechaHasta(e.target.value)}
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box' }}
                    />
                  </div>
                </>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button
                onClick={cargarGastosDesdeCuadres}
                disabled={cargando}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#7b1fa2',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: cargando ? 'wait' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {cargando ? '⏳ Cargando...' : '🔍 Consultar Gastos'}
              </button>
              {Object.keys(gastosPorSede).length > 0 && (
                <button
                  onClick={generarResumenEjecutivo}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  📈 Generar Resumen Ejecutivo
                </button>
              )}
            </div>
          </div>

          {/* Resultados */}
          {Object.keys(gastosPorSede).length > 0 && (
            <div>
              {/* Total General */}
              <div style={{
                backgroundColor: '#7b1fa2',
                color: 'white',
                padding: 20,
                borderRadius: 12,
                marginBottom: 20,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: 14, opacity: 0.9 }}>💰 TOTAL GASTOS - TODAS LAS SEDES</div>
                <div style={{ fontSize: 32, fontWeight: 'bold' }}>{formatearMoneda(totalGeneral)}</div>
                <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8 }}>
                  📅 {formatearFecha(fechaConsultaDesde)} — {formatearFecha(fechaConsultaHasta)}
                </div>
              </div>

              {/* Gastos por sede */}
              <div style={{ display: 'grid', gap: 16 }}>
                {Object.entries(gastosPorSede).map(([sede, datos]) => (
                  <div key={sede} style={{
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: 12,
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      padding: 16,
                      backgroundColor: '#f3e5f5',
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4 style={{ margin: 0, color: '#7b1fa2' }}>🏢 {sede}</h4>
                        <span style={{ 
                          fontSize: 18, 
                          fontWeight: 'bold', 
                          color: '#7b1fa2' 
                        }}>
                          {formatearMoneda(datos.total)}
                        </span>
                      </div>
                      {fechasPorSede[sede] && (
                        <div style={{ fontSize: 12, color: '#7b1fa2', opacity: 0.8, marginTop: 6 }}>
                          📅 {formatearFecha(fechasPorSede[sede].min)} — {formatearFecha(fechasPorSede[sede].max)}
                        </div>
                      )}
                    </div>
                    <div style={{ padding: 16 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                        <div style={{ padding: 12, backgroundColor: '#e3f2fd', borderRadius: 8, textAlign: 'center' }}>
                          <div style={{ fontSize: 12, color: '#1565c0' }}>💵 Facturas Caja</div>
                          <div style={{ fontSize: 16, fontWeight: 'bold', color: '#1565c0' }}>
                            {formatearMoneda(datos.facturasCaja)}
                          </div>
                        </div>
                        <div style={{ padding: 12, backgroundColor: '#e8eaf6', borderRadius: 8, textAlign: 'center' }}>
                          <div style={{ fontSize: 12, color: '#3949ab' }}>🏦 Facturas Transfer</div>
                          <div style={{ fontSize: 16, fontWeight: 'bold', color: '#3949ab' }}>
                            {formatearMoneda(datos.facturasTransferencia)}
                          </div>
                        </div>
                        <div style={{ padding: 12, backgroundColor: '#fff3e0', borderRadius: 8, textAlign: 'center' }}>
                          <div style={{ fontSize: 12, color: '#e65100' }}>🛵 Costo Domicilios</div>
                          <div style={{ fontSize: 16, fontWeight: 'bold', color: '#e65100' }}>
                            {formatearMoneda(datos.domicilios)}
                          </div>
                        </div>
                        <div style={{ padding: 12, backgroundColor: '#e8f5e9', borderRadius: 8, textAlign: 'center' }}>
                          <div style={{ fontSize: 12, color: '#2e7d32' }}>👥 Turnos</div>
                          <div style={{ fontSize: 16, fontWeight: 'bold', color: '#2e7d32' }}>
                            {formatearMoneda(datos.turnos)}
                          </div>
                        </div>
                        <div style={{ padding: 12, backgroundColor: '#fce4ec', borderRadius: 8, textAlign: 'center' }}>
                          <div style={{ fontSize: 12, color: '#c2185b' }}>💵 Adelantos</div>
                          <div style={{ fontSize: 16, fontWeight: 'bold', color: '#c2185b' }}>
                            {formatearMoneda(datos.adelantos)}
                          </div>
                        </div>
                      </div>
                      
                      {/* Gastos Manuales por Concepto */}
                      {datos.manuales > 0 && datos.manualesPorConcepto && Object.keys(datos.manualesPorConcepto).length > 0 && (
                        <div style={{ marginTop: 16 }}>
                          <h5 style={{ margin: '0 0 12px', color: '#512da8', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                            📝 Gastos Manuales: {formatearMoneda(datos.manuales)}
                          </h5>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                            {Object.entries(datos.manualesPorConcepto)
                              .sort((a, b) => b[1] - a[1])
                              .map(([concepto, valor]) => (
                              <div key={concepto} style={{ 
                                padding: 10, 
                                backgroundColor: '#ede7f6', 
                                borderRadius: 8, 
                                textAlign: 'center',
                                border: '1px solid #d1c4e9'
                              }}>
                                <div style={{ fontSize: 11, color: '#512da8', fontWeight: 500, marginBottom: 4 }}>
                                  {concepto}
                                </div>
                                <div style={{ fontSize: 14, fontWeight: 'bold', color: '#512da8' }}>
                                  {formatearMoneda(valor)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vista: Gastos Manuales */}
      {modo === "manual" && (
        <div>
          <div style={{
            backgroundColor: 'white',
            padding: 20,
            borderRadius: 12,
            marginBottom: 24,
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: '#7b1fa2' }}>➕ Registrar Gasto Manual</h3>
              <button
                onClick={() => {
                  setGastoEditandoId(null);
                  setGastoManualSede("");
                  setGastoManualFecha("");
                  setGastoManualConcepto("");
                  setGastoManualProveedor("");
                  setGastoManualValor("");
                  setMostrarModalGastoManual(true);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#7b1fa2',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                ➕ Nuevo Gasto
              </button>
            </div>

            <p style={{ color: '#666', fontSize: 14 }}>
              Registra gastos adicionales que no están incluidos en los cuadres diarios 
              (proveedores, servicios, compras, etc.)
            </p>
          </div>

          {/* Lista de gastos manuales recientes */}
          <div style={{
            backgroundColor: 'white',
            padding: 20,
            borderRadius: 12,
            border: '1px solid #e0e0e0'
          }}>
            <h4 style={{ margin: '0 0 16px', color: '#333' }}>📋 Gastos Manuales Registrados</h4>
            {gastosManualeas.length === 0 ? (
              <p style={{ color: '#999', textAlign: 'center', padding: 20 }}>
                No hay gastos manuales registrados
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {gastosManualeas.slice(0, 20).map(gm => (
                  <div key={gm.id} style={{
                    padding: 12,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', color: '#512da8' }}>{gm.concepto || "Sin concepto"}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        🏢 {gm.sede} | 📅 {gm.fecha} {gm.proveedor_beneficiario && gm.proveedor_beneficiario !== gm.concepto && `| 👤 ${gm.proveedor_beneficiario}`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ fontWeight: 'bold', color: '#7b1fa2', fontSize: 16 }}>
                        {formatearMoneda(gm.valor)}
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          onClick={() => editarGastoManual(gm)}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#ff9800',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 'bold'
                          }}
                          title="Editar gasto"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => eliminarGastoManual(gm.id)}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 'bold'
                          }}
                          title="Eliminar gasto"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vista: Resumen Ejecutivo */}
      {modo === "resumen" && (
        <div>
          {resumenGenerado ? (
            <div id="resumen-ejecutivo-print" style={{
              fontFamily: 'Arial, Helvetica, sans-serif',
              fontSize: 14,
              color: '#333',
              lineHeight: 1.5
            }}>
              {/* Botones de acción (no se imprimen) */}
              <div className="no-print" style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: 8, 
                marginBottom: 16 
              }}>
                <button
                  onClick={() => window.print()}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: 14,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  🖨️ Imprimir
                </button>
                <button
                  onClick={guardarResumen}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: 14
                  }}
                >
                  💾 Guardar
                </button>
                <button
                  onClick={() => setResumenGenerado(null)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#757575',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  ✖️ Cerrar
                </button>
              </div>

              {/* Contenido del Informe */}
              <div style={{
                backgroundColor: 'white',
                padding: 32,
                borderRadius: 12,
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                {/* Encabezado */}
                <div style={{ 
                  textAlign: 'center', 
                  borderBottom: '3px solid #7b1fa2',
                  paddingBottom: 20,
                  marginBottom: 24
                }}>
                  <h1 style={{ 
                    margin: 0, 
                    fontSize: 24, 
                    fontWeight: 'bold',
                    color: '#7b1fa2',
                    letterSpacing: 1
                  }}>
                    INFORME DE GASTOS
                  </h1>
                  <p style={{ margin: '8px 0 0', fontSize: 14, color: '#666' }}>
                    Resumen Ejecutivo
                  </p>
                </div>

                {/* Información del período */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 16,
                  backgroundColor: '#f8f9fa',
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 24
                }}>
                  <div>
                    <span style={{ fontSize: 12, color: '#666', display: 'block' }}>Período</span>
                    <span style={{ fontWeight: 'bold', fontSize: 15 }}>{resumenGenerado.periodo}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: 12, color: '#666', display: 'block' }}>Alcance</span>
                    <span style={{ fontWeight: 'bold', fontSize: 15 }}>
                      {resumenGenerado.tipo === 'todas' ? 'Todas las sedes' : resumenGenerado.sede}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: 12, color: '#666', display: 'block' }}>Fecha de generación</span>
                    <span style={{ fontWeight: 'bold', fontSize: 15 }}>
                      {new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                </div>

                {/* Total General */}
                <div style={{
                  backgroundColor: '#7b1fa2',
                  color: 'white',
                  padding: 20,
                  borderRadius: 8,
                  textAlign: 'center',
                  marginBottom: 24
                }}>
                  <span style={{ fontSize: 14, opacity: 0.9 }}>TOTAL GASTOS DEL PERÍODO</span>
                  <div style={{ fontSize: 32, fontWeight: 'bold', marginTop: 4 }}>
                    {formatearMoneda(resumenGenerado.totalGeneral)}
                  </div>
                </div>

                {/* Sección: Desglose por Categoría */}
                <div style={{ marginBottom: 32 }}>
                  <h2 style={{ 
                    fontSize: 16, 
                    fontWeight: 'bold', 
                    color: '#333',
                    borderBottom: '2px solid #7b1fa2',
                    paddingBottom: 8,
                    marginBottom: 16
                  }}>
                    DESGLOSE POR CATEGORÍA
                  </h2>
                  
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    fontSize: 14
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Categoría</th>
                        <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #ddd' }}>Valor</th>
                        <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #ddd' }}>Porcentaje</th>
                        <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd', width: '30%' }}>Distribución</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: 'facturasCaja', label: 'Facturas pagadas por caja', color: '#e91e63' },
                        { key: 'domicilios', label: 'Costo de domicilios', color: '#9c27b0' },
                        { key: 'turnos', label: 'Pago de turnos', color: '#673ab7' },
                        { key: 'adelantos', label: 'Adelantos a empleados', color: '#3f51b5' },
                        { key: 'manuales', label: 'Gastos manuales', color: '#2196f3' }
                      ].map(cat => {
                        const valor = resumenGenerado.detallePorCategoria?.[cat.key] || 0;
                        const porcentaje = resumenGenerado.totalGeneral > 0 
                          ? (valor / resumenGenerado.totalGeneral) * 100 
                          : 0;
                        return (
                          <tr key={cat.key}>
                            <td style={{ padding: 12, borderBottom: '1px solid #eee' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: cat.color }} />
                                {cat.label}
                              </div>
                            </td>
                            <td style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>
                              {formatearMoneda(valor)}
                            </td>
                            <td style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #eee' }}>
                              {porcentaje.toFixed(1)}%
                            </td>
                            <td style={{ padding: 12, borderBottom: '1px solid #eee' }}>
                              <div style={{ 
                                height: 16, 
                                backgroundColor: '#f0f0f0', 
                                borderRadius: 8, 
                                overflow: 'hidden' 
                              }}>
                                <div style={{
                                  height: '100%',
                                  width: `${Math.min(porcentaje, 100)}%`,
                                  backgroundColor: cat.color,
                                  borderRadius: 8
                                }} />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                        <td style={{ padding: 12 }}>TOTAL</td>
                        <td style={{ padding: 12, textAlign: 'right' }}>{formatearMoneda(resumenGenerado.totalGeneral)}</td>
                        <td style={{ padding: 12, textAlign: 'right' }}>100%</td>
                        <td style={{ padding: 12 }}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Sección: Gastos Manuales por Concepto */}
                {resumenGenerado.manualesPorConcepto && Object.keys(resumenGenerado.manualesPorConcepto).length > 0 && (
                  <div style={{ marginBottom: 32 }}>
                    <h2 style={{ 
                      fontSize: 16, 
                      fontWeight: 'bold', 
                      color: '#333',
                      borderBottom: '2px solid #2196f3',
                      paddingBottom: 8,
                      marginBottom: 16
                    }}>
                      DETALLE DE GASTOS MANUALES
                    </h2>
                    
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse',
                      fontSize: 14
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#e3f2fd' }}>
                          <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #90caf9' }}>Concepto</th>
                          <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #90caf9' }}>Valor</th>
                          <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #90caf9' }}>% del Total Manuales</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(resumenGenerado.manualesPorConcepto)
                          .sort((a, b) => b[1] - a[1])
                          .map(([concepto, valor]) => {
                            const porcentaje = resumenGenerado.detallePorCategoria?.manuales > 0
                              ? (valor / resumenGenerado.detallePorCategoria.manuales) * 100
                              : 0;
                            return (
                              <tr key={concepto}>
                                <td style={{ padding: 12, borderBottom: '1px solid #eee' }}>{concepto}</td>
                                <td style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>
                                  {formatearMoneda(valor)}
                                </td>
                                <td style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #eee' }}>
                                  {porcentaje.toFixed(1)}%
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                      <tfoot>
                        <tr style={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
                          <td style={{ padding: 12 }}>SUBTOTAL MANUALES</td>
                          <td style={{ padding: 12, textAlign: 'right' }}>
                            {formatearMoneda(resumenGenerado.detallePorCategoria?.manuales || 0)}
                          </td>
                          <td style={{ padding: 12, textAlign: 'right' }}>100%</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                {/* Sección: Gastos por Día de la Semana */}
                {resumenGenerado.detallePorDia && Object.keys(resumenGenerado.detallePorDia).length > 0 && (
                  <div style={{ marginBottom: 32 }}>
                    <h2 style={{ 
                      fontSize: 16, 
                      fontWeight: 'bold', 
                      color: '#333',
                      borderBottom: '2px solid #ff9800',
                      paddingBottom: 8,
                      marginBottom: 16
                    }}>
                      DISTRIBUCIÓN POR DÍA DE LA SEMANA
                    </h2>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
                      {['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'].map(dia => {
                        const valor = resumenGenerado.detallePorDia[dia] || 0;
                        const maxValor = Math.max(...Object.values(resumenGenerado.detallePorDia), 1);
                        const altura = (valor / maxValor) * 80;
                        return (
                          <div key={dia} style={{ textAlign: 'center' }}>
                            <div style={{ 
                              height: 100, 
                              display: 'flex', 
                              alignItems: 'flex-end', 
                              justifyContent: 'center',
                              marginBottom: 8
                            }}>
                              <div style={{
                                width: '60%',
                                height: altura,
                                backgroundColor: valor === Math.max(...Object.values(resumenGenerado.detallePorDia)) ? '#ff9800' : '#ffcc80',
                                borderRadius: '4px 4px 0 0',
                                minHeight: valor > 0 ? 8 : 0
                              }} />
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 'bold', textTransform: 'capitalize' }}>{dia}</div>
                            <div style={{ fontSize: 11, color: '#666' }}>{formatearMoneda(valor)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Sección: Gastos por Sede (si aplica) */}
                {resumenGenerado.tipo === 'todas' && resumenGenerado.gastosPorSede && Object.keys(resumenGenerado.gastosPorSede).length > 1 && (
                  <div style={{ marginBottom: 32 }}>
                    <h2 style={{ 
                      fontSize: 16, 
                      fontWeight: 'bold', 
                      color: '#333',
                      borderBottom: '2px solid #4caf50',
                      paddingBottom: 8,
                      marginBottom: 16
                    }}>
                      GASTOS POR SEDE
                    </h2>
                    
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse',
                      fontSize: 14
                    }}>
                      <thead>
                        <tr style={{ backgroundColor: '#e8f5e9' }}>
                          <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #a5d6a7' }}>Sede</th>
                          <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #a5d6a7' }}>Total Gastos</th>
                          <th style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #a5d6a7' }}>Porcentaje</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(resumenGenerado.gastosPorSede)
                          .sort((a, b) => b[1].total - a[1].total)
                          .map(([sede, datos]) => {
                            const porcentaje = resumenGenerado.totalGeneral > 0
                              ? (datos.total / resumenGenerado.totalGeneral) * 100
                              : 0;
                            return (
                              <tr key={sede}>
                                <td style={{ padding: 12, borderBottom: '1px solid #eee', fontWeight: 500 }}>{sede}</td>
                                <td style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>
                                  {formatearMoneda(datos.total)}
                                </td>
                                <td style={{ padding: 12, textAlign: 'right', borderBottom: '1px solid #eee' }}>
                                  {porcentaje.toFixed(1)}%
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Gráfico Circular */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  gap: 32,
                  padding: 24,
                  backgroundColor: '#fafafa',
                  borderRadius: 8,
                  marginBottom: 24
                }}>
                  <div style={{
                    width: 160,
                    height: 160,
                    borderRadius: '50%',
                    background: (() => {
                      const cats = resumenGenerado.detallePorCategoria || {};
                      const total = resumenGenerado.totalGeneral || 1;
                      const p1 = ((cats.facturasCaja || 0) / total) * 100;
                      const p2 = p1 + ((cats.domicilios || 0) / total) * 100;
                      const p3 = p2 + ((cats.turnos || 0) / total) * 100;
                      const p4 = p3 + ((cats.adelantos || 0) / total) * 100;
                      return `conic-gradient(
                        #e91e63 0% ${p1}%,
                        #9c27b0 ${p1}% ${p2}%,
                        #673ab7 ${p2}% ${p3}%,
                        #3f51b5 ${p3}% ${p4}%,
                        #2196f3 ${p4}% 100%
                      )`;
                    })(),
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 'bold', marginBottom: 12, color: '#333' }}>Leyenda:</div>
                    {[
                      { label: 'Facturas Caja', color: '#e91e63' },
                      { label: 'Domicilios', color: '#9c27b0' },
                      { label: 'Turnos', color: '#673ab7' },
                      { label: 'Adelantos', color: '#3f51b5' },
                      { label: 'Manuales', color: '#2196f3' }
                    ].map(item => (
                      <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <div style={{ width: 14, height: 14, borderRadius: 3, backgroundColor: item.color }} />
                        <span style={{ fontSize: 13 }}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pie de página */}
                <div style={{ 
                  textAlign: 'center', 
                  paddingTop: 20, 
                  borderTop: '1px solid #e0e0e0',
                  color: '#999',
                  fontSize: 12
                }}>
                  <p style={{ margin: 0 }}>
                    Informe generado automáticamente por el Sistema de Gestión de Gastos
                  </p>
                  <p style={{ margin: '4px 0 0' }}>
                    {new Date().toLocaleString('es-CO')}
                  </p>
                </div>
              </div>

              {/* Estilos para impresión */}
              <style>{`
                @media print {
                  body * {
                    visibility: hidden;
                  }
                  #resumen-ejecutivo-print, #resumen-ejecutivo-print * {
                    visibility: visible;
                  }
                  #resumen-ejecutivo-print {
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                  }
                  .no-print {
                    display: none !important;
                  }
                  @page {
                    margin: 1cm;
                    size: A4;
                  }
                }
              `}</style>
            </div>
          ) : (
            <div>
              {/* Resúmenes guardados */}
              <div style={{
                backgroundColor: 'white',
                padding: 20,
                borderRadius: 12,
                border: '1px solid #e0e0e0'
              }}>
                <h3 style={{ margin: '0 0 16px', color: '#7b1fa2' }}>📚 Resúmenes Guardados</h3>
                {resumenesGuardados.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
                    <p>No hay resúmenes guardados</p>
                    <p style={{ fontSize: 13 }}>Consulta gastos y genera un resumen ejecutivo para guardarlo</p>
                    <button
                      onClick={() => setModo("consultar")}
                      style={{
                        marginTop: 16,
                        padding: '12px 24px',
                        backgroundColor: '#7b1fa2',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer'
                      }}
                    >
                      🔍 Ir a Consultar Gastos
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 12 }}>
                    {resumenesGuardados.map(r => (
                      <div key={r.id} style={{
                        padding: 16,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 8,
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        const resumenParsed = typeof r.resumen === 'string' ? JSON.parse(r.resumen) : r.resumen;
                        setResumenGenerado(resumenParsed);
                      }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                              {r.tipo === 'todas' ? '📊 Todas las sedes' : `🏢 ${r.sede}`}
                            </div>
                            <div style={{ fontSize: 12, color: '#666' }}>
                              📅 {r.fecha_desde} al {r.fecha_hasta}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 'bold', color: '#333' }}>
                              {formatearMoneda(r.total_gastos)}
                            </div>
                            <div style={{ fontSize: 11, color: '#999' }}>
                              {new Date(r.created_at).toLocaleDateString('es-CO')}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vista: Gastos Recurrentes */}
      {modo === "recurrentes" && (
        <div>
          {/* Selector de mes y sede */}
          <div style={{
            backgroundColor: 'white',
            padding: 20,
            borderRadius: 12,
            marginBottom: 24,
            border: '2px solid #2e7d32'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
              <div>
                <h3 style={{ margin: 0, color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 8 }}>
                  🔄 Gastos Recurrentes
                </h3>
                <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>
                  Gestiona los gastos fijos mensuales de cada sede
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label style={{ fontWeight: 'bold', fontSize: 13 }}>📅 Período:</label>
                  <input
                    type="month"
                    value={mesRecurrente}
                    onChange={(e) => setMesRecurrente(e.target.value)}
                    style={{ padding: 10, borderRadius: 8, border: '2px solid #2e7d32', fontWeight: 'bold' }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <label style={{ fontWeight: 'bold', fontSize: 13 }}>🏢 Sede:</label>
                  <select
                    value={sedeRecurrente}
                    onChange={(e) => setSedeRecurrente(e.target.value)}
                    style={{ padding: 10, borderRadius: 8, border: '2px solid #2e7d32', fontWeight: 'bold', minWidth: 160 }}
                  >
                    <option value="todas">📊 Todas las sedes</option>
                    {sedesRecurrentes.map(sede => (
                      <option key={sede} value={sede}>{sede}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={cargarGastosRecurrentes}
                  disabled={cargandoRecurrentes}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: '#2e7d32',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  {cargandoRecurrentes ? '⏳ Cargando...' : '🔄 Actualizar'}
                </button>
                <button
                  onClick={() => setVistaVencimientos(!vistaVencimientos)}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: vistaVencimientos ? '#ff5722' : '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  {vistaVencimientos ? '📋 Vista Completa' : '📅 Fechas de Vencimiento'}
                </button>
                <button
                  onClick={() => setMostrarConfigSMSGastos(true)}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: configSMSGastos.telefono ? '#4caf50' : '#ff9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  📱 {configSMSGastos.telefono ? 'SMS Configurado' : 'Configurar SMS'}
                </button>
              </div>
            </div>
          </div>

          {/* Modal Configuración SMS para Gastos */}
          {mostrarConfigSMSGastos && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: 16,
                padding: 24,
                width: '450px',
                maxWidth: '95vw',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <h3 style={{ margin: 0, color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 8 }}>
                    📱 Configurar Notificaciones SMS
                  </h3>
                  <button onClick={() => setMostrarConfigSMSGastos(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer' }}>×</button>
                </div>
                
                <div style={{ 
                  backgroundColor: '#e8f5e9', 
                  padding: 16, 
                  borderRadius: 12, 
                  marginBottom: 20,
                  border: '1px solid #a5d6a7'
                }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#2e7d32' }}>
                    <strong>📅 Notificación Automática:</strong> El sistema enviará un SMS automáticamente cada día que haya un gasto con fecha de vencimiento ese mismo día.
                  </p>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6, color: '#333' }}>
                    📞 Teléfono para notificaciones:
                  </label>
                  <input
                    type="tel"
                    value={configSMSGastos.telefono}
                    onChange={(e) => setConfigSMSGastos({ ...configSMSGastos, telefono: e.target.value })}
                    placeholder="Ej: 3001234567"
                    style={{
                      width: '100%',
                      padding: 12,
                      fontSize: 16,
                      borderRadius: 8,
                      border: '2px solid #2e7d32',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 6, color: '#333' }}>
                    👤 Nombre del receptor:
                  </label>
                  <input
                    type="text"
                    value={configSMSGastos.nombre}
                    onChange={(e) => setConfigSMSGastos({ ...configSMSGastos, nombre: e.target.value })}
                    placeholder="Ej: Administrador"
                    style={{
                      width: '100%',
                      padding: 12,
                      fontSize: 16,
                      borderRadius: 8,
                      border: '2px solid #ddd',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setMostrarConfigSMSGastos(false)}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#e0e0e0',
                      color: '#333',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={guardarConfigSMSGastos}
                    disabled={guardandoConfigSMSGastos || !configSMSGastos.telefono}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#2e7d32',
                      color: 'white',
                      border: 'none',
                      borderRadius: 8,
                      cursor: guardandoConfigSMSGastos || !configSMSGastos.telefono ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      opacity: guardandoConfigSMSGastos || !configSMSGastos.telefono ? 0.6 : 1
                    }}
                  >
                    {guardandoConfigSMSGastos ? '⏳ Guardando...' : '💾 Guardar Configuración'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Vista Simplificada de Vencimientos */}
          {vistaVencimientos ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              {/* Columna: Por Vencer / Vencidos */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: 12,
                border: '2px solid #1976d2',
                padding: 16
              }}>
                <h3 style={{ margin: '0 0 16px', color: '#1565c0', display: 'flex', alignItems: 'center', gap: 8 }}>
                  📅 Estado de Vencimientos
                </h3>
                
                {gastosSeparados.pendientes.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>✨</div>
                    <p>No hay gastos pendientes</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {gastosSeparados.pendientes
                      .filter(g => g.datosDelMes?.fecha_vencimiento)
                      .sort((a, b) => new Date(a.datosDelMes.fecha_vencimiento) - new Date(b.datosDelMes.fecha_vencimiento))
                      .map(gasto => {
                        const fechaVenc = new Date(gasto.datosDelMes.fecha_vencimiento);
                        const hoy = new Date();
                        hoy.setHours(0, 0, 0, 0);
                        fechaVenc.setHours(0, 0, 0, 0);
                        const diffTime = fechaVenc - hoy;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        const estaVencido = diffDays < 0;
                        
                        return (
                          <div key={gasto.id} style={{
                            backgroundColor: estaVencido ? '#ffebee' : '#e8f5e9',
                            borderRadius: 8,
                            padding: 10,
                            border: estaVencido ? '1px solid #ef9a9a' : '1px solid #a5d6a7',
                            display: 'grid',
                            gridTemplateColumns: '1fr auto',
                            alignItems: 'center',
                            gap: 8
                          }}>
                            <div>
                              <div style={{ fontWeight: 'bold', fontSize: 13, color: estaVencido ? '#c62828' : '#2e7d32' }}>
                                {gasto.nombre}
                              </div>
                              <div style={{ fontSize: 11, color: '#666' }}>
                                📍 {gasto.sede} | 📅 {formatearFecha(gasto.datosDelMes.fecha_vencimiento)}
                              </div>
                              <div style={{ fontSize: 12, fontWeight: 'bold', color: '#1565c0' }}>
                                💰 {formatearMoneda(gasto.datosDelMes.valor || 0)}
                              </div>
                            </div>
                            <div style={{
                              padding: '6px 10px',
                              borderRadius: 6,
                              backgroundColor: estaVencido ? '#c62828' : '#2e7d32',
                              color: 'white',
                              fontSize: 11,
                              fontWeight: 'bold',
                              textAlign: 'center',
                              minWidth: 80
                            }}>
                              {estaVencido 
                                ? '❌ VENCIDA' 
                                : (diffDays === 0 
                                    ? '⚠️ Hoy vence' 
                                    : `⏳ ${diffDays} día${diffDays > 1 ? 's' : ''}`)
                              }
                            </div>
                          </div>
                        );
                      })
                    }
                    {/* Gastos sin fecha de vencimiento */}
                    {gastosSeparados.pendientes.filter(g => !g.datosDelMes?.fecha_vencimiento).length > 0 && (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #ccc' }}>
                        <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>Sin fecha registrada:</div>
                        {gastosSeparados.pendientes.filter(g => !g.datosDelMes?.fecha_vencimiento).map(gasto => (
                          <div key={gasto.id} style={{
                            backgroundColor: '#f5f5f5',
                            borderRadius: 6,
                            padding: 8,
                            marginBottom: 4,
                            fontSize: 12,
                            color: '#666'
                          }}>
                            {gasto.nombre} - {gasto.sede}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Columna: Pagados (simplificada) */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: 12,
                border: '2px solid #4caf50',
                padding: 16
              }}>
                <h3 style={{ margin: '0 0 16px', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 8 }}>
                  ✅ Pagados ({gastosSeparados.pagados.length})
                </h3>
                
                {gastosSeparados.pagados.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>💸</div>
                    <p>Aún no hay pagos este mes</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {gastosSeparados.pagados.map(gasto => (
                      <div key={gasto.id} style={{
                        backgroundColor: '#e8f5e9',
                        borderRadius: 8,
                        padding: 10,
                        border: '1px solid #a5d6a7',
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: 13, color: '#2e7d32' }}>
                            {gasto.nombre}
                          </div>
                          <div style={{ fontSize: 11, color: '#666' }}>
                            📍 {gasto.sede} | 📅 {formatearFecha(gasto.datosDelMes?.fecha_pago)}
                          </div>
                        </div>
                        <div style={{
                          padding: '6px 10px',
                          borderRadius: 6,
                          backgroundColor: '#2e7d32',
                          color: 'white',
                          fontSize: 11,
                          fontWeight: 'bold'
                        }}>
                          ✅ PAGADO
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
          /* Vista normal: Dos columnas: Pendientes y Pagados */
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Columna: Pendientes de Pago */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: 12,
              border: '1px solid #ffcc80',
              padding: 16
            }}>
              <h3 style={{ margin: '0 0 16px', color: '#e65100', display: 'flex', alignItems: 'center', gap: 8 }}>
                ⏳ Pendientes de Pago ({gastosSeparados.pendientes.length})
              </h3>
              
              {gastosSeparados.pendientes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
                  <p>¡Todos los gastos del mes están pagados!</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {gastosSeparados.pendientes.map(gasto => {
                    const datosDelMes = gasto.datosDelMes;
                    const tieneVencimiento = datosDelMes?.fecha_vencimiento;
                    const tieneValor = datosDelMes?.valor > 0;
                    const tieneFechaPago = datosDelMes?.fecha_pago;
                    const tieneAdjuntos = datosDelMes?.adjuntos?.length > 0;
                    
                    return (
                      <div key={gasto.id} style={{
                        backgroundColor: '#fff8e1',
                        borderRadius: 8,
                        padding: 12,
                        border: '1px solid #ffcc80',
                        cursor: (tieneVencimiento || tieneAdjuntos) ? 'pointer' : 'default'
                      }}
                      onClick={() => {
                        if (tieneVencimiento || tieneAdjuntos) {
                          setModalDetallesGasto({ gasto, datosDelMes });
                        }
                      }}>
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ fontWeight: 'bold', fontSize: 13, color: '#e65100' }}>
                              {gasto.nombre}
                            </div>
                            {(tieneVencimiento || tieneAdjuntos) && (
                              <span style={{ fontSize: 10, color: '#1976d2', fontWeight: '500' }}>👆 Ver</span>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: '#bf360c' }}>
                            📍 {gasto.sede} | 📁 {gasto.categoria}
                          </div>
                          {tieneVencimiento && (
                            <div style={{ fontSize: 11, marginTop: 4 }}>
                              📅 Vence: {formatearFecha(datosDelMes.fecha_vencimiento)} | 💰 {formatearMoneda(datosDelMes.valor || 0)}
                            </div>
                          )}
                          {tieneAdjuntos && (
                            <div style={{ fontSize: 10, marginTop: 4, color: '#1976d2', fontWeight: 'bold' }}>
                              📎 {datosDelMes.adjuntos.length} adjunto(s)
                            </div>
                          )}
                        </div>
                        
                        {/* Sub-botones */}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {(tienePermiso("admin_gastos_recurrentes_vencimiento") || tienePermiso("admin_gastos_recurrentes") || tienePermiso("admin_gastos")) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); abrirModalRecurrente(gasto, 'vencimiento'); }}
                            style={{
                              padding: '4px 8px',
                              fontSize: 11,
                              backgroundColor: tieneVencimiento ? '#4caf50' : '#ff9800',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: 'pointer'
                            }}
                          >
                            {tieneVencimiento ? '✅ Vencimiento' : '📅 Vencimiento/Valor'}
                          </button>
                          )}
                          
                          {(tienePermiso("admin_gastos_recurrentes_pago") || tienePermiso("admin_gastos_recurrentes") || tienePermiso("admin_gastos")) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); abrirModalRecurrente(gasto, 'pago'); }}
                            disabled={!tieneVencimiento}
                            style={{
                              padding: '4px 8px',
                              fontSize: 11,
                              backgroundColor: !tieneVencimiento ? '#ccc' : (tieneFechaPago ? '#4caf50' : '#2196f3'),
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: tieneVencimiento ? 'pointer' : 'not-allowed'
                            }}
                          >
                            {tieneFechaPago ? '✅ Pago registrado' : '💳 Fecha Pago/Adjuntos'}
                          </button>
                          )}
                          
                          {(tienePermiso("admin_gastos_recurrentes_confirmar") || tienePermiso("admin_gastos_recurrentes") || tienePermiso("admin_gastos")) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); abrirModalRecurrente(gasto, 'confirmar'); }}
                            disabled={!tieneFechaPago || !tieneValor}
                            style={{
                              padding: '4px 8px',
                              fontSize: 11,
                              backgroundColor: (!tieneFechaPago || !tieneValor) ? '#ccc' : '#7b1fa2',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: (tieneFechaPago && tieneValor) ? 'pointer' : 'not-allowed'
                            }}
                          >
                            ✓ Confirmar Pago
                          </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Columna: Pagados */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: 12,
              border: '2px solid #4caf50',
              padding: 16
            }}>
              <h3 style={{ margin: '0 0 16px', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 8 }}>
                ✅ Pagados ({gastosSeparados.pagados.length})
              </h3>
              
              {gastosSeparados.pagados.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>💸</div>
                  <p>Aún no hay gastos pagados este mes</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {gastosSeparados.pagados.map(gasto => {
                    const datosDelMes = gasto.datosDelMes;
                    
                    return (
                      <div key={gasto.id} style={{
                        backgroundColor: '#e8f5e9',
                        borderRadius: 8,
                        padding: 12,
                        border: '1px solid #a5d6a7',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => setModalDetallesGasto({ gasto, datosDelMes })}
                      onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)'}
                      onMouseOut={(e) => e.currentTarget.style.boxShadow = 'none'}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <div style={{ fontWeight: 'bold', fontSize: 13, color: '#2e7d32' }}>
                              ✅ {gasto.nombre}
                            </div>
                            <div style={{ fontSize: 11, color: '#1b5e20' }}>
                              📍 {gasto.sede} | 📁 {gasto.categoria}
                            </div>
                            <div style={{ fontSize: 11, marginTop: 4, color: '#388e3c' }}>
                              💰 {formatearMoneda(datosDelMes?.valor || 0)} | 📅 Pagado: {formatearFecha(datosDelMes?.fecha_pago)}
                            </div>
                            {datosDelMes?.adjuntos?.length > 0 && (
                              <div style={{ fontSize: 10, marginTop: 4, color: '#1976d2', fontWeight: 'bold' }}>
                                📎 {datosDelMes.adjuntos.length} adjunto(s) - Click para ver
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 24 }}>✅</span>
                            <span style={{ fontSize: 10, color: '#666' }}>👆 Ver</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Resumen por categoría */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: 12,
            border: '1px solid #e0e0e0',
            padding: 16,
            marginTop: 24
          }}>
            <h3 style={{ margin: '0 0 16px', color: '#7b1fa2' }}>📊 Resumen del Período</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
              <div style={{ backgroundColor: '#fff3e0', padding: 12, borderRadius: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#e65100' }}>
                  {gastosSeparados.pendientes.length}
                </div>
                <div style={{ fontSize: 12, color: '#bf360c' }}>Pendientes</div>
              </div>
              <div style={{ backgroundColor: '#e8f5e9', padding: 12, borderRadius: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#2e7d32' }}>
                  {gastosSeparados.pagados.length}
                </div>
                <div style={{ fontSize: 12, color: '#1b5e20' }}>Pagados</div>
              </div>
              <div style={{ backgroundColor: '#e3f2fd', padding: 12, borderRadius: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1565c0' }}>
                  {formatearMoneda(gastosSeparados.pagados.reduce((sum, g) => sum + (g.datosDelMes?.valor || 0), 0))}
                </div>
                <div style={{ fontSize: 12, color: '#0d47a1' }}>Total Pagado</div>
              </div>
              <div style={{ backgroundColor: '#fce4ec', padding: 12, borderRadius: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#c2185b' }}>
                  {formatearMoneda(gastosSeparados.pendientes.reduce((sum, g) => sum + (g.datosDelMes?.valor || 0), 0))}
                </div>
                <div style={{ fontSize: 12, color: '#880e4f' }}>Pendiente por Pagar</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Gastos Recurrentes */}
      {modalRecurrente && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 16,
            padding: 24,
            width: '100%',
            maxWidth: 500,
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 8px', color: '#2e7d32' }}>
              {modalRecurrente.accion === 'vencimiento' && '📅 Fecha de Vencimiento y Valor'}
              {modalRecurrente.accion === 'pago' && '💳 Registrar Pago y Adjuntos'}
              {modalRecurrente.accion === 'confirmar' && '✓ Confirmar Pago Realizado'}
            </h3>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
              <strong>{modalRecurrente.gasto.nombre}</strong> - {modalRecurrente.gasto.sede}
            </div>

            {/* Formulario según acción */}
            {modalRecurrente.accion === 'vencimiento' && (
              <div style={{ display: 'grid', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 13 }}>
                    Fecha de Vencimiento
                  </label>
                  <input
                    type="date"
                    value={formRecurrente.fechaVencimiento}
                    onChange={(e) => setFormRecurrente(prev => ({ ...prev, fechaVencimiento: e.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 13 }}>
                    Valor a Pagar
                  </label>
                  <input
                    type="number"
                    value={formRecurrente.valor}
                    onChange={(e) => setFormRecurrente(prev => ({ ...prev, valor: e.target.value }))}
                    placeholder="0"
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}

            {modalRecurrente.accion === 'pago' && (
              <div style={{ display: 'grid', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 13 }}>
                    Fecha de Pago
                  </label>
                  <input
                    type="date"
                    value={formRecurrente.fechaPago}
                    onChange={(e) => setFormRecurrente(prev => ({ ...prev, fechaPago: e.target.value }))}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 13 }}>
                    Adjuntar Comprobantes (PDF o Imágenes)
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const archivo = e.target.files?.[0];
                      if (archivo) subirAdjuntoRecurrente(archivo);
                    }}
                    disabled={subiendoAdjunto}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box' }}
                  />
                  {subiendoAdjunto && <p style={{ fontSize: 12, color: '#666' }}>⏳ Subiendo archivo...</p>}
                </div>
                {formRecurrente.adjuntos?.length > 0 && (
                  <div>
                    <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 13 }}>
                      📎 Archivos adjuntos:
                    </label>
                    {formRecurrente.adjuntos.map((adj, idx) => (
                      <div key={idx} style={{ fontSize: 12, padding: 4, backgroundColor: '#f5f5f5', borderRadius: 4, marginBottom: 4 }}>
                        <a href={adj.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                          📄 {adj.nombre}
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {modalRecurrente.accion === 'confirmar' && (
              <div style={{ backgroundColor: '#fff3e0', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                <p style={{ margin: 0, fontSize: 13 }}>
                  <strong>⚠️ Confirmar pago realizado:</strong>
                </p>
                <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 13 }}>
                  <li>Este gasto se marcará como <strong style={{ color: '#2e7d32' }}>PAGADO</strong></li>
                  <li>Se enviará automáticamente a <strong>Gastos Manuales</strong> de la sede correspondiente</li>
                  <li>Aparecerá en verde en la columna de pagados</li>
                </ul>
                <div style={{ marginTop: 12, padding: 12, backgroundColor: 'white', borderRadius: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
                    <div><strong>Valor:</strong> {formatearMoneda(obtenerGastoMes(modalRecurrente.gasto.id)?.valor || 0)}</div>
                    <div><strong>Fecha pago:</strong> {formatearFecha(obtenerGastoMes(modalRecurrente.gasto.id)?.fecha_pago)}</div>
                    <div><strong>Sede destino:</strong> {mapearSedeAReal(modalRecurrente.gasto.sede)}</div>
                    <div><strong>Concepto:</strong> {modalRecurrente.gasto.categoria}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Botones del modal */}
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button
                onClick={() => {
                  setModalRecurrente(null);
                  setFormRecurrente({ fechaVencimiento: '', valor: '', fechaPago: '', adjuntos: [] });
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (modalRecurrente.accion === 'vencimiento') guardarVencimientoGasto();
                  else if (modalRecurrente.accion === 'pago') guardarPagoGasto();
                  else if (modalRecurrente.accion === 'confirmar') confirmarPagoYEnviar();
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: modalRecurrente.accion === 'confirmar' ? '#7b1fa2' : '#2e7d32',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {modalRecurrente.accion === 'vencimiento' && '💾 Guardar'}
                {modalRecurrente.accion === 'pago' && '💾 Guardar Pago'}
                {modalRecurrente.accion === 'confirmar' && '✓ Confirmar y Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Detalles del Gasto Recurrente con Adjuntos */}
      {modalDetallesGasto && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1001
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) setModalDetallesGasto(null);
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 16,
            padding: 24,
            width: '100%',
            maxWidth: 550,
            maxHeight: '85vh',
            overflow: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }}>
            {/* Cabecera */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 20,
              paddingBottom: 16,
              borderBottom: '2px solid #e0e0e0'
            }}>
              <div>
                <h3 style={{ margin: 0, color: modalDetallesGasto.datosDelMes?.estado === 'pagado' ? '#2e7d32' : '#e65100' }}>
                  {modalDetallesGasto.datosDelMes?.estado === 'pagado' ? '✅' : '⏳'} {modalDetallesGasto.gasto.nombre}
                </h3>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                  📍 {modalDetallesGasto.gasto.sede} | 📁 {modalDetallesGasto.gasto.categoria}
                </div>
              </div>
              <button
                onClick={() => setModalDetallesGasto(null)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 16
                }}
              >
                ✕
              </button>
            </div>

            {/* Información del Gasto */}
            <div style={{
              backgroundColor: modalDetallesGasto.datosDelMes?.estado === 'pagado' ? '#e8f5e9' : '#fff8e1',
              borderRadius: 12,
              padding: 16,
              marginBottom: 20
            }}>
              <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#333' }}>📋 Información del Gasto</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Estado</div>
                  <div style={{
                    fontWeight: 'bold',
                    color: modalDetallesGasto.datosDelMes?.estado === 'pagado' ? '#2e7d32' : '#e65100',
                    fontSize: 14
                  }}>
                    {modalDetallesGasto.datosDelMes?.estado === 'pagado' ? '✅ PAGADO' : '⏳ PENDIENTE'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Valor</div>
                  <div style={{ fontWeight: 'bold', color: '#1565c0', fontSize: 16 }}>
                    {formatearMoneda(modalDetallesGasto.datosDelMes?.valor || 0)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Fecha Vencimiento</div>
                  <div style={{ fontWeight: '500', fontSize: 13 }}>
                    {modalDetallesGasto.datosDelMes?.fecha_vencimiento 
                      ? formatearFecha(modalDetallesGasto.datosDelMes.fecha_vencimiento) 
                      : '— Sin registrar'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 2 }}>Fecha de Pago</div>
                  <div style={{ fontWeight: '500', fontSize: 13 }}>
                    {modalDetallesGasto.datosDelMes?.fecha_pago 
                      ? formatearFecha(modalDetallesGasto.datosDelMes.fecha_pago) 
                      : '— Sin registrar'}
                  </div>
                </div>
              </div>
              
              {/* Info adicional si existe */}
              {(modalDetallesGasto.gasto.matricula || modalDetallesGasto.gasto.referencia) && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #ccc' }}>
                  {modalDetallesGasto.gasto.matricula && (
                    <div style={{ fontSize: 12, marginBottom: 4 }}>
                      <strong>🔢 Matrícula:</strong> {modalDetallesGasto.gasto.matricula}
                    </div>
                  )}
                  {modalDetallesGasto.gasto.referencia && (
                    <div style={{ fontSize: 12 }}>
                      <strong>📝 Referencia:</strong> {modalDetallesGasto.gasto.referencia}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sección de Adjuntos */}
            <div style={{
              backgroundColor: '#f5f5f5',
              borderRadius: 12,
              padding: 16
            }}>
              <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#333', display: 'flex', alignItems: 'center', gap: 8 }}>
                📎 Archivos Adjuntos
                {modalDetallesGasto.datosDelMes?.adjuntos?.length > 0 && (
                  <span style={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: 12,
                    fontSize: 11
                  }}>
                    {modalDetallesGasto.datosDelMes.adjuntos.length}
                  </span>
                )}
              </h4>
              
              {(!modalDetallesGasto.datosDelMes?.adjuntos || modalDetallesGasto.datosDelMes.adjuntos.length === 0) ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                  <p style={{ margin: 0, fontSize: 13 }}>No hay archivos adjuntos</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {modalDetallesGasto.datosDelMes.adjuntos.map((adj, idx) => {
                    const esImagen = adj.nombre?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                    const esPDF = adj.nombre?.match(/\.pdf$/i);
                    
                    return (
                      <div key={idx} style={{
                        backgroundColor: 'white',
                        borderRadius: 8,
                        padding: 12,
                        border: '1px solid #e0e0e0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                          <div style={{
                            width: 40,
                            height: 40,
                            backgroundColor: esPDF ? '#f44336' : '#4caf50',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: 18
                          }}>
                            {esPDF ? '📄' : '🖼️'}
                          </div>
                          <div style={{ overflow: 'hidden' }}>
                            <div style={{
                              fontWeight: '500',
                              fontSize: 13,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 200
                            }}>
                              {adj.nombre || 'Archivo sin nombre'}
                            </div>
                            <div style={{ fontSize: 11, color: '#666' }}>
                              {esPDF ? 'Documento PDF' : (esImagen ? 'Imagen' : 'Archivo')}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {/* Botón Ver */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(adj.url, '_blank');
                            }}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#1976d2',
                              color: 'white',
                              border: 'none',
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontSize: 12,
                              fontWeight: 'bold'
                            }}
                            title="Ver archivo"
                          >
                            Ver
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Botón Cerrar */}
            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={() => setModalDetallesGasto(null)}
                style={{
                  padding: '12px 32px',
                  backgroundColor: '#424242',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: 14
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Nuevo Gasto Manual */}
      {mostrarModalGastoManual && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 16,
            padding: 24,
            width: '100%',
            maxWidth: 450
          }}>
            <h3 style={{ margin: '0 0 20px', color: '#7b1fa2' }}>
              {gastoEditandoId ? '✏️ Editar Gasto Manual' : '➕ Nuevo Gasto Manual'}
            </h3>
            
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 13 }}>
                  Sede *
                </label>
                <select
                  value={gastoManualSede}
                  onChange={(e) => setGastoManualSede(e.target.value)}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                >
                  <option value="">Seleccionar...</option>
                  {sedes.map(s => (
                    <option key={s.id || s.nombre} value={s.nombre}>{s.nombre}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 13 }}>
                  Fecha *
                </label>
                <input
                  type="date"
                  value={gastoManualFecha}
                  onChange={(e) => setGastoManualFecha(e.target.value)}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 13 }}>
                  Concepto *
                </label>
                <select
                  value={gastoManualConcepto}
                  onChange={(e) => setGastoManualConcepto(e.target.value)}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                >
                  <option value="">Seleccionar concepto...</option>
                  {conceptosGastos.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 13 }}>
                  Proveedor / Beneficiario (opcional)
                </label>
                <input
                  type="text"
                  value={gastoManualProveedor}
                  onChange={(e) => setGastoManualProveedor(e.target.value)}
                  placeholder="Nombre del proveedor o beneficiario"
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box' }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 13 }}>
                  Valor *
                </label>
                <input
                  type="number"
                  value={gastoManualValor}
                  onChange={(e) => setGastoManualValor(e.target.value)}
                  placeholder="0"
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button
                onClick={() => {
                  setMostrarModalGastoManual(false);
                  setGastoEditandoId(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={gastoEditandoId ? actualizarGastoManual : guardarGastoManual}
                disabled={guardandoGasto}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  backgroundColor: gastoEditandoId ? '#ff9800' : '#7b1fa2',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: guardandoGasto ? 'wait' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {guardandoGasto ? '⏳ Guardando...' : (gastoEditandoId ? '💾 Actualizar' : '💾 Guardar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================== SECRETARÍA DE SALUD ====================== */

// Configuración por defecto de items de limpieza
const ITEMS_LIMPIEZA_COCINA_DIARIO_DEFAULT = ["PISOS", "PARRILLA", "PLANCHA", "VAPORIZADOR", "PREPARACIÓN", "SALSEROS", "FREIDOR", "PAPERO", "MICROONDAS"];
const ITEMS_LIMPIEZA_COCINA_SEMANAL_DEFAULT = ["CONGELADOR PAPAS", "CONGELADOR CARNE", "CONGELADOR PULPA", "GABINETE COCINA", "CAMPANA", "PAREDES", "ESTUFA", "CANECAS BASURA"];
const ITEMS_LIMPIEZA_SALON_DIARIO_DEFAULT = ["PISOS", "MESAS Y SILLAS", "SALSEROS", "BAÑOS", "LICUADORAS", "BARRA", "LAVAMANOS"];
const ITEMS_LIMPIEZA_SALON_SEMANAL_DEFAULT = ["NEVERAS GASEOSAS", "PISO ENTRADA", "COMPUTADORES", "DISPENSADOR AGUA", "VIDRIOS", "PAREDES", "TELEVISORES", "HIELERA"];

// Bimestres fijos
const BIMESTRES = [
  { inicio: 1, fin: 2, nombre: "ENERO-FEBRERO" },
  { inicio: 3, fin: 4, nombre: "MARZO-ABRIL" },
  { inicio: 5, fin: 6, nombre: "MAYO-JUNIO" },
  { inicio: 7, fin: 8, nombre: "JULIO-AGOSTO" },
  { inicio: 9, fin: 10, nombre: "SEPTIEMBRE-OCTUBRE" },
  { inicio: 11, fin: 12, nombre: "NOVIEMBRE-DICIEMBRE" }
];

// Función para obtener el bimestre de un mes
function obtenerBimestre(mes) {
  return BIMESTRES.find(b => mes >= b.inicio && mes <= b.fin);
}

// Función para obtener último día del mes
function ultimoDiaDelMes(anio, mes) {
  return new Date(anio, mes, 0).getDate();
}

// Función para obtener días del mes
function obtenerDiasDelMes(anio, mes) {
  const ultimoDia = ultimoDiaDelMes(anio, mes);
  const dias = [];
  for (let d = 1; d <= ultimoDia; d++) {
    const fecha = new Date(anio, mes - 1, d);
    dias.push({
      dia: d,
      fecha: `${anio}-${String(mes).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
      diaSemana: fecha.getDay(), // 0=Dom, 1=Lun, ..., 6=Sab
      nombreDia: ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"][fecha.getDay()]
    });
  }
  return dias;
}

// Función para generar temperatura aleatoria estable
function generarTemperaturaEstable(baseTemp, variacion = 1) {
  const delta = (Math.random() - 0.5) * variacion * 2;
  return Math.round((baseTemp + delta) * 10) / 10;
}

// Función para generar hora aleatoria entre 11:30 AM y 7:00 PM (formato AM/PM)
function generarHoraAleatoria() {
  const horaBase = 11 + Math.floor(Math.random() * 8); // 11 a 18
  const minutos = Math.floor(Math.random() * 60);
  // Si es 11, asegurar que sea al menos 11:30
  const minutosFinales = horaBase === 11 ? Math.max(30, minutos) : minutos;
  // Convertir a formato AM/PM
  const hora12 = horaBase > 12 ? horaBase - 12 : horaBase;
  const periodo = horaBase >= 12 ? 'PM' : 'AM';
  return `${hora12}:${String(minutosFinales).padStart(2, '0')} ${periodo}`;
}

// Cargar configuración de Secretaría de Salud por sede
async function cargarConfigSecretariaSalud(empresaId, sedeId = null) {
  try {
    let query = supabase.from("secretaria_salud_config").select("*");
    if (empresaId) query = query.eq("empresa_id", empresaId);
    if (sedeId) query = query.eq("sede_id", sedeId);
    const { data, error } = await query;
    if (error) {
      console.error("Error cargando config secretaría salud:", error);
      return [];
    }
    return data || [];
  } catch (e) {
    console.error("Error en cargarConfigSecretariaSalud:", e);
    return [];
  }
}

// Guardar configuración de Secretaría de Salud
async function guardarConfigSecretariaSalud(config) {
  try {
    const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const payload = {
      id: (config.id && UUID_REGEX.test(config.id)) ? config.id : crypto.randomUUID(),
      sede_id: config.sedeId,
      empresa_id: config.empresaId,
      num_congeladores: config.numCongeladores || 1,
      num_refrigeradores: config.numRefrigeradores || 1,
      tiene_tanque_reserva: config.tieneTanqueReserva || false,
      items_limpieza_cocina_diario: config.itemsLimpiezaCocinaDiario || ITEMS_LIMPIEZA_COCINA_DIARIO_DEFAULT,
      items_limpieza_cocina_semanal: config.itemsLimpiezaCocinaSemanal || ITEMS_LIMPIEZA_COCINA_SEMANAL_DEFAULT,
      items_limpieza_salon_diario: config.itemsLimpiezaSalonDiario || ITEMS_LIMPIEZA_SALON_DIARIO_DEFAULT,
      items_limpieza_salon_semanal: config.itemsLimpiezaSalonSemanal || ITEMS_LIMPIEZA_SALON_SEMANAL_DEFAULT,
      // Nuevas planillas - habilitación por sede
      aplica_residuos: config.aplicaResiduos !== undefined ? config.aplicaResiduos : true,
      aplica_agua_potable: config.aplicaAguaPotable !== undefined ? config.aplicaAguaPotable : true,
      aplica_plagas: config.aplicaPlagas !== undefined ? config.aplicaPlagas : true,
      updated_at: new Date().toISOString()
    };
    
    const { data: existente } = await supabase
      .from("secretaria_salud_config")
      .select("id")
      .eq("sede_id", config.sedeId)
      .maybeSingle();
    
    if (existente) {
      payload.id = existente.id;
      const { error } = await supabase.from("secretaria_salud_config").update(payload).eq("id", existente.id);
      if (error) throw error;
    } else {
      payload.created_at = new Date().toISOString();
      const { error } = await supabase.from("secretaria_salud_config").insert([payload]);
      if (error) throw error;
    }
    return true;
  } catch (e) {
    console.error("Error guardando config secretaría salud:", e);
    return false;
  }
}

// Cargar archivos de Secretaría de Salud
async function cargarArchivosSecretariaSalud(empresaId, sedeId = null) {
  try {
    let query = supabase.from("secretaria_salud_archivos").select("*").order("created_at", { ascending: false });
    if (empresaId) query = query.eq("empresa_id", empresaId);
    if (sedeId) query = query.eq("sede_id", sedeId);
    const { data, error } = await query;
    if (error) {
      console.error("Error cargando archivos secretaría salud:", error);
      return [];
    }
    return data || [];
  } catch (e) {
    console.error("Error en cargarArchivosSecretariaSalud:", e);
    return [];
  }
}

// Subir archivo de Secretaría de Salud
async function subirArchivoSecretariaSalud(archivo, nombre, sedeId, empresaId) {
  try {
    const ext = archivo.name.split('.').pop();
    const archivoId = crypto.randomUUID();
    const path = `secretaria-salud/${sedeId}/${Date.now()}-${slugifyPathSegment(nombre)}.${ext}`;
    
    const { error: uploadError } = await supabase.storage
      .from(SUPABASE_BUCKET_DOCS)
      .upload(path, archivo);
    
    if (uploadError) throw uploadError;
    
    const { data: urlData } = supabase.storage.from(SUPABASE_BUCKET_DOCS).getPublicUrl(path);
    
    const payload = {
      id: archivoId,
      sede_id: sedeId,
      empresa_id: empresaId,
      nombre: nombre,
      archivo_url: urlData.publicUrl,
      archivo_path: path,
      archivo_nombre_original: archivo.name,
      created_at: new Date().toISOString()
    };
    
    const { error: dbError } = await supabase.from("secretaria_salud_archivos").insert([payload]);
    if (dbError) throw dbError;
    
    return payload;
  } catch (e) {
    console.error("Error subiendo archivo:", e);
    return null;
  }
}

// Eliminar archivo de Secretaría de Salud
async function eliminarArchivoSecretariaSalud(archivoId, archivoPath) {
  try {
    if (archivoPath) {
      await supabase.storage.from(SUPABASE_BUCKET_DOCS).remove([archivoPath]);
    }
    const { error } = await supabase.from("secretaria_salud_archivos").delete().eq("id", archivoId);
    if (error) throw error;
    return true;
  } catch (e) {
    console.error("Error eliminando archivo:", e);
    return false;
  }
}

// Cargar registros de trampa de grasa
async function cargarTrampaGrasa(empresaId, sedeId = null) {
  try {
    let query = supabase.from("secretaria_salud_trampa_grasa").select("*").order("fecha", { ascending: false });
    if (empresaId) query = query.eq("empresa_id", empresaId);
    if (sedeId) query = query.eq("sede_id", sedeId);
    const { data, error } = await query;
    if (error) {
      console.error("Error cargando trampa grasa:", error);
      return [];
    }
    return data || [];
  } catch (e) {
    console.error("Error en cargarTrampaGrasa:", e);
    return [];
  }
}

// Guardar registro de trampa de grasa
async function guardarTrampaGrasa(registro) {
  try {
    const payload = {
      id: crypto.randomUUID(),
      sede_id: registro.sedeId,
      empresa_id: registro.empresaId,
      fecha: registro.fecha,
      proveedor: registro.proveedor.toUpperCase(),
      created_at: new Date().toISOString()
    };
    const { error } = await supabase.from("secretaria_salud_trampa_grasa").insert([payload]);
    if (error) throw error;
    return payload;
  } catch (e) {
    console.error("Error guardando trampa grasa:", e);
    return null;
  }
}

// Cargar planillas de Secretaría de Salud
async function cargarPlanillasSecretariaSalud(empresaId, sedeId = null, mes = null, anio = null) {
  try {
    let query = supabase.from("secretaria_salud_planillas").select("*").order("anio", { ascending: false }).order("mes", { ascending: false });
    if (empresaId) query = query.eq("empresa_id", empresaId);
    if (sedeId) query = query.eq("sede_id", sedeId);
    if (mes) query = query.eq("mes", mes);
    if (anio) query = query.eq("anio", anio);
    const { data, error } = await query;
    if (error) {
      console.error("Error cargando planillas:", error);
      return [];
    }
    return data || [];
  } catch (e) {
    console.error("Error en cargarPlanillasSecretariaSalud:", e);
    return [];
  }
}

// Guardar planilla de Secretaría de Salud
async function guardarPlanillaSecretariaSalud(planilla) {
  try {
    // Normalizar sedeId (puede venir como sedeId o sede_id)
    const sedeId = planilla.sedeId || planilla.sede_id;
    const empresaId = planilla.empresaId || planilla.empresa_id;
    
    if (!sedeId || !empresaId) {
      console.error("Error: sedeId o empresaId faltante", { sedeId, empresaId });
      return null;
    }
    
    // Validar que los IDs de supervisores sean UUIDs válidos o null
    const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const supervisorCocinaId = planilla.supervisorCocinaId || planilla.supervisor_cocina_id;
    const supervisorSalonId = planilla.supervisorSalonId || planilla.supervisor_salon_id;
    
    const payload = {
      id: (planilla.id && UUID_REGEX.test(planilla.id)) ? planilla.id : crypto.randomUUID(),
      sede_id: sedeId,
      empresa_id: empresaId,
      mes: planilla.mes,
      anio: planilla.anio,
      supervisor_cocina_id: (supervisorCocinaId && UUID_REGEX.test(supervisorCocinaId)) ? supervisorCocinaId : null,
      supervisor_salon_id: (supervisorSalonId && UUID_REGEX.test(supervisorSalonId)) ? supervisorSalonId : null,
      supervisor_cocina_nombre: planilla.supervisorCocinaNombre || planilla.supervisor_cocina_nombre || null,
      supervisor_salon_nombre: planilla.supervisorSalonNombre || planilla.supervisor_salon_nombre || null,
      // Revisores de las nuevas planillas
      revisor_residuos: planilla.revisorResiduos || planilla.revisor_residuos || null,
      revisor_agua_potable: planilla.revisorAguaPotable || planilla.revisor_agua_potable || null,
      revisor_plagas: planilla.revisorPlagas || planilla.revisor_plagas || null,
      datos_desinfeccion_cocina: planilla.datosDesinfeccionCocina || planilla.datos_desinfeccion_cocina || {},
      datos_desinfeccion_salon: planilla.datosDesinfeccionSalon || planilla.datos_desinfeccion_salon || {},
      datos_temperatura: planilla.datosTemperatura || planilla.datos_temperatura || {},
      datos_tanque_reserva: planilla.datosTanqueReserva || planilla.datos_tanque_reserva || {},
      // Nuevas planillas
      datos_residuos: planilla.datosResiduos || planilla.datos_residuos || {},
      datos_agua_potable: planilla.datosAguaPotable || planilla.datos_agua_potable || {},
      datos_plagas: planilla.datosPlagas || planilla.datos_plagas || {},
      cerrado: planilla.cerrado || false,
      fecha_cierre: planilla.fechaCierre || planilla.fecha_cierre || null,
      updated_at: new Date().toISOString()
    };
    
    console.log("[SecretariaSalud] Guardando planilla:", { mes: payload.mes, anio: payload.anio, sede_id: payload.sede_id });
    
    // Buscar planilla existente por sede+mes+anio
    const { data: existente } = await supabase
      .from("secretaria_salud_planillas")
      .select("id")
      .eq("sede_id", sedeId)
      .eq("mes", planilla.mes)
      .eq("anio", planilla.anio)
      .maybeSingle();
    
    if (existente) {
      payload.id = existente.id;
      const { error } = await supabase.from("secretaria_salud_planillas").update(payload).eq("id", existente.id);
      if (error) {
        console.error("Error actualizando planilla:", error);
        throw error;
      }
      console.log("[SecretariaSalud] Planilla actualizada:", payload.id);
    } else {
      payload.created_at = new Date().toISOString();
      // Usar upsert para evitar conflictos de concurrencia
      const { error } = await supabase.from("secretaria_salud_planillas").upsert([payload], {
        onConflict: 'sede_id,mes,anio',
        ignoreDuplicates: false
      });
      if (error) {
        console.error("Error insertando planilla:", error);
        throw error;
      }
      console.log("[SecretariaSalud] Planilla insertada:", payload.id);
    }
    return payload;
  } catch (e) {
    console.error("Error guardando planilla:", e);
    return null;
  }
}

// Componente principal de Secretaría de Salud
function SecretariaSaludAdmin({ empleados, sedes, horariosRepo, ausencias, vacaciones, tienePermiso, empresaId, usuarioActual, empleadoActual }) {
  const [seccion, setSeccion] = useState("planillas"); // planillas | archivos | trampa | config
  const [sedeSeleccionada, setSedeSeleccionada] = useState(null);
  const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth() + 1);
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
  const [cargando, setCargando] = useState(false);
  
  // Estados para datos
  const [configSedes, setConfigSedes] = useState([]);
  const [archivos, setArchivos] = useState([]);
  const [trampaGrasa, setTrampaGrasa] = useState([]);
  const [planillas, setPlanillas] = useState([]);
  
  // Estados para formularios
  const [nuevoArchivo, setNuevoArchivo] = useState({ nombre: "", archivo: null });
  const [nuevaTrampa, setNuevaTrampa] = useState({ fecha: "", proveedor: "" });
  const [configEditando, setConfigEditando] = useState(null);
  
  // Estados para planilla actual
  const [planillaActual, setPlanillaActual] = useState(null);
  const [tipoPlanilla, setTipoPlanilla] = useState("desinfeccion_cocina"); // desinfeccion_cocina | desinfeccion_salon | temperatura | tanque_reserva
  
  // Estados para modal de impresión
  const [mostrarModalImpresion, setMostrarModalImpresion] = useState(false);
  const [mesInicioImpresion, setMesInicioImpresion] = useState(new Date().getMonth() + 1);
  const [anioInicioImpresion, setAnioInicioImpresion] = useState(new Date().getFullYear());
  const [mesFinImpresion, setMesFinImpresion] = useState(new Date().getMonth() + 1);
  const [anioFinImpresion, setAnioFinImpresion] = useState(new Date().getFullYear());
  const [vistaImpresion, setVistaImpresion] = useState(false);
  const [planillasParaImprimir, setPlanillasParaImprimir] = useState([]);
  
  // Filtrar empleados por cargo/perfil para REVISÓ (no depende de horarios)
  // Para COCINA y TEMPERATURA: Supervisor Cocina, Supervisor de Cocina, Gerencia, Administración, Administracion general, ADMINISTRACION CORA SUR PRADO
  const supervisoresCocina = useMemo(() => {
    const filtrados = empleados.filter(e => {
      const cargo = (e.cargo || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const perfil = (e.perfil || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      
      // Cargos válidos para cocina
      const cargoValido = (
        (cargo.includes("supervisor") && cargo.includes("cocina")) ||
        cargo.includes("supervisor cocina") ||
        cargo.includes("supervisor de cocina")
      );
      
      // Perfiles válidos (aplican para ambas áreas)
      const perfilValido = (
        perfil.includes("gerencia") ||
        perfil.includes("administracion") ||
        perfil.includes("administrador") ||
        cargo.includes("gerencia") ||
        cargo.includes("administracion") ||
        cargo.includes("administrador")
      );
      
      return cargoValido || perfilValido;
    });
    console.log(`[SecretariaSalud] Supervisores COCINA encontrados: ${filtrados.length}`, filtrados.map(e => ({nombre: e.nombre || `${e.nombres} ${e.apellidos}`, cargo: e.cargo, perfil: e.perfil})));
    if (filtrados.length === 0) {
      console.log(`[SecretariaSalud] Cargos/Perfiles disponibles:`, empleados.map(e => ({cargo: e.cargo, perfil: e.perfil})));
    }
    return filtrados;
  }, [empleados]);
  
  // Para SALÓN y TANQUE DE RESERVA: Supervisor Salón, Supervisor de Salón, Gerencia, Administración, Administracion general, ADMINISTRACION CORA SUR PRADO
  const supervisoresSalon = useMemo(() => {
    const filtrados = empleados.filter(e => {
      const cargo = (e.cargo || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const perfil = (e.perfil || '').toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      
      // Cargos válidos para salón
      const cargoValido = (
        (cargo.includes("supervisor") && cargo.includes("salon")) ||
        cargo.includes("supervisor salon") ||
        cargo.includes("supervisor de salon")
      );
      
      // Perfiles válidos (aplican para ambas áreas)
      const perfilValido = (
        perfil.includes("gerencia") ||
        perfil.includes("administracion") ||
        perfil.includes("administrador") ||
        cargo.includes("gerencia") ||
        cargo.includes("administracion") ||
        cargo.includes("administrador")
      );
      
      return cargoValido || perfilValido;
    });
    console.log(`[SecretariaSalud] Supervisores SALÓN encontrados: ${filtrados.length}`, filtrados.map(e => ({nombre: e.nombre || `${e.nombres} ${e.apellidos}`, cargo: e.cargo, perfil: e.perfil})));
    return filtrados;
  }, [empleados]);
  
  // Personal para aseo de SALÓN: cajeros, cajeras, meseros, meseras
  const cajeros = useMemo(() => {
    const filtrados = empleados.filter(e => {
      const cargo = (e.cargo || '').toLowerCase();
      return (
        cargo.includes("cajero") ||
        cargo.includes("cajera") ||
        cargo.includes("mesero") ||
        cargo.includes("mesera")
      );
    });
    console.log(`[SecretariaSalud] Personal salón (cajeros/meseros) encontrados: ${filtrados.length}`);
    if (filtrados.length === 0) {
      console.log(`[SecretariaSalud] Cargos disponibles:`, [...new Set(empleados.map(e => e.cargo).filter(Boolean))]);
    }
    return filtrados;
  }, [empleados]);
  
  // Personal para aseo de COCINA: auxiliares cocina, cocineros, supernumerarios
  const auxiliaresCocina = useMemo(() => {
    const filtrados = empleados.filter(e => {
      const cargo = (e.cargo || '').toLowerCase();
      return (
        cargo.includes("auxiliar de cocina") ||
        cargo.includes("auxiliar cocina") ||
        cargo.includes("cocinero") ||
        cargo.includes("cocina") ||
        cargo.includes("supernumerario")
      );
    });
    console.log(`[SecretariaSalud] Personal cocina (auxiliares/supernumerarios) encontrados: ${filtrados.length}`);
    if (filtrados.length === 0) {
      console.log(`[SecretariaSalud] Cargos disponibles:`, [...new Set(empleados.map(e => e.cargo).filter(Boolean))]);
    }
    return filtrados;
  }, [empleados]);
  
  // Cargar datos iniciales
  useEffect(() => {
    const cargarDatos = async () => {
      if (!empresaId) return;
      setCargando(true);
      try {
        const [configs, archs, trampas, plans] = await Promise.all([
          cargarConfigSecretariaSalud(empresaId),
          cargarArchivosSecretariaSalud(empresaId),
          cargarTrampaGrasa(empresaId),
          cargarPlanillasSecretariaSalud(empresaId)
        ]);
        setConfigSedes(configs);
        setArchivos(archs);
        setTrampaGrasa(trampas);
        setPlanillas(plans);
        
        // Seleccionar primera sede por defecto
        if (sedes.length > 0 && !sedeSeleccionada) {
          setSedeSeleccionada(sedes[0].id);
        }
      } catch (e) {
        console.error("Error cargando datos:", e);
      }
      setCargando(false);
    };
    cargarDatos();
  }, [empresaId, sedes]);
  
  // Obtener configuración de la sede seleccionada
  const configSedeActual = useMemo(() => {
    if (!sedeSeleccionada) return null;
    return configSedes.find(c => c.sede_id === sedeSeleccionada) || {
      num_congeladores: 1,
      num_refrigeradores: 1,
      tiene_tanque_reserva: false,
      items_limpieza_cocina_diario: ITEMS_LIMPIEZA_COCINA_DIARIO_DEFAULT,
      items_limpieza_cocina_semanal: ITEMS_LIMPIEZA_COCINA_SEMANAL_DEFAULT,
      items_limpieza_salon_diario: ITEMS_LIMPIEZA_SALON_DIARIO_DEFAULT,
      items_limpieza_salon_semanal: ITEMS_LIMPIEZA_SALON_SEMANAL_DEFAULT
    };
  }, [sedeSeleccionada, configSedes]);
  
  // Obtener nombre de la sede
  const nombreSedeActual = useMemo(() => {
    const sede = sedes.find(s => s.id === sedeSeleccionada);
    return sede?.nombre || "";
  }, [sedeSeleccionada, sedes]);
  
  // Función para verificar si un empleado trabaja en una fecha específica EN UNA SEDE
  // verificarHora8PM: true = solo si trabaja hasta 8PM+, false = cualquier horario en esa sede
  const empleadoTrabajaEnFecha = useCallback((empleadoId, fecha, sedeId, verificarHora8PM = true) => {
    // Si no hay horarios cargados, NO asumir que trabaja (debe haber horario)
    if (!horariosRepo || horariosRepo.length === 0) return false;
    
    // Obtener el lunes de la semana de la fecha
    const d = parseISO(fecha);
    const jsDay = d.getDay();
    const diffToMonday = (jsDay + 6) % 7;
    const mondayDate = new Date(d);
    mondayDate.setDate(d.getDate() - diffToMonday);
    const lunes = mondayDate.toISOString().split('T')[0];
    
    const sundayDate = new Date(mondayDate);
    sundayDate.setDate(mondayDate.getDate() + 6);
    const domingo = sundayDate.toISOString().split('T')[0];
    
    // Buscar horario de esa semana
    const horario = horariosRepo.find(h => h.semanaInicio === lunes && h.semanaFin === domingo);
    
    // Si no hay horario para esa semana, no trabaja
    if (!horario || !horario.celdas || !horario.celdas[empleadoId]) {
      return false;
    }
    
    // Obtener índice del día (0=Lunes, 6=Domingo)
    const diaIdx = (jsDay + 6) % 7;
    const celda = horario.celdas[empleadoId][diaIdx];
    
    // Verificar que tenga turno asignado (e1 es la hora de entrada)
    if (!celda || celda.tipo === "DESCANSO" || celda.tipo === "NO_TRABAJO" || celda.descanso || !celda.e1) {
      return false;
    }
    
    // Obtener nombre de la sede buscada (normalizado)
    const sedeObj = sedes.find(s => s.id === sedeId);
    const sedeNombre = (sedeObj?.nombre || '').toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    
    if (!sedeNombre) return false;
    
    // Función para normalizar sede
    const normalizarSede = (sedeCelda) => {
      if (!sedeCelda) return '';
      return sedeCelda.toLowerCase().trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    };
    
    // Función para verificar si las sedes coinciden (comparación flexible)
    const sedesCoinciden = (sedeCelda) => {
      if (!sedeCelda) return false;
      const sedeNorm = normalizarSede(sedeCelda);
      if (!sedeNorm) return false;
      return sedeNorm === sedeNombre || sedeNorm.includes(sedeNombre) || sedeNombre.includes(sedeNorm);
    };
    
    // Función para verificar si la hora de salida es >= 20:00 (8 PM)
    const salidaDespuesDe8PM = (horaSalida) => {
      if (!horaSalida) return false;
      let hora = 0;
      const horaStr = String(horaSalida).trim();
      if (horaStr.includes(':')) {
        const partes = horaStr.replace(/\s?(AM|PM)/i, '').split(':');
        hora = parseInt(partes[0]) || 0;
        if (horaStr.toLowerCase().includes('pm') && hora < 12) hora += 12;
        if (horaStr.toLowerCase().includes('am') && hora === 12) hora = 0;
      } else {
        hora = parseInt(horaStr) || 0;
      }
      return hora >= 20;
    };
    
    // Obtener sedes del empleado en ese día
    const sede1 = celda.sede1 || celda.sede || '';
    const sede2 = celda.sede2 || '';
    
    // Determinar si es horario partido
    const esPartido = celda.e2 && celda.s2 && String(celda.e2).trim() !== '' && String(celda.s2).trim() !== '';
    
    let trabajaEnSede = false;
    let cumpleHorario8PM = false;
    
    if (esPartido) {
      // Verificar sede2 (tarde)
      if (sedesCoinciden(sede2)) {
        trabajaEnSede = true;
        if (salidaDespuesDe8PM(celda.s2)) cumpleHorario8PM = true;
      }
      // Verificar sede1 (mañana)
      if (sedesCoinciden(sede1)) {
        trabajaEnSede = true;
        if (salidaDespuesDe8PM(celda.s1)) cumpleHorario8PM = true;
      }
    } else {
      // Horario derecho: solo sede1
      if (sedesCoinciden(sede1)) {
        trabajaEnSede = true;
        if (salidaDespuesDe8PM(celda.s1)) cumpleHorario8PM = true;
      }
    }
    
    // Si se requiere verificar 8 PM, aplicar esa condición
    if (verificarHora8PM) {
      if (!trabajaEnSede || !cumpleHorario8PM) return false;
    } else {
      // Solo verificar que trabaje en la sede (sin importar hora)
      if (!trabajaEnSede) return false;
    }
    
    // Verificar ausencias
    const tieneAusencia = ausencias.some(a => 
      a.empleadoId === empleadoId && 
      fecha >= a.inicio && 
      fecha <= a.fin
    );
    if (tieneAusencia) return false;
    
    // Verificar vacaciones
    const tieneVacaciones = vacaciones.some(v => 
      v.empleadoId === empleadoId && 
      fecha >= v.inicio && 
      fecha <= v.fin
    );
    if (tieneVacaciones) return false;
    
    return true;
  }, [horariosRepo, ausencias, vacaciones, sedes]);
  
  // Función para obtener empleados disponibles en una fecha (con fallback)
  const obtenerEmpleadosDisponibles = useCallback((fecha, sedeId, tipoEmpleado) => {
    let listaEmpleados = [];
    
    if (tipoEmpleado === "cajero") {
      listaEmpleados = cajeros;
    } else if (tipoEmpleado === "auxiliar_cocina") {
      listaEmpleados = auxiliaresCocina;
    } else if (tipoEmpleado === "supervisor_cocina") {
      listaEmpleados = supervisoresCocina;
    } else if (tipoEmpleado === "supervisor_salon") {
      listaEmpleados = supervisoresSalon;
    }
    
    const sedeNombre = sedes.find(s => s.id === sedeId)?.nombre || 'Desconocida';
    
    // Primero: buscar empleados que trabajaron hasta 8 PM o más en esa sede
    let disponibles = listaEmpleados.filter(e => empleadoTrabajaEnFecha(e.id, fecha, sedeId, true));
    
    // Fallback: si no hay con 8 PM, buscar cualquier empleado que trabajó en esa sede (sin importar hora)
    if (disponibles.length === 0) {
      disponibles = listaEmpleados.filter(e => empleadoTrabajaEnFecha(e.id, fecha, sedeId, false));
      if (disponibles.length > 0) {
        console.log(`[SecretariaSalud] FALLBACK: ${tipoEmpleado} que trabajaron en "${sedeNombre}" el ${fecha} (sin requisito 8PM):`, disponibles.map(e => e.nombre || `${e.nombres} ${e.apellidos}`));
      }
    }
    
    // Log de diagnóstico
    if (disponibles.length === 0 && listaEmpleados.length > 0) {
      console.log(`[SecretariaSalud] Sin ${tipoEmpleado} el ${fecha} en sede "${sedeNombre}". Total en lista: ${listaEmpleados.length}`);
    } else if (disponibles.length > 0) {
      console.log(`[SecretariaSalud] ${tipoEmpleado} disponibles el ${fecha} en "${sedeNombre}":`, disponibles.map(e => e.nombre || `${e.nombres} ${e.apellidos}`));
    }
    
    return disponibles;
  }, [cajeros, auxiliaresCocina, supervisoresCocina, supervisoresSalon, empleadoTrabajaEnFecha, sedes]);
  
  // Función para seleccionar supervisor automáticamente para el mes (NO depende de horarios, solo cargos/perfiles)
  const seleccionarSupervisorMes = useCallback((mes, anio, sedeId, tipoSupervisor, indicePlanilla = 0) => {
    // Usar directamente la lista de supervisores filtrados por cargo/perfil (sin verificar horarios)
    const listaSupers = tipoSupervisor === "supervisor_cocina" ? supervisoresCocina : supervisoresSalon;
    
    if (listaSupers.length > 0) {
      // Rotar entre supervisores usando mes + indicePlanilla como semilla
      // Esto hace que cada mes y cada planilla pueda tener un supervisor diferente
      const indice = (mes + indicePlanilla + anio) % listaSupers.length;
      const supervisor = listaSupers[indice];
      console.log(`[SecretariaSalud] Supervisor ${tipoSupervisor} seleccionado (rotación): ${supervisor.nombre || supervisor.nombres} (${supervisor.cargo || supervisor.perfil})`);
      return supervisor;
    }
    
    // Si no hay del tipo específico, intentar con el otro tipo de supervisor (comparten perfiles válidos)
    const otrosSupers = tipoSupervisor === "supervisor_cocina" ? supervisoresSalon : supervisoresCocina;
    if (otrosSupers.length > 0) {
      const indice = (mes + indicePlanilla + anio) % otrosSupers.length;
      console.log(`[SecretariaSalud] Usando supervisor alternativo (rotación): ${otrosSupers[indice].nombre || otrosSupers[indice].nombres}`);
      return otrosSupers[indice];
    }
    
    console.log(`[SecretariaSalud] No se encontró ningún supervisor para ${tipoSupervisor}`);
    return null;
  }, [supervisoresCocina, supervisoresSalon]);
  
  // Función para generar datos de planilla automáticamente (conservando datos existentes)
  const generarDatosPlanilla = useCallback((mes, anio, sedeId, config, datosExistentes = null) => {
    // Función auxiliar para obtener nombre completo del empleado
    const obtenerNombreEmpleado = (empleado) => {
      if (!empleado) return '';
      if (empleado.nombre) return empleado.nombre;
      if (empleado.nombres && empleado.apellidos) return `${empleado.nombres} ${empleado.apellidos}`;
      if (empleado.nombres) return empleado.nombres;
      return '';
    };
    
    const todosDias = obtenerDiasDelMes(anio, mes);
    
    // Calcular fecha límite (ayer) - usando fecha local para evitar problemas de zona horaria
    const hoy = new Date();
    const ayer = new Date(hoy);
    ayer.setDate(hoy.getDate() - 1);
    // Usar formato local en lugar de toISOString() que convierte a UTC
    const anioAyer = ayer.getFullYear();
    const mesAyer = String(ayer.getMonth() + 1).padStart(2, '0');
    const diaAyer = String(ayer.getDate()).padStart(2, '0');
    const fechaLimite = `${anioAyer}-${mesAyer}-${diaAyer}`;
    
    // Filtrar días: solo hasta ayer (no incluir hoy ni días futuros)
    const diasNuevos = todosDias.filter(d => d.fecha <= fechaLimite);
    
    console.log(`[SecretariaSalud] Generando planilla: ${diasNuevos.length} días hasta ${fechaLimite} de ${todosDias.length} totales`);
    
    const itemsCocinaDiario = config?.items_limpieza_cocina_diario || ITEMS_LIMPIEZA_COCINA_DIARIO_DEFAULT;
    const itemsCocinaSemanal = config?.items_limpieza_cocina_semanal || ITEMS_LIMPIEZA_COCINA_SEMANAL_DEFAULT;
    const itemsSalonDiario = config?.items_limpieza_salon_diario || ITEMS_LIMPIEZA_SALON_DIARIO_DEFAULT;
    const itemsSalonSemanal = config?.items_limpieza_salon_semanal || ITEMS_LIMPIEZA_SALON_SEMANAL_DEFAULT;
    const numCongeladores = config?.num_congeladores || 1;
    const numRefrigeradores = config?.num_refrigeradores || 1;
    const tieneTanque = config?.tiene_tanque_reserva || false;
    
    // Inicializar con datos existentes o vacíos
    const desinfeccionCocina = datosExistentes?.desinfeccionCocina || datosExistentes?.datos_desinfeccion_cocina || { diario: {}, semanal: {} };
    const desinfeccionSalon = datosExistentes?.desinfeccionSalon || datosExistentes?.datos_desinfeccion_salon || { diario: {}, semanal: {} };
    const temperatura = datosExistentes?.temperatura || datosExistentes?.datos_temperatura || { registros: [] };
    const tanqueReserva = datosExistentes?.tanqueReserva || datosExistentes?.datos_tanque_reserva || { registros: [] };
    
    // Asegurar que existan las estructuras
    if (!desinfeccionCocina.diario) desinfeccionCocina.diario = {};
    if (!desinfeccionCocina.semanal) desinfeccionCocina.semanal = {};
    if (!desinfeccionSalon.diario) desinfeccionSalon.diario = {};
    if (!desinfeccionSalon.semanal) desinfeccionSalon.semanal = {};
    if (!Array.isArray(temperatura.registros)) temperatura.registros = [];
    if (!Array.isArray(tanqueReserva.registros)) tanqueReserva.registros = [];
    
    // Generar datos de desinfección cocina (solo para días que no tengan datos válidos)
    itemsCocinaDiario.forEach(item => {
      if (!desinfeccionCocina.diario[item]) desinfeccionCocina.diario[item] = {};
      diasNuevos.forEach(d => {
        // Regenerar si no existe dato o si está vacío
        const datoActual = desinfeccionCocina.diario[item][d.dia];
        if (!datoActual || datoActual === '' || datoActual === '-') {
          // Solo usar empleados que trabajaron en esta sede hasta las 8 PM
          const disponibles = obtenerEmpleadosDisponibles(d.fecha, sedeId, "auxiliar_cocina");
          if (disponibles.length > 0) {
            const empleado = disponibles[Math.floor(Math.random() * disponibles.length)];
            desinfeccionCocina.diario[item][d.dia] = obtenerNombreEmpleado(empleado);
          }
          // Si no hay disponibles, dejar vacío (no mezclar empleados de otras sedes)
        }
      });
    });
    itemsCocinaSemanal.forEach(item => {
      if (!desinfeccionCocina.semanal[item]) desinfeccionCocina.semanal[item] = {};
      
      // Agrupar días por semana
      const diasPorSemana = {};
      diasNuevos.forEach(d => {
        const semana = Math.ceil(d.dia / 7);
        if (!diasPorSemana[semana]) diasPorSemana[semana] = [];
        diasPorSemana[semana].push(d);
      });
      
      // Procesar cada semana
      Object.keys(diasPorSemana).forEach(semanaKey => {
        const semana = parseInt(semanaKey);
        if (desinfeccionCocina.semanal[item][`semana_${semana}`]) return; // Ya existe
        
        const diasSemana = diasPorSemana[semana];
        let empleadoEncontrado = null;
        let diaAsignado = null;
        
        // Buscar primero en lunes, luego en cualquier día de la semana
        const diasOrdenados = [...diasSemana].sort((a, b) => {
          if (a.diaSemana === 1) return -1;
          if (b.diaSemana === 1) return 1;
          return a.dia - b.dia;
        });
        
        for (const d of diasOrdenados) {
          const disponibles = obtenerEmpleadosDisponibles(d.fecha, sedeId, "auxiliar_cocina");
          if (disponibles.length > 0) {
            empleadoEncontrado = disponibles[Math.floor(Math.random() * disponibles.length)];
            diaAsignado = d.dia;
            break;
          }
        }
        
        // Solo usar empleados que trabajaron en esta sede (no mezclar sedes)
        if (empleadoEncontrado && diaAsignado) {
          desinfeccionCocina.semanal[item][`semana_${semana}`] = {
            dia: diaAsignado,
            empleado: obtenerNombreEmpleado(empleadoEncontrado)
          };
        }
      });
    });
    
    // Generar datos de desinfección salón (solo para días que no tengan datos válidos)
    itemsSalonDiario.forEach(item => {
      if (!desinfeccionSalon.diario[item]) desinfeccionSalon.diario[item] = {};
      diasNuevos.forEach(d => {
        // Regenerar si no existe dato o si está vacío
        const datoActual = desinfeccionSalon.diario[item][d.dia];
        if (!datoActual || datoActual === '' || datoActual === '-') {
          // Solo usar empleados que trabajaron en esta sede hasta las 8 PM
          const disponibles = obtenerEmpleadosDisponibles(d.fecha, sedeId, "cajero");
          if (disponibles.length > 0) {
            const empleado = disponibles[Math.floor(Math.random() * disponibles.length)];
            desinfeccionSalon.diario[item][d.dia] = obtenerNombreEmpleado(empleado);
          }
          // Si no hay disponibles, dejar vacío (no mezclar empleados de otras sedes)
        }
      });
    });
    itemsSalonSemanal.forEach(item => {
      if (!desinfeccionSalon.semanal[item]) desinfeccionSalon.semanal[item] = {};
      
      // Agrupar días por semana
      const diasPorSemanaSalon = {};
      diasNuevos.forEach(d => {
        const semana = Math.ceil(d.dia / 7);
        if (!diasPorSemanaSalon[semana]) diasPorSemanaSalon[semana] = [];
        diasPorSemanaSalon[semana].push(d);
      });
      
      // Procesar cada semana
      Object.keys(diasPorSemanaSalon).forEach(semanaKey => {
        const semana = parseInt(semanaKey);
        if (desinfeccionSalon.semanal[item][`semana_${semana}`]) return; // Ya existe
        
        const diasSemana = diasPorSemanaSalon[semana];
        let empleadoEncontrado = null;
        let diaAsignado = null;
        
        // Buscar primero en lunes, luego en cualquier día de la semana
        const diasOrdenados = [...diasSemana].sort((a, b) => {
          if (a.diaSemana === 1) return -1;
          if (b.diaSemana === 1) return 1;
          return a.dia - b.dia;
        });
        
        for (const d of diasOrdenados) {
          const disponibles = obtenerEmpleadosDisponibles(d.fecha, sedeId, "cajero");
          if (disponibles.length > 0) {
            empleadoEncontrado = disponibles[Math.floor(Math.random() * disponibles.length)];
            diaAsignado = d.dia;
            break;
          }
        }
        
        // Solo usar empleados que trabajaron en esta sede (no mezclar sedes)
        if (empleadoEncontrado && diaAsignado) {
          desinfeccionSalon.semanal[item][`semana_${semana}`] = {
            dia: diaAsignado,
            empleado: obtenerNombreEmpleado(empleadoEncontrado)
          };
        }
      });
    });
    
    // Generar datos de temperatura (solo para días que no tengan datos)
    const diasYaRegistrados = new Set(temperatura.registros.map(r => r.dia));
    let tempBaseCongelacion = -12;
    let tempBaseRefrigeracion = 3;
    
    // Función auxiliar para generar equipos según configuración
    const generarEquipos = (horaBase) => {
      const equipos = [];
      // Agregar congeladores
      for (let i = 1; i <= numCongeladores; i++) {
        const minutoOffset = (i - 1) * 5;
        const [h, m] = horaBase.replace(/ [AP]M/, '').split(':').map(Number);
        const nuevosMinutos = m + minutoOffset;
        let horaEquipo = `${String(h + Math.floor(nuevosMinutos / 60)).padStart(2, '0')}:${String(nuevosMinutos % 60).padStart(2, '0')}`;
        if (horaBase.includes(' AM') || horaBase.includes(' PM')) {
          horaEquipo += horaBase.includes(' AM') ? ' AM' : ' PM';
        }
        equipos.push({
          tipo: "Congelación",
          numero: i,
          temperatura: generarTemperaturaEstable(tempBaseCongelacion, 2),
          hora: horaEquipo
        });
      }
      // Agregar refrigeradores
      for (let i = 1; i <= numRefrigeradores; i++) {
        const minutoOffset = (numCongeladores + i - 1) * 5;
        const [h, m] = horaBase.replace(/ [AP]M/, '').split(':').map(Number);
        const nuevosMinutos = m + minutoOffset;
        let horaEquipo = `${String(h + Math.floor(nuevosMinutos / 60)).padStart(2, '0')}:${String(nuevosMinutos % 60).padStart(2, '0')}`;
        if (horaBase.includes(' AM') || horaBase.includes(' PM')) {
          horaEquipo += horaBase.includes(' AM') ? ' AM' : ' PM';
        }
        equipos.push({
          tipo: "Refrigeración",
          numero: i,
          temperatura: generarTemperaturaEstable(tempBaseRefrigeracion, 1),
          hora: horaEquipo
        });
      }
      return equipos;
    };
    
    // Actualizar registros existentes: responsable vacío y ajustar equipos si cambió la configuración
    temperatura.registros.forEach(reg => {
      // Actualizar responsable si está vacío
      if (!reg.responsable || reg.responsable === '' || reg.responsable === '-') {
        const disponibles = obtenerEmpleadosDisponibles(reg.fecha, sedeId, "auxiliar_cocina");
        if (disponibles.length > 0) {
          reg.responsable = obtenerNombreEmpleado(disponibles[Math.floor(Math.random() * disponibles.length)]);
        }
      }
      
      // Verificar si la cantidad de equipos coincide con la configuración actual
      const congActuales = (reg.equipos || []).filter(e => e.tipo === "Congelación").length;
      const refriActuales = (reg.equipos || []).filter(e => e.tipo === "Refrigeración").length;
      
      if (congActuales !== numCongeladores || refriActuales !== numRefrigeradores) {
        // Regenerar equipos con la configuración correcta
        const horaBase = reg.hora || generarHoraAleatoria();
        reg.equipos = generarEquipos(horaBase);
      }
    });
    
    diasNuevos.forEach(d => {
      if (!diasYaRegistrados.has(d.dia)) {
        const horaBase = generarHoraAleatoria();
        // Solo usar empleados que trabajaron en esta sede hasta las 8 PM
        const disponibles = obtenerEmpleadosDisponibles(d.fecha, sedeId, "auxiliar_cocina");
        const responsable = disponibles.length > 0 
          ? obtenerNombreEmpleado(disponibles[Math.floor(Math.random() * disponibles.length)])
          : "";
        
        const registroDia = {
          dia: d.dia,
          fecha: d.fecha,
          hora: horaBase,
          responsable,
          equipos: generarEquipos(horaBase)
        };
        
        tempBaseCongelacion = generarTemperaturaEstable(tempBaseCongelacion, 0.5);
        tempBaseCongelacion = Math.max(-18, Math.min(-5, tempBaseCongelacion));
        tempBaseRefrigeracion = generarTemperaturaEstable(tempBaseRefrigeracion, 0.3);
        tempBaseRefrigeracion = Math.max(-2, Math.min(4, tempBaseRefrigeracion));
        
        temperatura.registros.push(registroDia);
      }
    });
    
    // Ordenar registros de temperatura por día
    temperatura.registros.sort((a, b) => a.dia - b.dia);
    
    // Generar datos de tanque de reserva (bimestral) - solo si no existe
    if (tieneTanque && tanqueReserva.registros.length === 0) {
      const bimestre = obtenerBimestre(mes);
      // Generar para el bimestre actual
      if (bimestre) {
        // Verificar si es el último mes del bimestre Y si ya pasó el último día
        const ultimoDia = ultimoDiaDelMes(anio, mes);
        const fechaUltimoDia = `${anio}-${String(mes).padStart(2, '0')}-${String(ultimoDia).padStart(2, '0')}`;
        const hoyISO = hoy.toISOString().split('T')[0];
        
        // Solo generar si ya pasó el último día del mes
        if (mes === bimestre.fin && fechaUltimoDia < hoyISO) {
          const disponibles = obtenerEmpleadosDisponibles(fechaUltimoDia, sedeId, "cajero");
          const responsable = disponibles.length > 0 
            ? obtenerNombreEmpleado(disponibles[Math.floor(Math.random() * disponibles.length)])
            : "";
          
          tanqueReserva.registros.push({
            bimestre: bimestre.nombre,
            fecha: fechaUltimoDia,
            responsable
          });
        }
      }
    }
    
    // ============================================
    // NUEVAS PLANILLAS: RESIDUOS, AGUA POTABLE, PLAGAS
    // ============================================
    
    // Inicializar datos existentes o vacíos para nuevas planillas
    const residuos = datosExistentes?.residuos || datosExistentes?.datos_residuos || { registros: {} };
    const aguaPotable = datosExistentes?.aguaPotable || datosExistentes?.datos_agua_potable || { registros: [] };
    const plagas = datosExistentes?.plagas || datosExistentes?.datos_plagas || { registros: {} };
    
    if (!residuos.registros) residuos.registros = {};
    if (!Array.isArray(aguaPotable.registros)) aguaPotable.registros = [];
    if (!plagas.registros) plagas.registros = {};
    
    // --- PLANILLA 1: CONTROL DE RESIDUOS ---
    // Por cada día, generar cantidades de bolsas (1-3) para cada tipo de residuo
    // Estructura: { registros: { [dia]: { aprovechables: 1-3, organicos: 1-3, noAprovechables: 1-3 } } }
    diasNuevos.forEach(d => {
      if (!residuos.registros[d.dia]) {
        // Generar patrón estable - usar semilla basada en día
        const semilla = d.dia % 7;
        // Los valores varían poco para crear un patrón mensual estable
        const baseAprovechables = 1 + (semilla % 2); // 1 o 2
        const baseOrganicos = 2 + ((d.dia + 1) % 2); // 2 o 3
        const baseNoAprovechables = 1 + ((d.dia + 2) % 3); // 1, 2 o 3
        
        residuos.registros[d.dia] = {
          aprovechables: Math.max(1, Math.min(3, baseAprovechables)),
          organicos: Math.max(1, Math.min(3, baseOrganicos)),
          noAprovechables: Math.max(1, Math.min(3, baseNoAprovechables))
        };
      }
    });
    
    // --- PLANILLA 2: CONTROL DE AGUA POTABLE ---
    // Por cada día, generar registro con hora, pH (6.5-9), cloro (0.3-2)
    // Estructura: { registros: [{ dia, fecha, hora, lugar:"Cocina", color:"C", olor:"C", sabor:"normal", ph:6.5-9, cloro:0.3-2, responsable }] }
    const diasYaRegistradosAgua = new Set(aguaPotable.registros.map(r => r.dia));
    
    diasNuevos.forEach(d => {
      if (!diasYaRegistradosAgua.has(d.dia)) {
        // Hora entre 11:30 AM y 3:30 PM 
        const horasBase = [11, 12, 13, 14, 15];
        const horaSeleccionada = horasBase[d.dia % horasBase.length];
        const minutosBase = [0, 15, 20, 30, 45];
        const minutos = minutosBase[(d.dia * 3) % minutosBase.length];
        // Si es 11, asegurar que sea >= 30 minutos
        const minutosFinales = horaSeleccionada === 11 ? Math.max(30, minutos) : minutos;
        // Si es 15, asegurar que sea <= 30 minutos
        const minutosAjustados = horaSeleccionada === 15 ? Math.min(30, minutosFinales) : minutosFinales;
        
        // Formatear hora en 12h AM/PM
        const hora12 = horaSeleccionada > 12 ? horaSeleccionada - 12 : horaSeleccionada;
        const periodo = horaSeleccionada >= 12 ? "PM" : "AM";
        const horaFormateada = `${hora12}:${String(minutosAjustados).padStart(2, '0')} ${periodo}`;
        
        // pH entre 6.5 y 9 - patrón estable
        const phBase = [6.8, 7.0, 7.2, 7.5, 7.8, 8.0, 8.2];
        const ph = phBase[d.dia % phBase.length];
        
        // Cloro entre 0.3 y 2 - patrón estable
        const cloroBase = [0.5, 0.8, 1.0, 1.2, 1.5, 1.8, 2.0, 2.1, 2.8];
        const cloro = cloroBase[d.dia % cloroBase.length];
        
        // Responsable: empleado que trabaja ese día en la sede
        const disponibles = obtenerEmpleadosDisponibles(d.fecha, sedeId, "auxiliar_cocina");
        const responsable = disponibles.length > 0
          ? obtenerNombreEmpleado(disponibles[Math.floor(Math.random() * disponibles.length)])
          : "";
        
        aguaPotable.registros.push({
          dia: d.dia,
          fecha: d.fecha,
          hora: horaFormateada,
          lugar: "Cocina",
          color: "C",
          olor: "C",
          sabor: "normal",
          ph: ph,
          cloro: cloro,
          responsable: responsable
        });
      }
    });
    
    // Ordenar registros de agua potable por día
    aguaPotable.registros.sort((a, b) => a.dia - b.dia);
    
    // --- PLANILLA 3: CONTROL DE PLAGAS Y ROEDORES ---
    // Por cada día, registrar "NO" para cada tipo de plaga
    // Estructura: { registros: { [dia]: { cucarachaAmericana:"NO", cucarachaGermanica:"NO", roedores:"NO", moscas:"NO", aves:"NO", aranas:"NO", area:"" } } }
    diasNuevos.forEach(d => {
      if (!plagas.registros[d.dia]) {
        plagas.registros[d.dia] = {
          cucarachaAmericana: "NO",
          cucarachaGermanica: "NO",
          roedores: "NO",
          moscas: "NO",
          aves: "NO",
          aranas: "NO",
          area: "" // Solo se llena si hay evidencia
        };
      }
    });
    
    return {
      desinfeccionCocina,
      desinfeccionSalon,
      temperatura,
      tanqueReserva,
      residuos,
      aguaPotable,
      plagas
    };
  }, [obtenerEmpleadosDisponibles]);
  
  // Función para generar/actualizar planilla del mes
  const generarPlanillaMes = useCallback(async (mes, anio, sedeId, forzar = false) => {
    if (!sedeId || !empresaId) {
      console.log("[SecretariaSalud] No se puede generar planilla: sedeId o empresaId faltante", { sedeId, empresaId });
      return null;
    }
    
    console.log(`[SecretariaSalud] Intentando generar planilla para ${mes}/${anio}, sede: ${sedeId}`);
    console.log(`[SecretariaSalud] Total horarios disponibles: ${horariosRepo?.length || 0}`);
    
    // Verificar si ya existe planilla
    const existente = planillas.find(p => 
      p.sede_id === sedeId && p.mes === mes && p.anio === anio
    );
    
    if (existente && existente.cerrado && !forzar) {
      console.log(`[SecretariaSalud] Planilla ya existe y está cerrada para ${mes}/${anio}`);
      return existente; // Ya cerrada, no modificar
    }
    
    // Calcular primer y último día del mes
    const primerDiaMes = `${anio}-${String(mes).padStart(2, '0')}-01`;
    const ultimoDiaMes = `${anio}-${String(mes).padStart(2, '0')}-${ultimoDiaDelMes(anio, mes)}`;
    
    // Verificar si hay horarios que cubran algún día de este mes
    // Un horario cubre el mes si: semanaInicio <= ultimoDiaMes AND semanaFin >= primerDiaMes
    const horariosDelMes = horariosRepo.filter(h => {
      if (!h.semanaInicio || !h.semanaFin) return false;
      return h.semanaInicio <= ultimoDiaMes && h.semanaFin >= primerDiaMes;
    });
    
    console.log(`[SecretariaSalud] Horarios que cubren ${mes}/${anio}:`, horariosDelMes.length);
    if (horariosDelMes.length > 0) {
      console.log(`[SecretariaSalud] Semanas: ${horariosDelMes.map(h => `${h.semanaInicio} - ${h.semanaFin}`).join(' | ')}`);
    }
    
    if (horariosDelMes.length === 0) {
      console.log(`[SecretariaSalud] No hay horarios para ${mes}/${anio}, no se genera planilla`);
      return null;
    }
    
    const config = configSedes.find(c => c.sede_id === sedeId) || {};
    
    // Pasar datos existentes para preservarlos
    const datosExistentes = existente ? {
      desinfeccionCocina: existente.datos_desinfeccion_cocina || { diario: {}, semanal: {} },
      desinfeccionSalon: existente.datos_desinfeccion_salon || { diario: {}, semanal: {} },
      temperatura: existente.datos_temperatura || { registros: [] },
      tanqueReserva: existente.datos_tanque_reserva || { registros: [] },
      residuos: existente.datos_residuos || { registros: {} },
      aguaPotable: existente.datos_agua_potable || { registros: [] },
      plagas: existente.datos_plagas || { registros: {} }
    } : null;
    
    const datos = generarDatosPlanilla(mes, anio, sedeId, config, datosExistentes);
    
    // Seleccionar supervisores automáticamente (solo si no existen en planilla existente)
    let supervisorCocina = existente?.supervisor_cocina_id 
      ? empleados.find(e => e.id === existente.supervisor_cocina_id) 
      : seleccionarSupervisorMes(mes, anio, sedeId, "supervisor_cocina");
    let supervisorSalon = existente?.supervisor_salon_id 
      ? empleados.find(e => e.id === existente.supervisor_salon_id) 
      : seleccionarSupervisorMes(mes, anio, sedeId, "supervisor_salon");
    
    // Si no se encontró supervisor, intentar directamente de las listas filtradas
    if (!supervisorCocina && supervisoresCocina.length > 0) {
      supervisorCocina = supervisoresCocina[0];
    }
    if (!supervisorSalon && supervisoresSalon.length > 0) {
      supervisorSalon = supervisoresSalon[0];
    }
    // Fallback cruzado
    if (!supervisorCocina && supervisoresSalon.length > 0) {
      supervisorCocina = supervisoresSalon[0];
    }
    if (!supervisorSalon && supervisoresCocina.length > 0) {
      supervisorSalon = supervisoresCocina[0];
    }
    
    // Función auxiliar para obtener nombre
    const getNombreSupervisor = (sup) => {
      if (!sup) return '';
      return sup.nombre || (sup.nombres && sup.apellidos ? `${sup.nombres} ${sup.apellidos}` : sup.nombres || '');
    };
    
    // Seleccionar revisores para las nuevas planillas (rotación por mes y por planilla)
    // indicePlanilla: 0=cocina, 1=salon, 2=temperatura, 3=tanque, 4=residuos, 5=agua, 6=plagas
    const revisorResiduos = existente?.revisor_residuos || getNombreSupervisor(seleccionarSupervisorMes(mes, anio, sedeId, "supervisor_cocina", 4));
    const revisorAguaPotable = existente?.revisor_agua_potable || getNombreSupervisor(seleccionarSupervisorMes(mes, anio, sedeId, "supervisor_cocina", 5));
    const revisorPlagas = existente?.revisor_plagas || getNombreSupervisor(seleccionarSupervisorMes(mes, anio, sedeId, "supervisor_salon", 6));
    
    const planilla = {
      id: existente?.id,
      sedeId,
      empresaId,
      mes,
      anio,
      supervisorCocinaId: supervisorCocina?.id || existente?.supervisor_cocina_id,
      supervisorSalonId: supervisorSalon?.id || existente?.supervisor_salon_id,
      supervisorCocinaNombre: existente?.supervisor_cocina_nombre || getNombreSupervisor(supervisorCocina),
      supervisorSalonNombre: existente?.supervisor_salon_nombre || getNombreSupervisor(supervisorSalon),
      // Revisores nuevas planillas
      revisorResiduos: revisorResiduos,
      revisorAguaPotable: revisorAguaPotable,
      revisorPlagas: revisorPlagas,
      datosDesinfeccionCocina: datos.desinfeccionCocina,
      datosDesinfeccionSalon: datos.desinfeccionSalon,
      datosTemperatura: datos.temperatura,
      datosTanqueReserva: datos.tanqueReserva,
      datosResiduos: datos.residuos,
      datosAguaPotable: datos.aguaPotable,
      datosPlagas: datos.plagas,
      cerrado: false,
      fechaCierre: null
    };
    
    const guardada = await guardarPlanillaSecretariaSalud(planilla);
    if (guardada) {
      setPlanillas(prev => {
        const sinEsta = prev.filter(p => !(p.sede_id === sedeId && p.mes === mes && p.anio === anio));
        return [...sinEsta, guardada];
      });
    }
    
    return guardada;
  }, [empresaId, planillas, horariosRepo, configSedes, generarDatosPlanilla, seleccionarSupervisorMes, supervisoresCocina, supervisoresSalon, empleados]);
  
  // Estado para saber si ya se verificaron los meses
  const [planillasGeneradas, setPlanillasGeneradas] = useState(false);
  
  // Verificar y cerrar meses anteriores al entrar
  useEffect(() => {
    const verificarMesesAnteriores = async () => {
      if (!sedeSeleccionada || !empresaId) {
        console.log("[SecretariaSalud] Esperando sedeSeleccionada y empresaId...");
        return;
      }
      
      if (!horariosRepo || horariosRepo.length === 0) {
        console.log("[SecretariaSalud] No hay horarios cargados aún, esperando...");
        return;
      }
      
      console.log("[SecretariaSalud] Iniciando verificación de meses...");
      console.log("[SecretariaSalud] Sede:", sedeSeleccionada);
      console.log("[SecretariaSalud] Total horarios:", horariosRepo.length);
      console.log("[SecretariaSalud] Planillas existentes:", planillas.length);
      
      const hoy = new Date();
      const mesActual = hoy.getMonth() + 1;
      const anioActual = hoy.getFullYear();
      
      console.log(`[SecretariaSalud] Fecha actual: ${mesActual}/${anioActual}`);
      
      // Generar desde enero 2026 hasta el mes anterior
      const mesesAGenerar = [];
      for (let a = 2026; a <= anioActual; a++) {
        const mesInicio = 1;
        const mesFin = a === anioActual ? mesActual - 1 : 12;
        for (let m = mesInicio; m <= mesFin; m++) {
          mesesAGenerar.push({ mes: m, anio: a });
        }
      }
      
      console.log("[SecretariaSalud] Meses a verificar:", mesesAGenerar.map(m => `${m.mes}/${m.anio}`).join(", "));
      
      for (const { mes, anio } of mesesAGenerar) {
        const existente = planillas.find(p => 
          p.sede_id === sedeSeleccionada && p.mes === mes && p.anio === anio
        );
        
        // Verificar si la planilla tiene datos vacíos en diario
        const tieneDataVacia = existente && (
          !existente.datos_desinfeccion_cocina?.diario ||
          Object.keys(existente.datos_desinfeccion_cocina?.diario || {}).length === 0 ||
          Object.values(existente.datos_desinfeccion_cocina?.diario || {}).every(item => 
            Object.keys(item || {}).length === 0 || Object.values(item || {}).every(v => !v || v === '' || v === '-')
          )
        );
        
        if (!existente || tieneDataVacia) {
          console.log(`[SecretariaSalud] Generando/regenerando planilla para ${mes}/${anio}...`);
          // Generar planilla (con forzar=true si tiene data vacía)
          const planilla = await generarPlanillaMes(mes, anio, sedeSeleccionada, tieneDataVacia);
          if (planilla) {
            // Cerrar automáticamente
            await guardarPlanillaSecretariaSalud({
              ...planilla,
              cerrado: true,
              fechaCierre: new Date().toISOString()
            });
            console.log(`[SecretariaSalud] Planilla ${mes}/${anio} generada y cerrada`);
          } else {
            console.log(`[SecretariaSalud] No se pudo generar planilla para ${mes}/${anio}`);
          }
        } else if (!existente.cerrado) {
          // Cerrar planilla existente no cerrada
          console.log(`[SecretariaSalud] Cerrando planilla existente de ${mes}/${anio}`);
          await guardarPlanillaSecretariaSalud({
            ...existente,
            cerrado: true,
            fechaCierre: new Date().toISOString()
          });
        }
      }
      
      // Generar planilla del mes actual si no existe
      const planillaActualExiste = planillas.find(p => 
        p.sede_id === sedeSeleccionada && p.mes === mesActual && p.anio === anioActual
      );
      if (!planillaActualExiste) {
        console.log(`[SecretariaSalud] Generando planilla del mes actual ${mesActual}/${anioActual}...`);
        await generarPlanillaMes(mesActual, anioActual, sedeSeleccionada);
      }
      
      setPlanillasGeneradas(true);
      console.log("[SecretariaSalud] Verificación de meses completada");
    };
    
    verificarMesesAnteriores();
  }, [sedeSeleccionada, empresaId, horariosRepo, planillas, generarPlanillaMes]);
  
  // Función para regenerar planilla manualmente
  const regenerarPlanillaActual = useCallback(async () => {
    if (!sedeSeleccionada || !empresaId) {
      alert("Seleccione una sede primero");
      return;
    }
    
    setCargando(true);
    console.log(`[SecretariaSalud] Regenerando planilla ${mesSeleccionado}/${anioSeleccionado} manualmente...`);
    
    const planilla = await generarPlanillaMes(mesSeleccionado, anioSeleccionado, sedeSeleccionada, true);
    if (planilla) {
      alert("Planilla generada correctamente");
      // Recargar planillas
      const nuevasPlanillas = await cargarPlanillasSecretariaSalud(empresaId, sedeSeleccionada);
      setPlanillas(nuevasPlanillas);
    } else {
      alert("No se pudo generar la planilla. Verifique que existan horarios para este mes.");
    }
    setCargando(false);
  }, [sedeSeleccionada, empresaId, mesSeleccionado, anioSeleccionado, generarPlanillaMes]);
  
  // Calcular horarios disponibles para el mes seleccionado
  const horariosDelMesSeleccionado = useMemo(() => {
    if (!horariosRepo || horariosRepo.length === 0) return [];
    
    const primerDiaMes = `${anioSeleccionado}-${String(mesSeleccionado).padStart(2, '0')}-01`;
    const ultimoDia = ultimoDiaDelMes(anioSeleccionado, mesSeleccionado);
    const ultimoDiaMes = `${anioSeleccionado}-${String(mesSeleccionado).padStart(2, '0')}-${ultimoDia}`;
    
    return horariosRepo.filter(h => {
      if (!h.semanaInicio || !h.semanaFin) return false;
      return h.semanaInicio <= ultimoDiaMes && h.semanaFin >= primerDiaMes;
    });
  }, [horariosRepo, mesSeleccionado, anioSeleccionado]);
  
  // Obtener planilla actual
  useEffect(() => {
    if (!sedeSeleccionada) {
      setPlanillaActual(null);
      return;
    }
    
    const planilla = planillas.find(p => 
      p.sede_id === sedeSeleccionada && p.mes === mesSeleccionado && p.anio === anioSeleccionado
    );
    setPlanillaActual(planilla || null);
  }, [sedeSeleccionada, mesSeleccionado, anioSeleccionado, planillas]);
  
  // Obtener supervisor de la planilla actual
  const supervisorCocinaActual = useMemo(() => {
    if (!planillaActual?.supervisor_cocina_id) return null;
    return empleados.find(e => e.id === planillaActual.supervisor_cocina_id);
  }, [planillaActual, empleados]);
  
  const supervisorSalonActual = useMemo(() => {
    if (!planillaActual?.supervisor_salon_id) return null;
    return empleados.find(e => e.id === planillaActual.supervisor_salon_id);
  }, [planillaActual, empleados]);
  
  // Manejar subida de archivo
  const handleSubirArchivo = async () => {
    if (!nuevoArchivo.nombre || !nuevoArchivo.archivo || !sedeSeleccionada) {
      alert("Debe ingresar un nombre y seleccionar un archivo");
      return;
    }
    
    setCargando(true);
    const resultado = await subirArchivoSecretariaSalud(
      nuevoArchivo.archivo,
      nuevoArchivo.nombre,
      sedeSeleccionada,
      empresaId
    );
    
    if (resultado) {
      setArchivos(prev => [resultado, ...prev]);
      setNuevoArchivo({ nombre: "", archivo: null });
      alert("Archivo subido correctamente");
    } else {
      alert("Error al subir el archivo");
    }
    setCargando(false);
  };
  
  // Manejar eliminación de archivo
  const handleEliminarArchivo = async (archivo) => {
    if (!confirm(`¿Está seguro de eliminar el archivo "${archivo.nombre}"?`)) return;
    
    setCargando(true);
    const exito = await eliminarArchivoSecretariaSalud(archivo.id, archivo.archivo_path);
    if (exito) {
      setArchivos(prev => prev.filter(a => a.id !== archivo.id));
      alert("Archivo eliminado correctamente");
    } else {
      alert("Error al eliminar el archivo");
    }
    setCargando(false);
  };
  
  // Manejar registro de trampa de grasa
  const handleGuardarTrampa = async () => {
    if (!nuevaTrampa.fecha || !nuevaTrampa.proveedor || !sedeSeleccionada) {
      alert("Debe completar todos los campos");
      return;
    }
    
    setCargando(true);
    const resultado = await guardarTrampaGrasa({
      sedeId: sedeSeleccionada,
      empresaId,
      fecha: nuevaTrampa.fecha,
      proveedor: nuevaTrampa.proveedor
    });
    
    if (resultado) {
      setTrampaGrasa(prev => [resultado, ...prev]);
      setNuevaTrampa({ fecha: "", proveedor: "" });
      alert("Registro guardado correctamente");
    } else {
      alert("Error al guardar el registro");
    }
    setCargando(false);
  };
  
  // Manejar guardado de configuración
  const handleGuardarConfig = async () => {
    if (!configEditando || !sedeSeleccionada) return;
    
    setCargando(true);
    const exito = await guardarConfigSecretariaSalud({
      ...configEditando,
      sedeId: sedeSeleccionada,
      empresaId
    });
    
    if (exito) {
      const configs = await cargarConfigSecretariaSalud(empresaId);
      setConfigSedes(configs);
      setConfigEditando(null);
      alert("Configuración guardada correctamente");
    } else {
      alert("Error al guardar la configuración");
    }
    setCargando(false);
  };
  
  // Función para imprimir planilla
  const imprimirPlanilla = () => {
    if (!planillaActual) {
      alert("No hay planilla para imprimir");
      return;
    }
    
    // Crear contenido HTML para impresión
    const contenido = document.getElementById("planilla-contenido");
    if (!contenido) return;
    
    const ventana = window.open('', '_blank');
    ventana.document.write(`
      <html>
        <head>
          <title>Planilla Secretaría de Salud - ${nombreSedeActual} - ${mesSeleccionado}/${anioSeleccionado}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .logo { width: 80px; height: 80px; border-radius: 50%; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 10px; }
            th, td { border: 1px solid #333; padding: 4px; text-align: center; }
            th { background: #f0f0f0; }
            .titulo { font-size: 16px; font-weight: bold; margin: 10px 0; }
            .subtitulo { font-size: 12px; margin: 5px 0; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          ${contenido.innerHTML}
        </body>
      </html>
    `);
    ventana.document.close();
    ventana.print();
  };
  
  // Función para preparar e imprimir múltiples meses
  const prepararImpresionMultiple = () => {
    // Calcular los meses entre inicio y fin
    const mesesAImprimir = [];
    let anioActual = anioInicioImpresion;
    let mesActual = mesInicioImpresion;
    
    while (anioActual < anioFinImpresion || (anioActual === anioFinImpresion && mesActual <= mesFinImpresion)) {
      const planilla = planillas.find(p => 
        p.sede_id === sedeSeleccionada && p.mes === mesActual && p.anio === anioActual
      );
      
      if (planilla) {
        mesesAImprimir.push({
          mes: mesActual,
          anio: anioActual,
          planilla,
          trampaGrasaMes: trampaGrasa.filter(t => {
            if (t.sede_id !== sedeSeleccionada) return false;
            const [a, m] = t.fecha.split('-').map(Number);
            return a === anioActual && m === mesActual;
          })
        });
      }
      
      mesActual++;
      if (mesActual > 12) {
        mesActual = 1;
        anioActual++;
      }
    }
    
    if (mesesAImprimir.length === 0) {
      alert("No hay planillas generadas en el rango de meses seleccionado");
      return;
    }
    
    setPlanillasParaImprimir(mesesAImprimir);
    setMostrarModalImpresion(false);
    setVistaImpresion(true);
  };
  
  // Función para generar HTML de una planilla completa
  const generarHTMLPlanillaCompleta = (datosImpresion) => {
    const { mes, anio, planilla, trampaGrasaMes: trampasMes } = datosImpresion;
    const nombreMes = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"][mes - 1];
    
    const diasDelMes = obtenerDiasDelMes(anio, mes);
    const config = configSedes.find(c => c.sede_id === sedeSeleccionada) || {};
    
    const itemsCocinaDiario = config.items_limpieza_cocina_diario || ITEMS_LIMPIEZA_COCINA_DIARIO_DEFAULT;
    const itemsCocinaSemanal = config.items_limpieza_cocina_semanal || ITEMS_LIMPIEZA_COCINA_SEMANAL_DEFAULT;
    const itemsSalonDiario = config.items_limpieza_salon_diario || ITEMS_LIMPIEZA_SALON_DIARIO_DEFAULT;
    const itemsSalonSemanal = config.items_limpieza_salon_semanal || ITEMS_LIMPIEZA_SALON_SEMANAL_DEFAULT;
    
    // Función de fallback para obtener nombre de supervisor
    const obtenerSupervisor = (tipo) => {
      const nombreGuardado = tipo === "cocina" ? planilla.supervisor_cocina_nombre : planilla.supervisor_salon_nombre;
      if (nombreGuardado && nombreGuardado !== "No asignado" && nombreGuardado.trim() !== '') return nombreGuardado;
      
      // Buscar en supervisores válidos
      const listaSupervisores = tipo === "cocina" ? supervisoresCocina : supervisoresSalon;
      if (listaSupervisores.length > 0) {
        const sup = listaSupervisores[0];
        return sup.nombre || `${sup.nombres || ''} ${sup.apellidos || ''}`.trim();
      }
      
      // Buscar en el otro tipo de supervisor (tienen los mismos perfiles válidos)
      const otrosSupers = tipo === "cocina" ? supervisoresSalon : supervisoresCocina;
      if (otrosSupers.length > 0) {
        const sup = otrosSupers[0];
        return sup.nombre || `${sup.nombres || ''} ${sup.apellidos || ''}`.trim();
      }
      
      // No hay supervisores válidos disponibles
      return "Supervisor";
    };
    
    const supervisorCocina = obtenerSupervisor("cocina");
    const supervisorSalon = obtenerSupervisor("salon");
    
    // Función para generar encabezado de cada planilla
    const generarEncabezado = (titulo) => `
      <div class="planilla-header">
        <div class="logo-container">
          <img src="${LOGO_BIGBURGUER}" alt="Logo" />
        </div>
        <div class="header-info">
          <h1>${titulo}</h1>
          <h2>${nombreSedeActual}</h2>
          <p>${nombreMes} ${anio}</p>
        </div>
      </div>
    `;
    
    let html = `
      <div class="pagina">
        <!-- DESINFECCIÓN COCINA -->
        <div class="seccion">
          ${generarEncabezado("DESINFECCIÓN Y LIMPIEZA - COCINA")}
          
          <h4>PERIODICIDAD DIARIA</h4>
          <table>
            <thead>
              <tr>
                <th style="min-width: 80px; font-weight: 900;">ÁREA</th>
                ${diasDelMes.map(d => `<th style="font-weight: 900;">${d.dia}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${itemsCocinaDiario.map(item => `
                <tr>
                  <td style="text-align: left; font-weight: 900;">${item.toUpperCase()}</td>
                  ${diasDelMes.map(d => {
                    const nombre = planilla.datos_desinfeccion_cocina?.diario?.[item]?.[d.dia] || '';
                    return `<td style="font-weight: 900;">${nombre.split(' ')[0] || '-'}</td>`;
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <h4>PERIODICIDAD SEMANAL</h4>
          <table style="max-width: 500px;">
            <thead>
              <tr>
                <th style="font-weight: 900;">ÁREA</th>
                <th style="font-weight: 900;">SEM 1</th>
                <th style="font-weight: 900;">SEM 2</th>
                <th style="font-weight: 900;">SEM 3</th>
                <th style="font-weight: 900;">SEM 4</th>
                <th style="font-weight: 900;">SEM 5</th>
              </tr>
            </thead>
            <tbody>
              ${itemsCocinaSemanal.map(item => `
                <tr>
                  <td style="text-align: left; font-weight: 900;">${item.toUpperCase()}</td>
                  ${[1,2,3,4,5].map(s => {
                    const dato = planilla.datos_desinfeccion_cocina?.semanal?.[item]?.[`semana_${s}`];
                    return `<td style="font-weight: 900;">${dato ? `${dato.empleado?.split(' ')[0] || ''} (${dato.dia})` : '-'}</td>`;
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          ${trampasMes.length > 0 ? `
            <h4>LIMPIEZA DE TRAMPA DE GRASA</h4>
            <table style="max-width: 400px;">
              <thead>
                <tr>
                  <th style="font-weight: 900;">FECHA</th>
                  <th style="font-weight: 900;">PROVEEDOR</th>
                </tr>
              </thead>
              <tbody>
                ${trampasMes.map(t => `
                  <tr>
                    <td style="font-weight: 900;">${t.fecha}</td>
                    <td style="font-weight: 900;">${t.proveedor}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : ''}
          
          <div class="firma">
            <div class="linea-firma"></div>
            <p><strong>Revisó: ${supervisorCocina}</strong></p>
          </div>
        </div>
        
        <div class="page-break"></div>
        
        <!-- DESINFECCIÓN SALÓN -->
        <div class="seccion">
          ${generarEncabezado("DESINFECCIÓN Y LIMPIEZA - SALÓN")}
          
          <h4>PERIODICIDAD DIARIA</h4>
          <table>
            <thead>
              <tr>
                <th style="min-width: 80px; font-weight: 900;">ÁREA</th>
                ${diasDelMes.map(d => `<th style="font-weight: 900;">${d.dia}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${itemsSalonDiario.map(item => `
                <tr>
                  <td style="text-align: left; font-weight: 900;">${item.toUpperCase()}</td>
                  ${diasDelMes.map(d => {
                    const nombre = planilla.datos_desinfeccion_salon?.diario?.[item]?.[d.dia] || '';
                    return `<td style="font-weight: 900;">${nombre.split(' ')[0] || '-'}</td>`;
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <h4>PERIODICIDAD SEMANAL</h4>
          <table style="max-width: 500px;">
            <thead>
              <tr>
                <th style="font-weight: 900;">ÁREA</th>
                <th style="font-weight: 900;">SEM 1</th>
                <th style="font-weight: 900;">SEM 2</th>
                <th style="font-weight: 900;">SEM 3</th>
                <th style="font-weight: 900;">SEM 4</th>
                <th style="font-weight: 900;">SEM 5</th>
              </tr>
            </thead>
            <tbody>
              ${itemsSalonSemanal.map(item => `
                <tr>
                  <td style="text-align: left; font-weight: 900;">${item.toUpperCase()}</td>
                  ${[1,2,3,4,5].map(s => {
                    const dato = planilla.datos_desinfeccion_salon?.semanal?.[item]?.[`semana_${s}`];
                    return `<td style="font-weight: 900;">${dato ? `${dato.empleado?.split(' ')[0] || ''} (${dato.dia})` : '-'}</td>`;
                  }).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="firma">
            <div class="linea-firma"></div>
            <p><strong>Revisó: ${supervisorSalon}</strong></p>
          </div>
        </div>
        
        <div class="page-break"></div>
        
        <!-- CONTROL DE TEMPERATURA -->
        <div class="seccion">
          ${generarEncabezado("CONTROL DE TEMPERATURA")}
          
          <table>
            <thead>
              <tr>
                <th style="font-weight: 900;">DÍA</th>
                <th style="font-weight: 900;">HORA</th>
                ${Array.from({ length: config.num_congeladores || 1 }, (_, i) => `<th style="font-weight: 900;">CONG. ${i+1} (°C)</th>`).join('')}
                ${Array.from({ length: config.num_refrigeradores || 1 }, (_, i) => `<th style="font-weight: 900;">REFRI. ${i+1} (°C)</th>`).join('')}
                <th style="font-weight: 900;">RESPONSABLE</th>
              </tr>
            </thead>
            <tbody>
              ${(planilla.datos_temperatura?.registros || []).map(reg => {
                const congeladores = reg.equipos?.filter(e => e.tipo === "Congelación") || [];
                const refrigeradores = reg.equipos?.filter(e => e.tipo === "Refrigeración") || [];
                const numCong = config.num_congeladores || 1;
                const numRefri = config.num_refrigeradores || 1;
                return `
                <tr>
                  <td style="font-weight: 900;">${reg.dia}</td>
                  <td style="font-weight: 900;">${reg.hora}</td>
                  ${Array.from({ length: numCong }, (_, i) => {
                    const eq = congeladores[i];
                    const temp = eq?.temperatura;
                    const colorClass = temp !== undefined ? (temp < -5 ? 'temp-ok' : 'temp-bad') : '';
                    return `<td class="${colorClass}" style="font-weight: 900;">${temp !== undefined ? temp : '-'}</td>`;
                  }).join('')}
                  ${Array.from({ length: numRefri }, (_, i) => {
                    const eq = refrigeradores[i];
                    const temp = eq?.temperatura;
                    const colorClass = temp !== undefined ? (temp <= 4 && temp >= -2 ? 'temp-ok' : 'temp-bad') : '';
                    return `<td class="${colorClass}" style="font-weight: 900;">${temp !== undefined ? temp : '-'}</td>`;
                  }).join('')}
                  <td style="font-weight: 900;">${reg.responsable?.split(' ')[0] || '-'}</td>
                </tr>
              `}).join('')}
            </tbody>
          </table>
          
          <div class="firma">
            <div class="linea-firma"></div>
            <p><strong>Revisó: ${supervisorCocina}</strong></p>
          </div>
        </div>
        
        ${config.tiene_tanque_reserva && planilla.datos_tanque_reserva?.registros?.length > 0 ? `
          <div class="page-break"></div>
          
          <!-- TANQUE DE RESERVA -->
          <div class="seccion">
            ${generarEncabezado("ASEO TANQUE DE RESERVA")}
            
            <table style="max-width: 500px;">
              <thead>
                <tr>
                  <th style="font-weight: 900;">BIMESTRE</th>
                  <th style="font-weight: 900;">FECHA</th>
                  <th style="font-weight: 900;">RESPONSABLE</th>
                </tr>
              </thead>
              <tbody>
                ${planilla.datos_tanque_reserva.registros.map(reg => `
                  <tr>
                    <td style="font-weight: 900;">${reg.bimestre}</td>
                    <td style="font-weight: 900;">${reg.fecha}</td>
                    <td style="font-weight: 900;">${reg.responsable || '-'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <div class="firma">
              <div class="linea-firma"></div>
              <p><strong>Revisó: ${supervisorSalon}</strong></p>
            </div>
          </div>
        ` : ''}
        
        <div class="page-break"></div>
        
        <!-- CONTROL DE RESIDUOS -->
        <div class="seccion">
          ${generarEncabezado("REGISTRO DIARIO DE CONTROL DE RESIDUOS")}
          
          <table>
            <thead>
              <tr>
                <th style="font-weight: 900;">DÍA</th>
                <th style="font-weight: 900;">APROVECHABLES</th>
                <th style="font-weight: 900;">ORGÁNICOS</th>
                <th style="font-weight: 900;">NO APROVECHABLES</th>
              </tr>
            </thead>
            <tbody>
              ${diasDelMes.map(d => {
                const regRes = planilla.datos_residuos?.registros?.[d.dia] || {};
                return `
                  <tr>
                    <td style="font-weight: 900;">${d.dia}</td>
                    <td style="font-weight: 900;">${regRes.aprovechables || '-'}</td>
                    <td style="font-weight: 900;">${regRes.organicos || '-'}</td>
                    <td style="font-weight: 900;">${regRes.noAprovechables || '-'}</td>
                  </tr>
                `;
              }).join('')}
              <tr style="background: #e5e7eb;">
                <td style="font-weight: 900;">TOTAL</td>
                <td style="font-weight: 900;">${Object.values(planilla.datos_residuos?.registros || {}).reduce((sum, r) => sum + (r.aprovechables || 0), 0)}</td>
                <td style="font-weight: 900;">${Object.values(planilla.datos_residuos?.registros || {}).reduce((sum, r) => sum + (r.organicos || 0), 0)}</td>
                <td style="font-weight: 900;">${Object.values(planilla.datos_residuos?.registros || {}).reduce((sum, r) => sum + (r.noAprovechables || 0), 0)}</td>
              </tr>
            </tbody>
          </table>
          
          <div class="firma">
            <div class="linea-firma"></div>
            <p><strong>Revisó: ${planilla.revisor_residuos || supervisorCocina}</strong></p>
          </div>
        </div>
        
        <div class="page-break"></div>
        
        <!-- CONTROL DE AGUA POTABLE -->
        <div class="seccion">
          ${generarEncabezado("REGISTRO DE CONTROL DE AGUA POTABLE")}
          
          <table>
            <thead>
              <tr>
                <th style="font-weight: 900;">DÍA</th>
                <th style="font-weight: 900;">HORA</th>
                <th style="font-weight: 900;">LUGAR</th>
                <th style="font-weight: 900;">COLOR</th>
                <th style="font-weight: 900;">OLOR</th>
                <th style="font-weight: 900;">SABOR</th>
                <th style="font-weight: 900;">PH</th>
                <th style="font-weight: 900;">CLORO</th>
                <th style="font-weight: 900;">RESPONSABLE</th>
              </tr>
            </thead>
            <tbody>
              ${(() => {
                const registrosAgua = planilla.datos_agua_potable?.registros || [];
                if (registrosAgua.length > 0) {
                  return registrosAgua.map(reg => `
                    <tr>
                      <td style="font-weight: 900;">${reg.dia}</td>
                      <td style="font-weight: 900;">${reg.hora || '-'}</td>
                      <td style="font-weight: 900;">${(reg.lugar || 'COCINA').toUpperCase()}</td>
                      <td style="font-weight: 900;">${(reg.color || 'C').toUpperCase()}</td>
                      <td style="font-weight: 900;">${(reg.olor || 'C').toUpperCase()}</td>
                      <td style="font-weight: 900;">${(reg.sabor || 'NORMAL').toUpperCase()}</td>
                      <td style="font-weight: 900;">${reg.ph || '-'}</td>
                      <td style="font-weight: 900;">${reg.cloro || '-'}</td>
                      <td style="font-weight: 900;">${reg.responsable?.split(' ')[0] || '-'}</td>
                    </tr>
                  `).join('');
                } else {
                  return diasDelMes.map(d => `
                    <tr>
                      <td style="font-weight: 900;">${d.dia}</td>
                      <td style="font-weight: 900;">-</td>
                      <td style="font-weight: 900;">COCINA</td>
                      <td style="font-weight: 900;">C</td>
                      <td style="font-weight: 900;">C</td>
                      <td style="font-weight: 900;">NORMAL</td>
                      <td style="font-weight: 900;">-</td>
                      <td style="font-weight: 900;">-</td>
                      <td style="font-weight: 900;">-</td>
                    </tr>
                  `).join('');
                }
              })()}
            </tbody>
          </table>
          
          <div class="firma">
            <div class="linea-firma"></div>
            <p><strong>Revisó: ${planilla.revisor_agua_potable || supervisorCocina}</strong></p>
          </div>
        </div>
        
        <div class="page-break"></div>
        
        <!-- CONTROL DE PLAGAS -->
        <div class="seccion">
          ${generarEncabezado("REGISTRO DE CONTROL DE PLAGAS Y ROEDORES")}
          
          <table style="font-size: 9px;">
            <thead>
              <tr>
                <th style="font-weight: 900;">DÍA</th>
                <th style="font-weight: 900;">CUC. AMERICANA</th>
                <th style="font-weight: 900;">CUC. GERMÁNICA</th>
                <th style="font-weight: 900;">ROEDORES</th>
                <th style="font-weight: 900;">MOSCAS</th>
                <th style="font-weight: 900;">AVES</th>
                <th style="font-weight: 900;">ARAÑAS</th>
                <th style="font-weight: 900;">ÁREA</th>
              </tr>
            </thead>
            <tbody>
              ${diasDelMes.map(d => {
                const regPlag = planilla.datos_plagas?.registros?.[d.dia] || {};
                return `
                  <tr>
                    <td style="font-weight: 900;">${d.dia}</td>
                    <td style="font-weight: 900; color: ${regPlag.cucarachaAmericana === 'SÍ' ? '#dc2626' : '#059669'};">${regPlag.cucarachaAmericana || 'NO'}</td>
                    <td style="font-weight: 900; color: ${regPlag.cucarachaGermanica === 'SÍ' ? '#dc2626' : '#059669'};">${regPlag.cucarachaGermanica || 'NO'}</td>
                    <td style="font-weight: 900; color: ${regPlag.roedores === 'SÍ' ? '#dc2626' : '#059669'};">${regPlag.roedores || 'NO'}</td>
                    <td style="font-weight: 900; color: ${regPlag.moscas === 'SÍ' ? '#dc2626' : '#059669'};">${regPlag.moscas || 'NO'}</td>
                    <td style="font-weight: 900; color: ${regPlag.aves === 'SÍ' ? '#dc2626' : '#059669'};">${regPlag.aves || 'NO'}</td>
                    <td style="font-weight: 900; color: ${regPlag.aranas === 'SÍ' ? '#dc2626' : '#059669'};">${regPlag.aranas || 'NO'}</td>
                    <td style="font-weight: 900;">${regPlag.area || '-'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          <div class="firma">
            <div class="linea-firma"></div>
            <p><strong>Revisó: ${planilla.revisor_plagas || supervisorSalon}</strong></p>
          </div>
        </div>
      </div>
    `;
    
    return html;
  };
  
  // Función para imprimir múltiples meses
  const imprimirMultiplesMeses = () => {
    if (planillasParaImprimir.length === 0) return;
    
    const contenidoHTML = planillasParaImprimir.map(p => generarHTMLPlanillaCompleta(p)).join('<div class="page-break"></div>');
    
    const ventana = window.open('', '_blank');
    ventana.document.write(`
      <html>
        <head>
          <title>Planillas - ${nombreSedeActual}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #000000; font-weight: bold; }
            .planilla-header { 
              display: flex; 
              align-items: center; 
              gap: 20px; 
              margin-bottom: 15px; 
              border-bottom: 3px solid #000000; 
              padding-bottom: 10px; 
            }
            .planilla-header .logo-container { width: 80px; flex-shrink: 0; }
            .planilla-header .logo-container img { width: 100%; height: auto; }
            .planilla-header .header-info { flex: 1; }
            .planilla-header .header-info h1 { margin: 0; font-size: 18px; font-weight: 900; color: #000000; }
            .planilla-header .header-info h2 { margin: 3px 0; font-size: 15px; color: #000000; font-weight: bold; }
            .planilla-header .header-info p { margin: 2px 0; font-size: 13px; color: #000000; font-weight: bold; }
            .seccion { margin-bottom: 25px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 10px; color: #000000; font-weight: bold; }
            th, td { border: 1px solid #000000; padding: 4px; text-align: center; color: #000000; font-weight: bold; }
            th { background: #d0d0d0; font-weight: 900; color: #000000; }
            h4 { margin: 12px 0 6px; font-size: 12px; color: #000000; font-weight: 900; }
            .firma { margin-top: 20px; text-align: center; max-width: 300px; }
            .linea-firma { border-bottom: 2px solid #000000; height: 30px; margin-bottom: 5px; }
            .firma p { margin: 0; font-size: 12px; color: #000000; font-weight: bold; }
            .page-break { page-break-after: always; }
            .pagina { margin-bottom: 20px; }
            .temp-ok { color: #000000; font-weight: 900; }
            .temp-bad { color: #000000; font-weight: 900; text-decoration: underline; }
            @media print {
              body { margin: 10px; color: #000000; font-weight: bold; }
              .page-break { page-break-after: always; }
              * { color: #000000 !important; font-weight: bold !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              th { font-weight: 900 !important; }
            }
          </style>
        </head>
        <body>
          ${contenidoHTML}
        </body>
      </html>
    `);
    ventana.document.close();
    ventana.print();
  };
  
  // Obtener datos de trampa de grasa para el mes seleccionado
  const trampaGrasaMes = useMemo(() => {
    if (!sedeSeleccionada) return [];
    return trampaGrasa.filter(t => {
      if (t.sede_id !== sedeSeleccionada) return false;
      const [anio, mes] = t.fecha.split('-').map(Number);
      return anio === anioSeleccionado && mes === mesSeleccionado;
    });
  }, [trampaGrasa, sedeSeleccionada, mesSeleccionado, anioSeleccionado]);
  
  // Renderizar tabla de desinfección
  const renderTablaDesinfeccion = (tipo) => {
    if (!planillaActual) {
      return (
        <div style={{ textAlign: 'center', padding: 30, background: '#fef3c7', borderRadius: 8 }}>
          <p style={{ fontSize: 16, color: '#92400e', margin: 0 }}>⚠️ No hay planilla generada para este mes</p>
          <p style={{ fontSize: 13, color: '#a16207', marginTop: 8 }}>
            {horariosDelMesSeleccionado.length === 0 
              ? "No hay horarios registrados para este mes. Primero debe crear horarios en la sección de Horarios."
              : `Hay ${horariosDelMesSeleccionado.length} horario(s) disponibles. Presione "Generar Planilla" para crear la planilla.`
            }
          </p>
        </div>
      );
    }
    
    const datos = tipo === "cocina" 
      ? planillaActual.datos_desinfeccion_cocina 
      : planillaActual.datos_desinfeccion_salon;
    
    if (!datos) return <p>No hay datos de desinfección</p>;
    
    const itemsDiario = tipo === "cocina" 
      ? (configSedeActual?.items_limpieza_cocina_diario || ITEMS_LIMPIEZA_COCINA_DIARIO_DEFAULT)
      : (configSedeActual?.items_limpieza_salon_diario || ITEMS_LIMPIEZA_SALON_DIARIO_DEFAULT);
    
    const itemsSemanal = tipo === "cocina"
      ? (configSedeActual?.items_limpieza_cocina_semanal || ITEMS_LIMPIEZA_COCINA_SEMANAL_DEFAULT)
      : (configSedeActual?.items_limpieza_salon_semanal || ITEMS_LIMPIEZA_SALON_SEMANAL_DEFAULT);
    
    const dias = obtenerDiasDelMes(anioSeleccionado, mesSeleccionado);
    const supervisor = tipo === "cocina" ? supervisorCocinaActual : supervisorSalonActual;
    const nombreSupervisorGuardado = tipo === "cocina" 
      ? planillaActual?.supervisor_cocina_nombre
      : planillaActual?.supervisor_salon_nombre;
    
    // Función para obtener nombre de supervisor con fallback
    const obtenerNombreReviso = () => {
      // 1. Nombre guardado en planilla
      if (nombreSupervisorGuardado) return nombreSupervisorGuardado;
      
      // 2. Supervisor actual encontrado por ID
      if (supervisor?.nombre) return supervisor.nombre;
      if (supervisor?.nombres) return `${supervisor.nombres} ${supervisor.apellidos || ''}`.trim();
      
      // 3. Buscar cualquier supervisor válido del tipo correspondiente
      const listaSupervisores = tipo === "cocina" ? supervisoresCocina : supervisoresSalon;
      if (listaSupervisores.length > 0) {
        const sup = listaSupervisores[Math.floor(Math.random() * listaSupervisores.length)];
        return sup.nombre || `${sup.nombres || ''} ${sup.apellidos || ''}`.trim();
      }
      
      // 4. Buscar supervisor del otro tipo (tienen los mismos perfiles válidos)
      const otrosSupers = tipo === "cocina" ? supervisoresSalon : supervisoresCocina;
      if (otrosSupers.length > 0) {
        const sup = otrosSupers[Math.floor(Math.random() * otrosSupers.length)];
        return sup.nombre || `${sup.nombres || ''} ${sup.apellidos || ''}`.trim();
      }
      
      // No hay supervisores válidos disponibles
      return "No asignado";
    };
    
    const nombreReviso = obtenerNombreReviso();
    
    return (
      <div>
        <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10, color: '#000000' }}>
          <strong style={{ color: '#000000' }}>Revisó:</strong> 
          <span style={{ color: '#000000', fontWeight: 500 }}>{nombreReviso}</span>
        </div>
        
        {/* Tabla diaria */}
        <h4 style={{ margin: '15px 0 10px', color: '#000000' }}>PERIODICIDAD DIARIA</h4>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, color: '#000000' }}>
            <thead>
              <tr style={{ background: '#f0f0f0' }}>
                <th style={{ border: '1px solid #333', padding: 4, minWidth: 100, color: '#000000', fontWeight: 'bold' }}>ÁREA</th>
                {dias.map(d => (
                  <th key={d.dia} style={{ border: '1px solid #333', padding: 2, minWidth: 25, color: '#000000', fontWeight: 'bold' }}>
                    {d.dia}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {itemsDiario.map(item => (
                <tr key={item}>
                  <td style={{ border: '1px solid #333', padding: 4, textAlign: 'left', fontWeight: 700, color: '#000000' }}>{item.toUpperCase()}</td>
                  {dias.map(d => {
                    const nombreCompleto = datos.diario?.[item]?.[d.dia] || '';
                    const primerNombre = nombreCompleto.split(' ')[0] || '-';
                    return (
                      <td key={d.dia} style={{ border: '1px solid #000', padding: 2, fontSize: 10, minWidth: 50, whiteSpace: 'nowrap', color: '#000000', fontWeight: 700 }} title={nombreCompleto}>
                        {primerNombre}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Tabla semanal */}
        <h4 style={{ margin: '15px 0 10px', color: '#000000' }}>PERIODICIDAD SEMANAL</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, maxWidth: 600, color: '#000000' }}>
          <thead>
            <tr style={{ background: '#f0f0f0' }}>
              <th style={{ border: '1px solid #333', padding: 6, color: '#000000', fontWeight: 'bold' }}>ÁREA</th>
              <th style={{ border: '1px solid #333', padding: 6, color: '#000000', fontWeight: 'bold' }}>SEMANA 1</th>
              <th style={{ border: '1px solid #333', padding: 6, color: '#000000', fontWeight: 'bold' }}>SEMANA 2</th>
              <th style={{ border: '1px solid #333', padding: 6, color: '#000000', fontWeight: 'bold' }}>SEMANA 3</th>
              <th style={{ border: '1px solid #333', padding: 6, color: '#000000', fontWeight: 'bold' }}>SEMANA 4</th>
              <th style={{ border: '1px solid #333', padding: 6, color: '#000000', fontWeight: 'bold' }}>SEMANA 5</th>
            </tr>
          </thead>
          <tbody>
            {itemsSemanal.map(item => (
              <tr key={item}>
                <td style={{ border: '1px solid #333', padding: 6, fontWeight: 700, color: '#000000' }}>{item.toUpperCase()}</td>
                {[1, 2, 3, 4, 5].map(semana => {
                  const dato = datos.semanal?.[item]?.[`semana_${semana}`];
                  return (
                    <td key={semana} style={{ border: '1px solid #000', padding: 4, fontSize: 11, color: '#000000', fontWeight: 700 }}>
                      {dato ? `${dato.empleado?.split(' ')[0] || ''} (${dato.dia})` : '-'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        
        {/* Sección de Trampa de Grasa - Solo para cocina */}
        {tipo === "cocina" && trampaGrasaMes.length > 0 && (
          <div style={{ marginTop: 25 }}>
            <h4 style={{ margin: '0 0 10px', fontSize: 14, borderBottom: '2px solid #059669', paddingBottom: 5, color: '#000000' }}>Limpieza de Trampa de Grasa</h4>
            <table style={{ borderCollapse: 'collapse', fontSize: 12, color: '#000000' }}>
              <thead>
                <tr style={{ background: '#f0f0f0' }}>
                  <th style={{ border: '1px solid #333', padding: 8, minWidth: 100, color: '#000000', fontWeight: 'bold' }}>Fecha</th>
                  <th style={{ border: '1px solid #333', padding: 8, minWidth: 200, color: '#000000', fontWeight: 'bold' }}>Proveedor</th>
                </tr>
              </thead>
              <tbody>
                {trampaGrasaMes.map(t => (
                  <tr key={t.id}>
                    <td style={{ border: '1px solid #000', padding: 8, color: '#000000', fontWeight: 700 }}>{t.fecha}</td>
                    <td style={{ border: '1px solid #000', padding: 8, color: '#000000', fontWeight: 700 }}>{t.proveedor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Sección de firmas */}
        <div style={{ marginTop: 30, padding: '20px 0', borderTop: '1px solid #333' }}>
          <div style={{ textAlign: 'center', maxWidth: 400, margin: '0 auto' }}>
            <div style={{ borderBottom: '1px solid #000', marginBottom: 5, height: 40 }}></div>
            <strong style={{ color: '#000000' }}>Revisó: {nombreReviso}</strong>
          </div>
        </div>
      </div>
    );
  };
  
  // Renderizar tabla de temperatura
  const renderTablaTemperatura = () => {
    if (!planillaActual?.datos_temperatura?.registros) {
      return (
        <div style={{ textAlign: 'center', padding: 30, background: '#fef3c7', borderRadius: 8 }}>
          <p style={{ fontSize: 16, color: '#92400e', margin: 0 }}>⚠️ No hay datos de temperatura</p>
          <p style={{ fontSize: 13, color: '#a16207', marginTop: 8 }}>
            {!planillaActual 
              ? (horariosDelMesSeleccionado.length === 0 
                ? "No hay horarios registrados para este mes."
                : `Presione "Generar Planilla" para crear los datos.`)
              : "La planilla no tiene datos de temperatura generados."
            }
          </p>
        </div>
      );
    }
    
    const registros = planillaActual.datos_temperatura.registros;
    const numCongeladores = configSedeActual?.num_congeladores || 1;
    const numRefrigeradores = configSedeActual?.num_refrigeradores || 1;
    
    // Función para obtener nombre de supervisor (solo supervisores, gerencia, administración)
    const obtenerNombreRevisoTemp = () => {
      if (planillaActual?.supervisor_cocina_nombre && planillaActual.supervisor_cocina_nombre !== 'No asignado') return planillaActual.supervisor_cocina_nombre;
      if (supervisorCocinaActual?.nombre) return supervisorCocinaActual.nombre;
      if (supervisorCocinaActual?.nombres) return `${supervisorCocinaActual.nombres} ${supervisorCocinaActual.apellidos || ''}`.trim();
      if (supervisoresCocina.length > 0) {
        const sup = supervisoresCocina[0];
        return sup.nombre || `${sup.nombres || ''} ${sup.apellidos || ''}`.trim();
      }
      if (supervisoresSalon.length > 0) {
        const sup = supervisoresSalon[0];
        return sup.nombre || `${sup.nombres || ''} ${sup.apellidos || ''}`.trim();
      }
      return "Supervisor";
    };
    
    const nombreRevisoTemp = obtenerNombreRevisoTemp();
    
    return (
      <div>
        <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10, color: '#000000' }}>
          <strong style={{ color: '#000000' }}>Supervisor Cocina:</strong> 
          <span style={{ color: '#000000', fontWeight: 500 }}>{nombreRevisoTemp}</span>
        </div>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, color: '#000000' }}>
            <thead>
              <tr style={{ background: '#f0f0f0' }}>
                <th style={{ border: '1px solid #333', padding: 4, color: '#000000', fontWeight: 'bold' }}>DÍA</th>
                <th style={{ border: '1px solid #333', padding: 4, color: '#000000', fontWeight: 'bold' }}>HORA</th>
                {Array.from({ length: numCongeladores }, (_, i) => (
                  <th key={`cong-${i}`} style={{ border: '1px solid #333', padding: 4, color: '#000000', fontWeight: 'bold' }}>
                    CONG. {i + 1} (°C)
                  </th>
                ))}
                {Array.from({ length: numRefrigeradores }, (_, i) => (
                  <th key={`refr-${i}`} style={{ border: '1px solid #333', padding: 4, color: '#000000', fontWeight: 'bold' }}>
                    REFRI. {i + 1} (°C)
                  </th>
                ))}
                <th style={{ border: '1px solid #333', padding: 4, color: '#000000', fontWeight: 'bold' }}>RESPONSABLE</th>
              </tr>
            </thead>
            <tbody>
              {registros.map(reg => {
                const congeladores = reg.equipos?.filter(e => e.tipo === "Congelación") || [];
                const refrigeradores = reg.equipos?.filter(e => e.tipo === "Refrigeración") || [];
                return (
                  <tr key={reg.dia}>
                    <td style={{ border: '1px solid #000', padding: 4, textAlign: 'center', color: '#000000', fontWeight: 700 }}>{reg.dia}</td>
                    <td style={{ border: '1px solid #000', padding: 4, textAlign: 'center', color: '#000000', fontWeight: 700 }}>{reg.hora}</td>
                    {Array.from({ length: numCongeladores }, (_, i) => {
                      const eq = congeladores[i];
                      const temp = eq?.temperatura;
                      return (
                        <td key={`cong-${i}`} style={{ border: '1px solid #000', padding: 4, textAlign: 'center', color: '#000000', fontWeight: 'bold' }}>
                          {temp !== undefined ? temp : '-'}
                        </td>
                      );
                    })}
                    {Array.from({ length: numRefrigeradores }, (_, i) => {
                      const eq = refrigeradores[i];
                      const temp = eq?.temperatura;
                      return (
                        <td key={`refr-${i}`} style={{ border: '1px solid #000', padding: 4, textAlign: 'center', color: '#000000', fontWeight: 'bold' }}>
                          {temp !== undefined ? temp : '-'}
                        </td>
                      );
                    })}
                    <td style={{ border: '1px solid #000', padding: 4, fontSize: 11, whiteSpace: 'nowrap', color: '#000000', fontWeight: 700 }} title={reg.responsable || ''}>
                      {reg.responsable?.split(' ')[0] || '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Sección de firmas */}
        <div style={{ marginTop: 30, padding: '20px 0', borderTop: '1px solid #333' }}>
          <div style={{ textAlign: 'center', maxWidth: 400, margin: '0 auto' }}>
            <div style={{ borderBottom: '1px solid #000', marginBottom: 5, height: 40 }}></div>
            <strong style={{ color: '#000000' }}>Revisó: {nombreRevisoTemp}</strong>
          </div>
        </div>
      </div>
    );
  };
  
  // Renderizar tabla de tanque de reserva
  const renderTablaTanqueReserva = () => {
    if (!configSedeActual?.tiene_tanque_reserva) {
      return <p style={{ color: '#666' }}>Esta sede no tiene tanque de reserva configurado</p>;
    }
    
    if (!planillaActual?.datos_tanque_reserva?.registros?.length) {
      const bimestre = obtenerBimestre(mesSeleccionado);
      if (bimestre && mesSeleccionado !== bimestre.fin) {
        return <p style={{ color: '#666' }}>El aseo del tanque se registra en el último mes del bimestre ({bimestre.nombre})</p>;
      }
      return <p>No hay registros de aseo de tanque</p>;
    }
    
    const registros = planillaActual.datos_tanque_reserva.registros;
    
    // Función para obtener nombre de supervisor (solo supervisores, gerencia, administración)
    const obtenerNombreRevisoTanque = () => {
      if (planillaActual?.supervisor_salon_nombre && planillaActual.supervisor_salon_nombre !== 'No asignado') return planillaActual.supervisor_salon_nombre;
      if (supervisorSalonActual?.nombre) return supervisorSalonActual.nombre;
      if (supervisorSalonActual?.nombres) return `${supervisorSalonActual.nombres} ${supervisorSalonActual.apellidos || ''}`.trim();
      if (supervisoresSalon.length > 0) {
        const sup = supervisoresSalon[0];
        return sup.nombre || `${sup.nombres || ''} ${sup.apellidos || ''}`.trim();
      }
      if (supervisoresCocina.length > 0) {
        const sup = supervisoresCocina[0];
        return sup.nombre || `${sup.nombres || ''} ${sup.apellidos || ''}`.trim();
      }
      return "Supervisor";
    };
    
    const nombreRevisoTanque = obtenerNombreRevisoTanque();
    
    return (
      <div>
        <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10, color: '#000000' }}>
          <strong style={{ color: '#000000' }}>Supervisor Salón:</strong> 
          <span style={{ color: '#000000', fontWeight: 500 }}>{nombreRevisoTanque}</span>
        </div>
        
        <table style={{ width: '100%', maxWidth: 500, borderCollapse: 'collapse', fontSize: 12, color: '#000000' }}>
          <thead>
            <tr style={{ background: '#f0f0f0' }}>
              <th style={{ border: '1px solid #333', padding: 8, color: '#000000', fontWeight: 'bold' }}>BIMESTRE</th>
              <th style={{ border: '1px solid #333', padding: 8, color: '#000000', fontWeight: 'bold' }}>FECHA</th>
              <th style={{ border: '1px solid #333', padding: 8, color: '#000000', fontWeight: 'bold' }}>RESPONSABLE</th>
            </tr>
          </thead>
          <tbody>
            {registros.map((reg, i) => (
              <tr key={i}>
                <td style={{ border: '1px solid #000', padding: 8, color: '#000000', fontWeight: 700 }}>{reg.bimestre}</td>
                <td style={{ border: '1px solid #000', padding: 8, color: '#000000', fontWeight: 700 }}>{reg.fecha}</td>
                <td style={{ border: '1px solid #000', padding: 8, color: '#000000', fontWeight: 700 }}>{reg.responsable || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {/* Sección de firmas */}
        <div style={{ marginTop: 30, padding: '20px 0', borderTop: '1px solid #333', maxWidth: 500 }}>
          <div style={{ textAlign: 'center', maxWidth: 400, margin: '0 auto' }}>
            <div style={{ borderBottom: '1px solid #000', marginBottom: 5, height: 40 }}></div>
            <strong style={{ color: '#000000' }}>Revisó: {nombreRevisoTanque}</strong>
          </div>
        </div>
      </div>
    );
  };
  
  // ============================================
  // PLANILLA 1: REGISTRO DIARIO DE CONTROL DE RESIDUOS
  // ============================================
  const renderTablaResiduos = () => {
    const datos = planillaActual?.datos_residuos || { registros: {} };
    const registros = datos.registros || {};
    const diasDelMes = ultimoDiaDelMes(anioSeleccionado, mesSeleccionado);
    
    // Crear array de días
    const dias = [];
    for (let d = 1; d <= diasDelMes; d++) {
      dias.push(d);
    }
    
    // Calcular totales por tipo
    let totalAprovechables = 0;
    let totalOrganicos = 0;
    let totalNoAprovechables = 0;
    
    Object.values(registros).forEach(r => {
      totalAprovechables += r.aprovechables || 0;
      totalOrganicos += r.organicos || 0;
      totalNoAprovechables += r.noAprovechables || 0;
    });
    
    return (
      <div>
        <p style={{ marginBottom: 10, fontSize: 12, color: '#666' }}>
          <strong>Instrucciones:</strong> Registrar diariamente la cantidad de bolsas por tipo de residuo
        </p>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, color: '#000000' }}>
            <thead>
              <tr style={{ background: '#059669', color: 'white' }}>
                <th style={{ border: '1px solid #333', padding: 6, minWidth: 40 }}>DÍA</th>
                <th style={{ border: '1px solid #333', padding: 6, minWidth: 100 }}>APROVECHABLES</th>
                <th style={{ border: '1px solid #333', padding: 6, minWidth: 100 }}>ORGÁNICOS</th>
                <th style={{ border: '1px solid #333', padding: 6, minWidth: 120 }}>NO APROVECHABLES</th>
              </tr>
            </thead>
            <tbody>
              {dias.map(d => {
                const reg = registros[d] || {};
                return (
                  <tr key={d}>
                    <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', background: '#f9f9f9', fontWeight: 'bold', color: '#000' }}>{d}</td>
                    <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000', fontWeight: 600 }}>{reg.aprovechables || '-'}</td>
                    <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000', fontWeight: 600 }}>{reg.organicos || '-'}</td>
                    <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000', fontWeight: 600 }}>{reg.noAprovechables || '-'}</td>
                  </tr>
                );
              })}
              {/* Fila de totales */}
              <tr style={{ background: '#e5e7eb', fontWeight: 'bold' }}>
                <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000' }}>TOTAL</td>
                <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000' }}>{totalAprovechables}</td>
                <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000' }}>{totalOrganicos}</td>
                <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000' }}>{totalNoAprovechables}</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Leyenda */}
        <div style={{ marginTop: 15, padding: 10, background: '#f3f4f6', borderRadius: 6, fontSize: 11 }}>
          <strong>Tipos de Residuos:</strong>
          <ul style={{ margin: '5px 0 0 15px', padding: 0 }}>
            <li><strong>Aprovechables:</strong> Plástico, papel, cartón, vidrio, metal (reciclables)</li>
            <li><strong>Orgánicos:</strong> Restos de alimentos, cáscaras, residuos biodegradables</li>
            <li><strong>No Aprovechables:</strong> Servilletas usadas, empaques contaminados, residuos no reciclables</li>
          </ul>
        </div>
        
        {/* Sección de firmas */}
        <div style={{ marginTop: 30, padding: '20px 0', borderTop: '1px solid #333' }}>
          <div style={{ textAlign: 'center', maxWidth: 400, margin: '0 auto' }}>
            <div style={{ borderBottom: '1px solid #000', marginBottom: 5, height: 40 }}></div>
            <strong style={{ color: '#000000' }}>Revisó: {planillaActual?.revisor_residuos || planillaActual?.supervisor_cocina_nombre || supervisorCocinaActual?.nombre || (supervisorCocinaActual?.nombres ? `${supervisorCocinaActual.nombres} ${supervisorCocinaActual.apellidos || ''}`.trim() : 'Supervisor')}</strong>
          </div>
        </div>
      </div>
    );
  };
  
  // ============================================
  // PLANILLA 2: REGISTRO DE CONTROL DE AGUA POTABLE
  // ============================================
  const renderTablaAguaPotable = () => {
    const datos = planillaActual?.datos_agua_potable || { registros: [] };
    const registros = Array.isArray(datos.registros) ? datos.registros : [];
    
    return (
      <div>
        <p style={{ marginBottom: 10, fontSize: 12, color: '#666' }}>
          <strong>Instrucciones:</strong> Registrar diariamente las características del agua potable
        </p>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, color: '#000000' }}>
            <thead>
              <tr style={{ background: '#059669', color: 'white' }}>
                <th style={{ border: '1px solid #333', padding: 6, minWidth: 40 }}>DÍA</th>
                <th style={{ border: '1px solid #333', padding: 6, minWidth: 70 }}>HORA</th>
                <th style={{ border: '1px solid #333', padding: 6, minWidth: 70 }}>LUGAR</th>
                <th style={{ border: '1px solid #333', padding: 6, minWidth: 50 }}>COLOR</th>
                <th style={{ border: '1px solid #333', padding: 6, minWidth: 50 }}>OLOR</th>
                <th style={{ border: '1px solid #333', padding: 6, minWidth: 60 }}>SABOR</th>
                <th style={{ border: '1px solid #333', padding: 6, minWidth: 50 }}>PH</th>
                <th style={{ border: '1px solid #333', padding: 6, minWidth: 50 }}>CLORO</th>
                <th style={{ border: '1px solid #333', padding: 6, minWidth: 100 }}>RESPONSABLE</th>
              </tr>
            </thead>
            <tbody>
              {registros.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ border: '1px solid #000', padding: 15, textAlign: 'center', color: '#666' }}>
                    No hay registros de agua potable para este mes
                  </td>
                </tr>
              ) : registros.map((reg, i) => (
                <tr key={i}>
                  <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', background: '#f9f9f9', fontWeight: 'bold', color: '#000' }}>{reg.dia}</td>
                  <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000', fontWeight: 600 }}>{reg.hora || '-'}</td>
                  <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000', fontWeight: 600 }}>{(reg.lugar || 'COCINA').toUpperCase()}</td>
                  <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000', fontWeight: 600 }}>{reg.color || 'C'}</td>
                  <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000', fontWeight: 600 }}>{reg.olor || 'C'}</td>
                  <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000', fontWeight: 600 }}>{(reg.sabor || 'NORMAL').toUpperCase()}</td>
                  <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000', fontWeight: 600, background: (reg.ph < 6.5 || reg.ph > 9) ? '#fef2f2' : 'transparent' }}>
                    {reg.ph || '-'}
                  </td>
                  <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000', fontWeight: 600, background: (reg.cloro < 0.3 || reg.cloro > 2) ? '#fef2f2' : 'transparent' }}>
                    {reg.cloro || '-'}
                  </td>
                  <td style={{ border: '1px solid #000', padding: 6, textAlign: 'center', color: '#000', fontWeight: 600, fontSize: 10 }}>{reg.responsable || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Leyenda */}
        <div style={{ marginTop: 15, padding: 10, background: '#f3f4f6', borderRadius: 6, fontSize: 11 }}>
          <strong>Parámetros Aceptables:</strong>
          <ul style={{ margin: '5px 0 0 15px', padding: 0 }}>
            <li><strong>Color:</strong> C = Claro/Cristalino</li>
            <li><strong>Olor:</strong> C = Característico (sin olores extraños)</li>
            <li><strong>Sabor:</strong> Normal (sin sabores extraños)</li>
            <li><strong>pH:</strong> Entre 6.5 y 9.0</li>
            <li><strong>Cloro residual:</strong> Entre 0.3 y 2.0 mg/L</li>
          </ul>
        </div>
        
        {/* Sección de firmas */}
        <div style={{ marginTop: 30, padding: '20px 0', borderTop: '1px solid #333' }}>
          <div style={{ textAlign: 'center', maxWidth: 400, margin: '0 auto' }}>
            <div style={{ borderBottom: '1px solid #000', marginBottom: 5, height: 40 }}></div>
            <strong style={{ color: '#000000' }}>Revisó: {planillaActual?.revisor_agua_potable || planillaActual?.supervisor_cocina_nombre || supervisorCocinaActual?.nombre || (supervisorCocinaActual?.nombres ? `${supervisorCocinaActual.nombres} ${supervisorCocinaActual.apellidos || ''}`.trim() : 'Supervisor')}</strong>
          </div>
        </div>
      </div>
    );
  };
  
  // ============================================
  // PLANILLA 3: REGISTRO DE CONTROL DE PLAGAS Y ROEDORES
  // ============================================
  const renderTablaPlagas = () => {
    const datos = planillaActual?.datos_plagas || { registros: {} };
    const registros = datos.registros || {};
    const diasDelMes = ultimoDiaDelMes(anioSeleccionado, mesSeleccionado);
    
    // Crear array de días
    const dias = [];
    for (let d = 1; d <= diasDelMes; d++) {
      dias.push(d);
    }
    
    // Tipos de plagas
    const tiposPlagas = [
      { id: 'cucarachaAmericana', label: 'CUCARACHA AMERICANA' },
      { id: 'cucarachaGermanica', label: 'CUCARACHA GERMÁNICA' },
      { id: 'roedores', label: 'ROEDORES' },
      { id: 'moscas', label: 'MOSCAS' },
      { id: 'aves', label: 'AVES' },
      { id: 'aranas', label: 'ARAÑAS' }
    ];
    
    return (
      <div>
        <p style={{ marginBottom: 10, fontSize: 12, color: '#666' }}>
          <strong>Instrucciones:</strong> Registrar diariamente la presencia o evidencia de plagas. Marcar "NO" si no hay evidencia, "SÍ" si se detecta presencia.
        </p>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, color: '#000000' }}>
            <thead>
              <tr style={{ background: '#059669', color: 'white' }}>
                <th style={{ border: '1px solid #333', padding: 5, minWidth: 35 }}>DÍA</th>
                {tiposPlagas.map(t => (
                  <th key={t.id} style={{ border: '1px solid #333', padding: 5, minWidth: 60, fontSize: 9 }}>{t.label}</th>
                ))}
                <th style={{ border: '1px solid #333', padding: 5, minWidth: 80 }}>ÁREA</th>
              </tr>
            </thead>
            <tbody>
              {dias.map(d => {
                const reg = registros[d] || {};
                return (
                  <tr key={d}>
                    <td style={{ border: '1px solid #000', padding: 5, textAlign: 'center', background: '#f9f9f9', fontWeight: 'bold', color: '#000' }}>{d}</td>
                    {tiposPlagas.map(t => {
                      const valor = reg[t.id] || 'NO';
                      const esSi = valor === 'SÍ' || valor === 'SI';
                      return (
                        <td key={t.id} style={{ 
                          border: '1px solid #000', 
                          padding: 5, 
                          textAlign: 'center', 
                          color: esSi ? '#dc2626' : '#059669', 
                          fontWeight: 700,
                          background: esSi ? '#fef2f2' : 'transparent'
                        }}>
                          {valor}
                        </td>
                      );
                    })}
                    <td style={{ border: '1px solid #000', padding: 5, textAlign: 'center', color: '#000', fontSize: 9 }}>{reg.area || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Leyenda */}
        <div style={{ marginTop: 15, padding: 10, background: '#f3f4f6', borderRadius: 6, fontSize: 11 }}>
          <strong>Indicaciones:</strong>
          <ul style={{ margin: '5px 0 0 15px', padding: 0 }}>
            <li><strong style={{ color: '#059669' }}>NO:</strong> Sin evidencia de presencia de la plaga</li>
            <li><strong style={{ color: '#dc2626' }}>SÍ:</strong> Se detectó evidencia o presencia de la plaga (especificar área)</li>
            <li><strong>Área:</strong> Indicar el área donde se detectó la plaga (solo si hay evidencia)</li>
          </ul>
          <p style={{ marginTop: 8, fontStyle: 'italic', color: '#666' }}>
            En caso de detectar presencia de plagas, notificar inmediatamente al supervisor y coordinar acciones de control.
          </p>
        </div>
        
        {/* Sección de firmas */}
        <div style={{ marginTop: 30, padding: '20px 0', borderTop: '1px solid #333' }}>
          <div style={{ textAlign: 'center', maxWidth: 400, margin: '0 auto' }}>
            <div style={{ borderBottom: '1px solid #000', marginBottom: 5, height: 40 }}></div>
            <strong style={{ color: '#000000' }}>Revisó: {planillaActual?.revisor_plagas || planillaActual?.supervisor_salon_nombre || supervisorSalonActual?.nombre || (supervisorSalonActual?.nombres ? `${supervisorSalonActual.nombres} ${supervisorSalonActual.apellidos || ''}`.trim() : 'Supervisor')}</strong>
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 15 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
          <span style={{ fontSize: 32 }}>🏥</span>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, color: '#059669' }}>Secretaría de Salud</h2>
            <p style={{ margin: 0, fontSize: 12, color: '#666' }}>Gestión de documentos y planillas sanitarias</p>
          </div>
        </div>
        
        {/* Selector de sede */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ fontWeight: 500 }}>Sede:</label>
          <select
            value={sedeSeleccionada || ""}
            onChange={(e) => setSedeSeleccionada(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', minWidth: 180 }}
          >
            <option value="">Seleccionar sede...</option>
            {sedes.map(s => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Navegación de secciones */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { id: "planillas", label: "📋 Planillas", color: "#059669" },
          { id: "archivos", label: "📁 Archivos", color: "#2563eb" },
          { id: "trampa", label: "🔧 Trampa de Grasa", color: "#d97706" },
          { id: "config", label: "⚙️ Configuración", color: "#7c3aed" }
        ].map(s => (
          <button
            key={s.id}
            onClick={() => setSeccion(s.id)}
            disabled={!tienePermiso(`admin_secretaria_salud_${s.id === "planillas" ? "planillas" : s.id === "archivos" ? "archivos" : s.id === "trampa" ? "trampa" : "config"}`)}
            style={{
              padding: '10px 18px',
              borderRadius: 8,
              border: seccion === s.id ? `2px solid ${s.color}` : '1px solid #ddd',
              background: seccion === s.id ? s.color : 'white',
              color: seccion === s.id ? 'white' : '#333',
              fontWeight: 500,
              cursor: 'pointer',
              opacity: !tienePermiso(`admin_secretaria_salud_${s.id === "planillas" ? "planillas" : s.id === "archivos" ? "archivos" : s.id === "trampa" ? "trampa" : "config"}`) ? 0.5 : 1
            }}
          >
            {s.label}
          </button>
        ))}
      </div>
      
      {cargando && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <p>Cargando...</p>
        </div>
      )}
      
      {!sedeSeleccionada && !cargando && (
        <div style={{ textAlign: 'center', padding: 60, background: '#f9fafb', borderRadius: 12 }}>
          <span style={{ fontSize: 48 }}>🏢</span>
          <h3 style={{ marginTop: 15, color: '#374151' }}>Seleccione una sede</h3>
          <p style={{ color: '#6b7280' }}>Para comenzar, seleccione una sede en el menú superior</p>
        </div>
      )}
      
      {sedeSeleccionada && !cargando && (
        <>
          {/* Sección: Planillas */}
          {seccion === "planillas" && (
            <div>
              {/* Selector de mes/año */}
              <div style={{ display: 'flex', gap: 15, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <label>Mes:</label>
                  <select
                    value={mesSeleccionado}
                    onChange={(e) => setMesSeleccionado(Number(e.target.value))}
                    style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd' }}
                  >
                    {["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"].map((m, i) => (
                      <option key={i + 1} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <label>Año:</label>
                  <select
                    value={anioSeleccionado}
                    onChange={(e) => setAnioSeleccionado(Number(e.target.value))}
                    style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd' }}
                  >
                    {[2026, 2027, 2028].map(a => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
                
                <button
                  onClick={imprimirPlanilla}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 6,
                    border: 'none',
                    background: '#059669',
                    color: 'white',
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  🖨️ Imprimir
                </button>
                
                <button
                  onClick={() => {
                    setMesInicioImpresion(mesSeleccionado);
                    setAnioInicioImpresion(anioSeleccionado);
                    setMesFinImpresion(mesSeleccionado);
                    setAnioFinImpresion(anioSeleccionado);
                    setMostrarModalImpresion(true);
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 6,
                    border: 'none',
                    background: '#7c3aed',
                    color: 'white',
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  📄 Imprimir Múltiples Meses
                </button>
                
                <button
                  onClick={regenerarPlanillaActual}
                  disabled={cargando}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 6,
                    border: 'none',
                    background: '#2563eb',
                    color: 'white',
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    opacity: cargando ? 0.6 : 1
                  }}
                >
                  🔄 {planillaActual ? "Regenerar" : "Generar"} Planilla
                </button>
                
                {/* Indicador de horarios disponibles */}
                <span style={{ 
                  padding: '6px 12px', 
                  background: horariosDelMesSeleccionado.length > 0 ? '#dcfce7' : '#fef3c7',
                  color: horariosDelMesSeleccionado.length > 0 ? '#166534' : '#92400e',
                  borderRadius: 6, 
                  fontSize: 12, 
                  fontWeight: 500 
                }}>
                  📅 {horariosDelMesSeleccionado.length} horario(s) | Total: {horariosRepo?.length || 0}
                </span>
                
                {planillaActual?.cerrado && (
                  <span style={{ padding: '6px 12px', background: '#fee2e2', color: '#dc2626', borderRadius: 6, fontSize: 12, fontWeight: 500 }}>
                    🔒 Mes cerrado
                  </span>
                )}
              </div>
              
              {/* Tabs de tipos de planilla */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 15, borderBottom: '2px solid #e5e7eb', paddingBottom: 10, flexWrap: 'wrap' }}>
                {[
                  { id: "desinfeccion_cocina", label: "Desinfección Cocina", visible: true },
                  { id: "desinfeccion_salon", label: "Desinfección Salón", visible: true },
                  { id: "temperatura", label: "Control Temperatura", visible: true },
                  { id: "tanque_reserva", label: "Aseo Tanque Reserva", visible: configSedeActual?.tiene_tanque_reserva },
                  { id: "residuos", label: "Control Residuos", visible: configSedeActual?.aplica_residuos !== false },
                  { id: "agua_potable", label: "Control Agua Potable", visible: configSedeActual?.aplica_agua_potable !== false },
                  { id: "plagas", label: "Control Plagas", visible: configSedeActual?.aplica_plagas !== false }
                ].filter(t => t.visible).map(t => (
                  <button
                    key={t.id}
                    onClick={() => setTipoPlanilla(t.id)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px 6px 0 0',
                      border: 'none',
                      background: tipoPlanilla === t.id ? '#059669' : '#f3f4f6',
                      color: tipoPlanilla === t.id ? 'white' : '#374151',
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontSize: 13
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              
              {/* Contenido de planilla */}
              <div id="planilla-contenido" style={{ background: 'white', padding: 20, borderRadius: 8, border: '1px solid #e5e7eb' }}>
                {/* Header de planilla */}
                <div className="header" style={{ textAlign: 'center', marginBottom: 20, borderBottom: '2px solid #059669', paddingBottom: 15 }}>
                  <img src={LOGO_BIGBURGUER} alt="Logo" className="logo" style={{ width: 60, height: 60, borderRadius: '50%', marginBottom: 10 }} />
                  <h3 style={{ margin: '5px 0', color: '#059669' }}>
                    {tipoPlanilla === "desinfeccion_cocina" && "PLANILLA DE DESINFECCIÓN - COCINA"}
                    {tipoPlanilla === "desinfeccion_salon" && "PLANILLA DE DESINFECCIÓN - SALÓN"}
                    {tipoPlanilla === "temperatura" && "CONTROL DIARIO DE TEMPERATURA"}
                    {tipoPlanilla === "tanque_reserva" && "ASEO TANQUE DE AGUA DE RESERVA"}
                    {tipoPlanilla === "residuos" && "REGISTRO DIARIO DE CONTROL DE RESIDUOS"}
                    {tipoPlanilla === "agua_potable" && "REGISTRO DE CONTROL DE AGUA POTABLE"}
                    {tipoPlanilla === "plagas" && "REGISTRO DE CONTROL DE PLAGAS Y ROEDORES"}
                  </h3>
                  <p style={{ margin: 0, fontSize: 14 }}>
                    <strong>Sede:</strong> {nombreSedeActual} | <strong>Mes:</strong> {["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"][mesSeleccionado - 1]} {anioSeleccionado}
                  </p>
                </div>
                
                {/* Contenido según tipo */}
                {tipoPlanilla === "desinfeccion_cocina" && renderTablaDesinfeccion("cocina")}
                {tipoPlanilla === "desinfeccion_salon" && renderTablaDesinfeccion("salon")}
                {tipoPlanilla === "temperatura" && renderTablaTemperatura()}
                {tipoPlanilla === "tanque_reserva" && renderTablaTanqueReserva()}
                {tipoPlanilla === "residuos" && renderTablaResiduos()}
                {tipoPlanilla === "agua_potable" && renderTablaAguaPotable()}
                {tipoPlanilla === "plagas" && renderTablaPlagas()}
              </div>
            </div>
          )}
          
          {/* Sección: Archivos */}
          {seccion === "archivos" && (
            <div>
              <h3 style={{ marginBottom: 15 }}>📁 Archivos de la sede: {nombreSedeActual}</h3>
              
              {/* Formulario para subir archivo */}
              <div style={{ background: '#f9fafb', padding: 20, borderRadius: 8, marginBottom: 20 }}>
                <h4 style={{ margin: '0 0 15px' }}>Subir nuevo archivo</h4>
                <div style={{ display: 'flex', gap: 15, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 5, fontSize: 13 }}>Nombre del documento:</label>
                    <input
                      type="text"
                      value={nuevoArchivo.nombre}
                      onChange={(e) => setNuevoArchivo(prev => ({ ...prev, nombre: e.target.value }))}
                      placeholder="Ej: Certificado manipulación alimentos"
                      style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', width: 300 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 5, fontSize: 13 }}>Archivo (solo PDF):</label>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setNuevoArchivo(prev => ({ ...prev, archivo: e.target.files[0] }))}
                      style={{ padding: '6px', border: '1px solid #ddd', borderRadius: 6 }}
                    />
                  </div>
                  <button
                    onClick={handleSubirArchivo}
                    disabled={cargando}
                    style={{
                      padding: '10px 20px',
                      borderRadius: 6,
                      border: 'none',
                      background: '#2563eb',
                      color: 'white',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    📤 Subir archivo
                  </button>
                </div>
              </div>
              
              {/* Lista de archivos */}
              <div style={{ display: 'grid', gap: 10 }}>
                {archivos.filter(a => a.sede_id === sedeSeleccionada).length === 0 ? (
                  <p style={{ color: '#666', textAlign: 'center', padding: 30 }}>No hay archivos para esta sede</p>
                ) : (
                  archivos.filter(a => a.sede_id === sedeSeleccionada).map(archivo => (
                    <div key={archivo.id} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      padding: 15,
                      background: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 24 }}>📄</span>
                        <div>
                          <strong>{archivo.nombre}</strong>
                          <p style={{ margin: 0, fontSize: 11, color: '#666' }}>
                            {archivo.archivo_nombre_original} - Subido: {new Date(archivo.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <a
                          href={archivo.archivo_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: '6px 12px',
                            borderRadius: 6,
                            background: '#059669',
                            color: 'white',
                            textDecoration: 'none',
                            fontSize: 12
                          }}
                        >
                          👁️ Ver
                        </a>
                        <button
                          onClick={() => handleEliminarArchivo(archivo)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: 6,
                            border: 'none',
                            background: '#dc2626',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: 12
                          }}
                        >
                          🗑️ Eliminar
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          
          {/* Sección: Trampa de Grasa */}
          {seccion === "trampa" && (
            <div>
              <h3 style={{ marginBottom: 15 }}>🔧 Trampa de Grasa - {nombreSedeActual}</h3>
              
              {/* Formulario para registrar */}
              <div style={{ background: '#fef3c7', padding: 20, borderRadius: 8, marginBottom: 20 }}>
                <h4 style={{ margin: '0 0 15px', color: '#92400e' }}>Registrar mantenimiento</h4>
                <div style={{ display: 'flex', gap: 15, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 5, fontSize: 13 }}>Fecha:</label>
                    <input
                      type="date"
                      value={nuevaTrampa.fecha}
                      onChange={(e) => setNuevaTrampa(prev => ({ ...prev, fecha: e.target.value }))}
                      style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 5, fontSize: 13 }}>Proveedor:</label>
                    <input
                      type="text"
                      value={nuevaTrampa.proveedor}
                      onChange={(e) => setNuevaTrampa(prev => ({ ...prev, proveedor: e.target.value.toUpperCase() }))}
                      placeholder="Nombre del proveedor"
                      style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', width: 250, textTransform: 'uppercase' }}
                    />
                  </div>
                  <button
                    onClick={handleGuardarTrampa}
                    disabled={cargando}
                    style={{
                      padding: '10px 20px',
                      borderRadius: 6,
                      border: 'none',
                      background: '#d97706',
                      color: 'white',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    💾 Guardar
                  </button>
                </div>
              </div>
              
              {/* Historial */}
              <h4>Historial de mantenimientos</h4>
              <table style={{ width: '100%', maxWidth: 600, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f0f0f0' }}>
                    <th style={{ border: '1px solid #ccc', padding: 10, textAlign: 'left' }}>Fecha</th>
                    <th style={{ border: '1px solid #ccc', padding: 10, textAlign: 'left' }}>Proveedor</th>
                  </tr>
                </thead>
                <tbody>
                  {trampaGrasa.filter(t => t.sede_id === sedeSeleccionada).length === 0 ? (
                    <tr>
                      <td colSpan={2} style={{ border: '1px solid #ccc', padding: 20, textAlign: 'center', color: '#666' }}>
                        No hay registros
                      </td>
                    </tr>
                  ) : (
                    trampaGrasa.filter(t => t.sede_id === sedeSeleccionada).map(t => (
                      <tr key={t.id}>
                        <td style={{ border: '1px solid #ccc', padding: 10 }}>{t.fecha}</td>
                        <td style={{ border: '1px solid #ccc', padding: 10 }}>{t.proveedor}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Sección: Configuración */}
          {seccion === "config" && (
            <div>
              <h3 style={{ marginBottom: 15 }}>⚙️ Configuración de sede: {nombreSedeActual}</h3>
              
              <div style={{ background: '#f9fafb', padding: 25, borderRadius: 8, maxWidth: 600 }}>
                {!configEditando ? (
                  <>
                    <div style={{ marginBottom: 20 }}>
                      <p><strong>Número de congeladores:</strong> {configSedeActual?.num_congeladores || 1}</p>
                      <p><strong>Número de refrigeradores:</strong> {configSedeActual?.num_refrigeradores || 1}</p>
                      <p><strong>Tiene tanque de reserva:</strong> {configSedeActual?.tiene_tanque_reserva ? "Sí" : "No"}</p>
                    </div>
                    
                    <h4>Planillas habilitadas</h4>
                    <div style={{ marginBottom: 15, fontSize: 12, color: '#666' }}>
                      <p>✅ Desinfección Cocina - <em>Siempre activa</em></p>
                      <p>✅ Desinfección Salón - <em>Siempre activa</em></p>
                      <p>✅ Control Temperatura - <em>Siempre activa</em></p>
                      <p>{configSedeActual?.tiene_tanque_reserva ? "✅" : "❌"} Aseo Tanque Reserva - <em>{configSedeActual?.tiene_tanque_reserva ? "Activa" : "Desactivada"}</em></p>
                      <p>{configSedeActual?.aplica_residuos !== false ? "✅" : "❌"} Control de Residuos - <em>{configSedeActual?.aplica_residuos !== false ? "Activa" : "Desactivada"}</em></p>
                      <p>{configSedeActual?.aplica_agua_potable !== false ? "✅" : "❌"} Control Agua Potable - <em>{configSedeActual?.aplica_agua_potable !== false ? "Activa" : "Desactivada"}</em></p>
                      <p>{configSedeActual?.aplica_plagas !== false ? "✅" : "❌"} Control de Plagas - <em>{configSedeActual?.aplica_plagas !== false ? "Activa" : "Desactivada"}</em></p>
                    </div>
                    
                    <h4>Items de limpieza - Cocina</h4>
                    <p style={{ fontSize: 12, color: '#666' }}>
                      <strong>Diario:</strong> {(configSedeActual?.items_limpieza_cocina_diario || ITEMS_LIMPIEZA_COCINA_DIARIO_DEFAULT).join(", ")}
                    </p>
                    <p style={{ fontSize: 12, color: '#666' }}>
                      <strong>Semanal:</strong> {(configSedeActual?.items_limpieza_cocina_semanal || ITEMS_LIMPIEZA_COCINA_SEMANAL_DEFAULT).join(", ")}
                    </p>
                    
                    <h4>Items de limpieza - Salón</h4>
                    <p style={{ fontSize: 12, color: '#666' }}>
                      <strong>Diario:</strong> {(configSedeActual?.items_limpieza_salon_diario || ITEMS_LIMPIEZA_SALON_DIARIO_DEFAULT).join(", ")}
                    </p>
                    <p style={{ fontSize: 12, color: '#666' }}>
                      <strong>Semanal:</strong> {(configSedeActual?.items_limpieza_salon_semanal || ITEMS_LIMPIEZA_SALON_SEMANAL_DEFAULT).join(", ")}
                    </p>
                    
                    <button
                      onClick={() => setConfigEditando({
                        numCongeladores: configSedeActual?.num_congeladores || 1,
                        numRefrigeradores: configSedeActual?.num_refrigeradores || 1,
                        tieneTanqueReserva: configSedeActual?.tiene_tanque_reserva || false,
                        aplicaResiduos: configSedeActual?.aplica_residuos !== false,
                        aplicaAguaPotable: configSedeActual?.aplica_agua_potable !== false,
                        aplicaPlagas: configSedeActual?.aplica_plagas !== false,
                        itemsLimpiezaCocinaDiario: configSedeActual?.items_limpieza_cocina_diario || ITEMS_LIMPIEZA_COCINA_DIARIO_DEFAULT,
                        itemsLimpiezaCocinaSemanal: configSedeActual?.items_limpieza_cocina_semanal || ITEMS_LIMPIEZA_COCINA_SEMANAL_DEFAULT,
                        itemsLimpiezaSalonDiario: configSedeActual?.items_limpieza_salon_diario || ITEMS_LIMPIEZA_SALON_DIARIO_DEFAULT,
                        itemsLimpiezaSalonSemanal: configSedeActual?.items_limpieza_salon_semanal || ITEMS_LIMPIEZA_SALON_SEMANAL_DEFAULT
                      })}
                      style={{
                        marginTop: 15,
                        padding: '10px 20px',
                        borderRadius: 6,
                        border: 'none',
                        background: '#7c3aed',
                        color: 'white',
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      ✏️ Editar configuración
                    </button>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'grid', gap: 15 }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Número de congeladores:</label>
                        <input
                          type="number"
                          min="1"
                          max="5"
                          value={configEditando.numCongeladores}
                          onChange={(e) => setConfigEditando(prev => ({ ...prev, numCongeladores: Number(e.target.value) }))}
                          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', width: 100 }}
                        />
                      </div>
                      
                      <div>
                        <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Número de refrigeradores:</label>
                        <input
                          type="number"
                          min="1"
                          max="3"
                          value={configEditando.numRefrigeradores}
                          onChange={(e) => setConfigEditando(prev => ({ ...prev, numRefrigeradores: Number(e.target.value) }))}
                          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd', width: 100 }}
                        />
                      </div>
                      
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={configEditando.tieneTanqueReserva}
                            onChange={(e) => setConfigEditando(prev => ({ ...prev, tieneTanqueReserva: e.target.checked }))}
                            style={{ width: 18, height: 18 }}
                          />
                          <span style={{ fontWeight: 500 }}>Tiene tanque de reserva de agua</span>
                        </label>
                      </div>
                      
                      <h4 style={{ marginTop: 15, marginBottom: 10, color: '#059669' }}>Planillas adicionales</h4>
                      
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={configEditando.aplicaResiduos}
                            onChange={(e) => setConfigEditando(prev => ({ ...prev, aplicaResiduos: e.target.checked }))}
                            style={{ width: 18, height: 18 }}
                          />
                          <span style={{ fontWeight: 500 }}>Control de Residuos</span>
                        </label>
                        <p style={{ margin: '4px 0 0 28px', fontSize: 11, color: '#666' }}>Registro diario de bolsas por tipo de residuo</p>
                      </div>
                      
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={configEditando.aplicaAguaPotable}
                            onChange={(e) => setConfigEditando(prev => ({ ...prev, aplicaAguaPotable: e.target.checked }))}
                            style={{ width: 18, height: 18 }}
                          />
                          <span style={{ fontWeight: 500 }}>Control de Agua Potable</span>
                        </label>
                        <p style={{ margin: '4px 0 0 28px', fontSize: 11, color: '#666' }}>Registro diario de pH, cloro y características del agua</p>
                      </div>
                      
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={configEditando.aplicaPlagas}
                            onChange={(e) => setConfigEditando(prev => ({ ...prev, aplicaPlagas: e.target.checked }))}
                            style={{ width: 18, height: 18 }}
                          />
                          <span style={{ fontWeight: 500 }}>Control de Plagas y Roedores</span>
                        </label>
                        <p style={{ margin: '4px 0 0 28px', fontSize: 11, color: '#666' }}>Registro diario de presencia de plagas</p>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                      <button
                        onClick={handleGuardarConfig}
                        disabled={cargando}
                        style={{
                          padding: '10px 20px',
                          borderRadius: 6,
                          border: 'none',
                          background: '#059669',
                          color: 'white',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                      >
                        💾 Guardar
                      </button>
                      <button
                        onClick={() => setConfigEditando(null)}
                        style={{
                          padding: '10px 20px',
                          borderRadius: 6,
                          border: '1px solid #ccc',
                          background: 'white',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                      >
                        Cancelar
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Modal de selección de meses para impresión */}
      {mostrarModalImpresion && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            borderRadius: 12,
            padding: 30,
            maxWidth: 500,
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px', color: '#059669', display: 'flex', alignItems: 'center', gap: 10 }}>
              📄 Imprimir Planillas
            </h3>
            
            <p style={{ marginBottom: 20, color: '#666' }}>
              Seleccione el rango de meses que desea incluir en el documento de impresión. 
              Se generará un documento con todas las planillas (Cocina, Salón, Temperatura y Tanque de Reserva) de cada mes.
            </p>
            
            <div style={{ display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Desde:</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    value={mesInicioImpresion}
                    onChange={(e) => setMesInicioImpresion(Number(e.target.value))}
                    style={{ flex: 1, padding: '8px', borderRadius: 6, border: '1px solid #ddd' }}
                  >
                    {["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"].map((m, i) => (
                      <option key={i + 1} value={i + 1}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={anioInicioImpresion}
                    onChange={(e) => setAnioInicioImpresion(Number(e.target.value))}
                    style={{ width: 80, padding: '8px', borderRadius: 6, border: '1px solid #ddd' }}
                  >
                    {[2025, 2026, 2027, 2028].map(a => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Hasta:</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    value={mesFinImpresion}
                    onChange={(e) => setMesFinImpresion(Number(e.target.value))}
                    style={{ flex: 1, padding: '8px', borderRadius: 6, border: '1px solid #ddd' }}
                  >
                    {["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"].map((m, i) => (
                      <option key={i + 1} value={i + 1}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={anioFinImpresion}
                    onChange={(e) => setAnioFinImpresion(Number(e.target.value))}
                    style={{ width: 80, padding: '8px', borderRadius: 6, border: '1px solid #ddd' }}
                  >
                    {[2025, 2026, 2027, 2028].map(a => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            <div style={{ background: '#f0fdf4', padding: 15, borderRadius: 8, marginBottom: 20 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#166534' }}>
                <strong>📌 Nota:</strong> Solo se incluirán los meses que tengan planillas generadas. 
                Cada mes incluirá automáticamente las 4 planillas: Desinfección Cocina, Desinfección Salón, 
                Control de Temperatura y Tanque de Reserva (si aplica).
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setMostrarModalImpresion(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: 6,
                  border: '1px solid #ccc',
                  background: 'white',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={prepararImpresionMultiple}
                style={{
                  padding: '10px 20px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#059669',
                  color: 'white',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                🖨️ Generar e Imprimir
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Vista previa de impresión múltiple */}
      {vistaImpresion && planillasParaImprimir.length > 0 && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'white',
          zIndex: 9999,
          overflow: 'auto'
        }}>
          <div style={{
            position: 'sticky',
            top: 0,
            background: '#059669',
            padding: '15px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 10000
          }}>
            <h3 style={{ margin: 0, color: 'white' }}>
              📄 Vista Previa - {planillasParaImprimir.length} mes(es) seleccionado(s)
            </h3>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={imprimirMultiplesMeses}
                style={{
                  padding: '10px 20px',
                  borderRadius: 6,
                  border: 'none',
                  background: 'white',
                  color: '#059669',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                🖨️ Imprimir Todo
              </button>
              <button
                onClick={() => {
                  setVistaImpresion(false);
                  setPlanillasParaImprimir([]);
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: 6,
                  border: '2px solid white',
                  background: 'transparent',
                  color: 'white',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                ✕ Cerrar
              </button>
            </div>
          </div>
          
          <div style={{ padding: 20 }}>
            {planillasParaImprimir.map((datosImpresion, idx) => {
              const { mes, anio, planilla, trampaGrasaMes: trampasMes } = datosImpresion;
              const nombreMes = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"][mes - 1];
              const diasDelMes = obtenerDiasDelMes(anio, mes);
              const config = configSedes.find(c => c.sede_id === sedeSeleccionada) || {};
              
              const itemsCocinaDiario = config.items_limpieza_cocina_diario || ITEMS_LIMPIEZA_COCINA_DIARIO_DEFAULT;
              const itemsCocinaSemanal = config.items_limpieza_cocina_semanal || ITEMS_LIMPIEZA_COCINA_SEMANAL_DEFAULT;
              const itemsSalonDiario = config.items_limpieza_salon_diario || ITEMS_LIMPIEZA_SALON_DIARIO_DEFAULT;
              const itemsSalonSemanal = config.items_limpieza_salon_semanal || ITEMS_LIMPIEZA_SALON_SEMANAL_DEFAULT;
              
              // Función de fallback para obtener supervisor (solo supervisores, gerencia, administración)
              const obtenerSupervisorPreview = (tipo) => {
                const nombreGuardado = tipo === "cocina" ? planilla.supervisor_cocina_nombre : planilla.supervisor_salon_nombre;
                if (nombreGuardado && nombreGuardado !== "No asignado") return nombreGuardado;
                const listaSups = tipo === "cocina" ? supervisoresCocina : supervisoresSalon;
                if (listaSups.length > 0) return listaSups[0].nombre || `${listaSups[0].nombres || ''} ${listaSups[0].apellidos || ''}`.trim();
                const otrosSups = tipo === "cocina" ? supervisoresSalon : supervisoresCocina;
                if (otrosSups.length > 0) return otrosSups[0].nombre || `${otrosSups[0].nombres || ''} ${otrosSups[0].apellidos || ''}`.trim();
                return "Supervisor";
              };
              
              const supervisorCocina = obtenerSupervisorPreview("cocina");
              const supervisorSalon = obtenerSupervisorPreview("salon");
              
              return (
                <div key={idx} style={{ marginBottom: 40, borderBottom: idx < planillasParaImprimir.length - 1 ? '3px dashed #000' : 'none', paddingBottom: 40 }}>
                  
                  {/* DESINFECCIÓN COCINA */}
                  <div style={{ marginBottom: 30 }}>
                    {/* Encabezado con logo */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 15, borderBottom: '3px solid #000', paddingBottom: 10 }}>
                      <img src={LOGO_BIGBURGUER} alt="Logo" style={{ width: 70, height: 'auto' }} />
                      <div>
                        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#000000' }}>DESINFECCIÓN Y LIMPIEZA - COCINA</h1>
                        <h2 style={{ margin: '3px 0', fontSize: 15, color: '#000000', fontWeight: 700 }}>{nombreSedeActual}</h2>
                        <p style={{ margin: '2px 0', fontSize: 13, color: '#000000', fontWeight: 700 }}>{nombreMes} {anio}</p>
                      </div>
                    </div>
                    <p style={{ color: '#000000', fontWeight: 700 }}><strong>Revisó:</strong> {supervisorCocina}</p>
                    
                    <h4 style={{ margin: '15px 0 8px', fontSize: 13, color: '#000000', fontWeight: 900 }}>PERIODICIDAD DIARIA</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, color: '#000000' }}>
                        <thead>
                          <tr style={{ background: '#d0d0d0' }}>
                            <th style={{ border: '1px solid #000', padding: 4, minWidth: 80, fontWeight: 900, color: '#000000' }}>ÁREA</th>
                            {diasDelMes.map(d => (
                              <th key={d.dia} style={{ border: '1px solid #000', padding: 3, minWidth: 20, fontWeight: 900, color: '#000000' }}>{d.dia}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {itemsCocinaDiario.map(item => (
                            <tr key={item}>
                              <td style={{ border: '1px solid #000', padding: 4, fontWeight: 900, textAlign: 'left', color: '#000000' }}>{item.toUpperCase()}</td>
                              {diasDelMes.map(d => {
                                const nombre = planilla.datos_desinfeccion_cocina?.diario?.[item]?.[d.dia] || '';
                                return <td key={d.dia} style={{ border: '1px solid #000', padding: 3, fontWeight: 900, color: '#000000', textAlign: 'center' }}>{nombre.split(' ')[0] || '-'}</td>;
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    <h4 style={{ margin: '15px 0 8px', fontSize: 13, color: '#000000', fontWeight: 900 }}>PERIODICIDAD SEMANAL</h4>
                    <table style={{ maxWidth: 500, borderCollapse: 'collapse', fontSize: 11, color: '#000000' }}>
                      <thead>
                        <tr style={{ background: '#d0d0d0' }}>
                          <th style={{ border: '1px solid #000', padding: 5, fontWeight: 900, color: '#000000' }}>ÁREA</th>
                          <th style={{ border: '1px solid #000', padding: 5, fontWeight: 900, color: '#000000' }}>SEM 1</th>
                          <th style={{ border: '1px solid #000', padding: 5, fontWeight: 900, color: '#000000' }}>SEM 2</th>
                          <th style={{ border: '1px solid #000', padding: 5, fontWeight: 900, color: '#000000' }}>SEM 3</th>
                          <th style={{ border: '1px solid #000', padding: 5, fontWeight: 900, color: '#000000' }}>SEM 4</th>
                          <th style={{ border: '1px solid #000', padding: 5, fontWeight: 900, color: '#000000' }}>SEM 5</th>
                        </tr>
                      </thead>
                      <tbody>
                        {itemsCocinaSemanal.map(item => (
                          <tr key={item}>
                            <td style={{ border: '1px solid #000', padding: 5, fontWeight: 900, color: '#000000', textAlign: 'center' }}>{item.toUpperCase()}</td>
                            {[1,2,3,4,5].map(s => {
                              const dato = planilla.datos_desinfeccion_cocina?.semanal?.[item]?.[`semana_${s}`];
                              return <td key={s} style={{ border: '1px solid #000', padding: 4, fontSize: 10, fontWeight: 900, color: '#000000', textAlign: 'center' }}>{dato ? `${dato.empleado?.split(' ')[0] || ''} (${dato.dia})` : '-'}</td>;
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {trampasMes.length > 0 && (
                      <>
                        <h4 style={{ margin: '15px 0 8px', fontSize: 13, color: '#000000', fontWeight: 900 }}>LIMPIEZA DE TRAMPA DE GRASA</h4>
                        <table style={{ maxWidth: 400, borderCollapse: 'collapse', fontSize: 12, color: '#000000' }}>
                          <thead>
                            <tr style={{ background: '#d0d0d0' }}>
                              <th style={{ border: '1px solid #000', padding: 6, fontWeight: 900, color: '#000000' }}>FECHA</th>
                              <th style={{ border: '1px solid #000', padding: 6, fontWeight: 900, color: '#000000' }}>PROVEEDOR</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trampasMes.map(t => (
                              <tr key={t.id}>
                                <td style={{ border: '1px solid #000', padding: 6, fontWeight: 900, color: '#000000', textAlign: 'center' }}>{t.fecha}</td>
                                <td style={{ border: '1px solid #000', padding: 6, fontWeight: 900, color: '#000000', textAlign: 'center' }}>{t.proveedor}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                    
                    <div style={{ marginTop: 25, textAlign: 'center', maxWidth: 300 }}>
                      <div style={{ borderBottom: '2px solid #000', height: 35, marginBottom: 5 }}></div>
                      <p style={{ color: '#000000', fontWeight: 900 }}><strong>Revisó: {supervisorCocina}</strong></p>
                    </div>
                  </div>
                  
                  {/* DESINFECCIÓN SALÓN */}
                  <div style={{ marginBottom: 30 }}>
                    {/* Encabezado con logo */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 15, borderBottom: '3px solid #000', paddingBottom: 10 }}>
                      <img src={LOGO_BIGBURGUER} alt="Logo" style={{ width: 70, height: 'auto' }} />
                      <div>
                        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#000000' }}>DESINFECCIÓN Y LIMPIEZA - SALÓN</h1>
                        <h2 style={{ margin: '3px 0', fontSize: 15, color: '#000000', fontWeight: 700 }}>{nombreSedeActual}</h2>
                        <p style={{ margin: '2px 0', fontSize: 13, color: '#000000', fontWeight: 700 }}>{nombreMes} {anio}</p>
                      </div>
                    </div>
                    <p style={{ color: '#000000', fontWeight: 700 }}><strong>Revisó:</strong> {supervisorSalon}</p>
                    
                    <h4 style={{ margin: '15px 0 8px', fontSize: 13, color: '#000000', fontWeight: 900 }}>PERIODICIDAD DIARIA</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10, color: '#000000' }}>
                        <thead>
                          <tr style={{ background: '#d0d0d0' }}>
                            <th style={{ border: '1px solid #000', padding: 4, minWidth: 80, fontWeight: 900, color: '#000000' }}>ÁREA</th>
                            {diasDelMes.map(d => (
                              <th key={d.dia} style={{ border: '1px solid #000', padding: 3, minWidth: 20, fontWeight: 900, color: '#000000' }}>{d.dia}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {itemsSalonDiario.map(item => (
                            <tr key={item}>
                              <td style={{ border: '1px solid #000', padding: 4, fontWeight: 900, textAlign: 'left', color: '#000000' }}>{item.toUpperCase()}</td>
                              {diasDelMes.map(d => {
                                const nombre = planilla.datos_desinfeccion_salon?.diario?.[item]?.[d.dia] || '';
                                return <td key={d.dia} style={{ border: '1px solid #000', padding: 3, fontWeight: 900, color: '#000000', textAlign: 'center' }}>{nombre.split(' ')[0] || '-'}</td>;
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    <h4 style={{ margin: '15px 0 8px', fontSize: 13, color: '#000000', fontWeight: 900 }}>PERIODICIDAD SEMANAL</h4>
                    <table style={{ maxWidth: 500, borderCollapse: 'collapse', fontSize: 11, color: '#000000' }}>
                      <thead>
                        <tr style={{ background: '#d0d0d0' }}>
                          <th style={{ border: '1px solid #000', padding: 5, fontWeight: 900, color: '#000000' }}>ÁREA</th>
                          <th style={{ border: '1px solid #000', padding: 5, fontWeight: 900, color: '#000000' }}>SEM 1</th>
                          <th style={{ border: '1px solid #000', padding: 5, fontWeight: 900, color: '#000000' }}>SEM 2</th>
                          <th style={{ border: '1px solid #000', padding: 5, fontWeight: 900, color: '#000000' }}>SEM 3</th>
                          <th style={{ border: '1px solid #000', padding: 5, fontWeight: 900, color: '#000000' }}>SEM 4</th>
                          <th style={{ border: '1px solid #000', padding: 5, fontWeight: 900, color: '#000000' }}>SEM 5</th>
                        </tr>
                      </thead>
                      <tbody>
                        {itemsSalonSemanal.map(item => (
                          <tr key={item}>
                            <td style={{ border: '1px solid #000', padding: 5, fontWeight: 900, color: '#000000', textAlign: 'center' }}>{item.toUpperCase()}</td>
                            {[1,2,3,4,5].map(s => {
                              const dato = planilla.datos_desinfeccion_salon?.semanal?.[item]?.[`semana_${s}`];
                              return <td key={s} style={{ border: '1px solid #000', padding: 4, fontSize: 10, fontWeight: 900, color: '#000000', textAlign: 'center' }}>{dato ? `${dato.empleado?.split(' ')[0] || ''} (${dato.dia})` : '-'}</td>;
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    <div style={{ marginTop: 25, textAlign: 'center', maxWidth: 300 }}>
                      <div style={{ borderBottom: '2px solid #000', height: 35, marginBottom: 5 }}></div>
                      <p style={{ color: '#000000', fontWeight: 900 }}><strong>Revisó: {supervisorSalon}</strong></p>
                    </div>
                  </div>
                  
                  {/* CONTROL DE TEMPERATURA */}
                  <div style={{ marginBottom: 30 }}>
                    {/* Encabezado con logo */}
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 15, borderBottom: '2px solid #000', paddingBottom: 10 }}>
                      <img src={LOGO_BIGBURGUER} alt="Logo" style={{ height: 50, marginRight: 15 }} onError={(e) => e.target.style.display = 'none'} />
                      <div>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#000000' }}>CONTROL DE TEMPERATURA</h2>
                        <p style={{ margin: 0, fontSize: 11, color: '#000000', fontWeight: 700 }}>{nombreSedeActual} - {nombreMes} {anio}</p>
                      </div>
                    </div>
                    <p style={{ color: '#000000', fontWeight: 900 }}><strong>Supervisor Cocina:</strong> {supervisorCocina}</p>
                    
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
                        <thead>
                          <tr style={{ background: '#f0f0f0' }}>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>DÍA</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>HORA</th>
                            {Array.from({ length: config.num_congeladores || 1 }, (_, i) => (
                              <th key={`cong-${i}`} style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>CONG. {i+1}</th>
                            ))}
                            {Array.from({ length: config.num_refrigeradores || 1 }, (_, i) => (
                              <th key={`refri-${i}`} style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>REFRI. {i+1}</th>
                            ))}
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>RESP.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(planilla.datos_temperatura?.registros || []).map(reg => {
                            const congeladores = reg.equipos?.filter(e => e.tipo === "Congelación") || [];
                            const refrigeradores = reg.equipos?.filter(e => e.tipo === "Refrigeración") || [];
                            const numCong = config.num_congeladores || 1;
                            const numRefri = config.num_refrigeradores || 1;
                            return (
                              <tr key={reg.dia}>
                                <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{reg.dia}</td>
                                <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{reg.hora}</td>
                                {Array.from({ length: numCong }, (_, i) => {
                                  const eq = congeladores[i];
                                  const temp = eq?.temperatura;
                                  return (
                                    <td key={`cong-${i}`} style={{ border: '1px solid #000', padding: 2, color: temp !== undefined && temp < -5 ? '#059669' : temp !== undefined ? '#dc2626' : '#000000', fontWeight: 700, textAlign: 'center' }}>
                                      {temp !== undefined ? temp : '-'}
                                    </td>
                                  );
                                })}
                                {Array.from({ length: numRefri }, (_, i) => {
                                  const eq = refrigeradores[i];
                                  const temp = eq?.temperatura;
                                  return (
                                    <td key={`refri-${i}`} style={{ border: '1px solid #000', padding: 2, color: temp !== undefined && temp <= 4 && temp >= -2 ? '#059669' : temp !== undefined ? '#dc2626' : '#000000', fontWeight: 700, textAlign: 'center' }}>
                                      {temp !== undefined ? temp : '-'}
                                    </td>
                                  );
                                })}
                                <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{reg.responsable?.split(' ')[0] || '-'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    
                    <div style={{ marginTop: 25, textAlign: 'center', maxWidth: 300 }}>
                      <div style={{ borderBottom: '2px solid #000', height: 35, marginBottom: 5 }}></div>
                      <p style={{ color: '#000000', fontWeight: 900 }}><strong>Revisó: {supervisorCocina}</strong></p>
                    </div>
                  </div>
                  
                  {/* TANQUE DE RESERVA */}
                  {config.tiene_tanque_reserva && planilla.datos_tanque_reserva?.registros?.length > 0 && (
                    <div style={{ marginBottom: 30 }}>
                      {/* Encabezado con logo */}
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 15, borderBottom: '2px solid #000', paddingBottom: 10 }}>
                        <img src={LOGO_BIGBURGUER} alt="Logo" style={{ height: 50, marginRight: 15 }} onError={(e) => e.target.style.display = 'none'} />
                        <div>
                          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#000000' }}>ASEO TANQUE DE RESERVA</h2>
                          <p style={{ margin: 0, fontSize: 11, color: '#000000', fontWeight: 700 }}>{nombreSedeActual} - {nombreMes} {anio}</p>
                        </div>
                      </div>
                      <p style={{ color: '#000000', fontWeight: 900 }}><strong>Supervisor Salón:</strong> {supervisorSalon}</p>
                      
                      <table style={{ maxWidth: 500, borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr style={{ background: '#f0f0f0' }}>
                            <th style={{ border: '1px solid #000', padding: 6, color: '#000000', fontWeight: 900 }}>BIMESTRE</th>
                            <th style={{ border: '1px solid #000', padding: 6, color: '#000000', fontWeight: 900 }}>FECHA</th>
                            <th style={{ border: '1px solid #000', padding: 6, color: '#000000', fontWeight: 900 }}>RESPONSABLE</th>
                          </tr>
                        </thead>
                        <tbody>
                          {planilla.datos_tanque_reserva.registros.map((reg, i) => (
                            <tr key={i}>
                              <td style={{ border: '1px solid #000', padding: 6, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{reg.bimestre}</td>
                              <td style={{ border: '1px solid #000', padding: 6, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{reg.fecha}</td>
                              <td style={{ border: '1px solid #000', padding: 6, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{reg.responsable || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      
                      <div style={{ marginTop: 25, textAlign: 'center', maxWidth: 300 }}>
                        <div style={{ borderBottom: '2px solid #000', height: 35, marginBottom: 5 }}></div>
                        <p style={{ color: '#000000', fontWeight: 900 }}><strong>Revisó: {supervisorSalon}</strong></p>
                      </div>
                    </div>
                  )}
                  
                  {/* CONTROL DE RESIDUOS */}
                  <div style={{ marginBottom: 30 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 15, borderBottom: '2px solid #000', paddingBottom: 10 }}>
                      <img src={LOGO_BIGBURGUER} alt="Logo" style={{ height: 50, marginRight: 15 }} onError={(e) => e.target.style.display = 'none'} />
                      <div>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#000000' }}>REGISTRO DIARIO DE CONTROL DE RESIDUOS</h2>
                        <p style={{ margin: 0, fontSize: 11, color: '#000000', fontWeight: 700 }}>{nombreSedeActual} - {nombreMes} {anio}</p>
                      </div>
                    </div>
                    
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                        <thead>
                          <tr style={{ background: '#f0f0f0' }}>
                            <th style={{ border: '1px solid #000', padding: 4, color: '#000000', fontWeight: 900 }}>DÍA</th>
                            <th style={{ border: '1px solid #000', padding: 4, color: '#000000', fontWeight: 900 }}>APROVECHABLES</th>
                            <th style={{ border: '1px solid #000', padding: 4, color: '#000000', fontWeight: 900 }}>ORGÁNICOS</th>
                            <th style={{ border: '1px solid #000', padding: 4, color: '#000000', fontWeight: 900 }}>NO APROVECHABLES</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diasDelMes.map(d => {
                            const regRes = planilla.datos_residuos?.registros?.[d.dia] || {};
                            return (
                              <tr key={d.dia}>
                                <td style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{d.dia}</td>
                                <td style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{regRes.aprovechables || '-'}</td>
                                <td style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{regRes.organicos || '-'}</td>
                                <td style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{regRes.noAprovechables || '-'}</td>
                              </tr>
                            );
                          })}
                          <tr style={{ background: '#e5e7eb' }}>
                            <td style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900, textAlign: 'center' }}>TOTAL</td>
                            <td style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900, textAlign: 'center' }}>{Object.values(planilla.datos_residuos?.registros || {}).reduce((sum, r) => sum + (r.aprovechables || 0), 0)}</td>
                            <td style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900, textAlign: 'center' }}>{Object.values(planilla.datos_residuos?.registros || {}).reduce((sum, r) => sum + (r.organicos || 0), 0)}</td>
                            <td style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900, textAlign: 'center' }}>{Object.values(planilla.datos_residuos?.registros || {}).reduce((sum, r) => sum + (r.noAprovechables || 0), 0)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    <div style={{ marginTop: 25, textAlign: 'center', maxWidth: 300 }}>
                      <div style={{ borderBottom: '2px solid #000', height: 35, marginBottom: 5 }}></div>
                      <p style={{ color: '#000000', fontWeight: 900 }}><strong>Revisó: {planilla.revisor_residuos || supervisorCocina}</strong></p>
                    </div>
                  </div>
                  
                  {/* CONTROL DE AGUA POTABLE */}
                  <div style={{ marginBottom: 30 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 15, borderBottom: '2px solid #000', paddingBottom: 10 }}>
                      <img src={LOGO_BIGBURGUER} alt="Logo" style={{ height: 50, marginRight: 15 }} onError={(e) => e.target.style.display = 'none'} />
                      <div>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#000000' }}>REGISTRO DE CONTROL DE AGUA POTABLE</h2>
                        <p style={{ margin: 0, fontSize: 11, color: '#000000', fontWeight: 700 }}>{nombreSedeActual} - {nombreMes} {anio}</p>
                      </div>
                    </div>
                    
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
                        <thead>
                          <tr style={{ background: '#f0f0f0' }}>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>DÍA</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>HORA</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>LUGAR</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>COLOR</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>OLOR</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>SABOR</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>PH</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>CLORO</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>RESPONSABLE</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const registrosAgua = planilla.datos_agua_potable?.registros || [];
                            if (registrosAgua.length > 0) {
                              return registrosAgua.map((reg, idx) => (
                                <tr key={idx}>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{reg.dia}</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{reg.hora || '-'}</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{(reg.lugar || 'COCINA').toUpperCase()}</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{(reg.color || 'C').toUpperCase()}</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{(reg.olor || 'C').toUpperCase()}</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{(reg.sabor || 'NORMAL').toUpperCase()}</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{reg.ph || '-'}</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{reg.cloro || '-'}</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{reg.responsable?.split(' ')[0] || '-'}</td>
                                </tr>
                              ));
                            } else {
                              return diasDelMes.map(d => (
                                <tr key={d.dia}>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{d.dia}</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>-</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>COCINA</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>C</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>C</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>NORMAL</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>-</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>-</td>
                                  <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>-</td>
                                </tr>
                              ));
                            }
                          })()}
                        </tbody>
                      </table>
                    </div>
                    
                    <div style={{ marginTop: 25, textAlign: 'center', maxWidth: 300 }}>
                      <div style={{ borderBottom: '2px solid #000', height: 35, marginBottom: 5 }}></div>
                      <p style={{ color: '#000000', fontWeight: 900 }}><strong>Revisó: {planilla.revisor_agua_potable || supervisorCocina}</strong></p>
                    </div>
                  </div>
                  
                  {/* CONTROL DE PLAGAS */}
                  <div style={{ marginBottom: 30 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 15, borderBottom: '2px solid #000', paddingBottom: 10 }}>
                      <img src={LOGO_BIGBURGUER} alt="Logo" style={{ height: 50, marginRight: 15 }} onError={(e) => e.target.style.display = 'none'} />
                      <div>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: '#000000' }}>REGISTRO DE CONTROL DE PLAGAS Y ROEDORES</h2>
                        <p style={{ margin: 0, fontSize: 11, color: '#000000', fontWeight: 700 }}>{nombreSedeActual} - {nombreMes} {anio}</p>
                      </div>
                    </div>
                    
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
                        <thead>
                          <tr style={{ background: '#f0f0f0' }}>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>DÍA</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>CUC. AMERICANA</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>CUC. GERMÁNICA</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>ROEDORES</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>MOSCAS</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>AVES</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>ARAÑAS</th>
                            <th style={{ border: '1px solid #000', padding: 3, color: '#000000', fontWeight: 900 }}>ÁREA</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diasDelMes.map(d => {
                            const regPlag = planilla.datos_plagas?.registros?.[d.dia] || {};
                            return (
                              <tr key={d.dia}>
                                <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{d.dia}</td>
                                <td style={{ border: '1px solid #000', padding: 2, color: regPlag.cucarachaAmericana === 'SÍ' ? '#dc2626' : '#059669', fontWeight: 700, textAlign: 'center' }}>{regPlag.cucarachaAmericana || 'NO'}</td>
                                <td style={{ border: '1px solid #000', padding: 2, color: regPlag.cucarachaGermanica === 'SÍ' ? '#dc2626' : '#059669', fontWeight: 700, textAlign: 'center' }}>{regPlag.cucarachaGermanica || 'NO'}</td>
                                <td style={{ border: '1px solid #000', padding: 2, color: regPlag.roedores === 'SÍ' ? '#dc2626' : '#059669', fontWeight: 700, textAlign: 'center' }}>{regPlag.roedores || 'NO'}</td>
                                <td style={{ border: '1px solid #000', padding: 2, color: regPlag.moscas === 'SÍ' ? '#dc2626' : '#059669', fontWeight: 700, textAlign: 'center' }}>{regPlag.moscas || 'NO'}</td>
                                <td style={{ border: '1px solid #000', padding: 2, color: regPlag.aves === 'SÍ' ? '#dc2626' : '#059669', fontWeight: 700, textAlign: 'center' }}>{regPlag.aves || 'NO'}</td>
                                <td style={{ border: '1px solid #000', padding: 2, color: regPlag.aranas === 'SÍ' ? '#dc2626' : '#059669', fontWeight: 700, textAlign: 'center' }}>{regPlag.aranas || 'NO'}</td>
                                <td style={{ border: '1px solid #000', padding: 2, color: '#000000', fontWeight: 700, textAlign: 'center' }}>{regPlag.area || '-'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    
                    <div style={{ marginTop: 25, textAlign: 'center', maxWidth: 300 }}>
                      <div style={{ borderBottom: '2px solid #000', height: 35, marginBottom: 5 }}></div>
                      <p style={{ color: '#000000', fontWeight: 900 }}><strong>Revisó: {planilla.revisor_plagas || supervisorSalon}</strong></p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================== PROCESOS ADMINISTRATIVOS (CUADRE DE PAGOS) ====================== */
 
function CuadrePagos({ sedes, empleados, tareas, setTareas, cargando = false, usuarioActual = null, empleadoActual = null, esGerente = false, tienePermiso = () => true, filtroEmpresa = null, todosLosEmpleados = [] }) {
  const [modo, setModo] = useState("crear"); // "crear" | "consultar" | "tablero"
  const [tareaSeleccionada, setTareaSeleccionada] = useState(null);
  const [mostrarNotificaciones, setMostrarNotificaciones] = useState(false);
  const [alertaInicialMostrada, setAlertaInicialMostrada] = useState(false);
  const [notificacionesVistas, setNotificacionesVistas] = useState(false);

  // Log para depuración
  useEffect(() => {
    console.log("📋 CuadrePagos - Tareas recibidas:", tareas?.length || 0);
    console.log("📋 CuadrePagos - esGerente:", esGerente);
    console.log("📋 CuadrePagos - usuarioActual:", usuarioActual?.nombre, usuarioActual?.id);
    console.log("📋 CuadrePagos - empleadoActual:", empleadoActual?.nombre, empleadoActual?.id);
  }, [tareas, esGerente, usuarioActual, empleadoActual]);

  // Cargar configuración de SMS para tareas
  useEffect(() => {
    async function cargarConfigSMSTareas() {
      try {
        const { data } = await supabase
          .from('configuracion_sistema')
          .select('telefono_notificaciones_tareas, nombre_receptor_tareas')
          .eq('id', 'principal')
          .single();
        
        if (data) {
          setConfigSMSTareas({
            telefono: data.telefono_notificaciones_tareas || "",
            nombreReceptor: data.nombre_receptor_tareas || ""
          });
        }
      } catch (err) {
        console.warn("No se pudo cargar config SMS tareas:", err);
      }
    }
    cargarConfigSMSTareas();
  }, []);

  // Empleados para asignar responsable:
  // - Todos los empleados de la empresa actual
  // - Empleados de OTRAS empresas que tengan perfil Gerencia
  const empleadosParaResponsable = useMemo(() => {
    const empleadosEmpresaActual = empleados || [];
    const idsEmpresaActual = new Set(empleadosEmpresaActual.map(e => String(e.id)));
    
    // Filtrar gerentes de otras empresas
    const gerentesOtrasEmpresas = (todosLosEmpleados || []).filter(emp => {
      // Si ya está en la empresa actual, no lo incluimos de nuevo
      if (idsEmpresaActual.has(String(emp.id))) return false;
      
      // Verificar si tiene perfil de gerencia
      const perfil = (emp.perfil || "").toLowerCase();
      return perfil.includes("gerencia") || perfil.includes("gerente") || perfil.includes("admin");
    });
    
    // Combinar y ordenar
    const combinados = [...empleadosEmpresaActual, ...gerentesOtrasEmpresas];
    return combinados.sort((a, b) => 
      (a.nombre || "").localeCompare(b.nombre || "", 'es', { sensitivity: 'base' })
    );
  }, [empleados, todosLosEmpleados]);

  // Empleados ordenados alfabéticamente por nombre (solo empresa actual, para otros usos)
  const empleadosOrdenados = useMemo(() => 
    [...(empleados || [])].sort((a, b) => 
      (a.nombre || "").localeCompare(b.nombre || "", 'es', { sensitivity: 'base' })
    ), [empleados]
  );

  // Helpers de fecha en horario local
  function getTodayISO() {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
  }
  function getTomorrowISO() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
  }
 
  const [form, setForm] = useState({
    id: uid(),
    fechaCreacion: getTodayISO(),
    sede: "",
    tipo: "",
    fechaLimite: "",
    estado: "En gestión",
    comentariosGestion: "",
    servicioPublico: "",
    fechaVencimiento: "",
    valorPagar: "",
    queSeDanio: "",
    fueEmpleadoDanio: "no",
    empleadoId: "",
    motivo: "",
    comentarioFechas: "",
    proveedor: "",
    valorFactura: "",
    fechaReciboPedido: "",
    medioPago: "",
    comunicacionTexto: "",
    comentarioSencilla: "",
    numeroCuadres: "",
    novedades: "",
    responsableId: "",
    soportes: [], // {id, nombre, url}
    creadorId: empleadoActual?.id ? String(empleadoActual.id) : usuarioActual?.id ? String(usuarioActual.id) : "",
    creadorNombre: empleadoActual?.nombre || usuarioActual?.nombre || "",
    // Campos para liquidaciones y nuevos tipos de tarea
    todosEmpleados: false,
    empleadosSeleccionados: [], // [{empleadoId, nombreEmpleado, documento}]
    observacionesLiquidacion: "",
    // Campos para firma de documentos
    empleadosFirma: [], // [{empleadoId, nombreEmpleado, documento, documentosAdjuntos: []}]
    observacionesFirma: "",
    // Campos para tarea general
    observacionesTareaGeneral: "",
    tiempoEjecucion: "",
    // Campos para cancelaciones FUDO
    numeroPedidoFudo: "",
    empleadoCajaId: "",
    observacionesFudo: "",
    soportesFudo: [],
  });
 
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroSede, setFiltroSede] = useState("");
  const [filtroMes, setFiltroMes] = useState("");
  const [mostrarTareasConRespuesta, setMostrarTareasConRespuesta] = useState(false);
  const [previewFileCrear, setPreviewFileCrear] = useState(null);
  const [soloLectura, setSoloLectura] = useState(false);
  const [previewFileGestion, setPreviewFileGestion] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [subiendoSoporte, setSubiendoSoporte] = useState(false);
  const [subiendoAdjuntoGestion, setSubiendoAdjuntoGestion] = useState(false);
  
  // Estados para configuración de SMS de tareas
  const [mostrarConfigSMSTareas, setMostrarConfigSMSTareas] = useState(false);
  const [configSMSTareas, setConfigSMSTareas] = useState({ telefono: "", nombreReceptor: "" });
  const [guardandoConfigSMS, setGuardandoConfigSMS] = useState(false);
 
  const tiposTarea = [
    "Pago de servicios públicos",
    "Daños",
    "Solicitud empleados",
    "Pago de impuestos",
    "Pago de arrendamiento",
    "Pago de salud",
    "Reclamar incapacidad",
    "Solicitud de vacaciones",
    "Factura a pagar por transferencia",
    "Comunicación con empleado",
    "Solicitar sencilla",
    "Venta de aceite",
    "Entrega de cuadre",
    "Pago de facturas por transferencia",
    "Solicitud de firma a empleados",
    "Asignación de tarea",
    "Cambio de horario",
    "Pago de facturas en efectivo",
    "Préstamos a locales",
    "Afiliación a seguridad social",
    "Liquidar vacaciones",
    "Liquidar cesantías",
    "Liquidar primas",
    "Liquidar contrato de trabajo",
    "Realizar contrato de trabajo",
    "Firma de documentos por empleados",
    "Realizar tarea general",
    "Cancelaciones o modificaciones FUDO",
  ];
 
  const estados = [
    "En gestión",
    "Revisado",
    "Negado",
    "Aprobado",
    "Procesado",
  ];
 
  const grid2 = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px 16px",
    marginBottom: 8,
  };
 
  const grid3 = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "8px 16px",
    marginBottom: 8,
  };
 
  function setCampo(campo, valor) {
    setForm((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  }
 
  function resetForm() {
    setForm({
      id: uid(),
      fechaCreacion: getTodayISO(),
      sede: typeof sedes?.[0] === "object" ? sedes?.[0]?.nombre || "" : sedes?.[0] || "",
      tipo: "",
      fechaLimite: "",
      estado: "En gestión",
      comentariosGestion: "",
      servicioPublico: "",
      fechaVencimiento: "",
      valorPagar: "",
      queSeDanio: "",
      fueEmpleadoDanio: "no",
      empleadoId: "",
      motivo: "",
      comentarioFechas: "",
      proveedor: "",
      valorFactura: "",
      fechaReciboPedido: "",
      medioPago: "",
      comunicacionTexto: "",
      comentarioSencilla: "",
      numeroCuadres: "",
      novedades: "",
      soportes: [],
      creadorId: empleadoActual?.id ? String(empleadoActual.id) : usuarioActual?.id ? String(usuarioActual.id) : "",
      creadorNombre: empleadoActual?.nombre || usuarioActual?.nombre || "",
      // Campos para Pago de facturas en efectivo
      pagosEfectivo: [],
      // Campos para Préstamos a locales
      tipoPrestamo: "",
      valorPrestamo: "",
      insumo: "",
      cantidadInsumo: "",
      sedePrestamo: "",
      // Campos para Afiliación a seguridad social
      empleadoAfiliacion: "",
      epsActual: "",
      fondoPensionActual: "",
      beneficiarios: "",
      documentosAdjuntos: [],
      // Campos para liquidaciones (vacaciones, cesantías, primas, contrato)
      todosEmpleados: false,
      empleadosSeleccionados: [],
      observacionesLiquidacion: "",
      // Campos para firma de documentos por empleados
      empleadosFirma: [],
      observacionesFirma: "",
      // Campos para tarea general
      observacionesTareaGeneral: "",
      tiempoEjecucion: "",
      // Campos para cancelaciones FUDO
      numeroPedidoFudo: "",
      empleadoCajaId: "",
      observacionesFudo: "",
      soportesFudo: [],
    });
    setTareaSeleccionada(null);
  }
 
  async function handleArchivo(e) {
    const input = e.target;
    const file = input?.files?.[0];
    if (!file) return;

    setSubiendoSoporte(true);

    try {
      const soporte = await subirSoporteProcesoPago(file, {
        tareaId: form?.id,
        nombre: file.name,
      });

      if (!soporte) {
        alert("No se pudo preparar el soporte para guardarlo en Supabase.");
        return;
      }

      setForm((prev) => ({
        ...prev,
        soportes: [...(prev.soportes || []), soporte],
      }));
    } catch (error) {
      console.error("Error subiendo soporte de procesos administrativos", error);
      const mensaje = error?.message || "No se pudo subir el soporte. Intenta nuevamente.";
      alert(mensaje);
    } finally {
      setSubiendoSoporte(false);
      if (input) {
        input.value = "";
      }
    }
  }
 
  async function handleAdjuntoGestionInput(e) {
    const input = e.target;
    const file = input?.files?.[0];
    if (!file || !tareaSeleccionada) return;

    setSubiendoAdjuntoGestion(true);

    try {
      const soporte = await subirSoporteProcesoPago(file, {
        tareaId: tareaSeleccionada.id,
        nombre: file.name,
      });

      if (!soporte) {
        alert("No se pudo procesar el adjunto. Inténtalo nuevamente.");
        return;
      }

      setTareaSeleccionada((prev) => (prev ? { ...prev, adjuntoGestion: soporte } : prev));
    } catch (error) {
      console.error("Error subiendo adjunto de gestión", error);
      const mensaje = error?.message || "No se pudo subir el adjunto. Intenta nuevamente.";
      alert(mensaje);
    } finally {
      setSubiendoAdjuntoGestion(false);
      if (input) {
        input.value = "";
      }
    }
  }
 
  function bloquearTeclas(e) {
    const tag = e.target.tagName;
  
    // Si estoy escribiendo en un input, textarea o select…
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
      // Bloquea propagación normal
      e.stopPropagation();
  
      // Bloquea propagación inmediata (clave)
      if (e.nativeEvent.stopImmediatePropagation) {
        e.nativeEvent.stopImmediatePropagation();
      }
    }
  }
  
  // Guardar configuración de SMS para tareas
  async function guardarConfigSMSTareas() {
    if (!configSMSTareas.telefono) {
      alert("Por favor ingresa un numero de telefono");
      return;
    }
    
    setGuardandoConfigSMS(true);
    try {
      const { error } = await supabase
        .from('configuracion_sistema')
        .upsert({
          id: 'principal',
          telefono_notificaciones_tareas: configSMSTareas.telefono,
          nombre_receptor_tareas: configSMSTareas.nombreReceptor || "Administracion"
        }, { onConflict: 'id' });
      
      if (error) throw error;
      
      alert("Configuracion SMS guardada correctamente");
      setMostrarConfigSMSTareas(false);
    } catch (err) {
      console.error("Error guardando config SMS:", err);
      alert("Error al guardar configuracion: " + err.message);
    } finally {
      setGuardandoConfigSMS(false);
    }
  }
  
  // Enviar SMS de notificación de tarea
  async function enviarSMSTarea(telefono, mensaje) {
    try {
      const telefonoFormateado = formatearTelefonoColombia(telefono);
      const response = await fetch("https://intranetbigburguer.com/api/enviar-sms", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ telefono: telefonoFormateado, mensaje })
      });
      const result = await response.json();
      console.log("SMS Tarea enviado:", result);
      return result;
    } catch (err) {
      console.warn("Error enviando SMS tarea:", err);
      return { ok: false, error: err.message };
    }
  }
  
  // Notificar al admin cuando se crea una tarea
  async function notificarTareaNueva(tarea) {
    if (!configSMSTareas.telefono) return;
    
    const sedeNombre = typeof tarea.sede === "object" ? tarea.sede?.nombre || tarea.sede?.direccion || "" : tarea.sede || "";
    const tipoTarea = typeof tarea.tipo === "object" ? tarea.tipo?.nombre || "" : tarea.tipo || "";
    const fechaVenc = tarea.fechaLimite || "Sin fecha";
    
    const mensaje = `Nueva Tarea Creada
Sede: ${sedeNombre}
Tipo: ${tipoTarea}
Vencimiento: ${fechaVenc}
Revise el panel.`;
    
    return enviarSMSTarea(configSMSTareas.telefono, mensaje);
  }
  
  // Notificar al empleado cuando se le asigna una tarea
  async function notificarTareaAsignadaEmpleado(empleado, tarea) {
    if (!empleado?.telefono) return;
    
    const sedeNombre = typeof tarea.sede === "object" ? tarea.sede?.nombre || tarea.sede?.direccion || "" : tarea.sede || "";
    const tipoTarea = typeof tarea.tipo === "object" ? tarea.tipo?.nombre || "" : tarea.tipo || "";
    
    const mensaje = `Tarea Asignada
Sede: ${sedeNombre}
Tipo: ${tipoTarea}
Debe gestionarla. Ingrese al sistema.`;
    
    return enviarSMSTarea(empleado.telefono, mensaje);
  }
  
  // Notificar al radicador cuando se procesa una tarea
  async function notificarTareaProcesada(tarea, creadorTelefono) {
    if (!creadorTelefono) return;
    
    const sedeNombre = typeof tarea.sede === "object" ? tarea.sede?.nombre || tarea.sede?.direccion || "" : tarea.sede || "";
    const tipoTarea = typeof tarea.tipo === "object" ? tarea.tipo?.nombre || "" : tarea.tipo || "";
    
    const mensaje = `Tarea Procesada
Sede: ${sedeNombre}
Tipo: ${tipoTarea}
Su tarea ha sido procesada. Revise el panel.`;
    
    return enviarSMSTarea(creadorTelefono, mensaje);
  }
  
  function validarCamposObligatorios(tarea) {
    if (!tarea) return { ok: true, faltantes: [] };

    const faltantes = [];
    const obtenerTexto = (valor) => {
      if (valor == null) return "";
      if (typeof valor === "string") return valor.trim();
      if (typeof valor === "object") {
        if (Array.isArray(valor)) return valor.length ? "ok" : "";
        if (valor.nombre) return String(valor.nombre).trim();
        if (valor.id) return String(valor.id).trim();
      }
      return String(valor || "").trim();
    };

    const esTextoVacio = (valor) => obtenerTexto(valor) === "";
    const valorMonetario = tarea.valorServPublico || tarea.valorFactura || tarea.valorVenta || tarea.valorPagar || tarea.valor;
    const esValorMonetarioValido = () => {
      const texto = obtenerTexto(valorMonetario);
      if (texto === "") return false;
      const numero = Number(texto.replace(/[^0-9.,-]/g, "").replace(/,/g, "."));
      return Number.isFinite(numero) && numero > 0;
    };

    switch ((tarea.tipo || "").trim()) {
      case "Pago de servicios públicos": {
        if (esTextoVacio(tarea.servicio || tarea.servicioPublico)) {
          faltantes.push("Servicio a pagar");
        }
        if (!esValorMonetarioValido()) {
          faltantes.push("Valor del pago");
        }
        break;
      }
      case "Pago de arrendamiento": {
        if (!esValorMonetarioValido()) {
          faltantes.push("Valor del arrendamiento");
        }
        if (esTextoVacio(tarea.fechaVencimiento)) {
          faltantes.push("Fecha de vencimiento");
        }
        break;
      }
      case "Pago de salud": {
        if (!esValorMonetarioValido()) {
          faltantes.push("Valor a pagar");
        }
        if (esTextoVacio(tarea.fechaVencimiento)) {
          faltantes.push("Fecha de vencimiento");
        }
        break;
      }
      case "Daños": {
        if (esTextoVacio(tarea.queSeDanio)) {
          faltantes.push("Qué se dañó");
        }
        if (esTextoVacio(tarea.fueEmpleadoDanio)) {
          faltantes.push("¿Fue causado por un empleado?");
        }
        if (
          obtenerTexto(tarea.fueEmpleadoDanio).toLowerCase() === "si" &&
          esTextoVacio(tarea.empleadoId)
        ) {
          faltantes.push("Empleado involucrado");
        }
        break;
      }
      case "Reclamar incapacidad": {
        if (esTextoVacio(tarea.empleadoId)) {
          faltantes.push("Empleado");
        }
        if (esTextoVacio(tarea.comentarioFechas)) {
          faltantes.push("Fechas o comentario");
        }
        break;
      }
      case "Solicitud de vacaciones": {
        if (esTextoVacio(tarea.empleadoId)) {
          faltantes.push("Empleado");
        }
        if (esTextoVacio(tarea.comentarioFechas)) {
          faltantes.push("Fechas solicitadas");
        }
        break;
      }
      case "Factura a pagar por transferencia": {
        if (esTextoVacio(tarea.proveedor)) {
          faltantes.push("Proveedor");
        }
        if (!esValorMonetarioValido()) {
          faltantes.push("Valor a pagar");
        }
        break;
      }
      case "Comunicación con empleado": {
        if (esTextoVacio(tarea.empleadoId)) {
          faltantes.push("Empleado");
        }
        if (esTextoVacio(tarea.comunicacionTexto)) {
          faltantes.push("Detalle del requerimiento");
        }
        break;
      }
      case "Solicitar sencilla": {
        if (esTextoVacio(tarea.comentarioSencilla)) {
          faltantes.push("Detalle de la solicitud");
        }
        break;
      }
      case "Venta de aceite": {
        if (!esValorMonetarioValido()) {
          faltantes.push("Valor de la venta");
        }
        if (esTextoVacio(tarea.fechaVenta)) {
          faltantes.push("Fecha de la venta");
        }
        break;
      }
      case "Entrega de cuadre": {
        if (esTextoVacio(tarea.numeroCuadres)) {
          faltantes.push("Número de cuadres");
        }
        break;
      }
      case "Pago de facturas por transferencia": {
        if (esTextoVacio(tarea.proveedor)) {
          faltantes.push("Proveedor");
        }
        if (!esValorMonetarioValido()) {
          faltantes.push("Valor a pagar");
        }
        if (esTextoVacio(tarea.numeroCuenta)) {
          faltantes.push("Número de cuenta");
        }
        break;
      }
      case "Solicitud de firma a empleados": {
        if (esTextoVacio(tarea.empleadoId)) {
          faltantes.push("Empleado");
        }
        if (esTextoVacio(tarea.documentoFirmar)) {
          faltantes.push("Documento a firmar");
        }
        break;
      }
      case "Asignación de tarea": {
        if (esTextoVacio(tarea.empleadoId)) {
          faltantes.push("Empleado");
        }
        if (esTextoVacio(tarea.tareaAsignada)) {
          faltantes.push("Tarea asignada");
        }
        break;
      }
      case "Cambio de horario": {
        const empleadosHorario = tarea.empleadosCambioHorario || [];
        if (!empleadosHorario.length) {
          faltantes.push("Debe agregar al menos un empleado");
        } else {
          empleadosHorario.forEach((emp, idx) => {
            if (esTextoVacio(emp.fecha)) {
              faltantes.push(`Fecha para empleado ${idx + 1}`);
            }
            if (esTextoVacio(emp.sede)) {
              faltantes.push(`Sede para empleado ${idx + 1}`);
            }
            if (esTextoVacio(emp.horario)) {
              faltantes.push(`Horario para empleado ${idx + 1}`);
            }
          });
        }
        break;
      }
      case "Pago de facturas en efectivo": {
        const pagos = tarea.pagosEfectivo || [];
        if (!pagos.length) {
          faltantes.push("Debe agregar al menos un pago");
        }
        break;
      }
      case "Préstamos a locales": {
        if (esTextoVacio(tarea.tipoPrestamo)) {
          faltantes.push("Tipo de préstamo");
        }
        if (obtenerTexto(tarea.tipoPrestamo) === "Dinero en efectivo") {
          if (esTextoVacio(tarea.valorPrestamo)) {
            faltantes.push("Valor del préstamo");
          }
        } else if (obtenerTexto(tarea.tipoPrestamo) === "Mercancía") {
          if (esTextoVacio(tarea.insumo)) {
            faltantes.push("Nombre del insumo");
          }
          if (esTextoVacio(tarea.cantidadInsumo)) {
            faltantes.push("Cantidad");
          }
        }
        if (esTextoVacio(tarea.sedePrestamo)) {
          faltantes.push("Sede destino");
        }
        break;
      }
      case "Afiliación a seguridad social": {
        if (esTextoVacio(tarea.empleadoAfiliacion)) {
          faltantes.push("Empleado");
        }
        if (esTextoVacio(tarea.epsActual)) {
          faltantes.push("EPS actual");
        }
        if (esTextoVacio(tarea.fondoPensionActual)) {
          faltantes.push("Fondo de pensión actual");
        }
        break;
      }
      case "Liquidar vacaciones":
      case "Liquidar cesantías":
      case "Liquidar primas":
      case "Liquidar contrato de trabajo":
      case "Realizar contrato de trabajo": {
        const empleadosSel = tarea.empleadosSeleccionados || [];
        if (!tarea.todosEmpleados && empleadosSel.length === 0) {
          faltantes.push("Debe seleccionar al menos un empleado o marcar 'Todos los empleados'");
        }
        break;
      }
      case "Firma de documentos por empleados": {
        const empleadosFirma = tarea.empleadosFirma || [];
        if (empleadosFirma.length === 0) {
          faltantes.push("Debe agregar al menos un empleado para firma");
        }
        break;
      }
      case "Realizar tarea general": {
        if (esTextoVacio(tarea.observacionesTareaGeneral)) {
          faltantes.push("Observaciones / Descripción de la tarea");
        }
        break;
      }
      case "Cancelaciones o modificaciones FUDO": {
        if (esTextoVacio(tarea.numeroPedidoFudo)) {
          faltantes.push("Número de pedido");
        }
        if (esTextoVacio(tarea.empleadoCajaId)) {
          faltantes.push("Empleado en caja");
        }
        break;
      }
      default:
        break;
    }

    return { ok: faltantes.length === 0, faltantes };
  }

  function generarConsecutivo() {
    // 🔹 Generar ID único global usando timestamp + random para evitar colisiones entre empresas
    const timestamp = Date.now().toString(36).toUpperCase(); // Base36 del timestamp
    const random = Math.random().toString(36).substring(2, 6).toUpperCase(); // 4 caracteres random
    return `ADMIN-${timestamp}-${random}`;
  }

  function guardarTarea(ev) {
    ev.preventDefault();

    if (!form.sede || !form.tipo || !form.fechaCreacion || !form.fechaLimite) {
      alert("Por favor completa los campos obligatorios:\n• Sede\n• Tipo de tarea\n• Fecha límite");
      return;
    }

    // Validación: fecha límite no puede ser menor al día siguiente
    const minLimite = getTomorrowISO();
    if ((form.fechaLimite || "") < minLimite) {
      alert(`La fecha límite debe ser al menos el día siguiente (${minLimite}).`);
      return;
    }

    const validacion = validarCamposObligatorios(form);
    if (!validacion.ok) {
      alert(
        `Faltan datos obligatorios para "${form.tipo}":\n• ${validacion.faltantes.join("\n• ")}`
      );
      return;
    }

    const consecutivo = generarConsecutivo();
    const creadorId = form.creadorId || (empleadoActual?.id ? String(empleadoActual.id) : usuarioActual?.id ? String(usuarioActual.id) : "");
    const creadorNombre = form.creadorNombre || empleadoActual?.nombre || usuarioActual?.nombre || "";
    const nueva = {
      ...form,
      id: consecutivo,
      tipoModulo: "ADMIN",
      estado: "En gestión",
      creadorId,
      creadorNombre,
    };

    setTareas((prev) => [...prev, nueva]);
    alert(`Tarea guardada correctamente.\nConsecutivo: ${consecutivo}`);
    resetForm();

    setSincronizando(true);
    guardarProcesoPagoEnSupabase(nueva, filtroEmpresa)
      .then((guardada) => {
        if (!guardada) {
          alert("No se pudo sincronizar la tarea con Supabase. Intenta nuevamente.");
          return;
        }

        setTareas((prev) =>
          prev.map((t) => (t.id === guardada.id ? guardada : t))
        );
        setTareaSeleccionada((prevSel) => {
          if (!prevSel || prevSel.id !== guardada.id) return prevSel;
          return guardada;
        });

        // 📱 Enviar SMS al admin configurado cuando se crea la tarea
        notificarTareaNueva(guardada).then(result => {
          if (result?.ok) {
            console.log("SMS enviado a admin por nueva tarea");
          }
        }).catch(err => console.warn("No se pudo enviar SMS a admin:", err));

        // 📱 Enviar notificación SMS al empleado asignado (si existe)
        const empleadoIdAsignado = guardada.empleadoVentanillaId || guardada.empleadoCajaId || guardada.empleadoId || null;
        if (empleadoIdAsignado && empleados && empleados.length > 0) {
          const empleadoAsignado = empleados.find(e => String(e.id) === String(empleadoIdAsignado));
          if (empleadoAsignado) {
            notificarTareaAsignadaEmpleado(empleadoAsignado, guardada).then(result => {
              if (result?.ok) {
                console.log("SMS enviado a empleado asignado:", empleadoAsignado.nombre);
              }
            }).catch(err => console.warn("No se pudo enviar SMS a empleado:", err));
          }
        }
      })
      .catch((e) => {
        console.error("Error sincronizando tarea administrativa en Supabase", e);
      })
      .finally(() => setSincronizando(false));
  }

  function actualizarTareaDesdeConsulta(id, cambios) {
    let tareaOriginal = null;
    let tareaActualizada = null;

    setTareas((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        if ((t.estado || "").toString().trim() === "Procesado") {
          return t;
        }

        tareaOriginal = { ...t };
        tareaActualizada = { ...t, ...cambios };
        return tareaActualizada;
      })
    );

    if (!tareaActualizada) return;

    setSincronizando(true);
    guardarProcesoPagoEnSupabase(tareaActualizada, filtroEmpresa)
      .then((guardada) => {
        if (!guardada) {
          alert("No se pudo sincronizar la actualización con Supabase. Se restaurará el valor anterior.");
          if (tareaOriginal) {
            setTareas((prev) => prev.map((t) => (t.id === id ? { ...tareaOriginal } : t)));
            setTareaSeleccionada((prevSel) => {
              if (!prevSel || prevSel.id !== id) return prevSel;
              return { ...tareaOriginal };
            });
          }
          return;
        }

        setTareas((prev) =>
          prev.map((t) => (t.id === guardada.id ? guardada : t))
        );
        setTareaSeleccionada((prevSel) => {
          if (!prevSel || prevSel.id !== guardada.id) return prevSel;
          return guardada;
        });
      })
      .catch((e) => {
        console.error("Error actualizando tarea administrativa en Supabase", e);
        if (tareaOriginal) {
          setTareas((prev) => prev.map((t) => (t.id === id ? { ...tareaOriginal } : t)));
          setTareaSeleccionada((prevSel) => {
            if (!prevSel || prevSel.id !== id) return prevSel;
            return { ...tareaOriginal };
          });
        }
      })
      .finally(() => setSincronizando(false));
  }
 
  function nombreEmpleado(id) {
    const emp = (empleados || []).find((e) => e.id === id);
    return emp ? emp.nombre : "";
  }

  // Verificar si el usuario actual es el responsable, empleado o creador de la tarea seleccionada
  function esResponsableTarea(tarea) {
    if (!tarea) return false;
    const respId = tarea.responsableId || tarea.responsable || "";
    const empId = tarea.empleadoId || "";
    const creadorId = tarea.creadorId || ""; // Incluir creador
    const usuarioId = empleadoActual?.id ? String(empleadoActual.id) : (usuarioActual?.id ? String(usuarioActual.id) : "");
    const nombreUsuario = ((empleadoActual?.nombre || usuarioActual?.nombre || "") || "").toString().trim().toLowerCase();
    
    // Verificar por ID (incluyendo creador)
    if (usuarioId && (String(respId) === usuarioId || String(empId) === usuarioId || String(creadorId) === usuarioId)) return true;
    
    // Verificar por nombre
    const respName = (nombreEmpleado(respId) || "").trim().toLowerCase();
    const empName = (nombreEmpleado(empId) || "").trim().toLowerCase();
    const creadorName = (nombreEmpleado(creadorId) || tarea.creadorNombre || "").trim().toLowerCase();
    const respTexto = (typeof tarea.responsable === "string" ? tarea.responsable : "").trim().toLowerCase();
    
    if (nombreUsuario && (respName === nombreUsuario || empName === nombreUsuario || respTexto === nombreUsuario || creadorName === nombreUsuario)) return true;
    
    return false;
  }

  const tareasVisibles = useMemo(() => {
    console.log("🔍 tareasVisibles - esGerente:", esGerente, "total tareas:", tareas?.length || 0);
    
    if (esGerente) {
      console.log("✅ Usuario es gerente, mostrando todas las tareas");
      return Array.isArray(tareas) ? tareas : [];
    }

    const lista = Array.isArray(tareas) ? tareas : [];
    const idsPermitidos = new Set();
    const nombreUsuario = ((empleadoActual?.nombre || usuarioActual?.nombre || "") || "")
      .toString()
      .trim()
      .toLowerCase();

    // Agregar todos los IDs posibles del usuario actual
    if (empleadoActual?.id) {
      idsPermitidos.add(String(empleadoActual.id));
    }
    if (usuarioActual?.id) {
      idsPermitidos.add(String(usuarioActual.id));
    }
    
    console.log("🔍 IDs permitidos:", [...idsPermitidos], "nombreUsuario:", nombreUsuario);

    const filtradas = lista.filter((t) => {
      const respId = t.responsableId || t.responsable || "";
      const empId = t.empleadoId || "";
      const creadorId = t.creadorId || ""; // Incluir creador en el filtro

      const respName = (nombreEmpleado(respId) || "").trim().toLowerCase();
      const empName = (nombreEmpleado(empId) || "").trim().toLowerCase();
      const creadorName = (nombreEmpleado(creadorId) || t.creadorNombre || "").trim().toLowerCase();
      const respTexto = (typeof t.responsable === "string" ? t.responsable : "")
        .trim()
        .toLowerCase();

      // Verificar si el usuario es responsable, empleado asociado o CREADOR de la tarea
      const coincideId = idsPermitidos.has(String(respId)) || 
                         idsPermitidos.has(String(empId)) || 
                         idsPermitidos.has(String(creadorId));
      const coincideNombre =
        nombreUsuario &&
        (respName === nombreUsuario || empName === nombreUsuario || respTexto === nombreUsuario || creadorName === nombreUsuario);

      return coincideId || coincideNombre;
    });
    
    console.log("🔍 tareasVisibles - Resultado:", filtradas.length, "tareas para el usuario");
    return filtradas;
  }, [tareas, esGerente, empleadoActual, usuarioActual, empleados]);

  const pendientesVisibles = useMemo(
    () => (tareasVisibles || []).filter((t) => (t.estado || "").toString().trim().toLowerCase() !== "procesado"),
    [tareasVisibles]
  );

  const notificaciones = useMemo(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    const items = pendientesVisibles.map((t) => {
      const fecha = parseDate(t.fechaLimite || t.fechaCreacion || "");
      let dias = null;
      if (fecha) {
        fecha.setHours(0, 0, 0, 0);
        const diffMs = fecha.getTime() - hoy.getTime();
        dias = Math.round(diffMs / (1000 * 60 * 60 * 24));
      }
      return {
        id: t.id,
        tipo: typeof t.tipo === "object" ? t.tipo?.nombre || "" : t.tipo || "Tarea",
        fechaLimite: t.fechaLimite || "Sin fecha",
        diasRestantes: dias,
        estado: t.estado || "",
      };
    });

    return items.sort((a, b) => {
      const da = a.diasRestantes ?? 9999;
      const db = b.diasRestantes ?? 9999;
      return da - db;
    });
  }, [pendientesVisibles]);

  useEffect(() => {
    setNotificacionesVistas(false);
  }, [notificaciones.length]);

  useEffect(() => {
    if (alertaInicialMostrada) return;
    if (!pendientesVisibles.length) return;

    const proximas = notificaciones.filter((n) => n.diasRestantes !== null && n.diasRestantes <= 1);
    const mensaje = proximas.length
      ? `Tienes ${pendientesVisibles.length} tareas pendientes. ${proximas.length} vencen en 1 día o menos.`
      : `Tienes ${pendientesVisibles.length} tareas pendientes por procesar.`;

    try {
      alert(mensaje);
    } catch (e) {
      console.warn("No se pudo mostrar alerta inicial", e);
    }
    setAlertaInicialMostrada(true);
  }, [pendientesVisibles, notificaciones, alertaInicialMostrada]);

  function parseDate(iso) {
    if (!iso) return null;
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  // ---------- VISTA CREAR ----------
  const renderVistaCrear = () => {
    return (
      <form
        onSubmit={guardarTarea}
        
        style={{
          background: "#f3f7fc",
          border: "1px solid #d1e3ff",
          padding: 16,
          borderRadius: 8,
          fontSize: 13,
        }}
      >
 
        {/* CABECERA */}
        <div
          style={{
            background:
              "linear-gradient(90deg, #1d4ed8 0%, #0ea5e9 60%, #22c55e 100%)",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            color: "white",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>Procesos administrativos – Crear tarea</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button
                type="button"
                onClick={() => setMostrarConfigSMSTareas(true)}
                style={{
                  background: "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontSize: 11,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4
                }}
              >
                SMS Notificaciones
              </button>
              <span style={{ fontSize: 12 }}>
                Estado inicial: <strong>En gestion</strong>
              </span>
            </div>
          </div>
        </div>
 
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* DATOS GENERALES */}
          <div>
            <h3 style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 8 }}>
              Datos generales
            </h3>
            <div style={grid3}>
              <Field label="Fecha de creación">
                <div
                  style={{
                    width: "100%",
                    fontSize: 13,
                    padding: "6px 8px",
                    borderRadius: 4,
                    border: "1px solid #cbd5e1",
                    background: "#e8eef7",
                    fontWeight: 500,
                    color: "#1e293b",
                  }}
                >
                  {form.fechaCreacion}
                </div>
              </Field>
 
              <Field label="Sede">
                <select
                  style={{
                    width: "100%",
                    fontSize: 13,
                    padding: "6px",
                    borderRadius: 4,
                    border: "1px solid #cbd5e1",
                  }}
                  value={typeof form.sede === "object" ? form.sede?.nombre || "" : form.sede || ""}
                  onChange={(e) => setCampo("sede", e.target.value)}
                >
                  <option value="">-- Selecciona --</option>
                  {(sedes || []).map((sedeItem) => {
                    const key =
                      typeof sedeItem === "string"
                        ? sedeItem
                        : sedeItem?.id || sedeItem?.nombre || JSON.stringify(sedeItem);
                    const value =
                      typeof sedeItem === "string"
                        ? sedeItem
                        : sedeItem?.nombre || "";
                    const label =
                      typeof sedeItem === "string"
                        ? sedeItem
                        : sedeItem?.nombre || sedeItem?.direccion || "(Sin nombre)";
                    return (
                      <option key={key} value={value}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </Field>
 
              <Field label="Fecha límite">
                <Input
                  type="date"
                  value={form.fechaLimite || ""}
                  min={getTomorrowISO()}
                  onChange={(e) => setCampo("fechaLimite", e.target.value)}
                />
              </Field>
            </div>
          </div>
 
          {/* TIPO DE TAREA */}
          <div>
            <h3 style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 8 }}>
              Tipo de tarea
            </h3>
            <div style={grid2}>
              <Field label="Tarea a realizar">
                <select
                  style={{
                    width: "100%",
                    fontSize: 13,
                    padding: "6px",
                    borderRadius: 4,
                    border: "1px solid #cbd5e1",
                  }}
                  value={form.tipo}
                  onChange={(e) => {
                    const tipo = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      tipo,
                      fueEmpleadoDanio: "no",
                      empleadoId: "",
                      queSeDanio: "",
                      comentarioFechas: "",
                      proveedor: "",
                      medioPago: "",
                      comunicacionTexto: "",
                      comentarioSencilla: "",
                      numeroCuadres: "",
                      novedades: "",
                      valorPagar: "",
                      fechaVencimiento: "",
                      valorFactura: "",
                      fechaReciboPedido: "",
                      motivo: "",
                      soportes: [],
                      // Nuevos campos para nuevos tipos de tarea
                      numeroCuenta: "",
                      documentoFirmar: "",
                      tareaAsignada: "",
                      empleadosCambioHorario: [],
                      // Campos para liquidaciones
                      todosEmpleados: false,
                      empleadosSeleccionados: [],
                      observacionesLiquidacion: "",
                      // Campos para firma de documentos
                      empleadosFirma: [],
                      observacionesFirma: "",
                      // Campos para tarea general
                      observacionesTareaGeneral: "",
                      tiempoEjecucion: "",
                      // Campos para FUDO
                      numeroPedidoFudo: "",
                      empleadoCajaId: "",
                      observacionesFudo: "",
                      soportesFudo: [],
                    }));
                  }}
                >
                  <option value="">-- Selecciona --</option>
                  {tiposTarea.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
 
              <Field label="Estado actual">
                <select
                  disabled
                  style={{
                    width: "100%",
                    fontSize: 12,
                    padding: "4px",
                    borderRadius: 4,
                    border: "1px solid #cbd5e1",
                    background: "#e5e7eb",
                  }}
                  value="En gestión"
                >
                  <option>En gestión</option>
                </select>
              </Field>
            </div>
 
            {/* CAMPOS CONDICIONALES POR TIPO */}
 
            {form.tipo === "Pago de servicios públicos" && (
  <>
 
    {/* FILA HORIZONTAL */}
    <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
      
      {/* SERVICIO */}
      <Field label="Servicio" style={{ flex: 1 }}>
        <input
          type="text"
          className="input"
          value={form.servicio || ""}
          onChange={(e) => setCampo("servicio", e.target.value)}
        />
      </Field>
 
      {/* VALOR */}
      <Field label="Valor" style={{ flex: 1 }}>
        <input
          type="number"
          className="input"
          value={form.valorServPublico || ""}
          onChange={(e) => setCampo("valorServPublico", e.target.value)}
        />
      </Field>
 
      {/* REFERENCIA DE PAGO */}
      <Field label="Referencia" style={{ flex: 1 }}>
        <input
          type="text"
          className="input"
          value={form.referenciaPago || ""}
          onChange={(e) => setCampo("referenciaPago", e.target.value)}
        />
      </Field>
 
    </div>
 
    {/* SUBIR SOPORTE */}
    <Field label="Adjuntar soporte (opcional)">
      <div style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
      }}>
        <input 
          type="file" 
          onChange={handleArchivo}
          style={{ flex: 1 }}
        />
        {form.soportes && form.soportes.length > 0 && (
          <div className="mt-1 text-xs text-slate-600">
            {form.soportes.map((s) => (
              <div key={s.id}>
                📎 {s.nombre}
              </div>
            ))}
          </div>
        )}
      </div>
    </Field>
  </>
            )}

            {/* Venta de aceite */}
            {form.tipo === "Venta de aceite" && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                  <Field label="Valor de la venta">
                    <input
                      type="number"
                      className="input"
                      value={form.valorVenta || ""}
                      onChange={(e) => setCampo("valorVenta", e.target.value)}
                      min={0}
                      placeholder="Monto en pesos"
                    />
                  </Field>
                  <Field label="Fecha de venta">
                    <input
                      type="date"
                      className="input"
                      value={form.fechaVenta || ""}
                      onChange={(e) => setCampo("fechaVenta", e.target.value)}
                    />
                  </Field>
                </div>
                <Field label="Adjuntar soporte de venta">
                  <input type="file" onChange={handleArchivo} />
                  {form.soportes && form.soportes.length > 0 && (
                    <div className="mt-1 text-xs text-slate-600">
                      {form.soportes.map((s) => (
                        <div key={s.id}>
                          📎 {s.nombre}
                        </div>
                      ))}
                    </div>
                  )}
                </Field>
              </div>
            )}
 
            {/* Solicitud empleados */}
            {form.tipo === "Solicitud empleados" && (
              <div style={{ marginTop: 8 }}>
                <div style={grid2}>
                  <Field label="Empleado">
                    <select
                      style={{
                        width: "100%",
                        fontSize: 12,
                        padding: "4px",
                        borderRadius: 4,
                        border: "1px solid #cbd5e1",
                      }}
                      value={form.empleadoId}
                      onChange={(e) =>
                        setCampo("empleadoId", e.target.value)
                      }
                    >
                      <option value="">-- Selecciona empleado --</option>
                      {empleadosOrdenados.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.nombre}
                        </option>
                      ))}
                    </select>
                  </Field>
 
                  <Field label="Motivo">
                    <Input
                      value={typeof form.motivo === 'object' ? form.motivo?.nombre || '' : form.motivo || ''}
                      onChange={(e) => setCampo("motivo", e.target.value)}
                    />
                  </Field>
                </div>
 
                <Field label="Adjuntar evidencias (opcional)">
                  <Input type="file" onChange={handleArchivo} />
                </Field>
              </div>
            )}
 
            {/* Pago de impuestos */}
            {form.tipo === "Pago de impuestos" && (
              <div style={{ marginTop: 8 }}>
                <div style={grid2}>
                  <Field label="Fecha de vencimiento">
                    <Input
                      type="date"
                      value={form.fechaVencimiento || ""}
                      onChange={(e) =>
                        setCampo("fechaVencimiento", e.target.value)
                      }
                    />
                  </Field>
 
                  <Field label="Valor a pagar">
                    <Input
                      type="number"
                      value={form.valorPagar || ""}
                      onChange={(e) =>
                        setCampo("valorPagar", e.target.value)
                      }
                    />
                  </Field>
                </div>
              </div>
            )}
 
            {/* Pago de arrendamiento */}
            {form.tipo === "Pago de arrendamiento" && (
              <div style={{ marginTop: 8 }}>
                <div style={grid2}>
                  <Field label="Fecha de vencimiento">
                    <Input
                      type="date"
                      value={form.fechaVencimiento || ""}
                      onChange={(e) =>
                        setCampo("fechaVencimiento", e.target.value)
                      }
                    />
                  </Field>
 
                  <Field label="Valor">
                    <Input
                      type="number"
                      value={form.valorPagar || ""}
                      onChange={(e) =>
                        setCampo("valorPagar", e.target.value)
                      }
                    />
                  </Field>
                </div>
              </div>
            )}
 
            {/* Pago de salud */}
            {form.tipo === "Pago de salud" && (
              <div style={{ marginTop: 8 }}>
                <div style={grid2}>
                  <Field label="Fecha de vencimiento">
                    <Input
                      type="date"
                      value={form.fechaVencimiento || ""}
                      onChange={(e) =>
                        setCampo("fechaVencimiento", e.target.value)
                      }
                    />
                  </Field>
 
                  <Field label="Valor">
                    <Input
                      type="number"
                      value={form.valorPagar || ""}
                      onChange={(e) =>
                        setCampo("valorPagar", e.target.value)
                      }
                    />
                  </Field>
                </div>
              </div>
            )}
 
            {form.tipo === "Daños" && (
              <div style={{ marginTop: 8 }}>
                <Field label="¿Qué se dañó?">
                  <input
                    type="text"
                    className="input"
                    value={typeof form.queSeDanio === "object" ? form.queSeDanio?.nombre || "" : form.queSeDanio || ""}
                    onChange={(e) => setCampo("queSeDanio", e.target.value)}
                    placeholder="Describe el elemento afectado"
                  />
                </Field>

                <Field label="¿Fue causado por un empleado?">
                  <select
                    value={(form.fueEmpleadoDanio || "no").toString().toLowerCase()}
                    onChange={(e) => setCampo("fueEmpleadoDanio", e.target.value)}
                    style={{
                      width: "100%",
                      fontSize: 12,
                      padding: "4px",
                      borderRadius: 4,
                      border: "1px solid #cbd5e1",
                    }}
                  >
                    <option value="no">No</option>
                    <option value="si">Sí</option>
                  </select>
                </Field>

                {(form.fueEmpleadoDanio || "no").toString().toLowerCase() === "si" && (
                  <Field label="Empleado involucrado">
                    <select
                      value={form.empleadoId || ""}
                      onChange={(e) => setCampo("empleadoId", e.target.value)}
                      style={{
                        width: "100%",
                        fontSize: 12,
                        padding: "4px",
                        borderRadius: 4,
                        border: "1px solid #cbd5e1",
                      }}
                    >
                      <option value="">-- Selecciona empleado --</option>
                      {empleadosOrdenados.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.nombre}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </div>
            )}

            {/* Reclamar incapacidad */}
            {form.tipo === "Reclamar incapacidad" && (
              <div style={{ marginTop: 8 }}>
                <div style={grid2}>
                  <Field label="Empleado">
                    <select
                      style={{
                        width: "100%",
                        fontSize: 12,
                        padding: "4px",
                        borderRadius: 4,
                        border: "1px solid #cbd5e1",
                      }}
                      value={form.empleadoId}
                      onChange={(e) =>
                        setCampo("empleadoId", e.target.value)
                      }
                    >
                      <option value="">-- Selecciona empleado --</option>
                      {empleadosOrdenados.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.nombre}
                        </option>
                      ))}  
                    </select>
                  </Field>

                  <Field label="Fechas / comentario">
                    <textarea
                      className="input"
                      style={{ fontSize: 12, minHeight: 40 }}
                      value={typeof form.comentarioFechas === 'object' ? form.comentarioFechas?.nombre || '' : form.comentarioFechas || ''}
                      onChange={(e) =>
                        setCampo("comentarioFechas", e.target.value)
                      }
                    />
                  </Field>
                </div>
              </div>
            )}

            {/* Solicitud de vacaciones */}
            {form.tipo === "Solicitud de vacaciones" && (
              <div style={{ marginTop: 8 }}>
                <div style={grid2}>
                  <Field label="Empleado">
                    <select
                      style={{
                        width: "100%",
                        fontSize: 12,
                        padding: "4px",
                        borderRadius: 4,
                        border: "1px solid #cbd5e1",
                      }}
                      value={form.empleadoId}
                      onChange={(e) =>
                        setCampo("empleadoId", e.target.value)
                      }
                    >
                      <option value="">-- Selecciona empleado --</option>
                      {empleadosOrdenados.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.nombre}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Fechas / comentario">
                    <textarea
                      className="input"
                      style={{ fontSize: 12, minHeight: 40 }}
                      value={typeof form.comentarioFechas === 'object' ? form.comentarioFechas?.nombre || '' : form.comentarioFechas || ''}
                      onChange={(e) =>
                        setCampo("comentarioFechas", e.target.value)
                      }
                    />
                  </Field>
                </div>
              </div>
            )}            {/* Factura a pagar por transferencia */}
            {form.tipo === "Factura a pagar por transferencia" && (
              <div style={{ marginTop: 8 }}>
                <div style={grid3}>
                  <Field label="Proveedor">
                    <Input
                      value={typeof form.proveedor === 'object' ? form.proveedor?.nombre || '' : form.proveedor || ''}
                      onChange={(e) =>
                        setCampo("proveedor", e.target.value)
                      }
                    />
                  </Field>
 
                  {/* VALOR A PAGAR */}
                  <Field label="Valor a pagar">
                    <input
                      type="number"
                      className="input"
                      value={form.valorServPublico || ""}
                      onChange={(e) => setCampo("valorServPublico", e.target.value)}
                    />
                  </Field>
 
                  <Field label="Fecha recibo del pedido">
                    <Input
                      type="date"
                      value={form.fechaReciboPedido || ""}
                      onChange={(e) =>
                        setCampo("fechaReciboPedido", e.target.value)
                      }
                    />
                  </Field>
                </div>

                {/* REFERENCIA DE PAGO - Opcional */}
                <Field label="Referencia de pago (opcional)">
                  <input
                    type="text"
                    className="input"
                    placeholder="Letras o números"
                    value={form.referenciaPago || ""}
                    onChange={(e) => setCampo("referenciaPago", e.target.value)}
                  />
                </Field>

                {/* DATOS PARA EL PAGO */}
                <Field label="💳 Datos para el pago">
                  <textarea
                    className="input"
                    style={{ fontSize: 12, minHeight: 60 }}
                    placeholder="Ej: Banco Bancolombia, Cuenta Ahorros 123456789, NIT 900123456"
                    value={form.datosPago || ""}
                    onChange={(e) => setCampo("datosPago", e.target.value)}
                  />
                </Field>

                <Field label="Subir soporte">
                  <div style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                  }}>
                    <input 
                      type="file" 
                      onChange={handleArchivo}
                      style={{
                        flex: 1,
                        padding: "6px",
                        fontSize: 12,
                        border: "1px solid #cbd5e1",
                        borderRadius: 4,
                        cursor: "pointer",
                      }}
                    />
                    <span style={{ fontSize: 11, color: "#64748b" }}>📎</span>
                  </div>
                </Field>
              </div>
            )}
 
            {/* Comunicación con empleado */}
            {form.tipo === "Comunicación con empleado" && (
              <div style={{ marginTop: 8 }}>
                <div style={grid2}>
                  <Field label="Empleado">
                    <select
                      style={{
                        width: "100%",
                        fontSize: 12,
                        padding: "4px",
                        borderRadius: 4,
                        border: "1px solid #cbd5e1",
                      }}
                      value={form.empleadoId}
                      onChange={(e) =>
                        setCampo("empleadoId", e.target.value)
                      }
                    >
                      <option value="">-- Selecciona empleado --</option>
                      {empleadosOrdenados.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.nombre}
                        </option>
                      ))}
                    </select>
                  </Field>
 
                  <Field label="Requerimiento">
  <textarea
    className="input"
    style={{ fontSize: 12, minHeight: 40 }}
    value={typeof form.comunicacionTexto === 'object' ? form.comunicacionTexto?.nombre || '' : form.comunicacionTexto || ''}
    onChange={(e) =>
      setCampo("comunicacionTexto", e.target.value)
    }
  />
</Field>
 
                </div>
              </div>
            )}
 
            {/* Solicitar sencilla */}
            {form.tipo === "Solicitar sencilla" && (
              <div style={{ marginTop: 8 }}>
                <Field label="Detalle de la solicitud">
  <textarea
    className="input"
    style={{ fontSize: 12, minHeight: 40 }}
    value={typeof form.comentarioSencilla === 'object' ? form.comentarioSencilla?.nombre || '' : form.comentarioSencilla || ''}
    onChange={(e) =>
      setCampo("comentarioSencilla", e.target.value)
    }
  />
</Field>
 
              </div>
            )}

            {form.tipo === "Entrega de cuadre" && (
              <div style={{ marginTop: 8 }}>
                <Field label="Número de cuadres">
                  <Input
                    type="text"
                    value={form.numeroCuadres || ""}
                    onChange={(e) => setCampo("numeroCuadres", e.target.value)}
                    placeholder="Ej. 10-AB"
                  />
                </Field>

                <Field label="Novedades">
                  <textarea
                    className="input"
                    style={{ fontSize: 12, minHeight: 40 }}
                    value={form.novedades || ""}
                    onChange={(e) => setCampo("novedades", e.target.value)}
                    placeholder="Observaciones relevantes"
                  />
                </Field>
              </div>
            )}

            {/* ========== PAGO DE FACTURAS POR TRANSFERENCIA ========== */}
            {form.tipo === "Pago de facturas por transferencia" && (
              <div style={{ marginTop: 8 }}>
                <div style={grid2}>
                  <Field label="Proveedor">
                    <Input
                      value={form.proveedor || ""}
                      onChange={(e) => setCampo("proveedor", e.target.value)}
                      placeholder="Nombre del proveedor"
                    />
                  </Field>

                  <Field label="Valor a pagar">
                    <Input
                      type="number"
                      value={form.valorPagar || ""}
                      onChange={(e) => setCampo("valorPagar", e.target.value)}
                      placeholder="0"
                    />
                  </Field>
                </div>

                <div style={grid2}>
                  <Field label="Número de cuenta">
                    <Input
                      value={form.numeroCuenta || ""}
                      onChange={(e) => setCampo("numeroCuenta", e.target.value)}
                      placeholder="Número de cuenta bancaria"
                    />
                  </Field>

                  <Field label="Subir archivo adjunto">
                    <input 
                      type="file" 
                      onChange={handleArchivo}
                      style={{
                        padding: "6px",
                        fontSize: 12,
                        border: "1px solid #cbd5e1",
                        borderRadius: 4,
                        cursor: "pointer",
                        width: "100%",
                      }}
                    />
                  </Field>
                </div>

                <Field label="Novedades">
                  <textarea
                    className="input"
                    style={{ fontSize: 12, minHeight: 50 }}
                    value={form.novedades || ""}
                    onChange={(e) => setCampo("novedades", e.target.value)}
                    placeholder="Observaciones adicionales"
                  />
                </Field>
              </div>
            )}

            {/* ========== SOLICITUD DE FIRMA A EMPLEADOS ========== */}
            {form.tipo === "Solicitud de firma a empleados" && (
              <div style={{ marginTop: 8 }}>
                <div style={grid2}>
                  <Field label="Empleado">
                    <select
                      style={{
                        width: "100%",
                        fontSize: 12,
                        padding: "4px",
                        borderRadius: 4,
                        border: "1px solid #cbd5e1",
                      }}
                      value={form.empleadoId || ""}
                      onChange={(e) => setCampo("empleadoId", e.target.value)}
                    >
                      <option value="">-- Selecciona empleado --</option>
                      {empleadosOrdenados.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.nombre}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Documento a firmar">
                    <Input
                      value={form.documentoFirmar || ""}
                      onChange={(e) => setCampo("documentoFirmar", e.target.value)}
                      placeholder="Nombre o descripción del documento"
                    />
                  </Field>
                </div>

                <Field label="Archivo adjunto">
                  <input 
                    type="file" 
                    onChange={handleArchivo}
                    style={{
                      padding: "6px",
                      fontSize: 12,
                      border: "1px solid #cbd5e1",
                      borderRadius: 4,
                      cursor: "pointer",
                      width: "100%",
                    }}
                  />
                </Field>
              </div>
            )}

            {/* ========== ASIGNACIÓN DE TAREA ========== */}
            {form.tipo === "Asignación de tarea" && (
              <div style={{ marginTop: 8 }}>
                <Field label="Empleado">
                  <select
                    style={{
                      width: "100%",
                      fontSize: 12,
                      padding: "4px",
                      borderRadius: 4,
                      border: "1px solid #cbd5e1",
                    }}
                    value={form.empleadoId || ""}
                    onChange={(e) => setCampo("empleadoId", e.target.value)}
                  >
                    <option value="">-- Selecciona empleado --</option>
                    {empleadosOrdenados.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.nombre}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Tarea asignada">
                  <textarea
                    className="input"
                    style={{ fontSize: 12, minHeight: 60 }}
                    value={form.tareaAsignada || ""}
                    onChange={(e) => setCampo("tareaAsignada", e.target.value)}
                    placeholder="Describa la tarea que se asigna al empleado"
                  />
                </Field>
              </div>
            )}

            {/* ========== CAMBIO DE HORARIO ========== */}
            {form.tipo === "Cambio de horario" && (
              <div style={{ marginTop: 8 }}>
                <div style={{ marginBottom: 12 }}>
                  <Field label="Agregar empleado">
                    <div style={{ display: "flex", gap: 8 }}>
                      <select
                        id="selectEmpleadoCambioHorario"
                        style={{
                          flex: 1,
                          fontSize: 12,
                          padding: "4px",
                          borderRadius: 4,
                          border: "1px solid #cbd5e1",
                        }}
                      >
                        <option value="">-- Selecciona empleado --</option>
                        {[...(empleados || [])].filter(emp => 
                          !(form.empleadosCambioHorario || []).some(e => e.empleadoId === emp.id)
                        ).sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.nombre}
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        onClick={() => {
                          const select = document.getElementById("selectEmpleadoCambioHorario");
                          const empleadoId = select?.value;
                          if (!empleadoId) {
                            alert("Selecciona un empleado");
                            return;
                          }
                          const emp = (empleados || []).find(e => e.id === empleadoId);
                          setForm(prev => ({
                            ...prev,
                            empleadosCambioHorario: [
                              ...(prev.empleadosCambioHorario || []),
                              {
                                empleadoId,
                                nombreEmpleado: emp?.nombre || "",
                                fecha: "",
                                sede: "",
                                horario: "",
                              }
                            ]
                          }));
                          if (select) select.value = "";
                        }}
                      >
                        + Agregar
                      </Button>
                    </div>
                  </Field>
                </div>

                {/* Lista de empleados agregados */}
                {(form.empleadosCambioHorario || []).length > 0 && (
                  <div style={{ 
                    border: "1px solid #e2e8f0", 
                    borderRadius: 6, 
                    padding: 12, 
                    background: "#f8fafc",
                    marginTop: 8 
                  }}>
                    <h4 style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: "#334155" }}>
                      Empleados con cambio de horario ({(form.empleadosCambioHorario || []).length})
                    </h4>
                    {(form.empleadosCambioHorario || []).map((item, idx) => (
                      <div 
                        key={idx} 
                        style={{ 
                          background: "white", 
                          border: "1px solid #d1d5db", 
                          borderRadius: 6, 
                          padding: 10,
                          marginBottom: 8
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <span style={{ fontWeight: 600, fontSize: 12, color: "#0f172a" }}>
                            {item.nombreEmpleado || "Empleado"}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setForm(prev => ({
                                ...prev,
                                empleadosCambioHorario: (prev.empleadosCambioHorario || []).filter((_, i) => i !== idx)
                              }));
                            }}
                            style={{
                              background: "#ef4444",
                              color: "white",
                              border: "none",
                              borderRadius: 4,
                              padding: "2px 8px",
                              fontSize: 10,
                              cursor: "pointer",
                            }}
                          >
                            Eliminar
                          </button>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                          <Field label="Fecha">
                            <Input
                              type="date"
                              value={item.fecha || ""}
                              onChange={(e) => {
                                const nuevos = [...(form.empleadosCambioHorario || [])];
                                nuevos[idx] = { ...nuevos[idx], fecha: e.target.value };
                                setForm(prev => ({ ...prev, empleadosCambioHorario: nuevos }));
                              }}
                            />
                          </Field>
                          <Field label="Sede">
                            <select
                              style={{
                                width: "100%",
                                fontSize: 12,
                                padding: "4px",
                                borderRadius: 4,
                                border: "1px solid #cbd5e1",
                              }}
                              value={item.sede || ""}
                              onChange={(e) => {
                                const nuevos = [...(form.empleadosCambioHorario || [])];
                                nuevos[idx] = { ...nuevos[idx], sede: e.target.value };
                                setForm(prev => ({ ...prev, empleadosCambioHorario: nuevos }));
                              }}
                            >
                              <option value="">-- Sede --</option>
                              {(sedes || []).map((sedeItem) => {
                                const value = typeof sedeItem === "string" ? sedeItem : sedeItem?.nombre || "";
                                return (
                                  <option key={value} value={value}>
                                    {value}
                                  </option>
                                );
                              })}
                            </select>
                          </Field>
                          <Field label="Horario">
                            <Input
                              value={item.horario || ""}
                              onChange={(e) => {
                                const nuevos = [...(form.empleadosCambioHorario || [])];
                                nuevos[idx] = { ...nuevos[idx], horario: e.target.value };
                                setForm(prev => ({ ...prev, empleadosCambioHorario: nuevos }));
                              }}
                              placeholder="Ej: 8am - 5pm"
                            />
                          </Field>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ========== PAGO DE FACTURAS EN EFECTIVO ========== */}
            {form.tipo === "Pago de facturas en efectivo" && (
              <div style={{ marginTop: 8 }}>
                <div style={{ marginBottom: 12 }}>
                  <Field label="Agregar pago">
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                      <div style={{ flex: 1, minWidth: 150 }}>
                        <label style={{ fontSize: 11, color: "#64748b" }}>Proveedor</label>
                        <input
                          id="inputProveedorEfectivo"
                          type="text"
                          className="input"
                          placeholder="Nombre del proveedor"
                          style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                        />
                      </div>
                      <div style={{ width: 120 }}>
                        <label style={{ fontSize: 11, color: "#64748b" }}>Valor</label>
                        <input
                          id="inputValorEfectivo"
                          type="number"
                          className="input"
                          placeholder="$0"
                          style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                        />
                      </div>
                      <div style={{ width: 130 }}>
                        <label style={{ fontSize: 11, color: "#64748b" }}>Fecha factura</label>
                        <input
                          id="inputFechaFacturaEfectivo"
                          type="date"
                          className="input"
                          style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                        />
                      </div>
                      <div style={{ width: 130 }}>
                        <label style={{ fontSize: 11, color: "#64748b" }}>Factura entregada</label>
                        <select
                          id="selectFacturaEntregada"
                          style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                        >
                          <option value="">-- Selecciona --</option>
                          <option value="si">Sí</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                      <Button
                        type="button"
                        onClick={() => {
                          const proveedor = document.getElementById("inputProveedorEfectivo")?.value?.trim();
                          const valor = document.getElementById("inputValorEfectivo")?.value?.trim();
                          const fechaFactura = document.getElementById("inputFechaFacturaEfectivo")?.value;
                          const facturaEntregada = document.getElementById("selectFacturaEntregada")?.value;
                          
                          if (!proveedor || !valor || !fechaFactura || !facturaEntregada) {
                            alert("Completa todos los campos: Proveedor, Valor, Fecha de factura y Factura entregada");
                            return;
                          }
                          
                          setForm(prev => ({
                            ...prev,
                            pagosEfectivo: [
                              ...(prev.pagosEfectivo || []),
                              {
                                id: uid(),
                                proveedor,
                                valor: parseFloat(valor),
                                fechaFactura,
                                facturaEntregada,
                              }
                            ]
                          }));
                          
                          document.getElementById("inputProveedorEfectivo").value = "";
                          document.getElementById("inputValorEfectivo").value = "";
                          document.getElementById("inputFechaFacturaEfectivo").value = "";
                          document.getElementById("selectFacturaEntregada").value = "";
                        }}
                      >
                        + Agregar
                      </Button>
                    </div>
                  </Field>
                </div>

                {/* Lista de pagos agregados */}
                {(form.pagosEfectivo || []).length > 0 && (
                  <div style={{ 
                    border: "1px solid #ffe4c9", 
                    borderRadius: 8, 
                    padding: 12, 
                    background: "#fffbf5",
                    marginTop: 8 
                  }}>
                    <h4 style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: "#ff6b35" }}>
                      Pagos registrados ({(form.pagosEfectivo || []).length})
                    </h4>
                    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "#fff3e0" }}>
                          <th style={{ padding: 8, textAlign: "left", borderBottom: "1px solid #ffe4c9" }}>Proveedor</th>
                          <th style={{ padding: 8, textAlign: "right", borderBottom: "1px solid #ffe4c9" }}>Valor</th>
                          <th style={{ padding: 8, textAlign: "center", borderBottom: "1px solid #ffe4c9" }}>Fecha</th>
                          <th style={{ padding: 8, textAlign: "center", borderBottom: "1px solid #ffe4c9" }}>Factura</th>
                          <th style={{ padding: 8, textAlign: "center", borderBottom: "1px solid #ffe4c9" }}>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(form.pagosEfectivo || []).map((pago, idx) => (
                          <tr key={pago.id || idx} style={{ background: idx % 2 === 0 ? "white" : "#fffbf5" }}>
                            <td style={{ padding: 8, borderBottom: "1px solid #ffe4c9" }}>{pago.proveedor}</td>
                            <td style={{ padding: 8, textAlign: "right", borderBottom: "1px solid #ffe4c9", color: "#52c41a", fontWeight: 600 }}>
                              ${pago.valor?.toLocaleString() || 0}
                            </td>
                            <td style={{ padding: 8, textAlign: "center", borderBottom: "1px solid #ffe4c9", color: "#666" }}>
                              {pago.fechaFactura || "—"}
                            </td>
                            <td style={{ padding: 8, textAlign: "center", borderBottom: "1px solid #ffe4c9" }}>
                              <span style={{
                                padding: "2px 8px",
                                borderRadius: 12,
                                fontSize: 11,
                                fontWeight: 500,
                                background: pago.facturaEntregada === "si" ? "#dcfce7" : "#fee2e2",
                                color: pago.facturaEntregada === "si" ? "#16a34a" : "#dc2626",
                              }}>
                                {pago.facturaEntregada === "si" ? "✓ Sí" : "✗ No"}
                              </span>
                            </td>
                            <td style={{ padding: 8, textAlign: "center", borderBottom: "1px solid #ffe4c9" }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setForm(prev => ({
                                    ...prev,
                                    pagosEfectivo: (prev.pagosEfectivo || []).filter((_, i) => i !== idx)
                                  }));
                                }}
                                style={{
                                  background: "#ef4444",
                                  color: "white",
                                  border: "none",
                                  borderRadius: 4,
                                  padding: "2px 8px",
                                  fontSize: 10,
                                  cursor: "pointer",
                                }}
                              >
                                🗑️ Eliminar
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: "#fff3e0", fontWeight: 600 }}>
                          <td style={{ padding: 8 }}>TOTAL</td>
                          <td style={{ padding: 8, textAlign: "right", color: "#ff6b35" }}>
                            ${(form.pagosEfectivo || []).reduce((sum, p) => sum + (parseFloat(p.valor) || 0), 0).toLocaleString()}
                          </td>
                          <td colSpan={3}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ========== PRÉSTAMOS A LOCALES ========== */}
            {form.tipo === "Préstamos a locales" && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <Field label="Tipo de préstamo">
                    <select
                      style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                      value={form.tipoPrestamo || ""}
                      onChange={(e) => setCampo("tipoPrestamo", e.target.value)}
                    >
                      <option value="">-- Selecciona --</option>
                      <option value="Mercancía">Mercancía</option>
                      <option value="Dinero en efectivo">Dinero en efectivo</option>
                    </select>
                  </Field>

                  <Field label="Sede destino">
                    <select
                      style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                      value={form.sedePrestamo || ""}
                      onChange={(e) => setCampo("sedePrestamo", e.target.value)}
                    >
                      <option value="">-- Selecciona sede --</option>
                      {(sedes || []).map((sedeItem) => {
                        const value = typeof sedeItem === "string" ? sedeItem : sedeItem?.nombre || "";
                        return (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        );
                      })}
                    </select>
                  </Field>
                </div>

                {/* Si es Dinero en efectivo, mostrar campo Valor */}
                {form.tipoPrestamo === "Dinero en efectivo" && (
                  <div style={{ marginTop: 12, padding: 12, background: "#fffbf5", borderRadius: 8, border: "1px solid #ffe4c9" }}>
                    <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: "#ff6b35" }}>
                      Datos del préstamo en efectivo
                    </h4>
                    <Field label="Valor del préstamo">
                      <input
                        type="number"
                        className="input"
                        value={form.valorPrestamo || ""}
                        onChange={(e) => setCampo("valorPrestamo", e.target.value)}
                        placeholder="$0"
                        style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                      />
                    </Field>
                  </div>
                )}

                {/* Si es Mercancía, mostrar campos de Insumo */}
                {form.tipoPrestamo === "Mercancía" && (
                  <div style={{ marginTop: 12, padding: 12, background: "#fffbf5", borderRadius: 8, border: "1px solid #ffe4c9" }}>
                    <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: "#ff6b35" }}>
                      Datos del insumo
                    </h4>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <Field label="Insumo">
                        <input
                          type="text"
                          className="input"
                          value={form.insumo || ""}
                          onChange={(e) => setCampo("insumo", e.target.value)}
                          placeholder="Nombre del insumo"
                          style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                        />
                      </Field>

                      <Field label="Cantidad">
                        <input
                          type="text"
                          className="input"
                          value={form.cantidadInsumo || ""}
                          onChange={(e) => setCampo("cantidadInsumo", e.target.value)}
                          placeholder="Ej: 10 unidades, Cinco cajas..."
                          style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                        />
                      </Field>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ========== AFILIACIÓN A SEGURIDAD SOCIAL ========== */}
            {form.tipo === "Afiliación a seguridad social" && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <Field label="Empleado">
                    <select
                      style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                      value={form.empleadoAfiliacion || ""}
                      onChange={(e) => setCampo("empleadoAfiliacion", e.target.value)}
                    >
                      <option value="">-- Selecciona empleado --</option>
                      {empleadosOrdenados.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.nombre} - {emp.documento}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="EPS Actual">
                    <input
                      type="text"
                      className="input"
                      value={form.epsActual || ""}
                      onChange={(e) => setCampo("epsActual", e.target.value)}
                      placeholder="Nombre de la EPS"
                      style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                    />
                  </Field>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <Field label="Fondo de Pensión Actual">
                    <input
                      type="text"
                      className="input"
                      value={form.fondoPensionActual || ""}
                      onChange={(e) => setCampo("fondoPensionActual", e.target.value)}
                      placeholder="Nombre del fondo de pensión"
                      style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                    />
                  </Field>

                  <Field label="Beneficiarios">
                    <input
                      type="text"
                      className="input"
                      value={form.beneficiarios || ""}
                      onChange={(e) => setCampo("beneficiarios", e.target.value)}
                      placeholder="Nombres de beneficiarios"
                      style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                    />
                  </Field>
                </div>

                {/* Documentos adjuntos múltiples */}
                <div style={{ marginTop: 12, padding: 12, background: "#fffbf5", borderRadius: 8, border: "1px solid #ffe4c9" }}>
                  <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "#ff6b35" }}>
                    Documentos adjuntos
                  </h4>
                  
                  <Field label="Subir documento">
                    <input
                      type="file"
                      onChange={async (e) => {
                        const file = e.target?.files?.[0];
                        if (!file) return;
                        
                        try {
                          const soporte = await subirSoporteProcesoPago(file, {
                            tareaId: form?.id,
                            nombre: file.name,
                          });
                          
                          if (soporte) {
                            setForm(prev => ({
                              ...prev,
                              documentosAdjuntos: [...(prev.documentosAdjuntos || []), soporte]
                            }));
                          }
                        } catch (error) {
                          console.error("Error subiendo documento:", error);
                          alert("No se pudo subir el documento");
                        }
                        
                        e.target.value = "";
                      }}
                      style={{ fontSize: 11 }}
                    />
                  </Field>

                  {/* Lista de documentos */}
                  {(form.documentosAdjuntos || []).length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: "#fff3e0" }}>
                            <th style={{ padding: 6, textAlign: "left", borderBottom: "1px solid #ffe4c9" }}>Documento</th>
                            <th style={{ padding: 6, textAlign: "center", borderBottom: "1px solid #ffe4c9", width: 100 }}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(form.documentosAdjuntos || []).map((doc, idx) => (
                            <tr key={doc.id || idx}>
                              <td style={{ padding: 6, borderBottom: "1px solid #ffe4c9" }}>
                                📄 {doc.nombre}
                              </td>
                              <td style={{ padding: 6, textAlign: "center", borderBottom: "1px solid #ffe4c9" }}>
                                {doc.url && (
                                  <a
                                    href={doc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ marginRight: 8, color: "#1890ff", fontSize: 10 }}
                                  >
                                    👁️ Ver
                                  </a>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setForm(prev => ({
                                      ...prev,
                                      documentosAdjuntos: (prev.documentosAdjuntos || []).filter((_, i) => i !== idx)
                                    }));
                                  }}
                                  style={{
                                    background: "#ef4444",
                                    color: "white",
                                    border: "none",
                                    borderRadius: 4,
                                    padding: "2px 6px",
                                    fontSize: 10,
                                    cursor: "pointer",
                                  }}
                                >
                                  🗑️
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ========== LIQUIDAR VACACIONES / CESANTÍAS / PRIMAS / CONTRATO ========== */}
            {["Liquidar vacaciones", "Liquidar cesantías", "Liquidar primas", "Liquidar contrato de trabajo", "Realizar contrato de trabajo"].includes(form.tipo) && (
              <div style={{ marginTop: 8 }}>
                {/* Checkbox todos los empleados */}
                <div style={{ 
                  marginBottom: 12, 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 12,
                  padding: "10px 14px",
                  borderRadius: 8,
                  background: form.todosEmpleados 
                    ? "linear-gradient(90deg, #1d4ed8 0%, #0ea5e9 60%, #22c55e 100%)" 
                    : "#f8fafc",
                  border: form.todosEmpleados ? "none" : "1px solid #e2e8f0",
                  transition: "all 0.3s ease",
                }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={form.todosEmpleados || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm(prev => ({
                          ...prev,
                          todosEmpleados: checked,
                          empleadosSeleccionados: checked 
                            ? empleadosOrdenados.filter(emp => emp.activo !== false).map(emp => ({
                                empleadoId: emp.id,
                                nombreEmpleado: emp.nombre,
                                documento: emp.documento || ""
                              }))
                            : []
                        }));
                      }}
                      style={{ 
                        width: 20, 
                        height: 20, 
                        cursor: "pointer",
                        accentColor: form.todosEmpleados ? "#22c55e" : "#1d4ed8",
                      }}
                    />
                    <span style={{ 
                      fontSize: 14, 
                      fontWeight: 600, 
                      color: form.todosEmpleados ? "white" : "#1e3a5f",
                    }}>
                      📋 Todos los empleados {form.todosEmpleados ? `(${empleadosOrdenados.filter(emp => emp.activo !== false).length} seleccionados)` : ""}
                    </span>
                  </label>
                </div>

                {/* Selector de empleados (deshabilitado si todos están seleccionados) */}
                <div style={{ marginBottom: 12 }}>
                  <Field label="Agregar empleado">
                    <div style={{ display: "flex", gap: 8 }}>
                      <select
                        id="selectEmpleadoLiquidacion"
                        disabled={form.todosEmpleados}
                        style={{
                          flex: 1,
                          fontSize: 12,
                          padding: "6px",
                          borderRadius: 4,
                          border: "1px solid #cbd5e1",
                          background: form.todosEmpleados ? "#e5e7eb" : "white",
                          cursor: form.todosEmpleados ? "not-allowed" : "pointer",
                        }}
                      >
                        <option value="">-- Selecciona empleado --</option>
                        {(empleados || [])
                          .filter(emp => emp.activo !== false && !(form.empleadosSeleccionados || []).some(e => e.empleadoId === emp.id))
                          .map((emp) => (
                            <option key={emp.id} value={emp.id}>
                              {emp.nombre} - {emp.documento || "Sin doc."}
                            </option>
                          ))}
                      </select>
                      <Button
                        type="button"
                        disabled={form.todosEmpleados}
                        onClick={() => {
                          const select = document.getElementById("selectEmpleadoLiquidacion");
                          const empleadoId = select?.value;
                          if (!empleadoId) {
                            alert("Selecciona un empleado");
                            return;
                          }
                          const emp = (empleados || []).find(e => e.id === empleadoId);
                          setForm(prev => ({
                            ...prev,
                            empleadosSeleccionados: [
                              ...(prev.empleadosSeleccionados || []),
                              {
                                empleadoId,
                                nombreEmpleado: emp?.nombre || "",
                                documento: emp?.documento || "",
                              }
                            ]
                          }));
                          if (select) select.value = "";
                        }}
                        style={{ opacity: form.todosEmpleados ? 0.5 : 1 }}
                      >
                        + Agregar
                      </Button>
                    </div>
                  </Field>
                </div>

                {/* Lista de empleados seleccionados */}
                {(form.empleadosSeleccionados || []).length > 0 && (
                  <div style={{ 
                    border: "1px solid #bfdbfe", 
                    borderRadius: 8, 
                    padding: 12, 
                    background: "#eff6ff",
                    marginBottom: 12 
                  }}>
                    <h4 style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: "#1d4ed8" }}>
                      Empleados seleccionados ({(form.empleadosSeleccionados || []).length})
                    </h4>
                    <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "#dbeafe" }}>
                          <th style={{ padding: 6, textAlign: "left", borderBottom: "1px solid #93c5fd" }}>Nombre</th>
                          <th style={{ padding: 6, textAlign: "left", borderBottom: "1px solid #93c5fd" }}>Documento</th>
                          <th style={{ padding: 6, textAlign: "center", borderBottom: "1px solid #93c5fd", width: 60 }}>Quitar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(form.empleadosSeleccionados || []).map((emp, idx) => (
                          <tr key={emp.empleadoId || idx} style={{ background: idx % 2 === 0 ? "white" : "#eff6ff" }}>
                            <td style={{ padding: 6, borderBottom: "1px solid #bfdbfe" }}>{emp.nombreEmpleado}</td>
                            <td style={{ padding: 6, borderBottom: "1px solid #bfdbfe" }}>{emp.documento || "—"}</td>
                            <td style={{ padding: 6, textAlign: "center", borderBottom: "1px solid #bfdbfe" }}>
                              <button
                                type="button"
                                disabled={form.todosEmpleados}
                                onClick={() => {
                                  setForm(prev => ({
                                    ...prev,
                                    empleadosSeleccionados: (prev.empleadosSeleccionados || []).filter((_, i) => i !== idx)
                                  }));
                                }}
                                style={{
                                  background: form.todosEmpleados ? "#ccc" : "#ef4444",
                                  color: "white",
                                  border: "none",
                                  borderRadius: 4,
                                  padding: "2px 8px",
                                  fontSize: 10,
                                  cursor: form.todosEmpleados ? "not-allowed" : "pointer",
                                }}
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Observaciones */}
                <Field label="Observaciones">
                  <textarea
                    className="input"
                    style={{ fontSize: 12, minHeight: 80, width: "100%" }}
                    value={form.observacionesLiquidacion || ""}
                    onChange={(e) => setCampo("observacionesLiquidacion", e.target.value)}
                    placeholder="Escriba las observaciones o notas adicionales para esta liquidación..."
                  />
                </Field>
              </div>
            )}

            {/* ========== FIRMA DE DOCUMENTOS POR EMPLEADOS ========== */}
            {form.tipo === "Firma de documentos por empleados" && (
              <div style={{ marginTop: 8 }}>
                {/* Agregar empleado con sus documentos */}
                <div style={{ marginBottom: 12 }}>
                  <Field label="Agregar empleado para firma">
                    <div style={{ display: "flex", gap: 8 }}>
                      <select
                        id="selectEmpleadoFirma"
                        style={{
                          flex: 1,
                          fontSize: 12,
                          padding: "6px",
                          borderRadius: 4,
                          border: "1px solid #cbd5e1",
                        }}
                      >
                        <option value="">-- Selecciona empleado --</option>
                        {(empleados || [])
                          .filter(emp => emp.activo !== false && !(form.empleadosFirma || []).some(e => e.empleadoId === emp.id))
                          .map((emp) => (
                            <option key={emp.id} value={emp.id}>
                              {emp.nombre} - {emp.documento || "Sin doc."}
                            </option>
                          ))}
                      </select>
                      <Button
                        type="button"
                        onClick={() => {
                          const select = document.getElementById("selectEmpleadoFirma");
                          const empleadoId = select?.value;
                          if (!empleadoId) {
                            alert("Selecciona un empleado");
                            return;
                          }
                          const emp = (empleados || []).find(e => e.id === empleadoId);
                          setForm(prev => ({
                            ...prev,
                            empleadosFirma: [
                              ...(prev.empleadosFirma || []),
                              {
                                empleadoId,
                                nombreEmpleado: emp?.nombre || "",
                                documento: emp?.documento || "",
                                documentosAdjuntos: [],
                              }
                            ]
                          }));
                          if (select) select.value = "";
                        }}
                      >
                        + Agregar empleado
                      </Button>
                    </div>
                  </Field>
                </div>

                {/* Lista de empleados con sus documentos */}
                {(form.empleadosFirma || []).length > 0 && (
                  <div style={{ 
                    border: "1px solid #bfdbfe", 
                    borderRadius: 8, 
                    padding: 12, 
                    background: "#eff6ff",
                    marginBottom: 12 
                  }}>
                    <h4 style={{ fontSize: 12, fontWeight: 600, marginBottom: 12, color: "#1d4ed8" }}>
                      Empleados para firma ({(form.empleadosFirma || []).length})
                    </h4>
                    
                    {(form.empleadosFirma || []).map((empFirma, idx) => (
                      <div 
                        key={empFirma.empleadoId || idx} 
                        style={{ 
                          background: "white", 
                          border: "1px solid #dbeafe", 
                          borderRadius: 6, 
                          padding: 12, 
                          marginBottom: 10 
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <div>
                            <strong style={{ fontSize: 13, color: "#1e3a5f" }}>{empFirma.nombreEmpleado}</strong>
                            <span style={{ marginLeft: 8, fontSize: 11, color: "#64748b" }}>C.C. {empFirma.documento || "—"}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setForm(prev => ({
                                ...prev,
                                empleadosFirma: (prev.empleadosFirma || []).filter((_, i) => i !== idx)
                              }));
                            }}
                            style={{
                              background: "#ef4444",
                              color: "white",
                              border: "none",
                              borderRadius: 4,
                              padding: "4px 10px",
                              fontSize: 11,
                              cursor: "pointer",
                            }}
                          >
                            Quitar empleado
                          </button>
                        </div>

                        {/* Subir documento para este empleado */}
                        <div style={{ marginBottom: 8 }}>
                          <label style={{ fontSize: 11, color: "#64748b" }}>Adjuntar documento para {empFirma.nombreEmpleado}:</label>
                          <input
                            type="file"
                            onChange={async (e) => {
                              const file = e.target?.files?.[0];
                              if (!file) return;
                              
                              try {
                                const soporte = await subirSoporteProcesoPago(file, {
                                  tareaId: form?.id,
                                  nombre: file.name,
                                });
                                
                                if (soporte) {
                                  const nuevosEmpleados = [...(form.empleadosFirma || [])];
                                  nuevosEmpleados[idx] = {
                                    ...nuevosEmpleados[idx],
                                    documentosAdjuntos: [...(nuevosEmpleados[idx].documentosAdjuntos || []), soporte]
                                  };
                                  setForm(prev => ({ ...prev, empleadosFirma: nuevosEmpleados }));
                                }
                              } catch (error) {
                                console.error("Error subiendo documento:", error);
                                alert("No se pudo subir el documento");
                              }
                              
                              e.target.value = "";
                            }}
                            style={{ fontSize: 11, marginTop: 4 }}
                          />
                        </div>

                        {/* Documentos adjuntos de este empleado */}
                        {(empFirma.documentosAdjuntos || []).length > 0 && (
                          <div style={{ background: "#f8fafc", padding: 8, borderRadius: 4 }}>
                            <span style={{ fontSize: 10, color: "#64748b" }}>Documentos ({empFirma.documentosAdjuntos.length}):</span>
                            {empFirma.documentosAdjuntos.map((doc, docIdx) => (
                              <div key={doc.id || docIdx} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                                <span style={{ fontSize: 11 }}>📄 {doc.nombre}</span>
                                {doc.url && (
                                  <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: "#1890ff" }}>Ver</a>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    const nuevosEmpleados = [...(form.empleadosFirma || [])];
                                    nuevosEmpleados[idx] = {
                                      ...nuevosEmpleados[idx],
                                      documentosAdjuntos: nuevosEmpleados[idx].documentosAdjuntos.filter((_, di) => di !== docIdx)
                                    };
                                    setForm(prev => ({ ...prev, empleadosFirma: nuevosEmpleados }));
                                  }}
                                  style={{ background: "#ef4444", color: "white", border: "none", borderRadius: 3, padding: "1px 6px", fontSize: 9, cursor: "pointer" }}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Observaciones */}
                <Field label="Observaciones">
                  <textarea
                    className="input"
                    style={{ fontSize: 12, minHeight: 60, width: "100%" }}
                    value={form.observacionesFirma || ""}
                    onChange={(e) => setCampo("observacionesFirma", e.target.value)}
                    placeholder="Observaciones adicionales sobre los documentos a firmar..."
                  />
                </Field>
              </div>
            )}

            {/* ========== REALIZAR TAREA GENERAL ========== */}
            {form.tipo === "Realizar tarea general" && (
              <div style={{ marginTop: 8 }}>
                <Field label="Observaciones / Descripción de la tarea">
                  <textarea
                    className="input"
                    style={{ fontSize: 12, minHeight: 100, width: "100%" }}
                    value={form.observacionesTareaGeneral || ""}
                    onChange={(e) => setCampo("observacionesTareaGeneral", e.target.value)}
                    placeholder="Describa la tarea a realizar..."
                  />
                </Field>

                <div style={{ marginTop: 12 }}>
                  <Field label="Tiempo de ejecución">
                    <input
                      type="text"
                      className="input"
                      value={form.tiempoEjecucion || ""}
                      onChange={(e) => setCampo("tiempoEjecucion", e.target.value)}
                      placeholder="Ej: 2 horas, 3 días, 1 semana..."
                      style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                    />
                  </Field>
                </div>
              </div>
            )}

            {/* ========== CANCELACIONES O MODIFICACIONES FUDO ========== */}
            {form.tipo === "Cancelaciones o modificaciones FUDO" && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <Field label="Número de pedido">
                    <input
                      type="text"
                      className="input"
                      value={form.numeroPedidoFudo || ""}
                      onChange={(e) => setCampo("numeroPedidoFudo", e.target.value)}
                      placeholder="Ingrese el número de pedido"
                      style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                    />
                  </Field>

                  <Field label="Empleado en caja">
                    <select
                      style={{ width: "100%", fontSize: 13, padding: 6, borderRadius: 4, border: "1px solid #cbd5e1" }}
                      value={form.empleadoCajaId || ""}
                      onChange={(e) => setCampo("empleadoCajaId", e.target.value)}
                    >
                      <option value="">-- Selecciona empleado --</option>
                      {empleadosOrdenados.filter(emp => emp.activo !== false).map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.nombre}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field label="Observaciones">
                  <textarea
                    className="input"
                    style={{ fontSize: 12, minHeight: 80, width: "100%" }}
                    value={form.observacionesFudo || ""}
                    onChange={(e) => setCampo("observacionesFudo", e.target.value)}
                    placeholder="Describa el motivo de la cancelación o modificación..."
                  />
                </Field>

                {/* Documentos adjuntos de soporte */}
                <div style={{ marginTop: 12, padding: 12, background: "#fffbf5", borderRadius: 8, border: "1px solid #ffe4c9" }}>
                  <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "#ff6b35" }}>
                    Documentos de soporte
                  </h4>
                  
                  <Field label="Subir documento">
                    <input
                      type="file"
                      onChange={async (e) => {
                        const file = e.target?.files?.[0];
                        if (!file) return;
                        
                        try {
                          const soporte = await subirSoporteProcesoPago(file, {
                            tareaId: form?.id,
                            nombre: file.name,
                          });
                          
                          if (soporte) {
                            setForm(prev => ({
                              ...prev,
                              soportesFudo: [...(prev.soportesFudo || []), soporte]
                            }));
                          }
                        } catch (error) {
                          console.error("Error subiendo documento:", error);
                          alert("No se pudo subir el documento");
                        }
                        
                        e.target.value = "";
                      }}
                      style={{ fontSize: 11 }}
                    />
                  </Field>

                  {/* Lista de documentos */}
                  {(form.soportesFudo || []).length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: "#fff3e0" }}>
                            <th style={{ padding: 6, textAlign: "left", borderBottom: "1px solid #ffe4c9" }}>Documento</th>
                            <th style={{ padding: 6, textAlign: "center", borderBottom: "1px solid #ffe4c9", width: 100 }}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(form.soportesFudo || []).map((doc, idx) => (
                            <tr key={doc.id || idx}>
                              <td style={{ padding: 6, borderBottom: "1px solid #ffe4c9" }}>
                                📄 {doc.nombre}
                              </td>
                              <td style={{ padding: 6, textAlign: "center", borderBottom: "1px solid #ffe4c9" }}>
                                {doc.url && (
                                  <a
                                    href={doc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ marginRight: 8, color: "#1890ff", fontSize: 10 }}
                                  >
                                    👁️ Ver
                                  </a>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setForm(prev => ({
                                      ...prev,
                                      soportesFudo: (prev.soportesFudo || []).filter((_, i) => i !== idx)
                                    }));
                                  }}
                                  style={{
                                    background: "#ef4444",
                                    color: "white",
                                    border: "none",
                                    borderRadius: 4,
                                    padding: "2px 6px",
                                    fontSize: 10,
                                    cursor: "pointer",
                                  }}
                                >
                                  🗑️
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {subiendoSoporte && (
            <div
              style={{
                marginTop: 8,
                padding: "6px 8px",
                background: "#e0f2fe",
                borderRadius: 4,
                fontSize: 11,
                color: "#0f172a",
                border: "1px solid #bae6fd",
              }}
            >
              Subiendo soporte a Supabase…
            </div>
          )}

          {/* LISTA DE SOPORTES CARGADOS - CON PREVISUALIZACIÓN */}
          {form.soportes && form.soportes.length > 0 && (
            <>
              <div style={{ marginTop: 12, padding: 12, background: "#f8fafc", borderRadius: 6, border: "1px solid #e2e8f0" }}>
                <h4 className="text-[12px] font-semibold mb-3">
                  Archivos adjuntos ({form.soportes.length})
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 12 }}>
                  {form.soportes.map((s) => (
                    <div key={s.id} style={{ 
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      padding: 8,
                      background: "white",
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      fontSize: 11,
                    }}>
            {/* PREVISUALIZACIÓN SI ES IMAGEN */}
            {s.url && s.nombre.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) ? (
              <>
                <img
                  src={s.url}
                  alt={s.nombre}
                  style={{
                    width: "100%",
                    height: 120,
                    objectFit: "cover",
                    borderRadius: 4,
                    cursor: "pointer",
                    border: "1px solid #cbd5e1",
                  }}
                />
                <button
                  type="button"
                  style={{
                    background: "#0ea5e9",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    padding: "4px 8px",
                    fontSize: "10px",
                    fontWeight: 600,
                    marginTop: 4,
                    cursor: "pointer",
                  }}
                  onClick={() => setPreviewFileCrear(s)}
                >
                  Previsualizar
                </button>
              </>
            ) : (
              <>
                <div style={{
                  width: "100%",
                  height: 80,
                  background: "#f0f1f3",
                  borderRadius: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 24,
                  border: "1px solid #cbd5e1",
                }}>
                  {s.nombre.match(/\.(pdf)$/i) ? "📄" : "📎"}
                </div>
                <button
                  type="button"
                  style={{
                    background: "#0ea5e9",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    padding: "4px 8px",
                    fontSize: "10px",
                    fontWeight: 600,
                    marginTop: 4,
                    cursor: "pointer",
                  }}
                  onClick={() => setPreviewFileCrear(s)}
                >
                  Previsualizar
                </button>
              </>
            )}
            <div style={{ fontWeight: 600, color: "#1e293b", wordBreak: "break-word", textAlign: "center" }}>
              {s.nombre}
            </div>
          </div>
        ))}
      </div>
    </div>
    {/* MODAL DE PREVISUALIZACIÓN */}
    {previewFileCrear && (
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.6)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
        onClick={() => setPreviewFileCrear(null)}
      >
        <div style={{
          background: "white",
          borderRadius: 8,
          padding: 24,
          maxWidth: 600,
          maxHeight: "80vh",
          boxShadow: "0 2px 16px rgba(0,0,0,0.2)",
          position: "relative",
        }}
          onClick={e => e.stopPropagation()}
        >
          <button
            type="button"
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              background: "#ef4444",
              color: "white",
              border: "none",
              borderRadius: 4,
              padding: "2px 8px",
              fontSize: "12px",
              cursor: "pointer",
            }}
            onClick={() => setPreviewFileCrear(null)}
          >
            Cerrar
          </button>
          {(() => {
            const nombre = previewFileCrear.nombre || "";
            const tipo = previewFileCrear.tipo || "";
            const esImg = !!previewFileCrear.url && (tipo.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(nombre));
            const esPdf = !!previewFileCrear.url && (tipo === "application/pdf" || /\.(pdf)$/i.test(nombre));
            if (esImg) {
              return (
                <img
                  src={previewFileCrear.url}
                  alt={nombre}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    borderRadius: 6,
                    display: "block",
                    margin: "0 auto",
                  }}
                />
              );
            }
            if (esPdf) {
              return (
                <iframe
                  src={previewFileCrear.url}
                  title={nombre}
                  style={{ width: "100%", height: "70vh", border: "none" }}
                />
              );
            }
            if (previewFileCrear.url) {
              return (
                <div style={{ textAlign: "center", fontSize: 16 }}>
                  <a href={previewFileCrear.url} target="_blank" rel="noreferrer" style={{ color: "#0ea5e9" }}>
                    Descargar archivo
                  </a>
                </div>
              );
            }
            return null;
          })()}
          <div style={{ marginTop: 12, textAlign: "center", fontSize: 12, color: "#64748b" }}>
            {previewFileCrear.nombre}
          </div>
        </div>
      </div>
      )}
    </>
  )}

        {/* BOTONES */}
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          {tienePermiso("admin_procesos_guardar") && (
          <Button type="submit">Guardar tarea</Button>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={resetForm}
          >
            Limpiar formulario
          </Button>
        </div>
      </div>
    </form>
  );
};
 
  function esImagenNombre(nombre) {
    return !!(
      nombre &&
      /\.(jpg|jpeg|png|gif|bmp|webp|jfif)$/i.test(nombre)
    );
  }
  function esPdfNombre(nombre) {
    return !!(nombre && /\.pdf$/i.test(nombre));
  }
 
// ---------- VISTA CONSULTAR / GESTIONAR ----------
const renderVistaConsultar = () => {

  function parseDate(iso) {
    if (!iso) return null;
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  // Verificar si hay algún filtro activo (para decidir si mostrar tareas con respuesta)
  const hayFiltrosActivos = filtroTipo || filtroEstado || filtroSede || filtroMes;

  // Filtro de tareas
  const filtradas = (tareasVisibles || []).filter((t) => {
    const tipoTarea = typeof t.tipo === "object" ? t.tipo?.nombre || "" : t.tipo;
    const estadoTarea = typeof t.estado === "object" ? t.estado?.nombre || "" : t.estado;
    const sedeTarea = typeof t.sede === "object" ? t.sede?.nombre || t.sede?.direccion || "" : t.sede;

    // Si NO hay filtros activos Y NO está marcado el checkbox, ocultar Procesado y Negado
    if (!hayFiltrosActivos && !mostrarTareasConRespuesta) {
      if (estadoTarea === "Procesado" || estadoTarea === "Negado") {
        return false;
      }
    }

    if (filtroTipo && tipoTarea !== filtroTipo) return false;
    if (filtroEstado && estadoTarea !== filtroEstado) return false;
    if (filtroSede && sedeTarea !== filtroSede) return false;
    if (filtroMes) {
      // Filtrar por mes de CREACIÓN (no de límite)
      if (!t.fechaCreacion) return false;
      const d = parseDate(t.fechaCreacion);
      if (!d) return false;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (key !== filtroMes) return false;
    }
    return true;
  });

  // Contar tareas ocultas (Procesado/Negado)
  const tareasOcultas = (tareasVisibles || []).filter(t => {
    const estadoTarea = typeof t.estado === "object" ? t.estado?.nombre || "" : t.estado;
    return estadoTarea === "Procesado" || estadoTarea === "Negado";
  }).length;

  // Solo lectura si está procesada, según lo que YA está guardado en la lista (opcional, si quieres usarlo)
  const tareaOriginal =
    tareaSeleccionada &&
    (tareasVisibles || []).find((t) => t.id === tareaSeleccionada.id);

  // Helpers
  function seleccionarTarea(t) {
    const actual = (tareasVisibles || []).find((x) => x.id === t?.id) || t;

    const clon = {
      ...actual,
      soportes: Array.isArray(actual?.soportes)
        ? actual.soportes.map((item) => ({ ...item }))
        : [],
      adjuntoGestion:
        actual?.adjuntoGestion && typeof actual.adjuntoGestion === "object"
          ? { ...actual.adjuntoGestion }
          : actual?.adjuntoGestion || null,
    };

    const normalizarCampo = (valor, fallback = "") => {
      if (!valor) return fallback;
      if (typeof valor === "string") return valor;
      if (typeof valor === "number") return String(valor);
      if (typeof valor === "object") {
        if (Array.isArray(valor)) return valor;
        if (valor.nombre) return valor.nombre;
        if (valor.direccion) return valor.direccion;
        if (valor.id) return valor.id;
        return JSON.stringify(valor);
      }
      return fallback;
    };

    clon.sede = normalizarCampo(actual?.sede, clon.sede || "");
    clon.tipo = normalizarCampo(actual?.tipo, clon.tipo || "");
    clon.estado = normalizarCampo(actual?.estado, clon.estado || "");
    clon.responsableId = normalizarCampo(actual?.responsableId || actual?.responsable, clon.responsableId || "");

    setTareaSeleccionada(clon);
    setSoloLectura(!!(clon && clon.estado === "Procesado"));
    setSubiendoAdjuntoGestion(false);
  }

  function esImagenNombre(nombre) {
    return !!(
      nombre &&
      /\.(jpg|jpeg|png|gif|bmp|webp|jfif)$/i.test(nombre)
    );
  }

  // Cambios en el detalle
  function cambiarResponsable(e) {
    const responsableId = e.target.value;
    setTareaSeleccionada((prev) =>
      prev ? { ...prev, responsableId } : prev
    );
  }

  function cambiarEstado(e) {
    const estado = e.target.value;
    setTareaSeleccionada((prev) =>
      prev ? { ...prev, estado } : prev
    );
  }

  function cambiarComentario(e) {
    const comentariosGestion = e.target.value;
    setTareaSeleccionada((prev) =>
      prev ? { ...prev, comentariosGestion } : prev
    );
  }

  function cambiarAdjuntoGestion(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target && ev.target.result;
      if (!url) return;

      setTareaSeleccionada((prev) =>
        prev
          ? {
              ...prev,
              adjuntoGestion: {
                id: uid(),
                nombre: file.name,
                tipo: file.type,
                url,
              },
            }
          : prev
      );
    };
    reader.readAsDataURL(file);
  }

  function guardarCambiosDetalle() {
    if (!tareaSeleccionada) return;

    // Obtener tarea original para detectar cambios
    const tareaOriginal = tareas.find(t => t.id === tareaSeleccionada.id);
    const responsableAnterior = tareaOriginal?.responsableId || "";
    const estadoAnterior = tareaOriginal?.estado || "";

    const cambios = {
      fechaCreacion: tareaSeleccionada.fechaCreacion || "",
      sede: typeof tareaSeleccionada.sede === "object" ? tareaSeleccionada.sede?.nombre || tareaSeleccionada.sede?.direccion || "" : tareaSeleccionada.sede || "",
      tipo: typeof tareaSeleccionada.tipo === "object" ? tareaSeleccionada.tipo?.nombre || "" : tareaSeleccionada.tipo || "",
      fechaLimite: tareaSeleccionada.fechaLimite || "",
      estado: typeof tareaSeleccionada.estado === "object" ? tareaSeleccionada.estado?.nombre || "" : tareaSeleccionada.estado,
      comentariosGestion: tareaSeleccionada.comentariosGestion || "",
      responsableId: tareaSeleccionada.responsableId || "",
      adjuntoGestion: tareaSeleccionada.adjuntoGestion || null,
      soportes: Array.isArray(tareaSeleccionada.soportes)
        ? tareaSeleccionada.soportes.map((s) => ({ ...s }))
        : undefined,
    };

    const baseValidacion = { ...tareaSeleccionada, ...cambios };
    const validacion = validarCamposObligatorios(baseValidacion);
    if (!validacion.ok) {
      alert(
        `Faltan datos obligatorios para "${tareaSeleccionada.tipo}":\n• ${validacion.faltantes.join("\n• ")}`
      );
      return;
    }

    // Actualiza en el array principal
    actualizarTareaDesdeConsulta(tareaSeleccionada.id, cambios);

    // Actualiza la copia local para reflejar el nuevo estado inmediatamente
    const nuevaSeleccion = { ...tareaSeleccionada, ...cambios };
    setTareaSeleccionada(nuevaSeleccion);

    // Si se asignó un nuevo responsable, enviarle SMS
    const nuevoResponsable = cambios.responsableId;
    if (nuevoResponsable && nuevoResponsable !== responsableAnterior) {
      const responsable = empleados?.find(e => String(e.id) === String(nuevoResponsable));
      if (responsable?.telefono) {
        notificarTareaAsignadaEmpleado(responsable, nuevaSeleccion).then(result => {
          if (result?.ok) {
            console.log("SMS enviado al responsable asignado:", responsable.nombre);
          }
        }).catch(err => console.warn("No se pudo enviar SMS al responsable:", err));
      }
    }

    // Si quedó en "Procesado", bloquear edición localmente y notificar al radicador
    if (nuevaSeleccion.estado === "Procesado" && estadoAnterior !== "Procesado") {
      setSoloLectura(true);
      
      // Buscar teléfono del creador de la tarea para notificarle
      const creadorId = nuevaSeleccion.creadorId || tareaSeleccionada.creadorId;
      if (creadorId && empleados && empleados.length > 0) {
        const creador = empleados.find(e => String(e.id) === String(creadorId));
        if (creador?.telefono) {
          notificarTareaProcesada(nuevaSeleccion, creador.telefono).then(result => {
            if (result?.ok) {
              console.log("SMS enviado al radicador:", creador.nombre);
            }
          }).catch(err => console.warn("No se pudo enviar SMS al radicador:", err));
        }
      }
    } else if (nuevaSeleccion.estado === "Procesado") {
      setSoloLectura(true);
    }

    alert("Tarea actualizada correctamente");
  }

  function eliminarTarea() {
    if (!tareaSeleccionada) return;

    const confirmar = window.confirm(
      `¿Está seguro de que desea eliminar la tarea "${tareaSeleccionada.id}"?\n\nEsta acción no se puede deshacer.`
    );

    if (!confirmar) return;

    const idEliminar = tareaSeleccionada.id;
    const indice = tareas.findIndex((t) => t.id === idEliminar);

    if (indice === -1) {
      alert("No se encontró la tarea seleccionada en la lista local.");
      return;
    }

    const tareaEliminada = { ...tareas[indice] };

    setTareas((prev) => prev.filter((t) => t.id !== idEliminar));
    setTareaSeleccionada(null);
    setSoloLectura(false);

    setSincronizando(true);
    eliminarProcesoPagoEnSupabase(idEliminar)
      .then((resultado) => {
        if (!resultado) {
          alert("No se pudo eliminar la tarea en Supabase. Se restaurará la información local.");
          setTareas((prev) => {
            const copia = [...prev];
            copia.splice(indice, 0, tareaEliminada);
            return copia;
          });
          setTareaSeleccionada(tareaEliminada);
          setSoloLectura((tareaEliminada.estado || "").toString().trim() === "Procesado");
          return;
        }

        alert("Tarea eliminada correctamente");
      })
      .catch((e) => {
        console.error("Error eliminando tarea administrativa en Supabase", e);
        alert("Ocurrió un error eliminando en Supabase. Se restaurará la tarea local.");
        setTareas((prev) => {
          const copia = [...prev];
          copia.splice(indice, 0, tareaEliminada);
          return copia;
        });
        setTareaSeleccionada(tareaEliminada);
        setSoloLectura((tareaEliminada.estado || "").toString().trim() === "Procesado");
      })
      .finally(() => setSincronizando(false));
  }

  // Render de datos específicos por tipo de tarea - MUESTRA TODOS LOS CAMPOS
  function renderDatosEspecificos(t) {
    if (!t) return null;

    return (
      <div
        style={{
          marginTop: 6,
          padding: 8,
          borderRadius: 6,
          background: "#f1f5f9",
          fontSize: 12,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>Datos específicos de la tarea</div>

        {/* PAGO DE SERVICIOS PÚBLICOS */}
        {t.tipo === "Pago de servicios públicos" && (
          <>
            {(t.servicio || t.servicioPublico) && (
              <div><strong>Servicio:</strong> {typeof (t.servicio || t.servicioPublico) === 'object' ? (t.servicio?.nombre || t.servicioPublico?.nombre || '') : (t.servicio || t.servicioPublico)}</div>
            )}
            {(t.valorServPublico || t.valor || t.valorPagar) && (
              <div><strong>Valor:</strong> ${Number(t.valorServPublico || t.valor || t.valorPagar || 0).toLocaleString()}</div>
            )}
            {t.referenciaPago && (
              <div><strong>Referencia de pago:</strong> {typeof t.referenciaPago === 'object' ? t.referenciaPago?.nombre || '' : t.referenciaPago}</div>
            )}
            {t.fechaVencimiento && (
              <div><strong>Fecha vencimiento:</strong> {t.fechaVencimiento}</div>
            )}
          </>
        )}

        {/* VENTA DE ACEITE */}
        {t.tipo === "Venta de aceite" && (
          <>
            {(t.valorVenta || t.valor) && (
              <div><strong>Valor de la venta:</strong> ${Number(t.valorVenta || t.valor || 0).toLocaleString()}</div>
            )}
            {t.fechaVenta && (
              <div><strong>Fecha de venta:</strong> {t.fechaVenta}</div>
            )}
          </>
        )}

        {/* DAÑOS */}
        {t.tipo === "Daños" && (
          <>
            {t.queSeDanio && (
              <div><strong>Qué se dañó:</strong> {typeof t.queSeDanio === 'object' ? t.queSeDanio?.nombre || '' : t.queSeDanio}</div>
            )}
            <div>
              <strong>¿Fue empleado?</strong> {t.fueEmpleadoDanio === "si" ? "Sí" : "No"}
            </div>
            {t.empleadoId && (
              <div><strong>Empleado:</strong> {nombreEmpleado(t.empleadoId)}</div>
            )}
          </>
        )}

        {/* PAGO DE ARRENDAMIENTO */}
        {t.tipo === "Pago de arrendamiento" && (
          <>
            {t.fechaVencimiento && (
              <div><strong>Fecha de vencimiento:</strong> {t.fechaVencimiento}</div>
            )}
            {t.valorPagar && (
              <div><strong>Valor:</strong> ${Number(t.valorPagar || 0).toLocaleString()}</div>
            )}
          </>
        )}

        {/* PAGO DE SALUD */}
        {t.tipo === "Pago de salud" && (
          <>
            {t.fechaVencimiento && (
              <div><strong>Fecha de vencimiento:</strong> {t.fechaVencimiento}</div>
            )}
            {t.valorPagar && (
              <div><strong>Valor:</strong> ${Number(t.valorPagar || 0).toLocaleString()}</div>
            )}
          </>
        )}

        {/* RECLAMAR INCAPACIDAD */}
        {t.tipo === "Reclamar incapacidad" && (
          <>
            {t.empleadoId && (
              <div><strong>Empleado:</strong> {nombreEmpleado(t.empleadoId)}</div>
            )}
            {t.comentarioFechas && (
              <div><strong>Fechas / Comentario:</strong> {typeof t.comentarioFechas === 'object' ? t.comentarioFechas?.nombre || '' : t.comentarioFechas}</div>
            )}
          </>
        )}

        {/* SOLICITUD DE VACACIONES */}
        {t.tipo === "Solicitud de vacaciones" && (
          <>
            {t.empleadoId && (
              <div><strong>Empleado:</strong> {nombreEmpleado(t.empleadoId)}</div>
            )}
            {t.comentarioFechas && (
              <div><strong>Fechas / Comentario:</strong> {typeof t.comentarioFechas === 'object' ? t.comentarioFechas?.nombre || '' : t.comentarioFechas}</div>
            )}
          </>
        )}

        {/* FACTURA A PAGAR POR TRANSFERENCIA */}
        {t.tipo === "Factura a pagar por transferencia" && (
          <>
            {t.proveedor && (
              <div><strong>Proveedor:</strong> {typeof t.proveedor === 'object' ? t.proveedor.nombre || JSON.stringify(t.proveedor) : t.proveedor}</div>
            )}
            {(t.valorServPublico || t.valorFactura || t.valorPagar) && (
              <div>
                <strong>Valor a pagar:</strong> ${Number(t.valorServPublico || t.valorFactura || t.valorPagar || 0).toLocaleString()}
              </div>
            )}
            {t.referenciaPago && (
              <div><strong>Referencia de pago:</strong> {typeof t.referenciaPago === 'object' ? t.referenciaPago?.nombre || '' : t.referenciaPago}</div>
            )}
            {t.fechaReciboPedido && (
              <div><strong>Fecha recibo del pedido:</strong> {t.fechaReciboPedido}</div>
            )}
            {t.datosPago && (
              <div style={{ marginTop: 8, padding: 8, backgroundColor: "#f0f9ff", borderRadius: 6, border: "1px solid #bae6fd" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#0369a1", marginBottom: 4 }}>💳 Datos para el pago:</div>
                <div style={{ whiteSpace: "pre-wrap" }}>{t.datosPago}</div>
              </div>
            )}
            {t.medioPago && (
              <div><strong>Medio de pago:</strong> {typeof t.medioPago === 'object' ? t.medioPago?.nombre || '' : t.medioPago}</div>
            )}
          </>
        )}

        {/* COMUNICACIÓN CON EMPLEADO */}
        {t.tipo === "Comunicación con empleado" && (
          <>
            {t.empleadoId && (
              <div><strong>Empleado:</strong> {nombreEmpleado(t.empleadoId)}</div>
            )}
            {t.comunicacionTexto && (
              <div>
                <strong>Requerimiento:</strong>
                <div className="whitespace-pre-wrap mt-1 pl-2 border-l-2 border-slate-300">
                  {typeof t.comunicacionTexto === 'object' ? t.comunicacionTexto?.nombre || '' : t.comunicacionTexto}
                </div>
              </div>
            )}
          </>
        )}

        {/* SOLICITAR SENCILLA */}
        {(t.tipo === "Solicitar sencilla" || t.tipo === "Solicitud sencilla") && (
          <>
            {t.comentarioSencilla && (
              <div>
                <strong>Detalle de la solicitud:</strong>
                <div className="whitespace-pre-wrap mt-1 pl-2 border-l-2 border-slate-300">
                  {typeof t.comentarioSencilla === 'object' ? t.comentarioSencilla?.nombre || '' : t.comentarioSencilla}
                </div>
              </div>
            )}
          </>
        )}

        {t.tipo === "Entrega de cuadre" && (
          <>
            {t.numeroCuadres && (
              <div><strong>Número de cuadres:</strong> {t.numeroCuadres}</div>
            )}
            {t.novedades && (
              <div>
                <strong>Novedades:</strong>
                <div className="whitespace-pre-wrap mt-1 pl-2 border-l-2 border-slate-300">
                  {t.novedades}
                </div>
              </div>
            )}
          </>
        )}

        {/* PAGO DE FACTURAS POR TRANSFERENCIA (NUEVO) */}
        {t.tipo === "Pago de facturas por transferencia" && (
          <>
            {t.proveedor && (
              <div><strong>Proveedor:</strong> {typeof t.proveedor === 'object' ? t.proveedor?.nombre || '' : t.proveedor}</div>
            )}
            {t.valorPagar && (
              <div><strong>Valor a pagar:</strong> ${Number(t.valorPagar || 0).toLocaleString()}</div>
            )}
            {t.numeroCuenta && (
              <div><strong>Número de cuenta:</strong> {t.numeroCuenta}</div>
            )}
            {t.novedades && (
              <div>
                <strong>Novedades:</strong>
                <div className="whitespace-pre-wrap mt-1 pl-2 border-l-2 border-slate-300">
                  {t.novedades}
                </div>
              </div>
            )}
          </>
        )}

        {/* SOLICITUD DE FIRMA A EMPLEADOS (NUEVO) */}
        {t.tipo === "Solicitud de firma a empleados" && (
          <>
            {t.empleadoId && (
              <div><strong>Empleado:</strong> {nombreEmpleado(t.empleadoId)}</div>
            )}
            {t.documentoFirmar && (
              <div><strong>Documento a firmar:</strong> {t.documentoFirmar}</div>
            )}
          </>
        )}

        {/* ASIGNACIÓN DE TAREA (NUEVO) */}
        {t.tipo === "Asignación de tarea" && (
          <>
            {t.empleadoId && (
              <div><strong>Empleado:</strong> {nombreEmpleado(t.empleadoId)}</div>
            )}
            {t.tareaAsignada && (
              <div>
                <strong>Tarea asignada:</strong>
                <div className="whitespace-pre-wrap mt-1 pl-2 border-l-2 border-slate-300">
                  {t.tareaAsignada}
                </div>
              </div>
            )}
          </>
        )}

        {/* CAMBIO DE HORARIO (NUEVO) */}
        {t.tipo === "Cambio de horario" && (
          <>
            {Array.isArray(t.empleadosCambioHorario) && t.empleadosCambioHorario.length > 0 ? (
              <div>
                <strong>Empleados con cambio de horario:</strong>
                <div style={{ marginTop: 6 }}>
                  {t.empleadosCambioHorario.map((emp, idx) => (
                    <div 
                      key={idx} 
                      style={{ 
                        background: "white", 
                        border: "1px solid #d1d5db", 
                        borderRadius: 4, 
                        padding: 6, 
                        marginBottom: 4,
                        fontSize: 11
                      }}
                    >
                      <div><strong>{emp.nombreEmpleado || nombreEmpleado(emp.empleadoId) || "Empleado"}</strong></div>
                      <div>Fecha: {emp.fecha || "—"}</div>
                      <div>Sede: {emp.sede || "—"}</div>
                      <div>Horario: {emp.horario || "—"}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div><em>Sin empleados asignados</em></div>
            )}
          </>
        )}

        {/* PAGO DE FACTURAS EN EFECTIVO */}
        {t.tipo === "Pago de facturas en efectivo" && (
          <>
            {Array.isArray(t.pagosEfectivo) && t.pagosEfectivo.length > 0 ? (
              <div>
                <strong>Pagos registrados ({t.pagosEfectivo.length}):</strong>
                <div style={{ marginTop: 6 }}>
                  {t.pagosEfectivo.map((pago, idx) => (
                    <div 
                      key={idx} 
                      style={{ 
                        background: "white", 
                        border: "1px solid #ffe4c9", 
                        borderRadius: 4, 
                        padding: 8, 
                        marginBottom: 4,
                        fontSize: 11
                      }}
                    >
                      <div><strong>Proveedor:</strong> {pago.proveedor}</div>
                      <div><strong>Valor:</strong> <span style={{ color: "#52c41a", fontWeight: 600 }}>${Number(pago.valor || 0).toLocaleString()}</span></div>
                      <div><strong>Fecha factura:</strong> {pago.fechaFactura || "—"}</div>
                      <div><strong>Factura entregada:</strong> 
                        <span style={{
                          marginLeft: 4,
                          padding: "2px 6px",
                          borderRadius: 10,
                          fontSize: 10,
                          fontWeight: 500,
                          background: pago.facturaEntregada === "si" ? "#dcfce7" : "#fee2e2",
                          color: pago.facturaEntregada === "si" ? "#16a34a" : "#dc2626",
                        }}>
                          {pago.facturaEntregada === "si" ? "✓ Sí" : "✗ No"}
                        </span>
                      </div>
                    </div>
                  ))}
                  <div style={{ marginTop: 6, fontWeight: 600, color: "#ff6b35" }}>
                    TOTAL: ${t.pagosEfectivo.reduce((sum, p) => sum + (parseFloat(p.valor) || 0), 0).toLocaleString()}
                  </div>
                </div>
              </div>
            ) : (
              <div><em>Sin pagos registrados</em></div>
            )}
          </>
        )}

        {/* PRÉSTAMOS A LOCALES */}
        {t.tipo === "Préstamos a locales" && (
          <div style={{ marginTop: 4 }}>
            <div><strong>Tipo de préstamo:</strong> {t.tipoPrestamo || "—"}</div>
            <div><strong>Sede destino:</strong> {t.sedePrestamo || "—"}</div>
            
            {t.tipoPrestamo === "Dinero en efectivo" && (
              <div style={{ marginTop: 6, padding: 8, background: "#fff5eb", borderRadius: 4, border: "1px solid #ffe4c9" }}>
                <strong>Valor del préstamo:</strong> 
                <span style={{ color: "#52c41a", fontWeight: 600, marginLeft: 4 }}>
                  ${Number(t.valorPrestamo || 0).toLocaleString()}
                </span>
              </div>
            )}
            
            {t.tipoPrestamo === "Mercancía" && (
              <div style={{ marginTop: 6, padding: 8, background: "#fff5eb", borderRadius: 4, border: "1px solid #ffe4c9" }}>
                <div><strong>Insumo:</strong> {t.insumo || "—"}</div>
                <div><strong>Cantidad:</strong> {t.cantidadInsumo || t.cantidadNumero || "—"} {t.cantidadLetras ? `(${t.cantidadLetras})` : ""}</div>
              </div>
            )}
          </div>
        )}

        {/* AFILIACIÓN A SEGURIDAD SOCIAL */}
        {t.tipo === "Afiliación a seguridad social" && (
          <div style={{ marginTop: 4 }}>
            <div><strong>Empleado:</strong> {nombreEmpleado(t.empleadoAfiliacion) || t.empleadoAfiliacion || "—"}</div>
            <div><strong>EPS Actual:</strong> {t.epsActual || "—"}</div>
            <div><strong>Fondo de Pensión:</strong> {t.fondoPensionActual || "—"}</div>
            <div><strong>Beneficiarios:</strong> {t.beneficiarios || "—"}</div>
            
            {Array.isArray(t.documentosAdjuntos) && t.documentosAdjuntos.length > 0 && (
              <div style={{ marginTop: 6 }}>
                <strong>Documentos adjuntos ({t.documentosAdjuntos.length}):</strong>
                <div style={{ marginTop: 4 }}>
                  {t.documentosAdjuntos.map((doc, idx) => (
                    <div key={idx} style={{ fontSize: 11, marginBottom: 2 }}>
                      {doc.nombre}
                      {doc.url && (
                        <a 
                          href={doc.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ marginLeft: 6, color: "#1890ff", fontSize: 10 }}
                        >
                          Ver
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* LIQUIDAR VACACIONES / CESANTÍAS / PRIMAS / CONTRATO / REALIZAR CONTRATO */}
        {["Liquidar vacaciones", "Liquidar cesantías", "Liquidar primas", "Liquidar contrato de trabajo", "Realizar contrato de trabajo"].includes(t.tipo) && (
          <div style={{ marginTop: 4 }}>
            <div style={{ 
              padding: "8px 12px", 
              background: t.todosEmpleados ? "linear-gradient(90deg, #1d4ed8 0%, #0ea5e9 60%, #22c55e 100%)" : "#eff6ff", 
              borderRadius: 6,
              marginBottom: 8,
              border: t.todosEmpleados ? "none" : "1px solid #bfdbfe"
            }}>
              <strong style={{ color: t.todosEmpleados ? "white" : "#1d4ed8" }}>
                {t.todosEmpleados 
                  ? "📋 Todos los empleados" 
                  : `📋 Empleados seleccionados (${(t.empleadosSeleccionados || []).length})`
                }
              </strong>
            </div>
            
            {!t.todosEmpleados && Array.isArray(t.empleadosSeleccionados) && t.empleadosSeleccionados.length > 0 && (
              <div style={{ marginTop: 6, marginBottom: 8 }}>
                <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#dbeafe" }}>
                      <th style={{ padding: 4, textAlign: "left", borderBottom: "1px solid #93c5fd" }}>Nombre</th>
                      <th style={{ padding: 4, textAlign: "left", borderBottom: "1px solid #93c5fd" }}>Documento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.empleadosSeleccionados.map((emp, idx) => (
                      <tr key={idx} style={{ background: idx % 2 === 0 ? "white" : "#eff6ff" }}>
                        <td style={{ padding: 4, borderBottom: "1px solid #bfdbfe" }}>{emp.nombreEmpleado || nombreEmpleado(emp.empleadoId) || "—"}</td>
                        <td style={{ padding: 4, borderBottom: "1px solid #bfdbfe" }}>{emp.documento || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {t.observacionesLiquidacion && (
              <div style={{ marginTop: 6, padding: 8, background: "#f8fafc", borderRadius: 4, border: "1px solid #e2e8f0" }}>
                <strong>Observaciones:</strong>
                <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{t.observacionesLiquidacion}</div>
              </div>
            )}
          </div>
        )}

        {/* FIRMA DE DOCUMENTOS POR EMPLEADOS */}
        {t.tipo === "Firma de documentos por empleados" && (
          <div style={{ marginTop: 4 }}>
            {Array.isArray(t.empleadosFirma) && t.empleadosFirma.length > 0 ? (
              <div>
                <strong style={{ color: "#1d4ed8" }}>Empleados para firma ({t.empleadosFirma.length}):</strong>
                <div style={{ marginTop: 6 }}>
                  {t.empleadosFirma.map((empFirma, idx) => (
                    <div 
                      key={idx} 
                      style={{ 
                        background: "white", 
                        border: "1px solid #bfdbfe", 
                        borderRadius: 6, 
                        padding: 10, 
                        marginBottom: 6,
                        fontSize: 11
                      }}
                    >
                      <div style={{ fontWeight: 600, color: "#1e3a5f", marginBottom: 4 }}>
                        {empFirma.nombreEmpleado || nombreEmpleado(empFirma.empleadoId) || "Empleado"}
                        <span style={{ marginLeft: 8, fontWeight: 400, color: "#64748b" }}>C.C. {empFirma.documento || "—"}</span>
                      </div>
                      
                      {Array.isArray(empFirma.documentosAdjuntos) && empFirma.documentosAdjuntos.length > 0 && (
                        <div style={{ marginTop: 4, paddingLeft: 8, borderLeft: "2px solid #93c5fd" }}>
                          <span style={{ fontSize: 10, color: "#64748b" }}>Documentos ({empFirma.documentosAdjuntos.length}):</span>
                          {empFirma.documentosAdjuntos.map((doc, docIdx) => (
                            <div key={docIdx} style={{ marginTop: 2 }}>
                              📄 {doc.nombre}
                              {doc.url && (
                                <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 6, color: "#1890ff", fontSize: 10 }}>Ver</a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div><em>Sin empleados asignados</em></div>
            )}
            
            {t.observacionesFirma && (
              <div style={{ marginTop: 8, padding: 8, background: "#f8fafc", borderRadius: 4, border: "1px solid #e2e8f0" }}>
                <strong>Observaciones:</strong>
                <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{t.observacionesFirma}</div>
              </div>
            )}
          </div>
        )}

        {/* REALIZAR TAREA GENERAL */}
        {t.tipo === "Realizar tarea general" && (
          <div style={{ marginTop: 4 }}>
            {t.observacionesTareaGeneral && (
              <div style={{ marginBottom: 8, padding: 8, background: "#f8fafc", borderRadius: 4, border: "1px solid #e2e8f0" }}>
                <strong>Observaciones / Descripción:</strong>
                <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{t.observacionesTareaGeneral}</div>
              </div>
            )}
            
            {t.tiempoEjecucion && (
              <div style={{ padding: "6px 10px", background: "#dbeafe", borderRadius: 4, display: "inline-block" }}>
                <strong>⏱️ Tiempo de ejecución:</strong> {t.tiempoEjecucion}
              </div>
            )}
          </div>
        )}

        {/* CANCELACIONES O MODIFICACIONES FUDO */}
        {t.tipo === "Cancelaciones o modificaciones FUDO" && (
          <div style={{ marginTop: 4 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <div style={{ padding: 8, background: "#fff5eb", borderRadius: 4, border: "1px solid #ffe4c9" }}>
                <strong>Número de pedido:</strong> {t.numeroPedidoFudo || "—"}
              </div>
              <div style={{ padding: 8, background: "#fff5eb", borderRadius: 4, border: "1px solid #ffe4c9" }}>
                <strong>Empleado en caja:</strong> {nombreEmpleado(t.empleadoCajaId) || t.empleadoCajaId || "—"}
              </div>
            </div>
            
            {t.observacionesFudo && (
              <div style={{ marginBottom: 8, padding: 8, background: "#f8fafc", borderRadius: 4, border: "1px solid #e2e8f0" }}>
                <strong>Observaciones:</strong>
                <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{t.observacionesFudo}</div>
              </div>
            )}
            
            {Array.isArray(t.soportesFudo) && t.soportesFudo.length > 0 && (
              <div style={{ marginTop: 6, padding: 8, background: "#fffbf5", borderRadius: 4, border: "1px solid #ffe4c9" }}>
                <strong style={{ color: "#ff6b35" }}>Documentos de soporte ({t.soportesFudo.length}):</strong>
                <div style={{ marginTop: 4 }}>
                  {t.soportesFudo.map((doc, idx) => (
                    <div key={idx} style={{ fontSize: 11, marginBottom: 2 }}>
                      📄 {doc.nombre}
                      {doc.url && (
                        <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 6, color: "#1890ff", fontSize: 10 }}>Ver</a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  const soportesVisibles = Array.isArray(tareaSeleccionada?.soportes)
    ? tareaSeleccionada.soportes
    : Array.isArray(tareaOriginal?.soportes)
    ? tareaOriginal.soportes
    : [];

  // Meses disponibles según fechaLimite en las tareas
  const monthsSet = new Set();
  (tareasVisibles || []).forEach((tt) => {
    if (!tt.fechaLimite) return;
    const d = parseDate(tt.fechaLimite);
    if (!d) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    monthsSet.add(key);
  });
  const monthsArr = Array.from(monthsSet).sort().reverse();
  const monthNames = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  function monthLabel(key) {
    const [y, mm] = (key || "").split("-");
    const m = Number(mm) - 1;
    return key ? `${monthNames[m] || mm} ${y}` : "";
  }
 
  return (
    <>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "2fr 1.4fr",
        gap: 16,
      }}
    >
      {/* LISTADO + FILTROS (IZQUIERDA) */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          padding: 12,
        }}
      >
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 10 }}>
          Consultar / gestionar tareas
        </h2>
 
        {/* SOLO FILTROS AQUÍ */}
        <div style={{ ...grid3, marginBottom: 12 }}>
          <Field label="Tipo de tarea">
            <select
              style={{
                width: "100%",
                fontSize: 14,
                padding: "6px",
                borderRadius: 4,
                border: "1px solid #cbd5e1",
              }}
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
            >
              <option value="">Todos</option>
              {tiposTarea.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
 
          <Field label="Estado">
            <select
              style={{
                width: "100%",
                fontSize: 14,
                padding: "6px",
                borderRadius: 4,
                border: "1px solid #cbd5e1",
              }}
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
            >
              <option value="">Todos</option>
              {estados.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </Field>
 
          <Field label="Sede">
            <select
              style={{
                width: "100%",
                fontSize: 14,
                padding: "6px",
                borderRadius: 4,
                border: "1px solid #cbd5e1",
              }}
              value={filtroSede}
              onChange={(e) => setFiltroSede(e.target.value)}
            >
              <option value="">Todas</option>
              {(sedes || []).map((sedeItem) => {
                const key =
                  typeof sedeItem === "string"
                    ? sedeItem
                    : sedeItem?.id || sedeItem?.nombre || JSON.stringify(sedeItem);
                const value =
                  typeof sedeItem === "string"
                    ? sedeItem
                    : sedeItem?.nombre || sedeItem?.direccion || "";
                const label =
                  typeof sedeItem === "string"
                    ? sedeItem
                    : sedeItem?.nombre || sedeItem?.direccion || "(Sin nombre)";
                return (
                  <option key={key} value={value}>
                    {label}
                  </option>
                );
              })}
            </select>
          </Field>

          <Field label="Mes de creación">
            <select
              style={{
                width: "100%",
                fontSize: 14,
                padding: "6px",
                borderRadius: 4,
                border: "1px solid #cbd5e1",
              }}
              value={filtroMes}
              onChange={(e) => setFiltroMes(e.target.value)}
            >
              <option value="">Todos los meses</option>
              {(() => {
                const meses = new Set();
                (tareas || []).forEach((t) => {
                  const f = parseDate(t.fechaCreacion);
                  if (f) {
                    const key = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, "0")}`;
                    meses.add(key);
                  }
                });
                const mesesArr = Array.from(meses).sort().reverse();
                const nombres = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
                return mesesArr.map((key) => {
                  const [year, month] = key.split("-");
                  return (
                    <option key={key} value={key}>
                      {nombres[parseInt(month) - 1]} {year}
                    </option>
                  );
                });
              })()}
            </select>
          </Field>
        </div>

        {/* Checkbox para mostrar tareas con respuesta + contador */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 12,
          padding: '8px 12px',
          backgroundColor: mostrarTareasConRespuesta ? '#e8f5e9' : '#f8fafc',
          borderRadius: 8,
          border: mostrarTareasConRespuesta ? '1px solid #4caf50' : '1px solid #e2e8f0',
        }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 8, 
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
          }}>
            <input
              type="checkbox"
              checked={mostrarTareasConRespuesta}
              onChange={(e) => setMostrarTareasConRespuesta(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }}
            />
            <span style={{ color: mostrarTareasConRespuesta ? '#2e7d32' : '#64748b' }}>
              ✅ Mostrar tareas con respuesta (Procesado / Negado)
            </span>
          </label>
          {tareasOcultas > 0 && !mostrarTareasConRespuesta && !hayFiltrosActivos && (
            <span style={{ 
              fontSize: 11, 
              color: '#64748b', 
              backgroundColor: '#e2e8f0', 
              padding: '4px 8px', 
              borderRadius: 10 
            }}>
              {tareasOcultas} tarea{tareasOcultas !== 1 ? 's' : ''} oculta{tareasOcultas !== 1 ? 's' : ''}
            </span>
          )}
        </div>
 
        {/* TABLA DE TAREAS */}
        {filtradas.length === 0 ? (
          <p style={{ fontSize: 14, color: '#64748b' }}>
            No hay tareas que cumplan con los filtros seleccionados.
          </p>
        ) : (
          <div style={{ maxHeight: 400, overflow: "auto" }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#e2e8f0' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #cbd5e1', fontSize: 14 }}>Consecutivo</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #cbd5e1', fontSize: 14 }}>Fecha</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #cbd5e1', fontSize: 14 }}>Sede</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #cbd5e1', fontSize: 14 }}>Tipo</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #cbd5e1', fontSize: 14 }}>Estado</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #cbd5e1', fontSize: 14 }}>Fecha límite</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid #cbd5e1', fontSize: 14 }}>Radicado por</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, borderBottom: '2px solid #cbd5e1', fontSize: 14 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((t) => (
                  <tr key={t.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '10px 12px', fontSize: 14 }}><strong>{t.id}</strong></td>
                    <td style={{ padding: '10px 12px', fontSize: 14 }}>{t.fechaCreacion}</td>
                    <td style={{ padding: '10px 12px', fontSize: 14 }}>{typeof t.sede === "object" ? t.sede?.nombre || t.sede?.direccion || "" : t.sede}</td>
                    <td style={{ padding: '10px 12px', fontSize: 14 }}>{typeof t.tipo === "object" ? t.tipo?.nombre || "" : t.tipo}</td>
                    <td style={{ padding: '10px 12px', fontSize: 14 }}>{typeof t.estado === "object" ? t.estado?.nombre || "" : t.estado}</td>
                    <td style={{ padding: '10px 12px', fontSize: 14 }}>{t.fechaLimite || "—"}</td>
                    <td style={{ padding: '10px 12px', fontSize: 14, color: '#0369a1', fontWeight: 500 }}>{t.creadorNombre || "—"}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      <Button
                        variant="outline"
                        style={{ fontSize: 13, padding: '6px 14px' }}
                        onClick={() => seleccionarTarea(t)}
                      >
                        Ver detalle
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
 
      {/* DETALLE / GESTIÓN (DERECHA) */}
      <div
        style={{
          background: "#f8fafc",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          padding: 12,
        }}
      >
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#334155', marginBottom: 8 }}>
          Detalle de la tarea
        </h3>
 
        {!tareaSeleccionada ? (
          <p style={{ fontSize: 14, color: '#64748b' }}>
            Selecciona una tarea del listado para ver el detalle y gestionar su
            estado.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* RESUMEN GENERAL - AHORA EDITABLE */}
            <div style={{ fontSize: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <strong>Consecutivo:</strong>
                <span style={{ fontWeight: 600, color: "#0369a1", fontSize: 15 }}>
                  {tareaSeleccionada.id}
                </span>
              </div>
              
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, backgroundColor: '#e0f2fe', padding: '6px 10px', borderRadius: 6 }}>
                <strong>📝 Radicado por:</strong>
                <span style={{ fontWeight: 600, color: "#0369a1", fontSize: 14 }}>
                  {tareaSeleccionada.creadorNombre || "—"}
                </span>
              </div>
              
              <Field label="Fecha creación">
                <input
                  type="date"
                  value={tareaSeleccionada.fechaCreacion || ""}
                  onChange={(e) => setTareaSeleccionada(prev => prev ? { ...prev, fechaCreacion: e.target.value } : prev)}
                  style={{
                    width: "100%",
                    fontSize: 14,
                    padding: "6px",
                    borderRadius: 4,
                    border: "1px solid #cbd5e1",
                    background: (!esGerente || soloLectura || (tareaSeleccionada?.estado || "").toString().trim() === "Procesado") ? "#f1f5f9" : "white",
                  }}
                  disabled={!esGerente || soloLectura || (tareaSeleccionada?.estado || "").toString().trim() === "Procesado"}
                />
              </Field>

              <Field label="Sede">
                <select
                  value={typeof tareaSeleccionada.sede === "object" ? tareaSeleccionada.sede?.nombre || tareaSeleccionada.sede?.direccion || "" : tareaSeleccionada.sede || ""}
                  onChange={(e) => setTareaSeleccionada(prev => prev ? { ...prev, sede: e.target.value } : prev)}
                  style={{
                    width: "100%",
                    fontSize: 14,
                    padding: "6px",
                    borderRadius: 4,
                    border: "1px solid #cbd5e1",
                    background: (!esGerente || soloLectura || (tareaSeleccionada?.estado || "").toString().trim() === "Procesado") ? "#f1f5f9" : "white",
                  }}
                  disabled={!esGerente || soloLectura || (tareaSeleccionada?.estado || "").toString().trim() === "Procesado"}
                >
                  <option value="">Seleccione sede</option>
                  {(sedes || []).map((sedeItem) => {
                    const key =
                      typeof sedeItem === "string"
                        ? sedeItem
                        : sedeItem?.id || sedeItem?.nombre || JSON.stringify(sedeItem);
                    const value =
                      typeof sedeItem === "string"
                        ? sedeItem
                        : sedeItem?.nombre || sedeItem?.direccion || "";
                    const label =
                      typeof sedeItem === "string"
                        ? sedeItem
                        : sedeItem?.nombre || sedeItem?.direccion || "(Sin nombre)";
                    return (
                      <option key={key} value={value}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </Field>

              <Field label="Tipo">
                <select
                  value={tareaSeleccionada.tipo || ""}
                  onChange={(e) => setTareaSeleccionada(prev => prev ? { ...prev, tipo: e.target.value } : prev)}
                  style={{
                    width: "100%",
                    fontSize: 14,
                    padding: "6px",
                    borderRadius: 4,
                    border: "1px solid #cbd5e1",
                    background: (!esGerente || soloLectura || (tareaSeleccionada?.estado || "").toString().trim() === "Procesado") ? "#f1f5f9" : "white",
                  }}
                  disabled={!esGerente || soloLectura || (tareaSeleccionada?.estado || "").toString().trim() === "Procesado"}
                >
                  <option value="">Seleccione tipo</option>
                  {tiposTarea.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Fecha límite">
                <input
                  type="date"
                  value={tareaSeleccionada.fechaLimite || ""}
                  onChange={(e) => setTareaSeleccionada(prev => prev ? { ...prev, fechaLimite: e.target.value } : prev)}
                  style={{
                    width: "100%",
                    fontSize: 14,
                    padding: "6px",
                    borderRadius: 4,
                    border: "1px solid #cbd5e1",
                    background: (!esGerente || soloLectura || (tareaSeleccionada?.estado || "").toString().trim() === "Procesado") ? "#f1f5f9" : "white",
                  }}
                  disabled={!esGerente || soloLectura || (tareaSeleccionada?.estado || "").toString().trim() === "Procesado"}
                />
              </Field>

              {tareaSeleccionada.empleadoId && (
                <div>
                  <strong>Empleado asociado:</strong>{" "}
                  {nombreEmpleado(tareaSeleccionada.empleadoId)}
                </div>
              )}
            </div>
 
            {/* DATOS ESPECÍFICOS SEGÚN TIPO */}
            {renderDatosEspecificos(tareaSeleccionada)}
 
            {/* RESPONSABLE (SE DEFINE AQUÍ) */}
            <Field label="Responsable">
              <select
                style={{
                  width: "100%",
                  fontSize: 14,
                  padding: "6px",
                  borderRadius: 4,
                  border: "1px solid #cbd5e1",
                  background: (!esGerente || soloLectura || (tareaSeleccionada?.estado || "").toString().trim() === "Procesado") ? "#f1f5f9" : "white",
                }}
                value={tareaSeleccionada.responsableId || ""}
                onChange={cambiarResponsable}
                disabled={!esGerente || soloLectura || (tareaSeleccionada?.estado || "").toString().trim() === "Procesado"}
              >
                <option value="">Seleccione responsable</option>
                {empleadosParaResponsable.map((emp) => {
                  // Marcar gerentes de otras empresas
                  const esDeOtraEmpresa = filtroEmpresa && emp.empresa_id && emp.empresa_id !== filtroEmpresa;
                  return (
                    <option key={emp.id} value={emp.id}>
                      {emp.nombre}{esDeOtraEmpresa ? ' (Gerencia)' : ''}
                    </option>
                  );
                })}
              </select>
            </Field>
 
            {/* ESTADO */}
            <Field label="Estado de la tarea">
              <select
                style={{
                  width: "100%",
                  fontSize: 14,
                  padding: "6px",
                  borderRadius: 4,
                  border: "1px solid #cbd5e1",
                }}
                value={tareaSeleccionada.estado}
                onChange={cambiarEstado}
                disabled={soloLectura || (tareaSeleccionada?.estado || "").toString().trim() === "Procesado"}
              >
                {estados.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
            </Field>
 
            {/* COMENTARIOS DE GESTIÓN */}
            <Field label="Comentarios de gestión">
              <textarea
                className="input"
                style={{ fontSize: 14, minHeight: 70, padding: 6 }}
                value={tareaSeleccionada.comentariosGestion || ""}
                onChange={cambiarComentario}
                disabled={soloLectura || (tareaSeleccionada?.estado || "").toString().trim() === "Procesado"}
              />
            </Field>
            <Field label="Adjuntar archivo a la gestión">
              <input
                type="file"
                onChange={handleAdjuntoGestionInput}
                disabled={soloLectura || (tareaSeleccionada?.estado || "").toString().trim() === "Procesado"}
                style={{ fontSize: 14 }}
              />
              {subiendoAdjuntoGestion && (
                <div
                  style={{
                    marginTop: 6,
                    padding: "6px 8px",
                    background: "#e0f2fe",
                    borderRadius: 4,
                    fontSize: 13,
                    color: "#0f172a",
                    border: "1px solid #bae6fd",
                  }}
                >
                  Subiendo adjunto de gestión…
                </div>
              )}
              {tareaSeleccionada.adjuntoGestion ? (
                <div
                  style={{ fontSize: 13, color: '#475569', marginTop: 4 }}
                >
                  <div style={{ fontWeight: 600 }}>{tareaSeleccionada.adjuntoGestion.nombre}</div>

                  {tareaSeleccionada.adjuntoGestion.url &&
                  (tareaSeleccionada.adjuntoGestion.tipo?.startsWith("image/") ||
                  esImagenNombre(tareaSeleccionada.adjuntoGestion.nombre)) && (
                    <img
                      src={tareaSeleccionada.adjuntoGestion.url}
                      alt={tareaSeleccionada.adjuntoGestion.nombre}
                      style={{
                        width: 160,
                        borderRadius: 4,
                        border: "1px solid #e5e7eb",
                        marginTop: 4,
                        cursor: "pointer",
                      }}
                      onClick={() => setPreviewFile(tareaSeleccionada.adjuntoGestion)}
                      title="Clic para previsualizar"
                    />
                  )}

                  <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                    {tareaSeleccionada.adjuntoGestion.url && (
                      <>
                        <button
                          type="button"
                          style={{
                            background: "#0ea5e9",
                            color: "white",
                            border: "none",
                            borderRadius: 4,
                            padding: "4px 8px",
                            fontSize: 11,
                            cursor: "pointer",
                          }}
                          onClick={() => setPreviewFileGestion(tareaSeleccionada.adjuntoGestion)}
                        >
                          Previsualizar
                        </button>
                        <a
                          href={tareaSeleccionada.adjuntoGestion.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            background: "#475569",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: 4,
                            fontSize: 11,
                            textDecoration: "none",
                          }}
                          download
                        >
                          Descargar
                        </a>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <span className="text-[11px] text-slate-500">Ningún archivo seleccionado</span>
              )}
            </Field>
 
            {/* SOPORTES ORIGINALES (CREACIÓN) */}
            {soportesVisibles && soportesVisibles.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <h4 className="text-[11px] font-semibold mb-1">Soportes adjuntos (creación de la tarea)</h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px,1fr))", gap: 12 }}>
                  {soportesVisibles.map(s => (
                    <div key={s.id} style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: 8, background: "white", fontSize: 11, display: "flex", flexDirection: "column", gap: 6 }}>
                      {s.url && esImagenNombre(s.nombre) ? (
                        <img
                          src={s.url}
                          alt={s.nombre}
                          style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 4, border: "1px solid #e2e8f0" }}
                        />
                      ) : (
                        <div style={{ width: "100%", height: 80, background: "#f1f5f9", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, border: "1px solid #e2e8f0" }}>
                          {s.nombre.match(/\.(pdf)$/i) ? "📄" : "📎"}
                        </div>
                      )}
                      <div style={{ fontWeight: 600, textAlign: "center", wordBreak: "break-word" }}>{s.nombre}</div>
                      {s.url && (
                        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                          <button
                            type="button"
                            style={{ background: "#0ea5e9", color: "white", border: "none", borderRadius: 4, padding: "4px 6px", fontSize: 10, cursor: "pointer" }}
                            onClick={() => setPreviewFileGestion(s)}
                          >
                            Ver
                          </button>
                          <a
                            href={s.url}
                            target="_blank"
                            rel="noreferrer"
                            download
                            style={{ background: "#475569", color: "white", borderRadius: 4, padding: "4px 6px", fontSize: 10, textDecoration: "none" }}
                          >
                            Descargar
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
 
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 10,
              }}
            >
             {esGerente && tienePermiso("admin_procesos_eliminar") && (
               <Button
                onClick={eliminarTarea}
                disabled={!tareaSeleccionada}
                style={{ background: "#dc2626", color: "white" }}
              >
                Eliminar tarea
              </Button>
             )}
             {/* El botón Guardar cambios se muestra si:
                 - Tiene permiso admin_procesos_editar, O
                 - Es el responsable de la tarea (puede gestionar su tarea asignada)
             */}
             {(tienePermiso("admin_procesos_editar") || esResponsableTarea(tareaSeleccionada)) && (
             <Button
  onClick={guardarCambiosDetalle}
  disabled={!tareaSeleccionada || soloLectura}
>
  Guardar cambios
</Button>
             )}

            </div>
          </div>
        )}
      </div>
    </div>
    {previewFileGestion && (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
        }}
        onClick={() => setPreviewFileGestion(null)}
      >
        <div
          style={{
            background: "white",
            borderRadius: 8,
            maxWidth: 900,
            width: "100%",
            maxHeight: "85vh",
            overflow: "hidden",
            position: "relative",
            padding: 20,
            boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              background: "#ef4444",
              color: "white",
              border: "none",
              borderRadius: 4,
              padding: "4px 8px",
              fontSize: 12,
              cursor: "pointer",
            }}
            onClick={() => setPreviewFileGestion(null)}
          >
            Cerrar
          </button>
          <div style={{ flex: 1, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 6, padding: 12, background: "#f8fafc" }}>
            {(() => {
              const nombre = previewFileGestion?.nombre || "";
              const tipo = previewFileGestion?.tipo || "";
              const esImg = !!previewFileGestion?.url && (tipo.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(nombre));
              const esPdf = !!previewFileGestion?.url && (tipo === "application/pdf" || /\.(pdf)$/i.test(nombre));
              if (esImg) {
                return (
                  <img src={previewFileGestion.url} alt={nombre} style={{ maxWidth: "100%", borderRadius: 6, display: "block", margin: "0 auto" }} />
                );
              }
              if (esPdf) {
                return (
                  <iframe src={previewFileGestion.url} title={nombre} style={{ width: "100%", height: "70vh", border: "none" }} />
                );
              }
              if (previewFileGestion?.url) {
                return (
                  <div style={{ textAlign: "center", fontSize: 14 }}>
                    <a href={previewFileGestion.url} target="_blank" rel="noreferrer" style={{ color: "#0ea5e9" }}>Descargar archivo</a>
                  </div>
                );
              }
              return null;
            })()}
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, textAlign: "center", color: "#334155" }}>{previewFileGestion?.nombre}</div>
        </div>
      </div>
    )}
    </>
  );
};
 
  // ---------- TABLERO MENSUAL ----------
  function VistaTablero() {
    function parseDate(iso) {
      if (!iso) return null;
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, m - 1, d);
    }
  
    function esImagenNombre(nombre) {
      return !!(
        nombre &&
        /\.(jpg|jpeg|png|gif|bmp|webp|jfif)$/i.test(nombre)
      );
    }
  
    const hoy = new Date();
    const mesActual = hoy.getMonth();
    const anioActual = hoy.getFullYear();

    const [selectedMonth, setSelectedMonth] = useState(mesActual);
    const [selectedYear, setSelectedYear] = useState(anioActual);
    const [previewFile, setPreviewFile] = useState(null);
    const [previewList, setPreviewList] = useState([]); // lista de archivos del modal
    const [filtroTipoTarea, setFiltroTipoTarea] = useState(""); // filtro por tipo de tarea
    const [filtroEstado, setFiltroEstado] = useState(""); // filtro por estado

    function prevMonth() {
      setSelectedMonth((m) => {
        if (m === 0) {
          setSelectedYear((y) => y - 1);
          return 11;
        }
        return m - 1;
      });
    }
    function nextMonth() {
      setSelectedMonth((m) => {
        if (m === 11) {
          setSelectedYear((y) => y + 1);
          return 0;
        }
        return m + 1;
      });
    }

    function daysRemaining(iso) {
      if (!iso) return Infinity;
      const d = parseDate(iso);
      if (!d) return Infinity;
      const diff = d.setHours(0,0,0,0) - new Date().setHours(0,0,0,0);
      return Math.ceil(diff / (1000 * 60 * 60 * 24));
    }

    function colorForTask(t) {
      const days = daysRemaining(t.fechaLimite);
      if (days === Infinity) return { background: "#d1fae5" };
      if (days <= 2) return { background: "#fee2e2" }; // rojo
      if (days <= 5) return { background: "#fef3c7" }; // amarillo
      return { background: "#d1fae5" }; // verde
    }

    const tareasTablero = useMemo(() => {
      if (esGerente) return Array.isArray(tareas) ? tareas : [];

      const lista = Array.isArray(tareas) ? tareas : [];
      const idsPermitidos = new Set();
      if (empleadoActual?.id) idsPermitidos.add(String(empleadoActual.id));
      if (usuarioActual?.id) idsPermitidos.add(String(usuarioActual.id));

      const nombreUsuario = ((empleadoActual?.nombre || usuarioActual?.nombre || "") || "")
        .toString()
        .trim()
        .toLowerCase();

      return lista.filter((t) => {
        const respId = t.responsableId || t.responsable || "";
        const empId = t.empleadoId || "";
        const creadorId = t.creadorId || t.creador || "";

        const respName = (nombreEmpleado(respId) || respId || "").trim().toLowerCase();
        const empName = (nombreEmpleado(empId) || empId || "").trim().toLowerCase();
        const respTexto = (typeof t.responsable === "string" ? t.responsable : "")
          .trim()
          .toLowerCase();
        const creadorNombre = (t.creadorNombre || "")
          .toString()
          .trim()
          .toLowerCase();

        const coincideId =
          idsPermitidos.has(String(respId)) ||
          idsPermitidos.has(String(empId)) ||
          idsPermitidos.has(String(creadorId));

        const coincideNombre =
          nombreUsuario &&
          (respName === nombreUsuario ||
            empName === nombreUsuario ||
            respTexto === nombreUsuario ||
            creadorNombre === nombreUsuario);

        return coincideId || coincideNombre;
      });
    }, [tareas, esGerente, empleadoActual, usuarioActual, empleados]);

    const tareasMes = (tareasTablero || []).filter((t) => {
      // Determinar qué fecha usar: si tiene fecha límite, usarla. Si no, usar fecha creación
      const fechaParaFiltro = t.fechaLimite || t.fechaCreacion;
      
      const f = parseDate(fechaParaFiltro);
      if (!f) return false; // no mostramos sin fecha alguna
      const coincideMes = f.getMonth() === selectedMonth && f.getFullYear() === selectedYear;
      
      // Filtrar por tipo de tarea si está seleccionado
      const coincideTipo = !filtroTipoTarea || t.tipo === filtroTipoTarea;
      
      // Filtrar por estado si está seleccionado
      const coincideEstado = !filtroEstado || (t.estado || "En gestión") === filtroEstado;
      
      return coincideMes && coincideTipo && coincideEstado;
    });
  
    const porEstado = {};
    estados.forEach((e) => {
      porEstado[e] = [];
    });
    tareasMes.forEach((t) => {
      const est = t.estado || "En gestión";
      if (!porEstado[est]) porEstado[est] = [];
      porEstado[est].push(t);
    });
  
    return (
      <div
        style={{
          background: "#f8fafc",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          padding: 12,
        }}
      >
        <h2 className="text-sm font-semibold mb-3">
          Tablero mensual de tareas
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <button type="button" onClick={prevMonth} style={{ padding: "4px 8px" }}>{"◀"}</button>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {(() => {
              const names = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
              return `${names[selectedMonth]} ${selectedYear}`;
            })()}
          </div>
          <button type="button" onClick={nextMonth} style={{ padding: "4px 8px" }}>{"▶"}</button>
          
          {/* Filtro por Tipo de Tarea */}
          <div style={{ marginLeft: 12, display: "flex", alignItems: "center", gap: 4 }}>
            <label style={{ fontSize: 12, color: "#475569", fontWeight: 500 }}>Tipo:</label>
            <select
              value={filtroTipoTarea}
              onChange={(e) => setFiltroTipoTarea(e.target.value)}
              style={{ fontSize: 11, padding: "4px 8px", borderRadius: 4, border: "1px solid #cbd5e1", background: "white", cursor: "pointer", maxWidth: 180 }}
            >
              <option value="">Todos los tipos</option>
              {[
                "Pago de servicios públicos",
                "Daños",
                "Solicitud empleados",
                "Pago de impuestos",
                "Pago de arrendamiento",
                "Pago de salud",
                "Reclamar incapacidad",
                "Solicitud de vacaciones",
                "Factura a pagar por transferencia",
                "Comunicación con empleado",
                "Solicitar sencilla",
                "Venta de aceite",
                "Entrega de cuadre",
                "Pago de facturas por transferencia",
                "Solicitud de firma a empleados",
                "Asignación de tarea",
                "Cambio de horario",
                "Pago de facturas en efectivo",
                "Préstamos a locales",
                "Afiliación a seguridad social",
              ].map((tipo) => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </select>
          </div>
          
          {/* Filtro por Estado */}
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <label style={{ fontSize: 12, color: "#475569", fontWeight: 500 }}>Estado:</label>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              style={{ fontSize: 11, padding: "4px 8px", borderRadius: 4, border: "1px solid #cbd5e1", background: "white", cursor: "pointer" }}
            >
              <option value="">Todos los estados</option>
              {["En gestión", "Revisado", "Negado", "Aprobado", "Procesado"].map((estado) => (
                <option key={estado} value={estado}>{estado}</option>
              ))}
            </select>
          </div>
          
          {/* Botón para limpiar filtros */}
          {(filtroTipoTarea || filtroEstado) && (
            <button
              type="button"
              onClick={() => { setFiltroTipoTarea(""); setFiltroEstado(""); }}
              style={{ fontSize: 11, padding: "4px 8px", borderRadius: 4, border: "1px solid #ef4444", background: "#fee2e2", color: "#dc2626", cursor: "pointer" }}
            >
              Limpiar filtros
            </button>
          )}
          
          <div style={{ marginLeft: 12, fontSize: 12, color: "#475569" }}>
            Mostrar tareas por fecha límite del mes
          </div>
          {previewFile && (
            <div
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
              onClick={() => { setPreviewFile(null); setPreviewList([]); }}
            >
              <div
                style={{ background: "white", borderRadius: 8, maxWidth: 900, width: "100%", maxHeight: "85vh", overflow: "hidden", position: "relative", padding: 20, boxShadow: "0 4px 24px rgba(0,0,0,0.3)", display: "flex", flexDirection: "column", gap: 12 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  style={{ position: "absolute", top: 10, right: 10, background: "#ef4444", color: "white", border: "none", borderRadius: 4, padding: "4px 8px", fontSize: 12, cursor: "pointer" }}
                  onClick={() => { setPreviewFile(null); setPreviewList([]); }}
                >Cerrar</button>
                <div style={{ flex: 1, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: 6, padding: 12, background: "#f8fafc" }}>
                  {previewFile.url && esImagenNombre(previewFile.nombre) ? (
                    <img src={previewFile.url} alt={previewFile.nombre} style={{ maxWidth: "100%", borderRadius: 6, display: "block", margin: "0 auto" }} />
                  ) : previewFile.url && previewFile.nombre.match(/\.(pdf)$/i) ? (
                    <iframe src={previewFile.url} title={previewFile.nombre} style={{ width: "100%", height: "70vh", border: "none" }} />
                  ) : previewFile.url ? (
                    <div style={{ textAlign: "center", fontSize: 14 }}>
                      <a href={previewFile.url} target="_blank" rel="noreferrer" style={{ color: "#0ea5e9" }}>Descargar archivo</a>
                    </div>
                  ) : null}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, textAlign: "center", color: "#334155" }}>{previewFile.nombre}</div>
                {previewList.length > 1 && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px,1fr))", gap: 8, maxHeight: 160, overflow: "auto", paddingTop: 8, borderTop: "1px solid #e2e8f0" }}>
                    {previewList.map(f => (
                      <div key={f.id} style={{ border: f.id === previewFile.id ? "2px solid #0ea5e9" : "1px solid #cbd5e1", borderRadius: 6, padding: 4, background: "white", cursor: "pointer", display: "flex", flexDirection: "column", gap: 4 }} onClick={() => setPreviewFile(f)}>
                        {esImagenNombre(f.nombre) ? (
                          <img src={f.url} alt={f.nombre} style={{ width: "100%", height: 60, objectFit: "cover", borderRadius: 4 }} />
                        ) : (
                          <div style={{ width: "100%", height: 60, background: "#f1f5f9", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{f.nombre.match(/\.(pdf)$/i) ? "PDF" : "DOC"}</div>
                        )}
                        <div style={{ fontSize: 9, textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.nombre}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
  
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${estados.length}, minmax(0, 1fr))`,
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          {estados.map((es) => (
            <div key={es}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  marginBottom: 6,
                  padding: "4px 6px",
                  borderRadius: 4,
                  background: "#e5e7eb",
                }}
              >
                {es}
              </div>
  
              {porEstado[es].length === 0 ? (
                <div
                  style={{
                    fontSize: 11,
                    color: "#94a3b8",
                    fontStyle: "italic",
                  }}
                >
                  Sin tareas
                </div>
              ) : (
                porEstado[es].map((t) => {
                  const styleColor = colorForTask(t);
                  return (
                    <div
                      key={t.id}
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 4,
                        padding: 6,
                        fontSize: 11,
                        marginBottom: 8,
                        ...styleColor,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <strong>{t.tipo}</strong>
                        <span style={{ fontWeight: 600, color: "#0369a1", fontSize: 10 }}>
                          {t.id}
                        </span>
                      </div>
                      <div>Sede: {typeof t.sede === "object" ? t.sede?.nombre || t.sede?.direccion || "" : t.sede}</div>
                      <div>F. creación: {t.fechaCreacion}</div>
                      <div>F. límite: {t.fechaLimite || "—"}</div>

                      {t.empleadoId && (
                        <div>
                          Emp: {nombreEmpleado(t.empleadoId)}
                        </div>
                      )}
                      {t.responsableId && (
                        <div>
                          Resp: {nombreEmpleado(t.responsableId)}
                        </div>
                      )}

                      {t.tipo === "Daños" && (
                        <div style={{ marginTop: 4 }}>
                          {t.queSeDanio && (
                            <div>Daño: {typeof t.queSeDanio === 'object' ? t.queSeDanio?.nombre || '' : t.queSeDanio}</div>
                          )}
                          <div>
                            Emp?:{" "}
                            {t.fueEmpleadoDanio === "si" ? "Sí" : "No"}
                          </div>
                        </div>
                      )}

                      {t.tipo === "Factura a pagar por transferencia" && (
                        <div style={{ marginTop: 4 }}>
                          {t.proveedor && (
                            <div>Prov: {typeof t.proveedor === 'object' ? t.proveedor.nombre || JSON.stringify(t.proveedor) : t.proveedor}</div>
                          )}
                          {(t.valorFactura || t.valorPagar) && (
                            <div>
                              Valor: {t.valorFactura || t.valorPagar}
                            </div>
                          )}
                        </div>
                      )}

                      {(t.tipo === "Comunicación con empleado" ||
                        t.tipo === "Solicitar sencilla" ||
                        t.tipo === "Solicitud sencilla" ||
                        t.tipo === "Solicitud empleados") &&
                        (t.comunicacionTexto || t.comentarioSencilla) && (
                          <div
                            style={{ marginTop: 4 }}
                            className="whitespace-pre-wrap"
                          >
                            {typeof t.comunicacionTexto === 'object' ? t.comunicacionTexto?.nombre || '' : t.comunicacionTexto || typeof t.comentarioSencilla === 'object' ? t.comentarioSencilla?.nombre || '' : t.comentarioSencilla || ''}
                          </div>
                        )}

                      {/* COMENTARIO DE GESTIÓN */}
                      {t.comentariosGestion && (
                        <div style={{ marginTop: 4 }}>
                          <span style={{ fontWeight: 600 }}>
                            Comentario:
                          </span>{" "}
                          {typeof t.comentariosGestion === 'object' ? t.comentariosGestion?.nombre || '' : t.comentariosGestion}
                        </div>
                      )}

                      {/* ADJUNTO DE LA GESTIÓN - CON PREVISUALIZACIÓN */}
                      {t.adjuntoGestion && t.adjuntoGestion.url && (
                        <div style={{ marginTop: 4 }}>
                          <span style={{ fontWeight: 600 }}>Adjunto:</span>{" "}
                          <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 4 }}>
                            {t.adjuntoGestion.tipo?.startsWith("image/") || esImagenNombre(t.adjuntoGestion.nombre) ? (
                              <img
                                src={t.adjuntoGestion.url}
                                alt={t.adjuntoGestion.nombre || "Imagen adjunta"}
                                style={{ width: "100%", maxWidth: 180, borderRadius: 4, border: "1px solid #e5e7eb" }}
                              />
                            ) : (
                              <div style={{ fontSize: 11 }}>
                                {t.adjuntoGestion.tipo === "application/pdf" ? "PDF" : "Archivo"} – {t.adjuntoGestion.nombre}
                              </div>
                            )}
                            <div style={{ display: "flex", gap: 6 }}>
                              <button
                                type="button"
                                style={{ background: "#0ea5e9", color: "white", border: "none", borderRadius: 4, padding: "4px 6px", fontSize: 10, cursor: "pointer" }}
                                onClick={() => { setPreviewFile(t.adjuntoGestion); setPreviewList([t.adjuntoGestion]); }}
                              >Ver</button>
                              <a
                                href={t.adjuntoGestion.url}
                                target="_blank"
                                rel="noreferrer"
                                download
                                style={{ background: "#475569", color: "white", borderRadius: 4, padding: "4px 6px", fontSize: 10, textDecoration: "none" }}
                              >Descargar</a>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* En tablero solo mostramos el adjunto de gestión, no los soportes de creación */}
                    </div>
                  );
                })
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // ---------- RENDER PRINCIPAL DE PROCESOS ADMINISTRATIVOS ----------
  return (
    <div
      className="flex flex-col gap-3 text-sm"
      onKeyDownCapture={(e) => {
        const tag = e.target.tagName;
        // Si estoy escribiendo dentro de un campo de formulario,
        // no dejo que otros manejadores globales de teclado actúen.
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
          e.stopPropagation();
        }
      }}
    >
      {/* Barra de navegación interna de este módulo */}
 
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        marginBottom: '20px',
        padding: '16px 20px',
        backgroundColor: '#fffbf5',
        borderRadius: '12px',
        border: '2px solid #ffe4c9'
      }}>
        <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none' }}>
          <button
            className={modo === "crear" ? "tab active" : "tab"}
            onClick={() => {
              setModo("crear");
              resetForm();
              setTareaSeleccionada(null);
              setSoloLectura(false);
              setPreviewFileGestion(null);
            }}
          >
            Crear tarea
          </button>
          <button
            className={modo === "consultar" ? "tab active" : "tab"}
            onClick={() => {
              setModo("consultar");
              setTareaSeleccionada(null);
              setSoloLectura(false);
              setPreviewFileGestion(null);
            }}
          >
            Consultar / gestionar tareas
          </button>
          <button
            className={modo === "tablero" ? "tab active" : "tab"}
            onClick={() => {
              setModo("tablero");
              setTareaSeleccionada(null);
              setSoloLectura(false);
              setPreviewFileGestion(null);
            }}
          >
            Tablero mensual
          </button>
        </div>
        <h1 style={{ 
          fontSize: '18px', 
          fontWeight: '700', 
          color: '#ff6b35',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          📋 Procesos Administrativos
        </h1>
      </div>
 
      {/* Modal configuración SMS para tareas */}
      {mostrarConfigSMSTareas && (
        <div style={{
          position: "fixed",
          top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999
        }}>
          <div style={{
            background: "white",
            borderRadius: 12,
            padding: 24,
            width: "90%",
            maxWidth: 400,
            boxShadow: "0 10px 40px rgba(0,0,0,0.3)"
          }}>
            <h3 style={{ margin: "0 0 16px", color: "#1e40af" }}>
              Configurar Notificaciones SMS - Tareas
            </h3>
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
              Configura quien recibira SMS cuando se creen nuevas tareas y cuando se procesen.
            </p>
            
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                Nombre del receptor
              </label>
              <input
                type="text"
                value={configSMSTareas.nombreReceptor}
                onChange={(e) => setConfigSMSTareas(prev => ({ ...prev, nombreReceptor: e.target.value }))}
                placeholder="Ej: Administracion"
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  fontSize: 14
                }}
              />
            </div>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                Telefono (sin +57)
              </label>
              <input
                type="tel"
                value={configSMSTareas.telefono}
                onChange={(e) => setConfigSMSTareas(prev => ({ ...prev, telefono: e.target.value }))}
                placeholder="Ej: 3207254372"
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  fontSize: 14
                }}
              />
            </div>
            
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setMostrarConfigSMSTareas(false)}
                style={{
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  background: "white",
                  cursor: "pointer"
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={guardarConfigSMSTareas}
                disabled={guardandoConfigSMS}
                style={{
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "none",
                  background: "#10b981",
                  color: "white",
                  cursor: "pointer"
                }}
              >
                {guardandoConfigSMS ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modo === "crear" && renderVistaCrear()}
      {modo === "consultar" && renderVistaConsultar()}
      {modo === "tablero" && <VistaTablero />}
    </div>
  );
}

function Sedes({ sedes = [], setSedes, filtroEmpresa = null }) {
  const [nombre, setNombre] = useState("");
  const [razonSocial, setRazonSocial] = useState("");
  const [telefono, setTelefono] = useState("");
  const [direccion, setDireccion] = useState("");
  const [nit, setNit] = useState("");
  const [representanteLegal, setRepresentanteLegal] = useState("");
  const [tipoDocRepresentante, setTipoDocRepresentante] = useState("Cédula de Ciudadanía");
  const [cedulaRepresentante, setCedulaRepresentante] = useState("");
  const [generoRepresentante, setGeneroRepresentante] = useState("Masculino");
  const [firmaElectronica, setFirmaElectronica] = useState(null); // { firma: base64, fechaRegistro, timestamp }
  const [sedeSeleccionada, setSedeSeleccionada] = useState(null);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [mostrarCanvasFirmaRep, setMostrarCanvasFirmaRep] = useState(false);
  const generarIdSede = useCallback(() => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return uid();
  }, []);

  const limpiarFormulario = () => {
    setNombre("");
    setRazonSocial("");
    setTelefono("");
    setDireccion("");
    setNit("");
    setRepresentanteLegal("");
    setTipoDocRepresentante("Cédula de Ciudadanía");
    setCedulaRepresentante("");
    setGeneroRepresentante("Masculino");
    setFirmaElectronica(null);
    setSedeSeleccionada(null);
    setModoEdicion(false);
  };

  // Manejar cuando se completa la firma del representante
  const manejarFirmaRepresentante = (firmaData) => {
    setMostrarCanvasFirmaRep(false);
    const datosCompletos = {
      firma: firmaData.firma,
      fechaRegistro: firmaData.fechaFirma,
      timestamp: firmaData.timestamp,
      nombreRepresentante: representanteLegal
    };
    setFirmaElectronica(datosCompletos);
    alert("✅ Firma digital del representante almacenada correctamente.\n\nRecuerda hacer clic en 'Guardar Cambios' para que se guarde en la base de datos.");
  };

  // Eliminar firma electrónica
  const eliminarFirmaElectronica = () => {
    if (window.confirm("¿Está seguro de eliminar la firma digital del representante?")) {
      setFirmaElectronica(null);
    }
  };

  const agregarSede = async () => {
    if (!nombre.trim()) {
      alert("Por favor ingresa el nombre de la sede");
      return;
    }

    const nuevaSede = {
      id: generarIdSede(),
      nombre: nombre.trim(),
      razonSocial: razonSocial.trim(),
      telefono: telefono.trim(),
      direccion: direccion.trim(),
      nit: nit.trim(),
      representanteLegal: representanteLegal.trim(),
      tipoDocRepresentante: tipoDocRepresentante,
      cedulaRepresentante: cedulaRepresentante.trim(),
      generoRepresentante: generoRepresentante,
      firmaElectronica: firmaElectronica
    };

    setSedes((prev) => [...(prev || []), nuevaSede]);

    const exito = await crearSedeEnSupabase(nuevaSede, filtroEmpresa);
    if (!exito) {
      alert("No se pudo guardar la sede en Supabase. Revisa la consola para más detalles.");
    }
    limpiarFormulario();
  };

  const editarSede = async () => {
    if (!sedeSeleccionada) return;

    const sedeActualizada = {
      ...sedeSeleccionada,
      nombre: nombre.trim(),
      razonSocial: razonSocial.trim(),
      telefono: telefono.trim(),
      direccion: direccion.trim(),
      nit: nit.trim(),
      representanteLegal: representanteLegal.trim(),
      tipoDocRepresentante: tipoDocRepresentante,
      cedulaRepresentante: cedulaRepresentante.trim(),
      generoRepresentante: generoRepresentante,
      firmaElectronica: firmaElectronica
    };

    setSedes((prev) =>
      (prev || []).map((sede) =>
        sede.id === sedeSeleccionada.id ? sedeActualizada : sede
      )
    );
    limpiarFormulario();

    const exito = await actualizarSedeEnSupabase(sedeActualizada, filtroEmpresa);
    if (!exito) {
      alert("No se pudieron guardar los cambios de la sede en Supabase. Revisa la consola.");
    }
  };

  const seleccionarParaEditar = (sede) => {
    setSedeSeleccionada(sede);
    setNombre(sede.nombre || "");
    setRazonSocial(sede.razonSocial || "");
    setTelefono(sede.telefono || "");
    setDireccion(sede.direccion || "");
    setNit(sede.nit || "");
    setRepresentanteLegal(sede.representanteLegal || "");
    setTipoDocRepresentante(sede.tipoDocRepresentante || "Cédula de Ciudadanía");
    setCedulaRepresentante(sede.cedulaRepresentante || "");
    setGeneroRepresentante(sede.generoRepresentante || "Masculino");
    setFirmaElectronica(sede.firmaElectronica || null);
    setModoEdicion(true);
  };

  const eliminarSede = async (id) => {
    if (confirm("¿Estás seguro de eliminar esta sede?")) {
      setSedes((prev) => (prev || []).filter((sede) => sede.id !== id));
      if (sedeSeleccionada?.id === id) {
        limpiarFormulario();
      }
      const exito = await eliminarSedeEnSupabase(id);
      if (!exito) {
        alert("No se pudo eliminar la sede en Supabase. Revisa la consola.");
      }
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <h2 style={{ textAlign: 'center', marginBottom: '30px' }}>Gestión de Sedes</h2>
      
      <div style={{ backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
        <h3 style={{ marginTop: 0 }}>{modoEdicion ? 'Editar Sede' : 'Nueva Sede'}</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Nombre de la Sede:</label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej: Sede Principal"
              style={{ width: '100%', padding: '10px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Razón Social:</label>
            <input
              type="text"
              value={razonSocial}
              onChange={(e) => setRazonSocial(e.target.value)}
              placeholder="Ej: Empresa S.A.S."
              style={{ width: '100%', padding: '10px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Teléfono:</label>
            <input
              type="text"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="Ej: 3001234567"
              style={{ width: '100%', padding: '10px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Dirección:</label>
            <input
              type="text"
              value={direccion}
              onChange={(e) => setDireccion(e.target.value)}
              placeholder="Ej: Calle 123 #45-67"
              style={{ width: '100%', padding: '10px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>NIT:</label>
            <input
              type="text"
              value={nit}
              onChange={(e) => setNit(e.target.value)}
              placeholder="Ej: 900123456-7"
              style={{ width: '100%', padding: '10px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Representante Legal:</label>
            <input
              type="text"
              value={representanteLegal}
              onChange={(e) => setRepresentanteLegal(e.target.value)}
              placeholder="Ej: Juan Pérez"
              style={{ width: '100%', padding: '10px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Tipo de Documento del Representante:</label>
            <select
              value={tipoDocRepresentante}
              onChange={(e) => setTipoDocRepresentante(e.target.value)}
              style={{ width: '100%', padding: '10px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
            >
              <option value="Cédula de Ciudadanía">Cédula de Ciudadanía</option>
              <option value="Cédula de Extranjería">Cédula de Extranjería</option>
              <option value="Pasaporte">Pasaporte</option>
              <option value="NIT">NIT</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Número de Documento del Representante:</label>
            <input
              type="text"
              value={cedulaRepresentante}
              onChange={(e) => setCedulaRepresentante(e.target.value)}
              placeholder="Ej: 1234567890"
              style={{ width: '100%', padding: '10px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Género del Representante:</label>
            <select
              value={generoRepresentante}
              onChange={(e) => setGeneroRepresentante(e.target.value)}
              style={{ width: '100%', padding: '10px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
            >
              <option value="Masculino">Masculino</option>
              <option value="Femenino">Femenino</option>
            </select>
          </div>
        </div>

        {/* Sección de Firma Digital del Representante Legal */}
        <div style={{
          marginTop: '25px',
          padding: '20px',
          background: firmaElectronica ? '#f8f9fa' : '#fff5f5',
          border: `2px solid ${firmaElectronica ? '#6c757d' : '#dc3545'}`,
          borderRadius: '10px'
        }}>
          <h4 style={{ 
            margin: '0 0 15px', 
            color: firmaElectronica ? '#495057' : '#dc3545',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            ✍️ Firma Digital del Representante Legal
            {firmaElectronica && (
              <span style={{
                background: '#6c757d',
                color: 'white',
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                ALMACENADA
              </span>
            )}
          </h4>

          {!representanteLegal.trim() ? (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              color: '#999',
              fontStyle: 'italic'
            }}>
              ⚠️ Primero ingrese el nombre del representante legal para poder registrar su firma
            </div>
          ) : firmaElectronica ? (
            <div>
              {/* Vista previa de la firma registrada */}
              <div style={{
                display: 'flex',
                gap: '20px',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  background: 'white',
                  border: '2px solid #6c757d',
                  borderRadius: '8px',
                  padding: '10px',
                  textAlign: 'center'
                }}>
                  <img 
                    src={firmaElectronica.firma} 
                    alt="Firma electrónica"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '100px',
                      filter: 'grayscale(100%)'
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <p style={{ margin: '0 0 8px', fontSize: '13px' }}>
                    <strong>Representante:</strong> {firmaElectronica.nombreRepresentante || representanteLegal}
                  </p>
                  <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#6c757d' }}>
                    <strong>Fecha de registro:</strong> {firmaElectronica.fechaRegistro}
                  </p>
                  <p style={{ margin: '0', fontSize: '11px', color: '#999' }}>
                    Esta firma se usará automáticamente en los contratos
                  </p>
                </div>
                <div>
                  <button
                    onClick={eliminarFirmaElectronica}
                    style={{
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    🗑️ Eliminar Firma
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <p style={{ 
                margin: '0 0 15px', 
                fontSize: '13px', 
                color: '#666' 
              }}>
                📌 Registre la firma del representante legal para que se use automáticamente en los contratos.
                Solo se requerirá la firma del trabajador al momento de firmar documentos.
              </p>
              <button
                onClick={() => setMostrarCanvasFirmaRep(true)}
                style={{
                  background: 'linear-gradient(135deg, #495057 0%, #6c757d 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 25px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                ✍️ Registrar Firma Digital
              </button>
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          {modoEdicion ? (
            <>
              <button
                onClick={editarSede}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  padding: '12px 30px',
                  fontSize: '16px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  marginRight: '10px'
                }}
              >
                Guardar Cambios
              </button>
              <button
                onClick={limpiarFormulario}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  padding: '12px 30px',
                  fontSize: '16px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
            </>
          ) : (
            <button
              onClick={agregarSede}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                padding: '12px 30px',
                fontSize: '16px',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Agregar Sede
            </button>
          )}
        </div>
      </div>

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #ddd' }}>
        <h3 style={{ marginTop: 0 }}>Sedes Registradas ({sedes.length})</h3>
        
        {sedes.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
            No hay sedes registradas. Agrega la primera sede usando el formulario superior.
          </p>
        ) : (
          <div style={{ display: 'grid', gap: '15px' }}>
            {sedes.map((sede) => (
              <div
                key={sede.id}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  padding: '15px',
                  backgroundColor: sedeSeleccionada?.id === sede.id ? '#e7f3ff' : '#f9f9f9'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <h4 style={{ margin: '0', color: '#333' }}>{sede.nombre}</h4>
                      {sede.firmaElectronica && sede.firmaElectronica.firma && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: 'linear-gradient(135deg, #4b5563 0%, #6b7280 100%)',
                          color: 'white',
                          padding: '3px 10px',
                          borderRadius: '12px',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>
                          ✍️ Tiene Firma Digital Almacenada
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                      <p style={{ margin: '5px 0' }}>
                        <strong>Razón Social:</strong> {sede.razonSocial || 'No especificada'}
                      </p>
                      <p style={{ margin: '5px 0' }}>
                        <strong>Teléfono:</strong> {sede.telefono || 'No especificado'}
                      </p>
                      <p style={{ margin: '5px 0' }}>
                        <strong>Dirección:</strong> {sede.direccion || 'No especificada'}
                      </p>
                      <p style={{ margin: '5px 0' }}>
                        <strong>NIT:</strong> {sede.nit || 'No especificado'}
                      </p>
                      <p style={{ margin: '5px 0' }}>
                        <strong>Representante Legal:</strong> {sede.representanteLegal || 'No especificado'}
                      </p>
                      <p style={{ margin: '5px 0' }}>
                        <strong>Tipo Doc. Representante:</strong> {sede.tipoDocRepresentante || 'Cédula de Ciudadanía'}
                      </p>
                      <p style={{ margin: '5px 0' }}>
                        <strong>N° Doc. Representante:</strong> {sede.cedulaRepresentante || 'No especificado'}
                      </p>
                      <p style={{ margin: '5px 0' }}>
                        <strong>Género Representante:</strong> {sede.generoRepresentante || 'Masculino'}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginLeft: '15px' }}>
                    <button
                      onClick={() => seleccionarParaEditar(sede)}
                      style={{
                        backgroundColor: '#ffc107',
                        color: 'white',
                        padding: '8px 16px',
                        fontSize: '14px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => eliminarSede(sede.id)}
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        padding: '8px 16px',
                        fontSize: '14px',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Canvas para capturar firma del representante */}
      {mostrarCanvasFirmaRep && (
        <FirmaDigitalCanvas
          onFirmaCompleta={manejarFirmaRepresentante}
          onCancelar={() => setMostrarCanvasFirmaRep(false)}
          nombreFirmante={representanteLegal || "Representante Legal"}
          tipoDocumento="Firma Electrónica - Representante Legal"
          ancho={400}
          alto={200}
        />
      )}
    </div>
  );
}

/* ====================== CONVERTIR NÚMEROS A LETRAS ====================== */
function numeroALetras(num) {
  const unidades = ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
  const decenas = ['', '', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
  const especiales = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISEIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
  const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

  num = Math.round(num);
  if (num === 0) return 'CERO';

  let salida = '';
  let resto = 0;

  // Millones
  if (num >= 1000000) {
    let millones = Math.floor(num / 1000000);
    if (millones === 1) {
      salida += 'UN MILLON';
    } else {
      salida += numeroALetras(millones) + ' MILLONES';
    }
    resto = num % 1000000;
    if (resto > 0) {
      salida += ' ';
    }
    num = resto;
  }

  // Miles
  if (num >= 1000) {
    let miles = Math.floor(num / 1000);
    if (miles === 1) {
      salida += 'MIL';
    } else {
      salida += numeroALetras(miles) + ' MIL';
    }
    resto = num % 1000;
    if (resto > 0) {
      salida += ' ';
    }
    num = resto;
  }

  // Centenas
  if (num >= 100) {
    let cent = Math.floor(num / 100);
    if (cent === 1 && num % 100 === 0) {
      salida += 'CIEN';
    } else {
      salida += centenas[cent];
      if (num % 100 > 0) {
        salida += ' ';
      }
    }
    num = num % 100;
  }

  // Decenas y unidades
  if (num >= 20) {
    let dec = Math.floor(num / 10);
    salida += decenas[dec];
    if (num % 10 > 0) {
      salida += ' Y ';
      salida += unidades[num % 10];
    }
  } else if (num >= 10) {
    salida += especiales[num - 10];
  } else if (num > 0) {
    salida += unidades[num];
  }

  return salida.trim();
}

function formatoSalarioEnLetras(num) {
  const texto = numeroALetras(num);
  // Si el número termina en 000 (es redondo), agregar "DE" antes de PESOS
  if (num % 1000 === 0) {
    return texto + ' DE';
  }
  return texto;
}

// ====== COMPONENTE CONFIGURACIONES ======
function Configuraciones({ empleados = [], setEmpleados = () => {}, setAmbiente, user = null, tienePermiso = () => true, valoresAnuales: valoresAnualesProp = {}, setValoresAnuales: setValoresAnualesProp = () => {}, empresas = [], filtroEmpresa = null, esSuperAdmin = false }) {
  // Determinar el primer tab disponible según permisos
  const obtenerPrimerTabDisponible = () => {
    if (tienePermiso("config_usuarios")) return "usuarios";
    if (tienePermiso("config_perfiles")) return "perfiles";
    if (tienePermiso("config_valores_anuales")) return "valores";
    if (tienePermiso("config_avisos")) return "avisos";
    if (tienePermiso("config_reglamento")) return "reglamento";
    return "usuarios"; // Fallback
  };
  
  const [tabConfig, setTabConfig] = useState(obtenerPrimerTabDisponible); // "usuarios" | "perfiles" | "valores" | "avisos" | "reglamento"
  const STORAGE_PERFILES_KEY = "perfiles_app_v1";
  const STORAGE_VALORES_KEY = "valoresAnuales_app_v1";

  // Efecto para redirigir si el tab actual no tiene permiso
  useEffect(() => {
    const tabsPermisos = {
      usuarios: "config_usuarios",
      perfiles: "config_perfiles",
      valores: "config_valores_anuales",
      avisos: "config_avisos",
      reglamento: "config_reglamento"
    };
    
    // Si el tab actual no tiene permiso, cambiar al primer tab disponible
    if (!tienePermiso(tabsPermisos[tabConfig])) {
      const nuevoTab = obtenerPrimerTabDisponible();
      if (nuevoTab !== tabConfig) {
        setTabConfig(nuevoTab);
      }
    }
  }, [tabConfig, tienePermiso]);

  // ========== REGLAMENTO INTERNO DE TRABAJO ==========
  const [reglamentosSedes, setReglamentosSedes] = useState([]);
  const [cargandoReglamentos, setCargandoReglamentos] = useState(false);
  const [sedesDisponibles, setSedesDisponibles] = useState([]);
  const [sedeReglamentoSel, setSedeReglamentoSel] = useState('');
  const [subiendoReglamento, setSubiendoReglamento] = useState(false);
  const [vistaPreviewReglamento, setVistaPreviewReglamento] = useState(null);

  // Cargar sedes disponibles para reglamentos
  const cargarSedesParaReglamentos = useCallback(async () => {
    try {
      let query = supabase.from('sedes').select('*');
      if (filtroEmpresa) {
        query = query.eq('empresa_id', filtroEmpresa);
      }
      const { data, error } = await query.order('nombre');
      if (!error && data) {
        setSedesDisponibles(data);
      }
    } catch (e) {
      console.error('Error cargando sedes:', e);
    }
  }, [filtroEmpresa]);

  // Cargar reglamentos existentes
  const cargarReglamentos = useCallback(async () => {
    setCargandoReglamentos(true);
    try {
      let query = supabase
        .from('reglamentos_internos')
        .select('*')
        .order('fecha_publicacion', { ascending: false });
      
      if (filtroEmpresa) {
        query = query.eq('empresa_id', filtroEmpresa);
      }
      
      const { data, error } = await query;
      if (!error) {
        setReglamentosSedes(data || []);
      } else {
        console.error('Error en query reglamentos:', error);
      }
    } catch (e) {
      console.error('Error cargando reglamentos:', e);
    }
    setCargandoReglamentos(false);
  }, [filtroEmpresa]);

  // Subir reglamento PDF con firma incrustada
  const subirReglamentoPDF = async (file, sedeId) => {
    if (!file || !sedeId) return null;
    
    setSubiendoReglamento(true);
    try {
      const sede = sedesDisponibles.find(s => s.id === sedeId);
      
      // Obtener firma de la sede
      let firmaData = null;
      if (sede?.firma_electronica) {
        try {
          firmaData = typeof sede.firma_electronica === 'string' 
            ? JSON.parse(sede.firma_electronica) 
            : sede.firma_electronica;
        } catch (e) {
          console.error('Error parseando firma:', e);
        }
      }

      // Cargar pdf-lib dinámicamente si no está cargada
      if (!window.PDFLib) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://unpkg.com/pdf-lib@1.17.1/dist/pdf-lib.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      const { PDFDocument, rgb, StandardFonts } = window.PDFLib;

      // Leer el PDF original
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      
      // Agregar logo a la primera página del PDF (parte superior derecha)
      try {
        const logoResponse = await fetch('/logo BIGBURGUER.jpg.jpg');
        const logoArrayBuffer = await logoResponse.arrayBuffer();
        const logoBytes = new Uint8Array(logoArrayBuffer);
        const logoImage = await pdfDoc.embedJpg(logoBytes);
        
        // Obtener la primera página del PDF
        const pages = pdfDoc.getPages();
        if (pages.length > 0) {
          const firstPage = pages[0];
          const { width, height } = firstPage.getSize();
          
          // Dibujar logo en la parte superior derecha
          const logoWidth = 70;
          const logoHeight = 70;
          firstPage.drawImage(logoImage, {
            x: width - logoWidth - 30, // 30px desde el borde derecho
            y: height - logoHeight - 30, // 30px desde el borde superior
            width: logoWidth,
            height: logoHeight,
          });
        }
      } catch (logoError) {
        console.error('Error agregando logo a primera página:', logoError);
      }
      
      // Si hay firma, agregar página con firma al final
      if (firmaData && firmaData.firma) {
        // Crear nueva página para la firma
        const page = pdfDoc.addPage([612, 792]); // Tamaño carta
        const { width, height } = page.getSize();
        
        // Cargar fuentes
        const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
        
        // Posición Y inicial
        let yPos = height - 150;
        
        // Título de la sección
        const tituloText = 'CONSTANCIA DE FIRMA';
        const tituloWidth = fontBold.widthOfTextAtSize(tituloText, 16);
        page.drawText(tituloText, {
          x: width / 2 - tituloWidth / 2,
          y: yPos,
          size: 16,
          font: fontBold,
          color: rgb(0, 0, 0),
        });
        
        yPos -= 50;
        
        // Línea decorativa
        page.drawLine({
          start: { x: 100, y: yPos },
          end: { x: width - 100, y: yPos },
          thickness: 1,
          color: rgb(0.7, 0.7, 0.7),
        });
        
        yPos -= 60;
        
        // Incrustar imagen de la firma
        try {
          // La firma viene en base64
          const firmaBase64 = firmaData.firma.split(',')[1] || firmaData.firma;
          const firmaBytes = Uint8Array.from(atob(firmaBase64), c => c.charCodeAt(0));
          
          let firmaImage;
          if (firmaData.firma.includes('image/png')) {
            firmaImage = await pdfDoc.embedPng(firmaBytes);
          } else {
            firmaImage = await pdfDoc.embedJpg(firmaBytes);
          }
          
          // Dibujar la firma centrada
          const firmaDims = firmaImage.scale(0.5);
          const firmaWidth = Math.min(firmaDims.width, 200);
          const firmaHeight = (firmaDims.height / firmaDims.width) * firmaWidth;
          
          page.drawImage(firmaImage, {
            x: width / 2 - firmaWidth / 2,
            y: yPos - firmaHeight,
            width: firmaWidth,
            height: firmaHeight,
          });
          
          yPos -= firmaHeight + 10;
        } catch (imgError) {
          console.error('Error incrustando firma:', imgError);
          yPos -= 60;
        }
        
        // Línea para la firma
        page.drawLine({
          start: { x: width / 2 - 120, y: yPos },
          end: { x: width / 2 + 120, y: yPos },
          thickness: 1,
          color: rgb(0, 0, 0),
        });
        
        yPos -= 25;
        
        // Nombre del firmante (usar datos de la sede o firmaData)
        const nombreFirmante = sede?.representantelegal || sede?.representante_legal || sede?.representanteLegal || firmaData?.nombreRepresentante || firmaData?.nombre || 'REPRESENTANTE LEGAL';
        const nombreWidth = fontBold.widthOfTextAtSize(nombreFirmante.toUpperCase(), 12);
        page.drawText(nombreFirmante.toUpperCase(), {
          x: width / 2 - nombreWidth / 2,
          y: yPos,
          size: 12,
          font: fontBold,
          color: rgb(0, 0, 0),
        });
        
        yPos -= 18;
        
        // Tipo y número de documento del firmante (usar datos de la sede)
        const tipoDoc = sede?.tipodocrepresentante || sede?.tipo_doc_representante || sede?.tipoDocRepresentante || 'C.C.';
        const numDoc = sede?.cedularepresentante || sede?.cedula_representante || sede?.cedulaRepresentante || firmaData?.documento || '';
        if (numDoc) {
          const docText = `${tipoDoc}: ${numDoc}`;
          const docWidth = fontRegular.widthOfTextAtSize(docText, 10);
          page.drawText(docText, {
            x: width / 2 - docWidth / 2,
            y: yPos,
            size: 10,
            font: fontRegular,
            color: rgb(0.3, 0.3, 0.3),
          });
          yPos -= 16;
        }
        
        // Sede
        const sedeText = `Sede: ${sede?.nombre || 'N/A'}`;
        const sedeWidth = fontRegular.widthOfTextAtSize(sedeText, 10);
        page.drawText(sedeText, {
          x: width / 2 - sedeWidth / 2,
          y: yPos,
          size: 10,
          font: fontRegular,
          color: rgb(0.3, 0.3, 0.3),
        });
        
        yPos -= 30;
        
        // Cargo
        const cargoText = firmaData.cargo || 'Empleador / Propietario';
        const cargoWidth = fontBold.widthOfTextAtSize(cargoText, 11);
        page.drawText(cargoText, {
          x: width / 2 - cargoWidth / 2,
          y: yPos,
          size: 11,
          font: fontBold,
          color: rgb(0, 0, 0),
        });
        
        yPos -= 60;
        
        // Fecha de publicación
        const fechaText = `Fecha de publicación: ${new Date().toLocaleDateString('es-CO', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })}`;
        const fechaWidth = fontRegular.widthOfTextAtSize(fechaText, 9);
        page.drawText(fechaText, {
          x: width / 2 - fechaWidth / 2,
          y: yPos,
          size: 9,
          font: fontRegular,
          color: rgb(0.5, 0.5, 0.5),
        });
      }
      
      // Guardar el PDF modificado
      const pdfModificado = await pdfDoc.save();
      const blobModificado = new Blob([pdfModificado], { type: 'application/pdf' });
      
      const nombreArchivo = `reglamento_${sedeId}_${Date.now()}.pdf`;
      const rutaArchivo = `reglamentos/${filtroEmpresa}/${nombreArchivo}`;
      
      // Subir archivo a storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('empleados-docs')
        .upload(rutaArchivo, blobModificado, { upsert: true });
      
      if (uploadError) throw uploadError;
      
      // Obtener URL pública
      const { data: urlData } = supabase.storage
        .from('empleados-docs')
        .getPublicUrl(rutaArchivo);
      
      // Guardar registro en la tabla
      const payload = {
        empresa_id: filtroEmpresa,
        sede_id: sedeId,
        nombre_sede: sede?.nombre || 'Sin sede',
        archivo_url: urlData.publicUrl,
        archivo_nombre: file.name,
        firma_incluida: !!firmaData,
        firma_nombre: firmaData?.nombre || null,
        firma_cargo: firmaData?.cargo || null,
        firma_imagen: firmaData?.firma || null,
        fecha_publicacion: new Date().toISOString(),
        activo: true
      };
      
      // Verificar si ya existe uno para esta sede
      const { data: existente } = await supabase
        .from('reglamentos_internos')
        .select('id')
        .eq('sede_id', sedeId)
        .single();
      
      if (existente) {
        // Actualizar existente
        const { error } = await supabase
          .from('reglamentos_internos')
          .update(payload)
          .eq('id', existente.id);
        if (error) throw error;
      } else {
        // Crear nuevo
        const { error } = await supabase
          .from('reglamentos_internos')
          .insert(payload);
        if (error) throw error;
      }
      
      alert('✅ Reglamento publicado correctamente para ' + (sede?.nombre || 'la sede'));
      cargarReglamentos();
      return urlData.publicUrl;
    } catch (e) {
      console.error('Error subiendo reglamento:', e);
      alert('❌ Error al subir el reglamento: ' + e.message);
      return null;
    } finally {
      setSubiendoReglamento(false);
    }
  };

  // Eliminar reglamento
  const eliminarReglamento = async (reglamentoId) => {
    if (!confirm('¿Está seguro de eliminar este reglamento?')) return;
    
    try {
      const { error } = await supabase
        .from('reglamentos_internos')
        .delete()
        .eq('id', reglamentoId);
      
      if (error) throw error;
      
      alert('✅ Reglamento eliminado');
      cargarReglamentos();
    } catch (e) {
      console.error('Error eliminando reglamento:', e);
      alert('❌ Error al eliminar');
    }
  };

  // Cargar datos al cambiar a tab reglamento
  useEffect(() => {
    if (tabConfig === 'reglamento') {
      cargarSedesParaReglamentos();
      cargarReglamentos();
    }
  }, [tabConfig, cargarSedesParaReglamentos, cargarReglamentos]);

  // ========== AVISOS INTRANET ==========
  const [avisos, setAvisos] = useState([]);
  const [cargandoAvisos, setCargandoAvisos] = useState(false);
  const [modalAviso, setModalAviso] = useState(false);
  const [avisoEditando, setAvisoEditando] = useState(null);
  const [formAviso, setFormAviso] = useState({
    titulo: '',
    contenido: '',
    tipo: 'noticia',
    imagen: '',
    activo: true
  });

  // Cargar avisos de la intranet
  const cargarAvisosIntranet = useCallback(async () => {
    setCargandoAvisos(true);
    try {
      let query = supabase
        .from('avisos_intranet')
        .select('*')
        .order('fecha', { ascending: false });
      
      if (filtroEmpresa) {
        query = query.eq('empresa_id', filtroEmpresa);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error cargando avisos:', error);
      } else {
        setAvisos(data || []);
      }
    } catch (e) {
      console.error('Error:', e);
    }
    setCargandoAvisos(false);
  }, [filtroEmpresa]);

  // Guardar aviso
  const guardarAviso = async () => {
    if (!formAviso.titulo.trim()) {
      alert('El título es obligatorio');
      return;
    }
    if (!formAviso.contenido.trim()) {
      alert('El contenido es obligatorio');
      return;
    }
    
    try {
      const payload = {
        titulo: formAviso.titulo.trim(),
        contenido: formAviso.contenido.trim(),
        tipo: formAviso.tipo,
        imagen: formAviso.imagen.trim() || null,
        activo: formAviso.activo,
        empresa_id: filtroEmpresa,
        fecha: new Date().toISOString()
      };
      
      if (avisoEditando) {
        // Actualizar
        const { error } = await supabase
          .from('avisos_intranet')
          .update(payload)
          .eq('id', avisoEditando.id);
        
        if (error) throw error;
        alert('✅ Aviso actualizado correctamente');
      } else {
        // Crear nuevo
        const { error } = await supabase
          .from('avisos_intranet')
          .insert([payload]);
        
        if (error) throw error;
        alert('✅ Aviso creado correctamente');
      }
      
      setModalAviso(false);
      setAvisoEditando(null);
      setFormAviso({ titulo: '', contenido: '', tipo: 'noticia', imagen: '', activo: true });
      cargarAvisosIntranet();
    } catch (e) {
      console.error('Error guardando aviso:', e);
      alert('Error al guardar: ' + e.message);
    }
  };

  // Eliminar aviso
  const eliminarAviso = async (aviso) => {
    if (!window.confirm(`¿Eliminar el aviso "${aviso.titulo}"?`)) return;
    
    try {
      const { error } = await supabase
        .from('avisos_intranet')
        .delete()
        .eq('id', aviso.id);
      
      if (error) throw error;
      alert('✅ Aviso eliminado');
      cargarAvisosIntranet();
    } catch (e) {
      console.error('Error eliminando:', e);
      alert('Error al eliminar: ' + e.message);
    }
  };

  // Abrir modal para editar
  const editarAviso = (aviso) => {
    setAvisoEditando(aviso);
    setFormAviso({
      titulo: aviso.titulo || '',
      contenido: aviso.contenido || '',
      tipo: aviso.tipo || 'noticia',
      imagen: aviso.imagen || '',
      activo: aviso.activo !== false
    });
    setModalAviso(true);
  };

  // Abrir modal para nuevo aviso
  const nuevoAviso = () => {
    setAvisoEditando(null);
    setFormAviso({ titulo: '', contenido: '', tipo: 'noticia', imagen: '', activo: true });
    setModalAviso(true);
  };

  // Toggle activo/inactivo
  const toggleActivoAviso = async (aviso) => {
    try {
      const { error } = await supabase
        .from('avisos_intranet')
        .update({ activo: !aviso.activo })
        .eq('id', aviso.id);
      
      if (error) throw error;
      cargarAvisosIntranet();
    } catch (e) {
      console.error('Error:', e);
    }
  };

  // Cargar avisos cuando se selecciona la pestaña
  useEffect(() => {
    if (tabConfig === 'avisos') {
      cargarAvisosIntranet();
    }
  }, [tabConfig, cargarAvisosIntranet]);

  // Renderizar sección de Reglamento Interno de Trabajo
  const renderReglamento = () => {
    return (
      <div style={{ padding: 16 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>
              📖 Reglamento Interno de Trabajo
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>
              Publica el reglamento interno para cada sede. Los empleados podrán verlo en la intranet.
            </p>
          </div>
        </div>

        {/* Formulario para subir nuevo reglamento */}
        <div style={{
          background: 'linear-gradient(135deg, #fff5f0 0%, #fff9f5 100%)',
          border: '2px solid #ff6b35',
          borderRadius: 12,
          padding: 20,
          marginBottom: 24
        }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#ff6b35' }}>
            📤 Publicar Nuevo Reglamento
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 16, alignItems: 'end' }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>
                Sede
              </label>
              <select
                value={sedeReglamentoSel}
                onChange={(e) => setSedeReglamentoSel(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '2px solid #e0e0e0',
                  borderRadius: 8,
                  fontSize: 14,
                  background: 'white'
                }}
              >
                <option value="">-- Seleccionar sede --</option>
                {sedesDisponibles.map(sede => (
                  <option key={sede.id} value={sede.id}>
                    {sede.nombre} {sede.firma_electronica ? '✓ (con firma)' : ''}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>
                Archivo PDF del Reglamento
              </label>
              <input
                type="file"
                accept=".pdf"
                id="inputReglamentoPDF"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (!sedeReglamentoSel) {
                    alert('Seleccione primero una sede');
                    e.target.value = '';
                    return;
                  }
                  if (!file.name.toLowerCase().endsWith('.pdf')) {
                    alert('Solo se permiten archivos PDF');
                    e.target.value = '';
                    return;
                  }
                  await subirReglamentoPDF(file, sedeReglamentoSel);
                  e.target.value = '';
                  setSedeReglamentoSel('');
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '2px dashed #ff6b35',
                  borderRadius: 8,
                  background: 'white',
                  cursor: 'pointer'
                }}
                disabled={subiendoReglamento}
              />
            </div>
            
            {subiendoReglamento && (
              <div style={{ padding: '10px 20px', color: '#ff6b35', fontWeight: 600 }}>
                ⏳ Subiendo...
              </div>
            )}
          </div>
          
          <p style={{ margin: '12px 0 0', fontSize: 12, color: '#666' }}>
            💡 Si la sede tiene firma electrónica registrada, se incluirá automáticamente en el documento.
            Los empleados podrán visualizar el reglamento pero no descargarlo.
          </p>
        </div>

        {/* Lista de reglamentos publicados */}
        <div style={{
          background: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: 12,
          overflow: 'hidden'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #2d2d44 100%)',
            padding: '14px 20px',
            color: 'white',
            fontWeight: 700,
            fontSize: 15
          }}>
            📋 Reglamentos Publicados ({reglamentosSedes.length})
          </div>
          
          {cargandoReglamentos ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
              ⏳ Cargando reglamentos...
            </div>
          ) : reglamentosSedes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
              📭 No hay reglamentos publicados aún
            </div>
          ) : (
            <div style={{ padding: 16 }}>
              {reglamentosSedes.map((reg, idx) => (
                <div
                  key={reg.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 16,
                    background: idx % 2 === 0 ? '#fafafa' : 'white',
                    borderRadius: 8,
                    marginBottom: 8,
                    border: '1px solid #e8e8e8'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <span style={{ fontSize: 32 }}>📄</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>
                        {reg.nombre_sede}
                      </div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                        {reg.archivo_nombre} • Publicado: {new Date(reg.fecha_publicacion).toLocaleDateString('es-CO')}
                      </div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        {reg.firma_incluida ? (
                          <span style={{ color: '#059669' }}>✓ Con firma de {reg.firma_nombre} ({reg.firma_cargo})</span>
                        ) : (
                          <span style={{ color: '#dc2626' }}>✗ Sin firma electrónica</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => setVistaPreviewReglamento(reg)}
                      style={{
                        padding: '8px 16px',
                        background: '#0891b2',
                        border: 'none',
                        borderRadius: 6,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: 'pointer'
                      }}
                    >
                      👁️ Vista Previa
                    </button>
                    <button
                      onClick={() => eliminarReglamento(reg.id)}
                      style={{
                        padding: '8px 16px',
                        background: '#dc2626',
                        border: 'none',
                        borderRadius: 6,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: 'pointer'
                      }}
                    >
                      🗑️ Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal de vista previa */}
        {vistaPreviewReglamento && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              background: 'white',
              borderRadius: 12,
              width: '90%',
              maxWidth: 900,
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '16px 20px',
                background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ color: 'white', fontWeight: 700 }}>
                  📖 Reglamento - {vistaPreviewReglamento.nombre_sede}
                </div>
                <button
                  onClick={() => setVistaPreviewReglamento(null)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 12px',
                    color: 'white',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  ✕ Cerrar
                </button>
              </div>
              <div style={{ flex: 1, overflow: 'auto', padding: 0 }}>
                <iframe
                  src={vistaPreviewReglamento.archivo_url + '#toolbar=0&navpanes=0'}
                  style={{
                    width: '100%',
                    height: '70vh',
                    border: 'none'
                  }}
                  title="Vista previa del reglamento"
                />
              </div>
              {vistaPreviewReglamento.firma_incluida && vistaPreviewReglamento.firma_imagen && (
                <div style={{
                  padding: 16,
                  background: '#f8f9fa',
                  borderTop: '1px solid #e0e0e0',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: '0 0 8px', fontSize: 12, color: '#666' }}>Firmado electrónicamente por:</p>
                  <img 
                    src={vistaPreviewReglamento.firma_imagen} 
                    alt="Firma" 
                    style={{ maxHeight: 60, marginBottom: 4 }} 
                  />
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{vistaPreviewReglamento.firma_nombre}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>{vistaPreviewReglamento.firma_cargo}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Renderizar sección de avisos
  const renderAvisos = () => {
    const tiposAviso = [
      { value: 'noticia', label: '📰 Noticia', color: '#1565C0' },
      { value: 'aviso', label: '📢 Aviso', color: '#F57C00' },
      { value: 'importante', label: '⚠️ Importante', color: '#C62828' },
      { value: 'evento', label: '🎉 Evento', color: '#7B1FA2' }
    ];

    const getTipoInfo = (tipo) => tiposAviso.find(t => t.value === tipo) || tiposAviso[0];

    return (
      <div style={{ padding: 16 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1a1a2e' }}>
              📣 Avisos y Noticias - Intranet
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>
              Administra los avisos que aparecen en la página principal de la intranet de empleados
            </p>
          </div>
          {tienePermiso("config_avisos_crear") && (
            <button
              onClick={nuevoAviso}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
                border: 'none',
                borderRadius: 8,
                color: 'white',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)'
              }}
            >
              ➕ Nuevo Aviso
            </button>
          )}
        </div>

        {/* Lista de avisos */}
        {cargandoAvisos ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
            ⏳ Cargando avisos...
          </div>
        ) : avisos.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: 60, 
            background: '#f8f9fa', 
            borderRadius: 12,
            border: '2px dashed #ddd'
          }}>
            <p style={{ fontSize: 48, margin: 0 }}>📭</p>
            <p style={{ fontSize: 16, color: '#666', marginTop: 12 }}>
              No hay avisos creados todavía
            </p>
            <p style={{ fontSize: 13, color: '#999' }}>
              Crea el primer aviso para que aparezca en la intranet de empleados
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {avisos.map(aviso => {
              const tipoInfo = getTipoInfo(aviso.tipo);
              return (
                <div 
                  key={aviso.id}
                  style={{
                    background: 'white',
                    borderRadius: 12,
                    padding: 16,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: '1px solid #eee',
                    opacity: aviso.activo ? 1 : 0.6
                  }}
                >
                  <div style={{ display: 'flex', gap: 16 }}>
                    {/* Imagen preview */}
                    {aviso.imagen && (
                      <img 
                        src={aviso.imagen} 
                        alt="" 
                        style={{ 
                          width: 100, 
                          height: 80, 
                          objectFit: 'cover', 
                          borderRadius: 8,
                          flexShrink: 0
                        }}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    
                    {/* Contenido */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{
                          padding: '2px 10px',
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 600,
                          background: tipoInfo.color + '15',
                          color: tipoInfo.color
                        }}>
                          {tipoInfo.label}
                        </span>
                        <span style={{
                          padding: '2px 10px',
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 600,
                          background: aviso.activo ? '#E8F5E9' : '#FFEBEE',
                          color: aviso.activo ? '#2E7D32' : '#C62828'
                        }}>
                          {aviso.activo ? '✅ Activo' : '❌ Inactivo'}
                        </span>
                        <span style={{ fontSize: 11, color: '#999' }}>
                          {aviso.fecha ? new Date(aviso.fecha).toLocaleDateString('es-CO', { 
                            day: 'numeric', 
                            month: 'short', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : ''}
                        </span>
                      </div>
                      
                      <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 600, color: '#1a1a2e' }}>
                        {aviso.titulo}
                      </h3>
                      
                      <p style={{ 
                        margin: 0, 
                        fontSize: 13, 
                        color: '#666',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {aviso.contenido}
                      </p>
                    </div>
                    
                    {/* Acciones */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                      {tienePermiso("config_avisos_editar") && (
                        <>
                          <button
                            onClick={() => editarAviso(aviso)}
                            style={{
                              padding: '6px 12px',
                              background: '#E3F2FD',
                              border: 'none',
                              borderRadius: 6,
                              color: '#1565C0',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            ✏️ Editar
                          </button>
                          <button
                            onClick={() => toggleActivoAviso(aviso)}
                            style={{
                              padding: '6px 12px',
                              background: aviso.activo ? '#FFF3E0' : '#E8F5E9',
                              border: 'none',
                              borderRadius: 6,
                              color: aviso.activo ? '#E65100' : '#2E7D32',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            {aviso.activo ? '🔒 Desactivar' : '🔓 Activar'}
                          </button>
                        </>
                      )}
                      {tienePermiso("config_avisos_eliminar") && (
                        <button
                          onClick={() => eliminarAviso(aviso)}
                          style={{
                            padding: '6px 12px',
                            background: '#FFEBEE',
                            border: 'none',
                            borderRadius: 6,
                            color: '#C62828',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          🗑️ Eliminar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal para crear/editar aviso */}
        {modalAviso && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: 16,
              padding: 24,
              width: '90%',
              maxWidth: 600,
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <h2 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 700 }}>
                {avisoEditando ? '✏️ Editar Aviso' : '➕ Nuevo Aviso'}
              </h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Tipo */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>
                    Tipo de aviso *
                  </label>
                  <select
                    value={formAviso.tipo}
                    onChange={(e) => setFormAviso({...formAviso, tipo: e.target.value, contenido: ''})}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '2px solid #ddd',
                      fontSize: 14
                    }}
                  >
                    {tiposAviso.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <p style={{ margin: '6px 0 0', fontSize: 11, color: '#666' }}>
                    {formAviso.tipo === 'noticia' 
                      ? '📰 Las noticias abren un enlace externo en nueva pestaña'
                      : '📄 Los avisos y eventos muestran el contenido completo en la intranet'
                    }
                  </p>
                </div>
                
                {/* Título */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>
                    Título *
                  </label>
                  <input
                    type="text"
                    value={formAviso.titulo}
                    onChange={(e) => setFormAviso({...formAviso, titulo: e.target.value})}
                    placeholder={formAviso.tipo === 'noticia' ? 'Ej: Nueva normativa laboral 2026' : 'Ej: Nuevo horario de atención'}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '2px solid #ddd',
                      fontSize: 14
                    }}
                  />
                </div>
                
                {/* Contenido - diferente según el tipo */}
                {formAviso.tipo === 'noticia' ? (
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>
                      🔗 URL de la noticia *
                    </label>
                    <input
                      type="url"
                      value={formAviso.contenido}
                      onChange={(e) => setFormAviso({...formAviso, contenido: e.target.value})}
                      placeholder="https://ejemplo.com/noticia-completa"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: '2px solid #ddd',
                        fontSize: 14
                      }}
                    />
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: '#666' }}>
                      Al hacer clic en la noticia, se abrirá este enlace en una nueva pestaña
                    </p>
                  </div>
                ) : (
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>
                      Contenido * (Puedes usar HTML para dar formato)
                    </label>
                    <textarea
                      value={formAviso.contenido}
                      onChange={(e) => setFormAviso({...formAviso, contenido: e.target.value})}
                      placeholder="Escribe el contenido del aviso aquí...&#10;&#10;Puedes usar HTML:&#10;<b>Negrita</b>&#10;<i>Cursiva</i>&#10;<br> para salto de línea&#10;<ul><li>Lista</li></ul>"
                      rows={8}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: '2px solid #ddd',
                        fontSize: 14,
                        resize: 'vertical',
                        fontFamily: 'monospace'
                      }}
                    />
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: '#666' }}>
                      💡 Tip: Usa &lt;b&gt;texto&lt;/b&gt; para negrita, &lt;br&gt; para saltos de línea
                    </p>
                  </div>
                )}
                
                {/* Imagen URL */}
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>
                    URL de imagen (opcional)
                  </label>
                  <input
                    type="url"
                    value={formAviso.imagen}
                    onChange={(e) => setFormAviso({...formAviso, imagen: e.target.value})}
                    placeholder="https://ejemplo.com/imagen.jpg"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '2px solid #ddd',
                      fontSize: 14
                    }}
                  />
                  {formAviso.imagen && (
                    <img 
                      src={formAviso.imagen} 
                      alt="Preview" 
                      style={{ 
                        marginTop: 8, 
                        maxWidth: 200, 
                        maxHeight: 120, 
                        borderRadius: 8,
                        objectFit: 'cover'
                      }}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                </div>
                
                {/* Activo */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={formAviso.activo}
                    onChange={(e) => setFormAviso({...formAviso, activo: e.target.checked})}
                    style={{ width: 18, height: 18 }}
                  />
                  <label style={{ fontSize: 14, color: '#333' }}>
                    Publicar aviso (visible en la intranet)
                  </label>
                </div>
              </div>
              
              {/* Botones */}
              <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setModalAviso(false);
                    setAvisoEditando(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={guardarAviso}
                  style={{
                    padding: '10px 24px',
                    background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
                    border: 'none',
                    borderRadius: 8,
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)'
                  }}
                >
                  {avisoEditando ? '💾 Guardar Cambios' : '✅ Crear Aviso'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ========== TODOS LOS EMPLEADOS (para super_admin) ==========
  const [todosLosEmpleados, setTodosLosEmpleados] = useState([]);
  const [cargandoTodosEmpleados, setCargandoTodosEmpleados] = useState(false);
  
  // Cargar todos los empleados de todas las empresas (solo para super_admin)
  const cargarTodosLosEmpleados = useCallback(async () => {
    if (!esSuperAdmin) {
      setTodosLosEmpleados(empleados);
      return;
    }
    
    setCargandoTodosEmpleados(true);
    try {
      const { data, error } = await supabase
        .from("empleados")
        .select("*")
        .order("nombres", { ascending: true });
      
      if (error) {
        console.error("Error cargando todos los empleados:", error);
        setTodosLosEmpleados(empleados);
      } else {
        // Mapear campos de BD a React
        const empleadosMapeados = (data || []).map(emp => {
          const nombres = emp.nombres || "";
          const apellidos = emp.apellidos || "";
          const nombreCompleto = `${nombres} ${apellidos}`.trim();
          
          return {
            id: emp.id,
            nombre: nombreCompleto,
            nombres: nombres,
            apellidos: apellidos,
            documento: emp.documento || "",
            empresa_id: emp.empresa_id,
            cargo: emp.cargo || "",
            sede: emp.sede || "",
            activo: emp.activo !== false
          };
        });
        setTodosLosEmpleados(empleadosMapeados);
        console.log(`✅ Cargados ${empleadosMapeados.length} empleados de todas las empresas`);
      }
    } catch (err) {
      console.error("Error cargando empleados:", err);
      setTodosLosEmpleados(empleados);
    } finally {
      setCargandoTodosEmpleados(false);
    }
  }, [esSuperAdmin, empleados]);
  
  useEffect(() => {
    cargarTodosLosEmpleados();
  }, [cargarTodosLosEmpleados]);
  
  // Empleados a mostrar en selects: todos si es super_admin, o los filtrados si no
  const empleadosParaSelects = esSuperAdmin ? todosLosEmpleados : empleados;

  // ========== USUARIOS ==========
  const [usuarios, setUsuarios] = useState([]);
  const [cargandoUsuarios, setCargandoUsuarios] = useState(false);
  const [errorUsuarios, setErrorUsuarios] = useState(null);

  const cargarUsuarios = useCallback(async () => {
    setCargandoUsuarios(true);
    setErrorUsuarios(null);
    
    let query = supabase
      .from("usuarios")
      .select("*")
      .order("created_at", { ascending: false });
    
    // Filtrar por empresa si no es super_admin o si hay un filtro específico
    if (!esSuperAdmin && filtroEmpresa) {
      query = query.eq("empresa_id", filtroEmpresa);
    } else if (esSuperAdmin && filtroEmpresa) {
      // Super admin con empresa seleccionada: filtrar por esa empresa
      query = query.eq("empresa_id", filtroEmpresa);
    }
    // Si es super_admin sin filtro, ver todos los usuarios
    
    const { data, error } = await query;
    if (error) {
      console.error("Error cargando usuarios:", error);
      setErrorUsuarios(error.message || "No se pudo cargar usuarios");
    } else {
      setUsuarios(data || []);
    }
    setCargandoUsuarios(false);
  }, [esSuperAdmin, filtroEmpresa]);

  useEffect(() => {
    cargarUsuarios();
  }, [cargarUsuarios]);

  const [nuevoUsuario, setNuevoUsuario] = useState({ nombre: "", email: "", usuario: "", clave: "", perfil: "", activo: true, empresa_id: "", rol_sistema: "admin_empresa" });
  const [editandoUsuario, setEditandoUsuario] = useState(null);
  
  // Estados para transferencias
  const [mostrarTransferirUsuario, setMostrarTransferirUsuario] = useState(null);
  const [nuevaEmpresaUsuario, setNuevaEmpresaUsuario] = useState("");
  const [mostrarTransferirEmpleado, setMostrarTransferirEmpleado] = useState(false);
  const [empleadoATransferir, setEmpleadoATransferir] = useState("");
  const [nuevaEmpresaEmpleado, setNuevaEmpresaEmpleado] = useState("");

  const crearUsuario = async () => {
    if (!nuevoUsuario.nombre || !nuevoUsuario.usuario || !nuevoUsuario.clave) {
      alert("Completa nombre, usuario y clave");
      return;
    }
    
    // Determinar la empresa: si es super_admin puede elegir, si no usa filtroEmpresa
    const empresaDelUsuario = esSuperAdmin 
      ? (nuevoUsuario.empresa_id || filtroEmpresa) 
      : filtroEmpresa;
    
    if (!empresaDelUsuario) {
      alert("Debes seleccionar una empresa para el usuario");
      return;
    }
    
    setCargandoUsuarios(true);
    try {
      const timestamp = new Date().toISOString();
      // Determinar rol del sistema: solo super_admin puede crear otros super_admin
      const rolSistema = (esSuperAdmin && nuevoUsuario.rol_sistema === 'super_admin') ? 'super_admin' : 'admin_empresa';
      const empresaFinal = rolSistema === 'super_admin' ? null : empresaDelUsuario;
      
      const payload = {
        id: uid(),
        nombre: nuevoUsuario.nombre,
        usuario: nuevoUsuario.usuario,
        clave: nuevoUsuario.clave,
        perfil: nuevoUsuario.perfil || null,
        activo: nuevoUsuario.activo ?? true,
        empresa_id: empresaFinal,
        rol_sistema: rolSistema,
        created_at: timestamp,
        updated_at: timestamp
      };
      const { data, error } = await supabase
        .from("usuarios")
        .insert([payload])
        .select()
        .single();
      if (error) {
        throw error;
      }
      if (data) {
        setUsuarios(prev => [data, ...prev.filter(u => u.id !== data.id)]);
      }
      setNuevoUsuario({ nombre: "", email: "", usuario: "", clave: "", perfil: "", activo: true, empresa_id: "", rol_sistema: "admin_empresa" });
      alert(`Usuario ${rolSistema === 'super_admin' ? 'SUPER ADMINISTRADOR' : ''} creado exitosamente`);
    } catch (error) {
      console.error("Error creando usuario:", error);
      alert(`Error creando el usuario: ${error.message || error}`);
    } finally {
      setCargandoUsuarios(false);
    }
  };

  // Función para transferir usuario a otra empresa
  const transferirUsuario = async () => {
    if (!mostrarTransferirUsuario || !nuevaEmpresaUsuario) {
      alert("Selecciona una empresa destino");
      return;
    }
    setCargandoUsuarios(true);
    try {
      const timestamp = new Date().toISOString();
      const { data, error } = await supabase
        .from("usuarios")
        .update({
          empresa_id: nuevaEmpresaUsuario,
          updated_at: timestamp
        })
        .eq("id", mostrarTransferirUsuario.id)
        .select()
        .single();
      if (error) throw error;
      if (data) {
        setUsuarios(prev => prev.map(u => (u.id === data.id ? data : u)));
      }
      const empresaDestino = empresas.find(e => e.id === nuevaEmpresaUsuario);
      alert(`Usuario "${mostrarTransferirUsuario.nombre}" transferido a ${empresaDestino?.nombre || 'nueva empresa'} exitosamente`);
      setMostrarTransferirUsuario(null);
      setNuevaEmpresaUsuario("");
    } catch (error) {
      console.error("Error transfiriendo usuario:", error);
      alert(`Error al transferir usuario: ${error.message || error}`);
    } finally {
      setCargandoUsuarios(false);
    }
  };

  // Función para transferir empleado a otra empresa
  const transferirEmpleado = async () => {
    if (!empleadoATransferir || !nuevaEmpresaEmpleado) {
      alert("Selecciona un empleado y una empresa destino");
      return;
    }
    setCargandoUsuarios(true);
    try {
      const { data, error } = await supabase
        .from("empleados")
        .update({
          empresa_id: nuevaEmpresaEmpleado
        })
        .eq("id", empleadoATransferir)
        .select()
        .single();
      if (error) throw error;
      
      const empleadoTransferido = empleadosParaSelects.find(e => e.id === empleadoATransferir);
      const empresaDestino = empresas.find(e => e.id === nuevaEmpresaEmpleado);
      
      // Actualizar la lista local de todos los empleados
      setTodosLosEmpleados(prev => prev.map(e => e.id === empleadoATransferir ? { ...e, empresa_id: nuevaEmpresaEmpleado } : e));
      // También actualizar la lista de empleados del padre si está filtrada
      setEmpleados(prev => prev.map(e => e.id === empleadoATransferir ? { ...e, empresa_id: nuevaEmpresaEmpleado } : e));
      
      alert(`Empleado "${empleadoTransferido?.nombre || 'Empleado'}" transferido a ${empresaDestino?.nombre || 'nueva empresa'} exitosamente`);
      setEmpleadoATransferir("");
      setNuevaEmpresaEmpleado("");
      setMostrarTransferirEmpleado(false);
      
      // Recargar la lista de empleados para reflejar los cambios
      cargarTodosLosEmpleados();
    } catch (error) {
      console.error("Error transfiriendo empleado:", error);
      alert(`Error al transferir empleado: ${error.message || error}`);
    } finally {
      setCargandoUsuarios(false);
    }
  };

  // Función para cambiar rol de usuario a super_admin o admin_empresa
  const cambiarRolSistema = async (usuarioId, nuevoRol) => {
    if (!window.confirm(`¿Estás seguro de cambiar el rol a ${nuevoRol === 'super_admin' ? 'SUPER ADMINISTRADOR' : 'Administrador de Empresa'}?`)) return;
    setCargandoUsuarios(true);
    try {
      const timestamp = new Date().toISOString();
      const updateData = {
        rol_sistema: nuevoRol,
        updated_at: timestamp
      };
      // Si se convierte en super_admin, quitar empresa asignada
      if (nuevoRol === 'super_admin') {
        updateData.empresa_id = null;
      }
      const { data, error } = await supabase
        .from("usuarios")
        .update(updateData)
        .eq("id", usuarioId)
        .select()
        .single();
      if (error) throw error;
      if (data) {
        setUsuarios(prev => prev.map(u => (u.id === data.id ? data : u)));
      }
      alert(`Rol actualizado a ${nuevoRol === 'super_admin' ? 'SUPER ADMINISTRADOR' : 'Administrador de Empresa'}`);
    } catch (error) {
      console.error("Error cambiando rol:", error);
      alert(`Error al cambiar rol: ${error.message || error}`);
    } finally {
      setCargandoUsuarios(false);
    }
  };

  const actualizarUsuario = async () => {
    if (!editandoUsuario.nombre || !editandoUsuario.usuario) {
      alert("Completa nombre y usuario");
      return;
    }
    setCargandoUsuarios(true);
    try {
      const timestamp = new Date().toISOString();
      const { data, error } = await supabase
        .from("usuarios")
        .update({
          nombre: editandoUsuario.nombre,
          usuario: editandoUsuario.usuario,
          clave: editandoUsuario.clave,
          perfil: editandoUsuario.perfil || null,
          activo: editandoUsuario.activo ?? true,
          updated_at: timestamp
        })
        .eq("id", editandoUsuario.id)
        .select()
        .single();
      if (error) {
        throw error;
      }
      if (data) {
        setUsuarios(prev => prev.map(u => (u.id === data.id ? data : u)));
      }
      setEditandoUsuario(null);
      alert("Usuario actualizado exitosamente");
    } catch (error) {
      console.error("Error actualizando usuario:", error);
      alert(`Error actualizando el usuario: ${error.message || error}`);
    } finally {
      setCargandoUsuarios(false);
    }
  };

  const eliminarUsuario = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este usuario?")) return;
    setCargandoUsuarios(true);
    try {
      const { error } = await supabase
        .from("usuarios")
        .delete()
        .eq("id", id);
      if (error) {
        throw error;
      }
      setUsuarios(prev => prev.filter(u => u.id !== id));
      alert("Usuario eliminado");
    } catch (error) {
      console.error("Error eliminando usuario:", error);
      alert(`Error eliminando el usuario: ${error.message || error}`);
    } finally {
      setCargandoUsuarios(false);
    }
  };

  const restaurarClave = async (id) => {
    const usuarioIndex = usuarios.findIndex(u => u.id === id);
    if (usuarioIndex === -1) return;
    
    const nuevaClave = "Temp" + Math.random().toString(36).substr(2, 6).toUpperCase();
    setCargandoUsuarios(true);
    try {
      const timestamp = new Date().toISOString();
      const { data, error } = await supabase
        .from("usuarios")
        .update({ clave: nuevaClave, updated_at: timestamp })
        .eq("id", id)
        .select()
        .single();
      if (error) {
        throw error;
      }
      if (data) {
        setUsuarios(prev => prev.map(u => (u.id === data.id ? data : u)));
      }
      alert(`Nueva clave temporal: ${nuevaClave}`);
    } catch (error) {
      console.error("Error restaurando clave:", error);
      alert(`Error restaurando la clave: ${error.message || error}`);
    } finally {
      setCargandoUsuarios(false);
    }
  };

  // ========== PERFILES ==========
  const [perfiles, setPerfiles] = useState(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_PERFILES_KEY);
      return raw ? JSON.parse(raw) : [
        { id: "admin", nombre: "Administrador", permisos: ["ver_todo", "crear", "editar", "eliminar", "configurar"] },
        { id: "rh", nombre: "Recursos Humanos", permisos: ["ver_empleados", "crear_empleados", "editar_empleados", "nómina", "ausencias"] },
        { id: "contador", nombre: "Contador", permisos: ["ver_nómina", "ver_liquidaciones", "reportes"] }
      ];
    } catch (e) {
      return [];
    }
  });
  const [cargandoPerfiles, setCargandoPerfiles] = useState(false);
  const [errorPerfiles, setErrorPerfiles] = useState(null);

  const normalizarPerfil = (perfil = {}) => ({
    id: perfil.id,
    nombre: perfil.nombre,
    permisos: Array.isArray(perfil.permisos) ? perfil.permisos : [],
    created_at: perfil.created_at,
    updated_at: perfil.updated_at
  });

  const cargarPerfiles = useCallback(async () => {
    setCargandoPerfiles(true);
    setErrorPerfiles(null);
    try {
      const { data, error } = await supabase
        .from("perfiles")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      const normalizados = (data || []).map(normalizarPerfil);
      setPerfiles(normalizados);
      try {
        window.localStorage.setItem(STORAGE_PERFILES_KEY, JSON.stringify(normalizados));
      } catch (err) {
        console.warn("No se pudo guardar perfiles en localStorage", err);
      }
    } catch (err) {
      console.error("Error cargando perfiles:", err);
      setErrorPerfiles(err.message || "No se pudo cargar perfiles");
    } finally {
      setCargandoPerfiles(false);
    }
  }, []);

  useEffect(() => {
    cargarPerfiles();
  }, [cargarPerfiles]);

  const [nuevoPerfil, setNuevoPerfil] = useState({ nombre: "", permisos: [] });
  const [editandoPerfil, setEditandoPerfil] = useState(null);

  const permisosDisponibles = [
    // === NIVEL 1: AMBIENTES PRINCIPALES ===
    { id: "ambiente_rh", label: "RH (Recursos Humanos)", grupo: "Ambientes", nivel: 1 },
    { id: "ambiente_admin", label: "ADMIN (Administrativo)", grupo: "Ambientes", nivel: 1 },
    { id: "ambiente_configuraciones", label: "CONFIGURACIONES", grupo: "Ambientes", nivel: 1 },
    
    // === NIVEL 2: RH - MÓDULOS ===
    { id: "rh_empleados", label: "Empleados", grupo: "RH", parent: "ambiente_rh", nivel: 2 },
    { id: "rh_sedes", label: "Sedes", grupo: "RH", parent: "ambiente_rh", nivel: 2 },
    { id: "rh_horarios", label: "Horarios", grupo: "RH", parent: "ambiente_rh", nivel: 2 },
    { id: "rh_memorandos", label: "Procesos Disciplinarios", grupo: "RH", parent: "ambiente_rh", nivel: 2 },
    { id: "rh_ausencias", label: "Ausencias", grupo: "RH", parent: "ambiente_rh", nivel: 2 },
    { id: "rh_vacaciones", label: "Vacaciones", grupo: "RH", parent: "ambiente_rh", nivel: 2 },
    { id: "rh_prestamos", label: "Préstamos", grupo: "RH", parent: "ambiente_rh", nivel: 2 },
    { id: "rh_nomina", label: "Nómina", grupo: "RH", parent: "ambiente_rh", nivel: 2 },
    { id: "rh_liquidaciones", label: "Liquidaciones", grupo: "RH", parent: "ambiente_rh", nivel: 2 },
    { id: "rh_certificaciones", label: "Certificaciones", grupo: "RH", parent: "ambiente_rh", nivel: 2 },
    { id: "rh_contratos", label: "Contratos", grupo: "RH", parent: "ambiente_rh", nivel: 2 },
    { id: "rh_actualizacion_datos", label: "Actualización de Datos", grupo: "RH", parent: "ambiente_rh", nivel: 2 },
    
    // === NIVEL 3: RH EMPLEADOS - OPCIONES ===
    { id: "rh_empleados_crear", label: "├─ Crear Empleado", grupo: "RH", parent: "rh_empleados", nivel: 3 },
    { id: "rh_empleados_ver", label: "├─ Ver Empleados", grupo: "RH", parent: "rh_empleados", nivel: 3 },
    { id: "rh_empleados_editar", label: "├─ Editar Empleado", grupo: "RH", parent: "rh_empleados", nivel: 3 },
    { id: "rh_empleados_eliminar", label: "└─ Eliminar Empleado", grupo: "RH", parent: "rh_empleados", nivel: 3 },
    
    // === NIVEL 3: RH ACTUALIZACIÓN DE DATOS - OPCIONES ===
    { id: "rh_actualizacion_datos_ver", label: "├─ 📋 Ver Solicitudes", grupo: "RH", parent: "rh_actualizacion_datos", nivel: 3 },
    { id: "rh_actualizacion_datos_aprobar", label: "└─ ✅ Aprobar/Rechazar", grupo: "RH", parent: "rh_actualizacion_datos", nivel: 3 },
    
    // === NIVEL 3: RH SEDES - OPCIONES ===
    { id: "rh_sedes_crear", label: "├─ Crear Sede", grupo: "RH", parent: "rh_sedes", nivel: 3 },
    { id: "rh_sedes_ver", label: "├─ Ver Sedes", grupo: "RH", parent: "rh_sedes", nivel: 3 },
    { id: "rh_sedes_editar", label: "├─ Editar Sede", grupo: "RH", parent: "rh_sedes", nivel: 3 },
    { id: "rh_sedes_eliminar", label: "└─ Eliminar Sede", grupo: "RH", parent: "rh_sedes", nivel: 3 },
    
    // === NIVEL 3: RH HORARIOS - OPCIONES (SUB-BOTONES) ===
    { id: "rh_horarios_ver", label: "├─ Ver Horarios", grupo: "RH", parent: "rh_horarios", nivel: 3 },
    { id: "rh_horarios_guardar", label: "├─ 💾 Guardar horario", grupo: "RH", parent: "rh_horarios", nivel: 3 },
    { id: "rh_horarios_cierre", label: "├─ 🔒 Cierre de horario", grupo: "RH", parent: "rh_horarios", nivel: 3 },
    { id: "rh_horarios_copiar", label: "├─ 📋 Copiar horarios", grupo: "RH", parent: "rh_horarios", nivel: 3 },
    { id: "rh_horarios_imprimir", label: "├─ 🖨️ Imprimir", grupo: "RH", parent: "rh_horarios", nivel: 3 },
    { id: "rh_horarios_elegir_empleados", label: "├─ 👥 Elegir empleados", grupo: "RH", parent: "rh_horarios", nivel: 3 },
    { id: "rh_horarios_subir_marcaciones", label: "├─ 📤 Subir marcaciones CSV", grupo: "RH", parent: "rh_horarios", nivel: 3 },
    { id: "rh_horarios_aprobar_marcaciones", label: "├─ ✅ Aprobar marcaciones", grupo: "RH", parent: "rh_horarios", nivel: 3 },
    { id: "rh_horarios_ver_marcaciones_reales", label: "├─ 📊 Ver horas reales", grupo: "RH", parent: "rh_horarios", nivel: 3 },
    { id: "rh_horarios_modificar_cerrado", label: "└─ 🔓 Modificar horario cerrado", grupo: "RH", parent: "rh_horarios", nivel: 3 },
    
    // === NIVEL 3: RH PROCESOS DISCIPLINARIOS - OPCIONES (SUB-BOTONES) ===
    { id: "rh_memorandos_crear", label: "├─ 💾 Guardar proceso", grupo: "RH", parent: "rh_memorandos", nivel: 3 },
    { id: "rh_memorandos_ver", label: "├─ 📑 Ver Procesos", grupo: "RH", parent: "rh_memorandos", nivel: 3 },
    { id: "rh_memorandos_editar", label: "├─ ✏️ Editar proceso", grupo: "RH", parent: "rh_memorandos", nivel: 3 },
    { id: "rh_memorandos_adjuntar", label: "├─ 📎 Adjuntar archivos", grupo: "RH", parent: "rh_memorandos", nivel: 3 },
    { id: "rh_memorandos_completar", label: "├─ ✅ Marcar completado", grupo: "RH", parent: "rh_memorandos", nivel: 3 },
    { id: "rh_memorandos_reabrir", label: "├─ 🔓 Reabrir proceso", grupo: "RH", parent: "rh_memorandos", nivel: 3 },
    { id: "rh_memorandos_eliminar", label: "└─ 🗑️ Eliminar proceso", grupo: "RH", parent: "rh_memorandos", nivel: 3 },
    
    // === NIVEL 3: RH AUSENCIAS - OPCIONES (SUB-BOTONES) ===
    { id: "rh_ausencias_guardar", label: "├─ 💾 Guardar ausencia", grupo: "RH", parent: "rh_ausencias", nivel: 3 },
    { id: "rh_ausencias_editar", label: "├─ ✏️ Editar ausencia", grupo: "RH", parent: "rh_ausencias", nivel: 3 },
    { id: "rh_ausencias_consultar", label: "├─ 📋 Consultar ausencias", grupo: "RH", parent: "rh_ausencias", nivel: 3 },
    { id: "rh_ausencias_eliminar", label: "└─ 🗑️ Eliminar ausencia", grupo: "RH", parent: "rh_ausencias", nivel: 3 },
    
    // === NIVEL 3: RH VACACIONES - OPCIONES (SUB-BOTONES) ===
    { id: "rh_vacaciones_ver", label: "├─ 📋 Ver vacaciones", grupo: "RH", parent: "rh_vacaciones", nivel: 3 },
    { id: "rh_vacaciones_imprimir_individual", label: "├─ 🖨️ Imprimir individual", grupo: "RH", parent: "rh_vacaciones", nivel: 3 },
    { id: "rh_vacaciones_imprimir_todos", label: "└─ 📄 Imprimir todos", grupo: "RH", parent: "rh_vacaciones", nivel: 3 },
    
    // === NIVEL 3: RH PRÉSTAMOS - OPCIONES (SUB-BOTONES) ===
    { id: "rh_prestamos_registrar", label: "├─ 📝 Registrar préstamo", grupo: "RH", parent: "rh_prestamos", nivel: 3 },
    { id: "rh_prestamos_consultar", label: "├─ 📋 Consultar / gestionar", grupo: "RH", parent: "rh_prestamos", nivel: 3 },
    { id: "rh_prestamos_abonar", label: "├─ 💵 Registrar abono", grupo: "RH", parent: "rh_prestamos", nivel: 3 },
    { id: "rh_prestamos_imprimir", label: "├─ 🖨️ Imprimir reporte", grupo: "RH", parent: "rh_prestamos", nivel: 3 },
    { id: "rh_prestamos_recibo", label: "├─ 🧾 Imprimir recibo", grupo: "RH", parent: "rh_prestamos", nivel: 3 },
    { id: "rh_prestamos_abono_credito", label: "├─ 💰 Abono a crédito", grupo: "RH", parent: "rh_prestamos", nivel: 3 },
    { id: "rh_prestamos_eliminar", label: "└─ 🗑️ Eliminar préstamo", grupo: "RH", parent: "rh_prestamos", nivel: 3 },
    
    // === NIVEL 3: RH NÓMINA - OPCIONES (SUB-BOTONES) ===
    { id: "rh_nomina_guardar_borrador", label: "├─ 📝 Guardar borrador", grupo: "RH", parent: "rh_nomina", nivel: 3 },
    { id: "rh_nomina_descartar_borrador", label: "├─ 🗑️ Descartar borrador", grupo: "RH", parent: "rh_nomina", nivel: 3 },
    { id: "rh_nomina_guardar", label: "├─ 💾 Guardar nómina", grupo: "RH", parent: "rh_nomina", nivel: 3 },
    { id: "rh_nomina_repositorio", label: "├─ 📂 Ver repositorio", grupo: "RH", parent: "rh_nomina", nivel: 3 },
    { id: "rh_nomina_reabrir", label: "├─ 🔓 Reabrir nómina", grupo: "RH", parent: "rh_nomina", nivel: 3 },
    { id: "rh_nomina_desprendible", label: "├─ 🧾 Desprendible", grupo: "RH", parent: "rh_nomina", nivel: 3 },
    { id: "rh_nomina_subir_csv", label: "├─ 📄 Subir horas (CSV)", grupo: "RH", parent: "rh_nomina", nivel: 3 },
    { id: "rh_nomina_comidas", label: "├─ 🍽️ Comandas", grupo: "RH", parent: "rh_nomina", nivel: 3 },
    { id: "rh_nomina_horas_extras", label: "├─ ⏱️ Horas extras", grupo: "RH", parent: "rh_nomina", nivel: 3 },
    { id: "rh_nomina_tablero", label: "└─ 📊 Ver Tablero", grupo: "RH", parent: "rh_nomina", nivel: 3 },
    
    // === NIVEL 3: RH LIQUIDACIONES - OPCIONES (SUB-BOTONES) ===
    { id: "rh_liquidaciones_calcular", label: "├─ 📊 Calcular / Previsualizar", grupo: "RH", parent: "rh_liquidaciones", nivel: 3 },
    { id: "rh_liquidaciones_guardar", label: "├─ 💾 Guardar liquidación", grupo: "RH", parent: "rh_liquidaciones", nivel: 3 },
    { id: "rh_liquidaciones_imprimir_historico", label: "├─ 🖨️ Imprimir histórico", grupo: "RH", parent: "rh_liquidaciones", nivel: 3 },
    { id: "rh_liquidaciones_imprimir_individual", label: "├─ 📄 Imprimir individual", grupo: "RH", parent: "rh_liquidaciones", nivel: 3 },
    { id: "rh_liquidaciones_consultar", label: "└─ 📚 Ver histórico", grupo: "RH", parent: "rh_liquidaciones", nivel: 3 },
    
    // === NIVEL 3: RH CERTIFICACIONES - OPCIONES (SUB-BOTONES) ===
    { id: "rh_certificaciones_generar", label: "├─ 📝 Generar Certificación", grupo: "RH", parent: "rh_certificaciones", nivel: 3 },
    { id: "rh_certificaciones_guardar", label: "├─ 💾 Guardar Certificación", grupo: "RH", parent: "rh_certificaciones", nivel: 3 },
    { id: "rh_certificaciones_imprimir", label: "├─ 🖨️ Imprimir Certificación", grupo: "RH", parent: "rh_certificaciones", nivel: 3 },
    { id: "rh_certificaciones_consultar", label: "├─ 📋 Ver histórico", grupo: "RH", parent: "rh_certificaciones", nivel: 3 },
    { id: "rh_certificaciones_eliminar", label: "└─ 🗑️ Eliminar Certificación", grupo: "RH", parent: "rh_certificaciones", nivel: 3 },
    
    // === NIVEL 3: RH CONTRATOS - OPCIONES (SUB-BOTONES) ===
    { id: "rh_contratos_generar", label: "├─ 📝 Generar Contrato", grupo: "RH", parent: "rh_contratos", nivel: 3 },
    { id: "rh_contratos_imprimir", label: "└─ 🖨️ Imprimir Contrato", grupo: "RH", parent: "rh_contratos", nivel: 3 },
    
    // === NIVEL 2: ADMIN - MÓDULOS ===
    { id: "admin_cuadre_diario", label: "Cuadre Diario", grupo: "ADMIN", parent: "ambiente_admin", nivel: 2 },
    { id: "admin_revisar_cuadre", label: "Revisar Cuadre", grupo: "ADMIN", parent: "ambiente_admin", nivel: 2 },
    { id: "admin_gastos", label: "Gastos", grupo: "ADMIN", parent: "ambiente_admin", nivel: 2 },
    { id: "admin_inventario", label: "Inventario", grupo: "ADMIN", parent: "ambiente_admin", nivel: 2 },
    { id: "admin_procesos_pago", label: "Procesos de Pago", grupo: "ADMIN", parent: "ambiente_admin", nivel: 2 },
    { id: "admin_calendario", label: "Calendario", grupo: "ADMIN", parent: "ambiente_admin", nivel: 2 },
    { id: "admin_directorio", label: "Directorio", grupo: "ADMIN", parent: "ambiente_admin", nivel: 2 },
    { id: "admin_planes_trabajo", label: "Planes de Trabajo", grupo: "ADMIN", parent: "ambiente_admin", nivel: 2 },
    { id: "admin_arqueos_caja", label: "Arqueos de Caja", grupo: "ADMIN", parent: "ambiente_admin", nivel: 2 },
    
    // === NIVEL 3: ADMIN CUADRE DIARIO - OPCIONES (SUB-BOTONES) ===
    { id: "admin_cuadre_abrir", label: "├─ 📂 Abrir cuadre", grupo: "ADMIN", parent: "admin_cuadre_diario", nivel: 3 },
    { id: "admin_cuadre_turnos", label: "├─ 👥 Gestionar turnos", grupo: "ADMIN", parent: "admin_cuadre_diario", nivel: 3 },
    { id: "admin_cuadre_adelantos", label: "├─ 💵 Gestionar adelantos", grupo: "ADMIN", parent: "admin_cuadre_diario", nivel: 3 },
    { id: "admin_cuadre_denominaciones", label: "├─ 💰 Cuadre denominaciones", grupo: "ADMIN", parent: "admin_cuadre_diario", nivel: 3 },
    { id: "admin_cuadre_guardar", label: "├─ 💾 Guardar cuadre (cerrar)", grupo: "ADMIN", parent: "admin_cuadre_diario", nivel: 3 },
    { id: "admin_cuadre_imprimir", label: "├─ 🖨️ Imprimir cuadre", grupo: "ADMIN", parent: "admin_cuadre_diario", nivel: 3 },
    { id: "admin_cuadre_consultar", label: "├─ 📋 Consultar cuadres", grupo: "ADMIN", parent: "admin_cuadre_diario", nivel: 3 },
    { id: "admin_cuadre_reabrir", label: "├─ 🔓 Reabrir cuadres cerrados", grupo: "ADMIN", parent: "admin_cuadre_diario", nivel: 3 },
    { id: "admin_cuadre_retomar_cualquiera", label: "├─ 🔄 Retomar cualquier cuadre abierto (cualquier sede)", grupo: "ADMIN", parent: "admin_cuadre_diario", nivel: 3 },
    { id: "admin_cuadre_facturas_eliminar_gestion", label: "├─ 🗑️ Eliminar facturas en gestión", grupo: "ADMIN", parent: "admin_cuadre_diario", nivel: 3 },
    { id: "admin_cuadre_modificar_efectivo", label: "└─ ✏️ Modificar efectivo confirmado", grupo: "ADMIN", parent: "admin_cuadre_diario", nivel: 3 },
    
    // === NIVEL 3: ADMIN REVISAR CUADRES - OPCIONES (SUB-BOTONES) ===
    { id: "admin_revisar_cuadre_ver", label: "├─ 👁️ Ver revisiones", grupo: "ADMIN", parent: "admin_revisar_cuadre", nivel: 3 },
    { id: "admin_revisar_cuadre_ver_historial", label: "├─ 📋 Ver historial completo", grupo: "ADMIN", parent: "admin_revisar_cuadre", nivel: 3 },
    { id: "admin_revisar_cuadre_revisar", label: "├─ ✅ Revisar secciones", grupo: "ADMIN", parent: "admin_revisar_cuadre", nivel: 3 },
    { id: "admin_revisar_cuadre_asignar", label: "├─ 👤 Asignar secciones", grupo: "ADMIN", parent: "admin_revisar_cuadre", nivel: 3 },
    { id: "admin_revisar_cuadre_cerrar", label: "├─ 🔒 Cerrar revisión (Gerencia)", grupo: "ADMIN", parent: "admin_revisar_cuadre", nivel: 3 },
    { id: "admin_revisar_cuadre_reabrir", label: "├─ 🔓 Reabrir revisión (Gerencia)", grupo: "ADMIN", parent: "admin_revisar_cuadre", nivel: 3 },
    { id: "admin_revisar_cuadre_ver_sobrantes", label: "├─ 💰 Ver sobrantes", grupo: "ADMIN", parent: "admin_revisar_cuadre", nivel: 3 },
    { id: "admin_revisar_cuadre_asignar_revision", label: "└─ 📋 Asignar revisiones a empleados", grupo: "ADMIN", parent: "admin_revisar_cuadre", nivel: 3 },
    
    // === NIVEL 3: ADMIN GASTOS - OPCIONES (SUB-BOTONES) ===
    { id: "admin_gastos_consultar", label: "├─ 📊 Consultar gastos", grupo: "ADMIN", parent: "admin_gastos", nivel: 3 },
    { id: "admin_gastos_manual", label: "├─ ➕ Registrar gastos manuales", grupo: "ADMIN", parent: "admin_gastos", nivel: 3 },
    { id: "admin_gastos_resumen", label: "├─ 📈 Generar resumen ejecutivo", grupo: "ADMIN", parent: "admin_gastos", nivel: 3 },
    { id: "admin_gastos_guardar_resumen", label: "├─ 💾 Guardar resúmenes", grupo: "ADMIN", parent: "admin_gastos", nivel: 3 },
    { id: "admin_gastos_recurrentes", label: "└─ 🔄 Gastos Recurrentes", grupo: "ADMIN", parent: "admin_gastos", nivel: 3 },
    
    // === NIVEL 4: ADMIN GASTOS RECURRENTES - SUB-OPCIONES ===
    { id: "admin_gastos_recurrentes_vencimiento", label: "    ├─ 📅 Registrar vencimiento/valor", grupo: "ADMIN", parent: "admin_gastos_recurrentes", nivel: 4 },
    { id: "admin_gastos_recurrentes_pago", label: "    ├─ 💳 Registrar fecha pago/adjuntos", grupo: "ADMIN", parent: "admin_gastos_recurrentes", nivel: 4 },
    { id: "admin_gastos_recurrentes_confirmar", label: "    └─ ✓ Confirmar pago y enviar a manuales", grupo: "ADMIN", parent: "admin_gastos_recurrentes", nivel: 4 },
    
    // === NIVEL 3: ADMIN INVENTARIO - OPCIONES (SUB-BOTONES) ===
    { id: "admin_inventario_diario", label: "├─ 📋 Inventario Diario", grupo: "ADMIN", parent: "admin_inventario", nivel: 3 },
    { id: "admin_inventario_crear", label: "    ├─ 📝 Crear Inventario", grupo: "ADMIN", parent: "admin_inventario_diario", nivel: 4 },
    { id: "admin_inventario_guardar", label: "    ├─ 💾 Guardar inventario", grupo: "ADMIN", parent: "admin_inventario_diario", nivel: 4 },
    { id: "admin_inventario_cerrar", label: "    ├─ 🔒 Cerrar inventario", grupo: "ADMIN", parent: "admin_inventario_diario", nivel: 4 },
    { id: "admin_inventario_imprimir", label: "    ├─ 🖨️ Imprimir inventario", grupo: "ADMIN", parent: "admin_inventario_diario", nivel: 4 },
    { id: "admin_inventario_eliminar", label: "    ├─ 🗑️ Eliminar inventario", grupo: "ADMIN", parent: "admin_inventario_diario", nivel: 4 },
    { id: "admin_inventario_consultar", label: "    └─ 📋 Consultar inventarios", grupo: "ADMIN", parent: "admin_inventario_diario", nivel: 4 },
    
    { id: "admin_inventario_integral", label: "└─ 📊 Inventario Integral", grupo: "ADMIN", parent: "admin_inventario", nivel: 3 },
    { id: "admin_inventario_integral_crear", label: "    ├─ 📝 Crear Inv. Integral", grupo: "ADMIN", parent: "admin_inventario_integral", nivel: 4 },
    { id: "admin_inventario_integral_ver", label: "    ├─ 👁️ Ver Mis Inventarios", grupo: "ADMIN", parent: "admin_inventario_integral", nivel: 4 },
    { id: "admin_inventario_integral_ver_todos", label: "    ├─ 📋 Ver Todos (Admin)", grupo: "ADMIN", parent: "admin_inventario_integral", nivel: 4 },
    { id: "admin_inventario_integral_gestionar", label: "    ├─ ✅ Gestionar Inventarios", grupo: "ADMIN", parent: "admin_inventario_integral", nivel: 4 },
    { id: "admin_inventario_integral_editar", label: "    └─ ✏️ Editar Inventarios", grupo: "ADMIN", parent: "admin_inventario_integral", nivel: 4 },
    
    // === NIVEL 3: ADMIN PROCESOS DE PAGO - OPCIONES (SUB-BOTONES) ===
    { id: "admin_procesos_crear", label: "├─ 📝 Crear tarea", grupo: "ADMIN", parent: "admin_procesos_pago", nivel: 3 },
    { id: "admin_procesos_guardar", label: "├─ 💾 Guardar tarea", grupo: "ADMIN", parent: "admin_procesos_pago", nivel: 3 },
    { id: "admin_procesos_ver", label: "├─ 📋 Ver tareas", grupo: "ADMIN", parent: "admin_procesos_pago", nivel: 3 },
    { id: "admin_procesos_editar", label: "├─ ✏️ Editar tarea", grupo: "ADMIN", parent: "admin_procesos_pago", nivel: 3 },
    { id: "admin_procesos_adjuntar", label: "├─ 📎 Adjuntar soportes", grupo: "ADMIN", parent: "admin_procesos_pago", nivel: 3 },
    { id: "admin_procesos_eliminar", label: "└─ 🗑️ Eliminar tarea", grupo: "ADMIN", parent: "admin_procesos_pago", nivel: 3 },
    
    // === NIVEL 3: ADMIN CALENDARIO - OPCIONES (SUB-BOTONES) ===
    { id: "admin_calendario_crear", label: "├─ 📝 Crear evento", grupo: "ADMIN", parent: "admin_calendario", nivel: 3 },
    { id: "admin_calendario_editar", label: "├─ ✏️ Editar evento", grupo: "ADMIN", parent: "admin_calendario", nivel: 3 },
    { id: "admin_calendario_eliminar", label: "├─ 🗑️ Eliminar evento", grupo: "ADMIN", parent: "admin_calendario", nivel: 3 },
    { id: "admin_calendario_ver", label: "└─ 📋 Ver calendario", grupo: "ADMIN", parent: "admin_calendario", nivel: 3 },
    
    // === NIVEL 3: ADMIN DIRECTORIO - OPCIONES (SUB-BOTONES) ===
    { id: "admin_directorio_proveedores", label: "├─ 📇 Proveedores", grupo: "ADMIN", parent: "admin_directorio", nivel: 3 },
    { id: "admin_directorio_crear", label: "├─ 📝 Crear contacto", grupo: "ADMIN", parent: "admin_directorio", nivel: 3 },
    { id: "admin_directorio_editar", label: "├─ ✏️ Editar contacto", grupo: "ADMIN", parent: "admin_directorio", nivel: 3 },
    { id: "admin_directorio_eliminar", label: "├─ 🗑️ Eliminar contacto", grupo: "ADMIN", parent: "admin_directorio", nivel: 3 },
    { id: "admin_directorio_sedes", label: "└─ 🏢 Info Sedes", grupo: "ADMIN", parent: "admin_directorio", nivel: 3 },
    
    // === NIVEL 3: ADMIN PLANES DE TRABAJO - OPCIONES (SUB-BOTONES) ===
    { id: "admin_planes_crear", label: "├─ 📝 Crear plan", grupo: "ADMIN", parent: "admin_planes_trabajo", nivel: 3 },
    { id: "admin_planes_editar", label: "├─ ✏️ Editar plan", grupo: "ADMIN", parent: "admin_planes_trabajo", nivel: 3 },
    { id: "admin_planes_eliminar", label: "├─ 🗑️ Eliminar plan", grupo: "ADMIN", parent: "admin_planes_trabajo", nivel: 3 },
    { id: "admin_planes_diligenciar", label: "├─ ✍️ Diligenciar plan", grupo: "ADMIN", parent: "admin_planes_trabajo", nivel: 3 },
    { id: "admin_planes_revisar", label: "├─ 👁️ Revisar y cerrar", grupo: "ADMIN", parent: "admin_planes_trabajo", nivel: 3 },
    { id: "admin_planes_bitacora", label: "├─ 📚 Ver bitácora", grupo: "ADMIN", parent: "admin_planes_trabajo", nivel: 3 },
    { id: "admin_planes_ver", label: "└─ 📋 Ver planes", grupo: "ADMIN", parent: "admin_planes_trabajo", nivel: 3 },
    
    // === NIVEL 3: ADMIN ARQUEOS DE CAJA - OPCIONES (SUB-BOTONES) ===
    { id: "admin_arqueos_crear", label: "├─ 📝 Crear arqueo", grupo: "ADMIN", parent: "admin_arqueos_caja", nivel: 3 },
    { id: "admin_arqueos_editar", label: "├─ ✏️ Editar arqueo", grupo: "ADMIN", parent: "admin_arqueos_caja", nivel: 3 },
    { id: "admin_arqueos_eliminar", label: "├─ 🗑️ Eliminar arqueo", grupo: "ADMIN", parent: "admin_arqueos_caja", nivel: 3 },
    { id: "admin_arqueos_ver", label: "└─ 📋 Ver arqueos", grupo: "ADMIN", parent: "admin_arqueos_caja", nivel: 3 },
    
    // === NIVEL 2: ADMIN - INFORME RENDIMIENTO Y COSTOS ===
    { id: "admin_informe_rendimiento", label: "Informe Rendimiento", grupo: "ADMIN", parent: "ambiente_admin", nivel: 2 },
    
    // === NIVEL 3: ADMIN INFORME RENDIMIENTO - OPCIONES (SUB-BOTONES) ===
    { id: "admin_informe_generar", label: "├─ 📊 Generar informe", grupo: "ADMIN", parent: "admin_informe_rendimiento", nivel: 3 },
    { id: "admin_informe_imprimir", label: "├─ 🖨️ Imprimir informe", grupo: "ADMIN", parent: "admin_informe_rendimiento", nivel: 3 },
    { id: "admin_informe_exportar", label: "├─ 📥 Exportar Excel", grupo: "ADMIN", parent: "admin_informe_rendimiento", nivel: 3 },
    { id: "admin_informe_graficas", label: "├─ 📈 Ver gráficas", grupo: "ADMIN", parent: "admin_informe_rendimiento", nivel: 3 },
    { id: "admin_informe_detalle", label: "└─ 👥 Ver detalle empleados", grupo: "ADMIN", parent: "admin_informe_rendimiento", nivel: 3 },
    
    // === NIVEL 2: ADMIN - SECRETARÍA DE SALUD ===
    { id: "admin_secretaria_salud", label: "Secretaría de Salud", grupo: "ADMIN", parent: "ambiente_admin", nivel: 2 },
    
    // === NIVEL 3: ADMIN SECRETARÍA DE SALUD - OPCIONES (SUB-BOTONES) ===
    { id: "admin_secretaria_salud_ver", label: "├─ 👁️ Ver módulo", grupo: "ADMIN", parent: "admin_secretaria_salud", nivel: 3 },
    { id: "admin_secretaria_salud_config", label: "├─ ⚙️ Configurar sedes", grupo: "ADMIN", parent: "admin_secretaria_salud", nivel: 3 },
    { id: "admin_secretaria_salud_archivos", label: "├─ 📁 Gestionar archivos", grupo: "ADMIN", parent: "admin_secretaria_salud", nivel: 3 },
    { id: "admin_secretaria_salud_trampa", label: "├─ 🔧 Trampa de grasa", grupo: "ADMIN", parent: "admin_secretaria_salud", nivel: 3 },
    { id: "admin_secretaria_salud_planillas", label: "└─ 📋 Ver planillas", grupo: "ADMIN", parent: "admin_secretaria_salud", nivel: 3 },
    
    // === NIVEL 2: RH - MÓDULO INTRANET ===
    { id: "rh_intranet_solicitudes", label: "Solicitudes Intranet", grupo: "RH", parent: "ambiente_rh", nivel: 2 },
    
    // === NIVEL 3: RH INTRANET SOLICITUDES - OPCIONES ===
    { id: "rh_intranet_solicitudes_ver", label: "├─ 📋 Ver solicitudes", grupo: "RH", parent: "rh_intranet_solicitudes", nivel: 3 },
    { id: "rh_intranet_solicitudes_aprobar", label: "├─ ✅ Aprobar solicitud", grupo: "RH", parent: "rh_intranet_solicitudes", nivel: 3 },
    { id: "rh_intranet_solicitudes_rechazar", label: "├─ ❌ Rechazar solicitud", grupo: "RH", parent: "rh_intranet_solicitudes", nivel: 3 },
    { id: "rh_intranet_solicitudes_responder", label: "└─ 💬 Responder solicitud", grupo: "RH", parent: "rh_intranet_solicitudes", nivel: 3 },
    
    // === NIVEL 2: CONFIGURACIONES - MÓDULOS ===
    { id: "config_usuarios", label: "Usuarios", grupo: "CONFIGURACIONES", parent: "ambiente_configuraciones", nivel: 2 },
    { id: "config_perfiles", label: "Perfiles y Permisos", grupo: "CONFIGURACIONES", parent: "ambiente_configuraciones", nivel: 2 },
    { id: "config_valores_anuales", label: "Valores Anuales", grupo: "CONFIGURACIONES", parent: "ambiente_configuraciones", nivel: 2 },
    { id: "config_avisos", label: "Avisos Intranet", grupo: "CONFIGURACIONES", parent: "ambiente_configuraciones", nivel: 2 },
    { id: "config_reglamento", label: "Reglamento Interno", grupo: "CONFIGURACIONES", parent: "ambiente_configuraciones", nivel: 2 },
    
    // === NIVEL 3: CONFIG USUARIOS - OPCIONES ===
    { id: "config_usuarios_crear", label: "├─ Crear Usuario", grupo: "CONFIGURACIONES", parent: "config_usuarios", nivel: 3 },
    { id: "config_usuarios_ver", label: "├─ Ver Usuarios", grupo: "CONFIGURACIONES", parent: "config_usuarios", nivel: 3 },
    { id: "config_usuarios_editar", label: "├─ Editar Usuario", grupo: "CONFIGURACIONES", parent: "config_usuarios", nivel: 3 },
    { id: "config_usuarios_eliminar", label: "└─ Eliminar Usuario", grupo: "CONFIGURACIONES", parent: "config_usuarios", nivel: 3 },
    
    // === NIVEL 3: CONFIG PERFILES - OPCIONES ===
    { id: "config_perfiles_crear", label: "├─ Crear Perfil", grupo: "CONFIGURACIONES", parent: "config_perfiles", nivel: 3 },
    { id: "config_perfiles_ver", label: "├─ Ver Perfiles", grupo: "CONFIGURACIONES", parent: "config_perfiles", nivel: 3 },
    { id: "config_perfiles_editar", label: "├─ Editar Perfil", grupo: "CONFIGURACIONES", parent: "config_perfiles", nivel: 3 },
    { id: "config_perfiles_eliminar", label: "└─ Eliminar Perfil", grupo: "CONFIGURACIONES", parent: "config_perfiles", nivel: 3 },
    
    // === NIVEL 3: CONFIG VALORES ANUALES - OPCIONES ===
    { id: "config_valores_ver", label: "├─ Ver Valores", grupo: "CONFIGURACIONES", parent: "config_valores_anuales", nivel: 3 },
    { id: "config_valores_actualizar", label: "└─ Actualizar Valores", grupo: "CONFIGURACIONES", parent: "config_valores_anuales", nivel: 3 },
    
    // === NIVEL 3: CONFIG AVISOS INTRANET - OPCIONES ===
    { id: "config_avisos_crear", label: "├─ Crear Aviso", grupo: "CONFIGURACIONES", parent: "config_avisos", nivel: 3 },
    { id: "config_avisos_editar", label: "├─ Editar Aviso", grupo: "CONFIGURACIONES", parent: "config_avisos", nivel: 3 },
    { id: "config_avisos_eliminar", label: "└─ Eliminar Aviso", grupo: "CONFIGURACIONES", parent: "config_avisos", nivel: 3 }
  ];

  const crearPerfil = async () => {
    if (!nuevoPerfil.nombre) {
      alert("Ingresa un nombre de perfil");
      return;
    }
    setCargandoPerfiles(true);
    setErrorPerfiles(null);
    try {
      const timestamp = new Date().toISOString();
      const payload = {
        id: nuevoPerfil.nombre.toLowerCase().replace(/ /g, "_"),
        nombre: nuevoPerfil.nombre,
        permisos: nuevoPerfil.permisos || [],
        created_at: timestamp,
        updated_at: timestamp
      };
      const { data, error } = await supabase
        .from("perfiles")
        .insert([payload])
        .select()
        .single();
      if (error) throw error;
      if (data) {
        const normalizado = normalizarPerfil(data);
        const nuevos = [normalizado, ...perfiles.filter(p => p.id !== normalizado.id)];
        setPerfiles(nuevos);
        try {
          window.localStorage.setItem(STORAGE_PERFILES_KEY, JSON.stringify(nuevos));
        } catch (err) {
          console.warn("No se pudo guardar perfiles en localStorage", err);
        }
      }
      setNuevoPerfil({ nombre: "", permisos: [] });
      alert("Perfil creado exitosamente");
    } catch (err) {
      console.error("Error creando perfil:", err);
      alert(`Error creando el perfil: ${err.message || err}`);
    } finally {
      setCargandoPerfiles(false);
    }
  };

  const actualizarPerfil = async () => {
    if (!editandoPerfil?.nombre) {
      alert("Ingresa un nombre de perfil");
      return;
    }
    setCargandoPerfiles(true);
    setErrorPerfiles(null);
    try {
      const timestamp = new Date().toISOString();
      const { data, error } = await supabase
        .from("perfiles")
        .update({
          nombre: editandoPerfil.nombre,
          permisos: editandoPerfil.permisos || [],
          updated_at: timestamp
        })
        .eq("id", editandoPerfil.id)
        .select()
        .single();
      if (error) throw error;
      if (data) {
        const normalizado = normalizarPerfil(data);
        const nuevos = perfiles.map(p => p.id === normalizado.id ? normalizado : p);
        setPerfiles(nuevos);
        try {
          window.localStorage.setItem(STORAGE_PERFILES_KEY, JSON.stringify(nuevos));
        } catch (err) {
          console.warn("No se pudo guardar perfiles en localStorage", err);
        }
      }
      setEditandoPerfil(null);
      alert("Perfil actualizado exitosamente");
    } catch (err) {
      console.error("Error actualizando perfil:", err);
      alert(`Error actualizando el perfil: ${err.message || err}`);
    } finally {
      setCargandoPerfiles(false);
    }
  };

  const eliminarPerfil = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este perfil?")) return;
    setCargandoPerfiles(true);
    setErrorPerfiles(null);
    try {
      const { error } = await supabase
        .from("perfiles")
        .delete()
        .eq("id", id);
      if (error) throw error;
      const nuevos = perfiles.filter(p => p.id !== id);
      setPerfiles(nuevos);
      try {
        window.localStorage.setItem(STORAGE_PERFILES_KEY, JSON.stringify(nuevos));
      } catch (err) {
        console.warn("No se pudo guardar perfiles en localStorage", err);
      }
      alert("Perfil eliminado");
    } catch (err) {
      console.error("Error eliminando perfil:", err);
      alert(`Error eliminando el perfil: ${err.message || err}`);
    } finally {
      setCargandoPerfiles(false);
    }
  };

  // ========== VALORES ANUALES ==========
  // Valores por defecto para cuando no hay datos
  const VALORES_DEFAULT_CONFIG = {
    smlv: 1750905, // SMLV 2025-2026 actualizado
    valorHoraNormal: 6471,
    valorHoraNocturna: 8355,
    valorHoraFestiva: 11323,
    valorHoraExtraFestiva: 13587,
    valorExtraDiurna: 8088,
    valorExtraNocturna: 10422,
    salud: 4.0,
    pension: 4.0,
    auxTransporte: 200000,
    interesCesantias: 12.0
  };

  const mapDbToState = (row = {}) => ({
    id: row.id || "config",
    empresa_id: row.empresa_id || null,
    smlv: Number(row.smlv) || VALORES_DEFAULT_CONFIG.smlv,
    valorHoraNormal: Number(row.valorhoranormal) || VALORES_DEFAULT_CONFIG.valorHoraNormal,
    valorHoraNocturna: Number(row.valorhoranocturna) || VALORES_DEFAULT_CONFIG.valorHoraNocturna,
    valorHoraFestiva: Number(row.valorhorafestiva) || VALORES_DEFAULT_CONFIG.valorHoraFestiva,
    valorHoraExtraFestiva: Number(row.valorhoraextrafestiva) || VALORES_DEFAULT_CONFIG.valorHoraExtraFestiva,
    valorExtraDiurna: Number(row.valorextradiurna) || VALORES_DEFAULT_CONFIG.valorExtraDiurna,
    valorExtraNocturna: Number(row.valorextranocturna) || VALORES_DEFAULT_CONFIG.valorExtraNocturna,
    salud: Number(row.salud) || VALORES_DEFAULT_CONFIG.salud,
    pension: Number(row.pension) || VALORES_DEFAULT_CONFIG.pension,
    auxTransporte: Number(row.auxtransporte) || VALORES_DEFAULT_CONFIG.auxTransporte,
    valorVacaciones: Number(row.valorvacaciones) || 0,
    valorCesantias: Number(row.valorcesantias) || 0,
    interesCesantias: Number(row.interescesantias) || VALORES_DEFAULT_CONFIG.interesCesantias,
    ultimaActualizacion: row.ultimaactualizacion || new Date().toISOString().split('T')[0],
    created_at: row.created_at || null,
    updated_at: row.updated_at || null
  });

  const mapStateToDb = (state, empresaId = null) => {
    const payload = {
      id: state.id || "config",
      smlv: Number(state.smlv) || 0,
      valorhoranormal: Number(state.valorHoraNormal) || 0,
      valorhoranocturna: Number(state.valorHoraNocturna) || 0,
      valorhorafestiva: Number(state.valorHoraFestiva) || 0,
      valorhoraextrafestiva: Number(state.valorHoraExtraFestiva) || 0,
      valorextradiurna: Number(state.valorExtraDiurna) || 0,
      valorextranocturna: Number(state.valorExtraNocturna) || 0,
      salud: Number(state.salud) || 0,
      pension: Number(state.pension) || 0,
      auxtransporte: Number(state.auxTransporte) || 0,
      valorvacaciones: Number(state.valorVacaciones) || 0,
      valorcesantias: Number(state.valorCesantias) || 0,
      interescesantias: Number(state.interesCesantias) || 0,
      ultimaactualizacion: state.ultimaActualizacion || null,
      created_at: state.created_at || null,
      updated_at: state.updated_at || null
    };
    // Agregar empresa_id si está disponible
    if (empresaId) {
      payload.empresa_id = empresaId;
    } else if (state.empresa_id) {
      payload.empresa_id = state.empresa_id;
    }
    return payload;
  };

  // Usar mapDbToState con un objeto vacío para obtener todos los valores por defecto
  const DEFAULT_VALORES = mapDbToState({ id: "config" });

  // Usar los valores del prop global en lugar de estado local
  const valoresAnuales = { ...DEFAULT_VALORES, ...valoresAnualesProp };

  const [cargandoValores, setCargandoValores] = useState(false);
  const [errorValores, setErrorValores] = useState(null);
  const [editandoValores, setEditandoValores] = useState(null);

  // Determinar el empresa_id a usar (de filtroEmpresa o del usuario actual)
  const empresaIdActual = filtroEmpresa || user?.empresa_id || null;

  const cargarValores = useCallback(async () => {
    setCargandoValores(true);
    setErrorValores(null);
    try {
      let query = supabase
        .from("valores_anuales")
        .select("*");
      
      // Si hay empresa filtrada y no es super admin, filtrar por empresa
      if (empresaIdActual && !esSuperAdmin) {
        query = query.eq("empresa_id", empresaIdActual);
      }
      
      const { data, error } = await query
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        const merged = mapDbToState(data);
        setValoresAnualesProp(merged); // Actualizar estado global
        try {
          window.localStorage.setItem(STORAGE_VALORES_KEY, JSON.stringify(merged));
        } catch (err) {
          console.warn("No se pudo guardar valores en localStorage", err);
        }
      } else {
        setValoresAnualesProp(DEFAULT_VALORES);
      }
    } catch (err) {
      console.error("Error cargando valores anuales:", err);
      setErrorValores(err.message || "No se pudo cargar valores anuales");
    } finally {
      setCargandoValores(false);
    }
  }, [setValoresAnualesProp, empresaIdActual, esSuperAdmin]);

  useEffect(() => {
    cargarValores();
  }, [cargarValores]);

  const actualizarValores = (campo, valor) => {
    const num = Number(valor);
    const parsed = Number.isFinite(num) ? num : valor;
    // Actualizar editandoValores (el estado del modal) en lugar de valoresAnuales
    setEditandoValores(prev => prev ? { ...prev, [campo]: parsed } : null);
  };

  const guardarValores = async () => {
    if (!editandoValores) return; // Si no hay valores editados, no hacer nada
    setCargandoValores(true);
    setErrorValores(null);
    const timestamp = new Date().toISOString();
    
    // Usar un ID fijo "config" para que siempre actualice el mismo registro
    const idConfig = "config";
    
    const valoresActualizados = {
      ...editandoValores,
      id: idConfig,
      ultimaActualizacion: new Date().toISOString().split('T')[0],
      updated_at: timestamp,
      created_at: editandoValores.created_at || timestamp
    };
    try {
      const payload = mapStateToDb(valoresActualizados, empresaIdActual);
      payload.id = idConfig; // Forzar ID fijo
      payload.updated_at = timestamp;
      payload.created_at = payload.created_at || timestamp;
      
      // Agregar empresa_id solo si está disponible
      if (empresaIdActual) {
        payload.empresa_id = empresaIdActual;
      }
      
      console.log("📤 Guardando valores anuales con payload:", JSON.stringify(payload, null, 2));
      
      // Primero intentar UPDATE
      const { data: updateData, error: updateError } = await supabase
        .from("valores_anuales")
        .update(payload)
        .eq("id", idConfig)
        .select()
        .maybeSingle();
      
      let data = updateData;
      let error = updateError;
      
      // Si UPDATE no encontró registro, hacer INSERT
      if (!updateError && !updateData) {
        console.log("📝 No existe registro, creando nuevo...");
        const insertResult = await supabase
          .from("valores_anuales")
          .insert([payload])
          .select()
          .single();
        data = insertResult.data;
        error = insertResult.error;
      }
      
      if (error) {
        console.error("❌ Error en operación de BD:", error);
        throw error;
      }
      if (data) {
        const merged = mapDbToState(data);
        setValoresAnualesProp(merged); // Actualizar estado global
        try {
          window.localStorage.setItem(STORAGE_VALORES_KEY, JSON.stringify(merged));
        } catch (err) {
          console.warn("No se pudo guardar valores en localStorage", err);
        }
        
        // ========== SINCRONIZAR SMLV CON SALARIO BASE DE EMPLEADOS ==========
        const nuevoSMLV = merged.smlv;
        if (nuevoSMLV && nuevoSMLV > 0) {
          console.log(`💰 Actualizando salarioBase de empleados con nuevo SMLV: $${nuevoSMLV.toLocaleString('es-CO')}`);
          
          // Valores SMLV históricos que deben actualizarse (todos los SMLV de años anteriores)
          const valoresAntiguosSMLV = [1750905, 1423500, 1300000, 1160000, 1000000, 908526, 877803, 828116, 781242, 737717, valoresAnualesProp?.smlv];
          
          // Actualizar empleados en estado local y base de datos
          const empleadosActualizados = empleados.map(emp => {
            const salarioActual = Number(emp.salarioBase) || 0;
            // Actualizar si el salario es un SMLV anterior conocido
            if (valoresAntiguosSMLV.includes(salarioActual) && salarioActual !== nuevoSMLV) {
              return { ...emp, salarioBase: nuevoSMLV };
            }
            return emp;
          });
          
          // Actualizar estado local
          setEmpleados(empleadosActualizados);
          
          // Actualizar en Supabase
          let actualizados = 0;
          for (const emp of empleadosActualizados) {
            const empOriginal = empleados.find(e => e.id === emp.id);
            if (empOriginal && empOriginal.salarioBase !== emp.salarioBase) {
              try {
                await supabase
                  .from("empleados")
                  .update({ salariobase: emp.salarioBase })
                  .eq("id", emp.id);
                actualizados++;
                console.log(`✅ Salario actualizado para ${emp.nombre}: $${emp.salarioBase.toLocaleString('es-CO')}`);
              } catch (errEmp) {
                console.error(`❌ Error actualizando ${emp.nombre}:`, errEmp);
              }
            }
          }
          if (actualizados > 0) {
            console.log(`✅ ${actualizados} empleado(s) actualizados con el nuevo SMLV`);
          }
        }
      }
      setEditandoValores(null);
      alert("Valores anuales actualizados exitosamente. Los salarios base de empleados con SMLV anterior han sido actualizados.");
    } catch (err) {
      console.error("Error guardando valores anuales:", err);
      alert(`Error guardando valores: ${err.message || err}`);
    } finally {
      setCargandoValores(false);
    }
  };

  // ========== RENDER USUARIOS ==========
  const renderUsuarios = () => (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <h2 style={{ marginBottom: "20px" }}>Gestión de Usuarios</h2>
      {errorUsuarios && (
        <div style={{ marginBottom: "15px", padding: "10px", borderRadius: "6px", backgroundColor: "#fdecea", color: "#b71c1c" }}>
          Error al cargar usuarios: {errorUsuarios}
        </div>
      )}
      {cargandoUsuarios && (
        <div style={{ marginBottom: "15px", color: "#555" }}>Cargando usuarios...</div>
      )}

      {/* Crear Usuario */}
      <div style={{ backgroundColor: "#f5f5f5", padding: "20px", borderRadius: "8px", marginBottom: "20px" }}>
        <h3 style={{ marginBottom: "15px" }}>Crear Nuevo Usuario</h3>
        
        {/* Selector de empresa y rol para super_admin */}
        {esSuperAdmin && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: "15px" }}>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", color: "#333" }}>
                  🏢 Empresa del usuario:
                </label>
                <select
                  value={nuevoUsuario.empresa_id || filtroEmpresa || ""}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, empresa_id: e.target.value })}
                  style={{ padding: "10px", border: "2px solid #ff6b35", borderRadius: "6px", width: "100%", backgroundColor: "#fff8f5" }}
                  disabled={nuevoUsuario.rol_sistema === 'super_admin'}
                >
                  <option value="">-- Selecciona una empresa --</option>
                  {empresas.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nombre}
                    </option>
                  ))}
                </select>
                {nuevoUsuario.rol_sistema === 'super_admin' && (
                  <small style={{ color: "#666", fontSize: "11px" }}>Los Super Administradores no tienen empresa asignada</small>
                )}
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", color: "#333" }}>
                  👑 Rol del Sistema:
                </label>
                <select
                  value={nuevoUsuario.rol_sistema}
                  onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, rol_sistema: e.target.value })}
                  style={{ 
                    padding: "10px", 
                    border: nuevoUsuario.rol_sistema === 'super_admin' ? "2px solid #9c27b0" : "2px solid #2196F3", 
                    borderRadius: "6px", 
                    width: "100%", 
                    backgroundColor: nuevoUsuario.rol_sistema === 'super_admin' ? "#f3e5f5" : "#e3f2fd",
                    fontWeight: "bold"
                  }}
                >
                  <option value="admin_empresa">👤 Administrador de Empresa</option>
                  <option value="super_admin">👑 Super Administrador (Acceso Total)</option>
                </select>
              </div>
            </div>
            {nuevoUsuario.rol_sistema === 'super_admin' && (
              <div style={{ marginBottom: "15px", padding: "10px", backgroundColor: "#f3e5f5", borderRadius: "6px", border: "1px solid #9c27b0" }}>
                <span style={{ color: "#7b1fa2", fontWeight: "bold" }}>⚠️ ATENCIÓN:</span> 
                <span style={{ color: "#7b1fa2" }}> Los Super Administradores tienen acceso completo a TODAS las empresas del sistema.</span>
              </div>
            )}
          </>
        )}
        
        {/* Mostrar empresa actual para usuarios normales */}
        {!esSuperAdmin && filtroEmpresa && (
          <div style={{ marginBottom: "15px", padding: "10px", backgroundColor: "#e3f2fd", borderRadius: "6px" }}>
            <span style={{ fontWeight: "bold" }}>🏢 Empresa: </span>
            {empresas.find(e => e.id === filtroEmpresa)?.nombre || "Tu empresa"}
          </div>
        )}
        
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "10px" }}>
          <select
            value={nuevoUsuario.nombre}
            onChange={(e) => {
              const empleadoSel = empleadosParaSelects.find(emp => emp.nombre === e.target.value);
              if (empleadoSel) {
                setNuevoUsuario({ ...nuevoUsuario, nombre: empleadoSel.nombre, usuario: empleadoSel.documento || "", empresa_id: empleadoSel.empresa_id || nuevoUsuario.empresa_id });
              } else {
                setNuevoUsuario({ ...nuevoUsuario, nombre: e.target.value, usuario: "" });
              }
            }}
            style={{ padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
          >
            <option value="">Selecciona un empleado {cargandoTodosEmpleados ? '(Cargando...)' : `(${empleadosParaSelects.length})`}</option>
            {empleadosParaSelects.map((emp) => {
              const empresaEmp = empresas.find(e => e.id === emp.empresa_id);
              return (
                <option key={emp.id} value={emp.nombre}>
                  {emp.nombre} - {emp.documento} {esSuperAdmin && empresaEmp ? `[${empresaEmp.nombre}]` : ''}
                </option>
              );
            })}
          </select>
          <input
            type="text"
            placeholder="Usuario (Documento - Automático)"
            value={nuevoUsuario.usuario}
            readOnly={true}
            style={{ padding: "8px", border: "1px solid #ccc", borderRadius: "4px", backgroundColor: "#e8e8e8" }}
          />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "10px" }}>
          <input
            type="password"
            placeholder="Clave"
            value={nuevoUsuario.clave}
            onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, clave: e.target.value })}
            style={{ padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
          />
          <select
            value={nuevoUsuario.perfil}
            onChange={(e) => setNuevoUsuario({ ...nuevoUsuario, perfil: e.target.value })}
            style={{ padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
          >
            <option value="">Selecciona un perfil</option>
            {perfiles.map(p => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
          <button
            onClick={crearUsuario}
            style={{ 
              padding: "8px", 
              backgroundColor: nuevoUsuario.rol_sistema === 'super_admin' ? "#9c27b0" : "#4CAF50", 
              color: "white", 
              border: "none", 
              borderRadius: "4px", 
              cursor: "pointer",
              fontWeight: "bold"
            }}
          >
            {nuevoUsuario.rol_sistema === 'super_admin' ? '👑 Crear Super Admin' : '+ Crear Usuario'}
          </button>
        </div>
      </div>
      
      {/* Panel de Transferencias - Solo para super_admin */}
      {esSuperAdmin && (
        <div style={{ backgroundColor: "#e8f5e9", padding: "20px", borderRadius: "8px", marginBottom: "20px", border: "2px solid #4CAF50" }}>
          <h3 style={{ marginBottom: "15px", color: "#2e7d32" }}>🔄 Transferencias entre Empresas</h3>
          
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
            <div style={{ backgroundColor: "white", padding: "15px", borderRadius: "8px", border: "1px solid #c8e6c9" }}>
              <h4 style={{ marginBottom: "10px", color: "#1b5e20" }}>👤 Transferir Empleado</h4>
              <div style={{ marginBottom: "10px" }}>
                <label style={{ display: "block", marginBottom: "5px", fontSize: "12px", color: "#666" }}>Empleado a transferir: {cargandoTodosEmpleados && '(Cargando...)'}</label>
                <select
                  value={empleadoATransferir}
                  onChange={(e) => setEmpleadoATransferir(e.target.value)}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                >
                  <option value="">-- Selecciona un empleado ({empleadosParaSelects.length}) --</option>
                  {empleadosParaSelects.map((emp) => {
                    const empresaEmp = empresas.find(e => e.id === emp.empresa_id);
                    return (
                      <option key={emp.id} value={emp.id}>
                        {emp.nombre} - {emp.documento} {empresaEmp ? `[${empresaEmp.nombre}]` : '[Sin empresa]'}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div style={{ marginBottom: "10px" }}>
                <label style={{ display: "block", marginBottom: "5px", fontSize: "12px", color: "#666" }}>Empresa destino:</label>
                <select
                  value={nuevaEmpresaEmpleado}
                  onChange={(e) => setNuevaEmpresaEmpleado(e.target.value)}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                >
                  <option value="">-- Selecciona empresa destino --</option>
                  {empresas.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={transferirEmpleado}
                disabled={!empleadoATransferir || !nuevaEmpresaEmpleado}
                style={{ 
                  width: "100%", 
                  padding: "10px", 
                  backgroundColor: empleadoATransferir && nuevaEmpresaEmpleado ? "#4CAF50" : "#ccc", 
                  color: "white", 
                  border: "none", 
                  borderRadius: "4px", 
                  cursor: empleadoATransferir && nuevaEmpresaEmpleado ? "pointer" : "not-allowed",
                  fontWeight: "bold"
                }}
              >
                🔄 Transferir Empleado
              </button>
            </div>
            
            {/* Información */}
            <div style={{ backgroundColor: "#fff3e0", padding: "15px", borderRadius: "8px", border: "1px solid #ffe0b2" }}>
              <h4 style={{ marginBottom: "10px", color: "#e65100" }}>ℹ️ Información</h4>
              <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "13px", color: "#bf360c" }}>
                <li style={{ marginBottom: "8px" }}>Para transferir un <b>usuario del sistema</b>, usa el botón "Transferir" en la tabla de usuarios.</li>
                <li style={{ marginBottom: "8px" }}>Para convertir un usuario en <b>Super Admin</b>, usa el botón "👑" en la tabla.</li>
                <li style={{ marginBottom: "8px" }}>Los <b>Super Administradores</b> tienen acceso a todas las empresas.</li>
                <li>Los <b>empleados transferidos</b> mantendrán sus datos pero aparecerán en la nueva empresa.</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Usuarios */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px" }}>
        <thead>
          <tr style={{ backgroundColor: "#f0f0f0" }}>
            <th style={{ padding: "10px", border: "1px solid #ddd", textAlign: "left" }}>Nombre</th>
            <th style={{ padding: "10px", border: "1px solid #ddd", textAlign: "left" }}>Usuario</th>
            {esSuperAdmin && <th style={{ padding: "10px", border: "1px solid #ddd", textAlign: "left" }}>Empresa</th>}
            {esSuperAdmin && <th style={{ padding: "10px", border: "1px solid #ddd", textAlign: "left" }}>Rol Sistema</th>}
            <th style={{ padding: "10px", border: "1px solid #ddd", textAlign: "left" }}>Perfil</th>
            <th style={{ padding: "10px", border: "1px solid #ddd", textAlign: "left" }}>Estado</th>
            <th style={{ padding: "10px", border: "1px solid #ddd", textAlign: "left" }}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {usuarios.map(u => (
            <tr key={u.id} style={{ borderBottom: "1px solid #ddd", backgroundColor: u.rol_sistema === 'super_admin' ? '#f3e5f5' : 'transparent' }}>
              <td style={{ padding: "10px" }}>
                {u.rol_sistema === 'super_admin' && <span title="Super Administrador" style={{ marginRight: "5px" }}>👑</span>}
                {u.nombre}
              </td>
              <td style={{ padding: "10px" }}>{u.usuario}</td>
              {esSuperAdmin && (
                <td style={{ padding: "10px", fontSize: "12px" }}>
                  {u.rol_sistema === 'super_admin' 
                    ? <span style={{ color: "#9c27b0", fontWeight: "bold" }}>Todas</span>
                    : (empresas.find(e => e.id === u.empresa_id)?.nombre || <span style={{ color: "#999" }}>Sin empresa</span>)
                  }
                </td>
              )}
              {esSuperAdmin && (
                <td style={{ padding: "10px", fontSize: "11px" }}>
                  <span style={{ 
                    padding: "3px 8px", 
                    borderRadius: "12px", 
                    backgroundColor: u.rol_sistema === 'super_admin' ? '#9c27b0' : '#2196F3',
                    color: 'white',
                    fontWeight: 'bold'
                  }}>
                    {u.rol_sistema === 'super_admin' ? 'SUPER ADMIN' : 'Admin Empresa'}
                  </span>
                </td>
              )}
              <td style={{ padding: "10px" }}>{perfiles.find(p => p.id === u.perfil)?.nombre || "-"}</td>
              <td style={{ padding: "10px", color: u.activo ? "green" : "red" }}>{u.activo ? "Activo" : "Inactivo"}</td>
              <td style={{ padding: "10px" }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "3px" }}>
                  <button
                    onClick={() => setEditandoUsuario(u)}
                    style={{ padding: "5px 8px", backgroundColor: "#2196F3", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}
                    title="Editar usuario"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => restaurarClave(u.id)}
                    style={{ padding: "5px 8px", backgroundColor: "#FF9800", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}
                    title="Restaurar clave"
                  >
                    🔑
                  </button>
                  {esSuperAdmin && u.rol_sistema !== 'super_admin' && (
                    <button
                      onClick={() => {
                        setMostrarTransferirUsuario(u);
                        setNuevaEmpresaUsuario("");
                      }}
                      style={{ padding: "5px 8px", backgroundColor: "#4CAF50", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}
                      title="Transferir a otra empresa"
                    >
                      🔄
                    </button>
                  )}
                  {esSuperAdmin && (
                    <button
                      onClick={() => cambiarRolSistema(u.id, u.rol_sistema === 'super_admin' ? 'admin_empresa' : 'super_admin')}
                      style={{ 
                        padding: "5px 8px", 
                        backgroundColor: u.rol_sistema === 'super_admin' ? '#607d8b' : '#9c27b0', 
                        color: "white", 
                        border: "none", 
                        borderRadius: "4px", 
                        cursor: "pointer", 
                        fontSize: "11px" 
                      }}
                      title={u.rol_sistema === 'super_admin' ? 'Quitar Super Admin' : 'Hacer Super Admin'}
                    >
                      {u.rol_sistema === 'super_admin' ? '👤' : '👑'}
                    </button>
                  )}
                  <button
                    onClick={() => eliminarUsuario(u.id)}
                    style={{ padding: "5px 8px", backgroundColor: "#F44336", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "11px" }}
                    title="Eliminar usuario"
                  >
                    🗑️
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Modal Transferir Usuario */}
      {mostrarTransferirUsuario && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ backgroundColor: "white", padding: "30px", borderRadius: "8px", width: "90%", maxWidth: "450px" }}>
            <h3 style={{ marginBottom: "15px", color: "#2e7d32" }}>🔄 Transferir Usuario</h3>
            <p style={{ marginBottom: "15px", color: "#666" }}>
              Transferir a <strong>{mostrarTransferirUsuario.nombre}</strong> a otra empresa:
            </p>
            <div style={{ marginBottom: "15px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Empresa actual:</label>
              <div style={{ padding: "10px", backgroundColor: "#f5f5f5", borderRadius: "4px", color: "#666" }}>
                {empresas.find(e => e.id === mostrarTransferirUsuario.empresa_id)?.nombre || "Sin empresa asignada"}
              </div>
            </div>
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Nueva empresa:</label>
              <select
                value={nuevaEmpresaUsuario}
                onChange={(e) => setNuevaEmpresaUsuario(e.target.value)}
                style={{ width: "100%", padding: "10px", border: "2px solid #4CAF50", borderRadius: "6px", boxSizing: "border-box" }}
              >
                <option value="">-- Selecciona empresa destino --</option>
                {empresas.filter(e => e.id !== mostrarTransferirUsuario.empresa_id).map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  setMostrarTransferirUsuario(null);
                  setNuevaEmpresaUsuario("");
                }}
                style={{ padding: "10px 20px", backgroundColor: "#999", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
              >
                Cancelar
              </button>
              <button
                onClick={transferirUsuario}
                disabled={!nuevaEmpresaUsuario}
                style={{ 
                  padding: "10px 20px", 
                  backgroundColor: nuevaEmpresaUsuario ? "#4CAF50" : "#ccc", 
                  color: "white", 
                  border: "none", 
                  borderRadius: "4px", 
                  cursor: nuevaEmpresaUsuario ? "pointer" : "not-allowed",
                  fontWeight: "bold"
                }}
              >
                🔄 Transferir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Usuario */}
      {editandoUsuario && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ backgroundColor: "white", padding: "30px", borderRadius: "8px", width: "90%", maxWidth: "500px" }}>
            <h3 style={{ marginBottom: "15px" }}>Editar Usuario</h3>
            <input
              type="text"
              placeholder="Nombre"
              value={editandoUsuario.nombre}
              onChange={(e) => setEditandoUsuario({ ...editandoUsuario, nombre: e.target.value })}
              style={{ width: "100%", padding: "8px", marginBottom: "10px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
            />
            <input
              type="text"
              placeholder="Usuario (login)"
              value={editandoUsuario.usuario}
              onChange={(e) => setEditandoUsuario({ ...editandoUsuario, usuario: e.target.value })}
              style={{ width: "100%", padding: "8px", marginBottom: "10px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
            />
            <select
              value={editandoUsuario.perfil}
              onChange={(e) => setEditandoUsuario({ ...editandoUsuario, perfil: e.target.value })}
              style={{ width: "100%", padding: "8px", marginBottom: "10px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
            >
              <option value="">Selecciona un perfil</option>
              {perfiles.map(p => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
            <label style={{ display: "flex", alignItems: "center", marginBottom: "15px" }}>
              <input
                type="checkbox"
                checked={editandoUsuario.activo}
                onChange={(e) => setEditandoUsuario({ ...editandoUsuario, activo: e.target.checked })}
                style={{ marginRight: "10px" }}
              />
              Activo
            </label>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setEditandoUsuario(null)}
                style={{ padding: "8px 16px", backgroundColor: "#999", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
              >
                Cancelar
              </button>
              <button
                onClick={actualizarUsuario}
                style={{ padding: "8px 16px", backgroundColor: "#4CAF50", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ========== RENDER PERFILES ==========
  // Referencia global para mantener la posición del scroll entre re-renders
  const permisosScrollRef = React.useRef(null);
  const permisosScrollPosRef = React.useRef(0);
  
  const RenderPermisosSelector = ({ permisos, setPermisos }) => {
    const ambientes = [...new Set(permisosDisponibles.filter(p => p.nivel === 1).map(p => p.id))];
    
    // Restaurar scroll después de cada render
    React.useLayoutEffect(() => {
      if (permisosScrollRef.current && permisosScrollPosRef.current > 0) {
        permisosScrollRef.current.scrollTop = permisosScrollPosRef.current;
      }
    });
    
    const handleCheckboxChange = (id, checked) => {
      // Guardar la posición del scroll antes de actualizar
      if (permisosScrollRef.current) {
        permisosScrollPosRef.current = permisosScrollRef.current.scrollTop;
      }
      
      if (checked) {
        setPermisos([...permisos, id]);
      } else {
        setPermisos(permisos.filter(p => p !== id));
      }
    };

    return (
      <div ref={permisosScrollRef} style={{ display: "flex", flexDirection: "column", gap: "15px", backgroundColor: "#fafafa", padding: "15px", borderRadius: "8px", maxHeight: "400px", overflow: "auto" }}>
        {ambientes.map(ambienteId => {
          const ambiente = permisosDisponibles.find(p => p.id === ambienteId);
          const modulosDelAmbiente = permisosDisponibles.filter(p => p.parent === ambienteId && p.nivel === 2);
          
          return (
            <div key={ambienteId} style={{ borderLeft: "4px solid #2196F3", paddingLeft: "15px" }}>
              {/* AMBIENTE */}
              <label style={{ display: "flex", alignItems: "center", fontWeight: "bold", color: "#333", marginBottom: "10px", fontSize: "14px" }}>
                <input
                  type="checkbox"
                  checked={permisos.includes(ambienteId)}
                  onChange={(e) => handleCheckboxChange(ambienteId, e.target.checked)}
                  style={{ marginRight: "8px", width: "16px", height: "16px", cursor: "pointer" }}
                />
                📦 {ambiente.label}
              </label>

              {/* MÓDULOS DEL AMBIENTE */}
              {modulosDelAmbiente.length > 0 && (
                <div style={{ marginLeft: "25px", display: "flex", flexDirection: "column", gap: "10px" }}>
                  {modulosDelAmbiente.map(modulo => {
                    const opcionesDelModulo = permisosDisponibles.filter(p => p.parent === modulo.id && p.nivel === 3);
                    
                    return (
                      <div key={modulo.id}>
                        {/* MÓDULO */}
                        <label style={{ display: "flex", alignItems: "center", fontWeight: "600", color: "#555", marginBottom: "8px", fontSize: "13px" }}>
                          <input
                            type="checkbox"
                            checked={permisos.includes(modulo.id)}
                            onChange={(e) => handleCheckboxChange(modulo.id, e.target.checked)}
                            style={{ marginRight: "8px", width: "14px", height: "14px", cursor: "pointer" }}
                          />
                          {modulo.label}
                        </label>

                        {/* OPCIONES/ACCIONES DEL MÓDULO (NIVEL 3) */}
                        {opcionesDelModulo.length > 0 && (
                          <div style={{ marginLeft: "25px", display: "flex", flexDirection: "column", gap: "6px" }}>
                            {opcionesDelModulo.map(opcion => {
                              // Buscar sub-opciones de nivel 4 para esta opción
                              const subOpcionesNivel4 = permisosDisponibles.filter(p => p.parent === opcion.id && p.nivel === 4);
                              
                              return (
                                <div key={opcion.id}>
                                  <label style={{ display: "flex", alignItems: "center", fontSize: "12px", color: "#666" }}>
                                    <input
                                      type="checkbox"
                                      checked={permisos.includes(opcion.id)}
                                      onChange={(e) => handleCheckboxChange(opcion.id, e.target.checked)}
                                      style={{ marginRight: "8px", width: "13px", height: "13px", cursor: "pointer" }}
                                    />
                                    {opcion.label}
                                  </label>
                                  
                                  {/* SUB-OPCIONES NIVEL 4 (si existen) */}
                                  {subOpcionesNivel4.length > 0 && (
                                    <div style={{ marginLeft: "25px", display: "flex", flexDirection: "column", gap: "4px", marginTop: "4px" }}>
                                      {subOpcionesNivel4.map(subOpcion => (
                                        <label key={subOpcion.id} style={{ display: "flex", alignItems: "center", fontSize: "11px", color: "#888" }}>
                                          <input
                                            type="checkbox"
                                            checked={permisos.includes(subOpcion.id)}
                                            onChange={(e) => handleCheckboxChange(subOpcion.id, e.target.checked)}
                                            style={{ marginRight: "8px", width: "12px", height: "12px", cursor: "pointer" }}
                                          />
                                          {subOpcion.label}
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderPerfiles = () => (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <h2 style={{ marginBottom: "20px" }}>Gestión de Perfiles y Permisos</h2>

      {errorPerfiles && (
        <div style={{ marginBottom: "15px", padding: "10px", borderRadius: "6px", backgroundColor: "#fdecea", color: "#b71c1c" }}>
          Error al cargar perfiles: {errorPerfiles}
        </div>
      )}
      {cargandoPerfiles && (
        <div style={{ marginBottom: "15px", color: "#555" }}>Cargando perfiles...</div>
      )}

      {/* Crear Perfil */}
      <div style={{ backgroundColor: "#f5f5f5", padding: "20px", borderRadius: "8px", marginBottom: "20px" }}>
        <h3 style={{ marginBottom: "15px" }}>Crear Nuevo Perfil</h3>
        <div style={{ marginBottom: "10px" }}>
          <input
            type="text"
            placeholder="Nombre del perfil"
            value={nuevoPerfil.nombre}
            onChange={(e) => setNuevoPerfil({ ...nuevoPerfil, nombre: e.target.value })}
            style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box", marginBottom: "10px" }}
          />
        </div>
        <div style={{ marginBottom: "10px" }}>
          <h4>Seleccionar Permisos (3 niveles):</h4>
          <RenderPermisosSelector permisos={nuevoPerfil.permisos} setPermisos={(perms) => setNuevoPerfil({ ...nuevoPerfil, permisos: perms })} />
        </div>
        <button
          onClick={crearPerfil}
          disabled={cargandoPerfiles}
          style={{ padding: "8px 16px", backgroundColor: cargandoPerfiles ? "#a5d6a7" : "#4CAF50", color: "white", border: "none", borderRadius: "4px", cursor: cargandoPerfiles ? "not-allowed" : "pointer" }}
        >
          + Crear Perfil
        </button>
      </div>

      {/* Lista de Perfiles */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
        {perfiles.map(p => (
          <div key={p.id} style={{ border: "1px solid #ddd", padding: "15px", borderRadius: "8px", backgroundColor: "white" }}>
            <h4>{p.nombre}</h4>
            <p style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>ID: {p.id}</p>
            <div style={{ marginBottom: "10px" }}>
              <strong>Permisos:</strong>
              <ul style={{ margin: "5px 0", paddingLeft: "20px", fontSize: "12px" }}>
                {p.permisos.map(perm => (
                  <li key={perm}>{permisosDisponibles.find(pp => pp.id === perm)?.label || perm}</li>
                ))}
              </ul>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setEditandoPerfil(p)}
                style={{ flex: 1, padding: "8px", backgroundColor: "#2196F3", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}
              >
                Editar
              </button>
              <button
                onClick={() => eliminarPerfil(p.id)}
                style={{ flex: 1, padding: "8px", backgroundColor: "#F44336", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}
              >
                Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Editar Perfil */}
      {editandoPerfil && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ backgroundColor: "white", padding: "30px", borderRadius: "8px", width: "90%", maxWidth: "800px", maxHeight: "90vh", overflow: "auto" }}>
            <h3 style={{ marginBottom: "15px" }}>Editar Perfil</h3>
            <input
              type="text"
              placeholder="Nombre del perfil"
              value={editandoPerfil.nombre}
              onChange={(e) => setEditandoPerfil({ ...editandoPerfil, nombre: e.target.value })}
              style={{ width: "100%", padding: "8px", marginBottom: "15px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
            />
            <h4 style={{ marginBottom: "10px" }}>Seleccionar Permisos (3 niveles):</h4>
            <RenderPermisosSelector permisos={editandoPerfil.permisos} setPermisos={(perms) => setEditandoPerfil({ ...editandoPerfil, permisos: perms })} />
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "15px" }}>
              <button
                onClick={() => setEditandoPerfil(null)}
                style={{ padding: "8px 16px", backgroundColor: "#999", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
              >
                Cancelar
              </button>
              <button
                onClick={actualizarPerfil}
                style={{ padding: "8px 16px", backgroundColor: "#4CAF50", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
              >
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ========== RENDER VALORES ANUALES ==========
  const renderValoresAnuales = () => (
    <div style={{ padding: "20px", maxWidth: "900px", margin: "0 auto" }}>
      <h2 style={{ marginBottom: "20px" }}>Valores Anuales</h2>

      {errorValores && (
        <div style={{ marginBottom: "15px", padding: "10px", borderRadius: "6px", backgroundColor: "#fdecea", color: "#b71c1c" }}>
          Error al cargar valores: {errorValores}
        </div>
      )}
      {cargandoValores && (
        <div style={{ marginBottom: "15px", color: "#555" }}>Cargando valores...</div>
      )}

      {/* Mostrar Valores Actuales */}
      <div style={{ backgroundColor: "#f5f5f5", padding: "20px", borderRadius: "8px", marginBottom: "20px" }}>
        <h3 style={{ marginBottom: "15px" }}>Valores Actualmente Configurados</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "15px" }}>
          <div>
            <strong>SMLV:</strong> ${valoresAnuales.smlv?.toLocaleString('es-CO') || 0}
          </div>
          <div>
            <strong>Valor Hora Normal:</strong> ${valoresAnuales.valorHoraNormal?.toLocaleString('es-CO') || 0}
          </div>
          <div>
            <strong>Valor Hora Nocturna:</strong> ${valoresAnuales.valorHoraNocturna?.toLocaleString('es-CO') || 0}
          </div>
          <div>
            <strong>Valor Hora Festiva:</strong> ${valoresAnuales.valorHoraFestiva?.toLocaleString('es-CO') || 0}
          </div>
          <div>
            <strong>Valor Hora Extra Festiva:</strong> ${valoresAnuales.valorHoraExtraFestiva?.toLocaleString('es-CO') || 0}
          </div>
          <div>
            <strong>Valor Extra Diurna:</strong> ${valoresAnuales.valorExtraDiurna?.toLocaleString('es-CO') || 0}
          </div>
          <div>
            <strong>Valor Extra Nocturna:</strong> ${valoresAnuales.valorExtraNocturna?.toLocaleString('es-CO') || 0}
          </div>
          <div>
            <strong>% Salud:</strong> {valoresAnuales.salud || 0}%
          </div>
          <div>
            <strong>% Pensión:</strong> {valoresAnuales.pension || 0}%
          </div>
          <div>
            <strong>Aux. Transporte:</strong> ${valoresAnuales.auxTransporte?.toLocaleString('es-CO') || 0}
          </div>
          <div>
            <strong>Valor Vacaciones:</strong> ${valoresAnuales.valorVacaciones?.toLocaleString('es-CO') || 0}
          </div>
          <div>
            <strong>Valor Cesantías:</strong> ${valoresAnuales.valorCesantias?.toLocaleString('es-CO') || 0}
          </div>
          <div>
            <strong>% Interés Cesantías:</strong> {valoresAnuales.interesCesantias || 0}%
          </div>
          <div>
            <strong>Última Actualización:</strong> {valoresAnuales.ultimaActualizacion || "-"}
          </div>
        </div>
        <button
          onClick={() => setEditandoValores(valoresAnuales)}
          disabled={cargandoValores}
          style={{ marginTop: "20px", padding: "8px 16px", backgroundColor: cargandoValores ? "#9ecbf3" : "#2196F3", color: "white", border: "none", borderRadius: "4px", cursor: cargandoValores ? "not-allowed" : "pointer" }}
        >
          Actualizar Valores
        </button>
      </div>

      {/* Modal Actualizar Valores */}
      {editandoValores && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ backgroundColor: "white", padding: "30px", borderRadius: "8px", width: "90%", maxWidth: "900px", maxHeight: "90vh", overflow: "auto" }}>
            <h3 style={{ marginBottom: "20px" }}>Actualizar Valores Anuales</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "15px", marginBottom: "20px" }}>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "12px" }}>SMLV:</label>
                <input
                  type="number"
                  value={editandoValores.smlv || 0}
                  onChange={(e) => actualizarValores("smlv", e.target.value)}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "12px" }}>Valor Hora Normal ($):</label>
                <input
                  type="number"
                  value={editandoValores.valorHoraNormal || 0}
                  onChange={(e) => actualizarValores("valorHoraNormal", e.target.value)}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "12px" }}>Valor Hora Nocturna ($):</label>
                <input
                  type="number"
                  value={editandoValores.valorHoraNocturna || 0}
                  onChange={(e) => actualizarValores("valorHoraNocturna", e.target.value)}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "12px" }}>Valor Hora Festiva ($):</label>
                <input
                  type="number"
                  value={editandoValores.valorHoraFestiva || 0}
                  onChange={(e) => actualizarValores("valorHoraFestiva", e.target.value)}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "12px" }}>Valor Hora Extra Festiva ($):</label>
                <input
                  type="number"
                  value={editandoValores.valorHoraExtraFestiva || 0}
                  onChange={(e) => actualizarValores("valorHoraExtraFestiva", e.target.value)}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "12px" }}>Valor Extra Diurna ($):</label>
                <input
                  type="number"
                  value={editandoValores.valorExtraDiurna || 0}
                  onChange={(e) => actualizarValores("valorExtraDiurna", e.target.value)}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "12px" }}>Valor Extra Nocturna ($):</label>
                <input
                  type="number"
                  value={editandoValores.valorExtraNocturna || 0}
                  onChange={(e) => actualizarValores("valorExtraNocturna", e.target.value)}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "12px" }}>% Salud:</label>
                <input
                  type="number"
                  value={editandoValores.salud || 0}
                  onChange={(e) => actualizarValores("salud", e.target.value)}
                  step="0.01"
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "12px" }}>% Pensión:</label>
                <input
                  type="number"
                  value={editandoValores.pension || 0}
                  onChange={(e) => actualizarValores("pension", e.target.value)}
                  step="0.01"
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "12px" }}>Aux. Transporte ($):</label>
                <input
                  type="number"
                  value={editandoValores.auxTransporte || 0}
                  onChange={(e) => actualizarValores("auxTransporte", e.target.value)}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "12px" }}>Valor Vacaciones ($):</label>
                <input
                  type="number"
                  value={editandoValores.valorVacaciones || 0}
                  onChange={(e) => actualizarValores("valorVacaciones", e.target.value)}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "12px" }}>Valor Cesantías ($):</label>
                <input
                  type="number"
                  value={editandoValores.valorCesantias || 0}
                  onChange={(e) => actualizarValores("valorCesantias", e.target.value)}
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold", fontSize: "12px" }}>% Interés Cesantías:</label>
                <input
                  type="number"
                  value={editandoValores.interesCesantias || 0}
                  onChange={(e) => actualizarValores("interesCesantias", e.target.value)}
                  step="0.01"
                  style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", boxSizing: "border-box" }}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setEditandoValores(null)}
                style={{ padding: "8px 16px", backgroundColor: "#999", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
              >
                Cancelar
              </button>
              <button
                onClick={guardarValores}
                  disabled={cargandoValores}
                  style={{ padding: "10px 16px", backgroundColor: cargandoValores ? "#a5d6a7" : "#4CAF50", color: "white", border: "none", borderRadius: "4px", cursor: cargandoValores ? "not-allowed" : "pointer" }}
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ========== RENDER PRINCIPAL ==========
  return (
    <div className="flex flex-col h-screen text-slate-900" style={{ background: 'linear-gradient(180deg, #fff8f5 0%, #fff 100%)' }}>
      {/* Header con gradiente naranja igual que RRHH */}
      <header style={{ background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)', borderBottom: '3px solid #ffc53d' }}>
        <div style={{ padding: '16px 16px 16px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <button
            onClick={() => setAmbiente(null)}
            style={{ 
              background: 'rgba(255,255,255,0.95)', 
              color: '#ff6b35',
              border: 'none',
              borderRadius: 20,
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 107, 53, 0.3)';
            }}
          >
            🏠 Ir al inicio
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-base font-bold text-white flex items-center gap-2">
              <span>⚙️</span> Configuraciones
            </h1>
          </div>
        </div>
        
        {/* Navegación horizontal de botones */}
        <nav style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          flexWrap: 'nowrap', 
          gap: '8px', 
          padding: '0 16px 16px 16px',
          overflowX: 'auto',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
        }}>
          {tienePermiso("config_usuarios") && (
          <button
            onClick={() => setTabConfig("usuarios")}
            style={{
              background: tabConfig === "usuarios"
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)'
                : 'rgba(255,255,255,0.9)',
              border: tabConfig === "usuarios"
                ? '2px solid #ff6b35'
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tabConfig === "usuarios"
                ? '0 4px 12px rgba(255, 107, 53, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (tabConfig !== "usuarios") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tabConfig !== "usuarios") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>👤</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tabConfig === "usuarios" ? '#ffffff' : '#1a1a2e'
            }}>
              Usuarios
            </span>
          </button>
          )}
          {tienePermiso("config_perfiles") && (
          <button
            onClick={() => setTabConfig("perfiles")}
            style={{
              background: tabConfig === "perfiles"
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)'
                : 'rgba(255,255,255,0.9)',
              border: tabConfig === "perfiles"
                ? '2px solid #ff6b35'
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tabConfig === "perfiles"
                ? '0 4px 12px rgba(255, 107, 53, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (tabConfig !== "perfiles") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tabConfig !== "perfiles") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>🔐</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tabConfig === "perfiles" ? '#ffffff' : '#1a1a2e'
            }}>
              Perfiles y Permisos
            </span>
          </button>
          )}
          {tienePermiso("config_valores_anuales") && (
          <button
            onClick={() => setTabConfig("valores")}
            style={{
              background: tabConfig === "valores"
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)'
                : 'rgba(255,255,255,0.9)',
              border: tabConfig === "valores"
                ? '2px solid #ff6b35'
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tabConfig === "valores"
                ? '0 4px 12px rgba(255, 107, 53, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (tabConfig !== "valores") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tabConfig !== "valores") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>📊</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tabConfig === "valores" ? '#ffffff' : '#1a1a2e'
            }}>
              Valores Anuales
            </span>
          </button>
          )}
          {tienePermiso("config_avisos") && (
          <button
            onClick={() => setTabConfig("avisos")}
            style={{
              background: tabConfig === "avisos"
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)'
                : 'rgba(255,255,255,0.9)',
              border: tabConfig === "avisos"
                ? '2px solid #ff6b35'
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tabConfig === "avisos"
                ? '0 4px 12px rgba(255, 107, 53, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (tabConfig !== "avisos") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tabConfig !== "avisos") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>📣</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tabConfig === "avisos" ? '#ffffff' : '#1a1a2e'
            }}>
              Avisos Intranet
            </span>
          </button>
          )}
          {tienePermiso("config_reglamento") && (
          <button
            onClick={() => setTabConfig("reglamento")}
            style={{
              background: tabConfig === "reglamento"
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)'
                : 'rgba(255,255,255,0.9)',
              border: tabConfig === "reglamento"
                ? '2px solid #ff6b35'
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tabConfig === "reglamento"
                ? '0 4px 12px rgba(255, 107, 53, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (tabConfig !== "reglamento") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tabConfig !== "reglamento") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>📖</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tabConfig === "reglamento" ? '#ffffff' : '#1a1a2e'
            }}>
              Reglamento Interno
            </span>
          </button>
          )}
        </nav>
      </header>

      <main style={{ flex: 1, overflow: "auto", padding: "16px" }}>
        {tabConfig === "usuarios" && tienePermiso("config_usuarios") && renderUsuarios()}
        {tabConfig === "perfiles" && tienePermiso("config_perfiles") && renderPerfiles()}
        {tabConfig === "valores" && tienePermiso("config_valores_anuales") && renderValoresAnuales()}
        {tabConfig === "avisos" && tienePermiso("config_avisos") && renderAvisos()}
        {tabConfig === "reglamento" && tienePermiso("config_reglamento") && renderReglamento()}
      </main>
    </div>
  );
}

// ====== COMPONENTE DE FIRMA DIGITAL TÁCTIL ======
function FirmaDigitalCanvas({ 
  onFirmaCompleta, 
  onCancelar, 
  nombreFirmante = "Firmante",
  tipoDocumento = "Documento",
  ancho = 400,
  alto = 200
}) {
  const canvasRef = React.useRef(null);
  const [dibujando, setDibujando] = React.useState(false);
  const [hayFirma, setHayFirma] = React.useState(false);
  const [ultimaPosicion, setUltimaPosicion] = React.useState({ x: 0, y: 0 });

  // Inicializar canvas
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  // Obtener posición del toque/mouse
  const obtenerPosicion = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  // Iniciar dibujo
  const iniciarDibujo = (e) => {
    e.preventDefault();
    const pos = obtenerPosicion(e);
    setUltimaPosicion(pos);
    setDibujando(true);
  };

  // Dibujar
  const dibujar = (e) => {
    if (!dibujando) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = obtenerPosicion(e);
    
    // Detectar presión del Apple Pencil si está disponible
    let presion = 1;
    if (e.touches && e.touches[0] && e.touches[0].force) {
      presion = e.touches[0].force;
    }
    
    ctx.lineWidth = 1.5 + (presion * 3);
    ctx.beginPath();
    ctx.moveTo(ultimaPosicion.x, ultimaPosicion.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    
    setUltimaPosicion(pos);
    setHayFirma(true);
  };

  // Terminar dibujo
  const terminarDibujo = (e) => {
    if (e) e.preventDefault();
    setDibujando(false);
  };

  // Limpiar canvas
  const limpiarFirma = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHayFirma(false);
  };

  // Confirmar firma
  const confirmarFirma = () => {
    if (!hayFirma) {
      alert('Por favor, dibuje su firma antes de confirmar');
      return;
    }
    
    const canvas = canvasRef.current;
    const firmaBase64 = canvas.toDataURL('image/png');
    const fechaFirma = new Date().toLocaleString('es-CO', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    onFirmaCompleta({
      firma: firmaBase64,
      fechaFirma: fechaFirma,
      nombreFirmante: nombreFirmante,
      tipoDocumento: tipoDocumento,
      timestamp: new Date().toISOString()
    });
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        background: 'white',
        borderRadius: 16,
        padding: 24,
        maxWidth: 500,
        width: '95%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>✍️</div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>
            Firma Digital
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>
            {tipoDocumento} - {nombreFirmante}
          </p>
        </div>

        {/* Instrucciones */}
        <div style={{
          background: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: 8,
          padding: 10,
          marginBottom: 16,
          fontSize: 11,
          color: '#0369a1'
        }}>
          📱 Use su dedo, Apple Pencil o stylus para firmar en el área blanca
        </div>

        {/* Canvas de firma */}
        <div style={{
          border: '2px solid #e2e8f0',
          borderRadius: 12,
          overflow: 'hidden',
          marginBottom: 16,
          background: '#fafafa'
        }}>
          <canvas
            ref={canvasRef}
            width={ancho}
            height={alto}
            style={{
              width: '100%',
              height: 'auto',
              touchAction: 'none',
              cursor: 'crosshair',
              background: 'white'
            }}
            onMouseDown={iniciarDibujo}
            onMouseMove={dibujar}
            onMouseUp={terminarDibujo}
            onMouseLeave={terminarDibujo}
            onTouchStart={iniciarDibujo}
            onTouchMove={dibujar}
            onTouchEnd={terminarDibujo}
          />
        </div>

        {/* Línea de firma */}
        <div style={{
          borderTop: '1px solid #1a1a2e',
          marginBottom: 16,
          paddingTop: 4,
          textAlign: 'center'
        }}>
          <span style={{ fontSize: 10, color: '#64748b' }}>Firma del: {nombreFirmante}</span>
        </div>

        {/* Botones */}
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={onCancelar}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '2px solid #e2e8f0',
              borderRadius: 10,
              background: 'white',
              fontSize: 14,
              fontWeight: 600,
              color: '#64748b',
              cursor: 'pointer'
            }}
          >
            ❌ Cancelar
          </button>
          <button
            onClick={limpiarFirma}
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '2px solid #fecaca',
              borderRadius: 10,
              background: '#fef2f2',
              fontSize: 14,
              fontWeight: 600,
              color: '#dc2626',
              cursor: 'pointer'
            }}
          >
            🗑️ Borrar
          </button>
          <button
            onClick={confirmarFirma}
            disabled={!hayFirma}
            style={{
              flex: 1.5,
              padding: '12px 16px',
              border: 'none',
              borderRadius: 10,
              background: hayFirma 
                ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)' 
                : '#e2e8f0',
              fontSize: 14,
              fontWeight: 700,
              color: hayFirma ? 'white' : '#94a3b8',
              cursor: hayFirma ? 'pointer' : 'not-allowed',
              boxShadow: hayFirma ? '0 4px 12px rgba(22, 163, 74, 0.3)' : 'none'
            }}
          >
            ✅ Confirmar Firma
          </button>
        </div>
      </div>
    </div>
  );
}

// ====== MODAL DE SELECCIÓN: FIRMA ESTÁTICA O DIGITAL ======
function ModalTipoFirma({ 
  visible, 
  onSeleccionar, 
  onCerrar,
  tipoDocumento = "Documento"
}) {
  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        background: 'white',
        borderRadius: 20,
        padding: 32,
        maxWidth: 500,
        width: '90%',
        boxShadow: '0 25px 80px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 50, marginBottom: 12 }}>📝</div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>
            ¿Cómo desea firmar el documento?
          </h2>
          <p style={{ margin: '8px 0 0', fontSize: 14, color: '#64748b' }}>
            {tipoDocumento}
          </p>
        </div>

        {/* Opciones */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Opción: Firma Estática (para imprimir) */}
          <button
            onClick={() => onSeleccionar('estatica')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              padding: 20,
              border: '2px solid #e2e8f0',
              borderRadius: 16,
              background: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#3b82f6';
              e.currentTarget.style.background = '#eff6ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.background = 'white';
            }}
          >
            <div style={{
              width: 60,
              height: 60,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 28
            }}>
              🖨️
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>
                Firma Estática (Imprimir)
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Genera el documento para imprimir y firmar manualmente con bolígrafo
              </div>
            </div>
            <div style={{ fontSize: 24, color: '#94a3b8' }}>→</div>
          </button>

          {/* Opción: Firma Digital */}
          <button
            onClick={() => onSeleccionar('digital')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              padding: 20,
              border: '2px solid #e2e8f0',
              borderRadius: 16,
              background: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#16a34a';
              e.currentTarget.style.background = '#f0fdf4';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e2e8f0';
              e.currentTarget.style.background = 'white';
            }}
          >
            <div style={{
              width: 60,
              height: 60,
              borderRadius: 12,
              background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 28
            }}>
              ✍️
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>
                Firma Digital (Táctil)
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Firme directamente en pantalla con dedo, Apple Pencil o stylus. 
                Se guarda con sello "Firmado Digitalmente"
              </div>
            </div>
            <div style={{ fontSize: 24, color: '#94a3b8' }}>→</div>
          </button>
        </div>

        {/* Botón cancelar */}
        <button
          onClick={onCerrar}
          style={{
            width: '100%',
            marginTop: 20,
            padding: '14px',
            border: '2px solid #e2e8f0',
            borderRadius: 12,
            background: 'white',
            fontSize: 14,
            fontWeight: 600,
            color: '#64748b',
            cursor: 'pointer'
          }}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ====== FUNCIÓN PARA GENERAR SELLO DE FIRMA DIGITAL ======
function generarSelloFirmaDigital(firmaData) {
  const { firma, fechaFirma, nombreFirmante } = firmaData;
  
  return `
    <div style="
      margin-top: 30px;
      padding: 20px;
      border: 2px solid #16a34a;
      border-radius: 12px;
      background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
    ">
      <div style="display: flex; align-items: center; gap: 20px;">
        <div style="flex-shrink: 0;">
          <img src="${firma}" alt="Firma Digital" style="
            max-width: 200px;
            max-height: 80px;
            border: 1px solid #bbf7d0;
            border-radius: 8px;
            background: white;
            padding: 5px;
          "/>
        </div>
        <div style="flex: 1;">
          <div style="
            display: inline-block;
            background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
            color: white;
            padding: 6px 14px;
            border-radius: 20px;
            font-size: 11px;
            font-weight: 700;
            margin-bottom: 8px;
          ">
            ✓ FIRMADO DIGITALMENTE
          </div>
          <div style="font-size: 12px; color: #166534; font-weight: 600;">
            ${nombreFirmante}
          </div>
          <div style="font-size: 10px; color: #15803d; margin-top: 4px;">
            📅 Fecha y hora: ${fechaFirma}
          </div>
          <div style="font-size: 9px; color: #22c55e; margin-top: 4px; font-style: italic;">
            Este documento fue firmado electrónicamente
          </div>
        </div>
      </div>
    </div>
  `;
}

// Función para generar sello de FIRMA ELECTRÓNICA (pre-guardada) en GRIS
function generarSelloFirmaElectronica(firmaData, fechaUsoActual = null) {
  const { firma, fechaRegistro, nombreRepresentante } = firmaData;
  const fechaUso = fechaUsoActual || new Date().toLocaleString('es-CO', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
  
  return `
    <div style="
      margin-top: 30px;
      padding: 20px;
      border: 2px solid #6b7280;
      border-radius: 12px;
      background: linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%);
    ">
      <div style="display: flex; align-items: center; gap: 20px;">
        <div style="flex-shrink: 0;">
          <img src="${firma}" alt="Firma Electrónica" style="
            max-width: 200px;
            max-height: 80px;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            background: white;
            padding: 5px;
            filter: grayscale(100%);
          "/>
        </div>
        <div style="flex: 1;">
          <div style="
            display: inline-block;
            background: linear-gradient(135deg, #4b5563 0%, #6b7280 100%);
            color: white;
            padding: 6px 14px;
            border-radius: 20px;
            font-size: 11px;
            font-weight: 700;
            margin-bottom: 8px;
          ">
            ✓ FIRMA ELECTRÓNICA
          </div>
          <div style="font-size: 12px; color: #374151; font-weight: 600;">
            ${nombreRepresentante}
          </div>
          <div style="font-size: 10px; color: #4b5563; margin-top: 4px;">
            📋 Firma registrada: ${fechaRegistro}
          </div>
          <div style="font-size: 10px; color: #6b7280; margin-top: 2px;">
            📅 Aplicada: ${fechaUso}
          </div>
          <div style="font-size: 9px; color: #9ca3af; margin-top: 4px; font-style: italic;">
            Firma electrónica pre-registrada del representante legal
          </div>
        </div>
      </div>
    </div>
  `;
}

// ====== COMPONENTE CONTRATOS DE TRABAJO ======
function ContratosLaborales({ empleados = [], sedes = [], tienePermiso = () => true, valoresAnuales = {}, filtroEmpresa = null }) {
  const [tab, setTab] = useState("generar"); // "generar" | "consultar"
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState("");
  const [sedeSeleccionada, setSedeSeleccionada] = useState("");
  const [contratoGenerado, setContratoGenerado] = useState(null);
  const [contratosGuardados, setContratosGuardados] = useState([]);
  const [cargandoContratos, setCargandoContratos] = useState(false);
  const [guardandoContrato, setGuardandoContrato] = useState(false);
  const [filtroEmpleado, setFiltroEmpleado] = useState("");
  const [esContratoConsultado, setEsContratoConsultado] = useState(false); // Para saber si es un contrato cargado desde Consultar
  
  // Estados para firma digital
  const [mostrarModalTipoFirma, setMostrarModalTipoFirma] = useState(false);
  const [mostrarCanvasFirma, setMostrarCanvasFirma] = useState(false);
  const [firmaTrabajador, setFirmaTrabajador] = useState(null);
  const [firmaEmpleador, setFirmaEmpleador] = useState(null);
  const [quienFirma, setQuienFirma] = useState("trabajador"); // "trabajador" | "empleador"
  
  // Estados para guardar contrato firmado
  const [contratoFirmado, setContratoFirmado] = useState(false);
  const [firmasTempGuardadas, setFirmasTempGuardadas] = useState({ trabajador: null, empleador: null, esElectronica: false });

  const empleado = empleados.find(emp => emp.id === empleadoSeleccionado);
  const sede = sedes.find(s => s.id === sedeSeleccionada);
  
  // Obtener SMLV actual de valoresAnuales
  const smlvActual = valoresAnuales?.smlv || 1750905;
  const smlvEnLetras = formatoSalarioEnLetras(smlvActual);

  // Cargar contratos guardados al iniciar
  useEffect(() => {
    let activo = true;
    const cargarInicial = async () => {
      setCargandoContratos(true);
      const lista = await cargarContratosDeSupabase(filtroEmpresa);
      if (activo) {
        setContratosGuardados(lista);
      }
      if (activo) {
        setCargandoContratos(false);
      }
    };
    cargarInicial();
    return () => {
      activo = false;
    };
  }, [filtroEmpresa]);

  // Formatear fecha de hoy
  const formatearFechaHoy = () => {
    const hoy = new Date();
    const dia = hoy.getDate();
    const meses = ["enero", "febrero", "marzo", "abril", "mayo", "junio", 
                   "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
    const mes = meses[hoy.getMonth()];
    const anio = hoy.getFullYear();
    return `${dia} de ${mes} de ${anio}`;
  };

  // Formatear fecha para mostrar
  const formatearFecha = (fecha) => {
    if (!fecha) return "";
    const partes = fecha.split("-");
    if (partes.length === 3) {
      const [anio, mes, dia] = partes;
      const meses = ["enero", "febrero", "marzo", "abril", "mayo", "junio", 
                     "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
      return `${parseInt(dia)} de ${meses[parseInt(mes) - 1]} de ${anio}`;
    }
    return fecha;
  };

  const generarContrato = () => {
    if (!empleado) {
      alert("Por favor selecciona un empleado");
      return;
    }
    if (!sede) {
      alert("Por favor selecciona una sede");
      return;
    }

    // Determinar género del trabajador y representante
    const generoTrabajador = empleado.genero || "Masculino";
    const generoRepresentante = sede.generoRepresentante || "Masculino";
    
    // Artículos y términos según género del trabajador
    const esEmpleadoMujer = generoTrabajador === "Femenino";
    const elLaTrabajador = esEmpleadoMujer ? "LA" : "EL";
    const delDeLaTrabajador = esEmpleadoMujer ? "DE LA" : "DEL";
    const trabajadorNombre = esEmpleadoMujer ? "TRABAJADORA" : "TRABAJADOR";
    const trabajadorMin = esEmpleadoMujer ? "La trabajadora" : "El trabajador";
    const contratadoA = esEmpleadoMujer ? "contratada" : "contratado";
    
    // Artículos según género del representante legal
    const esRepresentanteMujer = generoRepresentante === "Femenino";
    const representanteTitulo = esRepresentanteMujer ? "Representante Legal" : "Representante Legal";
    
    // DEBUG: Ver qué datos tiene la sede
    console.log("📋 Datos de la sede para contrato:", {
      id: sede.id,
      nombre: sede.nombre,
      representanteLegal: sede.representanteLegal,
      tipoDocRepresentante: sede.tipoDocRepresentante,
      cedulaRepresentante: sede.cedulaRepresentante,
      generoRepresentante: sede.generoRepresentante
    });
    
    const datos = {
      // Datos del empleador (sede)
      nombreEmpleador: sede.razonSocial || sede.nombre || "",
      nitEmpleador: sede.nit || "",
      direccionEmpleador: sede.direccion || "",
      telefonoEmpleador: sede.telefono || "",
      representanteLegal: sede.representanteLegal || "",
      tipoDocRepresentante: sede.tipoDocRepresentante || "Cédula de Ciudadanía",
      cedulaRepresentante: sede.cedulaRepresentante || "",
      generoRepresentante: generoRepresentante,
      // Datos del trabajador
      nombreTrabajador: empleado.nombre || "",
      tipoDocTrabajador: empleado.tipoDoc || "Cédula de Ciudadanía",
      cedulaTrabajador: empleado.documento || "",
      generoTrabajador: generoTrabajador,
      lugarFechaNacimiento: `${empleado.nacionalidad || "Colombia"}, ${formatearFecha(empleado.fechaNacimiento)}`,
      direccionTrabajador: empleado.direccion || "",
      telefonoTrabajador: empleado.telefono || "",
      cargo: empleado.cargo || "",
      tipoSalario: "Salario Mínimo Legal Mensual Vigente",
      remuneracion: `$${smlvActual.toLocaleString('es-CO')}`,
      remuneracionLetras: smlvEnLetras,
      periodoPago: "Quincenal",
      fechaInicio: formatearFecha(empleado.fechaIngreso) || formatearFechaHoy(),
      fechaTerminacion: (empleado.tipoContrato === "Fijo" || empleado.tipoContrato === "Término Fijo") 
        ? (empleado.fechaRetiro ? formatearFecha(empleado.fechaRetiro) : "Según contrato") 
        : "Indefinido",
      lugarTrabajo: sede.direccion || "",
      lugarContratacion: "Pereira, Risaralda",
      ciudad: "Pereira",
      fechaFirma: formatearFechaHoy(),
      // Tipo de contrato
      tipoContrato: empleado.tipoContrato || "Indefinido",
      duracionContrato: empleado.duracionContrato || "",
      // Términos con género
      elLaTrabajador,
      delDeLaTrabajador,
      trabajadorNombre,
      trabajadorMin,
      contratadoA
    };

    setContratoGenerado(datos);
    setEsContratoConsultado(false); // Es un contrato nuevo, no consultado
  };

  // Función para iniciar el proceso de firma (muestra el modal de selección)
  const iniciarProcesoFirma = () => {
    if (!contratoGenerado) {
      alert("Primero genera el contrato");
      return;
    }
    setMostrarModalTipoFirma(true);
  };

  // Manejar selección del tipo de firma
  const manejarSeleccionTipoFirma = (tipo) => {
    setMostrarModalTipoFirma(false);
    if (tipo === 'estatica') {
      // Firma estática: imprimir directamente sin firmas digitales
      imprimirContratoConFirma(null, null, false);
    } else if (tipo === 'digital') {
      // Verificar si la sede tiene firma electrónica pre-registrada
      if (sede && sede.firmaElectronica && sede.firmaElectronica.firma) {
        // La sede tiene firma electrónica, solo pedir firma del trabajador
        setQuienFirma("trabajador");
        setMostrarCanvasFirma(true);
      } else {
        // No hay firma pre-registrada, pedir ambas firmas
        setQuienFirma("trabajador");
        setMostrarCanvasFirma(true);
      }
    }
  };

  // Manejar cuando se completa una firma digital
  const manejarFirmaCompletada = (firmaData) => {
    setMostrarCanvasFirma(false);
    
    if (quienFirma === "trabajador") {
      setFirmaTrabajador(firmaData);
      
      // Verificar si hay firma electrónica pre-registrada del representante
      if (sede && sede.firmaElectronica && sede.firmaElectronica.firma) {
        // Usar la firma electrónica pre-registrada (gris)
        setTimeout(() => {
          imprimirContratoConFirma(firmaData, sede.firmaElectronica, true);
          setFirmaTrabajador(null);
          setFirmaEmpleador(null);
        }, 300);
      } else {
        // No hay firma pre-registrada, pedir firma del empleador
        setQuienFirma("empleador");
        setTimeout(() => setMostrarCanvasFirma(true), 300);
      }
    } else {
      setFirmaEmpleador(firmaData);
      // Ambas firmas completas, generar documento
      setTimeout(() => {
        imprimirContratoConFirma(firmaTrabajador, firmaData, false);
        // Limpiar firmas después de imprimir
        setFirmaTrabajador(null);
        setFirmaEmpleador(null);
      }, 300);
    }
  };

  // Cancelar proceso de firma
  const cancelarFirma = () => {
    setMostrarCanvasFirma(false);
    setMostrarModalTipoFirma(false);
    setFirmaTrabajador(null);
    setFirmaEmpleador(null);
  };

  // Función para imprimir contrato con o sin firmas digitales
  // esElectronica: true si la firma del empleador es electrónica (gris), false si es digital (verde)
  const imprimirContratoConFirma = (firmaTrab, firmaEmpl, esElectronica = false) => {
    const datos = contratoGenerado;
    
    // Si hay firmas, guardarlas temporalmente para el botón "Guardar Contrato Firmado"
    if (firmaTrab && firmaEmpl) {
      setContratoFirmado(true);
      setFirmasTempGuardadas({
        trabajador: firmaTrab,
        empleador: firmaEmpl,
        esElectronica: esElectronica
      });
    }
    
    const win = window.open("", "_blank", "width=900,height=700");
    if (!win) return;

    // Variables de género
    const esEmpleadoMujer = datos.generoTrabajador === "Femenino";
    const esRepresentanteMujer = datos.generoRepresentante === "Femenino";
    const elLaTrabajador = esEmpleadoMujer ? "LA" : "EL";
    const trabajadorNombre = esEmpleadoMujer ? "TRABAJADORA" : "TRABAJADOR";
    const trabajadorMin = esEmpleadoMujer ? "la trabajadora" : "el trabajador";
    const labelNombreTrabajador = esEmpleadoMujer ? "NOMBRE DE LA TRABAJADORA" : "NOMBRE DEL TRABAJADOR";
    const ellaEl = esEmpleadoMujer ? "ella" : "él";

    win.document.write(`
      <html>
        <head>
          <title>${datos.tipoContrato === "Fijo" || datos.tipoContrato === "Término Fijo" 
            ? "Contrato Individual de Trabajo a Término Fijo" 
            : "Contrato Individual de Trabajo a Término Indefinido"} - ${datos.nombreTrabajador}</title>
          <style>
            @page { 
              size: letter; 
              margin: 1.8cm 2cm 1.8cm 2cm; 
            }
            body { 
              font-family: 'Times New Roman', Times, serif; 
              font-size: 11pt; 
              line-height: 1.4;
              color: #000;
              text-align: justify;
              word-wrap: break-word;
              overflow-wrap: break-word;
              hyphens: auto;
            }
            .header-container {
              position: relative;
              margin-bottom: 10px;
              min-height: 80px;
            }
            .logo-header {
              position: absolute;
              top: 0;
              right: 0;
              width: 80px;
              height: 80px;
            }
            .logo-header img {
              width: 100%;
              height: 100%;
              object-fit: contain;
              border-radius: 8px;
            }
            h1 {
              text-align: center;
              font-size: 12pt;
              font-weight: bold;
              margin: 10px 90px 15px 0;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              padding-top: 20px;
            }
            .tabla-datos {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 15px;
              font-size: 9.5pt;
              table-layout: fixed;
            }
            .tabla-datos td {
              border: 1px solid #333;
              padding: 4px 6px;
              vertical-align: top;
              word-wrap: break-word;
              overflow-wrap: break-word;
            }
            .tabla-datos .label {
              font-weight: bold;
              width: 40%;
              background-color: #f5f5f5;
            }
            .tabla-datos .valor {
              width: 60%;
            }
            .intro-text {
              margin: 10px 0;
              font-size: 10.5pt;
            }
            .clausula {
              margin: 8px 0;
              text-align: justify;
              font-size: 10.5pt;
            }
            .clausula-titulo {
              font-weight: bold;
              text-transform: uppercase;
            }
            .paragrafo {
              margin: 6px 0 6px 15px;
              font-style: italic;
            }
            .firma-container {
              margin-top: 80px;
              display: flex;
              justify-content: space-between;
              page-break-inside: avoid;
            }
            .firma-box {
              width: 45%;
              text-align: center;
            }
            .espacio-firma {
              height: 80px;
            }
            .linea-firma {
              border-top: 1px solid #000;
              margin-bottom: 5px;
              width: 100%;
            }
            .nombre-firma {
              font-weight: bold;
              font-size: 10pt;
            }
            .cedula-firma {
              font-size: 9pt;
            }
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="header-container">
            <div class="logo-header">
              <img src="${LOGO_BIGBURGUER}" alt="BigBurguer Logo" />
            </div>
            <h1>CONTRATO INDIVIDUAL DE TRABAJO A TÉRMINO ${datos.tipoContrato === "Fijo" || datos.tipoContrato === "Término Fijo" ? "FIJO" : "INDEFINIDO"}</h1>
          </div>

          <table class="tabla-datos">
            <tr>
              <td class="label">NOMBRE DEL EMPLEADOR</td>
              <td class="valor">${datos.nombreEmpleador}</td>
            </tr>
            <tr>
              <td class="label">NIT</td>
              <td class="valor">${datos.nitEmpleador}</td>
            </tr>
            <tr>
              <td class="label">DIRECCIÓN DEL EMPLEADOR</td>
              <td class="valor">${datos.direccionEmpleador}</td>
            </tr>
            <tr>
              <td class="label">TELÉFONO</td>
              <td class="valor">${datos.telefonoEmpleador}</td>
            </tr>
            <tr>
              <td class="label">REPRESENTANTE LEGAL</td>
              <td class="valor">${datos.representanteLegal}</td>
            </tr>
            <tr>
              <td class="label">${datos.tipoDocRepresentante ? datos.tipoDocRepresentante.toUpperCase() : "CÉDULA DE CIUDADANÍA"}</td>
              <td class="valor">${datos.cedulaRepresentante}</td>
            </tr>
            <tr><td colspan="2" style="height:8px;border:none;"></td></tr>
            <tr>
              <td class="label">${labelNombreTrabajador}</td>
              <td class="valor">${datos.nombreTrabajador}</td>
            </tr>
            <tr>
              <td class="label">${datos.tipoDocTrabajador ? datos.tipoDocTrabajador.toUpperCase() : "CÉDULA DE CIUDADANÍA"}</td>
              <td class="valor">${datos.cedulaTrabajador}</td>
            </tr>
            <tr>
              <td class="label">LUGAR Y FECHA NACIMIENTO</td>
              <td class="valor">${datos.lugarFechaNacimiento}</td>
            </tr>
            <tr>
              <td class="label">DIRECCIÓN</td>
              <td class="valor">${datos.direccionTrabajador}</td>
            </tr>
            <tr>
              <td class="label">TELÉFONO</td>
              <td class="valor">${datos.telefonoTrabajador}</td>
            </tr>
            <tr>
              <td class="label">CARGO</td>
              <td class="valor">${datos.cargo}</td>
            </tr>
            <tr>
              <td class="label">TIPO DE SALARIO</td>
              <td class="valor">${datos.tipoSalario}</td>
            </tr>
            <tr>
              <td class="label">REMUNERACIÓN SALARIAL MENSUAL</td>
              <td class="valor">${datos.remuneracion} (${datos.remuneracionLetras} PESOS M/CTE)</td>
            </tr>
            <tr>
              <td class="label">PERÍODO DE PAGO</td>
              <td class="valor">${datos.periodoPago}</td>
            </tr>
            <tr>
              <td class="label">FECHA INICIACIÓN DE LABORES</td>
              <td class="valor">${datos.fechaInicio}</td>
            </tr>
            <tr>
              <td class="label">FECHA DE TERMINACIÓN DE LABORES</td>
              <td class="valor">${datos.fechaTerminacion}</td>
            </tr>
            <tr>
              <td class="label">LUGAR DE TRABAJO</td>
              <td class="valor">${datos.lugarTrabajo}</td>
            </tr>
            <tr>
              <td class="label">LUGAR DE CONTRATACIÓN</td>
              <td class="valor">${datos.lugarContratacion}</td>
            </tr>
          </table>

          <p class="intro-text">Entre el EMPLEADOR y ${elLaTrabajador} ${trabajadorNombre}, de las condiciones ya dichas, identificados como aparece al pie de sus firmas, se ha celebrado el presente contrato individual de trabajo a término ${datos.tipoContrato === "Fijo" || datos.tipoContrato === "Término Fijo" ? "fijo" : "indefinido"}, regido además por las siguientes <strong>CLÁUSULAS:</strong></p>

          <div class="clausula">
            <span class="clausula-titulo">PRIMERA: OBJETO.</span> EL EMPLEADOR contrata los servicios personales de ${elLaTrabajador} ${trabajadorNombre} en el cargo reseñado y éste se obliga: a) a poner al servicio del EMPLEADOR toda su capacidad normal de trabajo en el desempeño de las funciones propias del oficio mencionado y en las labores descritas en el literal f de la presente cláusula y complementarias del mismo, de conformidad con las órdenes e instrucciones que le imparta EL EMPLEADOR directamente o través de sus representantes. Las funciones serán detalladas en Anexo al presente Contrato; b) a prestar sus servicios en forma exclusiva a EL EMPLEADOR, es decir, a no prestar directa ni indirectamente servicios laborales a otros empleadores, ni trabajar por cuenta propia en el mismo oficio, durante la vigencia de este contrato; y c) a guardar absoluta reserva y confidencialidad sobre los hechos, documentos físicos y/o electrónicos, informaciones y en general, sobre todos los asuntos y materias que lleguen a su conocimiento por causa o por ocasión de su contrato de trabajo y aun después dos (2) años de liquidado el mismo. En caso de incumplimiento de la presente obligación, ${elLaTrabajador} ${trabajadorNombre} responderá legalmente por los daños y/o perjuicios que se causen a la empresa, de conformidad con las normas vigentes en la materia. d) a reportar cualquier orden, solicitud, o novedad que reciba de su jefe inmediato o de cualquier compañero o colaborador, tendiente a realizar o encubrir actos fraudulentos o ilícitos que afecten de cualquier forma a EL EMPLEADOR. e) Dar cumplimiento a las políticas que estipule el Empleador, los cuales constan en los anexos que forman parte integral de este contrato. f) ${elLaTrabajador} ${trabajadorNombre} desempeñará las funciones tales como: Presentar el menú, conocer los ingredientes y las preparaciones, sugerir platos, presentar las recomendaciones del día y las bebidas disponibles, ser enlace entre la cocina y el cliente, debe anotar pedidos y entregarlos al comando de la cocina, cerciorarse que los platos hayan sido preparados de forma correcta, en caso de que el comensal haya hecho una petición especial, mantener comunicación continua con los clientes, prestar atención a las reacciones de los clientes y canalizar quejas o sugerencias que busquen mejorar el servicio, mantener las mesas limpias y desinfectadas antes y después de su uso por parte del cliente, y demás indicaciones que se le asignen o se le requieran, demás instrucciones dadas por el EMPLEADOR.
          </div>

          <div class="clausula">
            <span class="clausula-titulo">SEGUNDA: REMUNERACIÓN.</span> ${elLaTrabajador} ${trabajadorNombre} devengará una remuneración de UN (1) SALARIO MÍNIMO LEGAL MENSUAL VIGENTE, equivalente actualmente a la suma de ${datos.remuneracionLetras} PESOS M/CTE (${datos.remuneracion}).
            <div class="paragrafo">
              <strong>PARÁGRAFO PRIMERO: SALARIO ORDINARIO.</strong> Dentro del salario ordinario se encuentra incluida la remuneración de los descansos dominicales y festivos de que tratan los Capítulos I, II y III del Título VII del C.S.T. De igual manera se aclara y se conviene que en los casos en que ${elLaTrabajador} ${trabajadorNombre} devengue comisiones o cualquiera otra modalidad de salario variable, el 82.5% de dichos ingresos constituye remuneración de la labor realizada, y el 17.5% restante estará destinado a remunerar el descanso en los días dominicales y festivos de que tratan los Capítulos I y II del Título VIII del C.S.T.
            </div>
            <div class="paragrafo">
              <strong>PARÁGRAFO SEGUNDO: SALARIO INTEGRAL.</strong> En la eventualidad en que ${elLaTrabajador} ${trabajadorNombre} devengue salario integral, se entiende de conformidad con el numeral 2 del artículo 132 del C.S.T, subrogado por el artículo 18 de la ley 50/90, que dentro del salario integral convenido se encuentra incorporado el factor prestacional de ${elLaTrabajador} ${trabajadorNombre}, el cual no será inferior al 30% del salario antes mencionado.
            </div>
            <div class="paragrafo">
              <strong>PARÁGRAFO TERCERO:</strong> Las partes acuerdan que en los casos en que se le reconozcan a ${elLaTrabajador} ${trabajadorNombre} beneficios diferentes al salario por concepto de alimentación, comunicaciones, habitación o vivienda, transporte, vestuario, auxilios en dinero o en especie o bonificaciones ocasionales, ésos no se considerarán como factor constitutivo de salario y no se tendrán en cuenta como factor prestacional para la liquidación de acreencias laborales, ni para el pago de aportes parafiscales y cotizaciones a la seguridad social, de conformidad con los Arts. 15 y 16 de la ley 50 de 1990, en concordancia el Art. 17 de la ley 344 de 1996.
            </div>
          </div>

          <div class="clausula">
            <span class="clausula-titulo">TERCERA: DURACIÓN DEL CONTRATO.</span> ${datos.tipoContrato === "Fijo" || datos.tipoContrato === "Término Fijo" 
              ? `La duración del presente contrato será por el término establecido en la parte inicial del presente documento, contado a partir de la fecha de iniciación de labores. No obstante, si antes de la fecha de vencimiento del término estipulado, ninguna de las partes avisare por escrito a la otra su determinación de no prorrogar el contrato, con una antelación no inferior a treinta (30) días, éste se entenderá renovado por un período igual al inicialmente pactado.`
              : `La duración del presente contrato será de manera indefinida, periodo entre la fecha de iniciación del contrato establecida en la parte inicial del presente documento y terminará según las razones dispuestas por la ley.`
            }
          </div>

          <div class="clausula">
            <span class="clausula-titulo">CUARTA: TRABAJO NOCTURNO, SUPLEMENTARIO, DOMINICAL Y/O FESTIVO.</span> Todo trabajo nocturno, suplementario o en horas extras, y todo trabajo en día domingo o festivo en los que legalmente debe concederse descanso, se remunerará conforme los dispone expresamente la ley, salvo acuerdo en contrario contenido en convención, pacto colectivo o laudo arbitral. Para el reconocimiento y pago del trabajo suplementario, nocturno, dominical o festivo, EL EMPLEADOR o sus representantes deberán haberlo autorizado previamente y por escrito.
          </div>

          <div class="clausula">
            <span class="clausula-titulo">QUINTA: JORNADA DE TRABAJO.</span> ${elLaTrabajador} ${trabajadorNombre} se obliga a laborar la jornada máxima legal, salvo acuerdo especial, cumpliendo con los turnos y horarios que señale EL EMPLEADOR, quien podrá cambiarlos o ajustarlos cuando lo estime conveniente sin que ello se considere una desmejora en las condiciones laborales ${esEmpleadoMujer ? "de LA TRABAJADORA" : "del TRABAJADOR"}.
          </div>

          <div class="clausula">
            <span class="clausula-titulo">SEXTA: PERIODO DE PRUEBA.</span> Los 60 días iniciales del contrato se considera como periodo de prueba sin que exceda los límites permitidos a partir de la fecha de inicio y por consiguiente, cualquiera de las partes podrá terminar el contrato unilateralmente, en cualquier momento durante dicho periodo.
          </div>

          <div class="clausula">
            <span class="clausula-titulo">SÉPTIMA: TERMINACIÓN UNILATERAL.</span> Son justas causas para dar terminado unilateralmente este contrato, por cualquiera de las partes, las enumeradas en el Art. 62 del C.S.T., modificado por el Art. 7ª del Decreto 2351 de 1965 y además, por parte de EL EMPLEADOR, las faltas que para el efecto se califiquen como graves en reglamentos, manuales, instructivos y demás documentos que contengan reglamentaciones, órdenes, instrucciones o prohibiciones de carácter general o particular.
            <div class="paragrafo">
              <strong>PARÁGRAFO:</strong> Al finalizar el contrato de trabajo por cualquier concepto, ${elLaTrabajador} ${trabajadorNombre} autoriza descontar de su liquidación final de prestaciones sociales el valor correspondiente a los faltantes y/o deterioro anormal de elementos puestos bajo su responsabilidad.
            </div>
          </div>

          <div class="clausula">
            <span class="clausula-titulo">OCTAVA: PROPIEDAD INTELECTUAL.</span> Las partes acuerdan que todas las invenciones, descubrimientos y trabajos originales concebidos o hechos por ${elLaTrabajador} ${trabajadorNombre} en vigencia del presente contrato pertenecerán a EL EMPLEADOR, por lo cual ${elLaTrabajador} ${trabajadorNombre} se obliga a informar a EL EMPLEADOR, de forma inmediata, sobre la existencia de dichas invenciones y/o trabajos originales.
          </div>

          <div class="clausula">
            <span class="clausula-titulo">NOVENA: MODIFICACIÓN DE LAS CONDICIONES LABORALES.</span> ${elLaTrabajador} ${trabajadorNombre} acepta desde ahora expresamente todas las modificaciones de sus condiciones laborales determinadas por EL EMPLEADOR en ejercicio de su poder subordinante, tales como el horario de trabajo, el lugar de prestación del servicio y el cargo u oficio y/o funciones, siempre que tales modificaciones no afecten ${esEmpleadoMujer ? "su" : "su"} honor, dignidad o sus derechos mínimos, ni impliquen desmejoras sustanciales o graves perjuicios para ${ellaEl}.
          </div>

          <div class="clausula">
            <span class="clausula-titulo">DÉCIMA: DIRECCIÓN ${esEmpleadoMujer ? "DE LA TRABAJADORA" : "DEL TRABAJADOR"}.</span> ${elLaTrabajador} ${trabajadorNombre} se compromete a informar por escrito y de manera inmediata a EL EMPLEADOR cualquier cambio en su dirección de residencia, teniéndose en todo caso como suya, la última dirección registrada en su hoja de vida.
          </div>

          <div class="clausula">
            <span class="clausula-titulo">DÉCIMA PRIMERA: EFECTOS.</span> El presente contrato reemplaza en su integridad y deja sin efecto cualquiera otro contrato, verbal o escrito, celebrado entre las partes con anterioridad, pudiendo las partes convenir por escrito modificaciones al mismo, las que formarán parte integral de este contrato.
          </div>

          <div class="clausula">
            <span class="clausula-titulo">DÉCIMA SEGUNDA: USO DE INTERNET.</span> ${elLaTrabajador} ${trabajadorNombre}, en razón de sus funciones, tendrá acceso a Internet. ${elLaTrabajador} ${trabajadorNombre} se compromete a realizar un uso adecuado del Internet desde su computador o dispositivo móvil o cualquier otro dispositivo de la empresa con conexión a Internet. Se abstiene de usarlo para el ingreso a páginas que no sean del desarrollo de sus funciones.
          </div>

          <div class="clausula">
            <span class="clausula-titulo">DÉCIMA TERCERA: HABEAS DATA.</span> Los datos consignados en el presente Contrato serán tratados de acuerdo a lo establecido en la Ley 1581 de 2012, en el Decreto 1377 de 2013 y cualquier otra normatividad en lo que respecta a la protección de la información.
          </div>

          <div class="clausula">
            <span class="clausula-titulo">DÉCIMA CUARTA: AUTORIZACIÓN DESCUENTOS.</span> ${elLaTrabajador} ${trabajadorNombre} autoriza desde ahora al EMPLEADOR para que, de sus salarios, prestaciones sociales e indemnizaciones, le descuente, durante la vigencia del contrato o al momento de la terminación del mismo por cualquier causa, las sumas de dinero que por cualquier motivo le llegare a adeudar.
          </div>

          <div class="clausula">
            <span class="clausula-titulo">DÉCIMA QUINTA: OBLIGACIONES ESPECIALES DE CONFIDENCIALIDAD ${esEmpleadoMujer ? "DE LA TRABAJADORA" : "DEL TRABAJADOR"}.</span> ${elLaTrabajador} ${trabajadorNombre} se obliga a:
            <br/>a. Guardar absoluta confidencialidad respecto a: procedimientos, métodos, características, lista de clientes, fórmulas de productos y similares, al igual que claves de seguridad, suministros, software, base de datos de cualquier índole, valores de bienes y servicios, información técnica, financiera, económica o comercial del contratante o sus clientes.
            <br/>b. No ejercer actos de competencia desleal frente a ${datos.nombreEmpleador}.
            <br/>c. Adoptar todas las precauciones necesarias y apropiadas para guardar la confidencialidad de la información.
            <br/>d. Devolver inmediatamente a la terminación de su contrato: la lista de clientes, claves, bases de datos, equipos, información técnica, y demás que tenga del empleador.
            <div class="paragrafo">
              <strong>PARÁGRAFO:</strong> El incumplimiento u omisión de cualquiera de las obligaciones aquí acordadas no solo es causal de terminación de los vínculos laborales existentes entre las partes, sino que podría conllevar a iniciar acciones judiciales en contra ${esEmpleadoMujer ? "de la trabajadora" : "del trabajador"} por los perjuicios materiales e inmateriales que cause.
            </div>
          </div>

          <p style="margin-top: 25px;">Para constancia se firma en dos ejemplares del mismo tenor y valor, ante testigos en la ciudad y fecha que se indican a continuación:</p>

          <p style="margin: 15px 0;"><strong>CIUDAD:</strong> ${datos.ciudad} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<strong>FECHA:</strong> ${datos.fechaFirma}</p>

          ${firmaTrab && firmaEmpl ? `
            <!-- FIRMAS DIGITALES/ELECTRÓNICAS -->
            <div style="margin-top: 30px; padding: 15px; border: 2px solid #6b7280; border-radius: 12px; background: linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%);">
              <div style="text-align: center; margin-bottom: 15px;">
                <span style="display: inline-block; background: linear-gradient(135deg, #4b5563 0%, #6b7280 100%); color: white; padding: 8px 20px; border-radius: 25px; font-size: 12px; font-weight: 700;">
                  ✓ DOCUMENTO FIRMADO ${esElectronica ? 'CON FIRMA ELECTRÓNICA' : 'DIGITALMENTE'}
                </span>
              </div>
              
              <div style="display: flex; gap: 30px; justify-content: center; flex-wrap: wrap;">
                <!-- Firma Empleador -->
                <div style="text-align: center; padding: 15px; background: white; border-radius: 10px; border: 1px solid #d1d5db; min-width: 200px;">
                  <img src="${firmaEmpl.firma}" alt="Firma Empleador" style="max-width: 180px; max-height: 60px; margin-bottom: 8px; filter: grayscale(100%);"/>
                  <div style="border-top: 1px solid #1a1a2e; padding-top: 8px;">
                    <div style="display: inline-block; background: #6b7280; color: white; padding: 2px 8px; border-radius: 10px; font-size: 8px; font-weight: 600; margin-bottom: 4px;">
                      ${esElectronica ? 'FIRMA ELECTRÓNICA' : 'FIRMA DIGITAL'}
                    </div>
                    <div style="font-weight: 700; font-size: 10px; color: #000;">EMPLEADOR</div>
                    <div style="font-size: 10px; color: #000;">${datos.representanteLegal}</div>
                    <div style="font-size: 9px; color: #374151;">${datos.tipoDocRepresentante || "C.C."} ${datos.cedulaRepresentante}</div>
                    ${esElectronica ? `
                      <div style="font-size: 8px; color: #374151; margin-top: 4px;">📋 Registrada: ${firmaEmpl.fechaRegistro || 'N/A'}</div>
                      <div style="font-size: 8px; color: #374151;">📅 Aplicada: ${firmaTrab.fechaFirma}</div>
                    ` : `
                      <div style="font-size: 8px; color: #374151; margin-top: 4px;">📅 ${firmaEmpl.fechaFirma}</div>
                    `}
                  </div>
                </div>
                
                <!-- Firma Trabajador -->
                <div style="text-align: center; padding: 15px; background: white; border-radius: 10px; border: 1px solid #d1d5db; min-width: 200px;">
                  <img src="${firmaTrab.firma}" alt="Firma Trabajador" style="max-width: 180px; max-height: 60px; margin-bottom: 8px; filter: grayscale(100%);"/>
                  <div style="border-top: 1px solid #1a1a2e; padding-top: 8px;">
                    <div style="display: inline-block; background: #6b7280; color: white; padding: 2px 8px; border-radius: 10px; font-size: 8px; font-weight: 600; margin-bottom: 4px;">
                      FIRMA DIGITAL
                    </div>
                    <div style="font-weight: 700; font-size: 10px; color: #000;">${trabajadorNombre}</div>
                    <div style="font-size: 10px; color: #000;">${datos.nombreTrabajador}</div>
                    <div style="font-size: 9px; color: #374151;">${datos.tipoDocTrabajador || "C.C."} ${datos.cedulaTrabajador}</div>
                    <div style="font-size: 8px; color: #374151; margin-top: 4px;">📅 ${firmaTrab.fechaFirma}</div>
                  </div>
                </div>
              </div>
              
              <div style="text-align: center; margin-top: 12px; font-size: 9px; color: #374151; font-style: italic;">
                ${esElectronica 
                  ? 'Firma del empleador: electrónica pre-registrada | Firma del trabajador: digital' 
                  : 'Este documento fue firmado digitalmente por ambas partes'}
              </div>
            </div>
          ` : `
            <!-- FIRMAS ESTÁTICAS (para imprimir y firmar manualmente) -->
            <div class="firma-container">
              <div class="firma-box">
                <div class="espacio-firma"></div>
                <div class="linea-firma"></div>
                <div class="nombre-firma">EMPLEADOR</div>
                <div class="nombre-firma">${datos.representanteLegal}</div>
                <div class="cedula-firma">${datos.tipoDocRepresentante || "Cédula de Ciudadanía"} ${datos.cedulaRepresentante}</div>
                <div class="cedula-firma">Representante Legal</div>
              </div>
              <div class="firma-box">
                <div class="espacio-firma"></div>
                <div class="linea-firma"></div>
                <div class="nombre-firma">${trabajadorNombre}</div>
                <div class="nombre-firma">${datos.nombreTrabajador}</div>
                <div class="cedula-firma">${datos.tipoDocTrabajador || "Cédula de Ciudadanía"} ${datos.cedulaTrabajador}</div>
              </div>
            </div>
          `}

          <script>
            // Esperar a que el documento y todas las imágenes estén completamente cargadas
            window.onload = function() {
              // Dar tiempo adicional para que el navegador renderice todo
              setTimeout(function() {
                window.print();
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    win.document.close();
  };

  return (
    <div style={{ padding: "20px", maxWidth: "800px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(26, 35, 126, 0.3)'
      }}>
        <h1 style={{ color: 'white', margin: 0, fontSize: '24px', fontWeight: '700' }}>
          📝 Contratos de Trabajo
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.8)', margin: '8px 0 0', fontSize: '14px' }}>
          Genera y consulta contratos de trabajo
        </p>
      </div>

      {/* Pestañas */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '24px',
        background: 'white',
        borderRadius: '12px',
        padding: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <button
          onClick={() => setTab("generar")}
          style={{
            flex: 1,
            padding: '12px 20px',
            background: tab === "generar" 
              ? 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)' 
              : 'transparent',
            color: tab === "generar" ? 'white' : '#1a237e',
            border: tab === "generar" ? 'none' : '2px solid #e0e0e0',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          ✏️ Generar Contrato
        </button>
        <button
          onClick={() => setTab("consultar")}
          style={{
            flex: 1,
            padding: '12px 20px',
            background: tab === "consultar" 
              ? 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)' 
              : 'transparent',
            color: tab === "consultar" ? 'white' : '#1a237e',
            border: tab === "consultar" ? 'none' : '2px solid #e0e0e0',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          📋 Consultar Contratos ({contratosGuardados.length})
        </button>
      </div>

      {tab === "generar" && (
      <>
      {/* Formulario */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 20px', color: '#1a237e', fontSize: '16px' }}>
          Selecciona empleado y sede
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
          {/* Selector de Empleado */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333', fontSize: '13px' }}>
              👤 Empleado
            </label>
            <select
              value={empleadoSeleccionado}
              onChange={(e) => {
                setEmpleadoSeleccionado(e.target.value);
                setContratoGenerado(null);
              }}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #e0e0e0',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="">-- Selecciona un empleado --</option>
              {[...(empleados || [])].filter(emp => emp.activo !== false).sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.nombre} - {emp.documento}
                </option>
              ))}
            </select>
          </div>

          {/* Selector de Sede */}
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333', fontSize: '13px' }}>
              🏢 Sede (Empleador)
            </label>
            <select
              value={sedeSeleccionada}
              onChange={(e) => {
                setSedeSeleccionada(e.target.value);
                setContratoGenerado(null);
              }}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #e0e0e0',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="">-- Selecciona una sede --</option>
              {sedes.map(s => (
                <option key={s.id} value={s.id}>
                  {s.nombre || s.razonSocial}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Botones de acción */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
          {tienePermiso("rh_contratos_generar") && (
            <button
              onClick={generarContrato}
              disabled={!empleadoSeleccionado || !sedeSeleccionada}
              style={{
                background: empleadoSeleccionado && sedeSeleccionada 
                  ? 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)' 
                  : '#ccc',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '600',
                cursor: empleadoSeleccionado && sedeSeleccionada ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              📄 Generar Contrato
            </button>
          )}

          {tienePermiso("rh_contratos_imprimir") && contratoGenerado && (
            <>
              {/* Botón Solo Imprimir (sin firmas) */}
              <button
                onClick={() => imprimirContratoConFirma(null, null, false)}
                style={{
                  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                🖨️ Solo Imprimir
              </button>
              
              {/* Botón Firmar e Imprimir - Solo si NO es contrato consultado */}
              {!esContratoConsultado && (
                <button
                  onClick={iniciarProcesoFirma}
                  style={{
                    background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  ✍️ Firmar e Imprimir
                </button>
              )}
            </>
          )}

          {contratoGenerado && !esContratoConsultado && (
            <button
              onClick={async () => {
                if (!empleado || !sede) return;
                setGuardandoContrato(true);
                const tipoContratoTexto = (contratoGenerado.tipoContrato === "Fijo" || contratoGenerado.tipoContrato === "Término Fijo") 
                  ? "Término Fijo" 
                  : "Término Indefinido";
                  
                // Crear objeto contrato con datos de firma si está firmado
                const contrato = {
                  id: uid(),
                  fechaGeneracion: toISO(new Date()),
                  empleadoId: empleadoSeleccionado,
                  empleadoNombre: contratoGenerado.nombreTrabajador,
                  sedeId: sedeSeleccionada,
                  sedeName: contratoGenerado.nombreEmpleador,
                  tipoContrato: tipoContratoTexto,
                  datos: contratoGenerado,
                  // Datos de firma si está firmado (asegurar que firmado sea booleano)
                  firmado: !!(contratoFirmado && firmasTempGuardadas.trabajador && firmasTempGuardadas.empleador),
                  firmaTrabajador: firmasTempGuardadas.trabajador ? JSON.stringify(firmasTempGuardadas.trabajador) : null,
                  firmaEmpleador: firmasTempGuardadas.empleador ? JSON.stringify(firmasTempGuardadas.empleador) : null,
                  fechaFirma: contratoFirmado ? toISO(new Date()) : null,
                  tipoFirmaEmpleador: firmasTempGuardadas.esElectronica ? 'electronica' : 'digital'
                };
                const guardado = await guardarContratoEnSupabase(contrato, filtroEmpresa);
                if (guardado) {
                  setContratosGuardados(prev => [guardado, ...prev]);
                  if (contratoFirmado) {
                    alert("✅ Contrato firmado guardado correctamente.\n\n🔒 Este contrato ya no podrá ser modificado.");
                  } else {
                    alert("✅ Contrato guardado correctamente (sin firmas)");
                  }
                  // Limpiar estados de firma
                  setContratoFirmado(false);
                  setFirmasTempGuardadas({ trabajador: null, empleador: null, esElectronica: false });
                } else {
                  alert("❌ Error al guardar el contrato");
                }
                setGuardandoContrato(false);
              }}
              disabled={guardandoContrato}
              style={{
                background: guardandoContrato 
                  ? '#ccc' 
                  : contratoFirmado 
                    ? 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)'
                    : 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '14px',
                fontWeight: '600',
                cursor: guardandoContrato ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {guardandoContrato ? '⏳ Guardando...' : contratoFirmado ? '🔒 Guardar Contrato Firmado' : '💾 Guardar Contrato'}
            </button>
          )}
        </div>
      </div>

      {/* Vista previa del contrato */}
      {contratoGenerado && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '2px solid #1a237e'
        }}>
          <h3 style={{ margin: '0 0 20px', color: '#1a237e', fontSize: '16px', textAlign: 'center' }}>
            📋 Vista Previa del Contrato
          </h3>

          <div style={{ 
            background: '#f5f5f5', 
            padding: '20px', 
            borderRadius: '8px',
            fontSize: '12px'
          }}>
            <h4 style={{ textAlign: 'center', margin: '0 0 16px', textTransform: 'uppercase' }}>
              {(contratoGenerado.tipoContrato === "Fijo" || contratoGenerado.tipoContrato === "Término Fijo")
                ? "Contrato Individual de Trabajo a Término Fijo"
                : "Contrato Individual de Trabajo a Término Indefinido"}
            </h4>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '6px', border: '1px solid #ccc', fontWeight: 'bold', background: '#e8e8e8', width: '40%' }}>NOMBRE DEL EMPLEADOR</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>{contratoGenerado.nombreEmpleador}</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px', border: '1px solid #ccc', fontWeight: 'bold', background: '#e8e8e8' }}>NIT</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>{contratoGenerado.nitEmpleador}</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px', border: '1px solid #ccc', fontWeight: 'bold', background: '#e8e8e8' }}>REPRESENTANTE LEGAL</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>{contratoGenerado.representanteLegal}</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px', border: '1px solid #ccc', fontWeight: 'bold', background: '#e8e8e8' }}>{(contratoGenerado.tipoDocRepresentante || "Cédula de Ciudadanía").toUpperCase()}</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>{contratoGenerado.cedulaRepresentante || "N/A"}</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px', border: '1px solid #ccc', fontWeight: 'bold', background: '#e8e8e8' }}>NOMBRE DEL TRABAJADOR</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>{contratoGenerado.nombreTrabajador}</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px', border: '1px solid #ccc', fontWeight: 'bold', background: '#e8e8e8' }}>{(contratoGenerado.tipoDocTrabajador || "Cédula de Ciudadanía").toUpperCase()}</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>{contratoGenerado.cedulaTrabajador}</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px', border: '1px solid #ccc', fontWeight: 'bold', background: '#e8e8e8' }}>CARGO</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>{contratoGenerado.cargo}</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px', border: '1px solid #ccc', fontWeight: 'bold', background: '#e8e8e8' }}>TIPO DE CONTRATO</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>
                    {(contratoGenerado.tipoContrato === "Fijo" || contratoGenerado.tipoContrato === "Término Fijo")
                      ? "Término Fijo"
                      : "Término Indefinido"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '6px', border: '1px solid #ccc', fontWeight: 'bold', background: '#e8e8e8' }}>REMUNERACIÓN</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>{contratoGenerado.remuneracion}</td>
                </tr>
                <tr>
                  <td style={{ padding: '6px', border: '1px solid #ccc', fontWeight: 'bold', background: '#e8e8e8' }}>FECHA INICIO</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>{contratoGenerado.fechaInicio}</td>
                </tr>
              </tbody>
            </table>

            <p style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>
              El contrato completo incluye todas las cláusulas legales. Haz clic en "Imprimir Contrato" para ver el documento completo.
            </p>
          </div>
        </div>
      )}
      </>
      )}

      {/* Pestaña Consultar Contratos */}
      {tab === "consultar" && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
          <div style={{ marginBottom: '20px' }}>
            <input
              type="text"
              placeholder="🔍 Buscar por nombre de empleado..."
              value={filtroEmpleado}
              onChange={(e) => setFiltroEmpleado(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #e0e0e0',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>

          {cargandoContratos ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              ⏳ Cargando contratos...
            </div>
          ) : contratosGuardados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              📋 No hay contratos guardados
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {contratosGuardados
                .filter(c => 
                  !filtroEmpleado || 
                  (c.empleadoNombre && c.empleadoNombre.toLowerCase().includes(filtroEmpleado.toLowerCase()))
                )
                .map((contrato) => (
                  <div
                    key={contrato.id}
                    style={{
                      padding: '16px',
                      background: contrato.firmado ? '#f0fdf4' : '#f8f9fa',
                      borderRadius: '12px',
                      border: contrato.firmado ? '2px solid #16a34a' : '1px solid #e0e0e0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '12px'
                    }}
                  >
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <span style={{ fontWeight: '600', color: '#1a237e' }}>
                          {contrato.empleadoNombre || "Sin nombre"}
                        </span>
                        {contrato.firmado && (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: contrato.tipoFirmaEmpleador === 'electronica' 
                              ? 'linear-gradient(135deg, #4b5563 0%, #6b7280 100%)'
                              : 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                            color: 'white',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            fontSize: '10px',
                            fontWeight: '700'
                          }}>
                            🔒 {contrato.tipoFirmaEmpleador === 'electronica' ? 'FIRMADO (Electrónica)' : 'FIRMADO (Digital)'}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        <strong>Tipo:</strong> {contrato.tipoContrato === "Término Fijo" 
                          ? "Contrato Individual de Trabajo a Término Fijo" 
                          : "Contrato Individual de Trabajo a Término Indefinido"}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        <strong>Sede:</strong> {contrato.sedeName || "-"}
                      </div>
                      <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                        Generado: {contrato.fechaGeneracion 
                          ? new Date(contrato.fechaGeneracion).toLocaleDateString('es-CO', { 
                              day: 'numeric', month: 'long', year: 'numeric' 
                            })
                          : "-"}
                        {contrato.firmado && contrato.fechaFirma && (
                          <span style={{ marginLeft: '10px', color: '#16a34a' }}>
                            | Firmado: {new Date(contrato.fechaFirma).toLocaleDateString('es-CO', { 
                              day: 'numeric', month: 'long', year: 'numeric' 
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => {
                          if (contrato.datos) {
                            setContratoGenerado(contrato.datos);
                            setEsContratoConsultado(true); // Marcar que es un contrato cargado desde Consultar
                            // Si el contrato está firmado, cargar las firmas temporales
                            if (contrato.firmado && contrato.firmaTrabajador && contrato.firmaEmpleador) {
                              try {
                                const firmaTrab = typeof contrato.firmaTrabajador === 'string' 
                                  ? JSON.parse(contrato.firmaTrabajador) 
                                  : contrato.firmaTrabajador;
                                const firmaEmpl = typeof contrato.firmaEmpleador === 'string' 
                                  ? JSON.parse(contrato.firmaEmpleador) 
                                  : contrato.firmaEmpleador;
                                setFirmasTempGuardadas({
                                  trabajador: firmaTrab,
                                  empleador: firmaEmpl,
                                  esElectronica: contrato.tipoFirmaEmpleador === 'electronica'
                                });
                                setContratoFirmado(true);
                              } catch (e) {
                                console.error("Error parseando firmas:", e);
                              }
                            } else {
                              setContratoFirmado(false);
                              setFirmasTempGuardadas({ trabajador: null, empleador: null, esElectronica: false });
                            }
                            setTab("generar");
                          }
                        }}
                        style={{
                          padding: '8px 16px',
                          background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        👁️ Ver
                      </button>
                      {/* Botón para imprimir directamente con firmas si está firmado */}
                      {contrato.firmado && contrato.firmaTrabajador && contrato.firmaEmpleador && (
                        <button
                          onClick={() => {
                            if (contrato.datos) {
                              try {
                                const firmaTrab = typeof contrato.firmaTrabajador === 'string' 
                                  ? JSON.parse(contrato.firmaTrabajador) 
                                  : contrato.firmaTrabajador;
                                const firmaEmpl = typeof contrato.firmaEmpleador === 'string' 
                                  ? JSON.parse(contrato.firmaEmpleador) 
                                  : contrato.firmaEmpleador;
                                // Guardar temporalmente el contrato y llamar imprimir
                                setContratoGenerado(contrato.datos);
                                setTimeout(() => {
                                  imprimirContratoConFirma(firmaTrab, firmaEmpl, contrato.tipoFirmaEmpleador === 'electronica');
                                }, 100);
                              } catch (e) {
                                console.error("Error parseando firmas para imprimir:", e);
                                alert("Error al cargar las firmas del contrato");
                              }
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          🖨️ Imprimir Firmado
                        </button>
                      )}
                      {contrato.firmado && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '8px 16px',
                          background: '#e5e7eb',
                          color: '#6b7280',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          🔒 No modificable
                        </span>
                      )}
                      <button
                        onClick={async () => {
                          const mensaje = contrato.firmado 
                            ? "⚠️ Este contrato está FIRMADO.\n\n¿Está seguro que desea eliminarlo?\n\nEsta acción no se puede deshacer."
                            : "¿Eliminar este contrato?";
                          if (confirm(mensaje)) {
                            const eliminado = await eliminarContratoEnSupabase(contrato.id);
                            if (eliminado) {
                              setContratosGuardados(prev => prev.filter(c => c.id !== contrato.id));
                              alert("✅ Contrato eliminado correctamente");
                            } else {
                              alert("❌ Error al eliminar el contrato");
                            }
                          }
                        }}
                        style={{
                          padding: '8px 16px',
                          background: 'linear-gradient(135deg, #c62828 0%, #e53935 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        🗑️ Eliminar Contrato
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Modal de selección: Firma Estática o Digital */}
      <ModalTipoFirma
        visible={mostrarModalTipoFirma}
        onSeleccionar={manejarSeleccionTipoFirma}
        onCerrar={() => setMostrarModalTipoFirma(false)}
        tipoDocumento={`Contrato de Trabajo - ${contratoGenerado?.nombreTrabajador || 'Empleado'}`}
      />

      {/* Canvas de Firma Digital */}
      {mostrarCanvasFirma && (
        <FirmaDigitalCanvas
          onFirmaCompleta={manejarFirmaCompletada}
          onCancelar={cancelarFirma}
          nombreFirmante={quienFirma === "trabajador" 
            ? contratoGenerado?.nombreTrabajador || "Trabajador"
            : contratoGenerado?.representanteLegal || "Empleador"
          }
          tipoDocumento={quienFirma === "trabajador" 
            ? "Firma del Trabajador (1 de 2)"
            : "Firma del Empleador (2 de 2)"
          }
        />
      )}
    </div>
  );
}

function CertificacionesLaborales({ empleados = [], sedes = [], tienePermiso = () => true, filtroEmpresa = null }) {
  const [tab, setTab] = useState("generar"); // "generar" | "consultar"
  const [tipoSalario, setTipoSalario] = useState("base");
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState("");
  const [sedeSeleccionada, setSedeSeleccionada] = useState("");
  const [datosCompletos, setDatosCompletos] = useState(null);
  const [certificacionesGuardadas, setCertificacionesGuardadas] = useState([]);
  const [cargandoCertificaciones, setCargandoCertificaciones] = useState(false);
  const [guardandoCertificacion, setGuardandoCertificacion] = useState(false);
  const [filtroEmpleado, setFiltroEmpleado] = useState("");
  const [tipoEmpleado, setTipoEmpleado] = useState("activo"); // "activo" | "inactivo"

  // Filtrar empleados según tipo seleccionado
  const empleadosFiltrados = useMemo(() => {
    if (tipoEmpleado === "activo") {
      return empleados.filter(emp => emp.activo !== false);
    } else {
      return empleados.filter(emp => emp.activo === false);
    }
  }, [empleados, tipoEmpleado]);

  const empleado = empleadosFiltrados.find(emp => emp.id === empleadoSeleccionado);
  const sede = sedes.find(s => s.id === sedeSeleccionada);
  
  // Limpiar empleado seleccionado cuando cambia el tipo
  useEffect(() => {
    setEmpleadoSeleccionado("");
    setDatosCompletos(null);
  }, [tipoEmpleado]);

  useEffect(() => {
    let activo = true;
    const cargarInicial = async () => {
      setCargandoCertificaciones(true);
      const lista = await cargarCertificacionesDeSupabase(filtroEmpresa);
      if (activo) {
        setCertificacionesGuardadas(lista);
      }
      if (activo) {
        setCargandoCertificaciones(false);
      }
    };
    cargarInicial();
    return () => {
      activo = false;
    };
  }, [filtroEmpresa]);

  // Guardar certificación en Supabase
  const guardarCertificacion = async (datos) => {
    if (!empleado) {
      alert("Por favor selecciona un empleado");
      return;
    }

    if (!sede) {
      alert("Por favor selecciona una sede");
      return;
    }

    const cert = {
      id: uid(),
      fechaGeneracion: toISO(new Date()),
      empleadoId: empleadoSeleccionado,
      empleadoNombre: datos.nombreEmpleado,
      sedeId: sedeSeleccionada,
      sedeName: datos.razonSocial,
      tipoSalario: tipoSalario,
      datos,
    };

    try {
      setGuardandoCertificacion(true);
      const guardada = await guardarCertificacionEnSupabase(cert, filtroEmpresa);
      if (!guardada) {
        alert("No se pudo guardar la certificación en Supabase.");
        return;
      }

      setCertificacionesGuardadas((prev) => {
        const restantes = prev.filter((c) => c.id !== guardada.id);
        return [guardada, ...restantes];
      });

      alert("Certificación guardada correctamente");
    } catch (e) {
      console.error(e);
      alert("Error inesperado al guardar la certificación.");
    } finally {
      setGuardandoCertificacion(false);
    }
  };

  // Función auxiliar que genera los datos y los devuelve (sin depender del estado)
  // Auxilio de transporte 2026
  const AUXILIO_TRANSPORTE_2026 = 249095;
  
  const obtenerDatosCertificacion = () => {
    if (!empleado) {
      return null;
    }
    if (!sede) {
      return null;
    }

    // Obtener el salario según la selección
    const salarioValorBase = tipoSalario === "promedio" 
      ? empleado.salarioProm6m 
      : empleado.salarioBase;
    
    const salarioTexto = tipoSalario === "promedio" 
      ? "promedio de los últimos 6 meses" 
      : "salario base";
    
    // 🔐 CORRECCIÓN: Solo sumar auxilio de transporte si es SALARIO BASE
    // Si es salario promedio (últimos 6 meses), NO sumar auxilio
    const auxilioTransporteAplicable = tipoSalario === "promedio" ? 0 : AUXILIO_TRANSPORTE_2026;
    const salarioTotal = (salarioValorBase || 0) + auxilioTransporteAplicable;

    // Formatear fecha actual
    const fechaActual = new Date();
    const meses = ["enero", "febrero", "marzo", "abril", "mayo", "junio", 
                   "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
    const fechaTexto = `${fechaActual.getDate()} de ${meses[fechaActual.getMonth()]} de ${fechaActual.getFullYear()}`;

    // Formatear salarios con separador de miles
    const salarioBaseFormateado = salarioValorBase ? salarioValorBase.toLocaleString('es-CO') : "0";
    const auxilioFormateado = auxilioTransporteAplicable.toLocaleString('es-CO');
    const salarioTotalFormateado = salarioTotal ? salarioTotal.toLocaleString('es-CO') : "0";
    
    // Convertir salario total a letras (con "DE" si es número redondo)
    const salarioTotalEnLetras = salarioTotal ? formatoSalarioEnLetras(salarioTotal) : "CERO";

    return {
      nombreEmpleado: empleado.nombre || "",
      tipoDoc: empleado.tipoDoc || "",
      documento: empleado.documento || "",
      fechaIngreso: empleado.fechaIngreso || "",
      fechaRetiro: empleado.fechaRetiro || "",
      tipoContrato: empleado.tipoContrato || "",
      cargo: empleado.cargo || "",
      salarioTexto: salarioTexto,
      salarioValor: salarioBaseFormateado,
      salarioBase: salarioBaseFormateado,
      auxilioTransporte: auxilioFormateado,
      salarioTotal: salarioTotalFormateado,
      salarioEnLetras: salarioTotalEnLetras,
      fechaEmision: fechaTexto,
      esEmpleadoInactivo: tipoEmpleado === "inactivo",
      // Datos de la sede
      razonSocial: sede.razonSocial || "",
      nitSede: sede.nit || "",
      representanteLegal: sede.representanteLegal || "",
      tipoDocRepresentante: sede.tipoDocRepresentante || "Cédula de Ciudadanía",
      cedulaRepresentante: sede.cedulaRepresentante || "",
      generoRepresentante: sede.generoRepresentante || "Masculino",
      direccionSede: sede.direccion || "",
      telefonoSede: sede.telefono || "",
      // Firma electrónica del representante legal - validación robusta
      firmaRepresentante: (() => {
        const firma = sede.firmaElectronica;
        if (!firma) return null;
        // Verificar que la firma tenga datos válidos (base64 de imagen)
        if (typeof firma === 'object' && firma.firma && firma.firma.startsWith('data:image')) {
          return firma;
        }
        // Si es string, intentar parsearlo
        if (typeof firma === 'string') {
          try {
            const parsed = JSON.parse(firma);
            if (parsed.firma && parsed.firma.startsWith('data:image')) {
              return parsed;
            }
          } catch (e) {}
        }
        // Si no tiene formato válido, retornar null
        return null;
      })(),
      // 🔐 Incluir tipo de salario en los datos para usar en el template
      tipoSalario: tipoSalario
    };
  };

  const generarCertificacion = () => {
    if (!empleado) {
      alert("Por favor selecciona un empleado");
      return;
    }

    if (!sede) {
      alert("Por favor selecciona una sede");
      return;
    }

    const datosNuevos = obtenerDatosCertificacion();
    setDatosCompletos(datosNuevos);
  };

  const imprimirCertificacion = (info, conFirma = true) => {
    // Si no se pasan datos, intentar obtenerlos frescos
    let datosImprimir = info;
    if (!datosImprimir || !datosImprimir.nombreEmpleado) {
      datosImprimir = obtenerDatosCertificacion();
    }
    // Si aún no hay datos, usar el estado
    if (!datosImprimir || !datosImprimir.nombreEmpleado) {
      datosImprimir = datosCompletos;
    }
    if (!datosImprimir || !datosImprimir.nombreEmpleado) {
      if (!empleado) {
        alert("Por favor selecciona un empleado");
      } else if (!sede) {
        alert("Por favor selecciona una sede");
      } else {
        alert("Primero genera una certificación");
      }
      return;
    }
    
    const ventanaImpresion = window.open('', '_blank');
    ventanaImpresion.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title></title>
          <meta charset="UTF-8">
          <style>
            @page {
              size: letter;
              margin: 2.5cm 2.5cm 3cm 2.5cm;
            }
            
            body { 
              font-family: 'Times New Roman', Times, serif; 
              font-size: 12pt;
              line-height: 1.8;
              color: #000;
              max-width: 21cm;
              margin: 0 auto;
              padding: 2cm;
              position: relative;
            }
            
            .logo-header {
              position: absolute;
              top: 0;
              right: 0;
              width: 100px;
              height: auto;
            }
            
            .logo-header img {
              width: 100%;
              height: auto;
              border-radius: 8px;
            }
            
            .encabezado {
              text-align: center;
              margin-bottom: 40px;
              font-weight: bold;
              padding-top: 20px;
            }
            
            .logo {
              font-size: 48pt;
              margin-right: 20px;
              filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.2));
            }
            
            .info-empresa {
              text-align: center;
            }
            
            .empresa {
              font-size: 14pt;
              margin-bottom: 5px;
            }
            
            .nit {
              font-size: 11pt;
              margin-bottom: 30px;
            }
            
            .titulo {
              font-size: 13pt;
              font-weight: bold;
              text-align: center;
              margin: 30px 0;
              text-decoration: underline;
            }
            
            .contenido {
              text-align: justify;
              margin: 30px 0;
            }
            
            .fecha {
              text-align: left;
              margin: 40px 0 20px 0;
            }
            
            @media print {
              .fecha {
                text-align: left;
              }
              .logo-header {
                position: fixed;
                top: 0;
                right: 0;
                width: 90px;
              }
            }
            
            .firma {
              margin-top: 80px;
              text-align: left;
            }
            
            .linea-firma {
              border-top: 1px solid #000;
              width: 250px;
              margin: 0 0 10px 0;
            }
            
            .nombre-firma {
              font-weight: bold;
              margin: 0;
            }
            
            .cargo-firma {
              margin: 0;
            }
            
            @media print {
              body {
                padding: 0;
              }
              .logo {
                display: none;
              }
              .encabezado {
                display: none;
              }
              @page {
                margin-top: 2.5cm;
                margin-bottom: 3cm;
              }
              .header-print {
                display: none;
              }
              .footer-print {
                display: block;
                position: fixed;
                bottom: 0.5cm;
                left: 0;
                right: 0;
                text-align: center;
                font-size: 10pt;
                color: #666;
              }
            }
            
            .header-print, .footer-print {
              display: none;
            }
          </style>
        </head>
        <body>
          
          <div class="logo-header">
            <img src="${LOGO_BIGBURGUER}" alt="BigBurguer Logo" />
          </div>
          
          <div class="encabezado">
            <div class="info-empresa">
              <div class="empresa">${datosImprimir.razonSocial}</div>
            </div>
          </div>

          <div class="fecha">Pereira, ${datosImprimir.fechaEmision}</div>

          <div class="titulo">CERTIFICACIÓN LABORAL</div>

          <div class="contenido">
            <p>${datosImprimir.generoRepresentante === "Femenino" ? "La suscrita" : "El suscrito"} <strong>${datosImprimir.representanteLegal}</strong>, en calidad de Representante Legal de <strong>${datosImprimir.razonSocial}</strong>, identificad${datosImprimir.generoRepresentante === "Femenino" ? "a" : "o"} con NIT <strong>${datosImprimir.nitSede}</strong>,</p>
            
            <p style="text-align: center; margin: 30px 0;"><strong>CERTIFICA QUE:</strong></p>
            
            ${datosImprimir.esEmpleadoInactivo ? `
            <p>El (la) Señor(a) <strong>${datosImprimir.nombreEmpleado}</strong>, identificado(a) con <strong>${datosImprimir.tipoDoc === 'CC' ? 'Cédula de Ciudadanía' : datosImprimir.tipoDoc === 'CE' ? 'Cédula de Extranjería' : datosImprimir.tipoDoc === 'TI' ? 'Tarjeta de Identidad' : datosImprimir.tipoDoc === 'PA' ? 'Pasaporte' : datosImprimir.tipoDoc === 'PEP' ? 'Permiso Especial de Permanencia' : datosImprimir.tipoDoc || 'Cédula de Ciudadanía'} No. ${datosImprimir.documento}</strong>, laboró en nuestra empresa desde el <strong>${datosImprimir.fechaIngreso}</strong> hasta el <strong>${datosImprimir.fechaRetiro || "N/A"}</strong>, con un contrato <strong>${datosImprimir.tipoContrato}</strong>, desempeñando el cargo de <strong>${datosImprimir.cargo}</strong>.</p>
            
            <p>A la fecha de retiro, el (la) trabajador(a) se encontraba a paz y salvo con la empresa.</p>
            ` : `
            <p>El (la) Señor(a) <strong>${datosImprimir.nombreEmpleado}</strong>, identificado(a) con <strong>${datosImprimir.tipoDoc} ${datosImprimir.documento}</strong>, labora en nuestra empresa desde el <strong>${datosImprimir.fechaIngreso}</strong>, con un contrato <strong>${datosImprimir.tipoContrato}</strong>, desempeñando el cargo de <strong>${datosImprimir.cargo}</strong>, devengando un ${datosImprimir.salarioTexto} de <strong>$${datosImprimir.salarioBase}</strong>${datosImprimir.tipoSalario !== "promedio" ? `, más auxilio de transporte de <strong>$${datosImprimir.auxilioTransporte}</strong>,` : ''} para un total mensual de <strong>$${datosImprimir.salarioTotal}</strong> (${datosImprimir.salarioEnLetras} PESOS M/CTE).</p>
            `}
            
            <p>La presente certificación se expide a solicitud del interesado para los fines que estime conveniente.</p>
          </div>

          ${conFirma && datosImprimir.firmaRepresentante ? `
          <!-- FIRMA CON IMAGEN ELECTRÓNICA -->
          <div class="firma" style="margin-top: 30px;">
            <div style="
              padding: 10px;
              border: 1px solid #6b7280;
              border-radius: 8px;
              background: linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%);
              max-width: 280px;
              display: inline-block;
            ">
              <div style="text-align: center; margin-bottom: 6px;">
                <span style="
                  display: inline-block;
                  background: linear-gradient(135deg, #4b5563 0%, #6b7280 100%);
                  color: white;
                  padding: 2px 8px;
                  border-radius: 10px;
                  font-size: 7px;
                  font-weight: 700;
                ">
                  ✓ FIRMA ELECTRÓNICA
                </span>
              </div>
              <div style="text-align: center; padding: 6px; background: white; border-radius: 6px; border: 1px solid #d1d5db; margin-bottom: 6px;">
                <img src="${(() => {
                  try {
                    const firmaData = typeof datosImprimir.firmaRepresentante === 'string' 
                      ? JSON.parse(datosImprimir.firmaRepresentante) 
                      : datosImprimir.firmaRepresentante;
                    return firmaData.firma || firmaData.imagen || '';
                  } catch(e) {
                    return datosImprimir.firmaRepresentante;
                  }
                })()}" alt="Firma Representante" style="max-width: 150px; max-height: 50px; filter: grayscale(100%);"/>
              </div>
              <div style="text-align: center;">
                <div style="font-weight: 700; font-size: 9px; color: #000;">${datosImprimir.representanteLegal}</div>
                <div style="font-size: 8px; color: #374151;">Representante Legal</div>
                <div style="font-size: 7px; color: #374151;">${datosImprimir.tipoDocRepresentante} ${datosImprimir.cedulaRepresentante}</div>
                <div style="font-size: 7px; color: #374151;">NIT ${datosImprimir.nitSede}</div>
              </div>
            </div>
          </div>
          <div style="margin-top: 20px; font-size: 12pt; color: #000; text-align: center;">
            <strong>Dirección:</strong> ${datosImprimir.direccionSede} &nbsp;&nbsp;|&nbsp;&nbsp; <strong>Teléfono:</strong> ${datosImprimir.telefonoSede}
          </div>
          ` : `
          <!-- FIRMA ESTÁTICA (sin imagen) -->
          <div class="firma">
            <div class="linea-firma"></div>
            <div class="nombre-firma">${datosImprimir.representanteLegal}</div>
            <div class="cargo-firma">Representante Legal</div>
            <div class="cargo-firma">${datosImprimir.tipoDocRepresentante} ${datosImprimir.cedulaRepresentante}</div>
            <div class="cargo-firma">NIT ${datosImprimir.nitSede}</div>
          </div>
          <div style="margin-top: 20px; font-size: 12pt; color: #000; text-align: center;">
            <strong>Dirección:</strong> ${datosImprimir.direccionSede} &nbsp;&nbsp;|&nbsp;&nbsp; <strong>Teléfono:</strong> ${datosImprimir.telefonoSede}
          </div>
          `}
          
          <div class="footer-print">Copia Controlada</div>
        </body>
      </html>
    `);
    ventanaImpresion.document.close();
    
    // Esperar a que el documento esté completamente cargado antes de imprimir
    ventanaImpresion.onload = () => {
      setTimeout(() => {
        ventanaImpresion.focus();
        ventanaImpresion.print();
      }, 500);
    };
    
    // Fallback por si onload no se dispara correctamente
    setTimeout(() => {
      if (ventanaImpresion && !ventanaImpresion.closed) {
        ventanaImpresion.focus();
        ventanaImpresion.print();
      }
    }, 1000);
  };

  // Eliminar una certificación
  const eliminarCertificacion = async (certId) => {
    if (!window.confirm("¿Estás seguro que deseas eliminar esta certificación?")) return;
    const exito = await eliminarCertificacionEnSupabase(certId);
    if (!exito) {
      alert("No se pudo eliminar la certificación en Supabase.");
      return;
    }
    setCertificacionesGuardadas((prev) => prev.filter((c) => c.id !== certId));
    alert("Certificación eliminada");
  };

  // Filtrar certificaciones por empleado
  const certificacionesFiltradas = useMemo(() => {
    let lista = [...certificacionesGuardadas];
    if (filtroEmpleado) {
      lista = lista.filter((c) => c.empleadoId === filtroEmpleado);
    }
    return lista.sort((a, b) => {
      const fechaB = new Date(b.createdAt || b.fechaGeneracion || 0).getTime();
      const fechaA = new Date(a.createdAt || a.fechaGeneracion || 0).getTime();
      return fechaB - fechaA;
    });
  }, [certificacionesGuardadas, filtroEmpleado]);

  return (
    <div style={{ padding: '16px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2 style={{
        fontSize: '18px',
        fontWeight: '700',
        color: '#ff6b35',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        borderBottom: '3px solid #ffc53d',
        paddingBottom: '10px'
      }}>📄 Certificaciones Laborales</h2>
      
      {/* TABS */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '3px solid #ffe4c9' }}>
        <button 
          onClick={() => setTab("generar")}
          style={{
            padding: '12px 24px',
            backgroundColor: tab === "generar" ? '#ff6b35' : '#fffbf5',
            color: tab === "generar" ? 'white' : '#ff6b35',
            border: tab === "generar" ? 'none' : '2px solid #ffc53d',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px',
          }}
        >
          ➕ Generar Certificación
        </button>
        <button 
          onClick={() => setTab("consultar")}
          style={{
            padding: '12px 24px',
            backgroundColor: tab === "consultar" ? '#ff6b35' : '#fffbf5',
            color: tab === "consultar" ? 'white' : '#ff6b35',
            border: tab === "consultar" ? 'none' : '2px solid #ffc53d',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px',
          }}
        >
          📚 Historial
        </button>
      </div>

      {/* TAB GENERAR */}
      {tab === "generar" && (
        <>
          <div style={{ backgroundColor: '#fffbf5', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '2px solid #ffe4c9' }}>
            
            {/* SELECTOR DE TIPO DE EMPLEADO */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>👥 Tipo de Empleado:</label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setTipoEmpleado("activo")}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    border: tipoEmpleado === "activo" ? 'none' : '2px solid #ffc53d',
                    borderRadius: '8px',
                    backgroundColor: tipoEmpleado === "activo" ? '#52c41a' : '#fffbf5',
                    color: tipoEmpleado === "activo" ? 'white' : '#52c41a',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  ✅ Empleados Activos
                </button>
                <button
                  onClick={() => setTipoEmpleado("inactivo")}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    border: tipoEmpleado === "inactivo" ? 'none' : '2px solid #ffc53d',
                    borderRadius: '8px',
                    backgroundColor: tipoEmpleado === "inactivo" ? '#ff4d4f' : '#fffbf5',
                    color: tipoEmpleado === "inactivo" ? 'white' : '#ff4d4f',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  ❌ Empleados Inactivos (Retirados)
                </button>
              </div>
              {tipoEmpleado === "inactivo" && (
                <div style={{ 
                  marginTop: '10px', 
                  padding: '10px 14px', 
                  backgroundColor: '#fff1f0', 
                  border: '1px solid #ffa39e',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: '#cf1322'
                }}>
                  ⚠️ <strong>Certificación para ex-empleados:</strong> Se generará una certificación que indica que el empleado <strong>laboró</strong> en la empresa, incluyendo fecha de ingreso y fecha de retiro.
                </div>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>🏢 Sede:</label>
              <select 
                value={sedeSeleccionada} 
                onChange={(e) => setSedeSeleccionada(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                  cursor: 'pointer',
                }}
              >
                <option value="">Seleccione una sede</option>
                {sedes.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.nombre || s.razonSocial}
                  </option>
                ))}
              </select>
            </div>

            {/* Solo mostrar selector de salario para empleados activos */}
            {tipoEmpleado === "activo" && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>💵 Tipo de Salario:</label>
              <select 
                value={tipoSalario} 
                onChange={(e) => setTipoSalario(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                  cursor: 'pointer',
                }}
              >
                <option value="base">💰 Salario Base</option>
                <option value="promedio">📊 Promedio Últimos 6 Meses</option>
              </select>
            </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#ff6b35', marginBottom: '6px' }}>👤 Empleado:</label>
              <select 
                value={empleadoSeleccionado} 
                onChange={(e) => setEmpleadoSeleccionado(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '13px',
                  border: '2px solid #ffc53d',
                  borderRadius: '8px',
                  backgroundColor: '#fffbf5',
                  cursor: 'pointer',
                }}
              >
                <option value="">Seleccione un empleado</option>
                {empleadosFiltrados.length === 0 ? (
                  <option value="" disabled>No hay empleados {tipoEmpleado === "activo" ? "activos" : "inactivos"}</option>
                ) : (
                  [...empleadosFiltrados].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nombre} - {emp.documento}
                    </option>
                  ))
                )}
              </select>
              {tipoEmpleado === "inactivo" && empleadosFiltrados.length === 0 && (
                <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#999' }}>
                  💡 No hay empleados inactivos registrados. Los empleados inactivos son aquellos con estado "activo = false" en la base de datos.
                </p>
              )}
            </div>

            {empleado && (
              <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px', border: '2px solid #ffc53d', marginTop: '12px' }}>
                <h3 style={{ marginTop: 0, marginBottom: '12px', color: '#ff6b35', fontSize: '14px', fontWeight: '700' }}>
                  📝 Datos del Empleado {tipoEmpleado === "inactivo" && <span style={{ color: '#ff4d4f' }}>(RETIRADO)</span>}:
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                  <p style={{ margin: '4px 0' }}><strong style={{ color: '#ff6b35' }}>Tipo de Documento:</strong> {empleado.tipoDoc || "N/A"}</p>
                  <p style={{ margin: '4px 0' }}><strong style={{ color: '#ff6b35' }}>Número de Documento:</strong> {empleado.documento || "N/A"}</p>
                  <p style={{ margin: '4px 0' }}><strong style={{ color: '#ff6b35' }}>Tipo de Contrato:</strong> {empleado.tipoContrato || "N/A"}</p>
                  <p style={{ margin: '4px 0' }}><strong style={{ color: '#ff6b35' }}>Fecha de Ingreso:</strong> {empleado.fechaIngreso || "N/A"}</p>
                  {tipoEmpleado === "inactivo" && (
                    <p style={{ margin: '4px 0', backgroundColor: '#fff1f0', padding: '6px 10px', borderRadius: '6px' }}>
                      <strong style={{ color: '#cf1322' }}>Fecha de Retiro:</strong> <span style={{ color: '#cf1322', fontWeight: '600' }}>{empleado.fechaRetiro || "N/A"}</span>
                    </p>
                  )}
                  <p style={{ margin: '4px 0' }}><strong style={{ color: '#ff6b35' }}>Cargo:</strong> {empleado.cargo || "N/A"}</p>
                  {tipoEmpleado === "activo" && (
                    <>
                      <p style={{ margin: '4px 0' }}><strong style={{ color: '#ff6b35' }}>Salario Base:</strong> <span style={{ color: '#52c41a', fontWeight: '600' }}>${empleado.salarioBase?.toLocaleString('es-CO') || "N/A"}</span></p>
                      <p style={{ margin: '4px 0' }}><strong style={{ color: '#ff6b35' }}>Salario Promedio 6m:</strong> <span style={{ color: '#52c41a', fontWeight: '600' }}>${empleado.salarioProm6m?.toLocaleString('es-CO') || "N/A"}</span></p>
                      <p style={{ margin: '4px 0', backgroundColor: '#fff3e0', padding: '6px 10px', borderRadius: '6px' }}><strong style={{ color: '#ff6b35' }}>Salario Seleccionado:</strong> <span style={{ color: '#52c41a', fontWeight: '700' }}>${(tipoSalario === "promedio" ? empleado.salarioProm6m : empleado.salarioBase)?.toLocaleString('es-CO') || "N/A"}</span></p>
                    </>
                  )}
                </div>
              </div>
            )}

            {sede && (
              <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px', border: '2px solid #ffc53d', marginTop: '12px' }}>
                <h3 style={{ marginTop: 0, marginBottom: '12px', color: '#ff6b35', fontSize: '14px', fontWeight: '700' }}>🏢 Datos de la Sede:</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                  <p style={{ margin: '4px 0' }}><strong style={{ color: '#ff6b35' }}>Razón Social:</strong> {sede.razonSocial || "N/A"}</p>
                  <p style={{ margin: '4px 0' }}><strong style={{ color: '#ff6b35' }}>NIT:</strong> {sede.nit || "N/A"}</p>
                  <p style={{ margin: '4px 0' }}><strong style={{ color: '#ff6b35' }}>Representante Legal:</strong> {sede.representanteLegal || "N/A"}</p>
                  <p style={{ margin: '4px 0' }}><strong style={{ color: '#ff6b35' }}>Tipo Doc. Representante:</strong> {sede.tipoDocRepresentante || "Cédula de Ciudadanía"}</p>
                  <p style={{ margin: '4px 0' }}><strong style={{ color: '#ff6b35' }}>N° Doc. Representante:</strong> {sede.cedulaRepresentante || "N/A"}</p>
                  <p style={{ margin: '4px 0' }}><strong style={{ color: '#ff6b35' }}>Dirección:</strong> {sede.direccion || "N/A"}</p>
                  <p style={{ margin: '4px 0' }}><strong style={{ color: '#ff6b35' }}>Teléfono:</strong> {sede.telefono || "N/A"}</p>
                  <p style={{ margin: '4px 0', gridColumn: '1 / -1' }}>
                    <strong style={{ color: '#ff6b35' }}>✍️ Firma Electrónica:</strong>{' '}
                    {sede.firmaElectronica ? (
                      <span style={{ 
                        backgroundColor: '#f0fdf4', 
                        color: '#16a34a', 
                        padding: '4px 10px', 
                        borderRadius: '12px',
                        fontWeight: '600',
                        fontSize: '11px'
                      }}>
                        ✅ Registrada
                      </span>
                    ) : (
                      <span style={{ 
                        backgroundColor: '#fef2f2', 
                        color: '#dc2626', 
                        padding: '4px 10px', 
                        borderRadius: '12px',
                        fontWeight: '600',
                        fontSize: '11px'
                      }}>
                        ❌ No registrada - Configúrela en Sedes
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
            {tienePermiso("rh_certificaciones_generar") && (
            <button 
              onClick={generarCertificacion} 
              style={{ 
                padding: '12px 24px',
                fontSize: '13px',
                fontWeight: '600',
                border: 'none',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                color: 'white',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
              }}
            >
              📝 Generar Certificación
            </button>
            )}
            {tienePermiso("rh_certificaciones_guardar") && (
            <button 
              onClick={async () => {
                if (!datosCompletos) {
                  alert("Primero genera una certificación");
                  return;
                }
                await guardarCertificacion(datosCompletos);
              }} 
              style={{ 
                padding: '12px 24px',
                fontSize: '13px',
                fontWeight: '600',
                border: 'none',
                borderRadius: '8px',
                background: (!datosCompletos || guardandoCertificacion) ? '#ccc' : 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)',
                color: 'white',
                cursor: guardandoCertificacion || !datosCompletos ? 'not-allowed' : 'pointer',
                boxShadow: (!datosCompletos || guardandoCertificacion) ? 'none' : '0 2px 8px rgba(255, 107, 53, 0.3)',
              }}
              disabled={guardandoCertificacion || !datosCompletos}
            >
              {guardandoCertificacion ? '⏳ Guardando…' : '💾 Guardar Certificación'}
            </button>
            )}
            {tienePermiso("rh_certificaciones_imprimir") && (
            <>
              <button 
                onClick={() => imprimirCertificacion(null, true)} 
                style={{ 
                  padding: '12px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '8px',
                  background: sede?.firmaElectronica ? 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)' : '#ccc',
                  color: 'white',
                  cursor: sede?.firmaElectronica ? 'pointer' : 'not-allowed',
                  boxShadow: sede?.firmaElectronica ? '0 2px 8px rgba(82, 196, 26, 0.3)' : 'none',
                }}
                disabled={!sede?.firmaElectronica}
                title={!sede?.firmaElectronica ? "La sede no tiene firma electrónica registrada" : "Imprimir con firma electrónica del representante"}
              >
                ✍️ Imprimir con Firma
              </button>
              <button 
                onClick={() => imprimirCertificacion(null, false)} 
                style={{ 
                  padding: '12px 24px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(21, 101, 192, 0.3)',
                }}
              >
                🖨️ Imprimir sin Firma
              </button>
            </>
            )}
          </div>

          {datosCompletos && (
            <div style={{ 
              backgroundColor: 'white', 
              padding: '40px', 
              border: '3px solid #ffc53d', 
              borderRadius: '12px',
              boxShadow: '0 4px 16px rgba(255, 107, 53, 0.15)',
              fontFamily: 'Times New Roman, serif',
              lineHeight: '1.8'
            }}>
              <div style={{ textAlign: 'left', marginBottom: '20px', color: '#666' }}>Pereira, {datosCompletos.fechaEmision}</div>

              <h3 style={{ textAlign: 'center', margin: '30px 0', textDecoration: 'underline' }}>CERTIFICACIÓN LABORAL</h3>

              <div style={{ textAlign: 'justify', margin: '30px 0' }}>
                <p>{datosCompletos.generoRepresentante === "Femenino" ? "La suscrita" : "El suscrito"} <strong>{datosCompletos.representanteLegal}</strong>, en calidad de Representante Legal de <strong>{datosCompletos.razonSocial}</strong>, identificad{datosCompletos.generoRepresentante === "Femenino" ? "a" : "o"} con NIT <strong>{datosCompletos.nitSede}</strong>,</p>
                
                <p style={{ textAlign: 'center', margin: '30px 0' }}><strong>CERTIFICA QUE:</strong></p>
                
                {datosCompletos.esEmpleadoInactivo ? (
                  <>
                    <p>El (la) Señor(a) <strong>{datosCompletos.nombreEmpleado}</strong>, identificado(a) con <strong>{datosCompletos.tipoDoc === 'CC' ? 'Cédula de Ciudadanía' : datosCompletos.tipoDoc === 'CE' ? 'Cédula de Extranjería' : datosCompletos.tipoDoc === 'TI' ? 'Tarjeta de Identidad' : datosCompletos.tipoDoc === 'PA' ? 'Pasaporte' : datosCompletos.tipoDoc === 'PEP' ? 'Permiso Especial de Permanencia' : datosCompletos.tipoDoc || 'Cédula de Ciudadanía'} No. {datosCompletos.documento}</strong>, laboró en nuestra empresa desde el <strong>{datosCompletos.fechaIngreso}</strong> hasta el <strong>{datosCompletos.fechaRetiro || "N/A"}</strong>, con un contrato <strong>{datosCompletos.tipoContrato}</strong>, desempeñando el cargo de <strong>{datosCompletos.cargo}</strong>.</p>
                    <p>A la fecha de retiro, el (la) trabajador(a) se encontraba a paz y salvo con la empresa.</p>
                  </>
                ) : (
                  <p>El (la) Señor(a) <strong>{datosCompletos.nombreEmpleado}</strong>, identificado(a) con <strong>{datosCompletos.tipoDoc} {datosCompletos.documento}</strong>, labora en nuestra empresa desde el <strong>{datosCompletos.fechaIngreso}</strong>, con un contrato <strong>{datosCompletos.tipoContrato}</strong>, desempeñando el cargo de <strong>{datosCompletos.cargo}</strong>, devengando un {datosCompletos.salarioTexto} de <strong>${datosCompletos.salarioBase}</strong>, más auxilio de transporte de <strong>${datosCompletos.auxilioTransporte}</strong>, para un total mensual de <strong>${datosCompletos.salarioTotal}</strong> ({datosCompletos.salarioEnLetras} PESOS M/CTE).</p>
                )}
                
                <p>La presente certificación se expide a solicitud del interesado para los fines que estime conveniente.</p>
              </div>

              <div style={{ marginTop: '80px', textAlign: 'left' }}>
                <div style={{ borderTop: '1px solid black', width: '250px', margin: '0 0 10px 0' }}></div>
                <div style={{ fontWeight: 'bold', margin: '0' }}>{datosCompletos.representanteLegal}</div>
                <div style={{ margin: '0' }}>Representante Legal</div>
                <div style={{ margin: '0' }}>NIT {datosCompletos.nitSede}</div>
                <div style={{ margin: '0' }}>Dirección: {datosCompletos.direccionSede}</div>
                <div style={{ margin: '0' }}>Teléfono: {datosCompletos.telefonoSede}</div>
              </div>
            </div>
          )}
        </>
      )}

      {/* TAB CONSULTAR */}
      {tab === "consultar" && (
        <>
          <div style={{ 
            backgroundColor: '#fffbf5', 
            padding: '20px', 
            borderRadius: '12px', 
            marginBottom: '20px',
            border: '2px solid #ffe4c9'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '10px', 
                fontWeight: '600',
                color: '#ff6b35',
                fontSize: '14px'
              }}>
                🔍 Filtrar por Empleado:
              </label>
              <select 
                value={filtroEmpleado} 
                onChange={(e) => setFiltroEmpleado(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '12px 14px', 
                  fontSize: '14px', 
                  borderRadius: '8px', 
                  border: '2px solid #ffe4c9',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">👥 Todos los empleados</option>
                {[...(empleados || [])].sort((a, b) => ((a.nombre || "").localeCompare((b.nombre || ""), 'es', { sensitivity: 'base' }))).map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.nombre} - {emp.documento}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {cargandoCertificaciones ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              backgroundColor: '#fff5eb', 
              borderRadius: '12px',
              border: '2px solid #ffe4c9'
            }}>
              <p style={{ color: '#ff6b35', fontSize: '16px', fontWeight: '500' }}>⏳ Cargando certificaciones…</p>
            </div>
          ) : certificacionesFiltradas.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              backgroundColor: '#fff5eb', 
              borderRadius: '12px',
              border: '2px solid #ffe4c9'
            }}>
              <p style={{ color: '#666', fontSize: '16px' }}>📝 No hay certificaciones guardadas.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', borderRadius: '12px', border: '2px solid #ffe4c9' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' }}>
                    <th style={{ padding: '14px 12px', textAlign: 'left', color: 'white', fontWeight: '600', fontSize: '13px' }}>👤 Empleado</th>
                    <th style={{ padding: '14px 12px', textAlign: 'left', color: 'white', fontWeight: '600', fontSize: '13px' }}>🏢 Sede</th>
                    <th style={{ padding: '14px 12px', textAlign: 'left', color: 'white', fontWeight: '600', fontSize: '13px' }}>💰 Tipo Salario</th>
                    <th style={{ padding: '14px 12px', textAlign: 'left', color: 'white', fontWeight: '600', fontSize: '13px' }}>📅 Fecha Generación</th>
                    <th style={{ padding: '14px 12px', textAlign: 'center', color: 'white', fontWeight: '600', fontSize: '13px' }}>⚙️ Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {certificacionesFiltradas.map((cert, idx) => (
                    <tr key={cert.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#fffbf5' }}>
                      <td style={{ padding: '12px', borderBottom: '1px solid #ffe4c9', fontWeight: '500' }}>{cert.empleadoNombre}</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #ffe4c9' }}>{cert.sedeName}</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #ffe4c9' }}>
                        <span style={{ 
                          backgroundColor: cert.tipoSalario === 'promedio' ? '#fff3e0' : '#f5f5f5', 
                          color: cert.tipoSalario === 'promedio' ? '#ff6b35' : '#666',
                          padding: '4px 10px', 
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {cert.tipoSalario === 'promedio' ? '📊 Promedio 6m' : '💵 Base'}
                        </span>
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #ffe4c9', color: '#666' }}>{cert.fechaGeneracion}</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #ffe4c9', textAlign: 'center' }}>
                        <button 
                          onClick={() => imprimirCertificacion(cert.datos, true)}
                          style={{ 
                            background: cert.datos?.firmaRepresentante ? 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)' : '#ccc', 
                            color: 'white', 
                            padding: '8px 14px', 
                            border: 'none', 
                            borderRadius: '6px', 
                            cursor: cert.datos?.firmaRepresentante ? 'pointer' : 'not-allowed',
                            marginRight: '6px',
                            fontSize: '11px',
                            fontWeight: '500',
                            boxShadow: cert.datos?.firmaRepresentante ? '0 2px 4px rgba(82, 196, 26, 0.3)' : 'none'
                          }}
                          disabled={!cert.datos?.firmaRepresentante}
                          title={cert.datos?.firmaRepresentante ? "Imprimir con firma electrónica" : "Sin firma electrónica guardada"}
                        >
                          ✍️ Con Firma
                        </button>
                        <button 
                          onClick={() => imprimirCertificacion(cert.datos, false)}
                          style={{ 
                            background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)', 
                            color: 'white', 
                            padding: '8px 14px', 
                            border: 'none', 
                            borderRadius: '6px', 
                            cursor: 'pointer',
                            marginRight: '6px',
                            fontSize: '11px',
                            fontWeight: '500',
                            boxShadow: '0 2px 4px rgba(21, 101, 192, 0.3)'
                          }}
                        >
                          🖨️ Sin Firma
                        </button>
                        <button 
                          onClick={() => eliminarCertificacion(cert.id)}
                          style={{ 
                            background: 'linear-gradient(135deg, #ff4d4f 0%, #cf1322 100%)', 
                            color: 'white', 
                            padding: '8px 14px', 
                            border: 'none', 
                            borderRadius: '6px', 
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: '500',
                            boxShadow: '0 2px 4px rgba(255, 77, 79, 0.3)'
                          }}
                        >
                          🗑️ Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ====================== SOLICITUDES INTRANET ADMIN ====================== */
function SolicitudesIntranetAdmin({ tienePermiso = () => true, filtroEmpresa = null }) {
  const [solicitudes, setSolicitudes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('todas');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [respuesta, setRespuesta] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [archivosRespuesta, setArchivosRespuesta] = useState([]);
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);
  const [requiereConfirmacion, setRequiereConfirmacion] = useState(false); // Para propuestas que necesitan aprobación del empleado
  const [filtroRespuesta, setFiltroRespuesta] = useState('ninguna'); // 'ninguna', 'con_respuesta', 'sin_respuesta'
  const [filtroEmpleado, setFiltroEmpleado] = useState(''); // Filtro por documento o nombre de empleado
  const [mostrarListaEmpleados, setMostrarListaEmpleados] = useState(false); // Controlar lista desplegable
  const [empleadoSeleccionadoNombre, setEmpleadoSeleccionadoNombre] = useState(''); // Nombre del empleado seleccionado
  
  // Estados para bloqueos de solicitudes por fechas
  const [mostrarBloqueos, setMostrarBloqueos] = useState(false);
  const [bloqueos, setBloqueos] = useState([]);
  const [nuevoBloqueo, setNuevoBloqueo] = useState({
    tipo_solicitud: '',
    fecha_inicio: '',
    fecha_fin: '',
    motivo: '',
    activo: true,
    es_recurrente: false,
    dia_inicio: 1,
    dia_fin: 5
  });
  const [guardandoBloqueo, setGuardandoBloqueo] = useState(false);

  // Estados para bloqueos de módulos por empleado
  const [mostrarBloqueosEmpleado, setMostrarBloqueosEmpleado] = useState(false);
  const [bloqueosEmpleado, setBloqueosEmpleado] = useState([]);
  const [empleadosLista, setEmpleadosLista] = useState([]);
  const [busquedaEmpleado, setBusquedaEmpleado] = useState('');
  const [nuevoBloqueEmpleado, setNuevoBloqueEmpleado] = useState({
    documento_empleado: '',
    nombre_empleado: '',
    modulo: '',
    motivo: '',
    activo: true,
    es_recurrente: false,
    fecha_inicio: '',
    fecha_fin: '',
    dia_inicio: 1,
    dia_fin: 5
  });
  const [guardandoBloqueoEmpleado, setGuardandoBloqueoEmpleado] = useState(false);

  // Estados para configuración de notificaciones SMS (por empresa)
  const [mostrarConfigNotificaciones, setMostrarConfigNotificaciones] = useState(false);
  const [configNotificaciones, setConfigNotificaciones] = useState({
    telefono_solicitudes_1: '',
    nombre_receptor_1: '',
    telefono_solicitudes_2: '',
    nombre_receptor_2: ''
  });
  const [guardandoConfig, setGuardandoConfig] = useState(false);

  // === ESTADOS PARA SOLICITUDES DEL EMPLEADOR ===
  const [mostrarFormSolicitudEmpleador, setMostrarFormSolicitudEmpleador] = useState(false);
  const [solicitudesEmpleador, setSolicitudesEmpleador] = useState([]);
  const [nuevaSolicitudEmpleador, setNuevaSolicitudEmpleador] = useState({
    empleado_documento: '',
    empleado_nombre: '',
    empleado_telefono: '',
    asunto: '',
    descripcion: '',
    prioridad: 'normal',
    requiere_respuesta: true
  });
  const [archivosSolicitudEmpleador, setArchivosSolicitudEmpleador] = useState([]);
  const [guardandoSolicitudEmpleador, setGuardandoSolicitudEmpleador] = useState(false);
  const [subiendoArchivoEmpleador, setSubiendoArchivoEmpleador] = useState(false);
  const [filtroSolicitudesEmpleador, setFiltroSolicitudesEmpleador] = useState('pendientes'); // 'pendientes', 'respondidas', 'todas'
  const [solicitudEmpleadorSeleccionada, setSolicitudEmpleadorSeleccionada] = useState(null);
  const [vistaActiva, setVistaActiva] = useState('solicitudes_empleados'); // 'solicitudes_empleados' o 'solicitudes_empleador'


  // Cargar configuración de notificaciones SMS (desde tabla empresas)
  const cargarConfigNotificaciones = async () => {
    if (!filtroEmpresa) {
      console.warn("📱 No hay empresa seleccionada para cargar config SMS solicitudes");
      return;
    }
    try {
      const { data, error } = await supabase
        .from('empresas')
        .select('telefono_solicitudes_1, nombre_receptor_1, telefono_solicitudes_2, nombre_receptor_2')
        .eq('id', filtroEmpresa)
        .single();
      
      console.log("📱 Cargando config SMS solicitudes desde empresa:", filtroEmpresa, data);
      
      if (!error && data) {
        setConfigNotificaciones({
          telefono_solicitudes_1: data.telefono_solicitudes_1 || '',
          nombre_receptor_1: data.nombre_receptor_1 || '',
          telefono_solicitudes_2: data.telefono_solicitudes_2 || '',
          nombre_receptor_2: data.nombre_receptor_2 || ''
        });
      }
    } catch (error) {
      console.log('No se encontró configuración de notificaciones para esta empresa');
    }
  };

  // Guardar configuración de notificaciones SMS (en tabla empresas)
  const guardarConfigNotificaciones = async () => {
    if (!filtroEmpresa) {
      alert("❌ No hay empresa seleccionada");
      return;
    }
    setGuardandoConfig(true);
    try {
      const { error } = await supabase
        .from('empresas')
        .update({
          telefono_solicitudes_1: configNotificaciones.telefono_solicitudes_1 || null,
          nombre_receptor_1: configNotificaciones.nombre_receptor_1 || null,
          telefono_solicitudes_2: configNotificaciones.telefono_solicitudes_2 || null,
          nombre_receptor_2: configNotificaciones.nombre_receptor_2 || null
        })
        .eq('id', filtroEmpresa);
      
      if (error) {
        console.error("Error actualizando empresa:", error);
        throw error;
      }
      
      console.log("✅ Config SMS solicitudes guardada en empresa:", filtroEmpresa, configNotificaciones);
      alert('✅ Configuración guardada correctamente');
      setMostrarConfigNotificaciones(false);
    } catch (error) {
      console.error('Error guardando configuración:', error);
      alert('❌ Error al guardar: ' + error.message);
    }
    setGuardandoConfig(false);
  };

  // Módulos de la Intranet que se pueden bloquear
  const modulosIntranet = [
    { id: 'desprendible', nombre: 'Desprendible de Pago', icono: '💰' },
    { id: 'prestamos', nombre: 'Préstamos/Adelantos', icono: '💳' },
    { id: 'carta_laboral', nombre: 'Carta Laboral', icono: '📄' },
    { id: 'contrato', nombre: 'Contrato de Trabajo', icono: '📋' },
    { id: 'horarios', nombre: 'Mis Horarios', icono: '🕐' },
    { id: 'solicitudes', nombre: 'Radicar Solicitud', icono: '📝' },
    { id: 'actualizar_datos', nombre: 'Actualizar Mis Datos', icono: '👤' },
    { id: 'reglamento', nombre: 'Reglamento Interno', icono: '📖' },
    { id: 'formatos', nombre: 'Formatos', icono: '📁' },
  ];

  const tiposSolicitud = [
    { id: 'incapacidad_permiso', nombre: 'Incapacidad/Permiso', icono: '🏥' },
    { id: 'permiso', nombre: 'Permiso', icono: '🙋' },
    { id: 'vacaciones', nombre: 'Vacaciones', icono: '🏖️' },
    { id: 'adelanto_nomina', nombre: 'Adelanto de Nómina', icono: '💰' },
    { id: 'cambio_eps', nombre: 'Cambio de EPS', icono: '🏥' },
    { id: 'documentos_vinculacion', nombre: 'Documentos Vinculación', icono: '📁' },
    { id: 'documentos_actualizacion', nombre: 'Docs. Actualización', icono: '🔄' },
    { id: 'cambio_horario', nombre: 'Cambio de Horario', icono: '🕐' },
    { id: 'certificado', nombre: 'Certificado Laboral', icono: '📄' },
    { id: 'incapacidad', nombre: 'Incapacidad', icono: '🩺' },
    { id: 'otro', nombre: 'Otra Solicitud', icono: '📝' },
  ];

  // Cargar solicitudes al montar
  useEffect(() => {
    cargarSolicitudes();
    cargarBloqueos();
    cargarBloqueosEmpleado();
    cargarEmpleados();
    cargarConfigNotificaciones();
  }, [filtroEmpresa]);

  // Cargar lista de empleados
  const cargarEmpleados = async () => {
    try {
      // Usar los campos correctos de la tabla: nombres, apellidos (no nombre, apellido)
      let query = supabase
        .from('empleados')
        .select('documento, nombres, apellidos, cargo, sede, empresa_id')
        .order('nombres');
      
      // Filtrar por empresa si hay filtro activo
      if (filtroEmpresa) {
        query = query.eq('empresa_id', filtroEmpresa);
      }
      
      const { data, error } = await query;
      
      console.log('📋 Empleados cargados:', data?.length, error);
      if (!error && data) {
        // Mapear a formato esperado (nombre = nombres + apellidos)
        const empleadosMapeados = data.map(emp => ({
          documento: emp.documento,
          nombre: emp.nombres || '',
          apellido: emp.apellidos || '',
          cargo: emp.cargo || '',
          sede: emp.sede || ''
        }));
        setEmpleadosLista(empleadosMapeados);
      } else if (error) {
        console.error('Error en query empleados:', error);
      }
    } catch (error) {
      console.error('Error cargando empleados:', error);
    }
  };

  // Cargar bloqueos de módulos por empleado
  const cargarBloqueosEmpleado = async () => {
    try {
      const { data, error } = await supabase
        .from('bloqueos_modulos_empleado')
        .select('*')
        .order('fecha_creacion', { ascending: false });
      
      if (error) throw error;
      setBloqueosEmpleado(data || []);
    } catch (error) {
      console.error('Error cargando bloqueos por empleado:', error);
    }
  };

  // Guardar bloqueo de módulo por empleado
  const guardarBloqueoEmpleado = async () => {
    if (!nuevoBloqueEmpleado.documento_empleado || !nuevoBloqueEmpleado.modulo) {
      alert('Por favor selecciona un empleado y un módulo');
      return;
    }

    // Validar según tipo de bloqueo
    if (nuevoBloqueEmpleado.es_recurrente) {
      if (!nuevoBloqueEmpleado.dia_inicio || !nuevoBloqueEmpleado.dia_fin) {
        alert('Por favor ingresa el día de inicio y fin del mes');
        return;
      }
      if (nuevoBloqueEmpleado.dia_inicio > nuevoBloqueEmpleado.dia_fin) {
        alert('El día de inicio no puede ser mayor al día fin');
        return;
      }
      if (nuevoBloqueEmpleado.dia_inicio < 1 || nuevoBloqueEmpleado.dia_fin > 31) {
        alert('Los días deben estar entre 1 y 31');
        return;
      }
    } else if (nuevoBloqueEmpleado.fecha_inicio || nuevoBloqueEmpleado.fecha_fin) {
      // Si tiene fechas específicas, validar
      if (!nuevoBloqueEmpleado.fecha_inicio || !nuevoBloqueEmpleado.fecha_fin) {
        alert('Por favor completa ambas fechas (inicio y fin)');
        return;
      }
      if (new Date(nuevoBloqueEmpleado.fecha_inicio) > new Date(nuevoBloqueEmpleado.fecha_fin)) {
        alert('La fecha de inicio no puede ser mayor a la fecha fin');
        return;
      }
    }

    setGuardandoBloqueoEmpleado(true);
    try {
      const bloqueoData = {
        documento_empleado: nuevoBloqueEmpleado.documento_empleado,
        nombre_empleado: nuevoBloqueEmpleado.nombre_empleado,
        modulo: nuevoBloqueEmpleado.modulo,
        motivo: nuevoBloqueEmpleado.motivo,
        activo: true,
        es_recurrente: nuevoBloqueEmpleado.es_recurrente,
        creado_por: 'admin',
        fecha_creacion: new Date().toISOString()
      };

      // Agregar campos de fecha según el tipo
      if (nuevoBloqueEmpleado.es_recurrente) {
        bloqueoData.dia_inicio = nuevoBloqueEmpleado.dia_inicio;
        bloqueoData.dia_fin = nuevoBloqueEmpleado.dia_fin;
        bloqueoData.fecha_inicio = null;
        bloqueoData.fecha_fin = null;
      } else if (nuevoBloqueEmpleado.fecha_inicio && nuevoBloqueEmpleado.fecha_fin) {
        bloqueoData.fecha_inicio = nuevoBloqueEmpleado.fecha_inicio;
        bloqueoData.fecha_fin = nuevoBloqueEmpleado.fecha_fin;
        bloqueoData.dia_inicio = null;
        bloqueoData.dia_fin = null;
      } else {
        // Bloqueo permanente (sin fechas)
        bloqueoData.fecha_inicio = null;
        bloqueoData.fecha_fin = null;
        bloqueoData.dia_inicio = null;
        bloqueoData.dia_fin = null;
      }
      
      console.log('📝 Guardando bloqueo:', bloqueoData);
      
      const { data, error } = await supabase
        .from('bloqueos_modulos_empleado')
        .insert([bloqueoData])
        .select();
      
      console.log('📝 Resultado guardar bloqueo:', { data, error });
      
      if (error) throw error;
      
      alert('Bloqueo guardado correctamente');
      setNuevoBloqueEmpleado({
        documento_empleado: '',
        nombre_empleado: '',
        modulo: '',
        motivo: '',
        activo: true,
        es_recurrente: false,
        fecha_inicio: '',
        fecha_fin: '',
        dia_inicio: 1,
        dia_fin: 5
      });
      setBusquedaEmpleado('');
      cargarBloqueosEmpleado();
    } catch (error) {
      console.error('Error guardando bloqueo:', error);
      alert('Error al guardar el bloqueo: ' + error.message);
    }
    setGuardandoBloqueoEmpleado(false);
  };

  // Eliminar bloqueo de empleado
  const eliminarBloqueoEmpleado = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este bloqueo?')) return;
    
    try {
      const { error } = await supabase
        .from('bloqueos_modulos_empleado')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      cargarBloqueosEmpleado();
    } catch (error) {
      console.error('Error eliminando bloqueo:', error);
      alert('Error al eliminar: ' + error.message);
    }
  };

  // Toggle bloqueo empleado activo/inactivo
  const toggleBloqueoEmpleadoActivo = async (bloqueo) => {
    try {
      const { error } = await supabase
        .from('bloqueos_modulos_empleado')
        .update({ activo: !bloqueo.activo })
        .eq('id', bloqueo.id);
      
      if (error) throw error;
      cargarBloqueosEmpleado();
    } catch (error) {
      console.error('Error actualizando bloqueo:', error);
    }
  };

  // Filtrar empleados para búsqueda
  const empleadosFiltrados = useMemo(() => {
    if (!busquedaEmpleado.trim()) {
      // Si no hay búsqueda, mostrar los primeros 20 empleados
      return empleadosLista.slice(0, 20);
    }
    const busqueda = busquedaEmpleado.toLowerCase();
    return empleadosLista.filter(e => 
      e.nombre?.toLowerCase().includes(busqueda) ||
      e.apellido?.toLowerCase().includes(busqueda) ||
      e.documento?.toString().includes(busqueda) ||
      e.cargo?.toLowerCase().includes(busqueda)
    ).slice(0, 20);
  }, [busquedaEmpleado, empleadosLista]);

  // Cargar bloqueos de solicitudes
  const cargarBloqueos = async () => {
    try {
      const { data, error } = await supabase
        .from('bloqueos_solicitudes')
        .select('*')
        .order('fecha_inicio', { ascending: false });
      
      if (error) throw error;
      setBloqueos(data || []);
    } catch (error) {
      console.error('Error cargando bloqueos:', error);
    }
  };

  // Guardar nuevo bloqueo
  const guardarBloqueo = async () => {
    if (!nuevoBloqueo.tipo_solicitud) {
      alert('Por favor selecciona el tipo de solicitud');
      return;
    }

    // Validar según tipo de bloqueo
    if (nuevoBloqueo.es_recurrente) {
      if (!nuevoBloqueo.dia_inicio || !nuevoBloqueo.dia_fin) {
        alert('Por favor ingresa el día de inicio y fin del mes');
        return;
      }
      if (nuevoBloqueo.dia_inicio > nuevoBloqueo.dia_fin) {
        alert('El día de inicio no puede ser mayor al día fin');
        return;
      }
      if (nuevoBloqueo.dia_inicio < 1 || nuevoBloqueo.dia_fin > 31) {
        alert('Los días deben estar entre 1 y 31');
        return;
      }
    } else {
      if (!nuevoBloqueo.fecha_inicio || !nuevoBloqueo.fecha_fin) {
        alert('Por favor completa fecha inicio y fecha fin');
        return;
      }
      if (new Date(nuevoBloqueo.fecha_inicio) > new Date(nuevoBloqueo.fecha_fin)) {
        alert('La fecha de inicio no puede ser mayor a la fecha fin');
        return;
      }
    }

    setGuardandoBloqueo(true);
    try {
      const dataToInsert = {
        tipo_solicitud: nuevoBloqueo.tipo_solicitud,
        motivo: nuevoBloqueo.motivo,
        activo: nuevoBloqueo.activo,
        es_recurrente: nuevoBloqueo.es_recurrente,
        creado_por: 'admin',
        fecha_creacion: new Date().toISOString()
      };

      if (nuevoBloqueo.es_recurrente) {
        dataToInsert.dia_inicio = nuevoBloqueo.dia_inicio;
        dataToInsert.dia_fin = nuevoBloqueo.dia_fin;
        // Para recurrentes, usamos fechas dummy para no tener null
        dataToInsert.fecha_inicio = '2000-01-01';
        dataToInsert.fecha_fin = '2099-12-31';
      } else {
        dataToInsert.fecha_inicio = nuevoBloqueo.fecha_inicio;
        dataToInsert.fecha_fin = nuevoBloqueo.fecha_fin;
        dataToInsert.dia_inicio = null;
        dataToInsert.dia_fin = null;
      }

      const { error } = await supabase
        .from('bloqueos_solicitudes')
        .insert([dataToInsert]);
      
      if (error) throw error;
      
      alert('Bloqueo guardado correctamente');
      setNuevoBloqueo({
        tipo_solicitud: '',
        fecha_inicio: '',
        fecha_fin: '',
        motivo: '',
        activo: true,
        es_recurrente: false,
        dia_inicio: 1,
        dia_fin: 5
      });
      cargarBloqueos();
    } catch (error) {
      console.error('Error guardando bloqueo:', error);
      alert('Error al guardar el bloqueo: ' + error.message);
    }
    setGuardandoBloqueo(false);
  };

  // Eliminar bloqueo
  const eliminarBloqueo = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este bloqueo?')) return;
    
    try {
      const { error } = await supabase
        .from('bloqueos_solicitudes')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      cargarBloqueos();
    } catch (error) {
      console.error('Error eliminando bloqueo:', error);
      alert('Error al eliminar: ' + error.message);
    }
  };

  // Alternar estado activo/inactivo del bloqueo
  const toggleBloqueoActivo = async (bloqueo) => {
    try {
      const { error } = await supabase
        .from('bloqueos_solicitudes')
        .update({ activo: !bloqueo.activo })
        .eq('id', bloqueo.id);
      
      if (error) throw error;
      cargarBloqueos();
    } catch (error) {
      console.error('Error actualizando bloqueo:', error);
    }
  };

  const cargarSolicitudes = async () => {
    setCargando(true);
    try {
      let query = supabase
        .from('solicitudes_empleados')
        .select('*')
        .order('fecha_creacion', { ascending: false });
      
      if (filtroEmpresa) {
        query = query.eq('empresa_id', filtroEmpresa);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      setSolicitudes(data || []);
    } catch (error) {
      console.error('Error cargando solicitudes:', error);
    }
    setCargando(false);
  };

  // === FUNCIONES PARA SOLICITUDES DEL EMPLEADOR ===
  
  // Cargar solicitudes del empleador
  const cargarSolicitudesEmpleador = async () => {
    try {
      let query = supabase
        .from('solicitudes_empleador')
        .select('*')
        .order('fecha_creacion', { ascending: false });
      
      if (filtroEmpresa) {
        query = query.eq('empresa_id', filtroEmpresa);
      }
      
      const { data, error } = await query;
      
      if (error) {
        // Si la tabla no existe, no mostrar error
        if (error.code === '42P01') {
          console.log('⚠️ Tabla solicitudes_empleador no existe aún');
          return;
        }
        throw error;
      }
      setSolicitudesEmpleador(data || []);
    } catch (error) {
      console.error('Error cargando solicitudes del empleador:', error);
    }
  };

  // Subir archivo para solicitud del empleador
  const subirArchivoSolicitudEmpleador = async (archivo) => {
    setSubiendoArchivoEmpleador(true);
    try {
      const nombreArchivo = `solicitudes_empleador/${Date.now()}_${archivo.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const { error } = await supabase.storage
        .from('empleados-docs')
        .upload(nombreArchivo, archivo);
      
      if (error) throw error;
      
      const { data: urlData } = supabase.storage
        .from('empleados-docs')
        .getPublicUrl(nombreArchivo);
      
      setArchivosSolicitudEmpleador(prev => [...prev, {
        nombre: archivo.name,
        url: urlData.publicUrl
      }]);
    } catch (error) {
      console.error('Error subiendo archivo:', error);
      alert('Error al subir el archivo');
    }
    setSubiendoArchivoEmpleador(false);
  };

  // Guardar solicitud del empleador
  const guardarSolicitudEmpleador = async () => {
    if (!nuevaSolicitudEmpleador.empleado_documento) {
      alert('Por favor selecciona un empleado');
      return;
    }
    if (!nuevaSolicitudEmpleador.asunto.trim()) {
      alert('Por favor ingresa el asunto de la solicitud');
      return;
    }
    if (!nuevaSolicitudEmpleador.descripcion.trim()) {
      alert('Por favor ingresa la descripción de la solicitud');
      return;
    }

    setGuardandoSolicitudEmpleador(true);
    try {
      const solicitudData = {
        id: crypto.randomUUID(),
        empleado_documento: nuevaSolicitudEmpleador.empleado_documento,
        empleado_nombre: nuevaSolicitudEmpleador.empleado_nombre,
        asunto: nuevaSolicitudEmpleador.asunto.trim(),
        descripcion: nuevaSolicitudEmpleador.descripcion.trim(),
        prioridad: nuevaSolicitudEmpleador.prioridad,
        requiere_respuesta: nuevaSolicitudEmpleador.requiere_respuesta,
        archivos_adjuntos: archivosSolicitudEmpleador.length > 0 ? JSON.stringify(archivosSolicitudEmpleador) : null,
        estado: 'pendiente',
        fecha_creacion: new Date().toISOString(),
        empresa_id: filtroEmpresa,
        creado_por: 'admin'
      };

      const { data, error } = await supabase
        .from('solicitudes_empleador')
        .insert([solicitudData])
        .select();

      if (error) throw error;

      // 📱 Enviar SMS al empleado notificando la solicitud
      if (nuevaSolicitudEmpleador.empleado_telefono) {
        try {
          const resultado = await notificarSolicitudEmpleador(
            { telefono: nuevaSolicitudEmpleador.empleado_telefono, nombre: nuevaSolicitudEmpleador.empleado_nombre },
            { asunto: nuevaSolicitudEmpleador.asunto }
          );
          if (resultado?.ok) {
            console.log('📱 SMS enviado al empleado:', nuevaSolicitudEmpleador.empleado_nombre);
          } else {
            console.warn('📱 No se pudo enviar SMS:', resultado?.error);
          }
        } catch (smsErr) {
          console.warn('Error enviando SMS:', smsErr);
        }
      }

      alert('✅ Solicitud enviada correctamente al empleado');
      
      // Limpiar formulario
      setNuevaSolicitudEmpleador({
        empleado_documento: '',
        empleado_nombre: '',
        empleado_telefono: '',
        asunto: '',
        descripcion: '',
        prioridad: 'normal',
        requiere_respuesta: true
      });
      setArchivosSolicitudEmpleador([]);
      setMostrarFormSolicitudEmpleador(false);
      cargarSolicitudesEmpleador();
    } catch (error) {
      console.error('Error guardando solicitud:', error);
      alert('❌ Error al enviar la solicitud: ' + (error.message || 'Error desconocido'));
    }
    setGuardandoSolicitudEmpleador(false);
  };

  // Cargar solicitudes del empleador al montar
  useEffect(() => {
    cargarSolicitudesEmpleador();
  }, [filtroEmpresa]);

  const responderSolicitud = async (estado) => {
    if (!solicitudSeleccionada) return;
    if (estado === 'negado' && !respuesta.trim()) {
      alert('Por favor ingresa un motivo para el rechazo');
      return;
    }
    
    setProcesando(true);
    try {
      // Si requiere confirmación, mantener en_proceso hasta que RRHH dé respuesta definitiva
      const estadoFinal = requiereConfirmacion ? 'en_proceso' : estado;
      
      // Obtener historial existente
      const historialExistente = solicitudSeleccionada.historial_conversacion || [];
      
      // Crear nuevo mensaje para el historial
      const nuevoMensaje = {
        tipo: 'admin',
        mensaje: respuesta.trim() || (requiereConfirmacion ? 'Propuesta enviada - Esperando respuesta' : estado === 'aprobado' ? 'Solicitud aprobada' : estado === 'negado' ? 'Solicitud rechazada' : 'Solicitud en proceso'),
        archivos: archivosRespuesta,
        fecha: new Date().toISOString(),
        estado: estadoFinal,
        requiere_confirmacion: requiereConfirmacion
      };
      
      const updateData = {
        estado: estadoFinal,
        requiere_confirmacion: requiereConfirmacion,
        historial_conversacion: [...historialExistente, nuevoMensaje]
      };
      
      // Agregar respuesta/propuesta de RRHH
      if (respuesta.trim()) {
        updateData.respuesta = respuesta.trim();
      } else if (requiereConfirmacion) {
        updateData.respuesta = 'Propuesta enviada - Esperando respuesta del empleado';
      } else if (estado === 'aprobado') {
        updateData.respuesta = 'Solicitud aprobada';
      } else if (estado === 'en_proceso') {
        updateData.respuesta = 'Su solicitud está siendo procesada';
      }
      
      updateData.fecha_respuesta = new Date().toISOString();
      
      // Agregar archivos de respuesta si hay
      if (archivosRespuesta.length > 0) {
        updateData.archivos_respuesta = JSON.stringify(archivosRespuesta);
      }
      
      // Limpiar la respuesta del empleado si el admin responde nuevamente (para permitir nueva respuesta)
      if (solicitudSeleccionada.respuesta_empleado && requiereConfirmacion) {
        updateData.respuesta_empleado = null;
        updateData.fecha_respuesta_empleado = null;
        updateData.archivos_respuesta_empleado = null;
      }
      
      console.log('📤 Enviando actualización:', updateData);
      
      const { data, error } = await supabase
        .from('solicitudes_empleados')
        .update(updateData)
        .eq('id', solicitudSeleccionada.id)
        .select();
      
      if (error) throw error;
      
      const mensajes = {
        'en_proceso': requiereConfirmacion 
          ? '📨 Propuesta enviada. Esperando respuesta del empleado.' 
          : '⏳ Solicitud marcada como en proceso',
        'aprobado': '✅ Solicitud aprobada correctamente',
        'negado': '❌ Solicitud rechazada'
      };
      alert(mensajes[estadoFinal] || '✅ Solicitud actualizada');
      
      // 📱 Enviar notificación SMS al empleado sobre la respuesta
      const empleadoIdODocumento = solicitudSeleccionada.empleado_id || solicitudSeleccionada.documento || solicitudSeleccionada.usuario_id;
      if (empleadoIdODocumento) {
        try {
          // Buscar datos del empleado por id o documento
          let empData = null;
          
          // Primero intentar por id
          if (solicitudSeleccionada.empleado_id) {
            const { data } = await supabase
              .from('empleados')
              .select('telefono, nombres, apellidos')
              .eq('id', solicitudSeleccionada.empleado_id)
              .single();
            empData = data;
          }
          
          // Si no encontró, buscar por documento (convertir a string para evitar errores de tipo)
          if (!empData && solicitudSeleccionada.documento) {
            const documentoBuscar = String(solicitudSeleccionada.documento).trim();
            console.log("📱 Buscando empleado por documento:", documentoBuscar);
            
            // Buscar empleados y filtrar en JavaScript para evitar problemas de tipo
            const { data: todosEmpleados, error: empError } = await supabase
              .from('empleados')
              .select('telefono, nombres, apellidos, documento');
            
            if (empError) {
              console.warn("📱 Error cargando empleados:", empError);
            } else if (todosEmpleados) {
              // Buscar coincidencia exacta del documento (como string)
              empData = todosEmpleados.find(emp => 
                String(emp.documento).trim() === documentoBuscar
              );
              console.log("📱 Empleado encontrado:", empData ? "Sí" : "No", "de", todosEmpleados.length, "empleados");
            }
          }
          
          console.log("📱 Datos empleado para SMS:", empData);
          
          if (empData?.telefono) {
            const nombreEmpleado = `${empData.nombres || ''} ${empData.apellidos || ''}`.trim();
            notificarRespuestaSolicitud({ ...empData, nombre: nombreEmpleado }, solicitudSeleccionada, estadoFinal).then(result => {
              if (result?.ok) {
                console.log("📱 SMS enviado a empleado:", nombreEmpleado);
              } else {
                console.warn("📱 SMS no enviado:", result?.error);
              }
            }).catch(err => console.warn("No se pudo enviar SMS:", err));
          } else {
            console.warn("📱 Empleado sin teléfono registrado:", empData);
          }
        } catch (smsErr) {
          console.warn("Error al intentar enviar SMS:", smsErr);
        }
      } else {
        console.warn("📱 Solicitud sin empleado_id ni documento, no se puede notificar");
      }
      
      setSolicitudSeleccionada(null);
      setRespuesta('');
      setArchivosRespuesta([]);
      setRequiereConfirmacion(false);
      cargarSolicitudes();
    } catch (error) {
      console.error('Error respondiendo solicitud:', error);
      alert('❌ Error al procesar la solicitud: ' + (error.message || JSON.stringify(error)));
    }
    setProcesando(false);
  };
  
  // Función para subir archivos de respuesta
  const subirArchivoRespuesta = async (archivo) => {
    setSubiendoArchivo(true);
    try {
      const nombreArchivo = `solicitudes/respuesta_${Date.now()}_${archivo.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const { error } = await supabase.storage
        .from('empleados-docs')
        .upload(nombreArchivo, archivo);
      
      if (error) throw error;
      
      const { data: urlData } = supabase.storage
        .from('empleados-docs')
        .getPublicUrl(nombreArchivo);
      
      setArchivosRespuesta(prev => [...prev, {
        nombre: archivo.name,
        url: urlData.publicUrl
      }]);
    } catch (error) {
      console.error('Error subiendo archivo:', error);
      alert('Error al subir el archivo');
    }
    setSubiendoArchivo(false);
  };

  // Estado para controlar el guardado de documentos en ficha
  const [guardandoEnFicha, setGuardandoEnFicha] = useState(null);
  const [documentosGuardadosEnFicha, setDocumentosGuardadosEnFicha] = useState([]); // URLs de docs ya guardados

  // Verificar al abrir una solicitud si los documentos ya están en la ficha del empleado
  useEffect(() => {
    const verificarDocumentosEnFicha = async () => {
      if (!solicitudSeleccionada?.documento) return;
      
      try {
        // Buscar los docs del empleado
        const { data: empleadoData, error } = await supabase
          .from('empleados')
          .select('docs')
          .eq('documento', solicitudSeleccionada.documento)
          .maybeSingle();
        
        if (error || !empleadoData) return;
        
        const docsEmpleado = Array.isArray(empleadoData.docs) ? empleadoData.docs : [];
        const urlsEnFicha = docsEmpleado.map(d => d.url).filter(Boolean);
        
        // Obtener todas las URLs de archivos de la solicitud
        let archivosVerificar = [];
        
        // Archivos adjuntos originales
        if (solicitudSeleccionada.archivos_adjuntos) {
          let adj = solicitudSeleccionada.archivos_adjuntos;
          if (typeof adj === 'string') {
            try { adj = JSON.parse(adj); } catch(e) { adj = []; }
          }
          archivosVerificar = [...archivosVerificar, ...adj];
        }
        
        // Archivos de respuesta del empleado
        if (solicitudSeleccionada.archivos_respuesta_empleado) {
          let resp = solicitudSeleccionada.archivos_respuesta_empleado;
          if (typeof resp === 'string') {
            try { resp = JSON.parse(resp); } catch(e) { resp = []; }
          }
          archivosVerificar = [...archivosVerificar, ...resp];
        }
        
        // Archivos del historial
        if (solicitudSeleccionada.historial_conversacion) {
          solicitudSeleccionada.historial_conversacion.forEach(msg => {
            if (msg.archivos && msg.tipo !== 'admin') {
              archivosVerificar = [...archivosVerificar, ...msg.archivos];
            }
          });
        }
        
        // Marcar los que ya están en la ficha
        const yaGuardados = archivosVerificar
          .filter(arch => arch.url && urlsEnFicha.includes(arch.url))
          .map(arch => arch.url);
        
        if (yaGuardados.length > 0) {
          setDocumentosGuardadosEnFicha(prev => [...new Set([...prev, ...yaGuardados])]);
        }
      } catch (error) {
        console.error('Error verificando documentos en ficha:', error);
      }
    };
    
    if (solicitudSeleccionada) {
      verificarDocumentosEnFicha();
    } else {
      // Limpiar cuando se cierra el modal
      setDocumentosGuardadosEnFicha([]);
    }
  }, [solicitudSeleccionada]);

  // Función para guardar documento de solicitud en la ficha del empleado
  const guardarDocumentoEnFichaEmpleado = async (documento, documentoEmpleado) => {
    if (!documento || !documentoEmpleado) {
      alert('No se encontró el documento o el empleado');
      return;
    }

    // Pedir nombre para el documento
    const nombrePrompt = window.prompt(
      '¿Con qué nombre quieres guardar este documento en la ficha del empleado?',
      documento.nombre || 'Documento de solicitud'
    );
    
    if (nombrePrompt === null) return; // Canceló
    const nombreFinal = (nombrePrompt || documento.nombre || 'Documento').trim();
    if (!nombreFinal) {
      alert('El nombre del documento no puede estar vacío');
      return;
    }

    setGuardandoEnFicha(documento.url);
    
    try {
      // 1. Buscar el empleado en la base de datos
      const { data: empleadoData, error: errorBuscar } = await supabase
        .from('empleados')
        .select('id, docs')
        .eq('documento', documentoEmpleado)
        .maybeSingle();
      
      if (errorBuscar) throw errorBuscar;
      
      if (!empleadoData) {
        alert(`No se encontró el empleado con documento: ${documentoEmpleado}`);
        setGuardandoEnFicha(null);
        return;
      }
      
      // 2. Preparar el nuevo documento
      const nuevoDoc = {
        id: Math.random().toString(36).slice(2, 9),
        nombre: nombreFinal,
        url: documento.url,
        size: documento.tamaño || 0,
        type: documento.tipo || 'application/octet-stream',
        fecha: new Date().toISOString().split('T')[0],
        origen: 'solicitud_intranet'
      };
      
      // 3. Agregar al array de docs existente
      const docsActuales = Array.isArray(empleadoData.docs) ? empleadoData.docs : [];
      const docsActualizados = [...docsActuales, nuevoDoc];
      
      // 4. Actualizar el empleado
      const { error: errorUpdate } = await supabase
        .from('empleados')
        .update({ docs: docsActualizados })
        .eq('id', empleadoData.id);
      
      if (errorUpdate) throw errorUpdate;
      
      // Marcar el documento como guardado
      setDocumentosGuardadosEnFicha(prev => [...prev, documento.url]);
      
    } catch (error) {
      console.error('Error guardando documento en ficha:', error);
      alert('❌ Error al guardar el documento: ' + (error.message || 'Error desconocido'));
    }
    
    setGuardandoEnFicha(null);
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'aprobado': 
      case 'aprobada': return { bg: '#e8f5e9', color: '#2e7d32', texto: '✅ APROBADO', icono: '✅' };
      case 'negado': 
      case 'rechazada': return { bg: '#ffebee', color: '#c62828', texto: '❌ NEGADO', icono: '❌' };
      case 'en_proceso': return { bg: '#e3f2fd', color: '#1565c0', texto: '⏳ EN PROCESO', icono: '⏳' };
      case 'recibido': return { bg: '#fff3e0', color: '#e65100', texto: '📥 RECIBIDO', icono: '📥' };
      case 'pendiente_confirmacion': return { bg: '#f3e5f5', color: '#7b1fa2', texto: '📨 PENDIENTE CONFIRMACIÓN', icono: '📨' };
      case 'confirmado_empleado': return { bg: '#e8f5e9', color: '#2e7d32', texto: '✅ CONFIRMADO', icono: '✅' };
      case 'rechazado_empleado': return { bg: '#fff3e0', color: '#e65100', texto: '🔄 RECHAZADO POR EMPLEADO', icono: '🔄' };
      default: return { bg: '#fff3e0', color: '#e65100', texto: '📥 RECIBIDO', icono: '📥' };
    }
  };

  const solicitudesFiltradas = solicitudes.filter(sol => {
    // Si no se ha seleccionado ningún filtro, no mostrar nada
    if (filtroRespuesta === 'ninguna') return false;
    
    if (filtroEstado !== 'todas' && sol.estado !== filtroEstado) return false;
    if (filtroTipo !== 'todos' && sol.tipo !== filtroTipo) return false;
    
    // Filtro por empleado (usar los campos correctos: empleado_nombre y documento)
    if (filtroEmpleado.trim()) {
      const busqueda = filtroEmpleado.toLowerCase().trim();
      const nombreEmpleado = (sol.empleado_nombre || '').toLowerCase();
      const documentoEmpleado = (sol.documento || '').toLowerCase();
      if (!nombreEmpleado.includes(busqueda) && !documentoEmpleado.includes(busqueda)) {
        return false;
      }
    }
    
    // Filtro por respuesta
    if (filtroRespuesta === 'con_respuesta') {
      // Con respuesta = estados que no sean recibido/pendiente
      const sinRespuesta = ['recibido', 'pendiente', 'en_proceso'];
      if (sinRespuesta.includes(sol.estado)) return false;
    } else if (filtroRespuesta === 'sin_respuesta') {
      // Sin respuesta = recibido, pendiente, en_proceso
      const sinRespuesta = ['recibido', 'pendiente', 'en_proceso'];
      if (!sinRespuesta.includes(sol.estado)) return false;
    }
    
    return true;
  });

  const contadores = {
    recibidos: solicitudes.filter(s => s.estado === 'recibido' || s.estado === 'pendiente').length,
    en_proceso: solicitudes.filter(s => s.estado === 'en_proceso').length,
    aprobados: solicitudes.filter(s => s.estado === 'aprobado' || s.estado === 'aprobada' || s.estado === 'confirmado_empleado').length,
    negados: solicitudes.filter(s => s.estado === 'negado' || s.estado === 'rechazada').length,
    pendientes_confirmacion: solicitudes.filter(s => s.estado === 'pendiente_confirmacion').length,
    rechazados_empleado: solicitudes.filter(s => s.estado === 'rechazado_empleado').length
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #00897b 0%, #4db6ac 100%)',
        color: 'white',
        padding: 24,
        borderRadius: 16,
        marginBottom: 24
      }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 32 }}>📬</span>
          Solicitudes de Empleados (Intranet)
        </h2>
        <p style={{ margin: '8px 0 0', opacity: 0.9 }}>
          Gestiona las solicitudes enviadas por los empleados desde el Portal del Empleado
        </p>
        {/* Botón para gestionar bloqueos */}
        <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          <button
            onClick={() => { setMostrarBloqueos(!mostrarBloqueos); setMostrarBloqueosEmpleado(false); }}
            style={{
              padding: '8px 16px',
              backgroundColor: mostrarBloqueos ? '#ffebee' : 'rgba(255,255,255,0.2)',
              color: mostrarBloqueos ? '#c62828' : 'white',
              border: mostrarBloqueos ? '2px solid #c62828' : '1px solid rgba(255,255,255,0.5)',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            🚫 {mostrarBloqueos ? 'Ocultar' : 'Bloqueos por Fechas'}
            {bloqueos.filter(b => b.activo).length > 0 && (
              <span style={{
                backgroundColor: '#c62828',
                color: 'white',
                padding: '2px 8px',
                borderRadius: 10,
                fontSize: 12
              }}>
                {bloqueos.filter(b => b.activo).length}
              </span>
            )}
          </button>
          <button
            onClick={() => { 
              setMostrarBloqueosEmpleado(!mostrarBloqueosEmpleado); 
              setMostrarBloqueos(false); 
              if (!mostrarBloqueosEmpleado) {
                cargarEmpleados(); // Cargar empleados al abrir
              }
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: mostrarBloqueosEmpleado ? '#e3f2fd' : 'rgba(255,255,255,0.2)',
              color: mostrarBloqueosEmpleado ? '#1565c0' : 'white',
              border: mostrarBloqueosEmpleado ? '2px solid #1565c0' : '1px solid rgba(255,255,255,0.5)',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            👤 {mostrarBloqueosEmpleado ? 'Ocultar' : 'Bloqueos por Empleado'}
            {bloqueosEmpleado.filter(b => b.activo).length > 0 && (
              <span style={{
                backgroundColor: '#1565c0',
                color: 'white',
                padding: '2px 8px',
                borderRadius: 10,
                fontSize: 12
              }}>
                {bloqueosEmpleado.filter(b => b.activo).length}
              </span>
            )}
          </button>
          <button
            onClick={() => setMostrarConfigNotificaciones(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: (configNotificaciones.telefono_solicitudes_1 || configNotificaciones.telefono_solicitudes_2) ? '#e8f5e9' : 'rgba(255,255,255,0.2)',
              color: (configNotificaciones.telefono_solicitudes_1 || configNotificaciones.telefono_solicitudes_2) ? '#2e7d32' : 'white',
              border: (configNotificaciones.telefono_solicitudes_1 || configNotificaciones.telefono_solicitudes_2) ? '2px solid #2e7d32' : '1px solid rgba(255,255,255,0.5)',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            📱 Notificaciones SMS
            {(configNotificaciones.telefono_solicitudes_1 || configNotificaciones.telefono_solicitudes_2) && (
              <span style={{ fontSize: 11, opacity: 0.8 }}>✓ {configNotificaciones.telefono_solicitudes_1 && configNotificaciones.telefono_solicitudes_2 ? '2' : '1'}</span>
            )}
          </button>
        </div>
        
        {/* Filtros de Respuesta - Selecciona uno para ver las solicitudes */}
        <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center' }}>
          <span style={{ fontSize: 13, opacity: 0.9 }}>📋 Selecciona para ver:</span>
          <button
            onClick={() => setFiltroRespuesta(filtroRespuesta === 'sin_respuesta' ? 'ninguna' : 'sin_respuesta')}
            style={{
              padding: '10px 20px',
              backgroundColor: filtroRespuesta === 'sin_respuesta' ? '#ff9800' : 'rgba(255,255,255,0.2)',
              color: 'white',
              border: filtroRespuesta === 'sin_respuesta' ? '2px solid #fff' : '1px solid rgba(255,255,255,0.5)',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14
            }}
          >
            ⏳ Pendientes de Respuesta
            <span style={{
              backgroundColor: filtroRespuesta === 'sin_respuesta' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
              padding: '2px 8px',
              borderRadius: 10,
              fontSize: 12
            }}>
              {solicitudes.filter(s => ['recibido', 'pendiente', 'en_proceso'].includes(s.estado)).length}
            </span>
          </button>
          <button
            onClick={() => setFiltroRespuesta(filtroRespuesta === 'con_respuesta' ? 'ninguna' : 'con_respuesta')}
            style={{
              padding: '10px 20px',
              backgroundColor: filtroRespuesta === 'con_respuesta' ? '#4caf50' : 'rgba(255,255,255,0.2)',
              color: 'white',
              border: filtroRespuesta === 'con_respuesta' ? '2px solid #fff' : '1px solid rgba(255,255,255,0.5)',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14
            }}
          >
            ✅ Con Respuesta
            <span style={{
              backgroundColor: filtroRespuesta === 'con_respuesta' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
              padding: '2px 8px',
              borderRadius: 10,
              fontSize: 12
            }}>
              {solicitudes.filter(s => !['recibido', 'pendiente', 'en_proceso'].includes(s.estado)).length}
            </span>
          </button>
        </div>
      </div>

      {/* === PESTAÑAS DE NAVEGACIÓN === */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <button
          onClick={() => setVistaActiva('solicitudes_empleados')}
          style={{
            padding: '14px 28px',
            backgroundColor: vistaActiva === 'solicitudes_empleados' ? '#00897b' : '#f5f5f5',
            color: vistaActiva === 'solicitudes_empleados' ? 'white' : '#333',
            border: 'none',
            borderRadius: '12px 12px 0 0',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          📥 Solicitudes de Empleados
          {solicitudes.filter(s => ['recibido', 'pendiente', 'en_proceso'].includes(s.estado)).length > 0 && (
            <span style={{
              backgroundColor: vistaActiva === 'solicitudes_empleados' ? 'rgba(255,255,255,0.3)' : '#ff9800',
              color: 'white',
              padding: '2px 8px',
              borderRadius: 10,
              fontSize: 11
            }}>
              {solicitudes.filter(s => ['recibido', 'pendiente', 'en_proceso'].includes(s.estado)).length}
            </span>
          )}
        </button>
        <button
          onClick={() => setVistaActiva('solicitudes_empleador')}
          style={{
            padding: '14px 28px',
            backgroundColor: vistaActiva === 'solicitudes_empleador' ? '#1565c0' : '#f5f5f5',
            color: vistaActiva === 'solicitudes_empleador' ? 'white' : '#333',
            border: 'none',
            borderRadius: '12px 12px 0 0',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          📤 Solicitudes del Empleador
          {solicitudesEmpleador.filter(s => s.estado === 'respondida').length > 0 && (
            <span style={{
              backgroundColor: vistaActiva === 'solicitudes_empleador' ? 'rgba(255,255,255,0.3)' : '#4caf50',
              color: 'white',
              padding: '2px 8px',
              borderRadius: 10,
              fontSize: 11
            }}>
              {solicitudesEmpleador.filter(s => s.estado === 'respondida').length} resp
            </span>
          )}
        </button>
      </div>

      {/* === CONTENIDO: SOLICITUDES DEL EMPLEADOR === */}
      {vistaActiva === 'solicitudes_empleador' && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '0 12px 12px 12px',
          padding: 24,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
        }}>
          {/* Header con botón para crear nueva solicitud */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, color: '#1565c0' }}>📤 Solicitudes Enviadas a Empleados</h3>
              <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>
                Crea solicitudes para empleados y recibe sus respuestas
              </p>
            </div>
            <button
              onClick={() => {
                setMostrarFormSolicitudEmpleador(true);
                if (empleadosLista.length === 0) cargarEmpleados();
              }}
              style={{
                padding: '12px 24px',
                backgroundColor: '#1565c0',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              ➕ Nueva Solicitud
            </button>
          </div>

          {/* Filtros de solicitudes del empleador */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {['pendientes', 'respondidas', 'todas'].map(filtro => (
              <button
                key={filtro}
                onClick={() => setFiltroSolicitudesEmpleador(filtro)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: filtroSolicitudesEmpleador === filtro ? '#1565c0' : '#f5f5f5',
                  color: filtroSolicitudesEmpleador === filtro ? 'white' : '#333',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600
                }}
              >
                {filtro === 'pendientes' ? '⏳ Pendientes' : filtro === 'respondidas' ? '✅ Respondidas' : '📋 Todas'}
              </button>
            ))}
          </div>

          {/* Lista de solicitudes del empleador */}
          {solicitudesEmpleador.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📭</div>
              <p>No hay solicitudes del empleador aún</p>
              <button
                onClick={() => {
                  setMostrarFormSolicitudEmpleador(true);
                  if (empleadosLista.length === 0) cargarEmpleados();
                }}
                style={{
                  marginTop: 16,
                  padding: '12px 24px',
                  backgroundColor: '#1565c0',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                ➕ Crear primera solicitud
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {solicitudesEmpleador
                .filter(sol => {
                  if (filtroSolicitudesEmpleador === 'pendientes') return sol.estado === 'pendiente';
                  if (filtroSolicitudesEmpleador === 'respondidas') return sol.estado === 'respondida';
                  return true;
                })
                .map(sol => {
                  let archivosAdj = [];
                  try {
                    if (sol.archivos_adjuntos) {
                      archivosAdj = typeof sol.archivos_adjuntos === 'string' 
                        ? JSON.parse(sol.archivos_adjuntos) 
                        : sol.archivos_adjuntos;
                    }
                  } catch (e) { archivosAdj = []; }

                  return (
                    <div
                      key={sol.id}
                      style={{
                        padding: 16,
                        backgroundColor: sol.estado === 'respondida' ? '#e8f5e9' : '#fafafa',
                        border: '1px solid #e0e0e0',
                        borderRadius: 12,
                        cursor: 'pointer'
                      }}
                      onClick={() => setSolicitudEmpleadorSeleccionada(sol)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 20 }}>👤</span>
                            <span style={{ fontWeight: 'bold', color: '#1565c0' }}>
                              {sol.empleado_nombre || sol.empleado_documento}
                            </span>
                            <span style={{
                              padding: '2px 8px',
                              backgroundColor: sol.prioridad === 'urgente' ? '#ffebee' : sol.prioridad === 'alta' ? '#fff3e0' : '#e8f5e9',
                              color: sol.prioridad === 'urgente' ? '#c62828' : sol.prioridad === 'alta' ? '#e65100' : '#2e7d32',
                              borderRadius: 4,
                              fontSize: 10,
                              fontWeight: 'bold'
                            }}>
                              {sol.prioridad?.toUpperCase() || 'NORMAL'}
                            </span>
                          </div>
                          <div style={{ fontWeight: 600, marginTop: 6, color: '#333' }}>
                            {sol.asunto}
                          </div>
                          <p style={{ margin: '8px 0', color: '#666', fontSize: 13 }}>
                            {sol.descripcion?.substring(0, 150)}{sol.descripcion?.length > 150 ? '...' : ''}
                          </p>
                          <div style={{ fontSize: 11, color: '#999' }}>
                            📅 Enviada: {new Date(sol.fecha_creacion).toLocaleDateString('es-CO')}
                            {archivosAdj.length > 0 && ` | 📎 ${archivosAdj.length} archivo(s)`}
                          </div>
                        </div>
                        <span style={{
                          padding: '6px 12px',
                          backgroundColor: sol.estado === 'respondida' ? '#4caf50' : '#ff9800',
                          color: 'white',
                          borderRadius: 20,
                          fontSize: 11,
                          fontWeight: 'bold'
                        }}>
                          {sol.estado === 'respondida' ? '✅ RESPONDIDA' : '⏳ PENDIENTE'}
                        </span>
                      </div>
                      {sol.respuesta_empleado && (
                        <div style={{
                          marginTop: 12,
                          padding: 12,
                          backgroundColor: '#e3f2fd',
                          borderRadius: 8,
                          fontSize: 13
                        }}>
                          <strong>💬 Respuesta del empleado:</strong>
                          <p style={{ margin: '4px 0 0', color: '#333' }}>{sol.respuesta_empleado}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* === MODAL DE DETALLE DE SOLICITUD DEL EMPLEADOR === */}
      {solicitudEmpleadorSeleccionada && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 16,
            padding: 24,
            maxWidth: 700,
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                📋 Detalle de Solicitud
              </h3>
              <button
                onClick={() => setSolicitudEmpleadorSeleccionada(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            {/* Info del empleado */}
            <div style={{
              padding: 12,
              backgroundColor: '#e3f2fd',
              borderRadius: 8,
              marginBottom: 16
            }}>
              <div style={{ fontSize: 14, fontWeight: 'bold', color: '#1565c0' }}>
                👤 {solicitudEmpleadorSeleccionada.empleado_nombre || 'Sin nombre'}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                Documento: {solicitudEmpleadorSeleccionada.empleado_documento}
              </div>
            </div>

            {/* Asunto y descripción */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 'bold', fontSize: 16, marginBottom: 4 }}>
                {solicitudEmpleadorSeleccionada.asunto}
              </div>
              <p style={{ margin: 0, color: '#666', lineHeight: 1.6 }}>
                {solicitudEmpleadorSeleccionada.descripcion}
              </p>
            </div>

            {/* Archivos adjuntos enviados */}
            {(() => {
              let archivosAdj = [];
              try {
                if (solicitudEmpleadorSeleccionada.archivos_adjuntos) {
                  archivosAdj = typeof solicitudEmpleadorSeleccionada.archivos_adjuntos === 'string' 
                    ? JSON.parse(solicitudEmpleadorSeleccionada.archivos_adjuntos) 
                    : solicitudEmpleadorSeleccionada.archivos_adjuntos;
                }
              } catch (e) { archivosAdj = []; }
              
              return archivosAdj.length > 0 && (
                <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8 }}>📎 Archivos enviados al empleado:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {archivosAdj.map((arch, idx) => (
                      <a
                        key={idx}
                        href={arch.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '6px 12px',
                          backgroundColor: 'white',
                          borderRadius: 6,
                          fontSize: 12,
                          color: '#1565c0',
                          textDecoration: 'none',
                          border: '1px solid #ddd'
                        }}
                      >
                        📄 {arch.nombre}
                      </a>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Estado */}
            <div style={{
              padding: 12,
              backgroundColor: solicitudEmpleadorSeleccionada.estado === 'respondida' ? '#e8f5e9' : '#fff3e0',
              borderRadius: 8,
              marginBottom: 16,
              textAlign: 'center'
            }}>
              <span style={{
                fontSize: 14,
                fontWeight: 'bold',
                color: solicitudEmpleadorSeleccionada.estado === 'respondida' ? '#2e7d32' : '#e65100'
              }}>
                {solicitudEmpleadorSeleccionada.estado === 'respondida' ? '✅ RESPONDIDA' : '⏳ PENDIENTE DE RESPUESTA'}
              </span>
              <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                Enviada: {new Date(solicitudEmpleadorSeleccionada.fecha_creacion).toLocaleString('es-CO')}
              </div>
            </div>

            {/* Respuesta del empleado */}
            {solicitudEmpleadorSeleccionada.respuesta_empleado && (
              <div style={{
                padding: 16,
                backgroundColor: '#e8f5e9',
                borderRadius: 8,
                border: '2px solid #4caf50',
                marginBottom: 16
              }}>
                <div style={{ fontSize: 14, fontWeight: 'bold', color: '#2e7d32', marginBottom: 8 }}>
                  💬 Respuesta del Empleado:
                </div>
                <p style={{ margin: '0 0 12px', color: '#333', lineHeight: 1.6 }}>
                  {solicitudEmpleadorSeleccionada.respuesta_empleado}
                </p>
                {solicitudEmpleadorSeleccionada.fecha_respuesta_empleado && (
                  <div style={{ fontSize: 11, color: '#666' }}>
                    📅 Respondida: {new Date(solicitudEmpleadorSeleccionada.fecha_respuesta_empleado).toLocaleString('es-CO')}
                  </div>
                )}

                {/* Archivos adjuntos de la respuesta del empleado */}
                {(() => {
                  let archivosResp = [];
                  try {
                    if (solicitudEmpleadorSeleccionada.archivos_respuesta_empleado) {
                      archivosResp = typeof solicitudEmpleadorSeleccionada.archivos_respuesta_empleado === 'string' 
                        ? JSON.parse(solicitudEmpleadorSeleccionada.archivos_respuesta_empleado) 
                        : solicitudEmpleadorSeleccionada.archivos_respuesta_empleado;
                    }
                  } catch (e) { archivosResp = []; }
                  
                  return archivosResp.length > 0 && (
                    <div style={{ marginTop: 12, padding: 12, backgroundColor: '#c8e6c9', borderRadius: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 'bold', color: '#1b5e20', marginBottom: 8 }}>
                        📎 Archivos adjuntos del empleado ({archivosResp.length}):
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {archivosResp.map((arch, idx) => (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                            <a
                              href={arch.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                padding: '6px 12px',
                                backgroundColor: 'white',
                                borderRadius: 6,
                                fontSize: 12,
                                color: '#2e7d32',
                                textDecoration: 'none',
                                border: '1px solid #81c784',
                                flex: 1
                              }}
                            >
                              📄 {arch.nombre}
                            </a>
                            <button
                              onClick={async () => {
                                // Guardar archivo en ficha del empleado
                                try {
                                  const documento = solicitudEmpleadorSeleccionada.empleado_documento;
                                  
                                  // Buscar el empleado
                                  const { data: empleadoData, error: errEmp } = await supabase
                                    .from('empleados')
                                    .select('id, docs')
                                    .eq('documento', documento)
                                    .maybeSingle();
                                  
                                  if (errEmp || !empleadoData) {
                                    alert('❌ No se encontró el empleado');
                                    return;
                                  }
                                  
                                  // Obtener docs actuales y agregar el nuevo
                                  const docsActuales = Array.isArray(empleadoData.docs) ? empleadoData.docs : [];
                                  
                                  // Verificar si ya existe
                                  if (docsActuales.some(d => d.url === arch.url)) {
                                    alert('⚠️ Este documento ya está guardado en la ficha del empleado');
                                    return;
                                  }
                                  
                                  const nuevoDoc = {
                                    nombre: arch.nombre,
                                    url: arch.url,
                                    fechaSubida: new Date().toISOString(),
                                    origen: 'Respuesta a solicitud del empleador',
                                    asuntoSolicitud: solicitudEmpleadorSeleccionada.asunto
                                  };
                                  
                                  const { error: errUpdate } = await supabase
                                    .from('empleados')
                                    .update({ docs: [...docsActuales, nuevoDoc] })
                                    .eq('id', empleadoData.id);
                                  
                                  if (errUpdate) throw errUpdate;
                                  
                                  alert('✅ Documento guardado en la ficha del empleado');
                                } catch (error) {
                                  console.error('Error guardando en ficha:', error);
                                  alert('❌ Error al guardar: ' + (error.message || 'Error desconocido'));
                                }
                              }}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#1565c0',
                                color: 'white',
                                border: 'none',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontSize: 11,
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              💾 Guardar en Ficha
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Botón cerrar */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSolicitudEmpleadorSeleccionada(null)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === MODAL PARA CREAR SOLICITUD DEL EMPLEADOR === */}
      {mostrarFormSolicitudEmpleador && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 16,
            padding: 24,
            maxWidth: 600,
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                📤 Nueva Solicitud del Empleador
              </h3>
              <button
                onClick={() => setMostrarFormSolicitudEmpleador(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            {/* Seleccionar Empleado */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 14 }}>
                Empleado *
              </label>
              <input
                type="text"
                value={busquedaEmpleado}
                onChange={(e) => setBusquedaEmpleado(e.target.value)}
                placeholder="Buscar por nombre o documento..."
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  boxSizing: 'border-box'
                }}
              />
              {busquedaEmpleado && empleadosLista.filter(emp => {
                const busq = busquedaEmpleado.toLowerCase();
                return (emp.nombre || '').toLowerCase().includes(busq) ||
                       (emp.apellido || '').toLowerCase().includes(busq) ||
                       (emp.documento || '').includes(busq);
              }).length > 0 && (
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  maxHeight: 150,
                  overflowY: 'auto',
                  marginTop: 4
                }}>
                  {empleadosLista.filter(emp => {
                    const busq = busquedaEmpleado.toLowerCase();
                    return (emp.nombre || '').toLowerCase().includes(busq) ||
                           (emp.apellido || '').toLowerCase().includes(busq) ||
                           (emp.documento || '').includes(busq);
                  }).map(emp => (
                    <div
                      key={emp.documento}
                      onClick={async () => {
                        // Buscar teléfono del empleado
                        let telefono = '';
                        try {
                          const { data } = await supabase
                            .from('empleados')
                            .select('telefono')
                            .eq('documento', emp.documento)
                            .single();
                          telefono = data?.telefono || '';
                        } catch (e) {}
                        
                        setNuevaSolicitudEmpleador({
                          ...nuevaSolicitudEmpleador,
                          empleado_documento: emp.documento,
                          empleado_nombre: `${emp.nombre} ${emp.apellido}`.trim(),
                          empleado_telefono: telefono
                        });
                        setBusquedaEmpleado('');
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        backgroundColor: 'white'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <strong>{emp.nombre} {emp.apellido}</strong>
                      <span style={{ marginLeft: 8, color: '#666', fontSize: 12 }}>
                        CC: {emp.documento} | {emp.cargo || 'Sin cargo'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {nuevaSolicitudEmpleador.empleado_nombre && (
                <div style={{
                  marginTop: 8,
                  padding: 10,
                  backgroundColor: '#e3f2fd',
                  borderRadius: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>✅ {nuevaSolicitudEmpleador.empleado_nombre} ({nuevaSolicitudEmpleador.empleado_documento})</span>
                  <button
                    onClick={() => setNuevaSolicitudEmpleador({
                      ...nuevaSolicitudEmpleador,
                      empleado_documento: '',
                      empleado_nombre: '',
                      empleado_telefono: ''
                    })}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828' }}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>

            {/* Asunto */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 14 }}>
                Asunto *
              </label>
              <input
                type="text"
                value={nuevaSolicitudEmpleador.asunto}
                onChange={(e) => setNuevaSolicitudEmpleador({ ...nuevaSolicitudEmpleador, asunto: e.target.value })}
                placeholder="Ej: Actualización de documentos, Firma de contrato, etc."
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Descripción */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 14 }}>
                Descripción de la solicitud *
              </label>
              <textarea
                value={nuevaSolicitudEmpleador.descripcion}
                onChange={(e) => setNuevaSolicitudEmpleador({ ...nuevaSolicitudEmpleador, descripcion: e.target.value })}
                placeholder="Describe detalladamente lo que necesitas que el empleado haga o responda..."
                rows={4}
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  boxSizing: 'border-box',
                  resize: 'vertical'
                }}
              />
            </div>

            {/* Prioridad */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 14 }}>
                Prioridad
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { id: 'normal', label: '🟢 Normal', color: '#4caf50' },
                  { id: 'alta', label: '🟠 Alta', color: '#ff9800' },
                  { id: 'urgente', label: '🔴 Urgente', color: '#f44336' }
                ].map(pri => (
                  <button
                    key={pri.id}
                    type="button"
                    onClick={() => setNuevaSolicitudEmpleador({ ...nuevaSolicitudEmpleador, prioridad: pri.id })}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      backgroundColor: nuevaSolicitudEmpleador.prioridad === pri.id ? pri.color : '#f5f5f5',
                      color: nuevaSolicitudEmpleador.prioridad === pri.id ? 'white' : '#333',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    {pri.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Archivos adjuntos */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold', fontSize: 14 }}>
                Archivos adjuntos (opcional)
              </label>
              <input
                type="file"
                multiple
                onChange={(e) => {
                  Array.from(e.target.files).forEach(file => subirArchivoSolicitudEmpleador(file));
                }}
                disabled={subiendoArchivoEmpleador}
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  border: '1px dashed #ccc',
                  backgroundColor: '#fafafa'
                }}
              />
              {subiendoArchivoEmpleador && <p style={{ color: '#666', fontSize: 12 }}>⏳ Subiendo archivo...</p>}
              {archivosSolicitudEmpleador.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {archivosSolicitudEmpleador.map((arch, idx) => (
                    <div key={idx} style={{
                      padding: '6px 12px',
                      backgroundColor: '#e3f2fd',
                      borderRadius: 6,
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      📄 {arch.nombre}
                      <button
                        onClick={() => setArchivosSolicitudEmpleador(prev => prev.filter((_, i) => i !== idx))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828' }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ¿Requiere respuesta? */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={nuevaSolicitudEmpleador.requiere_respuesta}
                  onChange={(e) => setNuevaSolicitudEmpleador({ ...nuevaSolicitudEmpleador, requiere_respuesta: e.target.checked })}
                  style={{ width: 18, height: 18 }}
                />
                <span style={{ fontWeight: 500 }}>Requiere respuesta del empleado</span>
              </label>
            </div>

            {/* Botones */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setMostrarFormSolicitudEmpleador(false);
                  setNuevaSolicitudEmpleador({
                    empleado_documento: '',
                    empleado_nombre: '',
                    empleado_telefono: '',
                    asunto: '',
                    descripcion: '',
                    prioridad: 'normal',
                    requiere_respuesta: true
                  });
                  setArchivosSolicitudEmpleador([]);
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={guardarSolicitudEmpleador}
                disabled={guardandoSolicitudEmpleador}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#1565c0',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: guardandoSolicitudEmpleador ? 'wait' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {guardandoSolicitudEmpleador ? '⏳ Enviando...' : '📤 Enviar Solicitud'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === CONTENIDO SOLICITUDES DE EMPLEADOS (ORIGINAL) === */}
      {vistaActiva === 'solicitudes_empleados' && (
        <>
      {/* Modal de Configuración de Notificaciones SMS - 2 Teléfonos por Empresa */}
      {mostrarConfigNotificaciones && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 16,
            padding: 24,
            maxWidth: 600,
            width: '95%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
              📱 Configurar Notificaciones SMS para Solicitudes
            </h3>
            <p style={{ color: '#666', marginBottom: 20, fontSize: 14 }}>
              Configura hasta <strong>2 números de teléfono</strong> que recibirán SMS cuando un empleado radique una nueva solicitud en esta empresa. 
              <br/><span style={{ color: '#888', fontSize: 12 }}>Puedes dejar vacío cualquier campo si no lo necesitas.</span>
            </p>
            
            {/* TELÉFONO 1 */}
            <div style={{ 
              backgroundColor: '#e3f2fd', 
              padding: 16, 
              borderRadius: 12, 
              marginBottom: 16,
              border: '1px solid #90caf9'
            }}>
              <h4 style={{ margin: '0 0 12px', color: '#1565c0', fontSize: 14 }}>📞 Teléfono Principal (1)</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>
                    Nombre del receptor
                  </label>
                  <input
                    type="text"
                    value={configNotificaciones.nombre_receptor_1}
                    onChange={(e) => setConfigNotificaciones({ ...configNotificaciones, nombre_receptor_1: e.target.value })}
                    placeholder="Ej: Juan Pérez (Gerente)"
                    style={{
                      width: '100%',
                      padding: 10,
                      borderRadius: 8,
                      border: '1px solid #ddd',
                      fontSize: 14
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={configNotificaciones.telefono_solicitudes_1}
                    onChange={(e) => setConfigNotificaciones({ ...configNotificaciones, telefono_solicitudes_1: e.target.value })}
                    placeholder="Ej: 3001234567"
                    style={{
                      width: '100%',
                      padding: 10,
                      borderRadius: 8,
                      border: '1px solid #ddd',
                      fontSize: 14
                    }}
                  />
                </div>
              </div>
            </div>
            
            {/* TELÉFONO 2 */}
            <div style={{ 
              backgroundColor: '#f3e5f5', 
              padding: 16, 
              borderRadius: 12, 
              marginBottom: 16,
              border: '1px solid #ce93d8'
            }}>
              <h4 style={{ margin: '0 0 12px', color: '#7b1fa2', fontSize: 14 }}>📞 Teléfono Secundario (2) - Opcional</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>
                    Nombre del receptor
                  </label>
                  <input
                    type="text"
                    value={configNotificaciones.nombre_receptor_2}
                    onChange={(e) => setConfigNotificaciones({ ...configNotificaciones, nombre_receptor_2: e.target.value })}
                    placeholder="Ej: María López (Asistente)"
                    style={{
                      width: '100%',
                      padding: 10,
                      borderRadius: 8,
                      border: '1px solid #ddd',
                      fontSize: 14
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={configNotificaciones.telefono_solicitudes_2}
                    onChange={(e) => setConfigNotificaciones({ ...configNotificaciones, telefono_solicitudes_2: e.target.value })}
                    placeholder="Ej: 3109876543"
                    style={{
                      width: '100%',
                      padding: 10,
                      borderRadius: 8,
                      border: '1px solid #ddd',
                      fontSize: 14
                    }}
                  />
                </div>
              </div>
            </div>
            
            <small style={{ color: '#666', display: 'block', marginBottom: 16 }}>
              💡 Para Colombia: ingresa el número con o sin +57 (se formateará automáticamente)
            </small>
            
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setMostrarConfigNotificaciones(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={guardarConfigNotificaciones}
                disabled={guardandoConfig}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: guardandoConfig ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {guardandoConfig ? 'Guardando...' : '💾 Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Panel de Gestión de Bloqueos por Empleado */}
      {mostrarBloqueosEmpleado && (
        <div style={{
          backgroundColor: '#e3f2fd',
          border: '2px solid #1976d2',
          borderRadius: 16,
          padding: 20,
          marginBottom: 24
        }}>
          <h3 style={{ margin: '0 0 16px', color: '#1565c0', display: 'flex', alignItems: 'center', gap: 8 }}>
            👤 Bloquear Módulos por Empleado
          </h3>
          <p style={{ color: '#666', marginBottom: 16, fontSize: 14 }}>
            Bloquea acceso a módulos específicos de la Intranet para empleados individuales.
          </p>
          
          {/* Formulario nuevo bloqueo por empleado */}
          <div style={{
            backgroundColor: 'white',
            padding: 16,
            borderRadius: 12,
            marginBottom: 16,
            border: '1px solid #ddd'
          }}>
            <h4 style={{ margin: '0 0 12px', color: '#333' }}>➕ Nuevo Bloqueo de Módulo</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              {/* Buscar empleado */}
              <div style={{ position: 'relative' }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>Empleado *</label>
                {nuevoBloqueEmpleado.documento_empleado ? (
                  <div style={{ 
                    padding: 10, 
                    backgroundColor: '#e8f5e9', 
                    borderRadius: 8, 
                    border: '1px solid #4caf50',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontWeight: 'bold', color: '#2e7d32' }}>
                      ✅ {nuevoBloqueEmpleado.nombre_empleado}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setNuevoBloqueEmpleado({...nuevoBloqueEmpleado, documento_empleado: '', nombre_empleado: ''});
                        setBusquedaEmpleado('');
                      }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}
                    >❌</button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={busquedaEmpleado}
                      onChange={(e) => setBusquedaEmpleado(e.target.value)}
                      placeholder={`Buscar entre ${empleadosLista.length} empleados...`}
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                      onFocus={() => {}} // Mantener la lista abierta
                    />
                    <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>
                      {empleadosLista.length === 0 ? '⏳ Cargando empleados...' : `📋 ${empleadosLista.length} empleados disponibles (clic para desplegar)`}
                    </div>
                    {busquedaEmpleado.length > 0 && empleadosFiltrados.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: 'calc(100% + 20px)',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: 8,
                        maxHeight: 200,
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}>
                        {/* Opción TODOS LOS EMPLEADOS */}
                        <div
                          onClick={() => {
                            setNuevoBloqueEmpleado({
                              ...nuevoBloqueEmpleado,
                              documento_empleado: 'TODOS',
                              nombre_empleado: '🌐 TODOS LOS EMPLEADOS'
                            });
                            setBusquedaEmpleado('');
                          }}
                          style={{
                            padding: 12,
                            cursor: 'pointer',
                            borderBottom: '2px solid #1976d2',
                            backgroundColor: '#e3f2fd',
                            fontWeight: 'bold',
                            color: '#1565c0'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#bbdefb'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e3f2fd'}
                        >
                          🌐 TODOS LOS EMPLEADOS
                          <div style={{ fontSize: 10, color: '#666', fontWeight: 'normal', marginTop: 2 }}>
                            Bloquear este módulo para todos los empleados de la empresa
                          </div>
                        </div>
                        
                        <div style={{ padding: 8, backgroundColor: '#f5f5f5', fontSize: 11, color: '#666', borderBottom: '1px solid #ddd' }}>
                          O seleccionar empleado específico ({empleadosFiltrados.length} de {empleadosLista.length})
                        </div>
                        {empleadosFiltrados.map(emp => (
                          <div
                            key={emp.documento}
                            onClick={() => {
                              setNuevoBloqueEmpleado({
                                ...nuevoBloqueEmpleado,
                                documento_empleado: emp.documento,
                                nombre_empleado: `${emp.nombre} ${emp.apellido || ''}`
                              });
                              setBusquedaEmpleado('');
                            }}
                            style={{
                              padding: 10,
                              cursor: 'pointer',
                              borderBottom: '1px solid #eee'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e3f2fd'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                          >
                            <div style={{ fontWeight: 'bold' }}>{emp.nombre} {emp.apellido}</div>
                            <div style={{ fontSize: 11, color: '#666' }}>
                              📄 {emp.documento} | {emp.cargo || 'Sin cargo'} | {emp.sede || ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {/* Seleccionar módulo */}
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>Módulo a Bloquear *</label>
                <select
                  value={nuevoBloqueEmpleado.modulo}
                  onChange={(e) => setNuevoBloqueEmpleado({...nuevoBloqueEmpleado, modulo: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                >
                  <option value="">Seleccionar módulo...</option>
                  {modulosIntranet.map(m => (
                    <option key={m.id} value={m.id}>{m.icono} {m.nombre}</option>
                  ))}
                </select>
              </div>
              
              {/* Motivo */}
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>Motivo (opcional)</label>
                <input
                  type="text"
                  value={nuevoBloqueEmpleado.motivo}
                  onChange={(e) => setNuevoBloqueEmpleado({...nuevoBloqueEmpleado, motivo: e.target.value})}
                  placeholder="Ej: En proceso disciplinario"
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                />
              </div>
            </div>

            {/* Selector de tipo de bloqueo por fechas */}
            <div style={{ marginTop: 16, marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 12, color: '#666', fontWeight: 'bold' }}>⏰ Configuración de Vigencia del Bloqueo</label>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setNuevoBloqueEmpleado({...nuevoBloqueEmpleado, es_recurrente: false, fecha_inicio: '', fecha_fin: ''})}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: !nuevoBloqueEmpleado.es_recurrente && !nuevoBloqueEmpleado.fecha_inicio ? '#1565c0' : '#f5f5f5',
                    color: !nuevoBloqueEmpleado.es_recurrente && !nuevoBloqueEmpleado.fecha_inicio ? 'white' : '#666',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: 12
                  }}
                >
                  🔒 Bloqueo Permanente
                </button>
                <button
                  type="button"
                  onClick={() => setNuevoBloqueEmpleado({...nuevoBloqueEmpleado, es_recurrente: false, fecha_inicio: new Date().toISOString().split('T')[0], fecha_fin: ''})}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: !nuevoBloqueEmpleado.es_recurrente && nuevoBloqueEmpleado.fecha_inicio ? '#1565c0' : '#f5f5f5',
                    color: !nuevoBloqueEmpleado.es_recurrente && nuevoBloqueEmpleado.fecha_inicio ? 'white' : '#666',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: 12
                  }}
                >
                  📅 Por Fechas Específicas
                </button>
                <button
                  type="button"
                  onClick={() => setNuevoBloqueEmpleado({...nuevoBloqueEmpleado, es_recurrente: true, fecha_inicio: '', fecha_fin: ''})}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: nuevoBloqueEmpleado.es_recurrente ? '#1565c0' : '#f5f5f5',
                    color: nuevoBloqueEmpleado.es_recurrente ? 'white' : '#666',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: 12
                  }}
                >
                  🔄 Recurrente Mensual
                </button>
              </div>
            </div>

            {/* Campos de fechas según el tipo seleccionado */}
            {!nuevoBloqueEmpleado.es_recurrente && nuevoBloqueEmpleado.fecha_inicio !== undefined && nuevoBloqueEmpleado.fecha_inicio !== '' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16, padding: 12, backgroundColor: '#e3f2fd', borderRadius: 8 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#1565c0' }}>📅 Fecha Inicio *</label>
                  <input
                    type="date"
                    value={nuevoBloqueEmpleado.fecha_inicio}
                    onChange={(e) => setNuevoBloqueEmpleado({...nuevoBloqueEmpleado, fecha_inicio: e.target.value})}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #1976d2' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#1565c0' }}>📅 Fecha Fin *</label>
                  <input
                    type="date"
                    value={nuevoBloqueEmpleado.fecha_fin}
                    onChange={(e) => setNuevoBloqueEmpleado({...nuevoBloqueEmpleado, fecha_fin: e.target.value})}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #1976d2' }}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#1565c0' }}>
                  ℹ️ El bloqueo estará activo desde la fecha de inicio hasta la fecha de fin (inclusive)
                </div>
              </div>
            )}

            {nuevoBloqueEmpleado.es_recurrente && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16, padding: 12, backgroundColor: '#fff3e0', borderRadius: 8 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#e65100' }}>🔄 Día del Mes (Inicio) *</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={nuevoBloqueEmpleado.dia_inicio}
                    onChange={(e) => setNuevoBloqueEmpleado({...nuevoBloqueEmpleado, dia_inicio: parseInt(e.target.value) || 1})}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ff9800' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#e65100' }}>🔄 Día del Mes (Fin) *</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={nuevoBloqueEmpleado.dia_fin}
                    onChange={(e) => setNuevoBloqueEmpleado({...nuevoBloqueEmpleado, dia_fin: parseInt(e.target.value) || 5})}
                    style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ff9800' }}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1', fontSize: 12, color: '#e65100', backgroundColor: '#ffecb3', padding: 8, borderRadius: 6 }}>
                  🔄 Este bloqueo se aplicará <strong>todos los meses</strong> del día {nuevoBloqueEmpleado.dia_inicio} al {nuevoBloqueEmpleado.dia_fin}
                </div>
              </div>
            )}

            <button
              onClick={guardarBloqueoEmpleado}
              disabled={guardandoBloqueoEmpleado}
              style={{
                marginTop: 12,
                padding: '10px 24px',
                backgroundColor: guardandoBloqueoEmpleado ? '#ccc' : '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: guardandoBloqueoEmpleado ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {guardandoBloqueoEmpleado ? '⏳ Guardando...' : '💾 Guardar Bloqueo'}
            </button>
          </div>
          
          {/* Lista de bloqueos por empleado */}
          <h4 style={{ margin: '16px 0 12px', color: '#333' }}>📋 Bloqueos por Empleado Configurados</h4>
          {bloqueosEmpleado.length === 0 ? (
            <p style={{ color: '#999', textAlign: 'center', padding: 20 }}>No hay bloqueos por empleado configurados</p>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {bloqueosEmpleado.map(bloqueo => {
                const moduloInfo = modulosIntranet.find(m => m.id === bloqueo.modulo) || { icono: '❓', nombre: bloqueo.modulo };
                const hoy = new Date();
                const diaHoy = hoy.getDate();
                const fechaHoyStr = hoy.toISOString().split('T')[0];
                
                // Determinar si está vigente
                let estaVigente = true; // Por defecto permanente
                let tipoBloqueo = '🔒 Permanente';
                
                if (bloqueo.es_recurrente && bloqueo.dia_inicio && bloqueo.dia_fin) {
                  estaVigente = diaHoy >= bloqueo.dia_inicio && diaHoy <= bloqueo.dia_fin;
                  tipoBloqueo = `🔄 Días ${bloqueo.dia_inicio}-${bloqueo.dia_fin} de cada mes`;
                } else if (bloqueo.fecha_inicio && bloqueo.fecha_fin) {
                  estaVigente = bloqueo.fecha_inicio <= fechaHoyStr && bloqueo.fecha_fin >= fechaHoyStr;
                  tipoBloqueo = `📅 ${bloqueo.fecha_inicio} al ${bloqueo.fecha_fin}`;
                }
                
                return (
                  <div
                    key={bloqueo.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 12,
                      backgroundColor: bloqueo.activo ? (estaVigente ? '#fff3e0' : '#fff') : '#f5f5f5',
                      borderRadius: 8,
                      border: `2px solid ${bloqueo.activo ? (estaVigente ? '#ff9800' : '#1976d2') : '#ccc'}`,
                      opacity: bloqueo.activo ? 1 : 0.6
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 24 }}>👤</span>
                      <div>
                        <div style={{ fontWeight: 'bold', color: '#333' }}>
                          {bloqueo.nombre_empleado}
                          <span style={{ 
                            marginLeft: 8, 
                            backgroundColor: '#1976d2', 
                            color: 'white', 
                            padding: '2px 8px', 
                            borderRadius: 4, 
                            fontSize: 10 
                          }}>
                            {moduloInfo.icono} {moduloInfo.nombre}
                          </span>
                          {estaVigente && bloqueo.activo && (
                            <span style={{ 
                              marginLeft: 8, 
                              backgroundColor: '#f44336', 
                              color: 'white', 
                              padding: '2px 8px', 
                              borderRadius: 4, 
                              fontSize: 10 
                            }}>
                              🔴 VIGENTE HOY
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          📄 Doc: {bloqueo.documento_empleado}
                          {bloqueo.motivo && <span style={{ marginLeft: 8 }}>| 💬 {bloqueo.motivo}</span>}
                        </div>
                        <div style={{ fontSize: 11, color: '#1565c0', marginTop: 4 }}>
                          ⏰ {tipoBloqueo}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => toggleBloqueoEmpleadoActivo(bloqueo)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: bloqueo.activo ? '#4caf50' : '#9e9e9e',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                      >
                        {bloqueo.activo ? '✅ Activo' : '⏸️ Inactivo'}
                      </button>
                      <button
                        onClick={() => eliminarBloqueoEmpleado(bloqueo.id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Panel de Gestión de Bloqueos */}
      {mostrarBloqueos && (
        <div style={{
          backgroundColor: '#fff8e1',
          border: '2px solid #ff9800',
          borderRadius: 16,
          padding: 20,
          marginBottom: 24
        }}>
          <h3 style={{ margin: '0 0 16px', color: '#e65100', display: 'flex', alignItems: 'center', gap: 8 }}>
            🚫 Bloquear Tipos de Solicitudes por Fechas
          </h3>
          <p style={{ color: '#666', marginBottom: 16, fontSize: 14 }}>
            Los empleados no podrán radicar solicitudes del tipo seleccionado durante las fechas configuradas.
          </p>
          
          {/* Formulario nuevo bloqueo */}
          <div style={{
            backgroundColor: 'white',
            padding: 16,
            borderRadius: 12,
            marginBottom: 16,
            border: '1px solid #ddd'
          }}>
            <h4 style={{ margin: '0 0 12px', color: '#333' }}>➕ Nuevo Bloqueo</h4>
            
            {/* Selector de tipo: Recurrente o Por fechas */}
            <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
              <button
                type="button"
                onClick={() => setNuevoBloqueo({...nuevoBloqueo, es_recurrente: false})}
                style={{
                  padding: '10px 20px',
                  backgroundColor: !nuevoBloqueo.es_recurrente ? '#1a237e' : '#f5f5f5',
                  color: !nuevoBloqueo.es_recurrente ? 'white' : '#666',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                📅 Bloqueo por Fechas Específicas
              </button>
              <button
                type="button"
                onClick={() => setNuevoBloqueo({...nuevoBloqueo, es_recurrente: true})}
                style={{
                  padding: '10px 20px',
                  backgroundColor: nuevoBloqueo.es_recurrente ? '#1a237e' : '#f5f5f5',
                  color: nuevoBloqueo.es_recurrente ? 'white' : '#666',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                🔄 Bloqueo Recurrente Mensual
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: nuevoBloqueo.es_recurrente ? 'repeat(4, 1fr)' : 'repeat(4, 1fr)', gap: 12 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>Tipo de Solicitud *</label>
                <select
                  value={nuevoBloqueo.tipo_solicitud}
                  onChange={(e) => setNuevoBloqueo({...nuevoBloqueo, tipo_solicitud: e.target.value})}
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                >
                  <option value="">Seleccionar...</option>
                  <option value="todas">🚫 TODAS LAS SOLICITUDES</option>
                  {tiposSolicitud.map(t => (
                    <option key={t.id} value={t.id}>{t.icono} {t.nombre}</option>
                  ))}
                </select>
              </div>
              
              {nuevoBloqueo.es_recurrente ? (
                <>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>Día del Mes (Inicio) *</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={nuevoBloqueo.dia_inicio}
                      onChange={(e) => setNuevoBloqueo({...nuevoBloqueo, dia_inicio: parseInt(e.target.value) || 1})}
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>Día del Mes (Fin) *</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={nuevoBloqueo.dia_fin}
                      onChange={(e) => setNuevoBloqueo({...nuevoBloqueo, dia_fin: parseInt(e.target.value) || 5})}
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>Fecha Inicio *</label>
                    <input
                      type="date"
                      value={nuevoBloqueo.fecha_inicio}
                      onChange={(e) => setNuevoBloqueo({...nuevoBloqueo, fecha_inicio: e.target.value})}
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>Fecha Fin *</label>
                    <input
                      type="date"
                      value={nuevoBloqueo.fecha_fin}
                      onChange={(e) => setNuevoBloqueo({...nuevoBloqueo, fecha_fin: e.target.value})}
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                    />
                  </div>
                </>
              )}
              
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>Motivo (visible al empleado)</label>
                <input
                  type="text"
                  value={nuevoBloqueo.motivo}
                  onChange={(e) => setNuevoBloqueo({...nuevoBloqueo, motivo: e.target.value})}
                  placeholder="Ej: Cierre de nómina"
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                />
              </div>
            </div>
            
            {nuevoBloqueo.es_recurrente && (
              <div style={{ 
                marginTop: 12, 
                padding: 12, 
                backgroundColor: '#e3f2fd', 
                borderRadius: 8, 
                fontSize: 13, 
                color: '#1565c0' 
              }}>
                🔄 Este bloqueo se aplicará <strong>todos los meses</strong> del día {nuevoBloqueo.dia_inicio} al {nuevoBloqueo.dia_fin}
              </div>
            )}
            
            <button
              onClick={guardarBloqueo}
              disabled={guardandoBloqueo}
              style={{
                marginTop: 12,
                padding: '10px 24px',
                backgroundColor: guardandoBloqueo ? '#ccc' : '#ff9800',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: guardandoBloqueo ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {guardandoBloqueo ? '⏳ Guardando...' : '💾 Guardar Bloqueo'}
            </button>
          </div>
          
          {/* Lista de bloqueos existentes */}
          <h4 style={{ margin: '16px 0 12px', color: '#333' }}>📋 Bloqueos Configurados</h4>
          {bloqueos.length === 0 ? (
            <p style={{ color: '#999', textAlign: 'center', padding: 20 }}>No hay bloqueos configurados</p>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {bloqueos.map(bloqueo => {
                const tipoInfo = tiposSolicitud.find(t => t.id === bloqueo.tipo_solicitud) || 
                                 (bloqueo.tipo_solicitud === 'todas' ? { icono: '🚫', nombre: 'TODAS LAS SOLICITUDES' } : { icono: '❓', nombre: bloqueo.tipo_solicitud });
                const hoy = new Date();
                const diaHoy = hoy.getDate();
                const fechaHoyStr = hoy.toISOString().split('T')[0];
                
                // Determinar si está vigente
                let estaVigente = false;
                if (bloqueo.es_recurrente) {
                  estaVigente = diaHoy >= bloqueo.dia_inicio && diaHoy <= bloqueo.dia_fin;
                } else {
                  estaVigente = bloqueo.fecha_inicio <= fechaHoyStr && bloqueo.fecha_fin >= fechaHoyStr;
                }
                
                return (
                  <div
                    key={bloqueo.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 12,
                      backgroundColor: bloqueo.activo ? (estaVigente ? '#ffebee' : '#fff') : '#f5f5f5',
                      borderRadius: 8,
                      border: `2px solid ${bloqueo.activo ? (estaVigente ? '#f44336' : '#ddd') : '#ccc'}`,
                      opacity: bloqueo.activo ? 1 : 0.6
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 24 }}>{tipoInfo.icono}</span>
                      <div>
                        <div style={{ fontWeight: 'bold', color: '#333' }}>
                          {tipoInfo.nombre}
                          {estaVigente && bloqueo.activo && (
                            <span style={{ 
                              marginLeft: 8, 
                              backgroundColor: '#f44336', 
                              color: 'white', 
                              padding: '2px 8px', 
                              borderRadius: 4, 
                              fontSize: 10 
                            }}>
                              VIGENTE
                            </span>
                          )}
                          {bloqueo.es_recurrente && (
                            <span style={{ 
                              marginLeft: 8, 
                              backgroundColor: '#2196f3', 
                              color: 'white', 
                              padding: '2px 8px', 
                              borderRadius: 4, 
                              fontSize: 10 
                            }}>
                              🔄 RECURRENTE
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {bloqueo.es_recurrente ? (
                            <span>🔄 Todos los meses del día <strong>{bloqueo.dia_inicio}</strong> al <strong>{bloqueo.dia_fin}</strong></span>
                          ) : (
                            <span>📅 {new Date(bloqueo.fecha_inicio + 'T12:00:00').toLocaleDateString('es-CO')} → {new Date(bloqueo.fecha_fin + 'T12:00:00').toLocaleDateString('es-CO')}</span>
                          )}
                          {bloqueo.motivo && <span style={{ marginLeft: 8 }}>| 💬 {bloqueo.motivo}</span>}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => toggleBloqueoActivo(bloqueo)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: bloqueo.activo ? '#4caf50' : '#9e9e9e',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                      >
                        {bloqueo.activo ? '✅ Activo' : '⏸️ Inactivo'}
                      </button>
                      <button
                        onClick={() => eliminarBloqueo(bloqueo.id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 12
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Contadores */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={{
          padding: 20,
          backgroundColor: '#fff3e0',
          borderRadius: 12,
          textAlign: 'center',
          border: '2px solid #ff9800'
        }}>
          <div style={{ fontSize: 32, fontWeight: 'bold', color: '#e65100' }}>{contadores.recibidos}</div>
          <div style={{ color: '#e65100' }}>📥 Recibidos</div>
        </div>
        <div style={{
          padding: 20,
          backgroundColor: '#e3f2fd',
          borderRadius: 12,
          textAlign: 'center',
          border: '2px solid #2196f3'
        }}>
          <div style={{ fontSize: 32, fontWeight: 'bold', color: '#1565c0' }}>{contadores.en_proceso}</div>
          <div style={{ color: '#1565c0' }}>⏳ En Proceso</div>
        </div>
        <div style={{
          padding: 20,
          backgroundColor: '#e8f5e9',
          borderRadius: 12,
          textAlign: 'center',
          border: '2px solid #4caf50'
        }}>
          <div style={{ fontSize: 32, fontWeight: 'bold', color: '#2e7d32' }}>{contadores.aprobados}</div>
          <div style={{ color: '#2e7d32' }}>✅ Aprobados</div>
        </div>
        <div style={{
          padding: 20,
          backgroundColor: '#ffebee',
          borderRadius: 12,
          textAlign: 'center',
          border: '2px solid #f44336'
        }}>
          <div style={{ fontSize: 32, fontWeight: 'bold', color: '#c62828' }}>{contadores.negados}</div>
          <div style={{ color: '#c62828' }}>❌ Negados</div>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>Estado</label>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #ddd', minWidth: 150 }}
          >
            <option value="todas">Todas</option>
            <option value="recibido">📥 Recibidos</option>
            <option value="en_proceso">⏳ En Proceso</option>
            <option value="pendiente_confirmacion">📨 Pendiente Confirmación</option>
            <option value="aprobado">✅ Aprobados</option>
            <option value="negado">❌ Negados</option>
            <option value="rechazado_empleado">🔄 Rechazado por Empleado</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>Tipo</label>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #ddd', minWidth: 180 }}
          >
            <option value="todos">Todos los tipos</option>
            {tiposSolicitud.map(t => (
              <option key={t.id} value={t.id}>{t.icono} {t.nombre}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>Empleado</label>
          <div style={{ position: 'relative' }}>
            {empleadoSeleccionadoNombre ? (
              <div style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: '1px solid #4caf50',
                backgroundColor: '#e8f5e9',
                minWidth: 250,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ fontWeight: 'bold', color: '#2e7d32', fontSize: 13 }}>
                  ✅ {empleadoSeleccionadoNombre}
                </span>
                <button
                  onClick={() => {
                    setFiltroEmpleado('');
                    setEmpleadoSeleccionadoNombre('');
                    setMostrarListaEmpleados(false);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 14,
                    color: '#666'
                  }}
                >
                  ✖
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={filtroEmpleado}
                  onChange={(e) => {
                    setFiltroEmpleado(e.target.value);
                    setMostrarListaEmpleados(e.target.value.length > 0);
                  }}
                  onFocus={() => {
                    if (filtroEmpleado.length > 0) setMostrarListaEmpleados(true);
                  }}
                  placeholder="Buscar por nombre o documento..."
                  style={{ 
                    padding: '8px 16px', 
                    borderRadius: 8, 
                    border: '1px solid #ddd', 
                    minWidth: 250,
                    paddingRight: filtroEmpleado ? 30 : 16
                  }}
                />
                {filtroEmpleado && (
                  <button
                    onClick={() => {
                      setFiltroEmpleado('');
                      setMostrarListaEmpleados(false);
                    }}
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 14,
                      color: '#999'
                    }}
                  >
                    ✖
                  </button>
                )}
              </>
            )}
            {/* Lista desplegable de empleados */}
            {mostrarListaEmpleados && empleadosLista.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: 8,
                maxHeight: 200,
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                marginTop: 4
              }}>
                {empleadosLista
                  .filter(emp => {
                    const busqueda = filtroEmpleado.toLowerCase();
                    const nombre = `${emp.nombre} ${emp.apellido}`.toLowerCase();
                    const doc = (emp.documento || '').toLowerCase();
                    return nombre.includes(busqueda) || doc.includes(busqueda);
                  })
                  .slice(0, 10)
                  .map(emp => (
                    <div
                      key={emp.documento}
                      onClick={() => {
                        setFiltroEmpleado(emp.documento);
                        setEmpleadoSeleccionadoNombre(`${emp.nombre} ${emp.apellido}`);
                        setMostrarListaEmpleados(false);
                      }}
                      style={{
                        padding: 10,
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e3f2fd'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <div style={{ fontWeight: 'bold', fontSize: 13 }}>{emp.nombre} {emp.apellido}</div>
                      <div style={{ fontSize: 11, color: '#666' }}>
                        📄 {emp.documento} | {emp.cargo || 'Sin cargo'}
                      </div>
                    </div>
                  ))
                }
                {empleadosLista.filter(emp => {
                  const busqueda = filtroEmpleado.toLowerCase();
                  const nombre = `${emp.nombre} ${emp.apellido}`.toLowerCase();
                  const doc = (emp.documento || '').toLowerCase();
                  return nombre.includes(busqueda) || doc.includes(busqueda);
                }).length === 0 && (
                  <div style={{ padding: 10, color: '#999', textAlign: 'center', fontSize: 12 }}>
                    No se encontraron empleados
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: '#666' }}>&nbsp;</label>
          <button
            onClick={cargarSolicitudes}
            style={{
              padding: '8px 16px',
              backgroundColor: '#00897b',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer'
            }}
          >
            🔄 Actualizar
          </button>
        </div>
      </div>

      {/* Lista de solicitudes */}
      {cargando ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
          <p>Cargando solicitudes...</p>
        </div>
      ) : filtroRespuesta === 'ninguna' ? (
        <div style={{
          padding: 60,
          backgroundColor: '#f5f5f5',
          borderRadius: 16,
          textAlign: 'center',
          border: '2px dashed #ccc'
        }}>
          <h3 style={{ color: '#333', marginBottom: 12 }}>Selecciona una opción para ver las solicitudes</h3>
          <p style={{ color: '#666', fontSize: 15 }}>
            Usa los botones <strong>⏳ Pendientes de Respuesta</strong> o <strong>✅ Con Respuesta</strong> en el panel superior
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 24 }}>
            <div style={{ padding: 16, backgroundColor: '#fff3e0', borderRadius: 12, minWidth: 150 }}>
              <div style={{ fontSize: 28 }}>⏳</div>
              <div style={{ fontWeight: 'bold', color: '#e65100' }}>Pendientes</div>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ff9800' }}>
                {solicitudes.filter(s => ['recibido', 'pendiente', 'en_proceso'].includes(s.estado)).length}
              </div>
            </div>
            <div style={{ padding: 16, backgroundColor: '#e8f5e9', borderRadius: 12, minWidth: 150 }}>
              <div style={{ fontSize: 28 }}>✅</div>
              <div style={{ fontWeight: 'bold', color: '#2e7d32' }}>Con Respuesta</div>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#4caf50' }}>
                {solicitudes.filter(s => !['recibido', 'pendiente', 'en_proceso'].includes(s.estado)).length}
              </div>
            </div>
          </div>
        </div>
      ) : solicitudesFiltradas.length === 0 ? (
        <div style={{
          padding: 40,
          backgroundColor: '#f5f5f5',
          borderRadius: 12,
          textAlign: 'center'
        }}>
          <div style={{ fontSize: 60, marginBottom: 16 }}>📭</div>
          <h3>No hay solicitudes</h3>
          <p style={{ color: '#666' }}>
            {filtroRespuesta === 'sin_respuesta' 
              ? '¡Excelente! No hay solicitudes pendientes de respuesta'
              : filtroRespuesta === 'con_respuesta'
              ? 'Aún no hay solicitudes con respuesta'
              : 'No hay solicitudes que coincidan con los filtros seleccionados'
            }
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {solicitudesFiltradas.map(sol => {
            const estadoStyle = getEstadoColor(sol.estado);
            const tipo = tiposSolicitud.find(t => t.id === sol.tipo);
            const esGestionable = ['recibido', 'pendiente', 'en_proceso', 'rechazado_empleado'].includes(sol.estado);
            const puedeVerDetalle = true; // Siempre se puede ver el detalle de cualquier solicitud
            // Parsear archivos adjuntos
            let archivosAdj = [];
            try {
              if (sol.archivos_adjuntos) {
                archivosAdj = typeof sol.archivos_adjuntos === 'string' 
                  ? JSON.parse(sol.archivos_adjuntos) 
                  : sol.archivos_adjuntos;
              }
            } catch (e) { archivosAdj = []; }
            
            let archivosResp = [];
            try {
              if (sol.archivos_respuesta) {
                archivosResp = typeof sol.archivos_respuesta === 'string' 
                  ? JSON.parse(sol.archivos_respuesta) 
                  : sol.archivos_respuesta;
              }
            } catch (e) { archivosResp = []; }
            
            return (
              <div
                key={sol.id}
                style={{
                  padding: 20,
                  backgroundColor: 'white',
                  border: esGestionable ? '2px solid #ff9800' : '1px solid #e0e0e0',
                  borderRadius: 12,
                  cursor: 'pointer'
                }}
                onClick={() => tienePermiso('rh_intranet_solicitudes_ver') && setSolicitudSeleccionada(sol)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 24 }}>{tipo?.icono || '📝'}</span>
                      <div>
                        <div style={{ fontWeight: 'bold', color: '#1a237e' }}>
                          {tipo?.nombre || sol.tipo}
                        </div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {sol.empleado_nombre} | Doc: {sol.documento}
                        </div>
                      </div>
                    </div>
                    <p style={{ margin: '8px 0', color: '#333', fontSize: 14 }}>
                      {sol.descripcion}
                    </p>
                    <div style={{ fontSize: 12, color: '#999' }}>
                      📅 Radicada: {new Date(sol.fecha_creacion).toLocaleDateString('es-CO')}
                      {sol.fecha_inicio && ` | Del ${sol.fecha_inicio} al ${sol.fecha_fin}`}
                    </div>
                    
                    {/* Archivos adjuntos del empleado */}
                    {archivosAdj.length > 0 && (
                      <div style={{ marginTop: 8, padding: 8, backgroundColor: '#e3f2fd', borderRadius: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 'bold' }}>📎 Archivos del empleado:</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                          {archivosAdj.map((arch, idx) => (
                            <a
                              key={idx}
                              href={arch.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                padding: '3px 8px',
                                backgroundColor: 'white',
                                borderRadius: 4,
                                fontSize: 11,
                                color: '#1976d2',
                                textDecoration: 'none'
                              }}
                            >
                              📄 {arch.nombre}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{
                      padding: '6px 12px',
                      backgroundColor: estadoStyle.bg,
                      color: estadoStyle.color,
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 'bold'
                    }}>
                      {estadoStyle.texto}
                    </span>
                    {esGestionable && tienePermiso('rh_intranet_solicitudes_aprobar') && (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#ff9800' }}>
                        👆 Click para gestionar
                      </div>
                    )}
                    {!esGestionable && tienePermiso('rh_intranet_solicitudes_ver') && (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#666' }}>
                        👆 Click para ver detalle
                      </div>
                    )}
                  </div>
                </div>
                {sol.respuesta && (
                  <div style={{
                    marginTop: 12,
                    padding: 12,
                    backgroundColor: estadoStyle.bg,
                    borderRadius: 8,
                    fontSize: 13
                  }}>
                    <strong>💬 Respuesta:</strong> {sol.respuesta}
                    {sol.fecha_respuesta && (
                      <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                        Respondida: {new Date(sol.fecha_respuesta).toLocaleDateString('es-CO')}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Archivos de respuesta */}
                {archivosResp.length > 0 && (
                  <div style={{ marginTop: 8, padding: 8, backgroundColor: '#fff8e1', borderRadius: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 'bold' }}>📎 Archivos de respuesta:</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                      {archivosResp.map((arch, idx) => (
                        <a
                          key={idx}
                          href={arch.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            padding: '3px 8px',
                            backgroundColor: 'white',
                            borderRadius: 4,
                            fontSize: 11,
                            color: '#f57c00',
                            textDecoration: 'none'
                          }}
                        >
                          📄 {arch.nombre}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal para gestionar solicitud */}
      {solicitudSeleccionada && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 16,
            padding: 24,
            width: '100%',
            maxWidth: 500,
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: '#1a237e' }}>📝 Gestionar Solicitud</h3>
              <button
                onClick={() => { setSolicitudSeleccionada(null); setRespuesta(''); setArchivosRespuesta([]); setRequiereConfirmacion(false); }}
                style={{
                  backgroundColor: '#f5f5f5',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 12px',
                  cursor: 'pointer'
                }}
              >
                ✖️
              </button>
            </div>

            <div style={{ marginBottom: 20, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 24 }}>
                  {tiposSolicitud.find(t => t.id === solicitudSeleccionada.tipo)?.icono || '📝'}
                </span>
                <div>
                  <div style={{ fontWeight: 'bold' }}>
                    {tiposSolicitud.find(t => t.id === solicitudSeleccionada.tipo)?.nombre || solicitudSeleccionada.tipo}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Solicitante: {solicitudSeleccionada.empleado_nombre} | Doc: {solicitudSeleccionada.documento}
                  </div>
                </div>
              </div>
              <p style={{ margin: 0, color: '#333' }}>{solicitudSeleccionada.descripcion}</p>
              {solicitudSeleccionada.fecha_inicio && (
                <div style={{ marginTop: 12, fontSize: 13, color: '#666' }}>
                  📅 Período: {solicitudSeleccionada.fecha_inicio} al {solicitudSeleccionada.fecha_fin}
                </div>
              )}
              
              {/* Archivos adjuntos del empleado */}
              {(() => {
                let archivos = [];
                try {
                  if (solicitudSeleccionada.archivos_adjuntos) {
                    archivos = typeof solicitudSeleccionada.archivos_adjuntos === 'string' 
                      ? JSON.parse(solicitudSeleccionada.archivos_adjuntos) 
                      : solicitudSeleccionada.archivos_adjuntos;
                  }
                } catch (e) { archivos = []; }
                
                return archivos.length > 0 ? (
                  <div style={{ marginTop: 12, padding: 10, backgroundColor: '#e3f2fd', borderRadius: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 6 }}>📎 Archivos adjuntos del empleado:</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {archivos.map((arch, idx) => (
                        <div key={idx} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          padding: '6px 10px',
                          backgroundColor: documentosGuardadosEnFicha.includes(arch.url) ? '#e8f5e9' : 'white',
                          borderRadius: 6,
                          border: documentosGuardadosEnFicha.includes(arch.url) ? '1px solid #4caf50' : '1px solid #bbdefb'
                        }}>
                          <a
                            href={arch.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: 12,
                              color: '#1976d2',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4
                            }}
                          >
                            📄 {arch.nombre}
                          </a>
                          {documentosGuardadosEnFicha.includes(arch.url) ? (
                            <span style={{
                              padding: '4px 8px',
                              backgroundColor: '#4caf50',
                              color: 'white',
                              borderRadius: 4,
                              fontSize: 11,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4
                            }}>
                              ✅ Guardado en ficha del empleado
                            </span>
                          ) : (
                            <button
                              onClick={() => guardarDocumentoEnFichaEmpleado(arch, solicitudSeleccionada.documento)}
                              disabled={guardandoEnFicha === arch.url}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: guardandoEnFicha === arch.url ? '#ccc' : '#4caf50',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                fontSize: 11,
                                cursor: guardandoEnFicha === arch.url ? 'wait' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                              }}
                              title="Guardar en la ficha del empleado"
                            >
                              {guardandoEnFicha === arch.url ? '⏳' : '💾'} Guardar en Ficha
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}
            </div>

            {/* Historial de conversación */}
            {solicitudSeleccionada.historial_conversacion && solicitudSeleccionada.historial_conversacion.length > 0 && (
              <div style={{ 
                marginBottom: 20, 
                padding: 16, 
                backgroundColor: '#f5f5f5', 
                borderRadius: 12,
                border: '1px solid #ddd',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                <div style={{ 
                  fontWeight: 'bold', 
                  color: '#333', 
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <span style={{ fontSize: 20 }}>📜</span>
                  Historial de Conversación:
                </div>
                {solicitudSeleccionada.historial_conversacion.map((msg, idx) => (
                  <div key={idx} style={{
                    padding: 12,
                    marginBottom: 8,
                    borderRadius: 8,
                    backgroundColor: msg.tipo === 'admin' ? '#e3f2fd' : '#e8f5e9',
                    borderLeft: `4px solid ${msg.tipo === 'admin' ? '#2196f3' : '#4caf50'}`
                  }}>
                    <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>
                      {msg.tipo === 'admin' ? '👔 RRHH' : '👤 Empleado'} - {new Date(msg.fecha).toLocaleString('es-CO')}
                    </div>
                    <p style={{ margin: 0, color: '#333', fontSize: 13 }}>{msg.mensaje}</p>
                    {msg.archivos && msg.archivos.length > 0 && (
                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {msg.archivos.map((arch, i) => (
                          <div key={i} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '4px 8px',
                            backgroundColor: documentosGuardadosEnFicha.includes(arch.url) 
                              ? '#a5d6a7' 
                              : (msg.tipo === 'admin' ? '#bbdefb' : '#c8e6c9'),
                            borderRadius: 4,
                            border: documentosGuardadosEnFicha.includes(arch.url) ? '1px solid #4caf50' : 'none'
                          }}>
                            <a href={arch.url} target="_blank" rel="noopener noreferrer"
                              style={{ fontSize: 11, color: msg.tipo === 'admin' ? '#1565c0' : '#1b5e20', textDecoration: 'none' }}>
                              📎 {arch.nombre}
                            </a>
                            {msg.tipo !== 'admin' && (
                              documentosGuardadosEnFicha.includes(arch.url) ? (
                                <span style={{
                                  padding: '2px 6px',
                                  backgroundColor: '#1b5e20',
                                  color: 'white',
                                  borderRadius: 3,
                                  fontSize: 10
                                }}>
                                  ✅ Guardado
                                </span>
                              ) : (
                                <button
                                  onClick={() => guardarDocumentoEnFichaEmpleado(arch, solicitudSeleccionada.documento)}
                                  disabled={guardandoEnFicha === arch.url}
                                  style={{
                                    padding: '2px 6px',
                                    backgroundColor: guardandoEnFicha === arch.url ? '#ccc' : '#1b5e20',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 3,
                                    fontSize: 10,
                                    cursor: guardandoEnFicha === arch.url ? 'wait' : 'pointer'
                                  }}
                                  title="Guardar en ficha"
                                >
                                  {guardandoEnFicha === arch.url ? '⏳' : '💾'}
                                </button>
                              )
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Mostrar respuesta del empleado si existe */}
            {solicitudSeleccionada.respuesta_empleado && (
              <div style={{ 
                marginBottom: 20, 
                padding: 16, 
                backgroundColor: '#e8f5e9', 
                borderRadius: 12,
                border: '2px solid #4caf50'
              }}>
                <div style={{ 
                  fontWeight: 'bold', 
                  color: '#2e7d32', 
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <span style={{ fontSize: 20 }}>💬</span>
                  Última Respuesta del Empleado:
                </div>
                <p style={{ margin: 0, color: '#333' }}>
                  {solicitudSeleccionada.respuesta_empleado}
                </p>
                {solicitudSeleccionada.fecha_respuesta_empleado && (
                  <div style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                    📅 Respondió: {new Date(solicitudSeleccionada.fecha_respuesta_empleado).toLocaleString('es-CO')}
                  </div>
                )}
                {/* Mostrar archivos adjuntos del empleado */}
                {(() => {
                  let archivosEmpleado = solicitudSeleccionada.archivos_respuesta_empleado;
                  if (typeof archivosEmpleado === 'string') {
                    try { archivosEmpleado = JSON.parse(archivosEmpleado); } catch(e) { archivosEmpleado = []; }
                  }
                  return archivosEmpleado && archivosEmpleado.length > 0 ? (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 'bold', color: '#2e7d32', marginBottom: 6 }}>📎 Archivos adjuntos del empleado:</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {archivosEmpleado.map((arch, i) => (
                          <div key={i} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '6px 12px',
                            backgroundColor: documentosGuardadosEnFicha.includes(arch.url) ? '#a5d6a7' : '#c8e6c9',
                            borderRadius: 6,
                            border: documentosGuardadosEnFicha.includes(arch.url) ? '2px solid #4caf50' : '1px solid #a5d6a7'
                          }}>
                            <a href={arch.url} target="_blank" rel="noopener noreferrer"
                              style={{
                                fontSize: 12,
                                color: '#1b5e20',
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4
                              }}>
                              📄 {arch.nombre}
                            </a>
                            {documentosGuardadosEnFicha.includes(arch.url) ? (
                              <span style={{
                                padding: '4px 8px',
                                backgroundColor: '#1b5e20',
                                color: 'white',
                                borderRadius: 4,
                                fontSize: 11,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                              }}>
                                ✅ Guardado en ficha del empleado
                              </span>
                            ) : (
                              <button
                                onClick={() => guardarDocumentoEnFichaEmpleado(arch, solicitudSeleccionada.documento)}
                                disabled={guardandoEnFicha === arch.url}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: guardandoEnFicha === arch.url ? '#ccc' : '#1b5e20',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 4,
                                  fontSize: 11,
                                  cursor: guardandoEnFicha === arch.url ? 'wait' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4
                                }}
                                title="Guardar en la ficha del empleado"
                              >
                                {guardandoEnFicha === arch.url ? '⏳' : '💾'} Guardar en Ficha
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>
            )}

            {/* Indicador si está esperando respuesta del empleado */}
            {solicitudSeleccionada.requiere_confirmacion && !solicitudSeleccionada.respuesta_empleado && 
             !['aprobado', 'aprobada', 'negado', 'rechazada'].includes(solicitudSeleccionada.estado) && (
              <div style={{ 
                marginBottom: 20, 
                padding: 16, 
                backgroundColor: '#fff3e0', 
                borderRadius: 12,
                border: '2px solid #ff9800',
                textAlign: 'center'
              }}>
                <span style={{ fontSize: 24 }}>⏳</span>
                <div style={{ fontWeight: 'bold', color: '#e65100', marginTop: 8 }}>
                  Esperando respuesta del empleado...
                </div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  El empleado aún no ha respondido a la propuesta enviada.
                </div>
              </div>
            )}

            {/* Mostrar resumen cuando la solicitud ya está cerrada (aprobada o negada) */}
            {['aprobado', 'aprobada', 'negado', 'rechazada'].includes(solicitudSeleccionada.estado) && (
              <div style={{ 
                marginBottom: 20, 
                padding: 16, 
                backgroundColor: solicitudSeleccionada.estado === 'aprobado' || solicitudSeleccionada.estado === 'aprobada' ? '#e8f5e9' : '#ffebee', 
                borderRadius: 12,
                border: `2px solid ${solicitudSeleccionada.estado === 'aprobado' || solicitudSeleccionada.estado === 'aprobada' ? '#4caf50' : '#f44336'}`,
                textAlign: 'center'
              }}>
                <span style={{ fontSize: 32 }}>
                  {solicitudSeleccionada.estado === 'aprobado' || solicitudSeleccionada.estado === 'aprobada' ? '✅' : '❌'}
                </span>
                <div style={{ 
                  fontWeight: 'bold', 
                  color: solicitudSeleccionada.estado === 'aprobado' || solicitudSeleccionada.estado === 'aprobada' ? '#2e7d32' : '#c62828', 
                  marginTop: 8,
                  fontSize: 16
                }}>
                  Solicitud {solicitudSeleccionada.estado === 'aprobado' || solicitudSeleccionada.estado === 'aprobada' ? 'APROBADA' : 'RECHAZADA'}
                </div>
                {solicitudSeleccionada.respuesta && (
                  <div style={{ marginTop: 12, padding: 12, backgroundColor: 'white', borderRadius: 8, textAlign: 'left' }}>
                    <strong>💬 Respuesta final:</strong>
                    <p style={{ margin: '8px 0 0 0', color: '#333' }}>{solicitudSeleccionada.respuesta}</p>
                  </div>
                )}
                {solicitudSeleccionada.fecha_respuesta && (
                  <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                    📅 Fecha de respuesta: {new Date(solicitudSeleccionada.fecha_respuesta).toLocaleString('es-CO')}
                  </div>
                )}
              </div>
            )}

            {/* Solo mostrar formulario de respuesta si la solicitud NO está cerrada */}
            {!['aprobado', 'aprobada', 'negado', 'rechazada'].includes(solicitudSeleccionada.estado) && (
              <>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                    {solicitudSeleccionada.respuesta_empleado ? 'Respuesta definitiva:' : 'Respuesta / Comentario:'}
                  </label>
                  <textarea
                    value={respuesta}
                    onChange={(e) => setRespuesta(e.target.value)}
                    rows={3}
                    placeholder={solicitudSeleccionada.respuesta_empleado 
                      ? "Escribe la respuesta definitiva basada en la respuesta del empleado..." 
                      : "Escribe una respuesta o comentario..."}
                    style={{
                      width: '100%',
                      padding: 12,
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

            {/* Sección para adjuntar archivos de respuesta */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                📎 Adjuntar archivos (opcional):
              </label>
              <div style={{
                border: '2px dashed #ddd',
                borderRadius: 8,
                padding: 16,
                textAlign: 'center',
                backgroundColor: '#fafafa'
              }}>
                <input
                  type="file"
                  id="archivo-respuesta-admin"
                  multiple
                  onChange={async (e) => {
                    const files = Array.from(e.target.files);
                    for (const file of files) {
                      await subirArchivoRespuesta(file);
                    }
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                />
                <label
                  htmlFor="archivo-respuesta-admin"
                  style={{
                    display: 'inline-block',
                    padding: '8px 16px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 13
                  }}
                >
                  {subiendoArchivo ? '⏳ Subiendo...' : '📁 Seleccionar archivos'}
                </label>
              </div>
              
              {archivosRespuesta.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  {archivosRespuesta.map((archivo, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '6px 10px',
                      backgroundColor: '#e8f5e9',
                      borderRadius: 6,
                      marginBottom: 4,
                      fontSize: 12
                    }}>
                      <span>📄 {archivo.nombre}</span>
                      <button
                        type="button"
                        onClick={() => setArchivosRespuesta(prev => prev.filter((_, i) => i !== idx))}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#c62828',
                          cursor: 'pointer',
                          fontSize: 14
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Checkbox para propuesta que requiere confirmación */}
            <div style={{ 
              marginBottom: 20, 
              padding: 16, 
              backgroundColor: '#f3e5f5', 
              borderRadius: 8,
              border: '1px solid #ce93d8'
            }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 12, 
                cursor: 'pointer',
                fontWeight: 'bold',
                color: '#7b1fa2'
              }}>
                <input
                  type="checkbox"
                  checked={requiereConfirmacion}
                  onChange={(e) => setRequiereConfirmacion(e.target.checked)}
                  style={{ 
                    width: 20, 
                    height: 20,
                    accentColor: '#7b1fa2'
                  }}
                />
                <span>📨 Enviar como propuesta (requiere confirmación del empleado)</span>
              </label>
              <p style={{ 
                margin: '8px 0 0 32px', 
                fontSize: 12, 
                color: '#666' 
              }}>
                Si activas esta opción, el empleado podrá confirmar o rechazar la respuesta/propuesta que envíes. 
                Útil para negociaciones de fechas, condiciones, etc.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {/* Botón En Proceso / Enviar Respuesta */}
              {tienePermiso('rh_intranet_solicitudes_aprobar') && (
                solicitudSeleccionada.estado !== 'en_proceso' || 
                (solicitudSeleccionada.estado === 'en_proceso' && solicitudSeleccionada.respuesta_empleado)
              ) && (
                <button
                  onClick={() => responderSolicitud('en_proceso')}
                  disabled={procesando}
                  style={{
                    flex: 1,
                    minWidth: 120,
                    padding: '12px 16px',
                    backgroundColor: '#2196f3',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: procesando ? 'wait' : 'pointer',
                    fontSize: 13,
                    fontWeight: 'bold'
                  }}
                >
                  {solicitudSeleccionada.estado === 'en_proceso' && solicitudSeleccionada.respuesta_empleado 
                    ? '💬 Responder al Empleado' 
                    : '⏳ En Proceso'}
                </button>
              )}
              {tienePermiso('rh_intranet_solicitudes_aprobar') && (
                <button
                  onClick={() => responderSolicitud('aprobado')}
                  disabled={procesando}
                  style={{
                    flex: 1,
                    minWidth: 120,
                    padding: '12px 16px',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: procesando ? 'wait' : 'pointer',
                    fontSize: 13,
                    fontWeight: 'bold'
                  }}
                >
                  ✅ Aprobar
                </button>
              )}
              {tienePermiso('rh_intranet_solicitudes_rechazar') && (
                <button
                  onClick={() => responderSolicitud('negado')}
                  disabled={procesando}
                  style={{
                    flex: 1,
                    minWidth: 120,
                    padding: '12px 16px',
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: procesando ? 'wait' : 'pointer',
                    fontSize: 13,
                    fontWeight: 'bold'
                  }}
                >
                  ❌ Negar
                </button>
              )}
            </div>
              </>
            )}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}

/* ====================== INFORME DE RENDIMIENTO Y COSTOS ====================== */
function InformeRendimientoCostos({
  empleados = [],
  nominasGuardadas = [],
  memorandos = [],
  ausencias = [],
  vacaciones = [],
  horariosRepo = [],
  prestamos = [],
  sedes = [],
  tienePermiso = () => true,
  empresaId = null,
  valoresAnuales = {}
}) {
  // Estados de filtros
  const [modoFiltro, setModoFiltro] = useState('todos'); // 'todos', 'individual', 'grupal'
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState('');
  const [empleadosSeleccionados, setEmpleadosSeleccionados] = useState([]);
  const [sedeSeleccionada, setSedeSeleccionada] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [periodoRapido, setPeriodoRapido] = useState('mes_actual');
  const [cargando, setCargando] = useState(false);
  const [datosCalculados, setDatosCalculados] = useState(null);
  const [vistaActiva, setVistaActiva] = useState('resumen'); // 'resumen', 'detalle', 'graficas', 'comparativo'
  
  // Función para establecer periodos rápidos
  const aplicarPeriodoRapido = (periodo) => {
    setPeriodoRapido(periodo);
    const hoy = new Date();
    let inicio, fin;
    
    switch (periodo) {
      case 'hoy':
        inicio = fin = toISO(hoy);
        break;
      case 'semana':
        const inicioSemana = new Date(hoy);
        inicioSemana.setDate(hoy.getDate() - hoy.getDay());
        inicio = toISO(inicioSemana);
        fin = toISO(hoy);
        break;
      case 'quincena_actual':
        const dia = hoy.getDate();
        if (dia <= 15) {
          inicio = toISO(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
          fin = toISO(new Date(hoy.getFullYear(), hoy.getMonth(), 15));
        } else {
          inicio = toISO(new Date(hoy.getFullYear(), hoy.getMonth(), 16));
          fin = toISO(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0));
        }
        break;
      case 'quincena_anterior':
        // Quincena inmediatamente anterior
        const diaQ = hoy.getDate();
        if (diaQ <= 15) {
          // Estamos en Q1, entonces Q anterior es Q2 del mes anterior
          inicio = toISO(new Date(hoy.getFullYear(), hoy.getMonth() - 1, 16));
          fin = toISO(new Date(hoy.getFullYear(), hoy.getMonth(), 0));
        } else {
          // Estamos en Q2, entonces Q anterior es Q1 del mismo mes
          inicio = toISO(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
          fin = toISO(new Date(hoy.getFullYear(), hoy.getMonth(), 15));
        }
        break;
      case 'mes_actual':
        inicio = toISO(new Date(hoy.getFullYear(), hoy.getMonth(), 1));
        fin = toISO(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0));
        break;
      case 'ultimo_mes':
        inicio = toISO(new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1));
        fin = toISO(new Date(hoy.getFullYear(), hoy.getMonth(), 0));
        break;
      case 'ultimos_2_meses':
        inicio = toISO(new Date(hoy.getFullYear(), hoy.getMonth() - 2, 1));
        fin = toISO(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0));
        break;
      case 'ultimos_3_meses':
        inicio = toISO(new Date(hoy.getFullYear(), hoy.getMonth() - 3, 1));
        fin = toISO(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0));
        break;
      case 'trimestre':
        const mesActual = hoy.getMonth();
        const inicioTrimestre = mesActual - (mesActual % 3);
        inicio = toISO(new Date(hoy.getFullYear(), inicioTrimestre, 1));
        fin = toISO(hoy);
        break;
      case 'semestre':
        const inicioSem = hoy.getMonth() < 6 ? 0 : 6;
        inicio = toISO(new Date(hoy.getFullYear(), inicioSem, 1));
        fin = toISO(hoy);
        break;
      case 'anio':
        inicio = toISO(new Date(hoy.getFullYear(), 0, 1));
        fin = toISO(hoy);
        break;
      case 'personalizado':
        // No cambiar las fechas, el usuario las ingresa manualmente
        return;
      default:
        return;
    }
    setFechaInicio(inicio);
    setFechaFin(fin);
  };
  
  // Aplicar periodo por defecto al cargar
  useEffect(() => {
    aplicarPeriodoRapido('mes_actual');
  }, []);
  
  // Función para obtener empleados filtrados según modo
  const getEmpleadosFiltrados = () => {
    // IMPORTANTE: Solo incluir empleados ACTIVOS (no inactivos)
    let lista = [...empleados].filter(e => {
      const estado = (e.estado || 'activo').toLowerCase().trim();
      // Solo excluir si explícitamente dice inactivo, retirado o desvinculado
      const esInactivo = estado === 'inactivo' || estado === 'retirado' || estado === 'desvinculado';
      return !esInactivo;
    });
    
    // DEBUG: Buscar empleado específico
    const jonnier = empleados.find(e => (e.nombre || '').toUpperCase().includes('JONNIER'));
    if (jonnier) {
      console.log("🔍 DEBUG - Empleado JONNIER encontrado:", {
        id: jonnier.id,
        nombre: jonnier.nombre,
        estado: jonnier.estado,
        sede: jonnier.sede,
        enListaFiltrada: lista.some(e => e.id === jonnier.id)
      });
    } else {
      console.log("⚠️ DEBUG - Empleado JONNIER NO encontrado en lista de empleados");
    }
    
    if (modoFiltro === 'individual' && empleadoSeleccionado) {
      lista = lista.filter(e => e.id === empleadoSeleccionado);
    } else if (modoFiltro === 'grupal') {
      if (sedeSeleccionada) {
        lista = lista.filter(e => e.sede === sedeSeleccionada);
      }
      if (empleadosSeleccionados.length > 0) {
        lista = lista.filter(e => empleadosSeleccionados.includes(e.id));
      }
    }
    
    console.log("📋 Empleados filtrados finales:", lista.length, "de", empleados.length, "totales");
    
    return lista;
  };
  
  // Función auxiliar para verificar si una fecha está en el rango
  const fechaEnRango = (fecha, inicio, fin) => {
    if (!fecha || !inicio || !fin) return false;
    const f = new Date(fecha);
    const i = new Date(inicio);
    const fn = new Date(fin);
    return f >= i && f <= fn;
  };
  
  // Función principal de cálculo
  const calcularInforme = () => {
    setCargando(true);
    
    setTimeout(() => {
      const empleadosFiltrados = getEmpleadosFiltrados();
      const resultados = [];
      
      // DEBUG: Ver datos de ausencias recibidos
      console.log("📊 INFORME - Datos recibidos:");
      console.log("   - Empleados filtrados:", empleadosFiltrados.length);
      console.log("   - Ausencias totales:", ausencias?.length || 0);
      console.log("   - Memorandos totales:", memorandos?.length || 0);
      console.log("   - Vacaciones totales:", vacaciones?.length || 0);
      console.log("   - Nóminas guardadas:", nominasGuardadas?.length || 0);
      console.log("   - Rango:", fechaInicio, "a", fechaFin);
      
      // Mostrar estructura de ausencias para depurar
      if (ausencias && ausencias.length > 0) {
        console.log("📋 Muestra de ausencias (primeras 5):");
        ausencias.slice(0, 5).forEach((aus, i) => {
          console.log(`   ${i+1}. empleadoId: ${aus.empleadoId}, tipo: ${aus.tipo || aus.tipoAusencia}, dias: ${aus.dias || aus.diastotales || aus.diasTotales}, fecha: ${aus.inicio || aus.fechaInicio || aus.fecha}`);
        });
      }
      
      // Totalizadores globales
      let totalHorasNormales = 0;
      let totalHorasExtras = 0;
      let totalHorasExtrasDiurnas = 0;
      let totalHorasExtrasNocturnas = 0;
      let totalHorasExtrasFestivas = 0;
      let totalHorasFestivas = 0;
      let totalHorasNocturnas = 0;
      let totalDescansos = 0;
      let totalDiasNoTrabajados = 0;
      let totalDiasIncapacidad = 0;
      let totalDiasVacaciones = 0;
      let totalMemorandos = 0;
      let totalLlamadosAtencion = 0;
      let totalPermisos = 0;
      let totalValorComidas = 0;
      let totalOtrosDescuentos = 0;
      let totalNominaBruta = 0;
      let totalNominaNeta = 0;
      let totalDescuentos = 0;
      
      empleadosFiltrados.forEach(emp => {
        const empId = emp.id;
        
        // ===== PRIMERO: Obtener fechas de ausencias del empleado =====
        // Esto nos permite excluirlas de los "días no trabajados"
        const fechasAusencia = new Set();
        let diasIncapacidad = 0;
        let diasPermiso = 0;
        let diasVacaciones = 0;
        
        // Procesar ausencias - CORREGIDO: Buscar ausencias que SE CRUCEN con el rango, no solo las que inicien en el rango
        (ausencias || []).forEach(aus => {
          if (aus.empleadoId === empId) {
            const fechaAusInicio = aus.inicio || aus.fechaInicio || aus.fecha || '';
            const dias = Number(aus.dias || aus.diastotales || aus.diasTotales || 1);
            const tipo = (aus.tipo || aus.tipoAusencia || '').toLowerCase();
            
            if (!fechaAusInicio) return;
            
            // Calcular fecha fin de la ausencia
            const ausInicioDate = new Date(fechaAusInicio);
            const ausFinDate = new Date(ausInicioDate);
            ausFinDate.setDate(ausFinDate.getDate() + dias - 1);
            
            // Rango del filtro
            const filtroInicioDate = new Date(fechaInicio);
            const filtroFinDate = new Date(fechaFin);
            
            // Verificar si la ausencia SE CRUZA con el rango (no solo si inicia en el rango)
            // La ausencia se cruza si: inicio_ausencia <= fin_filtro AND fin_ausencia >= inicio_filtro
            if (ausInicioDate <= filtroFinDate && ausFinDate >= filtroInicioDate) {
              // Calcular cuántos días de la ausencia caen dentro del rango
              const inicioEfectivo = ausInicioDate > filtroInicioDate ? ausInicioDate : filtroInicioDate;
              const finEfectivo = ausFinDate < filtroFinDate ? ausFinDate : filtroFinDate;
              const diasEnRango = Math.floor((finEfectivo - inicioEfectivo) / (1000 * 60 * 60 * 24)) + 1;
              
              // Agregar fechas de la ausencia (solo las que caen en el rango) al Set
              for (let d = 0; d < diasEnRango; d++) {
                const fechaDia = new Date(inicioEfectivo);
                fechaDia.setDate(fechaDia.getDate() + d);
                fechasAusencia.add(fechaDia.toISOString().split('T')[0]);
              }
              
              if (tipo.includes('incapacidad')) {
                diasIncapacidad += diasEnRango;
              } else if (tipo.includes('vacacion')) {
                diasVacaciones += diasEnRango;
              } else if (tipo.includes('permiso')) {
                diasPermiso += diasEnRango;
              }
            }
          }
        });
        
        // También procesar vacaciones de la tabla vacaciones - CORREGIDO: Buscar las que SE CRUCEN
        (vacaciones || []).forEach(vac => {
          if (vac.empleadoId === empId) {
            const vacInicio = vac.inicio || vac.fechaInicio;
            const vacFin = vac.fin || vac.fechaFin;
            if (vacInicio && vacFin) {
              const vi = new Date(vacInicio);
              const vf = new Date(vacFin);
              const fi = new Date(fechaInicio);
              const ff = new Date(fechaFin);
              
              // Verificar si las vacaciones SE CRUZAN con el rango
              if (vi <= ff && vf >= fi) {
                // Calcular cuántos días de vacaciones caen dentro del rango
                const inicioEfectivo = vi > fi ? vi : fi;
                const finEfectivo = vf < ff ? vf : ff;
                const diasEnRango = Math.floor((finEfectivo - inicioEfectivo) / (1000 * 60 * 60 * 24)) + 1;
                
                diasVacaciones += diasEnRango;
                
                // Agregar fechas de vacaciones (solo las que caen en el rango) al Set
                for (let d = 0; d < diasEnRango; d++) {
                  const fechaDia = new Date(inicioEfectivo);
                  fechaDia.setDate(fechaDia.getDate() + d);
                  fechasAusencia.add(fechaDia.toISOString().split('T')[0]);
                }
              }
            }
          }
        });
        
        // ===== HORAS TRABAJADAS (desde nóminas) =====
        let horasNormales = 0;
        let horasExtras = 0;
        let horasExtrasDiurnas = 0;
        let horasExtrasNocturnas = 0;
        let horasExtrasFestivas = 0;
        let horasFestivas = 0;
        let horasNocturnas = 0;
        let valorNominaBruta = 0;
        let valorNominaNeta = 0;
        let valorDescuentos = 0;
        let valorComidas = 0;
        let valorOtrosDescuentos = 0;
        
        // ===== DESCANSOS Y DÍAS NO TRABAJADOS =====
        let diasDescanso = 0;
        let diasNoTrabajados = 0;
        
        nominasGuardadas.forEach(nomina => {
          // Verificar si la nómina está en el rango de fechas
          if (nomina.rango && fechaEnRango(nomina.rango.inicio, fechaInicio, fechaFin)) {
            const item = nomina.items?.find(i => i.empleadoId === empId);
            if (item) {
              // Verificar método de liquidación del item
              const metodoItem = item.metodoLiquidacion || nomina.metodoLiquidacion || "con_recargos";
              const esSinRecargos = metodoItem === "sin_recargos";
              
              // Usar detalleHoras como fuente principal para horas extras, luego horasTotales
              const horasBase = item.horasTotales || {};
              const detalleH = item.detalleHoras || {};
              
              // Combinar: horas normales desde horasTotales o detalleHoras
              horasNormales += Number(horasBase.hNorm || detalleH.hNorm || 0);
              horasNocturnas += Number(horasBase.hNoct || detalleH.hNoct || 0);
              horasFestivas += Number(horasBase.hFest || detalleH.hFest || 0);
              
              // Horas extras: SOLO si NO es método sin_recargos
              if (!esSinRecargos) {
                const hExDia = Number(detalleH.hExDia || horasBase.hExDia || 0);
                const hExNoc = Number(detalleH.hExNoc || horasBase.hExNoc || 0);
                const hExFest = Number(detalleH.hExFest || horasBase.hExFest || 0);
                horasExtrasDiurnas += hExDia;
                horasExtrasNocturnas += hExNoc;
                horasExtrasFestivas += hExFest;
                horasExtras += hExDia + hExNoc + hExFest;
              }
              
              valorNominaBruta += Number(item.totalDevengado || item.bruto || 0);
              // Agregar valor de incapacidad si existe
              if (item.incapacidad && item.incapacidad.valorTotal) {
                valorNominaBruta += Number(item.incapacidad.valorTotal || 0);
              }
              valorNominaNeta += Number(item.neto || 0);
              valorDescuentos += Number(item.totalDescuentos || 0);
              valorComidas += Number(item.descuentos?.comida || 0);
              valorOtrosDescuentos += Number(item.descuentos?.otrosDescuentos || 0);
              
              // ===== LEER DESCANSOS Y DÍAS NO TRABAJADOS DESDE detallePorFecha (nómina) =====
              const detallePorFecha = item.detallePorFecha || [];
              
              // DEBUG: Ver estructura de detallePorFecha para empleados específicos
              if ((emp.nombre || '').toUpperCase().includes('JONNIER') && detallePorFecha.length > 0) {
                console.log(`🔍 DEBUG JONNIER - detallePorFecha (${detallePorFecha.length} días):`, detallePorFecha.slice(0, 5));
                console.log(`   Fechas de ausencia registradas:`, [...fechasAusencia]);
              }
              
              detallePorFecha.forEach(dia => {
                const horario = (dia.horario || '').toLowerCase().trim();
                const fechaDia = dia.fecha || '';
                
                // Calcular total de horas del día
                const horasTotalesDia = 
                  Number(dia.hNorm || 0) + 
                  Number(dia.hNoct || 0) + 
                  Number(dia.hFest || 0) + 
                  Number(dia.hExDia || 0) + 
                  Number(dia.hExNoc || 0) + 
                  Number(dia.hExFest || 0);
                
                // Verificar si esta fecha es una ausencia registrada (incapacidad, vacaciones, permiso)
                const esAusenciaRegistrada = fechasAusencia.has(fechaDia);
                
                // Verificar si es descanso (usando el campo booleano esDescanso o el texto del horario)
                if (dia.esDescanso === true || horario.includes('descanso')) {
                  diasDescanso++;
                } 
                // Verificar si es día no trabajado:
                // SOLO si NO es una ausencia ya registrada (incapacidad, vacaciones, permiso)
                else if (!esAusenciaRegistrada && (
                  dia.esNoTrabajado === true ||
                  horario.includes('no trabaj') ||
                  // Si todas las horas son 0 y no tiene tipo especial, es día no trabajado
                  (horasTotalesDia === 0 && !dia.esDescanso && !dia.esIncapacidad)
                )) {
                  diasNoTrabajados++;
                  // DEBUG: mostrar qué días no trabajados se encuentran
                  if ((emp.nombre || '').toUpperCase().includes('JONNIER')) {
                    console.log(`   ✅ Día no trabajado encontrado: ${fechaDia}, horas: ${horasTotalesDia}, horario: "${horario}"`);
                  }
                }
              });
            }
          }
        });
        
        // Log de incapacidades por empleado si tiene alguna
        if (diasIncapacidad > 0) {
          console.log(`🏥 Empleado ${emp.nombre || empId}: ${diasIncapacidad} días incapacidad encontrados`);
        }
        
        // ===== MEMORANDOS =====
        let countMemorandos = 0;
        let countLlamadosAtencion = 0;
        
        (memorandos || []).forEach(mem => {
          if (mem.empleadoId === empId) {
            // Usar fechaEnvio o fechaEmision
            const fechaMem = mem.fechaEnvio || mem.fechaEmision || '';
            if (fechaEnRango(fechaMem, fechaInicio, fechaFin)) {
              countMemorandos++;
              const tipo = (mem.tipo || mem.descripcion || '').toLowerCase();
              if (tipo.includes('llamado') || tipo.includes('atencion') || tipo.includes('atención')) {
                countLlamadosAtencion++;
              }
            }
          }
        });
        
        // Crear registro del empleado
        const registro = {
          id: empId,
          nombre: emp.nombre || `${emp.nombres || ''} ${emp.apellidos || ''}`.trim(),
          documento: emp.documento || '',
          cargo: emp.cargo || '',
          sede: emp.sede || '',
          horasNormales,
          horasNocturnas,
          horasFestivas,
          horasExtras,
          horasExtrasDiurnas,
          horasExtrasNocturnas,
          horasExtrasFestivas,
          totalHoras: horasNormales + horasNocturnas + horasFestivas + horasExtras,
          diasDescanso,
          diasNoTrabajados,
          diasIncapacidad,
          diasVacaciones,
          diasPermiso,
          countMemorandos,
          countLlamadosAtencion,
          valorComidas,
          valorOtrosDescuentos,
          valorNominaBruta,
          valorNominaNeta,
          valorDescuentos,
          costoTotal: valorNominaBruta + valorComidas,
        };
        
        resultados.push(registro);
        
        // Acumular totales
        totalHorasNormales += horasNormales;
        totalHorasExtras += horasExtras;
        totalHorasExtrasDiurnas += horasExtrasDiurnas;
        totalHorasExtrasNocturnas += horasExtrasNocturnas;
        totalHorasExtrasFestivas += horasExtrasFestivas;
        totalHorasFestivas += horasFestivas;
        totalHorasNocturnas += horasNocturnas;
        totalDescansos += diasDescanso;
        totalDiasNoTrabajados += diasNoTrabajados;
        totalDiasIncapacidad += diasIncapacidad;
        totalDiasVacaciones += diasVacaciones;
        totalMemorandos += countMemorandos;
        totalLlamadosAtencion += countLlamadosAtencion;
        totalPermisos += diasPermiso;
        totalValorComidas += valorComidas;
        totalOtrosDescuentos += valorOtrosDescuentos;
        totalNominaBruta += valorNominaBruta;
        totalNominaNeta += valorNominaNeta;
        totalDescuentos += valorDescuentos;
      });
      
      // Calcular promedios y métricas adicionales
      const numEmpleados = resultados.length || 1;
      
      setDatosCalculados({
        empleados: resultados,
        totales: {
          empleados: numEmpleados,
          horasNormales: totalHorasNormales,
          horasNocturnas: totalHorasNocturnas,
          horasFestivas: totalHorasFestivas,
          horasExtras: totalHorasExtras,
          horasExtrasDiurnas: totalHorasExtrasDiurnas,
          horasExtrasNocturnas: totalHorasExtrasNocturnas,
          horasExtrasFestivas: totalHorasExtrasFestivas,
          totalHoras: totalHorasNormales + totalHorasNocturnas + totalHorasFestivas + totalHorasExtras,
          descansos: totalDescansos,
          diasNoTrabajados: totalDiasNoTrabajados,
          diasIncapacidad: totalDiasIncapacidad,
          diasVacaciones: totalDiasVacaciones,
          memorandos: totalMemorandos,
          llamadosAtencion: totalLlamadosAtencion,
          permisos: totalPermisos,
          valorComidas: totalValorComidas,
          otrosDescuentos: totalOtrosDescuentos,
          nominaBruta: totalNominaBruta,
          nominaNeta: totalNominaNeta,
          descuentos: totalDescuentos,
        },
        promedios: {
          horasPorEmpleado: Math.round((totalHorasNormales + totalHorasExtras + totalHorasFestivas) / numEmpleados),
          costoPromedio: Math.round(totalNominaBruta / numEmpleados),
          incapacidadPromedio: (totalDiasIncapacidad / numEmpleados).toFixed(1),
          memorandosPromedio: (totalMemorandos / numEmpleados).toFixed(1),
        },
        periodo: { inicio: fechaInicio, fin: fechaFin },
      });
      
      setCargando(false);
    }, 500);
  };
  
  // Formato de moneda
  const formatMoney = (val) => {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(val || 0);
  };
  
  // Ordenar empleados por nombre
  const empleadosOrdenados = [...empleados].sort((a, b) => {
    const nombreA = (a.nombre || `${a.nombres || ''} ${a.apellidos || ''}`).trim();
    const nombreB = (b.nombre || `${b.nombres || ''} ${b.apellidos || ''}`).trim();
    return nombreA.localeCompare(nombreB, 'es', { sensitivity: 'base' });
  });
  
  // Obtener sedes únicas
  const sedesUnicas = [...new Set(empleados.map(e => e.sede).filter(Boolean))].sort();
  
  // Función para generar gráfica de barras SVG mejorada
  const generarGraficaBarras = (datos, titulo, color = '#3b82f6') => {
    if (!datos || datos.length === 0) return null;
    
    const maxVal = Math.max(...datos.map(d => d.valor), 1);
    // Ajustar ancho de barra según cantidad de datos - más ancho para nombres largos
    const barWidth = datos.length > 20 ? 30 : datos.length > 10 ? 45 : Math.max(50, Math.min(80, 700 / datos.length));
    const spacing = datos.length > 20 ? 12 : datos.length > 10 ? 18 : 22;
    const chartWidth = Math.max(700, datos.length * (barWidth + spacing) + 100);
    const chartHeight = 180;
    const marginTop = 50; // Espacio arriba para los números (siempre afuera)
    const marginBottom = 130; // Aumentado para nombres más largos
    
    return (
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 16px', fontSize: 15, color: '#1e293b', fontWeight: 700 }}>{titulo}</h4>
        <div style={{ overflowX: 'auto', background: '#f8fafc', borderRadius: 12, padding: 16 }}>
          <svg width={chartWidth} height={chartHeight + marginBottom + marginTop} style={{ display: 'block' }}>
            {/* Líneas de guía horizontales */}
            {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
              <g key={i}>
                <line 
                  x1="50" 
                  y1={marginTop + chartHeight - (pct * chartHeight)} 
                  x2={chartWidth - 10} 
                  y2={marginTop + chartHeight - (pct * chartHeight)} 
                  stroke="#e2e8f0" 
                  strokeWidth="1"
                  strokeDasharray={pct === 0 ? "0" : "4,4"}
                />
                <text
                  x="45"
                  y={marginTop + chartHeight - (pct * chartHeight) + 4}
                  textAnchor="end"
                  fontSize={9}
                  fill="#94a3b8"
                >
                  {typeof maxVal === 'number' && maxVal > 999999 
                    ? `$${(Math.round(maxVal * pct) / 1000000).toFixed(1)}M`
                    : Math.round(maxVal * pct).toLocaleString()}
                </text>
              </g>
            ))}
            
            {/* Barras */}
            {datos.map((d, i) => {
              const barHeight = Math.max(2, (d.valor / maxVal) * chartHeight); // Mínimo 2px para valores muy pequeños
              const x = i * (barWidth + spacing) + 60;
              const y = marginTop + chartHeight - barHeight;
              
              // Número SIEMPRE arriba de la barra
              const numeroY = y - 8;
              
              return (
                <g key={i}>
                  {/* Sombra de la barra */}
                  <rect
                    x={x + 2}
                    y={y + 2}
                    width={barWidth}
                    height={barHeight}
                    fill="rgba(0,0,0,0.08)"
                    rx={4}
                  />
                  {/* Barra principal con gradiente */}
                  <defs>
                    <linearGradient id={`bar-grad-${i}`} x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor={d.color || color} stopOpacity="1"/>
                      <stop offset="100%" stopColor={d.color || color} stopOpacity="0.7"/>
                    </linearGradient>
                  </defs>
                  <rect
                    x={x}
                    y={y}
                    width={barWidth}
                    height={barHeight}
                    fill={`url(#bar-grad-${i})`}
                    rx={4}
                    style={{ transition: 'all 0.3s', cursor: 'pointer' }}
                  />
                  {/* Valor - SIEMPRE arriba de la barra */}
                  <text
                    x={x + barWidth / 2}
                    y={numeroY}
                    textAnchor="middle"
                    fontSize={datos.length > 15 ? 9 : 11}
                    fill="#1e293b"
                    fontWeight="700"
                  >
                    {typeof d.valor === 'number' && d.valor > 999999 
                      ? `$${(d.valor / 1000000).toFixed(1)}M`
                      : d.valor.toLocaleString()}
                  </text>
                  {/* Etiqueta debajo */}
                  <text
                    x={x + barWidth / 2}
                    y={marginTop + chartHeight + 20}
                    textAnchor="end"
                    fontSize={datos.length > 15 ? 9 : 11}
                    fill="#475569"
                    fontWeight="600"
                    transform={`rotate(-55 ${x + barWidth / 2} ${marginTop + chartHeight + 20})`}
                  >
                    {d.label}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    );
  };
  
  // Función para generar gráfica circular SVG mejorada
  const generarGraficaCircular = (datos, titulo) => {
    if (!datos || datos.length === 0) return null;
    
    const total = datos.reduce((sum, d) => sum + d.valor, 0);
    if (total === 0) return null;
    
    const colores = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
    let angulo = 0;
    const radio = 90;
    const centro = 110;
    
    return (
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 16px', fontSize: 15, color: '#1e293b', fontWeight: 700 }}>{titulo}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap', justifyContent: 'center' }}>
          <svg width={220} height={220} style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}>
            {datos.map((d, i) => {
              if (d.valor === 0) return null;
              const porcentaje = d.valor / total;
              const anguloInicio = angulo;
              const anguloFin = angulo + porcentaje * 360;
              angulo = anguloFin;
              
              const startRad = (anguloInicio - 90) * Math.PI / 180;
              const endRad = (anguloFin - 90) * Math.PI / 180;
              
              const x1 = centro + radio * Math.cos(startRad);
              const y1 = centro + radio * Math.sin(startRad);
              const x2 = centro + radio * Math.cos(endRad);
              const y2 = centro + radio * Math.sin(endRad);
              
              const largeArc = porcentaje > 0.5 ? 1 : 0;
              
              const pathD = `M ${centro} ${centro} L ${x1} ${y1} A ${radio} ${radio} 0 ${largeArc} 1 ${x2} ${y2} Z`;
              
              return (
                <path
                  key={i}
                  d={pathD}
                  fill={colores[i % colores.length]}
                  stroke="white"
                  strokeWidth="3"
                  style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                />
              );
            })}
            {/* Círculo central */}
            <circle cx={centro} cy={centro} r={40} fill="white" />
            <text x={centro} y={centro - 5} textAnchor="middle" fontSize={14} fontWeight="700" fill="#1e293b">
              {total.toLocaleString()}
            </text>
            <text x={centro} y={centro + 12} textAnchor="middle" fontSize={10} fill="#64748b">
              Total
            </text>
          </svg>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {datos.map((d, i) => (
              <div key={i} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10, 
                fontSize: 13,
                padding: '8px 12px',
                background: '#f8fafc',
                borderRadius: 8,
                minWidth: 180
              }}>
                <div style={{
                  width: 14,
                  height: 14,
                  borderRadius: 4,
                  backgroundColor: colores[i % colores.length],
                  flexShrink: 0
                }} />
                <span style={{ color: '#475569', flex: 1 }}>{d.label}</span>
                <span style={{ fontWeight: 700, color: '#1e293b' }}>{d.valor.toLocaleString()}</span>
                <span style={{ 
                  color: '#64748b', 
                  fontSize: 11, 
                  background: '#e2e8f0',
                  padding: '2px 6px',
                  borderRadius: 4
                }}>
                  {((d.valor / total) * 100).toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };
  
  // Función para imprimir informe
  const imprimirInforme = () => {
    if (!datosCalculados) return;
    
    const { empleados: emps, totales, promedios, periodo } = datosCalculados;
    
    // Ordenar empleados por nómina neta descendente para rankings
    const topNomina = [...emps].filter(e => e.valorNominaNeta > 0).sort((a, b) => b.valorNominaNeta - a.valorNominaNeta).slice(0, 5);
    const topHoras = [...emps].filter(e => e.totalHoras > 0).sort((a, b) => b.totalHoras - a.totalHoras).slice(0, 5);
    const topIncapacidad = [...emps].filter(e => e.diasIncapacidad > 0).sort((a, b) => b.diasIncapacidad - a.diasIncapacidad).slice(0, 5);
    const topMemorandos = [...emps].filter(e => e.countMemorandos > 0).sort((a, b) => b.countMemorandos - a.countMemorandos).slice(0, 5);
    
    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Informe Ejecutivo de Rendimiento y Costos</title>
  <style>
    @page { size: A4 landscape; margin: 10mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { 
      font-family: 'Segoe UI', 'Roboto', Arial, sans-serif; 
      background: #fff; 
      color: #1a1a2e; 
      font-size: 10px;
      line-height: 1.4;
    }
    
    /* Header Principal */
    .main-header {
      background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0ea5e9 100%);
      color: white;
      padding: 20px 25px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-radius: 0 0 16px 16px;
      margin-bottom: 16px;
      box-shadow: 0 4px 20px rgba(14, 165, 233, 0.3);
    }
    .main-header h1 {
      font-size: 22px;
      font-weight: 700;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .header-info {
      text-align: right;
      font-size: 11px;
    }
    .header-info .periodo {
      background: rgba(255,255,255,0.15);
      padding: 6px 14px;
      border-radius: 20px;
      margin-top: 6px;
      display: inline-block;
      backdrop-filter: blur(10px);
    }
    
    /* KPIs Grid */
    .kpis-container {
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: 10px;
      margin-bottom: 16px;
      padding: 0 10px;
    }
    .kpi-card {
      background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
      border-radius: 12px;
      padding: 14px 12px;
      text-align: center;
      box-shadow: 0 2px 10px rgba(0,0,0,0.06);
      border-left: 4px solid #e2e8f0;
      position: relative;
      overflow: hidden;
    }
    .kpi-card::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 40px;
      height: 40px;
      background: linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.02) 50%);
    }
    .kpi-card.blue { border-left-color: #3b82f6; background: linear-gradient(145deg, #eff6ff 0%, #dbeafe 100%); }
    .kpi-card.green { border-left-color: #10b981; background: linear-gradient(145deg, #ecfdf5 0%, #d1fae5 100%); }
    .kpi-card.red { border-left-color: #ef4444; background: linear-gradient(145deg, #fef2f2 0%, #fee2e2 100%); }
    .kpi-card.yellow { border-left-color: #f59e0b; background: linear-gradient(145deg, #fffbeb 0%, #fef3c7 100%); }
    .kpi-card.purple { border-left-color: #8b5cf6; background: linear-gradient(145deg, #f5f3ff 0%, #ede9fe 100%); }
    .kpi-icon { font-size: 20px; margin-bottom: 6px; }
    .kpi-value { font-size: 20px; font-weight: 800; color: #0f172a; }
    .kpi-card.blue .kpi-value { color: #1d4ed8; }
    .kpi-card.green .kpi-value { color: #059669; }
    .kpi-card.red .kpi-value { color: #dc2626; }
    .kpi-card.yellow .kpi-value { color: #d97706; }
    .kpi-card.purple .kpi-value { color: #7c3aed; }
    .kpi-label { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px; font-weight: 600; }
    
    /* Contenedor Principal */
    .content-wrapper {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      padding: 0 10px;
      margin-bottom: 12px;
    }
    
    /* Secciones */
    .section {
      background: #fff;
      border-radius: 12px;
      padding: 14px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.05);
      border: 1px solid #e2e8f0;
    }
    .section-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 2px solid #f1f5f9;
    }
    .section-header h3 {
      font-size: 13px;
      font-weight: 700;
      color: #0f172a;
    }
    .section-icon {
      width: 28px;
      height: 28px;
      background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
    }
    
    /* Métricas */
    .metric-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      border-bottom: 1px dashed #e2e8f0;
    }
    .metric-row:last-child { border-bottom: none; }
    .metric-row.highlight {
      background: linear-gradient(90deg, #f0f9ff 0%, transparent 100%);
      margin: 0 -8px;
      padding: 8px;
      border-radius: 6px;
      border-bottom: none;
    }
    .metric-label { color: #475569; font-size: 10px; }
    .metric-value { font-weight: 700; color: #0f172a; font-size: 11px; }
    .metric-value.money { color: #059669; }
    .metric-value.danger { color: #dc2626; }
    
    /* Tabla Principal */
    .table-container {
      padding: 0 10px;
      margin-bottom: 12px;
    }
    .table-section {
      background: #fff;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 2px 10px rgba(0,0,0,0.05);
      border: 1px solid #e2e8f0;
    }
    .table-header-bar {
      background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%);
      color: white;
      padding: 12px 16px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .table-header-bar h3 { font-size: 13px; font-weight: 600; }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 9px;
    }
    thead th {
      background: linear-gradient(180deg, #1e3a5f 0%, #0f172a 100%);
      color: white;
      padding: 10px 6px;
      text-align: left;
      font-weight: 600;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    tbody td {
      padding: 8px 6px;
      border-bottom: 1px solid #f1f5f9;
      vertical-align: middle;
    }
    tbody tr:nth-child(even) { background: #f8fafc; }
    tbody tr:hover { background: #e0f2fe; }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    
    /* Badges */
    .badge {
      display: inline-block;
      padding: 3px 8px;
      border-radius: 12px;
      font-size: 8px;
      font-weight: 700;
    }
    .badge-blue { background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%); color: #1d4ed8; }
    .badge-green { background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%); color: #166534; }
    .badge-red { background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%); color: #dc2626; }
    .badge-yellow { background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); color: #92400e; }
    .badge-purple { background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%); color: #7c3aed; }
    
    /* Fila de Totales */
    .totales-row {
      background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%) !important;
      color: white !important;
      font-weight: 700;
    }
    .totales-row td { border: none !important; padding: 12px 6px; }
    
    /* Rankings Container */
    .rankings-container {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
      padding: 0 10px;
      margin-bottom: 12px;
    }
    .ranking-card {
      background: #fff;
      border-radius: 10px;
      padding: 12px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
      border: 1px solid #e2e8f0;
    }
    .ranking-title {
      font-size: 10px;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .ranking-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 4px 0;
      font-size: 9px;
      border-bottom: 1px dashed #f1f5f9;
    }
    .ranking-item:last-child { border-bottom: none; }
    .ranking-pos {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 8px;
      font-weight: 700;
      margin-right: 6px;
    }
    .ranking-pos.gold { background: linear-gradient(135deg, #fbbf24, #f59e0b); color: white; }
    .ranking-pos.silver { background: linear-gradient(135deg, #94a3b8, #64748b); color: white; }
    .ranking-pos.bronze { background: linear-gradient(135deg, #fb923c, #ea580c); color: white; }
    .ranking-pos.normal { background: #e2e8f0; color: #64748b; }
    
    /* Footer */
    .footer {
      text-align: center;
      padding: 12px;
      color: #94a3b8;
      font-size: 9px;
      border-top: 1px solid #e2e8f0;
      margin: 0 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .footer-logo {
      font-weight: 700;
      color: #0f172a;
    }
    
    /* Page Break */
    .page-break { page-break-before: always; }
    
    /* Gráficas */
    .charts-section {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      padding: 0 10px;
      margin-bottom: 16px;
    }
    .chart-container {
      background: #fff;
      border-radius: 12px;
      padding: 16px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.05);
      border: 1px solid #e2e8f0;
    }
    .chart-title {
      font-size: 13px;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .chart-svg {
      width: 100%;
      height: auto;
    }
    
    /* Rankings y Alertas */
    .analysis-section {
      background: #fff;
      border-radius: 12px;
      padding: 16px;
      margin: 0 10px 16px 10px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.05);
      border: 1px solid #e2e8f0;
    }
    .analysis-title {
      font-size: 14px;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 2px solid #e2e8f0;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .rankings-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 12px;
    }
    .ranking-card {
      background: linear-gradient(145deg, #f8fafc 0%, #f1f5f9 100%);
      border-radius: 10px;
      padding: 12px;
      border-left: 3px solid #3b82f6;
    }
    .ranking-card.horas { border-left-color: #3b82f6; }
    .ranking-card.incapacidad { border-left-color: #f59e0b; }
    .ranking-card.memorandos { border-left-color: #ef4444; }
    .ranking-card.costos { border-left-color: #10b981; }
    .ranking-header {
      font-size: 10px;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 8px;
    }
    .ranking-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 4px 0;
      border-bottom: 1px solid #e2e8f0;
      font-size: 9px;
    }
    .ranking-item:last-child { border-bottom: none; }
    .ranking-position {
      background: #e2e8f0;
      color: #475569;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 8px;
      margin-right: 6px;
    }
    .ranking-position.gold { background: #fbbf24; color: #78350f; }
    .ranking-position.silver { background: #94a3b8; color: #1e293b; }
    .ranking-position.bronze { background: #d97706; color: #fff; }
    .ranking-name {
      flex: 1;
      font-weight: 500;
      color: #1e293b;
    }
    .ranking-value {
      font-weight: 700;
      color: #0f172a;
    }
    
    /* Alertas */
    .alerts-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      margin-top: 12px;
    }
    .alert-card {
      background: linear-gradient(145deg, #fef2f2 0%, #fee2e2 100%);
      border-radius: 10px;
      padding: 12px;
      border-left: 3px solid #ef4444;
    }
    .alert-card.warning {
      background: linear-gradient(145deg, #fffbeb 0%, #fef3c7 100%);
      border-left-color: #f59e0b;
    }
    .alert-card.info {
      background: linear-gradient(145deg, #eff6ff 0%, #dbeafe 100%);
      border-left-color: #3b82f6;
    }
    .alert-title {
      font-size: 10px;
      font-weight: 700;
      color: #1e293b;
      margin-bottom: 6px;
    }
    .alert-employees {
      font-size: 8px;
      color: #64748b;
    }
    .alert-count {
      font-size: 18px;
      font-weight: 800;
      color: #dc2626;
    }
    .alert-card.warning .alert-count { color: #d97706; }
    .alert-card.info .alert-count { color: #2563eb; }
    
    @media print {
      body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      .main-header { margin-top: 0; }
      .charts-section, .analysis-section { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <!-- Header Principal -->
  <div class="main-header">
    <div>
      <h1>📊 Informe Ejecutivo de Rendimiento</h1>
      <p style="opacity: 0.85; font-size: 11px; margin-top: 4px;">Análisis integral del desempeño y costos del personal</p>
    </div>
    <div class="header-info">
      <div style="font-weight: 600;">👥 ${totales.empleados} empleados analizados</div>
      <div class="periodo">
        📅 ${new Date(periodo.inicio).toLocaleDateString('es-CO', {day: '2-digit', month: 'short', year: 'numeric'})} — ${new Date(periodo.fin).toLocaleDateString('es-CO', {day: '2-digit', month: 'short', year: 'numeric'})}
      </div>
    </div>
  </div>
  
  <!-- KPIs Principales -->
  <div class="kpis-container">
    <div class="kpi-card blue">
      <div class="kpi-icon">⏱️</div>
      <div class="kpi-value">${totales.totalHoras.toLocaleString()}</div>
      <div class="kpi-label">Horas Totales</div>
    </div>
    <div class="kpi-card green">
      <div class="kpi-icon">💰</div>
      <div class="kpi-value">${formatMoney(totales.nominaBruta).replace('COP ', '$')}</div>
      <div class="kpi-label">Nómina Bruta</div>
    </div>
    <div class="kpi-card green">
      <div class="kpi-icon">💵</div>
      <div class="kpi-value">${formatMoney(totales.nominaNeta).replace('COP ', '$')}</div>
      <div class="kpi-label">Nómina Neta</div>
    </div>
    <div class="kpi-card red">
      <div class="kpi-icon">📉</div>
      <div class="kpi-value">${formatMoney(totales.descuentos).replace('COP ', '$')}</div>
      <div class="kpi-label">Descuentos</div>
    </div>
    <div class="kpi-card yellow">
      <div class="kpi-icon">🏥</div>
      <div class="kpi-value">${totales.diasIncapacidad}</div>
      <div class="kpi-label">Días Incapacidad</div>
    </div>
    <div class="kpi-card purple">
      <div class="kpi-icon">📝</div>
      <div class="kpi-value">${totales.memorandos}</div>
      <div class="kpi-label">Memorandos</div>
    </div>
  </div>
  
  <!-- Contenido en 2 columnas -->
  <div class="content-wrapper">
    <!-- Desglose de Horas -->
    <div class="section">
      <div class="section-header">
        <div class="section-icon">⏱️</div>
        <h3>Desglose de Horas Trabajadas</h3>
      </div>
      <div class="metric-row">
        <span class="metric-label">Horas Normales</span>
        <span class="metric-value">${totales.horasNormales.toLocaleString()} hrs</span>
      </div>
      <div class="metric-row">
        <span class="metric-label">Horas Nocturnas</span>
        <span class="metric-value">${totales.horasNocturnas.toLocaleString()} hrs</span>
      </div>
      <div class="metric-row">
        <span class="metric-label">Horas Extras</span>
        <span class="metric-value">${totales.horasExtras.toLocaleString()} hrs</span>
      </div>
      <div class="metric-row">
        <span class="metric-label">Horas Festivas</span>
        <span class="metric-value">${totales.horasFestivas.toLocaleString()} hrs</span>
      </div>
      <div class="metric-row highlight">
        <span class="metric-label"><strong>📊 Promedio por empleado</strong></span>
        <span class="metric-value"><strong>${promedios.horasPorEmpleado} hrs</strong></span>
      </div>
    </div>
    
    <!-- Análisis de Costos -->
    <div class="section">
      <div class="section-header">
        <div class="section-icon">💰</div>
        <h3>Análisis de Costos</h3>
      </div>
      <div class="metric-row">
        <span class="metric-label">Nómina Bruta Total</span>
        <span class="metric-value money">${formatMoney(totales.nominaBruta).replace('COP ', '$')}</span>
      </div>
      <div class="metric-row">
        <span class="metric-label">Total Descuentos</span>
        <span class="metric-value danger">${formatMoney(totales.descuentos).replace('COP ', '$')}</span>
      </div>
      <div class="metric-row">
        <span class="metric-label">Valor Comidas</span>
        <span class="metric-value">${formatMoney(totales.valorComidas).replace('COP ', '$')}</span>
      </div>
      <div class="metric-row">
        <span class="metric-label">Otros Descuentos</span>
        <span class="metric-value">${formatMoney(totales.otrosDescuentos).replace('COP ', '$')}</span>
      </div>
      <div class="metric-row highlight">
        <span class="metric-label"><strong>💵 Costo promedio/empleado</strong></span>
        <span class="metric-value money"><strong>${formatMoney(promedios.costoPromedio).replace('COP ', '$')}</strong></span>
      </div>
    </div>
    
    <!-- Ausencias y Novedades -->
    <div class="section">
      <div class="section-header">
        <div class="section-icon">📋</div>
        <h3>Ausencias y Novedades</h3>
      </div>
      <div class="metric-row">
        <span class="metric-label">🏥 Días de Incapacidad</span>
        <span class="metric-value">${totales.diasIncapacidad} días</span>
      </div>
      <div class="metric-row">
        <span class="metric-label">🏖️ Días de Vacaciones</span>
        <span class="metric-value">${totales.diasVacaciones} días</span>
      </div>
      <div class="metric-row">
        <span class="metric-label">🙋 Permisos</span>
        <span class="metric-value">${totales.permisos} días</span>
      </div>
      <div class="metric-row">
        <span class="metric-label">😴 Descansos</span>
        <span class="metric-value">${totales.descansos} días</span>
      </div>
      <div class="metric-row highlight">
        <span class="metric-label"><strong>📉 Días No Trabajados</strong></span>
        <span class="metric-value danger"><strong>${totales.diasNoTrabajados} días</strong></span>
      </div>
    </div>
    
    <!-- Disciplinario -->
    <div class="section">
      <div class="section-header">
        <div class="section-icon">📝</div>
        <h3>Gestión Disciplinaria</h3>
      </div>
      <div class="metric-row">
        <span class="metric-label">Total Memorandos</span>
        <span class="metric-value">${totales.memorandos}</span>
      </div>
      <div class="metric-row">
        <span class="metric-label">Llamados de Atención</span>
        <span class="metric-value">${totales.llamadosAtencion}</span>
      </div>
      <div class="metric-row highlight">
        <span class="metric-label"><strong>📊 Promedio memos/empleado</strong></span>
        <span class="metric-value"><strong>${promedios.memorandosPromedio}</strong></span>
      </div>
      <div class="metric-row highlight">
        <span class="metric-label"><strong>🏥 Promedio incap./empleado</strong></span>
        <span class="metric-value"><strong>${promedios.incapacidadPromedio} días</strong></span>
      </div>
    </div>
  </div>
  
  <!-- Rankings -->
  <div class="rankings-container">
    <div class="ranking-card">
      <div class="ranking-title">🏆 Top Nómina Neta</div>
      ${topNomina.length > 0 ? topNomina.map((e, i) => `
        <div class="ranking-item">
          <div style="display: flex; align-items: center;">
            <span class="ranking-pos ${i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : 'normal'}">${i + 1}</span>
            <span>${e.nombre.split(' ').slice(0, 2).join(' ')}</span>
          </div>
          <span style="font-weight: 700; color: #059669;">${formatMoney(e.valorNominaNeta).replace('COP ', '$')}</span>
        </div>
      `).join('') : '<div style="color: #94a3b8; font-size: 9px;">Sin datos</div>'}
    </div>
    
    <div class="ranking-card">
      <div class="ranking-title">⏱️ Top Horas Trabajadas</div>
      ${topHoras.length > 0 ? topHoras.map((e, i) => `
        <div class="ranking-item">
          <div style="display: flex; align-items: center;">
            <span class="ranking-pos ${i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : 'normal'}">${i + 1}</span>
            <span>${e.nombre.split(' ').slice(0, 2).join(' ')}</span>
          </div>
          <span style="font-weight: 700; color: #1d4ed8;">${e.totalHoras} hrs</span>
        </div>
      `).join('') : '<div style="color: #94a3b8; font-size: 9px;">Sin datos</div>'}
    </div>
    
    <div class="ranking-card">
      <div class="ranking-title">🏥 Mayor Incapacidad</div>
      ${topIncapacidad.length > 0 ? topIncapacidad.map((e, i) => `
        <div class="ranking-item">
          <div style="display: flex; align-items: center;">
            <span class="ranking-pos ${i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : 'normal'}">${i + 1}</span>
            <span>${e.nombre.split(' ').slice(0, 2).join(' ')}</span>
          </div>
          <span style="font-weight: 700; color: #d97706;">${e.diasIncapacidad} días</span>
        </div>
      `).join('') : '<div style="color: #94a3b8; font-size: 9px;">Sin incapacidades</div>'}
    </div>
    
    <div class="ranking-card">
      <div class="ranking-title">📝 Más Memorandos</div>
      ${topMemorandos.length > 0 ? topMemorandos.map((e, i) => `
        <div class="ranking-item">
          <div style="display: flex; align-items: center;">
            <span class="ranking-pos ${i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : 'normal'}">${i + 1}</span>
            <span>${e.nombre.split(' ').slice(0, 2).join(' ')}</span>
          </div>
          <span style="font-weight: 700; color: #dc2626;">${e.countMemorandos}</span>
        </div>
      `).join('') : '<div style="color: #94a3b8; font-size: 9px;">Sin memorandos</div>'}
    </div>
  </div>
  
  <!-- Tabla Detallada -->
  <div class="table-container">
    <div class="table-section">
      <div class="table-header-bar">
        <span style="font-size: 16px;">👥</span>
        <h3>Detalle por Empleado</h3>
      </div>
      <table>
        <thead>
          <tr>
            <th style="width: 22%;">Empleado</th>
            <th style="width: 10%;">Cargo</th>
            <th style="width: 10%;">Sede</th>
            <th class="text-center" style="width: 7%;">H. Norm.</th>
            <th class="text-center" style="width: 6%;">H. Ext.</th>
            <th class="text-center" style="width: 6%;">Incap.</th>
            <th class="text-center" style="width: 6%;">Vacac.</th>
            <th class="text-center" style="width: 5%;">Memos</th>
            <th class="text-right" style="width: 10%;">Bruto</th>
            <th class="text-right" style="width: 8%;">Desc.</th>
            <th class="text-right" style="width: 10%;">Neto</th>
          </tr>
        </thead>
        <tbody>
          ${emps.map(e => `
            <tr>
              <td><strong style="color: #0f172a;">${e.nombre}</strong></td>
              <td style="color: #64748b; font-size: 8px;">${e.cargo || '-'}</td>
              <td style="color: #64748b; font-size: 8px;">${e.sede || '-'}</td>
              <td class="text-center">${e.horasNormales > 0 ? e.horasNormales.toLocaleString() : '-'}</td>
              <td class="text-center">${e.horasExtras > 0 ? `<span class="badge badge-blue">${e.horasExtras}</span>` : '-'}</td>
              <td class="text-center">${e.diasIncapacidad > 0 ? `<span class="badge badge-yellow">${e.diasIncapacidad}</span>` : '-'}</td>
              <td class="text-center">${e.diasVacaciones > 0 ? `<span class="badge badge-green">${e.diasVacaciones}</span>` : '-'}</td>
              <td class="text-center">${e.countMemorandos > 0 ? `<span class="badge badge-red">${e.countMemorandos}</span>` : '-'}</td>
              <td class="text-right" style="color: #059669;">${e.valorNominaBruta > 0 ? formatMoney(e.valorNominaBruta).replace('COP ', '$') : '-'}</td>
              <td class="text-right" style="color: #dc2626; font-size: 8px;">${e.valorDescuentos > 0 ? formatMoney(e.valorDescuentos).replace('COP ', '$') : '-'}</td>
              <td class="text-right"><strong style="color: #0f172a;">${e.valorNominaNeta > 0 ? formatMoney(e.valorNominaNeta).replace('COP ', '$') : '-'}</strong></td>
            </tr>
          `).join('')}
          <tr class="totales-row">
            <td colspan="3"><strong>📊 TOTALES GENERALES</strong></td>
            <td class="text-center"><strong>${totales.horasNormales.toLocaleString()}</strong></td>
            <td class="text-center"><strong>${totales.horasExtras.toLocaleString()}</strong></td>
            <td class="text-center"><strong>${totales.diasIncapacidad}</strong></td>
            <td class="text-center"><strong>${totales.diasVacaciones}</strong></td>
            <td class="text-center"><strong>${totales.memorandos}</strong></td>
            <td class="text-right"><strong>${formatMoney(totales.nominaBruta).replace('COP ', '$')}</strong></td>
            <td class="text-right"><strong>${formatMoney(totales.descuentos).replace('COP ', '$')}</strong></td>
            <td class="text-right"><strong>${formatMoney(totales.nominaNeta).replace('COP ', '$')}</strong></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  
  <!-- Sección de Gráficas -->
  <div class="charts-section">
    <!-- Gráfica de Distribución de Horas -->
    <div class="chart-container">
      <div class="chart-title">📊 Distribución de Horas Trabajadas</div>
      <svg viewBox="0 0 400 230" class="chart-svg">
        <defs>
          <linearGradient id="barGradient1" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#3b82f6"/>
            <stop offset="100%" style="stop-color:#1d4ed8"/>
          </linearGradient>
          <linearGradient id="barGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#8b5cf6"/>
            <stop offset="100%" style="stop-color:#7c3aed"/>
          </linearGradient>
          <linearGradient id="barGradient3" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#f59e0b"/>
            <stop offset="100%" style="stop-color:#d97706"/>
          </linearGradient>
          <linearGradient id="barGradient4" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#10b981"/>
            <stop offset="100%" style="stop-color:#059669"/>
          </linearGradient>
        </defs>
        ${[
          { label: 'Normales', value: totales.horasNormales, color: 'url(#barGradient1)' },
          { label: 'Nocturnas', value: totales.horasNocturnas, color: 'url(#barGradient2)' },
          { label: 'Extras', value: totales.horasExtras, color: 'url(#barGradient3)' },
          { label: 'Festivas', value: totales.horasFestivas, color: 'url(#barGradient4)' }
        ].map((d, i) => {
          const maxVal = Math.max(totales.horasNormales, totales.horasNocturnas, totales.horasExtras, totales.horasFestivas, 1);
          const barWidth = 55;
          const spacing = 25;
          const chartHeight = 120;
          const marginTop = 35;
          const startX = 55;
          const barHeight = (d.value / maxVal) * chartHeight;
          const x = startX + i * (barWidth + spacing);
          const y = marginTop + chartHeight - barHeight;
          // Número siempre visible: arriba de la barra con fondo
          const numeroY = y - 10;
          return '<rect x="' + (x - 2) + '" y="' + (numeroY - 12) + '" width="' + (barWidth + 4) + '" height="16" fill="white" rx="3"/>' +
            '<rect x="' + x + '" y="' + y + '" width="' + barWidth + '" height="' + barHeight + '" fill="' + d.color + '" rx="4"/>' +
            '<text x="' + (x + barWidth/2) + '" y="' + numeroY + '" text-anchor="middle" font-size="11" font-weight="700" fill="#1e293b">' + d.value.toLocaleString() + '</text>' +
            '<text x="' + (x + barWidth/2) + '" y="' + (marginTop + chartHeight + 18) + '" text-anchor="middle" font-size="10" fill="#475569" font-weight="600">' + d.label + '</text>';
        }).join('')}
        <!-- Línea base -->
        <line x1="40" y1="${35 + 120}" x2="380" y2="${35 + 120}" stroke="#e2e8f0" stroke-width="2"/>
      </svg>
    </div>
    
    <!-- Gráfica de Costos -->
    <div class="chart-container">
      <div class="chart-title">💰 Distribución de Costos de Nómina</div>
      <svg viewBox="0 0 400 200" class="chart-svg">
        ${(() => {
          const costosData = [
            { label: 'Nómina Neta', value: totales.nominaNeta, color: '#10b981' },
            { label: 'Descuentos', value: totales.descuentos, color: '#ef4444' }
          ];
          const totalCostos = costosData.reduce((sum, d) => sum + d.value, 0);
          const centerX = 120;
          const centerY = 100;
          const radius = 70;
          const innerRadius = 40;
          let currentAngle = -90;
          
          let resultado = '';
          
          // Arcos del donut
          costosData.forEach((d, i) => {
            const percentage = totalCostos > 0 ? (d.value / totalCostos) * 100 : 0;
            const angle = (percentage / 100) * 360;
            const startAngle = currentAngle;
            const endAngle = currentAngle + angle;
            currentAngle = endAngle;
            
            const startRad = (startAngle * Math.PI) / 180;
            const endRad = (endAngle * Math.PI) / 180;
            
            const x1 = centerX + radius * Math.cos(startRad);
            const y1 = centerY + radius * Math.sin(startRad);
            const x2 = centerX + radius * Math.cos(endRad);
            const y2 = centerY + radius * Math.sin(endRad);
            const x3 = centerX + innerRadius * Math.cos(endRad);
            const y3 = centerY + innerRadius * Math.sin(endRad);
            const x4 = centerX + innerRadius * Math.cos(startRad);
            const y4 = centerY + innerRadius * Math.sin(startRad);
            
            const largeArc = angle > 180 ? 1 : 0;
            
            resultado += '<path d="M ' + x1 + ' ' + y1 + ' A ' + radius + ' ' + radius + ' 0 ' + largeArc + ' 1 ' + x2 + ' ' + y2 + ' L ' + x3 + ' ' + y3 + ' A ' + innerRadius + ' ' + innerRadius + ' 0 ' + largeArc + ' 0 ' + x4 + ' ' + y4 + ' Z" fill="' + d.color + '" opacity="0.9"/>';
          });
          
          // Leyenda
          costosData.forEach((d, i) => {
            const percentage = totalCostos > 0 ? ((d.value / totalCostos) * 100).toFixed(1) : 0;
            resultado += '<rect x="240" y="' + (70 + i * 45) + '" width="16" height="16" rx="4" fill="' + d.color + '"/>';
            resultado += '<text x="262" y="' + (82 + i * 45) + '" font-size="10" font-weight="600" fill="#1e293b">' + d.label + '</text>';
            resultado += '<text x="262" y="' + (95 + i * 45) + '" font-size="9" fill="#64748b">' + formatMoney(d.value).replace('COP ', '$') + ' (' + percentage + '%)</text>';
          });
          
          // Centro
          resultado += '<text x="' + centerX + '" y="' + (centerY - 5) + '" text-anchor="middle" font-size="10" fill="#64748b">Total</text>';
          resultado += '<text x="' + centerX + '" y="' + (centerY + 12) + '" text-anchor="middle" font-size="12" font-weight="700" fill="#0f172a">' + formatMoney(totales.nominaBruta).replace('COP ', '$') + '</text>';
          
          return resultado;
        })()}
      </svg>
    </div>
  </div>
  
  <!-- Sección de Novedades y Ausencias Detallada -->
  <div class="analysis-section">
    <div class="analysis-title">📋 Resumen de Novedades y Ausencias</div>
    <div style="display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; margin-bottom: 12px;">
      <div class="ranking-card" style="border-left-color: #4338ca;">
        <div class="ranking-header">😴 Días de Descanso</div>
        <div style="font-size: 22px; font-weight: 800; color: #4338ca; margin-bottom: 4px;">${totales.descansos || 0}</div>
        <div style="font-size: 8px; color: #64748b;">Días sin laborar</div>
      </div>
      
      <div class="ranking-card" style="border-left-color: #64748b;">
        <div class="ranking-header">🚫 No Trabajados</div>
        <div style="font-size: 22px; font-weight: 800; color: #64748b; margin-bottom: 4px;">${totales.diasNoTrabajados || 0}</div>
        <div style="font-size: 8px; color: #64748b;">Días no laborados</div>
      </div>
      
      <div class="ranking-card" style="border-left-color: #8b5cf6;">
        <div class="ranking-header">📋 Días de Permiso</div>
        <div style="font-size: 22px; font-weight: 800; color: #8b5cf6; margin-bottom: 4px;">${totales.permisos || 0}</div>
        <div style="font-size: 8px; color: #64748b;">Permisos otorgados</div>
      </div>
      
      <div class="ranking-card" style="border-left-color: #10b981;">
        <div class="ranking-header">🏖️ Días Vacaciones</div>
        <div style="font-size: 22px; font-weight: 800; color: #059669; margin-bottom: 4px;">${totales.diasVacaciones}</div>
        <div style="font-size: 8px; color: #64748b;">Vacaciones tomadas</div>
      </div>
      
      <div class="ranking-card incapacidad">
        <div class="ranking-header">🏥 Días Incapacidad</div>
        <div style="font-size: 22px; font-weight: 800; color: #d97706; margin-bottom: 4px;">${totales.diasIncapacidad}</div>
        <div style="font-size: 8px; color: #64748b;">Días de ausencia médica</div>
      </div>
      
      <div class="ranking-card memorandos">
        <div class="ranking-header">📝 Memorandos</div>
        <div style="font-size: 22px; font-weight: 800; color: #dc2626; margin-bottom: 4px;">${totales.memorandos}</div>
        <div style="font-size: 8px; color: #64748b;">Total memorandos</div>
      </div>
    </div>
    
    <!-- Rankings detallados -->
    <div class="rankings-grid">
      <div class="ranking-card incapacidad">
        <div class="ranking-header">🏥 Top Incapacidad</div>
        ${topIncapacidad.slice(0, 3).length > 0 ? topIncapacidad.slice(0, 3).map((e, i) => 
          '<div class="ranking-item">' +
            '<span class="ranking-position ' + (i === 0 ? 'gold' : i === 1 ? 'silver' : 'bronze') + '">' + (i + 1) + '</span>' +
            '<span class="ranking-name">' + e.nombre.split(' ').slice(0, 2).join(' ') + '</span>' +
            '<span class="ranking-value">' + e.diasIncapacidad + 'd</span>' +
          '</div>'
        ).join('') : '<div style="font-size: 8px; color: #94a3b8;">Sin registros</div>'}
      </div>
      
      <div class="ranking-card" style="border-left-color: #10b981;">
        <div class="ranking-header">🏖️ Top Vacaciones</div>
        ${emps.filter(e => e.diasVacaciones > 0).sort((a, b) => b.diasVacaciones - a.diasVacaciones).slice(0, 3).length > 0 ? emps.filter(e => e.diasVacaciones > 0).sort((a, b) => b.diasVacaciones - a.diasVacaciones).slice(0, 3).map((e, i) => 
          '<div class="ranking-item">' +
            '<span class="ranking-position ' + (i === 0 ? 'gold' : i === 1 ? 'silver' : 'bronze') + '">' + (i + 1) + '</span>' +
            '<span class="ranking-name">' + e.nombre.split(' ').slice(0, 2).join(' ') + '</span>' +
            '<span class="ranking-value">' + e.diasVacaciones + 'd</span>' +
          '</div>'
        ).join('') : '<div style="font-size: 8px; color: #94a3b8;">Sin registros</div>'}
      </div>
      
      <div class="ranking-card memorandos">
        <div class="ranking-header">📝 Top Memorandos</div>
        ${topMemorandos.slice(0, 3).length > 0 ? topMemorandos.slice(0, 3).map((e, i) => 
          '<div class="ranking-item">' +
            '<span class="ranking-position ' + (i === 0 ? 'gold' : i === 1 ? 'silver' : 'bronze') + '">' + (i + 1) + '</span>' +
            '<span class="ranking-name">' + e.nombre.split(' ').slice(0, 2).join(' ') + '</span>' +
            '<span class="ranking-value">' + e.countMemorandos + '</span>' +
          '</div>'
        ).join('') : '<div style="font-size: 8px; color: #94a3b8;">Sin registros</div>'}
      </div>
      
      <div class="ranking-card horas">
        <div class="ranking-header">⏱️ Top Horas Extras</div>
        ${emps.filter(e => e.horasExtras > 0).sort((a, b) => b.horasExtras - a.horasExtras).slice(0, 3).length > 0 ? emps.filter(e => e.horasExtras > 0).sort((a, b) => b.horasExtras - a.horasExtras).slice(0, 3).map((e, i) => 
          '<div class="ranking-item">' +
            '<span class="ranking-position ' + (i === 0 ? 'gold' : i === 1 ? 'silver' : 'bronze') + '">' + (i + 1) + '</span>' +
            '<span class="ranking-name">' + e.nombre.split(' ').slice(0, 2).join(' ') + '</span>' +
            '<span class="ranking-value">' + e.horasExtras + 'h</span>' +
          '</div>'
        ).join('') : '<div style="font-size: 8px; color: #94a3b8;">Sin registros</div>'}
      </div>
    </div>
    
    <!-- Alertas de Atención -->
    <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid #e2e8f0;">
      <div style="font-size: 12px; font-weight: 700; color: #1e293b; margin-bottom: 12px;">⚠️ Alertas de Atención</div>
      <div class="alerts-grid">
        <div class="alert-card">
          <div class="alert-title">🏥 Empleados con Incapacidad</div>
          <div class="alert-count">${emps.filter(e => e.diasIncapacidad > 0).length}</div>
          <div class="alert-employees">${emps.filter(e => e.diasIncapacidad > 0).slice(0, 3).map(e => e.nombre.split(' ').slice(0, 2).join(' ')).join(', ') || 'Ninguno'}</div>
        </div>
        <div class="alert-card warning">
          <div class="alert-title">📝 Empleados con Memorandos</div>
          <div class="alert-count">${emps.filter(e => e.countMemorandos > 0).length}</div>
          <div class="alert-employees">${emps.filter(e => e.countMemorandos > 0).slice(0, 3).map(e => e.nombre.split(' ').slice(0, 2).join(' ')).join(', ') || 'Ninguno'}</div>
        </div>
        <div class="alert-card info">
          <div class="alert-title">🏖️ Empleados en Vacaciones</div>
          <div class="alert-count">${emps.filter(e => e.diasVacaciones > 0).length}</div>
          <div class="alert-employees">${emps.filter(e => e.diasVacaciones > 0).slice(0, 3).map(e => e.nombre.split(' ').slice(0, 2).join(' ')).join(', ') || 'Ninguno'}</div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Footer -->
  <div class="footer">
    <div class="footer-logo">📊 Sistema de Gestión de Recursos Humanos</div>
    <div>Generado: ${new Date().toLocaleString('es-CO', {dateStyle: 'medium', timeStyle: 'short'})}</div>
    <div style="color: #64748b;">Documento confidencial - Uso interno</div>
  </div>
</body>
</html>
    `;
    
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 500);
    }
  };
  
  // Función para exportar a Excel (CSV)
  const exportarExcel = () => {
    if (!datosCalculados) return;
    
    const { empleados: emps, totales, periodo } = datosCalculados;
    
    let csv = 'Informe de Rendimiento y Costos\n';
    csv += `Período: ${periodo.inicio} a ${periodo.fin}\n\n`;
    
    // Encabezados
    csv += 'Empleado,Documento,Cargo,Sede,H. Normales,H. Extras,H. Festivas,H. Nocturnas,Total Horas,Descansos,No Trabajados,Incapacidad,Vacaciones,Permisos,Memorandos,Comidas,Otros Desc.,Nómina Bruta,Descuentos,Nómina Neta\n';
    
    // Datos
    emps.forEach(e => {
      csv += `"${e.nombre}","${e.documento}","${e.cargo}","${e.sede}",${e.horasNormales},${e.horasExtras},${e.horasFestivas},${e.horasNocturnas},${e.totalHoras},${e.diasDescanso},${e.diasNoTrabajados},${e.diasIncapacidad},${e.diasVacaciones},${e.diasPermiso},${e.countMemorandos},${e.valorComidas},${e.valorOtrosDescuentos},${e.valorNominaBruta},${e.valorDescuentos},${e.valorNominaNeta}\n`;
    });
    
    // Totales
    csv += `\n"TOTALES","","","",${totales.horasNormales},${totales.horasExtras},${totales.horasFestivas},${totales.horasNocturnas},${totales.totalHoras},${totales.descansos},${totales.diasNoTrabajados},${totales.diasIncapacidad},${totales.diasVacaciones},${totales.permisos},${totales.memorandos},${totales.valorComidas},${totales.otrosDescuentos},${totales.nominaBruta},${totales.descuentos},${totales.nominaNeta}\n`;
    
    // Descargar
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Informe_Rendimiento_${periodo.inicio}_${periodo.fin}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  return (
    <div style={{ padding: 24, maxWidth: 1600, margin: '0 auto' }}>
      {/* Header ejecutivo */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #0ea5e9 100%)',
        color: 'white',
        padding: 28,
        borderRadius: 16,
        marginBottom: 24,
        boxShadow: '0 10px 40px rgba(14, 165, 233, 0.3)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 28, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 36 }}>📊</span>
              Informe de Rendimiento y Costos
            </h2>
            <p style={{ margin: '8px 0 0', opacity: 0.9, fontSize: 14 }}>
              Análisis ejecutivo del desempeño y costos del personal para la toma de decisiones estratégicas
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {datosCalculados && (
              <>
                <button
                  onClick={imprimirInforme}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontWeight: 600,
                    fontSize: 13
                  }}
                >
                  🖨️ Imprimir
                </button>
                <button
                  onClick={exportarExcel}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontWeight: 600,
                    fontSize: 13
                  }}
                >
                  📥 Exportar Excel
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Panel de filtros */}
      <div style={{
        background: 'white',
        borderRadius: 16,
        padding: 24,
        marginBottom: 24,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>🔍</span> Filtros de Análisis
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
          {/* Modo de filtro */}
          <div>
            <label style={{ fontSize: 12, color: '#64748b', marginBottom: 6, display: 'block' }}>Tipo de análisis</label>
            <select
              value={modoFiltro}
              onChange={(e) => {
                setModoFiltro(e.target.value);
                setEmpleadoSeleccionado('');
                setEmpleadosSeleccionados([]);
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #e2e8f0',
                borderRadius: 10,
                fontSize: 13,
                cursor: 'pointer'
              }}
            >
              <option value="todos">👥 Todos los empleados</option>
              <option value="individual">👤 Individual</option>
              <option value="grupal">📁 Por grupo/sede</option>
            </select>
          </div>
          
          {/* Selector individual */}
          {modoFiltro === 'individual' && (
            <div>
              <label style={{ fontSize: 12, color: '#64748b', marginBottom: 6, display: 'block' }}>Empleado</label>
              <select
                value={empleadoSeleccionado}
                onChange={(e) => setEmpleadoSeleccionado(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: 10,
                  fontSize: 13
                }}
              >
                <option value="">Seleccionar empleado...</option>
                {empleadosOrdenados.map(e => (
                  <option key={e.id} value={e.id}>
                    {e.nombre || `${e.nombres || ''} ${e.apellidos || ''}`.trim()}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          {/* Selector grupal por sede */}
          {modoFiltro === 'grupal' && (
            <div>
              <label style={{ fontSize: 12, color: '#64748b', marginBottom: 6, display: 'block' }}>Sede</label>
              <select
                value={sedeSeleccionada}
                onChange={(e) => setSedeSeleccionada(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: 10,
                  fontSize: 13
                }}
              >
                <option value="">Todas las sedes</option>
                {sedesUnicas.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Periodo rápido */}
          <div>
            <label style={{ fontSize: 12, color: '#64748b', marginBottom: 6, display: 'block' }}>Período rápido</label>
            <select
              value={periodoRapido}
              onChange={(e) => aplicarPeriodoRapido(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #e2e8f0',
                borderRadius: 10,
                fontSize: 13
              }}
            >
              <option value="hoy">📅 Hoy</option>
              <option value="semana">📆 Esta semana</option>
              <option value="quincena_actual">📊 Quincena actual</option>
              <option value="quincena_anterior">⏮️ Quincena anterior</option>
              <option value="mes_actual">🗓️ Mes actual</option>
              <option value="ultimo_mes">⏮️ Mes anterior</option>
              <option value="ultimos_2_meses">📆 Últimos 2 meses</option>
              <option value="ultimos_3_meses">📆 Últimos 3 meses</option>
              <option value="trimestre">📈 Trimestre</option>
              <option value="semestre">📉 Semestre</option>
              <option value="anio">📅 Año completo</option>
              <option value="personalizado">🔧 Personalizado</option>
            </select>
          </div>
          
          {/* Fecha inicio */}
          <div>
            <label style={{ fontSize: 12, color: '#64748b', marginBottom: 6, display: 'block' }}>Fecha inicio</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => {
                setFechaInicio(e.target.value);
                setPeriodoRapido('');
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #e2e8f0',
                borderRadius: 10,
                fontSize: 13
              }}
            />
          </div>
          
          {/* Fecha fin */}
          <div>
            <label style={{ fontSize: 12, color: '#64748b', marginBottom: 6, display: 'block' }}>Fecha fin</label>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => {
                setFechaFin(e.target.value);
                setPeriodoRapido('');
              }}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #e2e8f0',
                borderRadius: 10,
                fontSize: 13
              }}
            />
          </div>
        </div>
        
        <button
          onClick={calcularInforme}
          disabled={cargando || !fechaInicio || !fechaFin}
          style={{
            background: cargando ? '#94a3b8' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            border: 'none',
            padding: '14px 32px',
            borderRadius: 12,
            fontSize: 15,
            fontWeight: 700,
            cursor: cargando ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: cargando ? 'none' : '0 6px 20px rgba(16, 185, 129, 0.35)'
          }}
        >
          {cargando ? (
            <>⏳ Calculando...</>
          ) : (
            <>🚀 Generar Informe</>
          )}
        </button>
      </div>
      
      {/* Resultados */}
      {datosCalculados && (
        <>
          {/* Tabs de navegación */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {[
              { id: 'resumen', label: '📊 Resumen Ejecutivo', icon: '📊' },
              { id: 'detalle', label: '👥 Detalle por Empleado', icon: '👥' },
              { id: 'graficas', label: '📈 Gráficas', icon: '📈' },
              { id: 'comparativo', label: '📋 Análisis', icon: '📋' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setVistaActiva(tab.id)}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  borderRadius: 10,
                  background: vistaActiva === tab.id
                    ? 'linear-gradient(135deg, #1e3a5f 0%, #0ea5e9 100%)'
                    : 'white',
                  color: vistaActiva === tab.id ? 'white' : '#64748b',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  boxShadow: vistaActiva === tab.id
                    ? '0 4px 15px rgba(14, 165, 233, 0.3)'
                    : '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Vista: Resumen Ejecutivo */}
          {vistaActiva === 'resumen' && (
            <div>
              {/* KPIs principales */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 16,
                marginBottom: 24
              }}>
                {[
                  { label: 'Empleados Analizados', value: datosCalculados.totales.empleados, icon: '👥', color: '#3b82f6' },
                  { label: 'Total Horas Trabajadas', value: datosCalculados.totales.totalHoras.toLocaleString(), icon: '⏱️', color: '#10b981' },
                  { label: 'Horas Extras', value: datosCalculados.totales.horasExtras.toLocaleString(), icon: '⚡', color: '#f59e0b' },
                  { label: 'Nómina Bruta', value: formatMoney(datosCalculados.totales.nominaBruta), icon: '💰', color: '#8b5cf6' },
                  { label: 'Nómina Neta', value: formatMoney(datosCalculados.totales.nominaNeta), icon: '💵', color: '#059669' },
                  { label: 'Total Descuentos', value: formatMoney(datosCalculados.totales.descuentos), icon: '📉', color: '#ef4444' },
                  { label: 'Días Incapacidad', value: datosCalculados.totales.diasIncapacidad, icon: '🏥', color: '#f97316' },
                  { label: 'Días Vacaciones', value: datosCalculados.totales.diasVacaciones, icon: '🏖️', color: '#06b6d4' },
                  { label: 'Total Memorandos', value: datosCalculados.totales.memorandos, icon: '📝', color: '#dc2626' },
                  { label: 'Permisos', value: datosCalculados.totales.permisos, icon: '🙋', color: '#7c3aed' },
                  { label: 'Valor Comidas', value: formatMoney(datosCalculados.totales.valorComidas), icon: '🍽️', color: '#0891b2' },
                  { label: 'Costo Promedio/Emp.', value: formatMoney(datosCalculados.promedios.costoPromedio), icon: '📊', color: '#1e3a5f' },
                ].map((kpi, i) => (
                  <div
                    key={i}
                    style={{
                      background: 'white',
                      borderRadius: 14,
                      padding: 20,
                      boxShadow: '0 4px 15px rgba(0,0,0,0.06)',
                      borderLeft: `4px solid ${kpi.color}`,
                      transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>
                          {kpi.label}
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a' }}>
                          {kpi.value}
                        </div>
                      </div>
                      <span style={{ fontSize: 28 }}>{kpi.icon}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Tarjetas de resumen */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div style={{
                  background: 'white',
                  borderRadius: 16,
                  padding: 24,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
                }}>
                  <h4 style={{ margin: '0 0 16px', fontSize: 16, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                    ⏱️ Distribución de Horas
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {[
                      { label: 'Horas Normales', value: datosCalculados.totales.horasNormales, color: '#3b82f6' },
                      { label: 'Horas Nocturnas', value: datosCalculados.totales.horasNocturnas, color: '#8b5cf6' },
                      { label: 'Horas Extras', value: datosCalculados.totales.horasExtras, color: '#f59e0b' },
                      { label: 'Horas Festivas', value: datosCalculados.totales.horasFestivas, color: '#10b981' },
                    ].map((item, i) => {
                      const total = datosCalculados.totales.totalHoras || 1;
                      const pct = (item.value / total) * 100;
                      return (
                        <div key={i}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                            <span style={{ fontSize: 13, color: '#64748b' }}>{item.label}</span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                              {item.value.toLocaleString()} ({pct.toFixed(1)}%)
                            </span>
                          </div>
                          <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{
                              height: '100%',
                              width: `${pct}%`,
                              background: item.color,
                              borderRadius: 4,
                              transition: 'width 0.5s ease'
                            }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div style={{
                  background: 'white',
                  borderRadius: 16,
                  padding: 24,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
                }}>
                  <h4 style={{ margin: '0 0 16px', fontSize: 16, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                    📋 Novedades y Ausencias
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    {[
                      { label: 'Días de Descanso', value: datosCalculados.totales.descansos, icon: '😴' },
                      { label: 'Días No Trabajados', value: datosCalculados.totales.diasNoTrabajados, icon: '📵' },
                      { label: 'Días de Incapacidad', value: datosCalculados.totales.diasIncapacidad, icon: '🏥' },
                      { label: 'Días de Vacaciones', value: datosCalculados.totales.diasVacaciones, icon: '🏖️' },
                      { label: 'Días de Permiso', value: datosCalculados.totales.permisos, icon: '🙋' },
                      { label: 'Memorandos', value: datosCalculados.totales.memorandos, icon: '📝' },
                    ].map((item, i) => (
                      <div key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: 12,
                        background: '#f8fafc',
                        borderRadius: 10
                      }}>
                        <span style={{ fontSize: 24 }}>{item.icon}</span>
                        <div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>{item.label}</div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{item.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Vista: Detalle por Empleado */}
          {vistaActiva === 'detalle' && (
            <div style={{
              background: 'white',
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
              overflowX: 'auto'
            }}>
              <h4 style={{ margin: '0 0 16px', fontSize: 16, color: '#1e293b' }}>
                👥 Detalle por Empleado ({datosCalculados.empleados.length} empleados)
              </h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                <thead>
                  <tr style={{ background: '#1e3a5f', color: 'white' }}>
                    <th style={{ padding: '8px 4px', textAlign: 'left' }}>Empleado</th>
                    <th style={{ padding: '8px 4px', textAlign: 'left' }}>Cargo</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', background: '#2563eb' }} title="Horas Normales">H.Norm</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', background: '#4338ca' }} title="Horas Nocturnas">H.Noct</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', background: '#7c3aed' }} title="Horas Festivas">H.Fest</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', background: '#059669' }} title="Horas Extras Diurnas">Ex.Día</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', background: '#0d9488' }} title="Horas Extras Nocturnas">Ex.Noc</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', background: '#0891b2' }} title="Horas Extras Festivas">Ex.Fest</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', background: '#0369a1' }} title="Total Horas">Total</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center' }}>Desc.</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center' }}>NoTrab</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center' }}>Incap.</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center' }}>Vacac.</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center' }}>Perm.</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center' }}>Memos</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right' }}>Bruto</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right' }}>Neto</th>
                  </tr>
                </thead>
                <tbody>
                  {datosCalculados.empleados.map((e, i) => (
                    <tr
                      key={e.id}
                      style={{
                        background: i % 2 === 0 ? '#f8fafc' : 'white',
                        borderBottom: '1px solid #e2e8f0'
                      }}
                    >
                      <td style={{ padding: '6px 4px', fontWeight: 600, fontSize: 9 }}>{e.nombre}</td>
                      <td style={{ padding: '6px 4px', color: '#64748b', fontSize: 9 }}>{e.cargo || '-'}</td>
                      <td style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 500 }}>{e.horasNormales > 0 ? e.horasNormales : '-'}</td>
                      <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                        {e.horasNocturnas > 0 ? (
                          <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '1px 4px', borderRadius: 6, fontSize: 9, fontWeight: 600 }}>{e.horasNocturnas}</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                        {e.horasFestivas > 0 ? (
                          <span style={{ background: '#f3e8ff', color: '#7c3aed', padding: '1px 4px', borderRadius: 6, fontSize: 9, fontWeight: 600 }}>{e.horasFestivas}</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                        {e.horasExtrasDiurnas > 0 ? (
                          <span style={{ background: '#d1fae5', color: '#059669', padding: '1px 4px', borderRadius: 6, fontSize: 9, fontWeight: 600 }}>{e.horasExtrasDiurnas}</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                        {e.horasExtrasNocturnas > 0 ? (
                          <span style={{ background: '#ccfbf1', color: '#0d9488', padding: '1px 4px', borderRadius: 6, fontSize: 9, fontWeight: 600 }}>{e.horasExtrasNocturnas}</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                        {e.horasExtrasFestivas > 0 ? (
                          <span style={{ background: '#cffafe', color: '#0891b2', padding: '1px 4px', borderRadius: 6, fontSize: 9, fontWeight: 600 }}>{e.horasExtrasFestivas}</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'center', fontWeight: 700, color: '#0369a1' }}>{e.totalHoras > 0 ? e.totalHoras : '-'}</td>
                      <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                        {e.diasDescanso > 0 ? (
                          <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '1px 4px', borderRadius: 6, fontSize: 9, fontWeight: 600 }}>{e.diasDescanso}</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                        {e.diasNoTrabajados > 0 ? (
                          <span style={{ background: '#fef3c7', color: '#92400e', padding: '1px 4px', borderRadius: 6, fontSize: 9, fontWeight: 600 }}>{e.diasNoTrabajados}</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                        {e.diasIncapacidad > 0 ? (
                          <span style={{ background: '#fee2e2', color: '#dc2626', padding: '1px 4px', borderRadius: 6, fontSize: 9, fontWeight: 600 }}>{e.diasIncapacidad}</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                        {e.diasVacaciones > 0 ? (
                          <span style={{ background: '#dcfce7', color: '#166534', padding: '1px 4px', borderRadius: 6, fontSize: 9, fontWeight: 600 }}>{e.diasVacaciones}</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                        {e.diasPermiso > 0 ? (
                          <span style={{ background: '#f3e8ff', color: '#7c3aed', padding: '1px 4px', borderRadius: 6, fontSize: 9, fontWeight: 600 }}>{e.diasPermiso}</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                        {e.countMemorandos > 0 ? (
                          <span style={{ background: '#fee2e2', color: '#dc2626', padding: '1px 4px', borderRadius: 6, fontSize: 9, fontWeight: 600 }}>{e.countMemorandos}</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '6px 4px', textAlign: 'right', fontSize: 9 }}>{formatMoney(e.valorNominaBruta)}</td>
                      <td style={{ padding: '6px 4px', textAlign: 'right', fontWeight: 700, color: '#059669', fontSize: 9 }}>{formatMoney(e.valorNominaNeta)}</td>
                    </tr>
                  ))}
                  {/* Fila de totales */}
                  <tr style={{ background: '#1e3a5f', color: 'white', fontWeight: 700 }}>
                    <td colSpan="2" style={{ padding: '8px 4px' }}>TOTALES</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>{datosCalculados.totales.horasNormales}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>{datosCalculados.totales.horasNocturnas}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>{datosCalculados.totales.horasFestivas}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>{datosCalculados.totales.horasExtrasDiurnas}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>{datosCalculados.totales.horasExtrasNocturnas}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>{datosCalculados.totales.horasExtrasFestivas}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>{datosCalculados.totales.totalHoras}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>{datosCalculados.totales.descansos}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>{datosCalculados.totales.diasNoTrabajados}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>{datosCalculados.totales.diasIncapacidad}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>{datosCalculados.totales.diasVacaciones}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>{datosCalculados.totales.permisos}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>{datosCalculados.totales.memorandos}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'right' }}>{formatMoney(datosCalculados.totales.nominaBruta)}</td>
                    <td style={{ padding: '8px 4px', textAlign: 'right' }}>{formatMoney(datosCalculados.totales.nominaNeta)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          
          {/* Vista: Gráficas */}
          {vistaActiva === 'graficas' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div style={{
                background: 'white',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
              }}>
                {generarGraficaCircular(
                  [
                    { label: 'H. Normales', valor: datosCalculados.totales.horasNormales },
                    { label: 'H. Extras', valor: datosCalculados.totales.horasExtras },
                    { label: 'H. Festivas', valor: datosCalculados.totales.horasFestivas },
                    { label: 'H. Nocturnas', valor: datosCalculados.totales.horasNocturnas },
                  ],
                  '⏱️ Distribución de Horas por Tipo'
                )}
              </div>
              
              <div style={{
                background: 'white',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
              }}>
                {generarGraficaCircular(
                  [
                    { label: 'Incapacidades', valor: datosCalculados.totales.diasIncapacidad },
                    { label: 'Vacaciones', valor: datosCalculados.totales.diasVacaciones },
                    { label: 'Permisos', valor: datosCalculados.totales.permisos },
                    { label: 'Descansos', valor: datosCalculados.totales.descansos },
                    { label: 'No Trabajados', valor: datosCalculados.totales.diasNoTrabajados },
                  ],
                  '📋 Distribución de Ausencias'
                )}
              </div>
              
              <div style={{
                background: 'white',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                gridColumn: 'span 2'
              }}>
                {generarGraficaBarras(
                  datosCalculados.empleados
                    .filter(e => e.totalHoras > 0 || e.valorNominaBruta > 0 || e.diasIncapacidad > 0)
                    .sort((a, b) => b.totalHoras - a.totalHoras)
                    .map(e => ({
                      label: e.nombre.split(' ').slice(0, 2).join(' '),
                      valor: e.totalHoras,
                      color: '#3b82f6'
                    })),
                  '👥 Horas Trabajadas por Empleado (Todos)'
                )}
              </div>
              
              <div style={{
                background: 'white',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                gridColumn: 'span 2'
              }}>
                {generarGraficaBarras(
                  datosCalculados.empleados
                    .filter(e => e.valorNominaNeta > 0 || e.valorNominaBruta > 0 || e.diasIncapacidad > 0)
                    .sort((a, b) => b.valorNominaNeta - a.valorNominaNeta)
                    .map(e => ({
                      label: e.nombre.split(' ').slice(0, 2).join(' '),
                      valor: e.valorNominaNeta,
                      color: '#10b981'
                    })),
                  '💰 Nómina Neta por Empleado (Todos)'
                )}
              </div>
              
              <div style={{
                background: 'white',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                gridColumn: 'span 2'
              }}>
                {generarGraficaBarras(
                  datosCalculados.empleados
                    .filter(e => e.valorNominaBruta > 0 || e.diasIncapacidad > 0)
                    .sort((a, b) => b.valorNominaBruta - a.valorNominaBruta)
                    .map(e => ({
                      label: e.nombre.split(' ').slice(0, 2).join(' '),
                      valor: e.valorNominaBruta,
                      color: '#f59e0b'
                    })),
                  '💵 Salario Devengado por Empleado (Sin Descuentos)'
                )}
              </div>
            </div>
          )}
          
          {/* Vista: Análisis Comparativo */}
          {vistaActiva === 'comparativo' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Análisis de productividad */}
              <div style={{
                background: 'white',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
              }}>
                <h4 style={{ margin: '0 0 20px', fontSize: 16, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                  📊 Indicadores Clave de Rendimiento
                </h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ padding: 16, background: '#f0fdf4', borderRadius: 12, border: '1px solid #bbf7d0' }}>
                    <div style={{ fontSize: 12, color: '#166534', marginBottom: 4 }}>Horas promedio por empleado</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#15803d' }}>
                      {datosCalculados.promedios.horasPorEmpleado} hrs
                    </div>
                  </div>
                  
                  <div style={{ padding: 16, background: '#eff6ff', borderRadius: 12, border: '1px solid #bfdbfe' }}>
                    <div style={{ fontSize: 12, color: '#1d4ed8', marginBottom: 4 }}>Costo promedio por empleado</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#1e40af' }}>
                      {formatMoney(datosCalculados.promedios.costoPromedio)}
                    </div>
                  </div>
                  
                  <div style={{ padding: 16, background: '#fef3c7', borderRadius: 12, border: '1px solid #fde68a' }}>
                    <div style={{ fontSize: 12, color: '#92400e', marginBottom: 4 }}>Días incapacidad promedio</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#b45309' }}>
                      {datosCalculados.promedios.incapacidadPromedio} días
                    </div>
                  </div>
                  
                  <div style={{ padding: 16, background: '#fef2f2', borderRadius: 12, border: '1px solid #fecaca' }}>
                    <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 4 }}>Memorandos promedio</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#b91c1c' }}>
                      {datosCalculados.promedios.memorandosPromedio}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Ranking de empleados */}
              <div style={{
                background: 'white',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
              }}>
                <h4 style={{ margin: '0 0 20px', fontSize: 16, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                  🏆 Ranking de Empleados (por horas trabajadas)
                </h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
                  {datosCalculados.empleados
                    .filter(e => e.totalHoras > 0 || e.valorNominaBruta > 0 || e.diasIncapacidad > 0)
                    .sort((a, b) => b.totalHoras - a.totalHoras)
                    .map((e, i) => (
                      <div
                        key={e.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          padding: '10px 14px',
                          background: i === 0 ? '#fef3c7' : i === 1 ? '#f1f5f9' : i === 2 ? '#fed7aa' : '#f8fafc',
                          borderRadius: 10,
                          border: `1px solid ${i === 0 ? '#fbbf24' : i === 1 ? '#94a3b8' : i === 2 ? '#fb923c' : '#e2e8f0'}`
                        }}
                      >
                        <span style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: i === 0 ? '#fbbf24' : i === 1 ? '#94a3b8' : i === 2 ? '#fb923c' : '#e2e8f0',
                          color: i < 3 ? 'white' : '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: 12
                        }}>
                          {i + 1}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{e.nombre}</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>{e.cargo || e.sede || '-'}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{e.totalHoras} hrs</div>
                          <div style={{ fontSize: 10, color: '#64748b' }}>{formatMoney(e.valorNominaNeta)}</div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
              
              {/* Alertas y observaciones */}
              <div style={{
                background: 'white',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                gridColumn: 'span 2'
              }}>
                <h4 style={{ margin: '0 0 20px', fontSize: 16, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                  ⚠️ Alertas y Observaciones
                </h4>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                  {/* Empleados con más memorandos */}
                  <div style={{ padding: 16, background: '#fef2f2', borderRadius: 12 }}>
                    <h5 style={{ margin: '0 0 12px', fontSize: 13, color: '#dc2626' }}>📝 Mayor cantidad de memorandos</h5>
                    {datosCalculados.empleados
                      .filter(e => e.countMemorandos > 0)
                      .sort((a, b) => b.countMemorandos - a.countMemorandos)
                      .slice(0, 5)
                      .map((e, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dashed #fecaca' }}>
                          <span style={{ fontSize: 12, color: '#7f1d1d' }}>{e.nombre.split(' ').slice(0, 2).join(' ')}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>{e.countMemorandos}</span>
                        </div>
                      ))}
                    {datosCalculados.empleados.filter(e => e.countMemorandos > 0).length === 0 && (
                      <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>Sin memorandos en el período</p>
                    )}
                  </div>
                  
                  {/* Empleados con más incapacidades */}
                  <div style={{ padding: 16, background: '#fef3c7', borderRadius: 12 }}>
                    <h5 style={{ margin: '0 0 12px', fontSize: 13, color: '#92400e' }}>🏥 Mayor incapacidad</h5>
                    {datosCalculados.empleados
                      .filter(e => e.diasIncapacidad > 0)
                      .sort((a, b) => b.diasIncapacidad - a.diasIncapacidad)
                      .slice(0, 5)
                      .map((e, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dashed #fde68a' }}>
                          <span style={{ fontSize: 12, color: '#78350f' }}>{e.nombre.split(' ').slice(0, 2).join(' ')}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#b45309' }}>{e.diasIncapacidad} días</span>
                        </div>
                      ))}
                    {datosCalculados.empleados.filter(e => e.diasIncapacidad > 0).length === 0 && (
                      <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>Sin incapacidades en el período</p>
                    )}
                  </div>
                  
                  {/* Empleados con más horas extras */}
                  <div style={{ padding: 16, background: '#eff6ff', borderRadius: 12 }}>
                    <h5 style={{ margin: '0 0 12px', fontSize: 13, color: '#1d4ed8' }}>⚡ Mayor cantidad horas extras</h5>
                    {datosCalculados.empleados
                      .filter(e => e.horasExtras > 0)
                      .sort((a, b) => b.horasExtras - a.horasExtras)
                      .slice(0, 5)
                      .map((e, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dashed #bfdbfe' }}>
                          <span style={{ fontSize: 12, color: '#1e3a8a' }}>{e.nombre.split(' ').slice(0, 2).join(' ')}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>{e.horasExtras} hrs</span>
                        </div>
                      ))}
                    {datosCalculados.empleados.filter(e => e.horasExtras > 0).length === 0 && (
                      <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>Sin horas extras en el período</p>
                    )}
                  </div>
                  
                  {/* Empleados con más descansos */}
                  <div style={{ padding: 16, background: '#e0e7ff', borderRadius: 12 }}>
                    <h5 style={{ margin: '0 0 12px', fontSize: 13, color: '#4338ca' }}>😴 Mayor cantidad de descansos</h5>
                    {datosCalculados.empleados
                      .filter(e => e.diasDescanso > 0)
                      .sort((a, b) => b.diasDescanso - a.diasDescanso)
                      .slice(0, 5)
                      .map((e, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dashed #c7d2fe' }}>
                          <span style={{ fontSize: 12, color: '#3730a3' }}>{e.nombre.split(' ').slice(0, 2).join(' ')}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#4338ca' }}>{e.diasDescanso} días</span>
                        </div>
                      ))}
                    {datosCalculados.empleados.filter(e => e.diasDescanso > 0).length === 0 && (
                      <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>Sin días de descanso en el período</p>
                    )}
                  </div>
                  
                  {/* Empleados con más días no trabajados */}
                  <div style={{ padding: 16, background: '#fef9c3', borderRadius: 12 }}>
                    <h5 style={{ margin: '0 0 12px', fontSize: 13, color: '#a16207' }}>📵 Mayor días no trabajados</h5>
                    {datosCalculados.empleados
                      .filter(e => e.diasNoTrabajados > 0)
                      .sort((a, b) => b.diasNoTrabajados - a.diasNoTrabajados)
                      .slice(0, 5)
                      .map((e, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dashed #fde047' }}>
                          <span style={{ fontSize: 12, color: '#854d0e' }}>{e.nombre.split(' ').slice(0, 2).join(' ')}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#a16207' }}>{e.diasNoTrabajados} días</span>
                        </div>
                      ))}
                    {datosCalculados.empleados.filter(e => e.diasNoTrabajados > 0).length === 0 && (
                      <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>Sin días no trabajados en el período</p>
                    )}
                  </div>
                  
                  {/* Empleados con más permisos */}
                  <div style={{ padding: 16, background: '#f3e8ff', borderRadius: 12 }}>
                    <h5 style={{ margin: '0 0 12px', fontSize: 13, color: '#7c3aed' }}>🙋 Mayor cantidad de permisos</h5>
                    {datosCalculados.empleados
                      .filter(e => e.diasPermiso > 0)
                      .sort((a, b) => b.diasPermiso - a.diasPermiso)
                      .slice(0, 5)
                      .map((e, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dashed #ddd6fe' }}>
                          <span style={{ fontSize: 12, color: '#5b21b6' }}>{e.nombre.split(' ').slice(0, 2).join(' ')}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>{e.diasPermiso} días</span>
                        </div>
                      ))}
                    {datosCalculados.empleados.filter(e => e.diasPermiso > 0).length === 0 && (
                      <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>Sin permisos en el período</p>
                    )}
                  </div>
                  
                  {/* Empleados con vacaciones */}
                  <div style={{ padding: 16, background: '#dcfce7', borderRadius: 12 }}>
                    <h5 style={{ margin: '0 0 12px', fontSize: 13, color: '#166534' }}>🏖️ Días de vacaciones</h5>
                    {datosCalculados.empleados
                      .filter(e => e.diasVacaciones > 0)
                      .sort((a, b) => b.diasVacaciones - a.diasVacaciones)
                      .slice(0, 5)
                      .map((e, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dashed #bbf7d0' }}>
                          <span style={{ fontSize: 12, color: '#14532d' }}>{e.nombre.split(' ').slice(0, 2).join(' ')}</span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#166534' }}>{e.diasVacaciones} días</span>
                        </div>
                      ))}
                    {datosCalculados.empleados.filter(e => e.diasVacaciones > 0).length === 0 && (
                      <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>Sin vacaciones en el período</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Mensaje inicial */}
      {!datosCalculados && !cargando && (
        <div style={{
          background: 'white',
          borderRadius: 16,
          padding: 60,
          textAlign: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
        }}>
          <span style={{ fontSize: 64 }}>📊</span>
          <h3 style={{ margin: '20px 0 10px', color: '#1e293b' }}>Genera tu Informe de Rendimiento</h3>
          <p style={{ color: '#64748b', margin: 0 }}>
            Selecciona los filtros deseados y haz clic en "Generar Informe" para visualizar el análisis completo
          </p>
        </div>
      )}
    </div>
  );
}
 
function AppConAutenticacion() {
  const { usuario, cargando, sesionCerradaRemotamente, limpiarAlertaSesion } = useAuth();

  // Mientras carga, muestra un mensaje
  if (cargando) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        ⏳ Cargando sesión...
      </div>
    );
  }

  // Si NO hay usuario, muestra el login
  if (!usuario) {
    return (
      <>
        {/* Mostrar alerta si la sesión fue cerrada porque se abrió otra */}
        {sesionCerradaRemotamente && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: '#ff9800',
            color: '#fff',
            padding: '15px 20px',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '15px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}>
            <span>⚠️ Tu sesión fue cerrada porque se inició sesión en otro dispositivo o navegador.</span>
            <button
              onClick={limpiarAlertaSesion}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.5)',
                color: '#fff',
                padding: '5px 15px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Entendido
            </button>
          </div>
        )}
        <LoginComponent_ROJO />
      </>
    );
  }

  // Si hay usuario, muestra la app
  return <AppPrincipal usuario={usuario} />;
}

// Constantes para localStorage de navegación
const STORAGE_NAV_AMBIENTE = "app_navegacion_ambiente_v1";
const STORAGE_NAV_VISTA = "app_navegacion_vistaAdmin_v1";

function AppPrincipal({ usuario }) {
  // Leer estado de navegación desde localStorage para persistir al recargar
  const [ambiente, setAmbienteInterno] = useState(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_NAV_AMBIENTE);
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });
  const [vistaAdmin, setVistaAdminInterno] = useState(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_NAV_VISTA);
      return saved ? JSON.parse(saved) : "MENU";
    } catch (e) {
      return "MENU";
    }
  });

  // Wrappers para guardar en localStorage al cambiar navegación
  const setAmbiente = useCallback((nuevoAmbiente) => {
    setAmbienteInterno(nuevoAmbiente);
    try {
      window.localStorage.setItem(STORAGE_NAV_AMBIENTE, JSON.stringify(nuevoAmbiente));
      // Al cambiar de ambiente, resetear vistaAdmin a MENU
      if (nuevoAmbiente !== ambiente) {
        setVistaAdminInterno("MENU");
        window.localStorage.setItem(STORAGE_NAV_VISTA, JSON.stringify("MENU"));
      }
    } catch (e) {
      console.warn("Error guardando navegación en localStorage", e);
    }
  }, [ambiente]);

  const setVistaAdmin = useCallback((nuevaVista) => {
    setVistaAdminInterno(nuevaVista);
    try {
      window.localStorage.setItem(STORAGE_NAV_VISTA, JSON.stringify(nuevaVista));
    } catch (e) {
      console.warn("Error guardando vista en localStorage", e);
    }
  }, []);

  const [previewFile, setPreviewFile] = useState(null);
  const [tareasPago, setTareasPago] = useState([]); 
  const [cargandoTareasPago, setCargandoTareasPago] = useState(false);
  const [tipoInventario, setTipoInventario] = useState("selector"); // "selector" | "diario" | "integral"
  const [inventariosIntegrales, setInventariosIntegrales] = useState([]);
  const [cargandoInventariosIntegrales, setCargandoInventariosIntegrales] = useState(false);
  const [sedes, setSedes] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [empleadosGerenciaTodas, setEmpleadosGerenciaTodas] = useState([]); // Gerentes de todas las empresas
  const [usuarioConPermisos, setUsuarioConPermisos] = useState(usuario);

  // 🔹 NUEVO: Estados para multi-empresa
  const [empresas, setEmpresas] = useState([]);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);
  const [esSuperAdminVerificado, setEsSuperAdminVerificado] = useState(false);
  const [empresaYaCargada, setEmpresaYaCargada] = useState(false); // 🔹 Control para evitar re-selección automática
  
  // Verificar si es super_admin directamente desde Supabase
  useEffect(() => {
    async function verificarSuperAdmin() {
      if (!usuario?.id) return;
      
      try {
        console.log("🔍 Verificando rol de usuario en Supabase...");
        const { data, error } = await supabase
          .from('usuarios')
          .select('rol_sistema, empresa_id, empresa_preferida')
          .eq('id', usuario.id)
          .single();
        
        if (error) {
          console.error('Error verificando rol:', error);
          return;
        }
        
        console.log("📋 Datos de rol desde Supabase:", data);
        const esSuper = data?.rol_sistema === 'super_admin';
        setEsSuperAdminVerificado(esSuper);
        console.log("✅ Es super_admin:", esSuper);
        
        if (!esSuper) {
          // Si no es super_admin, usar su empresa asignada
          setEmpresaSeleccionada(data?.empresa_id || null);
        } else if (data?.empresa_preferida) {
          // 🔹 Si es super_admin y tiene empresa preferida guardada, usarla
          console.log("💾 Empresa preferida encontrada en Supabase:", data.empresa_preferida);
          setEmpresaSeleccionada(data.empresa_preferida);
          setEmpresaYaCargada(true);
        }
      } catch (err) {
        console.error('Error verificando super admin:', err);
      }
    }
    
    verificarSuperAdmin();
  }, [usuario?.id]);

  // 🔹 Cargar empresas (para super_admin todas, para usuarios normales solo la suya)
  useEffect(() => {
    async function cargarEmpresas() {
      // Esperar a que el usuario esté cargado
      if (!usuario) {
        console.log("🏢 Esperando usuario...");
        return;
      }
      
      console.log("🏢 Intentando cargar empresas...");
      console.log("   - esSuperAdminVerificado:", esSuperAdminVerificado);
      console.log("   - usuario.empresaId:", usuario?.empresaId);
      console.log("   - usuario.empresa_id:", usuario?.empresa_id);
      console.log("   - usuario.esSuperAdmin:", usuario?.esSuperAdmin);
      
      try {
        // Determinar si es super_admin (desde estado verificado O desde el objeto usuario)
        const esSuper = esSuperAdminVerificado || usuario?.esSuperAdmin || usuario?.rolSistema === 'super_admin';
        
        if (esSuper) {
          // Super admin: cargar todas las empresas
          console.log("✅ Es super_admin, cargando lista de empresas...");
          const { data, error } = await supabase
            .from('empresas')
            .select('*')
            .eq('activa', true)
            .order('nombre');
          
          if (error) {
            console.error('Error cargando empresas:', error);
          } else {
            console.log("📋 Empresas cargadas:", data);
            setEmpresas(data || []);
            
            // 🔹 Solo establecer empresa por defecto si no se cargó desde Supabase
            if (!empresaSeleccionada && !empresaYaCargada) {
              // Si no hay empresa preferida en Supabase, usar la primera empresa (no ADMINISTRADOR GENERAL)
              const empresaDefault = data?.find(e => e.id !== '00000000-0000-0000-0000-000000000001');
              if (empresaDefault) {
                setEmpresaSeleccionada(empresaDefault.id);
                console.log("🏢 Empresa por defecto seleccionada:", empresaDefault.nombre);
              }
              setEmpresaYaCargada(true);
            } else if (!empresaYaCargada) {
              setEmpresaYaCargada(true);
            }
          }
        } else {
          // Usuario normal: cargar solo su empresa para mostrar el nombre
          // Usar empresaSeleccionada (que viene de la verificación de super_admin) como respaldo
          const empresaId = usuario?.empresaId || usuario?.empresa_id || empresaSeleccionada;
          
          if (empresaId) {
            console.log("👤 Usuario normal, cargando su empresa:", empresaId);
            const { data, error } = await supabase
              .from('empresas')
              .select('*')
              .eq('id', empresaId)
              .single();
            
            if (error) {
              console.error('Error cargando empresa del usuario:', error);
            } else if (data) {
              console.log("📋 Empresa del usuario cargada:", data.nombre);
              setEmpresas([data]);
              // Asegurar que empresaSeleccionada esté establecida
              if (!empresaSeleccionada) {
                setEmpresaSeleccionada(data.id);
              }
            }
          } else {
            console.log("⚠️ Usuario normal sin empresa asignada - esperando verificación");
          }
        }
      } catch (err) {
        console.error('Error cargando empresas:', err);
      }
    }
    
    cargarEmpresas();
  }, [usuario, esSuperAdminVerificado, empresaSeleccionada]);

  const empleadoActual = useMemo(() => {
    const nombreUsuario = (usuarioConPermisos?.nombre || usuario?.nombre || "")
      .toString()
      .trim()
      .toLowerCase();
    if (!nombreUsuario) return null;
    return (empleados || []).find(
      (e) => (e?.nombre || "").toString().trim().toLowerCase() === nombreUsuario
    ) || null;
  }, [empleados, usuarioConPermisos, usuario]);

  const esGerente = useMemo(() => {
    const perfil = (
      empleadoActual?.perfil ||
      usuarioConPermisos?.rol ||
      usuarioConPermisos?.perfil ||
      usuario?.rol ||
      usuario?.perfil ||
      ""
    )
      .toString()
      .toUpperCase();
    // 🔐 RESTRICCIÓN: Solo GERENCIA/GERENTE/SUPER_ADMIN pueden editar el efectivo del cuadre
    // NO incluir supervisores generales - solo gerencia específica
    return perfil.includes("GERENCIA") || 
           perfil.includes("GERENTE") || 
           perfil === "SUPER_ADMIN";
  }, [empleadoActual, usuarioConPermisos, usuario]);

  // 🔹 Información del usuario actual para mostrar en la barra
  const infoUsuarioActual = useMemo(() => {
    const nombre = usuario?.nombre || usuario?.email || "Usuario";
    const perfil = empleadoActual?.perfil || usuarioConPermisos?.rol || usuarioConPermisos?.perfil || usuario?.rol || usuario?.perfil || "Sin perfil";
    
    // Determinar el nombre de la empresa
    let empresaNombre = "Sin empresa asignada";
    
    // Para super_admin sin selección, mostrar "Todas las empresas"
    if (esSuperAdminVerificado && !empresaSeleccionada) {
      empresaNombre = "Todas las empresas";
    } else {
      // Buscar el nombre de la empresa: primero en la lista de empresas cargadas
      const empresaIdActual = empresaSeleccionada || usuario?.empresaId || usuario?.empresa_id;
      
      if (empresas.length > 0) {
        // Si tenemos empresas cargadas, buscar por ID o tomar la primera (para usuarios normales solo tienen una)
        if (empresaIdActual) {
          const empresaEncontrada = empresas.find(e => e.id === empresaIdActual);
          if (empresaEncontrada) {
            empresaNombre = empresaEncontrada.nombre;
          }
        } else if (empresas.length === 1) {
          // Si solo hay una empresa (usuario normal), mostrar esa
          empresaNombre = empresas[0].nombre;
        }
      } else if (empresaIdActual) {
        // Si aún no se cargaron las empresas pero hay un ID, mostrar cargando
        empresaNombre = "Cargando...";
      }
    }
    
    return { nombre, perfil, empresaNombre };
  }, [usuario, empleadoActual, usuarioConPermisos, empresaSeleccionada, empresas, esSuperAdminVerificado]);

  // 🔹 Calcular filtro de empresa para pasar a componentes hijos
  const filtroEmpresa = useMemo(() => {
    if (esSuperAdminVerificado) {
      // Super admin: usa la empresa seleccionada, o null para ver todas
      return empresaSeleccionada && empresaSeleccionada !== '' ? empresaSeleccionada : null;
    } else {
      // Usuario normal: siempre filtra por su empresa asignada
      return usuario?.empresaId || empresaSeleccionada || null;
    }
  }, [esSuperAdminVerificado, empresaSeleccionada, usuario?.empresaId]);

  // 🔹 Detectar si estamos en modo "Todas las empresas" (solo aplica a super_admin)
  const esVistaTodasEmpresas = useMemo(() => {
    return esSuperAdminVerificado && (!empresaSeleccionada || empresaSeleccionada === '');
  }, [esSuperAdminVerificado, empresaSeleccionada]);

  // 🔹 Función para mostrar alerta cuando intenten usar funciones bloqueadas en "Todas las empresas"
  const alertaTodasEmpresas = (nombreFuncion = "esta función") => {
    alert(`⚠️ ${nombreFuncion} no está disponible cuando está seleccionada "Todas las empresas".\n\nPor favor, seleccione una empresa individual para continuar.`);
    return false;
  };

  // Cargar permisos si no existen
  useEffect(() => {
    const cargarPermisosDelUsuario = async () => {
      if (!usuario) return;
      
      console.log("📋 Usuario actual:", usuario);
      console.log("📋 Permisos actuales:", usuario.permisos);
      console.log("📋 Rol/Perfil:", usuario.rol, usuario.perfil);
      
      // Si ya tiene permisos en el objeto usuario, usarlos
      if (usuario.permisos && Array.isArray(usuario.permisos) && usuario.permisos.length > 0) {
        console.log("✅ Usuario ya tiene permisos cargados:", usuario.permisos);
        setUsuarioConPermisos(usuario);
        return;
      }
      
      // Si no tiene permisos, intentar cargarlos desde Supabase
      const perfilNombre = usuario.rol || usuario.perfil;
      if (perfilNombre) {
        console.log(`🔄 Cargando permisos para perfil: ${perfilNombre}`);
        try {
          // Intentar buscar por id primero
          let { data: perfilData, error } = await supabase
            .from("perfiles")
            .select("id, nombre, permisos")
            .eq("id", perfilNombre)
            .maybeSingle();
          
          // Si no encuentra por id, buscar por nombre
          if (!perfilData) {
            console.log("🔍 No encontrado por ID, buscando por nombre...");
            const { data: perfilPorNombre, error: errorNombre } = await supabase
              .from("perfiles")
              .select("id, nombre, permisos")
              .ilike("nombre", perfilNombre)
              .maybeSingle();
            
            if (!errorNombre && perfilPorNombre) {
              perfilData = perfilPorNombre;
            }
          }
          
          if (perfilData?.permisos) {
            const permisosArray = Array.isArray(perfilData.permisos) ? perfilData.permisos : [];
            console.log("✅ Permisos cargados desde Supabase:", permisosArray);
            setUsuarioConPermisos({
              ...usuario,
              permisos: permisosArray
            });
          } else {
            console.warn("⚠️ No se encontraron permisos para el perfil:", perfilNombre);
            // NO dar permisos por defecto, dejar sin acceso
            setUsuarioConPermisos({
              ...usuario,
              permisos: []
            });
          }
        } catch (err) {
          console.error("❌ Error cargando permisos:", err);
          // NO dar permisos por defecto, dejar sin acceso
          setUsuarioConPermisos({
            ...usuario,
            permisos: []
          });
        }
      } else {
        console.warn("⚠️ Usuario sin rol/perfil definido");
        setUsuarioConPermisos({
          ...usuario,
          permisos: []
        });
      }
    };
    
    cargarPermisosDelUsuario();
  }, [usuario]);

  // Función para validar si el usuario tiene un permiso
  const tienePermiso = (nombrePermiso) => {
    // Si no hay usuario, no tiene permisos
    if (!usuarioConPermisos) {
      console.log(`🔒 tienePermiso("${nombrePermiso}"): false - No hay usuario`);
      return false;
    }
    
    // Si el rol es admin, tiene todos los permisos
    if (usuarioConPermisos.rol === "admin" || usuarioConPermisos.perfil === "admin") {
      return true;
    }
    
    // Si no tiene permisos definidos, no tiene acceso
    if (!Array.isArray(usuarioConPermisos.permisos) || usuarioConPermisos.permisos.length === 0) {
      console.log(`🔒 tienePermiso("${nombrePermiso}"): false - Sin permisos definidos`);
      return false;
    }
    
    const permisos = usuarioConPermisos.permisos;
    
    // Verificar permiso exacto
    if (permisos.includes(nombrePermiso)) {
      return true;
    }
    
    // IMPORTANTE: Para permisos de nivel 3 (sub-botones), NO dar acceso automático
    // solo porque tenga el módulo padre. El usuario debe tener el permiso específico.
    // Ejemplo: tener "rh_horarios" NO significa tener "rh_horarios_guardar"
    
    // Solo verificar jerarquía para permisos de nivel 2 (módulos)
    // Si tiene el ambiente, tiene acceso al módulo (para poder verlo en el menú)
    const partes = nombrePermiso.split("_");
    if (partes.length === 2 && partes[0] !== "ambiente") {
      // Es un permiso de nivel 2 (módulo), verificar si tiene el nivel 1 (ambiente)
      // PERO solo para mostrar el módulo en el menú, no para acciones
      // NO dar acceso automático aquí
    }
    
    // Verificar si tiene "ver_todo" (permiso especial de admin)
    if (permisos.includes("ver_todo")) {
      return true;
    }
    
    console.log(`🔒 tienePermiso("${nombrePermiso}"): false - Permiso no encontrado en:`, permisos);
    return false;
  };
 
  // === CENTRO DE NOTIFICACIONES (nivel principal) ===
  const [mostrarNotificacionesPrincipal, setMostrarNotificacionesPrincipal] = useState(false);
  const [notificacionesVistasPrincipal, setNotificacionesVistasPrincipal] = useState(false);

  const [ausencias, setAusencias] = useState([]);
  const [usuarios, setUsuarios] = useState(() => {
    try {
      const raw = window.localStorage.getItem("usuarios_app_v1");
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      console.error("Error cargando usuarios desde localStorage", e);
      return [];
    }
  });
  const [vacaciones, setVacaciones] = useState([]);
  const [prestamos, setPrestamos] = useState([]);
  const [incapacidades, setIncapacidades] = useState([]);
  const [novedades, setNovedades] = useState([]);
  const [memorandos, setMemorandos] = useState([]);
  const [nominasRepo, setNominasRepo] = useState([]);
  const [liquidacionesRepo, setLiquidacionesRepo] = useState([]);

  // === VALORES ANUALES (SMLV, tarifas, porcentajes) ===
  const STORAGE_VALORES_KEY_GLOBAL = "valoresAnuales_app_v1";
  const DEFAULT_VALORES_GLOBAL = {
    id: "config",
    smlv: 1750905, // SMLV 2025-2026 actualizado
    valorHoraNormal: 6471,
    valorHoraNocturna: 8355,
    valorHoraFestiva: 11323,
    valorHoraExtraFestiva: 13587,
    valorExtraDiurna: 8088,
    valorExtraNocturna: 10422,
    salud: 4.0,
    pension: 4.0,
    auxTransporte: 200000,
    valorVacaciones: 0,
    valorCesantias: 0,
    interesCesantias: 12.0,
    ultimaActualizacion: null
  };

  const [valoresAnuales, setValoresAnuales] = useState(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_VALORES_KEY_GLOBAL);
      return raw ? { ...DEFAULT_VALORES_GLOBAL, ...JSON.parse(raw) } : DEFAULT_VALORES_GLOBAL;
    } catch (e) {
      return DEFAULT_VALORES_GLOBAL;
    }
  });

  // Cargar valores anuales desde Supabase al iniciar
  useEffect(() => {
    const cargarValoresAnualesGlobal = async () => {
      try {
        const { data, error } = await supabase
          .from("valores_anuales")
          .select("*")
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        if (data) {
          const merged = {
            id: data.id || "config",
            smlv: Number(data.smlv) || DEFAULT_VALORES_GLOBAL.smlv,
            valorHoraNormal: Number(data.valorhoranormal) || DEFAULT_VALORES_GLOBAL.valorHoraNormal,
            valorHoraNocturna: Number(data.valorhoranocturna) || DEFAULT_VALORES_GLOBAL.valorHoraNocturna,
            valorHoraFestiva: Number(data.valorhorafestiva) || DEFAULT_VALORES_GLOBAL.valorHoraFestiva,
            valorHoraExtraFestiva: Number(data.valorhoraextrafestiva) || DEFAULT_VALORES_GLOBAL.valorHoraExtraFestiva,
            valorExtraDiurna: Number(data.valorextradiurna) || DEFAULT_VALORES_GLOBAL.valorExtraDiurna,
            valorExtraNocturna: Number(data.valorextranocturna) || DEFAULT_VALORES_GLOBAL.valorExtraNocturna,
            salud: Number(data.salud) || DEFAULT_VALORES_GLOBAL.salud,
            pension: Number(data.pension) || DEFAULT_VALORES_GLOBAL.pension,
            auxTransporte: Number(data.auxtransporte) || DEFAULT_VALORES_GLOBAL.auxTransporte,
            valorVacaciones: Number(data.valorvacaciones) || 0,
            valorCesantias: Number(data.valorcesantias) || 0,
            interesCesantias: Number(data.interescesantias) || DEFAULT_VALORES_GLOBAL.interesCesantias,
            ultimaActualizacion: data.ultimaactualizacion || null
          };
          setValoresAnuales(merged);
          try {
            window.localStorage.setItem(STORAGE_VALORES_KEY_GLOBAL, JSON.stringify(merged));
          } catch (err) {
            console.warn("No se pudo guardar valores en localStorage", err);
          }
        }
      } catch (err) {
        console.error("Error cargando valores anuales:", err);
      }
    };
    cargarValoresAnualesGlobal();
  }, []);

  // ========== SINCRONIZAR SMLV CON SALARIO BASE DE EMPLEADOS ==========
  // Cuando cambia el SMLV en Valores Anuales, actualizar automáticamente el salarioBase de los empleados
  const [smlvAnterior, setSmlvAnterior] = useState(null);
  
  useEffect(() => {
    const actualizarSalariosConSMLV = async () => {
      const smlvActual = valoresAnuales?.smlv;
      
      // Solo procesar si hay un SMLV válido y ha cambiado
      if (!smlvActual || smlvActual <= 0) return;
      if (smlvAnterior === null) {
        // Primera carga, solo guardar el valor sin actualizar
        setSmlvAnterior(smlvActual);
        return;
      }
      if (smlvActual === smlvAnterior) return;
      
      console.log(`💰 SMLV actualizado de $${smlvAnterior?.toLocaleString('es-CO')} a $${smlvActual.toLocaleString('es-CO')}`);
      console.log("🔄 Actualizando salarioBase de todos los empleados con el nuevo SMLV...");
      
      // Actualizar el estado local de empleados
      const empleadosActualizados = empleados.map(emp => {
        // Actualizar salarioBase si el empleado tiene el SMLV anterior como base
        // o si tiene un valor por defecto de SMLV histórico
        const salarioActual = Number(emp.salarioBase) || 0;
        const valoresAntiguosSMLV = [1750905, 1423500, 1300000, 1160000, 1000000, 908526, 877803, 828116, 781242, 737717, smlvAnterior]; // Valores SMLV históricos
        
        if (valoresAntiguosSMLV.includes(salarioActual) && salarioActual !== smlvActual) {
          return { ...emp, salarioBase: smlvActual };
        }
        return emp;
      });
      
      setEmpleados(empleadosActualizados);
      
      // Actualizar en Supabase todos los empleados que tenían el SMLV anterior
      try {
        for (const emp of empleadosActualizados) {
          const empOriginal = empleados.find(e => e.id === emp.id);
          if (empOriginal && empOriginal.salarioBase !== emp.salarioBase) {
            // El salario fue actualizado, guardar en base de datos
            await supabase
              .from("empleados")
              .update({ salariobase: emp.salarioBase })
              .eq("id", emp.id);
            console.log(`✅ Salario actualizado para ${emp.nombre}: $${emp.salarioBase.toLocaleString('es-CO')}`);
          }
        }
        console.log("✅ Todos los salarios base han sido sincronizados con el nuevo SMLV");
      } catch (err) {
        console.error("❌ Error actualizando salarios en Supabase:", err);
      }
      
      setSmlvAnterior(smlvActual);
    };
    
    actualizarSalariosConSMLV();
  }, [valoresAnuales?.smlv, empleados.length]);

  // Función para obtener el primer tab permitido
  const obtenerPrimerTabPermitido = () => {
    const tabsOrdenados = [
      { id: "empleados", permiso: "rh_empleados" },
      { id: "sedes", permiso: "rh_sedes" },
      { id: "horarios", permiso: "rh_horarios" },
      { id: "memorandos", permiso: "rh_memorandos" },
      { id: "ausencias", permiso: "rh_ausencias" },
      { id: "vacaciones", permiso: "rh_vacaciones" },
      { id: "prestamos", permiso: "rh_prestamos" },
      { id: "nomina", permiso: "rh_nomina" },
      { id: "liquidaciones", permiso: "rh_liquidaciones" },
      { id: "certificaciones", permiso: "rh_certificaciones" },
      { id: "contratos", permiso: "rh_contratos" },
      { id: "permisos", permiso: "rh_permisos" },
      { id: "configuracion", permiso: "rh_configuracion" },
    ];
    
    for (const tab of tabsOrdenados) {
      if (tienePermiso(tab.permiso)) {
        return tab.id;
      }
    }
    return "empleados"; // fallback si no tiene ningún permiso
  };

  // Constante para localStorage del tab de RH
  const STORAGE_NAV_TAB = "app_navegacion_tab_rh_v1";

  const [tab, setTabInterno] = useState(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_NAV_TAB);
      if (saved) {
        const tabGuardado = JSON.parse(saved);
        // Verificar que sea un tab válido
        const tabsValidos = ["empleados", "sedes", "horarios", "memorandos", "ausencias", "vacaciones", "prestamos", "nomina", "liquidaciones", "certificaciones", "contratos", "permisos", "configuracion"];
        if (tabsValidos.includes(tabGuardado)) {
          return tabGuardado;
        }
      }
    } catch (e) {
      // Si hay error, usar el fallback
    }
    return obtenerPrimerTabPermitido();
  });

  // Wrapper para guardar tab en localStorage al cambiar
  const setTab = useCallback((nuevoTab) => {
    setTabInterno(nuevoTab);
    try {
      window.localStorage.setItem(STORAGE_NAV_TAB, JSON.stringify(nuevoTab));
    } catch (e) {
      console.warn("Error guardando tab en localStorage", e);
    }
  }, []);
  
  // Efecto para cambiar el tab si el usuario no tiene permiso para el tab actual
  React.useEffect(() => {
    const tabActualPermitido = () => {
      const permisosTab = {
        "empleados": "rh_empleados",
        "sedes": "rh_sedes",
        "horarios": "rh_horarios",
        "memorandos": "rh_memorandos",
        "ausencias": "rh_ausencias",
        "vacaciones": "rh_vacaciones",
        "prestamos": "rh_prestamos",
        "nomina": "rh_nomina",
        "liquidaciones": "rh_liquidaciones",
        "certificaciones": "rh_certificaciones",
        "contratos": "rh_contratos",
        "permisos": "rh_permisos",
        "configuracion": "rh_configuracion",
      };
      return tienePermiso(permisosTab[tab] || "");
    };
    
    if (!tabActualPermitido()) {
      setTab(obtenerPrimerTabPermitido());
    }
  }, [usuarioConPermisos]);

  const [busqueda, setBusqueda] = useState("");
  const [verInactivos, setVerInactivos] = useState(false);
  const [editEmpleado, setEditEmpleado] = useState(null);
  const [viewEmpleado, setViewEmpleado] = useState(null);
  const [memorandoEditando, setMemorandoEditando] = useState(null);
  const [memorandoAdjuntando, setMemorandoAdjuntando] = useState(null);
 
  // === REPOSITORIO DE HORARIOS ===
  const [horariosRepo, setHorariosRepo] = useState([]);
  const [horarioSemanaInicio, setHorarioSemanaInicio] = useState(toISO(new Date()));
  const [horarioSemanaFin, setHorarioSemanaFin] = useState(
    addDaysLocal(toISO(new Date()), 6)
  );
  const [horarioEstado, setHorarioEstado] = useState("Borrador");
  const [horarioVista, setHorarioVista] = useState("configurar");

  React.useEffect(() => {
    try {
      window.localStorage.removeItem("liquidaciones");
    } catch (e) {
      console.error("Error limpiando liquidaciones del almacenamiento local", e);
    }
  }, []);

  useEffect(() => {
    let activo = true;
    setCargandoTareasPago(true);
    console.log("🔄 Iniciando carga de procesos administrativos... filtroEmpresa:", filtroEmpresa);
    cargarProcesosPagoDeSupabase(filtroEmpresa)
      .then((lista) => {
        if (!activo) return;
        console.log(`📋 Procesos administrativos cargados: ${lista?.length || 0} tareas`);
        if (Array.isArray(lista)) {
          setTareasPago(lista);
          if (lista.length > 0) {
            console.log("📋 Primera tarea:", lista[0]?.tipo, lista[0]?.estado, lista[0]?.id);
          }
        } else {
          console.warn("⚠️ La lista de tareas no es un array:", lista);
          setTareasPago([]);
        }
      })
      .catch((e) => {
        console.error("❌ No se pudieron cargar los procesos de pago desde Supabase", e);
        setTareasPago([]);
      })
      .finally(() => {
        if (activo) setCargandoTareasPago(false);
      });

    return () => {
      activo = false;
    };
  }, [filtroEmpresa]);

  // === NOTIFICACIONES GLOBALES (basadas en tareasPago) ===
  // Helper para obtener nombre de empleado
  const nombreEmpleadoNotif = (id) => {
    if (!id) return "";
    const emp = (empleados || []).find((e) => String(e?.id) === String(id));
    return emp?.nombre || "";
  };

  const notificacionesGlobales = useMemo(() => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    // Filtrar tareas pendientes (no procesadas)
    let pendientes = (tareasPago || []).filter(
      (t) => (t.estado || "").toString().trim().toLowerCase() !== "procesado"
    );

    // Si NO es gerente, filtrar solo las tareas del usuario actual
    if (!esGerente) {
      const nombreUsuario = (empleadoActual?.nombre || usuarioConPermisos?.nombre || usuario?.nombre || "")
        .toString().trim().toLowerCase();
      const idsPermitidos = new Set();
      if (empleadoActual?.id) idsPermitidos.add(String(empleadoActual.id));
      if (usuarioConPermisos?.id) idsPermitidos.add(String(usuarioConPermisos.id));
      if (usuario?.id) idsPermitidos.add(String(usuario.id));

      pendientes = pendientes.filter((t) => {
        const respId = t.responsableId || t.responsable || "";
        const empId = t.empleadoId || "";
        const creadorId = t.creadorId || ""; // Incluir creador en el filtro
        const respName = (nombreEmpleadoNotif(respId) || "").trim().toLowerCase();
        const empName = (nombreEmpleadoNotif(empId) || "").trim().toLowerCase();
        const creadorName = (nombreEmpleadoNotif(creadorId) || t.creadorNombre || "").trim().toLowerCase();
        const respTexto = (typeof t.responsable === "string" ? t.responsable : "").trim().toLowerCase();

        // Incluir creador en las verificaciones
        const coincideId = idsPermitidos.has(String(respId)) || idsPermitidos.has(String(empId)) || idsPermitidos.has(String(creadorId));
        const coincideNombre = nombreUsuario && (respName === nombreUsuario || empName === nombreUsuario || respTexto === nombreUsuario || creadorName === nombreUsuario);

        return coincideId || coincideNombre;
      });
    }

    const items = pendientes.map((t) => {
      let fecha = null;
      const fechaStr = t.fechaLimite || t.fechaCreacion || "";
      if (fechaStr) {
        const [y, m, d] = fechaStr.split("-").map(Number);
        fecha = new Date(y, m - 1, d);
      }
      let dias = null;
      if (fecha) {
        fecha.setHours(0, 0, 0, 0);
        const diffMs = fecha.getTime() - hoy.getTime();
        dias = Math.round(diffMs / (1000 * 60 * 60 * 24));
      }
      return {
        id: t.id,
        tipo: typeof t.tipo === "object" ? t.tipo?.nombre || "" : t.tipo || "Tarea",
        fechaLimite: t.fechaLimite || "Sin fecha",
        diasRestantes: dias,
        estado: t.estado || "",
      };
    });

    return items.sort((a, b) => {
      const da = a.diasRestantes ?? 9999;
      const db = b.diasRestantes ?? 9999;
      return da - db;
    });
  }, [tareasPago, esGerente, empleadoActual, usuarioConPermisos, usuario, empleados]);

  const pendientesGlobales = useMemo(() => {
    let pendientes = (tareasPago || []).filter((t) => (t.estado || "").toString().trim().toLowerCase() !== "procesado");
    
    // Si NO es gerente, filtrar solo las tareas del usuario actual
    if (!esGerente) {
      const nombreUsuario = (empleadoActual?.nombre || usuarioConPermisos?.nombre || usuario?.nombre || "")
        .toString().trim().toLowerCase();
      const idsPermitidos = new Set();
      if (empleadoActual?.id) idsPermitidos.add(String(empleadoActual.id));
      if (usuarioConPermisos?.id) idsPermitidos.add(String(usuarioConPermisos.id));
      if (usuario?.id) idsPermitidos.add(String(usuario.id));

      pendientes = pendientes.filter((t) => {
        const respId = t.responsableId || t.responsable || "";
        const empId = t.empleadoId || "";
        const respName = (nombreEmpleadoNotif(respId) || "").trim().toLowerCase();
        const empName = (nombreEmpleadoNotif(empId) || "").trim().toLowerCase();
        const respTexto = (typeof t.responsable === "string" ? t.responsable : "").trim().toLowerCase();

        const coincideId = idsPermitidos.has(String(respId)) || idsPermitidos.has(String(empId));
        const coincideNombre = nombreUsuario && (respName === nombreUsuario || empName === nombreUsuario || respTexto === nombreUsuario);

        return coincideId || coincideNombre;
      });
    }
    
    return pendientes;
  }, [tareasPago, esGerente, empleadoActual, usuarioConPermisos, usuario, empleados]);

  useEffect(() => {
    setNotificacionesVistasPrincipal(false);
  }, [notificacionesGlobales.length]);
 
    // === MANEJO SEGURO DEL REPOSITORIO DE HORARIOS ===
 
    React.useEffect(() => {
      console.log("🔄 [Horarios] useEffect disparado - filtroEmpresa:", filtroEmpresa, "tipo:", typeof filtroEmpresa);
      cargarHorariosDeSupabase(filtroEmpresa).then((lista) => {
        console.log("📦 [Horarios] Recibidos", lista?.length || 0, "horarios para empresa:", filtroEmpresa || "TODAS");
        if (Array.isArray(lista)) {
          setHorariosRepo(lista);
        }
      });
    }, [filtroEmpresa]);

    // 🔹 Usar useCallback para garantizar que filtroEmpresa esté actualizado
    const guardarHorarioRepo = useCallback((nuevoHorario) => {
      // 🔒 Validar que haya una empresa seleccionada para guardar
      if (!filtroEmpresa) {
        console.error("❌ No se puede guardar horario sin empresa seleccionada. filtroEmpresa:", filtroEmpresa);
        alert("⚠️ Debes seleccionar una empresa específica para crear o modificar horarios.\n\nNo se puede usar 'Todas las empresas'.");
        return false;
      }
      
      console.log("💾 Guardando horario con empresa_id:", filtroEmpresa);
      
      setHorariosRepo((prev) => {
        // Aseguramos que siempre sea un array
        const arr = Array.isArray(prev) ? prev : [];
  
        // Si ya existe un horario con el mismo id, lo sustituimos
        const idx = arr.findIndex((h) => h.id === nuevoHorario.id);
  
        if (idx >= 0) {
          const copia = [...arr];
          copia[idx] = nuevoHorario;
          return copia;
        }
  
        // Si no existe, lo agregamos al final
        return [...arr, nuevoHorario];
      });

      guardarHorarioEnSupabase(nuevoHorario, filtroEmpresa).catch((err) => {
        console.error("Error guardando horario en Supabase (nuevo)", err);
      });
      
      return true;
    }, [filtroEmpresa]);
  
    // 🔹 Usar useCallback para garantizar que filtroEmpresa esté actualizado
    const actualizarHorarioRepo = useCallback((id, horarioActualizado) => {
      // 🔒 Validar que haya una empresa seleccionada para actualizar
      if (!filtroEmpresa) {
        console.error("❌ No se puede actualizar horario sin empresa seleccionada. filtroEmpresa:", filtroEmpresa);
        alert("⚠️ Debes seleccionar una empresa específica para crear o modificar horarios.\n\nNo se puede usar 'Todas las empresas'.");
        return false;
      }
      
      console.log("💾 Actualizando horario con empresa_id:", filtroEmpresa);
      
      setHorariosRepo((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
  
        const idx = arr.findIndex((h) => h.id === id);
        if (idx === -1) {
          // Por si acaso no lo encuentra, lo agrega
          return [...arr, horarioActualizado];
        }
  
        const copia = [...arr];
        copia[idx] = horarioActualizado; // reemplazamos el registro completo
        return copia;
      });

      guardarHorarioEnSupabase(horarioActualizado, filtroEmpresa).catch((err) => {
        console.error("Error guardando horario en Supabase (update)", err);
      });
      
      return true;
    }, [filtroEmpresa]);
  
 
  // === ESTADO NOMINA / LIQUIDACIONES / PRESTAMOS ===
  const [liquidaciones, setLiquidaciones] = useState([]);
 
  // === FUNCIONES AUXILIARES QUE YA TENÍAS ===
  function addSede(nuevaSede) {
    if (!nuevaSede) return;
    setSedes((prev) => {
      const lista = Array.isArray(prev) ? [...prev] : [];
      if (typeof nuevaSede === "string") {
        const yaExiste = lista.some((s) =>
          typeof s === "string" ? s === nuevaSede : s?.nombre === nuevaSede
        );
        return yaExiste ? lista : [...lista, nuevaSede];
      }

      const yaExiste = lista.some((s) => {
        if (!s) return false;
        if (typeof s === "string") {
          return s === nuevaSede.nombre;
        }
        return (s.id && nuevaSede.id && s.id === nuevaSede.id) || s.nombre === nuevaSede.nombre;
      });
      return yaExiste ? lista : [...lista, nuevaSede];
    });
  }
 
  function renameSede(oldName, newName) {
    if (!oldName || !newName) return;
    setSedes((prev) =>
      (Array.isArray(prev) ? prev : []).map((s) => {
        if (typeof s === "string") {
          return s === oldName ? newName : s;
        }
        if (!s) return s;
        if (s.id === oldName || s.nombre === oldName) {
          return { ...s, nombre: newName };
        }
        return s;
      })
    );
    setEmpleados((prev) =>
      prev.map((e) => (e.sede === oldName ? { ...e, sede: newName } : e))
    );
  }
 
  function guardarEmpleado(empleado) {
    console.log("💾 guardarEmpleado llamado con:", empleado);
    
    // Guardar en el estado local
    setEmpleados((prev) => {
      const existe = prev.find((e) => e.id === empleado.id);
      if (existe) {
        console.log("🔄 Actualizando empleado existente:", empleado.id);
        return prev.map((e) => (e.id === empleado.id ? empleado : e));
      }
      console.log("✨ Agregando nuevo empleado:", empleado.id);
      return [...prev, empleado];
    });

    // Cerrar el modal del formulario
    setEditEmpleado(null);

    // Guardar en Supabase de forma asincrónica (sin mostrar avisos)
    console.log("📤 Enviando a Supabase...");
    guardarEmpleadoEnSupabase(empleado, filtroEmpresa)
      .then((exito) => {
        if (exito) {
          console.log("✅ Empleado guardado en Supabase:", empleado.nombres);
        } else {
          console.error("❌ Error al guardar en Supabase - respuesta false");
        }
      })
      .catch((err) => {
        console.error("❌ Error en guardarEmpleado:", err);
      });
  }
 
  function toggleActivo(id, nuevoEstado) {
    // Actualizar estado local
    setEmpleados((prev) =>
      prev.map((e) => (e.id === id ? { ...e, activo: nuevoEstado } : e))
    );
    
    // Guardar cambio de estado activo en Supabase
    const empleadoActual = empleados.find(e => e.id === id);
    if (empleadoActual) {
      const empleadoActualizado = { ...empleadoActual, activo: nuevoEstado };
      guardarEmpleadoEnSupabase(empleadoActualizado, filtroEmpresa)
        .then((exito) => {
          if (exito) {
            console.log(`✅ Estado del empleado ${empleadoActual.nombre} actualizado a ${nuevoEstado ? 'activo' : 'inactivo'} en Supabase`);
          } else {
            console.error("❌ Error al actualizar estado del empleado en Supabase");
          }
        })
        .catch((err) => {
          console.error("❌ Error al guardar estado del empleado:", err);
        });
    }
  }
 
  async function crearMemorando(m) {
    const base = {
      ...m,
      id: ensureUuid(m.id),
      creadoEn: m.creadoEn || toISO(new Date()),
      adjuntos: Array.isArray(m.adjuntos) ? m.adjuntos : [],
    };

    setMemorandos((prev) => [...prev, base]);

    try {
      const guardado = await guardarMemorandoEnSupabase(base, filtroEmpresa);
      if (guardado) {
        setMemorandos((prev) => prev.map((mem) => (mem.id === guardado.id ? guardado : mem)));
      } else {
        throw new Error("Supabase no devolvió confirmación del memorando");
      }
    } catch (err) {
      console.error("Error creando memorando en Supabase:", err);
      setMemorandos((prev) => prev.filter((mem) => mem.id !== base.id));
      if (typeof window !== "undefined") {
        alert("No se pudo guardar el memorando en Supabase. Revisa la consola para más detalles.");
      }
    }
  }
 
  async function eliminarMemorando(id) {
    if (!id) return;
    
    const confirmacion = window.confirm("¿Estás seguro de que deseas eliminar este memorando? Esta acción no se puede deshacer.");
    if (!confirmacion) return;
    
    setMemorandos((prev) => prev.filter((m) => m.id !== id));
    try {
      const exito = await eliminarMemorandoEnSupabase(id);
      if (!exito) {
        throw new Error("Supabase no confirmó la eliminación");
      }
    } catch (err) {
      console.error("Error eliminando memorando en Supabase:", err);
      if (typeof window !== "undefined") {
        alert("No se pudo eliminar el memorando en Supabase. Revisa la consola para más detalles.");
      }
    }
  }

  async function actualizarMemorando(memorandoActualizado) {
    if (!memorandoActualizado?.id) return;
    const base = {
      ...memorandoActualizado,
      id: ensureUuid(memorandoActualizado.id),
      adjuntos: Array.isArray(memorandoActualizado.adjuntos) ? memorandoActualizado.adjuntos : [],
    };

    setMemorandos((prev) => prev.map((m) => (m.id === base.id ? base : m)));

    try {
      const guardado = await guardarMemorandoEnSupabase(base, filtroEmpresa);
      if (guardado) {
        setMemorandos((prev) => prev.map((mem) => (mem.id === guardado.id ? guardado : mem)));
      } else {
        throw new Error("Supabase no devolvió confirmación del memorando");
      }
    } catch (err) {
      console.error("Error actualizando memorando en Supabase:", err);
      if (typeof window !== "undefined") {
        alert("No se pudo actualizar el memorando en Supabase. Revisa la consola para más detalles.");
      }
    }
  }

  async function adjuntarDocumentoMemorando(memorandoId, nuevoAdjunto) {
    if (!memorandoId || !nuevoAdjunto) return;
    const origen = memorandos.find((m) => m.id === memorandoId);
    if (!origen || origen.cerrado) return;
    const adjuntoConId = { ...nuevoAdjunto, id: nuevoAdjunto.id || uid() };
    const actual = {
      ...origen,
      adjuntos: [...(Array.isArray(origen.adjuntos) ? origen.adjuntos : []), adjuntoConId],
    };

    setMemorandos((prev) => prev.map((m) => (m.id === memorandoId ? actual : m)));

    try {
      const guardado = await guardarMemorandoEnSupabase(actual, filtroEmpresa);
      if (guardado) {
        setMemorandos((prev) => prev.map((mem) => (mem.id === memorandoId ? guardado : mem)));
      } else {
        throw new Error("Supabase no devolvió confirmación del memorando");
      }
    } catch (err) {
      console.error("Error adjuntando documento de memorando en Supabase:", err);
      if (typeof window !== "undefined") {
        alert("No se pudo guardar el adjunto en Supabase. Revisa la consola para más detalles.");
      }
    }
  }
 
  function crearAusencia(a) {
    // Verificar si es una actualización (el ID ya existe) o una nueva ausencia
    const esActualizacion = ausencias.some((au) => au.id === a.id);
    
    if (esActualizacion) {
      // Actualizar ausencia existente
      setAusencias((prev) => prev.map((au) => (au.id === a.id ? a : au)));
    } else {
      // Agregar nueva ausencia
      setAusencias((prev) => [...prev, a]);
    }
    
    // Guardar en Supabase
    guardarAusenciaEnSupabase(a, filtroEmpresa)
      .then((ausenciaGuardada) => {
        if (ausenciaGuardada) {
          console.log(esActualizacion ? "✅ Ausencia actualizada en Supabase" : "✅ Ausencia guardada en Supabase");
          // Combinar datos originales con los devueltos por Supabase para no perder campos
          // Los datos originales tienen prioridad para campos como 'inicio', 'fin', etc.
          const ausenciaCombinada = {
            ...ausenciaGuardada,
            ...a, // Mantener datos originales
            id: ausenciaGuardada.id || a.id,
            creadoEn: ausenciaGuardada.creadoEn || a.creadoEn,
            actualizadoEn: ausenciaGuardada.actualizadoEn,
          };
          console.log("📋 Ausencia combinada para actualizar estado:", JSON.stringify(ausenciaCombinada, null, 2));
          setAusencias((prev) =>
            prev.map((au) => (au.id === a.id ? ausenciaCombinada : au))
          );
        } else {
          console.error("❌ No se guardó la ausencia en Supabase");
          // Mantener la ausencia local aunque no se haya guardado en Supabase
        }
      })
      .catch((err) => {
        console.error("❌ Error al guardar ausencia:", err);
        alert("No se pudo guardar la ausencia en Supabase. Revisa la consola para más detalles.");
      });
  }
 
  function inactivarEmpleado(empleadoId) {
    setEmpleados((prev) =>
      prev.map((e) => (e.id === empleadoId ? { ...e, activo: false } : e))
    );
  }
 
  // 🔹 Eliminar ausencia (con persistencia en Supabase)
  async function eliminarAusencia(id) {
    // Primero eliminar del estado local para respuesta inmediata
    setAusencias((prev) => prev.filter((a) => a.id !== id));
    
    // Luego eliminar de Supabase
    const eliminado = await eliminarAusenciaDeSupabase(id);
    
    if (eliminado) {
      console.log("✅ Ausencia eliminada correctamente de Supabase:", id);
    } else {
      console.error("❌ Error al eliminar ausencia de Supabase. La ausencia puede reaparecer al recargar.");
      alert("⚠️ La ausencia se eliminó localmente pero hubo un error al sincronizar con Supabase. Puede reaparecer al recargar.");
    }
  }
 
  async function crearPrestamo(p) {
    // Guardar en Supabase
    const prestamoGuardado = await guardarPrestamoEnSupabase(p, filtroEmpresa);
    
    if (prestamoGuardado) {
      // Si se guardó correctamente, usar los datos de Supabase
      setPrestamos((prev) => [...prev, prestamoGuardado]);
      console.log("✅ Préstamo creado y guardado en Supabase");
    } else {
      // Si hay error, guardar al menos en el estado local
      setPrestamos((prev) => [...prev, p]);
      console.log("⚠️  Préstamo guardado en estado local (error con Supabase)");
    }
  }

  // Función para eliminar un préstamo (con bitácora)
  async function eliminarPrestamo(prestamo, motivo) {
    try {
      console.log("🗑️ Iniciando eliminación de préstamo:", prestamo.id);
      
      // 1. Guardar en bitácora antes de eliminar
      const usuarioNombre = usuario?.nombre || usuario?.email || "Usuario desconocido";
      const usuarioId = usuario?.id || usuario?.email || "sin-id";
      
      await guardarBitacoraPrestamoEliminado(prestamo, usuarioId, usuarioNombre, motivo);
      
      // 2. Eliminar de Supabase
      const eliminado = await eliminarPrestamoDeSupabase(prestamo.id);
      
      if (eliminado) {
        // 3. Actualizar estado local
        setPrestamos((prev) => prev.filter((p) => p.id !== prestamo.id));
        console.log("✅ Préstamo eliminado correctamente");
        return true;
      } else {
        console.error("❌ No se pudo eliminar el préstamo de Supabase");
        return false;
      }
    } catch (e) {
      console.error("❌ Error en eliminarPrestamo:", e);
      return false;
    }
  }
 
  // Esta función se usa cuando marcas / desmarcas una cuota en

  async function abonarPrestamo(idPrestamo, cuotaId) {
    // Primero actualizar el estado local
    let prestamoActualizado = null;
    
    setPrestamos((prev) =>
      prev.map((p) => {
        if (p.id !== idPrestamo) return p;

        const planOriginal = p.plan || p.cuotas || [];

        // Solo cambiar pagado, NO noDescontable (que se cambia al procesar nómina)
        const nuevoPlan = planOriginal.map((c) =>
          c.id === cuotaId
            ? { ...c, pagado: !c.pagado }
            : c
        );

        // Calcular saldo basado en cuotas bloqueadas (noDescontable)
        const abonadoDefinitivo = nuevoPlan
          .filter((c) => c.noDescontable)
          .reduce((sum, c) => sum + Number(c.valor || 0), 0);

        const totalPrestamo = Number(p.valor || p.monto || 0);
        const saldo = Math.max(0, totalPrestamo - abonadoDefinitivo);

        prestamoActualizado = {
          ...p,
          plan: nuevoPlan,
          saldo,
        };

        return prestamoActualizado;
      })
    );

    // Guardar en Supabase después de actualizar el estado
    if (prestamoActualizado) {
      const resultado = await guardarPrestamoEnSupabase(prestamoActualizado, filtroEmpresa);
      if (!resultado) {
        console.warn("⚠️  Toggle registrado localmente pero hubo error con Supabase");
      } else {
        console.log("✅ Toggle guardado en Supabase");
      }
    }
  }

  // Función para reactivar una cuota individual que ya fue descontada (solo Gerencia)
  async function reactivarCuotaIndividual(idPrestamo, cuotaId) {
    if (!esGerente) {
      alert("❌ Solo usuarios con perfil Gerencia pueden reactivar cuotas descontadas.");
      return;
    }

    const confirmacion = window.confirm(
      "⚠️ ATENCIÓN: Está a punto de reactivar una cuota que ya fue descontada en una nómina anterior.\n\n" +
      "Esto permitirá que la cuota vuelva a ser descontada en una nómina futura.\n\n" +
      "¿Está seguro de continuar?"
    );

    if (!confirmacion) return;

    let prestamoActualizado = null;

    setPrestamos((prev) =>
      prev.map((p) => {
        if (p.id !== idPrestamo) return p;

        const planOriginal = p.plan || [];

        // Quitar noDescontable de la cuota específica
        const nuevoPlan = planOriginal.map((c) =>
          c.id === cuotaId
            ? { ...c, noDescontable: false, pagado: true } // Dejar marcada como pagado para que aparezca seleccionada
            : c
        );

        // Recalcular saldo (cuotas que están bloqueadas = ya descontadas definitivamente)
        const abonadoDefinitivo = nuevoPlan
          .filter((c) => c.noDescontable)
          .reduce((sum, c) => sum + Number(c.valor || 0), 0);

        const totalPrestamo = Number(p.valor || p.monto || 0);
        const saldo = Math.max(0, totalPrestamo - abonadoDefinitivo);

        prestamoActualizado = {
          ...p,
          plan: nuevoPlan,
          saldo,
        };

        return prestamoActualizado;
      })
    );

    // Guardar en Supabase
    if (prestamoActualizado) {
      const resultado = await guardarPrestamoEnSupabase(prestamoActualizado, filtroEmpresa);
      if (resultado) {
        console.log("✅ Cuota reactivada y guardada en Supabase");
        alert("✅ Cuota reactivada correctamente.\n\nLa cuota ahora está disponible para ser descontada en una nómina futura.");
      } else {
        console.warn("⚠️ Cuota reactivada localmente pero hubo error con Supabase");
        alert("⚠️ La cuota se reactivó localmente pero hubo un error al guardar en la base de datos.");
      }
    }
  }
  

  function registrarVacaciones(v) {
    setVacaciones((prev) => [...prev, v]);    // Guardar en Supabase
    guardarVacacionesEnSupabase(v, filtroEmpresa)
      .then((vacacionGuardada) => {
        if (vacacionGuardada) {
          console.log("✅ Vacación guardada en Supabase");
          // Actualizar con los datos devueltos por Supabase
          setVacaciones((prev) =>
            prev.map((vac) => (vac.id === v.id ? vacacionGuardada : vac))
          );
        } else {
          console.error("❌ No se guardó la vacación en Supabase");
        }
      })
      .catch((err) => {
        console.error("❌ Error al guardar vacación:", err);
        alert("No se pudo guardar la vacación en Supabase. Revisa la consola para más detalles.");
      });
  }
 
  function guardarNominaRepo(nomina) {
    console.log(`📦 Actualizando nominasRepo con nueva nómina: ${nomina.nombre} (${nomina.items?.length} empleados)`);
    setNominasRepo((prev) => [nomina, ...prev]);

    // Guardar en Supabase
    console.log(`📤 Iniciando guardado en Supabase...`);
    guardarNominaEnSupabase(nomina, filtroEmpresa)
      .then((exito) => {
        if (exito) {
          console.log(`✅ Nómina guardada exitosamente en Supabase`);
        }
      })
      .catch((err) => console.error("Error al guardar nómina:", err));

    // Guardar comidas de la quincena en Supabase
    if (nomina.periodoComidas && nomina.comidasQuincena) {
      console.log(`📤 Guardando comidas de quincena ${nomina.periodoComidas} en Supabase...`);
      guardarComidasQuincenaEnSupabase(nomina.periodoComidas, nomina.comidasQuincena, filtroEmpresa)
        .then((exito) => {
          if (exito) {
            console.log(`✅ Comidas guardadas exitosamente en Supabase`);
          }
        })
        .catch((err) => console.error("Error al guardar comidas:", err));
    }
  }

  // Función para reabrir una nómina (eliminarla del repositorio para poder editarla)
  function reabrirNomina(nomina) {
    console.log(`🔓 Reabriendo nómina: ${nomina.nombre} (ID: ${nomina.id})`);
    
    // Reactivar cuotas de préstamos que fueron descontadas en esta nómina
    // (quitar noDescontable pero mantener pagado para que sigan seleccionadas)
    let cuotasAReactivar = [];
    
    // Buscar en detallesPrestamos a nivel de nómina (nóminas guardadas localmente)
    if (nomina.detallesPrestamos && nomina.detallesPrestamos.length > 0) {
      cuotasAReactivar = nomina.detallesPrestamos.flatMap(dp => dp.cuotasProcesadas || []);
    }
    
    // También buscar en los items (nóminas cargadas desde Supabase)
    if (cuotasAReactivar.length === 0 && nomina.items && nomina.items.length > 0) {
      cuotasAReactivar = nomina.items.flatMap(item => {
        // Buscar cuotasProcesadas directamente en el item
        if (item.cuotasProcesadas && item.cuotasProcesadas.length > 0) {
          return item.cuotasProcesadas;
        }
        // También buscar en detallesPrestamos del item (si existe)
        if (item.detallesPrestamos && Array.isArray(item.detallesPrestamos)) {
          return item.detallesPrestamos.flatMap(dp => dp.cuotasProcesadas || []);
        }
        return [];
      });
    }
    
    console.log(`🔍 Cuotas encontradas para reactivar: ${cuotasAReactivar.length}`);
    
    if (cuotasAReactivar.length > 0) {
      console.log(`🔄 Reactivando cuotas de préstamos de la nómina...`);
      reactivarCuotasPrestamos(cuotasAReactivar);
    } else {
      console.log(`⚠️ No se encontraron cuotas de préstamos para reactivar en esta nómina`);
    }

    // Restaurar comidas de la nómina para que queden disponibles para editar
    if (nomina.periodoComidas || nomina.rango) {
      const periodo = nomina.periodoComidas || (() => {
        // Calcular el período desde el rango de la nómina
        const fecha = nomina.rango?.inicio || new Date().toISOString().slice(0, 10);
        const [anio, mes] = fecha.split("-");
        const dia = parseInt(fecha.split("-")[2] || "1");
        const q = dia <= 15 ? "Q1" : "Q2";
        return `${anio}-${mes}-${q}`;
      })();

      // Restaurar comidas desde los items de la nómina
      const comidasRestaurar = {};
      (nomina.items || []).forEach(item => {
        if (item.descuentos?.comida > 0) {
          comidasRestaurar[item.empleadoId] = item.descuentos.comida;
        }
      });

      if (Object.keys(comidasRestaurar).length > 0) {
        console.log(`🍝 Restaurando comidas para período ${periodo}:`, comidasRestaurar);
        // Las comidas ya están en el estado, no necesitamos hacer nada más
        // porque al eliminar la nómina, la quincena queda abierta para editar
      }
    }
    
    // Eliminar del estado local
    setNominasRepo((prev) => prev.filter((n) => n.id !== nomina.id));
    
    // Eliminar de Supabase
    eliminarNominaDeSupabase(nomina.id)
      .then((exito) => {
        if (exito) {
          console.log(`✅ Nómina eliminada de Supabase para reapertura`);
        } else {
          console.error("❌ No se pudo eliminar la nómina de Supabase");
        }
      })
      .catch((err) => {
        console.error("❌ Error al eliminar nómina de Supabase:", err);
      });
    
    // === ELIMINAR RESUMEN DE NÓMINA ===
    const resumenId = `resumen_${nomina.id}`;
    eliminarResumenNominaDeSupabase(resumenId)
      .then((exito) => {
        if (exito) {
          console.log(`📊 Resumen de nómina eliminado: ${resumenId}`);
        } else {
          console.log(`⚠️ No se encontró resumen para eliminar o ya fue eliminado`);
        }
      })
      .catch((err) => {
        console.error("❌ Error al eliminar resumen de nómina:", err);
      });
    
    // === ELIMINAR SALDOS PENDIENTES CREADOS POR ESTA NÓMINA ===
    eliminarSaldosPendientesPorNomina(nomina.id)
      .then((saldosEliminados) => {
        if (saldosEliminados && saldosEliminados.length > 0) {
          console.log(`💰 ${saldosEliminados.length} saldo(s) pendiente(s) eliminados de la nómina`);
          // Actualizar estado local de otros descuentos (marcar como inactivos)
          setOtrosDescuentos(prev => 
            prev.map(d => 
              saldosEliminados.some(s => s.id === d.id) 
                ? { ...d, activo: false } 
                : d
            )
          );
        }
      })
      .catch((err) => {
        console.error("❌ Error al eliminar saldos pendientes:", err);
      });
  }

  // Función para reactivar cuotas de préstamos (quitar noDescontable pero MANTENER pagado para identificar cuáles eran)
  function reactivarCuotasPrestamos(cuotasAReactivar = []) {
    if (!cuotasAReactivar || cuotasAReactivar.length === 0) return;
    
    console.log("🔓 Reactivando cuotas de préstamos:", JSON.stringify(cuotasAReactivar, null, 2));
    
    setPrestamos((prev) =>
      prev.map((p) => {
        // Encontrar cuotas de este préstamo que deben reactivarse
        const cuotasDeEste = cuotasAReactivar.filter(
          (c) => c.prestamoId === p.id
        );
        
        if (cuotasDeEste.length === 0) return p;
        
        // Quitar noDescontable PERO MANTENER pagado=true para identificar cuáles eran las cuotas de la quincena
        // Así el usuario puede ver cuáles estaban seleccionadas y decidir si las mantiene o las deselecciona
        const nuevoPlan = (p.plan || []).map((cuota) => {
          const debeReactivarse = cuotasDeEste.some(
            (c) => c.cuotaId === cuota.id
          );
          if (debeReactivarse) {
            console.log(`   🔓 Reactivando cuota #${cuota.nro} del préstamo ${p.id} (valor: $${cuota.valor}) - quedará seleccionada`);
            // Quitar noDescontable pero MANTENER pagado=true para identificar
            return { ...cuota, noDescontable: false, pagado: true };
          }
          return cuota;
        });
        
        // Recalcular saldo (cuotas que no están bloqueadas)
        const saldo = nuevoPlan
          .filter((c) => !c.noDescontable)
          .reduce((acc, c) => acc + (c.valor || 0), 0);
        
        const prestamoActualizado = { ...p, plan: nuevoPlan, saldo };
        
        // Guardar en Supabase
        guardarPrestamoEnSupabase(prestamoActualizado, filtroEmpresa)
          .then(() => {
            console.log(`✅ Cuotas del préstamo ${p.id} reactivadas en Supabase`);
          })
          .catch((err) => {
            console.error(`❌ Error al reactivar cuotas del préstamo ${p.id}:`, err);
          });
        
        return prestamoActualizado;
      })
    );
    
    console.log(`✅ ${cuotasAReactivar.length} cuotas reactivadas (seleccionadas para identificar, pueden deseleccionarse)`);
  }
 
  function handleGuardarLiquidacion(reg) {
    // Validación: Verificar que no exista otra liquidación del mismo tipo y empleado con fechas que se crucen
    const yaExisteLiquidacion = liquidacionesRepo.some((liq) => {
      // Solo validar si es el mismo empleado Y el mismo tipo
      if (liq.empleadoId !== reg.empleadoId) return false;
      if ((liq.tipo || liq.tipoPrestacion) !== reg.tipo) return false;
      
      // Para tipos sin fechas (si existen), solo comparar ID
      if (!reg.fechaInicio || !reg.fechaFin) {
        return liq.id === reg.id;
      }
      
      // Si existen fechas, verificar que no haya cruce de períodos
      if (!liq.fechaInicio || !liq.fechaFin) {
        return false; // No hay conflicto si liq no tiene fechas
      }
      
      const a1 = parseISO(liq.fechaInicio);
      const a2 = parseISO(liq.fechaFin);
      const b1 = parseISO(reg.fechaInicio);
      const b2 = parseISO(reg.fechaFin);
      
      // Verificar si hay cruce: a1 <= b2 AND b1 <= a2
      return a1 <= b2 && b1 <= a2;
    });
    
    if (yaExisteLiquidacion) {
      const emp = empleados.find(e => e.id === reg.empleadoId);
      const fechaInicio = reg.fechaInicio || reg.fecha || new Date().toISOString().slice(0, 10);
      const fechaFin = reg.fechaFin || reg.fecha || new Date().toISOString().slice(0, 10);
      
      console.log("⚠️ Ya existe liquidación para este período, omitiendo...");
      // No mostrar alert para liquidaciones automáticas de vacaciones
      if (!(reg.tipo || '').toLowerCase().includes('vacaciones')) {
        alert(
          `❌ YA EXISTE UNA LIQUIDACIÓN PARA ESTE PERÍODO\n\n` +
          `Empleado: ${emp?.nombre || 'Sin nombre'}\n` +
          `Tipo: ${reg.tipo}\n` +
          `Período: ${fechaInicio} al ${fechaFin}\n\n` +
          `No se puede liquidar dos veces el mismo período. Consulte el historial.`
        );
      }
      return;
    }

    setLiquidacionesRepo((prev) => [...prev, reg]);
    
    console.log("💾 Guardando liquidación:", {
      tipo: reg.tipo,
      empleado: reg.empleadoNombre,
      fechaInicio: reg.fechaInicio,
      fechaFin: reg.fechaFin,
      total: reg.total,
      detalle: reg.detalle,
    });

    // Guardar en Supabase
    console.log("🔄 Iniciando guardado en Supabase...");
    guardarLiquidacionEnSupabase(reg, filtroEmpresa)
      .then((exito) => {
        if (exito) {
          console.log("✅ Liquidación guardada exitosamente en Supabase:", reg.tipo, reg.empleadoNombre);
          alert(`✅ Liquidación de ${reg.tipo} guardada correctamente.`);
        } else {
          console.error("❌ Error: guardarLiquidacionEnSupabase retornó false");
          alert("Error al guardar la liquidación en Supabase. Revisa la consola.");
        }
      })
      .catch((err) => {
        console.error("❌ Error al guardar liquidación:", err);
        alert(`Error al guardar liquidación: ${err.message || err}`);
      });
  }

  // Función para eliminar liquidación del estado local
  function handleEliminarLiquidacion(liquidacionId) {
    setLiquidacionesRepo((prev) => prev.filter((l) => l.id !== liquidacionId));
    console.log("🗑️ Liquidación eliminada del estado local:", liquidacionId);
  }
 
  function marcarCuotasUsadasEnPrestamos(cuotasAplicadas = []) {
    if (!cuotasAplicadas || cuotasAplicadas.length === 0) return;
    
    console.log("🔒 Marcando cuotas como descontadas:", JSON.stringify(cuotasAplicadas, null, 2));
    
    setPrestamos((prev) =>
      prev.map((p) => {
        // Encontrar cuotas de este préstamo que fueron descontadas
        const cuotasDescontadasDeEste = cuotasAplicadas.filter(
          (c) => c.prestamoId === p.id
        );
        
        if (cuotasDescontadasDeEste.length === 0) return p;
        
        // Marcar esas cuotas como noDescontable (bloqueadas)
        const nuevoPlan = (p.plan || []).map((cuota) => {
          const fueDescontada = cuotasDescontadasDeEste.some(
            (c) => c.cuotaId === cuota.id
          );
          if (fueDescontada) {
            return { ...cuota, noDescontable: true, pagado: false };
          }
          return cuota;
        });
        
        // Calcular nuevo saldo (cuotas que no están bloqueadas)
        const saldo = nuevoPlan
          .filter((c) => !c.noDescontable)
          .reduce((acc, c) => acc + (c.valor || 0), 0);
        
        const prestamoActualizado = { ...p, plan: nuevoPlan, saldo };
        
        // Guardar en Supabase
        guardarPrestamoEnSupabase(prestamoActualizado, filtroEmpresa)
          .then(() => {
            console.log(`✅ Cuotas del préstamo ${p.id} marcadas como descontadas en Supabase`);
          })
          .catch((err) => {
            console.error(`❌ Error al guardar cuotas descontadas del préstamo ${p.id}:`, err);
          });
        
        return prestamoActualizado;
      })
    );
  }
 
  function getSemesterRange(isoDate) {
    if (!isoDate) isoDate = toISO(new Date());
    const d = new Date(isoDate);
    const y = d.getFullYear();
    const month = d.getMonth() + 1;
    if (month <= 6) return { start: `${y}-01-01`, end: `${y}-06-30`, label: `${y}-S1` };
    return { start: `${y}-07-01`, end: `${y}-12-31`, label: `${y}-S2` };
  }
 
  function isBetween(iso, start, end) {
    if (!iso) return false;
    const x = new Date(iso);
    return x >= new Date(start) && x <= new Date(end);
  }
 
  function getYearRange(isoDate) {
    if (!isoDate) isoDate = toISO(new Date());
    const d = new Date(isoDate);
    const y = d.getFullYear();
    return { start: `${y}-01-01`, end: `${y}-12-31`, label: `${y}` };
  }
 
  function liquidarEmpleados(tipo, ids, fecha, periodoVacInput) {
    const hoy = fecha || toISO(new Date());
    const nuevasLiqs = [];
 
    const empleadosActualizados = empleados.map((emp) => {
      if (!ids.includes(emp.id)) return emp;
 
      let total = 0;
      let periodo = "";
 
      if (tipo === "Prima") {
        const sem = getSemesterRange(hoy);
        const yaLiquidoEsteSem =
          emp.ultimaPrima && isBetween(emp.ultimaPrima, sem.start, sem.end);
        if (yaLiquidoEsteSem) {
          alert(
            `El empleado ${emp.nombre} ya tiene una prima liquidada en el periodo ${sem.label}.`
          );
          return emp;
        }
        total = emp.salarioProm6m;
        periodo = sem.label;
        nuevasLiqs.push({
          id: uid(),
          empleadoId: emp.id,
          tipo: "Prima",
          valor: total,
          fecha: hoy,
          periodo,
        });
 
        return { ...emp, ultimaPrima: hoy };
      }
 
      if (tipo === "Cesantias") {
        const anio = getYearRange(hoy);
        const yaLiquidoEsteAnio =
          emp.ultimasCesantias && isBetween(emp.ultimasCesantias, anio.start, anio.end);
        if (yaLiquidoEsteAnio) {
          alert(
            `El empleado ${emp.nombre} ya tiene cesantías liquidadas en el año ${anio.label}.`
          );
          return emp;
        }
        total = emp.salarioProm6m;
        periodo = anio.label;
        nuevasLiqs.push({
          id: uid(),
          empleadoId: emp.id,
          tipo: "Cesantias",
          valor: total,
          fecha: hoy,
          periodo,
        });
        const intereses = Math.round((total * 0.12 * diasEntre(anio.start, hoy)) / 360);
        nuevasLiqs.push({
          id: uid(),
          empleadoId: emp.id,
          tipo: "Intereses Cesantias",
          valor: intereses,
          fecha: hoy,
          periodo,
        });
 
        return { ...emp, ultimasCesantias: hoy };
      }
 
      if (tipo === "Vacaciones" && periodoVacInput) {
        const { periodoLabel, diasLiquidados, valorVacaciones } = periodoVacInput;
        total = valorVacaciones;
        periodo = periodoLabel;
        nuevasLiqs.push({
          id: uid(),
          empleadoId: emp.id,
          tipo: "Vacaciones",
          valor: total,
          fecha: hoy,
          periodo,
        });
        return { ...emp, ultimasVacaciones: hoy };
      }
 
      return emp;
    });
 
    if (nuevasLiqs.length) {
      setLiquidaciones((prev) => [...prev, ...nuevasLiqs]);
      nuevasLiqs.forEach((reg) => handleGuardarLiquidacion(reg));
      setEmpleados(empleadosActualizados);
    }
  }
 
  // Efecto para cargar datos desde Supabase al iniciar
  React.useEffect(() => {
    const cargarDatos = async () => {
      console.log("🚀 Iniciando carga de datos desde Supabase...");
      
      // Primero prueba la conexión
      const conexionOk = await probarConexionSupabase();
      
      if (!conexionOk) {
        console.warn("⚠️ No hay conexión con Supabase. Usando datos locales.");
        return;
      }
      
      // Determinar el filtro de empresa a usar
      // Si hay una empresa seleccionada (ya sea por super_admin o por usuario normal), usar esa
      // empresaSeleccionada puede ser un UUID o cadena vacía "" (todas las empresas para super_admin)
      let filtroEmpresa = null;
      
      if (esSuperAdminVerificado) {
        // Super admin: usa la empresa seleccionada, o null para ver todas
        filtroEmpresa = empresaSeleccionada && empresaSeleccionada !== '' ? empresaSeleccionada : null;
        console.log("🏢 Super Admin - Filtro de empresa:", filtroEmpresa || "TODAS LAS EMPRESAS");
      } else {
        // Usuario normal: siempre filtra por su empresa asignada
        filtroEmpresa = usuario?.empresaId || empresaSeleccionada || null;
        console.log("🏢 Usuario normal - Filtro de empresa:", filtroEmpresa || "Usando RLS");
      }
      
      try {
        // Cargar empleados
        const empleadosDB = await cargarEmpleadosDeSupabase(filtroEmpresa);
        if (empleadosDB.length > 0) {
          setEmpleados(empleadosDB);
          console.log("✅ Empleados cargados:", empleadosDB.length);
          
          // Limpiar datos históricos con JSON en sede
          await limpiarSedesEnHistoricos();
        } else {
          console.log("ℹ️ No hay empleados en Supabase");
          setEmpleados([]);
        }
        
        // Cargar empleados con perfil Gerencia de TODAS las empresas (para asignar responsables)
        try {
          const { data: gerentesData, error: gerentesError } = await supabase
            .from('empleados')
            .select('*')
            .or('perfil.ilike.%gerencia%,perfil.ilike.%gerente%,perfil.ilike.%admin%');
          
          if (!gerentesError && gerentesData) {
            // Mapear los datos para que tengan el campo 'nombre' completo
            const gerentesMapeados = gerentesData.map(emp => {
              const nombres = emp.nombres || "";
              const apellidos = emp.apellidos || "";
              const nombreCompleto = `${nombres} ${apellidos}`.trim();
              return {
                ...emp,
                id: emp.id,
                nombre: nombreCompleto || emp.nombre || "(Sin nombre)",
                perfil: emp.perfil || "",
                empresa_id: emp.empresa_id || null
              };
            });
            setEmpleadosGerenciaTodas(gerentesMapeados);
            console.log("✅ Gerentes de todas las empresas cargados:", gerentesMapeados.length, gerentesMapeados.map(g => g.nombre));
          }
        } catch (e) {
          console.error("Error cargando gerentes:", e);
        }

        // Cargar sedes
        const sedesDB = await cargarSedesDeSupabase(filtroEmpresa);
        if (sedesDB.length > 0) {
          setSedes(sedesDB);
          console.log("✅ Sedes cargadas:", sedesDB.length);
        } else {
          setSedes([]);
        }

        // Cargar ausencias
        const ausenciasDB = await cargarAusenciasDeSupabase(filtroEmpresa);
        if (ausenciasDB.length > 0) {
          setAusencias(ausenciasDB);
          console.log("✅ Ausencias cargadas:", ausenciasDB.length);
        } else {
          setAusencias([]);
        }

        // Cargar vacaciones
        const vacacionesDB = await cargarVacacionesDeSupabase(filtroEmpresa);
        if (vacacionesDB.length > 0) {
          setVacaciones(vacacionesDB);
          console.log("✅ Vacaciones cargadas:", vacacionesDB.length);
        } else {
          setVacaciones([]);
        }

        // Cargar préstamos
        const prestamosDB = await cargarPrestamosDeSupabase(filtroEmpresa);
        if (prestamosDB.length > 0) {
          setPrestamos(prestamosDB);
          console.log("✅ Préstamos cargados:", prestamosDB.length);
        } else {
          setPrestamos([]);
        }

        // Cargar memorandos
        const memorandosDB = await cargarMemorandosDeSupabase(filtroEmpresa);
        if (memorandosDB.length > 0) {
          setMemorandos(memorandosDB);
          console.log("✅ Memorandos cargados:", memorandosDB.length);
        } else {
          setMemorandos([]);
        }

        // Cargar nóminas
        const nominasDB = await cargarNominasDeSupabase(filtroEmpresa);
        if (nominasDB.length > 0) {
          // Enriquecer nóminas con datos de empleados
          const nominasEnriquecidas = nominasDB.map((nomina) => ({
            ...nomina,
            items: nomina.items.map((item) => {
              const emp = empleados.find((e) => e.id === item.empleadoId);
              return {
                ...item,
                empleadoNombre: emp?.nombre || item.empleadoNombre || "",
                documento: emp?.documento || item.documento || "",
              };
            }),
          }));
          
          setNominasRepo(nominasEnriquecidas);
          console.log("✅ Nóminas cargadas:", nominasEnriquecidas.length);
        } else {
          setNominasRepo([]);
        }

        // Cargar liquidaciones
        const liquidacionesDB = await cargarLiquidacionesDeSupabase(filtroEmpresa);
        if (liquidacionesDB.length > 0) {
          setLiquidacionesRepo(liquidacionesDB);
          console.log("✅ Liquidaciones cargadas:", liquidacionesDB.length);
        } else {
          setLiquidacionesRepo([]);
        }
      } catch (error) {
        console.error("❌ Error cargando datos desde Supabase:", error);
      }
    };

    cargarDatos();
  }, [empresaSeleccionada, esSuperAdminVerificado]); // Recargar cuando cambie la empresa seleccionada

  // Efecto para sincronizar usuarios desde localStorage (cambios en Configuraciones)
  React.useEffect(() => {
    const interval = setInterval(() => {
      try {
        const raw = window.localStorage.getItem("usuarios_app_v1");
        const nuevosUsuarios = raw ? JSON.parse(raw) : [];
        // Solo actualiza si hay diferencia real en el contenido
        setUsuarios((prevUsuarios) => {
          const sonIguales = JSON.stringify(prevUsuarios) === JSON.stringify(nuevosUsuarios);
          return sonIguales ? prevUsuarios : nuevosUsuarios;
        });
      } catch (e) {
        console.error("Error sincronizando usuarios desde localStorage", e);
      }
    }, 500); // Cada 500ms revisa cambios
    
    return () => clearInterval(interval);
  }, []);
 
  const empleadosFiltrados = useMemo(() => {
    const texto = busqueda.toLowerCase().trim();
    // Ordenar empleados alfabéticamente por nombre
    const ordenarPorNombre = (lista) => [...lista].sort((a, b) => 
      (a.nombre || "").localeCompare(b.nombre || "", 'es', { sensitivity: 'base' })
    );
    const activos = ordenarPorNombre(empleados.filter((e) => e.activo));
    const inactivos = ordenarPorNombre(empleados.filter((e) => !e.activo));
 
    const filtrar = (lista) =>
      lista.filter(
        (e) =>
          e.nombre.toLowerCase().includes(texto) ||
          e.documento.toLowerCase().includes(texto)
      );
 
    const activosFiltrados = texto ? filtrar(activos) : activos;
    const inactivosFiltrados = texto ? filtrar(inactivos) : inactivos;
    return { activosFiltrados, inactivosFiltrados };
  }, [empleados, busqueda]);
 
  const { activosFiltrados, inactivosFiltrados } = empleadosFiltrados;

  // Ref para mantener el foco del input de búsqueda
  const busquedaInputRef = React.useRef(null);

  // === MODAL ACTUALIZACIÓN DE DATOS ===
  function ModalActualizacionDatos({ onClose }) {
    const [solicitudes, setSolicitudes] = React.useState([]);
    const [cargando, setCargando] = React.useState(true);
    const [solicitudSeleccionada, setSolicitudSeleccionada] = React.useState(null);
    const [procesando, setProcesando] = React.useState(false);

    // Cargar solicitudes pendientes
    React.useEffect(() => {
      cargarSolicitudes();
    }, []);

    const cargarSolicitudes = async () => {
      setCargando(true);
      try {
        const { data, error } = await supabase
          .from('solicitudes_actualizacion_datos')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        setSolicitudes(data || []);
      } catch (error) {
        console.error('Error cargando solicitudes:', error);
      } finally {
        setCargando(false);
      }
    };

    // Obtener empleado relacionado
    const getEmpleadoInfo = (documento) => {
      const emp = empleados.find(e => e.documento === documento);
      return emp ? `${emp.nombre} (${emp.documento})` : documento;
    };

    // Aprobar solicitud
    const aprobarSolicitud = async (solicitud) => {
      if (!tienePermiso("rh_actualizacion_datos_aprobar")) {
        alert("No tienes permiso para aprobar solicitudes");
        return;
      }
      
      if (!window.confirm(`¿Está seguro de aprobar la actualización de datos para ${getEmpleadoInfo(solicitud.documento_empleado)}?`)) {
        return;
      }

      setProcesando(true);
      try {
        // Parsear los cambios solicitados (puede venir como string o como objeto)
        const cambios = typeof solicitud.datos_nuevos === 'string' 
          ? JSON.parse(solicitud.datos_nuevos) 
          : solicitud.datos_nuevos || {};
        
        console.log('Cambios a aplicar:', cambios);
        console.log('Documento empleado:', solicitud.documento_empleado);
        
        // Actualizar el empleado con los nuevos datos
        const { data: dataEmpleado, error: errorEmpleado } = await supabase
          .from('empleados')
          .update(cambios)
          .eq('documento', solicitud.documento_empleado)
          .select();
        
        console.log('Respuesta update empleado:', { data: dataEmpleado, error: errorEmpleado });
        
        if (errorEmpleado) {
          console.error('Error detallado:', errorEmpleado);
          throw errorEmpleado;
        }

        // Marcar la solicitud como aprobada
        const { error: errorSolicitud } = await supabase
          .from('solicitudes_actualizacion_datos')
          .update({ 
            estado: 'aprobada',
            fecha_respuesta: new Date().toISOString(),
            aprobado_por: usuario?.id || 'admin'
          })
          .eq('id', solicitud.id);
        
        if (errorSolicitud) throw errorSolicitud;

        alert('Solicitud aprobada y datos actualizados correctamente');
        setSolicitudSeleccionada(null);
        cargarSolicitudes();
      } catch (error) {
        console.error('Error aprobando solicitud:', error);
        console.error('Mensaje:', error.message);
        console.error('Detalles:', error.details);
        console.error('Hint:', error.hint);
        alert('Error al aprobar la solicitud: ' + (error.message || JSON.stringify(error)));
      } finally {
        setProcesando(false);
      }
    };

    // Rechazar solicitud
    const rechazarSolicitud = async (solicitud) => {
      if (!tienePermiso("rh_actualizacion_datos_aprobar")) {
        alert("No tienes permiso para rechazar solicitudes");
        return;
      }

      const motivo = window.prompt('Ingrese el motivo del rechazo:');
      if (!motivo) return;

      setProcesando(true);
      try {
        const { error } = await supabase
          .from('solicitudes_actualizacion_datos')
          .update({ 
            estado: 'rechazada',
            fecha_respuesta: new Date().toISOString(),
            aprobado_por: usuario?.id || 'admin',
            motivo_rechazo: motivo
          })
          .eq('id', solicitud.id);
        
        if (error) throw error;

        alert('Solicitud rechazada');
        setSolicitudSeleccionada(null);
        cargarSolicitudes();
      } catch (error) {
        console.error('Error rechazando solicitud:', error);
        alert('Error al rechazar la solicitud: ' + error.message);
      } finally {
        setProcesando(false);
      }
    };

    // Renderizar estado con color
    const renderEstado = (estado) => {
      const colores = {
        pendiente: { bg: '#FEF3C7', color: '#92400E', texto: 'Pendiente' },
        aprobada: { bg: '#D1FAE5', color: '#065F46', texto: 'Aprobada' },
        rechazada: { bg: '#FEE2E2', color: '#991B1B', texto: 'Rechazada' }
      };
      const config = colores[estado] || colores.pendiente;
      return (
        <span style={{
          padding: '4px 8px',
          borderRadius: 4,
          fontSize: 12,
          fontWeight: 600,
          backgroundColor: config.bg,
          color: config.color
        }}>
          {config.texto}
        </span>
      );
    };

    // Función para parsear datos (puede venir como string o como objeto)
    const parsearDatos = (datos) => {
      if (!datos) return {};
      if (typeof datos === 'string') {
        try {
          return JSON.parse(datos);
        } catch (e) {
          return {};
        }
      }
      return datos; // Ya es un objeto
    };

    // Vista detalle de solicitud
    if (solicitudSeleccionada) {
      const cambios = parsearDatos(solicitudSeleccionada.datos_nuevos);
      const datosAnteriores = parsearDatos(solicitudSeleccionada.datos_originales);
      
      return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '90%',
            maxWidth: '900px',
            maxHeight: '85vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <button
              onClick={() => setSolicitudSeleccionada(null)}
              style={{
                marginBottom: 16,
                padding: '8px 16px',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
                background: '#e0e0e0'
              }}
            >
              ← Volver a la lista
            </button>
          
            <div style={{
              background: 'white',
              borderRadius: 12,
              padding: 24,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 20 }}>
                Solicitud de {getEmpleadoInfo(solicitudSeleccionada.documento_empleado)}
              </h2>
              {renderEstado(solicitudSeleccionada.estado)}
            </div>

            <p style={{ color: '#666', marginBottom: 20 }}>
              Fecha solicitud: {new Date(solicitudSeleccionada.created_at).toLocaleString()}
            </p>

            <h3 style={{ marginTop: 24, marginBottom: 16, fontSize: 16 }}>Cambios Solicitados:</h3>
            
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Campo</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Valor Anterior</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Valor Nuevo</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(cambios).map(campo => (
                  <tr key={campo}>
                    <td style={{ padding: 12, borderBottom: '1px solid #eee', fontWeight: 600 }}>
                      {campo}
                    </td>
                    <td style={{ padding: 12, borderBottom: '1px solid #eee', color: '#999' }}>
                      {datosAnteriores[campo] || '-'}
                    </td>
                    <td style={{ padding: 12, borderBottom: '1px solid #eee', color: '#2563eb', fontWeight: 500 }}>
                      {cambios[campo] || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {solicitudSeleccionada.estado === 'pendiente' && tienePermiso("rh_actualizacion_datos_aprobar") && (
              <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                <button
                  onClick={() => aprobarSolicitud(solicitudSeleccionada)}
                  disabled={procesando}
                  style={{
                    padding: '12px 24px',
                    background: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: procesando ? 'not-allowed' : 'pointer',
                    opacity: procesando ? 0.6 : 1
                  }}
                >
                  ✓ Aprobar Cambios
                </button>
                <button
                  onClick={() => rechazarSolicitud(solicitudSeleccionada)}
                  disabled={procesando}
                  style={{
                    padding: '12px 24px',
                    background: '#EF4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    fontWeight: 600,
                    cursor: procesando ? 'not-allowed' : 'pointer',
                    opacity: procesando ? 0.6 : 1
                  }}
                >
                  ✗ Rechazar
                </button>
              </div>
            )}

            {solicitudSeleccionada.estado === 'rechazada' && solicitudSeleccionada.motivo_rechazo && (
              <div style={{
                marginTop: 24,
                padding: 16,
                background: '#FEE2E2',
                borderRadius: 8,
                color: '#991B1B'
              }}>
                <strong>Motivo del rechazo:</strong> {solicitudSeleccionada.motivo_rechazo}
              </div>
            )}
          </div>
          </div>
        </div>
      );
    }

    // Vista lista
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          width: '90%',
          maxWidth: '1100px',
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '2px solid #e0e0e0'
        }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#7c3aed' }}>
            📝 Solicitudes de Actualización de Datos
          </h2>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={cargarSolicitudes}
              style={{
                padding: '8px 16px',
                background: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer'
              }}
            >
              🔄 Actualizar
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                background: '#9e9e9e',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer'
              }}
            >
              ✕ Cerrar
            </button>
          </div>
        </div>

        {cargando ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            Cargando solicitudes...
          </div>
        ) : solicitudes.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: 60,
            background: 'white',
            borderRadius: 12
          }}>
            <p style={{ fontSize: 48, margin: 0 }}>📭</p>
            <p style={{ color: '#666', marginTop: 16 }}>No hay solicitudes de actualización de datos</p>
          </div>
        ) : (
          <div style={{
            background: 'white',
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: 16, textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Empleado</th>
                  <th style={{ padding: 16, textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Fecha Solicitud</th>
                  <th style={{ padding: 16, textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Estado</th>
                  <th style={{ padding: 16, textAlign: 'center', borderBottom: '1px solid #dee2e6' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {solicitudes.map(solicitud => (
                  <tr key={solicitud.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 16 }}>
                      <strong>{getEmpleadoInfo(solicitud.documento_empleado)}</strong>
                    </td>
                    <td style={{ padding: 16, color: '#666' }}>
                      {new Date(solicitud.created_at).toLocaleString()}
                    </td>
                    <td style={{ padding: 16, textAlign: 'center' }}>
                      {renderEstado(solicitud.estado)}
                    </td>
                    <td style={{ padding: 16, textAlign: 'center' }}>
                      <button
                        onClick={() => setSolicitudSeleccionada(solicitud)}
                        style={{
                          padding: '6px 12px',
                          background: '#3B82F6',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 13
                        }}
                      >
                        Ver Detalles
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>
    );
  }
  // === FIN MODAL ACTUALIZACIÓN DE DATOS ===
 
  // === TAB EMPLEADOS (YA LO TENÍAS) ===
  function EmpleadosTab() {
    // Estado para la vista: 'lista' o 'detalle'
    const [vistaEmpleado, setVistaEmpleado] = React.useState('lista');
    const [empleadoSeleccionado, setEmpleadoSeleccionado] = React.useState(null);
    const [modoEdicion, setModoEdicion] = React.useState(false);
    const [mostrarCargaMasiva, setMostrarCargaMasiva] = React.useState(false);
    const [mostrarActualizacionDatos, setMostrarActualizacionDatos] = React.useState(false);
    const [mostrarCumpleanos, setMostrarCumpleanos] = React.useState(false);
    const [mostrarAsistencia, setMostrarAsistencia] = React.useState(false);
    const [mostrarModalLiquidar, setMostrarModalLiquidar] = React.useState(false);
    const [vistaAsistencia, setVistaAsistencia] = React.useState('crear'); // 'crear' o 'repositorio'
    const [planillasAsistencia, setPlanillasAsistencia] = React.useState([]);
    const [documentosFirmados, setDocumentosFirmados] = React.useState([]);
    const [formAsistencia, setFormAsistencia] = React.useState({
      tipoEvento: 'CAPACITACIÓN', // 'REUNIÓN' o 'CAPACITACIÓN'
      nombreReunion: '',
      descripcion: '',
      fecha: new Date().toISOString().split('T')[0],
      empleadosSeleccionados: [],
      tipoResponsable: 'empleado', // 'empleado' o 'externo'
      responsableEmpleadoId: '',
      responsableExterno: {
        nombre: '',
        tipoDoc: 'Cédula de ciudadanía',
        documento: ''
      }
    });
    const [cargandoAsistencia, setCargandoAsistencia] = React.useState(false);
    const [subiendoFirmado, setSubiendoFirmado] = React.useState(false);
    const [cargandoMasivo, setCargandoMasivo] = React.useState(false);
    const [resultadoCarga, setResultadoCarga] = React.useState(null);
    const [empleadosPreview, setEmpleadosPreview] = React.useState([]);
    const archivoInputRef = React.useRef(null);

    // Función para descargar plantilla Excel
    const descargarPlantillaExcel = () => {
      // Crear cabeceras de la plantilla - EXACTAMENTE los campos del formulario EmpleadoForm
      const cabeceras = [
        // Datos personales
        "tipoDoc", "documento", "nombres", "apellidos", "fechaNacimiento", "fechaExpedicion", "rh", "nacionalidad", "genero",
        // Ubicación
        "telefono", "direccion",
        // Datos laborales
        "fechaIngreso", "tipoContrato", "sede", "cargo",
        // Seguridad social
        "eps", "arl", "fondo",
        // Datos bancarios
        "banco", "tipoCuenta", "numeroCuenta",
        // Información salarial
        "salarioBase", "salarioProm6m", "valorBonificacion"
      ];
      
      // Ejemplo de datos
      const ejemploFila = [
        // Datos personales
        "Cédula de ciudadanía", "1234567890", "Juan Carlos", "Pérez García", "1990-05-15", "2008-06-20", "O+", "Colombiana", "Masculino",
        // Ubicación
        "3001234567", "Calle 123 #45-67 Bogotá",
        // Datos laborales
        "2024-01-15", "Indefinido", "Sede Principal", "Cajero",
        // Seguridad social
        "Sura", "Sura", "Porvenir",
        // Datos bancarios
        "Bancolombia", "Ahorros", "123456789",
        // Información salarial
        "1423500", "1500000", "100000"
      ];

      // Crear contenido CSV con BOM para que Excel reconozca UTF-8
      const BOM = "\uFEFF";
      const contenido = BOM + cabeceras.join(";") + "\n" + ejemploFila.join(";");
      
      // Crear blob y descargar
      const blob = new Blob([contenido], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "plantilla_empleados.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    // Función para procesar archivo CSV/Excel
    const procesarArchivo = (e) => {
      const archivo = e.target.files[0];
      if (!archivo) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const texto = event.target.result;
          const lineas = texto.split(/\r?\n/).filter(l => l.trim());
          
          if (lineas.length < 2) {
            alert("El archivo debe tener al menos una línea de cabeceras y una de datos");
            return;
          }

          // Detectar separador (coma o punto y coma)
          const separador = lineas[0].includes(";") ? ";" : ",";
          const cabeceras = lineas[0].split(separador).map(h => h.trim().toLowerCase().replace(/"/g, ""));
          
          const empleadosNuevos = [];
          const errores = [];

          for (let i = 1; i < lineas.length; i++) {
            const valores = lineas[i].split(separador).map(v => v.trim().replace(/"/g, ""));
            if (valores.length < 3 || !valores[0]) continue; // Saltar líneas vacías

            const emp = {};
            cabeceras.forEach((cab, idx) => {
              emp[cab] = valores[idx] || "";
            });

            // Validar campos obligatorios (nombres o apellidos y documento)
            const tieneNombres = emp.nombres || emp.nombre;
            if (!tieneNombres || !emp.documento) {
              errores.push("Línea " + (i + 1) + ": Faltan nombres o documento");
              continue;
            }

            // Verificar si ya existe un empleado con ese documento
            const existe = empleados.some(e => e.documento === emp.documento);
            if (existe) {
              errores.push("Línea " + (i + 1) + ": Ya existe empleado con documento " + emp.documento);
              continue;
            }

            // Construir nombre completo
            const nombreCompleto = emp.nombre || ((emp.nombres || "") + " " + (emp.apellidos || "")).trim();

            // Construir objeto empleado con estructura correcta - EXACTAMENTE como EmpleadoForm
            empleadosNuevos.push({
              id: uid(),
              activo: true,
              // Datos personales
              nombre: nombreCompleto,
              nombres: emp.nombres || "",
              apellidos: emp.apellidos || "",
              tipoDoc: emp.tipodoc || "Cédula de ciudadanía",
              documento: emp.documento || "",
              fechaNacimiento: emp.fechanacimiento || "",
              fechaExpedicion: emp.fechaexpedicion || "",
              rh: emp.rh || "",
              nacionalidad: emp.nacionalidad || "Colombiana",
              genero: emp.genero || "Masculino",
              // Ubicación
              telefono: emp.telefono || "",
              direccion: emp.direccion || "",
              // Datos laborales
              fechaIngreso: emp.fechaingreso || new Date().toISOString().split("T")[0],
              tipoContrato: emp.tipocontrato || "Indefinido",
              sede: emp.sede || (sedes[0]?.nombre || ""),
              cargo: emp.cargo || "",
              fechaRetiro: "",
              // Seguridad social
              eps: emp.eps || "",
              arl: emp.arl || "",
              fondo: emp.fondo || "",
              // Datos bancarios
              banco: emp.banco || "",
              tipoCuenta: emp.tipocuenta || "Ahorros",
              numeroCuenta: emp.numerocuenta || "",
              // Información salarial
              salarioBase: parseFloat(emp.salariobase) || valoresAnuales?.smlv || 1423500,
              salarioProm6m: parseFloat(emp.salarioprom6m) || 0,
              valorBonificacion: parseFloat(emp.valorbonificacion) || 0,
              // Campos adicionales con valores por defecto
              hijos: [],
              tieneHijos: false,
              docs: [],
              smlv: valoresAnuales?.smlv || 1423500,
              ultimaPrima: "",
              ultimasCesantias: "",
              ultimasVacaciones: "",
              ultimaLiquidacionContrato: "",
              perfil: ""
            });
          }

          setEmpleadosPreview(empleadosNuevos);
          setResultadoCarga({
            total: lineas.length - 1,
            validos: empleadosNuevos.length,
            errores: errores
          });

        } catch (err) {
          console.error("Error procesando archivo:", err);
          alert("Error al procesar el archivo. Asegúrese de que sea un CSV válido.");
        }
      };

      reader.readAsText(archivo, "UTF-8");
    };

    // Función para guardar empleados en bloque
    const guardarEmpleadosMasivo = async () => {
      if (empleadosPreview.length === 0) {
        alert("No hay empleados para guardar");
        return;
      }

      setCargandoMasivo(true);
      let guardados = 0;
      let fallidos = 0;

      for (const emp of empleadosPreview) {
        try {
          // Agregar empresa_id
          const empConEmpresa = { ...emp, empresa_id: filtroEmpresa };
          await guardarEmpleado(empConEmpresa);
          guardados++;
        } catch (err) {
          console.error("Error guardando empleado:", emp.nombre, err);
          fallidos++;
        }
      }

      setCargandoMasivo(false);
      alert("Carga completada:\n✅ Guardados: " + guardados + "\n❌ Fallidos: " + fallidos);
      
      // Limpiar y cerrar
      setEmpleadosPreview([]);
      setResultadoCarga(null);
      setMostrarCargaMasiva(false);
      if (archivoInputRef.current) {
        archivoInputRef.current.value = "";
      }
    };
    
    // Función para ver un empleado (abre página de detalle)
    const verEmpleado = (emp) => {
      setEmpleadoSeleccionado(emp);
      setModoEdicion(false);
      setVistaEmpleado('detalle');
    };
    
    // Función para editar un empleado (abre página de detalle en modo edición)
    const editarEmpleado = (emp) => {
      setEmpleadoSeleccionado(emp);
      setModoEdicion(true);
      setVistaEmpleado('detalle');
    };
    
    // Función para volver a la lista
    const volverALista = () => {
      setVistaEmpleado('lista');
      setEmpleadoSeleccionado(null);
      setModoEdicion(false);
    };

    // ========== VISTA DE DETALLE DEL EMPLEADO ==========
    if (vistaEmpleado === 'detalle' && empleadoSeleccionado) {
      return (
        <div className="p-6" style={{ 
          background: 'linear-gradient(135deg, #fef9f3 0%, #fff5eb 100%)', 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          {/* Contenedor centrado con ancho máximo */}
          <div style={{ width: '100%', maxWidth: '750px' }}>
            {/* Header con botón volver */}
            <div style={{
              background: modoEdicion 
                ? 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)'
                : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  onClick={volverALista}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderRadius: '8px',
                    padding: '8px 14px',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                >
                  ← Volver
                </button>
                <div>
                  <h2 style={{
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    margin: 0,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
                  }}>
                    {modoEdicion ? '✏️ Editar Empleado' : '👤 Información del Empleado'}
                  </h2>
                  <p style={{ color: 'rgba(255,255,255,0.9)', margin: '2px 0 0 0', fontSize: '13px' }}>
                    {empleadoSeleccionado.nombre || 'Sin nombre'}
                  </p>
                </div>
              </div>
            
              {/* Botón para cambiar entre Ver y Editar */}
              {!modoEdicion && (
                <button
                  onClick={() => setModoEdicion(true)}
                  style={{
                    background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.4)',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  ✏️ Editar
                </button>
              )}
              {modoEdicion && (
                <button
                  onClick={() => setModoEdicion(false)}
                  style={{
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: '600',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  👁 Solo Ver
                </button>
              )}
            </div>

            {/* Contenido: Vista o Formulario de Edición */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              padding: '20px',
              border: modoEdicion ? '2px solid #ff9800' : '2px solid #1976d2'
            }}>
              {modoEdicion ? (
                <EmpleadoForm
                  sedes={sedes}
                  empleado={empleadoSeleccionado}
                  usuarios={usuarios}
                  valoresAnuales={valoresAnuales}
                  onCancel={() => setModoEdicion(false)}
                  onSave={(emp) => {
                    guardarEmpleado(emp);
                    setEmpleadoSeleccionado(emp);
                    setModoEdicion(false);
                  }}
                />
              ) : (
                <EmpleadoView
                  empleado={empleadoSeleccionado}
                  sedes={sedes}
                  usuarios={usuarios}
                  onClose={volverALista}
                  ausenciasAll={ausencias}
                  vacacionesAll={vacaciones}
                  memorandosAll={memorandos}
                />
              )}
            </div>
          </div>
        </div>
      );
    }

    // ========== VISTA DE LISTA DE EMPLEADOS ==========
    return (
      <div className="p-6" style={{ background: 'linear-gradient(135deg, #fef9f3 0%, #fff5eb 100%)', minHeight: '100vh' }}>
        {/* Header de la sección */}
        <div style={{
          background: 'linear-gradient(135deg, #d32f2f 0%, #ff5722 50%, #ff9800 100%)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 8px 32px rgba(211, 47, 47, 0.3)'
        }}>
          <h2 style={{
            color: 'white',
            fontSize: '24px',
            fontWeight: 'bold',
            margin: '0 0 8px 0',
            textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
          }}>
            👥 Gestión de Empleados
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.9)', margin: 0, fontSize: '14px' }}>
            Administra la información del equipo BigBurguer
          </p>
        </div>

        {/* Barra de búsqueda y acciones */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          gap: '16px'
        }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#424242', fontSize: '13px' }}>
              🔍 Buscar empleado
            </label>
            <input
              ref={busquedaInputRef}
              type="text"
              placeholder="Nombre o documento..."
              value={busqueda}
              onChange={(e) => {
                setBusqueda(e.target.value);
                // Mantener el foco después de la actualización
                setTimeout(() => {
                  if (busquedaInputRef.current) {
                    busquedaInputRef.current.focus();
                  }
                }, 0);
              }}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #e0e0e0',
                fontSize: '14px',
                transition: 'border-color 0.2s',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#ff5722'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>
          
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 16px',
            background: '#f5f5f5',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            color: '#616161',
            fontWeight: '500'
          }}>
            <input
              type="checkbox"
              checked={verInactivos}
              onChange={(e) => setVerInactivos(e.target.checked)}
              style={{ width: '18px', height: '18px', accentColor: '#ff5722' }}
            />
            Ver inactivos
          </label>
          
          <button
            onClick={() =>
              setEditEmpleado({
                id: uid(),
                activo: true,
                nombre: "",
                nombres: "",
                apellidos: "",
                tipoDoc: "CC",
                documento: "",
                fechaExpedicion: "",
                fechaIngreso: toISO(new Date()),
                fechaNacimiento: "",
                cargo: "",
                sede: (sedes && sedes.length && (typeof sedes[0] === 'string' ? sedes[0] : sedes[0]?.nombre)) || "",
                tipoContrato: "Indefinido",
                eps: "",
                arl: "",
                fondo: "",
                nacionalidad: "",
                banco: "",
                tipoCuenta: "",
                numeroCuenta: "",
                hijos: [],
                tieneHijos: false,
                fechaRetiro: "",
                docs: [],
                salarioBase: 0,
                salarioProm6m: 0,
                smlv: 1423500,
                ultimaPrima: "",
                ultimasCesantias: "",
                ultimasVacaciones: "",
                ultimaLiquidacionContrato: "",
                valorBonificacion: 0,
                afiliadoSeguridadSocial: true,
              })
            }
            style={{
              background: 'linear-gradient(135deg, #d32f2f 0%, #ff5722 100%)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(211, 47, 47, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(211, 47, 47, 0.3)';
            }}
          >
            ➕ Nuevo empleado
          </button>

          {/* Botón Carga Masiva */}
          <button
            onClick={() => setMostrarCargaMasiva(true)}
            style={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(25, 118, 210, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(25, 118, 210, 0.3)';
            }}
          >
            📥 Carga Masiva
          </button>

          {/* Botón Actualización de Datos */}
          {tienePermiso("rh_actualizacion_datos") && (
          <button
            onClick={() => setMostrarActualizacionDatos(true)}
            style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(124, 58, 237, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.3)';
            }}
          >
            📝 Actualización Datos
          </button>
          )}

          {/* Botón Cumpleaños */}
          <button
            onClick={() => setMostrarCumpleanos(true)}
            style={{
              background: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(236, 72, 153, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(236, 72, 153, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(236, 72, 153, 0.3)';
            }}
          >
            🎂 Cumpleaños
          </button>

          {/* Botón Asistencia Empleados */}
          <button
            onClick={async () => {
              setMostrarAsistencia(true);
              setVistaAsistencia('crear');
              setCargandoAsistencia(true);
              try {
                // Cargar planillas existentes
                const { data: planillas } = await supabase
                  .from('asistencia_reuniones')
                  .select('*')
                  .eq('empresa_id', empresaSeleccionada)
                  .order('fecha', { ascending: false });
                setPlanillasAsistencia(planillas || []);
                
                // Cargar documentos firmados
                const { data: firmados } = await supabase
                  .from('asistencia_reuniones_firmadas')
                  .select('*')
                  .eq('empresa_id', empresaSeleccionada)
                  .order('created_at', { ascending: false });
                setDocumentosFirmados(firmados || []);
              } catch (err) {
                console.error('Error cargando asistencia:', err);
              }
              setCargandoAsistencia(false);
            }}
            style={{
              background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(8, 145, 178, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(8, 145, 178, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(8, 145, 178, 0.3)';
            }}
          >
            📋 Asistencia Empleados
          </button>

          {/* Botón Liquidar Salario Promedio */}
          <button
            onClick={() => setMostrarModalLiquidar(true)}
            style={{
              background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(22, 163, 74, 0.3)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(22, 163, 74, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(22, 163, 74, 0.3)';
            }}
          >
            💰 Liquidar Salario Promedio
          </button>
        </div>

        {/* Modal Liquidar Salario Promedio */}
        {mostrarModalLiquidar && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              width: '95%',
              maxWidth: '900px',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: '32px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1e293b' }}>
                  💰 Liquidación Masiva de Salarios Promedio
                </h2>
                <button
                  onClick={() => setMostrarModalLiquidar(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '28px',
                    cursor: 'pointer',
                    color: '#64748b'
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{
                padding: '16px',
                background: '#dbeafe',
                borderRadius: '8px',
                border: '2px solid #0ea5e9',
                color: '#0c4a6e',
                fontSize: '13px',
                marginBottom: '24px',
                lineHeight: '1.6'
              }}>
                <strong>📊 Resumen:</strong><br/>
                • Se procesarán <strong>{empleados.filter(e => e.activo !== false).length} empleados activos</strong><br/>
                • Período: {new Date().getMonth() < 6 ? 'Enero - Junio' : 'Julio - Diciembre'} {new Date().getFullYear()}<br/>
                • Se calculará el promedio de los últimos 6 meses para cada empleado<br/>
                • Los datos se extraerán directamente del repositorio de nómina
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '24px'
              }}>
                <button
                  onClick={async () => {
                    const empleadosActivos = empleados.filter(e => e.activo !== false);
                    
                    if (empleadosActivos.length === 0) {
                      alert('No hay empleados activos para procesar');
                      return;
                    }

                    if (!window.confirm(`¿Estás seguro de que deseas liquidar el promedio de salario para ${empleadosActivos.length} empleados?\n\nEsta acción actualizará la base de datos.`)) {
                      return;
                    }

                    try {
                      console.log(`🔄 Iniciando liquidación para ${empleadosActivos.length} empleados...`);
                      const resultado = await liquidarPromedioTodosEmpleados(empresaSeleccionada, empleadosActivos);
                      
                      // Recargar empleados
                      const { data: empleadosActualizados } = await supabase
                        .from('empleados')
                        .select('*')
                        .eq('empresa_id', empresaSeleccionada);
                      
                      if (empleadosActualizados) {
                        setEmpleados(empleadosActualizados);
                      }

                      // Mostrar resumen
                      let mensaje = `✅ LIQUIDACIÓN COMPLETADA\n\n`;
                      mensaje += `📋 Resumen General:\n`;
                      mensaje += `• Total procesados: ${resultado.resumen.procesados}\n`;
                      mensaje += `• Exitosos: ${resultado.resumen.exitosos}\n`;
                      mensaje += `• Fallidos: ${resultado.resumen.fallidos}\n\n`;
                      
                      if (resultado.exitosos.length > 0) {
                        mensaje += `✅ Empleados procesados exitosamente (VALORES MENSUALES):\n`;
                        resultado.exitosos.slice(0, 10).forEach(emp => {
                          let nota = emp.aplicoMinimo ? ' ⚠️ (Aplicó mínimo legal)' : '';
                          mensaje += `  • ${emp.empleado}: $${emp.promedio.toLocaleString()}/mes${nota}\n`;
                          mensaje += `    └─ (Promedio quincena: $${emp.promedioQuincenal.toLocaleString()} × 2)\n`;
                        });
                        if (resultado.exitosos.length > 10) {
                          mensaje += `  ... y ${resultado.exitosos.length - 10} más\n`;
                        }
                      }
                      
                      if (resultado.errores.length > 0) {
                        mensaje += `\n❌ Errores encontrados:\n`;
                        resultado.errores.slice(0, 3).forEach(err => {
                          mensaje += `  • ${err.empleado || 'General'}: ${err.error}\n`;
                        });
                      }

                      alert(mensaje);
                      setMostrarModalLiquidar(false);
                    } catch (e) {
                      console.error('Error:', e);
                      alert('Error en la liquidación: ' + e.message);
                    }
                  }}
                  style={{
                    padding: '14px 28px',
                    background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    fontSize: '15px',
                    flex: 1
                  }}
                >
                  ⚡ LIQUIDAR TODOS LOS EMPLEADOS
                </button>

                <button
                  onClick={() => setMostrarModalLiquidar(false)}
                  style={{
                    padding: '14px 28px',
                    background: '#e2e8f0',
                    color: '#334155',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  ✕ Cancelar
                </button>
              </div>

              <div style={{
                padding: '16px',
                background: '#fef3c7',
                borderRadius: '8px',
                border: '2px solid #fcd34d',
                color: '#92400e',
                fontSize: '13px',
                lineHeight: '1.6'
              }}>
                <strong>ℹ️ Cálculo MENSUAL de los Últimos 6 Meses:</strong><br/>
                <strong style={{fontSize: '12px'}}>Proceso:</strong><br/>
                1. Se toman las 12 quincenas (6 meses × 2 quincenas)<br/>
                2. Reglas por quincena:<br/>
                   • Si vacaciones &gt; 6 días: salario mínimo ÷ 2 (875,452)<br/>
                   • Si incapacidades &gt; 10 días: salario mínimo ÷ 2 (875,452)<br/>
                   • Si BRUTO = 0: se registra como 0<br/>
                   • Si no: se usa BRUTO normal<br/>
                3. Se promedia: Suma 12 quincenas ÷ 12 = promedio quincenal<br/>
                4. Se convierte a mensual: promedio quincena × 2<br/>
                5. Si &lt; Salario Mínimo (1,750,905) + Auxilio (300,000):<br/>
                   → Se aplica el mínimo legal (2,050,905/mes)<br/>
                • Los datos se extraen del repositorio de nómina<br/>
                • Los resultados se guardan automáticamente en la BD
              </div>
            </div>
          </div>
        )}

        {/* Modal de Cumpleaños - Mejorado */}
        {mostrarCumpleanos && (() => {
          const hoy = new Date();
          const mesActual = hoy.getMonth();
          const diaActual = hoy.getDate();
          
          const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
          
          // Colores por mes (estacionales)
          const COLORES_MES = [
            { bg: '#dbeafe', header: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)', text: '#1e40af' }, // Enero - Azul
            { bg: '#fce7f3', header: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)', text: '#9d174d' }, // Febrero - Rosa
            { bg: '#d1fae5', header: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)', text: '#065f46' }, // Marzo - Verde
            { bg: '#fef3c7', header: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)', text: '#92400e' }, // Abril - Amarillo
            { bg: '#e0e7ff', header: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)', text: '#3730a3' }, // Mayo - Indigo
            { bg: '#ffedd5', header: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)', text: '#9a3412' }, // Junio - Naranja
            { bg: '#fee2e2', header: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)', text: '#991b1b' }, // Julio - Rojo
            { bg: '#fae8ff', header: 'linear-gradient(135deg, #a855f7 0%, #c084fc 100%)', text: '#6b21a8' }, // Agosto - Púrpura
            { bg: '#ccfbf1', header: 'linear-gradient(135deg, #14b8a6 0%, #2dd4bf 100%)', text: '#0f766e' }, // Septiembre - Teal
            { bg: '#fef9c3', header: 'linear-gradient(135deg, #eab308 0%, #facc15 100%)', text: '#854d0e' }, // Octubre - Dorado
            { bg: '#f1f5f9', header: 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)', text: '#334155' }, // Noviembre - Gris
            { bg: '#dcfce7', header: 'linear-gradient(135deg, #22c55e 0%, #4ade80 100%)', text: '#166534' }  // Diciembre - Verde
          ];
          
          // Procesar cumpleaños de empleados activos
          const cumpleanosPorMes = {};
          MESES.forEach((_, i) => cumpleanosPorMes[i] = []);
          
          const empleadosActivos = empleados.filter(emp => emp.activo !== false);
          
          empleadosActivos.forEach(emp => {
            if (emp.fechaNacimiento) {
              const fecha = new Date(emp.fechaNacimiento + 'T00:00:00');
              if (!isNaN(fecha.getTime())) {
                const mes = fecha.getMonth();
                const dia = fecha.getDate();
                const edad = hoy.getFullYear() - fecha.getFullYear();
                cumpleanosPorMes[mes].push({
                  ...emp,
                  dia,
                  edad,
                  esHoy: mes === mesActual && dia === diaActual,
                  esEsteMes: mes === mesActual,
                  esteAno: hoy.getFullYear()
                });
              }
            }
          });
          
          // Ordenar cada mes por día
          Object.keys(cumpleanosPorMes).forEach(mes => {
            cumpleanosPorMes[mes].sort((a, b) => a.dia - b.dia);
          });
          
          // Cumpleaños de hoy
          const cumpleanosHoy = cumpleanosPorMes[mesActual].filter(c => c.esHoy);
          
          // Próximos cumpleaños (siguientes 30 días)
          const proximosCumpleanos = [];
          for (let i = 0; i < 30; i++) {
            const fechaCheck = new Date(hoy);
            fechaCheck.setDate(hoy.getDate() + i);
            const mesCheck = fechaCheck.getMonth();
            const diaCheck = fechaCheck.getDate();
            cumpleanosPorMes[mesCheck].forEach(c => {
              if (c.dia === diaCheck && i > 0) {
                proximosCumpleanos.push({ ...c, diasFaltan: i, mesIdx: mesCheck });
              }
            });
          }
          
          // Estadísticas
          const totalCumpleanos = Object.values(cumpleanosPorMes).flat().length;
          const cumpleEsteMes = cumpleanosPorMes[mesActual].length;
          const mesesConMasCumples = MESES.map((nombre, idx) => ({ nombre, cantidad: cumpleanosPorMes[idx].length, idx }))
            .sort((a, b) => b.cantidad - a.cantidad).slice(0, 3);
          
          return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)'
          }}>
            <div style={{
              background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: '20px',
              width: '95%',
              maxWidth: '1300px',
              maxHeight: '92vh',
              overflow: 'hidden',
              boxShadow: '0 25px 80px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Header Mejorado */}
              <div style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%)',
                padding: '24px 30px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Decoración de fondo */}
                <div style={{
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: 150,
                  height: 150,
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '50%'
                }} />
                <div style={{
                  position: 'absolute',
                  bottom: -30,
                  left: '30%',
                  width: 100,
                  height: 100,
                  background: 'rgba(255,255,255,0.08)',
                  borderRadius: '50%'
                }} />
                
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, zIndex: 1 }}>
                  <div style={{
                    width: 56,
                    height: 56,
                    borderRadius: 16,
                    background: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 32,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}>
                    🎂
                  </div>
                  <div>
                    <h2 style={{ margin: 0, color: 'white', fontSize: 24, fontWeight: 800, letterSpacing: '-0.5px' }}>
                      Cumpleaños del Equipo
                    </h2>
                    <div style={{ display: 'flex', gap: 16, marginTop: 6 }}>
                      <span style={{ 
                        color: 'rgba(255,255,255,0.95)', 
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}>
                        👥 {empleadosActivos.length} empleados activos
                      </span>
                      <span style={{ 
                        color: 'rgba(255,255,255,0.95)', 
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}>
                        📅 {MESES[mesActual]} {hoy.getFullYear()}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setMostrarCumpleanos(false)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderRadius: 12,
                    width: 44,
                    height: 44,
                    fontSize: 20,
                    cursor: 'pointer',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    zIndex: 1
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  ✕
                </button>
              </div>
              
              {/* Tarjetas de estadísticas */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 16,
                padding: '20px 24px',
                background: '#f8fafc',
                borderBottom: '1px solid #e2e8f0'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  borderRadius: 12,
                  padding: 16,
                  border: '1px solid #fbbf24'
                }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#92400e' }}>{cumpleanosHoy.length}</div>
                  <div style={{ fontSize: 12, color: '#b45309', fontWeight: 600 }}>🎉 Hoy</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                  borderRadius: 12,
                  padding: 16,
                  border: '1px solid #60a5fa'
                }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#1e40af' }}>{proximosCumpleanos.filter(p => p.diasFaltan <= 7).length}</div>
                  <div style={{ fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>📅 Esta semana</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)',
                  borderRadius: 12,
                  padding: 16,
                  border: '1px solid #f472b6'
                }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#9d174d' }}>{cumpleEsteMes}</div>
                  <div style={{ fontSize: 12, color: '#be185d', fontWeight: 600 }}>📆 Este mes</div>
                </div>
                <div style={{
                  background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                  borderRadius: 12,
                  padding: 16,
                  border: '1px solid #818cf8'
                }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#3730a3' }}>{totalCumpleanos}</div>
                  <div style={{ fontSize: 12, color: '#4338ca', fontWeight: 600 }}>🎂 Total año</div>
                </div>
              </div>
              
              {/* Contenido */}
              <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
                {/* Cumpleaños de hoy - Especial */}
                {cumpleanosHoy.length > 0 && (
                  <div style={{
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #fed7aa 100%)',
                    borderRadius: 16,
                    padding: 24,
                    marginBottom: 24,
                    border: '3px solid #f59e0b',
                    boxShadow: '0 8px 32px rgba(245, 158, 11, 0.25)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      fontSize: 60,
                      opacity: 0.15
                    }}>🎊</div>
                    <h3 style={{ 
                      margin: '0 0 20px 0', 
                      color: '#78350f', 
                      fontSize: 20, 
                      fontWeight: 800,
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 10 
                    }}>
                      <span style={{
                        background: '#f59e0b',
                        color: 'white',
                        padding: '6px 12px',
                        borderRadius: 8,
                        fontSize: 14
                      }}>🎉 HOY</span>
                      ¡Feliz Cumpleaños!
                    </h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                      {cumpleanosHoy.map(emp => (
                        <div key={emp.id} style={{
                          background: 'white',
                          borderRadius: 14,
                          padding: 20,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 16,
                          boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                          border: '2px solid #fbbf24',
                          minWidth: 280
                        }}>
                          <div style={{
                            width: 60,
                            height: 60,
                            borderRadius: 14,
                            background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: 22,
                            fontWeight: 800,
                            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)'
                          }}>
                            {emp.nombres?.charAt(0)}{emp.apellidos?.charAt(0)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 800, color: '#1e293b', fontSize: 16 }}>
                              {emp.nombres} {emp.apellidos}
                            </div>
                            <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>{emp.cargo}</div>
                            <div style={{ 
                              color: 'white',
                              background: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)',
                              fontSize: 12, 
                              fontWeight: 700,
                              padding: '4px 10px',
                              borderRadius: 20,
                              display: 'inline-block',
                              marginTop: 6
                            }}>
                              🎈 Cumple {emp.edad} años
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Próximos cumpleaños - Mejorado */}
                {proximosCumpleanos.length > 0 && (
                  <div style={{
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                    borderRadius: 16,
                    padding: 24,
                    marginBottom: 24,
                    border: '2px solid #7dd3fc'
                  }}>
                    <h3 style={{ 
                      margin: '0 0 18px 0', 
                      color: '#0c4a6e', 
                      fontSize: 17, 
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}>
                      <span style={{
                        background: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)',
                        color: 'white',
                        padding: '5px 10px',
                        borderRadius: 6,
                        fontSize: 13
                      }}>📅</span>
                      Próximos 30 días
                    </h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                      {proximosCumpleanos.slice(0, 12).map((emp, idx) => (
                        <div key={idx} style={{
                          background: 'white',
                          borderRadius: 10,
                          padding: '12px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          border: emp.diasFaltan <= 3 ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                          minWidth: 220,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}>
                          <div style={{
                            background: emp.diasFaltan === 1 
                              ? 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
                              : emp.diasFaltan <= 3 
                                ? 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)'
                                : COLORES_MES[emp.mesIdx].header,
                            borderRadius: 8,
                            padding: '6px 10px',
                            fontSize: 11,
                            fontWeight: 700,
                            color: 'white',
                            textAlign: 'center',
                            minWidth: 60
                          }}>
                            {emp.diasFaltan === 1 ? '🔔 Mañana' : `${emp.diasFaltan} días`}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>
                              {emp.nombres?.split(' ')[0]} {emp.apellidos?.split(' ')[0]}
                            </div>
                            <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>
                              {emp.dia} de {MESES[emp.mesIdx]}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Calendario anual por meses - Colorido */}
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ 
                    margin: '0 0 8px 0', 
                    color: '#1e293b', 
                    fontSize: 17, 
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <span style={{
                      background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                      color: 'white',
                      padding: '5px 10px',
                      borderRadius: 6,
                      fontSize: 13
                    }}>📆</span>
                    Calendario Anual
                  </h3>
                  <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
                    Meses con más cumpleaños: {mesesConMasCumples.map(m => `${m.nombre} (${m.cantidad})`).join(', ')}
                  </p>
                </div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                  gap: 18 
                }}>
                  {MESES.map((nombreMes, mesIdx) => {
                    const cumplesMes = cumpleanosPorMes[mesIdx];
                    const esMesActual = mesIdx === mesActual;
                    const colores = COLORES_MES[mesIdx];
                    
                    return (
                      <div 
                        key={mesIdx}
                        style={{
                          background: esMesActual ? colores.bg : '#ffffff',
                          borderRadius: 14,
                          border: esMesActual ? `3px solid ${colores.text}` : '1px solid #e5e7eb',
                          overflow: 'hidden',
                          boxShadow: esMesActual ? '0 8px 24px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.04)',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{
                          background: colores.header,
                          padding: '12px 16px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span style={{ 
                            fontWeight: 700, 
                            fontSize: 15,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6
                          }}>
                            {esMesActual && <span>👉</span>}
                            {nombreMes}
                          </span>
                          <span style={{
                            background: 'rgba(255,255,255,0.25)',
                            padding: '4px 12px',
                            borderRadius: 12,
                            fontSize: 13,
                            fontWeight: 700,
                            color: 'white'
                          }}>
                            {cumplesMes.length} {cumplesMes.length === 1 ? 'cumple' : 'cumples'}
                          </span>
                        </div>
                        <div style={{ padding: 12, minHeight: 70, background: esMesActual ? colores.bg : '#fafafa' }}>
                          {cumplesMes.length === 0 ? (
                            <div style={{ 
                              color: '#9ca3af', 
                              fontSize: 13, 
                              textAlign: 'center',
                              padding: 16,
                              fontStyle: 'italic'
                            }}>
                              Sin cumpleaños este mes
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {cumplesMes.map((emp, idx) => (
                                <div 
                                  key={idx}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    padding: '8px 10px',
                                    background: emp.esHoy 
                                      ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' 
                                      : 'white',
                                    borderRadius: 8,
                                    border: emp.esHoy ? '2px solid #f59e0b' : '1px solid #e5e7eb'
                                  }}
                                >
                                  <div style={{
                                    minWidth: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    background: emp.esHoy 
                                      ? 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
                                      : colores.header,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: 'white'
                                  }}>
                                    {emp.dia}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ 
                                      fontWeight: 600, 
                                      fontSize: 13, 
                                      color: '#1e293b',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}>
                                      {emp.nombres?.split(' ')[0]} {emp.apellidos?.split(' ')[0]}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#64748b' }}>
                                      {emp.sede || emp.cargo || 'Sin sede'}
                                    </div>
                                  </div>
                                  {emp.esHoy && <span style={{ fontSize: 16 }}>🎉</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Footer Mejorado */}
              <div style={{
                padding: '16px 24px',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                borderTop: '2px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ display: 'flex', gap: 20, fontSize: 13 }}>
                  <span style={{ 
                    color: '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}>
                    🎂 <strong style={{ color: '#1e293b' }}>{totalCumpleanos}</strong> cumpleaños registrados
                  </span>
                  <span style={{ 
                    color: '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}>
                    📊 <strong style={{ color: '#1e293b' }}>{Math.round(totalCumpleanos / 12)}</strong> promedio por mes
                  </span>
                </div>
                <button
                  onClick={() => setMostrarCumpleanos(false)}
                  style={{
                    padding: '10px 24px',
                    borderRadius: 8,
                    border: 'none',
                    background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(124, 58, 237, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.3)';
                  }}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
          );
        })()}

        {/* Modal de Asistencia Empleados */}
        {mostrarAsistencia && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              width: '95%',
              maxWidth: '1100px',
              height: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
            }}>
              {/* Header del modal */}
              <div style={{
                background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
                padding: '20px 24px',
                borderRadius: '16px 16px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h2 style={{ margin: 0, color: 'white', fontSize: 22, fontWeight: 700 }}>📋 Asistencia Empleados</h2>
                  <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
                    Planillas de asistencia para reuniones y capacitaciones
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button
                    onClick={() => setVistaAsistencia('crear')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 8,
                      border: vistaAsistencia === 'crear' ? '2px solid white' : '2px solid rgba(255,255,255,0.3)',
                      background: vistaAsistencia === 'crear' ? 'white' : 'transparent',
                      color: vistaAsistencia === 'crear' ? '#0891b2' : 'white',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    ➕ Crear Planilla
                  </button>
                  <button
                    onClick={() => setVistaAsistencia('repositorio')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 8,
                      border: vistaAsistencia === 'repositorio' ? '2px solid white' : '2px solid rgba(255,255,255,0.3)',
                      background: vistaAsistencia === 'repositorio' ? 'white' : 'transparent',
                      color: vistaAsistencia === 'repositorio' ? '#0891b2' : 'white',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    📁 Repositorio
                  </button>
                  <button
                    onClick={() => {
                      setMostrarAsistencia(false);
                      setFormAsistencia({
                        tipoEvento: 'CAPACITACIÓN',
                        nombreReunion: '',
                        descripcion: '',
                        fecha: new Date().toISOString().split('T')[0],
                        empleadosSeleccionados: [],
                        tipoResponsable: 'empleado',
                        responsableEmpleadoId: '',
                        responsableExterno: {
                          nombre: '',
                          tipoDoc: 'Cédula de ciudadanía',
                          documento: ''
                        }
                      });
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: 8,
                      width: 36,
                      height: 36,
                      fontSize: 18,
                      cursor: 'pointer',
                      color: 'white'
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Contenido */}
              <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
                {cargandoAsistencia ? (
                  <div style={{ textAlign: 'center', padding: 60 }}>
                    <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                    <p style={{ color: '#64748b' }}>Cargando...</p>
                  </div>
                ) : vistaAsistencia === 'crear' ? (
                  /* Vista de Crear Planilla */
                  <div>
                    {/* Tipo de evento */}
                    <div style={{ marginBottom: 20 }}>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, color: '#334155' }}>
                        Tipo de Evento *
                      </label>
                      <div style={{ display: 'flex', gap: 16 }}>
                        <button
                          onClick={() => setFormAsistencia({...formAsistencia, tipoEvento: 'CAPACITACIÓN'})}
                          style={{
                            flex: 1,
                            padding: '14px 20px',
                            borderRadius: 10,
                            border: formAsistencia.tipoEvento === 'CAPACITACIÓN' ? '3px solid #0891b2' : '2px solid #e2e8f0',
                            background: formAsistencia.tipoEvento === 'CAPACITACIÓN' ? '#ecfeff' : 'white',
                            cursor: 'pointer',
                            fontSize: 15,
                            fontWeight: 600,
                            color: formAsistencia.tipoEvento === 'CAPACITACIÓN' ? '#0891b2' : '#64748b',
                            transition: 'all 0.2s'
                          }}
                        >
                          📚 CAPACITACIÓN
                        </button>
                        <button
                          onClick={() => setFormAsistencia({...formAsistencia, tipoEvento: 'REUNIÓN'})}
                          style={{
                            flex: 1,
                            padding: '14px 20px',
                            borderRadius: 10,
                            border: formAsistencia.tipoEvento === 'REUNIÓN' ? '3px solid #7c3aed' : '2px solid #e2e8f0',
                            background: formAsistencia.tipoEvento === 'REUNIÓN' ? '#f5f3ff' : 'white',
                            cursor: 'pointer',
                            fontSize: 15,
                            fontWeight: 600,
                            color: formAsistencia.tipoEvento === 'REUNIÓN' ? '#7c3aed' : '#64748b',
                            transition: 'all 0.2s'
                          }}
                        >
                          👥 REUNIÓN
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                          Nombre de la {formAsistencia.tipoEvento === 'REUNIÓN' ? 'Reunión' : 'Capacitación'} *
                        </label>
                        <input
                          type="text"
                          value={formAsistencia.nombreReunion}
                          onChange={(e) => setFormAsistencia({...formAsistencia, nombreReunion: e.target.value.toUpperCase()})}
                          placeholder={formAsistencia.tipoEvento === 'REUNIÓN' ? 'EJ: REUNIÓN MENSUAL DE PERSONAL' : 'EJ: CAPACITACIÓN SERVICIO AL CLIENTE'}
                          style={{
                            width: '100%',
                            padding: '12px 14px',
                            borderRadius: 8,
                            border: '2px solid #e2e8f0',
                            fontSize: 14,
                            textTransform: 'uppercase'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                          Fecha *
                        </label>
                        <input
                          type="date"
                          value={formAsistencia.fecha}
                          onChange={(e) => setFormAsistencia({...formAsistencia, fecha: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '12px 14px',
                            borderRadius: 8,
                            border: '2px solid #e2e8f0',
                            fontSize: 14
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ marginBottom: 20 }}>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#334155' }}>
                        Descripción de la {formAsistencia.tipoEvento === 'REUNIÓN' ? 'Reunión' : 'Capacitación'}
                      </label>
                      <textarea
                        value={formAsistencia.descripcion}
                        onChange={(e) => setFormAsistencia({...formAsistencia, descripcion: e.target.value})}
                        placeholder="Breve descripción de los temas a tratar..."
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '12px 14px',
                          borderRadius: 8,
                          border: '2px solid #e2e8f0',
                          fontSize: 14,
                          resize: 'vertical'
                        }}
                      />
                    </div>

                    {/* Selección de empleados */}
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <label style={{ fontWeight: 600, color: '#334155' }}>
                          Empleados Asistentes ({formAsistencia.empleadosSeleccionados.length} seleccionados)
                        </label>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => {
                              const empleadosActivos = empleados.filter(e => e.activo !== false);
                              setFormAsistencia({
                                ...formAsistencia,
                                empleadosSeleccionados: empleadosActivos.map(e => ({
                                  id: e.id,
                                  nombre: e.nombre || `${e.nombres || ''} ${e.apellidos || ''}`.trim(),
                                  documento: e.documento,
                                  cargo: e.cargo
                                }))
                              });
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 6,
                              border: 'none',
                              background: '#059669',
                              color: 'white',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            ✓ Seleccionar Todos
                          </button>
                          <button
                            onClick={() => setFormAsistencia({...formAsistencia, empleadosSeleccionados: []})}
                            style={{
                              padding: '6px 12px',
                              borderRadius: 6,
                              border: 'none',
                              background: '#dc2626',
                              color: 'white',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            ✕ Quitar Todos
                          </button>
                        </div>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                        gap: 8,
                        maxHeight: 250,
                        overflowY: 'auto',
                        padding: 10,
                        background: '#f8fafc',
                        borderRadius: 8,
                        border: '2px solid #e2e8f0'
                      }}>
                        {empleados.filter(e => e.activo !== false).map(emp => {
                          const nombreEmp = emp.nombre || `${emp.nombres || ''} ${emp.apellidos || ''}`.trim();
                          const estaSeleccionado = formAsistencia.empleadosSeleccionados.some(s => s.id === emp.id);
                          return (
                            <div
                              key={emp.id}
                              onClick={() => {
                                if (estaSeleccionado) {
                                  setFormAsistencia({
                                    ...formAsistencia,
                                    empleadosSeleccionados: formAsistencia.empleadosSeleccionados.filter(s => s.id !== emp.id)
                                  });
                                } else {
                                  setFormAsistencia({
                                    ...formAsistencia,
                                    empleadosSeleccionados: [...formAsistencia.empleadosSeleccionados, {
                                      id: emp.id,
                                      nombre: nombreEmp,
                                      documento: emp.documento,
                                      cargo: emp.cargo
                                    }]
                                  });
                                }
                              }}
                              style={{
                                padding: '10px 12px',
                                borderRadius: 8,
                                border: estaSeleccionado ? '2px solid #0891b2' : '2px solid #e2e8f0',
                                background: estaSeleccionado ? '#ecfeff' : 'white',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 4,
                                  background: estaSeleccionado ? '#0891b2' : '#e2e8f0',
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 12
                                }}>
                                  {estaSeleccionado ? '✓' : ''}
                                </span>
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{nombreEmp}</div>
                                  <div style={{ fontSize: 11, color: '#64748b' }}>{emp.cargo || 'Sin cargo'}</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Sección de Responsable */}
                    <div style={{ marginBottom: 20, padding: 16, background: '#fef3c7', borderRadius: 10, border: '2px solid #f59e0b' }}>
                      <label style={{ display: 'block', fontWeight: 700, marginBottom: 12, color: '#92400e', fontSize: 15 }}>
                        👤 Responsable de la {formAsistencia.tipoEvento === 'REUNIÓN' ? 'Reunión' : 'Capacitación'}
                      </label>
                      
                      {/* Selector tipo responsable */}
                      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                        <button
                          onClick={() => setFormAsistencia({...formAsistencia, tipoResponsable: 'empleado'})}
                          style={{
                            padding: '10px 20px',
                            borderRadius: 8,
                            border: formAsistencia.tipoResponsable === 'empleado' ? '2px solid #059669' : '2px solid #d1d5db',
                            background: formAsistencia.tipoResponsable === 'empleado' ? '#d1fae5' : 'white',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 600,
                            color: formAsistencia.tipoResponsable === 'empleado' ? '#059669' : '#6b7280'
                          }}
                        >
                          👔 Empleado Interno
                        </button>
                        <button
                          onClick={() => setFormAsistencia({...formAsistencia, tipoResponsable: 'externo'})}
                          style={{
                            padding: '10px 20px',
                            borderRadius: 8,
                            border: formAsistencia.tipoResponsable === 'externo' ? '2px solid #7c3aed' : '2px solid #d1d5db',
                            background: formAsistencia.tipoResponsable === 'externo' ? '#ede9fe' : 'white',
                            cursor: 'pointer',
                            fontSize: 13,
                            fontWeight: 600,
                            color: formAsistencia.tipoResponsable === 'externo' ? '#7c3aed' : '#6b7280'
                          }}
                        >
                          🌐 Persona Externa
                        </button>
                      </div>

                      {/* Campos según tipo de responsable */}
                      {formAsistencia.tipoResponsable === 'empleado' ? (
                        <div>
                          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#92400e', fontSize: 13 }}>
                            Seleccionar Empleado Responsable
                          </label>
                          <select
                            value={formAsistencia.responsableEmpleadoId}
                            onChange={(e) => setFormAsistencia({...formAsistencia, responsableEmpleadoId: e.target.value})}
                            style={{
                              width: '100%',
                              padding: '12px 14px',
                              borderRadius: 8,
                              border: '2px solid #d97706',
                              fontSize: 14,
                              background: 'white'
                            }}
                          >
                            <option value="">-- Seleccionar responsable --</option>
                            {empleados.filter(e => e.activo !== false).map(emp => {
                              const nombreEmp = emp.nombre || `${emp.nombres || ''} ${emp.apellidos || ''}`.trim();
                              return (
                                <option key={emp.id} value={emp.id}>{nombreEmp} - {emp.cargo || 'Sin cargo'}</option>
                              );
                            })}
                          </select>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                          <div>
                            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#92400e', fontSize: 13 }}>
                              Nombre Completo *
                            </label>
                            <input
                              type="text"
                              value={formAsistencia.responsableExterno?.nombre || ''}
                              onChange={(e) => setFormAsistencia({
                                ...formAsistencia, 
                                responsableExterno: {...formAsistencia.responsableExterno, nombre: e.target.value.toUpperCase()}
                              })}
                              placeholder="Nombre del responsable"
                              style={{
                                width: '100%',
                                padding: '12px 14px',
                                borderRadius: 8,
                                border: '2px solid #d97706',
                                fontSize: 14,
                                textTransform: 'uppercase'
                              }}
                            />
                          </div>
                          <div>
                            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#92400e', fontSize: 13 }}>
                              Tipo Documento
                            </label>
                            <select
                              value={formAsistencia.responsableExterno?.tipoDoc || 'Cédula de ciudadanía'}
                              onChange={(e) => setFormAsistencia({
                                ...formAsistencia, 
                                responsableExterno: {...formAsistencia.responsableExterno, tipoDoc: e.target.value}
                              })}
                              style={{
                                width: '100%',
                                padding: '12px 14px',
                                borderRadius: 8,
                                border: '2px solid #d97706',
                                fontSize: 14,
                                background: 'white'
                              }}
                            >
                              <option value="Cédula de ciudadanía">Cédula de ciudadanía</option>
                              <option value="Cédula de extranjería">Cédula de extranjería</option>
                              <option value="Pasaporte">Pasaporte</option>
                              <option value="NIT">NIT</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#92400e', fontSize: 13 }}>
                              Número Documento *
                            </label>
                            <input
                              type="text"
                              value={formAsistencia.responsableExterno?.documento || ''}
                              onChange={(e) => setFormAsistencia({
                                ...formAsistencia, 
                                responsableExterno: {...formAsistencia.responsableExterno, documento: e.target.value}
                              })}
                              placeholder="Número de documento"
                              style={{
                                width: '100%',
                                padding: '12px 14px',
                                borderRadius: 8,
                                border: '2px solid #d97706',
                                fontSize: 14
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Vista previa de la planilla */}
                    {formAsistencia.nombreReunion && formAsistencia.empleadosSeleccionados.length > 0 && (
                      <div style={{ marginTop: 20, border: '2px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                        <div style={{ background: '#f1f5f9', padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
                          <h3 style={{ margin: 0, fontSize: 16, color: '#334155' }}>👁️ Vista Previa de la Planilla</h3>
                        </div>
                        <div id="planilla-asistencia-preview" style={{ padding: 20, background: 'white' }}>
                          {/* Encabezado centrado con logo */}
                          <div style={{ textAlign: 'center', marginBottom: 20, paddingBottom: 15, borderBottom: '3px solid #000' }}>
                            <img src={LOGO_BIGBURGUER} alt="Logo" style={{ width: 80, height: 'auto', marginBottom: 10 }} onError={(e) => e.target.style.display = 'none'} />
                            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: '#000' }}>
                              PLANILLA DE ASISTENCIA - {formAsistencia.tipoEvento || 'CAPACITACIÓN'}
                            </h1>
                            <p style={{ margin: '8px 0 0', fontSize: 14, color: '#000', fontWeight: 700 }}>
                              FECHA: {new Date(formAsistencia.fecha + 'T00:00:00').toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }).toUpperCase()}
                            </p>
                            <p style={{ margin: '8px 0 0', fontSize: 18, fontWeight: 900, color: '#000' }}>
                              {formAsistencia.nombreReunion}
                            </p>
                          </div>

                          {/* Descripción */}
                          {formAsistencia.descripcion && (
                            <div style={{ marginBottom: 20, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                              <strong style={{ color: '#334155' }}>Descripción:</strong>
                              <p style={{ margin: '5px 0 0', color: '#475569' }}>{formAsistencia.descripcion}</p>
                            </div>
                          )}

                          {/* Tabla de asistentes */}
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr style={{ background: '#1e293b' }}>
                                <th style={{ border: '1px solid #000', padding: 10, color: 'white', fontWeight: 700 }}>N°</th>
                                <th style={{ border: '1px solid #000', padding: 10, color: 'white', fontWeight: 700 }}>NOMBRE COMPLETO</th>
                                <th style={{ border: '1px solid #000', padding: 10, color: 'white', fontWeight: 700 }}>DOCUMENTO</th>
                                <th style={{ border: '1px solid #000', padding: 10, color: 'white', fontWeight: 700 }}>CARGO</th>
                                <th style={{ border: '1px solid #000', padding: 10, color: 'white', fontWeight: 700, width: '150px' }}>FIRMA</th>
                              </tr>
                            </thead>
                            <tbody>
                              {formAsistencia.empleadosSeleccionados.map((emp, idx) => (
                                <tr key={emp.id}>
                                  <td style={{ border: '1px solid #000', padding: 10, textAlign: 'center', fontWeight: 600 }}>{idx + 1}</td>
                                  <td style={{ border: '1px solid #000', padding: 10, fontWeight: 600 }}>{emp.nombre}</td>
                                  <td style={{ border: '1px solid #000', padding: 10, textAlign: 'center' }}>{emp.documento}</td>
                                  <td style={{ border: '1px solid #000', padding: 10 }}>{emp.cargo || '-'}</td>
                                  <td style={{ border: '1px solid #000', padding: 10, height: 50 }}></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>

                          {/* Sección de Responsable */}
                          <div style={{ marginTop: 30, paddingTop: 20, borderTop: '2px dashed #cbd5e1' }}>
                            <p style={{ margin: '0 0 15px', fontSize: 13, fontWeight: 700, color: '#475569' }}>
                              RESPONSABLE DE LA {formAsistencia.tipoEvento || 'CAPACITACIÓN'}:
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 30 }}>
                              <div>
                                <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Nombre:</p>
                                <p style={{ margin: '5px 0 0', fontSize: 14, fontWeight: 700, borderBottom: '1px solid #000', paddingBottom: 5, minHeight: 20 }}>
                                  {formAsistencia.tipoResponsable === 'empleado' 
                                    ? empleados.find(e => e.id === formAsistencia.responsableEmpleadoId)?.nombre || 
                                      (empleados.find(e => e.id === formAsistencia.responsableEmpleadoId) && 
                                       `${empleados.find(e => e.id === formAsistencia.responsableEmpleadoId).nombres || ''} ${empleados.find(e => e.id === formAsistencia.responsableEmpleadoId).apellidos || ''}`.trim()) ||
                                      '________________________________'
                                    : formAsistencia.responsableExterno?.nombre || '________________________________'}
                                </p>
                              </div>
                              <div>
                                <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Documento:</p>
                                <p style={{ margin: '5px 0 0', fontSize: 14, fontWeight: 700, borderBottom: '1px solid #000', paddingBottom: 5, minHeight: 20 }}>
                                  {formAsistencia.tipoResponsable === 'empleado' 
                                    ? empleados.find(e => e.id === formAsistencia.responsableEmpleadoId)?.documento || '________________________________'
                                    : formAsistencia.responsableExterno?.documento ? `${formAsistencia.responsableExterno.tipoDoc}: ${formAsistencia.responsableExterno.documento}` : '________________________________'}
                                </p>
                              </div>
                            </div>
                            <div style={{ marginTop: 40, textAlign: 'center' }}>
                              <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Firma del Responsable:</p>
                              <div style={{ width: 250, height: 50, borderBottom: '2px solid #000', margin: '30px auto 0' }}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Botones de acción */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20, paddingTop: 20, borderTop: '1px solid #e2e8f0' }}>
                      <button
                        onClick={() => {
                          if (!formAsistencia.nombreReunion || formAsistencia.empleadosSeleccionados.length === 0) {
                            alert('Debe ingresar el nombre de la reunión y seleccionar al menos un empleado');
                            return;
                          }
                          // Imprimir planilla
                          const contenido = document.getElementById('planilla-asistencia-preview');
                          if (!contenido) return;
                          const ventana = window.open('', '_blank');
                          ventana.document.write(`
                            <html>
                              <head>
                                <title>Planilla de Asistencia - ${formAsistencia.nombreReunion}</title>
                                <style>
                                  body { font-family: Arial, sans-serif; margin: 30px; }
                                  table { width: 100%; border-collapse: collapse; }
                                  th, td { border: 1px solid #000; padding: 10px; }
                                  th { background: #1e293b; color: white; }
                                  @media print { body { margin: 15px; } }
                                </style>
                              </head>
                              <body>
                                ${contenido.innerHTML}
                              </body>
                            </html>
                          `);
                          ventana.document.close();
                          ventana.print();
                        }}
                        disabled={!formAsistencia.nombreReunion || formAsistencia.empleadosSeleccionados.length === 0}
                        style={{
                          padding: '12px 24px',
                          borderRadius: 8,
                          border: 'none',
                          background: formAsistencia.nombreReunion && formAsistencia.empleadosSeleccionados.length > 0 
                            ? 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' 
                            : '#cbd5e1',
                          color: 'white',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: formAsistencia.nombreReunion && formAsistencia.empleadosSeleccionados.length > 0 ? 'pointer' : 'not-allowed'
                        }}
                      >
                        🖨️ Imprimir Planilla
                      </button>
                      <button
                        onClick={async () => {
                          if (!formAsistencia.nombreReunion || formAsistencia.empleadosSeleccionados.length === 0) {
                            alert('Debe ingresar el nombre de la reunión y seleccionar al menos un empleado');
                            return;
                          }
                          
                          // Obtener datos del responsable
                          let responsableNombre = '';
                          let responsableDocumento = '';
                          let responsableTipoDoc = '';
                          let responsableEsExterno = false;
                          
                          if (formAsistencia.tipoResponsable === 'empleado' && formAsistencia.responsableEmpleadoId) {
                            const empResp = empleados.find(e => e.id === formAsistencia.responsableEmpleadoId);
                            if (empResp) {
                              responsableNombre = empResp.nombre || `${empResp.nombres || ''} ${empResp.apellidos || ''}`.trim();
                              responsableDocumento = empResp.documento || '';
                              responsableTipoDoc = 'Cédula de ciudadanía';
                            }
                          } else if (formAsistencia.tipoResponsable === 'externo') {
                            responsableNombre = formAsistencia.responsableExterno?.nombre || '';
                            responsableDocumento = formAsistencia.responsableExterno?.documento || '';
                            responsableTipoDoc = formAsistencia.responsableExterno?.tipoDoc || 'Cédula de ciudadanía';
                            responsableEsExterno = true;
                          }
                          
                          setCargandoAsistencia(true);
                          try {
                            const { data, error } = await supabase
                              .from('asistencia_reuniones')
                              .insert({
                                empresa_id: empresaSeleccionada,
                                nombre_reunion: formAsistencia.nombreReunion,
                                descripcion: formAsistencia.descripcion,
                                fecha: formAsistencia.fecha,
                                tipo_evento: formAsistencia.tipoEvento || 'CAPACITACIÓN',
                                responsable_nombre: responsableNombre,
                                responsable_documento: responsableDocumento,
                                responsable_tipo_doc: responsableTipoDoc,
                                responsable_es_externo: responsableEsExterno,
                                empleados_ids: formAsistencia.empleadosSeleccionados.map(e => String(e.id)),
                                empleados_nombres: formAsistencia.empleadosSeleccionados.map(e => e.nombre),
                                empleados_documentos: formAsistencia.empleadosSeleccionados.map(e => e.documento || '-'),
                                empleados_cargos: formAsistencia.empleadosSeleccionados.map(e => e.cargo || '-')
                              })
                              .select()
                              .single();
                            
                            if (error) throw error;
                            
                            setPlanillasAsistencia([data, ...planillasAsistencia]);
                            setFormAsistencia({
                              tipoEvento: 'CAPACITACIÓN',
                              nombreReunion: '',
                              descripcion: '',
                              fecha: new Date().toISOString().split('T')[0],
                              empleadosSeleccionados: [],
                              tipoResponsable: 'empleado',
                              responsableEmpleadoId: '',
                              responsableExterno: {
                                nombre: '',
                                tipoDoc: 'Cédula de ciudadanía',
                                documento: ''
                              }
                            });
                            alert('¡Planilla guardada exitosamente!');
                          } catch (err) {
                            console.error('Error guardando planilla:', err);
                            alert('Error al guardar la planilla');
                          }
                          setCargandoAsistencia(false);
                        }}
                        disabled={!formAsistencia.nombreReunion || formAsistencia.empleadosSeleccionados.length === 0}
                        style={{
                          padding: '12px 24px',
                          borderRadius: 8,
                          border: 'none',
                          background: formAsistencia.nombreReunion && formAsistencia.empleadosSeleccionados.length > 0 
                            ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)' 
                            : '#cbd5e1',
                          color: 'white',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: formAsistencia.nombreReunion && formAsistencia.empleadosSeleccionados.length > 0 ? 'pointer' : 'not-allowed'
                        }}
                      >
                        💾 Guardar Planilla
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Vista de Repositorio */
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                      {/* Planillas creadas */}
                      <div>
                        <h3 style={{ margin: '0 0 15px', fontSize: 16, color: '#334155', display: 'flex', alignItems: 'center', gap: 8 }}>
                          📋 Planillas Creadas ({planillasAsistencia.length})
                        </h3>
                        <div style={{ maxHeight: 400, overflowY: 'auto', border: '2px solid #e2e8f0', borderRadius: 8 }}>
                          {planillasAsistencia.length === 0 ? (
                            <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>
                              No hay planillas creadas
                            </div>
                          ) : planillasAsistencia.map(plan => (
                            <div key={plan.id} style={{
                              padding: 15,
                              borderBottom: '1px solid #e2e8f0',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                  <span style={{
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    background: plan.tipo_evento === 'REUNIÓN' ? '#f5f3ff' : '#ecfeff',
                                    color: plan.tipo_evento === 'REUNIÓN' ? '#7c3aed' : '#0891b2'
                                  }}>
                                    {plan.tipo_evento || 'CAPACITACIÓN'}
                                  </span>
                                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{plan.nombre_reunion}</span>
                                </div>
                                <div style={{ fontSize: 12, color: '#64748b' }}>
                                  {new Date(plan.fecha + 'T00:00:00').toLocaleDateString('es-CO')} • {plan.empleados_nombres?.length || 0} asistentes
                                  {plan.responsable_nombre ? ` • Resp: ${plan.responsable_nombre}` : ''}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                  onClick={() => {
                                    // Reconstruir planilla para imprimir
                                    const fechaFormateada = new Date(plan.fecha + 'T00:00:00').toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }).toUpperCase();
                                    const tipoEvento = plan.tipo_evento || 'CAPACITACIÓN';
                                    const empleadosHtml = (plan.empleados_nombres || []).map((nombre, idx) => `
                                      <tr>
                                        <td style="border: 1px solid #000; padding: 10px; text-align: center; font-weight: 600;">${idx + 1}</td>
                                        <td style="border: 1px solid #000; padding: 10px; font-weight: 600;">${nombre}</td>
                                        <td style="border: 1px solid #000; padding: 10px; text-align: center;">${plan.empleados_documentos?.[idx] || '-'}</td>
                                        <td style="border: 1px solid #000; padding: 10px;">${plan.empleados_cargos?.[idx] || '-'}</td>
                                        <td style="border: 1px solid #000; padding: 10px; height: 50px;"></td>
                                      </tr>
                                    `).join('');
                                    
                                    // Sección del responsable
                                    const responsableHtml = `
                                      <div style="margin-top: 30px; padding-top: 20px; border-top: 2px dashed #cbd5e1;">
                                        <p style="margin: 0 0 15px; font-size: 13px; font-weight: 700; color: #475569;">
                                          RESPONSABLE DE LA ${tipoEvento}:
                                        </p>
                                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px;">
                                          <div>
                                            <p style="margin: 0; font-size: 12px; color: #64748b;">Nombre:</p>
                                            <p style="margin: 5px 0 0; font-size: 14px; font-weight: 700; border-bottom: 1px solid #000; padding-bottom: 5px; min-height: 20px;">
                                              ${plan.responsable_nombre || '________________________________'}
                                            </p>
                                          </div>
                                          <div>
                                            <p style="margin: 0; font-size: 12px; color: #64748b;">Documento:</p>
                                            <p style="margin: 5px 0 0; font-size: 14px; font-weight: 700; border-bottom: 1px solid #000; padding-bottom: 5px; min-height: 20px;">
                                              ${plan.responsable_documento ? `${plan.responsable_tipo_doc || 'Documento'}: ${plan.responsable_documento}` : '________________________________'}
                                            </p>
                                          </div>
                                        </div>
                                        <div style="margin-top: 40px; text-align: center;">
                                          <p style="margin: 0; font-size: 12px; color: #64748b;">Firma del Responsable:</p>
                                          <div style="width: 250px; height: 50px; border-bottom: 2px solid #000; margin: 30px auto 0;"></div>
                                        </div>
                                      </div>
                                    `;
                                    
                                    const ventana = window.open('', '_blank');
                                    ventana.document.write(`
                                      <html>
                                        <head>
                                          <title>Planilla de Asistencia - ${plan.nombre_reunion}</title>
                                          <style>
                                            body { font-family: Arial, sans-serif; margin: 30px; }
                                            table { width: 100%; border-collapse: collapse; }
                                            th, td { border: 1px solid #000; padding: 10px; }
                                            th { background: #1e293b; color: white; }
                                            @media print { body { margin: 15px; } }
                                          </style>
                                        </head>
                                        <body>
                                          <div style="text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 3px solid #000;">
                                            <img src="${LOGO_BIGBURGUER}" alt="Logo" style="width: 80px; height: auto; margin-bottom: 10px;" onerror="this.style.display='none'" />
                                            <h1 style="margin: 0; font-size: 22px; font-weight: 900; color: #000;">PLANILLA DE ASISTENCIA - ${tipoEvento}</h1>
                                            <p style="margin: 8px 0 0; font-size: 14px; color: #000; font-weight: 700;">FECHA: ${fechaFormateada}</p>
                                            <p style="margin: 8px 0 0; font-size: 18px; font-weight: 900; color: #000;">${plan.nombre_reunion}</p>
                                          </div>
                                          ${plan.descripcion ? `<div style="margin-bottom: 20px; padding: 12px; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0;"><strong>Descripción:</strong><p style="margin: 5px 0 0;">${plan.descripcion}</p></div>` : ''}
                                          <table>
                                            <thead>
                                              <tr>
                                                <th>N°</th>
                                                <th>NOMBRE COMPLETO</th>
                                                <th>DOCUMENTO</th>
                                                <th>CARGO</th>
                                                <th style="width: 150px;">FIRMA</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              ${empleadosHtml}
                                            </tbody>
                                          </table>
                                          ${responsableHtml}
                                        </body>
                                      </html>
                                    `);
                                    ventana.document.close();
                                    ventana.print();
                                  }}
                                  style={{
                                    padding: '6px 10px',
                                    borderRadius: 6,
                                    border: 'none',
                                    background: '#6366f1',
                                    color: 'white',
                                    fontSize: 11,
                                    cursor: 'pointer'
                                  }}
                                >
                                  🖨️
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!confirm('¿Está seguro de eliminar esta planilla?')) return;
                                    try {
                                      await supabase.from('asistencia_reuniones').delete().eq('id', plan.id);
                                      setPlanillasAsistencia(planillasAsistencia.filter(p => p.id !== plan.id));
                                    } catch (err) {
                                      alert('Error al eliminar');
                                    }
                                  }}
                                  style={{
                                    padding: '6px 10px',
                                    borderRadius: 6,
                                    border: 'none',
                                    background: '#dc2626',
                                    color: 'white',
                                    fontSize: 11,
                                    cursor: 'pointer'
                                  }}
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Documentos firmados */}
                      <div>
                        <h3 style={{ margin: '0 0 15px', fontSize: 16, color: '#334155', display: 'flex', alignItems: 'center', gap: 8 }}>
                          ✍️ Documentos Firmados ({documentosFirmados.length})
                        </h3>
                        
                        {/* Formulario para subir */}
                        <div style={{ padding: 15, background: '#f1f5f9', borderRadius: 8, marginBottom: 15 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                            <input
                              type="text"
                              id="nombre-doc-firmado"
                              placeholder="Nombre del documento"
                              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                            />
                            <input
                              type="text"
                              id="reunion-doc-firmado"
                              placeholder="Nombre de la reunión"
                              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, textTransform: 'uppercase' }}
                              onInput={(e) => e.target.value = e.target.value.toUpperCase()}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: 10 }}>
                            <input
                              type="date"
                              id="fecha-doc-firmado"
                              defaultValue={new Date().toISOString().split('T')[0]}
                              style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, flex: 1 }}
                            />
                            <input
                              type="file"
                              id="archivo-firmado"
                              accept=".pdf,.jpg,.jpeg,.png"
                              style={{ flex: 2 }}
                            />
                            <button
                              onClick={async () => {
                                const nombreDoc = document.getElementById('nombre-doc-firmado').value;
                                const reunionDoc = document.getElementById('reunion-doc-firmado').value;
                                const fechaDoc = document.getElementById('fecha-doc-firmado').value;
                                const archivoInput = document.getElementById('archivo-firmado');
                                const archivo = archivoInput.files[0];
                                
                                if (!nombreDoc || !reunionDoc || !fechaDoc || !archivo) {
                                  alert('Complete todos los campos');
                                  return;
                                }
                                
                                setSubiendoFirmado(true);
                                try {
                                  // Subir archivo
                                  const nombreArchivo = `${Date.now()}_${archivo.name}`;
                                  const { data: uploadData, error: uploadError } = await supabase.storage
                                    .from('asistencia-firmadas')
                                    .upload(nombreArchivo, archivo);
                                  
                                  if (uploadError) throw uploadError;
                                  
                                  // Obtener URL pública
                                  const { data: urlData } = supabase.storage
                                    .from('asistencia-firmadas')
                                    .getPublicUrl(nombreArchivo);
                                  
                                  // Guardar en base de datos
                                  const { data: docData, error: docError } = await supabase
                                    .from('asistencia_reuniones_firmadas')
                                    .insert({
                                      empresa_id: empresaSeleccionada,
                                      nombre_documento: nombreDoc,
                                      nombre_reunion: reunionDoc,
                                      fecha: fechaDoc,
                                      archivo_url: urlData.publicUrl,
                                      archivo_nombre: archivo.name
                                    })
                                    .select()
                                    .single();
                                  
                                  if (docError) throw docError;
                                  
                                  setDocumentosFirmados([docData, ...documentosFirmados]);
                                  document.getElementById('nombre-doc-firmado').value = '';
                                  document.getElementById('reunion-doc-firmado').value = '';
                                  archivoInput.value = '';
                                  alert('¡Documento guardado exitosamente!');
                                } catch (err) {
                                  console.error('Error subiendo documento:', err);
                                  alert('Error al subir el documento');
                                }
                                setSubiendoFirmado(false);
                              }}
                              disabled={subiendoFirmado}
                              style={{
                                padding: '8px 16px',
                                borderRadius: 6,
                                border: 'none',
                                background: subiendoFirmado ? '#94a3b8' : '#059669',
                                color: 'white',
                                fontSize: 13,
                                fontWeight: 600,
                                cursor: subiendoFirmado ? 'wait' : 'pointer'
                              }}
                            >
                              {subiendoFirmado ? '⏳' : '📤 Subir'}
                            </button>
                          </div>
                        </div>

                        <div style={{ maxHeight: 300, overflowY: 'auto', border: '2px solid #e2e8f0', borderRadius: 8 }}>
                          {documentosFirmados.length === 0 ? (
                            <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>
                              No hay documentos firmados
                            </div>
                          ) : documentosFirmados.map(doc => (
                            <div key={doc.id} style={{
                              padding: 12,
                              borderBottom: '1px solid #e2e8f0',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <div>
                                <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>{doc.nombre_documento}</div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                  {doc.nombre_reunion} • {new Date(doc.fecha + 'T00:00:00').toLocaleDateString('es-CO')}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 6 }}>
                                <a
                                  href={doc.archivo_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    padding: '6px 10px',
                                    borderRadius: 6,
                                    background: '#3b82f6',
                                    color: 'white',
                                    fontSize: 11,
                                    textDecoration: 'none'
                                  }}
                                >
                                  👁️
                                </a>
                                <button
                                  onClick={async () => {
                                    if (!confirm('¿Está seguro de eliminar este documento?')) return;
                                    try {
                                      // Extraer nombre de archivo de la URL
                                      const nombreArchivo = doc.archivo_url.split('/').pop();
                                      await supabase.storage.from('asistencia-firmadas').remove([nombreArchivo]);
                                      await supabase.from('asistencia_reuniones_firmadas').delete().eq('id', doc.id);
                                      setDocumentosFirmados(documentosFirmados.filter(d => d.id !== doc.id));
                                    } catch (err) {
                                      alert('Error al eliminar');
                                    }
                                  }}
                                  style={{
                                    padding: '6px 10px',
                                    borderRadius: 6,
                                    border: 'none',
                                    background: '#dc2626',
                                    color: 'white',
                                    fontSize: 11,
                                    cursor: 'pointer'
                                  }}
                                >
                                  🗑️
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal de Carga Masiva */}
        {mostrarCargaMasiva && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '85vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                paddingBottom: '16px',
                borderBottom: '2px solid #e0e0e0'
              }}>
                <h2 style={{ margin: 0, color: '#1976d2', fontSize: '20px' }}>
                  📥 Carga Masiva de Empleados
                </h2>
                <button
                  onClick={() => {
                    setMostrarCargaMasiva(false);
                    setEmpleadosPreview([]);
                    setResultadoCarga(null);
                    if (archivoInputRef.current) archivoInputRef.current.value = "";
                  }}
                  style={{
                    background: '#f5f5f5',
                    border: 'none',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    fontSize: '18px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Paso 1: Descargar Plantilla */}
              <div style={{
                background: '#e3f2fd',
                borderRadius: '10px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#1565c0', fontSize: '15px' }}>
                  📋 Paso 1: Descargar Plantilla
                </h3>
                <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#424242' }}>
                  Descarga la plantilla CSV con los campos exactos del formulario de empleados:
                </p>
                <div style={{ 
                  fontSize: '11px', 
                  color: '#1565c0', 
                  marginBottom: '12px',
                  padding: '8px',
                  background: 'rgba(255,255,255,0.7)',
                  borderRadius: '6px'
                }}>
                  <strong>Campos:</strong> tipoDoc, documento, nombres, apellidos, fechaNacimiento, fechaExpedicion, rh, nacionalidad, genero, telefono, direccion, fechaIngreso, tipoContrato, sede, cargo, eps, arl, fondo, banco, tipoCuenta, numeroCuenta, salarioBase, salarioProm6m, valorBonificacion
                </div>
                <button
                  onClick={descargarPlantillaExcel}
                  style={{
                    background: 'linear-gradient(135deg, #43a047 0%, #66bb6a 100%)',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: 'none',
                    fontWeight: '600',
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  ⬇️ Descargar Plantilla CSV
                </button>
              </div>

              {/* Paso 2: Cargar Archivo */}
              <div style={{
                background: '#fff3e0',
                borderRadius: '10px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#e65100', fontSize: '15px' }}>
                  📁 Paso 2: Cargar Archivo
                </h3>
                <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#424242' }}>
                  Selecciona el archivo CSV completado. Se aceptan archivos separados por coma (,) o punto y coma (;).
                </p>
                <input
                  ref={archivoInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={procesarArchivo}
                  style={{
                    padding: '10px',
                    border: '2px dashed #ff9800',
                    borderRadius: '8px',
                    width: '100%',
                    cursor: 'pointer',
                    backgroundColor: '#fffbf5'
                  }}
                />
              </div>

              {/* Resultados del procesamiento */}
              {resultadoCarga && (
                <div style={{
                  background: '#f5f5f5',
                  borderRadius: '10px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#424242', fontSize: '15px' }}>
                    📊 Resultado del Procesamiento
                  </h3>
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '12px' }}>
                    <div style={{
                      background: '#e8f5e9',
                      padding: '12px 20px',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2e7d32' }}>
                        {resultadoCarga.validos}
                      </div>
                      <div style={{ fontSize: '12px', color: '#388e3c' }}>Válidos</div>
                    </div>
                    <div style={{
                      background: resultadoCarga.errores.length > 0 ? '#ffebee' : '#f5f5f5',
                      padding: '12px 20px',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: resultadoCarga.errores.length > 0 ? '#c62828' : '#9e9e9e' }}>
                        {resultadoCarga.errores.length}
                      </div>
                      <div style={{ fontSize: '12px', color: resultadoCarga.errores.length > 0 ? '#d32f2f' : '#757575' }}>Errores</div>
                    </div>
                  </div>

                  {resultadoCarga.errores.length > 0 && (
                    <div style={{
                      background: '#ffebee',
                      borderRadius: '6px',
                      padding: '10px',
                      maxHeight: '100px',
                      overflowY: 'auto',
                      fontSize: '12px',
                      color: '#c62828'
                    }}>
                      {resultadoCarga.errores.map((err, i) => (
                        <div key={i}>⚠️ {err}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Vista previa de empleados */}
              {empleadosPreview.length > 0 && (
                <div style={{
                  background: 'white',
                  borderRadius: '10px',
                  border: '1px solid #e0e0e0',
                  marginBottom: '16px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    background: '#424242',
                    color: 'white',
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    👥 Vista Previa ({empleadosPreview.length} empleados)
                  </div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Nombre</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Documento</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Cargo</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>Sede</th>
                          <th style={{ padding: '8px', textAlign: 'right', borderBottom: '1px solid #e0e0e0' }}>Salario</th>
                        </tr>
                      </thead>
                      <tbody>
                        {empleadosPreview.map((emp, idx) => (
                          <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{emp.nombre}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{emp.documento}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{emp.cargo || '-'}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{emp.sede || '-'}</td>
                            <td style={{ padding: '8px', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                              {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(emp.salarioBase || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Botones de acción */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  onClick={() => {
                    setMostrarCargaMasiva(false);
                    setEmpleadosPreview([]);
                    setResultadoCarga(null);
                    if (archivoInputRef.current) archivoInputRef.current.value = "";
                  }}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: '2px solid #9e9e9e',
                    background: 'white',
                    color: '#616161',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                {empleadosPreview.length > 0 && (
                  <button
                    onClick={guardarEmpleadosMasivo}
                    disabled={cargandoMasivo}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: 'none',
                      background: cargandoMasivo 
                        ? '#9e9e9e' 
                        : 'linear-gradient(135deg, #43a047 0%, #66bb6a 100%)',
                      color: 'white',
                      fontWeight: '600',
                      cursor: cargandoMasivo ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {cargandoMasivo ? '⏳ Guardando...' : "💾 Guardar " + empleadosPreview.length + " Empleados"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal de Actualización de Datos */}
        {mostrarActualizacionDatos && (
          <ModalActualizacionDatos onClose={() => setMostrarActualizacionDatos(false)} />
        )}

        {/* Tabla de empleados activos */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          marginBottom: '24px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #424242 0%, #616161 100%)',
            padding: '16px 20px',
            borderBottom: '3px solid #ff5722'
          }}>
            <h3 style={{ color: 'white', margin: 0, fontSize: '16px', fontWeight: '600' }}>
              ✅ Empleados Activos ({activosFiltrados.length})
            </h3>
          </div>
          <div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '2px solid #e0e0e0' }}>Nombre</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '2px solid #e0e0e0' }}>Documento</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '2px solid #e0e0e0' }}>Cargo</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '2px solid #e0e0e0' }}>Sede</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '2px solid #e0e0e0' }}>Estado</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '2px solid #e0e0e0' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
              {activosFiltrados.map((e, idx) => (
                <tr 
                  key={e.id} 
                  style={{ 
                    background: idx % 2 === 0 ? 'white' : '#fafafa',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(ev) => ev.currentTarget.style.background = '#fff3e0'}
                  onMouseOut={(ev) => ev.currentTarget.style.background = idx % 2 === 0 ? 'white' : '#fafafa'}
                >
                  <td style={{ padding: '14px 16px', borderBottom: '1px solid #eee', fontWeight: '500', color: '#333' }}>{e.nombre}</td>
                  <td style={{ padding: '14px 16px', borderBottom: '1px solid #eee', color: '#666' }}>{e.documento}</td>
                  <td style={{ padding: '14px 16px', borderBottom: '1px solid #eee', color: '#666' }}>{e.cargo}</td>
                  <td style={{ padding: '14px 16px', borderBottom: '1px solid #eee', color: '#666' }}>{e.sede}</td>
                  <td style={{ padding: '14px 16px', borderBottom: '1px solid #eee' }}>
                    <span style={{
                      background: e.activo ? 'linear-gradient(135deg, #43a047 0%, #66bb6a 100%)' : '#9e9e9e',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {e.activo ? "✓ Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', borderBottom: '1px solid #eee' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => verEmpleado(e)}
                        style={{
                          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                          color: 'white',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'transform 0.2s'
                        }}
                        onMouseOver={(ev) => ev.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseOut={(ev) => ev.currentTarget.style.transform = 'scale(1)'}
                      >
                        👁 Ver
                      </button>
                      <button
                        onClick={() => editarEmpleado(e)}
                        style={{
                          background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                          color: 'white',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'transform 0.2s'
                        }}
                        onMouseOver={(ev) => ev.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseOut={(ev) => ev.currentTarget.style.transform = 'scale(1)'}
                      >
                        ✏ Editar
                      </button>
                      {e.activo && (
                        <button
                          onClick={() => toggleActivo(e.id, false)}
                          style={{
                            background: 'linear-gradient(135deg, #e53935 0%, #ef5350 100%)',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            border: 'none',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'transform 0.2s'
                          }}
                          onMouseOver={(ev) => ev.currentTarget.style.transform = 'scale(1.05)'}
                          onMouseOut={(ev) => ev.currentTarget.style.transform = 'scale(1)'}
                        >
                          ✕ Inactivar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!activosFiltrados.length && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#9e9e9e', fontSize: '15px' }}>
                    <div style={{ fontSize: '40px', marginBottom: '8px' }}>👤</div>
                    Sin empleados activos
                  </td>
                </tr>
              )}
                  </tbody>
                </table>
              </div>
            </div>

        {verInactivos && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            marginBottom: '24px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #757575 0%, #9e9e9e 100%)',
              padding: '16px 20px',
              borderBottom: '3px solid #ff9800'
            }}>
              <h3 style={{ color: 'white', margin: 0, fontSize: '16px', fontWeight: '600' }}>
                ⏸ Empleados Inactivos ({inactivosFiltrados.length})
              </h3>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#fafafa' }}>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '2px solid #e0e0e0' }}>Nombre</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '2px solid #e0e0e0' }}>Documento</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '2px solid #e0e0e0' }}>Cargo</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '2px solid #e0e0e0' }}>Sede</th>
                      <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#424242', borderBottom: '2px solid #e0e0e0' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inactivosFiltrados.map((e, idx) => (
                      <tr 
                        key={e.id}
                        style={{ 
                          background: idx % 2 === 0 ? 'white' : '#fafafa',
                          transition: 'background 0.2s'
                        }}
                        onMouseOver={(ev) => ev.currentTarget.style.background = '#fff3e0'}
                        onMouseOut={(ev) => ev.currentTarget.style.background = idx % 2 === 0 ? 'white' : '#fafafa'}
                      >
                        <td style={{ padding: '14px 16px', borderBottom: '1px solid #eee', fontWeight: '500', color: '#333' }}>{e.nombre}</td>
                        <td style={{ padding: '14px 16px', borderBottom: '1px solid #eee', color: '#666' }}>{e.documento}</td>
                        <td style={{ padding: '14px 16px', borderBottom: '1px solid #eee', color: '#666' }}>{e.cargo}</td>
                        <td style={{ padding: '14px 16px', borderBottom: '1px solid #eee', color: '#666' }}>{e.sede}</td>
                        <td style={{ padding: '14px 16px', borderBottom: '1px solid #eee' }}>
                          <button
                            onClick={() => toggleActivo(e.id, true)}
                            style={{
                              background: 'linear-gradient(135deg, #43a047 0%, #66bb6a 100%)',
                              color: 'white',
                              padding: '6px 14px',
                              borderRadius: '6px',
                              border: 'none',
                              fontSize: '12px',
                              fontWeight: '500',
                              cursor: 'pointer',
                              transition: 'transform 0.2s'
                            }}
                            onMouseOver={(ev) => ev.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseOut={(ev) => ev.currentTarget.style.transform = 'scale(1)'}
                          >
                            ↩ Reactivar
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!inactivosFiltrados.length && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#9e9e9e', fontSize: '15px' }}>
                          <div style={{ fontSize: '40px', marginBottom: '8px' }}>✓</div>
                          Sin empleados inactivos
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
 
        {editEmpleado && (
          <div 
            className="fixed inset-0" 
            style={{ 
              background: 'rgba(0,0,0,0.75)', 
              backdropFilter: 'blur(8px)', 
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px'
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #fff9f5 0%, #fff 100%)',
              borderRadius: '20px',
              boxShadow: '0 25px 80px rgba(211, 47, 47, 0.3), 0 15px 40px rgba(0,0,0,0.25)',
              width: '100%',
              maxWidth: '900px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              border: '3px solid #ff5722',
              margin: 'auto',
              overflow: 'hidden'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #d32f2f 0%, #ff5722 100%)',
                padding: '20px 24px'
              }}>
                <h3 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: '600' }}>
                  {editEmpleado.id ? "✏️ Editar empleado" : "➕ Nuevo empleado"}
                </h3>
              </div>
              <div style={{ padding: '24px', overflow: 'auto', flex: 1 }}>
                <EmpleadoForm
                  sedes={sedes}
                  empleado={editEmpleado}
                  usuarios={usuarios}
                  valoresAnuales={valoresAnuales}
                  onCancel={() => setEditEmpleado(null)}
                  onSave={guardarEmpleado}
                />
              </div>
            </div>
          </div>
        )}
 
        {viewEmpleado && (
          <div 
            className="fixed inset-0" 
            style={{ 
              background: 'rgba(0,0,0,0.75)', 
              backdropFilter: 'blur(8px)', 
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px'
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #fff9f5 0%, #fff 100%)',
              borderRadius: '20px',
              boxShadow: '0 25px 80px rgba(211, 47, 47, 0.3), 0 15px 40px rgba(0,0,0,0.25)',
              width: '100%',
              maxWidth: '900px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              border: '3px solid #ff5722',
              margin: 'auto'
            }}>
              {/* Header del modal */}
              <div style={{
                background: 'linear-gradient(135deg, #d32f2f 0%, #ff5722 50%, #ff9800 100%)',
                padding: '24px 28px',
                borderRadius: '16px 16px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0
              }}>
                <div>
                  <h3 style={{ color: 'white', margin: 0, fontSize: '22px', fontWeight: '700', textShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>
                    👤 Perfil del Empleado
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.9)', margin: '4px 0 0 0', fontSize: '14px' }}>
                    {viewEmpleado.nombre || 'Sin nombre'}
                  </p>
                </div>
                <button
                  onClick={() => setViewEmpleado(null)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  ✕
                </button>
              </div>
              {/* Contenido scrolleable */}
              <div style={{ 
                padding: '28px', 
                overflowY: 'auto',
                flex: 1
              }}>
                <EmpleadoView
                  empleado={viewEmpleado}
                  sedes={sedes}
                  usuarios={usuarios}
                  onClose={() => setViewEmpleado(null)}
                  ausenciasAll={ausencias}
                  vacacionesAll={vacaciones}
                  memorandosAll={memorandos}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
 
  // 👉 NUEVO: selector de ambiente antes de entrar
if (!ambiente) {
  return (
    <>
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)" }}>
      {/* 🔹 Barra de información del usuario - visible en todos los ambientes */}
      <div style={{
        background: 'linear-gradient(90deg, #0f0f23 0%, #1a1a2e 100%)',
        padding: '10px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255, 107, 53, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>👤</span>
            <div>
              <span style={{ color: 'white', fontSize: 13, fontWeight: 600, display: 'block' }}>{infoUsuarioActual.nombre}</span>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>{usuario.email}</span>
            </div>
          </div>
          <div style={{ height: 30, width: 1, background: 'rgba(255,255,255,0.2)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>🎖️</span>
            <div>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 9, display: 'block' }}>PERFIL</span>
              <span style={{ color: '#ffc53d', fontSize: 12, fontWeight: 600 }}>{infoUsuarioActual.perfil}</span>
            </div>
          </div>
          <div style={{ height: 30, width: 1, background: 'rgba(255,255,255,0.2)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>🏢</span>
            <div>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 9, display: 'block' }}>EMPRESA</span>
              <span style={{ color: '#7dd3fc', fontSize: 12, fontWeight: 600 }}>{infoUsuarioActual.empresaNombre}</span>
            </div>
          </div>
        </div>
        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
          📅 {new Date().toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>
      {/* Header ejecutivo con logo y controles */}
      <div style={{ 
        background: "linear-gradient(90deg, #d4380d 0%, #fa541c 50%, #ff7a45 100%)",
        borderBottom: "3px solid #ffc53d",
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0 4px 20px rgba(212, 56, 13, 0.3)"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <LogoBigBurguer size={50} />
          <div>
            <h2 style={{ color: "white", fontWeight: 700, fontSize: 18, margin: 0, textShadow: "1px 1px 2px rgba(0,0,0,0.3)" }}>
              {empresaSeleccionada && empresas.length > 0 
                ? empresas.find(e => e.id === empresaSeleccionada)?.nombre || 'BIGBURGUER'
                : 'BIGBURGUER'}
            </h2>
            <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 11, margin: 0 }}>Sistema de Gestión Empresarial</p>
          </div>
        </div>
        
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {/* 🏢 Selector de empresa para super_admin */}
          {esSuperAdminVerificado && empresas.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.1)', padding: '8px 12px', borderRadius: 8 }}>
              <span style={{ color: 'white', fontSize: 11, fontWeight: 500 }}>🏢</span>
              <select
                value={empresaSeleccionada || ''}
                onChange={async (e) => {
                  const valor = e.target.value;
                  setEmpresaSeleccionada(valor === '' ? null : valor);
                  setEmpresaYaCargada(true); // 🔹 Marcar que el usuario ya hizo una selección
                  // 💾 Guardar en Supabase para persistir entre dispositivos
                  if (usuario?.id) {
                    const { error } = await supabase
                      .from('usuarios')
                      .update({ empresa_preferida: valor || null })
                      .eq('id', usuario.id);
                    if (error) {
                      console.error("❌ Error guardando empresa preferida:", error);
                    } else {
                      console.log("💾 Empresa preferida guardada en Supabase:", valor || "NINGUNA");
                    }
                  }
                  console.log("🔄 Empresa cambiada a:", valor || "TODAS");
                }}
                style={{
                  background: 'white',
                  border: 'none',
                  borderRadius: 6,
                  padding: '6px 10px',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#d4380d',
                  cursor: 'pointer',
                  minWidth: 150
                }}
              >
                <option value="">Todas las empresas</option>
                {empresas.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Botón de Notificaciones */}
          <button
            type="button"
            onClick={() =>
              setMostrarNotificacionesPrincipal((prev) => {
                const next = !prev;
                if (!prev) setNotificacionesVistasPrincipal(true);
                return next;
              })
            }
            style={{ 
              height: 42, 
              width: 42, 
              display: "inline-flex", 
              alignItems: "center", 
              justifyContent: "center", 
              position: "relative",
              background: "rgba(255,255,255,0.15)",
              border: "2px solid rgba(255,255,255,0.3)",
              borderRadius: "50%",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            title="Centro de notificaciones"
          >
            <span role="img" aria-label="campana" style={{ fontSize: 18 }}>🔔</span>
            {pendientesGlobales.length > 0 && !notificacionesVistasPrincipal && (
              <span
                style={{
                  position: "absolute",
                  top: -2,
                  right: -2,
                  background: "#fadb14",
                  color: "#1a1a2e",
                  borderRadius: "999px",
                  padding: "0 6px",
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: "18px",
                  minWidth: 18,
                  textAlign: "center",
                }}
              >
                {pendientesGlobales.length}
              </span>
            )}
          </button>
          
          {/* Botón de Cerrar Sesión */}
          <button
            onClick={() => {
              // Limpiar sessionStorage (sesión temporal)
              sessionStorage.removeItem('authToken');
              sessionStorage.removeItem('usuarioActual');
              // También limpiar localStorage por si quedó algo antiguo
              localStorage.removeItem('authToken');
              localStorage.removeItem('usuarioActual');
              window.location.reload();
            }}
            style={{
              padding: "10px 18px",
              background: "rgba(0,0,0,0.25)",
              border: "2px solid rgba(255,255,255,0.3)",
              color: "white",
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 8,
              cursor: "pointer",
              transition: "all 0.2s"
            }}
          >
            🚪 Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Panel de Notificaciones */}
      {mostrarNotificacionesPrincipal && (
        <div
          style={{
            position: "fixed",
            top: 80,
            right: 20,
            width: 360,
            maxHeight: "70vh",
            overflow: "auto",
            background: "linear-gradient(180deg, #ffffff 0%, #fafafa 100%)",
            border: "2px solid #fa541c",
            borderRadius: 16,
            boxShadow: "0 15px 40px rgba(212, 56, 13, 0.25)",
            zIndex: 9999,
            padding: 20,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, paddingBottom: 12, borderBottom: "2px solid #fff1e6" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#d4380d", display: "flex", alignItems: "center", gap: 8 }}>
              <span>🔔</span> Centro de Notificaciones
            </div>
            <button
              type="button"
              onClick={() => setMostrarNotificacionesPrincipal(false)}
              style={{ fontSize: 12, color: "#8c8c8c", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
            >
              Cerrar ✕
            </button>
          </div>

          {notificacionesGlobales.length === 0 ? (
            <p style={{ fontSize: 13, color: "#8c8c8c", textAlign: "center", padding: 20 }}>✅ No tienes tareas pendientes.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {notificacionesGlobales.map((n) => {
                const urgente = n.diasRestantes !== null && n.diasRestantes <= 1;
                return (
                  <div
                    key={n.id}
                    style={{
                      border: urgente ? "2px solid #ff4d4f" : "1px solid #ffe7ba",
                      borderRadius: 12,
                      padding: 14,
                      background: urgente ? "linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)" : "linear-gradient(135deg, #fffbe6 0%, #fff7e6 100%)",
                      fontSize: 12,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontWeight: 700, color: "#1a1a2e" }}>{n.tipo}</span>
                      <span style={{ fontSize: 9, color: "#8c8c8c", background: "#f5f5f5", padding: "2px 6px", borderRadius: 4 }}>{n.id?.slice(0, 8)}...</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#595959", marginTop: 6 }}>
                      📅 Fecha límite: <strong>{n.fechaLimite}</strong>
                    </div>
                    {typeof n.diasRestantes === "number" && (
                      <div style={{ fontSize: 11, color: urgente ? "#cf1322" : "#1a1a2e", marginTop: 4, fontWeight: 600 }}>
                        {n.diasRestantes < 0
                          ? `⚠️ Vencida hace ${Math.abs(n.diasRestantes)} día(s)`
                          : n.diasRestantes === 0
                          ? "🔴 Vence hoy"
                          : n.diasRestantes === 1
                          ? "🟠 Vence en 1 día"
                          : `🟢 Vence en ${n.diasRestantes} días`}
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: "#8c8c8c", marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ 
                        display: "inline-block", 
                        width: 8, 
                        height: 8, 
                        borderRadius: "50%", 
                        background: n.estado === "Procesado" ? "#52c41a" : "#faad14" 
                      }}></span>
                      Estado: {n.estado}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Contenido principal */}
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 16px",
        minHeight: "calc(100vh - 80px)"
      }}>
        <div style={{
          width: "100%",
          maxWidth: 920,
          margin: "0 auto",
          background: "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 24,
          boxShadow: "0 25px 60px rgba(0,0,0,0.4)",
          backdropFilter: "blur(20px)",
          padding: "40px 48px",
          position: "relative",
          overflow: "hidden"
        }}>
          {/* Decoración de fondo */}
          <div style={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            background: "radial-gradient(circle, rgba(250,84,28,0.15) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none"
          }}></div>
          <div style={{
            position: "absolute",
            bottom: -80,
            left: -80,
            width: 250,
            height: 250,
            background: "radial-gradient(circle, rgba(255,197,61,0.1) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none"
          }}></div>

          {/* Encabezado con logo central */}
          <div style={{ textAlign: "center", marginBottom: 40, position: "relative", zIndex: 1 }}>
            <div style={{ 
              display: "inline-block",
              marginBottom: 20
            }}>
              <LogoBigBurguer size={100} />
            </div>
            
            <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: "linear-gradient(90deg, rgba(250,84,28,0.2) 0%, rgba(255,197,61,0.2) 100%)",
              padding: "6px 16px",
              borderRadius: 20,
              marginBottom: 16
            }}>
              <span style={{ width: 8, height: 8, background: "#52c41a", borderRadius: "50%", animation: "pulse 2s infinite" }}></span>
              <span style={{ color: "#ffc53d", fontSize: 11, fontWeight: 600, letterSpacing: 1 }}>SISTEMA ACTIVO</span>
            </div>
            
            <h1 style={{ 
              color: "white", 
              fontSize: 32, 
              fontWeight: 700, 
              margin: "0 0 12px 0",
              textShadow: "0 2px 10px rgba(0,0,0,0.3)"
            }}>
              Bienvenido al Portal
            </h1>
            <p style={{ 
              color: "rgba(255,255,255,0.7)", 
              fontSize: 15, 
              margin: 0,
              maxWidth: 500,
              marginLeft: "auto",
              marginRight: "auto"
            }}>
              Selecciona el módulo de trabajo para comenzar. Gestiona tu equipo,
              operaciones y configuraciones desde un solo lugar.
            </p>
          </div>
 
          {/* Tarjetas de opciones */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", 
            gap: 20,
            position: "relative",
            zIndex: 1
          }}>
            {/* Recursos Humanos */}
            {tienePermiso("ambiente_rh") && (
              <button
                type="button"
                onClick={() => setAmbiente("RH")}
                style={{
                  background: "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
                  border: "1px solid rgba(82, 196, 26, 0.3)",
                  borderRadius: 16,
                  padding: 24,
                  textAlign: "left",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "hidden"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = "0 15px 40px rgba(82, 196, 26, 0.25)";
                  e.currentTarget.style.borderColor = "rgba(82, 196, 26, 0.6)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.borderColor = "rgba(82, 196, 26, 0.3)";
                }}
              >
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: "linear-gradient(90deg, #52c41a 0%, #73d13d 100%)"
                }}></div>
                <div style={{ 
                  height: 56, 
                  width: 56, 
                  borderRadius: 14, 
                  background: "linear-gradient(135deg, rgba(82, 196, 26, 0.2) 0%, rgba(115, 209, 61, 0.1) 100%)",
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  marginBottom: 16,
                  fontSize: 28
                }}>
                  👥
                </div>
                <h2 style={{ color: "white", fontSize: 17, fontWeight: 700, margin: "0 0 8px 0" }}>
                  Recursos Humanos
                </h2>
                <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, margin: "0 0 16px 0", lineHeight: 1.5 }}>
                  Gestión de personal, nómina, ausencias, vacaciones, préstamos y liquidaciones.
                </p>
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "space-between",
                  paddingTop: 12,
                  borderTop: "1px solid rgba(255,255,255,0.1)"
                }}>
                  <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 10 }}>Talento Humano</span>
                  <span style={{ color: "#73d13d", fontSize: 12, fontWeight: 600 }}>Acceder →</span>
                </div>
              </button>
            )}
 
            {/* Administrativo */}
            {tienePermiso("ambiente_admin") && (
              <button
                type="button"
                onClick={() => setAmbiente("ADMIN")}
                style={{
                  background: "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
                  border: "1px solid rgba(24, 144, 255, 0.3)",
                  borderRadius: 16,
                  padding: 24,
                  textAlign: "left",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "hidden"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = "0 15px 40px rgba(24, 144, 255, 0.25)";
                  e.currentTarget.style.borderColor = "rgba(24, 144, 255, 0.6)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.borderColor = "rgba(24, 144, 255, 0.3)";
                }}
              >
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: "linear-gradient(90deg, #1890ff 0%, #40a9ff 100%)"
                }}></div>
                <div style={{ 
                  height: 56, 
                  width: 56, 
                  borderRadius: 14, 
                  background: "linear-gradient(135deg, rgba(24, 144, 255, 0.2) 0%, rgba(64, 169, 255, 0.1) 100%)",
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  marginBottom: 16,
                  fontSize: 28
                }}>
                  📊
                </div>
                <h2 style={{ color: "white", fontSize: 17, fontWeight: 700, margin: "0 0 8px 0" }}>
                  Administrativo
                </h2>
                <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, margin: "0 0 16px 0", lineHeight: 1.5 }}>
                  Cuadres diarios, inventarios, procesos de pago y control operativo.
                </p>
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "space-between",
                  paddingTop: 12,
                  borderTop: "1px solid rgba(255,255,255,0.1)"
                }}>
                  <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 10 }}>Gerencia & Operaciones</span>
                  <span style={{ color: "#40a9ff", fontSize: 12, fontWeight: 600 }}>Acceder →</span>
                </div>
              </button>
            )}

            {/* Configuraciones */}
            {tienePermiso("ambiente_configuraciones") && (
              <button
                type="button"
                onClick={() => setAmbiente("CONFIGURACIONES")}
                style={{
                  background: "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
                  border: "1px solid rgba(250, 84, 28, 0.3)",
                  borderRadius: 16,
                  padding: 24,
                  textAlign: "left",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "hidden"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = "0 15px 40px rgba(250, 84, 28, 0.25)";
                  e.currentTarget.style.borderColor = "rgba(250, 84, 28, 0.6)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.borderColor = "rgba(250, 84, 28, 0.3)";
                }}
              >
                <div style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: "linear-gradient(90deg, #fa541c 0%, #ff7a45 100%)"
                }}></div>
                <div style={{ 
                  height: 56, 
                  width: 56, 
                  borderRadius: 14, 
                  background: "linear-gradient(135deg, rgba(250, 84, 28, 0.2) 0%, rgba(255, 122, 69, 0.1) 100%)",
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  marginBottom: 16,
                  fontSize: 28
                }}>
                  ⚙️
                </div>
                <h2 style={{ color: "white", fontSize: 17, fontWeight: 700, margin: "0 0 8px 0" }}>
                  Configuraciones
                </h2>
                <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, margin: "0 0 16px 0", lineHeight: 1.5 }}>
                  Gestión de usuarios, perfiles, permisos y parámetros del sistema.
                </p>
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "space-between",
                  paddingTop: 12,
                  borderTop: "1px solid rgba(255,255,255,0.1)"
                }}>
                  <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 10 }}>Panel de Control</span>
                  <span style={{ color: "#ff7a45", fontSize: 12, fontWeight: 600 }}>Acceder →</span>
                </div>
              </button>
            )}
          </div>
 
          {/* Pie de página */}
          <div style={{ 
            marginTop: 32, 
            paddingTop: 20, 
            borderTop: "1px solid rgba(255,255,255,0.1)",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            position: "relative",
            zIndex: 1
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ opacity: 0.7 }}>
                <LogoBigBurguer size={28} />
              </div>
              <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>
                <strong style={{ color: "rgba(255,255,255,0.6)" }}>BIGBURGUER</strong> • Sistema de Gestión Empresarial
              </span>
            </div>
            <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 10 }}>
              v2.0 • Desarrollado para uso interno • © 2025
            </span>
          </div>
        </div>
      </div>

    {previewFile && (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16,
        }}
        onClick={() => setPreviewFile(null)}
      >
        <div
          style={{
            background: "white",
            borderRadius: 8,
            maxWidth: 700,
            width: "100%",
            maxHeight: "80vh",
            overflow: "auto",
            position: "relative",
            padding: 24,
            boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              background: "#ef4444",
              color: "white",
              border: "none",
              borderRadius: 4,
              padding: "4px 8px",
              fontSize: 12,
              cursor: "pointer",
            }}
            onClick={() => setPreviewFile(null)}
          >
            Cerrar
          </button>
          {(() => {
            const nombre = previewFile.nombre || "";
            const tipo = previewFile.tipo || "";
            const esImg = !!previewFile.url && (tipo.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(nombre));
            const esPdf = !!previewFile.url && (tipo === "application/pdf" || /\.(pdf)$/i.test(nombre));
            if (esImg) {
              return (
                <img
                  src={previewFile.url}
                  alt={nombre}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    borderRadius: 6,
                    display: "block",
                    margin: "0 auto",
                  }}
                />
              );
            }
            if (esPdf) {
              return (
                <iframe
                  src={previewFile.url}
                  title={nombre}
                  style={{ width: "100%", height: "70vh", border: "none" }}
                />
              );
            }
            if (previewFile.url) {
              return (
                <div style={{ textAlign: "center", fontSize: 16 }}>
                  <a href={previewFile.url} target="_blank" rel="noreferrer" style={{ color: "#0ea5e9" }}>
                    Descargar archivo
                  </a>
                </div>
              );
            }
            return null;
          })()}
          <div style={{ textAlign: "center", fontSize: 12, color: "#475569", fontWeight: 600 }}>
            {previewFile.nombre}
          </div>
        </div>
      </div>
    )}
      </div>
    </>
  );
}
 
// 👉 Vista cuando el ambiente es ADMINISTRATIVO
if (ambiente === "ADMIN") {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(180deg, #fff8f5 0%, #fff 100%)' }}>
      {/* 🔹 Barra de información del usuario */}
      <div style={{
        background: 'linear-gradient(90deg, #1a1a2e 0%, #16213e 100%)',
        padding: '8px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '2px solid #ff6b35'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>👤</span>
            <span style={{ color: 'white', fontSize: 12, fontWeight: 600 }}>{infoUsuarioActual.nombre}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14 }}>🎖️</span>
            <span style={{ color: '#ffc53d', fontSize: 11, fontWeight: 500 }}>{infoUsuarioActual.perfil}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14 }}>🏢</span>
            <span style={{ color: '#7dd3fc', fontSize: 11, fontWeight: 500 }}>{infoUsuarioActual.empresaNombre}</span>
          </div>
        </div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>
          {new Date().toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>
      <header style={{ background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)', borderBottom: '3px solid #ffc53d' }}>
        <div style={{ padding: '16px 16px 16px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <button
            onClick={() => setAmbiente(null)}
            style={{ 
              background: 'rgba(255,255,255,0.95)', 
              color: '#ff6b35',
              border: 'none',
              borderRadius: 20,
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 107, 53, 0.3)';
            }}
          >
            🏠 Ir al inicio
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-base font-bold text-white flex items-center gap-2">
              <span>📊</span> Módulo Administrativo
            </h1>
          </div>
        </div>
        
        {/* Navegación horizontal de botones */}
        <nav style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          flexWrap: 'nowrap', 
          gap: '8px', 
          padding: '0 16px 16px 16px',
          overflowX: 'auto',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
        }}>
          {/* Botón: CUADRE DIARIO */}
          {tienePermiso("admin_cuadre_diario") && (
          <button
            type="button"
            onClick={() => setVistaAdmin("CUADRE_DIARIO")}
            style={{
              background: vistaAdmin === "CUADRE_DIARIO"
                ? 'linear-gradient(135deg, #ff6b35 0%, #ff8c42 50%, #ffc53d 100%)'
                : 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
              border: vistaAdmin === "CUADRE_DIARIO"
                ? 'none'
                : '1px solid rgba(255, 107, 53, 0.25)',
              borderRadius: 8,
              padding: '6px 12px',
              minWidth: 'auto',
              maxWidth: '130px',
              display: 'inline-flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              cursor: 'pointer',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: vistaAdmin === "CUADRE_DIARIO"
                ? '0 3px 10px rgba(255, 107, 53, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                : '0 2px 4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              outline: vistaAdmin === "CUADRE_DIARIO" ? '2px solid rgba(255, 107, 53, 0.3)' : 'none',
              outlineOffset: '2px'
            }}
            onMouseEnter={(e) => {
              if (vistaAdmin !== "CUADRE_DIARIO") {
                e.currentTarget.style.transform = 'translateY(-1px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.2), inset 0 1px 0 rgba(255,255,255,0.8)';
                e.currentTarget.style.borderColor = 'rgba(255, 107, 53, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (vistaAdmin !== "CUADRE_DIARIO") {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)';
                e.currentTarget.style.borderColor = 'rgba(255, 107, 53, 0.25)';
              }
            }}
          >
            <span style={{ fontSize: 14 }}>💰</span>
            <span style={{
              fontSize: 13,
              fontWeight: 'normal',
              color: vistaAdmin === "CUADRE_DIARIO" ? '#ffffff' : '#1a1a2e',
              textShadow: vistaAdmin === "CUADRE_DIARIO" ? '0 1px 2px rgba(0,0,0,0.15)' : 'none'
            }}>
              Cuadre Diario
            </span>
          </button>
          )}

          {/* Botón: REVISAR CUADRE */}
          {tienePermiso("admin_revisar_cuadre") && (
          <button
            type="button"
            onClick={() => setVistaAdmin("REVISAR_CUADRE")}
            style={{
              background: vistaAdmin === "REVISAR_CUADRE"
                ? 'linear-gradient(135deg, #1a237e 0%, #3949ab 50%, #5c6bc0 100%)'
                : 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
              border: vistaAdmin === "REVISAR_CUADRE"
                ? 'none'
                : '1px solid rgba(26, 35, 126, 0.25)',
              borderRadius: 8,
              padding: '6px 12px',
              minWidth: 'auto',
              maxWidth: '140px',
              display: 'inline-flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              cursor: 'pointer',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: vistaAdmin === "REVISAR_CUADRE"
                ? '0 3px 10px rgba(26, 35, 126, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                : '0 2px 4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              outline: vistaAdmin === "REVISAR_CUADRE" ? '2px solid rgba(26, 35, 126, 0.3)' : 'none',
              outlineOffset: '2px'
            }}
            onMouseEnter={(e) => {
              if (vistaAdmin !== "REVISAR_CUADRE") {
                e.currentTarget.style.transform = 'translateY(-1px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(26, 35, 126, 0.2), inset 0 1px 0 rgba(255,255,255,0.8)';
                e.currentTarget.style.borderColor = 'rgba(26, 35, 126, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (vistaAdmin !== "REVISAR_CUADRE") {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)';
                e.currentTarget.style.borderColor = 'rgba(26, 35, 126, 0.25)';
              }
            }}
          >
            <span style={{ fontSize: 14 }}>🔍</span>
            <span style={{
              fontSize: 13,
              fontWeight: 'normal',
              color: vistaAdmin === "REVISAR_CUADRE" ? '#ffffff' : '#1a1a2e',
              textShadow: vistaAdmin === "REVISAR_CUADRE" ? '0 1px 2px rgba(0,0,0,0.15)' : 'none'
            }}>
              Revisar Cuadre
            </span>
          </button>
          )}

          {/* Botón: GASTOS */}
          {tienePermiso("admin_gastos") && (
          <button
            type="button"
            onClick={() => setVistaAdmin("GASTOS")}
            style={{
              background: vistaAdmin === "GASTOS"
                ? 'linear-gradient(135deg, #7b1fa2 0%, #9c27b0 50%, #ba68c8 100%)'
                : 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
              border: vistaAdmin === "GASTOS"
                ? 'none'
                : '1px solid rgba(123, 31, 162, 0.25)',
              borderRadius: 8,
              padding: '6px 12px',
              minWidth: 'auto',
              maxWidth: '120px',
              display: 'inline-flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              cursor: 'pointer',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: vistaAdmin === "GASTOS"
                ? '0 3px 10px rgba(123, 31, 162, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                : '0 2px 4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              outline: vistaAdmin === "GASTOS" ? '2px solid rgba(123, 31, 162, 0.3)' : 'none',
              outlineOffset: '2px'
            }}
            onMouseEnter={(e) => {
              if (vistaAdmin !== "GASTOS") {
                e.currentTarget.style.transform = 'translateY(-1px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(123, 31, 162, 0.2), inset 0 1px 0 rgba(255,255,255,0.8)';
                e.currentTarget.style.borderColor = 'rgba(123, 31, 162, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (vistaAdmin !== "GASTOS") {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)';
                e.currentTarget.style.borderColor = 'rgba(123, 31, 162, 0.25)';
              }
            }}
          >
            <span style={{ fontSize: 14 }}>💸</span>
            <span style={{
              fontSize: 13,
              fontWeight: 'normal',
              color: vistaAdmin === "GASTOS" ? '#ffffff' : '#1a1a2e',
              textShadow: vistaAdmin === "GASTOS" ? '0 1px 2px rgba(0,0,0,0.15)' : 'none'
            }}>
              Gastos
            </span>
          </button>
          )}

          {/* Botón: INVENTARIO */}
          {tienePermiso("admin_inventario") && (
          <button
            type="button"
            onClick={() => setVistaAdmin("INVENTARIO")}
            style={{
              background: vistaAdmin === "INVENTARIO"
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)'
                : 'rgba(255,255,255,0.9)',
              border: vistaAdmin === "INVENTARIO"
                ? '2px solid #ff6b35'
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: vistaAdmin === "INVENTARIO"
                ? '0 4px 12px rgba(255, 107, 53, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (vistaAdmin !== "INVENTARIO") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (vistaAdmin !== "INVENTARIO") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 14 }}>📦</span>
            <span style={{
              fontSize: 13,
              fontWeight: 'normal',
              color: vistaAdmin === "INVENTARIO" ? '#ffffff' : '#1a1a2e'
            }}>
              Inventario
            </span>
          </button>
          )}

          {/* Botón: PROCESOS ADMINISTRATIVOS */}
          {tienePermiso("admin_procesos_pago") && (
          <button
            type="button"
            onClick={() => setVistaAdmin("CUADRE_PAGOS")}
            style={{
              background: vistaAdmin === "CUADRE_PAGOS"
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)'
                : 'rgba(255,255,255,0.9)',
              border: vistaAdmin === "CUADRE_PAGOS"
                ? '2px solid #ff6b35'
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: vistaAdmin === "CUADRE_PAGOS"
                ? '0 4px 12px rgba(255, 107, 53, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (vistaAdmin !== "CUADRE_PAGOS") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (vistaAdmin !== "CUADRE_PAGOS") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 14 }}>📋</span>
            <span style={{
              fontSize: 13,
              fontWeight: 'normal',
              color: vistaAdmin === "CUADRE_PAGOS" ? '#ffffff' : '#1a1a2e'
            }}>
              Procesos Administrativos
            </span>
          </button>
          )}

          {/* Botón: CALENDARIO */}
          {tienePermiso("admin_calendario") && (
          <button
            type="button"
            onClick={() => setVistaAdmin("CALENDARIO")}
            style={{
              background: vistaAdmin === "CALENDARIO"
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)'
                : 'rgba(255,255,255,0.9)',
              border: vistaAdmin === "CALENDARIO"
                ? '2px solid #ff6b35'
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: vistaAdmin === "CALENDARIO"
                ? '0 4px 12px rgba(255, 107, 53, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (vistaAdmin !== "CALENDARIO") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (vistaAdmin !== "CALENDARIO") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 14 }}>📅</span>
            <span style={{
              fontSize: 13,
              fontWeight: 'normal',
              color: vistaAdmin === "CALENDARIO" ? '#ffffff' : '#1a1a2e'
            }}>
              Calendario
            </span>
          </button>
          )}

          {/* Botón: DIRECTORIO */}
          {tienePermiso("admin_directorio") && (
          <button
            type="button"
            onClick={() => setVistaAdmin("DIRECTORIO")}
            style={{
              background: vistaAdmin === "DIRECTORIO"
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)'
                : 'rgba(255,255,255,0.9)',
              border: vistaAdmin === "DIRECTORIO"
                ? '2px solid #ff6b35'
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: vistaAdmin === "DIRECTORIO"
                ? '0 4px 12px rgba(255, 107, 53, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (vistaAdmin !== "DIRECTORIO") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (vistaAdmin !== "DIRECTORIO") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 14 }}>📇</span>
            <span style={{
              fontSize: 13,
              fontWeight: 'normal',
              color: vistaAdmin === "DIRECTORIO" ? '#ffffff' : '#1a1a2e'
            }}>
              Directorio
            </span>
          </button>
          )}

          {/* Botón: PLANES DE TRABAJO */}
          {tienePermiso("admin_planes_trabajo") && (
          <button
            type="button"
            onClick={() => setVistaAdmin("PLANES_TRABAJO")}
            style={{
              background: vistaAdmin === "PLANES_TRABAJO"
                ? 'linear-gradient(135deg, #00b894 0%, #00cec9 100%)'
                : 'rgba(255,255,255,0.9)',
              border: vistaAdmin === "PLANES_TRABAJO"
                ? '2px solid #00b894'
                : '2px solid rgba(0, 184, 148, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: vistaAdmin === "PLANES_TRABAJO"
                ? '0 4px 12px rgba(0, 184, 148, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (vistaAdmin !== "PLANES_TRABAJO") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 184, 148, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (vistaAdmin !== "PLANES_TRABAJO") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 14 }}>📋</span>
            <span style={{
              fontSize: 13,
              fontWeight: 'normal',
              color: vistaAdmin === "PLANES_TRABAJO" ? '#ffffff' : '#1a1a2e'
            }}>
              Planes de Trabajo
            </span>
          </button>
          )}

          {/* Botón: ARQUEOS DE CAJA */}
          {tienePermiso("admin_arqueos_caja") && (
          <button
            type="button"
            onClick={() => setVistaAdmin("ARQUEOS_CAJA")}
            style={{
              background: vistaAdmin === "ARQUEOS_CAJA"
                ? 'linear-gradient(135deg, #fdcb6e 0%, #f39c12 100%)'
                : 'rgba(255,255,255,0.9)',
              border: vistaAdmin === "ARQUEOS_CAJA"
                ? '2px solid #f39c12'
                : '2px solid rgba(243, 156, 18, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: vistaAdmin === "ARQUEOS_CAJA"
                ? '0 4px 12px rgba(243, 156, 18, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (vistaAdmin !== "ARQUEOS_CAJA") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(243, 156, 18, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (vistaAdmin !== "ARQUEOS_CAJA") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 14 }}>💰</span>
            <span style={{
              fontSize: 13,
              fontWeight: 'normal',
              color: vistaAdmin === "ARQUEOS_CAJA" ? '#ffffff' : '#1a1a2e'
            }}>
              Arqueos de Caja
            </span>
          </button>
          )}

          {/* Botón: INFORME RENDIMIENTO Y COSTOS */}
          {tienePermiso("admin_informe_rendimiento") && (
          <button
            type="button"
            onClick={() => {
              if (esVistaTodasEmpresas) {
                alertaTodasEmpresas("Informe de Rendimiento");
                return;
              }
              setVistaAdmin("INFORME_RENDIMIENTO");
            }}
            style={{
              background: vistaAdmin === "INFORME_RENDIMIENTO"
                ? 'linear-gradient(135deg, #1e3a5f 0%, #0ea5e9 100%)'
                : 'rgba(255,255,255,0.9)',
              border: vistaAdmin === "INFORME_RENDIMIENTO"
                ? '2px solid #0ea5e9'
                : '2px solid rgba(14, 165, 233, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: vistaAdmin === "INFORME_RENDIMIENTO"
                ? '0 4px 12px rgba(14, 165, 233, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              opacity: esVistaTodasEmpresas ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (vistaAdmin !== "INFORME_RENDIMIENTO") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (vistaAdmin !== "INFORME_RENDIMIENTO") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 14 }}>📊</span>
            <span style={{
              fontSize: 13,
              fontWeight: 'normal',
              color: vistaAdmin === "INFORME_RENDIMIENTO" ? '#ffffff' : '#1a1a2e'
            }}>
              Informe Rendimiento
            </span>
          </button>
          )}

          {/* Botón: SECRETARÍA DE SALUD */}
          {tienePermiso("admin_secretaria_salud") && (
          <button
            type="button"
            onClick={() => {
              if (esVistaTodasEmpresas) {
                alertaTodasEmpresas("Secretaría de Salud");
                return;
              }
              setVistaAdmin("SECRETARIA_SALUD");
            }}
            style={{
              background: vistaAdmin === "SECRETARIA_SALUD"
                ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
                : 'rgba(255,255,255,0.9)',
              border: vistaAdmin === "SECRETARIA_SALUD"
                ? '2px solid #059669'
                : '2px solid rgba(5, 150, 105, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: vistaAdmin === "SECRETARIA_SALUD"
                ? '0 4px 12px rgba(5, 150, 105, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              opacity: esVistaTodasEmpresas ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (vistaAdmin !== "SECRETARIA_SALUD") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (vistaAdmin !== "SECRETARIA_SALUD") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 14 }}>🏥</span>
            <span style={{
              fontSize: 13,
              fontWeight: 'normal',
              color: vistaAdmin === "SECRETARIA_SALUD" ? '#ffffff' : '#1a1a2e'
            }}>
              Secretaría de Salud
            </span>
          </button>
          )}
        </nav>
      </header>
 
      <main className="flex-1 px-4 py-6">
        {/* Vista: CUADRE DIARIO */}
        {vistaAdmin === "CUADRE_DIARIO" && (
          <div style={{ maxWidth: '100%', margin: '0 auto', padding: '0 16px' }}>
            <CuadreDiarioRoot 
              sedes={sedes} 
              empleados={empleados} 
              usuarioActual={usuarioConPermisos || usuario}
              empleadoActual={empleadoActual}
              esGerente={esGerente}
              tienePermiso={tienePermiso}
              filtroEmpresa={filtroEmpresa}
            />
          </div>
        )}

        {/* Vista: REVISAR CUADRE */}
        {vistaAdmin === "REVISAR_CUADRE" && (
          <div style={{ maxWidth: '100%', margin: '0 auto', padding: '0 16px' }}>
            <RevisarCuadreRoot 
              sedes={sedes} 
              empleados={empleados} 
              usuarioActual={usuarioConPermisos || usuario}
              empleadoActual={empleadoActual}
              esGerente={esGerente}
              tienePermiso={tienePermiso}
              filtroEmpresa={filtroEmpresa}
            />
          </div>
        )}

        {/* Vista: GASTOS */}
        {vistaAdmin === "GASTOS" && (
          <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 16px' }}>
            <GastosRoot 
              sedes={sedes} 
              empleados={empleados} 
              usuarioActual={usuarioConPermisos || usuario}
              empleadoActual={empleadoActual}
              esGerente={esGerente}
              tienePermiso={tienePermiso}
              filtroEmpresa={filtroEmpresa}
            />
          </div>
        )}
 
        {/* Vista: PROCESOS ADMINISTRATIVOS */}
        {vistaAdmin === "CUADRE_PAGOS" && (
          <div className="max-w-5xl mx-auto">
            <CuadrePagos
              sedes={sedes}
              empleados={empleados}
              todosLosEmpleados={empleadosGerenciaTodas}
              tareas={tareasPago}
              setTareas={setTareasPago}
              usuarioActual={usuarioConPermisos || usuario}
              empleadoActual={empleadoActual}
              esGerente={esGerente}
              tienePermiso={tienePermiso}
              filtroEmpresa={filtroEmpresa}
            />
          </div>
        )}
        {vistaAdmin === "INVENTARIO" && (
          <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 16px' }}>
            {/* Selector de tipo de inventario */}
            {tipoInventario === "selector" && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 24,
                paddingTop: 40,
                paddingBottom: 40
              }}>
                <h2 style={{ 
                  fontSize: 22, 
                  fontWeight: 700, 
                  color: '#1a1a2e', 
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10
                }}>
                  <span style={{ fontSize: 28 }}>📦</span>
                  Selecciona el Tipo de Inventario
                </h2>
                
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {/* Botón Inventario Diario */}
                  {(tienePermiso("admin_inventario_diario") || tienePermiso("admin_inventario_crear")) && (
                    <button
                      onClick={() => setTipoInventario("diario")}
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 16,
                        padding: '30px 40px',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 12,
                        minWidth: 200,
                        boxShadow: '0 8px 25px rgba(16, 185, 129, 0.35)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 12px 35px rgba(16, 185, 129, 0.45)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(16, 185, 129, 0.35)';
                      }}
                    >
                      <span style={{ fontSize: 48 }}>📋</span>
                      <span style={{ fontSize: 18, fontWeight: 700 }}>Inventario Diario</span>
                      <span style={{ fontSize: 12, opacity: 0.9, textAlign: 'center' }}>
                        Registro diario de productos frescos y adicionales
                      </span>
                    </button>
                  )}
                  
                  {/* Botón Inventario Integral */}
                  {(tienePermiso("admin_inventario_integral") || tienePermiso("admin_inventario_integral_crear") || tienePermiso("admin_inventario_integral_ver")) && (
                    <button
                      onClick={() => setTipoInventario("integral")}
                      style={{
                        background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: 16,
                        padding: '30px 40px',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 12,
                        minWidth: 200,
                        boxShadow: '0 8px 25px rgba(124, 58, 237, 0.35)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 12px 35px rgba(124, 58, 237, 0.45)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(124, 58, 237, 0.35)';
                      }}
                    >
                      <span style={{ fontSize: 48 }}>📊</span>
                      <span style={{ fontSize: 18, fontWeight: 700 }}>Inventario Integral</span>
                      <span style={{ fontSize: 12, opacity: 0.9, textAlign: 'center' }}>
                        Conteo físico completo con flujo de gestión
                      </span>
                    </button>
                  )}
                </div>
              </div>
            )}
            
            {/* Vista Inventario Diario */}
            {tipoInventario === "diario" && (
              <div>
                <button
                  onClick={() => setTipoInventario("selector")}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#7c3aed',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 16,
                    padding: '8px 0'
                  }}
                >
                  ← Volver al selector
                </button>
                <InventarioRoot sedes={sedes.map(s => s.nombre)} empleados={empleados} tienePermiso={tienePermiso} filtroEmpresa={filtroEmpresa} usuarioActual={usuarioConPermisos || usuario} empleadoActual={empleadoActual} />
              </div>
            )}
            
            {/* Vista Inventario Integral */}
            {tipoInventario === "integral" && (
              <div>
                <button
                  onClick={() => setTipoInventario("selector")}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#7c3aed',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 16,
                    padding: '8px 0'
                  }}
                >
                  ← Volver al selector
                </button>
                <InventarioIntegralRoot 
                  sedes={sedes} 
                  empleados={empleados} 
                  tienePermiso={tienePermiso} 
                  filtroEmpresa={filtroEmpresa} 
                  usuarioActual={usuarioConPermisos || usuario} 
                  empleadoActual={empleadoActual}
                  productosBase={[...PRODUCTOS_INVENTARIO_DIARIO, ...PRODUCTOS_INVENTARIO_ADICIONAL]}
                />
              </div>
            )}
          </div>
        )}

        {/* Vista: CALENDARIO */}
        {vistaAdmin === "CALENDARIO" && (
          <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 16px' }}>
            <CalendarioEventos
              empleados={empleados}
              sedes={sedes}
              usuarioActual={usuarioConPermisos || usuario}
              empleadoActual={empleadoActual}
              filtroEmpresa={filtroEmpresa}
              tienePermiso={tienePermiso}
            />
          </div>
        )}

        {/* Vista: DIRECTORIO */}
        {vistaAdmin === "DIRECTORIO" && (
          <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 16px' }}>
            <DirectorioContactos
              sedes={sedes}
              empleados={empleados}
              filtroEmpresa={filtroEmpresa}
              tienePermiso={tienePermiso}
              usuarioActual={usuarioConPermisos || usuario}
              empleadoActual={empleadoActual}
            />
          </div>
        )}

        {/* Vista: PLANES DE TRABAJO */}
        {vistaAdmin === "PLANES_TRABAJO" && (
          <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 16px' }}>
            <PlanesTrabajoAdmin
              sedes={sedes}
              empleados={empleados}
              filtroEmpresa={filtroEmpresa}
              tienePermiso={tienePermiso}
              usuarioActual={usuarioConPermisos || usuario}
              empleadoActual={empleadoActual}
            />
          </div>
        )}

        {/* Vista: ARQUEOS DE CAJA */}
        {vistaAdmin === "ARQUEOS_CAJA" && (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
            <ArqueosCajaAdmin
              sedes={sedes}
              empleados={empleados}
              filtroEmpresa={filtroEmpresa}
              tienePermiso={tienePermiso}
              esGerente={esGerente}
            />
          </div>
        )}

        {/* Vista: INFORME RENDIMIENTO Y COSTOS */}
        {vistaAdmin === "INFORME_RENDIMIENTO" && (
          <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '0 16px' }}>
            <InformeRendimientoCostos
              empleados={empleados}
              nominasGuardadas={nominasRepo}
              memorandos={memorandos}
              ausencias={ausencias}
              vacaciones={vacaciones}
              horariosRepo={horariosRepo}
              prestamos={prestamos}
              sedes={sedes}
              tienePermiso={tienePermiso}
              empresaId={filtroEmpresa}
              valoresAnuales={valoresAnuales}
            />
          </div>
        )}

        {/* Vista: SECRETARÍA DE SALUD */}
        {vistaAdmin === "SECRETARIA_SALUD" && (
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 16px' }}>
            <SecretariaSaludAdmin
              empleados={empleados}
              sedes={sedes}
              horariosRepo={horariosRepo}
              ausencias={ausencias}
              vacaciones={vacaciones}
              tienePermiso={tienePermiso}
              empresaId={filtroEmpresa}
              usuarioActual={usuarioConPermisos || usuario}
              empleadoActual={empleadoActual}
            />
          </div>
        )}


      </main>
    </div>
  );
}

  // 👉 Vista cuando el ambiente es CONFIGURACIONES
  if (ambiente === "CONFIGURACIONES") {
    return <Configuraciones empleados={empleados} setEmpleados={setEmpleados} setAmbiente={setAmbiente} user={usuario} tienePermiso={tienePermiso} valoresAnuales={valoresAnuales} setValoresAnuales={setValoresAnuales} empresas={empresas} filtroEmpresa={filtroEmpresa} esSuperAdmin={esSuperAdminVerificado} />;
  }

  // 👉 Si ambiente = "RH", seguimos con tu interfaz de siempre
  return (
    <div className="flex flex-col h-screen text-slate-900">
      {/* 🔹 Barra de información del usuario */}
      <div style={{
        background: 'linear-gradient(90deg, #1a1a2e 0%, #16213e 100%)',
        padding: '8px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '2px solid #ff6b35'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>👤</span>
            <span style={{ color: 'white', fontSize: 12, fontWeight: 600 }}>{infoUsuarioActual.nombre}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14 }}>🎖️</span>
            <span style={{ color: '#ffc53d', fontSize: 11, fontWeight: 500 }}>{infoUsuarioActual.perfil}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14 }}>🏢</span>
            <span style={{ color: '#7dd3fc', fontSize: 11, fontWeight: 500 }}>{infoUsuarioActual.empresaNombre}</span>
          </div>
        </div>
        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>
          {new Date().toLocaleDateString('es-CO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>
      {/* Header horizontal con navegación */}
      <header style={{ background: 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)', borderBottom: '3px solid #ffc53d' }}>
        <div style={{ padding: '16px 16px 16px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <button
            onClick={() => setAmbiente(null)}
            style={{ 
              background: 'rgba(255,255,255,0.95)', 
              color: '#ff6b35',
              border: 'none',
              borderRadius: 20,
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 107, 53, 0.3)';
            }}
          >
            🏠 Ir al inicio
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-base font-bold text-white flex items-center gap-2">
              <span>👥</span> Gestión RRHH
            </h1>
            {/* Selector de empresa para super_admin */}
            {esSuperAdminVerificado && empresas.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 16 }}>
                <span style={{ color: 'white', fontSize: 12, fontWeight: 500 }}>🏢 Empresa:</span>
                <select
                  value={empresaSeleccionada || ''}
                  onChange={async (e) => {
                    const valor = e.target.value;
                    setEmpresaSeleccionada(valor === '' ? null : valor);
                    setEmpresaYaCargada(true); // 🔹 Marcar que el usuario ya hizo una selección
                    // 💾 Guardar en Supabase para persistir entre dispositivos
                    if (usuario?.id) {
                      const { error } = await supabase
                        .from('usuarios')
                        .update({ empresa_preferida: valor || null })
                        .eq('id', usuario.id);
                      if (error) {
                        console.error("❌ Error guardando empresa preferida:", error);
                      } else {
                        console.log("💾 Empresa preferida guardada en Supabase:", valor || "NINGUNA");
                      }
                    }
                    console.log("🔄 Empresa seleccionada cambiada a:", valor || "TODAS");
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.95)',
                    border: '2px solid #ff6b35',
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#ff6b35',
                    cursor: 'pointer',
                    minWidth: 180
                  }}
                >
                  <option value="">-- Todas las empresas --</option>
                  {empresas.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
        
        {/* Navegación horizontal de botones */}
        <nav style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          flexWrap: 'nowrap', 
          gap: '8px', 
          padding: '0 16px 16px 16px',
          overflowX: 'auto',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)'
        }}>
          {false && (
          <button
            onClick={() => setTab("empleados")}
            className={
              "text-left px-2 py-1 rounded " +
              (tab === "empleados" ? "bg-white shadow" : "")
            }
          >
            <span className="inline-flex items-center gap-2">
              <img
                src="data:image/png;base64,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"
                alt=""
                className="w-4 h-4 rounded"
              />
              <span>Empleados</span>
            </span>
          </button>
          )}
 
          {tienePermiso("rh_empleados") && (
          <button
            onClick={() => {
              if (esVistaTodasEmpresas) {
                alertaTodasEmpresas("Empleados");
                return;
              }
              setTab("empleados");
            }}
            style={{
              background: tab === "empleados"
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)'
                : 'rgba(255,255,255,0.9)',
              border: tab === "empleados"
                ? '2px solid #ff6b35'
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tab === "empleados"
                ? '0 4px 12px rgba(255, 107, 53, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              opacity: esVistaTodasEmpresas ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (tab !== "empleados") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tab !== "empleados") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>👥</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tab === "empleados" ? '#ffffff' : '#1a1a2e'
            }}>
              Empleados
            </span>
          </button>
          )}

          {tienePermiso("rh_sedes") && (
          <button
            onClick={() => setTab("sedes")}
            style={{
              background: tab === "sedes"
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)'
                : 'rgba(255,255,255,0.9)',
              border: tab === "sedes"
                ? '2px solid #ff6b35'
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tab === "sedes"
                ? '0 4px 12px rgba(255, 107, 53, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              if (tab !== "sedes") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tab !== "sedes") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>🏢</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tab === "sedes" ? '#ffffff' : '#1a1a2e'
            }}>
              Sedes
            </span>
          </button>
          )}
 
          {tienePermiso("rh_horarios") && (
          <button
            onClick={() => {
              if (esVistaTodasEmpresas) {
                alertaTodasEmpresas("Horarios");
                return;
              }
              setTab("horarios");
            }}
            style={{
              background: tab === "horarios"
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)'
                : 'rgba(255,255,255,0.9)',
              border: tab === "horarios"
                ? '2px solid #ff6b35'
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tab === "horarios"
                ? '0 4px 12px rgba(255, 107, 53, 0.35)'
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              opacity: esVistaTodasEmpresas ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (tab !== "horarios") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tab !== "horarios") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>🕐</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tab === "horarios" ? '#ffffff' : '#1a1a2e'
            }}>
              Horarios
            </span>
          </button>
          )}

          {tienePermiso("rh_memorandos") && (
          <button
            onClick={() => setTab("memorandos")}
            style={{
              background: tab === "memorandos" 
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' 
                : 'rgba(255,255,255,0.9)',
              border: tab === "memorandos" 
                ? '2px solid #ff6b35' 
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tab === "memorandos" 
                ? '0 4px 12px rgba(255, 107, 53, 0.35)' 
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              width: '200px'
            }}
            onMouseEnter={(e) => {
              if (tab !== "memorandos") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tab !== "memorandos") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>⚖️</span>
            <span style={{
              fontSize: 12,
              fontWeight: 600,
              color: tab === "memorandos" ? '#ffffff' : '#1a1a2e'
            }}>
              Procesos Disciplinarios
            </span>
          </button>
          )}
 
          {tienePermiso("rh_ausencias") && (
          <button
            onClick={() => setTab("ausencias")}
            style={{
              background: tab === "ausencias" 
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' 
                : 'rgba(255,255,255,0.9)',
              border: tab === "ausencias" 
                ? '2px solid #ff6b35' 
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tab === "ausencias" 
                ? '0 4px 12px rgba(255, 107, 53, 0.35)' 
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              width: '180px'
            }}
            onMouseEnter={(e) => {
              if (tab !== "ausencias") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tab !== "ausencias") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>🏖️</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tab === "ausencias" ? '#ffffff' : '#1a1a2e'
            }}>
              Ausencias
            </span>
          </button>
          )}
 
          {tienePermiso("rh_vacaciones") && (
          <button
            onClick={() => setTab("vacaciones")}
            style={{
              background: tab === "vacaciones" 
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' 
                : 'rgba(255,255,255,0.9)',
              border: tab === "vacaciones" 
                ? '2px solid #ff6b35' 
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tab === "vacaciones" 
                ? '0 4px 12px rgba(255, 107, 53, 0.35)' 
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              width: '180px'
            }}
            onMouseEnter={(e) => {
              if (tab !== "vacaciones") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tab !== "vacaciones") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>🌴</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tab === "vacaciones" ? '#ffffff' : '#1a1a2e'
            }}>
              Vacaciones
            </span>
          </button>
          )}

          {tienePermiso("rh_prestamos") && (
          <button
            onClick={() => setTab("prestamos")}
            style={{
              background: tab === "prestamos" 
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' 
                : 'rgba(255,255,255,0.9)',
              border: tab === "prestamos" 
                ? '2px solid #ff6b35' 
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tab === "prestamos" 
                ? '0 4px 12px rgba(255, 107, 53, 0.35)' 
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              width: '180px'
            }}
            onMouseEnter={(e) => {
              if (tab !== "prestamos") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tab !== "prestamos") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>💸</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tab === "prestamos" ? '#ffffff' : '#1a1a2e'
            }}>
              Préstamos
            </span>
          </button>
          )}
 
          {tienePermiso("rh_nomina") && (
          <button
            onClick={() => {
              if (esVistaTodasEmpresas) {
                alertaTodasEmpresas("Nómina");
                return;
              }
              setTab("nomina");
            }}
            style={{
              background: tab === "nomina"
                ? 'linear-gradient(135deg, #ff6b35 0%, #ff8c42 50%, #ffc53d 100%)'
                : 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
              border: tab === "nomina"
                ? 'none'
                : '1px solid rgba(255, 107, 53, 0.25)',
              borderRadius: 8,
              padding: '6px 12px',
              minWidth: 'auto',
              maxWidth: '120px',
              display: 'inline-flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              cursor: 'pointer',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: tab === "nomina"
                ? '0 3px 10px rgba(255, 107, 53, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                : '0 2px 4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              outline: tab === "nomina" ? '2px solid rgba(255, 107, 53, 0.3)' : 'none',
              outlineOffset: '2px',
              opacity: esVistaTodasEmpresas ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (tab !== "nomina") {
                e.currentTarget.style.transform = 'translateY(-1px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.2), inset 0 1px 0 rgba(255,255,255,0.8)';
                e.currentTarget.style.borderColor = 'rgba(255, 107, 53, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (tab !== "nomina") {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)';
                e.currentTarget.style.borderColor = 'rgba(255, 107, 53, 0.25)';
              }
            }}
          >
            <span style={{ 
              fontSize: 14,
              filter: tab === "nomina" ? 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' : 'none'
            }}>📊</span>
            <span style={{
              fontSize: 12,
              fontWeight: 600,
              color: tab === "nomina" ? '#ffffff' : '#2d3436',
              textShadow: tab === "nomina" ? '0 1px 2px rgba(0,0,0,0.15)' : 'none',
              letterSpacing: '0.3px'
            }}>
              Nómina
            </span>
          </button>
          )}

          {tienePermiso("rh_liquidaciones") && (
          <button
            onClick={() => setTab("liquidaciones")}
            style={{
              background: tab === "liquidaciones" 
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' 
                : 'rgba(255,255,255,0.9)',
              border: tab === "liquidaciones" 
                ? '2px solid #ff6b35' 
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tab === "liquidaciones" 
                ? '0 4px 12px rgba(255, 107, 53, 0.35)' 
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              width: '180px'
            }}
            onMouseEnter={(e) => {
              if (tab !== "liquidaciones") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tab !== "liquidaciones") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>📄</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tab === "liquidaciones" ? '#ffffff' : '#1a1a2e'
            }}>
              Liquidaciones
            </span>
          </button>
          )}

          {tienePermiso("rh_certificaciones") && (
          <button
            onClick={() => setTab("certificaciones")}
            style={{
              background: tab === "certificaciones" 
                ? 'linear-gradient(135deg, #ff6b35 0%, #ffc53d 100%)' 
                : 'rgba(255,255,255,0.9)',
              border: tab === "certificaciones" 
                ? '2px solid #ff6b35' 
                : '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tab === "certificaciones" 
                ? '0 4px 12px rgba(255, 107, 53, 0.35)' 
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              width: '180px'
            }}
            onMouseEnter={(e) => {
              if (tab !== "certificaciones") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tab !== "certificaciones") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>📜</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tab === "certificaciones" ? '#ffffff' : '#1a1a2e'
            }}>
              Certificaciones
            </span>
          </button>
          )}

          {tienePermiso("rh_contratos") && (
          <button
            onClick={() => setTab("contratos")}
            style={{
              background: tab === "contratos" 
                ? 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)' 
                : 'rgba(255,255,255,0.9)',
              border: tab === "contratos" 
                ? '2px solid #1a237e' 
                : '2px solid rgba(26, 35, 126, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tab === "contratos" 
                ? '0 4px 12px rgba(26, 35, 126, 0.35)' 
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              width: '180px'
            }}
            onMouseEnter={(e) => {
              if (tab !== "contratos") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(26, 35, 126, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tab !== "contratos") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>📝</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tab === "contratos" ? '#ffffff' : '#1a1a2e'
            }}>
              Contratos
            </span>
          </button>
          )}

          {tienePermiso("rh_intranet_solicitudes") && (
          <button
            onClick={() => setTab("solicitudes_intranet")}
            style={{
              background: tab === "solicitudes_intranet" 
                ? 'linear-gradient(135deg, #00897b 0%, #4db6ac 100%)' 
                : 'rgba(255,255,255,0.9)',
              border: tab === "solicitudes_intranet" 
                ? '2px solid #00897b' 
                : '2px solid rgba(0, 137, 123, 0.3)',
              borderRadius: 10,
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: tab === "solicitudes_intranet" 
                ? '0 4px 12px rgba(0, 137, 123, 0.35)' 
                : '0 2px 6px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              width: '180px'
            }}
            onMouseEnter={(e) => {
              if (tab !== "solicitudes_intranet") {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 137, 123, 0.25)';
              }
            }}
            onMouseLeave={(e) => {
              if (tab !== "solicitudes_intranet") {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)';
              }
            }}
          >
            <span style={{ fontSize: 16 }}>📬</span>
            <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: tab === "solicitudes_intranet" ? '#ffffff' : '#1a1a2e'
            }}>
              Solicitudes Intranet
            </span>
          </button>
          )}
        </nav>
      </header>
 
      <main className="flex-1 overflow-auto bg-slate-100">
        {tab === "empleados" && tienePermiso("rh_empleados") && <EmpleadosTab />}

{tab === "horarios" && tienePermiso("rh_horarios") && (
  <Horarios
    empleados={empleados}
    sedes={sedes}
    ausencias={ausencias}
    horariosRepo={Array.isArray(horariosRepo) ? horariosRepo : []}
    onGuardarHorario={guardarHorarioRepo}
    onActualizarHorario={actualizarHorarioRepo}
    tienePermiso={tienePermiso}
  />
)}
 
        {tab === "memorandos" && tienePermiso("rh_memorandos") && (
          <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 16px' }}>
            <Memorandos
              empleados={empleados}
              memorandos={memorandos}
              onCreate={crearMemorando}
              onDelete={eliminarMemorando}
              onUpdate={actualizarMemorando}
              onAttach={adjuntarDocumentoMemorando}
              tienePermiso={tienePermiso}
            />
          </div>
        )}
 
        {tab === "ausencias" && tienePermiso("rh_ausencias") && (
          <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 16px' }}>
            <Ausencias
              empleados={empleados}
              ausencias={ausencias}
              onCreate={crearAusencia}
              onDelete={eliminarAusencia}
              nominasGuardadas={nominasRepo}
              tienePermiso={tienePermiso}
              onGuardarLiquidacion={handleGuardarLiquidacion}
              liquidacionesRepo={liquidacionesRepo}
              valoresAnuales={valoresAnuales}
            />
          </div>
        )}

        {tab === "sedes" && tienePermiso("rh_sedes") && (
          <Sedes sedes={sedes} setSedes={setSedes} filtroEmpresa={filtroEmpresa} />
        )}
 
        {tab === "vacaciones" && tienePermiso("rh_vacaciones") && (
          <Vacaciones empleados={empleados} ausencias={ausencias} />
        )}
 
{tab === "prestamos" && tienePermiso("rh_prestamos") && (
  <Prestamos
    empleados={empleados}
    prestamos={prestamos}
    sedes={sedes}        
    onCreate={crearPrestamo}
    onAbono={abonarPrestamo}
    tienePermiso={tienePermiso}
    esGerencia={esGerente}
    onReactivarCuota={reactivarCuotaIndividual}
    onEliminar={eliminarPrestamo}
    usuario={usuario}
  />
)}
 
        {tab === "nomina" && tienePermiso("rh_nomina") && (
          <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 16px' }}>
            <Nomina
              empleados={empleados}
              prestamos={prestamos}
              liquidaciones={liquidacionesRepo}
              onGuardarNomina={guardarNominaRepo}
              nominasGuardadas={nominasRepo}
              onMarcarCuotasUsadas={marcarCuotasUsadasEnPrestamos}
              onAbono={abonarPrestamo}
              onReabrirNomina={reabrirNomina}
              ausencias={ausencias}
              horariosRepo={horariosRepo}
              tienePermiso={tienePermiso}
              valoresAnuales={valoresAnuales}
              empresaId={filtroEmpresa}
              sedes={sedes}
            />
          </div>
        )}
 
        {tab === "liquidaciones" && tienePermiso("rh_liquidaciones") && (
          <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 16px' }}>
            <Liquidaciones
              empleados={empleados}
              ausencias={ausencias}
              repo={liquidacionesRepo}
              onGuardarLiquidacion={handleGuardarLiquidacion}
              onEliminarLiquidacion={handleEliminarLiquidacion}
              onCrearAusencia={crearAusencia}
              onInactivarEmpleado={inactivarEmpleado}
              tienePermiso={tienePermiso}
              valoresAnuales={valoresAnuales}
              sedes={sedes}
              empresaId={filtroEmpresa}
            />
          </div>
        )}

        {tab === "certificaciones" && tienePermiso("rh_certificaciones") && (
  <CertificacionesLaborales empleados={empleados} sedes={sedes} tienePermiso={tienePermiso} filtroEmpresa={filtroEmpresa} />
)}

        {tab === "contratos" && tienePermiso("rh_contratos") && (
  <ContratosLaborales empleados={empleados} sedes={sedes} tienePermiso={tienePermiso} valoresAnuales={valoresAnuales} filtroEmpresa={filtroEmpresa} />
)}

        {tab === "solicitudes_intranet" && tienePermiso("rh_intranet_solicitudes") && (
  <SolicitudesIntranetAdmin tienePermiso={tienePermiso} filtroEmpresa={filtroEmpresa} />
)}
      </main>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppConAutenticacion />
    </AuthProvider>
  );
}

